// DispatchDeck - Billing Module
// Calendar-first view with job drill-down, ticket management, and permit attachment
// Enhanced to show ticketLines subcollection detail

import {
  db,
  auth, // TASK-1333 — job creates must stamp the caller's uid (rules-enforced)
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  arrayUnion,
  arrayRemove, // TASK-9755 — crewRemovedEntryIds deltas (never a whole-array write)
  increment,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from '../firebase-config.js?v=1868';

// BI is now a standalone module — no longer imported here
import { processPhoto, generateThumbnail, validateImageFile } from '../utils/imageProcessor.js?v=1955';
import { toDrawablePhoto, photoErrorMessage, detectHeic, createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
import {
  createSlip as slipCreate,
  getSlipsForTicket as slipGetForTicket,
  lockSlip as slipLock,
  voidSlip as slipVoid,
  updateSlip as slipUpdate,
  getRunningTotals as slipGetRunningTotals,
  printSlip as slipPrint,
  buildSlipHTML as slipBuildHTML,
  // TASK-4832 — plain-words failure copy for a biller whose slip did not save.
  // It lives in slipHelpers.js because only that module can tell an EXHAUSTED
  // number allocation apart from an honest access denial: the monotonic
  // _counters rule answers both with `permission-denied`, and getNextSlipNumber
  // tags the one it gave up on. See the tagging note there before changing this.
  slipSaveFailedMessage
} from '../utils/slipHelpers.js?v=6';
import { capturePing as gpsCapturePing } from '../utils/gpsPing.js?v=2';
// MERGE NOTE (dispatcher 394f58cb, 2026-09-09): TASK-4825 and TASK-4828 each
// added an independent import at this exact line, in parallel lanes. Neither
// replaces the other and the two modules are unrelated — counterRetry is the
// shared _counters loop, poField is the shared PO hygiene. Resolution is BOTH,
// in the order they were authored. Verified after: both symbols are referenced
// in this file and the whole suite is green.
// TASK-4825 — the `_counters` retry loop that was written HERE (TASK-4823) now
// lives in utils/counterRetry.js so slipHelpers.js and dispatch.js run the SAME
// loop instead of copies that had already drifted (24 attempts vs 64). Nothing
// about the algorithm changed in the move. Aliased to the old private name so
// the two call sites below are byte-identical to the reviewed version.
// 🔴 Bump this `?v=` in lockstep with views/dispatch.js and utils/slipHelpers.js.
import { allocateCounterWithRetry as _allocateCounterWithRetry } from '../utils/counterRetry.js?v=1';
// TASK-4828 — PO hygiene on a money field. ONE implementation, imported, never
// re-typed: TASK-4782 shipped these as module-local functions in dispatch.js for
// the job form, and a second hand-written copy here is precisely how a money
// field rots. These are pure functions with zero module state, so the `?v=`
// instance rule (Pitfall #1) has no bite — but keep this buster in sync with
// projects.js, which imports the same module.
import {
  normalizePoForSave as poNormalizeForSave,
  poGroupKey,
  poEntryIssues,
  renderPoHintHTML
} from '../utils/poField.js?v=1';
// TASK-9868 — who uploaded a ticket document + what it is. Pure module (no
// state), shared with jobboard.js, so two instances are harmless.
import {
  normalizeTicketDocType,
  ticketDocTypeLabel,
  ticketDocPrintLabel,
  ticketDocTypeOptionsHtml,
  buildTicketDocUploader,
  ticketDocUploadedLine,
  // TASK-9958 — how a file got here + its FILE_ADDED audit row.
  stampFileProvenance,
  fileAddedAuditPayload,
  fileUploadCustomMetadata
} from '../shared/ticket-doc-meta.js?v=9958';
// TASK-10040b — the missed clock-out alert hours the SERVER cron actually uses
// (generated mirror of functions/src/shared/clockOutAlert.js). Pure module, no
// state, so a second instance is harmless. Replaces this screen's own `|| 12`.
import { resolveClockOutAlertHours } from '../shared/clock-out-alert.js?v=10040001';
// TASK-9694 — a driver's job-screen Doc (kind 'doc') rides in job.permits[] but is
// not a permit (generated mirror of functions/src/shared/jobPermitEntry.js; pure).
import { isJobDocEntry, jobPermitEntries } from '../shared/job-permit-entry.js?v=10282127';
// TASK-9646 S3b — ticket header money resolves against the server-written
// ticketMoney mirror at the load layer (_withTicketMoney). The generated mirror of
// functions/src/shared/ticketMoneyView.js is a LAZY chunk (_loadTicketMoneyView,
// perf budget): see the S3b load-layer block below.
import { jobPoList, jobPoLabel, jobHasPo } from '../shared/job-po-read.js?v=9811003'; // TASK-9811 S2 main + extra POs (same ?v= as dispatch.js)
// TASK-1354(b) — transactional ticket-number allocation. Imported straight from
// the pinned gstatic URL rather than added to firebase-config.js: 35 modules
// import firebase-config at ?v=1867, so adding an export there would force a
// 35-file buster sweep (incl. app.js) for one symbol. Same URL string as
// firebase-config.js uses ⇒ same module instance, so this is NOT the ?v=
// cross-import trap of Pitfall #1. Existing precedent: ap.js, dispatch.js,
// maintenance.js and pretrip.js all pull runTransaction in exactly this way.
import { runTransaction, waitForPendingWrites } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js'; // TASK-9735 f/u: waitForPendingWrites, same pinned URL/instance · TASK-9988 S2: getDocsFromServer dropped (the archive's open-clock re-check moved into the server op)

// ============================================
// BILLING STATE
// ============================================
const BillingState = {
  tickets: [],
  dispatchReview: null, // TASK-10041 — billingReview list result (null = not loaded / failed)
  reviewFilter: false,  // TASK-10041 — "Needs decision" chip on
  noClockFilter: false, // TASK-10186 — "No clock entry" chip on (its own rows; archived tickets count)
  _reviewHidden: null,  // TASK-10041 — Set of row ids decided this session (hidden before the next list)
  dispatchReviewRange: null, // TASK-10041 — the chip's range view ({fromKey,toKey,rows,...}); never feeds the badge
  payChangesTenantId: null, // integ/2108.1 — which company payChanges belongs to
  payChanges: null, // TASK-9983 — invoicedPayChanges rows (null = not loaded / failed)
  customers: [],
  people: [],
  selectedTicket: null,
  selectedTicketLines: [],
  filter: 'all', // 'all' (rendered as "Active"), 'daily', 'draft', 'ready', 'invoiced', 'paid', 'written_off', 'archived'
  _archivedHiddenCount: 0,
  dailyDate: new Date(), // date for 'daily' filter
  // TASK-1345 — union archived/locked back into the Active view. This flag and
  // its toggle already existed but NOTHING rendered a control and the filter
  // switch ignored it, so archived tickets were unconditionally hidden. Now
  // wired. Default OFF (COD Independent: ~520 archived docs is a perf risk);
  // the hidden COUNT renders either way.
  showArchived: false,
  unsubscribers: [],
  tenantId: null,
  // Calendar state
  viewMode: 'calendar',        // 'calendar' | 'tickets' | 'projects'  (TASK-1618 added 'projects'; this comment had gone stale)
  currentDate: new Date(),
  jobs: [],
  _jobsLoaded: false,          // TASK-1364 — has the jobs snapshot landed yet?
  jobDays: [],
  _jobDaysLoaded: false,       // TASK-10043 — the job-day picker waits for it
  timeOffRequests: [],
  showTimeOff: true,
  customerFilter: 'all',       // 'all' | customerId (calendar view)
  ticketCustomerFilter: new Set(), // multi-select customer IDs for ticket list (empty = all)
  selectedJob: null,
  ticketJobFilter: null,        // jobId when drilling into tickets
  ticketJobFilterName: null,    // Display name for breadcrumb
  // TASK-1618 trap 1 — a PROJECT spans many jobs, so the scalar ticketJobFilter
  // above cannot express the drill-down. Rather than widen that scalar in the ten
  // places it is read (:2433, :5372, :6103, :6493, :7289, :7297, :22014 …) — a
  // half-widening is how the CSV ends up disagreeing with the screen, the exact
  // bug class TASK-857 existed to kill — this is a PARALLEL filter, and BOTH the
  // list (renderTicketsList) and the export (exportFilteredTickets) are taught
  // about it in the same commit. `ticketProjectJobIds` is a Set of the project's
  // job ids, resolved from BillingState.jobs at drill-down time.
  // TASK-9414 — the Tickets view's Project filter drives the SAME state. The
  // captured `ticketProjectJobIds` Set is gone: a job linked from Dispatch while
  // the filter is open would never have joined it (COD Code Reviewer). The job →
  // project mapping is derived from BillingState.jobs on every render instead
  // (_billingJobProjectIndex), and 'none' is a real mode, not an empty Set.
  ticketProjectMode: null,          // null | 'project' | 'none'
  ticketProjectFilter: null,        // projectId when mode === 'project'
  ticketProjectFilterName: null,    // Display name for breadcrumb
  // TASK-1618 — projects are read on demand for the AR/AP Projects view. Reads
  // need no permission work (firestore.rules:4763 already allows canReadTenant),
  // and this is a one-shot getDocs rather than an eighth live listener: the AR/AP
  // mount already opens seven (TASK-1245) and this view is not on the paint path.
  projects: [],
  _projectsLoaded: false,
  _projectsLoading: false,
  _groupBusy: false,                // TASK-1618 trap 4 — double-click debounce
  // Projects view — grouped by customer (Lacey's ask). Filters are view-only.
  // TASK-9700 — default 'active', same as Dispatch -> Projects. Chip keys/words/
  // order come from window.ProjectStatusFilter; 'owed' is the AR/AP-only extra.
  projectsFilter: 'active',         // 'active' | 'completed' | 'archived' | 'all' | 'owed'
  projectsCustomer: '',             // customer group key, '' = all
  projectsSearch: '',
  projectsCollapsed: new Set(),     // collapsed customer group keys
  // Hierarchy & selection state
  selectedTicketIds: new Set(),  // checked ticket IDs
  expandedCustomers: {},         // { customerId: bool }
  expandedJobs: {},              // { "custId_jobId": bool }
  expandedProjectFolders: {},    // TASK-9610 — { "custId_proj_projectId": bool }
  companyLogoUrl: null,          // cached from tenantData
  itemCodes: [],                 // loaded for onboarding wizard + rate lookup
  ticketSearch: '',               // search query for ticket list
  // biCleanup removed — BI is now a standalone module
  activeTicketIds: new Set(),      // ticket IDs with active clock-in (clockOut == null)
  activeClockEntries: [],          // full active clock entry data for live hour calc
  activeClockState: null,          // TASK-9988 S1 — 'live' once the listener answered, 'error' if it failed
  // TASK-4649 — missing-permit flag (non-blocking). `permits` is a ONE-SHOT
  // getDocs at mount (28 docs on TITS), NOT a listener and NEVER a per-card
  // query — the flag predicate runs against this cache so the list paint issues
  // one permit read total (COD Binding 3, N+1).
  permits: [],
  _permitsLoaded: false,
  missingPermitFilter: false,      // header chip: show only flagged tickets
  // TASK-4662 — customer-delivery filter. DELIBERATELY NOT part of
  // BillingState.filter: that is the STATUS enum, and delivery is an orthogonal
  // axis. Keeping them separate is what makes "ready AND unsent" askable.
  // null = no delivery filter, which is the default so an owner-operator who
  // never emails a ticket sees nothing they have to dismiss.
  deliveryFilter: null,            // null | 'unsent' | 'awaiting' | 'edited'
  qboAgingFilter: null,            // TASK-9654: null | 'current' | '1-30' | '31-60' | '61-90' | '90+'
  // TASK-9588 — Unsent leaves out never-sent tickets of customers marked
  // `ticketEmailsOff` (paper customers). The chip row says how many it left
  // out and this flag puts them back, so nothing disappears silently.
  unsentIncludeNoEmail: false,
  // TASK-4740 (C) — per-chip tallies measured by renderTicketsList off the SAME
  // population it is about to render. null = not measured yet (loading), and the
  // chips then print no numbers at all rather than a stale or invented one.
  _deliveryCounts: null
};

// ============================================
// FALLBACK RATES (all zero — real rates come from
// item codes, contracts, or tenant defaultRates)
// ============================================
const DEFAULT_RATES = {
  cdl: 0,
  nonCdl: 0,
  truck: 0,
  trailer: 0,
  asset: 0
};

// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

// ---------------------------------------------------------------------------
// TASK-4741 — the unresolvable-equipment-ref audit.
//
// MEASURED READ-ONLY ACROSS ALL 93 LIVE TENANTS, 2026-09-10 (zero writes):
//
//   ticket EQUIPMENT lines (assetId)   1459 refs   by-id 1459   by-name 0   BROKEN 0
//   jobDay equipment refs              5910 refs   by-id 5719   by-name 160 BROKEN 31
//
// Two numbers decide this whole design.
//
// (1) THE FALSE-POSITIVE NUMBER. An audit that matched on id ALONE would flag
//     191 jobDay refs, and 160 of them (83.8%) are legacy GAS-imported
//     NAME-STRING refs that resolve perfectly well through _resolveFleetRef's
//     second pass — TITS's bird-named trucks: Pelican (35), Swallow (23), Bob
//     (21), Duck (19), Hooty (18), Seagull (4), plus named trailers like
//     "SDMP Side Dump (002387)" (18). An audit whose first run cries wolf 84%
//     of the time gets switched off, so this MUST route every ref through
//     _resolveFleetRef and never through a bare id compare.
//
// (2) WHERE THE REAL POPULATION LIVES. All 31 genuinely-broken refs are on
//     jobDays — 28× "BJ" on trippin-trucking (the TASK-4724 trigger case),
//     1× trailer "3", 2× a dead vehicle id on tenant mo68zs3wg9i24o2y0 — and
//     EVERY ONE of those 31 days produced NO TICKET AT ALL (probed
//     tickets-where-jobId-and-date, 0 of 31 had one). The pre-existing
//     TASK-1674 S4 check only stamps a line at ticket-generation time, so on
//     the real live population its coverage is exactly ZERO by construction.
//     That is also the expensive half of the bug: the equipment did not appear
//     as an over-priced line, it silently DROPPED OFF the invoice.
//
// 🔴 THE CONFIDENTLY-WRONG-AUDIT GUARD. loadReviewEquipment (:18043) reads only
// `where('active','==',true)` and SWALLOWS a read failure into empty arrays, so
// a check that naively re-derived against it would report every line on the
// ticket as broken the moment a roster read hiccuped. Measured: 12 live refs
// (5 ticket + 7 jobDay) resolve only against the FULL roster, because 6 roster
// docs carry no `active` field at all. So this function takes an explicit
// roster plus an explicit `rosterLoaded` flag and returns THREE states, never
// two: an unreadable or empty roster yields status 'could_not_check', which is
// NOT 'clean'. It never names a unit it could not resolve, and it never repairs
// anything — TASK-4724's repair needed a human to say "BJ means Blue Jay".
//
// Pure: no Firestore, no DOM, no globals. Exported on window.BillingModule so
// tests/task4741-equipment-ref-audit.unit.cjs can execute it on fixtures.
//
// refs: [{ source:'ticket'|'jobDay', sourceId, label, date, jobId, assetType,
//          ref, amount }]
// roster: { trucks:[], trailers:[], assets:[] }
function auditEquipmentRefs(refs, roster, rosterLoaded) {
  const list = Array.isArray(refs) ? refs : [];
  const trucks = (roster && Array.isArray(roster.trucks)) ? roster.trucks : [];
  const trailers = (roster && Array.isArray(roster.trailers)) ? roster.trailers : [];
  const assets = (roster && Array.isArray(roster.assets)) ? roster.assets : [];
  const all = trucks.concat(trailers, assets);

  // "I could not check this" is a distinct answer from "this is fine". An empty
  // probe result means DID NOT RUN. Refusing here is the whole point.
  if (rosterLoaded !== true || all.length === 0) {
    return {
      status: 'could_not_check',
      reason: rosterLoaded !== true ? 'roster_read_failed' : 'roster_empty',
      checked: 0, blank: 0, unresolved: [], resolvedByName: []
    };
  }

  const unresolved = [];
  const resolvedByName = [];
  let checked = 0;
  let blank = 0;

  for (const r of list) {
    if (!r) continue;
    const ref = r.ref;
    if (ref === null || ref === undefined || ref === '') { blank++; continue; }
    checked++;
    const pool = r.assetType === 'truck' ? trucks
      : r.assetType === 'trailer' ? trailers
        : r.assetType === 'asset' ? assets
          : all;
    // Typed pool first (that is what the render path does), then the whole
    // roster — a ref filed under the wrong type is a real vehicle, not a
    // missing one, and calling it missing would be the same cry-wolf error.
    let hit = _resolveFleetRef(ref, pool);
    if (!hit && pool !== all) hit = _resolveFleetRef(ref, all);
    if (!hit) {
      unresolved.push({
        source: r.source || '', sourceId: r.sourceId || '', label: r.label || '',
        date: r.date || '', jobId: r.jobId || '', assetType: r.assetType || '',
        ref: String(ref), amount: Number(r.amount) || 0
      });
      continue;
    }
    // Resolved — but record HOW, so the "we are not crying wolf" claim stays
    // measurable rather than asserted. A byId miss followed by a name hit is
    // the legacy GAS shape and is perfectly healthy.
    const byId = all.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === String(ref));
    if (!byId) resolvedByName.push({ ref: String(ref), resolvedTo: hit.id || '' });
  }

  return { status: 'ok', reason: '', checked, blank, unresolved, resolvedByName };
}

// The refs a ticket's EQUIPMENT lines point at, in auditEquipmentRefs shape.
function _equipmentRefsFromLines(lines, ticketLabel) {
  return (Array.isArray(lines) ? lines : [])
    .filter(l => l && l.type === 'EQUIPMENT')
    .map(l => ({
      source: 'ticket', sourceId: l.id || '', label: ticketLabel || '',
      date: '', jobId: '', assetType: l.assetType || '',
      ref: l.assetId, amount: Number(l.lineAmount) || 0
    }));
}

// The refs a jobDay points at. This is the surface that actually holds the live
// population (31 of 31) — a day whose truck ref never resolved never became a
// ticket line, so nothing downstream could ever have seen it.
function _equipmentRefsFromJobDays(jobDays) {
  const out = [];
  for (const jd of (Array.isArray(jobDays) ? jobDays : [])) {
    if (!jd) continue;
    const day = jd.date && jd.date.toDate ? jd.date.toDate() : jd.date;
    const dateLabel = day instanceof Date && !isNaN(day) ? day.toISOString().slice(0, 10) : String(jd.date || '');
    const buckets = [['truck', jd.trucks], ['trailer', jd.trailers], ['asset', jd.assets]];
    for (const b of buckets) {
      const kind = b[0];
      const arr = Array.isArray(b[1]) ? b[1] : (b[1] ? [b[1]] : []);
      for (const ref of arr) {
        out.push({
          source: 'jobDay', sourceId: jd.id || '', label: dateLabel,
          date: dateLabel, jobId: jd.jobId || '', assetType: kind, ref, amount: 0
        });
      }
    }
  }
  return out;
}

// Read the FULL roster — deliberately NOT loadReviewEquipment's active-only
// read. A retired or never-flagged vehicle is a real vehicle; calling its ref
// unresolvable would be a false alarm (12 live refs, measured). Returns
// { roster, loaded } and NEVER throws — a failed read comes back loaded:false
// so the audit can say "could not check" instead of "all clean".
async function _loadFullFleetRoster() {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const [tr, tl, as] = await Promise.all([
      getDocs(collection(db, 'tenants', tenant, 'trucks')),
      getDocs(collection(db, 'tenants', tenant, 'trailers')),
      getDocs(collection(db, 'tenants', tenant, 'assets'))
    ]);
    return {
      loaded: true,
      roster: {
        trucks: tr.docs.map(d => ({ id: d.id, ...d.data() })),
        trailers: tl.docs.map(d => ({ id: d.id, ...d.data() })),
        assets: as.docs.map(d => ({ id: d.id, ...d.data() }))
      }
    };
  } catch (err) {
    console.warn('[Billing] TASK-4741 — full fleet roster read failed; the equipment-ref audit will report "could not check", not "clean":', err);
    return { loaded: false, roster: { trucks: [], trailers: [], assets: [] } };
  }
}

// Cached full-roster read for the Review Ticket banner (one read per modal
// open). Cache the ENVELOPE, not just the roster, so a failed read is not
// remembered as an empty-but-successful one.
//
// TASK-4741 (COD Code Reviewer 🔴) — the cache is keyed on the TENANT and
// cleared by resetReviewState. Without both, switching accounts would judge one
// tenant's ticket against another tenant's fleet and flag every line on it,
// and a vehicle added mid-session would keep reading as missing until reload.
// A failed read is never cached: `loaded !== true` falls through and retries.
async function _reviewFullRoster() {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const cached = ReviewState._refRoster;
  if (cached && cached.loaded === true && cached.tenant === tenant) return cached;
  const fresh = await _loadFullFleetRoster();
  fresh.tenant = tenant;
  ReviewState._refRoster = fresh;
  return fresh;
}

// ============================================
// TASK-9258 — TEMPLATE-PLACED, NEVER-CONFIRMED QUANTITIES
// ============================================
// A job template (dispatch.js) pre-fills a job's kit. The danger is not that it
// auto-bills — the line lands on a DRAFT ticket and invoicing is a separate,
// deliberate human step (Option A trust model). The danger is narrower: a
// template makes every line look NORMAL. Six air movers on a job that always
// carries six is exactly what a biller expects to see, so the template removed
// the thing there was to notice. The gap is INFORMATION, so this hands the
// biller the one fact they cannot otherwise get: "a template put this quantity
// here and nobody on dispatch ever checked it."
//
// The truth lives on the jobDay: `templateMeta.unconfirmedAssets` (asset ids a
// template seeded and no human touched), kept honest by dispatch.js
// reconcileStoredTemplateMeta whenever the day is edited afterwards. So the flag
// is DERIVED LIVE at render from a fresh jobDay read — never stamped onto the
// line at birth — for three reasons, each of which would have been a bug:
//   1. `_billingReviewFlag` is ONE value. A template stamp would clobber an
//      `equipment_rate_guessed` / `equipment_ref_unresolved` stamp written in
//      the same block, and the server enrichment (equipmentLineEnrichment.js)
//      SKIPS already-flagged lines, so it would also silence the server's
//      hours-exceed-clock and outside-clock-window flags on that line.
//   2. Asset lines are born on three paths (generateTicketFromJob, the clock-out
//      fills in clock.js, and the server rental-slice engine). Only the jobDay
//      covers all three; a stamp would cover whichever path remembered it.
//      firestore.rules also pins a CLIENT-authored flag to exactly one shape, so
//      the clock-out path could not carry it without a rules change.
//   3. A stamp goes stale: the dispatcher confirms the day after the ticket is
//      cut and the line keeps shouting (TASK-4741's lesson, same surface).
//
// Ordinary asset lines bill ONE day, so they are judged against the jobDay on
// the ticket's workDate.
//
// 🔴 RENTAL-SLICE lines (rentalSlice:true, written server-side) are judged by
// the LEDGER, not by any jobDay. The engine bills a rental period from the
// DEPLOY assetTransactions row (rentalSlices.deployTxId), and dispatch stamps
// that exact row with templateConfirmed:false + templateId when a template put
// the quantity there (jobTemplateAssetStamp). That row is what the money is made
// of, so it is the only honest source. Four COD rounds tried to reach it through
// a jobDay instead, and every route broke:
//   • any day of the job — a range save copies the same marks onto every day and
//     a later edit reconciles only the day edited → cried wolf (round 1);
//   • the deploy DATE from periodStartMs — it is UTC midnight on the create path
//     but a save instant on the assignment path, and neither maps reliably to
//     the noon-local jobDay → went silent (rounds 2–4);
//   • the jobDay's current quantity — a quantity-only Assign Crew edit writes NO
//     ledger row, so the day can say 4 while the engine still bills the
//     template's 6 → a jobDay-based check read clean on a line billing an
//     unconfirmed number (round 4, Code Reviewer + Edge Case Hunter).
// Reading the deploy row fixes all three: while it says templateConfirmed:false
// the billed quantity IS the template's unconfirmed number, whatever the jobDay
// now says.
// A slice whose deploy row FAILED to read is reported as "could not check" —
// never inferred from the jobDays (COD round 5, Code Reviewer ❌: the jobDay can
// be edited clean while the engine still bills the template's number, so a
// jobDay fallback there under-warns). Same for a deploy row that has been
// DELETED (round 6). Only a legacy slice that names no deployTxId at all —
// nothing to read — uses the any-day jobDay check.
//
// Which job: the ticket's jobId AND its originalJobId. Bulk Move re-points
// jobId and deliberately keeps originalJobId (moveSelectedTicketsToJob); judging
// only the new job would false-clean a templated ticket moved onto an ordinary
// job (COD round 1, Code Reviewer).
//
// Clears on its own when: the dispatcher touches the row on the jobDay, a biller
// presses "Quantity is right" (`templateQtyAcknowledged`, BILLER-ONLY — pinned
// on the member/driver arms in firestore.rules, same as rateGuessAcknowledged),
// or the line was added by hand in Review (`_isNew` — a human made it).
// NEVER blocks Ready or invoice: the money is still owed; only a human knows
// whether the count is right.

/**
 * PURE. Group the ticket's asset lines whose quantity came from a template that
 * nobody confirmed.
 *   lines    — ReviewState.equipmentLines (or any ticketLine-shaped rows)
 *   jobDays  — the job's jobDay docs, each carrying `_dayStr` (YYYY-MM-DD local)
 *   workDate — the ticket's work date, YYYY-MM-DD
 *   sliceProvenance — { [ticketLineId]: { templateConfirmed, templateId,
 *              templateName, quantity } } from each rental slice's DEPLOY ledger
 *              row (templateSliceProvenance). { unresolved:true } = the row
 *              failed to read → kind 'unchecked'. A slice absent from it named
 *              no deployTxId at all → the any-day jobDay check.
 *   birthJobId — the job the ticket was CUT from (originalJobId || jobId). The
 *              stale / removed arms compare the line against that job's day only:
 *              after a Bulk Move the destination job's day says nothing about
 *              the quantity this line was born with (COD round 8).
 * Returns [{ assetId, lines:[...], templateId, templateName, proposedQty }],
 * one entry per asset (several rental slices of one asset are one fact).
 */
function templateUnconfirmedEquipGroups(lines, jobDays, workDate, sliceProvenance, birthJobId) {
  const days = Array.isArray(jobDays) ? jobDays : [];
  const wd = String(workDate || '').slice(0, 10);
  const prov = (sliceProvenance && typeof sliceProvenance === 'object') ? sliceProvenance : {};
  const byAsset = new Map();
  for (const l of (Array.isArray(lines) ? lines : [])) {
    if (!l || !l.assetId || l._isNew) continue;
    if (l.type && l.type !== 'EQUIPMENT') continue;
    const isSlice = l.rentalSlice === true;
    // Trucks/trailers are never carried in unconfirmedAssets (the jobDay only
    // persists the ASSETS section's marks), so only asset-class lines qualify.
    // A rental slice line carries no assetType at all — it is always an asset.
    if (!isSlice && l.assetType !== 'asset') continue;
    if (l.templateQtyAcknowledged === true) continue;
    // Rental slice with a readable deploy ledger row: that row decides, alone.
    if (isSlice && l.id && prov[l.id]) {
      const p = prov[l.id];
      if (p.unresolved !== true && p.templateConfirmed !== false) continue;
      // COD round 6 (Code Reviewer ❌): an unreadable slice must NEVER ride in
      // the same group as a readable one. Grouping by asset alone let "Quantity
      // is right" on the readable slice stamp the unread one too, hiding it on
      // reopen — and made the group's kind depend on line order. Separate key.
      const gKey = p.unresolved === true ? `${l.assetId}|unchecked` : l.assetId;
      const g0 = byAsset.get(gKey);
      if (g0) { g0.lines.push(l); continue; }
      byAsset.set(gKey, {
        assetId: l.assetId, lines: [l], rental: true,
        kind: p.unresolved === true ? 'unchecked' : 'unconfirmed', dayQty: null,
        templateId: p.templateId || '', templateName: p.templateName || '',
        // The DEPLOYED count, not the line's QTY: a slice line's quantity is
        // units × count (functions generateRentalTicketSlices). The banner says
        // so in words rather than showing a number the biller might type in.
        proposedQty: Number(p.quantity) > 0 ? Number(p.quantity) : null,
      });
      continue;
    }
    // Ordinary line → its work day. Unresolved slice → any day (conservative).
    // COD round 9 (Independent): an ordinary line was born from ONE job's day.
    // When that birth job has a day on this date, it alone decides — a Bulk
    // Move destination's day must not pre-empt it (its unconfirmed hit used to
    // break the loop before the birth-job stale check ran, and its ack then hid
    // the birth-day change for good). Only when the birth job has no such day
    // do the other loaded days count (conservative).
    const sameDate = isSlice ? days : days.filter((d) => d && d._dayStr === wd);
    const birthDays = (!isSlice && birthJobId) ? sameDate.filter((d) => d && d.jobId === birthJobId) : [];
    const candidates = birthDays.length > 0 ? birthDays : sameDate;
    // snapshot.assetQtys is what the template PROPOSED. dispatch stores a qty
    // only when it is not 1, so an absent entry is 1 — but only claim a number
    // when the snapshot actually carries this asset.
    const proposedOf = (m) => {
      const snap = (m && m.snapshot) || {};
      if (!(Array.isArray(snap.assets) && snap.assets.includes(l.assetId))) return null;
      const raw = (snap.assetQtys || {})[l.assetId];
      return (Number(raw) > 0) ? Number(raw) : 1;
    };
    let meta = null;
    let kind = 'unconfirmed';
    let dayQty = null;
    for (const d of candidates) {
      const m = d && d.templateMeta;
      if (m && Array.isArray(m.unconfirmedAssets) && m.unconfirmedAssets.includes(l.assetId)) { meta = m; break; }
    }
    // 🔴 COD round 2 (Edge Case Hunter) — the STALE arm. A person confirmed the
    // day by CHANGING the quantity after this ticket was cut (Assign Crew
    // rewrites the jobDay only, never ticketLines), so the mark is gone — but the
    // line still bills the template's number, not the one the person chose.
    // Detectable only where the line carries the seeded quantity (the legacy
    // rental seed copies assetQtys; a consumable line is born at 0 and is
    // skipped). Rental slices are excluded: their quantity is units × count.
    if (!meta && !isSlice) {
      const staleDays = birthJobId ? candidates.filter((d) => d && d.jobId === birthJobId) : candidates;
      for (const d of staleDays) {
        const m = d && d.templateMeta;
        const proposed = proposedOf(m);
        if (proposed == null) continue;
        const lineQty = Number(l.quantity) || 0;
        if (!(lineQty > 0 && lineQty === proposed)) continue;
        // COD round 3 (Edge Case Hunter): dispatch UNCHECKED the asset after the
        // ticket was cut. assetQtys only stores qty > 1, so a missing key reads
        // as 1 — which made "removed" look like "still 1". Check the day's asset
        // list first.
        const onDay = Array.isArray(d.assets) && d.assets.includes(l.assetId);
        if (!onDay) { meta = m; kind = 'removed'; break; }
        const nowQty = Number((d.assetQtys || {})[l.assetId]) > 0 ? Number(d.assetQtys[l.assetId]) : 1;
        if (nowQty !== proposed) { meta = m; kind = 'stale'; dayQty = nowQty; break; }
      }
    }
    if (!meta) continue;
    // COD round 7 (Edge Case Hunter): a legacy slice judged by the jobDays must
    // not share a group with a ledger-resolved slice of the same asset, or the
    // ack on one would stamp the other. Ordinary lines keep the plain key.
    const gKey = isSlice ? `${l.assetId}|jobday` : l.assetId;
    const g = byAsset.get(gKey);
    if (g) { g.lines.push(l); continue; }
    const apps = Array.isArray(meta.applications) ? meta.applications : [];
    const tplId = (meta.assetSource && meta.assetSource[l.assetId]) || '';
    const app = apps.find((a) => a && a.id === tplId)
      || (kind !== 'unconfirmed' && apps.length === 1 ? apps[0] : null);
    byAsset.set(gKey, {
      assetId: l.assetId,
      lines: [l],
      kind,
      dayQty,
      templateId: tplId || (app && app.id) || '',
      templateName: (app && app.name) || '',
      proposedQty: proposedOf(meta),
    });
  }
  return Array.from(byAsset.values());
}

/**
 * Does this ticket have any line the template check could apply to? Used to
 * skip the jobDay read entirely on the overwhelming majority of tickets (no
 * asset lines at all), the same zero-extra-reads contract as routeBadStops.
 */
function _ticketHasTemplateCandidateLines(lines) {
  return (Array.isArray(lines) ? lines : []).some((l) =>
    l && l.assetId && (l.type == null || l.type === 'EQUIPMENT')
    && (l.rentalSlice === true || l.assetType === 'asset'));
}

// PURE. { ticketLineId: { templateConfirmed, templateId, templateName, quantity } }
// for every rental-slice line whose rentalSlices row names a deployTxId we
// managed to read. A row whose READ FAILED is { unresolved:true } — reported as
// "could not check", never as clean. The loader puts a named row that no longer
// EXISTS in `failed` too. Only a line with no slice row / no deployTxId is
// ABSENT and gets the jobDay check.
// A ledger row with no template stamp at all (a hand-picked rental) resolves to
// templateConfirmed: undefined, which is not false → not flagged. Correct: no
// template placed it.
function templateSliceProvenance(lines, rentalSlices, txById, failedTxIds) {
  const out = {};
  const txs = (txById && typeof txById === 'object') ? txById : {};
  const failed = new Set(Array.isArray(failedTxIds) ? failedTxIds : []);
  const deployByLine = new Map();
  for (const rs of (Array.isArray(rentalSlices) ? rentalSlices : [])) {
    if (rs && rs.ticketLineId && rs.deployTxId) deployByLine.set(rs.ticketLineId, rs.deployTxId);
  }
  for (const l of (Array.isArray(lines) ? lines : [])) {
    if (!l || l.rentalSlice !== true || !l.id) continue;
    const txId = deployByLine.get(l.id);
    if (txId && failed.has(txId)) { out[l.id] = { unresolved: true }; continue; }
    const tx = txId ? txs[txId] : null;
    if (!tx) continue;
    out[l.id] = {
      templateConfirmed: tx.templateConfirmed,
      templateId: tx.templateId || '',
      templateName: tx.templateName || '',
      quantity: tx.quantity,
    };
  }
  return out;
}
function _reviewSliceProvenance(lines) {
  const env = ReviewState._tplDeployTx || {};
  // A rental ticket whose slice rows themselves failed to load has no deploy ids
  // to resolve — every slice is unresolved, not absent.
  if (ReviewState.ticket?.rentalTicket === true && ReviewState._rentalSlicesFailed === true) {
    const out = {};
    for (const l of (Array.isArray(lines) ? lines : [])) if (l && l.rentalSlice === true && l.id) out[l.id] = { unresolved: true };
    return out;
  }
  return templateSliceProvenance(lines, ReviewState.rentalSlices, env.rows, env.failed);
}

// The deploy ledger rows behind this rental ticket's slices, keyed by tx id.
// Best-effort per row: a row that fails to read goes in `failed`, and its
// slice renders "could not check" (never clean, never a jobDay guess).
async function _loadTemplateDeployTx(rentalSlices) {
  const ids = Array.from(new Set((Array.isArray(rentalSlices) ? rentalSlices : [])
    .map((rs) => rs && rs.deployTxId).filter(Boolean)));
  const rows = {};
  const failed = [];
  if (ids.length === 0) return { rows, failed };
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  await Promise.all(ids.map(async (id) => {
    try {
      const snap = await getDoc(doc(db, 'tenants', tenant, 'assetTransactions', id));
      // COD round 6 (Independent + Edge Case ❌): a slice that NAMES a deploy row
      // which no longer exists (roster can delete assetTransactions) is not a
      // legacy slice with nothing to read — the engine still bills off it. Treat
      // it exactly like a failed read: "could not check", never a jobDay guess.
      if (snap.exists()) rows[id] = snap.data(); else failed.push(id);
    } catch (err) {
      failed.push(id);
      console.warn('[Billing] TASK-9258 — deploy ledger row read failed; that slice will say "could not check":', id, err);
    }
  }));
  return { rows, failed };
}

function _reviewTemplateBirthJobId() {
  const t = ReviewState.ticket || {};
  return t.originalJobId || t.jobId || '';
}

function _reviewTemplateWorkDate() {
  const t = ReviewState.ticket || {};
  const raw = t.workDate || t.date || '';
  if (typeof raw === 'string') return raw.slice(0, 10);
  try { return raw?.toDate ? formatDateISO(raw.toDate()) : ''; } catch (_) { return ''; }
}

// Fresh read of the job's jobDays for the template check — once per modal open,
// deliberately NOT BillingState.jobDays (a deferred listener: a banner keyed on
// it could render "clean" purely because the snapshot had not landed yet).
// Returns an ENVELOPE so a failed read is never mistaken for "no templates":
//   { loaded:true, days:[…with _dayStr] } | { loaded:false, days:[] }
async function _loadTemplateCheckJobDays(jobIds) {
  const ids = Array.from(new Set((Array.isArray(jobIds) ? jobIds : [jobIds]).filter(Boolean)));
  if (ids.length === 0) return { loaded: true, days: [] };
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const snaps = await Promise.all(ids.map((id) =>
      getDocs(query(collection(db, 'tenants', tenant, 'jobDays'), where('jobId', '==', id)))));
    const days = snaps.flatMap((snap) => snap.docs).map((d) => {
      const x = { id: d.id, ...d.data() };
      let ds = '';
      try {
        ds = typeof x.date === 'string' ? x.date.slice(0, 10)
          : x.date?.toDate ? formatDateISO(x.date.toDate())
          : x.date ? formatDateISO(new Date(x.date)) : '';
      } catch (_) { ds = ''; }
      x._dayStr = ds;
      return x;
    });
    return { loaded: true, days };
  } catch (err) {
    console.warn('[Billing] TASK-9258 — jobDay read for the template check failed; the banner will say "could not check", not "clean":', err);
    return { loaded: false, days: [] };
  }
}

// TASK-9258 — "Quantity is right": the biller confirms a template-placed
// quantity without changing it. Stamped on EVERY line of that asset (the fact is
// per asset, and several rental slices of one asset are one decision), with who
// and when, so a later reader can tell a checked number from an unexamined one.
// Persists on SAVE like every other review edit — this only mutates ReviewState.
function acknowledgeTemplateQty(localId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const anchor = (ReviewState.equipmentLines || []).find((l) => l._localId === localId);
  if (!anchor || !anchor.assetId) return;
  // Only the lines the banner actually flagged for this asset — never a
  // same-asset line the check did not raise (that would pre-silence a future
  // flag on a line nobody looked at).
  const group = templateUnconfirmedEquipGroups(ReviewState.equipmentLines,
    ReviewState._tplDays?.days, _reviewTemplateWorkDate(),
    _reviewSliceProvenance(ReviewState.equipmentLines), _reviewTemplateBirthJobId()).find((g) => g.lines.includes(anchor));
  if (!group || group.kind === 'unchecked') return;
  const who = window.AppState?.user?.email || 'unknown';
  const now = Timestamp.now();
  for (const l of group.lines) {
    l.templateQtyAcknowledged = true;
    l.templateQtyAcknowledgedBy = who;
    l.templateQtyAcknowledgedAt = now;
  }
  ReviewState.dirty = true;
  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  renderReviewTotals();
}

function getDefaultRates() {
  const tenantRates = window.AppState?.settings?.defaultRates;
  if (tenantRates && typeof tenantRates === 'object') {
    return {
      cdl: tenantRates.cdl ?? DEFAULT_RATES.cdl,
      nonCdl: tenantRates.nonCdl ?? DEFAULT_RATES.nonCdl,
      truck: tenantRates.truck ?? DEFAULT_RATES.truck,
      trailer: tenantRates.trailer ?? DEFAULT_RATES.trailer,
      asset: tenantRates.asset ?? DEFAULT_RATES.asset
    };
  }
  return DEFAULT_RATES;
}

function isFounder() {
  return window.AppState?.tenant === 'trippin-trucking';
}

// TASK-865 — generalized from the trippin-trucking hardcode ("build for
// everybody"). When settings.showEmployeeNamesOnTickets is true, tickets and
// invoices show employee NAMES (plus a separate Role column) instead of role
// labels, and the per-view "Show names" checkbox is hidden (names are always
// on). Flag absent/false = role labels — the pre-existing non-TITS behavior.
// TITS is backfilled true via scripts/backfill-task865-tits-flags.cjs.
function namesOnTickets() {
  return window.AppState?.settings?.showEmployeeNamesOnTickets === true;
}

function isPerAssetNotesEnabled() {
  return window.AppState?.settings?.perAssetNotes === true;
}

function assetTypeGetsNotes(assetType) {
  if (!isPerAssetNotesEnabled()) return false;
  const types = window.AppState?.settings?.perAssetNotesTypes || {};
  if (assetType === 'truck') return types.truck !== false;
  if (assetType === 'trailer') return types.trailer !== false;
  if (assetType === 'asset') return types.asset !== false;
  return false;
}

// Calculate live running hours for a ticket with active clock-ins
function getTicketLiveHours(ticketId, storedHours) {
  const stored = storedHours || 0;
  if (!BillingState.activeTicketIds?.has(ticketId)) return stored;
  // Sum live hours from active clock entries for this ticket
  const activeForTicket = (BillingState.activeClockEntries || []).filter(e => e.ticketId === ticketId);
  if (activeForTicket.length === 0) return stored;
  let liveHours = stored; // start with already-clocked-out employee hours
  // TASK-730b: defensive cap. A forgotten/stale open clock-in (real root fix = TASK-441,
  // forgotten clock-outs at the source) would otherwise add (now - clockIn) with no upper
  // bound and render absurd "million hour" totals on the ticket card/list. No legitimate
  // single open shift exceeds ~36h (oilfield shifts run long), so EXCLUDE from the live-hours
  // sum any ACTIVE entry whose clockIn is older than that — it's a forgotten clock-out, not
  // real live time. Display-only: stored data is never mutated; persisted totalHours self-heals.
  const MAX_LIVE_SHIFT_HOURS = 36;
  for (const entry of activeForTicket) {
    const clockIn = entry.clockIn?.toDate?.() || (entry.clockIn ? new Date(entry.clockIn) : null);
    if (clockIn) {
      const elapsedHours = (Date.now() - clockIn.getTime()) / 3600000;
      if (elapsedHours > MAX_LIVE_SHIFT_HOURS) continue; // stale forgotten clock-in — don't inflate
      liveHours += Math.max(0, elapsedHours);
    }
  }
  return liveHours;
}

// Authoritative "is this employee line still clocked in?" check.
// "Active" must come from the real clock entry (clockOut == null, i.e. status:'active' in
// activeClockEntries), NOT from a missing toTime on the line. Piece-rate / Daily ($/day)
// employee lines legitimately have no toTime, and a build-ticket write can drop times on a
// closed line — neither means the person is currently clocked in. Match the line's
// clockEntryId against the live active set first; fall back to personId within this ticket's
// active entries for legacy lines that never stored a clockEntryId.
function isLineClockActive(line, ticketId) {
  if (!ticketId || !BillingState.activeTicketIds?.has(ticketId)) return false;
  const activeForTicket = (BillingState.activeClockEntries || []).filter(e => e.ticketId === ticketId);
  if (activeForTicket.length === 0) return false;
  if (line.clockEntryId) {
    return activeForTicket.some(e => e.id === line.clockEntryId);
  }
  // Legacy line without clockEntryId: fall back to personId — but ONLY when the line itself
  // has no stored toTime (i.e. it could plausibly be the open shift). A legacy CLOSED line
  // (toTime present) for a person who also has a separate open entry on this ticket must NOT
  // be force-badged Active by that sibling entry.
  if (line.personId && !line.toTime) {
    return activeForTicket.some(e => e.personId === line.personId);
  }
  // clockEntryId absent + (no personId, or line already has a toTime) — cannot prove open.
  return false;
}

// Flag off: show role label instead of person name on tickets (TASK-865 —
// settings.showEmployeeNamesOnTickets flips names on permanently)
let _showEmployeeNames = false; // toggle state for "Show Names" checkbox
function getEmployeeDisplayName(line) {
  if (namesOnTickets() || _showEmployeeNames) return line.personName || '-';
  if (line.role && CREW_TYPE_ITEM_CODE_MAP[line.role]) {
    return window.getTypeLabel?.(line.role, 'crewTypes') || line.role;
  }
  if (line.role === 'CDL') return 'CDL Driver';
  if (line.role === 'NON_CDL') return 'Non-CDL';
  if (line.role) return line.role;
  return 'Employee';
}

/** TASK-9275 — a quantity-tracked asset whose on-hand count is BELOW ZERO.
 *  Same rule as roster.js `assetStockIsNegative` and dispatch.js's copy;
 *  deliberately duplicated rather than cross-imported (Pitfall #1 — a `?v=`
 *  cross-import is a second module instance) and deliberately NOT gated on
 *  `consumable`, so the flag describes the DATA and not the writer. */
function reviewAssetStockIsNegative(asset) {
  return !!asset && asset.trackingMode === 'quantity' && Number(asset.quantity || 0) < 0;
}

/** TASK-9275 — the asset lines on a ticket whose roster stock is currently below
 *  zero, deduped by assetId. DERIVED LIVE off `ReviewState.assets` on every
 *  render, never from a flag stamped on the line at ticket-generation time:
 *  TASK-4741 is the standing lesson here — a stamp written once goes stale both
 *  ways, staying silent on an old line and shouting forever on a fixed one. The
 *  deduct in `saveReviewTicket` mutates the same cached asset objects, so a line
 *  that just pushed the count under zero shows up on the very next render. */
function reviewNegativeStockLines(lines) {
  const assets = ReviewState.assets || [];
  const seen = new Set();
  const out = [];
  for (const l of (lines || [])) {
    if (!l || l.assetType !== 'asset' || !l.assetId || seen.has(l.assetId)) continue;
    const asset = assets.find(a => a.id === l.assetId);
    if (!reviewAssetStockIsNegative(asset)) continue;
    seen.add(l.assetId);
    out.push({ line: l, asset });
  }
  return out;
}

// Resolve equipment display name: try current roster data first, fall back to stored assetName
function getEquipmentDisplayName(line) {
  if (!line) return '-';
  if (line.assetId && window.getUnitDisplay) {
    const trucks = ReviewState.trucks || [];
    const trailers = ReviewState.trailers || [];
    const assets = ReviewState.assets || [];
    const match = trucks.find(t => t.id === line.assetId)
      || trailers.find(t => t.id === line.assetId)
      || assets.find(a => a.id === line.assetId);
    if (match) return window.getUnitDisplay(match);
  }
  return line.assetName || '-';
}

function showClockTimes() {
  // Reads tenant-level setting, auto-synced by template builder save
  return window.AppState?.settings?.showClockTimesOnTickets !== false;
}

// Round hours UP to the next half hour (mirrors clock.js roundHalfHour) — TASK-1410
function roundHalfHour(hours) {
  // TASK-1410 — ALWAYS ROUND UP to the next half hour. Standard oilfield
  // practice (Chris, 2026-08-07). This function previously rounded to the
  // NEAREST half hour, so `frac < 0.25` silently rounded DOWN: 8.2h billed 8.0
  // when it should bill 8.5, and 8.6h billed 8.5 when it should bill 9.0 — on a
  // $285/hr truck that is $142.50 given away per line, on every partial hour,
  // for as long as the feature has shipped. The name and comment said
  // "nearest", so the wrong rule was implemented faithfully rather than typo'd.
  // 549 of TITS's 808 time-based contract line items have roundToHalfHour on.
  // The 1e-9 guard keeps float noise from bumping an EXACT half (8.5 stays 8.5).
  // NOTE: this INCREASES invoice amounts. Past invoices are not retroactive.
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  return Math.ceil((hours * 2) - 1e-9) / 2;
}

// TASK-4564 — the rounding DECISION, PURE and exported.
//
// THE BUG THIS CLOSES. A rounded EQUIPMENT line was getting SILENTLY UN-ROUNDED
// the next time a biller touched its times. `shouldRoundLine` only ever asked
// two questions — "is the flag on the LINE?" and "does `lineItemId` resolve to a
// price-book item with rounding on?" — but the clock.js close-out writers gate on
// `eq.roundToHalfHour` being TRUE and then persist ONLY `roundedFrom`, never the
// flag itself (clock.js:12973 / :13984 / :14490). The SERVER writer does persist
// it (functions/src/services/equipmentLineEnrichment.js:482), and the server only
// runs when the price book actually matches the asset — so a client-written line
// that the enrichment skipped reads back as
//     { roundedFrom: 7.13, hours: 7.5, roundToHalfHour: undefined }
// → both questions answer NO → `_recalcLineFromTimes` rewrites 7.5 back down to
// 7.13 and the amount with it. No warning, no diff, no audit row.
//
// MEASURED on the live collection group (1,938 EQUIPMENT ticketLines): 219 carry
// `roundedFrom`, 205 also carry the flag, ZERO rely on the `lineItemId` fallback,
// and 14 carry NEITHER — $289.30 of uplift a single recalc would drop.
//
// THE THIRD QUESTION. A line that carries a corroborated `roundedFrom` WAS
// rounded, and stays rounded. That is the domain truth the Architect lane signed
// off on: "ticket lines represent immutable event SNAPSHOTS rather than dynamic
// contract lookups." Deliberately narrow, because this is money:
//   • EQUIPMENT only — the measured population, and the only type any writer
//     stamps `roundedFrom` on (clock.js `eq.*` loops, ticketGateway.js `eqUpdate`,
//     equipmentLineEnrichment.js). Nothing else changes behaviour.
//   • the flag must be ABSENT, not `false`. equipmentLineEnrichment writes
//     `roundToHalfHour: false` when the price book says rounding is off but does
//     NOT delete a pre-existing `roundedFrom` — so a stale `roundedFrom` under an
//     explicit `false` must lose. An explicit false is a real answer; honour it.
//   • `hours` must actually BE the rounded form of `roundedFrom` (the COD-tightened
//     backfill predicate). That corroboration is what stops a stale or forged
//     `roundedFrom` from inventing rounding on a line that was never rounded.
//
// PURE: every input is an argument, so tests assert it without Firestore
// (tests/task4564-line-rounding.unit.cjs). `shouldRoundLine` below is the thin
// ReviewState-bound wrapper the render/recalc paths keep calling.
//
// 🔴 The persisted flag is a TRUSTED value, so firestore.rules pins
// `roundToHalfHour`/`roundedFrom` driver-immutable on the ticketLines create AND
// update driver arms (COD Adversary, blocking). Read that block before widening
// anything here — a client-only version of this fix was explicitly rejected.
// TASK-9489 — `companyDefault` (optional, default off): the company "round
// equipment to the half hour" switch. It only answers for an EQUIPMENT line
// whose own flag is ABSENT and whose price-book line is set to Company default
// (unset). An explicit line flag, or a price-book Always / Never, still decides.
// TASK-4673 — SERVER PROVENANCE FIRST. `roundToHalfHour` / `roundedFrom` stay
// writable on the bare-member arm (TASK-4581: clock.js close-out writes them at
// eight member-arm sites; pinning them traps drivers), so a clock-only member
// could raw-SDK stamp rounding on any open EQUIPMENT line and the 'line' and
// 'evidence' answers below would trust it. `roundingProvenance` is written ONLY
// by the server rate passes (ticketLineRateAuthority / equipmentLineEnrichment)
// and firestore.rules keeps it out of every member / workforce / driver write.
// While it is well-formed and still bound to the line's lineItemId (a biller
// re-pick retires it) it REPLACES the two client-writable answers; the price
// book and company-default answers are unchanged (a member cannot write those).
// No stamp = every pre-4673 line = exactly the old decision. Server twin:
// functions/src/services/roundingProvenance.js provenanceOf.
function decideLineRounding(line, contractLineItems, companyDefault) {
  const L = line || {};
  const P = L.roundingProvenance;
  const pv = (P && typeof P === 'object' && typeof P.round === 'boolean'
      && (P.lineItemId || null) === (L.lineItemId || null)) ? P : null;
  if (pv && pv.round === true) return { round: true, source: 'server' };
  if (!pv && L.roundToHalfHour === true) return { round: true, source: 'line' };
  if (L.lineItemId) {
    const li = (contractLineItems || []).find(l => l && l.id === L.lineItemId);
    if (li && li.roundToHalfHour === true) return { round: true, source: 'priceBook' };
    // TASK-4673 — not over a server stamp: the server already resolved the
    // company default into pv.round, and `roundToHalfHour == null` is member-
    // reachable (delete the server's explicit false) — the forgery this closes.
    if (!pv && li && companyDefault === true && L.type === 'EQUIPMENT' && L.roundToHalfHour == null
        && li.roundToHalfHour == null) { // unset = Company default (kept pure: tests run this in a sandbox)
      return { round: true, source: 'companyDefault' };
    }
  }
  // Persisted evidence — the snapshot says this line was rounded. Never over a
  // server stamp (TASK-4673): the stamp already answered for this line.
  if (!pv && L.type === 'EQUIPMENT' && L.roundToHalfHour == null) {
    const rf = Number(L.roundedFrom);
    const h = Number(L.hours);
    if (Number.isFinite(rf) && rf > 0 && Number.isFinite(h)
        && Math.abs(h - roundHalfHour(rf)) < 0.005) {
      return { round: true, source: 'evidence' };
    }
  }
  return { round: false, source: 'none' };
}

// Check if a line should round to half hour — check the line itself first, then
// the persisted-rounding evidence, then the contract line item.
function shouldRoundLine(line) {
  // TASK-9489 — the company switch never reaches a locked / invoiced ticket.
  const _co = !isReviewReadOnly(ReviewState.ticket || {}) && _billingCompanyRoundsEquipment();
  return decideLineRounding(line, ReviewState.contractLineItems, _co).round;
}

// TASK-9489 — the company "round equipment to the half hour" switch and the ONE
// decision (window.EquipmentRounding, generated from
// functions/src/shared/equipmentRounding.js — the server enrichment runs the
// same file). Read at CALL time (Pitfall #1). If the shared module is missing,
// each helper answers exactly what the pre-9489 code did.
function _billingCompanyRoundsEquipment() {
  const ER = window.EquipmentRounding;
  return ER ? ER.companyRoundsEquipment(window.AppState?.settings) : false;
}
function _billingResolveEquipRound(lineValue, companyDefault) {
  const ER = window.EquipmentRounding;
  return ER ? ER.resolveEquipmentRounding(lineValue, companyDefault === true) : lineValue === true;
}
// A dispatcher's jobDay pick: its snapshot cannot tell Never from Company
// default (pre-9489 picks stored false for an unset line), so the picked line
// as it stands now decides. Company switch off = exactly `=== true`.
function _billingSelectionRounds(sel, customerId) {
  const ER = window.EquipmentRounding;
  if (!ER) return sel?.roundToHalfHour === true;
  const cached = customerId ? ReviewState.contractRateCache?.[customerId] : null;
  const line = sel?.lineItemId && Array.isArray(cached?.lineItems)
    ? (cached.lineItems.find(l => l && l.id === sel.lineItemId) || null)
    : null;
  return ER.resolveSelectionRounding(sel?.roundToHalfHour, line, _billingCompanyRoundsEquipment());
}

// TASK-9489 — the three-way price-book control: Company default / Always /
// Never. Stored as roundToHalfHour null / true / false. The "Company default"
// label says what the company switch does TODAY so nobody has to go look.
function _equipRoundChoiceOf(v) {
  return v === true ? 'always' : (v === false ? 'never' : 'default');
}
function _equipRoundSelectHtml(id, value, kind) {
  const choice = _equipRoundChoiceOf(value);
  // The company switch is EQUIPMENT only: a person (man-hour) line never follows it.
  const coOn = kind === 'person' ? false : _billingCompanyRoundsEquipment();
  const opt = (v, label) => `<option value="${v}"${choice === v ? ' selected' : ''}>${label}</option>`;
  return `<select id="${id}" class="form-control" style="font-size:13px;padding:4px 8px;height:auto;width:auto;max-width:100%;" title="Rounds equipment hours UP to the next half hour on the customer's invoice. Employee pay is never rounded.">`
    + opt('default', kind === 'person' ? 'Default (no rounding)' : `Company default (${coOn ? 'rounds' : 'no rounding'})`)
    + opt('always', 'Always round up to ½ hr')
    + opt('never', 'Never round')
    + '</select>';
}
// The stored value for a select, or `fallback` when the control is not on screen.
function _readEquipRoundSelect(id, fallback) {
  const el = document.getElementById(id);
  if (!el) return fallback === undefined ? null : fallback;
  return el.value === 'always' ? true : (el.value === 'never' ? false : null);
}

// ============================================
// INVOICE SECTION ORDER
// ============================================
const INVOICE_SECTION_META = {
  notes:      { label: 'Work Performed',  icon: 'notes' },
  labor:      { label: 'Labor',           icon: 'people' },
  equipment:  { label: 'Equipment',       icon: 'local_shipping' },
  downtime:   { label: 'Downtime',        icon: 'pause_circle' },
  standby:    { label: 'Standby',         icon: 'hourglass_empty' },
  permit:     { label: 'Load Permits',    icon: 'description' },
  fieldItems: { label: 'Field Items',     icon: 'build' },
  misc:       { label: 'Miscellaneous',   icon: 'receipt' }
};
const DEFAULT_SECTION_ORDER = ['notes', 'labor', 'equipment', 'downtime', 'standby', 'permit', 'fieldItems', 'misc'];

function buildOrderedSections(sectionMap) {
  const order = window.AppState?.settings?.invoiceSectionOrder || DEFAULT_SECTION_ORDER;
  let html = order.map(key => sectionMap[key] || '').join('');
  // Append any sections not in the configured order (e.g. lineInfo, fieldItems, misc)
  for (const key of Object.keys(sectionMap)) {
    if (!order.includes(key)) html += sectionMap[key] || '';
  }
  return html;
}

// ============================================
// TASK-747-C — Display captured custom-ticket-section data on the ticket.
// `customSectionData` was write-only before this (never rendered after save).
// Handles BOTH persistence shapes for back-compat:
//   - non-repeatable (legacy + current): customSectionData[csId] = { fieldId: value, ... }
//   - repeatable (new):                  customSectionData[csId] = { _repeatable:true, instances:[{ fieldId:value }, ...] }
// `esc` is the caller's escaper (escapeHtml for in-app, invoiceEsc for print) — ALL user
// strings (section name, field label, captured value) pass through it.
// ---------------------------------------------------------------------------
// TASK-9116 — THE ONE PLACE that decides whether a custom ticket section is
// visible to a given AUDIENCE. It used to be an inline `cs.showOnInvoice === false`
// test inside the renderer below, and it failed OPEN.
//
// WHAT FAILING OPEN MEANT, and why it is not a rendering nit: a section the admin
// marked "not for the customer invoice" is where internal notes live — dispute
// history, credit-hold flags, margin comments. `=== false` is a STRICT test, so a
// flag stored as the string `"false"`, as `0`, as `null` or as `""` — every shape
// a bad import, a form post or a hand-edit produces — read as "not false" and was
// PRINTED on the customer's copy. Only a real boolean `false` ever hid anything.
//
// MEASURED LIVE before this changed (admin SDK, read-only, all 94 tenants,
// 2026-09-10): FIVE custom-section definitions exist in the entire product, across
// 3 tenants. THREE of the five carry no `showOnInvoice` field at all — and those
// three are the ones holding the data: 123 of the 127 populated section instances
// on the 102 live tickets that have any. The only tenant whose sections carry the
// flag is `demo`, where the SEEDER wrote it. No real tenant has ever touched it.
//
// 🔴 SO THE OBVIOUS FIX — `=== true`, fail closed everywhere — IS THE WRONG
// FIX, and the measurement is what says so: it would blank all 123 live populated
// instances off the customer invoice, silently, on tickets that print them
// correctly today. That is TASK-428's shape — a tightening that stranded real
// users. The INVOICE therefore keeps its back-compat default (absent = shown); a
// biller reviews an invoice before it is sent, and that human is what makes the
// default survivable there.
//
// WHAT ACTUALLY CHANGED:
//   1. A flag that is PRESENT but not recognisably affirmative now HIDES on every
//      customer-facing audience. A genuine absence is the only thing that still
//      gets the benefit of the doubt; an ambiguous value is not permission.
//   2. There is now a CUSTOMER-TICKET audience that fails CLOSED, for the
//      field-ticket approval document. That one is emailed automatically when a
//      ticket completes, with NO biller in between, so nothing may render there
//      without an explicit opt-in. Nothing passes it yet — the renderer is
//      TASK-9115's model work — and the gate exists first on purpose, so that
//      port cannot invent a fail-open test of its own the way this path did.
//
// The functions-side twin is `isCustomSectionCustomerVisible` in
// functions/src/services/fieldTicketDocument.js (stricter still: no audience
// argument at all, because that document has exactly one reader). Both are
// asserted by tests/task9116-showoninvoice-fails-closed.unit.cjs.
// ---------------------------------------------------------------------------
const CS_AUDIENCE_INTERNAL = 'internal';               // in-app detail, both review screens, and the PRINTED (internal) field ticket
const CS_AUDIENCE_INVOICE = 'invoice';                 // the printed/PDF customer invoice — reviewed by a biller before it is sent
const CS_AUDIENCE_CUSTOMER_TICKET = 'customer-ticket'; // the auto-emailed field-ticket approval document — no human in between

// Normalise the stored flag to 'on' | 'off' | 'unset'. `unset` is ONLY a genuinely
// absent (or explicitly `undefined`) value. Anything present that is not an
// affirmative resolves to 'off'.
function _csFlagState(cs) {
  if (!cs || typeof cs !== 'object') return 'off';
  if (!('showOnInvoice' in cs) || cs.showOnInvoice === undefined) return 'unset';
  const v = cs.showOnInvoice;
  if (v === true) return 'on';
  if (typeof v === 'number') return v === 1 ? 'on' : 'off';
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].indexOf(v.trim().toLowerCase()) >= 0 ? 'on' : 'off';
  return 'off';
}

function isCustomSectionVisibleTo(cs, audience) {
  const state = _csFlagState(cs);
  if (audience === CS_AUDIENCE_INTERNAL) return true;                 // the internal copy shows everything, always
  if (audience === CS_AUDIENCE_INVOICE) return state !== 'off';       // absent = shown (back-compat; 123 live instances depend on it)
  if (audience === CS_AUDIENCE_CUSTOMER_TICKET) return state === 'on'; // FAIL CLOSED — explicit opt-in only
  return false;                                                       // an unknown/typo'd audience must never be the one that leaks
}

// TASK-848 / TASK-9116 — `opts.audience` (or its back-compat spelling `opts.invoiceOnly`)
// decides which sections a given reader may see. The decision itself is NOT here — it is
// `isCustomSectionVisibleTo` immediately above, the only place in this file that reads
// `showOnInvoice`. Read that block before changing anything about visibility.
function buildCustomSectionDataHtml(customSectionData, esc, opts = {}) {
  if (!customSectionData || typeof customSectionData !== 'object') return '';
  const sections = _bCsDefs();
  // Render a single { fieldId: value } instance as label:value chips. Returns '' if no displayable values.
  const renderInstance = (cs, inst) => {
    if (!inst || typeof inst !== 'object') return '';
    const chips = [];
    for (const f of (cs.fields || [])) {
      // TASK-848 (COD Edge fold) — a malformed/missing field def must not throw
      // on the review screens; skip it instead.
      if (!f || typeof f !== 'object' || !f.id) continue;
      if (!(f.id in inst)) continue;
      let v = inst[f.id];
      if (f.type === 'checkbox') {
        if (v !== true) continue; // only show ticked boxes
        v = 'Yes';
      } else {
        if (v === '' || v === null || v === undefined) continue;
        if (f.type === 'number' && Number(v) === 0) continue;
        v = String(v);
      }
      chips.push(`<span style="display:inline-flex;gap:4px;"><span style="color:var(--text-secondary,#64748B);">${esc(f.label || '')}:</span> <span style="color:var(--text-primary,#0F172A);font-weight:500;">${esc(v)}</span></span>`);
    }
    if (chips.length === 0) return '';
    return `<div style="display:flex;flex-wrap:wrap;gap:6px 16px;font-size:0.85rem;">${chips.join('')}</div>`;
  };

  let out = '';
  for (const cs of sections) {
    // TASK-848 (COD 🔴 filter placement) / TASK-9116 — the visibility decision is
    // `isCustomSectionVisibleTo` and lives in exactly one place. `invoiceOnly` is kept as the
    // back-compat spelling of `audience:'invoice'` so the three internal call sites
    // (viewTicket, renderReviewCustomSections, the printed field ticket) stay untouched.
    const _csAudience = opts.audience || (opts.invoiceOnly ? CS_AUDIENCE_INVOICE : CS_AUDIENCE_INTERNAL);
    if (!isCustomSectionVisibleTo(cs, _csAudience)) continue;
    // TASK-848 (COD Edge fold) — one malformed section must not blank the whole
    // review screen; render what we can, skip what throws.
    try {
      const raw = customSectionData[cs.id];
      if (raw === undefined || raw === null) continue;
      // Detect the repeatable array shape unambiguously (existing object data never has .instances[]).
      // Defensive ceiling (Adversary): the 25-cap is UI-only — clamp here so out-of-UI / direct-SDK
      // writes with a huge instances[] can't lock the browser when someone opens the ticket.
      const CS_DISPLAY_MAX = 25;
      const instances = (raw._repeatable && Array.isArray(raw.instances)) ? raw.instances.slice(0, CS_DISPLAY_MAX) : [raw];
      const rows = instances.map(inst => renderInstance(cs, inst)).filter(Boolean);
      if (rows.length === 0) continue;
      const body = rows.map(r => `<div style="background:var(--bg-secondary,#F8FAFC);border-radius:var(--radius-sm,8px);padding:8px 12px;margin-bottom:6px;">${r}</div>`).join('');
      out += `<div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">${esc(cs.icon || 'tune')}</span>
          ${esc(cs.name || 'Custom Section')}${rows.length > 1 ? ` (${rows.length})` : ''}
        </div>
        ${body}
      </div>`;
    } catch (err) {
      console.warn('[Billing] Skipping malformed custom section in render:', cs?.id, err);
    }
  }
  return out;
}

// ============================================
// TASK-846-B — EDITABLE custom-section data in the billing Review Ticket modal.
// customSectionData is the SOURCE of truth for auto-billed lines: the 846-A server
// reconciler (reconcileDerivedTicketLines, functions/src/index.js) watches the
// ticket doc and re-derives/updates/deletes derivedFromBinding FIELD_ITEM lines
// whenever customSectionData changes. So the biller fixes a forgotten/wrong count
// HERE (never the derived line itself) and the server re-syncs billing on save.
// The persisted shapes are clock.js collectCustomSectionData's, byte-for-byte:
//   non-repeatable: customSectionData[csId] = { fieldId: value, ... }
//   repeatable:     customSectionData[csId] = { _repeatable:true, instances:[{...}] }
// with checkbox→boolean, number→float (0 default), everything else→trimmed string.
// ============================================
// Matches clock.js CS_MAX_INSTANCES and the read-only display clamp above.
const REVIEW_CS_MAX_INSTANCES = 25;

// Lazily materialize the staged working copy (deep clone so the shared
// BillingState.tickets cache object is never mutated before save).
function _csdWorking() {
  if (!ReviewState.csdEdits) {
    const src = ReviewState.ticket?.customSectionData;
    ReviewState.csdEdits = (src && typeof src === 'object') ? JSON.parse(JSON.stringify(src)) : {};
  }
  return ReviewState.csdEdits;
}

// Default value clock.js persists for an untouched input of this field type.
function _csDefaultValue(type) {
  if (type === 'number') return 0;
  if (type === 'checkbox') return false;
  return '';
}

// Fresh instance with every defined field present at its default — the same
// key set clock.js _csReadInstance writes for a rendered-but-untouched group.
function _csNewInstance(cs) {
  const inst = {};
  for (const f of (cs.fields || [])) {
    if (f && typeof f === 'object' && f.id) inst[f.id] = _csDefaultValue(f.type);
  }
  return inst;
}

// Resolve (creating if needed) the instance object a field edit should mutate.
// The STORED shape wins — a plain-object block stays plain for idx 0; conversion
// to {_repeatable, instances} only happens when the user's action requires a
// second instance (which is exactly the shape clock.js writes for a repeatable
// section today, and keeps derivedFromBinding instanceIdx 0 keys stable).
function _csdEnsureInstance(cs, idx) {
  const working = _csdWorking();
  const block = working[cs.id];
  if (block && typeof block === 'object' && block._repeatable === true && Array.isArray(block.instances)) {
    while (block.instances.length <= idx) block.instances.push(_csNewInstance(cs));
    return block.instances[idx];
  }
  if (block && typeof block === 'object') {
    if (idx === 0) return block; // legacy/plain shape preserved
    const conv = { _repeatable: true, instances: [block] };
    while (conv.instances.length <= idx) conv.instances.push(_csNewInstance(cs));
    working[cs.id] = conv;
    return conv.instances[idx];
  }
  // No stored data — create in the shape clock.js would write for this def.
  if (cs.repeatable === true) {
    const fresh = { _repeatable: true, instances: [] };
    while (fresh.instances.length <= idx) fresh.instances.push(_csNewInstance(cs));
    working[cs.id] = fresh;
    return fresh.instances[idx];
  }
  const inst = _csNewInstance(cs);
  working[cs.id] = inst;
  return inst;
}

// One editable input for a custom-section field. All ids/labels/values are
// tenant-admin/driver strings — escapeHtml everything; handler args travel via
// data-* attributes (never string-interpolated into the onclick JS).
function _reviewCsFieldInputHtml(cs, f, idx, inst) {
  if (!f || typeof f !== 'object' || !f.id) return '';
  const type = f.type || 'text';
  const v = (inst && typeof inst === 'object') ? inst[f.id] : undefined;
  const dataAttrs = `data-cs="${escapeHtml(cs.id)}" data-field="${escapeHtml(f.id)}" data-idx="${idx}" data-ftype="${escapeHtml(type)}" onchange="window.BillingModule.updateReviewCustomField(this)"`;
  if (type === 'checkbox') {
    return `<label style="display:flex;align-items:center;gap:6px;font-size:0.85rem;color:var(--text-primary);cursor:pointer;margin:0;min-width:120px;">
      <input type="checkbox" ${v === true ? 'checked' : ''} ${dataAttrs} style="accent-color:var(--brand-blue);"> ${escapeHtml(f.label || '')}
    </label>`;
  }
  const bindHint = f.lineItemBinding?.itemCode ? ` <span title="Tied to Price Book item ${escapeHtml(f.lineItemBinding.itemCode)} — this count auto-bills" style="font-size:0.65rem;color:var(--brand-blue);">⟶ ${escapeHtml(f.lineItemBinding.itemCode)}</span>` : '';
  const label = `<label style="display:block;font-size:0.75rem;color:var(--text-secondary,#64748B);margin:0 0 2px;">${escapeHtml(f.label || '')}${bindHint}</label>`;
  if (type === 'number') {
    const num = (typeof v === 'number' && !isNaN(v)) ? v : ((v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) ? Number(v) : 0);
    return `<div style="min-width:90px;">${label}
      <input type="number" min="0" step="any" class="form-control" value="${num}" style="font-size:0.85rem;padding:4px 8px;width:100px;" onfocus="this.select()" ${dataAttrs}>
    </div>`;
  }
  const str = (v === undefined || v === null) ? '' : String(v);
  // TASK-4607 — a file field's value is a Storage PATH, never a URL (TASK-996:
  // this service account cannot sign, so a minted link is a dead link). Show the
  // filename with a View button that streams the bytes through the callable, and
  // keep the path itself in a hidden input so the existing collector still saves
  // it. NOT a text box: a biller editing a Storage path by hand can only break
  // the pointer, and a cleared box would silently orphan the driver's upload.
  if (type === 'file') {
    const leaf = str ? (str.split('/').pop() || str) : '';
    const shown = leaf.replace(/^\d{10,}_/, '');
    const view = str
      ? `<button type="button" class="btn btn-sm btn-secondary" data-tsf-view="${window.escAttr(str)}" style="padding:3px 8px;font-size:0.75rem;">
           <span class="material-icons" style="font-size:14px;vertical-align:middle;">visibility</span> View
         </button>`
      : '';
    return `<div style="flex:1;min-width:140px;">${label}
      <input type="hidden" value="${escapeHtml(str)}" ${dataAttrs}>
      <div style="display:flex;align-items:center;gap:6px;font-size:0.8rem;">
        ${str
          ? `<span class="material-icons" style="font-size:15px;color:var(--success,#16A34A);">attach_file</span>
             <span style="flex:1;word-break:break-all;color:var(--text-primary);">${escapeHtml(shown)}</span>${view}`
          : `<span style="color:var(--text-secondary,#64748B);">Nothing attached</span>`}
      </div>
    </div>`;
  }
  return `<div style="flex:1;min-width:140px;">${label}
    <input type="text" class="form-control" value="${escapeHtml(str)}" style="font-size:0.85rem;padding:4px 8px;width:100%;" ${dataAttrs}>
  </div>`;
}

// TASK-4607 — stream a ticket-section attachment back through the callable and
// hand it to the browser. ONE delegated listener; the path travels in a data
// attribute and is never interpolated into an onclick string (a Storage path
// ends in a user-supplied filename, and HTML-escaping is not JS-string
// escaping — the exact defect COD caught twice on 2026-08-24).
let _tsfViewDelegated = false;
function _installTsfViewDelegate() {
  if (_tsfViewDelegated) return;
  _tsfViewDelegated = true;
  document.addEventListener('click', async (ev) => {
    const btn = ev.target && ev.target.closest ? ev.target.closest('[data-tsf-view]') : null;
    if (!btn) return;
    ev.preventDefault();
    const path = btn.getAttribute('data-tsf-view') || '';
    if (!path) return;
    const prev = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Opening…';
    let url = null;
    try {
      const fn = httpsCallable(functions, 'getTicketSectionFileBytes');
      const res = await fn({ path });
      const d = res?.data || {};
      if (!d.base64) throw new Error('That file came back empty.');
      const bin = atob(d.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: d.contentType || 'application/octet-stream' });
      url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup blocked — a silent no-op reads as a broken button.
        window.showToast?.('Your browser blocked the popup. Allow popups for this site to view attachments.', 'warning');
      }
      // Only revoke once the new tab has had time to take the blob; revoking
      // immediately races the open and yields a blank tab.
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
    } catch (err) {
      console.warn('[Billing] ticket-section file view failed:', err?.message);
      if (url) { try { URL.revokeObjectURL(url); } catch (_) {} }
      window.showToast?.(err?.message || 'Could not open that attachment.', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = prev;
    }
  }, true);
}

// Inner (editable) HTML for every defined custom section — re-rendered in place
// on add/remove entry so other in-progress review inputs are untouched.
function _reviewCustomSectionsInner() {
  _installTsfViewDelegate();   // TASK-4607 — idempotent
  const sections = _bCsDefs();
  const csd = ReviewState.csdEdits || ReviewState.ticket?.customSectionData || {};
  let out = `<div style="font-size:0.72rem;color:var(--text-secondary);margin-bottom:8px;">Captured at clock-out — editable here. Counts tied to a Price Book item (⟶) auto-update the billed Field Items after save.</div>`;
  for (const cs of sections) {
    try {
      if (!cs || typeof cs !== 'object' || !cs.id || !Array.isArray(cs.fields) || cs.fields.length === 0) continue;
      const raw = csd[cs.id];
      const storedRepeatable = !!(raw && typeof raw === 'object' && raw._repeatable === true && Array.isArray(raw.instances));
      // No data yet → one virtual blank row (staged only when the biller edits it).
      const instances = storedRepeatable ? raw.instances
        : (raw && typeof raw === 'object') ? [raw]
        : [_csNewInstance(cs)];
      const total = instances.length;
      const shown = instances.slice(0, REVIEW_CS_MAX_INSTANCES);
      const canRepeat = cs.repeatable === true || storedRepeatable;
      const rows = shown.map((inst, idx) => {
        const fieldsHtml = cs.fields.map(f => _reviewCsFieldInputHtml(cs, f, idx, inst)).join('');
        const removeBtn = canRepeat ? `<button type="button" class="btn-icon" title="Remove this entry" data-cs="${escapeHtml(cs.id)}" data-idx="${idx}" onclick="window.BillingModule.removeReviewCustomInstance(this)" style="position:absolute;top:4px;right:4px;padding:2px;">
            <span class="material-icons" style="font-size:16px;color:var(--status-danger,#DC2626);">close</span>
          </button>` : '';
        return `<div style="position:relative;background:var(--bg-secondary,#F8FAFC);border-radius:var(--radius-sm,8px);padding:8px ${canRepeat ? '32px' : '12px'} 8px 12px;margin-bottom:6px;">
          ${removeBtn}
          <div style="display:flex;flex-wrap:wrap;gap:8px 16px;align-items:flex-end;">${fieldsHtml}</div>
        </div>`;
      }).join('');
      // Cap handling: never silently truncate — out-of-UI data beyond 25 is
      // preserved verbatim on save (the working copy keeps it), just not editable.
      const overCapNote = total > REVIEW_CS_MAX_INSTANCES
        ? `<div style="font-size:0.75rem;color:#D97706;margin-bottom:6px;">Showing the first ${REVIEW_CS_MAX_INSTANCES} of ${total} entries — the rest are preserved but can't be edited here.</div>` : '';
      const addControl = cs.repeatable === true
        ? (total >= REVIEW_CS_MAX_INSTANCES
          ? `<div style="font-size:0.75rem;color:#D97706;margin-top:2px;">This section is limited to ${REVIEW_CS_MAX_INSTANCES} entries per ticket.</div>`
          : `<button type="button" class="btn btn-secondary btn-sm" data-cs="${escapeHtml(cs.id)}" onclick="window.BillingModule.addReviewCustomInstance(this)" style="margin-top:2px;">
              <span class="material-icons" style="font-size:16px;">add</span> Add entry
            </button>`)
        : '';
      out += `<div class="detail-section" data-review-cs="${escapeHtml(cs.id)}" style="margin-bottom:12px;">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">${escapeHtml(cs.icon || 'tune')}</span>
          ${escapeHtml(cs.name || 'Custom Section')}${total > 1 ? ` (${total})` : ''}
        </div>
        ${overCapNote}${rows}${addControl}
      </div>`;
    } catch (err) {
      console.warn('[Billing] Skipping malformed custom section in review edit render:', cs?.id, err);
    }
  }
  return out;
}

// TASK-1167 — a ticket is READ-ONLY *to the biller* in the Review modal when it's
// invoiced, archived, or status-locked. Archived was the silent gap: at TITS
// "archived" is the post-QBO-invoice state (Lacey archives after invoicing in
// QuickBooks), so an archived ticket may already be billed — the old code coerced
// archived→draft and let it be edited + saved with no warning (Simonson #100239).
// NOTE: driverEditLocked is DELIBERATELY excluded — it locks the ticket for DRIVER
// sessions only, the biller keeps editing (TASK-566). Its narrower read-only
// handling (custom sections + the driver-lock toggle) stays separate below.
function isReviewReadOnly(t) {
  t = t || {};
  // TASK-4600 — Chris's call, 2026-08-24: "invoiced tickets should not be
  // editable." This now reads the DISPLAY predicate (flag OR invoiceNumber OR
  // invoicedAt), not the raw boolean, so a ticket that RENDERS as billed is
  // actually locked. Before this, 9 live tickets carrying an invoice number with
  // the flag cleared showed "Invoiced" and stayed fully editable — and a driver
  // edited a $10,153 invoiced ticket 31 minutes after it was billed.
  //
  // This matches how mature systems behave (ServiceTitan, Jobber, OpenInvoice,
  // Procore all hard-lock once an invoice is cut, with an explicit audited way
  // back). The way back here is the existing Uninvoice action, whose banner was
  // widened in lockstep — see the note there. A lock without a visible exit is
  // how #100172 became permanently unsaveable; never ship one half of this pair.
  //
  // 🔴 TWO SITES DELIBERATELY STAY ON THE RAW BOOLEAN and must not be swept:
  //   • `_clearReadyBy` (unarchiveTicket) MIRRORS the firestore.rules
  //     readyBy-reset condition, which reads raw `invoiced`. Widening it would
  //     skip the clear on the ~104 invoice-marker-only tickets and hand them
  //     back the permanently-unsaveable state TASK-1343 fixed.
  //   • `_cardCanUnarchive` keeps those legacy tickets' archive escape hatch.
  return isTicketInvoiced(t) || t.status === 'archived' || t.status === 'locked';
}

// TASK-4598 — was this ticket invoiced without ever reaching Ready?
// ============================================================================
// `invoicedFromNonReady` and `statusAtInvoice` are written on every invoice and
// were read in ZERO places until now. 18 of the 42 stamped tickets on TITS
// ($32,577) were invoiced straight from draft, and nobody could see it — the
// system recorded the evidence and showed it to no one.
//
// I very nearly shipped a whole second "billing state" chip here before reading
// the card properly: the AR/AP card ALREADY renders Written Off / Paid / Partial
// / Invoiced next to the work-status chip, off `isTicketInvoiced`. The biller's
// view was never the blind spot. So this stays small — it only adds the signal
// that was genuinely absent — and the driver-side banner (jobboard.js) is where
// the real gap was.
function _invoicedFromNonReady(t) {
  t = t || {};
  return t.invoicedFromNonReady === true || (!!t.statusAtInvoice && t.statusAtInvoice !== 'ready');
}

// TASK-9945 f/u — an online payment a person must look at: a SECOND payment on
// an already-paid invoice (recorded, not counted) or paid more than the invoice
// now totals (edited down after the link went out). Server-written flags
// (stripeConnectWebhook); amounts are integer cents.
function _paidNeedsReviewBadge(t) {
  const $ = (c) => '$' + ((Number(c) || 0) / 100).toFixed(2);
  let why = 'Online payment needs review.';
  if (t.paymentReviewReason === 'duplicate') why = `Paid twice online — ${$(t.duplicatePaymentCents)} extra was recorded, not counted. Refund it in Stripe if it is a double payment.`;
  // TASK-9973 (independent review 🟡): a held payment's overage is a snapshot of
  // the total when it was PAID; billing it afterwards can change the total, so it
  // is worded as "then", never as a current overpayment.
  else if (t.paymentReviewReason === 'not_billed') why = `Paid online ${$(t.amountPaidCents)} before it was billed`
    + (Number(t.overpaidByCents) > 0 ? ` — ${$(t.overpaidByCents)} more than the ticket total then (${$(t.expectedAmountCents)})` : '')
    + '. Check it against the invoice and refund any difference in Stripe.';
  else if (t.paymentReviewReason === 'overpaid' || Number(t.overpaidByCents) > 0) why = `Overpaid online by ${$(t.overpaidByCents)} — paid ${$(t.amountPaidCents)} of ${$(t.expectedAmountCents)}. Refund the difference in Stripe if needed.`;
  return `<span class="badge" style="background:#FEF3C7;color:#92400E;" title="${escapeHtml(why)}"><span class="material-icons">warning</span>Paid · Review</span> `;
}

// TASK-9973 — an online payment recorded but NOT counted as paid (paidShortfall).
// Two different things wear that flag: a real short payment, and a payment held
// because the ticket was not billed yet (stripeInvoicePayments.holdIfUnbilled,
// reason 'not_billed') — which can be MORE than the total. The old badge called
// every one "less than the invoice total".
function _paymentShortfallBadge(t) {
  const $ = (c) => '$' + ((Number(c) || 0) / 100).toFixed(2);
  const hasAmt = typeof t.amountPaidCents === 'number';
  const over = Number(t.overpaidByCents) > 0 ? Number(t.overpaidByCents) : 0;
  let label = 'Partial Payment';
  let why;
  if (t.paymentReviewReason === 'not_billed') {
    label = over ? 'Payment Held · Overpaid' : 'Payment Held';
    why = `Paid online${hasAmt ? ` ${$(t.amountPaidCents)}` : ''} before this ticket was billed — not marked paid yet.`
      + (over ? ` That is ${$(over)} more than the total of ${$(t.expectedAmountCents)} — refund the difference in Stripe if needed.` : '')
      + ' Bill it, then mark it paid.';
  } else {
    why = `Online payment was less than the invoice total${hasAmt ? ` — paid ${$(t.amountPaidCents)} of ${$(t.expectedAmountCents)}` : ''}. Review before treating as paid.`;
  }
  return `<span class="badge" style="background:#FEF3C7;color:#92400E;" title="${escapeHtml(why)}"><span class="material-icons">warning</span>${label}</span> `;
}

// TASK-1572 follow-up (Chris, 2026-09-29) — a ticket does NOT have to be Ready
// before it is invoiced: WARN, never block. Until now the invoice paths said
// nothing at all (ticketGateway only STAMPS invoicedFromNonReady after the fact),
// so 17%+ of invoices skipped review silently. This names which tickets were never
// marked Ready and lets the biller go on or go review. It is deliberately NOT a
// gate: no server/rules check was added, and every hard gate ($0, zero-hour,
// unpriced, mileage, fleet ref, required PO) still runs BEFORE this and still
// blocks — the warning only fires on a ticket that is otherwise invoiceable.
// Reads the live snapshot's status; a stale read only costs a needless warning,
// and the server stamps the true statusAtInvoice either way.
function ticketsNotReadyForInvoice(tickets) {
  return (tickets || []).filter(t => t && t.status !== 'ready');
}

function _notReadyTicketLabel(t) {
  const st = String((t && t.status) || 'draft');
  return `#${(t && t.ticketNo) || '?'} (${st.charAt(0).toUpperCase()}${st.slice(1)})`;
}

// Pure copy builder so the wording is testable. `total` = how many tickets are
// about to be invoiced (the not-ready ones included).
function notReadyInvoiceCopy(notReady, total) {
  const n = (notReady || []).length;
  if (n === 0) return null;
  const MAX = 8;
  const shown = notReady.slice(0, MAX).map(_notReadyTicketLabel).join(', ');
  const more = n > MAX ? ` and ${n - MAX} more` : '';
  if (total <= 1) {
    return {
      title: "Ticket isn't marked Ready",
      message: `Ticket ${_notReadyTicketLabel(notReady[0])} hasn't been marked Ready, so nobody has signed off that it's complete. ` +
        'You can invoice it now, or review it first.',
      confirmText: 'Invoice anyway',
      cancelText: 'Review it first'
    };
  }
  return {
    title: `${n} of ${total} tickets aren't marked Ready`,
    message: `Not marked Ready: ${shown}${more}. Nobody has signed off that ${n === 1 ? 'it is' : 'they are'} complete. ` +
      `You can invoice all ${total} now, or go back and review ${n === 1 ? 'it' : 'them'} first — your selection is kept.`,
    confirmText: `Invoice all ${total}`,
    cancelText: 'Go back'
  };
}

// Returns true to proceed. Single ticket: "Review it first" opens Review Ticket.
async function _notReadyInvoiceWarning(tickets, opts) {
  const list = tickets || [];
  const notReady = ticketsNotReadyForInvoice(list);
  const copy = notReadyInvoiceCopy(notReady, list.length);
  if (!copy) return true;
  if (typeof window.showConfirm !== 'function') return true; // warn-only: never block on missing chrome
  const go = await window.showConfirm(copy.title, copy.message, {
    confirmText: copy.confirmText, cancelText: copy.cancelText, icon: 'warning', iconColor: '#F59E0B'
  });
  if (go) return true;
  if (list.length === 1 && opts && opts.reviewOnCancel) {
    try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
    try { closeModal('ticket'); } catch (_) { /* not always open */ }
    openReviewTicket(list[0].id);
  }
  return false;
}

// TASK-1345 — "is this ticket billed?" as ONE predicate. Three different answers
// lived in this file (`invoiced===true` here, `!t.invoiced` in the list filter,
// `!t.invoiceNumber` in the export), so the same ticket read as invoiced in one
// view and not in another — which is why "what is unbilled?" was unanswerable.
// The boolean alone is not enough: generateInvoiceNumber() and the grouped-
// invoice loop write invoiceNumber/invoicedAt but never invoiced:true, so 110
// live TITS tickets carry every invoice marker with the flag still false.
// ⚠️ THE PARAGRAPH ABOVE IS STALE and was misleading me for an hour: BOTH invoice
// paths DO write `invoiced: true` today — the gateway does it server-side
// (ticketGateway.js, `const update = { invoiced: true, ... }`, unconditional) and
// the legacy branch does it in its own updateDoc. The 9 live tickets carrying an
// invoice number with `invoiced === false` got there through the UN-MARK path,
// not through invoicing (TASK-4597). Do not re-derive the old conclusion from
// the old sentence.
// DELIBERATELY NOT wired into isReviewReadOnly: broadening the read-only gate
// would instantly lock those 110 tickets against the biller who is still
// editing them. Filters/exports/guards use this; the edit lock keeps the narrow
// boolean until that's a decision someone makes on purpose (TASK-1346).
//
// TASK-1346 part 2 — THE SWEEP RULE for every `t.invoiced` read in this file.
// Blast's mandate was "audit every site", not "replace every site", because the
// two predicates answer different questions. Use:
//   isTicketInvoiced(t)  → "has this work been BILLED?" — every badge, every
//                          books-integrity gate (invoice / split / combine /
//                          consolidate / delete / drag / EDI export).
//   t.invoiced === true  → ONLY where the narrow boolean is the actual subject:
//                          (a) isReviewReadOnly and the banners/affordances that
//                              explain or undo THAT lock — they must describe the
//                              gate that is really firing, not a different one;
//                          (b) anything that feeds a WRITE of the boolean (the
//                              invoice page's "Mark as Invoiced" checkbox) — a
//                              pre-checked box would backfill the 110 by accident,
//                              which is exactly the un-announced lock we refused;
//                          (c) mirrors of a firestore.rules condition that reads
//                              the raw field (unarchiveTicket's readyBy reset).
// Every remaining `t.invoiced` in this file carries a comment naming which case
// it is. A bare, uncommented `t.invoiced` read is a bug.
function isTicketInvoiced(t) {
  t = t || {};
  return t.invoiced === true || !!t.invoiceNumber || !!t.invoicedAt;
}

// Archiving writes `status:'archived'` (or 'locked') — NOT the `archived`
// boolean, which ticket archive never sets. Test the status.
function isTicketArchivedStatus(t) {
  t = t || {};
  return t.status === 'archived' || t.status === 'locked';
}

// TASK-1387 — the calendar status-dot decision for one job's tickets on one day,
// shared by the billing month calendar and the dispatch week/month calendars
// (window.BillingDedup, never a ?v= cross-import — Pitfall #1). PURE.
// Returns 'invoiced' | 'draft' | 'ready' | 'archived' | '' (no dot).
//   invoiced  — every ticket is invoiced (green, hatched pill on billing)
//   draft     — any non-invoiced ticket is draft (red)
//   ready     — any non-invoiced ticket is ready (amber)
//   archived  — every ticket is invoiced or archived/locked, at least one
//               archived (grey; may already be invoiced in outside accounting)
function classifyCalendarTicketStatus(tickets) {
  const list = Array.isArray(tickets) ? tickets : [];
  if (list.length === 0) return '';
  if (list.every(t => isTicketInvoiced(t))) return 'invoiced';
  const nonInvoiced = list.filter(t => !isTicketInvoiced(t));
  if (nonInvoiced.some(t => t && t.status === 'draft')) return 'draft';
  if (nonInvoiced.some(t => t && t.status === 'ready')) return 'ready';
  if (nonInvoiced.every(t => isTicketArchivedStatus(t))) return 'archived';
  return '';
}

// TASK-9645 B — the DAY's billing state for the dispatch board dot and the job
// modal's per-day chip. Shared on window.BillingDedup (Pitfall #1). PURE.
// Builds on classifyCalendarTicketStatus, so the day shows its LEAST-billed
// state (Draft < Ready < Invoiced < Paid) exactly as the calendars already do,
// and adds one rung on top:
//   paid — every ticket is invoiced AND every one carries a real `paid:true`
//          (manual Mark Paid or the QuickBooks paid sync). Never inferred from
//          anything else.
// Billed elsewhere is NOT a separate state: it IS invoiced (BI counts it), so
// it rides as `billedElsewhere` for a note, never a different colour.
// `status:'deleted'` tickets are dropped first — a deleted twin must not hide
// the day's real state (classifyCalendarTicketStatus returns '' for it).
// Returns { kind, count, invoicedCount, billedElsewhere, refs[], paidAtMs }.
function classifyDayBillingState(tickets) {
  const list = (Array.isArray(tickets) ? tickets : []).filter(t => t && t.status !== 'deleted');
  const out = { kind: '', count: list.length, invoicedCount: 0, billedElsewhere: false, refs: [], paidAtMs: null };
  if (list.length === 0) return out;
  const billed = list.filter(t => isTicketInvoiced(t));
  out.invoicedCount = billed.length;
  out.billedElsewhere = billed.some(t => isBilledExternally(t));
  for (const t of billed) {
    const ref = isBilledExternally(t) ? (t.externalInvoiceRef || t.invoiceNumber) : t.invoiceNumber;
    if (ref && typeof ref === 'string' && !out.refs.includes(ref)) out.refs.push(ref);
  }
  let kind = classifyCalendarTicketStatus(list);
  if (kind === 'invoiced' && billed.every(t => t.paid === true)) {
    kind = 'paid';
    for (const t of billed) {
      const v = t.paidAt;
      let ms = null;
      if (v && typeof v.toMillis === 'function') ms = v.toMillis();
      else if (v && typeof v.seconds === 'number') ms = v.seconds * 1000;
      else if (v instanceof Date) ms = v.getTime();
      else if (typeof v === 'string' || typeof v === 'number') { const p = new Date(v).getTime(); ms = Number.isFinite(p) ? p : null; }
      if (ms !== null && (out.paidAtMs === null || ms > out.paidAtMs)) out.paidAtMs = ms;
    }
  }
  out.kind = kind;
  return out;
}

// =============================================================================
// TASK-1398 — the job-day duplicate DECISION, PURE and exported.
//
// THE BUG THIS PROTECTS. "Generate Ticket" used to mint a SECOND ticket for a
// job-day that already had an INVOICED or ARCHIVED one: the dedup read queried
// `status in ['draft','ready']` and then re-filtered invoiced/archived rows OUT,
// so a just-billed twin was STRUCTURALLY INVISIBLE. Measured on TITS: 15 of 581
// job+day groups held more than one ticket, and the dangerous shape is an
// unbilled twin sitting beside an invoiced one -- #100237 (ready, $1,296) is an
// exact-amount twin of invoiced #100422. Invoice either and the customer pays
// twice.
//
// The read was widened (TASK-1409) and the authoritative check moved into the
// createTicketChecked transaction (TASK-1433) -- but the CLIENT decision, the one
// the dispatcher actually sees, stayed inline inside a ~250-line async function
// wrapped around two getDocs() calls, i.e. unassertable without Firestore. This
// is that decision lifted out whole: same expressions, same order, no behaviour
// change, now pinned by tests/task1398-generate-ticket-dedup.unit.cjs.
//
// THE LOAD-BEARING RULE: the two buckets are defined by SUBTRACTION, never by
// enumerating statuses. `billed` is "everything that is NOT an open draft/ready
// row". The enumerated version (invoiced/archived/locked/isTicketInvoiced) failed
// OPEN on any status outside that list -- a legacy import, a status added later, a
// row with no `status` at all -- and "falls through both buckets" here means
// "mints a second ticket for a day that is already billed." Subtraction makes the
// buckets exhaustive by construction, so no row can be silently skipped. Mirrors
// findDuplicateTickets() in functions/src/shared/ticketCreateNormalize.js, which
// is the server half of the same decision.
//
// The action is INFORMED, never blocking: 10 of the 15 live multi-ticket job-days
// carry real money on BOTH rows with different amounts (separate loads/hitches on
// one job in one day is normal oilfield billing), so refusing outright would break
// legitimate work. 'open' reuses the existing draft; 'confirm' names the twin, its
// invoice and its amount and lets the operator decide; 'create' is friction-free.
//
// PURE: every input is an argument -- no Firestore, no DOM, no BillingState -- and
// the caller does all the formatting (formatCurrency/escapeHtml) and all the I/O.
// Exported on window.BillingDedup per Pitfall #1 (window.*, NEVER a ?v=
// cross-import: two busters = two module instances = two dedup rules).
//
//   dayTickets           -- every ticket doc ({id,...data}) whose workDate equals
//                           the target workDate.
//   target               -- { jobId, customerId } of the ticket about to be made.
//   legacyDateRangeDocs  -- ticket docs pulled by a `date` range over the same
//                           local day, for rows the workDate query structurally
//                           cannot see (pre-workDate imports).
//
// DO NOT fold `legacyDateRangeDocs` in unfiltered. Keeping ONLY rows that have
// no `workDate` is the whole correctness argument: `date` and `workDate`
// legitimately DIFFER -- a ticket built on the 4th for work done on the 3rd
// (TASK-1390) carries date=8/4, workDate=8/3 -- so folding in rows that HAVE a
// workDate would key the dedup on `date` too and raise a duplicate warning on
// correct work. That is how a warning gets trained away. Restricting to
// workDate-less rows adds exactly the invisible ones and cannot false-positive.
//
// Returns one of:
//   { action:'open',    existing }                       -- reuse the open draft
//   { action:'confirm', dupBilled, isBilled, state,
//                       label, invoiceNo, amount, twinNotes }
//   { action:'create' }                                  -- nothing in the way
// =============================================================================
function classifyJobDayTickets(dayTickets, target, legacyDateRangeDocs) {
  const jobId = (target && target.jobId) || null;
  const customerId = (target && target.customerId) || null;

  // Only the workDate-less rows from the legacy scan (see the DO NOT note above).
  const legacy = (legacyDateRangeDocs || []).filter(t => t && !t.workDate);
  // TASK-10107 — a SEALED, unbilled return estimate only; the client flag alone
  // exempts nothing. Twin of functions/src/shared/returnEstimate.js isReturnEstimate
  // (keep identical; tests/task10107-return-estimate-seal.unit.cjs runs both).
  const _isReturnEstimate = (t) => {
    if (!t || t.returnEstimate !== true) return false;
    const s = t.returnEstimateSeal;
    if (!s || typeof s !== 'object' || !t.jobId || s.jobId !== t.jobId || s.mirrorOf !== t.mirrorOf) return false;
    if (t.status !== 'draft' || t.invoiced === true || t.invoiceNumber || t.invoicedAt) return false;
    return !(Number(t.grandTotal) > 0);
  };
  // Splits always share jobId+workDate with their source; rental-engine drafts
  // share them with the crew ticket on a MIXED job (TASK-842-A3); a soft-deleted
  // row is gone, not billed. None is ever a dedup candidate.
  const all = [...(dayTickets || []), ...legacy]
    .filter(t => t && !t.splitFrom && t.rentalTicket !== true && !_isReturnEstimate(t) && t.status !== 'deleted');   // TASK-10107 COD: a SEALED estimated return ticket is not the day's crew ticket

  const open = all.filter(t => t.status === 'draft' || t.status === 'ready');
  const openIds = new Set(open.map(t => t.id));
  const billed = all.filter(t => !openIds.has(t.id));   // subtraction, not enumeration

  let existing = null;
  if (jobId) {
    existing = open.find(t => t.jobId === jobId && t.customerId === customerId);
    if (!existing) existing = open.find(t => t.jobId === jobId);
    if (!existing) existing = open.find(t => t.originalJobId === jobId);
  } else {
    existing = open.find(t => t.customerId === customerId && !t.jobId);
  }
  if (existing) return { action: 'open', existing, open, billed };

  const matchesJob = (t) => jobId
    ? (t.jobId === jobId || t.originalJobId === jobId)
    : (t.customerId === customerId && !t.jobId);
  const dupBilled = billed.find(matchesJob) || null;
  if (!dupBilled) return { action: 'create', open, billed };

  // Informational only: a same-customer, same-day ticket on a DIFFERENT job is
  // the twin-job shape. Deliberately NOT a second blocking path -- widening the
  // trigger itself would fire on routine multi-job customer days.
  const twinNotes = billed
    .filter(t => t !== dupBilled && t.customerId && t.customerId === customerId && t.jobId !== jobId)
    .map(t => `#${t.ticketNo}`)
    .slice(0, 3);
  const invoiceNo = dupBilled.invoiceNo || dupBilled.invoiceNumber || null;
  const amount = Number(dupBilled.grandTotal != null ? dupBilled.grandTotal : (dupBilled.total || 0));
  // The bucket above is exhaustive, so dupBilled can be a row that is genuinely
  // billed OR one that merely isn't an open draft/ready (an odd/legacy status).
  // Saying "already billed" about a row we can't show an invoice or a status for
  // is a claim we haven't earned -- and an operator who catches this dialog lying
  // once stops reading it. State only what the row supports.
  const isBilled = isTicketInvoiced(dupBilled) || dupBilled.status === 'invoiced' ||
    dupBilled.status === 'archived' || dupBilled.status === 'locked';
  const state = invoiceNo ? `invoiced (${invoiceNo})` : (dupBilled.status || 'already on file');
  // The rows this tier can reach include pre-allocator ones with no ticketNo
  // (TASK-1339), and "Ticket #undefined" is the kind of detail that makes an
  // operator distrust the whole warning.
  const label = dupBilled.ticketNo ? `#${dupBilled.ticketNo}` : 'An earlier ticket';
  return { action: 'confirm', dupBilled, isBilled, state, label, invoiceNo, amount, twinNotes, open, billed };
}

// TASK-1346 part 2 — "was this billed OUTSIDE DispatchDeck?" One predicate, because
// the answer decides whether we may push this ticket at anyone's accounting system
// (View Invoice / QB .iif / PIDX OpenInvoice / QBO sync all refuse it).
//
// It is NOT a bare `invoicedExternally === true` read, and that is the whole point:
// `uninvoiceTicket` clears invoiced/invoiceNumber/invoicedAt but CANNOT clear
// invoicedExternally/externalInvoiceRef — neither name is in the firestore.rules
// locked-ticket lifecycle allowlist, and uninvoice only ever runs on an invoiced
// ticket, so adding them would make hasOnly() reject the entire uninvoice write.
// So the flag outlives the billing it describes. Requiring the CURRENT invoice
// number to still be the EXT- number this flow wrote is what makes the stale flag
// harmless: mark-external → uninvoice → invoice in DD overwrites invoiceNumber with
// INV-YYYYMM-NNN, and the ticket correctly reads as DispatchDeck-billed again.
function isBilledExternally(t) {
  t = t || {};
  return t.invoicedExternally === true && String(t.invoiceNumber || '').startsWith('EXT-');
}

// TASK-9654 — a ticket LINKED to a QuickBooks invoice (`qboLink`, written only
// by the server: the qboLinkTicketInvoice callable or the one-time 2026 apply).
// Its DD invoice (INV-…) is real and stays viewable, but the biller already
// re-keyed it into QuickBooks — so every path that carries an invoice or a
// payment INTO QuickBooks (Sync to QB, .iif, payment .iif, CSV/PIDX batch
// export) treats it exactly like a "Billed elsewhere" ticket. Mirrors
// hasQboInvoiceLink in functions/src/services/qboInvoiceLink.js (pinned by
// tests/task9654-qbo-invoice-link.unit.cjs); qboPushInvoice refuses it server-side.
function hasQboInvoiceLink(t) {
  const l = t && t.qboLink;
  if (l == null) return false;
  if (typeof l !== 'object') return true;
  return l.on === true || (l.docNumber != null && String(l.docNumber).trim() !== '') || (l.invoiceId != null && String(l.invoiceId).trim() !== '');
}
// "Already in QuickBooks" — billed there (EXT) or linked to its invoice there.
function isAlreadyInQuickBooks(t) {
  return isBilledExternally(t) || hasQboInvoiceLink(t);
}
// TASK-9537 — already PUSHED to QuickBooks by DD (the server refuses a second push).
// Mirrors pushedQboId in functions/src/services/qboInvoicePushGuard.js: a real
// id, or synced outside the sandbox stub. A STUB_ id / qboSandbox is NOT a push.
function qboPushedId(t, field = 'qboInvoiceId') {
  if (!t || typeof t !== 'object') return null;
  const id = t[field] == null ? '' : String(t[field]).trim();
  if (id && !id.startsWith('STUB_')) return id;
  if (t.qboSynced === true && t.qboSandbox !== true && !id.startsWith('STUB_')) return '(unknown id)';
  return null;
}
function _qboLinkDocNo(t) {
  const l = t && t.qboLink;
  return l && typeof l === 'object' && l.docNumber ? String(l.docNumber) : '';
}
// The small "QB #N" chip, plus "QB $X / DD $Y" when the paid sync found the
// QuickBooks total differs from DD (Chris's ask 1). BI keeps the DD amount
// until TASK-9418 decides otherwise — the title says so. Totals differing is
// all it claims (a taxable-line difference can hide in it).
function _qboLinkChipHTML(t, cls) {
  const n = _qboLinkDocNo(t);
  const d = t && t.qboAmountDiff && typeof t.qboAmountDiff === 'object' ? t.qboAmountDiff : null;
  const diff = d && Number.isFinite(Number(d.qbo)) && Number.isFinite(Number(d.dd))
    ? ` · QB ${escapeHtml(formatCurrency(Number(d.qbo)))} / DD ${escapeHtml(formatCurrency(Number(d.dd)))}` : '';
  if (!n && !diff) return '';
  const title = diff
    ? 'QuickBooks invoice total differs from the DispatchDeck ticket total. Reports keep the DispatchDeck amount.'
    : 'This ticket is linked to its QuickBooks invoice; the paid sync reads it from there.';
  return `<span class="${cls || 'btn btn-sm btn-secondary'}" style="cursor:default;${diff ? 'border-color:#F59E0B;color:#92400E;' : ''}" title="${escapeHtml(title)}">` +
    `<span class="material-icons">${diff ? 'difference' : 'link'}</span> ${n ? `QB #${escapeHtml(n)}` : 'QuickBooks'}${diff}</span>`;
}

// TASK-1548 — "priced equipment with no hours" is never intentional. job_generate
// tickets are born with EQUIPMENT lines at hours:0 / no clockEntryId and NOTHING is
// wired to ever fill them (16 tickets over 4 months; #100464 was INVOICED at $0 for
// $375 of real hauling). The ticket renders indistinguishable from a deliberate $0,
// which is exactly why the biller shipped it. This predicate is the gate: a line that
// matches BLOCKS marking the ticket ready/invoiced until the biller either enters
// hours or explicitly marks the line non-billable (`zeroHoursOk` — the Dove #03
// "support unit / consolidated onto another ticket" pattern, made distinguishable
// in data from "nobody typed them").
//
// Deliberately NARROW (each exclusion protects a legitimate flow):
//   • per-unit lines (non-time-based rateUnit) bill on QUANTITY, and returned
//     rentals are deliberately qty:0/'Each' (TASK-730a) — blocking them would trap
//     the rental-return flow, so they are out of this gate's scope entirely;
//   • rate <= 0 is TASK-1165's "unpriced equipment" lane, not this one;
//   • lineAmount > 0 means money is already on the line (nothing is silently $0);
//   • includeOnInvoice === false is an existing explicit "don't bill this" choice.
// We NEVER auto-derive the hours from punches here — auto-deriving silently is how
// the wrong number gets billed confidently (see the TASK-1548 board entry).
// (Scope note, TASK-1646: this bans THIS PREDICATE deriving from clock punches to
// clear its own gate. Deriving hours from explicitly-set from/to times is a
// different thing — updateReviewTime, the load self-heal, and the Add Equipment
// time-mirror all do it via _recalcLineFromTimes, visibly, at the moment of edit.)
//
// 🔴 TWIN: clock.js `equipmentLineNeedsHours` (Timecard Exceptions detection arm)
// must stay byte-identical — tests/task1548-zero-hour-equipment.test.mjs asserts it.
function equipmentLineNeedsHours(line) {
  const l = line || {};
  if (l.type !== 'EQUIPMENT') return false;
  if (l.zeroHoursOk === true) return false;         // marked deliberately non-billable
  if (l.includeOnInvoice === false) return false;   // explicitly excluded from the invoice
  if (!((Number(l.rate) || 0) > 0)) return false;   // unpriced — TASK-1165's lane
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return false; // per-unit bills on quantity
  if ((Number(l.hours) || 0) > 0) return false;
  if ((Number(l.lineAmount) || 0) > 0) return false;
  return true;
}

// TASK-1405 — the SURVIVING half of the same hole, and the exact mirror image of
// the predicate above: a rate-0 EQUIPMENT line carrying REAL HOURS. The machine
// ran (the hours prove it), the rate is zero, the line bills $0 — and nothing
// catches it today:
//   • equipmentLineNeedsHours deliberately skips it (its rate is not > 0);
//   • the $0-total gate below never fires — the ticket's OTHER lines total > $0;
//   • the TASK-1165 "UNPRICED" badge needs the SAME asset to carry a priced line
//     too, so a lone unpriced asset shows nothing at all.
// So the ticket reads complete — named driver, real hours, real shift note — and
// sails to invoice with the machine's whole day priced at nothing.
//
// Same shape, same exclusions, hours and rate SWAPPED:
//   • hours > 0 is what makes it a leak (hours 0 AND rate 0 is an empty
//     placeholder, not lost revenue — nothing there says work happened);
//   • lineAmount > 0 means money is already on the line;
//   • rate > 0 is the hours gate's lane, not this one;
//   • per-unit lines bill on QUANTITY (returned rentals are deliberately
//     qty:0/'Each', TASK-730a) — out of scope entirely, same as above;
//   • zeroHoursOk / includeOnInvoice === false are the SAME human overrides the
//     hours gate honors. Reused ON PURPOSE rather than inventing a second flag:
//     firestore.rules already pins that whole field family to billers on every
//     member and driver arm (rules 4148 / 4199 / 4252 / 4350), so the release
//     valve inherits its authorization instead of shipping an unpinned one.
//
// 🔴 EMPLOYEE lines are out of scope BY TYPE and must stay that way: TITS bills
// the truck, never the person, so an EMPLOYEE line at rate 0 with real hours is
// CORRECT and deliberate (jobboard.js writes it that way).
// Nothing is ever auto-priced here — silently adopting a sibling's rate was
// rejected as an over-billing exploit (TASK-1165 COD). The human picks the
// number, or records the $0 as deliberate, then Saves.
//
// 🔴 TWIN: clock.js `equipmentLineUnpriced` (Timecard Exceptions detection arm)
// must stay byte-identical — tests/task1548-zero-hour-equipment.test.mjs asserts it.
function equipmentLineUnpriced(line) {
  const l = line || {};
  if (l.type !== 'EQUIPMENT') return false;
  if (l.zeroHoursOk === true) return false;         // marked deliberately non-billable
  if (l.includeOnInvoice === false) return false;   // explicitly excluded from the invoice
  if ((Number(l.rate) || 0) > 0) return false;      // priced — the zero-hours gate's lane
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return false; // per-unit bills on quantity
  if (!((Number(l.hours) || 0) > 0)) return false;  // no hours: nothing proves the machine ran
  if ((Number(l.lineAmount) || 0) > 0) return false;
  return true;
}

function _zeroHourOffenders(lines) {
  return (lines || []).filter(equipmentLineNeedsHours);
}

function _unpricedOffenders(lines) {
  return (lines || []).filter(equipmentLineUnpriced);
}

// Authoritative read for the gates below: fetch the ticket's lines FRESH from
// Firestore (never trust a cached list — the whole failure mode here is state that
// looks complete). Throws on read failure so callers fail CLOSED with a retry
// message rather than waving un-verified money through (house pattern: the
// showConfirm-unavailable branch in generateTicketFromJob fails closed too).
async function _fetchZeroHourOffenders(ticketId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  return _zeroHourOffenders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

function _zeroHourLineLabel(l) {
  const name = getEquipmentDisplayName(l) || l.assetName || 'Equipment';
  return `${name} ($${Number(l.rate) || 0}/${l.rateUnit || 'Hourly'}, 0 hours)`;
}

// The shared blocking gate. Returns true = proceed, false = blocked (dialog/toast
// already shown). `actionLabel` reads like "mark this ticket Ready" / "invoice this
// ticket". Fails CLOSED when the lines can't be read — with a message that says so.
async function _zeroHourGate(ticketId, actionLabel) {
  let offenders;
  try {
    offenders = await _fetchZeroHourOffenders(ticketId);
  } catch (err) {
    console.error('[Billing] Zero-hour gate: line read failed for', ticketId, err);
    window.showToast?.(`Couldn't verify equipment hours — check your connection and try again before you ${actionLabel}.`, 'error');
    return false;
  }
  if (offenders.length === 0) return true;
  const t = BillingState.tickets.find(x => x.id === ticketId);
  const tno = t?.ticketNo ? `#${t.ticketNo} ` : '';
  const list = offenders.map(_zeroHourLineLabel).join(' · ');
  const msg = `Ticket ${tno}has priced equipment with no hours: ${list}. ` +
    `A priced line with 0 hours invoices real work at $0. ` +
    `Enter the hours — or mark the line non-billable in Review Ticket — before you ${actionLabel}.`;
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      'Equipment hours are missing',
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// TASK-1405 — the unpriced twin of the three helpers above. Same authoritative
// FRESH read (the whole failure mode is state that LOOKS complete), same
// fail-CLOSED-on-read-error contract.
async function _fetchUnpricedOffenders(ticketId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  return _unpricedOffenders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

function _unpricedLineLabel(l) {
  const name = getEquipmentDisplayName(l) || l.assetName || 'Equipment';
  return `${name} (${Number(l.hours) || 0} hrs at $0/${l.rateUnit || 'Hourly'})`;
}

// The shared blocking gate for the unpriced shape. Mirrors _zeroHourGate exactly:
// fresh read, fails CLOSED on a read error, offers "Open Review Ticket" so the
// refusal always LANDS somewhere the biller can act (TASK-428 — a denial with no
// route out is worse than the bug it prevents). Two ways out, both one click from
// that dialog: type the rate, or "Mark non-billable" (a recorded, biller-only
// zeroHoursOk decision with who/when/why).
// Internal-time customers are exempt for the same reason they are exempt from the
// $0-total gate: their $0 lines are the correct answer, not a leak.
async function _unpricedGate(ticketId, actionLabel) {
  const _t0 = BillingState.tickets.find(x => x.id === ticketId);
  if (_ticketCustomerIsInternalTime(_t0)) return true;
  let offenders;
  try {
    offenders = await _fetchUnpricedOffenders(ticketId);
  } catch (err) {
    console.error('[Billing] Unpriced-equipment gate: line read failed for', ticketId, err);
    window.showToast?.(`Couldn't verify equipment rates — check your connection and try again before you ${actionLabel}.`, 'error');
    return false;
  }
  if (offenders.length === 0) return true;
  const tno = _t0?.ticketNo ? `#${_t0.ticketNo} ` : '';
  const list = offenders.map(_unpricedLineLabel).join(' · ');
  const msg = `Ticket ${tno}has equipment that ran with no rate: ${list}. ` +
    `The hours say the machine worked, so those hours bill $0 as-is. ` +
    `Enter the rate — or mark the line non-billable in Review Ticket — before you ${actionLabel}.`;
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      'Equipment has no rate',
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// TASK-1567 — a $0 ticket must never become a customer-facing invoice. THREE $0
// invoices (INV-202608-055 / -059 / -062) reached real customers for real,
// documented work: each ticket rendered as complete (named driver, real shift
// note) yet every billable line summed to $0 — the truck/asset that did the work
// was never on the ticket, or the only lines were non-billable labor. A $0 invoice
// to an outside customer is never correct, and the biller had no visual signal.
//
// The `_zeroHourGate` above catches ONE narrow shape (a priced EQUIPMENT line with
// 0 hours). This gate is the broad complement: the WHOLE ticket's billable total is
// $0 (which also covers "no billable lines at all"). It is computed the SAME way the
// invoice is — computeTicketTotalsFromLines, summing each line's persisted
// lineAmount — so the man-hour rule is honored for free: a non-billable EMPLOYEE
// line (TITS bills the truck, never the person) stores lineAmount 0, so a labor-only
// ticket whose asset was never billed totals $0 and is blocked; a tenant that DOES
// bill labor stores a real lineAmount and passes. No hardcoded "labor is free"
// assumption — the stored amounts ARE the truth (no TITS-special).
//
// Carve-out: internalTimeOnly customers (Shop / Office / yard) legitimately produce
// $0 records — internal time tracking, never invoiced to an outside party — so they
// are exempt. Mirrors clock.js TASK-1175 detection (flag first, ^(shop|office|
// internal|yard)$ name fallback ONLY when the flag is undefined). In practice these
// customers don't even get a ticket at clock-in (clock.js), but the carve-out keeps
// the gate from trapping the flag-flipped-after-the-fact edge.
const _ZT_INTERNAL_NAME_FALLBACK = /^(shop|office|internal|yard)$/i;
function _ticketCustomerIsInternalTime(ticket) {
  const cust = ticket && ticket.customerId
    ? (BillingState.customers || []).find(c => c.id === ticket.customerId)
    : null;
  if (!cust) return false;
  if (cust.internalTimeOnly === true) return true;
  if (cust.internalTimeOnly === undefined && _ZT_INTERNAL_NAME_FALLBACK.test(cust.name || '')) return true;
  return false;
}

// Authoritative billable total from a FRESH line read (never the stored grandTotal —
// generateInvoiceNumber stamps the invoice markers WITHOUT rewriting grandTotal, and
// the self-heal only runs on render, so the stored value can lag; the whole failure
// mode here is state that LOOKS complete). Uses the exact invoice math. Throws on a
// read failure so callers fail CLOSED. This is also why the enforcement lives in the
// client and not firestore.rules: rules cannot sum a subcollection, and a rule keyed
// on the stored grandTotal would wrongly DENY a legit invoice whose grandTotal is
// merely stale — same reason TASK-1548 landed client-side.
async function _fetchTicketBillableTotal(ticketId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const ticket = BillingState.tickets.find(t => t.id === ticketId) || {};
  const gross = computeTicketTotalsFromLines(lines, ticket).grandTotal;
  // TASK-1567 (COD Code Reviewer): the gate must use the CUSTOMER-INVOICE total,
  // not the raw line sum. Lines hidden from the invoice — includeOnInvoice===false
  // or the customer's invoiceExcludeLineTypes default — never reach the customer,
  // so a ticket whose only positive lines are invoice-excluded still produces a $0
  // invoice. Subtract the excluded amount with the SAME shared predicate the render
  // uses (getInvoiceExcludeLineTypes → excludedInvoiceAmount/isLineInvoiced) so the
  // gate == PDF == displayed total. PRELOAD the customer's contract prefs first
  // (the gate can run before the render warms the cache — 2nd COD cold-cache fix);
  // getInvoiceExcludeLineTypes reads that cache, so without this an all-excluded
  // ticket would read []→gross and slip through.
  if (ticket.customerId) { try { await loadReviewContractRates(ticket.customerId); } catch (_) {} }
  const exTypes = getInvoiceExcludeLineTypes(ticket.customerId);
  return gross - excludedInvoiceAmount(lines, exTypes);
}

// Returns { blocked, total }. blocked=true means the ticket's billable total is $0
// (or has no billable lines) AND the customer is not internal-time. Short-circuits
// the fresh read for the internal-time carve-out. Throws on read failure (callers
// fail closed).
async function _zeroTotalOffense(ticketId) {
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (_ticketCustomerIsInternalTime(ticket)) return { blocked: false, total: null };
  const total = await _fetchTicketBillableTotal(ticketId);
  return { blocked: !((Number(total) || 0) > 0), total };
}

// The shared blocking gate for a SINGLE ticket. Returns true = proceed, false =
// blocked (dialog/toast already shown). Mirrors _zeroHourGate: fresh read, fails
// CLOSED on a read error, offers Open Review Ticket. `actionLabel` reads like
// "invoice this ticket" / "record this ticket as billed".
async function _zeroTotalGate(ticketId, actionLabel) {
  let res;
  try {
    res = await _zeroTotalOffense(ticketId);
  } catch (err) {
    console.error('[Billing] Zero-total gate: line read failed for', ticketId, err);
    window.showToast?.(`Couldn't verify the ticket total — check your connection and try again before you ${actionLabel}.`, 'error');
    return false;
  }
  if (!res.blocked) return true;
  const t = BillingState.tickets.find(x => x.id === ticketId);
  const tno = t?.ticketNo ? `#${t.ticketNo} ` : '';
  const msg = `Ticket ${tno}has nothing to bill — its lines total $0. ` +
    `A $0 invoice to a customer is never correct: the truck or asset that did the work is likely missing from the ticket, ` +
    `or the hours/amounts were never entered. Add the billable lines in Review Ticket before you ${actionLabel}. ` +
    `(For genuine internal time, set the customer to internal-time-only.)`;
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      'Nothing to bill on this ticket',
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// ============================================
// TASK-1572 Slices 3 + 3b — TICKET COMPLETENESS AT READY
// ============================================
// "Ready" means "a biller looked and this can go to the customer." Two things
// made it a lie, and neither had an owner:
//
//   3.  NO BILLABLE LINE. The $0 gate above (TASK-1567) only runs at INVOICE.
//       Ready ran the zero-hour, unpriced and mileage gates — all of which need a
//       priced line to EXIST — so a ticket with labor hours and no truck at all
//       (category C: 21 of TITS's 57 non-internal $0 tickets) sailed to Ready and
//       sat there looking finished. #100472 and #100478 were both Ready.
//   3b. REQUIRED FIELDS. customers/{id}.requiredFields (Loenbro ["poNumber"], and
//       14 other customers on two tenants, measured 2026-09-28) only HIGHLIGHTED
//       an empty box. 14 tickets since 8/01 reached ready/invoiced without the PO
//       their customer requires (Lacey: "how can we call a ticket Ready if it
//       doesn't have a PO?").
//
// 🔑 NO NEW SETTING — every question is answered by data that already exists:
//   • Is $0 legitimate for this customer?  customers.internalTimeOnly (TASK-1175)
//   • Is THIS $0 deliberate?               `zeroHoursOk` on a line — the biller-
//                                          only, rules-pinned release valve
//                                          (TASK-1548's Dove #03 pattern: a support
//                                          unit consolidated onto another ticket)
//   • What is this customer billed FOR?    their Price Book: an active line with a
//                                          rate > 0. NO priced Price Book = nothing
//                                          to derive = the arm never fires. That is
//                                          what keeps a tenant that does not price
//                                          through DispatchDeck from being walled
//                                          off from its own Ready button.
//   • What must be on the paperwork?       customers.requiredFields
//
// 🔴 READY ONLY for the billable arm. The invoice already has its own $0 gate
// (client) AND a server backstop (ticketGateway markInvoiced, TASK-1583). Chris's
// call on 8/10 still stands for PREP: a missing PO never stops the truck and never
// blocks a driver or a draft. It stops the word "Ready".
//
// 🔴 NEVER A CLOCK-OUT. This file is the biller's surface; nothing here is reachable
// from a driver session.

const _TC_REQUIRED_FIELD_LABELS = {
  poNumber: 'PO Number',
  vendorNumber: 'Vendor #',
  afeNumber: 'AFE / Cost Center',
  wellName: 'Well Name',
  wellNumber: 'Well #',
  location: 'Location',
  workPerformed: 'Work Performed',
  notes: 'Notes',
};

// Same edge-whitespace class as utils/poField.js normalizePoForSave: NBSP and the
// zero-width characters are not "a PO". Internal whitespace is left alone.
const _TC_EDGE_WS = /^[\s  -​﻿]+|[\s  -​﻿]+$/g;
function _tcFilled(v) {
  if (v == null) return false;
  return String(v).replace(_TC_EDGE_WS, '') !== '';
}

// PURE. Which of the customer's requiredFields are empty on this ticket?
// Returns the missing KEYS in the customer's own order.
//   • A written exception counts as filled — the crew already writes
//     "no po required per eric" into the box, and that is an answer.
//   • workPerformed / notes are satisfied by the narrative ANYWHERE it renders to
//     the customer: the ticket note OR any line's note. On TITS the per-asset line
//     note is the MAJORITY surface (332 of 482 invoiced tickets — see
//     scripts/ticket-prep-report.cjs), so a header-only check would block tickets
//     whose invoice plainly carries the description.
//   • A key this code cannot evaluate is skipped, never guessed at.
function ticketMissingRequiredFields(ticket, customer, lines) {
  const req = Array.isArray(customer?.requiredFields) ? customer.requiredFields : [];
  if (req.length === 0) return [];
  const t = ticket || {};
  const hasNarrative = _tcFilled(t.workPerformed) || _tcFilled(t.notes)
    || (Array.isArray(lines) ? lines : []).some(l => _tcFilled(l && l.notes));
  const missing = [];
  for (const key of req) {
    if (!Object.prototype.hasOwnProperty.call(_TC_REQUIRED_FIELD_LABELS, key)) continue;
    if (missing.includes(key)) continue;
    if (key === 'workPerformed' || key === 'notes') {
      if (!hasNarrative) missing.push(key);
      continue;
    }
    if (!_tcFilled(t[key])) missing.push(key);
  }
  return missing;
}

// PURE. Does this customer's Price Book bill for anything at all? An active line
// (absent `active` reads as active — the rate matcher treats it the same way) with
// a rate > 0. TITS prices equipment and carries every Personnel line at rate 0 /
// inactive; show-account prices a CDL Driver day rate. Both answer "yes" here —
// the arm below then asks whether the TICKET bills, so a labor-billing tenant's
// labor-only ticket (which carries a real lineAmount) passes on its own merits.
function priceBookBillsSomething(lineItems) {
  return (Array.isArray(lineItems) ? lineItems : [])
    .some(li => !!li && li.active !== false && (Number(li.rate) || 0) > 0);
}

// PURE. The ticket-level sibling of equipmentLineNeedsHours (COD Blast, 2026-08-13:
// "extend the existing gate, do not fork it" — it shares that predicate's release
// valve instead of inventing a new one). True = this ticket has no billable line for
// a customer who is billed for something.
function ticketHasNoBillableLine({ internalTime, priceBookBills, total, lines }) {
  if (internalTime === true) return false;           // Shop / Office / yard: $0 is right
  if (priceBookBills !== true) return false;          // no priced Price Book: nothing to derive
  if ((Array.isArray(lines) ? lines : []).some(l => l && l.zeroHoursOk === true)) return false;
  return !((Number(total) || 0) > 0);
}

// PURE. Everything that stops this ticket being called Ready, as sentences a
// biller can act on. Empty array = complete.
function ticketCompletenessProblems({ ticket, customer, lines, total, priceBookBills, internalTime, checkBillable }) {
  const out = [];
  if (checkBillable !== false && ticketHasNoBillableLine({ internalTime, priceBookBills, total, lines })) {
    out.push('It has nothing to bill — no line on it carries money, but this customer\'s Price Book bills for work. The truck or asset that did the job is probably missing. If $0 really is right, mark the line non-billable in Review Ticket.');
  }
  const missing = ticketMissingRequiredFields(ticket, customer, lines);
  if (missing.length > 0) {
    const who = customer?.name || 'This customer';
    // TASK-9705 — name where the rule lives. A box ticked by accident on the
    // customer read as an unexplained ticket problem with no way to undo it.
    out.push(`${who} requires ${missing.map(k => _TC_REQUIRED_FIELD_LABELS[k]).join(', ')} and ${missing.length === 1 ? 'it is' : 'they are'} empty. A written exception in the box ("no PO per Eric") counts. This is set on the customer, not the ticket: CRM → Edit Customer → Billing → Required Fields.`);
  }
  return out;
}

// Gather the inputs from a FRESH read (the failure mode is state that LOOKS
// complete). `headerOverrides` carries values the caller is about to write in the
// same save (the edit-ticket modal's own PO box), so the gate judges the ticket as
// it WILL be, not as it was. Throws on a read failure — callers fail closed.
async function _fetchTicketCompleteness(ticketId, headerOverrides, opts) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const tSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
  const ticket = { id: ticketId, ...(tSnap.exists() ? tSnap.data() : {}), ...(headerOverrides || {}) };
  const customer = ticket.customerId
    ? (BillingState.customers || []).find(c => c.id === ticket.customerId) || null
    : null;
  const internalTime = _ticketCustomerIsInternalTime(ticket);
  const lSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  const lines = lSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const checkBillable = !(opts && opts.checkBillable === false);
  let priceBookBills = false;
  let total = null;
  if (checkBillable && !internalTime && ticket.customerId) {
    const pb = await loadReviewContractRates(ticket.customerId);
    // loadReviewContractRates swallows its own errors into null. With a customerId
    // in hand, null can only mean the read failed — "could not check" is not "no
    // Price Book", so fail CLOSED rather than wave an unverified ticket to Ready.
    if (!pb) throw new Error('price book read failed');
    priceBookBills = priceBookBillsSomething(pb.lineItems);
    if (priceBookBills) {
      // Same invoice math as _fetchTicketBillableTotal, on the lines already read.
      const gross = computeTicketTotalsFromLines(lines, ticket).grandTotal;
      total = gross - excludedInvoiceAmount(lines, getInvoiceExcludeLineTypes(ticket.customerId));
    }
  }
  return { ticket, customer, lines, total, priceBookBills, internalTime, checkBillable };
}

// The shared blocking gate for the Ready transitions that do not own an in-memory
// copy of the ticket (the detail view's Mark Ready, the edit-ticket modal's Status
// select). Mirrors _zeroHourGate: fresh read, fails CLOSED on a read error, offers
// Open Review Ticket so the refusal always lands somewhere the biller can act.
// opts.checkBillable:false runs the requiredFields arm only (the invoice path, whose
// $0 arm is _zeroTotalGate + the server backstop).
async function _ticketCompletenessGate(ticketId, actionLabel, headerOverrides, opts) {
  let problems;
  try {
    problems = ticketCompletenessProblems(await _fetchTicketCompleteness(ticketId, headerOverrides, opts));
  } catch (err) {
    console.error('[Billing] Ticket completeness gate: read failed for', ticketId, err);
    window.showToast?.(`Couldn't verify the ticket is complete — check your connection and try again before you ${actionLabel}.`, 'error');
    return false;
  }
  if (problems.length === 0) return true;
  const t = BillingState.tickets.find(x => x.id === ticketId);
  const tno = t?.ticketNo ? `#${t.ticketNo} ` : '';
  const msg = `Ticket ${tno}isn't complete yet. ${problems.join(' ')} Fix it in Review Ticket before you ${actionLabel}.`;
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      'Ticket is not complete',
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// The Review Ticket modal's in-memory twin of _fetchTicketCompleteness. The header
// is the ticket merged with this session's unsaved header edits; the lines are the
// modal's own arrays. A Price Book read failure is reported as a problem (so the
// save demotes to Draft) — "could not check" never reads as "complete".
async function _reviewCompletenessProblems() {
  const ticket = { ...(ReviewState.ticket || {}), ...(ReviewState.headerEdits || {}) };
  delete ticket.status;
  const customer = ticket.customerId
    ? (BillingState.customers || []).find(c => c.id === ticket.customerId) || null
    : null;
  const lines = [
    ...(ReviewState.employeeLines || []), ...(ReviewState.equipmentLines || []),
    ...(ReviewState.permitLines || []), ...(ReviewState.fieldItemLines || []),
    ...(ReviewState.downtimeLines || []), ...(ReviewState.standbyLines || []),
    ...(ReviewState.miscLines || []), ...(ReviewState.potholeLines || [])
  ];
  const internalTime = _ticketCustomerIsInternalTime(ticket);
  let priceBookBills = false;
  let total = null;
  if (!internalTime && ticket.customerId) {
    const pb = await loadReviewContractRates(ticket.customerId);
    if (!pb) return ['Couldn\'t check this customer\'s Price Book — check your connection and try again.'];
    priceBookBills = priceBookBillsSomething(pb.lineItems);
    if (priceBookBills) total = calcReviewSubtotal();
  }
  return ticketCompletenessProblems({ ticket, customer, lines, total, priceBookBills, internalTime, checkBillable: true });
}

// ============================================
// TASK-1615-B — "PER MILE" QUANTITY OFF A BAD ROUTE COORDINATE
// ============================================
// generateTicketFromJob auto-fills the quantity on a "Per Mile" ticket line from
// window.DispatchModule.calcRouteMileage(job.waypoints) — a haversine sum over
// whatever coordinates are stored on the job. Live job rows carry integer/garbage
// latitudes (one is 5304, no decimal point anywhere), so that quantity can reach a
// customer invoice as a multi-thousand-mile number.
//
// calcRouteMileage is deliberately NOT tightened — dispatch.js carries the
// reasoning at its definition. Both call sites read it as `if (routeMiles) qty =
// ...` with the quantity defaulting to 0, so returning null for a bad coordinate
// would convert a garbage-but-VISIBLE overcharge into a silent $0 line. BOTH
// shapes are already reachable today, depending on the garbage:
//   • lat 5304 / lng 105 — both truthy, haversine runs → inflated quantity.
//   • lat "abc" — truthy, haversine yields NaN → calcRouteMileage returns null →
//     quantity 0 → the line bills $0 for miles that were really driven.
// A "safer" number is not available: nothing in this app knows the real mileage.
// So the number is left EXACTLY as it is — nothing historical is rewritten, and
// no new line is silently zeroed — and the fix is that the person doing the
// invoicing SEES it: the Review Ticket screen names the bad stop, and
// Ready/invoice is blocked until a human types the real miles or records that
// they checked the number.
//
// Out of scope, observed while building this: a stop that was NEVER geocoded
// (empty lat/lng) also drives calcRouteMileage to null → quantity 0 → a $0
// mileage line. That is a missing-data problem on every job rather than a
// garbage-coordinate one, and flagging it here would fire on every per-mile line
// a biller simply has not filled in yet.
//
// 🔴 NOT rules-enforced: `routeMilesOk` is a gate-escape field exactly like
// `zeroHoursOk`, but firestore.rules pins the zeroHours* family out of the
// member/driver write arms and this family is NOT in that list. Follow-up: add
// routeMilesOk / routeMilesSuspect / routeMilesAutoQty / routeMilesMarked* to the
// same hasAny([...]) pins on ticketLines create+update. The gate itself is
// client-side regardless (TASK-1548 documents why), so this is a hardening gap,
// not the difference between enforced and unenforced.

// Mirror of plottableCoord() in public/src/views/dispatch.js (TASK-1615): finite
// numbers, |lat| <= 90, |lng| <= 180, the exact 0,0 sentinel rejected, and
// null/'' rejected so a missing longitude cannot coerce to 0. It is a local copy
// because dispatch.js is only reachable from here through a ?v= cache-busted
// import, which would instantiate a SECOND copy of that module (CLAUDE.md pitfall
// #1), and plottableCoord is not on the window.DispatchModule surface today.
// _rmPlottableCoord prefers the shared global the moment dispatch.js exports it,
// so the two stop being able to drift then; until then, keep this copy in sync by
// hand — a looser copy here means a bad coordinate the map rejects gets billed.
const _RM_NULL_ISLAND_EPS = 1e-7;
function _rmCoordToNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return NaN;
}
function _rmPlottableCoordLocal(wp) {
  if (!wp) return null;
  const lat = _rmCoordToNumber(wp.lat);
  const lng = _rmCoordToNumber(wp.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat > 90 || lat < -90) return null;
  if (lng > 180 || lng < -180) return null;
  if (Math.abs(lat) < _RM_NULL_ISLAND_EPS && Math.abs(lng) < _RM_NULL_ISLAND_EPS) return null;
  return { lat, lng };
}
function _rmPlottableCoord(wp) {
  const shared = window.DispatchModule?.plottableCoord;
  if (typeof shared === 'function') {
    try { return shared(wp); } catch (_) { /* fall through to the local copy */ }
  }
  return _rmPlottableCoordLocal(wp);
}
function _rmCoordWasProvided(wp) {
  const filled = v => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '');
  return !!wp && (filled(wp.lat) || filled(wp.lng));
}
// A stop that carries a coordinate we refuse to trust. "Never geocoded" is a
// different (and deliberately unflagged) case — see the block comment above.
function _rmCoordIsBad(wp) {
  return _rmCoordWasProvided(wp) && !_rmPlottableCoord(wp);
}

// The stops on a job whose stored coordinates are garbage → [{ index, name }].
// Under 2 waypoints calcRouteMileage returns null before doing any math, so no
// quantity was ever auto-filled and there is nothing to warn about.
function _rmBadStops(job) {
  const wps = job && Array.isArray(job.waypoints) ? job.waypoints : [];
  if (wps.length < 2) return [];
  const out = [];
  wps.forEach((wp, i) => {
    if (_rmCoordIsBad(wp)) out.push({ index: i, name: (wp && wp.name) || `Destination ${i + 1}` });
  });
  return out;
}
function _rmBadStopNames(badStops) {
  return (badStops || []).map(s => s.name).join(', ');
}

// The SAME unit test the auto-fill uses in generateTicketFromJob. Shared on
// purpose: if the two ever drift, a line gets a mileage quantity that this gate
// cannot see, which is the whole failure mode in one step.
const _RM_PER_MILE_RE = /per\s*mile/i;
function _rmIsPerMileUnit(unit) {
  return !!unit && _RM_PER_MILE_RE.test(unit);
}

// A per-mile line that is carrying real money off an untrustworthy route.
// Deliberately narrow so this never becomes background noise:
//   • rate must be > 0 — a $0-rate line bills nothing whatever the quantity says.
//   • includeOnInvoice === false never reaches the customer.
//   • routeMilesOk === true is the biller's recorded "I checked this" (mirrors
//     zeroHoursOk, with who/when/why).
//   • a quantity the biller CHANGED from the auto-filled value is theirs, not
//     ours — typing the real miles is the actual fix and clears this with no
//     extra click. Lines created before this shipped carry no routeMilesAutoQty
//     to compare against, so they stay flagged until someone acknowledges them.
function _rmLineIsSuspect(line, routeIsBad) {
  const l = line || {};
  if (!_rmIsPerMileUnit(l.rateUnit)) return false;
  // An acknowledgement only counts when it carries its PROVENANCE. markRouteMileageOk
  // always writes routeMilesMarkedBy + routeMilesMarkedAt with the flag, so a bare
  // `routeMilesOk: true` was not written by a human clicking "Mileage is correct" —
  // and an acknowledgement by nobody must not silence a money warning. This is a
  // correctness rule first (an unattributed sign-off is not a sign-off), and it also
  // raises the bar on the rules gap flagged in the block comment above: a one-field
  // raw-SDK write no longer suppresses the banner.
  // 🔴 It is NOT a substitute for pinning the routeMiles* family in firestore.rules.
  // Anyone who can forge the flag can forge two more fields alongside it. The real
  // fix is the rules pin; do not read this line as closing that hole.
  if (l.routeMilesOk === true && l.routeMilesMarkedBy && l.routeMilesMarkedAt) return false;
  if (l.includeOnInvoice === false) return false;
  if (!((Number(l.rate) || 0) > 0)) return false;
  if (l.routeMilesSuspect !== true && !routeIsBad) return false;
  if (l.routeMilesAutoQty !== null && l.routeMilesAutoQty !== undefined
      && Number(l.quantity) !== Number(l.routeMilesAutoQty)) return false;
  return true;
}
function _rmSuspectLines(lines, routeIsBad) {
  return (lines || []).filter(l => _rmLineIsSuspect(l, routeIsBad));
}

// True when a ticket has per-mile money at all. The cheap exit for every gate
// below: on a ticket with no priced, invoiced per-mile line this whole feature
// costs zero extra reads and can never block anything.
function _rmHasPerMileMoney(lines) {
  return (lines || []).some(l => _rmIsPerMileUnit(l.rateUnit)
    && (Number(l.rate) || 0) > 0
    && l.includeOnInvoice !== false);
}

function _rmLineLabel(l) {
  // getEmployeeDisplayName honours the "show names" privacy flag — never print a
  // crew member's name into a dialog the flag says to mask.
  const name = l.type === 'EMPLOYEE'
    ? getEmployeeDisplayName(l)
    : (getEquipmentDisplayName(l) || l.assetName || 'Equipment');
  const qty = Number(l.quantity) || 0;
  return `${name} (${qty.toLocaleString()} mi @ $${Number(l.rate) || 0}/${l.rateUnit || 'Per Mile'})`;
}

// Fresh read of the job this ticket was built from, so the Review screen and the
// Ready/invoice gates judge the CURRENT route — a dispatcher may have fixed the
// stop since the ticket was generated, and re-warning about a repaired route is
// its own way of teaching people to ignore the warning. Throws on a read failure
// so callers can fail CLOSED; a deleted/absent job is a clean empty list.
async function _rmFetchJobBadStops(jobId) {
  if (!jobId) return [];
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const snap = await getDoc(doc(db, 'tenants', tenant, 'jobs', jobId));
  if (!snap.exists()) return [];
  return _rmBadStops(snap.data());
}

function _rmGateMessage(ticketNoPrefix, offenders, badStops, actionLabel) {
  const where = badStops.length > 0
    ? `${badStops.length === 1 ? 'Destination' : 'Destinations'} "${_rmBadStopNames(badStops)}" on this job ${badStops.length === 1 ? 'has' : 'have'} a coordinate that is not a real point on earth`
    : 'This ticket was built from a route with a bad destination coordinate';
  return `Ticket ${ticketNoPrefix}bills mileage off a route we can't trust. ${where}, and the Per Mile quantity was filled in from that route — it can be thousands of miles too high, or zero. ` +
    `Affected: ${offenders.map(_rmLineLabel).join(' · ')}. ` +
    `Enter the real miles in Review Ticket — or mark the mileage verified there — before you ${actionLabel}.`;
}

// The shared blocking gate, mirroring _zeroHourGate: authoritative FRESH reads,
// fails CLOSED with a retry message when it cannot verify, and offers the way to
// fix it. Returns true = proceed, false = blocked (dialog/toast already shown).
async function _routeMileageGate(ticketId, actionLabel) {
  let lines;
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Billing] Route-mileage gate: line read failed for', ticketId, err);
    window.showToast?.(`Couldn't verify the mileage lines — check your connection and try again before you ${actionLabel}.`, 'error');
    return false;
  }
  if (!_rmHasPerMileMoney(lines)) return true; // no per-mile money — no job read, no gate

  const t = BillingState.tickets.find(x => x.id === ticketId);
  let badStops = [];
  try {
    badStops = await _rmFetchJobBadStops(t?.jobId);
  } catch (err) {
    console.error('[Billing] Route-mileage gate: job read failed for', t?.jobId, err);
    window.showToast?.(`Couldn't check this job's route before you ${actionLabel} — check your connection and try again.`, 'error');
    return false;
  }

  const offenders = _rmSuspectLines(lines, badStops.length > 0);
  if (offenders.length === 0) return true;

  const tno = t?.ticketNo ? `#${t.ticketNo} ` : '';
  const msg = _rmGateMessage(tno, offenders, badStops, actionLabel);
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      'Mileage came from a bad location',
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// TASK-4741 — the last line of defense before an unresolvable equipment ref is
// invoiced. A ref that resolves to nothing bills a customer for a unit nobody
// can identify, and it is the cheap half of the bug: cheap to fix now, a credit
// memo and a phone call after.
//
// 🔴 DELIBERATELY NOT FAIL-CLOSED ON A READ ERROR, and that departs from the
// four gates above. They guard a WRONG DOLLAR AMOUNT (a priced line at 0 hours
// invoiced $375 of hauling at $0), where blocking costs a retry and allowing
// costs money. This one guards an unidentifiable NAME on a line whose dollars
// are already correct. Measured live population on ticket lines: 0 of 1459.
// Blocking every invoice in the product because an unrelated roster read
// hiccuped would be a far larger harm than the bug, so:
//   found something  -> BLOCK (evidence exists)
//   could not check  -> ALLOW, and say out loud that the check did not run
//   checked, clean   -> ALLOW silently
// "Could not check" is never silently folded into "clean" — that is the whole
// discipline this gate is written to hold.
async function _unresolvedRefGate(ticketId, actionLabel) {
  let lines;
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
    lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Billing] TASK-4741 unresolved-ref gate: line read failed for', ticketId, err);
    window.showToast?.(`Couldn't check this ticket's vehicles before you ${actionLabel} — that check did not run.`, 'warning');
    return true; // could not check != found a problem
  }
  const t = BillingState.tickets.find(x => x.id === ticketId);
  const refs = _equipmentRefsFromLines(lines, t?.ticketNo ? `#${t.ticketNo}` : '');
  if (refs.length === 0) return true;

  const fr = await _loadFullFleetRoster(); // never throws; loaded:false on failure
  const audit = auditEquipmentRefs(refs, fr.roster, fr.loaded);
  if (audit.status !== 'ok') {
    window.showToast?.(`Couldn't load your fleet list, so this ticket's vehicles were not checked before you ${actionLabel}.`, 'warning');
    return true;
  }
  if (audit.unresolved.length === 0) return true;

  const tno = t?.ticketNo ? `#${t.ticketNo} ` : '';
  // Never fabricate a unit name we could not resolve — quote the raw reference.
  const list = audit.unresolved.map(u => `"${u.ref}"`).join(' · ');
  const n = audit.unresolved.length;
  const msg = `Ticket ${tno}bills ${n} line${n === 1 ? '' : 's'} against ${n === 1 ? 'a vehicle' : 'vehicles'} that ${n === 1 ? "isn't" : "aren't"} in your fleet: ${list}. ` +
    `That ${n === 1 ? 'line goes' : 'lines go'} on the customer's invoice as ${n === 1 ? 'a unit' : 'units'} nobody can look up, and the rate couldn't be matched from the price book. ` +
    `Pick the right unit in Review Ticket — and fix it on the job day — before you ${actionLabel}.`;
  if (typeof window.showConfirm === 'function') {
    const openReview = await window.showConfirm(
      n === 1 ? "A vehicle on this ticket isn't in your fleet" : "Vehicles on this ticket aren't in your fleet",
      msg,
      { confirmText: 'Open Review Ticket', cancelText: 'Close', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (openReview) {
      try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
      try { closeModal('ticket'); } catch (_) { /* not always open */ }
      openReviewTicket(ticketId);
    }
  } else {
    window.showToast?.(msg, 'error');
  }
  return false;
}

// TASK-1615-B — stamp a freshly-built line whose Per Mile quantity was auto-filled
// off a route carrying a garbage coordinate. Keyed on the STORED rateUnit and
// quantity, not on the looked-up rate: the rental branch can force both away from
// per-mile after the auto-fill has already run, and a stamp that describes
// something other than what the line actually bills is worse than none. Returns
// true when it stamped, so the caller can count them for the create toast.
function _rmStampSuspect(lineData, badStops) {
  if (!badStops || badStops.length === 0) return false;
  if (!_rmIsPerMileUnit(lineData && lineData.rateUnit)) return false;
  lineData.routeMilesSuspect = true;
  lineData.routeMilesAutoQty = Number(lineData.quantity) || 0;
  lineData.routeMilesBadStops = _rmBadStopNames(badStops);
  return true;
}

// Drop externally-billed tickets out of a QuickBooks-bound batch export, with a
// BLOCKING confirm naming the count and the dollar total (createGroupedInvoice's
// deliberate precedent: skipped money gets a dialog, not a toast). Returns the
// tickets to export, or null if the user cancelled.
//
// ONE helper because there are three of these batch exporters (.iif, CSV, PIDX)
// and a per-exporter copy is exactly how one of them ends up without the guard —
// which is what 3rd-COD found, and how the PIDX one ended up skipping silently
// while the other two confirmed, which is what 4th-COD found. Every artifact here
// carries an invoice into someone else's system, and for these tickets that system
// already issued the invoice; sending it again bills the customer twice.
async function _dropExternallyBilledForQb(tickets, artifactLabel) {
  // TASK-9654 — a ticket linked to a QuickBooks invoice is already there too.
  const ext = tickets.filter(t => isAlreadyInQuickBooks(t));
  if (ext.length === 0) return tickets;
  const kept = tickets.filter(t => !isAlreadyInQuickBooks(t));
  const extTotal = ext.reduce((sum, t) => sum + Number(t.grandTotal || 0), 0);
  const ok = await window.showConfirm?.(
    `${ext.length} ticket${ext.length !== 1 ? 's' : ''} already in QuickBooks or billed elsewhere`,
    `${ext.length} of the selected tickets (${formatCurrency(extTotal)}) were billed outside DispatchDeck or are already linked to a QuickBooks invoice, and will be SKIPPED — sending them would duplicate invoices that already exist. ` +
    (kept.length > 0 ? `Export the remaining ${kept.length} to ${artifactLabel}?` : 'Nothing left to export.'),
    { confirmText: kept.length > 0 ? `Export ${kept.length}` : 'OK', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
  );
  if (!ok) return null;
  return kept;
}

// TASK-1345 (COD mitigation i) — the on-screen list and the accountant's CSV
// each had their OWN copy of this switch, and they already disagreed. One
// function, both callers, so the export can never again show a different set
// than the screen it was exported from.
function filterTicketsByStatus(tickets, filter, opts) {
  const showArchived = !!(opts && opts.showArchived);
  switch (filter) {
    case 'daily': {
      const dailyStr = formatDateISO(BillingState.dailyDate);
      return tickets.filter(t => {
        const tDate = t.date?.toDate?.() || (t.date instanceof Date ? t.date : new Date(t.date));
        return formatDateISO(tDate) === dailyStr;
      });
    }
    case 'draft':
      return tickets.filter(t => t.status === 'draft' && !isTicketInvoiced(t));
    case 'ready':
      return tickets.filter(t => t.status === 'ready' && !isTicketInvoiced(t));
    // `!paid && !writtenOff` stays (COD Code Reviewer + Edge Case Hunter): a
    // broadened predicate must not resurrect voided/written-off work as billable.
    case 'invoiced':
      return tickets.filter(t => isTicketInvoiced(t) && !t.paid && !t.writtenOff);
    case 'paid':
      return tickets.filter(t => t.paid === true && !t.writtenOff);
    case 'written_off':
      return tickets.filter(t => t.writtenOff === true);
    case 'all':
    default:
      // "Active" — archived/locked are hidden unless the user asks for them.
      return showArchived ? tickets : tickets.filter(t => t.status !== 'archived' && t.status !== 'locked');
  }
}

// How many tickets the Active view is hiding right now. Rendered even when the
// checkbox is off, so archived work can never again be silently invisible —
// computed from already-loaded docs, no extra read.
function countArchivedHidden(tickets) {
  return (tickets || []).filter(t => t.status === 'archived' || t.status === 'locked').length;
}

// TASK-4740 (A) — the two rules the merged filter row needs, injected ONCE.
// They live here rather than in styles.css because this task's surface is
// billing.js only; they are additive and scoped to two classes this file owns.
//
//  1. Desktop placement. `.tcf-bar` is the flex row that holds the All Companies
//     trigger and the selected-company chips. `margin-left:auto` pushes the
//     archived toggle to the right END of that row WITHOUT `space-between`, so
//     when the toggle is absent (any status filter other than `all`) the left
//     control does not shift. `flex-shrink:0` stops a long chip run squeezing
//     it — chips wrap, the toggle does not. Inside `.billing-mc-archived` (the
//     mobile copy) it stays left, where a phone reads it first.
//  2. Touch target. Keyed on `(pointer: coarse)`, NOT a width query: a 13"
//     touchscreen laptop needs the 44px target and a narrow desktop window does
//     not. A bare checkbox is ~13px; the label carries the height so the whole
//     "Show archived" phrase is the tap target.
function _ensureTicketFilterBarStyles() {
  if (typeof document === 'undefined') return;
  // TASK-9340 — the toolbar's own rules ride along, so renderTicketsView makes
  // ONE style call. Called before the early return: each block is idempotent
  // on its own id, and the 9340 block must still land when this one already has.
  _ensureTickets9340Styles();
  if (document.getElementById('tcf-archived-toggle-styles')) return;
  const el = document.createElement('style');
  el.id = 'tcf-archived-toggle-styles';
  el.textContent = `
.tcf-bar > .tickets-archived-toggle { margin-left: auto; flex-shrink: 0; }
.billing-mc-archived > .tickets-archived-toggle { margin-left: 0; }
@media (pointer: coarse) {
  .tickets-archived-toggle > label { min-height: 44px; padding: 0 4px; }
  .tickets-archived-toggle input[type="checkbox"] { width: 20px; height: 20px; }
}`;
  document.head.appendChild(el);
}

// The Review Ticket modal's custom-sections block. Editable normally; falls back
// to TASK-848's read-only echo when the ticket is invoiced or driver-locked —
// the 846-A reconciler SKIPS those tickets, so allowing count edits there would
// desync customSectionData from the derived billing lines.
// TASK-10026 — a send stamps driverEditLocked with this actor. That lock is
// DRIVER-facing only: the server reconciler keeps deriving lines on a sent
// ticket, so the biller's count edits stay open on it.
function _bOfficeDriverLock(t) {
  return !!t && t.driverEditLocked === true && t.driverEditLockedBy !== 'ticket-sent';
}
function renderReviewCustomSections(t) {
  const sections = _bCsDefs();
  const locked = isReviewReadOnly(t) || _bOfficeDriverLock(t); // TASK-1167 — archived/locked/invoiced OR the original driver-lock (846-A reconciler skips these)
  if (locked || sections.length === 0) {
    const roHtml = t.customSectionData ? buildCustomSectionDataHtml(t.customSectionData, escapeHtml) : '';
    if (!roHtml) return '';
    // narrow boolean, case (a): this hint tells the biller they can unlock and
    // edit. On the 110 invoice-marker-only tickets the biller genuinely still
    // can (isReviewReadOnly stayed narrow), so the hint must stay narrow too —
    // isTicketInvoiced here would hide a true statement.
    const hint = (_bOfficeDriverLock(t) && !t.invoiced)
      ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">lock</span>Driver editing is locked — unlock it above to edit these counts (auto-billing is paused while locked).</div>`
      : '';
    return `<div class="review-section" id="review-custom-sections">${roHtml}${hint}</div>`;
  }
  return `<div class="review-section" id="review-custom-sections">${_reviewCustomSectionsInner()}</div>`;
}

function _rerenderReviewCustomSections() {
  const holder = document.getElementById('review-custom-sections');
  if (holder) holder.innerHTML = _reviewCustomSectionsInner();
}

// onchange handler for every custom-section input (args via el.dataset).
function updateReviewCustomField(el) {
  const t = ReviewState.ticket || {};
  if (isReviewReadOnly(t) || _bOfficeDriverLock(t)) return; // defense in depth — read-only (incl. archived) OR driver-locked
  const csId = el?.dataset?.cs;
  const fieldId = el?.dataset?.field;
  const idx = parseInt(el?.dataset?.idx, 10) || 0;
  const ftype = el?.dataset?.ftype || 'text';
  if (!csId || !fieldId) return;
  const cs = _bCsDefs().find(s => s.id === csId);
  if (!cs) return;
  const inst = _csdEnsureInstance(cs, idx);
  // Coerce EXACTLY like clock.js _csReadInstance persists: checkbox→boolean,
  // number→parseFloat||0, everything else→trimmed string. Never undefined.
  let val;
  if (ftype === 'checkbox') val = el.checked === true;
  else if (ftype === 'number') val = parseFloat(el.value) || 0;
  else val = (el.value ?? '').trim();
  inst[fieldId] = val;
  ReviewState.csdDirty = true;
  ReviewState.dirty = true;
}

function addReviewCustomInstance(el) {
  const t = ReviewState.ticket || {};
  if (isReviewReadOnly(t) || _bOfficeDriverLock(t)) return;
  const csId = el?.dataset?.cs;
  const cs = _bCsDefs().find(s => s.id === csId);
  if (!cs) return;
  const working = _csdWorking();
  let block = working[cs.id];
  if (!(block && typeof block === 'object' && block._repeatable === true && Array.isArray(block.instances))) {
    // Materialize: legacy plain data becomes instance 0; no data mirrors the
    // rendered virtual blank row so "Add entry" visibly yields a second row.
    block = { _repeatable: true, instances: (block && typeof block === 'object') ? [block] : [_csNewInstance(cs)] };
    working[cs.id] = block;
  }
  if (block.instances.length >= REVIEW_CS_MAX_INSTANCES) {
    window.showToast?.(`"${cs.name || 'This section'}" is limited to ${REVIEW_CS_MAX_INSTANCES} entries per ticket.`, 'error');
    return;
  }
  block.instances.push(_csNewInstance(cs));
  ReviewState.csdDirty = true;
  ReviewState.dirty = true;
  _rerenderReviewCustomSections();
}

function removeReviewCustomInstance(el) {
  const t = ReviewState.ticket || {};
  if (isReviewReadOnly(t) || _bOfficeDriverLock(t)) return;
  const csId = el?.dataset?.cs;
  const idx = parseInt(el?.dataset?.idx, 10);
  const cs = _bCsDefs().find(s => s.id === csId);
  if (!cs || isNaN(idx)) return;
  const working = _csdWorking();
  const block = working[cs.id];
  let changed = false;
  if (block && typeof block === 'object' && block._repeatable === true && Array.isArray(block.instances)) {
    if (idx >= 0 && idx < block.instances.length) {
      block.instances.splice(idx, 1);
      if (block.instances.length === 0) delete working[cs.id];
      changed = true;
    }
  } else if (block && typeof block === 'object' && idx === 0) {
    delete working[cs.id];
    changed = true;
  }
  if (changed) {
    ReviewState.csdDirty = true;
    ReviewState.dirty = true;
  }
  _rerenderReviewCustomSections();
}

// Save-time cleanup — mirrors clock.js collectCustomSectionData semantics:
// instances/sections with no real data drop out (checkbox true, non-zero number,
// or non-empty string counts as data), stored shapes are preserved, undefined is
// never written. Setting a bound count to 0 therefore removes the section/instance
// exactly like a driver clearing it would — the server reconciler then deletes
// the derived line (designed behavior, no client special-casing).
function _cleanCustomSectionDataForSave(working) {
  const out = {};
  const hasVal = (v) => v === true || (typeof v === 'number' && v !== 0 && !isNaN(v)) || (typeof v === 'string' && v.trim() !== '');
  for (const [csId, block] of Object.entries(working || {})) {
    if (!block || typeof block !== 'object') continue;
    if (block._repeatable === true && Array.isArray(block.instances)) {
      const instances = block.instances
        .filter(inst => inst && typeof inst === 'object' && Object.values(inst).some(hasVal))
        .map(inst => {
          const clean = {};
          for (const [k, v] of Object.entries(inst)) { if (v !== undefined) clean[k] = v; }
          return clean;
        });
      if (instances.length > 0) out[csId] = { _repeatable: true, instances };
    } else {
      const clean = {};
      let hasData = false;
      for (const [k, v] of Object.entries(block)) {
        if (v === undefined) continue;
        clean[k] = v;
        if (hasVal(v)) hasData = true;
      }
      if (hasData) out[csId] = clean;
    }
  }
  return out;
}

// The six invoice column sections, in grid order (renderers, both builders, the
// Price Book panel and the tenant template all key on these names).
const INVOICE_COLUMN_SECTIONS = ['labor', 'vehicles', 'trailers', 'assets', 'downtime', 'standby'];

// THE ONE precedence for an invoice column section — every reader goes through
// here (getInvoiceColumnConfig for the render, resolveInvoiceColumnsGrid for
// the template builder, the Price Book panel and onIBContractChange):
//   1. the contract's own grid            contractIc[section]
//   2. its legacy 'equipment' key         (vehicles / trailers / assets only)
//   3. the tenant template                settings.invoiceColumns[section]
//   4. null — the caller supplies its own defaults
// Lacey (TITS AP, 2026-09-24): turned People "Hrs/Qty" off in the invoice
// template and the invoice still showed hours. The builder wrote the grid onto
// every CONTRACT doc and nowhere else; a customer with no contract (33 of 75 on
// TITS) or only a Draft one (2 more) had no Active contract for the render to
// read, so it fell through to hardcoded defaults (showHours: true) on ~40 of
// the 244 invoices TITS issued in the prior 45 days. Step 3 is the fix;
// saveInvoiceBuilder's All-Contracts save now writes that key. COD round 2:
// two sibling readers (the Price Book panel, onIBContractChange) still skipped
// step 3, so opening single-contract mode on a template-reliant customer showed
// — and on Save re-baked — the hardcoded defaults. Hence one helper, not three.
function resolveInvoiceColumnSection(contractIc, sectionType) {
  const ic = contractIc || {};
  if (ic[sectionType]) return ic[sectionType];
  if (['vehicles', 'trailers', 'assets'].includes(sectionType) && ic.equipment) return ic.equipment;
  const tenantIc = window.AppState?.settings?.invoiceColumns;
  if (tenantIc?.[sectionType]) return tenantIc[sectionType];
  return null;
}

// Full six-section grid for the builders/panels: each section resolved through
// resolveInvoiceColumnSection and laid over the builder defaults, so every
// checkbox has a definite state. downtime/standby carry only the columns their
// grid row renders (Times / Hrs / Rate / Total) — same shapes the two builders
// used to hand-roll.
function resolveInvoiceColumnsGrid(contractIc) {
  const defaultCols = { showTimes: true, showRole: true, showRate: true, showTotal: true, showLineItem: false };
  const slimCols = { showTimes: true, showRate: true, showTotal: true };
  const out = {};
  for (const s of INVOICE_COLUMN_SECTIONS) {
    const base = (s === 'downtime' || s === 'standby') ? slimCols : defaultCols;
    out[s] = { ...base, ...(resolveInvoiceColumnSection(contractIc, s) || {}) };
  }
  return out;
}

function getInvoiceColumnConfig(customerId, sectionType) {
  const cached = ReviewState.contractRateCache[customerId];
  const cols = resolveInvoiceColumnSection(cached?.contractData?.invoiceColumns, sectionType);
  if (cols) return cols;
  return {
    showTimes: showClockTimes(),
    showRole: true,
    showHours: true,
    showRate: true,
    showTotal: true,
    showLineItem: false
  };
}

// ============================================
// TASK-511 — Per-customer "hide these line types on invoices" default
// ============================================
// Line types a customer can pre-select to hide-by-default on their invoices.
// Excluded lines DROP from both the invoice render AND the dollar total
// (matches the prior hand-delete behavior), but the underlying ticketLine docs
// are PRESERVED in Firestore so payroll / job-costing still read them.
const EXCLUDABLE_LINE_TYPES = [
  { type: 'EMPLOYEE',   label: 'Employee Time (Labor)' },
  { type: 'EQUIPMENT',  label: 'Equipment' },
  { type: 'DOWNTIME',   label: 'Downtime' },
  { type: 'STANDBY',    label: 'Standby' },
  { type: 'PERMIT',     label: 'Load Permits' },
  { type: 'FIELD_ITEM', label: 'Field Items' },
  { type: 'MISC',       label: 'Miscellaneous' }
];

// Resolve the customer's hide-by-default line types from their active contract
// (contract-scoped to match invoiceColumns — read off the same cached contractData).
function getInvoiceExcludeLineTypes(customerId) {
  if (!customerId) return [];
  const cached = ReviewState.contractRateCache[customerId];
  const arr = cached?.contractData?.invoiceExcludeLineTypes;
  return Array.isArray(arr) ? arr : [];
}

// TASK-4746 — does this customer's price book actually PRICE labour?
//
// The "Labor rate is $0" banner under the Employees header used to fire on the
// raw ticket shape alone (`empLines.some(l => !l.rate && l.hours > 0)`). On TITS
// that is 731 of 994 tickets — every ticket that has a man-hour line, forever —
// because TITS bills SHOP TO SHOP: the truck carries the money and the EMPLOYEE
// line deliberately carries none ([[reference_tits_billing_shop_to_shop]],
// [[feedback_man_hour_lines_never_carry_a_cost]]). A warning that is right 0% of
// the time on a billing surface trains the biller to ignore the warnings that
// are right, which is the actual cost.
//
// The banner is NOT deleted. For a tenant that really does bill labour, "the
// personnel items went inactive and every ticket has quietly billed $0 since" is
// a real and expensive failure — that is why it was written. What was missing
// was any way to tell the two apart. The price book is that signal, and it is
// the same thing the banner already sends you to look at ("Check CRM →
// Contracts"):
//
//   * A LIVE, PRICED personnel item → this customer is supposed to be billed for
//     labour, so a $0 man-hour line contradicts the contract → warn.
//   * Personnel items that are ALL `active: false` → that is the product's own
//     "No Charge" switch (the Price Book row button says exactly that, :11908,
//     and lookupReviewRate honours it by returning rate 0, :17907). Deliberate,
//     not broken → say nothing. Measured on TITS 2026-09-10: 152 personnel items
//     across 38 customers, every one inactive and $0.
//   * No personnel items at all → there is nothing in CRM → Contracts to fix, so
//     the banner's instruction has no target → say nothing.
//
// 🔴 Known, accepted blind spot: a tenant who deactivates personnel items BY
// ACCIDENT is indistinguishable from one who meant it, because it is literally
// the same button. The product cannot tell those apart, and pretending it could
// is what produced the false positive. Partial cover: if ANY personnel item is
// still live and priced, the banner still fires.
//
// Kinds are matched the way the rate matcher matches them — `appliesTo.kind`
// 'person' (current, lookupReviewRate :17902), plus 'employee' (live on
// show-account's price book), plus the legacy `category === 'Personnel'` rung
// (:17916). Returns the matching item so the banner can name its own evidence
// instead of asserting an unverified diagnosis.
function pricedLaborPriceBookItem(lineItems) {
  return (lineItems || []).find(li => {
    if (!li || li.active === false) return false;
    if (!((Number(li.rate) || 0) > 0)) return false;
    const kind = li.appliesTo?.kind;
    return kind === 'person' || kind === 'employee' || li.category === 'Personnel';
  }) || null;
}

// 🔴 ONE shared inclusion predicate — used by EVERY invoice render / total path
// (single invoice, multi-ticket invoice, field ticket detail, revenue breakdown).
// If these ever disagree, invoice ≠ PDF ≠ total = money bug. Centralize here.
//   - Manual per-line override wins: includeOnInvoice===false hides, ===true shows.
//   - Otherwise (unset): hidden iff the line's type is in the customer default list.
function isLineInvoiced(line, excludeTypes) {
  if (!line) return false;
  if (line.includeOnInvoice === false) return false; // manual exclude
  if (line.includeOnInvoice === true) return true;    // manual include beats default
  const ex = Array.isArray(excludeTypes) ? excludeTypes : [];
  return !ex.includes(line.type);
}

// TASK-9666 — the three states a Price Book rate can be in, from the form.
//   showAtZero ticked → rate 0, noCharge:true  (deliberate: "Show at $0")
//   a non-zero number → that rate              (priced)
//   blank / 0, unticked → rate 0, needsPrice:true (NOT a decision — flagged)
// The stored rate for the blank case stays 0: that is exactly what every save
// path wrote for a blank before, so no resolver prices anything differently.
// Only the flag is new. Man-hour (person) items never carry a cost, so a $0
// person item is never flagged as needing one.
function _priceBookRateFields(rawRate, showAtZero, kind) {
  if (showAtZero === true) return { rate: 0, noCharge: true, needsPrice: false };
  const s = rawRate == null ? '' : String(rawRate).trim();
  const n = s === '' ? NaN : parseFloat(s);
  if (Number.isFinite(n) && n !== 0) return { rate: n, noCharge: false, needsPrice: false };
  return { rate: 0, noCharge: false, needsPrice: kind !== 'person' };
}

// TASK-9672 follow-up — the Price Book line's Item code picker + its audit
// helpers live in the LAZY views/priceBookItemCode.js (cold-load budget). Same
// ?v= as crm.js's import (one instance, pitfall #1). Stateless.
function _icMod() { return __ddChunkWrap(import('./priceBookItemCode.js?v=9672002')); }

// TASK-9666/9667 — the Price Book item's own flags, carried onto the ticket line
// it creates (Review "Add Line"). TWIN of clock.js _clockPriceBookLineFlags —
// keep identical. Each key is ADDED ONLY when the item uses it, so a line from an
// item that uses neither is written exactly as before. invoiceHidePending is a
// REQUEST: the server re-reads the Price Book item and stamps
// includeOnInvoice:false only when settings.perItemInvoiceHide is on.
function _priceBookLineTicketFlags(li) {
  const out = {};
  if (!li) return out;
  if (li.noCharge === true && !(Number(li.rate) || 0)) out.noCharge = true;
  if (li.includeOnInvoice === false) out.invoiceHidePending = true;
  return out;
}

// TASK-9666 — "N/C" is shown only when the line really is $0. A noCharge flag on
// a line that carries money (rate edited later, or a raw write) never masks it.
function _isNoChargeLine(l) {
  return !!l && l.noCharge === true && !(Number(l.rate) || 0) && !(Number(l.lineAmount) || 0);
}

// TASK-9666 — Price Book row badge: "Show at $0" (deliberate) or "Needs price".
function _priceBookStateBadge(li) {
  if (!li || li.active === false) return '';
  if (_isNoChargeLine({ noCharge: li.noCharge, rate: li.rate })) {
    return ' <span title="Shows on the invoice at $0.00 — the customer sees what was used, but isn\'t charged." style="display:inline-block;font-size:11px;font-weight:600;background:#ECFDF5;color:#065F46;padding:2px 8px;border-radius:6px;margin-left:6px;vertical-align:middle;">Show at $0</span>';
  }
  if (li.needsPrice === true && !(Number(li.rate) || 0)) {
    return ' <span title="Saved without a price. Set a rate, or edit it and tick Show at $0 if it\'s meant to be free." style="display:inline-block;font-size:11px;font-weight:600;background:#FEF3C7;color:#92400E;padding:2px 8px;border-radius:6px;margin-left:6px;vertical-align:middle;">Needs price</span>';
  }
  return '';
}

// TASK-9725 S2 UI — chip on a billed-PPE roll-up line (id ppe_<assetId>,
// sourceType 'safetyEquipment', minted by services/ppeBilling.js). Tells the
// biller where the quantity came from and that an office edit here sticks.
// Display only; shown in Review (G2 Field Items) and the read-only ticket view.
function _isPpeRollupLine(l) {
  return !!l && (l.sourceType === 'safetyEquipment' || !!l.ppeAssetId || (typeof l.id === 'string' && l.id.startsWith('ppe_')));
}
function _ppeRollupChip(l, ticketId) {
  if (!_isPpeRollupLine(l)) return '';
  const n = Array.isArray(l.sourceIds) ? l.sourceIds.length : 0;
  const from = n > 0 ? `${n} signed sign-out${n === 1 ? '' : 's'}` : 'signed sign-outs';
  const title = `Rolled up by the server from ${from} of this item on this job and day (HR → PPE). The quantity is recomputed from the sign-outs; deleting this line or changing its quantity here sticks and is audited. Stock moved once, at sign-out — never from this ticket.`;
  // TASK-9725 drill-down: with a ticket + saved line id the chip is a button
  // that lists each signed issue (lazy views/billingPpeDrilldown.js).
  const tap = n > 0 && ticketId && l.id ? `button type="button" onclick="event.stopPropagation();window.BillingModule.openPpeIssueDrilldown('${window.escJsAttr(String(ticketId))}','${window.escJsAttr(String(l.id))}')"` : '';
  return ` <${tap || 'span'} class="ppe-rollup-chip" title="${window.escAttr(tap ? title + ' Tap for each sign-out.' : title)}" style="display:inline-flex;align-items:center;gap:3px;font-size:0.68rem;font-weight:600;color:#0369A1;background:#E0F2FE;border:1px solid #BAE6FD;border-radius:999px;padding:1px 7px;vertical-align:1px;white-space:nowrap;${tap ? 'cursor:pointer;font-family:inherit;' : ''}"><span class="material-icons" aria-hidden="true" style="font-size:12px;">health_and_safety</span>PPE roll-up${n > 0 ? ` · ${n}` : ''}${tap ? '<span class="material-icons" aria-hidden="true" style="font-size:12px;">chevron_right</span></button>' : '</span>'}`;
}
// TASK-9725 drill-down — the list lives in the LAZY views/billingPpeDrilldown.js
// (cold load ~0); deps handed over, never imported (pitfall #1).
let _ppeDrillLoad = null;
function openPpeIssueDrilldown(ticketId, lineId) {
  if (!_ppeDrillLoad) _ppeDrillLoad = __ddChunkWrap(import('./billingPpeDrilldown.js?v=9725301')).catch(() => (_ppeDrillLoad = null)); // a failed load retries on the next tap
  _ppeDrillLoad.then((m) => { if (m) m.openPpeIssueDrilldown({ httpsCallable, functions, escapeHtml }, ticketId, lineId); else window.showToast?.('Could not open the sign-outs. Try again.', 'error'); });
}

// TASK-9672 follow-up (UX) — a red warning tag whose explanation was hover-only
// (a title=) is unreadable on a tablet. Tap toggles the same text open under the
// tag; the title stays for a mouse. Only fixed strings go into the onclick.
function _tapTag(label, note, color) {
  const c = color || '#DC2626';
  return ' <button type="button" class="dd-tap-tag" aria-expanded="false" title="' + window.escAttr(note) + '"'
    + ' onclick="event.stopPropagation();var n=this.nextElementSibling;var o=n.style.display===\'none\';n.style.display=o?\'block\':\'none\';this.setAttribute(\'aria-expanded\',o?\'true\':\'false\')"'
    + ' style="color:' + c + ';">' // the rest of the look is .dd-tap-tag in styles.css (integ/2128: app cold-load budget)
    + label + '</button><span class="dd-tap-tag-note" style="display:none;">'
    + escapeHtml(note) + '</span>';
}

// TASK-9666/9667 — Review tags on a FIELD_ITEM / MISC line that came from a
// Price Book item: "N/C" (deliberate $0), "⚠ no price" (item saved without a
// price), "not on invoice" (hidden for this customer). Display only.
function _reviewPriceBookLineTags(l) {
  if (!l) return '';
  let out = '';
  if (_isNoChargeLine(l)) {
    out += ' <span title="Show at $0 — on the invoice at $0.00, not charged" style="font-size:0.7rem;color:#065F46;font-weight:600;">N/C</span>';
  } else if (!(Number(l.rate) || 0) && l.lineItemId) {
    const pb = (ReviewState.contractLineItems || []).find(x => x && x.id === l.lineItemId);
    if (pb && pb.needsPrice === true && !(Number(pb.rate) || 0)) {
      out += _tapTag('⚠ no price', 'This Price Book item has no price yet. Set a rate in the Price Book, or tick Show at $0 if it is meant to be free.');
    }
  }
  // Dispatcher COD (tax) — a hidden line's tax is NOT taken off the invoice. The
  // server never hides a taxable line, but a biller can turn tax on afterwards.
  const _taxCat = l.type === 'FIELD_ITEM' ? 'fieldItems' : (l.type === 'MISC' ? 'misc' : null);
  if (l.includeOnInvoice === false && _taxCat && ReviewState.taxEnabled && (Number(ReviewState.taxRate) || 0) > 0
      && (ReviewState.taxCategories || {})[_taxCat] !== false && (Number(l.lineAmount) || 0) !== 0) {
    out += _tapTag('⚠ hidden but taxed', 'This hidden line is still in the taxable total, so its tax stays on the invoice. Show the line, or untick this category in the tax settings.');
  }
  if (l.includeOnInvoice === false) {
    out += ' <span title="Recorded on the ticket, left off this customer\'s invoice" style="font-size:0.7rem;color:var(--text-secondary);font-weight:600;">not on invoice</span>';
  }
  // TASK-9665 — server-priced Price Book lines. A $0 that is really "no such
  // item under this customer" must never read as a price.
  if (l.priceBookItemMissing === true) {
    out += _tapTag('⚠ not in Price Book', 'This line\'s Price Book item was not found under this customer\'s Active price book, so the server priced it at $0. Set the rate here, or add the item to the Price Book.');
  } else if (l.priceBookRatePending === true) {
    out += ' <span title="Waiting for the server to price this line from the Price Book — reopen in a moment" style="font-size:0.7rem;color:var(--text-secondary);font-weight:600;">pricing…</span>';
  }
  return out;
}

// Sum of lineAmount for lines EXCLUDED from the invoice (manual override OR
// customer default), sign-aware (DOWNTIME is a deduction). Subtract this from
// the stored grandTotal at render time so the displayed total matches the
// rendered line items exactly.
function excludedInvoiceAmount(lines, excludeTypes) {
  return (lines || []).reduce((s, l) => {
    if (isLineInvoiced(l, excludeTypes)) return s;
    const sign = l.type === 'DOWNTIME' ? -1 : 1;
    return s + sign * (l.lineAmount || 0);
  }, 0);
}

// TASK-9668 (#6) — tooltip for "⚠ no price" on an auto-calculated (derived)
// Field Item line. TASK-9672 (Chris 2026-10-28 "Price Book first"): the server
// (reconcileDerivedTicketLines → services/derivedLinePricing.js) prices it from
// the ticket customer's Price Book line with that item code first, then the Item
// Code's default rate; a customer line that is turned off bills $0 (rateSource
// 'price_book_inactive') and is NOT replaced by the company default.
function _derivedNoPriceTitle(line) {
  const code = line && line.itemCode ? String(line.itemCode) : '';
  if (line && line.rateSource === 'price_book_inactive') {
    return (code ? `This customer's Price Book item for ${code} is turned off, ` : 'This customer\'s Price Book item is turned off, ') +
      'so the line bills $0. Turn it back on in the customer\'s Price Book, or change the count in the Custom Section.';
  }
  return (code ? `No price for item code ${code}. ` : 'No price for this item code. ') +
    'Auto-calculated lines use the customer\'s Price Book price for the code first, then the Item Code\'s default rate — ' +
    'pick the code under Item code on one of this customer\'s Price Book lines, or give it a default rate under Manage Item Codes.';
}

// Shared Invoice Column Settings panel — used in both single-contract and mass-update modes
// Pass a contract object (single mode) or null (mass mode = applies to all contracts)
function buildInvoiceColumnsPanel(contract) {
  const isMass = !contract;
  // Single mode: this contract's grid → legacy equipment → tenant template →
  // defaults (resolveInvoiceColumnsGrid). Mass mode ("all contracts") has no
  // contract of its own: seed from the tenant template, else the first contract
  // that carries a grid — the same order resetInvoiceBuilderState uses.
  let _ic = contract?.invoiceColumns || {};
  if (isMass) {
    const _tenantIc = window.AppState?.settings?.invoiceColumns;
    const firstContract = (LineItemsState.contracts || []).find(c => c.invoiceColumns && Object.keys(c.invoiceColumns).length > 0)
      || (LineItemsState._allContracts || []).map(p => p.contract).find(c => c?.invoiceColumns && Object.keys(c.invoiceColumns).length > 0);
    _ic = (_tenantIc && Object.keys(_tenantIc).length > 0) ? _tenantIc : (firstContract?.invoiceColumns || {});
  }
  const _icResolved = resolveInvoiceColumnsGrid(_ic);
  const sections = [
    { key: 'labor', label: 'People', cols: ['showTimes', 'showRole', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'vehicles', label: 'Vehicles', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'trailers', label: 'Trailers', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'assets', label: 'Assets', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'downtime', label: 'Downtime', cols: ['showTimes', 'showHours', 'showRate', 'showTotal'] },
    { key: 'standby', label: 'Standby', cols: ['showTimes', 'showHours', 'showRate', 'showTotal'] }
  ];
  const colLabels = { showTimes: 'Times', showRole: 'Role', showHours: 'Hrs/Qty', showRate: 'Rate', showTotal: 'Total', showLineItem: 'Line Item' };
  const allCols = ['showTimes', 'showRole', 'showHours', 'showRate', 'showTotal', 'showLineItem'];
  const saveAction = isMass ? 'saveInvoiceColumnsMass' : 'saveInvoiceColumns';
  const desc = isMass
    ? 'Applies to <strong>all contracts</strong> across all customers.'
    : 'Control which columns appear on invoices for this customer.';

  // TASK-511 — per-customer "hide these line types on invoices" default.
  let _exTypes = contract?.invoiceExcludeLineTypes;
  if (isMass && (!Array.isArray(_exTypes) || _exTypes.length === 0)) {
    const fc = (LineItemsState.contracts || []).find(c => Array.isArray(c.invoiceExcludeLineTypes) && c.invoiceExcludeLineTypes.length > 0)
      || (LineItemsState._allContracts || []).map(p => p.contract).find(c => Array.isArray(c?.invoiceExcludeLineTypes) && c.invoiceExcludeLineTypes.length > 0);
    if (fc) _exTypes = fc.invoiceExcludeLineTypes;
  }
  const _exSet = new Set(Array.isArray(_exTypes) ? _exTypes : []);
  const excludeChecks = EXCLUDABLE_LINE_TYPES.map(lt => `
    <label style="display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;padding:4px 8px;">
      <input type="checkbox" data-ic-exclude-type="${escapeHtml(lt.type)}" ${_exSet.has(lt.type) ? 'checked' : ''} style="accent-color:var(--brand-blue);cursor:pointer;">
      ${escapeHtml(lt.label)}
    </label>`).join('');

  // Build table rows
  const bodyRows = sections.map(sec => {
    const cfg = _icResolved[sec.key] || {};
    return `<tr data-ic-row="${sec.key}">
      <td style="font-weight:600;font-size:13px;color:var(--text-primary);padding:6px 8px;white-space:nowrap;">${sec.label}</td>
      ${allCols.map(col => {
        if (!sec.cols.includes(col)) return `<td style="padding:6px 4px;text-align:center;"><span style="color:var(--border-light);">&mdash;</span></td>`;
        return `<td style="padding:6px 4px;text-align:center;">
          <input type="checkbox" data-ic-section="${sec.key}" data-ic-col="${col}" ${cfg[col] !== false ? 'checked' : ''} style="accent-color:var(--brand-blue);cursor:pointer;">
        </td>`;
      }).join('')}
    </tr>`;
  }).join('');

  return `<details style="margin-bottom:16px;border:1px solid var(--border-light);border-radius:var(--radius-md);overflow:hidden;">
    <summary style="padding:10px 14px;font-size:13px;font-weight:600;color:var(--text-primary);cursor:pointer;background:var(--bg-secondary);user-select:none;">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:6px;">view_column</span>
      Invoice Column Settings
    </summary>
    <div style="padding:12px 14px;" id="li-invoice-columns-panel">
      <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">${desc}</p>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="border-bottom:2px solid var(--border-light);">
          <th style="text-align:left;padding:6px 8px;font-size:12px;color:var(--text-secondary);font-weight:600;">Section</th>
          ${allCols.map(col => `<th style="text-align:center;padding:6px 4px;font-size:12px;color:var(--text-secondary);font-weight:600;">${colLabels[col]}</th>`).join('')}
        </tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
      <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border-light);">
        <div style="font-size:13px;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Hide these line types on invoices</div>
        <p style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:8px;">Checked types are dropped from this customer's invoices and the dollar total by default. The hours stay on the ticket for payroll &amp; job costing — you can still re-include a single line per ticket.</p>
        <div style="display:flex;flex-wrap:wrap;gap:2px 6px;">${excludeChecks}</div>
      </div>
      <div style="text-align:right;margin-top:10px;">
        <button class="btn btn-primary btn-sm" onclick="window.BillingModule.${saveAction}()">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">save</span> Save Settings
        </button>
      </div>
    </div>
  </details>`;
}

function toggleShowNames(checked) {
  _showEmployeeNames = checked;
  // Sync both checkboxes if they exist
  const viewCb = document.getElementById('view-ticket-show-names');
  const reviewCb = document.getElementById('review-show-names');
  if (viewCb) viewCb.checked = checked;
  if (reviewCb) reviewCb.checked = checked;
  // Re-render whichever view is open
  if (BillingState.selectedTicket && !document.getElementById('modal-view-ticket')?.classList.contains('hidden')) {
    viewTicket(BillingState.selectedTicket.id);
  }
  if (ReviewState.ticketId) {
    renderReviewTicket();
  }
}

// ============================================
// CREW TYPE → ITEM CODE MAPPING (shared with clock.js)
// ============================================
const CREW_TYPE_ITEM_CODE_MAP = {
  cdl_driver: 'CREW-CDL',
  laborer: 'CREW-LABORER',
  foreman: 'CREW-FOREMAN',
  operator: 'CREW-OPERATOR',
  swamper: 'CREW-SWAMPER',
  supervisor: 'CREW-FOREMAN',
  mechanic: 'CREW-NONCDL'
};

// ============================================
// AUTO-SEED ITEM CODES FOR NEW TENANTS
// ============================================
const STANDARD_ITEM_CODES = [
  { code: 'CREW-FOREMAN',  description: 'Crew Foreman',          category: 'Personnel',        defaultRate: 58,  defaultRateUnit: 'Hourly' },
  { code: 'CREW-OPERATOR', description: 'Equipment Operator',    category: 'Personnel',        defaultRate: 55,  defaultRateUnit: 'Hourly' },
  { code: 'CREW-LABORER',  description: 'Laborer',               category: 'Personnel',        defaultRate: 45,  defaultRateUnit: 'Hourly' },
  { code: 'CREW-SWAMPER',  description: 'Swamper',               category: 'Personnel',        defaultRate: 40,  defaultRateUnit: 'Hourly' },
  { code: 'CREW-CDL',      description: 'CDL Driver',            category: 'Personnel',        defaultRate: 85,  defaultRateUnit: 'Hourly' },
  { code: 'CREW-NONCDL',   description: 'Non-CDL Helper',        category: 'Personnel',        defaultRate: 65,  defaultRateUnit: 'Hourly' },
  { code: 'HH-LOWBOY',     description: 'Lowboy Trailer',        category: 'Heavy Haul',       defaultRate: 150, defaultRateUnit: 'Hourly' },
  { code: 'HH-WINCH',      description: 'Winch Truck',           category: 'Heavy Haul',       defaultRate: 175, defaultRateUnit: 'Hourly' },
  { code: 'HH-RGN',        description: 'RGN Trailer',           category: 'Heavy Haul',       defaultRate: 165, defaultRateUnit: 'Hourly' },
  { code: 'HV-HYDROVAC',   description: 'Hydrovac Unit',         category: 'Hydrovac',         defaultRate: 250, defaultRateUnit: 'Hourly' },
  { code: 'HV-WATER',      description: 'Water Truck',           category: 'Hydrovac',         defaultRate: 125, defaultRateUnit: 'Hourly' },
  { code: 'TT-DAYCAB',     description: 'Day Cab Truck',         category: 'Trucks & Trailer', defaultRate: 150, defaultRateUnit: 'Hourly' },
  { code: 'TT-SLEEPER',    description: 'Sleeper Truck',         category: 'Trucks & Trailer', defaultRate: 160, defaultRateUnit: 'Hourly' },
  { code: 'TT-FLATBED',    description: 'Flatbed Trailer',       category: 'Trucks & Trailer', defaultRate: 75,  defaultRateUnit: 'Hourly' },
  { code: 'TT-STEPDECK',   description: 'Step Deck Trailer',     category: 'Trucks & Trailer', defaultRate: 85,  defaultRateUnit: 'Hourly' },
  { code: 'TT-PICKUP',     description: 'Pickup Truck',          category: 'Trucks & Trailer', defaultRate: 75,  defaultRateUnit: 'Hourly' },
  { code: 'EQ-GENERATOR',  description: 'Generator',             category: 'Equipment',        defaultRate: 50,  defaultRateUnit: 'Hourly' },
  { code: 'EQ-LIGHTPLANT', description: 'Light Plant',           category: 'Equipment',        defaultRate: 35,  defaultRateUnit: 'Hourly' },
  { code: 'EQ-COMPRESSOR', description: 'Air Compressor',        category: 'Equipment',        defaultRate: 45,  defaultRateUnit: 'Hourly' },
  { code: 'EQ-MISC',       description: 'Miscellaneous Equipment', category: 'Equipment',      defaultRate: 50,  defaultRateUnit: 'Hourly' }
];

async function seedItemCodesIfNeeded(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenant, 'itemCodes'), limit(1)));
    if (!snap.empty) return; // Already has item codes

    console.log('[Billing] Seeding standard item codes for new tenant:', tenant);
    const now = Timestamp.now();
    const promises = STANDARD_ITEM_CODES.map(ic =>
      setDoc(doc(db, 'tenants', tenant, 'itemCodes', ic.code), {
        code: ic.code,
        description: ic.description,
        category: ic.category,
        defaultRate: ic.defaultRate,
        defaultRateUnit: ic.defaultRateUnit,
        billingType: 'hourly',
        createdAt: now,
        updatedAt: now
      })
    );
    await Promise.all(promises);
    console.log('[Billing] Seeded', STANDARD_ITEM_CODES.length, 'item codes');
  } catch (err) {
    console.warn('[Billing] Error seeding item codes (non-fatal):', err);
  }
}

// ============================================
// BILLING ONBOARDING WIZARD (6-Step Guided Setup)
// ============================================
async function checkBillingOnboarding(tenantId) {
  // Only show for non-founder clockbilling subscribers who haven't completed onboarding
  if (isFounder()) return;
  if (!window.hasAddOnPackage?.('clockbilling')) return;

  // Load item codes for the wizard + checklist
  try {
    const snap = await getDocs(collection(db, 'tenants', tenantId, 'itemCodes'));
    BillingState.itemCodes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[Billing] Could not load item codes for onboarding:', e);
  }

  // Show wizard if not completed yet
  if (window.AppState?.settings?.billingOnboardingComplete !== true && !_t9986BillingReadOnly()) { // TASK-9986 S1
    setTimeout(() => showBillingOnboardingWizard(tenantId), 500);
  } else {
    // Wizard done — show persistent checklist if not dismissed
    setTimeout(() => injectBillingChecklist(tenantId), 600);
  }
}

// --- Category → appliesTo kind mapping for auto-generating line items ---
const CATEGORY_KIND_MAP = {
  'Personnel': 'person',
  'Heavy Haul': 'truck',
  'Hydrovac': 'truck',
  'Trucks & Trailer': 'truck',
  'Equipment': 'asset',
  'Miscellaneous': 'misc'
};

// The wizard (6 steps, Firestore writes) lives in the LAZY
// billingOnboardingWizard.js (perf budget): it pops once per tenant, so it is
// not part of the cold load. A failed load just skips the pop this visit; the
// next Billing visit tries again (billingOnboardingComplete is still unset).
// Editing billingOnboardingWizard.js: bump its ?v= here AND billing.js's ?v= in app.js.
// COD (perf/app-headroom-on2129): if the tenant switched while the chunk loaded, don't pop.
let _obwLoad = null;
function showBillingOnboardingWizard(tenantId) {
  const tidAtCall = BillingState.tenantId;
  (_obwLoad ||= __ddChunkWrap(import('./billingOnboardingWizard.js?v=2')).catch((e) => { _obwLoad = null; throw e; }))
    .then((m) => BillingState.tenantId === tidAtCall && m.open(tenantId, {
      BillingState, escapeHtml, getTenant, injectBillingChecklist, _billableCustomers,
      _billingSalesCustomerOptionsHtml, _isTicketClockMode, CATEGORY_KIND_MAP,
    }))
    .catch((e) => console.warn('[Billing] onboarding wizard unavailable:', e?.message));
}

// ============================================
// PERSISTENT BILLING CHECKLIST
// ============================================
async function injectBillingChecklist(tenantId) {
  if (isFounder()) return;
  if (!window.hasAddOnPackage?.('clockbilling')) return;
  if (window.AppState?.tenantData?.billingChecklistDismissed === true) return;

  // Auto-hide for accounts older than 60 days
  const createdAt = window.AppState?.tenantData?.createdAt;
  if (createdAt) {
    const created = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    const daysSince = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince > 60) return;
  }

  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  try {
    // Query each checklist condition in parallel
    const [custSnap, ticketSnap, jobSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'customers'), limit(1))),
      getDocs(query(collection(db, 'tenants', tenant, 'tickets'), limit(1))),
      getDocs(query(collection(db, 'tenants', tenant, 'jobs'), limit(1)))
    ]);

    // Check for active contract with appliesTo line items
    let hasActiveContract = false;
    let hasAppliesToLineItems = false;
    if (!custSnap.empty) {
      for (const custDoc of custSnap.docs) {
        const contractsSnap = await getDocs(
          query(collection(db, 'tenants', tenant, 'customers', custDoc.id, 'contracts'),
            where('status', '==', 'Active'), limit(1))
        );
        if (!contractsSnap.empty) {
          hasActiveContract = true;
          for (const cDoc of contractsSnap.docs) {
            const liSnap = await getDocs(
              query(collection(db, 'tenants', tenant, 'customers', custDoc.id, 'contracts', cDoc.id, 'lineItems'), limit(5))
            );
            if (liSnap.docs.some(d => d.data().appliesTo?.kind)) {
              hasAppliesToLineItems = true;
              break;
            }
          }
          if (hasAppliesToLineItems) break;
        }
      }
    }

    const steps = [
      { done: !custSnap.empty, label: 'Set up at least one customer', icon: 'business', action: `window.BillingModule.showLineItemsModal()` },
      { done: hasActiveContract, label: 'Create an active Price Book', icon: 'menu_book', action: `window.BillingModule.showLineItemsModal()` },
      { done: hasAppliesToLineItems, label: 'Add line items with rate mapping', icon: 'tune', action: `window.BillingModule.showLineItemsModal()` },
      { done: !jobSnap.empty, label: 'Dispatch a job with equipment', icon: 'add_task', action: `window.location.hash='#/dispatch'` },
      { done: !ticketSnap.empty, label: 'Create or auto-generate your first ticket', icon: 'receipt_long', action: `window.BillingModule.showNewTicketHelper()` }
    ];

    const completed = steps.filter(s => s.done).length;
    if (completed === 5) return; // All done — no need for checklist

    // Remove any existing billing checklist
    document.getElementById('billing-onboarding-checklist')?.remove();

    const pct = Math.round((completed / 5) * 100);
    const card = document.createElement('div');
    card.id = 'billing-onboarding-checklist';
    card.className = 'onboarding-checklist';
    card.innerHTML = `
      <div class="onboarding-header">
        <div>
          <h3 class="onboarding-title">Billing Setup Progress</h3>
          <p class="onboarding-subtitle">${completed}/5 steps complete — ${completed === 0 ? 'let\'s get started' : completed <= 2 ? 'making progress' : completed <= 3 ? 'almost there' : 'one more to go'}!</p>
        </div>
        <button class="btn-icon onboarding-dismiss" onclick="window.BillingModule.dismissBillingChecklist()" title="Dismiss">
          <span class="material-icons" style="color:var(--text-secondary);">close</span>
        </button>
      </div>
      <div class="onboarding-progress">
        <div class="onboarding-progress-fill" style="width:${pct}%"></div>
      </div>
      <div class="onboarding-steps">
        ${steps.map((s, i) => `
          <div class="onboarding-step ${s.done ? 'done' : ''}" data-step="${i}">
            <span class="material-icons onboarding-check">${s.done ? 'check_circle' : 'radio_button_unchecked'}</span>
            <span class="onboarding-step-label">${s.label}</span>
            ${!s.done ? `<button class="btn btn-sm btn-primary onboarding-go" onclick="${s.action}">
              <span class="material-icons" style="font-size:16px;">${s.icon}</span> Go
            </button>` : '<span class="material-icons onboarding-done-icon">done</span>'}
          </div>
        `).join('')}
      </div>
      <div class="onboarding-footer">
        <button class="btn-link" onclick="window.BillingModule.dismissBillingChecklist()">I'll figure it out</button>
      </div>
    `;

    // Insert at top of billing content area
    const contentArea = document.getElementById('billing-content-area');
    if (contentArea) {
      contentArea.insertBefore(card, contentArea.firstChild);
    }
  } catch (err) {
    console.warn('[Billing] Error loading billing checklist:', err);
  }
}

function showNewTicketHelper() {
  // Remove any existing helper modal
  document.getElementById('modal-new-ticket-helper')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'modal-new-ticket-helper';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:9999;';
  overlay.innerHTML = `
    <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);max-width:420px;">
      <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
        <span class="material-icons" style="font-size:48px;color:var(--brand-blue);">receipt_long</span>
        <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Create Your First Ticket</h3>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);line-height:1.5;">Choose how you want to get started:</p>
      <div style="display:flex;flex-direction:column;gap:var(--spacing-md);margin-bottom:var(--spacing-lg);">
        <div style="border:1px solid var(--border-light);border-radius:10px;padding:var(--spacing-md);cursor:pointer;transition:box-shadow 0.15s;" onmouseover="this.style.boxShadow='var(--shadow-md)'" onmouseout="this.style.boxShadow='none'" onclick="document.getElementById('modal-new-ticket-helper').remove();window.location.hash='#/clock'">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-xs);">
            <span class="material-icons" style="color:var(--accent-teal);font-size:24px;">auto_awesome</span>
            <strong style="font-size:1rem;">Auto-Generate</strong>
          </div>
          <p style="color:var(--text-secondary);font-size:0.85rem;margin:0;line-height:1.4;">Tickets auto-create when drivers clock out. Enable <em>"Build billing tickets from clock entries"</em> in Billing Settings.</p>
        </div>
        <div style="border:1px solid var(--border-light);border-radius:10px;padding:var(--spacing-md);cursor:pointer;transition:box-shadow 0.15s;" onmouseover="this.style.boxShadow='var(--shadow-md)'" onmouseout="this.style.boxShadow='none'" onclick="document.getElementById('modal-new-ticket-helper').remove();window.BillingModule.showAddTicket()">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-xs);">
            <span class="material-icons" style="color:var(--brand-blue);font-size:24px;">edit_note</span>
            <strong style="font-size:1rem;">Create Manually</strong>
          </div>
          <p style="color:var(--text-secondary);font-size:0.85rem;margin:0;line-height:1.4;">Pick the dispatched job and day, then build its ticket.</p>
        </div>
      </div>
      <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
        <button class="btn btn-secondary" style="flex:1;max-width:140px;" onclick="document.getElementById('modal-new-ticket-helper').remove()">Cancel</button>
      </div>
    </div>
  `;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

async function dismissBillingChecklist() {
  document.getElementById('billing-onboarding-checklist')?.remove();
  try {
    const t = window.AppState?.tenant;
    if (t) {
      await updateDoc(doc(db, 'tenants', t), { billingChecklistDismissed: true });
      if (window.AppState.tenantData) window.AppState.tenantData.billingChecklistDismissed = true;
    }
  } catch (err) {
    console.warn('[Billing] Error saving checklist dismiss:', err);
  }
}

// ============================================
// REVIEW TICKET STATE
// ============================================
const ReviewState = {
  ticketId: null,
  ticket: null,
  employeeLines: [],
  equipmentLines: [],
  permitLines: [],
  downtimeLines: [],
  standbyLines: [],
  potholeLines: [],
  fieldItemLines: [],
  miscLines: [],
  // TASK-9440 — FUEL_SURCHARGE lines. Server-owned: never in the save loop's
  // allLines; Save only refreshes the kept line's amount at its stamped %.
  fuelSurchargeLines: [],
  shipSlips: [],
  deletedLineIds: [],
  itemCodes: [],
  contractLineItems: [],
  trucks: [],
  trailers: [],
  assets: [],
  dirty: false,
  localIdCounter: 0,
  contractRateCache: {},
  headerEdits: {},
  // TASK-9608 — UI only: the biller opened the Customer/Job pickers ("Change")
  // or changed one, so re-renders keep them open. Never saved.
  headerExpanded: false,
  // TASK-846-B — staged edits to ticket.customSectionData (deep-cloned working
  // copy; null until the biller touches a custom-section input). csdDirty gates
  // whether saveReviewTicket writes customSectionData at all.
  csdEdits: null,
  csdDirty: false,
  customerRateCache: null,
  rentalSlices: [],        // TASK-842-A — this ticket's rentalSlices ledger docs (read-only; span + still-out UI)
  documents: [],
  pendingDocFiles: [],
  deletedDocIds: [],
  hiddenJobPermitIds: [],
  // TASK-1615-B — stops on this ticket's job whose stored coordinates are garbage
  // (_rmBadStops). Resolved on open from a FRESH job read so the Review banner and
  // the Ready/invoice gates judge the same route. null = NOT RESOLVED (no per-mile
  // money on this ticket, or the read failed) — never treated as "clean".
  routeBadStops: null,
  taxEnabled: false,
  taxRate: 0,
  taxCategories: { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true },
  // TASK-9246 — repairs the APP made to this ticket while opening it, kept
  // strictly APART from `dirty`. `dirty` means "the human changed something and
  // it is not saved yet" and is the ONLY thing allowed to arm the discard
  // prompt; a self-heal that armed it made the ✕ accuse a biller of edits they
  // never made (Chris, #100694 — three 0-hour lines recalculated on open).
  // Each entry: { lineId, type, label, kind, fromHours, toHours, changed }.
  systemHeals: [],
  // null = the heals above are persisted (or there were none). 'locked' = the
  // ticket is read-only so the repair is display-only. 'error' = the write was
  // attempted and FAILED — the only case that must still interrupt the close,
  // because those lines go on billing at $0 and nobody has been told.
  healUnpersisted: null,
  // TASK-9755 — clock entries whose ticketId is this ticket (null = not loaded /
  // read failed → no banner), the entries a biller ruled "not on this ticket"
  // (persisted as ticket.crewRemovedEntryIds), and this session's restores and
  // removals for the Save-time audit rows.
  ticketClockEntries: null,
  crewRemovedEntryIds: [],
  crewRestored: [],
  crewRemovedDecisions: [],
  removedLinesAudit: [],
  // TASK-9866 — the crew's same-day hours that sit on a SISTER job of the same
  // customer (null = not loaded / read failed → no banner), and the shifts
  // clocked to this ticket by people who are not on the job day's crew.
  sisterHours: null,
  offCrewClocked: [],
  crewDayRefs: null
};

function resetReviewState() {
  _clearReviewSavedUi(true); // TASK-9735
  ReviewState.ticketId = null;
  ReviewState.ticket = null;
  ReviewState.employeeLines = [];
  ReviewState.equipmentLines = [];
  ReviewState.permitLines = [];
  ReviewState.downtimeLines = [];
  ReviewState.standbyLines = [];
  ReviewState.potholeLines = [];
  ReviewState.fieldItemLines = [];
  ReviewState.miscLines = [];
  ReviewState.fuelSurchargeLines = []; // TASK-9440
  ReviewState._fuelRefreshFailed = false; // TASK-9440
  ReviewState.shipSlips = [];
  ReviewState.deletedLineIds = [];
  ReviewState.itemCodes = [];
  ReviewState.contractLineItems = [];
  ReviewState.trucks = [];
  ReviewState.trailers = [];
  ReviewState.assets = [];
  // TASK-4741 (COD Code Reviewer) — the full-roster envelope the equipment-ref
  // audit judges against. Cleared with the rest of the modal state so reopening
  // a ticket re-reads the fleet instead of judging it against a roster from
  // before the user added or renamed a vehicle.
  ReviewState._refRoster = null;
  // TASK-9258 — the jobDay envelope the template check judges against. Cleared
  // with the modal so a reopened ticket re-reads the days (the dispatcher may
  // have confirmed the kit in between) instead of judging stale ones.
  ReviewState._tplDays = null;
  ReviewState._tplDeployTx = null;
  ReviewState.rentalSlices = [];
  ReviewState.dirty = false;
  ReviewState.systemHeals = [];      // TASK-9246
  ReviewState.healUnpersisted = null; // TASK-9246
  ReviewState.localIdCounter = 0;
  ReviewState.headerEdits = {};
  ReviewState.headerExpanded = false; // TASK-9608 — each ticket opens collapsed
  ReviewState.csdEdits = null;
  ReviewState.csdDirty = false;
  ReviewState.deductDowntime = false;
  ReviewState.documents = [];
  ReviewState.pendingDocFiles = [];
  ReviewState.deletedDocIds = [];
  ReviewState.hiddenJobPermitIds = [];
  ReviewState.routeBadStops = null;
  ReviewState.taxEnabled = false;
  ReviewState.taxRate = 0;
  ReviewState.taxCategories = { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true };
  ReviewState.ticketClockEntries = null; // TASK-9755
  ReviewState.crewRemovedEntryIds = [];
  ReviewState.crewRestored = [];
  ReviewState.crewRemovedDecisions = [];
  ReviewState.removedLinesAudit = [];
  ReviewState.sisterHours = null; // TASK-9866
  ReviewState.offCrewClocked = [];
  ReviewState.crewDayRefs = null;
  ReviewState._openNotesText = null; // TASK-10036
}

function nextLocalId() {
  return ++ReviewState.localIdCounter;
}

// ============================================
// LINE ITEMS MANAGEMENT STATE
// ============================================
const LineItemsState = {
  selectedCustomerId: null,
  selectedContractId: null,
  massUpdateMode: false,
  _massLoading: false,
  contracts: [],
  lineItems: [],
  allLineItems: [],
  itemCodes: [],
  _pendingChanges: new Map(),
  _pendingAdds: [],
  _allContracts: [],
  _hasUnsavedChanges: false
};

// ============================================
// INVOICE BUILDER STATE
// ============================================
const InvoiceBuilderState = {
  mode: 'mass',
  selectedCustomerId: null,
  selectedContractId: null,
  previewTab: 'invoice',
  localSettings: {
    showClockTimesOnTickets: true,
    showEmployeeNamesOnTickets: false,
    invoiceSectionOrder: [],
    perAssetNotes: false,
    perAssetNotesTypes: { truck: true, trailer: true, asset: true },
    companyHeader: { showName: true, showPhone: true, showEmail: true, showAddress: true, phoneLabel: null, emailLabel: null, addressLabel: null }
  },
  localInvoiceColumns: {
    labor: {}, vehicles: {}, trailers: {}, assets: {}, downtime: {}, standby: {}
  },
  localFieldTicketSettings: {
    requireDriverSignature: false,
    requireCustomerSignature: false,
    requireStamp: false,
    requireEmailCopy: false,
    showCostsOnDriverTicket: true,
    customerApprovalSignature: 'optional' // TASK-4683 A3
  },
  localEmailTemplate: {
    replyTo: '',
    subject: 'Invoice {{invoiceNumber}} from {{companyName}}',
    body: 'Please find the invoice details below.\n\nInvoice: {{invoiceNumber}}\nAmount Due: {{amount}}\nPO: {{poNumber}}\nPayment Terms: {{terms}}',
    paymentInstructions: ''
  },
  gmailStatus: { connected: false, email: null, name: null, loaded: false },
  _debounceTimer: null,
  _contractCache: {},
  _initialized: false
};

// ============================================
// TICKET BUILDER STATE
// ============================================
let _tbLocalId = 0;
function tbNextId() { return ++_tbLocalId; }

const TB_SECTIONS = [
  { key: 'timeclock', label: 'Timeclock', icon: 'schedule', defaultOn: true },
  { key: 'startStopTimes', label: 'Work Start/Stop', icon: 'timer', defaultOn: false },
  { key: 'notes', label: 'Notes', icon: 'notes', defaultOn: true },
  { key: 'equipment', label: 'Equipment', icon: 'local_shipping', defaultOn: false },
  { key: 'downtime', label: 'Downtime', icon: 'pause_circle', defaultOn: false },
  { key: 'standby', label: 'Standby', icon: 'hourglass_empty', defaultOn: false },
  // TASK-9567 — the 'pieceRate' section was RETIRED. It had a toggle and a
  // preview here, but neither clock-out form (clock.js buildClockOutModalContent,
  // jobboard.js) ever rendered it, so turning it on did nothing. Drivers record
  // loads / tons / yards through a Custom Section number field tied to a Price
  // Book line (Custom Sections hint below + the in-app FAQ). A saved template
  // that still carries a pieceRate key is ignored (resetTicketBuilder maps only
  // keys in this list) and dropped on the next save. Guard:
  // tests/task9567-cof-sections-rendered.unit.cjs — every key here must be
  // rendered by both clock-out forms.
  // TASK-1483 — Personal Vehicle: miles a person drove their OWN vehicle, PAID
  // TO THEM on payroll. 🔴 Deliberately NOT piece-rate billing: a price-book
  // "Per Mile" unit BILLS THE CUSTOMER (money in); this pays the employee (money
  // out, non-taxable). Separate section, separate label, separate colour on
  // every surface. Consumed by clock.js buildClockOutModalContent and
  // jobboard.js (via window.ClockModule.pvSectionOn/pvSectionHTML) — both
  // honour this toggle through isCOFSectionEnabled/jbCOFEnabled semantics.
  // Only renders for people enrolled (people.hrVehicleMileageRate > 0) on a
  // tenant that turned the feature on (Workforce → Claims).
  { key: 'personalVehicle', label: 'Personal Vehicle', icon: 'directions_car', defaultOn: false },
  { key: 'photos', label: 'Photos', icon: 'photo_camera', defaultOn: false }
];

const TicketBuilderState = {
  sections: TB_SECTIONS.map(s => ({ ...s, enabled: s.defaultOn })),
  header: {
    employeeName: '', employeeId: '',
    customerName: '', customerId: '',
    jobName: '', jobId: '',
    location: '',
    workDate: (() => { const _d = new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })(),
    clockIn: '', clockOut: '',
    poNumber: ''
  },
  shiftNotes: '',
  ticketNotes: '',
  equipment: [],     // [{ assetId, assetName, assetType, fleetNumber, hours, rate }]
  downtime: [],      // [{ personId, personName, hours, rate, notes }]
  standby: [],       // [{ personId, personName, hours, rate, notes }]
  dirty: false,
  smartAssistEnabled: false
};

const INVOICE_BUILDER_SAMPLE_DATA = {
  ticket: {
    id: 'ib-sample', ticketNo: '1042', date: new Date(),
    workDate: (() => { const _d = new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })(),
    customerName: 'Acme Energy LLC', customerId: 'ib-sample-cust',
    jobName: 'Well Pad #7 — Frac Sand Haul', location: 'Watford City, ND',
    poNumber: 'PO-2026-0847', vendorNumber: 'VN-3301', jobType: 'Frac Sand Haul', // TASK-9390 sample
    notes: 'Hauled 12 loads of frac sand to well pad. Road conditions good.',
    workPerformed: 'Hauled 12 loads of frac sand to well pad. Road conditions good.',
    showNotesOnInvoice: true, grandTotal: 6925.00, documents: []
  },
  lines: [
    { type: 'EMPLOYEE', personName: 'Dusty Rhodes', personId: 'p1', role: 'cdl_driver', hours: 10.5, rate: 85, lineAmount: 892.50, fromTime: new Date(2026, 2, 4, 6, 0), toTime: new Date(2026, 2, 4, 16, 30) },
    { type: 'EMPLOYEE', personName: 'Wyatt Earp', personId: 'p2', role: 'laborer', hours: 8.0, rate: 65, lineAmount: 520.00, fromTime: new Date(2026, 2, 4, 7, 0), toTime: new Date(2026, 2, 4, 15, 0) },
    { type: 'EQUIPMENT', assetType: 'truck', assetName: 'Kenworth T800', assetId: 'eq1', fleetNumber: '101', hours: 10.5, rate: 150, lineAmount: 1575.00, rateUnit: 'Hourly', includeOnInvoice: true, fromTime: new Date(2026, 2, 4, 6, 0), toTime: new Date(2026, 2, 4, 16, 30), notes: 'Ran well all day, no issues.' },
    { type: 'EQUIPMENT', assetType: 'trailer', assetName: 'Belly Dump 40T', assetId: 'eq2', fleetNumber: 'TR-05', hours: 10.5, rate: 75, lineAmount: 787.50, rateUnit: 'Hourly', includeOnInvoice: true, fromTime: new Date(2026, 2, 4, 6, 0), toTime: new Date(2026, 2, 4, 16, 30) },
    { type: 'EQUIPMENT', assetType: 'asset', assetName: 'Light Tower', assetId: 'eq3', fleetNumber: 'LT-02', hours: 1, rate: 250, lineAmount: 250.00, rateUnit: 'Daily', quantity: 1, includeOnInvoice: true },
    { type: 'DOWNTIME', personName: 'Dusty Rhodes', personId: 'p1', hours: 1.5, rate: 85, lineAmount: 127.50, notes: 'Waiting on location for crane', fromTime: new Date(2026, 2, 4, 12, 0), toTime: new Date(2026, 2, 4, 13, 30) },
    { type: 'STANDBY', personName: 'Wyatt Earp', personId: 'p2', hours: 2.0, rate: 45, lineAmount: 90.00, notes: 'Standby at yard per customer request', fromTime: new Date(2026, 2, 4, 15, 0), toTime: new Date(2026, 2, 4, 17, 0) },
    { type: 'PERMIT', description: 'Oversize Load Permit — ND DOT', lineAmount: 350.00 }
  ],
  customer: { id: 'ib-sample-cust', name: 'Acme Energy LLC', address: '123 Oil Field Rd, Watford City, ND 58854', phone: '(701) 555-0199', email: 'billing@acmeenergy.com' },
  job: { id: 'ib-sample-job', title: 'Well Pad #7 — Frac Sand Haul', location: 'Watford City, ND' }
};

const LI_CATEGORIES = ['Personnel', 'Heavy Haul', 'Hydrovac', 'Trucks & Trailer', 'Equipment', 'Other'];
const DEFAULT_REVENUE_CATEGORIES = ['Labor', 'Rentals', 'Transportation', 'Materials', 'Services', 'Permits & Fees', 'Fuel', 'Other'];
function getRevenueCategorySuggestions() {
  // Merge defaults with previously-used categories from current line items
  const used = new Set(DEFAULT_REVENUE_CATEGORIES);
  (LineItemsState.lineItems || []).forEach(li => { if (li.revenueCategory) used.add(li.revenueCategory); });
  (LineItemsState.allLineItems || []).forEach(li => { if (li.revenueCategory) used.add(li.revenueCategory); });
  return [...used].sort();
}
const KIND_LABELS = { person: 'People', truck: 'Vehicles', trailer: 'Trailers', asset: 'Assets', misc: 'Miscellaneous', permit: 'Load Permits' };
const KIND_TO_CATEGORY = { person: 'Personnel', truck: 'Trucks & Trailer', trailer: 'Trucks & Trailer', asset: 'Equipment', misc: 'Miscellaneous', permit: 'Load Permits' };
const LI_RATE_UNITS = ['Hourly', 'Daily', 'Monthly'];
const UOM_SUGGESTIONS = ['Hourly', 'Daily', 'Monthly', 'Per Load', 'Per Ton', 'Per Yard', 'Per Mile', 'Per Delivery', 'Each', 'Flat'];
const TIME_BASED_UNITS = new Set(['hourly', 'daily', 'monthly']);
function isTimeBasedUnit(rateUnit) {
  return TIME_BASED_UNITS.has((rateUnit || 'Hourly').toLowerCase());
}
function formatUnitLabel(rateUnit) {
  if (!rateUnit || rateUnit === 'Hourly') return '/hr';
  if (rateUnit === 'Daily') return '/day';
  if (rateUnit === 'Monthly') return '/mo';
  return '/' + rateUnit.replace(/^per\s+/i, '').toLowerCase();
}
const LI_PAYMENT_TERMS = ['Due on Receipt', 'NET15', 'NET30', 'NET45', 'NET60'];

// ============================================
// JOB COLOR PALETTE (16 colors, hashed from jobId)
// ============================================
const JOB_COLORS = [
  '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#0EA5E9', '#6366F1',
  '#059669', '#DC2626', '#7C3AED', '#0891B2', '#CA8A04', '#BE185D'
];

function getJobColor(jobId) {
  if (!jobId) return JOB_COLORS[0];
  let hash = 0;
  for (let i = 0; i < jobId.length; i++) {
    hash = ((hash << 5) - hash) + jobId.charCodeAt(i);
    hash |= 0;
  }
  return JOB_COLORS[Math.abs(hash) % JOB_COLORS.length];
}

// ============================================
// INITIALIZE BILLING VIEW
// ============================================
export function initBilling(container, tenantId) {
  console.log('[Billing] Initializing for tenant:', tenantId);

  if (!tenantId) {
    console.error('[Billing] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <span class="material-icons empty-state-icon">warning</span>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load billing. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  BillingState.tenantId = tenantId;
  BillingState.viewMode = 'calendar';
  BillingState.currentDate = new Date();
  BillingState.ticketJobFilter = null;
  BillingState.ticketJobFilterName = null;
  // TASK-1618 — same per-mount reset as the job filter above.
  BillingState.ticketProjectFilter = null;
  BillingState.ticketProjectFilterName = null;
  BillingState.ticketProjectMode = null;
  BillingState._jobProjectSig = null; // TASK-9414
  BillingState._linkBusy = false;     // TASK-9414
  BillingState.projects = [];
  BillingState._projectsLoaded = false;
  BillingState._projectsLoading = false;
  BillingState._groupBusy = false;
  BillingState.customerFilter = 'all';
  BillingState.ticketCustomerFilter = new Set();
  BillingState.selectedTicketIds = new Set();
  BillingState.expandedCustomers = {};
  BillingState.expandedJobs = {};
  BillingState.expandedProjectFolders = {};
  BillingState._ticketsLoaded = false;
  // TASK-1364 — must reset per mount: a stale `true` from the previous mount
  // would defeat the PO guard in _resolveFallbackPoNumber on this one.
  BillingState._jobsLoaded = false;
  BillingState._jobDaysLoaded = false; // TASK-10043
  // TASK-1245 — new mount generation. Any phase-2 subscription still armed from
  // a previous mount now sees a stale gen and no-ops instead of attaching
  // listeners this mount's cleanup would never tear down.
  _billingMountGen++;
  _cancelSecondarySubs();
  // TASK-1245 (COD Code-Reviewer ❌ + Blast-Radius) — these four are populated by
  // the DEFERRED phase-2 listeners and were never cleared per mount, so until
  // phase 2 lands the view renders the PREVIOUS mount's jobs/jobDays/people/
  // time-off. Pre-existing (the listeners were always async), but deferring
  // widens that window from ~0ms to as much as
  // BILLING_SECONDARY_SUBS_MAX_DELAY_MS — and on a TENANT SWITCH the stale rows
  // belong to the other tenant, which is the part that actually matters. Also
  // closes the `.some(j => j.id === jobId)` escape hatch in
  // _resolveFallbackPoNumber, which would otherwise resolve a PO off a
  // previous-mount job. Clearing is strictly safer than the old behaviour: every
  // consumer already handles the empty array (that's the cold-start state).
  BillingState.jobs = [];
  BillingState.jobDays = [];
  BillingState.people = [];
  BillingState.timeOffRequests = [];
  // TASK-4649 — per-mount reset (same reasoning as the four above: a tenant
  // switch must not flag against the previous tenant's permits).
  BillingState.permits = [];
  BillingState._permitsLoaded = false;
  BillingState.missingPermitFilter = false;
  BillingState.deliveryFilter = null; // TASK-4662 — cleared with the other orthogonal chip
  // TASK-9654 — aging is per tenant: reload on every mount, start unfiltered.
  BillingState.qboAgingFilter = null;
  BillingState._qboAgingByTicket = null;
  BillingState._qboAgingTried = false;
  BillingState._qboAgingCounts = null;
  BillingState.companyLogoUrl = window.AppState?.tenantData?.companyLogoUrl || null;

  // Render the billing UI
  container.innerHTML = getBillingHTML();

  // Add QB setup modal (outside billing container so it layers properly)
  if (!document.getElementById('modal-qb-setup')) {
    document.body.insertAdjacentHTML('beforeend', getQBSetupHTML());
  }

  // Set up modal handlers
  setupModals();

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // Seed item codes for new tenants (fire-and-forget)
  seedItemCodesIfNeeded(tenantId);

  // Check if onboarding wizard should show (non-founder clockbilling subscribers)
  checkBillingOnboarding(tenantId);

  // Render initial view
  renderBillingView();

  // TASK-1364 — deep link from the Clock/Workforce receipt button. The ticket
  // modals live inside getBillingHTML(), so they only exist while THIS view is
  // mounted; clock.js navigates here first and leaves the target behind for us.
  // TASK-9411 — a return target lives for ONE clock-launched visit; a normal
  // Billing visit must never inherit the last one's "back to time clock".
  BillingState._clockReturn = null;
  if (window._clockTicketDeepLink) {
    const cdl = window._clockTicketDeepLink;
    delete window._clockTicketDeepLink;
    // Expire stale hand-offs: if the nav gate bounced the original click (trial
    // wall, cancelled PIN challenge), the flag would otherwise still be sitting
    // there the next time someone opens Billing on their own. 30s is generous
    // for the one path that legitimately delays — typing an access PIN — and
    // short enough that a cancelled challenge can't surprise anyone later.
    if (cdl?.ticketId && Date.now() - (cdl.at || 0) < 30000) {
      // TASK-9411 — remember where the clock hand-off came from so closing this
      // ticket goes back there. Same-tenant only (a tenant switch mid-hop must
      // not steer into another company's workforce screen).
      BillingState._clockReturn = (cdl.returnTo && cdl.returnTo.tenantId === tenantId)
        ? { ...cdl.returnTo, ticketId: cdl.ticketId } : null;
      _syncClockReturnButtons();
      _openTicketDeepLinkWhenReady(cdl.ticketId);
    }
  }
  _drainBillingOpenHint(); // TASK-9671 S2 — a Claude lookup link (one ticket, read only)

  // Handle deep links from BI module (e.g. Outstanding AR → tickets/ready)
  if (window._biDeepLink) {
    const dl = window._biDeepLink;
    delete window._biDeepLink;
    setTimeout(() => {
      if (dl.view === 'tickets') {
        switchBillingView('tickets');
        if (dl.filter) switchFilter(dl.filter);
      }
    }, 300);
  }

  // Initialize logo display + company header
  initBillingLogo();
  updateBillingHeader();

  // TASK-764 — check live QBO status on view load so a dead connection surfaces
  // the amber reconnect banner at the top of the dashboard (fire-and-forget).
  refreshQBOStatus();

  // TASK-9983 — pay changes on closed tickets (fire-and-forget).
  _loadPayChanges(tenantId);
  // TASK-10041 — dispatched-no-ticket review (fire-and-forget; the Tickets view
  // shows its rows inline once it lands).
  _loadDispatchReview(tenantId);
  _loadIntegrity(tenantId); // TASK-10045 S2 — billing warnings (lazy, fire-and-forget)

  // Return cleanup function
  return () => {
    // TASK-1245 — invalidate + disarm phase 2 FIRST. A pending timer that fired
    // after this point would attach listeners nothing tears down.
    _billingMountGen++;
    _cancelSecondarySubs();
    BillingState.unsubscribers.forEach(unsub => unsub());
    BillingState.unsubscribers = [];
    // TASK-857: the tickets listener is managed outside the unsubscribers array
    // (it can be re-subscribed to full history) — tear it down + reset to the
    // bounded default so the next view entry starts windowed again.
    if (_billingTicketsUnsub) { _billingTicketsUnsub(); _billingTicketsUnsub = null; }
    if (_billingMoneyUnsub) { _billingMoneyUnsub(); _billingMoneyUnsub = null; } // TASK-9646 S3b
    _billingMoneyById = new Map();
    _billingTicketTenant = null;
    BillingState._ticketsFullHistory = false;
    BillingState.selectedTicket = null;
    BillingState.selectedTicketLines = [];
    // TASK-4834 — this list MIRRORS the SA.register() block above, and it does
    // so RIGHT NOW: `ticket-po` and `ticket-vendor-number` are gone from both,
    // as of this change. Unregistering a never-registered id is a harmless
    // no-op — the reason to keep the two in step is that a teardown list naming
    // a field which is deliberately NOT a Smart Assistant field is exactly how
    // one gets re-added by reflex. tests/vendor-number-identifier.unit.cjs [3b]
    // fails if this list ever names an id the block above does not register.
    ['ticket-well-name','ticket-well-number','ticket-location','ticket-work',
     'li-qc-name','li-qc-phone','li-qc-email','obw-cust-name','obw-cust-phone','obw-cust-email',
     'tb-shift-notes','tb-ticket-notes']
      .forEach(id => window.SmartAssistant?.unregister(id));
    // Cleanup BI if it was active
    if (typeof BillingState.biCleanup === 'function') {
      BillingState.biCleanup();
      BillingState.biCleanup = null;
    }
  };
}

// ============================================
// HTML TEMPLATE (restructured with content area)
// ============================================
function getBillingHTML() {
  return `
    <div class="tickets-view">
      <!-- Header -->
      <div class="view-header">
        <div class="view-title" style="display:flex;align-items:center;gap:14px;">
          <div class="billing-logo-upload" id="billing-logo-upload" onclick="window.BillingModule.openCompanyInfoModal()" title="Edit company info & logo">
            <img id="billing-logo-img" src="" alt="" style="display:none;">
            <div class="billing-logo-placeholder" id="billing-logo-placeholder">
              <span class="material-icons" style="font-size:48px;color:var(--text-muted);">domain</span>
            </div>
            <div class="billing-logo-edit-badge">
              <span class="material-icons">edit</span>
            </div>
          </div>
          <input type="file" id="billing-logo-input" accept="image/png,image/jpeg,image/webp" style="display:none;" onchange="window.BillingModule.uploadLogo(this)">
          <div style="cursor:pointer;" onclick="window.BillingModule.openCompanyInfoModal()" title="Click to edit company info">
            <h1 id="billing-header-company-name" style="font-size:1.4rem;margin:0;line-height:1.2;">AR / AP</h1>
            <div id="billing-header-company-detail" style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;line-height:1.3;"></div>
          </div>
        </div>
        <div class="view-actions">
          <button class="btn btn-secondary billing-action-secondary" onclick="window.BillingModule.showInvoiceBuilder()" title="Templates">
            <span class="material-icons">tune</span>
            Templates
          </button>
          <button class="btn btn-secondary billing-action-secondary" onclick="window.BillingModule.showLineItemsModal()">
            <span class="material-icons">list_alt</span>
            Line Items
          </button>
          <button class="btn btn-secondary billing-action-secondary" onclick="window.BillingModule.openQBOSetup(event)" title="QuickBooks Setup">
            <span class="material-icons">sync</span>
            QuickBooks
          </button>
          <button class="btn btn-secondary billing-action-secondary btn-neon-settings btn-module-header" onclick="window.BillingModule.openBillingSettings()" title="Billing Settings">
            <span class="material-icons">settings</span>
            Settings
          </button>
          <button class="btn btn-primary" onclick="window.BillingModule.showAddTicket()">
            <span class="material-icons">add</span>
            New Ticket
          </button>
          <div class="billing-overflow-wrap">
            <button class="btn btn-secondary billing-overflow-btn" onclick="window.BillingModule.toggleBillingOverflow(event)" title="More actions">
              <span class="material-icons">more_vert</span>
            </button>
            <div class="billing-overflow-dropdown hidden" id="billing-overflow-dropdown">
              <button onclick="window.BillingModule.showInvoiceBuilder();document.getElementById('billing-overflow-dropdown').classList.add('hidden');">
                <span class="material-icons">description</span> Templates
              </button>
              <button onclick="window.BillingModule.showLineItemsModal();document.getElementById('billing-overflow-dropdown').classList.add('hidden');">
                <span class="material-icons">list_alt</span> Line Items
              </button>
              <button onclick="document.getElementById('billing-overflow-dropdown').classList.add('hidden');window.BillingModule.openQBOSetup(event)">
                <span class="material-icons">sync</span> QuickBooks
              </button>
              <button onclick="window.BillingModule.openBillingSettings();document.getElementById('billing-overflow-dropdown').classList.add('hidden');">
                <span class="material-icons">settings</span> Settings
              </button>
              <button onclick="window.BillingModule.openBillingOnlyJobs()">
                <span class="material-icons">link</span> Billing-only jobs
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- View Mode Toggle + Actions (compact on mobile) -->
      <div class="billing-view-bar" id="billing-view-pills">
        <div class="billing-view-bar-inner">
          <div class="view-toggle view-toggle--labeled">
            <button class="view-btn active" data-view="calendar" onclick="window.BillingModule.switchBillingView('calendar')">
              <span class="material-icons">calendar_month</span>
              <span class="view-btn-label">Calendar</span>
            </button>
            <button class="view-btn" data-view="tickets" onclick="window.BillingModule.switchBillingView('tickets')">
              <span class="material-icons">receipt_long</span>
              <span class="view-btn-label">Tickets</span>
            </button>
            ${/* TASK-1618 — Projects. The money lens on the same `projects`
                 collection Dispatch already owns: QuickBooks Projects leads with
                 Income/Costs/Profit and our Dispatch projects view has no money in
                 it at all. Sits on the AR side of the divider because it reads the
                 receivable, not the payable.
                 TASK-9651 — only for a login holding the 'projects' module. */''}
            ${window.canAccessProjects?.() === false ? '' : `<button class="view-btn" data-view="projects" onclick="window.BillingModule.switchBillingView('projects')" title="Group jobs into one engagement and see its money">
              <span class="material-icons">folder_special</span>
              <span class="view-btn-label">Projects</span>
            </button>`}
            <div class="view-toggle-divider"></div>
            <button class="view-btn" data-view="ap" onclick="window.BillingModule.navigateToAP()" title="Accounts Payable">
              <span class="material-icons">account_balance</span>
              <span class="view-btn-label">AP</span>
            </button>
            ${/* TASK-4867 — Reconcile. Lacey asked for it in this strip and the
                 usage data agrees: bi.js:519 is the ONLY entry point to
                 Reconciliation anywhere in the app, and across all 93 tenants
                 there are 3 reconciliationSessions total (TITS has one, from
                 2026-06-10). She reconciles card statements against driver
                 expenses — and those live one pill over, in AP.

                 v1 is a DEEP LINK, deliberately: reconciliation.js runs entirely
                 off BiState (revenue, costs, customers) which only initBI loads,
                 so hosting it natively here means loading BI's data here. That is
                 a separate, much larger task. The arrow glyph + title say out
                 loud that this leaves Billing, so a tap doesn't feel like a
                 dumping.

                 🔴 MODULE-GATED AT RENDER (COD Edge Case, RISK 🔴): `bi` is a
                 PER-MEMBER module. At TITS, steven@ and brian@ hold `billing`
                 and NOT `bi` — an ungated pill would deep-link them into a view
                 navigateToView() bounces. Hidden, not disabled: a locked pill
                 that does nothing generates support tickets (COD UX). */''}
            ${hasBIAccess() ? `
            <div class="view-toggle-divider"></div>
            <button class="view-btn" onclick="window.BillingModule.navigateToReconcile()" title="Reconcile card statements against expenses — opens Business Intelligence">
              <span class="material-icons">balance</span>
              <span class="view-btn-label">Reconcile</span>
              <span class="material-icons view-btn-jump" aria-hidden="true">north_east</span>
            </button>` : ''}
          </div>
          <div class="billing-bar-actions">
            <button class="btn btn-primary billing-bar-add" onclick="window.BillingModule.showAddTicket()" title="New Ticket">
              <span class="material-icons">add</span>
            </button>
            <div class="billing-overflow-wrap billing-bar-overflow">
              <button class="btn btn-secondary billing-overflow-btn" onclick="window.BillingModule.toggleBillingOverflow(event)" title="More actions">
                <span class="material-icons">more_vert</span>
              </button>
              <div class="billing-overflow-dropdown hidden" id="billing-overflow-dropdown-bar">
                <button onclick="window.BillingModule.showInvoiceBuilder();document.getElementById('billing-overflow-dropdown-bar').classList.add('hidden');">
                  <span class="material-icons">tune</span> Templates
                </button>
                <button onclick="window.BillingModule.showLineItemsModal();document.getElementById('billing-overflow-dropdown-bar').classList.add('hidden');">
                  <span class="material-icons">list_alt</span> Line Items
                </button>
                <button onclick="window.BillingModule.openBillingSettings();document.getElementById('billing-overflow-dropdown-bar').classList.add('hidden');">
                  <span class="material-icons">settings</span> Settings
                </button>
                <button onclick="window.BillingModule.openBillingOnlyJobs()">
                  <span class="material-icons">link</span> Billing-only jobs
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TASK-764 — persistent QBO reauth banner slot (populated by renderQBOReauthBanner) -->
      <div id="billing-qbo-banner" style="display:none;"></div>
      <!-- TASK-9983 — pay changed after invoicing (populated by renderPayChangesBanner) -->
      <div id="billing-paychanges-banner" style="display:none;"></div>

      <!-- Dynamic Content Area (calendar or tickets) -->
      <div id="billing-content-area">
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    </div>

    <!-- Add/Edit Ticket Modal -->
    <div class="modal hidden" id="modal-ticket">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-ticket-title">New Ticket</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeModal('ticket')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-ticket" novalidate onsubmit="window.BillingModule.saveTicket(event)">
          <input type="hidden" id="ticket-id">
          <input type="hidden" id="ticket-job-id">

          <div class="form-row">
            <div class="form-group">
              <label for="ticket-date">Date *</label>
              <input type="date" id="ticket-date" required>
            </div>
            <div class="form-group">
              <label for="ticket-status">Status</label>
              <select id="ticket-status">
                <option value="draft">Draft</option>
                <option value="ready">Ready</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="ticket-customer">Customer *</label>
              <select id="ticket-customer" required>
                <option value="">Select customer...</option>
              </select>
              <div id="ticket-customer-hint"></div>
            </div>
            <div class="form-group">
              <label for="ticket-po">PO Number</label>
              <input type="text" id="ticket-po" placeholder="PO-12345">
            </div>
            <div class="form-group">
              <label for="ticket-vendor-number">Vendor Number</label>
              <input type="text" id="ticket-vendor-number" placeholder="Vendor #">
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="ticket-well-name">Well Name</label>
              <input type="text" id="ticket-well-name" placeholder="Smith 1-H" data-maxlen="100">
            </div>
            <div class="form-group">
              <label for="ticket-well-number">Well #</label>
              <input type="text" id="ticket-well-number" placeholder="001">
            </div>
          </div>

          <div class="form-group">
            <label for="ticket-location">Location</label>
            <input type="text" id="ticket-location" placeholder="NW/4 Sec 12, T5N, R6W">
          </div>

          <div class="form-group">
            <label for="ticket-work">Work Performed</label>
            <textarea id="ticket-work" rows="4" placeholder="Describe work performed..."></textarea>
          </div>

          <!-- Employees Section -->
          <div class="form-section">
            <div class="form-section-header">
              <span class="material-icons">people</span>
              <span>Employees</span>
            </div>
            <div id="ticket-employees" class="employee-list"></div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addEmployeeRow()">
              <span class="material-icons">add</span> Add Employee
            </button>
          </div>

          <!-- Work Entries Section -->
          <div class="form-section">
            <div class="form-section-header">
              <span class="material-icons">schedule</span>
              <span>Time Entries</span>
            </div>
            <div id="ticket-time-entries" class="time-entries-list"></div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addTimeEntry()">
              <span class="material-icons">add</span> Add Time Entry
            </button>
          </div>

          <!-- Materials Section -->
          <div class="form-section">
            <div class="form-section-header">
              <span class="material-icons">inventory_2</span>
              <span>Materials</span>
            </div>
            <div id="ticket-materials" class="materials-list"></div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addMaterialRow()">
              <span class="material-icons">add</span> Add Material
            </button>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="window.BillingModule.closeModal('ticket')">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Ticket</button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Ticket Detail Modal -->
    <div class="modal hidden" id="modal-view-ticket">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="view-ticket-title">Ticket Details</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeViewTicketModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="view-ticket-content" class="ticket-detail-view" style="max-height:70vh;overflow-y:auto;padding:var(--spacing-lg);">
          <!-- Populated dynamically -->
        </div>
        <div class="form-actions" id="view-ticket-actions"></div>
      </div>
    </div>

    <!-- Job Detail Modal -->
    <div class="modal hidden" id="modal-billing-job">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="billing-job-title">Job Details</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeModal('billing-job')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="billing-job-content" style="max-height:70vh;overflow-y:auto;padding:var(--spacing-lg);">
          <!-- Populated dynamically -->
        </div>
        <div class="form-actions" id="billing-job-actions">
          <button class="btn btn-secondary" onclick="window.BillingModule.closeModal('billing-job')">Close</button>
        </div>
      </div>
    </div>

    <!-- Day Details Modal -->
    <div class="modal hidden" id="modal-billing-day">
      <div class="modal-content" style="max-height:85vh;max-height:85dvh;display:flex;flex-direction:column;">
        <div class="modal-header">
          <h3 id="billing-day-title">Day Details</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeModal('billing-day')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="billing-day-content" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;min-height:0;padding:var(--spacing-md);">
          <!-- Populated dynamically -->
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal hidden" id="modal-delete-ticket">
      <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);">
        <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
          <span class="material-icons" style="font-size:48px;color:#dc2626;">delete_forever</span>
          <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Confirm Delete</h3>
        </div>
        <p id="delete-ticket-message" style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);line-height:1.5;">Are you sure you want to delete this ticket?</p>
        <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
          <button class="btn btn-secondary" style="flex:1;max-width:140px;" onclick="window.BillingModule.closeModal('delete-ticket')">Cancel</button>
          <button class="btn btn-danger" style="flex:1;max-width:140px;" id="btn-confirm-delete-ticket">Delete</button>
        </div>
      </div>
    </div>

    <!-- Review Ticket Modal -->
    <!-- Item Codes Modal -->
    <div class="modal hidden" id="modal-billing-itemcodes">
      <div class="modal-content" style="max-width:640px;max-height:85vh;max-height:85dvh;display:flex;flex-direction:column;">
        <div class="modal-header">
          <h3>Item Codes</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeModal('billing-itemcodes')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="billing-itemcodes-body" style="flex:1;overflow-y:auto;padding:var(--spacing-lg);min-height:0;">
        </div>
      </div>
    </div>

    <div class="modal hidden" id="modal-review-ticket">
      <div class="modal-content modal-fullscreen" id="review-ticket-modal-content">
        <div class="modal-header">
          <h3 id="review-ticket-title">Review Ticket</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeReviewModal(true)">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="review-ticket-body" style="max-height:75vh;overflow-y:auto;padding:var(--spacing-lg);">
          <div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>
        </div>
        <div class="form-actions review-ticket-footer" id="review-ticket-footer" style="display:flex;justify-content:space-between;align-items:center;">
          <button class="btn btn-secondary review-footer-delete" onclick="window.BillingModule.deleteReviewTicket()" title="Delete this ticket">
            <span class="material-icons" style="font-size:16px;">delete_outline</span> Delete
          </button>
          <div style="display:flex;gap:8px;align-items:center;">
            <button class="btn review-footer-cancel" onclick="window.BillingModule.closeReviewModal(true)">Cancel</button>
            <button class="btn btn-secondary review-footer-print" onclick="window.BillingModule.printReviewTicket()">
              <span class="material-icons">print</span> Print
            </button>
            <button class="btn btn-secondary review-footer-email" onclick="window.BillingModule.emailReviewTicket()" title="Email this field ticket to the customer">
              <span class="material-icons">send</span> Email
            </button>
            <button class="btn btn-primary review-footer-save" id="btn-save-review" onclick="window.BillingModule.saveReviewTicket()">
              <span class="material-icons">save</span> Save Ticket
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Line Items Management Modal -->
    <div class="modal hidden" id="modal-billing-lineitems">
      <div class="modal-content modal-fullscreen">
        <div class="modal-header">
          <h3>Line Items</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeLineItemsModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="billing-lineitems-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;min-height:0;padding:var(--spacing-lg);">
          <div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>
        </div>
      </div>
    </div>

    <!-- Quick Add Customer Modal -->
    <div class="modal hidden" id="modal-li-quick-customer">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Add Customer</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-li-quick-customer').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-li-quick-customer" novalidate onsubmit="window.BillingModule.saveLiQuickCustomer(event)">
          <div style="padding:var(--spacing-lg);">
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Company Name *</label>
              <input type="text" id="li-qc-name" class="form-control" required placeholder="Acme Trucking">
            </div>
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Phone</label>
              <input type="tel" id="li-qc-phone" class="form-control" placeholder="555-123-4567">
            </div>
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Email</label>
              <input type="email" id="li-qc-email" class="form-control" placeholder="billing@company.com">
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-li-quick-customer').classList.add('hidden')">Cancel</button>
            <button type="submit" class="btn btn-primary">Add Customer</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Billing Settings Modal -->
    <div class="modal hidden" id="modal-billing-settings">
      <div class="modal-content" style="max-width:480px;display:flex;flex-direction:column;">
        <div class="modal-header" style="flex-shrink:0;">
          <h3>Billing Settings</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeBillingSettings()" style="color:var(--text-secondary);">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="padding:var(--spacing-lg);overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">
          <!-- TASK-572 — the clock-out MODE selector is the master switch that decides
               whether half the other toggles even matter, so it's pinned at the TOP,
               always visible, NEVER inside an accordion. -->
          <div class="form-group" style="margin-bottom:16px;">
            <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);margin-bottom:8px;display:block;">When employees clock out:</label>
            <div style="display:flex;flex-direction:column;gap:8px;">
              <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);transition:border-color 0.15s;" id="clockout-mode-simple-label">
                <input type="radio" name="clockout-mode" value="simple" id="billing-settings-clockout-simple" style="margin-top:2px;accent-color:var(--brand-blue);"
                  onchange="document.querySelectorAll('[id^=clockout-mode-][id$=-label]').forEach(l=>l.style.borderColor='var(--border-light)');this.closest('label').style.borderColor='var(--brand-blue)';window.BillingModule.onClockOutModeChange(this.value);">
                <div>
                  <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:flex;align-items:center;gap:4px;">
                    <span class="material-icons" style="font-size:16px;">schedule</span> Simple Clock Out
                  </div>
                  <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">Record time and notes only. Best for hourly labor, landscaping, cleaning crews.</div>
                </div>
              </label>
              <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);transition:border-color 0.15s;" id="clockout-mode-ticket-label">
                <input type="radio" name="clockout-mode" value="ticket" id="billing-settings-clockout-ticket" style="margin-top:2px;accent-color:var(--brand-blue);"
                  onchange="document.querySelectorAll('[id^=clockout-mode-][id$=-label]').forEach(l=>l.style.borderColor='var(--border-light)');this.closest('label').style.borderColor='var(--brand-blue)';window.BillingModule.onClockOutModeChange(this.value);">
                <div>
                  <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:flex;align-items:center;gap:4px;">
                    <span class="material-icons" style="font-size:16px;">receipt_long</span> Clock + Ticket
                  </div>
                  <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">Drivers clock in/out AND build a ticket at clock-out. Best for oilfield, construction, field service.</div>
                </div>
              </label>
              <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);transition:border-color 0.15s;" id="clockout-mode-ticketonly-label">
                <input type="radio" name="clockout-mode" value="ticketonly" id="billing-settings-clockout-ticketonly" style="margin-top:2px;accent-color:var(--brand-blue);"
                  onchange="document.querySelectorAll('[id^=clockout-mode-][id$=-label]').forEach(l=>l.style.borderColor='var(--border-light)');this.closest('label').style.borderColor='var(--brand-blue)';window.BillingModule.onClockOutModeChange(this.value);">
                <div>
                  <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:flex;align-items:center;gap:4px;">
                    <span class="material-icons" style="font-size:16px;">description</span> Ticket Only
                  </div>
                  <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">No separate clock-in — drivers tap Start Ticket on a job, then Finalize Ticket when done. Hours are the time between the two. Best when you mainly need field tickets.</div>
                </div>
              </label>
            </div>
            <!-- TASK-9553 — per-person override of the mode above. Its own sub-modal
                 with per-person saves (audited old → new); nothing rides the shared
                 Save below. -->
            <div style="display:flex;align-items:center;gap:10px;margin-top:8px;padding:10px 12px;border:1px dashed var(--border-light);border-radius:var(--radius-md);">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">group</span>
              <div style="flex:1;min-width:0;">
                <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Different mode for some people</div>
                <div style="font-size:0.78rem;color:var(--text-secondary);" id="billing-person-clockmode-summary">Everyone uses the mode above.</div>
              </div>
              <button type="button" class="btn btn-secondary btn-sm" id="billing-person-clockmode-open" onclick="window.BillingModule.openPersonClockModes()">Set per person</button>
            </div>
          </div>

          <!-- TASK-9373 — Driver App "template": what PIN drivers see (tabs, Actions
               tiles, job-detail sections) + every driver-side switch, mirrored. Its own
               sub-modal with per-switch saves — nothing here rides the shared Save
               below (saveBillingSettings must not absorb keys it does not own). -->
          <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;padding:12px 16px;display:flex;align-items:center;gap:10px;min-height:44px;">
            <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">smartphone</span>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:600;font-size:0.9rem;">Driver App</div>
              <div style="font-size:0.78rem;color:var(--text-secondary);" id="billing-driver-app-summary">Tabs, Actions tiles and job-detail sections your drivers see.</div>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.openDriverAppSettings()">Configure</button>
          </div>

          <!-- TASK-572 — collapsible sections (reuses the QB-wizard accordion primitive:
               toggleQBSection + .qb-wiz-chevron). Collapse is CSS display:none on the
               section BODY ONLY — every <input> stays in the DOM at all times because
               saveBillingSettings reads each by getElementById(id)?.checked ?? false,
               so a removed input would silently wipe that tenant setting. -->

          <!-- Section: Tickets & pricing — shown in EVERY clock-out mode: tax, permit
               markup, invoice hiding, equipment rounding and accounting method are
               company money settings that apply in Simple mode too. Only the
               #billing-tickets-mode-only rows hide in simple mode
               (syncBillingModeVisibility); inputs stay in the DOM, the save-side
               reconcile is the data backstop. -->
          <div id="billing-acc-tickets-wrap" style="border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;overflow:hidden;">
            <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;min-height:44px;cursor:pointer;background:var(--bg-secondary);user-select:none;" onclick="window.BillingModule.toggleQBSection('billing-acc-tickets')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">receipt_long</span>
              <span style="font-weight:600;font-size:0.9rem;">Tickets &amp; pricing</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="billing-acc-tickets" style="padding:16px;display:none;border-top:1px solid var(--border-light);">
              <!-- Ticket-mode only: building tickets at clock-out, ship slips (attach
                   to driver-built tickets), per-operator equipment (decides which
                   equipment lands on each crew member's ticket). Hidden in simple mode. -->
              <div id="billing-tickets-mode-only">
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-build-tickets">
                  <label for="billing-settings-build-tickets" style="cursor:pointer;margin:0;">Build billing tickets from clock entries</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, clocking out creates billing ticket line items for hours, equipment, downtime, and standby. When off, clock works as a simple time tracker.
                </p>
              </div>
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-enable-ship-slips">
                  <label for="billing-settings-enable-ship-slips" style="cursor:pointer;margin:0;">Enable Ship Slips</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Drivers can attach printable ship slips (a.k.a. drop slips, slip tickets) to a ticket. Each slip captures lease/well info, tank readings, and customer rep details for proof of delivery. Configure the slip layout in Templates → Ship Slip.
                </p>
              </div>
              <!-- TASK-491 — per-operator equipment tracking. DEFAULT OFF. When OFF,
                   clock-in is one-tap and every job's equipment auto-fills onto every
                   crew member's ticket exactly as before (zero change). When ON, each
                   driver confirms which equipment THEY ran at clock-in, and only that
                   equipment (for their own hours) lands on their ticket. -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-equipment-tie">
                  <label for="billing-settings-equipment-tie" style="cursor:pointer;margin:0;">Track equipment by operator (assign equipment at clock-in)</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, each crew member confirms which trucks/trailers/equipment they'll be in when they clock in. Only that equipment — billed for that crew member's own hours — lands on their ticket. Leave off (the default) and every crew member's ticket auto-fills with all of the job's equipment, exactly as today. Best for jobs where multiple trucks run different hours.
                </p>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              </div>
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-tax-enabled" onchange="document.getElementById('billing-settings-tax-detail').style.display=this.checked?'block':'none'">
                  <label for="billing-settings-tax-enabled" style="cursor:pointer;margin:0;font-weight:600;">Enable Tax on Billing Tickets</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, new tickets default to the tax rate and categories below. Each ticket can override.
                </p>
                <div id="billing-settings-tax-detail" style="display:none;margin-top:12px;padding:12px;background:var(--bg-secondary);border-radius:var(--radius-md);">
                  <div class="form-group" style="margin-bottom:12px;">
                    <label for="billing-settings-tax-rate" style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">Default Tax Rate (%)</label>
                    <input type="number" id="billing-settings-tax-rate" class="form-control" step="0.01" min="0" max="100" placeholder="e.g. 8.5" style="width:120px;margin-top:4px;">
                  </div>
                  <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);margin-bottom:8px;">Taxable Categories</div>
                  <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-labor" checked> Labor
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-equipment" checked> Equipment
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-permits" checked> Load Permits
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-fieldItems" checked> Field Items
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-misc" checked> Miscellaneous
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-downtime" checked> Downtime
                    </label>
                    <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;">
                      <input type="checkbox" id="billing-settings-tax-cat-standby" checked> Standby
                    </label>
                  </div>
                </div>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              <!-- TASK-9436 — company-wide permit markup. Used only when a customer's
                   Price Book has no permit markup of its own. Defaults to 0: no tenant
                   gets a markup it did not set. -->
              <div class="form-group" style="margin-bottom:16px;">
                <label for="billing-settings-permit-markup" style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Default Permit Markup (%)</label>
                <input type="number" id="billing-settings-permit-markup" class="form-control" step="0.01" min="0" max="100" placeholder="0" style="width:120px;margin-top:4px;">
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Added on top of the permit cost when a customer's Price Book doesn't set its own permit markup. Leave at 0 to bill permits at cost. You can change it on any ticket.
                </p>
              </div>
              <!-- TASK-9667 — per-item invoice hiding. Default OFF: with it off,
                   invoices are exactly what they were. -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-per-item-invoice-hide">
                  <label for="billing-settings-per-item-invoice-hide" style="cursor:pointer;margin:0;">Hide individual Price Book items on invoices</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When a Price Book item's <strong>Invoice</strong> box is unticked for a customer, the field-ticket and misc lines it creates are left off that customer's invoices. The quantity is still recorded on the ticket. Applies to tickets closed out after you turn it on.
                  <strong>Taxable items can't be hidden per item yet:</strong> if sales tax is on and the line has a price, it stays on the invoice. $0 items can always be hidden.
                </p>
              </div>
              <!-- TASK-9489 — company "round equipment to the half hour" default.
                   Saves on its own through setEquipmentRoundingDefault (audited
                   old -> new), never with the shared Save button below. -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-round-equipment" onchange="window.BillingModule.onEquipmentRoundingToggle(this)">
                  <label for="billing-settings-round-equipment" style="cursor:pointer;margin:0;">Round equipment hours up to the half hour</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  For every Price Book line set to <strong>Company default</strong>, and for customers with no Price Book yet. Example: a truck on for 4.43 h bills 4.5 h. Lines set to <strong>Always</strong> or <strong>Never</strong> keep their own choice. Employee pay is never rounded, and invoiced tickets never change.
                  This switch saves as soon as you change it.
                </p>
              </div>
              <!-- TASK-4875 — company labor billing. OFF by default (crew lines bill
                   $0, the truck carries the money). Saves on its own through
                   setLaborBillingSetting (owner or billing module, audited
                   old -> new), never with the shared Save button below. -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-bill-labor" onchange="window.BillingModule.onLaborBillingToggle(this)">
                  <label for="billing-settings-bill-labor" style="cursor:pointer;margin:0;">Bill labor hours from the Price Book</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Off: crew (man-hour) lines show the hours at $0 and the truck carries the money. On: crew hours are billed at the rate on each customer's Price Book crew lines (a customer with no priced crew line still bills crew at $0). Invoiced tickets never change.
                  This switch saves as soon as you change it.
                </p>
              </div>
              <!-- TASK-9982 — ticket time edits -> time clock (payroll). OFF by
                   default. Saves on its own through setTicketClockSyncSetting
                   (owner or billing module, audited old -> new). -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-ticket-times-clock" onchange="window.BillingModule.onTicketClockSyncToggle(this)">
                  <label for="billing-settings-ticket-times-clock" style="cursor:pointer;margin:0;">Editing an employee's times on a ticket also updates their time clock (changes payroll)</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Off: changing a crew member's in/out times in Review changes the ticket only. Their time clock, and what they are paid, stays as clocked. On: the same change also moves their time clock entry, which changes payroll.
                  This switch saves as soon as you change it.
                </p>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              <!-- TASK-9440 — fuel surcharge, flat %. OFF by default. Saved by its OWN
                   button through the setFuelSurchargeSettings callable (owner or the
                   billing module; audited old -> new), never by the Save below. -->
              <div class="form-group" id="billing-settings-fuel-block" style="margin-bottom:16px;">
                <label for="fuel-settings-mode" style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Fuel Surcharge</label>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Adds a "Fuel Surcharge" line to tickets and invoices: a percent of the charges you pick (equipment by default). Draft tickets update when you save here. Tickets already marked ready, sent to the customer, or invoiced keep the % they had.
                </p>
                <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:6px;">
                  <select id="fuel-settings-mode" class="form-control" style="max-width:150px;">
                    <option value="off">Off</option>
                    <option value="flat">Flat %</option>
                  </select>
                  <input type="number" id="fuel-settings-percent" class="form-control" step="0.01" min="0.01" max="100" placeholder="e.g. 12" style="width:110px;" aria-label="Fuel surcharge percent">
                  <span style="font-size:0.85rem;font-weight:600;color:var(--text-secondary);">%</span>
                </div>
                <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin:10px 0 6px;">Applies to</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-equipment" checked> Equipment</label>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-labor"> Labor</label>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-permits"> Load Permits</label>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-fieldItems"> Field Items</label>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-misc"> Miscellaneous</label>
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;"><input type="checkbox" id="fuel-settings-cat-standby"> Standby</label>
                </div>
                <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;cursor:pointer;margin-top:8px;">
                  <input type="checkbox" id="fuel-settings-taxable"> Charge sales tax on the surcharge &mdash; off by default; only applies when ticket tax is on. This is your accountant's call.
                </label>
                <div style="display:flex;align-items:center;gap:8px;margin-top:10px;flex-wrap:wrap;">
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-save-fuel-settings" onclick="window.BillingModule.saveFuelSurchargeCompany()">Save Fuel Surcharge</button>
                  <span id="fuel-settings-status" style="font-size:0.8rem;color:var(--text-secondary);"></span>
                </div>
                <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin:14px 0 6px;">Customer &amp; Price Book overrides</div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 6px;">
                  A Price Book's % wins over its customer's, and a customer's wins over the company %. "No surcharge" turns it off for that customer or Price Book.
                </p>
                <div id="fuel-overrides-list"></div>
                <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-top:6px;">
                  <select id="fuel-ovr-customer" class="form-control" style="max-width:200px;font-size:0.8rem;" onchange="window.BillingModule.loadFuelOverridePriceBooks(this.value)" aria-label="Customer"></select>
                  <select id="fuel-ovr-pricebook" class="form-control" style="max-width:200px;font-size:0.8rem;" aria-label="Price Book">
                    <option value="">All of this customer's Price Books</option>
                  </select>
                  <select id="fuel-ovr-mode" class="form-control" style="max-width:140px;font-size:0.8rem;" aria-label="Override">
                    <option value="flat">Flat %</option>
                    <option value="off">No surcharge</option>
                  </select>
                  <input type="number" id="fuel-ovr-percent" class="form-control" step="0.01" min="0.01" max="100" placeholder="%" style="width:80px;font-size:0.8rem;" aria-label="Override percent">
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-save-fuel-override" onclick="window.BillingModule.saveFuelSurchargeOverride()">Save Override</button>
                </div>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              <div class="form-group" style="margin-bottom:0;">
                <label style="font-weight:600;font-size:0.85rem;color:var(--text-primary);margin-bottom:8px;display:block;">Accounting Method</label>
                <select id="billing-settings-accounting-basis" class="form-control" style="max-width:200px;">
                  <option value="cash">Cash Basis</option>
                  <option value="accrual">Accrual Basis</option>
                </select>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Cash: revenue when paid. Accrual: revenue when invoiced. Your CPA can advise.
                </p>
              </div>
            </div>
          </div>

          <!-- Section: Driver permissions -->
          <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;overflow:hidden;">
            <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;min-height:44px;cursor:pointer;background:var(--bg-secondary);user-select:none;" onclick="window.BillingModule.toggleQBSection('billing-acc-driver')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">badge</span>
              <span style="font-weight:600;font-size:0.9rem;">Driver permissions</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="billing-acc-driver" style="padding:16px;display:none;border-top:1px solid var(--border-light);">
              <div class="form-group" id="billing-row-show-driver-tickets" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-show-driver-tickets">
                  <label for="billing-settings-show-driver-tickets" style="cursor:pointer;margin:0;">Allow drivers to access E-Tickets on Job Board</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Let drivers view and interact with their E-Tickets on the Job Board. Useful for transparency and driver sign-off.
                </p>
              </div>
              <div class="form-group" id="billing-row-show-driver-prices" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-show-driver-prices">
                  <label for="billing-settings-show-driver-prices" style="cursor:pointer;margin:0;">Show prices on driver tickets</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When on, the Job Board shows drivers the rates and dollar amounts on their tickets. When off, it shows only hours and line item descriptions. This hides prices on the driver's screen only.
                </p>
              </div>
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-allow-driver-edit">
                  <label for="billing-settings-allow-driver-edit" style="cursor:pointer;margin:0;">Allow drivers to edit their clock times</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, drivers can edit their own clock entries from the timesheet view. When off, only dispatchers and admins can modify time records. Changing a ticket after clock-out is a separate switch for each person, on their Roster card.
                </p>
              </div>
              <!-- TASK-428 Slice 3 — let drivers EDIT equipment on their clock-out ticket.
                   DEFAULT OFF (dispatch owns equipment). UI-only gate, mirrors
                   allowDriverTimeEdit: when OFF, a driver clocking out still SEES the
                   auto-filled equipment and can add per-equipment notes, but cannot
                   add/remove/re-time equipment lines. When ON, the driver can correct
                   equipment in the field. Supervisors/admins always have full edit.
                   Never blocks the clock-out save (auto-filled lines save regardless). -->
              <div class="form-group" id="billing-row-allow-driver-equipment-edit" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-allow-driver-equipment-edit">
                  <label for="billing-settings-allow-driver-equipment-edit" style="cursor:pointer;margin:0;">Allow drivers to edit equipment on their ticket</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, drivers can add, remove, and adjust the equipment lines on their own field ticket at clock-out. When off (the default), dispatch owns the equipment assignment — drivers still see the assigned equipment and can leave notes, but can't change which equipment is on the ticket.
                </p>
              </div>
              <div class="form-group" style="margin-bottom:0;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-clockin-ask-customer">
                  <label for="billing-settings-clockin-ask-customer" style="cursor:pointer;margin:0;">Ask drivers to select a customer at clock-in</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When enabled, drivers can optionally select a customer and job when clocking in. Always required for ticket-building accounts.
                </p>
              </div>
            </div>
          </div>

          <!-- Section: Clock tracking -->
          <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;overflow:hidden;">
            <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;min-height:44px;cursor:pointer;background:var(--bg-secondary);user-select:none;" onclick="window.BillingModule.toggleQBSection('billing-acc-clock')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">schedule</span>
              <span style="font-weight:600;font-size:0.9rem;">Clock tracking</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="billing-acc-clock" style="padding:16px;display:none;border-top:1px solid var(--border-light);">
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-capture-gps-on-clock">
                  <label for="billing-settings-capture-gps-on-clock" style="cursor:pointer;margin:0;">Capture GPS at clock-in/out</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Stamp each clock-in and clock-out event with the driver's GPS coordinates (foreground only — no continuous tracking). Visible to dispatchers on the Maps view as proof of presence. Drivers will be prompted once for location permission.
                </p>
              </div>
              <!-- TASK-9554 — where an inspection was submitted. Default OFF (opt-in). -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-capture-gps-on-inspection">
                  <label for="billing-settings-capture-gps-on-inspection" style="cursor:pointer;margin:0;">Capture GPS on inspections</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Record where each truck, trailer and equipment inspection was submitted (one location reading at Submit — no tracking). Shown on the inspection and its PDF, so anyone who can see the inspection can see where it was done. Drivers will be asked once for location permission; if they say no, the inspection still saves and shows "location not captured". Let your drivers know before turning this on.
                </p>
              </div>
              <!-- TASK-191 Phase 2 — Geofence defaults. Flags (never blocks) clock-ins/outs
                   that land outside a job's site fence. Per-job overrides live on the
                   dispatch job form; these are the tenant-wide defaults. -->
              <div class="form-group" style="margin-bottom:16px;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-geofence-autowaypoint">
                  <label for="billing-settings-geofence-autowaypoint" style="cursor:pointer;margin:0;">Auto-use a job's first destination as the geofence center</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  When a job has no explicit geofence configured, fall back to its first destination. Off by default — turn on only if drivers clock in at the job site (not the yard), otherwise every clock-in will flag "out of zone." Advisory only — drivers are never blocked from clocking.
                </p>
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-top:10px;">
                  <label for="billing-settings-geofence-radius" style="margin:0;font-size:0.85rem;white-space:nowrap;">Default geofence radius</label>
                  <select id="billing-settings-geofence-radius" class="form-control" style="width:160px;">
                    <option value="250">250 ft</option>
                    <option value="500" selected>500 ft (default)</option>
                    <option value="1000">1,000 ft</option>
                    <option value="2500">2,500 ft</option>
                    <option value="5280">1 mile</option>
                  </select>
                </div>
                <p style="font-size:0.78rem;color:var(--text-muted);margin-top:6px;">
                  Clock-ins/outs farther than this from the job site get an "out of zone" flag on the shift review — but the clock-in/out still goes through.
                </p>
              </div>
              <div class="form-group" style="margin-bottom:0;">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="billing-settings-clockout-alert" onchange="document.getElementById('billing-settings-clockout-alert-detail').style.display=this.checked?'flex':'none'">
                  <label for="billing-settings-clockout-alert" style="cursor:pointer;margin:0;">Clock Out Reminder</label>
                </div>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
                  Send an SMS reminder to drivers who have been clocked in longer than the threshold below. Uses SMS credits. Only sends to drivers with a phone number on file.
                </p>
                <div id="billing-settings-clockout-alert-detail" style="display:none;flex-direction:column;align-items:flex-start;gap:8px;margin-top:8px;">
                  <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                    <label for="billing-settings-clockout-alert-hours" style="margin:0;font-size:0.85rem;white-space:nowrap;">Remind the driver after</label>
                    <input type="number" id="billing-settings-clockout-alert-hours" class="form-control" min="4" max="24" step="1" value="${resolveClockOutAlertHours().remindHours}" style="width:70px;text-align:center;">
                    <span style="font-size:0.85rem;color:var(--text-secondary);">hours</span>
                  </div>
                  <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                    <label for="billing-settings-clockout-alert-esc-hours" style="margin:0;font-size:0.85rem;white-space:nowrap;">Escalate to admins after</label>
                    <input type="number" id="billing-settings-clockout-alert-esc-hours" class="form-control" min="8" max="48" step="1" value="${resolveClockOutAlertHours().escalateHours}" style="width:70px;text-align:center;">
                    <span style="font-size:0.85rem;color:var(--text-secondary);">hours</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section: Payments & integrations — COLLAPSED by default like the other
               three (TASK-9383, Chris 2026-09-24). TASK-572 shipped this one EXPANDED on
               the COD Skeptic's "never bury a broken integration behind a collapsed
               header"; Chris took the Skeptic's OTHER option: collapsed + an amber
               "Needs attention" chip on the header (#billing-acc-payments-chip, driven
               by syncPaymentsAttentionChip) whenever QBO needs reauth or Stripe Connect
               onboarding is incomplete. The title is flex:1 so on a phone the TITLE
               wraps and the chip stays on the row instead of overflowing it. -->
          <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;overflow:hidden;">
            <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;min-height:44px;cursor:pointer;background:var(--bg-secondary);user-select:none;" onclick="window.BillingModule.toggleQBSection('billing-acc-payments')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">payment</span>
              <span style="font-weight:600;font-size:0.9rem;flex:1;min-width:0;">Payments & integrations</span>
              <span id="billing-acc-payments-chip" style="display:none;align-items:center;gap:4px;flex-shrink:0;padding:2px 8px;border-radius:999px;background:#FFFBEB;border:1px solid #FDE68A;color:#92400E;font-size:0.72rem;font-weight:600;line-height:1.4;white-space:nowrap;"><span class="material-icons" style="font-size:14px;color:#D97706;">warning_amber</span>Needs attention</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="billing-acc-payments" style="padding:16px;display:none;border-top:1px solid var(--border-light);">
              <div class="form-group" style="margin-bottom:0;">
                <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
                  <span class="material-icons" style="font-size:18px;color:#7C3AED;">payment</span> Online Payments
                </h4>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:12px;">
                  Accept credit card and ACH payments on your invoices via Stripe. Money goes directly to your bank account.
                </p>
                <div id="billing-online-payments-status">
                  <!-- Dynamically populated by openBillingSettings -->
                </div>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              <div class="form-group" style="margin-bottom:0;">
                <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
                  <span class="material-icons" style="font-size:18px;color:#10B981;">account_balance</span> Bank Connection (Plaid)
                </h4>
                <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:12px;">
                  Link your bank account to auto-pull transactions for reconciliation and see your real-time balance.
                </p>
                <div id="billing-plaid-status">
                  <!-- Dynamically populated by openBillingSettings -->
                </div>
              </div>
              <hr style="border:none;border-top:1px solid var(--border-light);margin:16px 0;">
              <div class="form-group" style="margin-bottom:0;">
                <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
                  <span class="material-icons" style="font-size:18px;color:#2CA01C;">sync</span> QuickBooks Online or Desktop
                </h4>
                <div id="billing-qbo-status">
                  <!-- Dynamically populated by openBillingSettings -->
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="form-actions" style="flex-shrink:0;">
          <button class="btn btn-secondary" onclick="window.BillingModule.closeBillingSettings()">Cancel</button>
          <button class="btn btn-primary" id="btn-save-billing-settings" onclick="window.BillingModule.saveBillingSettings()">
            <span class="material-icons">save</span> Save
          </button>
        </div>
      </div>
    </div>
  `;
}

function getQBSetupHTML() {
  const secStyle = 'border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;overflow:hidden;';
  const secHead = 'display:flex;align-items:center;gap:8px;padding:12px 16px;cursor:pointer;background:var(--bg-secondary);user-select:none;';
  const secBody = 'padding:16px;display:none;border-top:1px solid var(--border-light);';
  return `
    <div class="modal hidden" id="modal-qb-setup" style="z-index:1100;">
      <div class="modal-content" style="max-width:700px;display:flex;flex-direction:column;max-height:90dvh;">
        <div class="modal-header" style="flex-shrink:0;background:linear-gradient(135deg, var(--brand-blue), var(--accent-teal));color:#fff;">
          <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons">sync</span> QuickBooks Desktop Setup</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeQBSetup()" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="padding:16px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">

          <!-- Step 1: Upload -->
          <div style="${secStyle}">
            <div style="${secHead}" onclick="window.BillingModule.toggleQBSection('qb-wiz-upload')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">upload_file</span>
              <span style="font-weight:600;font-size:0.9rem;">1. Upload QB Export</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="qb-wiz-upload" style="${secBody}display:block;">
              <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;">
                Export from QuickBooks: <strong>File → Utilities → Export → Lists to IIF Files</strong>. Check all lists and click OK. Upload that file here — we'll parse customers, items, and accounts from it.
              </p>
              <div style="display:flex;gap:12px;align-items:center;">
                <input type="file" id="qb-wiz-file" accept=".iif,.csv,.txt" style="display:none;" onchange="window.BillingModule.uploadQBFile()">
                <button class="btn btn-primary" onclick="document.getElementById('qb-wiz-file').click()" style="display:flex;align-items:center;gap:6px;">
                  <span class="material-icons">upload_file</span> Choose IIF File
                </button>
                <span id="qb-wiz-file-name" style="font-size:0.85rem;color:var(--text-secondary);"></span>
              </div>
              <div id="qb-wiz-upload-status" style="margin-top:12px;"></div>
            </div>
          </div>

          <!-- Step 2: Customer Mapping -->
          <div style="${secStyle}">
            <div style="${secHead}" onclick="window.BillingModule.toggleQBSection('qb-wiz-customers')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">people</span>
              <span style="font-weight:600;font-size:0.9rem;">2. Customer Mapping</span>
              <span id="qb-wiz-cust-badge" style="font-size:0.75rem;color:var(--text-secondary);background:var(--bg-page);padding:2px 8px;border-radius:10px;"></span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="qb-wiz-customers" style="${secBody}">
              <div id="qb-wiz-customer-results"></div>
              <div id="qb-wiz-customer-manual"></div>
            </div>
          </div>

          <!-- Step 3: Item Mapping -->
          <div style="${secStyle}">
            <div style="${secHead}" onclick="window.BillingModule.toggleQBSection('qb-wiz-items')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">inventory_2</span>
              <span style="font-weight:600;font-size:0.9rem;">3. Item / Service Mapping</span>
              <span id="qb-wiz-item-badge" style="font-size:0.75rem;color:var(--text-secondary);background:var(--bg-page);padding:2px 8px;border-radius:10px;"></span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="qb-wiz-items" style="${secBody}">
              <div id="qb-wiz-item-results"></div>
              <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">Map each billing line type to a QuickBooks item. Leave blank to skip that type in exports.</p>
              <div style="display:grid;grid-template-columns:100px 1fr;gap:6px 12px;align-items:center;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Labor</span>
                <input type="text" id="qb-wiz-item-EMPLOYEE" class="form-control" placeholder="(skip — not exported)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Equipment</span>
                <input type="text" id="qb-wiz-item-EQUIPMENT" class="form-control" placeholder="Equipment Rental" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Downtime</span>
                <input type="text" id="qb-wiz-item-DOWNTIME" class="form-control" placeholder="Downtime" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Standby</span>
                <input type="text" id="qb-wiz-item-STANDBY" class="form-control" placeholder="Standby" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Load Permits</span>
                <input type="text" id="qb-wiz-item-PERMIT" class="form-control" placeholder="Load Permits" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Field Items</span>
                <input type="text" id="qb-wiz-item-FIELD_ITEM" class="form-control" placeholder="Field Items" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Misc</span>
                <input type="text" id="qb-wiz-item-MISC" class="form-control" placeholder="Miscellaneous" style="font-size:0.85rem;padding:6px 10px;">
              </div>
            </div>
          </div>

          <!-- Step 4: Account Mapping -->
          <div style="${secStyle}">
            <div style="${secHead}" onclick="window.BillingModule.toggleQBSection('qb-wiz-accounts')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">account_balance</span>
              <span style="font-weight:600;font-size:0.9rem;">4. Income Accounts</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="qb-wiz-accounts" style="${secBody}">
              <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">
                Map each line type to a QB income account. Leave blank to let QB use the item's default account.
              </p>
              <div id="qb-wiz-account-grid" style="display:grid;grid-template-columns:100px 1fr;gap:6px 12px;align-items:center;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Labor</span>
                <input type="text" id="qb-wiz-acct-EMPLOYEE" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Equipment</span>
                <input type="text" id="qb-wiz-acct-EQUIPMENT" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Downtime</span>
                <input type="text" id="qb-wiz-acct-DOWNTIME" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Standby</span>
                <input type="text" id="qb-wiz-acct-STANDBY" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Load Permits</span>
                <input type="text" id="qb-wiz-acct-PERMIT" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Field Items</span>
                <input type="text" id="qb-wiz-acct-FIELD_ITEM" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
                <span style="font-size:0.8rem;color:var(--text-secondary);">Misc</span>
                <input type="text" id="qb-wiz-acct-MISC" class="form-control" placeholder="(use item default)" style="font-size:0.85rem;padding:6px 10px;">
              </div>
            </div>
          </div>

          <!-- Step 5: Export Settings -->
          <div style="${secStyle}">
            <div style="${secHead}" onclick="window.BillingModule.toggleQBSection('qb-wiz-export')">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">tune</span>
              <span style="font-weight:600;font-size:0.9rem;">5. Export Settings</span>
              <span class="material-icons qb-wiz-chevron" style="margin-left:auto;transition:transform 0.2s;">expand_more</span>
            </div>
            <div id="qb-wiz-export" style="${secBody}">
              <div class="form-group" style="margin-bottom:12px;">
                <label style="font-size:0.85rem;font-weight:600;">Payment Terms</label>
                <input type="text" id="qb-wiz-terms" class="form-control" placeholder="Net 30" style="max-width:160px;margin-top:4px;">
              </div>
              <div class="form-group" style="margin-bottom:0;">
                <label style="font-size:0.85rem;font-weight:600;">AR Account Name</label>
                <input type="text" id="qb-wiz-ar-account" class="form-control" placeholder="Accounts Receivable" style="max-width:260px;margin-top:4px;">
                <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">Must match your QB chart of accounts</p>
              </div>
            </div>
          </div>

        </div>
        <div class="form-actions" style="flex-shrink:0;">
          <button class="btn btn-secondary" onclick="window.BillingModule.closeQBSetup()">Cancel</button>
          <button class="btn btn-primary" id="btn-save-qb-setup" onclick="window.BillingModule.saveQBSetup()">
            <span class="material-icons">save</span> Save All Settings
          </button>
        </div>
      </div>
    </div>
  `;
}

async function startStripeConnect(btnId) {
  const btn = document.getElementById(btnId);
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Loading...'; }
  try {
    const fn = httpsCallable(functions, 'createStripeConnectAccount');
    const res = await fn({});
    if (res.data?.url) window.location.href = res.data.url;
  } catch (err) {
    console.error('[Billing] Stripe Connect error:', err);
    window.showToast?.('Failed to start Stripe setup', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">credit_card</span> Enable Online Payments'; }
  }
}

// ── Plaid Bank Connection ──────────────────────────────────────────
async function startPlaidLink() {
  const btn = document.getElementById('btn-plaid-connect');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Connecting...'; }
  try {
    const fn = httpsCallable(functions, 'plaidCreateLinkToken');
    const res = await fn({});

    if (res.data?.sandbox) {
      // No Plaid keys yet — show info message
      window.showToast?.('Plaid API keys not configured yet. Bank connection will be available once keys are added.', 'info');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">account_balance</span> Connect Your Bank'; }
      return;
    }

    if (res.data?.link_token) {
      // Load Plaid Link SDK dynamically
      if (!window.Plaid) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const handler = window.Plaid.create({
        token: res.data.link_token,
        onSuccess: async (publicToken, metadata) => {
          try {
            window.showToast?.('Linking bank account...', 'info');
            const exchange = httpsCallable(functions, 'plaidExchangeToken');
            await exchange({
              publicToken,
              accountId: metadata.accounts?.[0]?.id,
              institutionName: metadata.institution?.name,
              accountName: metadata.accounts?.[0]?.name,
              accountMask: metadata.accounts?.[0]?.mask,
            });
            window.showToast?.('Bank connected!', 'success');
            // Refresh tenant data
            if (window.AppState?.tenantData) {
              window.AppState.tenantData.plaidConnected = true;
              window.AppState.tenantData.plaidInstitution = metadata.institution?.name;
              window.AppState.tenantData.plaidAccountLast4 = metadata.accounts?.[0]?.mask;
            }
            openBillingSettings(); // Refresh the modal
          } catch (err) {
            console.error('[Billing] Plaid exchange error:', err);
            window.showToast?.('Failed to link bank: ' + err.message, 'error');
          }
        },
        onExit: () => {
          if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">account_balance</span> Connect Your Bank'; }
        },
      });
      handler.open();
    }
  } catch (err) {
    console.error('[Billing] Plaid link error:', err);
    window.showToast?.('Failed to start bank connection: ' + err.message, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">account_balance</span> Connect Your Bank'; }
  }
}

async function disconnectPlaid() {
  if (!confirm('Disconnect your bank account? You can reconnect anytime.')) return;
  try {
    window.showToast?.('Disconnecting...', 'info');
    const fn = httpsCallable(functions, 'plaidDisconnect');
    await fn({});
    if (window.AppState?.tenantData) {
      delete window.AppState.tenantData.plaidConnected;
      delete window.AppState.tenantData.plaidInstitution;
      delete window.AppState.tenantData.plaidAccountLast4;
    }
    window.showToast?.('Bank disconnected', 'success');
    openBillingSettings();
  } catch (err) {
    console.error('[Billing] Plaid disconnect error:', err);
    window.showToast?.('Failed to disconnect: ' + err.message, 'error');
  }
}

// ── QuickBooks Online Connection ──────────────────────────────────
// TASK-9538 — Chris 2026-10-01: "There was a long pause after I hit disconnect,
// you might want to provide feedback spinner or something." Both buttons now
// disable + say what they are doing until the callable returns, and come back
// (original label, enabled, error toast) when it fails. The button is the one
// that was CLICKED (`ev.currentTarget`), captured synchronously before the
// first await: the Reconnect button is `#btn-qbo-reconnect` and the AR/AP
// wizard's Connect has no id at all, so the old `#btn-qbo-connect` lookup gave
// those two no feedback. Falls back to the id lookups for a programmatic call.
function _qboButtonFrom(ev, ...ids) {
  const el = ev && ev.currentTarget;
  if (el && typeof el === 'object' && 'disabled' in el) return el;
  for (const id of ids) {
    const b = document.getElementById(id);
    if (b) return b;
  }
  return null;
}
function _qboButtonBusy(btn, label) {
  if (!btn) return '';
  const idle = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<span class="btn-spinner"></span> ${label}`;
  return idle;
}
function _qboButtonIdle(btn, idleHTML) {
  if (!btn) return;
  btn.disabled = false;
  btn.innerHTML = idleHTML || btn.innerHTML;
}

async function startQBOConnect(ev) {
  const btn = _qboButtonFrom(ev, 'btn-qbo-connect', 'btn-qbo-reconnect');
  const idle = _qboButtonBusy(btn, 'Connecting…');
  try {
    const fn = httpsCallable(functions, 'qboGetAuthUrl');
    const res = await fn({});

    if (res.data?.sandbox || !res.data?.url) {
      window.showToast?.(res.data?.message || 'QuickBooks API keys not configured yet.', 'info');
      _qboButtonIdle(btn, idle);
      return;
    }

    // Back from Intuit can restore this page from bfcache with the button still
    // "Connecting…" — put it back when that happens (COD Domain lane, 2026-10-02).
    window.addEventListener('pageshow', (e) => { if (e && e.persisted) _qboButtonIdle(btn, idle); }, { once: true });
    window.location.href = res.data.url;
  } catch (err) {
    console.error('[Billing] QBO connect error:', err);
    // TASK-9589 — a permission refusal already says what to do ("Ask the account
    // owner to connect QuickBooks."); don't bury it under a generic prefix.
    window.showToast?.(err?.code === 'functions/permission-denied' && err?.message
      ? err.message
      : 'Failed to start QuickBooks connection: ' + (err?.message || err), 'error');
    _qboButtonIdle(btn, idle);
  }
}

async function disconnectQBO(ev) {
  const btn = _qboButtonFrom(ev, 'btn-qbo-disconnect');
  if (!confirm('Disconnect QuickBooks Online? Sync will stop. You can reconnect anytime.')) return;
  const idle = _qboButtonBusy(btn, 'Disconnecting…');
  try {
    const fn = httpsCallable(functions, 'qboDisconnect');
    await fn({});
    if (window.AppState?.tenantData) {
      delete window.AppState.tenantData.qboConnected;
      delete window.AppState.tenantData.qboRealmId;
      delete window.AppState.tenantData.qboCompanyName;
      delete window.AppState.tenantData.qboStatus;
    }
    // The live status cache said "connected" a moment ago; drop it or the
    // re-render below paints the Disconnect button straight back — and retire
    // any qboCheckStatus round-trip still in flight, which would otherwise land
    // after this and repaint "Connected" (COD Code Reviewer, 2026-10-02).
    window._qboLiveStatus = null;
    _qboStatusGen += 1;
    _qboCheckInFlight = false;
    _qboCheckFailed = false;
    clearTimeout(_qboCheckTimer);
    window.showToast?.('QuickBooks disconnected', 'success');
    openBillingSettings();
  } catch (err) {
    console.error('[Billing] QBO disconnect error:', err);
    window.showToast?.('Failed to disconnect: ' + (err?.message || err), 'error');
    _qboButtonIdle(btn, idle);
  }
}

async function pullQBOAccounts() {
  const btn = document.getElementById('btn-qbo-pull-accounts');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Syncing...'; }
  try {
    const fn = httpsCallable(functions, 'qboPullAccounts');
    const res = await fn({});
    const accounts = res.data?.accounts || [];
    // Cache locally for mapping UI
    window._qboAccounts = accounts;
    window.showToast?.(`Pulled ${accounts.length} accounts from QuickBooks${res.data?.sandbox ? ' (sandbox)' : ''}`, 'success');
  } catch (err) {
    console.error('[Billing] QBO pull accounts error:', err);
    window.showToast?.('Failed to pull accounts: ' + err.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">sync</span> Refresh Accounts'; }
  }
}

// TASK-9417 — QuickBooks "paid" sync for tickets billed elsewhere (EXT-####).
// QuickBooks is only READ; the server marks the DD tickets paid on the QuickBooks
// payment date and lists everything it would not decide on its own. All state
// comes from the qboPaidSync* callables (integrations/* is server-only).
const _QBO_PAID_KIND_LABEL = {
  not_found: 'Not in QuickBooks', duplicate_docnumber: 'Duplicate number', shared_ref: 'Different customers',
  partial: 'Partly paid', credit_memo: 'Credit memo', paid_other: 'Closed another way',
  payment_not_found: 'Payment unreadable', voided: 'Voided', written_off_in_dd: 'Written off in DD',
  unmarked_in_dd: 'Un-marked in DD', bad_ref: 'Unusual number', amount_diff: 'Amount differs',
  missing_after_paid: 'Gone after paid', voided_after_paid: 'Voided after paid', reopened_after_paid: 'Balance again',
  customer_mismatch: 'Different customer in QB', write_failed: 'Not saved', changed_during_run: 'Changed during run',
  // TASK-9654 — linked (not just EXT) tickets
  link_id_mismatch: 'Link points elsewhere', link_realm_mismatch: 'Other QuickBooks company', link_conflict: 'Two invoice numbers',
  link_unproven: 'Link not from DD', link_held: 'Held: over 15% off',
  not_billed_in_dd: 'Not billed in DD', // integ/2107 — paid only when billed
};
function _qboPaidMoney(n) {
  return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function _qboPaidReportHTML(rep) {
  if (!rep) return '<p style="color:var(--text-secondary);font-size:0.85rem;">No run yet.</p>';
  const when = rep.ranAt ? new Date(rep.ranAt).toLocaleString() : '';
  const head = `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">${rep.dryRun ? 'Preview — nothing was changed' : 'Last run'} · ${escapeHtml(when)}</div>`;
  if (rep.status !== 'ok') {
    return head + `<div style="padding:10px 12px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;color:#92400E;font-size:0.85rem;">${escapeHtml(rep.message || 'The run did not finish. Nothing was changed.')}</div>`;
  }
  const s = rep.summary || {};
  const markedVerb = rep.dryRun ? 'Would mark paid' : 'Marked paid';
  let html = head + `<div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:12px;font-size:0.9rem;color:var(--text-primary);">
      <div><strong>${escapeHtml(String(s.markedPaid || 0))}</strong> ${markedVerb} (${escapeHtml(_qboPaidMoney(s.markedPaidAmount))})</div>
      <div><strong>${escapeHtml(String(s.exceptions || 0))}</strong> need a look</div>
      <div><strong>${escapeHtml(String(s.open || 0))}</strong> still open in QuickBooks</div>
    </div>`;
  const marked = Array.isArray(rep.marked) ? rep.marked : [];
  if (marked.length) {
    html += `<div style="font-weight:600;margin:8px 0 4px;color:var(--text-primary);">${markedVerb}</div><div style="overflow-x:auto;"><table class="data-table" style="width:100%;font-size:0.8rem;"><thead><tr><th>Ticket</th><th>Customer</th><th>QB #</th><th>Paid on</th><th style="text-align:right;">DD amount</th></tr></thead><tbody>`
      + marked.map(r => `<tr><td>${escapeHtml(String(r.ticketNo ?? ''))}</td><td>${escapeHtml(r.customer || '')}</td><td>${escapeHtml(r.qboDocNumber || r.ref || '')}</td><td>${escapeHtml(r.paidOn || '')}${r.priorPeriod ? ' <span title="Paid in an earlier month: that month&#39;s cash revenue changes" style="color:#92400E;">(earlier month)</span>' : ''}</td><td style="text-align:right;">${escapeHtml(_qboPaidMoney(r.dd))}</td></tr>`).join('')
      + '</tbody></table></div>';
  }
  const exc = Array.isArray(rep.exceptions) ? rep.exceptions : [];
  if (exc.length) {
    html += `<div style="font-weight:600;margin:12px 0 4px;color:var(--text-primary);">Needs a look</div><div style="overflow-x:auto;"><table class="data-table" style="width:100%;font-size:0.8rem;"><thead><tr><th>What</th><th>Ticket</th><th>Customer</th><th>QB #</th><th style="text-align:right;">DD</th><th style="text-align:right;">QB total</th><th style="text-align:right;">QB balance</th></tr></thead><tbody>`
      + exc.map(e => {
        const tn = e.ticketNo ?? (Array.isArray(e.ticketNos) ? e.ticketNos.filter(x => x != null).join(', ') : '');
        return `<tr title="${escapeHtml(e.message || '')}"><td>${escapeHtml(_QBO_PAID_KIND_LABEL[e.kind] || e.kind || '')}</td><td>${escapeHtml(String(tn ?? ''))}</td><td>${escapeHtml(e.customer || '')}</td><td>${escapeHtml(e.qboDocNumber || e.ref || '')}</td><td style="text-align:right;">${escapeHtml(_qboPaidMoney(e.dd))}</td><td style="text-align:right;">${escapeHtml(_qboPaidMoney(e.qbo))}</td><td style="text-align:right;">${escapeHtml(_qboPaidMoney(e.qboBalance))}</td></tr>`;
      }).join('')
      + '</tbody></table></div>';
  }
  // TASK-9654 — lists are no longer truncated (paged server-side); old reports may still say so.
  if (rep.truncated) html += '<p style="font-size:0.75rem;color:var(--text-secondary);margin-top:8px;">List shortened — the first 300 rows are shown.</p>';
  if (rep.amountNote) html += '<p style="font-size:0.75rem;color:var(--text-secondary);margin-top:8px;">' + escapeHtml(rep.amountNote) + '</p>';
  return html;
}
let _qboPaidSyncBusy = false;
async function _qboPaidSyncRefresh(previewRep) {
  const body = document.getElementById('qbo-paid-sync-body');
  if (!body) return;
  try {
    const res = await httpsCallable(functions, 'qboPaidSyncStatus')({});
    const st = res.data || {};
    const toggle = document.getElementById('qbo-paid-sync-enabled');
    if (toggle) toggle.checked = st.enabled === true;
    const runBtn = document.getElementById('btn-qbo-paid-sync-run');
    if (runBtn) runBtn.disabled = st.enabled !== true;
    body.innerHTML = _qboPaidReportHTML(previewRep || st.report);
  } catch (err) {
    body.innerHTML = `<p style="color:#B91C1C;font-size:0.85rem;">${escapeHtml(err?.message || 'Could not load the paid sync.')}</p>`;
  }
}
async function _qboPaidSyncAction(kind) {
  if (_qboPaidSyncBusy) {
    if (kind === 'toggle') { const t = document.getElementById('qbo-paid-sync-enabled'); if (t) t.checked = !t.checked; }
    return;
  }
  _qboPaidSyncBusy = true;
  const body = document.getElementById('qbo-paid-sync-body');
  try {
    if (kind === 'toggle') {
      const toggle = document.getElementById('qbo-paid-sync-enabled');
      const want = !!toggle?.checked;
      try {
        await httpsCallable(functions, 'qboPaidSyncSetEnabled')({ enabled: want });
        window.showToast?.(want ? 'QuickBooks paid sync turned on' : 'QuickBooks paid sync turned off', 'success');
      } catch (err) {
        if (toggle) toggle.checked = !want;
        window.showToast?.(err?.message || 'Could not change the setting', 'error');
      }
      await _qboPaidSyncRefresh();
      return;
    }
    const dryRun = kind !== 'run';
    if (body) body.innerHTML = `<p style="color:var(--text-secondary);font-size:0.85rem;">${dryRun ? 'Checking QuickBooks (preview)…' : 'Checking QuickBooks and marking paid…'}</p>`;
    const res = await httpsCallable(functions, 'qboPaidSyncRun')({ dryRun });
    await _qboPaidSyncRefresh(dryRun ? res.data : null);
  } catch (err) {
    if (body) body.innerHTML = `<p style="color:#B91C1C;font-size:0.85rem;">${escapeHtml(err?.message || 'The run failed. Nothing was changed.')}</p>`;
  } finally {
    _qboPaidSyncBusy = false;
  }
}
function openQBOPaidSync() {
  document.getElementById('modal-qbo-paid-sync')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-qbo-paid-sync';
  modal.style.zIndex = '1200';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:760px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons">price_check</span> QuickBooks paid sync</h3>
        <button class="btn-icon" id="btn-qbo-paid-sync-close" aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:16px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 12px;">
          For tickets marked <strong>Billed elsewhere</strong> with a QuickBooks invoice number, and tickets linked to their QuickBooks invoice. Every morning DispatchDeck reads those invoices in QuickBooks and marks a ticket paid when its invoice is paid in full, dated the day the payment came in. It never changes anything in QuickBooks. Partial payments, credit memos and anything unclear are listed below instead.
        </p>
        <label style="display:flex;align-items:center;gap:8px;font-weight:600;color:var(--text-primary);margin-bottom:12px;cursor:pointer;">
          <input type="checkbox" id="qbo-paid-sync-enabled"> Run the paid sync every morning
        </label>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px;">
          <button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-paid-sync-preview"><span class="material-icons" style="font-size:14px;">visibility</span> Preview (changes nothing)</button>
          <button type="button" class="btn btn-primary btn-sm" id="btn-qbo-paid-sync-run" disabled><span class="material-icons" style="font-size:14px;">sync</span> Run now</button>
        </div>
        <div id="qbo-paid-sync-body"><p style="color:var(--text-secondary);font-size:0.85rem;">Loading…</p></div>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.classList.remove('hidden');
  document.getElementById('btn-qbo-paid-sync-close')?.addEventListener('click', () => modal.remove());
  document.getElementById('qbo-paid-sync-enabled')?.addEventListener('change', () => _qboPaidSyncAction('toggle'));
  document.getElementById('btn-qbo-paid-sync-preview')?.addEventListener('click', () => _qboPaidSyncAction('preview'));
  document.getElementById('btn-qbo-paid-sync-run')?.addEventListener('click', () => _qboPaidSyncAction('run'));
  _qboPaidSyncRefresh();
}

// TASK-9611 slice 1 — the QuickBooks setup checklist + Vendors step lives in its
// own lazily-loaded module (qboSetup.js). It only READS QuickBooks; every write
// is a preview the person approves, re-checked on the server, with Undo.
async function openQBOSetup(ev, step) {
  const btn = _qboButtonFrom(ev, 'btn-qbo-setup');
  const idle = _qboButtonBusy(btn, 'Opening…');
  try {
    const mod = await __ddChunkWrap(import('./qboSetup.js?v=10123001')); // TASK-10123 step 0 Online or Desktop, Needs your answer banner, loading at the top · TASK-10060 Customers step redesign: light header, money first, measured match % · TASK-10049 tick-all on pairs, coverage strip, visible save progress · TASK-10004 S2 owner salaries through payroll (qboBooksStep 10004102) · TASK-10004 S1 owner draws label + owner-only account names · TASK-9756/9757 read-only switch + one navigation · TASK-9792 1099 suggestion copy · QB Books S1 Books step · TASK-9969 S2 QuickBooks transfer summary · TASK-10004 S3 owner payroll answer (qboBooksStep 10004302) · TASK-10049 tick-all on pairs, coverage strip, visible save progress · TASK-10004 S2 owner salaries through payroll (qboBooksStep 10004102) · TASK-10004 S1 owner draws label + owner-only account names · TASK-9756/9757 read-only switch + one navigation · TASK-9792 1099 suggestion copy · QB Books S1 Books step · TASK-9969 S2 QuickBooks transfer summary · TASK-10048 QuickBooks jobs to sort (qboJobsStep.js 10048001) · TASK-10060 Customers step redesign: light header, money first, measured match % · TASK-10049 tick-all on pairs, coverage strip, visible save progress · TASK-10004 S2 owner salaries through payroll (qboBooksStep 10004102) · TASK-10004 S1 owner draws label + owner-only account names · TASK-9756/9757 read-only switch + one navigation · TASK-9792 1099 suggestion copy · QB Books S1 Books step · TASK-9969 S2 QuickBooks transfer summary
    if (typeof step === 'string' && step) mod.openQboSetupAt(step); else mod.openQboSetup();
  } catch (err) {
    console.error('[Billing] QuickBooks setup failed to open:', err);
    window.showToast?.('Could not open QuickBooks setup. Reload the page and try again.', 'error');
  } finally {
    _qboButtonIdle(btn, idle);
  }
}

function openQBOAccountMapping() {
  // Pull accounts first if not cached
  if (!window._qboAccounts?.length) {
    pullQBOAccounts().then(() => showQBOAccountMappingModal());
  } else {
    showQBOAccountMappingModal();
  }
}

function showQBOAccountMappingModal() {
  const accounts = window._qboAccounts || [];
  const settings = window.AppState?.settings || {};
  const mapping = settings.qboAccountMapping || {};

  const expenseAccounts = accounts.filter(a => a.type === 'Expense' || a.type === 'Other Expense' || a.type === 'Cost of Goods Sold');
  const incomeAccounts = accounts.filter(a => a.type === 'Income' || a.type === 'Other Income');
  const bankAccounts = accounts.filter(a => a.type === 'Bank');
  const arAccounts = accounts.filter(a => a.type === 'Accounts Receivable');
  const apAccounts = accounts.filter(a => a.type === 'Accounts Payable');

  function optionList(list, selected) {
    return `<option value="">— Select —</option>` + list.map(a =>
      `<option value="${escapeHtml(a.id)}" ${a.id === selected ? 'selected' : ''}>${escapeHtml(a.name)}</option>`
    ).join('');
  }

  const categories = ['Fuel', 'Repairs', 'Insurance', 'Permits', 'Lodging', 'Materials', 'Subcontractor', 'Tolls', 'Miscellaneous'];

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-qbo-account-mapping';
  modal.style.zIndex = '1200';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:600px;display:flex;flex-direction:column;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;background:linear-gradient(135deg, #2CA01C, #10B981);color:#fff;">
        <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons">account_tree</span> QBO Account Mapping</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-qbo-account-mapping').remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:16px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Map DispatchDeck categories to your QuickBooks chart of accounts. This controls where synced invoices and bills land.
        </p>

        <div style="margin-bottom:16px;">
          <h4 style="font-size:0.85rem;font-weight:600;margin-bottom:8px;">Core Accounts</h4>
          <div style="display:grid;grid-template-columns:140px 1fr;gap:8px 12px;align-items:center;">
            <span style="font-size:0.8rem;color:var(--text-secondary);">Bank / Checking</span>
            <select class="form-control qbo-map-select" data-map="bankAccount" style="font-size:0.85rem;padding:6px;">${optionList(bankAccounts, mapping.bankAccount)}</select>
            <span style="font-size:0.8rem;color:var(--text-secondary);">Accounts Receivable</span>
            <select class="form-control qbo-map-select" data-map="accountsReceivable" style="font-size:0.85rem;padding:6px;">${optionList(arAccounts, mapping.accountsReceivable)}</select>
            <span style="font-size:0.8rem;color:var(--text-secondary);">Accounts Payable</span>
            <select class="form-control qbo-map-select" data-map="accountsPayable" style="font-size:0.85rem;padding:6px;">${optionList(apAccounts, mapping.accountsPayable)}</select>
            <span style="font-size:0.8rem;color:var(--text-secondary);">Revenue / Income</span>
            <select class="form-control qbo-map-select" data-map="income" style="font-size:0.85rem;padding:6px;">${optionList(incomeAccounts, mapping.income)}</select>
          </div>
        </div>

        <div>
          <h4 style="font-size:0.85rem;font-weight:600;margin-bottom:8px;">Expense Categories</h4>
          <div style="display:grid;grid-template-columns:140px 1fr;gap:8px 12px;align-items:center;">
            ${categories.map(cat => `
              <span style="font-size:0.8rem;color:var(--text-secondary);">${cat}</span>
              <select class="form-control qbo-map-select" data-map="expense_${cat}" style="font-size:0.85rem;padding:6px;">${optionList(expenseAccounts, mapping['expense_' + cat])}</select>
            `).join('')}
          </div>
        </div>
      </div>
      <div class="form-actions" style="flex-shrink:0;">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-qbo-account-mapping').remove()">Cancel</button>
        <button class="btn btn-primary" id="btn-save-qbo-mapping">
          <span class="material-icons">save</span> Save Mapping
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  document.getElementById('btn-save-qbo-mapping')?.addEventListener('click', async () => {
    const newMapping = {};
    modal.querySelectorAll('.qbo-map-select').forEach(sel => {
      const key = sel.dataset.map;
      if (sel.value) newMapping[key] = sel.value;
    });
    try {
      // TASK-4831 — this was a lazy `await import(gstatic 10.7.1/firebase-firestore.js)`
      // (a second SDK instance — the class that killed ship slips for 33 days) reading
      // `window.AppState?.tenantId` and `window.AppState.db`. NEITHER of those fields
      // is ever assigned: the tenant lives on `AppState.tenant` (app.js:1630 and
      // friends) and `AppState.db` does not exist at all. So `tenant` was always
      // undefined, the `if` never ran, and the modal closed looking like a successful
      // save while writing nothing — no throw, no toast, no log. The mapping is set on
      // 0 of 91 tenants. `db`, `doc` and `setDoc` come from firebase-config.js at the
      // top of this file, so the dynamic import is gone rather than re-pinned.
      const tenant = window.AppState?.tenant;
      if (tenant) {
        // COD Code Reviewer (TASK-4831): `{ merge: true }` deep-merges nested maps,
        // so clearing one dropdown and saving would LEAVE the cleared account
        // behind — a stale mapping silently keeps posting a category to the wrong
        // QuickBooks account. That was harmless while the save was dead; it goes
        // live the moment this write starts working. `mergeFields` with the dotted
        // path replaces qboAccountMapping wholesale while still leaving the rest of
        // `settings` untouched, and unlike updateDoc it is safe if the doc is thin.
        // TASK-9337 -- capture BEFORE the write (the tenant onSnapshot refreshes
        // AppState.settings before the await resolves).
        let prevMap = null;
        try { prevMap = JSON.parse(JSON.stringify(window.AppState?.settings?.qboAccountMapping ?? null)); } catch (_) { prevMap = null; }
        await setDoc(
          doc(db, 'tenants', tenant),
          { settings: { qboAccountMapping: newMapping } },
          { mergeFields: ['settings.qboAccountMapping'] }
        );
        // TASK-9337 -- money-routing setting now savable by billing staff: log old -> new.
        try {
          if (JSON.stringify(prevMap) !== JSON.stringify(newMapping)) {
            window.logAudit?.('settings', 'SAVE_QBO_ACCOUNT_MAPPING', { changed: { qboAccountMapping: { from: prevMap, to: newMapping } } });
          }
        } catch (auditErr) { console.warn('[Billing] QBO mapping audit log failed:', auditErr); }
        if (window.AppState.settings) window.AppState.settings.qboAccountMapping = newMapping;
        window.showToast?.('Account mapping saved', 'success');
      }
      modal.remove();
    } catch (err) {
      console.error('[Billing] Save QBO mapping error:', err);
      window.showToast?.('Failed to save mapping: ' + err.message, 'error');
    }
  });
}

// ── QBO Sync functions (callable from billing/AP views) ───────────
// TASK-764 COD#4 — a sync that fails because the QBO connection died must show a
// clear "QuickBooks disconnected — please reconnect" message (not a raw API error)
// AND light the dashboard reconnect banner. The server throws failed-precondition
// with details.reauthRequired on this path.
function _isQboReauthError(err) {
  if (!err) return false;
  if (err.details?.reauthRequired === true) return true;
  const code = err.code || '';
  const msg = (err.message || '').toLowerCase();
  return (code === 'functions/failed-precondition' || code === 'failed-precondition')
    && (msg.includes('reconnect') || msg.includes('disconnected'));
}
function handleQBOSyncError(err, fallbackPrefix) {
  console.error('[Billing] QBO sync error:', err);
  if (err?.details?.reason === 'qbo_read_only') { window.showToast?.(err.message, 'error'); return; } // TASK-9756 — the server's own sentence
  if (_isQboReauthError(err)) {
    // Reflect the dead connection locally + surface the banner immediately.
    window._qboLiveStatus = { ...(window._qboLiveStatus || {}), status: 'reauth_required' };
    if (window.AppState?.tenantData) window.AppState.tenantData.qboStatus = 'reauth_required';
    renderQBOReauthBanner();
    window.showToast?.('QuickBooks disconnected — please reconnect', 'error');
  } else {
    window.showToast?.(fallbackPrefix + (err?.message || 'unknown error'), 'error');
  }
}

async function syncTicketToQBO(ticketId) {
  // TASK-1346 — the button is hidden for these; this catches every OTHER caller in
  // this module. A ticket recorded as billed OUTSIDE DispatchDeck already has an
  // invoice in QuickBooks; pushing it would create a second one.
  // ⚠️ NOT a server boundary (COD AuthZ ⚠️) — this is client JS. The callable
  // `qboPushInvoice` (functions/src/qbo.js) checks auth/tenant/billing-module but
  // has NO invoicedExternally check, so a direct callable invocation can still
  // double-push. Closing that needs a functions deploy — filed, not fixed here.
  const _qt = BillingState.tickets.find(t => t.id === ticketId);
  if (isBilledExternally(_qt)) {
    window.showToast?.('This ticket was billed outside DispatchDeck — it is already invoiced in your accounting system.', 'error');
    return null;
  }
  // TASK-9654 — linked to a QuickBooks invoice: already there. qboPushInvoice refuses it too.
  if (hasQboInvoiceLink(_qt)) {
    const _n = _qboLinkDocNo(_qt);
    window.showToast?.(`This ticket is already in QuickBooks${_n ? ` as invoice #${_n}` : ''} — sending it again would create a second invoice.`, 'error');
    return null;
  }
  window.showToast?.('Syncing invoice to QuickBooks...', 'info');
  try {
    const fn = httpsCallable(functions, 'qboPushInvoice');
    const res = await fn({ ticketId });
    if (res.data?.sandbox) {
      // TASK-9537 — the placeholder connection sent nothing; never say "synced".
      window.showToast?.('Nothing was sent: QuickBooks is in placeholder mode. Connect QuickBooks, then send this invoice.', 'warning');
    } else {
      window.showToast?.('Invoice synced to QuickBooks', 'success');
    }
    return res.data;
  } catch (err) {
    handleQBOSyncError(err, 'Failed to sync: ');
    return null;
  }
}

async function syncAPToQBO(invoiceId) {
  window.showToast?.('Syncing bill to QuickBooks...', 'info');
  try {
    const fn = httpsCallable(functions, 'qboPushBill');
    const res = await fn({ invoiceId });
    if (res.data?.sandbox) {
      // TASK-9537 — the placeholder connection sent nothing; never say "synced".
      window.showToast?.('Nothing was sent: QuickBooks is in placeholder mode. Connect QuickBooks, then send this bill.', 'warning');
    } else {
      window.showToast?.('Bill synced to QuickBooks', 'success');
    }
    return res.data;
  } catch (err) {
    handleQBOSyncError(err, 'Failed to sync: ');
    return null;
  }
}

async function syncAPPaymentToQBO(invoiceId) {
  window.showToast?.('Syncing payment to QuickBooks...', 'info');
  try {
    const fn = httpsCallable(functions, 'qboPushBillPayment');
    const res = await fn({ invoiceId });
    if (res.data?.sandbox) {
      // TASK-9537 slice 3 — the placeholder connection sent nothing; never say "synced".
      window.showToast?.('Nothing was sent: QuickBooks is in placeholder mode. Connect QuickBooks, then send this payment.', 'warning');
    } else {
      window.showToast?.('Payment synced to QuickBooks', 'success');
    }
    return res.data;
  } catch (err) {
    handleQBOSyncError(err, 'Failed to sync payment: ');
    return null;
  }
}

// TASK-9537 slice 3 — "Voided in QuickBooks? Check & unlock". The SERVER reads the
// invoice/bill back from QuickBooks and clears the push only when it is voided or
// deleted there (qboUnlockVoidedPush); this only asks and reports.
let _qboUnlockBusy = false;
async function unlockVoidedQboPush(kind, id) {
  if (_qboUnlockBusy) return null;
  const noun = kind === 'bill' ? 'bill' : 'invoice';
  const what = kind === 'bill' ? 'bill' : 'ticket';
  if (typeof window.showConfirm === 'function') {
    const go = await window.showConfirm('Voided in QuickBooks?',
      `DispatchDeck will check QuickBooks. If the ${noun} was voided or deleted there, this ${what} can be sent again as a new ${noun}. If QuickBooks still has it, nothing changes.`,
      { confirmText: 'Check QuickBooks', cancelText: 'Cancel', icon: 'help_outline', iconColor: '#1D4ED8' });
    if (!go) return null;
  }
  _qboUnlockBusy = true;
  window.showToast?.('Checking QuickBooks...', 'info');
  try {
    const fn = httpsCallable(functions, 'qboUnlockVoidedPush');
    const res = await fn({ kind: kind === 'bill' ? 'bill' : 'invoice', id });
    window.showToast?.(`QuickBooks shows the ${noun} as ${res.data?.state === 'deleted' ? 'deleted' : 'voided'}. You can send this ${what} again.`, 'success');
    if (kind !== 'bill') {
      try { closeModal('view-ticket'); } catch (_) { /* not open */ }
    }
    return res.data;
  } catch (err) {
    handleQBOSyncError(err, '');
    return null;
  } finally {
    _qboUnlockBusy = false;
  }
}

// ============================================
// BILLING SETTINGS
// ============================================
// TASK-9986 S1 — Billing SETTINGS need the owner's per-person "can change
// Billing settings" switch (window.canChangeModuleSettings('billing'), app.js).
// Without it every Billing settings panel is read-only: inputs disabled, Save
// hidden, one line on top, and every settings writer below refuses before it
// writes (rules + callables refuse again — this is UX, not the fence).
function _t9986BillingReadOnly() {
  return typeof window.canChangeModuleSettings === 'function' && !window.canChangeModuleSettings('billing');
}
function _t9986Refuse() {
  window.showToast?.(window.SETTINGS_READ_ONLY_MSG || 'Ask the account owner to change these settings.', 'error');
}
// Disables every input in `root` (marking the ones it disabled, so a live
// grant re-enables only those), hides the save buttons, and pins the one-line
// note at the top (sticky, so a phone user sees it before the disabled inputs).
function _t9986ApplyReadOnly(root, saveBtnIds) {
  if (!root) return;
  const ro = _t9986BillingReadOnly();
  const host = root.querySelector('[data-t9986-host]') || root.querySelector('.modal-content > div[style*="overflow-y"]') || root;
  let note = root.querySelector('.t9986-settings-ro');
  if (ro && !note) {
    note = document.createElement('div');
    note.className = 't9986-settings-ro';
    note.setAttribute('role', 'note');
    note.style.cssText = 'position:sticky;top:0;z-index:2;display:flex;gap:8px;align-items:flex-start;padding:10px 12px;margin-bottom:12px;background:var(--bg-secondary, #F3F4F6);border:1px solid var(--border-light, #E5E7EB);border-radius:8px;font-size:0.85rem;color:var(--text-secondary);';
    note.innerHTML = '<span class="material-icons" style="font-size:18px;color:var(--text-secondary);">lock</span><span></span>';
    note.lastChild.textContent = window.SETTINGS_READ_ONLY_MSG || 'You can see these settings but not change them. Ask the account owner if something here needs to change.';
    host.prepend(note);
  } else if (!ro && note) {
    note.remove();
  }
  root.querySelectorAll('input, select, textarea').forEach((el) => {
    if (ro) { if (!el.disabled) { el.disabled = true; el.dataset.t9986Ro = '1'; } }
    else if (el.dataset.t9986Ro === '1') { el.disabled = false; delete el.dataset.t9986Ro; }
  });
  (saveBtnIds || []).forEach((id) => { const b = document.getElementById(id); if (b) b.style.display = ro ? 'none' : ''; });
}

function openBillingSettings() {
  const modal = document.getElementById('modal-billing-settings');
  if (!modal) return;
  const settings = window.AppState?.settings || {};
  // TASK-572 — fresh modal session: clear the in-modal ask-customer cache.
  _askCustomerPreForce = null;
  // TASK-9373 — Driver App card summary (preset + hidden count).
  _daSyncBillingSettingsCard(settings);
  _pcmRefreshSummary(); // TASK-9553 — "N people on a different mode"
  const buildEl = document.getElementById('billing-settings-build-tickets');
  const driverEl = document.getElementById('billing-settings-show-driver-tickets');
  const pricesEl = document.getElementById('billing-settings-show-driver-prices');
  if (buildEl) {
    buildEl.checked = settings.buildTicketsFromClock === true;
  }
  if (driverEl) {
    driverEl.checked = settings.showTicketsInDriverView === true;
  }
  if (pricesEl) pricesEl.checked = settings.showPricesOnDriverTickets === true;
  const driverEditEl = document.getElementById('billing-settings-allow-driver-edit');
  if (driverEditEl) driverEditEl.checked = settings.allowDriverTimeEdit === true;
  // TASK-428 Slice 3 — driver equipment-edit gate. DEFAULT OFF.
  const driverEquipEditEl = document.getElementById('billing-settings-allow-driver-equipment-edit');
  if (driverEquipEditEl) driverEquipEditEl.checked = settings.allowDriverEquipmentEdit === true;
  // Decentralized 2026-05-06 from main Settings (Phase D). Force-on when in
  // ticket-building modes since the customer/job selection is required there.
  const isTicketMode = settings.clockOutMode === 'ticket' || settings.clockOutMode === 'ticketonly';
  const clockInCustomerEl = document.getElementById('billing-settings-clockin-ask-customer');
  if (clockInCustomerEl) {
    clockInCustomerEl.checked = isTicketMode ? true : (settings.clockInAskCustomer === true);
    clockInCustomerEl.disabled = isTicketMode;
  }
  const captureGpsOnClockEl = document.getElementById('billing-settings-capture-gps-on-clock');
  if (captureGpsOnClockEl) captureGpsOnClockEl.checked = settings.captureGpsOnClock === true;
  const captureGpsOnInspectionEl = document.getElementById('billing-settings-capture-gps-on-inspection');
  if (captureGpsOnInspectionEl) captureGpsOnInspectionEl.checked = settings.captureGpsOnInspection === true; // TASK-9554 default OFF
  // TASK-191 Phase 2 — geofence defaults. autoFromFirstWaypoint defaults ON.
  const gf = settings.geofence || {};
  const geofenceAutoEl = document.getElementById('billing-settings-geofence-autowaypoint');
  // Default OFF (opt-in) — drivers clock in at the yard, first waypoint is the
  // distant pickup; auto-ON would false-flag every clock-in (COD Domain/Skeptic).
  if (geofenceAutoEl) geofenceAutoEl.checked = gf.autoFromFirstWaypoint === true;
  const geofenceRadiusEl = document.getElementById('billing-settings-geofence-radius');
  if (geofenceRadiusEl) geofenceRadiusEl.value = String(gf.defaultRadiusFeet || 500);
  const enableShipSlipsEl = document.getElementById('billing-settings-enable-ship-slips');
  if (enableShipSlipsEl) enableShipSlipsEl.checked = settings.enableShipSlips === true;
  // TASK-491 — per-operator equipment tracking. Nested object, DEFAULT OFF.
  const equipmentTieEl = document.getElementById('billing-settings-equipment-tie');
  if (equipmentTieEl) equipmentTieEl.checked = settings.equipmentTie?.enabled === true;
  // Clock-out mode (simple | ticket | ticketonly)
  const clockOutMode = settings.clockOutMode || 'simple';
  const simpleRadio = document.getElementById('billing-settings-clockout-simple');
  const ticketRadio = document.getElementById('billing-settings-clockout-ticket');
  const ticketOnlyRadio = document.getElementById('billing-settings-clockout-ticketonly');
  if (simpleRadio) simpleRadio.checked = clockOutMode === 'simple';
  if (ticketRadio) ticketRadio.checked = clockOutMode === 'ticket';
  if (ticketOnlyRadio) ticketOnlyRadio.checked = clockOutMode === 'ticketonly';
  // Highlight active label
  document.querySelectorAll('[id^=clockout-mode-][id$=-label]').forEach(l => l.style.borderColor = 'var(--border-light)');
  const activeLabel = document.getElementById(`clockout-mode-${clockOutMode === 'simple' ? 'simple' : clockOutMode === 'ticket' ? 'ticket' : 'ticketonly'}-label`);
  if (activeLabel) activeLabel.style.borderColor = 'var(--brand-blue)';
  // TASK-572 — init-sync the mode-driven visibility AFTER the radios are set,
  // so a simple-mode tenant opens with the ticket-only controls already hidden
  // (not only on a radio onchange).
  syncBillingModeVisibility(clockOutMode);
  // Clock Out Alert
  const clockoutAlertEl = document.getElementById('billing-settings-clockout-alert');
  const clockoutAlertHoursEl = document.getElementById('billing-settings-clockout-alert-hours');
  const clockoutAlertEscHoursEl = document.getElementById('billing-settings-clockout-alert-esc-hours');
  const clockoutAlertDetail = document.getElementById('billing-settings-clockout-alert-detail');
  if (clockoutAlertEl) {
    clockoutAlertEl.checked = settings.clockOutAlert === true;
    // TASK-10040b: show what the server will use (never saved → 24 / 24), not a local fallback.
    const coaHours = resolveClockOutAlertHours(settings);
    if (clockoutAlertHoursEl) clockoutAlertHoursEl.value = coaHours.remindHours;
    if (clockoutAlertEscHoursEl) clockoutAlertEscHoursEl.value = coaHours.escalateHours;
    if (clockoutAlertDetail) clockoutAlertDetail.style.display = settings.clockOutAlert ? 'flex' : 'none';
  }
  // Tax settings
  const taxEnabledEl = document.getElementById('billing-settings-tax-enabled');
  if (taxEnabledEl) {
    taxEnabledEl.checked = settings.taxEnabled === true;
    document.getElementById('billing-settings-tax-detail').style.display = taxEnabledEl.checked ? 'block' : 'none';
  }
  const taxRateEl = document.getElementById('billing-settings-tax-rate');
  if (taxRateEl) taxRateEl.value = settings.defaultTaxRate || '';
  // TASK-9436 — company default permit markup (blank shows as 0).
  const permitMkEl = document.getElementById('billing-settings-permit-markup');
  if (permitMkEl) permitMkEl.value = _normalizePermitMarkupPercent(settings.defaultPermitMarkupPercent) ?? '';
  const perItemHideEl = document.getElementById('billing-settings-per-item-invoice-hide');
  if (perItemHideEl) perItemHideEl.checked = settings.perItemInvoiceHide === true; // TASK-9667 default OFF
  const roundEqEl = document.getElementById('billing-settings-round-equipment');
  if (roundEqEl) { roundEqEl.checked = _billingCompanyRoundsEquipment(); roundEqEl.disabled = false; } // TASK-9489 default OFF
  const billLaborEl = document.getElementById('billing-settings-bill-labor');
  if (billLaborEl) { billLaborEl.checked = settings.billLaborHours === true; billLaborEl.disabled = false; } // TASK-4875 default OFF
  const ticketClockEl = document.getElementById('billing-settings-ticket-times-clock');
  if (ticketClockEl) { ticketClockEl.checked = settings.ticketTimesUpdateClock === true; ticketClockEl.disabled = false; } // TASK-9982 default OFF
  _fuelSettingsRender(settings); // TASK-9440
  const defaultCats = settings.defaultTaxCategories || {};
  ['labor', 'equipment', 'permits', 'fieldItems', 'misc', 'downtime', 'standby'].forEach(cat => {
    const el = document.getElementById('billing-settings-tax-cat-' + cat);
    if (el) el.checked = defaultCats[cat] !== false;
  });
  // Accounting method
  const acctBasisEl = document.getElementById('billing-settings-accounting-basis');
  if (acctBasisEl) acctBasisEl.value = settings.accountingBasis || 'cash';
  // QB Desktop settings
  const qbTermsEl = document.getElementById('qb-settings-terms');
  if (qbTermsEl) qbTermsEl.value = settings.qbDefaultTerms || '';
  const qbArEl = document.getElementById('qb-settings-ar-account');
  if (qbArEl) qbArEl.value = settings.qbAccountsReceivable || '';
  const qbIncomeEl = document.getElementById('qb-settings-income-account');
  if (qbIncomeEl) qbIncomeEl.value = settings.qbIncomeAccount || '';
  const qbMap = settings.qbItemMap || {};
  ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'MISC'].forEach(type => {
    const el = document.getElementById('qb-item-' + type);
    if (el) el.value = qbMap[type] || '';
  });
  // Render manual customer mapping
  renderQBCustomerMapManual();

  // Online Payments status
  const opStatus = document.getElementById('billing-online-payments-status');
  if (opStatus) {
    const data = window.AppState?.tenantData || {};
    const connectId = data.stripeConnectAccountId;
    const connectComplete = data.stripeConnectOnboardingComplete;
    if (connectId && connectComplete) {
      opStatus.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;">
          <span class="material-icons" style="color:#059669;font-size:22px;">check_circle</span>
          <div>
            <div style="font-weight:600;color:#065F46;">Online Payments Enabled</div>
            <div style="font-size:0.8rem;color:#047857;">Customers can pay invoices with credit card.</div>
          </div>
        </div>`;
    } else if (connectId && !connectComplete) {
      opStatus.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;margin-bottom:12px;">
          <span class="material-icons" style="color:#D97706;font-size:22px;">warning</span>
          <div>
            <div style="font-weight:600;color:#92400E;">Onboarding Incomplete</div>
            <div style="font-size:0.8rem;color:#A16207;">Finish setting up your Stripe account to accept payments.</div>
          </div>
        </div>
        <button type="button" class="btn btn-primary" id="btn-billing-stripe-resume" style="width:100%;">
          <span class="material-icons" style="font-size:16px;">launch</span> Continue Stripe Setup
        </button>`;
      document.getElementById('btn-billing-stripe-resume')?.addEventListener('click', () => startStripeConnect('btn-billing-stripe-resume'));
    } else {
      opStatus.innerHTML = `
        <button type="button" class="btn btn-primary" id="btn-billing-stripe-setup" style="width:100%;">
          <span class="material-icons" style="font-size:16px;">credit_card</span> Enable Online Payments
        </button>
        <p style="font-size:0.75rem;color:var(--text-muted);margin-top:8px;">
          You'll be redirected to Stripe to set up your account. No DispatchDeck fees — standard Stripe processing rates apply.
        </p>`;
      document.getElementById('btn-billing-stripe-setup')?.addEventListener('click', () => startStripeConnect('btn-billing-stripe-setup'));
    }
    // TASK-9383 — Stripe is one of the two chip inputs; sync now so the collapsed
    // header shows "Needs attention" even before the QBO refresh returns.
    syncPaymentsAttentionChip();
  }

  // Plaid Bank Connection status
  const plaidStatus = document.getElementById('billing-plaid-status');
  if (plaidStatus) {
    const data = window.AppState?.tenantData || {};
    if (data.plaidConnected) {
      plaidStatus.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;margin-bottom:8px;">
          <span class="material-icons" style="color:#059669;font-size:22px;">check_circle</span>
          <div style="flex:1;">
            <div style="font-weight:600;color:#065F46;">Bank Connected</div>
            <div style="font-size:0.8rem;color:#047857;">${escapeHtml(data.plaidInstitution || 'Bank')} ••${escapeHtml(data.plaidAccountLast4 || '0000')}</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" id="btn-plaid-disconnect" style="font-size:0.75rem;padding:4px 12px;">Disconnect</button>
        </div>`;
      document.getElementById('btn-plaid-disconnect')?.addEventListener('click', disconnectPlaid);
    } else {
      plaidStatus.innerHTML = `
        <button type="button" class="btn btn-primary" id="btn-plaid-connect" style="width:100%;background:#10B981;border-color:#10B981;">
          <span class="material-icons" style="font-size:16px;">account_balance</span> Connect Your Bank
        </button>
        <p style="font-size:0.75rem;color:var(--text-muted);margin-top:8px;">
          Securely link via Plaid. DD never sees your login credentials. ~$1.50 per connection.
        </p>`;
      document.getElementById('btn-plaid-connect')?.addEventListener('click', startPlaidLink);
    }
  }

  // QBO Connection status — render the cached view first, then refresh against
  // LIVE status (TASK-764 COD#2: the in-memory qboConnected boolean is set once at
  // OAuth callback and never re-fetched, so a server-side reauth_required flip
  // wouldn't show without this live read).
  // TASK-9589 — NO cached first paint: refreshQBOStatus() marks the check in flight
  // and renders "Checking QuickBooks…"; "Connected" only ever comes from the live answer.
  refreshQBOStatus();

  _t9986ApplyReadOnly(modal, ['btn-save-billing-settings', 'btn-save-fuel-settings']); // TASK-9986 S1
  modal.classList.remove('hidden');
}

// TASK-764 — render the QBO status block in billing settings. Reads
// window._qboLiveStatus (set by refreshQBOStatus) when available, else the cached
// tenantData.qboConnected boolean. Surfaces an amber "Reconnect QuickBooks" banner
// when the refresh token has died (status === 'reauth_required').
function renderQBOStatusPanel() {
  const qboStatus = document.getElementById('billing-qbo-status');
  if (!qboStatus) return;
  // TASK-9589 — the panel NEVER trusts the cached tenantData.qboConnected/qboStatus:
  // a stale tab painted green "Connected" for a tenant that had disconnected.
  const st = getQBOPanelState();
  const status = st.status;
  const companyName = st.companyName || 'QuickBooks';

  if (st.kind === 'checking') {
    qboStatus.innerHTML = `
      <div id="billing-qbo-checking" style="display:flex;align-items:center;gap:8px;padding:12px 16px;min-height:48px;background:var(--bg-secondary, #F3F4F6);border:1px solid var(--border-color, #D1D5DB);border-radius:8px;">
        <span class="material-icons" style="color:var(--text-secondary);font-size:22px;">hourglass_empty</span>
        <div style="font-weight:600;color:var(--text-primary);">Checking QuickBooks…</div>
      </div>`;
  } else if (st.kind === 'error') {
    qboStatus.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;margin-bottom:8px;">
        <span class="material-icons" style="color:#D97706;font-size:22px;">warning_amber</span>
        <div style="flex:1;font-weight:600;color:#92400E;">Couldn't check QuickBooks</div>
        <button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-retry" style="font-size:0.75rem;padding:4px 12px;">Retry</button>
      </div>`;
    document.getElementById('btn-qbo-retry')?.addEventListener('click', () => refreshQBOStatus());
  } else if (status === 'reauth_required') {
    qboStatus.innerHTML = `
      <div style="display:flex;align-items:flex-start;gap:8px;padding:12px 16px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;margin-bottom:8px;">
        <span class="material-icons" style="color:#D97706;font-size:22px;">warning_amber</span>
        <div style="flex:1;">
          <div style="font-weight:600;color:#92400E;">QuickBooks needs to be reconnected</div>
          <div style="font-size:0.8rem;color:#B45309;">Your QuickBooks authorization expired. Reconnect to resume syncing.</div>
        </div>
      </div>
      <button type="button" class="btn btn-primary" id="btn-qbo-reconnect" style="width:100%;background:#D97706;border-color:#D97706;">
        <span class="material-icons" style="font-size:16px;">sync</span> Reconnect QuickBooks
      </button>`;
    document.getElementById('btn-qbo-reconnect')?.addEventListener('click', startQBOConnect);
  } else if (status === 'connected') {
    qboStatus.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;margin-bottom:8px;">
        <span class="material-icons" style="color:#059669;font-size:22px;">check_circle</span>
        <div style="flex:1;">
          <div style="font-weight:600;color:#065F46;">QuickBooks Online Connected</div>
          <div style="font-size:0.8rem;color:#047857;">${escapeHtml(companyName)}</div>
        </div>
        <button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-disconnect" style="font-size:0.75rem;padding:4px 12px;">Disconnect</button>
      </div>
      <div style="display:flex;gap:8px;margin-top:8px;">
        <button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-pull-accounts" style="flex:1;">
          <span class="material-icons" style="font-size:14px;">sync</span> Refresh Accounts
        </button>
        ${window._qboLiveStatus?.readOnly === false ? `<button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-map-accounts" style="flex:1;">
          <span class="material-icons" style="font-size:14px;">account_tree</span> Where invoices post in QuickBooks
        </button>` : ''}
      </div>
      <button type="button" class="btn btn-secondary btn-sm" id="btn-qbo-paid-sync" style="width:100%;margin-top:8px;">
        <span class="material-icons" style="font-size:14px;">price_check</span> Paid sync (billed elsewhere)
      </button>
      <button type="button" class="btn btn-primary" id="btn-qbo-setup" style="width:100%;margin-top:8px;">
        <span class="material-icons" style="font-size:16px;">checklist</span> QuickBooks setup
      </button>`;
    document.getElementById('btn-qbo-paid-sync')?.addEventListener('click', openQBOPaidSync);
    document.getElementById('btn-qbo-setup')?.addEventListener('click', openQBOSetup);
    document.getElementById('btn-qbo-disconnect')?.addEventListener('click', disconnectQBO);
    document.getElementById('btn-qbo-pull-accounts')?.addEventListener('click', pullQBOAccounts);
    document.getElementById('btn-qbo-map-accounts')?.addEventListener('click', openQBOAccountMapping);
  } else {
    qboStatus.innerHTML = `
      <button type="button" class="btn btn-primary" id="btn-qbo-connect" style="width:100%;background:#2CA01C;border-color:#2CA01C;">
        <span class="material-icons" style="font-size:16px;">sync</span> Connect QuickBooks Online
      </button>
      <p style="font-size:0.75rem;color:var(--text-muted);margin-top:8px;">
        You'll authorize DispatchDeck in QuickBooks. Free — no per-sync charges.
      </p>${st.lastAttemptAt ? `<p id="billing-qbo-unfinished" style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Last connect attempt wasn't finished — try again.</p>` : ''}
      <button type="button" class="btn btn-secondary" id="btn-qbo-setup" style="width:100%;margin-top:8px;">QuickBooks Desktop? Open QuickBooks setup</button>`;
    document.getElementById('btn-qbo-connect')?.addEventListener('click', startQBOConnect);
    document.getElementById('btn-qbo-setup')?.addEventListener('click', openQBOSetup); // TASK-10123 step 0 -> Desktop
  }
  // TASK-9383 — every path through this renderer (first paint, live refresh,
  // post-reconnect) re-decides the header chip, so it can never disagree with
  // the panel body it sits above.
  syncPaymentsAttentionChip();
}

// TASK-9383 — does the "Payments & integrations" section hold a BROKEN integration?
// Same expressions the two status renderers use, on purpose:
//   • QBO   — renderQBOStatusPanel: live window._qboLiveStatus.status first, cached
//             tenantData.qboStatus second; broken ⇔ 'reauth_required'.
//   • Stripe — openBillingSettings "Onboarding Incomplete" branch:
//             connectId && !connectComplete.
// "Not connected" is NOT attention (a tenant that never set up Stripe/QBO sees no
// chip). Plaid's renderer has only connected / not-connected, so it contributes
// nothing — do not invent a state for it.
function paymentsNeedAttention() {
  const data = window.AppState?.tenantData || {};
  const qboStatus = window._qboLiveStatus?.status || data.qboStatus || null;
  const qboBroken = qboStatus === 'reauth_required';
  const stripeBroken = !!data.stripeConnectAccountId && !data.stripeConnectOnboardingComplete;
  return { needsAttention: qboBroken || stripeBroken, qboBroken, stripeBroken };
}

// TASK-9383 — show/hide the amber "Needs attention" chip on the COLLAPSED
// "Payments & integrations" header. Chris chose collapsed-by-default + this chip
// over TASK-572's expanded-by-default; the chip is the signal, the section is
// never auto-expanded. Idempotent; safe to call when the modal isn't in the DOM.
function syncPaymentsAttentionChip() {
  const chip = document.getElementById('billing-acc-payments-chip');
  if (!chip) return;
  const { needsAttention, qboBroken, stripeBroken } = paymentsNeedAttention();
  chip.style.display = needsAttention ? 'inline-flex' : 'none';
  const reasons = [];
  if (qboBroken) reasons.push('QuickBooks needs to be reconnected');
  if (stripeBroken) reasons.push('Stripe onboarding is incomplete');
  chip.title = reasons.join(' · ');
}

// TASK-764 — fetch LIVE QBO status from the server (status: connected |
// reauth_required | not_connected), cache it, then re-render the settings panel +
// the dashboard banner. Cheap, fire-and-forget; failures fall back to the cached
// boolean. Only meaningful for tenants that have ever connected.
// TASK-9538 — generation stamp: a disconnect bumps it, and a status round-trip
// that started before the bump throws its answer away instead of repainting
// "Connected" over a tenant that just disconnected.
//
// TASK-9589 — the LIVE answer is the only source of "Connected". Always call
// (the old "skip when the cache is falsy" shortcut hid a live connection from a tab
// whose cache was empty), and a failed/slow check is a visible "Couldn't check —
// Retry", never the cached green. _qboCheckSeq retires an older in-flight check
// when a newer one starts (Retry, reopen) so its answer can't land out of order.
const QBO_CHECK_CAP_MS = 4000;
let _qboStatusGen = 0;
let _qboCheckSeq = 0;
let _qboCheckInFlight = false;
let _qboCheckFailed = false;
let _qboCheckTimer = null;

// What the panel should show right now. Pure over module state + window._qboLiveStatus.
//   kind 'checking' — a live check is in flight (or none has run yet)
//   kind 'error'    — the last live check failed or ran past the ~4 s cap
//   kind 'live'     — a live answer: status connected | reauth_required | not_connected
function getQBOPanelState() {
  if (_qboCheckFailed) return { kind: 'error' };
  if (_qboCheckInFlight) return { kind: 'checking' };
  const live = window._qboLiveStatus || null;
  if (!live) return { kind: 'checking' };
  return {
    kind: 'live',
    status: live.status || 'not_connected',
    companyName: live.companyName || null,
    lastAttemptAt: live.lastAttemptAt || null,
  };
}

// The ONE shared answer to "is QuickBooks connected?" for every surface that used to
// read the cached tenantData.qboConnected. true / false from the last LIVE answer;
// null = no live answer yet (callers decide; nothing may paint it as connected).
function getQBOConnectionTruth() {
  const live = window._qboLiveStatus || null;
  if (!live) return null;
  return live.status === 'connected' && live.connected !== false;
}
function isQBOConnectedLive() { return getQBOConnectionTruth() === true; }

async function ensureQBOLiveStatus() {
  if (!window._qboLiveStatus) await refreshQBOStatus();
  return getQBOConnectionTruth();
}

async function refreshQBOStatus() {
  const seq = ++_qboCheckSeq;
  const gen = _qboStatusGen;
  _qboCheckInFlight = true;
  _qboCheckFailed = false;
  clearTimeout(_qboCheckTimer);
  _qboCheckTimer = setTimeout(() => {
    if (seq !== _qboCheckSeq || !_qboCheckInFlight) return;
    _qboCheckFailed = true; // slow = unknown; a late answer still repaints below
    renderQBOStatusPanel();
  }, QBO_CHECK_CAP_MS);
  renderQBOStatusPanel();
  try {
    const fn = httpsCallable(functions, 'qboCheckStatus');
    const res = await fn({});
    if (gen !== _qboStatusGen || seq !== _qboCheckSeq) return; // disconnected / superseded while in flight
    clearTimeout(_qboCheckTimer);
    _qboCheckInFlight = false;
    _qboCheckFailed = false;
    window._qboLiveStatus = res.data || null;
    // Keep the cached fields coherent so any remaining cache reader (AP view) is no
    // staler than the last live answer.
    if (window.AppState?.tenantData) {
      window.AppState.tenantData.qboStatus = res.data?.status || null;
      window.AppState.tenantData.qboConnected = res.data?.connected === true;
      if (res.data?.companyName) window.AppState.tenantData.qboCompanyName = res.data.companyName;
    }
    renderQBOStatusPanel();
    renderQBOReauthBanner();
  } catch (e) {
    console.warn('[Billing] qboCheckStatus failed:', e?.message);
    if (gen !== _qboStatusGen || seq !== _qboCheckSeq) return;
    clearTimeout(_qboCheckTimer);
    _qboCheckInFlight = false;
    _qboCheckFailed = true; // never fall back to the cached green
    renderQBOStatusPanel();
  }
}

// TASK-764 COD#4 — surface the amber "Reconnect QuickBooks" banner at the TOP of
// the main Billing dashboard (not only inside the settings modal), so a dead QBO
// connection is visible without opening settings. Renders into a persistent
// #billing-qbo-banner slot above the content area.
// ============================================
// TASK-9983 — "Pay changed after invoicing" (billing's side of the pay fork)
// ============================================
// A Workforce pay correction on a closed ticket leaves the ticket and invoice
// alone and writes a server-only invoicedPayChanges record. Billing sees every
// record here: the ones asking for an invoice check (invoice short / invoice
// wrong / hours added to another job) with a Dismiss, the rest listed quietly.
// Read + dismiss go through the gateway (no firestore.rules match — the
// collection is server-only). Nothing here changes a total, a revenue row, an
// invoice record or a QuickBooks link.
function _payChangeHours(n) { return (Math.round(Number(n || 0) * 100) / 100).toFixed(2); }

// One plain sentence per record. Pure (unit-tested).
function _payChangeSentence(r) {
  if (!r) return '';
  const who = r.personName || 'Someone';
  const day = r.workDate ? ` on ${r.workDate}` : '';
  const d = Number(r.deltaHours || 0);
  const signed = `${d > 0 ? '+' : ''}${_payChangeHours(d)} h`;
  const tno = r.ticketNo != null ? `#${r.ticketNo}` : 'the ticket';
  // TASK-9989 — a manual entry on an already-billed day, left off every ticket.
  if (r.kind === 'leftUnbilled') {
    const t = r.target || {};
    const where = [t.customerName, t.jobTitle].filter(Boolean).join(' — ');
    const inv = r.lock && r.lock.invoiceNumber ? ` (${r.lock.invoiceNumber})` : '';
    return `${who}${day}: ${_payChangeHours(d)} h added by hand${where ? ` for ${where}` : ''} were left unbilled — ${tno}${inv} was already billed. Bill them on a new ticket or dismiss.`;
  }
  if (r.kind === 'payFork') {
    const t = r.target || {};
    const where = r.answer === 'internal' ? (t.customerName || 'Shop') : [t.customerName, t.jobTitle].filter(Boolean).join(' — ');
    return r.answer === 'internal'
      ? `${who}${day}: ${_payChangeHours(d)} h added to ${where} (internal time) as its own entry.`
      : `${who}${day}: ${_payChangeHours(d)} h added to ${where} — check its ticket${r.ticketNo != null ? ` (#${r.ticketNo})` : ''}.`;
  }
  const base = `${who}${day}: pay changed after invoicing ${signed} on ${tno}`;
  switch (r.answer) {
    case 'invoiceShort': return `${base} — the invoice may be short.`;
    case 'invoiceWrong': return `${base} — the invoice may need correcting.`;
    case 'covered': return `${base} — already billed (no change needed).`;
    case 'payOnly': return `${base} — pay only (no change needed).`;
    default: return `${base}.`;
  }
}

function _payChangeMeta(r) {
  const by = r.createdBy && r.createdBy.email ? r.createdBy.email : '';
  const at = r.createdAtMs ? new Date(r.createdAtMs).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const lk = r.lock || {};
  const qb = lk.qboDocNumber ? ` · In QuickBooks as #${lk.qboDocNumber} — correct it there, then dismiss.` : '';
  const paid = lk.paid ? ' · Invoice is marked paid.' : '';
  const why = r.reduceReason && r.reduceReason.label ? ` · Reason: ${r.reduceReason.label}${r.reduceReason.note ? ` (${r.reduceReason.note})` : ''}` : '';
  const done = r.resolution === 'dismissed' ? ` · Dismissed${r.resolvedBy && r.resolvedBy.email ? ` by ${r.resolvedBy.email}` : ''}${r.resolutionNote ? `: ${r.resolutionNote}` : ''}` : '';
  return [by && `by ${by}`, at].filter(Boolean).join(' · ') + qb + paid + why + done;
}

// Pure: the banner HTML for a list of rows ('' when there is nothing to show).
function _payChangesBannerHtml(rows, opts) {
  const o = opts || {};
  const list = Array.isArray(rows) ? rows : [];
  // The page banner and the Review banner can be in the DOM together — one id
  // per surface so a note typed in Review is the one that is read (2nd review).
  const scope = o.scope === 'review' ? 'review' : 'page';
  const open = list.filter(r => r && r.state === 'needsBilling');
  const quiet = list.filter(r => r && r.state !== 'needsBilling');
  if (!open.length && !quiet.length) return '';
  const row = (r, canDismiss) => `
    <div class="paychange-row" data-paychange-id="${escapeHtml(r.id)}" style="padding:8px 0;border-top:1px solid var(--border-light);">
      <div style="color:var(--text-primary);font-size:0.88rem;font-weight:${canDismiss ? 600 : 400};">${escapeHtml(_payChangeSentence(r))}</div>
      <div style="color:var(--text-secondary);font-size:0.78rem;margin-top:2px;">${escapeHtml(_payChangeMeta(r))}</div>
      ${canDismiss && o.canDismiss ? `
      <div style="display:flex;gap:6px;align-items:center;margin-top:6px;flex-wrap:wrap;">
        <input type="text" class="form-control" id="paychange-note-${scope}-${escapeHtml(r.id)}" maxlength="300" placeholder="Note (optional) — e.g. credit memo sent" style="flex:1;min-width:160px;font-size:0.82rem;padding:4px 8px;">
        <button class="btn btn-secondary btn-sm" style="min-height:36px;" onclick="window.BillingModule.dismissPayChange('${window.escJsAttr(r.id)}', this, '${scope}')">Dismiss</button>
      </div>` : ''}
    </div>`;
  return `
    <div data-paychange-scope="${scope}" style="background:var(--bg-primary,#fff);border:1px solid #F59E0B;border-left:4px solid #F59E0B;border-radius:var(--radius-sm);padding:10px 14px;margin-bottom:10px;">
      <div style="display:flex;align-items:center;gap:8px;font-weight:600;color:var(--text-primary);">
        <span class="material-icons" style="font-size:20px;color:#B45309;" aria-hidden="true">payments</span>
        Pay changed after invoicing${open.length ? ` — ${open.length} to check` : ''}
      </div>
      ${open.map(r => row(r, true)).join('')}
      ${quiet.length ? `<details style="margin-top:6px;"><summary style="cursor:pointer;color:var(--text-secondary);font-size:0.82rem;">${quiet.length} other pay change${quiet.length !== 1 ? 's' : ''} — no action needed</summary>${quiet.slice(0, 50).map(r => row(r, false)).join('')}</details>` : ''}
    </div>`;
}

function _canDismissPayChanges() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  const uid = A.user?.uid;
  if (uid && A.tenantData?.ownerId === uid) return true;
  const ma = A.memberAccess;
  if (!ma || ['revoked', 'inactive', 'pending', 'removed', 'suspended'].includes(ma.status)) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('billing') && ma.viewUploadOnly !== true && ma.readOnly !== true;
}

async function _loadPayChanges(tenantId) {
  const tid = tenantId || BillingState.tenantId;
  if (!tid || tid === 'demo-tenant') { BillingState.payChanges = []; BillingState.payChangesTenantId = tid || null; renderPayChangesBanner(); return; }
  // integ/2108.1 COD (Code Reviewer): rows loaded for another company never
  // show while this company's list is in flight.
  if (BillingState.payChangesTenantId !== tid) { BillingState.payChanges = null; BillingState.payChangesTenantId = tid; renderPayChangesBanner(); }
  try {
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 30000 });
    const res = await fn({ op: 'listInvoicedPayChanges', tenantId: tid, payload: {} });
    if (BillingState.tenantId !== tid) return; // switched companies mid-read
    BillingState.payChanges = Array.isArray(res?.data?.rows) ? res.data.rows : [];
    BillingState.payChangesTenantId = tid;
  } catch (e) {
    // COD diff r2: a late failure from company A must not clear company B's rows.
    if (BillingState.tenantId !== tid) return;
    console.warn('[Billing] pay changes could not be loaded:', e?.message);
    BillingState.payChanges = null; // unknown — the banner stays hidden, the Review banner too
  }
  renderPayChangesBanner();
  // COD diff r2: a correction made while Billing stayed open shows on the
  // ticket the biller then opens (openReviewTicket reloads; re-render only
  // when this ticket has an open flag the modal is not showing yet).
  const _rt = ReviewState.ticket;
  const _rm = document.getElementById('modal-review-ticket');
  if (_rt && _rt.id && _rm && !_rm.classList.contains('hidden') &&
      (BillingState.payChanges || []).some(r => r && r.ticketId === _rt.id && r.state === 'needsBilling') &&
      !document.querySelector('#review-ticket-body [data-paychange-scope="review"]')) {
    // integ/2108.1 COD r2 (Code Reviewer): insert just the banner — a full
    // renderReviewTicket() from this late async answer would wipe unsaved
    // Review inputs. The full render places it in order on the next open.
    try {
      const _body = document.getElementById('review-ticket-body');
      const _bh = _reviewPayChangeBannerHtml(_rt.id);
      if (_body && _bh) _body.insertAdjacentHTML('afterbegin', _bh);
    } catch (_) { /* the banner shows on the next open */ }
  }
}

function renderPayChangesBanner() {
  const el = document.getElementById('billing-paychanges-banner');
  if (!el) return;
  const html = _payChangesBannerHtml(BillingState.payChanges, { canDismiss: _canDismissPayChanges() });
  el.innerHTML = html;
  el.style.display = html ? '' : 'none';
}

// ============================================
// TASK-10041 — "DISPATCHED, NO TICKET" REVIEW (Tickets view, inline)
// ============================================
// The office works from Tickets (customer → project → job), so a dispatched day
// with no ticket shows RIGHT THERE: a row under its customer/job with a warning
// chip, plus one header chip ("Needs decision N") that filters to just those
// rows. The rule and every write live on the server (`billingReview` callable,
// functions/src/shared/dispatchedNoTicket.js + services/billingReview.js); this
// block only renders what it returns and posts decisions back. Creating a ticket
// is NOT a new path: it opens the existing job-day modal (twin-ticket warning +
// words check + generateTicketFromJob → createTicketChecked's per-day dedup).
//
// 'daily' rows show inline and count on the chip; 'weekly' rows (date off, crew
// on a live ticket elsewhere, ticket with no board day — measured as almost
// always billed) sit behind a "Weekly cleanup" link. No dollar figures anywhere.
const _REVIEW_VIEW_FILTERS = new Set(['all', 'daily', 'draft', 'ready']);
const _REVIEW_KIND_LABEL = {
  no_ticket: 'Dispatched, no ticket',
  clock_no_ticket: 'Shift with no ticket',
  no_dispatch: 'Ticket with no dispatch day',
  no_truck: 'No truck billed',
  no_clock: 'No clock entry', // TASK-10186
};

async function _loadDispatchReview(tenantId) {
  const tid = tenantId || BillingState.tenantId;
  if (!tid || tid === 'demo-tenant') { BillingState.dispatchReview = null; return; }
  if (BillingState.dispatchReview && BillingState.dispatchReview.tenantId !== tid) BillingState.dispatchReview = null;
  try {
    const fn = httpsCallable(functions, 'billingReview', { timeout: 90000 });
    const res = await fn({ op: 'list', tenantId: tid });
    if (BillingState.tenantId !== tid) return; // switched companies mid-read
    const d = res?.data || {};
    BillingState.dispatchReview = {
      tenantId: tid,
      rows: Array.isArray(d.rows) ? d.rows : [],
      counts: d.counts || {},
      ub: d.unbillable, // TASK-10134 Meeting / Shop Day hours (billingReviewUi weekly modal)
      window: d.window || null,
      peopleNames: d.peopleNames || {},
      truckNames: d.truckNames || {},
    };
  } catch (e) {
    if (BillingState.tenantId !== tid) return;
    // Unknown, not zero: the chip hides rather than claim "0 need a decision".
    console.warn('[Billing] dispatched-no-ticket review could not load:', e?.message);
    BillingState.dispatchReview = null;
  }
  if ((BillingState.dispatchReview?.rows || []).length) { try { await _ensureReviewUi(); } catch (_) { /* cards fill in on the next render */ } }
  if (BillingState.viewMode === 'tickets') renderTicketsList();
}

// A row the office has already fixed in another tab disappears at once, without
// waiting for a reload: a ticket now covering the day hides a no_ticket row, a
// deleted ticket hides its no_truck / no_dispatch row.
function _reviewRowStillOpen(r) {
  const tickets = BillingState.tickets || [];
  if (r.kind === 'no_ticket' && r.coverage !== 'date_off') {
    // TASK-10107 — a SEALED return estimate is not the day's ticket (same answer
    // as the engine's ticketIsReturnEstimate; tests/task10107-return-estimate-seal.unit.cjs).
    const _isReturnEstimate = (t) => {
      if (!t || t.returnEstimate !== true) return false;
      const s = t.returnEstimateSeal;
      if (!s || typeof s !== 'object' || !t.jobId || s.jobId !== t.jobId || s.mirrorOf !== t.mirrorOf) return false;
      if (t.status !== 'draft' || t.invoiced === true || t.invoiceNumber || t.invoicedAt) return false;
      return !(Number(t.grandTotal) > 0);
    };
    return !tickets.some(t => t.status !== 'deleted' && !_isReturnEstimate(t) && (t.jobId === r.jobId || t.originalJobId === r.jobId)
      && (t.workDate === r.dateKey || (t.workDateEnd && r.dateKey >= t.workDate && r.dateKey <= t.workDateEnd)));
  }
  if (r.kind === 'no_ticket' && r.coverage === 'date_off' && r.offTicket) {
    const t = tickets.find(x => x.id === r.offTicket.id);
    return !(t && t.workDate === r.dateKey);
  }
  if (r.kind === 'no_truck' || r.kind === 'no_dispatch' || r.kind === 'no_clock') {
    const t = tickets.find(x => x.id === r.ticketId);
    return !t || t.status !== 'deleted';
  }
  return true;
}

// The badge, the weekly list and the invoice warning ALWAYS read the recent
// window. Only the chip's range view (history on demand) reads the range result.
function _reviewRowsAll(list, opts) {
  const useRange = !!(opts && opts.range);
  const rv = useRange ? BillingState.dispatchReviewRange : BillingState.dispatchReview;
  if (!rv || rv.tenantId !== BillingState.tenantId) return [];
  const hidden = BillingState._reviewHidden || new Set();
  return rv.rows.filter(r => (list ? (r.list || 'daily') === list : true) && !hidden.has(r.id) && _reviewRowStillOpen(r));
}

// The daily rows this Tickets view should show, under the same customer /
// project / search scope as the tickets around them. Ticket-only drill-downs
// (Missing Permits, a delivery chip, a single-job drill) show none.
function _reviewRowsInView() {
  // TASK-10186 — "No clock entry" chip on: ONLY those rows, whatever the status
  // filter (an archived ticket counts — Lacey), under the same customer /
  // project / search scope. A loaded range shows that range's rows.
  if (BillingState.noClockFilter === true) {
    let nc = BillingState.dispatchReviewRange ? _reviewRowsAll('noclock', { range: true }) : _reviewRowsAll('noclock');
    if (BillingState.ticketCustomerFilter.size > 0) nc = nc.filter(r => BillingState.ticketCustomerFilter.has(r.customerId));
    if (BillingState.ticketProjectMode) nc = nc.filter(r => _ticketInProjectFilter({ jobId: r.jobId || null, customerId: r.customerId }));
    const nq = _ticketSearchQuery();
    if (nq) nc = nc.filter(r => [r.customerName, r.jobTitle, r.ticketNo].some(f => f && String(f).toLowerCase().includes(nq)));
    return nc;
  }
  if (!_REVIEW_VIEW_FILTERS.has(BillingState.filter)) return [];
  if (BillingState.missingPermitFilter || BillingState.deliveryFilter || BillingState.ticketJobFilter) return [];
  // Chip on with a range loaded: every row in that range (history on demand,
  // daily + weekly). Otherwise the recent window's daily rows. Never the
  // 'noclock' rows (TASK-10186) — they have their own chip.
  const _rangeOn = BillingState.reviewFilter === true && !!BillingState.dispatchReviewRange;
  let rows = _rangeOn ? _reviewRowsAll(null, { range: true }).filter(r => r.list !== 'noclock') : _reviewRowsAll('daily');
  if (BillingState.filter === 'daily') {
    const _dk = formatDateISO(BillingState.dailyDate);
    rows = rows.filter(r => r.dateKey === _dk);
  }
  if (BillingState.ticketCustomerFilter.size > 0) rows = rows.filter(r => BillingState.ticketCustomerFilter.has(r.customerId));
  if (BillingState.ticketProjectMode) {
    rows = rows.filter(r => _ticketInProjectFilter({ jobId: r.jobId || null, customerId: r.customerId }));
  }
  const q = _ticketSearchQuery();
  if (q) rows = rows.filter(r => [r.customerName, r.jobTitle, r.personName, r.ticketNo].some(f => f && String(f).toLowerCase().includes(q)));
  return rows;
}

function _reviewChipHtml() {
  const rv = BillingState.dispatchReview;
  if (!rv || rv.tenantId !== BillingState.tenantId) return '<span id="tickets-review-chips"></span>';
  _ensureReviewStyles();
  const n = _reviewRowsAll('daily').length;
  const w = _reviewRowsAll('weekly').length;
  const ub = rv.ub?.shifts?.length || rv.ub?.days?.length || rv.ub?.tickets?.length; // TASK-10134 (+ tickets on a non-billable job)
  const on = BillingState.reviewFilter === true;
  const quiet = !on && n === 0;
  const tip = 'Dispatched work with no ticket (last two weeks). Click to show only those.';
  // TASK-10186 — "No clock entry" (Lacey): clock-in tickets no clock entry
  // points at any more. Own chip, own count; never part of Needs decision.
  const nc = _reviewRowsAll('noclock').length;
  const ncOn = BillingState.noClockFilter === true;
  const ncTip = 'Tickets made by a clock-in that no clock entry points at any more (the clock-in was moved or deleted). Archived tickets count. Last two weeks; pick a range for older ones.';
  const ncChip = `<button type="button" class="tickets-delivery-chip tickets-review-chip${ncOn ? ' active' : ''}${!ncOn && nc === 0 ? ' is-zero' : ''}" aria-pressed="${ncOn}" title="${escapeHtml(ncTip)}" onclick="window.BillingModule.toggleNoClockFilter()">
      No clock entry<span class="tdc-n">${nc}</span>${ncOn ? '<span class="material-icons" style="font-size:14px;margin-left:2px;">close</span>' : ''}
    </button>`;
  return `<span id="tickets-review-chips" class="tickets-review-chips">
    <button type="button" class="tickets-delivery-chip tickets-review-chip${on ? ' active' : ''}${quiet ? ' is-zero' : ''}" aria-pressed="${on}" title="${escapeHtml(tip)}" onclick="window.BillingModule.toggleReviewFilter()">
      Needs decision<span class="tdc-n">${n}</span>${on ? '<span class="material-icons" style="font-size:14px;margin-left:2px;">close</span>' : ''}
    </button>${ncChip}${on || ncOn ? _reviewRangeHtml() : ''}${w || ub ? `<button type="button" class="tickets-delivery-chip tickets-review-weekly" title="Probably billed another way — check weekly" onclick="window.BillingModule.openReviewWeekly()">Weekly cleanup${w ? ' ' + w : ''}</button>` : ''}
  </span>`;
}

function toggleReviewFilter() {
  BillingState.reviewFilter = !BillingState.reviewFilter;
  BillingState.noClockFilter = false; // TASK-10186 — one review chip at a time
  if (!BillingState.reviewFilter) BillingState.dispatchReviewRange = null;
  clearTicketSelection();
  renderTicketsList();
}
// TASK-10186 — the "No clock entry" chip. Same rows source (billingReview list)
// and the same range picker as Needs decision; the two chips never stack.
function toggleNoClockFilter() {
  BillingState.noClockFilter = !BillingState.noClockFilter;
  BillingState.reviewFilter = false;
  BillingState.dispatchReviewRange = null;
  clearTicketSelection();
  if (BillingState.noClockFilter) _ensureReviewUi().then(() => renderTicketsList()).catch(() => {});
  renderTicketsList();
}

// The range picker / loader / invoice warning live in billingReviewUi.js too.
function _reviewRangeHtml() {
  if (_reviewUi) return _reviewUi.rangeHtml();
  _ensureReviewUi().then(() => renderTicketsList()).catch(() => {});
  return '';
}
async function loadReviewRange() {
  try { return (await _ensureReviewUi()).loadRange(); }
  catch (e) { window.showToast?.('Could not load that range — reload the page and try again.', 'error'); }
}
// TASK-10041 — invoicing a customer with undecided days that month WARNS,
// never blocks. Nothing loaded / no rows = say nothing (and load nothing).
async function _undecidedDaysInvoiceWarning(tickets) {
  if (!_reviewRowsAll(null).length) return true;
  try { return await (await _ensureReviewUi()).undecidedDaysWarning(tickets); }
  catch (e) { return true; } // a warning that cannot load never blocks an invoice
}

function _fmtReviewDate(k) {
  if (!k) return '';
  return new Date(k + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}
function _fmtReviewDateLong(k) {
  if (!k) return '';
  return new Date(k + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// The chip a job row carries: "Dispatched 10/21, no ticket" (+N more).
function _reviewJobChipHtml(rows) {
  if (!rows || !rows.length) return '';
  _ensureReviewStyles();
  const first = rows[0];
  const label = first.kind === 'no_truck' ? `No truck billed${first.ticketNo ? ' · #' + first.ticketNo : ''}`
    : first.kind === 'no_clock' ? `No clock entry${first.ticketNo ? ' · #' + first.ticketNo : ''}` // TASK-10186
    : first.kind === 'clock_no_ticket' ? `Shift ${_fmtReviewDate(first.dateKey)}, no ticket`
    : first.kind === 'no_dispatch' ? 'No dispatch day'
    : `Dispatched ${_fmtReviewDate(first.dateKey)}, no ticket`;
  const more = rows.length > 1 ? ` +${rows.length - 1}` : '';
  return `<span class="review-chip" title="Needs a billing decision — open the job to see it">
    <span class="material-icons" aria-hidden="true">warning_amber</span>${escapeHtml(label)}${more}</span>`;
}
function _reviewCustChipHtml(n) {
  if (!n) return '';
  _ensureReviewStyles();
  return `<span class="review-chip" title="Work under this customer needs a billing decision"><span class="material-icons" aria-hidden="true">warning_amber</span>${n} need${n === 1 ? 's' : ''} a decision</span>`;
}

// TASK-10041 — the card / form / weekly modal live in billingReviewUi.js,
// loaded lazily (perf budget). Until it lands a card renders nothing; the
// job/customer chips and the header chip are always here.
let _reviewUi = null;
let _reviewUiLoading = null;
function _ensureReviewUi() {
  if (_reviewUi) return Promise.resolve(_reviewUi);
  if (!_reviewUiLoading) {
    _reviewUiLoading = __ddChunkWrap(import('./billingReviewUi.js?v=10304135')).then((m) => {
      m.initReviewUi({ BillingState, escapeHtml, _moneyViewOnly, _moneyBlocked, _fmtReviewDate, _fmtReviewDateLong, _reviewRowById, _reviewRowsAll, renderTicketsList, httpsCallable, functions });
      _reviewUi = m;
      return m;
    }).catch((e) => { _reviewUiLoading = null; throw e; });
  }
  return _reviewUiLoading;
}
// TASK-9264 — crew-reported materials at Review (accept -> server stock move) live in materialsReview.js, lazy.
let _matUi = null;
let _matUiLoading = null;
function _rerenderEquipSection() {
  const s = document.getElementById('review-equip-section');
  if (s) { s.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
}
function _ensureMaterialsUi() {
  if (_matUi) return Promise.resolve(_matUi);
  if (!_matUiLoading) {
    _matUiLoading = __ddChunkWrap(import('./materialsReview.js?v=1')).then((m) => {
      m.init({ BillingState, ReviewState, escapeHtml, escJsAttr: window.escJsAttr, isReviewReadOnly, httpsCallable, functions, openReviewTicket, rerender: _rerenderEquipSection });
      _matUi = m;
      return m;
    }).catch((e) => { _matUiLoading = null; throw e; });
  }
  return _matUiLoading;
}
// TASK-10045 S2 — the daily billing integrity warnings (toolbar chip, ticket /
// job chips, findings list + dismiss/reopen) live in billingIntegrityUi.js,
// loaded lazily after the view opens. Until it lands (or if it fails) nothing
// shows: a warning never blocks anything.
let _integrityUi = null;
let _integrityUiLoading = null;
function _ensureIntegrityUi() {
  if (_integrityUi) return Promise.resolve(_integrityUi);
  if (!_integrityUiLoading) {
    _integrityUiLoading = __ddChunkWrap(import('./billingIntegrityUi.js?v=1')).then((m) => {
      m.initIntegrityUi({ BillingState, escapeHtml, escJsAttr, _moneyViewOnly, _moneyBlocked, renderTicketsList, httpsCallable, functions });
      _integrityUi = m;
      return m;
    }).catch((e) => { _integrityUiLoading = null; throw e; });
  }
  return _integrityUiLoading;
}
function _loadIntegrity(tenantId) {
  _ensureIntegrityUi().then((m) => m.load(tenantId)).catch((e) => console.warn('[Billing] billing warnings unavailable:', e?.message));
}
const _integrityChip = (fn, id) => (_integrityUi ? _integrityUi[fn](id) : (fn === 'toolbarChipHtml' ? '<span id="tickets-integrity-chips"></span>' : ''));
// TASK-4767 S2 — "did they pay?": the Invoices panel (open balance by customer,
// record / remove a payment, check QuickBooks for a paid suggestion) lives in
// invoicePayments.js, loaded only on the first tap (perf budget). It reads and
// writes through the invoicePayments callable; the server re-checks access.
let _invoicesPanelLoad = null;
// TASK-4767 S2 one pay path — `invoiceId` (optional, from a ticket) lands on that invoice.
function openInvoicesPanel(invoiceId) {
  (_invoicesPanelLoad ||= __ddChunkWrap(import('./invoicePayments.js?v=4')).catch((e) => { _invoicesPanelLoad = null; throw e; }))
    .then((m) => m.open({ BillingState, escapeHtml, _moneyViewOnly, renderTicketsList, httpsCallable, functions, invoiceId: typeof invoiceId === 'string' ? invoiceId : null }))
    .catch(() => window.showToast?.('Could not open Invoices. Try again.', 'error'));
}
const _invoicesPanelChipHtml = () => '<button type="button" class="tickets-delivery-chip tickets-toolbar-chip" onclick="window.BillingModule.openInvoicesPanel()" title="Who owes you, and what they paid"><span class="material-icons" style="font-size:15px;">receipt_long</span> Invoices</button>';
function _reviewCardHtml(r) {
  if (!_reviewUi) { _ensureReviewUi().then(() => { if (BillingState.viewMode === 'tickets') renderTicketsList(); }).catch(() => {}); return ''; }
  return _reviewUi.cardHtml(r);
}

function _reviewRowById(rowId) {
  return ((BillingState.dispatchReview || {}).rows || []).find(r => r.id === rowId)
    || ((BillingState.dispatchReviewRange || {}).rows || []).find(r => r.id === rowId) || null;
}

function reviewCreateTicket(rowId) {
  const r = _reviewRowById(rowId);
  if (!r || !r.jobId) return;
  if (!(BillingState.jobs || []).some(j => j.id === r.jobId)) {
    window.showToast?.('That job is not loaded in AR/AP yet — open it from the Dispatch board to create the ticket.', 'warning');
    return;
  }
  closeModal('dispatch-review');
  // The existing job-day modal: twin-ticket warning, words check against
  // existing tickets (findWorkBilledElsewhere), then generateTicketFromJob.
  showCalendarJobTickets(r.jobId, r.dateKey);
}

async function reviewDecide(rowId, decision) {
  try { return (await _ensureReviewUi()).decide(rowId, decision); }
  catch (e) { window.showToast?.('Could not open that — reload the page and try again.', 'error'); }
}
async function openReviewWeekly() {
  try { (await _ensureReviewUi()).openWeekly(); }
  catch (e) { window.showToast?.('Could not open the weekly list — reload the page and try again.', 'error'); }
}

// TASK-10043 S1 — a ticket dated off its dispatch day (TITS #100891: ticket
// 10/15, job day 10/14) shows it ON THE TICKET, with a one-tap fix. The match
// is TASK-10041's own "covered, date off" row (shared/dispatchedNoTicket.js —
// one engine, never a second matcher), so a "Date is right" decision made
// there also quiets this chip. The fix is that row's existing fix_date decision
// (billingReview callable → fixTicketDate: its guards + TICKET_WORKDATE_FIXED
// audit); it moves the ticket's date ONLY — clock entries stay where they were
// punched (TASK-10059 owns moving them) and the confirm says so. The chip and
// confirm live in the lazy billingJobPicker.js chunk, loaded only when a
// date-off row exists (perf budget).
function _ticketDateOffChipHtml(ticket) {
  if (_jobPicker) return _jobPicker.dateOffChipHtml(ticket);
  if (_reviewRowsAll(null).concat(_reviewRowsAll(null, { range: true })).some(r => r.coverage === 'date_off')) {
    _ensureJobPicker().then(() => { if (BillingState.viewMode === 'tickets') renderTicketsList(); }).catch(() => {});
  }
  return '';
}
async function fixTicketDateToJobDay(rowId) {
  try { return (await _ensureJobPicker()).fixDateOff(rowId); }
  catch (e) { window.showToast?.('Could not open that — reload the page and try again.', 'error'); }
}

function _ensureReviewStyles() {
  if (document.getElementById('billing-review-10041-styles')) return;
  const s = document.createElement('style');
  s.id = 'billing-review-10041-styles';
  s.textContent = `
    .tickets-review-chips{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;}
    .tickets-review-chip:not(.is-zero){border-color:#F59E0B;}
    .review-chip{display:inline-flex;align-items:center;gap:3px;margin-left:6px;padding:2px 8px;border-radius:10px;border:1px solid #F59E0B;background:#FFFBEB;color:#78350F;font-size:11px;font-weight:600;white-space:nowrap;}
    .review-chip .material-icons{font-size:13px;}
  `;
  document.head.appendChild(s);
}

// Review modal banner for ONE ticket — only the rows that ask for a check.
function _reviewPayChangeBannerHtml(ticketId) {
  const rows = (BillingState.payChanges || []).filter(r => r && r.ticketId === ticketId && r.state === 'needsBilling');
  if (!rows.length) return '';
  return _payChangesBannerHtml(rows, { canDismiss: _canDismissPayChanges(), scope: 'review' });
}

async function dismissPayChange(id, btnEl, scope) {
  if (!id) return;
  if (btnEl) { if (btnEl.dataset.busy === '1') return; btnEl.dataset.busy = '1'; btnEl.disabled = true; }
  const note = (document.getElementById('paychange-note-' + (scope === 'review' ? 'review' : 'page') + '-' + id)?.value || '').trim();
  try {
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 30000 });
    await fn({ op: 'dismissInvoicedPayChange', tenantId: BillingState.tenantId, payload: { id, note } });
    window.showToast?.('Dismissed — the audit log has who and when.', 'success');
    await _loadPayChanges(BillingState.tenantId);
    // integ/2108.1 COD r3 (Code Reviewer): swap just the Review banner — a
    // full renderReviewTicket() would wipe unsaved Review inputs.
    if (ReviewState.ticket?.id) {
      const _old = document.querySelector('#review-ticket-body [data-paychange-scope="review"]');
      if (_old) {
        const _bh = _reviewPayChangeBannerHtml(ReviewState.ticket.id);
        if (_bh) _old.outerHTML = _bh; else _old.remove();
      }
    }
  } catch (e) {
    window.showToast?.(e?.message || 'Could not dismiss it — try again.', 'error');
    if (btnEl) { btnEl.dataset.busy = '0'; btnEl.disabled = false; }
  }
}

function renderQBOReauthBanner() {
  const slot = document.getElementById('billing-qbo-banner');
  if (!slot) return;
  const status = window._qboLiveStatus?.status || window.AppState?.tenantData?.qboStatus;
  if (status === 'reauth_required') {
    slot.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;padding:12px 16px;margin:0 0 12px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;">
        <span class="material-icons" style="color:#D97706;font-size:22px;">warning_amber</span>
        <div style="flex:1;font-size:0.85rem;color:#92400E;">
          <strong>QuickBooks disconnected.</strong> Your authorization expired — reconnect to resume syncing invoices and bills.
        </div>
        <button type="button" class="btn btn-sm" style="background:#D97706;border-color:#D97706;color:#fff;white-space:nowrap;" onclick="window.BillingModule.openBillingSettings()">
          <span class="material-icons" style="font-size:14px;">sync</span> Reconnect
        </button>
      </div>`;
    slot.style.display = '';
  } else {
    slot.innerHTML = '';
    slot.style.display = 'none';
  }
}

function closeBillingSettings() {
  const modal = document.getElementById('modal-billing-settings');
  if (modal) modal.classList.add('hidden');
}

// TASK-572 — progressive disclosure: in simple clock-out mode the ticket-only
// controls are meaningless, so CSS-hide them (display:none on the WRAPPERS only).
// Every <input> stays in the DOM at all times — saveBillingSettings reads each by
// id and a removed input would silently save false; the simple-mode reconcile in
// saveBillingSettings is the data-level backstop, this is visibility only.
// Kept visible in simple mode on purpose: allow-driver-edit (the *time* half
// applies) and clockin-ask-customer (cost allocation; the only mode where it's
// user-editable). The "Tickets & pricing" section itself stays visible: only its
// #billing-tickets-mode-only rows hide — tax, permit markup, invoice hiding,
// equipment rounding and accounting method are company money settings that
// apply in every mode (a Simple-mode company still bills tickets/invoices).
function syncBillingModeVisibility(mode) {
  const hide = mode === 'simple';
  ['billing-tickets-mode-only', 'billing-row-show-driver-tickets', 'billing-row-show-driver-prices', 'billing-row-allow-driver-equipment-edit'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.style.display = hide ? 'none' : '';
  });
}

// TASK-572 — radio onchange handler: re-sync visibility AND re-run the
// clockInAskCustomer force/disable logic (mirrors openBillingSettings) so an
// in-modal ticket→simple switch doesn't leave a stale forced-true checkbox.
// _askCustomerPreForce caches the user's unsaved in-modal choice so a
// simple→ticket→simple round-trip restores what they had toggled, not the
// last-saved value (COD Code Reviewer fold). Reset per modal open.
let _askCustomerPreForce = null;
function onClockOutModeChange(mode) {
  syncBillingModeVisibility(mode);
  const isTicketMode = mode === 'ticket' || mode === 'ticketonly';
  const clockInCustomerEl = document.getElementById('billing-settings-clockin-ask-customer');
  if (clockInCustomerEl) {
    if (isTicketMode) {
      // Entering a ticket mode: remember the user's current editable choice
      // before forcing it on (only if it wasn't already forced/disabled).
      if (!clockInCustomerEl.disabled) _askCustomerPreForce = clockInCustomerEl.checked;
      clockInCustomerEl.disabled = true;
      clockInCustomerEl.checked = true;
    } else {
      clockInCustomerEl.disabled = false;
      clockInCustomerEl.checked = _askCustomerPreForce !== null
        ? _askCustomerPreForce
        : (window.AppState?.settings?.clockInAskCustomer === true);
    }
  }
}

// TASK-9489 — the company switch. Its own callable, its own audit row; the
// shared Save button never carries it (lesson: TITS's workweek flipped silently
// on a shared Save). Flow: count the lines set individually -> confirm the flip
// -> if any, ask whether they reset too ("Keep their choice" is the safe answer
// and what closing the dialog means) -> save.
async function onEquipmentRoundingToggle(el) {
  if (!el) return;
  if (_t9986BillingReadOnly()) { el.checked = !el.checked; el.disabled = true; _t9986Refuse(); return; } // TASK-9986 S1
  const want = el.checked === true;
  const prev = !want;
  const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  el.disabled = true;
  try {
    const fn = httpsCallable(functions, 'setEquipmentRoundingDefault');
    const probe = await fn({ tenantId, dryRun: true });
    const n = Number(probe?.data?.overrideCount) || 0;
    const ok = await window.showConfirm(
      want ? 'Round equipment hours to the half hour?' : 'Stop rounding equipment hours?',
      (want
        ? 'Equipment hours will round up to the next half hour (4.43 h bills 4.5 h)'
        : 'Equipment hours will bill as clocked (4.43 h bills 4.43 h)')
        + ' on Price Book lines set to Company default and for customers with no Price Book. This applies to equipment hours recorded from now on (and to times you edit on an open ticket). Tickets already built keep their hours, and invoiced tickets never change.',
      { confirmText: want ? 'Turn on' : 'Turn off', cancelText: 'Cancel' });
    if (!ok) { el.checked = prev; return; }
    let reset = false;
    if (n > 0) {
      reset = await window.showConfirm(
        'Reset them to match too?',
        `${n.toLocaleString()} Price Book line${n === 1 ? ' is' : 's are'} set individually (Always or Never). Reset ${n === 1 ? 'it' : 'them'} to Company default so ${n === 1 ? 'it follows' : 'they follow'} this switch too?`,
        { confirmText: 'Reset them too', cancelText: 'No, keep their choice' });
    }
    const res = await fn({ tenantId, enabled: want, resetOverrides: reset === true });
    if (window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.roundEquipmentHalfHour = want;
    }
    // Rates cached for Review / clock-out carry the old line choices.
    try { ReviewState.contractRateCache = {}; } catch (_) { /* not loaded yet */ }
    try { window.ClockModule?.clearContractRateCache?.(); } catch (_) { /* clock not loaded */ }
    const resetN = Number(res?.data?.resetCount) || 0;
    window.showToast?.(
      (want ? 'Equipment hours now round to the half hour' : 'Equipment hours no longer round')
        + (resetN > 0 ? ` — ${resetN.toLocaleString()} line${resetN === 1 ? '' : 's'} reset to Company default` : ''),
      'success');
  } catch (err) {
    console.error('[Billing] TASK-9489 rounding switch failed:', err);
    if (err?.details?.switchSaved === true) {
      // COD round 2 — the switch DID save; only part of the reset failed. Show
      // the saved state, drop stale rate caches, and say what is left.
      el.checked = want;
      if (window.AppState) { window.AppState.settings = window.AppState.settings || {}; window.AppState.settings.roundEquipmentHalfHour = want; }
      try { ReviewState.contractRateCache = {}; } catch (_) { /* not loaded yet */ }
      try { window.ClockModule?.clearContractRateCache?.(); } catch (_) { /* clock not loaded */ }
      window.showToast?.(err.message, 'error');
      return;
    }
    el.checked = prev;
    window.showToast?.(err?.message ? `Not saved — ${err.message}` : 'Not saved — try again', 'error');
  } finally {
    el.disabled = false;
  }
}

// TASK-4875 — company labor billing. Its own callable + audit row (never the
// shared Save). Probe -> confirm (names how many customers price crew) -> save.
async function onLaborBillingToggle(el) {
  if (!el) return;
  if (_t9986BillingReadOnly()) { el.checked = !el.checked; el.disabled = true; _t9986Refuse(); return; } // TASK-9986 S1
  const want = el.checked === true;
  const prev = !want;
  const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  el.disabled = true;
  try {
    const fn = httpsCallable(functions, 'setLaborBillingSetting');
    const probe = await fn({ tenantId, dryRun: true });
    const n = Number(probe?.data?.customersWithLabor) || 0;
    const ok = await window.showConfirm(
      want ? 'Bill labor hours?' : 'Stop billing labor hours?',
      want
        ? `Crew hours on open tickets will be billed at each customer's Price Book crew rate. ${n === 0 ? 'No customer has a priced crew line yet, so nothing changes until you add one.' : `${n.toLocaleString()} customer${n === 1 ? ' has' : 's have'} a priced crew line.`} Invoiced tickets never change.`
        : 'Crew hours go back to $0 on open tickets as they are next saved; the truck carries the money. Invoiced tickets keep what they billed.',
      { confirmText: want ? 'Turn on' : 'Turn off', cancelText: 'Cancel' });
    if (!ok) { el.checked = prev; return; }
    await fn({ tenantId, enabled: want });
    if (window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.billLaborHours = want;
    }
    window.showToast?.(want ? 'Labor hours now bill from the Price Book' : 'Labor hours no longer bill', 'success');
  } catch (err) {
    console.error('[Billing] TASK-4875 labor billing switch failed:', err);
    el.checked = prev;
    window.showToast?.(err?.message ? `Not saved — ${err.message}` : 'Not saved — try again', 'error');
  } finally {
    el.disabled = false;
  }
}

// TASK-9982 — ticket time edits -> time clock. Its own callable + audit row
// (never the shared Save). An ordinary on/off switch, either way, any time.
async function onTicketClockSyncToggle(el) {
  if (!el) return;
  if (_t9986BillingReadOnly()) { el.checked = !el.checked; el.disabled = true; _t9986Refuse(); return; } // TASK-9986 S1
  const want = el.checked === true;
  const prev = !want;
  el.disabled = true;
  try {
    const ok = await window.showConfirm(
      want ? 'Update the time clock from tickets?' : 'Stop updating the time clock from tickets?',
      want
        ? 'From now on, moving an employee\'s in/out times on a ticket in Review also moves their time clock entry. That changes their paid hours (payroll).'
        : 'From now on, moving an employee\'s in/out times on a ticket changes the ticket only. Their time clock and pay stay as clocked.',
      { confirmText: want ? 'Turn on' : 'Turn off', cancelText: 'Cancel' });
    if (!ok) { el.checked = prev; return; }
    const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    await httpsCallable(functions, 'setTicketClockSyncSetting')({ tenantId, enabled: want });
    if (window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.ticketTimesUpdateClock = want;
    }
    window.showToast?.(want ? 'Ticket time edits now update the time clock' : 'Ticket time edits no longer touch the time clock', 'success');
  } catch (err) {
    console.error('[Billing] TASK-9982 ticket->clock switch failed:', err);
    el.checked = prev;
    window.showToast?.(err?.message ? `Not saved — ${err.message}` : 'Not saved — try again', 'error');
  } finally {
    el.disabled = false;
  }
}

// TASK-4875 — with labor billing ON the server sets every crew-line rate from
// the Price Book (laborLinePricing re-asserts it on each line write), so a rate
// typed in Review would silently revert. Lock the box and say where the number
// comes from. OFF: the box stays exactly as it was.
function _reviewLaborRateLockAttr() {
  if (window.AppState?.settings?.billLaborHours !== true) return '';
  return ' disabled title="Labor billing is on: this rate comes from the customer&#39;s Price Book crew line. Change it there."';
}

// ═══ TASK-9440 — Fuel Surcharge settings (company + overrides) ═══
// Written ONLY through the setFuelSurchargeSettings callable: tenant settings are
// admin-only in the rules, the callable admits the owner or the billing module,
// and it writes the old -> new audit row in the same transaction.
const _FUEL_UI_CATS = ['equipment', 'labor', 'permits', 'fieldItems', 'misc', 'standby'];
function _fuelCustomerName(id) {
  const c = (BillingState.customers || []).find(x => x.id === id);
  return c ? (c.name || c.companyName || id) : id;
}
function _fuelOverrideText(o) {
  if (!o || typeof o !== 'object') return '';
  if (o.mode === 'off') return 'No surcharge';
  const p = _normalizeFuelSurchargePercent(o.percent);
  return p === null ? '(invalid — ignored)' : p + '%';
}
function _fuelSettingsRender(settings) {
  const fs = (settings && settings.fuelSurcharge) || {};
  const modeEl = document.getElementById('fuel-settings-mode');
  if (!modeEl) return;
  modeEl.value = fs.mode === 'flat' ? 'flat' : 'off';
  const pEl = document.getElementById('fuel-settings-percent');
  if (pEl) pEl.value = _normalizeFuelSurchargePercent(fs.percent) ?? '';
  const cats = (fs.categories && typeof fs.categories === 'object') ? fs.categories : { equipment: true };
  for (const k of _FUEL_UI_CATS) {
    const el = document.getElementById('fuel-settings-cat-' + k);
    if (el) el.checked = cats[k] === true;
  }
  const tx = document.getElementById('fuel-settings-taxable');
  if (tx) tx.checked = fs.taxable === true;
  const st = document.getElementById('fuel-settings-status');
  if (st) st.textContent = '';
  const custSel = document.getElementById('fuel-ovr-customer');
  if (custSel) {
    // P0 2026-10-06 rule: vendors / subcontractors are never offered as a customer.
    // TASK-9548 slice 3 — grouped like every customer picker.
    custSel.innerHTML = '<option value="">Pick a customer…</option>' + window.PickerGroups.customerOptionsHtml(
      (BillingState.customers || []).filter(c => c && c.id),
      // Decision 2026-10-10 — billing picker: Shop/Office (internal time) are never billed.
      { nameOf: c => c.name || c.companyName || c.id, excludeInternal: true });
  }
  const pbSel = document.getElementById('fuel-ovr-pricebook');
  if (pbSel) pbSel.innerHTML = `<option value="">All of this customer's Price Books</option>`;
  const list = document.getElementById('fuel-overrides-list');
  if (list) {
    const rows = [];
    const custs = (fs.customers && typeof fs.customers === 'object') ? fs.customers : {};
    for (const [cid, o] of Object.entries(custs)) {
      rows.push(`<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);margin-bottom:4px;font-size:0.8rem;color:var(--text-primary);">
        <span>${escapeHtml(_fuelCustomerName(cid))} — ${escapeHtml(_fuelOverrideText(o))}</span>
        <button type="button" class="btn btn-secondary btn-sm" style="font-size:0.75rem;padding:2px 8px;" onclick="window.BillingModule.removeFuelSurchargeOverride('${window.escJsAttr(cid)}', '')">Remove</button>
      </div>`);
    }
    const books = (fs.priceBooks && typeof fs.priceBooks === 'object') ? fs.priceBooks : {};
    for (const [bid, o] of Object.entries(books)) {
      const label = (o && o.contractName) ? o.contractName : 'Price Book';
      rows.push(`<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);margin-bottom:4px;font-size:0.8rem;color:var(--text-primary);">
        <span>${escapeHtml(_fuelCustomerName(o && o.customerId))} · ${escapeHtml(label)} — ${escapeHtml(_fuelOverrideText(o))}</span>
        <button type="button" class="btn btn-secondary btn-sm" style="font-size:0.75rem;padding:2px 8px;" onclick="window.BillingModule.removeFuelSurchargeOverride('${window.escJsAttr((o && o.customerId) || '')}', '${window.escJsAttr(bid)}')">Remove</button>
      </div>`);
    }
    list.innerHTML = rows.length ? rows.join('') : '<div style="font-size:0.8rem;color:var(--text-secondary);">None — every customer uses the company setting.</div>';
  }
}
async function loadFuelOverridePriceBooks(customerId) {
  const sel = document.getElementById('fuel-ovr-pricebook');
  if (!sel) return;
  sel.innerHTML = `<option value="">All of this customer's Price Books</option>`;
  if (!customerId) return;
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const snap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'));
    if (document.getElementById('fuel-ovr-customer')?.value !== customerId) return; // a newer pick won
    sel.innerHTML += snap.docs.map(d => {
      const c = d.data() || {};
      const name = c.name || c.title || c.contractName || 'Price Book';
      return `<option value="${escapeHtml(d.id)}">${escapeHtml(name)}${c.status && c.status !== 'Active' ? ' (' + escapeHtml(c.status) + ')' : ''}</option>`;
    }).join('');
  } catch (err) {
    console.warn('[Billing] TASK-9440 could not load Price Books:', err?.message || err);
  }
}
function _fuelApplyResultLocally(scope, customerId, contractId, to) {
  if (!window.AppState) return;
  window.AppState.settings = window.AppState.settings || {};
  const fs = window.AppState.settings.fuelSurcharge = window.AppState.settings.fuelSurcharge || {};
  if (scope === 'company') {
    fs.mode = to.mode; fs.categories = to.categories; fs.taxable = to.taxable;
    if (to.percent === null || to.percent === undefined) delete fs.percent; else fs.percent = to.percent;
  } else {
    const bucket = scope === 'customer' ? 'customers' : 'priceBooks';
    const key = scope === 'customer' ? customerId : contractId;
    fs[bucket] = fs[bucket] || {};
    if (to === null) delete fs[bucket][key]; else fs[bucket][key] = to;
  }
}
function _fuelRepricedText(r) {
  const n = (r && r.repriced && r.repriced.changed) || 0;
  const tail = r && r.repriced && r.repriced.truncated ? ' (first 400 drafts only — older drafts update when next edited)' : '';
  return r && r.changed ? `Saved. ${n} draft ticket${n === 1 ? '' : 's'} updated.${tail}` : 'No change.';
}
async function _callFuelSurchargeSetting(payload) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const fn = httpsCallable(functions, 'setFuelSurchargeSettings');
  const res = await fn({ tenantId: tenant, ...payload });
  return res?.data || {};
}
async function saveFuelSurchargeCompany() {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return; } // TASK-9986 S1
  const btn = document.getElementById('btn-save-fuel-settings');
  const st = document.getElementById('fuel-settings-status');
  const mode = document.getElementById('fuel-settings-mode')?.value === 'flat' ? 'flat' : 'off';
  const raw = (document.getElementById('fuel-settings-percent')?.value ?? '').trim();
  const percent = raw === '' ? null : _normalizeFuelSurchargePercent(raw);
  if (raw !== '' && percent === null) { window.showToast?.('Fuel surcharge must be between 0.01% and 100%', 'error'); return; }
  if (mode === 'flat' && percent === null) { window.showToast?.('Enter the fuel surcharge %', 'error'); return; }
  const categories = {};
  for (const k of _FUEL_UI_CATS) categories[k] = document.getElementById('fuel-settings-cat-' + k)?.checked === true;
  if (!Object.values(categories).some(Boolean)) { window.showToast?.('Pick at least one kind of charge the surcharge applies to', 'error'); return; }
  const taxable = document.getElementById('fuel-settings-taxable')?.checked === true;
  if (btn) btn.disabled = true;
  if (st) st.textContent = 'Saving…';
  try {
    const r = await _callFuelSurchargeSetting({ scope: 'company', config: { mode, percent, categories, taxable } });
    if (r.changed) _fuelApplyResultLocally('company', null, null, r.to);
    if (st) st.textContent = _fuelRepricedText(r);
    window.showToast?.(r.changed ? 'Fuel surcharge saved' : 'Fuel surcharge unchanged', 'success');
  } catch (err) {
    if (st) st.textContent = 'Not saved — ' + (err?.message || 'error');
    window.showToast?.('Fuel surcharge not saved: ' + (err?.message || 'error'), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}
async function saveFuelSurchargeOverride() {
  const customerId = document.getElementById('fuel-ovr-customer')?.value || '';
  const contractId = document.getElementById('fuel-ovr-pricebook')?.value || '';
  const mode = document.getElementById('fuel-ovr-mode')?.value === 'off' ? 'off' : 'flat';
  const raw = (document.getElementById('fuel-ovr-percent')?.value ?? '').trim();
  const percent = raw === '' ? null : _normalizeFuelSurchargePercent(raw);
  if (!customerId) { window.showToast?.('Pick a customer', 'error'); return; }
  if (mode === 'flat' && percent === null) { window.showToast?.('Enter a fuel surcharge between 0.01% and 100%', 'error'); return; }
  await _saveFuelOverride(customerId, contractId, { mode, percent: mode === 'flat' ? percent : null });
}
async function removeFuelSurchargeOverride(customerId, contractId) {
  if (!customerId) return;
  await _saveFuelOverride(customerId, contractId, { mode: 'inherit' });
}
async function _saveFuelOverride(customerId, contractId, config) {
  const btn = document.getElementById('btn-save-fuel-override');
  const st = document.getElementById('fuel-settings-status');
  const scope = contractId ? 'priceBook' : 'customer';
  if (btn) btn.disabled = true;
  if (st) st.textContent = 'Saving…';
  try {
    const r = await _callFuelSurchargeSetting({ scope, customerId, contractId: contractId || undefined, config });
    if (r.changed) _fuelApplyResultLocally(scope, customerId, contractId, r.to === undefined ? null : r.to);
    _fuelSettingsRender(window.AppState?.settings || {});
    const st2 = document.getElementById('fuel-settings-status');
    if (st2) st2.textContent = _fuelRepricedText(r);
    window.showToast?.(r.changed ? 'Fuel surcharge override saved' : 'Override unchanged', 'success');
  } catch (err) {
    if (st) st.textContent = 'Not saved — ' + (err?.message || 'error');
    window.showToast?.('Override not saved: ' + (err?.message || 'error'), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function saveBillingSettings() {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return; } // TASK-9986 S1
  const btn = document.getElementById('btn-save-billing-settings');
  const origHTML = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
  try {
    let buildTicketsFromClock = document.getElementById('billing-settings-build-tickets')?.checked ?? false;
    let showTicketsInDriverView = document.getElementById('billing-settings-show-driver-tickets')?.checked ?? false;
    let showPricesOnDriverTickets = document.getElementById('billing-settings-show-driver-prices')?.checked ?? false;
    const allowDriverTimeEdit = document.getElementById('billing-settings-allow-driver-edit')?.checked ?? false;
    let allowDriverEquipmentEdit = document.getElementById('billing-settings-allow-driver-equipment-edit')?.checked ?? false;
    const enableShipSlips = document.getElementById('billing-settings-enable-ship-slips')?.checked ?? false;
    // TASK-491 — per-operator equipment tracking. Nested object to mirror
    // driverContactDirectory.enabled / driverDocs.enabled. DEFAULT OFF.
    const equipmentTieEnabled = document.getElementById('billing-settings-equipment-tie')?.checked ?? false;
    const clockOutMode = document.getElementById('billing-settings-clockout-ticketonly')?.checked ? 'ticketonly' : document.getElementById('billing-settings-clockout-ticket')?.checked ? 'ticket' : 'simple';
    // Decentralized 2026-05-06 from main Settings (Phase D). Force-on in
    // ticket modes (input is disabled in those modes).
    const isTicketMode = clockOutMode === 'ticket' || clockOutMode === 'ticketonly';
    const clockInAskCustomer = isTicketMode ? true : (document.getElementById('billing-settings-clockin-ask-customer')?.checked ?? false);
    // TASK-572 — ghost-flag reconcile (mirrors the clockInAskCustomer force-pattern
    // above): simple mode has no tickets, so force the ticket-only flags false
    // REGARDLESS of DOM state. The ticket controls are only CSS-hidden in simple
    // mode — a still-checked hidden toggle must never save
    // buildTicketsFromClock:true alongside clockOutMode:'simple' (contradictory
    // state that silently re-enables ticket-build downstream).
    if (!isTicketMode) {
      buildTicketsFromClock = false;
      showTicketsInDriverView = false;
      showPricesOnDriverTickets = false;
      allowDriverEquipmentEdit = false;
    }
    const captureGpsOnClock = document.getElementById('billing-settings-capture-gps-on-clock')?.checked ?? false;
    const captureGpsOnInspection = document.getElementById('billing-settings-capture-gps-on-inspection')?.checked ?? false; // TASK-9554 default OFF
    // TASK-191 Phase 2 — geofence defaults (preserve any existing shopFence we don't edit here).
    const geofenceAutoFromFirstWaypoint = document.getElementById('billing-settings-geofence-autowaypoint')?.checked ?? false;
    const geofenceDefaultRadiusFeet = parseInt(document.getElementById('billing-settings-geofence-radius')?.value || '500', 10) || 500;
    // Clock Out Alert
    const clockOutAlert = document.getElementById('billing-settings-clockout-alert')?.checked ?? false;
    // TASK-10040b: an empty box falls back to what the server would use. The
    // escalation fallback is resolved FOR the reminder being saved (reminder 12 +
    // blank escalation = 16, exactly what the cron does with {clockOutAlertHours: 12}).
    const clockOutAlertHours = parseInt(document.getElementById('billing-settings-clockout-alert-hours')?.value || String(resolveClockOutAlertHours().remindHours), 10);
    const coaDefaults = resolveClockOutAlertHours({ clockOutAlertHours });
    // TASK-422 — admin escalation threshold; clamp >= the driver threshold so admins never alert before the driver.
    const clockOutAlertEscalationHours = Math.max(clockOutAlertHours, parseInt(document.getElementById('billing-settings-clockout-alert-esc-hours')?.value || String(coaDefaults.escalateHours), 10));
    // Tax settings
    const taxEnabled = document.getElementById('billing-settings-tax-enabled')?.checked ?? false;
    const defaultTaxRate = parseFloat(document.getElementById('billing-settings-tax-rate')?.value) || 0;
    const defaultTaxCategories = {};
    ['labor', 'equipment', 'permits', 'fieldItems', 'misc', 'downtime', 'standby'].forEach(cat => {
      defaultTaxCategories[cat] = document.getElementById('billing-settings-tax-cat-' + cat)?.checked !== false;
    });
    // TASK-9436 — company default permit markup %. Blank = 0. Out of range is
    // refused, never clamped: a silently different money number is the bug class.
    const _permitMkRaw = (document.getElementById('billing-settings-permit-markup')?.value ?? '').trim();
    const defaultPermitMarkupPercent = _permitMkRaw === '' ? 0 : _normalizePermitMarkupPercent(_permitMkRaw);
    if (defaultPermitMarkupPercent === null) {
      window.showToast?.('Default permit markup must be between 0% and 100%', 'error');
      return;
    }
    const accountingBasis = document.getElementById('billing-settings-accounting-basis')?.value || 'cash';
    const perItemInvoiceHide = document.getElementById('billing-settings-per-item-invoice-hide')?.checked === true; // TASK-9667 default OFF
    // QB settings managed by QB Setup wizard — not saved here
    const tenant = BillingState.tenantId;
    const tenantRef = doc(db, 'tenants', tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingSettings = tenantDoc.exists() ? (tenantDoc.data().settings || {}) : {};
    // fix/2096-preship (integ/2096 COD) — TURNING ON a ticket mode is FAIL-SAFE:
    // the internal-time Shop customer must exist (or be created now) BEFORE the
    // mode is saved. A caller who may not mint the internalTimeOnly flag (rules:
    // tenant admin only — e.g. a billing-module member) used to get the mode
    // saved with no Shop customer, so shop hours went on a real customer's ticket.
    // Now nothing is written and the modal stays open with the reason.
    const _ticketModeWasOn = _isTicketClockMode(existingSettings.clockOutMode || 'simple');
    if (_isTicketClockMode(clockOutMode) && !_ticketModeWasOn) {
      const shop = await ensureInternalTimeCustomerForTicketMode('company clock mode: ' + clockOutMode, { blocking: true });
      if (shop !== 'exists' && shop !== 'created') return;
    }
    // TASK-191 — geofence: only the two leaves this modal edits. shopFence has no
    // UI here and is preserved by OMISSION (see the TASK-9280 note below — maps
    // recurse under merge:true, so an absent sub-key is left untouched; verified
    // against real Firestore 2026-09-22, not assumed).
    const geofence = { defaultRadiusFeet: geofenceDefaultRadiusFeet, autoFromFirstWaypoint: geofenceAutoFromFirstWaypoint };
    // 🔴 TASK-9280 (the TASK-9255 fix, applied to this file) — the payload carries
    // ONLY the keys THIS modal owns. It deliberately does NOT spread
    // existingSettings.
    //
    // THE BUG THIS REPLACED: `settings: { ...existingSettings, <owned keys> }`
    // round-tripped the ENTIRE settings map through the getDoc ~5 lines above, so
    // any writer that touched the tenant doc in that window was reverted.
    // `merge: true` deep-merges MAPS but REPLACES ARRAYS wholesale and rewrites
    // every scalar leaf it is handed. Reproduced on real Firestore (2026-09-22):
    // a concurrent `optOutOfAi` write (functions/src/services/smsInbox.js, fired
    // by an inbound "NO AI"/"HUMAN" text) had `settings.smsInbox.optedOutOfAi`
    // put back to the stale value by a Billing Settings save — the assistant
    // resumed answering someone who had just asked for a human, silently. Same
    // exposure on yards, customTypes.*, safetyTrainings, accessPins, and every
    // other key under `settings` this modal does not own.
    //
    // 🔴 Omission is what preserves them, NOT the spread. Do not "fix" a future
    // clobber by re-reading just before the write — that is the same TOCTOU
    // window, only narrower. The invariant: this function writes what this modal
    // owns and NOTHING else.
    //
    // setDoc(…, { merge: true }) over updateDoc(dotted paths): identical per-leaf
    // field mask, but it still CREATES the doc when it does not exist, which
    // updateDoc would reject with NOT_FOUND (the TASK-428 trap).
    //
    // ⚠️ merge:true never REMOVES a map key absent from the payload, so a map
    // written here can only grow. Nothing this modal writes has removal intent —
    // defaultTaxCategories writes all seven category keys on every save, and
    // equipmentTie/geofence write fixed leaves. (saveQBSetup does have that
    // problem; it is flagged in place there.)
    await setDoc(tenantRef, {
      settings: { buildTicketsFromClock, showTicketsInDriverView, showPricesOnDriverTickets, allowDriverTimeEdit, allowDriverEquipmentEdit, clockInAskCustomer, captureGpsOnClock, captureGpsOnInspection, enableShipSlips, equipmentTie: { enabled: equipmentTieEnabled }, clockOutMode, clockOutAlert, clockOutAlertHours, clockOutAlertEscalationHours, taxEnabled, defaultTaxRate, defaultTaxCategories, defaultPermitMarkupPercent, accountingBasis, perItemInvoiceHide, geofence }
    }, { merge: true });
    // TASK-591 — audit-log this save with from→to on CHANGED keys only.
    // `existingSettings` is the PRE-save Firestore state (getDoc above, never
    // mutated before this point). Old values are normalized with the same
    // defaults openBillingSettings/this save use, so an open→save with no
    // edits diffs clean and writes NO audit row. Values are booleans/numbers/
    // small strings — no PII. Nested keys logged dotted (equipmentTie.enabled).
    try {
      const auditChanged = {};
      // TASK-9373 — shared collector (was a local closure); structural compare.
      const auditDiff = _settingsAuditDiff(auditChanged);
      auditDiff('buildTicketsFromClock', existingSettings.buildTicketsFromClock === true, buildTicketsFromClock);
      auditDiff('showTicketsInDriverView', existingSettings.showTicketsInDriverView === true, showTicketsInDriverView);
      auditDiff('showPricesOnDriverTickets', existingSettings.showPricesOnDriverTickets === true, showPricesOnDriverTickets);
      auditDiff('allowDriverTimeEdit', existingSettings.allowDriverTimeEdit === true, allowDriverTimeEdit);
      auditDiff('allowDriverEquipmentEdit', existingSettings.allowDriverEquipmentEdit === true, allowDriverEquipmentEdit);
      auditDiff('clockInAskCustomer', existingSettings.clockInAskCustomer === true, clockInAskCustomer);
      auditDiff('captureGpsOnClock', existingSettings.captureGpsOnClock === true, captureGpsOnClock);
      auditDiff('captureGpsOnInspection', existingSettings.captureGpsOnInspection === true, captureGpsOnInspection);
      auditDiff('enableShipSlips', existingSettings.enableShipSlips === true, enableShipSlips);
      auditDiff('equipmentTie.enabled', existingSettings.equipmentTie?.enabled === true, equipmentTieEnabled);
      auditDiff('clockOutMode', existingSettings.clockOutMode || 'simple', clockOutMode);
      auditDiff('clockOutAlert', existingSettings.clockOutAlert === true, clockOutAlert);
      const coaOld = resolveClockOutAlertHours(existingSettings); // TASK-10040b: old = what the server was using
      auditDiff('clockOutAlertHours', coaOld.remindHours, clockOutAlertHours);
      auditDiff('clockOutAlertEscalationHours', coaOld.escalateHours, clockOutAlertEscalationHours);
      auditDiff('taxEnabled', existingSettings.taxEnabled === true, taxEnabled);
      auditDiff('defaultTaxRate', existingSettings.defaultTaxRate || 0, defaultTaxRate);
      auditDiff('defaultPermitMarkupPercent', _normalizePermitMarkupPercent(existingSettings.defaultPermitMarkupPercent) ?? 0, defaultPermitMarkupPercent); // TASK-9436
      Object.keys(defaultTaxCategories).forEach(cat => {
        auditDiff('defaultTaxCategories.' + cat, existingSettings.defaultTaxCategories?.[cat] !== false, defaultTaxCategories[cat]);
      });
      auditDiff('accountingBasis', existingSettings.accountingBasis || 'cash', accountingBasis);
      auditDiff('perItemInvoiceHide', existingSettings.perItemInvoiceHide === true, perItemInvoiceHide); // TASK-9667
      auditDiff('geofence.autoFromFirstWaypoint', existingSettings.geofence?.autoFromFirstWaypoint === true, geofenceAutoFromFirstWaypoint);
      auditDiff('geofence.defaultRadiusFeet', existingSettings.geofence?.defaultRadiusFeet || 500, geofenceDefaultRadiusFeet);
      if (Object.keys(auditChanged).length) {
        window.logAudit?.('settings', 'SAVE_BILLING_SETTINGS', { changed: auditChanged });
      }
    } catch (auditErr) {
      console.warn('[Billing] Settings audit log failed:', auditErr);
    }
    // Update local state — settings
    if (window.AppState?.settings) {
      window.AppState.settings.buildTicketsFromClock = buildTicketsFromClock;
      window.AppState.settings.showTicketsInDriverView = showTicketsInDriverView;
      window.AppState.settings.showPricesOnDriverTickets = showPricesOnDriverTickets;
      window.AppState.settings.allowDriverTimeEdit = allowDriverTimeEdit;
      window.AppState.settings.allowDriverEquipmentEdit = allowDriverEquipmentEdit;
      window.AppState.settings.clockInAskCustomer = clockInAskCustomer;
      window.AppState.settings.captureGpsOnClock = captureGpsOnClock;
      window.AppState.settings.captureGpsOnInspection = captureGpsOnInspection;
      window.AppState.settings.geofence = { ...(window.AppState.settings.geofence || {}), defaultRadiusFeet: geofenceDefaultRadiusFeet, autoFromFirstWaypoint: geofenceAutoFromFirstWaypoint };
      window.AppState.settings.enableShipSlips = enableShipSlips;
      window.AppState.settings.equipmentTie = { ...(window.AppState.settings.equipmentTie || {}), enabled: equipmentTieEnabled };
      window.AppState.settings.clockOutMode = clockOutMode;
      window.AppState.settings.clockOutAlert = clockOutAlert;
      window.AppState.settings.clockOutAlertHours = clockOutAlertHours;
      window.AppState.settings.clockOutAlertEscalationHours = clockOutAlertEscalationHours;
      window.AppState.settings.taxEnabled = taxEnabled;
      window.AppState.settings.defaultTaxRate = defaultTaxRate;
      window.AppState.settings.defaultPermitMarkupPercent = defaultPermitMarkupPercent;
      window.AppState.settings.defaultTaxCategories = defaultTaxCategories;
      window.AppState.settings.accountingBasis = accountingBasis;
      window.AppState.settings.perItemInvoiceHide = perItemInvoiceHide;
    }
    window.showToast?.('Billing settings saved', 'success');
    // 1017 — Ticket Only / Clock + Ticket force a customer at clock-in, so shop
    // and yard time needs an internal-time customer to land on. Turning a ticket
    // mode ON was checked BEFORE the write (above, blocking). A company already
    // in a ticket mode re-saving other settings gets the old warn-only check.
    if (_isTicketClockMode(clockOutMode) && _ticketModeWasOn) await ensureInternalTimeCustomerForTicketMode('company clock mode: ' + clockOutMode);
    updateBillingHeader();
    closeBillingSettings();
  } catch (err) {
    console.error('[Billing] Error saving settings:', err);
    window.showToast?.('Error saving settings: ' + (err.message || 'Unknown'), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHTML; }
  }
}

// ============================================
// QB SETUP WIZARD
// ============================================

let _qbParsedAccounts = [];

function openQBSetup() {
  const modal = document.getElementById('modal-qb-setup');
  if (!modal) return;
  const settings = window.AppState?.settings || {};
  // Populate item inputs
  const qbMap = settings.qbItemMap || {};
  ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'MISC'].forEach(type => {
    const el = document.getElementById('qb-wiz-item-' + type);
    if (el) el.value = qbMap[type] || '';
  });
  // Populate account inputs
  const acctMap = settings.qbAccountMap || {};
  ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'MISC'].forEach(type => {
    const el = document.getElementById('qb-wiz-acct-' + type);
    if (el) el.value = acctMap[type] || '';
  });
  // Populate export settings
  const termsEl = document.getElementById('qb-wiz-terms');
  if (termsEl) termsEl.value = settings.qbDefaultTerms || '';
  const arEl = document.getElementById('qb-wiz-ar-account');
  if (arEl) arEl.value = settings.qbAccountsReceivable || '';
  // Render manual customer map
  renderQBCustomerMapManual();
  // Update badges
  const custCount = (BillingState.customers || []).filter(c => c.qbCustomerName).length;
  const custBadge = document.getElementById('qb-wiz-cust-badge');
  if (custBadge) custBadge.textContent = custCount > 0 ? `${custCount} mapped` : '';
  const itemCount = Object.keys(qbMap).length;
  const itemBadge = document.getElementById('qb-wiz-item-badge');
  if (itemBadge) itemBadge.textContent = itemCount > 0 ? `${itemCount} mapped` : '';
  _t9986ApplyReadOnly(modal, ['btn-save-qb-setup']); // TASK-9986 S1
  modal.classList.remove('hidden');
}

function closeQBSetup() {
  const modal = document.getElementById('modal-qb-setup');
  if (modal) modal.classList.add('hidden');
}

// TASK-9373 — shared old→new audit collector. Was a local closure inside
// saveBillingSettings (reference compare); lifted so the Driver App page logs
// with the same shape, and made STRUCTURAL so lists/maps (driverApp,
// defaultTaxCategories) diff by value, not identity. Usage:
//   const changed = {}; const diff = _settingsAuditDiff(changed);
//   diff('key', oldValue, newValue);  →  changed.key = { from, to } when different
function _settingsAuditDiff(changed) {
  const norm = (v) => (v && typeof v === 'object') ? JSON.stringify(v) : v;
  return (key, from, to) => { if (norm(from) !== norm(to)) changed[key] = { from, to }; };
}

// ============================================
// TASK-9373 — DRIVER APP SETTINGS (what PIN drivers see)
// ============================================
// Chris 2026-09-24: one place, in AR/AP Settings next to the clock-out mode and
// the ticket-form templates, that owns every driver-side switch plus the PIN
// app's tab / Actions-tile / job-detail-section visibility and order. Access to
// this page = the AR/AP view's `billing` module gate (checkModuleAccess), the
// same gate Billing Settings has today — no extra gate, no role check.
//
// MIRROR, DON'T MIGRATE (COD unanimous): the gates in DRIVER_APP_GATES keep
// their EXISTING keys and their original screens keep working; this page is a
// second writer of the SAME keys. Every gate saves ALONE as a DOTTED LEAF —
// never a whole nested map: {driverExpenses:{enabled}} through _settingsPersist
// would delete categories / requireCostAllocation / notifyOn* that ap.js stores
// as leaves (COD Edge Case). WHO may write follows the MODULE that owns the key
// (Chris: module access, not roles): billing keys → `billing`, dispatch keys →
// `dispatch`, the two safety keys ride the hr-gated callable
// (window._persistHrSettings → updateHiringPacketTemplate). TASK-9527
// (Chris 2026-10-19, rules on fix/9527-settings-rules-1019): driverContactDirectory
// → dispatch, driverDocs + enableJsaTemplates → hr, enableCitations → compliance;
// all four save as a plain dotted leaf here (NOT the hr callable — the save
// route is `via`, not `owner`). A gate the viewer
// cannot write renders DISABLED with a hint; bundling it with driverApp would
// fail hasOnly and kill the whole save as "Save failed" (COD Independent).
//
// Layout = settings.driverApp, ONE field-path write per change, validated by
// firestore.rules t9373DriverAppOk (shape + NEVER_HIDE). Reader, id lists and
// presets live in app.js (window.driverAppLayout / DRIVER_APP_LAYOUT /
// driverAppPresetRecipe / normalizeDriverAppLayout) — reached via window.*,
// never a cross-import (Pitfall #1). Every save here writes one auditLog row
// 'settings' / 'SAVE_DRIVER_APP_SETTINGS' with { changed: { key: {from,to} } };
// a preset apply is ONE aggregate row carrying `preset`.
//
// Known gaps, documented on the page, not solved here: non-clock PIN drivers
// land on dispatch.js showDriverQuickview, which ignores jobModal.hidden;
// placard badges still show truck/trailer; two screens saving the same key =
// last-save-wins (pre-existing for every setting).

// TASK-9373 driver-app-gates BEGIN
// owner: the MODULE that may write the key — 'billing' (t9337BillingKeys) ·
// 'dispatch' (t9337DispatchKeys) · 'hr' / 'compliance' (t9337SettingsOk lists)
// · 'admin' (no module allow-list; admin arm only — none today). via: HOW it
// saves — 'hrCallable' = window._persistHrSettings (TASK-637, the two safety
// keys); absent = one dotted leaf through _daPersistLeaf. featuresOnly: saved
// by this writer but listed only on Settings → Features (not a driver-app
// layout switch). kind: 'bool' = toggle written here as a dotted
// leaf; 'readonly' / 'template' = shown with a jump, their writers stay put
// (clockOutMode carries the TASK-572 ticket-flag interlocks; the two section
// arrays have their own builders).
const DRIVER_APP_GATES = [
  { key: 'clockOutMode', owner: 'billing', kind: 'readonly', group: 'Clock & tickets', label: 'Clock-out mode', hint: 'Set at the top of Billing Settings. Decides whether the ticket switches below apply.' },
  { key: 'showTicketsInDriverView', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Show tickets to drivers', needsTicketMode: true, section: 'tickets', hint: 'Job details show the job’s tickets.' },
  { key: 'showPricesOnDriverTickets', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Show prices on driver tickets', needsTicketMode: true },
  { key: 'allowDriverTimeEdit', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Drivers can edit their clock times' },
  { key: 'allowDriverEquipmentEdit', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Drivers can edit equipment on tickets', needsTicketMode: true },
  { key: 'driverMaterialsReport', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Crew reports materials used', needsTicketMode: true }, // TASK-9264 (office accepts before stock moves)
  { key: 'equipmentTie.enabled', owner: 'billing', kind: 'bool', group: 'Clock & tickets', label: 'Per-operator equipment tracking' },
  { key: 'customTicketSections', owner: 'billing', kind: 'template', group: 'Clock & tickets', label: 'Field Ticket form sections', hint: 'Edit in Templates → Field Ticket.' },
  { key: 'clockOutFormSections', owner: 'billing', kind: 'template', group: 'Clock & tickets', label: 'Clock-Out form sections', hint: 'Edit in Templates → Clock-Out Form.' },
  { key: 'driverExpenses.enabled', owner: 'billing', kind: 'bool', group: 'Actions tiles', label: 'Record Expense tile', tile: 'tile-record-expense', hint: 'Categories and notifications: AR/AP → Driver Expenses.' },
  { key: 'driverContactDirectory.enabled', owner: 'dispatch', kind: 'bool', group: 'Actions tiles', label: 'Team tile (contact directory)', tile: 'tile-team', hint: 'Only people with “Show on Driver Card” appear (Roster).' },
  { key: 'driverDocs.enabled', owner: 'hr', kind: 'bool', group: 'Actions tiles', label: 'Doc Page tile (shared company documents)', tile: 'tile-docs' },
  { key: 'enableCitations', owner: 'compliance', kind: 'bool', group: 'Tabs & job details', label: 'Exam Report tab', defaultOn: true, tile: 'driver-btmnav-exam' },
  { key: 'enable811', owner: 'dispatch', kind: 'bool', group: 'Tabs & job details', label: '811 / One-Call section on jobs', section: '811', reloadAfter: true },
  { key: 'personalizedSchedule', owner: 'dispatch', kind: 'bool', group: 'Tabs & job details', label: 'Personalized schedule (drivers see only their own jobs)' },
  { key: 'safetyMeetingSignInEnabled', owner: 'hr', via: 'hrCallable', kind: 'bool', group: 'Inspections tab', label: 'Safety meeting sign-in' },
  { key: 'safetyEquipmentDriverEnabled', owner: 'hr', via: 'hrCallable', kind: 'bool', group: 'Inspections tab', label: 'PPE sign-off' },
  { key: 'enableJsaTemplates', owner: 'hr', kind: 'bool', group: 'Inspections tab', label: 'JSA templates', featuresOnly: true },
];
// TASK-9373 driver-app-gates END

const DRIVER_APP_LABELS = {
  nav: {
    'driver-btmnav-board': 'Job Board', 'driver-btmnav-inspect': 'Inspections', 'driver-btmnav-clock': 'Clock',
    'driver-btmnav-exam': 'Exam Report', 'driver-btmnav-actions': 'Actions',
  },
  actions: {
    'tile-timeoff': 'Time Off', 'tile-vehicles': 'Vehicles', 'tile-maintenance': 'Maintenance', 'tile-team': 'Team',
    'tile-docs': 'Doc Page', 'tile-mydocs': 'My Documents', 'tile-switchjob': 'Switch Job', 'tile-timesheet': 'My Hours',
    'tile-record-expense': 'Record Expense', 'tile-help': 'Help', 'tile-signout': 'Sign Out',
  },
  jobModal: {
    customer: 'Customer', po: 'PO Number', vendor: 'Vendor Number', type: 'Job Type', start: 'Start Time', location: 'Location',
    status: 'Status', crew: 'Crew', trucks: 'Trucks', trailers: 'Trailers', notes: 'Notes', attachments: 'Attachments',
    permits: 'Load Permits', '811': '811 / One-Call', clock: 'Clock In / Out', jsa: 'JSA', tickets: 'Tickets',
    inspections: 'Equipment inspections', documents: 'Documents (BOL / disposal)',
  },
};
const DRIVER_APP_KIND_TITLES = { nav: 'Bottom bar tabs', actions: 'Actions sheet tiles', jobModal: 'Job details sections' };

let _daLayout = null; // working copy of the normalized layout while the modal is open

function _daGet(obj, dotted) {
  return String(dotted).split('.').reduce((o, k) => (o && typeof o === 'object') ? o[k] : undefined, obj);
}
function _daSetNested(obj, dotted, value) {
  const parts = String(dotted).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}
function _daGateValue(gate, settings) {
  const v = _daGet(settings, gate.key);
  if (gate.kind !== 'bool') return v;
  return gate.defaultOn ? v !== false : v === true;
}
// Mirrors firestore.rules isTenantAdmin (members.role in owner/admin) the way
// app.js hrEffectiveAdmin does: no member doc = legacy single login = owner.
function _daViewerIsAdmin() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  const ma = st.memberAccess;
  if (ma == null) return true;
  if (ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  return ma.role === 'owner' || ma.role === 'admin';
}
function _daViewerHasModule(mod) {
  if (_daViewerIsAdmin()) return true;
  const ma = window.AppState?.memberAccess;
  if (!ma || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes(mod);
}
function _daCanWrite(gate) {
  if (gate.owner === 'billing') return !_t9986BillingReadOnly(); // TASK-9986 S1 — owner or the switch, never role
  if (['dispatch', 'hr', 'compliance'].includes(gate.owner)) return _daViewerHasModule(gate.owner);
  return _daViewerIsAdmin();
}
function _daAccessHint(gate) {
  if (gate.owner === 'billing') return 'ask the account owner (Billing settings)'; // TASK-9986 S1
  if (gate.owner === 'dispatch') return 'needs Dispatch module access';
  if (gate.owner === 'hr') return 'needs HR module access';
  if (gate.owner === 'compliance') return 'needs Compliance module access';
  return 'needs owner/admin access';
}
function _daEsc(s) { return escapeHtml(String(s == null ? '' : s)); }

// One dotted leaf, nested AppState update. NEVER a whole nested map.
async function _daPersistLeaf(dottedKey, value) {
  const tenant = window.AppState?.tenant;
  if (!tenant) { window.showToast?.('Not logged in', 'error'); return false; }
  try {
    await updateDoc(doc(db, 'tenants', tenant), { ['settings.' + dottedKey]: value });
    if (!window.AppState.settings) window.AppState.settings = {};
    _daSetNested(window.AppState.settings, dottedKey, value);
    return true;
  } catch (e) {
    console.error('[driverApp] persist failed', dottedKey, e);
    window.showToast?.('Save failed: ' + (e?.message || 'unknown'), 'error');
    return false;
  }
}

function _daSyncBillingSettingsCard(settings) {
  const el = document.getElementById('billing-driver-app-summary');
  if (!el || !window.normalizeDriverAppLayout) return;
  const l = window.normalizeDriverAppLayout(settings?.driverApp);
  const hidden = l.nav.hidden.length + l.actions.hidden.length + l.jobModal.hidden.length;
  const preset = l.preset.charAt(0).toUpperCase() + l.preset.slice(1);
  el.textContent = `Tabs, Actions tiles and job-detail sections your drivers see. Preset: ${preset}` + (hidden ? ` · ${hidden} hidden` : '');
}

// ONE settings.driverApp write + ONE audit row (old → new). `extra` rides on the
// audit row (preset applies carry { preset }).
async function _daPersistLayout(next, extra) {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return false; } // TASK-9986 S1
  const before = window.driverAppLayout ? window.driverAppLayout() : null;
  const layout = window.normalizeDriverAppLayout ? window.normalizeDriverAppLayout(next) : next;
  const ok = await _daPersistLeaf('driverApp', layout);
  if (ok) {
    _daLayout = layout;
    const changed = {};
    _settingsAuditDiff(changed)('driverApp', before, layout);
    if (Object.keys(changed).length) {
      try { window.logAudit?.('settings', 'SAVE_DRIVER_APP_SETTINGS', { changed, ...(extra || {}) }); }
      catch (auditErr) { console.warn('[driverApp] audit log failed:', auditErr); }
    }
    _daSyncBillingSettingsCard(window.AppState?.settings || {});
  }
  return ok;
}

// ============================================================================
// TASK-9553 — per-person clock mode. people/{id}.clockOutMode overrides the
// company's mode for that one person (null = follow the company). Every reader
// resolves it through window.ClockMode (app.js) / functions/src/shared/
// clockMode.js. Each change saves on its own and writes an audit row with the
// old and new value read from a FRESH getDoc — never the shared Save button.
// ============================================================================
const _PCM_LABELS = { simple: 'Simple Clock Out', ticket: 'Clock + Ticket', ticketonly: 'Ticket Only' };
let _pcmPeople = [];
let _pcmSaveChain = Promise.resolve();

function _pcmTenant() {
  return BillingState.tenantId === 'demo-tenant' ? 'demo' : (BillingState.tenantId || window.AppState?.tenant);
}
function _pcmOverride(p) {
  const m = p && p.clockOutMode;
  return Object.prototype.hasOwnProperty.call(_PCM_LABELS, m) ? m : null;
}
function _pcmCompanyMode() {
  return window.AppState?.settings?.clockOutMode || 'simple';
}
function _pcmIsListed(p) {
  return !!p && ((p.active !== false && p.terminated !== true) || !!_pcmOverride(p));
}

// Billing Settings card line: who is NOT on the company mode. Reads the live
// people snapshot when it is loaded; says nothing it cannot back up otherwise.
function _pcmRefreshSummary(list) {
  const el = document.getElementById('billing-person-clockmode-summary');
  if (!el) return;
  const people = Array.isArray(list) ? list : (BillingState.people || []);
  const company = _pcmCompanyMode();
  const diff = people.filter(p => _pcmIsListed(p) && _pcmOverride(p) && _pcmOverride(p) !== company);
  if (!people.length && !Array.isArray(list)) { el.textContent = 'Pick a mode for one person instead of the whole company.'; return; }
  if (!diff.length) { el.textContent = 'Everyone uses the mode above.'; return; }
  const names = diff.slice(0, 3).map(p => `${p.name || 'Unnamed'} (${_PCM_LABELS[_pcmOverride(p)]})`).join(', ');
  el.textContent = `${diff.length} ${diff.length === 1 ? 'person uses' : 'people use'} a different mode: ${names}${diff.length > 3 ? ', …' : ''}`;
}

function _ensurePersonClockModeModalDOM() {
  if (document.getElementById('modal-person-clockmode')) return;
  const wrap = document.createElement('div');
  wrap.id = 'modal-person-clockmode';
  wrap.className = 'modal hidden';
  wrap.setAttribute('onclick', 'if(event.target===this)window.BillingModule.closePersonClockModes()');
  wrap.innerHTML = `
    <div class="modal-content" style="max-width:560px;display:flex;flex-direction:column;max-height:95vh;max-height:95dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons" style="color:var(--brand-blue);">group</span> Clock mode by person</h3>
        <button class="btn-icon" onclick="window.BillingModule.closePersonClockModes()" style="color:var(--text-secondary);" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div id="person-clockmode-body" style="padding:var(--spacing-lg);overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;"></div>
      <div class="form-actions" style="flex-shrink:0;justify-content:space-between;align-items:center;">
        <span style="font-size:0.78rem;color:var(--text-secondary);">Changes save as you go. A driver sees the new mode on their next screen load.</span>
        <button class="btn btn-primary" id="person-clockmode-done" onclick="window.BillingModule.closePersonClockModes()">Done</button>
      </div>
    </div>`;
  document.body.appendChild(wrap);
}

function _renderPersonClockModesBody(errMsg) {
  const body = document.getElementById('person-clockmode-body');
  if (!body) return;
  const company = _pcmCompanyMode();
  const companyLabel = _PCM_LABELS[company] || _PCM_LABELS.simple;
  if (errMsg) {
    body.innerHTML = `<p style="color:var(--text-primary);font-size:0.9rem;">${_daEsc(errMsg)}</p>`;
    return;
  }
  const rows = _pcmPeople
    .filter(_pcmIsListed)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map((p) => {
      const own = _pcmOverride(p);
      const opt = (v, label) => `<option value="${v}" ${(own || '') === v ? 'selected' : ''}>${_daEsc(label)}</option>`;
      return `
      <div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--border-light);min-height:44px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.9rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${_daEsc(p.name || 'Unnamed')}</div>
          ${p.active === false || p.terminated === true ? '<div style="font-size:0.75rem;color:var(--text-secondary);">Inactive</div>' : ''}
        </div>
        <select style="max-width:220px;min-height:44px;padding:8px 10px;font-size:16px;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-primary, #fff);color:var(--text-primary);" data-person-clockmode="${_daEsc(p.id)}"
          aria-label="Clock mode for ${_daEsc(p.name || 'this person')}"
          onchange="window.BillingModule.setPersonClockMode('${window.escJsAttr(p.id)}', this.value, this)">
          ${opt('', `Company mode (${companyLabel})`)}
          ${opt('simple', _PCM_LABELS.simple)}
          ${opt('ticket', _PCM_LABELS.ticket)}
          ${opt('ticketonly', _PCM_LABELS.ticketonly)}
        </select>
      </div>`;
    }).join('');
  body.innerHTML = `
    <p style="font-size:0.85rem;color:var(--text-primary);margin:0 0 12px;">The company mode is <strong>${_daEsc(companyLabel)}</strong>. Pick a different mode for anyone who works another way — for example, most of the crew on Ticket Only and a few on a regular clock in / clock out.</p>
    ${rows || '<p style="font-size:0.9rem;color:var(--text-primary);">No people on the roster yet.</p>'}`;
}

async function openPersonClockModes() {
  _ensurePersonClockModeModalDOM();
  const body = document.getElementById('person-clockmode-body');
  if (body) body.innerHTML = '<p style="font-size:0.9rem;color:var(--text-primary);">Loading people…</p>';
  document.getElementById('modal-person-clockmode')?.classList.remove('hidden');
  const tenant = _pcmTenant();
  try {
    // Fresh read — the Billing people listener is loaded lazily and may be empty.
    const snap = await getDocs(collection(db, 'tenants', tenant, 'people'));
    _pcmPeople = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    window.ClockMode?.notePeople?.(_pcmPeople);
    _renderPersonClockModesBody();
  } catch (e) {
    console.error('[Billing] per-person clock mode: people read failed', e);
    _renderPersonClockModesBody('Couldn’t load your people. Close this and try again.');
  }
}

function closePersonClockModes() {
  const done = document.getElementById('person-clockmode-done');
  if (done) { done.disabled = true; done.textContent = 'Saving…'; }
  return _pcmSaveChain.then(() => {
    _pcmRefreshSummary(_pcmPeople.length ? _pcmPeople : undefined);
    document.getElementById('modal-person-clockmode')?.classList.add('hidden');
    if (done) { done.disabled = false; done.textContent = 'Done'; }
  });
}

function setPersonClockMode(personId, value, selectEl) {
  const next = Object.prototype.hasOwnProperty.call(_PCM_LABELS, value) ? value : null;
  if (selectEl) selectEl.disabled = true;
  _pcmSaveChain = _pcmSaveChain.then(async () => {
    const tenant = _pcmTenant();
    const ref = doc(db, 'tenants', tenant, 'people', String(personId));
    try {
      const fresh = await getDoc(ref);
      if (!fresh.exists()) throw new Error('That person is no longer on the roster.');
      const data = fresh.data() || {};
      const prev = _pcmOverride(data);
      if (prev === next) return;
      // COD round 2 (Edge Case 🔴): a mode change mid-shift closes the open
      // shift under the NEW mode, which can leave the ticket line the OLD mode
      // opened at clock-in never closed. Change it only while they are off the clock.
      const open = await getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'),
        where('personId', '==', String(personId)), where('status', '==', 'active'), limit(1)));
      if (!open.empty) {
        throw Object.assign(new Error(`${data.name || 'This person'} is on the clock — change their mode after they clock out.`), { code: 'on-clock' });
      }
      // fix/2096-preship — putting this person INTO a ticket mode is fail-safe:
      // the Shop customer exists (or is created) first, or nothing is saved
      // (ensure… has already said why).
      const _wasTicket = _isTicketClockMode(prev || _pcmCompanyMode());
      const _willTicket = _isTicketClockMode(next || _pcmCompanyMode());
      if (_willTicket && !_wasTicket) {
        const shop = await ensureInternalTimeCustomerForTicketMode('person clock mode: ' + (next || 'company'), { blocking: true });
        if (shop !== 'exists' && shop !== 'created') throw Object.assign(new Error('no internal-time customer'), { code: 'no-shop' });
      }
      await updateDoc(ref, { clockOutMode: next });
      const row = _pcmPeople.find(p => p.id === personId);
      if (row) row.clockOutMode = next;
      window.ClockMode?.notePerson?.(personId, { clockOutMode: next });
      try {
        await window.logAudit?.('settings', 'SET_PERSON_CLOCK_MODE', {
          personId: String(personId),
          personName: data.name || null,
          companyMode: _pcmCompanyMode(),
          changed: { clockOutMode: { from: prev || 'company', to: next || 'company' } },
        });
      } catch (auditErr) { console.warn('[Billing] per-person clock mode audit failed:', auditErr); }
      window.showToast?.(`${data.name || 'Person'}: ${next ? _PCM_LABELS[next] : 'company mode'}`, 'success');
      // 1017 — a ticket mode needs somewhere to put shop/yard time.
      // Back to company mode on a ticket-mode company counts too (COD r2).
      // Already in a ticket mode (checked before the write when turning it on).
      if (_willTicket && _wasTicket) await ensureInternalTimeCustomerForTicketMode('person clock mode: ' + (next || 'company'));
    } catch (e) {
      console.error('[Billing] per-person clock mode save failed', e);
      const denied = e && (e.code === 'permission-denied' || /permission/i.test(e.message || ''));
      // 'no-shop': ensureInternalTimeCustomerForTicketMode already showed the reason.
      if (!(e && e.code === 'no-shop')) window.showToast?.(denied ? 'You need Roster access to change a person’s clock mode.'
        : e && e.code === 'on-clock' ? e.message
        : ('Couldn’t save: ' + (e.message || 'unknown error')), 'error');
      // Put the dropdown back to what is actually saved.
      const row = _pcmPeople.find(p => p.id === personId);
      if (selectEl) selectEl.value = _pcmOverride(row) || '';
    } finally {
      if (selectEl) selectEl.disabled = false;
    }
  });
  return _pcmSaveChain;
}

// ============================================================================
// 1017 (Chris 2026-10-17: Ticket-Only KEEPS shop/yard hours) — the ticket modes
// (Clock + Ticket, Ticket Only) force a customer at clock-in on every surface
// (dispatch / jobboard / kiosk). Shop and yard time goes on the company's
// internal-time customer (TASK-1175: `internalTimeOnly`, never billed, still
// paid — payroll reads every clock entry). A company with NO internal customer
// therefore had nowhere to put that time once it (or one person, TASK-9553)
// went to a ticket mode: the hours went unrecorded or onto a billed customer.
// Read live 2026-10-17: 7 of 9 ticket-mode tenants had none.
// Fix: whenever a ticket mode is turned on, make sure one exists. Adds a
// "Shop" customer flagged internalTimeOnly (rules: only a tenant admin may mint
// the flag — anyone else is told who can add it). Audited like the CRM toggle.
// fix/2096-preship (integ/2096 COD): TURNING ON a ticket mode now runs this
// FIRST with { blocking: true } and the mode is saved only on 'exists' /
// 'created'. Before, the mode always saved and a caller who may not mint the
// flag (billing-module member, not a tenant admin) left the company in Ticket
// Only with no Shop customer — shop hours went onto a real customer's ticket.
// ============================================================================
function _isTicketClockMode(m) { return m === 'ticket' || m === 'ticketonly'; }

// COD r1 (Edge Case / Independent / Adversary / Domain): the FLAG is what
// stops a ticket at clock-in (dispatch.js skipInternalTime, clock.js
// findOrCreateTicket) and what puts a customer in the pickers' Internal group,
// so only a flagged customer counts as "a place for shop time". The shop/
// office/internal/yard NAME guess is payroll-only (pickerGroups.js header).
// Vendors / subcontractors never show in a clock picker, so they never count.
const _INTERNAL_TIME_SHOP_ID = 'internal-time-shop';
const _INTERNAL_NAME_GUESS = /^(shop|office|internal|yard)$/i;
// COD r2: the shared predicate (normalizes case/whitespace), not exact strings.
function _isVendorType(c) {
  const bc = window.BillableCustomer;
  if (bc && typeof bc.isBillableCustomer === 'function') return !!c && !bc.isBillableCustomer(c);
  const t = String((c && c.type) || '').trim().toLowerCase();
  return t === 'vendor' || t === 'subcontractor';
}
function _isInactiveCustomer(c) { return c && (c.status === 'inactive' || c.status === 'Inactive'); }

// 'ok' | 'inactive' (a flagged one exists but is switched off) |
// 'unflagged' (a customer NAMED Shop/Yard/... exists without the flag — CRM
// saves the box as false, so false counts too: COD r2) | 'none'
function _internalTimeCustomerState(customers) {
  const list = (customers || []).filter(c => c && !_isVendorType(c));
  if (list.some(c => c.internalTimeOnly === true && !_isInactiveCustomer(c))) return { state: 'ok' };
  const off = list.find(c => c.internalTimeOnly === true);
  if (off) return { state: 'inactive', name: off.name || 'Shop' };
  const guess = list.find(c => c.internalTimeOnly !== true && !_isInactiveCustomer(c) && _INTERNAL_NAME_GUESS.test(String(c.name || '').trim()));
  if (guess) return { state: 'unflagged', name: String(guess.name || '').trim() };
  return { state: 'none' };
}

// Name for the new customer: "Shop", unless a real (explicitly not internal)
// customer already carries that name — never shadow it.
function _internalTimeCustomerName(customers) {
  const taken = new Set((customers || []).map(c => String(c && c.name || '').trim().toLowerCase()));
  return taken.has('shop') ? 'Shop / Yard (internal)' : 'Shop';
}

// Returns 'exists' | 'created' | 'inactive' | 'unflagged' | 'denied' | 'error'.
// `why` lands on the audit row. Never throws. opts.blocking = the caller will
// NOT save the ticket mode unless this returns 'exists' / 'created', so every
// other outcome says so, as an error toast.
async function ensureInternalTimeCustomerForTicketMode(why, opts) {
  const blocking = !!(opts && opts.blocking);
  const tone = blocking ? 'error' : 'warning';
  const tenant = _pcmTenant();
  if (!tenant) {
    if (blocking) window.showToast?.('Clock mode not saved — couldn’t tell which company this is. Refresh and try again.', 'error');
    return 'error';
  }
  const fix = blocking
    ? 'Clock mode not saved. Clock + Ticket and Ticket Only need a customer marked “Internal time only” (like “Shop”) first, so shop and yard hours never land on a real customer’s ticket.'
    : 'Shop and yard time needs a customer marked “Internal time only” so it stays on the clock.';
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'customers'));
    const customers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const st = _internalTimeCustomerState(customers);
    if (st.state === 'ok') return 'exists';
    if (st.state === 'inactive') {
      window.showToast?.(`${fix} “${st.name}” is switched off — turn it back on in CRM.`, tone);
      return 'inactive';
    }
    if (st.state === 'unflagged') {
      window.showToast?.(`${fix} Tick “Internal time only” on “${st.name}” in CRM.`, tone);
      return 'unflagged';
    }
    // COD r1+r2: a fixed id written by create-if-absent in a transaction, so
    // two tabs or two admins saving at once end with ONE Shop and nothing
    // already there is ever overwritten. If something else already holds that
    // id (squatted), fall back to a fresh id rather than give up.
    const name = _internalTimeCustomerName(customers);
    const data = {
      name,
      status: 'Active', // CRM's spelling (crm.js) — its Active filter is exact
      internalTimeOnly: true,
      color: '',
      createdAt: Timestamp.now(),
      createdBy: auth?.currentUser?.uid || null,
      createdFrom: 'clock_mode_ticket',
    };
    const squatted = customers.some(c => c.id === _INTERNAL_TIME_SHOP_ID);
    const ref = squatted
      ? doc(collection(db, 'tenants', tenant, 'customers'))
      : doc(db, 'tenants', tenant, 'customers', _INTERNAL_TIME_SHOP_ID);
    const wrote = await runTransaction(db, async (tx) => {
      const cur = await tx.get(ref);
      if (cur.exists()) return cur.data()?.internalTimeOnly === true ? 'exists' : 'taken';
      tx.set(ref, data);
      return 'created';
    });
    if (wrote === 'exists') return 'exists'; // another tab / admin created it a moment ago
    if (wrote === 'taken') { window.showToast?.(`${fix} Add one in CRM.`, tone); return 'error'; }
    try {
      await window.logAudit?.('settings', 'CUSTOMER_INTERNAL_TIME_TOGGLE', {
        customerId: ref.id, customerName: name, from: false, to: true, onCreate: true, reason: why || 'ticket clock mode',
      });
    } catch (auditErr) { console.warn('[Billing] internal-time customer audit failed:', auditErr); }
    window.showToast?.(`Added a “${name}” customer (internal, not billed) so shop and yard time still goes on the clock.`, 'success');
    return 'created';
  } catch (e) {
    const denied = e && (e.code === 'permission-denied' || /permission/i.test(e.message || ''));
    console.warn('[Billing] ensureInternalTimeCustomerForTicketMode failed:', e);
    window.showToast?.(fix + ' ' + (denied ? 'Only the owner or an admin can add one — ask them to add it in CRM, then save again.' : 'Add one in CRM, then save again.'), tone);
    return denied ? 'denied' : 'error';
  }
}

function _ensureDriverAppModalDOM() {
  if (document.getElementById('modal-driver-app-settings')) return;
  const wrap = document.createElement('div');
  wrap.id = 'modal-driver-app-settings';
  wrap.className = 'modal hidden';
  wrap.setAttribute('onclick', 'if(event.target===this)window.BillingModule.closeDriverAppSettings()');
  wrap.innerHTML = `
    <div class="modal-content" style="max-width:640px;display:flex;flex-direction:column;max-height:95vh;max-height:95dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons" style="color:var(--brand-blue);">smartphone</span> Driver App</h3>
        <button class="btn-icon" onclick="window.BillingModule.closeDriverAppSettings()" style="color:var(--text-secondary);" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div id="driver-app-settings-body" style="padding:var(--spacing-lg);overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;"></div>
      <div class="form-actions" style="flex-shrink:0;justify-content:space-between;align-items:center;">
        <span style="font-size:0.78rem;color:var(--text-secondary);">Changes save as you go. Drivers see them on their next screen load.</span>
        <button class="btn btn-primary" id="driver-app-settings-done" onclick="window.BillingModule.closeDriverAppSettings()">Done</button>
      </div>
    </div>`;
  document.body.appendChild(wrap);
}

function openDriverAppSettings() {
  _ensureDriverAppModalDOM();
  _daLayout = window.driverAppLayout ? window.driverAppLayout() : null;
  renderDriverAppSettingsBody();
  document.getElementById('modal-driver-app-settings')?.classList.remove('hidden');
}
// COD round 2: close WAITS for the save queue before it syncs the parent form
// and hides. Otherwise toggle → close → Save Billing lets the parent read the
// old checkbox while the queued write is still in flight, and the parent save
// overwrites it. The sub-modal (which covers the parent) stays up, with Done
// showing "Saving…", until every queued save has settled.
let _daClosing = null;
function closeDriverAppSettings() {
  if (_daClosing) return _daClosing;
  const done = document.getElementById('driver-app-settings-done');
  if (done) { done.disabled = true; done.textContent = 'Saving…'; }
  _daClosing = _daSaveChain.then(() => {
    _daSyncBillingSettingsCard(window.AppState?.settings || {});
    _daSyncParentControls(window.AppState?.settings, [..._daSavedParentKeys]);
    _daSavedParentKeys.clear();
    document.getElementById('modal-driver-app-settings')?.classList.add('hidden');
    if (done) { done.disabled = false; done.textContent = 'Done'; }
    _daClosing = null;
  });
  return _daClosing;
}

// Why a tile / tab / section may still be missing even when "shown": its
// feature gate. Text only — every id here is from the closed spec list.
function _daGateHint(kind, id, settings) {
  const on = (k) => _daGet(settings, k) === true;
  if (kind === 'nav') {
    if (id === 'driver-btmnav-board') return 'off-board screens only (Inspections, Maintenance) — the way back to the board';
    if (id === 'driver-btmnav-clock') return 'shown when the account has clock access';
    if (id === 'driver-btmnav-exam') return settings.enableCitations === false ? 'Exam Report switch (below) is off' : '';
    return '';
  }
  if (kind === 'actions') {
    if (id === 'tile-timeoff') return on('driverTimeOff.enabled') ? '' : 'shown when Time Off is enabled for drivers (setting, VIP or HR add-on)';
    if (id === 'tile-maintenance') return 'shown to drivers with maintenance access or an assigned task';
    if (id === 'tile-team') return on('driverContactDirectory.enabled') ? '' : 'Team switch (below) is off';
    if (id === 'tile-docs') return on('driverDocs.enabled') ? '' : 'Doc Page switch (below) is off';
    if (id === 'tile-switchjob') return 'shown while clocked in';
    if (id === 'tile-timesheet') return 'needs clock access';
    if (id === 'tile-record-expense') return on('driverExpenses.enabled') ? '' : 'Record Expense switch (below) is off';
    if (id === 'tile-vehicles') return 'DVIR path — always shown';
    if (id === 'tile-mydocs') return 'the driver’s own qualification file — always shown';
    return '';
  }
  if (id === '811') return on('enable811') ? '' : '811 switch (below) is off';
  if (id === 'tickets') return on('showTicketsInDriverView') ? '' : 'Show tickets to drivers (below) is off';
  if (id === 'inspections') return 'equipment inspection section, when the job has one';
  if (id === 'clock' || id === 'jsa') return 'always shown';
  return '';
}

function _daListHTML(kind, layout, settings, canEdit) {
  const spec = window.DRIVER_APP_LAYOUT[kind];
  const labels = DRIVER_APP_LABELS[kind] || {};
  const ids = spec.hasOrder ? layout[kind].order : spec.ids;
  const movable = ids.filter(id => id !== spec.pinFirst && id !== spec.pinLast);
  const rows = ids.map((id) => {
    const locked = spec.neverHide.includes(id);
    const hidden = layout[kind].hidden.includes(id);
    const pinned = id === spec.pinFirst || id === spec.pinLast;
    const mi = movable.indexOf(id);
    const hint = locked && kind === 'nav' && id !== 'driver-btmnav-board' && id !== 'driver-btmnav-clock' ? 'always shown' : _daGateHint(kind, id, settings);
    const disableChk = locked || !canEdit;
    const upDisabled = !canEdit || pinned || mi <= 0;
    const downDisabled = !canEdit || pinned || mi === -1 || mi >= movable.length - 1;
    const orderBtns = spec.hasOrder ? `
        <button type="button" class="btn-icon" ${upDisabled ? 'disabled style="opacity:0.35;"' : ''} onclick="window.BillingModule.moveDriverAppItem('${id}','${kind}',-1)" aria-label="Move ${_daEsc(labels[id] || id)} up"><span class="material-icons">arrow_upward</span></button>
        <button type="button" class="btn-icon" ${downDisabled ? 'disabled style="opacity:0.35;"' : ''} onclick="window.BillingModule.moveDriverAppItem('${id}','${kind}',1)" aria-label="Move ${_daEsc(labels[id] || id)} down"><span class="material-icons">arrow_downward</span></button>` : '';
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:6px;min-height:44px;${hidden ? 'opacity:0.6;' : ''}">
        <input type="checkbox" ${hidden ? '' : 'checked'} ${disableChk ? 'disabled' : ''} style="accent-color:var(--brand-blue);"
          onchange="window.BillingModule.toggleDriverAppItem('${id}','${kind}',this.checked)" aria-label="Show ${_daEsc(labels[id] || id)}">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);display:flex;align-items:center;gap:4px;">${_daEsc(labels[id] || id)}${locked ? '<span class="material-icons" style="font-size:14px;color:var(--text-secondary);" title="Always shown">lock</span>' : ''}${pinned ? `<span style="font-size:0.7rem;color:var(--text-secondary);font-weight:500;">(${id === spec.pinFirst ? 'first' : 'last'})</span>` : ''}</div>
          ${hint ? `<div style="font-size:0.75rem;color:var(--text-secondary);">${_daEsc(hint)}</div>` : ''}
        </div>${orderBtns}
      </div>`;
  }).join('');
  return `
    <div style="margin-bottom:18px;">
      <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin:0 0 8px;">${DRIVER_APP_KIND_TITLES[kind]}</h4>
      ${rows}
    </div>`;
}

function _daGatesHTML(settings) {
  const isTicketMode = ['ticket', 'ticketonly'].includes(settings.clockOutMode || 'simple');
  const modeLabel = { simple: 'Simple Clock Out', ticket: 'Clock + Ticket', ticketonly: 'Ticket Only' }[settings.clockOutMode || 'simple'] || 'Simple Clock Out';
  const groups = [];
  const pageGates = DRIVER_APP_GATES.filter(g => !g.featuresOnly);
  for (const g of pageGates) if (!groups.includes(g.group)) groups.push(g.group);
  const rowsFor = (group) => pageGates.filter(g => g.group === group).map((g) => {
    const can = _daCanWrite(g);
    const val = _daGateValue(g, settings);
    let control = '';
    let hint = g.hint || '';
    if (g.kind === 'readonly') {
      control = `<span style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">${_daEsc(modeLabel)}</span>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.closeDriverAppSettings()">Change</button>`;
    } else if (g.kind === 'template') {
      const n = Array.isArray(val) ? val.length : 0;
      control = `<span style="font-size:0.85rem;color:var(--text-secondary);">${n ? n + ' custom section' + (n === 1 ? '' : 's') : 'default sections'}</span>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.closeDriverAppSettings();window.BillingModule.closeBillingSettings();window.BillingModule.showInvoiceBuilder()">Templates</button>`;
    } else {
      const blockedByMode = g.needsTicketMode && !isTicketMode;
      const disabled = !can || blockedByMode;
      if (!can) hint = _daAccessHint(g) + (hint ? ' · ' + hint : '');
      else if (blockedByMode) hint = 'needs Clock + Ticket or Ticket Only mode' + (hint ? ' · ' + hint : '');
      control = `<label style="display:inline-flex;align-items:center;gap:6px;cursor:${disabled ? 'not-allowed' : 'pointer'};margin:0;">
        <input type="checkbox" ${val ? 'checked' : ''} ${disabled ? 'disabled' : ''} style="accent-color:var(--brand-blue);"
          onchange="window.BillingModule.setDriverAppGate('${g.key}',this.checked)" aria-label="${_daEsc(g.label)}">
        <span style="font-size:0.8rem;color:var(--text-secondary);">${val ? 'On' : 'Off'}</span></label>`;
    }
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:6px;min-height:44px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${_daEsc(g.label)}</div>
          ${hint ? `<div style="font-size:0.75rem;color:var(--text-secondary);">${_daEsc(hint)}</div>` : ''}
        </div>
        <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;">${control}</div>
      </div>`;
  }).join('');
  return `
    <div style="margin-bottom:6px;">
      <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin:0 0 4px;">Driver switches</h4>
      <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 10px;">The same switches as on their own screens (Billing, Dispatch, Roster, HR, Inspection settings) — each one saves on its own. A greyed switch needs access you do not have on this login.</p>
      ${groups.map(gr => `<div style="font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--text-secondary);margin:10px 0 6px;">${_daEsc(gr)}</div>${rowsFor(gr)}`).join('')}
    </div>`;
}

function renderDriverAppSettingsBody() {
  const body = document.getElementById('driver-app-settings-body');
  if (!body) return;
  const settings = window.AppState?.settings || {};
  const spec = window.DRIVER_APP_LAYOUT;
  const layout = _daLayout || (window.driverAppLayout ? window.driverAppLayout() : null);
  if (!spec || !layout) {
    body.innerHTML = '<p style="color:var(--text-secondary);">Driver App layout is not available in this build.</p>';
    return;
  }
  const canEdit = !_t9986BillingReadOnly(); // TASK-9986 S1 — settings.driverApp is a Billing setting
  const presetName = layout.preset.charAt(0).toUpperCase() + layout.preset.slice(1);
  const presetBtn = (name, label, desc) => `
      <button type="button" class="btn ${layout.preset === name ? 'btn-primary' : 'btn-secondary'}" ${canEdit ? '' : 'disabled'} style="flex:1;min-width:140px;flex-direction:column;align-items:flex-start;gap:2px;padding:10px 12px;text-align:left;"
        onclick="window.BillingModule.applyDriverAppPreset('${name}')">
        <span style="font-weight:700;">${label}</span>
        <span style="font-size:0.75rem;font-weight:400;opacity:0.85;">${desc}</span>
      </button>`;
  body.innerHTML = `
    <div style="margin-bottom:18px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;">
        <h4 style="font-size:0.9rem;font-weight:700;color:var(--text-primary);margin:0;">Preset</h4>
        <span style="font-size:0.8rem;color:var(--text-secondary);">Current: <strong style="color:var(--text-primary);">${_daEsc(presetName)}</strong></span>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        ${presetBtn('standard', 'Standard', 'Everything on — today’s defaults.')}
        ${presetBtn('simple', 'Simple', 'Fewer tabs and tiles; job details show the essentials.')}
      </div>
      ${canEdit ? '' : '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;">Changing the layout needs AR/AP module access on this login.</div>'}
    </div>
    ${_daListHTML('nav', layout, settings, canEdit)}
    ${_daListHTML('actions', layout, settings, canEdit)}
    ${_daListHTML('jobModal', layout, settings, canEdit)}
    <div style="font-size:0.75rem;color:var(--text-secondary);margin:-8px 0 18px;">Job details section settings apply to the clock job board. Locked items are always shown — Inspections and Vehicles carry the DVIR, My Documents is the driver’s own file, and Sign Out stays last.</div>
    ${_daGatesHTML(settings)}
  `;
}

// TASK-9373 (COD Code Reviewer, merged-diff review) — every sub-page save runs
// through ONE promise chain. Before this, two quick taps each cloned the SAME
// _daLayout before the first write landed, so the second write silently
// dropped the first change while both toasts said "saved". Each queued step
// clones _daLayout only when it RUNS, i.e. after the previous save settled.
let _daSaveChain = Promise.resolve();
function _daEnqueue(step) {
  const run = _daSaveChain.then(step, step);
  _daSaveChain = run.catch(() => {});
  return run;
}

// TASK-9373 (COD Code Reviewer) — the Driver App sub-modal opens ON TOP of the
// Billing Settings modal. Its billing-owned switches also have checkboxes in
// that parent form, which saveBillingSettings reads back and writes. Without a
// sync, a later parent Save silently reverses what was just saved here. Called
// after every successful gate save and on close.
const DRIVER_APP_PARENT_CONTROLS = {
  showTicketsInDriverView: 'billing-settings-show-driver-tickets',
  showPricesOnDriverTickets: 'billing-settings-show-driver-prices',
  allowDriverTimeEdit: 'billing-settings-allow-driver-edit',
  allowDriverEquipmentEdit: 'billing-settings-allow-driver-equipment-edit',
  'equipmentTie.enabled': 'billing-settings-equipment-tie',
};
// COD round 2: sync ONLY the keys this sub-page actually saved. Re-seeding all
// five would throw away unrelated, still-unsaved edits the user made on the
// parent form before opening Driver App.
const _daSavedParentKeys = new Set();
function _daSyncParentControls(settings, keys) {
  const s = settings || window.AppState?.settings || {};
  for (const key of (keys || [])) {
    const elId = DRIVER_APP_PARENT_CONTROLS[key];
    if (!elId) continue;
    const el = document.getElementById(elId);
    if (el) el.checked = _daGet(s, key) === true;
  }
}

function toggleDriverAppItem(id, kind, show) { return _daEnqueue(() => _daToggleItemNow(id, kind, show)); }
function moveDriverAppItem(id, kind, delta) { return _daEnqueue(() => _daMoveItemNow(id, kind, delta)); }
function setDriverAppGate(key, checked) { return _daEnqueue(() => _daSetGateNow(key, checked)); }

async function _daToggleItemNow(id, kind, show) {
  const spec = window.DRIVER_APP_LAYOUT?.[kind];
  if (!spec || !_daLayout) return;
  if (spec.neverHide.includes(id) || !spec.ids.includes(id)) { renderDriverAppSettingsBody(); return; }
  const next = JSON.parse(JSON.stringify(_daLayout));
  const set = new Set(next[kind].hidden);
  if (show) set.delete(id); else set.add(id);
  next[kind].hidden = [...set];
  next.preset = 'custom';
  const ok = await _daPersistLayout(next);
  if (ok) window.showToast?.(`${(DRIVER_APP_LABELS[kind] || {})[id] || id} ${show ? 'shown' : 'hidden'} in the driver app`, 'success');
  renderDriverAppSettingsBody();
}

async function _daMoveItemNow(id, kind, delta) {
  const spec = window.DRIVER_APP_LAYOUT?.[kind];
  if (!spec || !spec.hasOrder || !_daLayout) return;
  const next = JSON.parse(JSON.stringify(_daLayout));
  const arr = next[kind].order;
  const i = arr.indexOf(id);
  const j = i + (delta < 0 ? -1 : 1);
  if (i === -1 || j < 0 || j >= arr.length) return;
  if (id === spec.pinFirst || id === spec.pinLast || arr[j] === spec.pinFirst || arr[j] === spec.pinLast) return;
  arr.splice(j, 0, arr.splice(i, 1)[0]);
  next.preset = 'custom';
  await _daPersistLayout(next);
  renderDriverAppSettingsBody();
}

// Confirm lists EXACTLY what the preset hides, then ONE write + ONE audit row.
async function applyDriverAppPreset(name) {
  const recipe = window.driverAppPresetRecipe ? window.driverAppPresetRecipe(name) : null;
  if (!recipe || recipe.preset !== name) return;
  const lines = [];
  for (const kind of ['nav', 'actions', 'jobModal']) {
    const labels = DRIVER_APP_LABELS[kind] || {};
    if (recipe[kind].hidden.length) lines.push(`${DRIVER_APP_KIND_TITLES[kind]}: ${recipe[kind].hidden.map(id => labels[id] || id).join(', ')}`);
  }
  const title = name.charAt(0).toUpperCase() + name.slice(1);
  const msg = lines.length
    ? `This hides — ${lines.join('. ')}. Everything else stays as it is today, and anything a feature switch already hides stays hidden. Inspections, Vehicles, My Documents, Time Off, Maintenance, Clock, Help and Sign Out are never hidden by a preset.`
    : 'Shows every tab, tile and job-details section — today’s defaults.';
  const ok = window.showConfirm
    ? await window.showConfirm(`Apply the ${title} preset?`, msg, { confirmText: 'Apply' })
    : window.confirm(msg);
  if (!ok) return;
  const saved = await _daEnqueue(() => _daPersistLayout(recipe, { preset: name }));
  if (saved) window.showToast?.(`${title} preset applied to the driver app`, 'success');
  renderDriverAppSettingsBody();
}

// One mirrored gate, saved ALONE at its existing dotted path, audited old → new.
async function _daSetGateNow(key, checked) {
  const gate = DRIVER_APP_GATES.find(g => g.key === key);
  if (!gate || gate.kind !== 'bool') return;
  if (!_daCanWrite(gate)) { window.showToast?.(_daAccessHint(gate), 'error'); renderDriverAppSettingsBody(); return; }
  const settings = window.AppState?.settings || {};
  const from = _daGateValue(gate, settings);
  const to = checked === true;
  if (from === to) return;
  let ok = false;
  if (gate.via === 'hrCallable') {
    // TASK-637 path: hr-gated callable, never a raw rules write for these two.
    ok = (await window._persistHrSettings?.({ [key]: to })) === true;
    if (!ok) window.showToast?.('Save failed', 'error');
  } else {
    ok = await _daPersistLeaf(key, to);
  }
  if (ok) {
    try {
      const changed = {};
      _settingsAuditDiff(changed)(key, from, to);
      window.logAudit?.('settings', 'SAVE_DRIVER_APP_SETTINGS', { changed });
    } catch (auditErr) { console.warn('[driverApp] audit log failed:', auditErr); }
    if (DRIVER_APP_PARENT_CONTROLS[key]) {
      _daSavedParentKeys.add(key);
      _daSyncParentControls(window.AppState?.settings, [key]);
    }
    window.showToast?.(`${gate.label} ${to ? 'on' : 'off'}`, 'success');
    if (gate.reloadAfter) {
      // Same as saveDispatchSettings: the job form template is built with this
      // flag at template time, so the app reloads to apply it.
      window.showToast?.('Reloading to apply to the job form…', 'info');
      setTimeout(() => location.reload(), 800);
      return;
    }
  }
  renderDriverAppSettingsBody();
}

function toggleQBSection(id) {
  const body = document.getElementById(id);
  if (!body) return;
  const isOpen = body.style.display !== 'none';
  body.style.display = isOpen ? 'none' : 'block';
  // Toggle chevron
  const header = body.previousElementSibling;
  const chevron = header?.querySelector('.qb-wiz-chevron');
  if (chevron) chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
}

function parseQBFile(text) {
  const customers = [];
  const items = [];
  const accountSet = new Set();
  let invitemCols = null;

  for (const line of text.split('\n')) {
    const parts = line.split('\t');
    // INVITEM header
    if (parts[0] === '!INVITEM') {
      invitemCols = {};
      parts.forEach((col, i) => { invitemCols[col] = i; });
      continue;
    }
    // INVITEM data
    if (parts[0] === 'INVITEM' && invitemCols) {
      const name = (parts[invitemCols.NAME] || '').replace(/^"|"$/g, '').trim();
      const accnt = (parts[invitemCols.ACCNT] || '').replace(/^"|"$/g, '').trim();
      const hidden = parts[invitemCols.HIDDEN] || '';
      if (name && hidden !== 'Y') {
        items.push({ name, account: accnt });
        if (accnt) accountSet.add(accnt);
      }
    }
    // CUST rows
    if (parts[0] === 'CUST' && parts[1]) {
      const name = parts[1].replace(/^"|"$/g, '').trim();
      if (!name.includes(':')) customers.push(name);
    }
    // ACCNT rows (if Chart of Accounts export is included)
    if (parts[0] === 'ACCNT' && parts[1]) {
      const name = parts[1].replace(/^"|"$/g, '').trim();
      if (name) accountSet.add(name);
    }
  }
  return { customers, items, accounts: [...accountSet].sort() };
}

async function uploadQBFile() {
  const fileInput = document.getElementById('qb-wiz-file');
  const file = fileInput?.files?.[0];
  if (!file) return;

  const nameEl = document.getElementById('qb-wiz-file-name');
  if (nameEl) nameEl.textContent = file.name;
  const statusEl = document.getElementById('qb-wiz-upload-status');

  statusEl.innerHTML = `<div style="display:flex;align-items:center;gap:8px;color:var(--text-secondary);font-size:0.85rem;">
    <span class="btn-spinner"></span> Parsing ${file.name}...
  </div>`;

  try {
    // TASK-1383 — guarded shared reader (settles on every path; existing
    // catch below renders the failure).
    const text = await window.FileReadUtil.readAsText(file);

    const parsed = parseQBFile(text);
    _qbParsedAccounts = parsed.accounts;

    const parts = [];
    if (parsed.customers.length) parts.push(`${parsed.customers.length} customers`);
    if (parsed.items.length) parts.push(`${parsed.items.length} items`);
    if (parsed.accounts.length) parts.push(`${parsed.accounts.length} accounts`);

    statusEl.innerHTML = `<div style="padding:10px 14px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:var(--radius-md);font-size:0.85rem;color:#166534;">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">check_circle</span>
      Found ${parts.join(', ')}. Sections below have been updated.
    </div>`;

    // Process customers
    if (parsed.customers.length > 0) {
      // Decision 2026-10-10 — internal customers are never invoiced, so never matched to QuickBooks.
      const ddCustomers = window.PickerGroups.withoutInternal(_billableCustomers()).map(c => ({ id: c.id, name: c.name }));
      const ddMap = new Map();
      for (const c of ddCustomers) ddMap.set(normalizeCompanyName(c.name), c);

      const matches = [];
      const unmatchedQB = [];
      for (const qbName of parsed.customers) {
        const norm = normalizeCompanyName(qbName);
        const exact = ddMap.get(norm);
        if (exact) {
          matches.push({ qbName, ddCustomerId: exact.id, ddCustomerName: exact.name, confidence: 1.0 });
          continue;
        }
        let best = null;
        for (const c of ddCustomers) {
          const ddNorm = normalizeCompanyName(c.name);
          if (ddNorm && norm && (ddNorm.includes(norm) || norm.includes(ddNorm))) {
            const lenRatio = Math.min(norm.length, ddNorm.length) / Math.max(norm.length, ddNorm.length);
            if (!best || lenRatio > best.confidence) best = { qbName, ddCustomerId: c.id, ddCustomerName: c.name, confidence: Math.round(lenRatio * 100) / 100 };
          }
        }
        if (best && best.confidence >= 0.5) matches.push(best);
        else unmatchedQB.push(qbName);
      }

      // Gemini fallback for unmatched
      if (unmatchedQB.length > 0) {
        try {
          const { functions, httpsCallable } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
          const parseQBList = httpsCallable(functions, 'parseQBCustomerList', { timeout: 120000 });
          const result = await parseQBList({ fileContent: unmatchedQB.join('\n'), ddCustomers, tenantId: BillingState.tenantId || window.AppState?.tenant });
          matches.push(...(result.data?.matches || []));
          unmatchedQB.length = 0;
          unmatchedQB.push(...(result.data?.unmatchedQB || []));
        } catch (err) { console.warn('[QB] Gemini customer fallback failed:', err.message); }
      }

      renderQBMatchResults(matches, unmatchedQB, []);
      const custBadge = document.getElementById('qb-wiz-cust-badge');
      if (custBadge) custBadge.textContent = `${matches.length} matched`;
      // Auto-expand
      const custSection = document.getElementById('qb-wiz-customers');
      if (custSection) custSection.style.display = 'block';
    }

    // Process items — use Gemini
    if (parsed.items.length > 0) {
      try {
        const { functions, httpsCallable } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
        const parseQBItems = httpsCallable(functions, 'parseQBItemList', { timeout: 120000 });
        const itemContent = parsed.items.map(i => i.name).join('\n');
        const result = await parseQBItems({ fileContent: itemContent, tenantId: BillingState.tenantId || window.AppState?.tenant });
        renderQBItemMatchResults(result.data?.matches || [], result.data?.unmatchedQB || []);
        const itemBadge = document.getElementById('qb-wiz-item-badge');
        if (itemBadge) itemBadge.textContent = `${(result.data?.matches || []).length} matched`;
      } catch (err) { console.warn('[QB] Item matching failed:', err.message); }
      // Auto-expand
      const itemSection = document.getElementById('qb-wiz-items');
      if (itemSection) itemSection.style.display = 'block';
    }

    // Populate account dropdowns with parsed accounts
    if (parsed.accounts.length > 0) {
      const acctSection = document.getElementById('qb-wiz-accounts');
      if (acctSection) acctSection.style.display = 'block';
    }

  } catch (err) {
    console.error('[QB] File upload error:', err);
    statusEl.innerHTML = `<p style="color:var(--error-color);font-size:0.85rem;">Error parsing file: ${err.message}</p>`;
  }
  fileInput.value = '';
}

async function saveQBSetup() {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return; } // TASK-9986 S1
  const btn = document.getElementById('btn-save-qb-setup');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const qbItemMap = {};
    ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'MISC'].forEach(type => {
      const val = document.getElementById('qb-wiz-item-' + type)?.value?.trim();
      if (val) qbItemMap[type] = val;
    });

    const qbAccountMap = {};
    ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'MISC'].forEach(type => {
      const val = document.getElementById('qb-wiz-acct-' + type)?.value?.trim();
      if (val) qbAccountMap[type] = val;
    });

    const qbDefaultTerms = document.getElementById('qb-wiz-terms')?.value?.trim() || '';
    const qbAccountsReceivable = document.getElementById('qb-wiz-ar-account')?.value?.trim() || '';

    const tenant = BillingState.tenantId;
    const tenantRef = doc(db, 'tenants', tenant);

    // 🔴 TASK-9280 — four owned keys, no `...existingSettings`, and the getDoc
    // that fed it is gone: nothing here needs the prior state, and reading it
    // only created the stale snapshot the spread then wrote back over the SMS
    // consent record (settings.smsInbox.optedOutOfAi) and every other key this
    // wizard does not own. `merge: true` preserves omitted keys — that is what
    // protects them, not the spread. See saveBillingSettings for the long note.
    //
    // ⚠️ PRE-EXISTING, NOT FIXED HERE, NOT INTRODUCED HERE — qbItemMap and
    // qbAccountMap are rebuilt from the UI and SKIP blank inputs, so clearing a
    // mapping is MEANT to remove it. openQBSetup renders all seven types and only
    // those seven, so the form is a complete picture of the map and a blank box
    // is an explicit "no mapping". But `merge: true` NEVER removes a map key that
    // is absent from the payload, so the cleared mapping stays live and keeps
    // posting that category to the old QuickBooks item/account while the toast
    // says saved. The pre-TASK-9280 spread behaved identically — merge recursed
    // into `qbItemMap` either way — so this narrowing neither causes nor worsens
    // it.
    //   ⚠️ The two clearing cases are NOT the same, and it is easy to state this
    //   gap too broadly (COD round 2 caught exactly that). Measured against real
    //   Firestore 2026-09-22: clearing EVERY input sends `qbItemMap: {}`, and an
    //   empty map is a LEAF in the field mask, so it really does replace the map
    //   and the mappings are gone. The gap is the PARTIAL case — clearing one
    //   input while another still has a value sends `{ EMPLOYEE: 'x' }`, which
    //   merges and leaves the cleared EQUIPMENT mapping live.
    //   THE FIX, and the precedent is already in this file: the QBO account-
    //   mapping save (~:4874, TASK-4831) uses
    //   `{ mergeFields: ['settings.qboAccountMapping'] }`, which replaces that one
    //   map WHOLESALE while leaving the rest of `settings` untouched, and still
    //   creates a thin doc. The same three-path mergeFields would fix this. Held
    //   back deliberately: saveQBSetup is the only writer of these maps, but the
    //   change alters what a QuickBooks export bills against, so it gets its own
    //   task + COD rather than riding along on a lost-write fix (same call
    //   TASK-9255 made on deleteAccessPin).
    // TASK-9337 -- snapshot the PRE-save values BEFORE the write: the tenant
    // onSnapshot (subscribeTenantSettings) overwrites AppState.settings from
    // the local write before this await resolves, so reading after would log
    // nothing. JSON copy so later mutation can't alias it.
    let _qbPrev = null;
    try {
      const _s = window.AppState?.settings || {};
      _qbPrev = JSON.parse(JSON.stringify({ qbItemMap: _s.qbItemMap ?? null, qbAccountMap: _s.qbAccountMap ?? null, qbDefaultTerms: _s.qbDefaultTerms ?? null, qbAccountsReceivable: _s.qbAccountsReceivable ?? null }));
    } catch (_) { _qbPrev = null; }
    await setDoc(tenantRef, {
      settings: { qbItemMap, qbAccountMap, qbDefaultTerms, qbAccountsReceivable }
    }, { merge: true });

    // TASK-9337 -- these maps decide which QuickBooks item/account every
    // invoice line posts to, and billing-module STAFF can now save them (not
    // just admins). Log old -> new on change (the workweek-flip lesson: a
    // money-governing setting changed with no trail). `from` is _qbPrev,
    // captured BEFORE the setDoc above; best-effort.
    try {
      const prev = _qbPrev || {};
      const next = { qbItemMap, qbAccountMap, qbDefaultTerms, qbAccountsReceivable };
      const changed = {};
      for (const k of Object.keys(next)) {
        const from = prev[k] === undefined ? null : prev[k];
        if (JSON.stringify(from) !== JSON.stringify(next[k])) changed[k] = { from, to: next[k] };
      }
      if (Object.keys(changed).length) window.logAudit?.('settings', 'SAVE_QB_SETUP', { changed });
    } catch (auditErr) {
      console.warn('[QB] Setup audit log failed:', auditErr);
    }

    // Update local state
    if (window.AppState?.settings) {
      window.AppState.settings.qbItemMap = qbItemMap;
      window.AppState.settings.qbAccountMap = qbAccountMap;
      window.AppState.settings.qbDefaultTerms = qbDefaultTerms;
      window.AppState.settings.qbAccountsReceivable = qbAccountsReceivable;
    }

    window.showToast?.('QuickBooks settings saved', 'success');
    closeQBSetup();
  } catch (err) {
    console.error('[QB] Error saving settings:', err);
    window.showToast?.('Error saving: ' + err.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save All Settings'; }
  }
}

// ============================================
// QB CUSTOMER MAPPING (Gemini + Manual)
// ============================================

function renderQBCustomerMapManual() {
  const container = document.getElementById('qb-wiz-customer-manual');
  if (!container) return;
  // Decision 2026-10-10 — internal customers are never invoiced: not listed,
  // unless one already carries a QuickBooks name (shown so it can be cleared).
  const _billable = _billableCustomers();
  const customers = window.PickerGroups.withoutInternal(_billable, _billable.filter(c => c.qbCustomerName).map(c => c.id));
  if (customers.length === 0) {
    container.innerHTML = '<p style="font-size:0.8rem;color:var(--text-secondary);">No CRM customers yet</p>';
    return;
  }
  container.innerHTML = `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 12px;align-items:center;max-height:200px;overflow-y:auto;padding:4px 0;">
      <span style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);">DD Customer</span>
      <span style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);">QB Name (leave blank to use DD name)</span>
      ${customers.map(c => `
        <span style="font-size:0.8rem;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" title="${(c.name || '').replace(/"/g, '&quot;')}">${escapeHtml(c.name) || '—'}</span>
        <input type="text" class="form-control qb-cust-map" data-cust-id="${c.id}" value="${(c.qbCustomerName || '').replace(/"/g, '&quot;')}" placeholder="${(c.name || '').replace(/"/g, '&quot;')}" style="font-size:0.8rem;padding:4px 8px;">
      `).join('')}
    </div>
    <button class="btn btn-sm btn-secondary" style="margin-top:8px;" onclick="window.BillingModule.saveQBCustomerMappings()">
      <span class="material-icons" style="font-size:14px;">save</span> Save Customer Mappings
    </button>
  `;
}

async function saveQBCustomerMappings() {
  const inputs = document.querySelectorAll('.qb-cust-map');
  if (inputs.length === 0) return;
  const btn = document.querySelector('[onclick*="saveQBCustomerMappings"]');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
  const tenant = BillingState.tenantId;
  let saved = 0;
  for (const input of inputs) {
    const custId = input.dataset.custId;
    const qbName = input.value.trim();
    if (!custId) continue;
    try {
      await setDoc(doc(db, 'tenants', tenant, 'customers', custId), { qbCustomerName: qbName || null }, { merge: true });
      const cust = BillingState.customers.find(c => c.id === custId);
      if (cust) cust.qbCustomerName = qbName || null;
      saved++;
    } catch (e) {
      console.warn('[QB] Error saving mapping for', custId, e);
    }
  }
  if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save Customer Mappings'; }
  window.showToast?.(`Saved ${saved} QB customer mapping${saved !== 1 ? 's' : ''}`, 'success');
}

// Normalize a company name for matching: lowercase, strip LLC/Inc/etc, remove punctuation
function normalizeCompanyName(name) {
  return (name || '').toLowerCase()
    .replace(/,?\s*(llc|inc|corp|ltd|co|company|services|service|group|enterprises|enterprise)\b\.?/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

// Extract parent customer names from IIF file (skip sub-jobs with colons)
function parseQBCustomerNames(text) {
  const names = [];
  for (const line of text.split('\n')) {
    const parts = line.split('\t');
    if (parts[0] === 'CUST' && parts[1]) {
      const name = parts[1].replace(/^"|"$/g, '').trim();
      if (!name.includes(':')) names.push(name);
    }
  }
  return names;
}

async function uploadQBCustomerList() {
  const fileInput = document.getElementById('qb-customer-file');
  const file = fileInput?.files?.[0];
  if (!file) return;

  const resultsDiv = document.getElementById('qb-wiz-customer-results');
  if (!resultsDiv) return;

  resultsDiv.innerHTML = `<div style="text-align:center;padding:20px;">
    <div style="width:40px;height:40px;border:3px solid var(--border-light);border-top-color:var(--brand-blue);border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 12px;"></div>
    <div style="font-weight:600;color:var(--text-primary);margin-bottom:4px;">Matching customers</div>
    <div class="qb-loading-msg" style="font-size:0.8rem;color:var(--text-secondary);">Parsing your QB file...</div>
  </div>`;

  try {
    // TASK-1383 — guarded shared reader (settles on every path; existing
    // catch below renders the failure).
    const text = await window.FileReadUtil.readAsText(file);

    // Decision 2026-10-10 — internal customers are never invoiced, so never matched to QuickBooks.
    const ddCustomers = window.PickerGroups.withoutInternal(_billableCustomers()).map(c => ({ id: c.id, name: c.name }));
    const qbNames = parseQBCustomerNames(text);

    if (qbNames.length === 0) {
      resultsDiv.innerHTML = '<p class="empty-state-small" style="font-size:0.85rem;color:var(--text-secondary);padding:12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.6;">upload_file</span>No customers found in uploaded file. Make sure it\'s a QB Customer List export (.iif).</p>';
      fileInput.value = '';
      return;
    }

    // Client-side first pass: normalized Map lookup
    const ddMap = new Map();
    for (const c of ddCustomers) {
      ddMap.set(normalizeCompanyName(c.name), c);
    }

    const matches = [];
    const unmatchedQB = [];

    for (const qbName of qbNames) {
      const norm = normalizeCompanyName(qbName);
      const exact = ddMap.get(norm);
      if (exact) {
        matches.push({ qbName, ddCustomerId: exact.id, ddCustomerName: exact.name, confidence: 1.0 });
        continue;
      }
      // Substring match — check if normalized QB name contains or is contained by a DD name
      let best = null;
      for (const c of ddCustomers) {
        const ddNorm = normalizeCompanyName(c.name);
        if (ddNorm && norm && (ddNorm.includes(norm) || norm.includes(ddNorm))) {
          const lenRatio = Math.min(norm.length, ddNorm.length) / Math.max(norm.length, ddNorm.length);
          if (!best || lenRatio > best.confidence) {
            best = { qbName, ddCustomerId: c.id, ddCustomerName: c.name, confidence: Math.round(lenRatio * 100) / 100 };
          }
        }
      }
      if (best && best.confidence >= 0.5) {
        matches.push(best);
      } else {
        unmatchedQB.push(qbName);
      }
    }

    const msgEl = resultsDiv.querySelector('.qb-loading-msg');

    // If there are unmatched names, send just those to Gemini for fuzzy matching
    if (unmatchedQB.length > 0) {
      if (msgEl) msgEl.textContent = `Matched ${matches.length} instantly. Checking ${unmatchedQB.length} remaining with Smart Tools...`;
      try {
        const { functions, httpsCallable } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
        const parseQBList = httpsCallable(functions, 'parseQBCustomerList', { timeout: 120000 });
        const result = await parseQBList({ fileContent: unmatchedQB.join('\n'), ddCustomers, tenantId: BillingState.tenantId || window.AppState?.tenant });
        const geminiMatches = result.data?.matches || [];
        const geminiUnmatched = result.data?.unmatchedQB || [];
        matches.push(...geminiMatches);
        // Replace unmatchedQB with only what Gemini couldn't match
        unmatchedQB.length = 0;
        unmatchedQB.push(...geminiUnmatched);
      } catch (err) {
        console.warn('[QB] Gemini fallback failed, showing partial results:', err.message);
        // Keep going with client-side matches only
      }
    }

    console.log(`[QB] Client matched ${matches.length}, unmatched ${unmatchedQB.length}`);
    renderQBMatchResults(matches, unmatchedQB, []);
  } catch (err) {
    console.error('[QB] Upload matching error:', err);
    resultsDiv.innerHTML = `<p style="color:var(--error-color);font-size:0.85rem;">Error matching customers: ${err.message || 'Unknown error'}. You can map them manually below.</p>`;
  }

  fileInput.value = '';
}

function renderQBMatchResults(matches, unmatchedQB, unmatchedDD) {
  const resultsDiv = document.getElementById('qb-wiz-customer-results');
  if (!resultsDiv) return;

  if (matches.length === 0 && unmatchedQB.length === 0) {
    resultsDiv.innerHTML = '<p class="empty-state-small" style="font-size:0.85rem;color:var(--text-secondary);padding:12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.6;">upload_file</span>No customers found in uploaded file.</p>';
    return;
  }

  // TASK-9548 — vendors and internal customers dropped (decision 2026-10-10:
  // Shop/Office are never invoiced, so a QuickBooks name has nothing to carry).
  const custOptions = _billingSalesCustomerOptionsHtml(null);

  let html = '<div style="margin-bottom:12px;">';
  html += `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">${matches.length} matched, ${unmatchedQB.length} unmatched from QB</div>`;

  // Matches table
  if (matches.length > 0) {
    html += '<div style="max-height:200px;overflow-y:auto;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;">';
    html += '<table style="width:100%;font-size:0.8rem;border-collapse:collapse;">';
    html += '<tr style="background:var(--bg-secondary);position:sticky;top:0;z-index:1;"><th style="padding:6px 8px;text-align:left;">QB Name</th><th style="padding:6px 8px;text-align:left;">DD Match</th><th style="padding:6px 8px;text-align:center;width:60px;">Score</th></tr>';
    for (const m of matches) {
      const conf = m.confidence || 0;
      const color = conf >= 0.8 ? '#16a34a' : conf >= 0.5 ? '#d97706' : '#dc2626';
      html += `<tr style="border-top:1px solid var(--border-light);">`;
      html += `<td style="padding:6px 8px;">${(m.qbName || '').replace(/</g, '&lt;')}</td>`;
      html += `<td style="padding:6px 8px;"><select class="form-control qb-match-select" data-qb-name="${(m.qbName || '').replace(/"/g, '&quot;')}" style="font-size:0.8rem;padding:2px 6px;">
        <option value="">-- Skip --</option>${custOptions}
      </select></td>`;
      html += `<td style="padding:6px 8px;text-align:center;"><span style="color:${color};font-weight:600;">${Math.round(conf * 100)}%</span></td>`;
      html += '</tr>';
      // Pre-select the matched customer
      setTimeout(() => {
        const sel = document.querySelector(`.qb-match-select[data-qb-name="${CSS.escape(m.qbName)}"]`);
        if (sel && m.ddCustomerId) sel.value = m.ddCustomerId;
      }, 0);
    }
    html += '</table></div>';
  }

  // Unmatched QB customers
  if (unmatchedQB.length > 0) {
    html += '<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Unmatched QB customers:</div>';
    html += '<div style="max-height:100px;overflow-y:auto;margin-bottom:8px;">';
    for (const name of unmatchedQB) {
      html += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
        <span style="font-size:0.8rem;min-width:120px;">${(name || '').replace(/</g, '&lt;')}</span>
        <select class="form-control qb-match-select" data-qb-name="${(name || '').replace(/"/g, '&quot;')}" style="font-size:0.8rem;padding:2px 6px;flex:1;">
          <option value="">-- Skip --</option>${custOptions}
        </select>
      </div>`;
    }
    html += '</div>';
  }

  html += `<button class="btn btn-sm btn-primary" onclick="window.BillingModule.applyQBMatches()">
    <span class="material-icons" style="font-size:14px;">check</span> Apply Matches
  </button>`;
  html += '</div>';
  resultsDiv.innerHTML = html;
}

async function applyQBMatches() {
  const selects = document.querySelectorAll('.qb-match-select');
  const btn = document.querySelector('[onclick*="applyQBMatches"]');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Applying...'; }
  const tenant = BillingState.tenantId;
  let applied = 0;

  for (const sel of selects) {
    const qbName = sel.dataset.qbName;
    const custId = sel.value;
    if (!qbName || !custId) continue;

    try {
      await setDoc(doc(db, 'tenants', tenant, 'customers', custId), { qbCustomerName: qbName }, { merge: true });
      const cust = BillingState.customers.find(c => c.id === custId);
      if (cust) cust.qbCustomerName = qbName;
      applied++;
    } catch (e) {
      console.warn('[QB] Error applying match:', qbName, e);
    }
  }

  window.showToast?.(`Saved ${applied} QB customer mapping${applied !== 1 ? 's' : ''} — ready for export`, 'success');
  // Refresh the manual map to show updated values
  renderQBCustomerMapManual();
  // Clear match results and show confirmation
  const resultsDiv = document.getElementById('qb-wiz-customer-results');
  if (resultsDiv) resultsDiv.innerHTML = `<div style="padding:12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:var(--radius-md);font-size:0.85rem;color:#166534;margin-bottom:12px;">
    <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">check_circle</span>
    ${applied} customer name${applied !== 1 ? 's' : ''} mapped and saved. You can edit them below or re-upload anytime.
  </div>`;
}

// ============================================
// QB ITEM LIST MATCHING (Gemini)
// ============================================

async function uploadQBItemList() {
  const fileInput = document.getElementById('qb-item-file');
  const file = fileInput?.files?.[0];
  if (!file) return;

  const resultsDiv = document.getElementById('qb-wiz-item-results');
  if (!resultsDiv) return;

  resultsDiv.innerHTML = `<div style="text-align:center;padding:20px;">
    <div style="width:40px;height:40px;border:3px solid var(--border-light);border-top-color:var(--brand-blue);border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 12px;"></div>
    <div style="font-weight:600;color:var(--text-primary);margin-bottom:4px;">Matching items with Smart Tools</div>
    <div class="qb-loading-msg" style="font-size:0.8rem;color:var(--text-secondary);">Parsing your QB file...</div>
  </div>`;

  try {
    // TASK-1383 — guarded shared reader (settles on every path; existing
    // catch below renders the failure).
    const text = await window.FileReadUtil.readAsText(file);

    // Update loading message
    const msgEl = resultsDiv.querySelector('.qb-loading-msg');
    if (msgEl) msgEl.textContent = 'Matching items to billing line types... this may take 10-15 seconds.';

    const { functions, httpsCallable } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
    const parseQBItems = httpsCallable(functions, 'parseQBItemList', { timeout: 120000 });
    const result = await parseQBItems({ fileContent: text, tenantId: BillingState.tenantId || window.AppState?.tenant });
    const { matches, unmatchedQB } = result.data;

    renderQBItemMatchResults(matches || [], unmatchedQB || []);
  } catch (err) {
    console.error('[QB] Item list matching error:', err);
    resultsDiv.innerHTML = `<p style="color:var(--error-color);font-size:0.85rem;">Error matching items: ${err.message || 'Unknown error'}. You can map them manually below.</p>`;
  }

  fileInput.value = '';
}

function renderQBItemMatchResults(matches, unmatchedQB) {
  const resultsDiv = document.getElementById('qb-wiz-item-results');
  if (!resultsDiv) return;

  if (matches.length === 0 && unmatchedQB.length === 0) {
    resultsDiv.innerHTML = '<p class="empty-state-small" style="font-size:0.85rem;color:var(--text-secondary);padding:12px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.6;">upload_file</span>No items found in uploaded file.</p>';
    return;
  }

  const typeLabels = { EMPLOYEE: 'Labor', EQUIPMENT: 'Equipment', DOWNTIME: 'Downtime', STANDBY: 'Standby', PERMIT: 'Load Permits', FIELD_ITEM: 'Field Items', MISC: 'Misc' };
  const typeOptions = Object.entries(typeLabels).map(([k, v]) => `<option value="${k}">${v}</option>`).join('');

  let html = '<div style="margin-bottom:12px;">';
  html += `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">${matches.length} matched, ${unmatchedQB.length} unmatched</div>`;

  if (matches.length > 0) {
    html += '<div style="max-height:200px;overflow-y:auto;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;">';
    html += '<table style="width:100%;font-size:0.8rem;border-collapse:collapse;">';
    html += '<tr style="background:var(--bg-secondary);position:sticky;top:0;z-index:1;"><th style="padding:6px 8px;text-align:left;">QB Item</th><th style="padding:6px 8px;text-align:left;">DD Type</th><th style="padding:6px 8px;text-align:center;width:60px;">Score</th></tr>';
    for (const m of matches) {
      const conf = m.confidence || 0;
      const color = conf >= 0.8 ? '#16a34a' : conf >= 0.5 ? '#d97706' : '#dc2626';
      html += `<tr style="border-top:1px solid var(--border-light);" title="${(m.reason || '').replace(/"/g, '&quot;')}">`;
      html += `<td style="padding:6px 8px;">${(m.qbItemName || '').replace(/</g, '&lt;')}</td>`;
      html += `<td style="padding:6px 8px;"><select class="form-control qb-item-match-select" data-qb-item="${(m.qbItemName || '').replace(/"/g, '&quot;')}" style="font-size:0.8rem;padding:2px 6px;">
        <option value="">-- Skip --</option>${typeOptions}
      </select></td>`;
      html += `<td style="padding:6px 8px;text-align:center;"><span style="color:${color};font-weight:600;">${Math.round(conf * 100)}%</span></td>`;
      html += '</tr>';
      // Pre-select matched type
      setTimeout(() => {
        const sel = document.querySelector(`.qb-item-match-select[data-qb-item="${CSS.escape(m.qbItemName)}"]`);
        if (sel && m.ddLineType) sel.value = m.ddLineType;
      }, 0);
    }
    html += '</table></div>';
  }

  html += `<button class="btn btn-sm btn-primary" onclick="window.BillingModule.applyQBItemMatches()">
    <span class="material-icons" style="font-size:14px;">check</span> Apply Item Mappings
  </button>`;
  html += '</div>';
  resultsDiv.innerHTML = html;
}

function applyQBItemMatches() {
  const selects = document.querySelectorAll('.qb-item-match-select');
  // Group by DD type — if multiple QB items map to same type, use the first one
  // But more importantly, set the QB item name input to the QB name for each type
  const typeToQBName = {};

  for (const sel of selects) {
    const qbItem = sel.dataset.qbItem;
    const ddType = sel.value;
    if (!qbItem || !ddType) continue;
    // First QB item for each DD type wins (user can edit manually after)
    if (!typeToQBName[ddType]) {
      typeToQBName[ddType] = qbItem;
    }
  }

  // Fill in the manual inputs
  let applied = 0;
  for (const [type, qbName] of Object.entries(typeToQBName)) {
    const input = document.getElementById('qb-wiz-item-' + type);
    if (input) {
      input.value = qbName;
      applied++;
    }
  }

  window.showToast?.(`Applied ${applied} item mapping${applied !== 1 ? 's' : ''}. Review below, then Save.`, 'success');
  // Clear results
  const resultsDiv = document.getElementById('qb-wiz-item-results');
  if (resultsDiv) resultsDiv.innerHTML = '';
}

// ============================================
// COMPANY INFO — MULTI-CONTACT ROWS
// ============================================
const BILLING_PHONE_LABELS = ['Main', 'Office', 'Dispatch', 'Billing', 'Fax'];
const BILLING_EMAIL_LABELS = ['General', 'Billing', 'Operations', 'Invoicing', 'Dispatch'];
const BILLING_ADDRESS_LABELS = ['Main', 'Billing', 'Mailing', 'Yard'];
let _billingPhoneCounter = 0;
let _billingEmailCounter = 0;
let _billingAddrCounter = 0;

function addBillingPhoneRow(label, phone) {
  const container = document.getElementById('billing-co-phones');
  if (!container) return;
  if (container.querySelectorAll('.billing-contact-row').length >= 5) {
    window.showToast?.('Maximum 5 phone numbers', 'error');
    return;
  }
  const idx = _billingPhoneCounter++;
  const row = document.createElement('div');
  row.className = 'billing-contact-row';
  row.id = `billing-phone-${idx}`;
  row.innerHTML = `
    <select class="form-control bc-phone-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${BILLING_PHONE_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="tel" class="form-control bc-phone-value" placeholder="(701) 555-1234" value="${escapeHtml(phone || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="window.BillingModule.removeBillingPhoneRow('billing-phone-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
}

function removeBillingPhoneRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function addBillingEmailRow(label, email) {
  const container = document.getElementById('billing-co-emails');
  if (!container) return;
  if (container.querySelectorAll('.billing-contact-row').length >= 5) {
    window.showToast?.('Maximum 5 email addresses', 'error');
    return;
  }
  const idx = _billingEmailCounter++;
  const row = document.createElement('div');
  row.className = 'billing-contact-row';
  row.id = `billing-email-${idx}`;
  row.innerHTML = `
    <select class="form-control bc-email-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${BILLING_EMAIL_LABELS.map(l => `<option value="${l}" ${l === (label || 'General') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="email" class="form-control bc-email-value" placeholder="email@company.com" value="${escapeHtml(email || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="window.BillingModule.removeBillingEmailRow('billing-email-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
}

function removeBillingEmailRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function addBillingAddressRow(label, address) {
  const container = document.getElementById('billing-co-addresses');
  if (!container) return;
  if (container.querySelectorAll('.billing-contact-row').length >= 5) {
    window.showToast?.('Maximum 5 addresses', 'error');
    return;
  }
  const idx = _billingAddrCounter++;
  const row = document.createElement('div');
  row.className = 'billing-contact-row';
  row.id = `billing-addr-${idx}`;
  row.innerHTML = `
    <select class="form-control bc-addr-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${BILLING_ADDRESS_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="text" class="form-control bc-addr-value" placeholder="123 Main St, City, ST 12345" value="${escapeHtml(address || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="window.BillingModule.removeBillingAddressRow('billing-addr-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
  const addrInput = row.querySelector('.bc-addr-value');
  if (addrInput && window.attachAddressAutocomplete) window.attachAddressAutocomplete(addrInput);
}

function removeBillingAddressRow(rowId) {
  document.getElementById(rowId)?.remove();
}

// ============================================
// MODAL HANDLERS
// ============================================
function setupModals() {
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        const hasFormInputs = modal.querySelector('input, select, textarea');
        if (hasFormInputs) return;
        modal.classList.add('hidden');
      }
    });
  });
}

// ============================================
// DATA SUBSCRIPTIONS
// ============================================

// TASK-857: tickets are the fastest-growing collection (1+/job/day). A permanent
// live subscription to every ticket a tenant ever created streamed tens of
// thousands of docs on every mutation for older tenants. The listener is bounded
// to a rolling window by default; views that legitimately need older tickets
// (open A/R aging, paid / written-off / archived history, search, or an old
// daily-date) upgrade it to the full set on demand via ensureFullTicketHistory().
const BILLING_TICKET_WINDOW_DAYS = 180;   // covers net-90 A/R terms + collection lag
const BILLING_TICKET_LIMIT = 8000;        // backstop cap; date window is the real bound
let _billingTicketsUnsub = null;
let _billingTicketTenant = null;

// ── TASK-1245: prioritized subscription phases ("Loading billing data… sure
// taking its sweet time" — Chris, on LTE) ────────────────────────────────────
// MEASURED on live TITS 2026-08-06 (admin SDK, read-only): the AR/AP mount
// opens SEVEN concurrent listeners totalling 2.66MB —
//   tickets 1.27MB (752 docs, bounded to the 180d window)
//   jobs    0.88MB (719 docs, UNBOUNDED)   jobDays 0.31MB (835 docs, UNBOUNDED)
//   people  0.13MB   customers 0.03MB   timeOff 0.03MB   activeClock ~0
// But first paint is gated on exactly ONE of them — `_ticketsLoaded`
// (renderBillingView calendar-mode spinner + renderTicketsList skeleton). The
// other six contribute nothing to that first frame yet take ~52% of the
// bandwidth away from the query the spinner is actually waiting on. On a fat
// pipe that's invisible; on LTE it roughly doubles time-to-first-paint.
//
// The fix is SCHEDULING, not schema: attach the paint-critical listeners
// (tickets + customers + active clock ≈ 1.30MB) immediately, and the remaining
// four as soon as the tickets snapshot lands — or after a hard cap, so an
// offline / permission-denied tickets query can never strand them. Every
// listener still attaches with the byte-identical query; none is dropped,
// narrowed, or field-projected, so no view can read less data than before —
// some of it just arrives a beat later, which every consumer already handles
// (they're async onSnapshot callbacks that each re-render on arrival, and the
// two paths that genuinely need jobs early — `_resolveFallbackPoNumber`'s
// `_jobsLoaded` guard and `_openTicketDeepLinkWhenReady` — are flushed
// synchronously via _flushSecondarySubs() so neither regresses).
const BILLING_SECONDARY_SUBS_MAX_DELAY_MS = 4000;
let _billingMountGen = 0;             // bumped per mount + on cleanup; a stale-gen phase 2 no-ops
let _billingSecondaryTimer = null;
// SINGLE source of truth for "phase 2 is still owed": { tenant, gen } while
// armed, null once it has run (or been cancelled). Deliberately not a separate
// `done` boolean — two flags could disagree, and the failure mode of the
// disagreement is double-attached listeners.
let _billingPendingSecondary = null;

// Arm phase 2. Fires on the first tickets snapshot (see _subscribeTickets) or
// at BILLING_SECONDARY_SUBS_MAX_DELAY_MS, whichever comes first.
function _armSecondarySubs(tenant, gen) {
  if (_billingSecondaryTimer) clearTimeout(_billingSecondaryTimer);
  _billingPendingSecondary = { tenant, gen };
  _billingSecondaryTimer = setTimeout(() => _runSecondarySubs(tenant, gen), BILLING_SECONDARY_SUBS_MAX_DELAY_MS);
}

// Run phase 2 NOW if it's still owed. Safe to call from anywhere that needs
// jobs/jobDays/people/timeOff before the natural trigger.
function _flushSecondarySubs() {
  const p = _billingPendingSecondary;
  if (p) _runSecondarySubs(p.tenant, p.gen);
}

// Disarm phase 2 (view unmounting, or it just ran).
function _cancelSecondarySubs() {
  if (_billingSecondaryTimer) { clearTimeout(_billingSecondaryTimer); _billingSecondaryTimer = null; }
  _billingPendingSecondary = null;
}

// TASK-9646 S3b — a ticket's header money (grandTotal / subtotal /
// taxableSubtotal / taxAmount) is resolved HERE, at the load layer, against the
// server-written ticketMoney mirror (shared/ticket-money-view.js — the same rule
// the functions readers use), so every downstream `t.grandTotal` reads it
// unchanged. No mirror / a stale one / closed books without a frozen snapshot /
// a login the rules deny (not billing / bi / owner) -> the ticket's own fields,
// exactly as before. Read-only: nothing here writes.
let _billingMoneyUnsub = null;
let _billingMoneyById = new Map();
// Perf budget (TASK-9646 S3b-0 lazy lane): shared/ticket-money-view.js is a lazy
// chunk, loaded when the money listener starts (after the first tickets paint,
// TASK-1245) or a single-ticket read refreshes its mirror. INVARIANT (COD):
// _billingMoneyById only ever holds mirrors once the helper is loaded — every
// writer of a non-empty map waits for it — so before it lands the map is empty
// and these two bindings (the helper's own "no mirror" answer,
// withTicketMoney(t, null) === t) are exactly the no-mirror state, emitters
// included. Resolves true / false, never rejects: a failed chunk load = mirror
// unavailable (the tickets' own totals); the next snapshot / fresh read retries.
let _tmvWithTicketMoney = (t) => t;
let _TMV_FIELDS = [];
let _tmvReady = false;
let _tmvLoad = null;
let _billingMoneyReplay = null; // the live listener's "apply the snapshot a failed load held back"
function _loadTicketMoneyView() {
  if (!_tmvLoad) {
    _tmvLoad = __ddChunkWrap(import('../shared/ticket-money-view.js?v=9646310')).then((m) => {
      _tmvWithTicketMoney = m.withTicketMoney;
      _TMV_FIELDS = m.TICKET_MONEY_FIELDS;
      _tmvReady = true;
      // COD r2 — a retry that succeeds (a fresh ticket read, a later snapshot)
      // also applies the snapshot an earlier failed load held back.
      try { if (_billingMoneyReplay) _billingMoneyReplay(); } catch (e) { console.error('[Billing] TASK-9646 ticketMoney replay failed:', e); }
      return true;
    }, (e) => {
      _tmvLoad = null;
      console.warn('[Billing] TASK-9646 ticket-money helper did not load, showing the tickets\' own totals:', e?.message || e);
      return false;
    });
  }
  return _tmvLoad;
}
// resolved ticket -> its OWN stored money keys: the stale check compares the
// mirror with the ticket as stored, never with an already-resolved copy.
const _billingHeaderMoney = new WeakMap();

function _withTicketMoney(t) {
  if (!t || typeof t !== 'object' || !t.id) return t;
  const out = _tmvWithTicketMoney(t, _billingMoneyById.get(t.id) || null);
  if (out !== t) {
    const h = {};
    for (const k of _TMV_FIELDS) if (Object.prototype.hasOwnProperty.call(t, k)) h[k] = t[k];
    _billingHeaderMoney.set(out, h);
  }
  return out;
}

// The ticket as STORED: an already-resolved copy gets its own header money back.
function _storedTicketMoney(cur) {
  const h = cur && _billingHeaderMoney.get(cur);
  if (!h) return cur;
  const raw = { ...cur };
  for (const k of _TMV_FIELDS) { if (Object.prototype.hasOwnProperty.call(h, k)) raw[k] = h[k]; else delete raw[k]; }
  return raw;
}

// COD (rebase onto integ/2124, Code Reviewer) — money-EMITTING browser paths
// (the invoice page and its inline QuickBooks .iif, QuickBooks CSV / IIF,
// payment IIF, OpenInvoice XML) follow the server emitters' rule: only a
// frozen `billed` snapshot replaces the ticket's stored header money, never
// the live open-books mirror the list shows. Read-only copy; never written back.
function _emitTicketMoney(t) {
  if (!t || typeof t !== 'object') return t;
  const raw = _storedTicketMoney(t);
  if (!raw.id) return raw;
  return _tmvWithTicketMoney(raw, _billingMoneyById.get(raw.id) || null, { billedOnly: true });
}

// Re-resolve the loaded tickets after a mirror change. True when a shown amount moved.
function _reapplyTicketMoney() {
  let moved = false;
  const list = BillingState.tickets || [];
  for (let i = 0; i < list.length; i++) {
    const cur = list[i];
    const raw = _storedTicketMoney(cur);
    const next = _withTicketMoney(raw);
    if (next && _TMV_FIELDS.some(k => next[k] !== cur[k])) { list[i] = next; moved = true; }
  }
  return moved;
}

async function _refreshTicketMoney(tenant, ticketId) {
  // COD r1/r2 (Code Reviewer) — a read that lands after unmount / a tenant
  // switch must not seed the next mount's map (keys are bare ticket ids); within
  // the same mount it lands in the CURRENT map even if a snapshot replaced it.
  const gen = _billingMountGen;
  try {
    const [s, ready] = await Promise.all([getDoc(doc(db, 'tenants', tenant, 'ticketMoney', ticketId)), _loadTicketMoneyView()]);
    if (!ready || gen !== _billingMountGen || _billingTicketTenant !== tenant) return;
    if (s.exists()) _billingMoneyById.set(ticketId, s.data()); else _billingMoneyById.delete(ticketId);
  } catch (_) { /* not a money reader, or offline: the ticket's own fields */ }
}

// Same window as the tickets listener (by workDate, 2 days of slack: `date`
// and workDate can straddle midnight); full history = the whole collection.
function _subscribeTicketMoney(tenant, full) {
  if (_billingMoneyUnsub) { _billingMoneyUnsub(); _billingMoneyUnsub = null; }
  // Snapshots apply in arrival order; one that is still waiting for the helper
  // chunk is dropped if a newer snapshot / error came first, or this listener
  // was torn down (unmount / re-subscribe) while it waited.
  let seq = 0;
  let live = true;
  let held = null; // the newest snapshot a failed chunk load could not apply (COD r2)
  const apply = (n, m) => {
    if (!live || n !== seq) return;
    _billingMoneyById = m;
    if (BillingState._ticketsLoaded && _reapplyTicketMoney()) renderBillingView();
  };
  const applyWhenReady = (n, m) => _loadTicketMoneyView().then((ok) => {
    if (ok) { apply(n, m); return; }
    if (n === seq) held = { n, m };
    apply(n, new Map());
  }).catch((e) => console.error('[Billing] TASK-9646 ticketMoney apply failed:', e));
  const replay = () => {
    if (!live || !held || held.n !== seq) return;
    const h = held; held = null;
    apply(h.n, h.m);
  };
  _billingMoneyReplay = replay;
  _loadTicketMoneyView(); // prefetch: the first snapshot usually finds the helper loaded
  const col = collection(db, 'tenants', tenant, 'ticketMoney');
  let q = col;
  if (!full) {
    const d = new Date(Date.now() - (BILLING_TICKET_WINDOW_DAYS + 2) * 24 * 60 * 60 * 1000);
    q = query(col, where('workDate', '>=', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`));
  }
  const unsub = onSnapshot(q, (snap) => {
    const m = new Map();
    snap.docs.forEach(d => m.set(d.id, d.data()));
    const n = ++seq;
    held = null;
    if (_tmvReady || !m.size) apply(n, m);
    else applyWhenReady(n, m);
  }, (error) => {
    console.warn('[Billing] TASK-9646 ticketMoney unavailable, showing the tickets\' own totals:', error?.code || error?.message || error);
    held = null;
    apply(++seq, new Map());
  });
  _billingMoneyUnsub = () => { live = false; held = null; if (_billingMoneyReplay === replay) _billingMoneyReplay = null; unsub(); };
}

function _mapTicketDoc(d) {
  const data = d.data();
  return _withTicketMoney({
    id: d.id,
    ...data,
    date: data.workDate
      ? new Date(data.workDate + 'T00:00:00')
      : (data.date?.toDate ? data.date.toDate() : new Date(data.date))
  });
}

function _subscribeTickets(tenant, full) {
  _billingTicketTenant = tenant;
  if (_billingTicketsUnsub) { _billingTicketsUnsub(); _billingTicketsUnsub = null; }
  // TASK-9646 S3b — the money listener attaches after the FIRST tickets snapshot
  // (TASK-1245: nothing competes with the paint-critical query; the first paint
  // shows the tickets' own fields, the mirror re-resolves when it lands).
  if (_billingMoneyUnsub) { _billingMoneyUnsub(); _billingMoneyUnsub = null; }
  const _startMoney = () => { if (!_billingMoneyUnsub && _billingTicketTenant === tenant) _subscribeTicketMoney(tenant, full); };
  let ticketsQuery;
  if (full) {
    // Full history — every non-deleted ticket, ordered by date desc.
    ticketsQuery = query(
      collection(db, 'tenants', tenant, 'tickets'),
      orderBy('date', 'desc')
    );
  } else {
    // Bounded live window. All app-created tickets store `date` as a Timestamp
    // (clock.js findOrCreateTicket + billing.js create paths), and the prior
    // unbounded query already used orderBy('date') — which itself excludes any
    // date-less doc — so the date floor drops no doc that wasn't already excluded.
    const cutoff = Timestamp.fromDate(new Date(Date.now() - BILLING_TICKET_WINDOW_DAYS * 24 * 60 * 60 * 1000));
    ticketsQuery = query(
      collection(db, 'tenants', tenant, 'tickets'),
      where('date', '>=', cutoff),
      orderBy('date', 'desc'),
      limit(BILLING_TICKET_LIMIT)
    );
  }
  _billingTicketsUnsub = onSnapshot(ticketsQuery, (snapshot) => {
    BillingState.tickets = snapshot.docs.map(_mapTicketDoc).filter(t => t.status !== 'deleted');
    BillingState._ticketsLoaded = true;
    // TASK-1245 — the gating query is done, so the pipe is free: bring up the
    // non-paint-critical listeners now. No-ops once they're already up (so the
    // ensureFullTicketHistory() re-subscribe doesn't re-attach them).
    _flushSecondarySubs();
    _startMoney();
    renderBillingView();
  }, (error) => {
    // TASK-368 pattern — flip _ticketsLoaded on error too so the skeleton
    // doesn't stick forever on a permission-denied / network-permanent
    // failure. Falls through to the existing empty-state render PLUS a
    // toast so admins don't misread the empty list as "my tickets are
    // gone" (UX Lead COD catch).
    console.error('[Billing] Error loading tickets:', error);
    BillingState._ticketsLoaded = true;
    // TASK-1245 — a permission-denied / permanently-failed tickets query must
    // NOT strand the other listeners. Same reason _ticketsLoaded flips here.
    _flushSecondarySubs();
    if (typeof window.showToast === 'function') {
      window.showToast('Error loading tickets — check your connection and refresh.', 'error');
    }
    renderBillingView();
  });
}

// Upgrade the bounded ticket listener to the full history (one-time per view
// session). Called when the user opens a view that legitimately needs older
// tickets: open A/R (invoiced & unpaid), paid, written-off, archived, search,
// or the explicit "Load older tickets" button.
function ensureFullTicketHistory() {
  if (BillingState._ticketsFullHistory) return;
  BillingState._ticketsFullHistory = true;
  if (_billingTicketTenant) {
    BillingState._ticketsLoaded = false;  // show skeleton while the wider set loads
    _subscribeTickets(_billingTicketTenant, true);
    renderBillingView();
  }
}

function loadOlderTickets() {
  ensureFullTicketHistory();
}

function _loadOlderTicketsFooterHtml() {
  if (BillingState._ticketsFullHistory) return '';
  return `<div class="billing-load-older" style="text-align:center;padding:16px 12px;">
      <button class="btn btn-secondary" onclick="window.BillingModule.loadOlderTickets()">
        <span class="material-icons">history</span> Load older tickets
      </button>
      <div style="color:var(--text-secondary);font-size:0.8rem;margin-top:6px;">Showing the last ${BILLING_TICKET_WINDOW_DAYS} days. Load full history to see older tickets.</div>
    </div>`;
}

// TASK-857: true when a date predates the bounded live window (and we haven't
// already loaded full history) — used by the daily-date entry points to pull
// history on demand so navigating to an old date never shows a false "empty".
function _isBeforeTicketWindow(d) {
  if (BillingState._ticketsFullHistory) return false;
  if (!(d instanceof Date) || isNaN(d.getTime())) return false;
  return d.getTime() < (Date.now() - BILLING_TICKET_WINDOW_DAYS * 24 * 60 * 60 * 1000);
}

// TASK-857: with the live listener bounded to a rolling window, a ticket id may
// legitimately not be in BillingState.tickets (out-of-window, or a just-created
// arbitrary-date ticket the listener hasn't surfaced). Fall back to a direct
// getDoc so money paths (recordRevenueEntry) and open paths (openReviewTicket)
// never silently no-op on an id that exists in Firestore but not in the window.
// TASK-9735 — server read for the after-save re-hydrate; refreshes the cache
// entry too. Falls back to the cached copy if the read fails.
async function _fetchTicketFresh(ticketId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  if (tenant) {
    try {
      const [snap] = await Promise.all([getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId)), _refreshTicketMoney(tenant, ticketId)]);
      if (snap.exists()) {
        const fresh = _mapTicketDoc(snap);
        const idx = BillingState.tickets.findIndex(t => t.id === ticketId);
        if (idx >= 0) BillingState.tickets[idx] = fresh;
        return fresh;
      }
    } catch (e) {
      console.warn('[Billing] TASK-9735 fresh ticket read failed, using cache:', e?.message || e);
    }
  }
  return _fetchTicketById(ticketId);
}

async function _fetchTicketById(ticketId) {
  const local = BillingState.tickets.find(t => t.id === ticketId);
  if (local) return local;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  if (!tenant) return null;
  try {
    const [snap] = await Promise.all([getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId)), _refreshTicketMoney(tenant, ticketId)]);
    if (snap.exists()) return _mapTicketDoc(snap);
  } catch (e) {
    console.error('[Billing] _fetchTicketById failed:', e);
  }
  return null;
}

// TASK-1245 — PHASE 1: only what the first paint is gated on. Everything else
// moves to _runSecondarySubs() so it can't steal bandwidth from the tickets
// query the spinner is waiting on. Keep this list minimal; if you add a
// listener here, justify why the FIRST FRAME cannot render without it.
function subscribeToData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
  const gen = _billingMountGen;

  // Arm phase 2 before the tickets listener attaches — _subscribeTickets'
  // snapshot handler is what normally fires it, and on a warm Firestore cache
  // that callback can run synchronously-ish.
  _armSecondarySubs(tenant, gen);

  // Subscribe to tickets — bounded live window by default (TASK-857).
  _subscribeTickets(tenant, BillingState._ticketsFullHistory === true);

  // Subscribe to customers — paint-critical (ticket cards + calendar cells show
  // customer names) and tiny (0.03MB on the largest measured tenant).
  const customersQuery = query(
    collection(db, 'tenants', tenant, 'customers'),
    orderBy('name')
  );
  const unsubCustomers = onSnapshot(customersQuery, (snapshot) => {
    BillingState.customers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    updateCustomerDropdown();
    // TASK-9588 — the Tickets view reads customer docs too (the per-customer
    // "No ticket emails" flag drives the row icon, the Unsent count and the
    // Paper ticket badge), so a flip must repaint it without a reload.
    if (BillingState.viewMode === 'calendar' || BillingState.viewMode === 'tickets') renderBillingView();
  }, (error) => {
    console.error('[Billing] Error loading customers:', error);
  });
  BillingState.unsubscribers.push(unsubCustomers);

  // Subscribe to active clock entries (clockOut == null) to show "Active" badge only on tickets with someone currently clocked in
  const activeClockQuery = query(
    collection(db, 'tenants', tenant, 'clockEntries'),
    where('status', '==', 'active')
  );
  BillingState.activeClockState = null; // TASK-9988 S1 — unknown until the first snapshot
  let _activeClockSeen = false;
  const unsubActiveClock = onSnapshot(activeClockQuery, { includeMetadataChanges: true }, (snapshot) => {
    // TASK-9988 S1 — 'live' only once the SERVER answered: a cached snapshot
    // can miss a clock-in from the last few seconds.
    BillingState.activeClockState = snapshot.metadata?.fromCache ? (BillingState.activeClockState === 'live' ? 'live' : null) : 'live';
    // includeMetadataChanges also fires on sync-status-only flips; skip the
    // (heavy) rebuild + render when no entry actually changed (review r3).
    if (_activeClockSeen && snapshot.docChanges().length === 0) return;
    _activeClockSeen = true;
    BillingState.activeTicketIds = new Set();
    BillingState.activeCustomerNames = new Set(); // For Shop/ticketless clock-ins
    BillingState.activeJobIds = new Set(); // For job-level Active badge even without ticketId
    BillingState.activeCustomerIds = new Set(); // For customer-level matching by ID
    BillingState.activeClockEntries = []; // Store full active entries for live hour calc
    for (const d of snapshot.docs) {
      const data = d.data();
      if (data.ticketId) BillingState.activeTicketIds.add(data.ticketId);
      if (data.customerName) BillingState.activeCustomerNames.add(data.customerName);
      if (data.jobId) BillingState.activeJobIds.add(data.jobId);
      if (data.customerId) BillingState.activeCustomerIds.add(data.customerId);
      BillingState.activeClockEntries.push({ id: d.id, ...data });
    }
    renderBillingView();
  }, (error) => {
    console.warn('[Billing] Error loading active clock entries:', error);
    BillingState.activeClockState = 'error'; // TASK-9988 S1 — archive fails CLOSED on this
    BillingState.activeTicketIds = new Set();
    BillingState.activeCustomerNames = new Set();
    BillingState.activeJobIds = new Set();
    BillingState.activeCustomerIds = new Set();
    BillingState.activeClockEntries = [];
  });
  BillingState.unsubscribers.push(unsubActiveClock);

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    // TASK-4828 — `ticket-po` is DELIBERATELY NOT a Smart Assistant field, for
    // the same reason TASK-4782 pulled `job-po` out of this list, and this
    // comment is here so it does not get re-added by reflex.
    //
    // A PO IS AN IDENTIFIER, NOT A NAME. On default options SmartAssistant runs
    // two branches that are right for a person's name and wrong for a PO:
    //   [a] cleanFormat() title-cases ALL-CAPS behind an "Auto-fix" button —
    //       cleanFormat('ND25TU2-U1') === 'Nd25tu2-u1', and that corrupted
    //       value is LIVE IN TITS today next to the correct one.
    //   [b] the levenshtein "Did you mean X?" Apply button offers ND25TU0-U1
    //       when you type ND25TU1-U1 — two different live corridors, distance 1.
    //       One click re-bills the job to the wrong authorization.
    //
    // HONEST SCOPE NOTE: the ticket-PO field this registered (`#ticket-po`, in
    // `#modal-ticket`) is UNREACHABLE — `editTicket` returns straight into
    // `openReviewTicket`, `showAddTicket` does too, and `prefillTicket` (the
    // only other opener) has zero callers. So this registration was inert, not
    // actively corrupting data. It is removed anyway: the modal is one restored
    // call site away from being live again, and leaving a loaded gun in the
    // drawer because it is currently locked away is not a reason to keep it.
    // The LIVE ticket PO field is `#review-po`, which was never registered here
    // and now carries the length warning instead.
    //
    // TASK-4834 — `ticket-vendor-number` is gone for the SAME reason, and this
    // comment replaces the ⚠️ this block used to carry pointing at it. Same
    // move, same mechanism, no second one invented: removal.
    //
    // A VENDOR NUMBER IS AN IDENTIFIER, not a name. cleanFormat('EMI') === 'Emi'
    // and 'EMI' is a live vendor number on 8 documents today; the levenshtein
    // Apply button is the same false-match hazard as on the PO, and
    // `skipAllCaps` does not close it (it only tightens the threshold to
    // max(1, ceil(len*0.15)), which at 10 characters is still 2 — the same 2 the
    // default gives; it suppresses title-casing ONLY).
    //
    // HONEST SCOPE NOTE, same as ticket-po above: `#ticket-vendor-number` lives
    // in the SAME unreachable `#modal-ticket`, so this registration was INERT —
    // re-verified for TASK-4834, not inherited: `editTicket` returns into
    // `openReviewTicket` before touching the modal, `showAddTicket` does too,
    // and `prefillTicket` still has zero callers. The LIVE ticket vendor field
    // is `#review-vendor-number`, which was never registered here.
    // The one that was genuinely live is `job-vendor-number` in dispatch.js.
    // Follow-up (task-smartassist-names-2) — same rule as job-well-name: no
    // "Did you mean" against another ticket's well. Well number is an
    // identifier ('1H' vs '2H'); location is a legal description/address
    // ('NW/4 SEC 12' must not become 'Nw/4 Sec 12'). All three sit in the
    // unreachable #modal-ticket (see above), so this is prevention.
    SA.register('ticket-well-name', () => BillingState.tickets.filter(t => t.wellName).map(t => ({ value: t.wellName, id: t.id })), 'Tickets', { skipAllCaps: true, noFuzzy: true });
    SA.register('ticket-well-number', () => BillingState.tickets.filter(t => t.wellNumber).map(t => ({ value: t.wellNumber, id: t.id })), 'Tickets', { identifier: true });
    SA.register('ticket-location', () => [], 'Tickets', { identifier: true });
    SA.register('ticket-work', () => [], 'Tickets', { formatOnly: true });
    // Line item form fields
    SA.register('li-form-desc', () => [], 'Line Items', { formatOnly: true });
    SA.register('li-form-unit', () => [], 'Line Items', { formatOnly: true });
    SA.register('li-form-revenue-cat', () => [], 'Line Items', { formatOnly: true });
    // Quick add customer
    SA.register('li-qc-name', () => [], 'Customers', { formatOnly: true });
    SA.register('li-qc-phone', () => [], 'Customers', { phoneFormat: true });
    SA.register('li-qc-email', () => [], 'Customers', { emailFormat: true });
    // Onboarding wizard
    SA.register('obw-cust-name', () => [], 'Customers', { formatOnly: true });
    SA.register('obw-cust-phone', () => [], 'Customers', { phoneFormat: true });
    SA.register('obw-cust-email', () => [], 'Customers', { emailFormat: true });
  }
}

// TASK-1245 — PHASE 2: everything the first frame does NOT need. Byte-identical
// queries to the ones that used to live in subscribeToData(); the only change
// is WHEN they attach. Runs exactly once per mount, fired by the first tickets
// snapshot (success or error) or the BILLING_SECONDARY_SUBS_MAX_DELAY_MS cap.
function _runSecondarySubs(tenant, gen) {
  if (!_billingPendingSecondary) return;   // already attached this mount, or cancelled
  // The view remounted since this run was scheduled — attaching now would leak
  // listeners the cleanup function has already run past. Return WITHOUT
  // cancelling: _billingPendingSecondary may already belong to a newer mount,
  // and clearing it here would silently strand that mount's phase 2.
  if (gen !== _billingMountGen) return;
  // TASK-1245 (COD Independent ⚠️) — belt-and-braces for the one ordering the
  // generation counter can't see: if the view is torn down WITHOUT its cleanup
  // function running, the armed timer is never cleared and the gen never bumps,
  // so it would fire into a dead view and attach listeners nothing unsubscribes.
  // Same mounted-sentinel pattern _openTicketDeepLinkWhenReady already uses.
  if (!document.getElementById('billing-logo-upload')) { _cancelSecondarySubs(); return; }
  _cancelSecondarySubs();                  // clears the timer AND marks phase 2 done

  // TASK-1481 — comp accessor: the piece-rate settlement generator reads
  // payType/payRate/payRateUnit (comp fields, moving to peopleComp). Mirror
  // the people subscription so those resolve comp-first at call time.
  window.PersonComp?.subscribe?.(tenant, { key: 'billing' });

  // Subscribe to people for employee dropdown
  const peopleQuery = query(
    collection(db, 'tenants', tenant, 'people'),
    orderBy('name')
  );
  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    BillingState.people = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    window.ClockMode?.notePeople?.(BillingState.people); // TASK-9553 per-person clock mode
    // TASK-1245 (COD Code-Reviewer, round 3) — openReviewTicket() flushes phase
    // 2, but a flush only ATTACHES the listener; it can't wait for the snapshot.
    // renderBillingView() deliberately skips while a modal is open, so an
    // employee <select> rendered before this callback would stay empty forever.
    // Heal it here instead.
    _refreshOpenReviewEmployeeSelects();
  }, (error) => {
    console.error('[Billing] Error loading people:', error);
  });
  BillingState.unsubscribers.push(unsubPeople);

  // Subscribe to jobs
  const jobsQuery = query(
    collection(db, 'tenants', tenant, 'jobs'),
    orderBy('startDate', 'desc')
  );
  const unsubJobs = onSnapshot(jobsQuery, (snapshot) => {
    const _wasJobsLoaded = BillingState._jobsLoaded;
    BillingState.jobs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    BillingState._jobsLoaded = true; // TASK-1364 — see _resolveFallbackPoNumber
    // TASK-9414 (COD Independent + Edge Case #2) — Tickets depends on jobs for
    // the Project control + job tags + the project predicate. Re-render it on the
    // FIRST jobs snapshot (else "Loading projects…" spins forever) and whenever
    // any job→project link changes (a job linked from Dispatch in another tab
    // joins an open filter without a reload). NOT on every job edit.
    const _jpSig = jobProjectSignature(BillingState.jobs);
    const _jpChanged = _jpSig !== BillingState._jobProjectSig;
    BillingState._jobProjectSig = _jpSig;
    // projects is a one-shot read; a link to a project made elsewhere after it
    // (Dispatch, another tab) names a project we don't have. Re-read once per
    // link change — never "Project" with no name for the rest of the session.
    if (_jpChanged && BillingState._projectsLoaded && !BillingState._projectsLoading) {
      const _known = new Set(BillingState.projects.map(p => p.id));
      if (BillingState.jobs.some(j => j.projectId && !_known.has(j.projectId))) {
        BillingState._projectsLoaded = false;
        loadBillingProjects();
      }
    }
    const _ticketsNeedJobs = BillingState.viewMode === 'tickets'
      && _ticketProjectControlShown() && (!_wasJobsLoaded || _jpChanged);
    // TASK-1618 — 'projects' MUST be here. The Projects view is keyed entirely on
    // jobs.projectId, and jobs are a DEFERRED phase-2 listener, so a first-paint
    // that beats this snapshot renders every project as "0 jobs · 0 tickets · $0"
    // and NEVER corrects itself — a silent, plausible-looking lie about money.
    // renderBillingProjectsView() calls _flushSecondarySubs() on entry, but a
    // flush only ATTACHES the listener; it cannot wait for the snapshot (the same
    // trap the people listener above documents). Caught by the browser pass —
    // every code-reading review, mine included, missed it.
    // TASK-4649 (COD Code Reviewer) — tickets ALSO depend on jobs when permit
    // tracking is on: _ticketMissingPermit reads job.permitRequired, and the
    // one-shot permits fetch can resolve BEFORE this snapshot. Without this
    // rerender the Tickets view stays unflagged until an unrelated render.
    if (BillingState.viewMode === 'calendar' || BillingState.viewMode === 'projects' || _ticketsNeedJobs
        || (BillingState.viewMode === 'tickets' && _permitTrackingEnabled() && BillingState._permitsLoaded)) renderBillingView();
  }, (error) => {
    console.error('[Billing] Error loading jobs:', error);
  });
  BillingState.unsubscribers.push(unsubJobs);

  // TASK-4649 — permits, ONE batched read at AR/AP load (Binding 3: never a
  // per-card query). One-shot getDocs, not a ninth listener — same call the
  // Projects view made for the same reason (TASK-1618). Only fetched when the
  // tenant has permit tracking on; rules read is canReadTenant so any billing
  // member passes. On error the flag simply doesn't fire (flag, never block).
  if (_permitTrackingEnabled()) {
    getDocs(collection(db, 'tenants', tenant, 'permits')).then((snap) => {
      if (gen !== _billingMountGen) return; // stale mount — drop, don't mix tenants
      BillingState.permits = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      BillingState._permitsLoaded = true;
      if (BillingState.viewMode === 'tickets') renderBillingView();
    }).catch((error) => {
      console.error('[Billing] Error loading permits:', error);
    });
  }

  // Subscribe to jobDays
  const jobDaysQuery = query(
    collection(db, 'tenants', tenant, 'jobDays'),
    orderBy('date', 'asc')
  );
  const unsubJobDays = onSnapshot(jobDaysQuery, (snapshot) => {
    BillingState.jobDays = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    BillingState._jobDaysLoaded = true; // TASK-10043
    if (BillingState.viewMode === 'calendar') renderBillingView();
  }, (error) => {
    console.error('[Billing] Error loading jobDays:', error);
  });
  BillingState.unsubscribers.push(unsubJobDays);

  // Subscribe to approved time-off requests
  const timeOffQuery = query(
    collection(db, 'tenants', tenant, 'timeOff'),
    where('status', '==', 'approved')
  );
  const unsubTimeOff = onSnapshot(timeOffQuery, (snapshot) => {
    BillingState.timeOffRequests = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    if (BillingState.viewMode === 'calendar') renderBillingView();
  }, (error) => {
    console.warn('[Billing] Error loading timeOff (may not exist):', error);
    BillingState.timeOffRequests = [];
  });
  BillingState.unsubscribers.push(unsubTimeOff);
}

// TASK-1245 — back-fill the Review modal's employee pickers if they were built
// before the (deferred) people snapshot landed. Deliberately narrow: it only
// touches a select whose ONLY option is the "Select employee…" placeholder, so
// it can never clobber a populated dropdown, a user's in-progress choice, or
// the modal's other .review-select dropdowns (rate unit, revenue category, …).
// Employee pickers only exist on `_isNew` lines (billing.js renderReviewLines /
// the table variant), which is exactly the case that would otherwise be stuck.
function _refreshOpenReviewEmployeeSelects() {
  const modal = document.getElementById('modal-review-ticket');
  if (!modal || modal.classList.contains('hidden')) return;
  if (!BillingState.people.length) return;
  const optionsHtml = BillingState.people
    .map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`).join('');
  modal.querySelectorAll('select.review-select').forEach(sel => {
    if (sel.options.length > 1) return;                       // already populated
    const first = sel.options[0];
    if (!first || first.value !== '') return;                 // not a placeholder select
    if (!/select employee/i.test(first.textContent || '')) return;  // not an employee picker
    const keep = sel.value;
    sel.innerHTML = `<option value="">Select employee...</option>${optionsHtml}`;
    if (keep) sel.value = keep;
  });
}

// TASK-9548 — ticket customer pickers group internal-time customers (Shop /
// Office — the tenant's own internalTimeOnly flag) under a labeled "Internal —
// not billed" optgroup, then "Customers" A–Z, via the ONE shared builder
// (window.PickerGroups, read at CALL time — Pitfall #1). Values unchanged.
// P0 2026-10-06 — the shared builder drops vendors/subcontractors (QBO vendor
// import), keeping `selectedId` so a ticket already on one still renders.
// TASK-10250 — a ticket is never newly put on an Inactive or merged-away
// customer (no job, a $0 ticket nobody can bill); the ticket's own stays.
function _billingCustomerOptionsHtml(selectedId) {
  return window.PickerGroups.customerOptionsHtml(BillingState.customers, { selectedId, nameOf: c => c.name || '', excludeInactive: true });
}
// Decision 2026-10-10 (Chris, TASK-9548) — the BILLING pickers (Invoice
// Builder, Price Book, onboarding wizard, QuickBooks mapping) do not offer
// internal-time customers (Shop / Office, internalTimeOnly): they are never
// billed. selectedId is kept, so a record already on one still shows it,
// labeled "Internal — not billed". Ticket pickers keep the grouped section
// (_billingCustomerOptionsHtml) — internal time can sit on a ticket.
function _billingSalesCustomerOptionsHtml(selectedId) {
  return window.PickerGroups.customerOptionsHtml(BillingState.customers, { selectedId, nameOf: c => c.name || '', excludeInternal: true });
}

// P0 2026-10-06 — `tenants/{t}/customers` also holds vendors + subcontractors
// (type 'vendor' / 'subcontractor'; QuickBooks vendor import 2026-10-05). Every
// billing surface that OFFERS or MATCHES a customer reads through this, never
// BillingState.customers raw. keepId (string | string[]) = value(s) already
// selected, kept even if a vendor so a selection never silently disappears.
// window.BillableCustomer is GENERATED from functions/src/shared/billableCustomer.js.
function _billableCustomers(keepId) {
  return window.BillableCustomer.billableCustomers(BillingState.customers || [], keepId);
}
function _billingCustomerHintHtml() {
  return window.PickerGroups.customerHintHtml(BillingState.customers, { nameOf: c => c.name || '' });
}

function updateCustomerDropdown() {
  const select = document.getElementById('ticket-customer');
  if (!select) return;
  const currentValue = select.value;
  // P0 2026-10-06 — keep the current pick even if it is a vendor (never silently blank it).
  select.innerHTML = '<option value="">Select customer...</option>' + _billingCustomerOptionsHtml(currentValue || null);
  if (currentValue) select.value = currentValue;
  const hint = document.getElementById('ticket-customer-hint');
  if (hint) hint.innerHTML = _billingCustomerHintHtml();
}

// ============================================
// VIEW SWITCHING
// ============================================
function switchBillingView(view) {
  if (view === 'projects' && window.canAccessProjects?.() === false) view = 'tickets'; // TASK-9651
  BillingState.viewMode = view;

  // Update toggle active states
  document.querySelectorAll('#billing-view-pills .view-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.view === view);
  });

  // Hide AR header actions when on AP (New Ticket, Invoice Builder, etc. don't apply)
  const headerActions = document.querySelector('.tickets-view > .view-header .view-actions');
  if (headerActions) headerActions.style.display = view === 'ap' ? 'none' : '';
  const headerTitle = document.getElementById('billing-header-company-name');
  if (headerTitle) headerTitle.textContent = view === 'ap' ? 'Accounts Payable' : 'AR / AP';

  // Clear ticket selection when leaving tickets view
  if (view !== 'tickets') {
    clearTicketSelection();
    BillingState.ticketJobFilter = null;
    BillingState.ticketJobFilterName = null;
    // TASK-1618 — the project drill-down is cleared on the SAME condition as the
    // job drill-down, so a stale project filter can never survive into a later
    // Tickets visit and hand the biller (or the CSV) a silently narrowed set.
    // Note the drill-down itself calls switchBillingView('tickets'), which does
    // NOT take this branch — that is why it is safe to set the filter first.
    BillingState.ticketProjectFilter = null;
    BillingState.ticketProjectFilterName = null;
    BillingState.ticketProjectMode = null;
  }

  renderBillingView();
}

function navigateToAP() {
  switchBillingView('ap');
}

// TASK-4867 — does the signed-in user have the `bi` module?
//
// ⚠️ THIS IS A UI GATE, NOT A SECURITY BOUNDARY. It decides whether the
// Reconcile pill RENDERS. navigateToView('bi') runs the real gate
// (checkModuleAccess, app.js:19828) on click and bounces anyone who slips past.
//
// Deliberately mirrors clock.js hasBillingAccess() (TASK-1364) branch for
// branch rather than sharing a parameterized helper — that module made the same
// call for the same reason, and keeping the two readable side by side is what
// makes a drift in either one obvious. checkModuleAccess() itself is NOT
// exported to window, so a view module cannot call it directly, and
// cross-importing app.js from a `?v=`-busted module is Pitfall #1.
//
// The mapping back to checkModuleAccess: only an explicit `false` hides the
// pill. 'needs-pin' (kiosk / owner-locked device) must still SHOW it — the app
// would PIN-challenge and then grant, so hiding it would deny an affordance the
// user is entitled to, and the hand-off below survives the challenge.
function hasBIAccess() {
  const A = window.AppState || {};
  // Driver / public / PIN-token modes never get BI.
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  // Member doc still resolving — don't flash a pill we may have to take back.
  // Billing re-renders on every snapshot, so it appears as soon as the read
  // lands. (In practice navigateToView already defers on 'loading' before this
  // view mounts; this is the belt.)
  if (A.memberAccessPending) return false;
  const memberAccess = A.memberAccess;
  const memberDocBlocked = memberAccess && ['revoked', 'inactive', 'pending'].includes(memberAccess.status);
  if (memberAccess && !memberDocBlocked) {
    // members/{uid}.modules is ABSOLUTE for signed-in members (TASK-319) and is
    // checked BEFORE the founder short-circuit, so a billing-only staff member
    // in the founder tenant (steven@, brian@) is NOT promoted by isFounder().
    if (memberAccess.role === 'owner') return true;
    // Legacy admin, member doc pre-dates the modules field = full access.
    if (memberAccess.role === 'admin' && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) {
      return true;
    }
    if (Array.isArray(memberAccess.modules)) {
      return memberAccess.modules.includes('bi');
    }
    // Member doc with no usable grant rule → deny. Do NOT fall through.
    return false;
  }
  // No active member doc — legacy single-login owner / founder / demo.
  if (isFounder()) return true;
  if (A.isDemo || A.tenant === 'demo') return true;
  try {
    // Compliance-only tenants browse everything in preview mode.
    if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return true;
  } catch (_) { /* ignore */ }
  // Kiosk / legacy shared-login, gated by access PINs: checkModuleAccess returns
  // true or 'needs-pin' on this branch — never false — so show it.
  return true;
}

// TASK-4867 — Billing → BI Reconciliation.
//
// Hands the target tab off through window._billingBiDeepLink and lets initBI()
// consume it once BI is mounted, instead of calling
// window.BIModule.switchTab('reconciliation') after navigateToView(). That
// matters: the nav gate can DEFER (a PIN challenge, or a still-resolving
// member-access read returns before anything is mounted), and a direct call in
// that window would be a silent no-op — the exact bug TASK-1364 root-caused on
// the Clock receipt button. It also avoids the switchTab + setTimeout race at
// bi.js:1361, which is intra-module and lands on Overview if BI's first render
// is slow.
function navigateToReconcile() {
  try {
    // Belt to the render-time gate: an onclick string is trivially replayable,
    // and navigateToView('bi') would otherwise strand a billing-only member on
    // a "no access" screen with their Billing view already destroyed.
    if (!hasBIAccess()) {
      window.showToast?.('Business Intelligence access is required to reconcile', 'warning');
      return;
    }
    if (typeof window.navigateToView !== 'function') {
      window.showToast?.('Business Intelligence module not loaded', 'error');
      return;
    }
    // Timestamped so a navigation the gate bounced (trial wall, cancelled PIN
    // challenge) can't leave a landmine that yanks some later, unrelated BI
    // visit onto Reconciliation. initBI drops hand-offs older than 30s.
    window._billingBiDeepLink = { tab: 'reconciliation', at: Date.now() };
    window.navigateToView('bi');
  } catch (err) {
    console.error('[Billing] navigateToReconcile error:', err);
    delete window._billingBiDeepLink;
    window.showToast?.('Could not open Reconciliation', 'error');
  }
}

// TASK-9491 — money controls for a login the rules would refuse. The gate is
// window.canEditMoney (app.js), the client mirror of firestore.rules
// canEditMoney: owner via ownerId, or `billing` and not view-only; members.role
// is never read. Two layers: (1) buttons that change money render dimmed with
// a plain "View only — ask the owner" note above the list, and (2) every money
// action function refuses at entry, so a keyboard / bulk / other-entry-point
// call gets the same sentence instead of a raw permission-denied.
function _moneyViewOnly() {
  return typeof window.canEditMoney === 'function' && window.canEditMoney() === false;
}
function _moneyBlocked() {
  if (!_moneyViewOnly()) return false;
  window.showToast?.(window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.', 'warning');
  return true;
}
// Ticket CONTENT edits (lines, notes, header) on an OPEN ticket are open to any
// writing member except a view-only one (tkUpdOk member arm) — so this gate is
// the view-only flag alone, not the billing module.
// TASK-10036 — the same test with no toast, for the Review banner.
function _ticketContentViewOnly() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return false;
  const ma = st.memberAccess;
  return !!ma && (ma.viewUploadOnly === true || ma.readOnly === true);
}
function _ticketContentBlocked() {
  if (!_ticketContentViewOnly()) return false;
  window.showToast?.(window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.', 'warning');
  return true;
}
// Price book + item codes: firestore.rules admit canEditMoney OR the `crm` module
// (not view-only). Mirror that so a view-only login gets the sentence, not a raw error.
function _priceBookBlocked() {
  if (!_moneyViewOnly()) return false;
  const ma = window.AppState?.memberAccess;
  if (ma && ma.viewUploadOnly !== true && Array.isArray(ma.modules) && ma.modules.includes('crm')) return false;
  window.showToast?.(window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.', 'warning');
  return true;
}

const _MONEY_ACTION_FNS = ['markTicketPaid', 'unmarkTicketPaid', 'writeOffTicket', 'unwriteOffTicket',
  'archiveTicket', 'deleteTicket', 'createGroupedInvoice', 'uninvoiceTicket', 'unarchiveAndInvoice', 'unarchiveFromReview',
  'markInvoicedExternally', 'markInvoicedExternallyBulk',
  // TASK-9735 follow-up (Lacey, 10/19) — the saved-screen Invoice / Billed
  // elsewhere and the bulk Mark paid are money changes too.
  'reviewSavedInvoice', 'reviewSavedBilledElsewhere', 'markSelectedTicketsPaid',
  'switchToBilledElsewhere', 'unarchiveAndMarkBilledElsewhere']; // hotfix/billed-elsewhere-1020

// TASK-9735 follow-up (Lacey, 10/19) — "Billed elsewhere" on the ticket CARD,
// beside Invoice. Before 10/16 Save Ticket dropped her on the read-only view,
// where Billed elsewhere lived; Save now stays on the edit screen, so the card
// only offered Invoice (a DD invoice) and Archive, and Archive is the exact
// "I billed this elsewhere" misuse TASK-1346 exists to end. Rendered only where
// the caller already decided Invoice is offered (unbilled, not archived/locked);
// a written-off ticket is refused by _externalBillBlockReason, so it is not
// offered a dead button. The click goes through markInvoicedExternally and its
// gates unchanged ($0, zero-hour, unpriced, mileage, view-only).
function _cardBilledElsewhereBtnHtml(ticket) {
  if (!ticket || ticket.writtenOff === true) return '';
  const tid = window.escJsAttr ? window.escJsAttr(ticket.id) : String(ticket.id).replace(/[^A-Za-z0-9_-]/g, '');
  return `
              <button class="btn btn-sm btn-secondary card-billed-elsewhere" title="Already billed in QuickBooks or another system — no DispatchDeck invoice is created" onclick="event.stopPropagation();window.BillingModule.markInvoicedExternally('${tid}')">
                <span class="material-icons">assignment_turned_in</span> Billed elsewhere
              </button>`;
}
// hotfix/billed-elsewhere-1020 — card buttons for Switch to Billed elsewhere
// (DD-invoiced, unpaid) and Unarchive & mark Billed elsewhere (archived, unbilled).
// `banner` = the white-on-slate locked banner styling.
function _cardToExternalBtnHtml(ticket, fn, label, banner) {
  const tid = window.escJsAttr ? window.escJsAttr(ticket.id) : String(ticket.id).replace(/[^A-Za-z0-9_-]/g, '');
  const cls = banner ? 'btn card-to-external' : 'btn btn-sm btn-secondary card-to-external';
  const style = banner ? ' style="background:#0EA5E9;color:#fff;border:1.5px solid rgba(255,255,255,0.35);padding:10px 16px;font-size:0.95rem;font-weight:700;border-radius:8px;min-height:44px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;"' : '';
  return `<button class="${cls}"${style} data-be-action="${fn}" title="Already billed in QuickBooks — record it, no DispatchDeck invoice" onclick="event.stopPropagation();window.BillingModule.${fn}('${tid}')"><span class="material-icons">assignment_turned_in</span> ${label}</button>`;
}
function _applyMoneyViewOnlyChrome(area) {
  const viewOnly = _moneyViewOnly();
  document.body.classList.toggle('dd-money-view-only', viewOnly);
  if (!document.getElementById('dd-money-view-only-style')) {
    const st = document.createElement('style');
    st.id = 'dd-money-view-only-style';
    st.textContent = _MONEY_ACTION_FNS
      .map(fn => `body.dd-money-view-only [onclick*="BillingModule.${fn}("]`).join(',\n')
      // COD UX: NOT pointer-events:none — a swallowed tap reads as a frozen app
      // on a phone. The button stays tappable and its function's _moneyBlocked()
      // guard answers with the "View only — ask the owner" toast.
      + ' { opacity: .45; cursor: not-allowed; }';
    document.head.appendChild(st);
  }
  const host = area && area.parentElement;
  if (!host) return;
  let note = document.getElementById('billing-money-view-only-note');
  if (!viewOnly) { if (note) note.remove(); return; }
  if (!note) {
    note = document.createElement('div');
    note.id = 'billing-money-view-only-note';
    note.setAttribute('role', 'status');
    note.style.cssText = 'display:flex;align-items:center;gap:8px;margin:0 0 12px;padding:10px 14px;border:1px solid var(--border-color, #CBD5E1);border-radius:8px;background:var(--bg-secondary, #F1F5F9);color:var(--text-primary);font-size:14px;';
    note.innerHTML = '<span class="material-icons" style="font-size:20px;color:var(--text-secondary);">visibility</span>'
      + '<span><strong>View only</strong> — ask the owner to invoice, take payments, write off, or change prices. You can still add expenses.</span>';
    host.insertBefore(note, area);
  }
}

// TASK-9735 follow-up (Lacey, 10/19) — Mark paid asks WHEN the money came in and,
// optionally, the QuickBooks invoice / payment number. A billed-elsewhere ticket
// is usually paid days or weeks before the biller gets to it, and stamping "now"
// put the cash in the wrong month on cash-basis P&L.
// The date lands on the ticket's existing paidAt (noon local, so no timezone can
// push it to the neighbouring day). The reference is stored on the MARK_PAID
// audit row ONLY — NOT on the ticket doc (an invoiced ticket is locked, and
// firestore.rules' locked-ticket lifecycle allowlist admits only
// paid/paidAt/paidBy) and NOT on the revenueEntries row: TASK-10122 (P0) — the
// revenueEntries real-tenant update allowlist (TASK-9942) has no `paymentRef`,
// so sending it got the WHOLE revenue-row update refused for every non-demo
// tenant from v1.15.2106.0 on (ticket Paid, cash-basis BI never counted it).
// The mirror writes below send ONLY allowlisted keys; rules are at their
// expression cap, so the fix lives here, pinned by
// tests/rules/task10122-markPaidRevenueRow.test.mjs (and TASK-4767 S2's
// tests/rules/task4767-s2-one-pay-path.test.mjs DENY case).
// Resolves { paidAt: Date, ref: string } or null on cancel.
function _ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function _parsePaidDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
  if (Number.isNaN(d.getTime()) || d.getMonth() !== Number(m[2]) - 1) return null; // 2026-02-31
  return d;
}
function _promptPaidDetails({ heading, sub, confirmText }) {
  return new Promise((resolve) => {
    document.getElementById('paid-details-modal')?.remove();
    const today = _ymdLocal(new Date());
    const modal = document.createElement('div');
    modal.id = 'paid-details-modal';
    modal.className = 'modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = `
      <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);">
        <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
          <span class="material-icons" style="font-size:48px;color:#7C3AED;">payments</span>
          <h3 style="font-size:1.25rem;font-weight:600;margin:0;">${escapeHtml(heading)}</h3>
        </div>
        <p style="color:var(--text-secondary);margin-bottom:var(--spacing-md);line-height:1.5;">${escapeHtml(sub)}</p>
        <div class="form-group" style="text-align:left;margin-bottom:var(--spacing-sm);">
          <label for="paid-details-date" style="font-weight:600;font-size:13px;">Date paid</label>
          <input id="paid-details-date" type="date" class="form-control" value="${today}" max="${today}">
        </div>
        <div class="form-group" style="text-align:left;margin-bottom:var(--spacing-md);">
          <label for="paid-details-ref" style="font-weight:600;font-size:13px;">QuickBooks invoice or payment # (optional)</label>
          <input id="paid-details-ref" type="text" class="form-control" maxlength="60" autocomplete="off" placeholder="e.g. 1042 or check 5531">
        </div>
        <div id="paid-details-err" role="alert" style="display:none;color:#B91C1C;font-size:13px;margin-bottom:var(--spacing-sm);"></div>
        <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
          <button type="button" class="btn btn-secondary" style="flex:1;max-width:140px;" id="btn-paid-details-cancel">Cancel</button>
          <button type="button" class="btn" style="flex:1;max-width:180px;background:#7C3AED;color:#fff;border:none;" id="btn-paid-details-ok">${escapeHtml(confirmText || 'Mark paid')}</button>
        </div>
      </div>`;
    let done = false;
    const finish = (val) => {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey, true);
      modal.remove();
      resolve(val);
    };
    const submit = () => {
      const d = _parsePaidDate(modal.querySelector('#paid-details-date')?.value);
      const err = modal.querySelector('#paid-details-err');
      const endToday = new Date(); endToday.setHours(23, 59, 59, 999);
      const bad = !d ? 'Pick the date the payment came in.' : (d > endToday ? 'The paid date can\'t be in the future.' : '');
      if (bad) { if (err) { err.textContent = bad; err.style.display = 'block'; } return; }
      const ref = String(modal.querySelector('#paid-details-ref')?.value || '').trim().slice(0, 60);
      finish({ paidAt: d, ref });
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); finish(null); }
      else if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT') { e.preventDefault(); submit(); }
    };
    modal.addEventListener('click', (e) => { if (e.target === modal) finish(null); });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(modal);
    modal.querySelector('#btn-paid-details-cancel').onclick = () => finish(null);
    modal.querySelector('#btn-paid-details-ok').onclick = submit;
    setTimeout(() => modal.querySelector('#paid-details-ref')?.focus(), 0);
  });
}

// Why a ticket can't be marked paid (null = it can). integ/2107: firestore.rules
// tkPaidNeedsBilled refuses a paid ticket only on CREATE — tickets.update is at
// the 1,000-expression cap — so on update THIS check (inside the transaction) is
// still the gate for client writes.
// TASK-4767 S2 follow-up — ONE PAY PATH. A ticket billed on a DispatchDeck
// invoice RECORD gets Paid from that invoice's payments (AR/AP → Tickets →
// Invoices), never from its own button: two places to pay one invoice is how
// money gets counted twice or not at all. Same test as the server's
// invoiceRecordIdOf (functions/src/services/invoicePayments.js): markInvoiced
// stamps invoiceId = the invoice number, and an uninvoice clears the number but
// not invoiceId, so both must agree. A Billed-elsewhere ticket, or one billed
// before invoice records existed, has no record and keeps its own Mark paid.
// The server puts back a per-ticket write that lands anyway (stale bundle).
function _invoiceRecordIdOf(t) {
  if (!t || t.status === 'deleted') return null;
  const id = typeof t.invoiceId === 'string' ? t.invoiceId : '';
  const num = t.invoiceNumber == null ? '' : String(t.invoiceNumber).trim();
  return id && num && num === id ? id : null;
}
// The ticket's Paid flag was set by its invoice (not by a person, Stripe or QuickBooks sync).
function _paidByItsInvoice(t) {
  const id = _invoiceRecordIdOf(t);
  return !!id && t.paid === true && typeof t.paidBy === 'string' && t.paidBy.startsWith('invoice:');
}
// TASK-4767 S2 on integ/2125 (COD Independent + Edge Case) — the ticket's Paid
// was written by QuickBooks paid sync (TASK-9417). Its documented undo is a
// person pressing Unpaid (the sync never re-marks after that), and there is no
// invoice payment to remove for it, so a sync-marked ticket on an invoice record
// keeps its per-ticket Unpaid. Same test as the server's realPaymentEvidence.
function _qboSyncPaid(t) {
  return !!t && t.paid === true && t.paidSource === 'qbo' && t.paidBy === 'qbo-sync';
}
// The record id for an onclick="...('id')" attribute (a JS string inside an attribute — escJsAttr, never an HTML escaper).
function _invoiceIdAttr(t) {
  const id = _invoiceRecordIdOf(t) || '';
  return window.escJsAttr ? window.escJsAttr(id) : id.replace(/[^A-Za-z0-9._-]/g, '');
}
function _onInvoiceRecordMsg(t) {
  return `This ticket is on invoice ${_invoiceRecordIdOf(t)}. Its Paid comes from that invoice — record or remove the payment under Invoices.`;
}

function _paidBlockReason(t) {
  if (!t) return 'no longer loaded';
  if (t.status === 'deleted') return 'deleted';
  if (t.writtenOff === true) return 'written off';
  if (_invoiceRecordIdOf(t)) return `on invoice ${_invoiceRecordIdOf(t)} — record the payment on the invoice (Invoices)`;
  if (t.paid === true) return 'already paid';
  if (!isTicketInvoiced(t)) return 'not billed yet — invoice it or use Billed elsewhere first';
  return null;
}

// The write. Re-reads the ticket from the server first (COD r1: the list cache
// can be stale — another tab may have unbilled, written off or paid it), then
// throws on refusal or a failed ticket write (callers count it). The revenue-row
// mirror stays best-effort; its outcome is RETURNED so the caller can say so.
async function _applyTicketPaid(tenant, ticketId, paidAtDate, ref) {
  const paidAt = Timestamp.fromDate(paidAtDate);
  const tRef = doc(db, 'tenants', tenant, 'tickets', ticketId);
  // COD r2: the eligibility read and the write are ONE transaction, so another
  // tab un-billing / writing off / paying it in between makes this retry or refuse.
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(tRef);
    const why = _paidBlockReason(snap.exists() ? { id: ticketId, ...snap.data() } : null);
    if (why) {
      const e = new Error(why);
      e._userMessage = `Can't mark this paid — it is ${why}.`;
      throw e;
    }
    tx.update(tRef, { paid: true, paidAt, paidBy: window.AppState?.uid || 'unknown' });
  });
  const cached = (BillingState.tickets || []).find(t => t.id === ticketId);
  if (cached) { cached.paid = true; cached.paidAt = paidAt; }
  // Also update matching revenueEntry so cash-basis P&L works
  let revenueOk = true;
  try {
    const revSnap = await getDocs(query(
      collection(db, 'tenants', tenant, 'revenueEntries'),
      where('ticketId', '==', ticketId)
    ));
    const batch = [];
    revSnap.forEach(d => {
      batch.push(updateDoc(doc(db, 'tenants', tenant, 'revenueEntries', d.id), { paid: true, paidAt })); // TASK-10122: allowlisted keys only — the payment reference stays on the audit row
    });
    if (batch.length) await Promise.all(batch);
  } catch (revErr) {
    revenueOk = false;
    console.warn('[Billing] Could not update revenueEntry paid status:', revErr);
  }
  window.logAudit?.('ticket', 'MARK_PAID', { ticketId, paidOn: _ymdLocal(paidAtDate), paymentRef: ref || null });
  return { revenueOk };
}

let _markPaidInFlight = false;
async function markTicketPaid(ticketId) {
  if (_moneyBlocked()) return;
  if (_markPaidInFlight) return;
  _markPaidInFlight = true;
  try {
    const tenant = BillingState.tenantId;
    if (!tenant || !ticketId) return;
    const t = (BillingState.tickets || []).find(x => x.id === ticketId);
    const why = t ? _paidBlockReason(t) : null; // not cached: the server re-read decides
    if (t && _invoiceRecordIdOf(t)) { window.showToast?.(_onInvoiceRecordMsg(t), 'info'); return; } // TASK-4767 S2 one pay path
    if (why) { window.showToast?.(`Can't mark this paid — it is ${why}.`, why === 'already paid' ? 'info' : 'error'); return; }
    const label = t ? `Ticket #${t.ticketNo || ticketId} — ${formatCurrency(t.grandTotal || 0)}` : 'This ticket';
    const ans = await _promptPaidDetails({ heading: 'Mark paid', sub: label, confirmText: 'Mark paid' });
    if (!ans) return; // cancelled
    const { revenueOk } = await _applyTicketPaid(tenant, ticketId, ans.paidAt, ans.ref);
    window.showToast?.(revenueOk
      ? 'Ticket marked as paid'
      : 'Marked paid — but your revenue reports did not update for it. Tap Unpaid, then Paid again, to fix the reports.',
      revenueOk ? 'success' : 'warning');
    renderBillingView();
  } catch (err) {
    console.error('[Billing] Mark paid error:', err);
    window.showToast?.(err?._userMessage || 'Failed to mark as paid', 'error');
  } finally {
    _markPaidInFlight = false;
  }
}

// TASK-9735 follow-up — BULK Mark paid for the selection (a QuickBooks payment
// often covers a week of billed-elsewhere tickets). Partitions like the bulk
// Billed elsewhere: only billed, unpaid, not-written-off, not-deleted tickets
// are marked; everything else is named in a blocking confirm with its total.
let _bulkPaidInFlight = false;
async function markSelectedTicketsPaid() {
  if (_moneyBlocked()) return;
  if (_bulkPaidInFlight) return;
  _bulkPaidInFlight = true;
  try {
    const tenant = BillingState.tenantId;
    const ids = [...(BillingState.selectedTicketIds || [])];
    if (!tenant || ids.length === 0) { window.showToast?.('Select tickets first', 'info'); return; }
    const eligible = [];
    const skipped = [];
    for (const id of ids) {
      const t = BillingState.tickets.find(x => x.id === id);
      const reason = _paidBlockReason(t);
      if (reason) skipped.push({ t: t || { grandTotal: 0 }, reason }); else eligible.push(t);
    }
    if (skipped.length > 0) {
      const skipTotal = skipped.reduce((s, x) => s + Number(x.t?.grandTotal || 0), 0);
      const byReason = {};
      for (const s of skipped) byReason[s.reason] = (byReason[s.reason] || 0) + 1;
      const reasonList = Object.entries(byReason).map(([r, n]) => `${n} ${r}`).join(', ');
      const goOn = await window.showConfirm?.(
        `${skipped.length} ticket${skipped.length !== 1 ? 's' : ''} can't be marked paid`,
        `${skipped.length} of the selected tickets will be SKIPPED (${formatCurrency(skipTotal)} total): ${reasonList}. ` +
        (eligible.length > 0 ? `Mark the remaining ${eligible.length} paid?` : 'Nothing in this selection can be marked paid.'),
        { confirmText: eligible.length > 0 ? `Continue with ${eligible.length}` : 'OK', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
      );
      if (!goOn) return;
    }
    if (eligible.length === 0) { window.showToast?.('Nothing left to mark paid in this selection.', 'info'); return; }
    const total = eligible.reduce((s, t) => s + Number(t.grandTotal || 0), 0);
    const ans = await _promptPaidDetails({
      heading: `Mark ${eligible.length} ticket${eligible.length !== 1 ? 's' : ''} paid`,
      sub: `${formatCurrency(total)} total. The date and reference apply to all ${eligible.length}.`,
      confirmText: `Mark ${eligible.length} paid`
    });
    if (!ans) return;
    let ok = 0;
    let revShort = 0;
    const failed = [];
    for (const t of eligible) {
      try {
        const { revenueOk } = await _applyTicketPaid(tenant, t.id, ans.paidAt, ans.ref);
        ok++;
        if (!revenueOk) revShort++;
      } catch (err) { console.error('[Billing] Bulk mark paid failed for', t.id, err); failed.push(t.ticketNo || t.id); }
    }
    const revNote = revShort ? ` Revenue reports did not update for ${revShort} — tap Unpaid, then Paid again, on those to fix the reports.` : '';
    if (failed.length === 0) window.showToast?.(`${ok} ticket${ok !== 1 ? 's' : ''} marked paid.${revNote}`, revShort ? 'warning' : 'success');
    else window.showToast?.(`${ok} marked paid. ${failed.length} could not be marked (changed since you selected them): #${failed.slice(0, 5).join(', #')}${failed.length > 5 ? '…' : ''}.${revNote}`, 'warning');
    clearTicketSelection();
    renderBillingView();
  } finally {
    _bulkPaidInFlight = false;
  }
}

async function unmarkTicketPaid(ticketId) {
  if (_moneyBlocked()) return;
  try {
    const tenant = BillingState.tenantId;
    if (!tenant || !ticketId) return;
    // TASK-4767 S2 one pay path — a ticket on an invoice record is un-paid on the
    // invoice (remove the payment), never here — whoever set its flag (COD r1:
    // no un-mark-only asymmetry for a legacy hand-paid ticket). Re-read in a
    // transaction so a stale card cannot un-mark what the invoice just marked.
    const tRef = doc(db, 'tenants', tenant, 'tickets', ticketId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(tRef);
      const cur = snap.exists() ? { id: ticketId, ...snap.data() } : null;
      if (cur && _invoiceRecordIdOf(cur) && !_qboSyncPaid(cur)) {
        const e = new Error('on an invoice record');
        e._userMessage = _onInvoiceRecordMsg(cur);
        throw e;
      }
      tx.update(tRef, { paid: false, paidAt: null, paidBy: null });
    });
    // Also clear on matching revenueEntry
    try {
      const revSnap = await getDocs(query(
        collection(db, 'tenants', tenant, 'revenueEntries'),
        where('ticketId', '==', ticketId)
      ));
      const batch = [];
      revSnap.forEach(d => {
        batch.push(updateDoc(doc(db, 'tenants', tenant, 'revenueEntries', d.id), { paid: false, paidAt: null })); // TASK-10122: allowlisted keys only — a reference key got the whole update refused
      });
      if (batch.length) await Promise.all(batch);
    } catch (revErr) {
      console.warn('[Billing] Could not clear revenueEntry paid status:', revErr);
    }
    window.logAudit?.('ticket', 'UNMARK_PAID', { ticketId });
    window.showToast?.('Payment status cleared', 'success');
    renderBillingView();
  } catch (err) {
    console.error('[Billing] Unmark paid error:', err);
    window.showToast?.(err?._userMessage || 'Failed to update payment status', err?._userMessage ? 'info' : 'error');
  }
}

async function writeOffTicket(ticketId) {
  if (_moneyBlocked()) return;
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (!ticket) return;

  // Prompt for reason
  const existing = document.getElementById('writeoff-reason-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'writeoff-reason-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content modal-small" style="text-align:center;padding:var(--spacing-xl);">
      <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
        <span class="material-icons" style="font-size:48px;color:#DC2626;">money_off</span>
        <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Write Off as Uncollectable</h3>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:var(--spacing-md);line-height:1.5;">
        Ticket #${ticket.ticketNo || ticketId} — ${formatCurrency(ticket.grandTotal || 0)}<br>
        This will be reported as bad debt in P&L exports.
      </p>
      <div class="form-group" style="text-align:left;margin-bottom:var(--spacing-md);">
        <label style="font-weight:600;font-size:13px;">Reason (optional)</label>
        <textarea id="writeoff-reason-input" class="form-control" rows="2" placeholder="e.g. Customer went out of business, disputed charges..." style="resize:vertical;"></textarea>
      </div>
      <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
        <button class="btn btn-secondary" style="flex:1;max-width:140px;" onclick="document.getElementById('writeoff-reason-modal').remove()">Cancel</button>
        <button class="btn" style="flex:1;max-width:180px;background:#DC2626;color:#fff;border:none;" id="btn-confirm-writeoff">Write Off</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  document.getElementById('btn-confirm-writeoff').onclick = async () => {
    const reason = document.getElementById('writeoff-reason-input')?.value?.trim() || '';
    modal.remove();
    try {
      const tenant = BillingState.tenantId;
      if (!tenant) return;
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
        writtenOff: true,
        writtenOffAt: Timestamp.now(),
        writtenOffBy: window.AppState?.uid || 'unknown',
        writeOffReason: reason,
        paid: false,
        paidAt: null
      });
      // Mark the revenueEntry as written off
      try {
        const revSnap = await getDocs(query(
          collection(db, 'tenants', tenant, 'revenueEntries'),
          where('ticketId', '==', ticketId)
        ));
        for (const rd of revSnap.docs) {
          await updateDoc(doc(db, 'tenants', tenant, 'revenueEntries', rd.id), {
            writtenOff: true,
            writtenOffAt: Timestamp.now(),
            writeOffReason: reason
          });
        }
      } catch (_) {}

      window.logAudit?.('ticket', 'WRITE_OFF', { ticketId, ticketNo: ticket.ticketNo, amount: ticket.grandTotal, reason });
      window.showToast?.('Ticket written off as uncollectable', 'success');
      document.getElementById('modal-view-ticket')?.classList.add('hidden');
      renderBillingView();
    } catch (err) {
      console.error('[Billing] Write off error:', err);
      window.showToast?.('Failed to write off ticket', 'error');
    }
  };
}

async function unwriteOffTicket(ticketId) {
  if (_moneyBlocked()) return;
  try {
    const tenant = BillingState.tenantId;
    if (!tenant || !ticketId) return;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      writtenOff: false,
      writtenOffAt: null,
      writtenOffBy: null,
      writeOffReason: null
    });
    // Unmark revenue entry
    try {
      const revSnap = await getDocs(query(
        collection(db, 'tenants', tenant, 'revenueEntries'),
        where('ticketId', '==', ticketId)
      ));
      for (const rd of revSnap.docs) {
        await updateDoc(doc(db, 'tenants', tenant, 'revenueEntries', rd.id), {
          writtenOff: false,
          writtenOffAt: null,
          writeOffReason: null
        });
      }
    } catch (_) {}

    window.showToast?.('Write-off reversed', 'success');
    renderBillingView();
  } catch (err) {
    console.error('[Billing] Unwrite-off error:', err);
    window.showToast?.('Failed to reverse write-off', 'error');
  }
}

// TASK-862 — delegates to shared window.Fmt.currency (sharedFormat.js); byte-identical.
function formatCurrency(val) {
  return window.Fmt.currency(val);
}

function renderBillingView() {
  const area = document.getElementById('billing-content-area');
  if (!area) return;
  // Skip render if any billing modal with user input is open
  const billingModalsOpen = ['modal-review-ticket', 'modal-ticket', 'modal-view-ticket', 'modal-billing-job',
    'modal-billing-settings', 'modal-invoice-builder', 'modal-billing-lineitems',
    'modal-company-info', 'modal-split-ticket'
  ].some(id => { const m = document.getElementById(id); return m && !m.classList.contains('hidden'); });
  if (billingModalsOpen) return;
  _applyMoneyViewOnlyChrome(area); // TASK-9491

  const countEl = document.getElementById('tickets-count');

  if (BillingState.viewMode === 'ap') {
    if (countEl) countEl.textContent = '';
    if (window.APModule?.renderAPView) {
      window.APModule.renderAPView(area, BillingState.tenantId);
    } else {
      area.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading AP...</p></div>';
      // AP module imported by app.js — wait for it
      setTimeout(() => {
        if (window.APModule?.renderAPView) {
          window.APModule.renderAPView(area, BillingState.tenantId);
        }
      }, 500);
    }
  } else if (BillingState.viewMode === 'projects') {
    // TASK-1618 part 1 — the AR/AP money lens on projects.
    if (countEl) countEl.textContent = '';
    renderBillingProjectsView(area);
  } else if (BillingState.viewMode === 'calendar') {
    if (countEl) countEl.textContent = '';
    // Show loading state until ticket data arrives (prevents empty calendar flash)
    if (BillingState.tickets.length === 0 && BillingState.unsubscribers.length > 0 && !BillingState._ticketsLoaded) {
      // TASK-1245 — --text-muted (#94A3B8) on the light page background breaks
      // DESIGN.md colour rule 1 / CLAUDE.md pitfall #3. This is the exact copy
      // Chris was staring at on LTE; make it readable. Matches the
      // renderTicketsList skeleton's caption, which already uses --text-secondary.
      area.innerHTML = '<div class="loading-state" style="padding:60px 0;text-align:center;"><div class="loading-spinner"></div><p style="color:var(--text-secondary);margin-top:12px;">Loading billing data…</p></div>';
      return;
    }
    renderBillingCalendar(area);
  } else {
    renderTicketsView(area, countEl);
  }
}

// ============================================
// CALENDAR HELPERS
// ============================================
function formatDateISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toDateStr(val) {
  // Convert Firestore Timestamp, Date, or string to YYYY-MM-DD
  if (!val) return null;
  if (typeof val === 'string') return val.slice(0, 10);
  const d = val?.toDate ? val.toDate() : new Date(val);
  return isNaN(d.getTime()) ? null : formatDateISO(d);
}

function getBillingJobsForDate(dateStr) {
  const results = [];
  const jobIdsWithJobDays = new Set();
  const seenJobIds = new Set();

  // Find jobDays for this date (date may be Timestamp, Date, or string)
  // Deduplicate by jobId — only show one pill per job per date
  for (const jd of BillingState.jobDays) {
    const jdDate = toDateStr(jd.date);
    if (jdDate !== dateStr) continue;
    const job = BillingState.jobs.find(j => j.id === jd.jobId);
    if (!job) continue;
    if (job.status === 'finished') continue;
    if (BillingState.customerFilter !== 'all' && job.customerId !== BillingState.customerFilter) continue;
    if (seenJobIds.has(job.id)) continue;
    seenJobIds.add(job.id);
    results.push({ job, jobDay: jd });
    jobIdsWithJobDays.add(job.id);
  }

  // Legacy jobs with date ranges but no jobDays at all
  // Jobs that use jobDays should ONLY appear on their specific jobDay dates
  const jobIdsWithAnyJobDays = new Set(BillingState.jobDays.map(jd => jd.jobId));
  for (const job of BillingState.jobs) {
    if (jobIdsWithAnyJobDays.has(job.id)) continue;
    if (job.status === 'finished') continue;
    if (BillingState.customerFilter !== 'all' && job.customerId !== BillingState.customerFilter) continue;
    const start = toDateStr(job.startDate);
    const end = toDateStr(job.endDate) || start;
    if (start && dateStr >= start && dateStr <= end) {
      results.push({ job, jobDay: null });
    }
  }

  return results;
}

function getBillingTimeOffForDate(dateStr) {
  if (!BillingState.showTimeOff) return [];
  const real = BillingState.timeOffRequests.filter(req => {
    const start = toDateStr(req.startDate);
    const end = toDateStr(req.endDate) || start;
    if (!start) return false;
    if (dateStr < start || dateStr > end) return false;
    if (Array.isArray(req.excludedDates) && req.excludedDates.includes(dateStr)) return false;
    return true;
  });
  // TASK-10183 — regular days off (weekly rotation), from the getRotationDays callable via the
  // lazy rotationDays.js chunk (window.DDRotations). Approved time off on the same day wins.
  const R = window.DDRotations;
  if (!R) {
    if (!_billingRotLoad) _billingRotLoad = __ddChunkWrap(import('./rotationDays.js?v=10183001')).then(() => renderBillingView()).catch(() => { _billingRotLoad = null; });
    return real;
  }
  if (!_billingRotSub) { _billingRotSub = true; R.subscribe(() => { if (BillingState.viewMode === 'calendar') renderBillingView(); }); }
  const offIds = new Set(real.filter(r => r.type !== 'days_on').map(r => r.personId));
  const rot = R.forDate(dateStr).filter(x => !offIds.has(x.personId) && BillingState.people.some(p => p.id === x.personId))
    .map(x => ({ personId: x.personId, type: 'rotation', _rotation: true, rotationLabel: x.label }));
  return rot.length ? real.concat(rot) : real;
}
let _billingRotLoad = null; let _billingRotSub = false;

function getPersonNameById(personId) {
  const person = BillingState.people.find(p => p.id === personId);
  return person?.name || 'Unknown';
}

function navigateMonth(delta) {
  const d = BillingState.currentDate;
  BillingState.currentDate = new Date(d.getFullYear(), d.getMonth() + delta, 1);
  // renders-audit #10 — dailyPickDate got the TASK-857 guard, the chevrons
  // didn't: paging back past the bounded window rendered months EMPTY that
  // have real tickets. Same guard, keyed to the month's END (its last instant
  // being in-window means the month is fully covered).
  const monthEnd = new Date(d.getFullYear(), d.getMonth() + delta + 1, 0, 23, 59, 59);
  if (_isBeforeTicketWindow(monthEnd)) ensureFullTicketHistory();
  renderBillingView();
}

function goToToday() {
  BillingState.currentDate = new Date();
  renderBillingView();
}

function filterByCustomer(value) {
  BillingState.customerFilter = value;
  renderBillingView();
}

function toggleCustomerDropdown(e) {
  e.stopPropagation();
  const panel = document.getElementById('bcf-dropdown-panel');
  if (!panel) return;
  const isOpen = !panel.classList.contains('hidden');
  if (isOpen) {
    closeCustomerDropdown();
  } else {
    panel.classList.remove('hidden');
    const input = document.getElementById('bcf-search');
    if (input) { input.value = ''; input.focus(); }
    filterCustomerDropdown('');
    // Close on outside click
    setTimeout(() => document.addEventListener('click', _closeDropdownOutside, { once: true }), 0);
  }
}

function _closeDropdownOutside() {
  closeCustomerDropdown();
}

function closeCustomerDropdown() {
  const panel = document.getElementById('bcf-dropdown-panel');
  if (panel) panel.classList.add('hidden');
}

function filterCustomerDropdown(term) {
  const lower = term.toLowerCase().trim();
  const opts = document.querySelectorAll('#bcf-options .bcf-option');
  opts.forEach(opt => {
    if (opt.dataset.value === 'all') {
      opt.style.display = lower ? 'none' : '';
      return;
    }
    const name = opt.textContent.toLowerCase();
    opt.style.display = name.includes(lower) ? '' : 'none';
  });
}

function selectCustomerFilter(value) {
  closeCustomerDropdown();
  BillingState.customerFilter = value;
  renderBillingView();
}

function toggleTimeOff() {
  BillingState.showTimeOff = !BillingState.showTimeOff;
  renderBillingView();
}

// ============================================
// TASK-10043 S3 — "Show dispatch board" overlay + week view (lazy chunk)
// ============================================
// The marks, the week grid and the one billingReview read live in the LAZY
// views/billingBoardOverlay.js (perf budget appColdLoadGzip): it loads only
// when the toggle is on or Week is picked. Deps are handed over, never imported
// (Pitfall #1). Toggle + Month/Week are per-viewer conveniences (localStorage).
let _boardOverlayMod = null;
let _boardOverlayLoad = null;
// Per-viewer pref: read once into BillingState, saved on change (storage may throw).
function _bboPref(key, val) {
  try { if (val === undefined) return localStorage.getItem(key); localStorage.setItem(key, val); } catch (_) { /* session only */ }
  return null;
}
function _boardOverlayOn() {
  if (typeof BillingState.boardOverlay !== 'boolean') BillingState.boardOverlay = _bboPref('dd_billing_board_overlay_v1') === '1';
  return BillingState.boardOverlay;
}
function _billingCalScope() {
  if (!BillingState.calendarScope) BillingState.calendarScope = _bboPref('dd_billing_cal_scope_v1') === 'week' ? 'week' : 'month';
  return BillingState.calendarScope;
}
function _ensureBoardOverlay() {
  if (!_boardOverlayLoad) {
    _boardOverlayLoad = __ddChunkWrap(import('./billingBoardOverlay.js?v=10304134')).then((m) => {
      m.initBoardOverlay({
        BillingState, escapeHtml, truncate, getJobColor, toDateStr, classifyCalendarTicketStatus,
        httpsCallable, functions, renderBillingView, showCalendarJobTickets, openReviewTicket, closeModal,
        _moneyViewOnly, beforeTicketWindow: _isBeforeTicketWindow, ensureFullTicketHistory,
        timeOffForDate: getBillingTimeOffForDate, getPersonNameById,
      });
      _boardOverlayMod = m;
      if (BillingState.viewMode === 'calendar') renderBillingView();
    }).catch((e) => {
      // Never a dead spinner: fall back to the plain month (the switch retries).
      _boardOverlayLoad = null;
      BillingState.boardOverlay = false;
      BillingState.calendarScope = 'month';
      console.warn('[Billing] billingBoardOverlay load failed:', e?.message);
      window.showToast?.('Could not load the dispatch board view — check your connection and try again.', 'error');
      if (BillingState.viewMode === 'calendar') renderBillingView();
    });
  }
}
function toggleBoardOverlay() {
  BillingState.boardOverlay = !_boardOverlayOn();
  _bboPref('dd_billing_board_overlay_v1', BillingState.boardOverlay ? '1' : '0');
  renderBillingView();
}
function setCalendarScope(scope) {
  BillingState.calendarScope = scope === 'week' ? 'week' : 'month';
  _bboPref('dd_billing_cal_scope_v1', BillingState.calendarScope);
  renderBillingView();
}

// ============================================
// RENDER BILLING CALENDAR
// ============================================
function renderBillingCalendar(area) {
  const now = new Date();
  const todayStr = formatDateISO(now);
  const cur = BillingState.currentDate;
  const year = cur.getFullYear();
  const month = cur.getMonth();
  const monthName = cur.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  // TASK-10043 S3 — week view / board overlay need the lazy chunk first.
  const _calWeek = _billingCalScope() === 'week';
  const _ovOn = _boardOverlayOn();
  const _bbo = (_calWeek || _ovOn) ? _boardOverlayMod : null;
  if ((_calWeek || _ovOn) && !_bbo) {
    _ensureBoardOverlay();
    area.innerHTML = '<div class="loading-state" style="padding:60px 0;text-align:center;"><div class="loading-spinner"></div></div>';
    return;
  }
  const _navFn = _calWeek ? 'window.BillingBoardOverlay.navigateWeek' : 'window.BillingModule.navigateMonth';
  const _navTitle = _calWeek ? _bbo.weekTitle(cur) : monthName;

  // Build customer dropdown options
  const customerOpts = _billableCustomers(BillingState.customerFilter).map(c =>
    `<option value="${c.id}" ${BillingState.customerFilter === c.id ? 'selected' : ''}>${escapeHtml(c.name)}</option>`
  ).join('');

  // Calendar controls bar
  let html = `
    <div class="billing-calendar-controls">
      <div class="billing-month-nav">
        <button class="btn-icon" onclick="${_navFn}(-1)" title="Previous ${_calWeek ? 'week' : 'month'}">
          <span class="material-icons">chevron_left</span>
        </button>
        <span class="billing-month-title">${escapeHtml(_navTitle)}</span>
        <button class="btn-icon" onclick="${_navFn}(1)" title="Next ${_calWeek ? 'week' : 'month'}">
          <span class="material-icons">chevron_right</span>
        </button>
        <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.goToToday()" style="margin-left:8px;">Today</button>
        ${['month', 'week'].map(s => `<button type="button" class="btn btn-sm ${(s === 'week') === _calWeek ? 'btn-primary' : 'btn-secondary'}" aria-pressed="${(s === 'week') === _calWeek}" onclick="window.BillingModule.setCalendarScope('${s}')" style="margin-left:${s === 'week' ? 2 : 8}px;">${s === 'week' ? 'Week' : 'Month'}</button>`).join('')}
      </div>
      <div class="billing-calendar-filters">
        <div class="billing-customer-filter-wrap${BillingState.customerFilter !== 'all' ? ' active-filter' : ''}" id="billing-customer-dropdown" onclick="window.BillingModule.toggleCustomerDropdown(event)">
          <span class="material-icons billing-filter-icon">filter_list</span>
          <span class="billing-customer-label">${BillingState.customerFilter === 'all' ? 'All Companies' : escapeHtml(BillingState.customers.find(c => c.id === BillingState.customerFilter)?.name || 'All Companies')}</span>
          <span class="material-icons bcf-chevron">expand_more</span>
          <div class="bcf-dropdown hidden" id="bcf-dropdown-panel" onclick="event.stopPropagation()">
            <div class="bcf-search-wrap">
              <span class="material-icons bcf-search-icon">search</span>
              <input type="text" class="bcf-search-input" placeholder="Search customers..." id="bcf-search" oninput="window.BillingModule.filterCustomerDropdown(this.value)" />
            </div>
            <div class="bcf-options" id="bcf-options">
              <div class="bcf-option${BillingState.customerFilter === 'all' ? ' bcf-selected' : ''}" data-value="all" onclick="window.BillingModule.selectCustomerFilter('all')">
                <span class="material-icons bcf-check">check</span> All Companies
              </div>
              ${_billableCustomers(BillingState.customerFilter).map(c => `
                <div class="bcf-option${BillingState.customerFilter === c.id ? ' bcf-selected' : ''}" data-value="${c.id}" onclick="window.BillingModule.selectCustomerFilter('${c.id}')">
                  <span class="material-icons bcf-check">check</span> ${escapeHtml(c.name)}
                </div>
              `).join('')}
            </div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
          <input type="checkbox" id="billing-timeoff-toggle" ${BillingState.showTimeOff ? 'checked' : ''} onchange="window.BillingModule.toggleTimeOff()">
          <label for="billing-timeoff-toggle" style="cursor:pointer;margin:0;font-size:0.8125rem;color:var(--text-secondary);">Time Off</label>
        </div>
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
          <input type="checkbox" id="billing-board-overlay-toggle" ${_ovOn ? 'checked' : ''} onchange="window.BillingModule.toggleBoardOverlay()">
          <label for="billing-board-overlay-toggle" style="cursor:pointer;margin:0;font-size:0.8125rem;color:var(--text-secondary);">Show dispatch board</label>
        </div>
      </div>
    </div>
  `;

  // TASK-10043 S3 — the week view is the lazy chunk's (overlay on or off).
  if (_calWeek) {
    area.innerHTML = html + _bbo.weekHtml(cur, _ovOn);
    return;
  }
  // Overlay on: mark the whole month in one pass; each cell reads from it.
  const _bboMonth = _ovOn ? _bbo.prepareMonth(cur) : null;
  if (_bboMonth) html += _bboMonth.banner;

  // Month grid
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay(); // 0=Sun
  const daysInMonth = lastDay.getDate();

  // Weekday headers
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  html += `
    <div class="billing-month-view">
      <div class="billing-month-header">
        ${weekdays.map(d => `<div class="billing-weekday">${d}</div>`).join('')}
      </div>
      <div class="billing-month-grid">
  `;

  // Empty cells before month starts
  for (let i = 0; i < startDow; i++) {
    html += `<div class="billing-month-day empty"></div>`;
  }

  // Day cells
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isToday = dateStr === todayStr;
    // TASK-10043 S3 — overlay on: the board's job days (marked) replace AR/AP's
    // job + no-job pills; time off stays.
    const jobEntries = _bboMonth ? [] : getBillingJobsForDate(dateStr);
    const timeOffEntries = getBillingTimeOffForDate(dateStr);
    const _tmdCount = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
    const unassignedCount = _bboMonth ? 0 : (BillingState.tickets || []).filter(t => !t.jobId && _tmdCount(t) && !t.archived).length;
    const maxShow = 4;
    const _bboCell = _bboMonth ? _bbo.monthCellHtml(_bboMonth, dateStr, maxShow) : null;
    const totalEntries = jobEntries.length + unassignedCount + timeOffEntries.length + (_bboCell ? _bboCell.total : 0);

    html += `<div class="billing-month-day${isToday ? ' today' : ''}" onclick="${_bboMonth ? 'window.BillingBoardOverlay.openDay' : 'window.BillingModule.showBillingDayDetails'}('${dateStr}')" style="cursor:pointer;">`;
    html += `<div class="billing-day-num${isToday ? ' today' : ''}">${day}</div>`;

    // Job pills (show up to maxShow)
    let shown = 0;
    if (_bboCell) { html += _bboCell.html; shown += Math.min(_bboCell.total, maxShow); }
    for (let i = 0; i < jobEntries.length && shown < maxShow; i++) {
      const { job, jobDay } = jobEntries[i];
      const color = getJobColor(job.id);
      const startTime = jobDay?.startTime || job.defaultStart || job.startTime || '';
      const displayTime = startTime ? formatJobTime(startTime) : '';
      const _tmd = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
      const hasActiveTicket = (BillingState.tickets || []).some(t => t.jobId === job.id && _tmd(t) && BillingState.activeTicketIds?.has(t.id));
      let jobTicketsForDate = (BillingState.tickets || []).filter(t => t.jobId === job.id && _tmd(t));
      // TASK-1346 sweep — "is this billed?" (display): the calendar pill went
      // green only on the boolean, so a job whose tickets all carry invoice
      // numbers still read as unbilled work on the month view.
      // Archived tickets carry status 'archived'/'locked' (not the dead `archived`
      // boolean). The decision is shared with the dispatch calendars (TASK-1387)
      // via classifyCalendarTicketStatus — invoiced=green overrides draft/ready.
      const calStatus = classifyCalendarTicketStatus(jobTicketsForDate);
      const allInvoiced = calStatus === 'invoiced';
      const allSettled = calStatus === 'archived';
      let ticketStatusDot = '';
      if (calStatus === 'invoiced') {
        ticketStatusDot = '<span style="width:8px;height:8px;border-radius:50%;background:#22C55E;border:1.5px solid rgba(255,255,255,0.7);flex-shrink:0;" title="Invoiced"></span>';
      } else if (calStatus === 'draft') {
        ticketStatusDot = '<span style="width:8px;height:8px;border-radius:50%;background:#EF4444;border:1.5px solid rgba(255,255,255,0.7);flex-shrink:0;" title="Draft"></span>';
      } else if (calStatus === 'ready') {
        ticketStatusDot = '<span style="width:8px;height:8px;border-radius:50%;background:#F59E0B;border:1.5px solid rgba(255,255,255,0.7);flex-shrink:0;" title="Ready"></span>';
      } else if (calStatus === 'archived') {
        ticketStatusDot = '<span style="width:8px;height:8px;border-radius:50%;background:#94A3B8;border:1.5px solid rgba(255,255,255,0.7);flex-shrink:0;" title="Archived — may already be invoiced in your accounting system"></span>';
      }
      html += `
        <div class="billing-job-pill${allInvoiced ? ' invoiced' : (allSettled ? ' archived' : '')}" style="background:${color};" onclick="event.stopPropagation();window.BillingModule.showCalendarJobTickets('${job.id}','${dateStr}')">
          ${hasActiveTicket ? '<span class="material-icons" style="font-size:10px;color:#fff;animation:activePulse 2s ease-in-out infinite;border-radius:50%;margin-right:2px;">radio_button_checked</span>' : '<span class="billing-job-dot" style="background:#fff;"></span>'}
          <span class="billing-job-name">${escapeHtml(truncate(job.title || job.customer || 'Job', 18))}</span>
          ${displayTime ? `<span class="billing-job-time">${displayTime}</span>` : ''}
          ${ticketStatusDot ? `<span style="margin-left:auto;flex-shrink:0;display:flex;align-items:center;">${ticketStatusDot}</span>` : ''}
        </div>
      `;
      shown++;
    }

    // Unassigned ticket pills (tickets without a jobId on this date)
    const _tmdUA = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
    const unassignedTickets = (BillingState.tickets || []).filter(t => !t.jobId && _tmdUA(t) && !t.archived);
    for (let i = 0; i < unassignedTickets.length && shown < maxShow; i++) {
      const ut = unassignedTickets[i];
      const utLabel = ut.customerName || 'No Job';
      const utNo = ut.ticketNo ? `#${ut.ticketNo}` : '';
      // TASK-1346 sweep — "is this billed?" (display).
      const utStatus = isTicketInvoiced(ut) ? 'invoiced' : (ut.status || 'draft');
      const utDotColor = isTicketInvoiced(ut) ? '#22C55E'
        : utStatus === 'ready' ? '#F59E0B'
        : (utStatus === 'archived' || utStatus === 'locked') ? '#94A3B8'
        : '#EF4444';
      // TASK-1411 — this pill's slate body is NOT a status and NOT "Shop": it
      // means the ticket has no job attached (Chris read it as a category). The
      // job-attached renderer above uses the JOB's colour, so grey is simply the
      // absence of one. Say so on hover, and swap the anonymous white dot for a
      // link_off glyph so it reads without the legend below.
      html += `
        <div class="billing-job-pill" style="background:#64748B;" title="No job attached — ad-hoc ticket" onclick="event.stopPropagation();window.BillingModule.openReviewTicket('${ut.id}')">
          <span class="material-icons" style="font-size:10px;color:#fff;margin-right:2px;flex-shrink:0;">link_off</span>
          <span class="billing-job-name">${escapeHtml(truncate(utNo ? utNo + ' ' + utLabel : utLabel, 18))}</span>
          <span style="margin-left:auto;flex-shrink:0;display:flex;align-items:center;">
            <span style="width:8px;height:8px;border-radius:50%;background:${utDotColor};border:1.5px solid rgba(255,255,255,0.7);flex-shrink:0;" title="${capitalizeFirst(utStatus)}"></span>
          </span>
        </div>
      `;
      shown++;
    }

    // Time-off pills
    for (let i = 0; i < timeOffEntries.length && shown < maxShow; i++) {
      const req = timeOffEntries[i];
      const name = req.personName || getPersonNameById(req.personId);
      const firstName = name.split(' ')[0];
      html += `
        <div class="billing-timeoff-pill">
          <span class="billing-job-name">${req._rotation ? 'ROTATION' : 'OFF'}: ${escapeHtml(firstName)}</span>
        </div>
      `;
      shown++;
    }

    // Overflow indicator
    if (totalEntries > maxShow) {
      html += `<div class="billing-more-link">+${totalEntries - maxShow} more</div>`;
    }

    html += `</div>`;
  }

  // Empty cells after month ends
  const totalCells = startDow + daysInMonth;
  const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
  for (let i = 0; i < remaining; i++) {
    html += `<div class="billing-month-day empty"></div>`;
  }

  html += `
      </div>
    </div>
  `;
  if (_bboMonth) { area.innerHTML = html + _bbo.legendHtml(_bboMonth.counts); return; }

  // TASK-1411 — the calendar's colour language was undocumented: the pill BODY
  // encodes job-vs-no-job, the right-hand DOT encodes billing status, and
  // nothing on screen said either (Chris: "why are the other ones gray?").
  // Footer legend rather than a header block so it never competes with the
  // month grid; wraps on narrow widths instead of pushing the cells.
  html += `
    <div class="billing-calendar-legend" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 16px;margin-top:10px;padding:8px 10px;border-top:1px solid var(--border-color,#E2E8F0);font-size:0.75rem;color:var(--text-secondary);">
      <span style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:#EF4444;flex-shrink:0;"></span>Draft</span>
      <span style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:#F59E0B;flex-shrink:0;"></span>Ready</span>
      <span style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:#22C55E;flex-shrink:0;"></span>Invoiced</span>
      <span style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:#94A3B8;flex-shrink:0;"></span>Archived</span>
      <span style="display:flex;align-items:center;gap:5px;"><span style="width:14px;height:10px;border-radius:3px;background:#64748B;flex-shrink:0;"></span>Grey pill = no job attached (coloured = the job's colour)</span>
    </div>
  `;

  area.innerHTML = html;
}

function formatJobTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m ? `${h12}:${String(m).padStart(2, '0')}${ampm}` : `${h12}${ampm}`;
}

// ============================================
// DAY DETAILS MODAL
// ============================================
function showBillingDayDetails(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const formattedDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  document.getElementById('billing-day-title').textContent = formattedDate;

  const jobEntries = getBillingJobsForDate(dateStr);
  const timeOffEntries = getBillingTimeOffForDate(dateStr);

  let html = '<div class="day-details-list">';

  const _dayTmdUA2 = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
  const unassignedDayTickets = (BillingState.tickets || []).filter(t => !t.jobId && _dayTmdUA2(t) && !t.archived);

  if (jobEntries.length === 0 && timeOffEntries.length === 0 && unassignedDayTickets.length === 0) {
    html += '<div class="day-empty"><span class="material-icons">event_busy</span> No jobs scheduled</div>';
  } else {
    // Jobs
    for (const { job, jobDay } of jobEntries) {
      const color = getJobColor(job.id);
      const customerName = job.customer || BillingState.customers.find(c => c.id === job.customerId)?.name || 'No customer';
      const startTime = jobDay?.startTime || job.defaultStart || job.startTime || '';

      // Build assignments line
      let assignParts = [];
      if (jobDay) {
        if (jobDay.drivers?.length > 0) {
          const names = jobDay.drivers.map(ref => {
            const p = BillingState.people.find(x => x.id === ref || x.name === ref);
            return p ? p.name.split(' ')[0] : (typeof ref === 'string' && ref.includes(' ') ? ref.split(' ')[0] : ref);
          });
          assignParts.push(names.join(', '));
        }
        if (jobDay.trucks?.length > 0) {
          // Trucks aren't in BillingState, just show the ref (TASK-1429: the
          // identity map + unused people read it carried are gone — perf budget).
          assignParts.push(jobDay.trucks.join(', '));
        }
      }
      const assignmentsHtml = assignParts.length > 0
        ? assignParts.map(p => `<span class="assignment-badge">${escapeHtml(p)}</span>`).join('  ')
        : '';

      // Ticket status for this job on this date
      const _dayTmd = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
      let dayJobTickets = (BillingState.tickets || []).filter(t => t.jobId === job.id && _dayTmd(t));
      if (dayJobTickets.length === 0) dayJobTickets = (BillingState.tickets || []).filter(t => t.jobId === job.id);
      let ticketStatusHtml = '';
      if (dayJobTickets.length > 0) {
        const badges = dayJobTickets.map(t => {
          let b = '';
          // TASK-1346 sweep — "is this billed?" (display).
          if (isTicketInvoiced(t) && t.invoiceNumber) b += `<span class="badge badge-invoice-num badge-sm">${escapeHtml(t.invoiceNumber)}</span> `;
          if (isTicketInvoiced(t)) b += '<span class="badge badge-invoiced badge-sm"><span class="material-icons">check_circle</span>Invoiced</span> ';
          else b += `<span class="badge badge-${t.status} badge-sm">${capitalizeFirst(t.status)}</span> `;
          return b;
        });
        ticketStatusHtml = `<div style="margin-top:4px;display:flex;gap:4px;flex-wrap:wrap;">${badges.join('')}</div>`;
      }

      html += `
        <div class="day-job-placard" style="border-left-color: ${color};" onclick="window.BillingModule.closeModal('billing-day');window.BillingModule.showCalendarJobTickets('${job.id}','${dateStr}')">
          <div class="placard-header">
            <span class="placard-title">${escapeHtml(job.title || 'Untitled')}</span>
            ${startTime ? `<span class="placard-time">${formatJobTime(startTime)}</span>` : ''}
          </div>
          <div class="placard-customer">${escapeHtml(customerName)}</div>
          ${assignmentsHtml ? `<div class="placard-assignments">${assignmentsHtml}</div>` : ''}
          ${ticketStatusHtml}
        </div>
      `;
    }

    // Unassigned tickets (no job)
    for (const ut of unassignedDayTickets) {
      const utNo = ut.ticketNo ? `#${ut.ticketNo}` : '';
      const utCust = ut.customerName || 'No Customer';
      // TASK-1346 sweep — "is this billed?" (display).
      const utStatus = isTicketInvoiced(ut) ? 'invoiced' : (ut.status || 'draft');
      let utBadge = '';
      if (isTicketInvoiced(ut)) utBadge = '<span class="badge badge-invoiced badge-sm">Invoiced</span>';
      else utBadge = `<span class="badge badge-${utStatus} badge-sm">${capitalizeFirst(utStatus)}</span>`;

      html += `
        <div class="day-job-placard" style="border-left-color: #64748B;" onclick="window.BillingModule.closeModal('billing-day');window.BillingModule.openReviewTicket('${ut.id}')">
          <div class="placard-header">
            <span class="placard-title">${utNo ? utNo + ' — ' : ''}${escapeHtml(utCust)}</span>
          </div>
          <div class="placard-customer" style="color:var(--text-secondary);font-style:italic;">No Job Assigned</div>
          <div style="margin-top:4px;display:flex;gap:4px;flex-wrap:wrap;">${utBadge}</div>
        </div>
      `;
    }

    // Time-off entries (compact)
    for (const req of timeOffEntries) {
      const name = req.personName || getPersonNameById(req.personId);
      const typeLabel = req._rotation ? (req.rotationLabel || 'Regular day off') : (req.type || 'unpaid').charAt(0).toUpperCase() + (req.type || 'unpaid').slice(1);
      const _c = req._rotation ? '#4F46E5' : '#EF4444'; // TASK-10183 — a regular day off is not leave: indigo, not red
      html += `
        <div style="display:flex;align-items:center;gap:6px;padding:6px 10px;background:${req._rotation ? 'rgba(79,70,229,0.08)' : 'rgba(239,68,68,0.08)'};border-left:3px solid ${_c};border-radius:6px;margin-bottom:6px;">
          <span class="material-icons" style="font-size:16px;color:${_c};">${req._rotation ? 'event_repeat' : 'event_busy'}</span>
          <span style="font-weight:600;font-size:0.85rem;color:${_c};">${req._rotation ? 'ROTATION' : 'OFF'}: ${escapeHtml(name)}</span>
          <span style="font-size:0.75rem;color:var(--text-secondary);margin-left:auto;">${escapeHtml(typeLabel)}</span>
        </div>
      `;
    }
  }

  html += '</div>';
  document.getElementById('billing-day-content').innerHTML = html;
  document.getElementById('modal-billing-day')?.classList.remove('hidden');
}

function closeDayThenShowJob(jobId) {
  closeModal('billing-day');
  showBillingJobDetail(jobId);
}

// ============================================
// TICKETS VIEW (with optional job filter)
// ============================================
// =============================================================================
// TASK-9725 S2 UI — "Supplies billed to customers: needs a look". The card, its
// loader and row text live in the LAZY views/billingPpeExceptions.js (integ/2100,
// perf budget appColdLoadGzip: it paints nothing before the Tickets view). The
// first Tickets render starts the import and the module re-renders the view
// when it lands — the same "absent until loaded" the card already had while
// its callable was in flight. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _ppeExcMod = null;
let _ppeExcModLoad = null;
function _ppeExcDeps() { return { BillingState, httpsCallable, functions, escapeHtml, renderBillingView }; }
function _loadPpeExcMod() {
  if (_ppeExcMod) return Promise.resolve(_ppeExcMod);
  if (!_ppeExcModLoad) {
    _ppeExcModLoad = __ddChunkWrap(import('./billingPpeExceptions.js?v=9725201')).then((m) => {
      _ppeExcMod = m;
      if (BillingState.viewMode === 'tickets') renderBillingView();
      return m;
    }).catch((e) => {
      _ppeExcModLoad = null; // the next Tickets render tries again
      console.warn('[Billing] billingPpeExceptions load failed:', e?.message || e);
      return null;
    });
  }
  return _ppeExcModLoad;
}
function renderPpeBillExceptionsCard() {
  if (!_ppeExcMod) { _loadPpeExcMod(); return ''; }
  return _ppeExcMod.renderPpeBillExceptionsCard(_ppeExcDeps());
}
function togglePpeBillExceptions() { _loadPpeExcMod().then((m) => { if (m) m.togglePpeBillExceptions(_ppeExcDeps()); }); }
function refreshPpeBillExceptions() { _loadPpeExcMod().then((m) => { if (m) m.refreshPpeBillExceptions(_ppeExcDeps()); }); }

function renderTicketsView(area, countEl) {
  // Build filter bar + breadcrumb
  let topBar = '';

  // TASK-9414 — the project filter needs the projects list for names/options.
  // One-shot getDocs (loadBillingProjects), re-rendering Tickets when it lands.
  if (!BillingState._projectsLoaded && !BillingState._projectsLoading) loadBillingProjects();

  if (BillingState.ticketProjectMode === 'project') {
    // TASK-1618 — project drill-down breadcrumb. Checked BEFORE the job filter
    // because the two are mutually exclusive by construction (each drill-down
    // clears the other) and the project is the wider scope.
    // TASK-9414 — job count derived from the LIVE jobs, not a captured Set.
    const _pid = BillingState.ticketProjectFilter;
    const jobCount = BillingState._jobsLoaded ? BillingState.jobs.filter(j => j.projectId === _pid).length : 0;
    topBar = `
      <div class="billing-ticket-filter-bar">
        <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.switchBillingView('projects')">
          <span class="material-icons" style="font-size:16px;">arrow_back</span>
          Projects
        </button>
        <span style="color:var(--text-secondary);font-size:0.875rem;">Tickets for project:</span>
        <strong style="font-size:0.875rem;">${escapeHtml(BillingState.ticketProjectFilterName || 'Project')}</strong>
        <span style="color:var(--text-secondary);font-size:0.8rem;">${BillingState._jobsLoaded ? `${jobCount} job${jobCount !== 1 ? 's' : ''}` : 'loading jobs…'}</span>
      </div>
    `;
  } else if (BillingState.ticketJobFilter) {
    topBar = `
      <div class="billing-ticket-filter-bar">
        <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.backToCalendar()">
          <span class="material-icons" style="font-size:16px;">arrow_back</span>
          Calendar
        </button>
        <span style="color:var(--text-secondary);font-size:0.875rem;">Tickets for:</span>
        <strong style="font-size:0.875rem;">${escapeHtml(BillingState.ticketJobFilterName || 'Job')}</strong>
      </div>
    `;
  }

  // TASK-9725 S2 UI — billed-PPE roll-ups that could not land, under the breadcrumb.
  topBar += renderPpeBillExceptionsCard();

  // Status filter pills
  // TASK-1345 (C) — "All" was a lie: it EXCLUDED archived/locked, so archived
  // work vanished from the one view anyone reconciling would use. Renamed to
  // "Active" (which is what it always was) and archived tickets come back via
  // the checkbox below — with the hidden count always visible, so 103 archived
  // tickets holding $152,096 can never again be silently absent. The separate
  // Archived pill stays for the archived-only view.
  const filters = [
    { key: 'all', icon: 'list', label: 'Active' },
    { key: 'daily', icon: 'today', label: 'Daily' },
    { key: 'draft', icon: 'edit_note', label: 'Draft' },
    { key: 'ready', icon: 'check_circle', label: 'Ready' },
    { key: 'invoiced', icon: 'receipt', label: 'Invoiced' },
    { key: 'paid', icon: 'payments', label: 'Paid' },
    { key: 'written_off', icon: 'money_off', label: 'Written Off' },
    { key: 'archived', icon: 'inventory_2', label: 'Archived' }
  ];

  const exportBtnStyle = 'background:linear-gradient(135deg,#2E7D32,#43A047);color:#fff;border:none;white-space:nowrap;padding:6px 14px;border-radius:8px;font-weight:600;font-size:13px;box-shadow:0 1px 3px rgba(0,0,0,0.12);cursor:pointer;';

  // TASK-9340 — TWO control bands above the list, not four (six counting the
  // page header and the module nav, which sit above this area untouched).
  //   Band 1 `.tickets-toolbar`  = status pills | Missing Permits + delivery chips
  //   Band 2 `.tcf-bar`          = company filter · search · Show archived (+ N hidden) · Export
  // The delivery chips JOIN the status row but stay their own axis — they are
  // NOT folded into BillingState.filter (see TICKET_DELIVERY_FILTERS: "Ready" +
  // "Unsent" must still compose), each keeps its own count, and none is summed
  // into a combined "needs attention" total (Awaiting and Edited overlap).
  // The status pills are shared by desktop and phone (styles.css restyles
  // `.tickets-filters--desktop` compact at <=768px); band 2 is desktop-only and
  // the phone keeps its own DOM, `.billing-mobile-compact`, below.
  //
  // Status = ONE segmented control, text only (outside review 2026-09-29:
  // "bubble soup" — every control on the bar had the same weight). Icons stay
  // on the module nav, not here. Everything on the right (count, Missing
  // Permits, delivery chips) lives in ONE `.tickets-toolbar-right` group pushed
  // over with margin-left:auto — COD: `space-between` on a wrapping row jumps
  // when the conditional Missing Permits chip mounts. Buttons carry
  // aria-pressed, not tab roles: status and delivery COMPOSE (Ready + Unsent),
  // so neither group is a tablist.
  let filterHtml = `<div class="tickets-toolbar">
    <div class="tickets-filters tickets-filters--desktop tickets-status-seg" role="group" aria-label="Ticket status">`;
  for (const f of filters) {
    const _on = BillingState.filter === f.key;
    filterHtml += `
      <button type="button" class="filter-btn filter-btn--${f.key} ${_on ? 'active' : ''}" aria-pressed="${_on}" onclick="window.BillingModule.switchFilter('${f.key}')">${f.label}</button>`;
  }
  filterHtml += `</div>
    <div class="tickets-toolbar-right">
      <span id="tickets-count" class="tickets-count"></span>`;

  // TASK-4649 — "Missing Permits (N)" triage chip (COD Binding 4: a red border
  // alone gets ignored; billing needs to triage, not hunt). Renders only when
  // permit tracking is on AND something is actually flagged — 85 of 86 tenants
  // never see it. Click = drill down to just the flagged tickets; click again
  // to clear. TASK-9340: sits on the status toolbar instead of its own row.
  if (_permitTrackingEnabled()) {
    const _mpCount = _countMissingPermits();
    if (_mpCount > 0 || BillingState.missingPermitFilter) {
      filterHtml += `
        <button type="button" class="permit-missing-chip tickets-toolbar-chip${BillingState.missingPermitFilter ? ' active' : ''}" aria-pressed="${!!BillingState.missingPermitFilter}" onclick="window.BillingModule.toggleMissingPermitFilter()"
          title="${BillingState.missingPermitFilter ? 'Show all tickets again' : 'Show only tickets on permit-required jobs with no permit attached'}">
          <span class="material-icons" style="font-size:15px;">warning_amber</span>
          Missing Permits (${_mpCount})${BillingState.missingPermitFilter ? '<span class="material-icons" style="font-size:14px;margin-left:2px;">close</span>' : ''}
        </button>`;
    }
  }

  // TASK-4662 — the customer-delivery chips. Same orthogonal-chip home as the
  // Missing Permits chip directly above, and applied at the same point in
  // renderTicketsList. Always rendered: "which ones did I miss" is a question
  // about ABSENCE, and a chip that only appears once something is wrong cannot
  // answer it. TASK-9340: they live on the status toolbar now (compact, each
  // with its own count), not on a row of their own.
  filterHtml += _ticketDeliveryFilterHtml();
  // TASK-10041 — "Needs decision" chip (dispatched work with no ticket).
  filterHtml += _reviewChipHtml();
  filterHtml += _integrityChip('toolbarChipHtml'); // TASK-10045 S2
  // TASK-9654 — QuickBooks aging buckets (hidden until a paid-sync report has aging).
  filterHtml += _qboAgingFilterHtml();
  _loadQboAging();
  filterHtml += _invoicesPanelChipHtml(); // TASK-4767 S2
  filterHtml += `</div></div>`;

  // The <select> copy of the status filter. styles.css hides it at every width
  // today (default `display:none`; the <=768px block re-hides it with
  // !important because the phone uses the compact pills above). Kept as-is —
  // TASK-9340 is a layout pass, not a markup purge. Its Export button moved out:
  // Export now lives on band 2 (desktop) and in `.billing-mobile-compact` (phone).
  filterHtml += `<div class="tickets-filters tickets-filters--mobile">
    <select class="tickets-filter-select" onchange="window.BillingModule.switchFilter(this.value)">
      ${filters.map(f => `<option value="${f.key}" ${BillingState.filter === f.key ? 'selected' : ''}>${f.label}</option>`).join('')}
    </select>
  </div>`;

  // TASK-1345 (C) — the Active view's archived toggle. Default OFF (COD
  // Independent: unioning ~520 archived docs into the default list is a
  // pagination/perf risk), but the hidden COUNT renders either way — computed
  // from already-loaded docs — because "silently hidden" is the actual defect.
  // TASK-4740 (A) — Chris, 2026-09-08: *"can we put that 'show archived' on the
  // same row as 'all companies' filter … it doesn't need it's own row imo."*
  // The markup is BUILT here and PLACED twice below (desktop `.tcf-bar`, mobile
  // `.billing-mobile-compact`); it no longer emits a full-width row of its own.
  //
  // 🔴 WHY TWO PLACEMENTS AND NOT ONE MOVE. `.tcf-bar` is
  // `display:none !important` under 768px and again in the standalone/PWA block
  // (styles.css :36461, :36589) — the mobile UI is `.billing-mobile-compact`
  // instead. `.tickets-archived-toggle` has no CSS rule anywhere and was a
  // SIBLING of `.tcf-bar`, so it is the thing that is visible on phones today.
  // Moving the markup into `.tcf-bar` alone would have deleted "Show archived"
  // AND its "N archived hidden" warning from every phone — a straight
  // regression on the exact silently-hidden defect TASK-1345 (C) exists to fix.
  // The two copies are never both displayed (`.billing-mobile-compact` is
  // `display:none` above the breakpoint), neither carries an id, and both call
  // the same handler, so there is no duplicate-id trap like TASK-1232 (1).
  let archivedToggleHtml = '';
  if (BillingState.filter === 'all') {
    // Computed during filtering, which runs AFTER this bar on the first render
    // (COD Code Reviewer) — fall back to counting the loaded docs so the very
    // first paint still tells the truth.
    const _hidden = (typeof BillingState._archivedHiddenCount === 'number' && BillingState._archivedHiddenCount > 0)
      ? BillingState._archivedHiddenCount
      : countArchivedHidden((BillingState.tickets || []).filter(t => t.status !== 'deleted'));
    // `margin-left:auto` and NOT `justify-content:space-between` on the parent:
    // the toggle only renders on the `all` status filter, so a space-between row
    // would leave an empty right half on every other filter and the All
    // Companies dropdown would visibly jump as the status changed. With
    // margin-left:auto there is simply no right cluster and the left control
    // stays put. `flex-shrink:0` keeps a long `.tcf-selected-chips` run from
    // squeezing the toggle off the row — the chips wrap, the toggle does not.
    archivedToggleHtml = `<div class="tickets-archived-toggle" style="display:flex;align-items:center;gap:8px;font-size:0.8rem;color:var(--text-primary);">
      <label style="display:inline-flex;align-items:center;gap:6px;cursor:pointer;">
        <input type="checkbox" ${BillingState.showArchived ? 'checked' : ''} onchange="window.BillingModule.toggleShowArchived(this.checked)" style="cursor:pointer;">
        Show archived
      </label>
      ${!BillingState.showArchived && _hidden > 0
        ? `<span style="color:#92400E;font-weight:600;">${_hidden} archived hidden</span>` : ''}
    </div>`;
  }

  // Customer multi-select: compact dropdown + selected chips
  const tcf = BillingState.ticketCustomerFilter;
  const hasCustomerFilter = tcf.size > 0;
  const sortedCustomers = [..._billableCustomers([...tcf])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const filterLabel = !hasCustomerFilter ? 'All Companies' : tcf.size === 1
    ? escapeHtml(BillingState.customers.find(c => tcf.has(c.id))?.name || '1 company')
    : `${tcf.size} companies`;
  // TASK-9340 — band 2's search box (was its own `.billing-search-wrap` row).
  // Same id, handler and clear button as before; only its home moved.
  const searchInputHtml = `
      <div class="tcf-search-inline">
        <span class="material-icons" style="position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:18px;color:var(--brand-blue);pointer-events:none;">search</span>
        <input type="text" id="ticket-search-input" placeholder="Search tickets…" value="${window.escAttr(BillingState.ticketSearch || '')}"
          oninput="window.BillingModule.onTicketSearch(this.value)"
          style="width:100%;padding:7px 34px 7px 36px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:0.85rem;font-weight:500;background:#fff;color:var(--text-primary);outline:none;transition:border-color 0.15s;"
          onfocus="this.style.borderColor='#3b82f6'" onblur="this.style.borderColor='#e2e8f0'">
        ${BillingState.ticketSearch ? `<button onclick="var i=this.parentNode.querySelector('input');if(i)i.value='';window.BillingModule.onTicketSearch('');" title="Clear search" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);background:#e2e8f0;border:none;cursor:pointer;color:var(--text-secondary);padding:2px;line-height:1;border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;">
          <span class="material-icons" style="font-size:14px;">close</span>
        </button>` : ''}
      </div>`;
  // TASK-9340 — Export ends band 2 (was absolutely positioned over the status
  // pills). `.billing-export-btn` keeps its <=768px hide; the phone's Export is
  // `.billing-mc-export` in `.billing-mobile-compact`.
  const exportHtml = `
      <button class="btn btn-sm billing-export-btn" style="${exportBtnStyle}flex-shrink:0;" onclick="window.BillingModule.exportFilteredTickets()" title="Export visible tickets to CSV">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;">download</span> Export
      </button>`;
  let customerChipsHtml = `
    <div class="tcf-bar">
      <div class="tcf-trigger${hasCustomerFilter ? ' tcf-active' : ''}" onclick="window.BillingModule.toggleTcfDropdown(event)">
        <span class="material-icons" style="font-size:16px;">filter_list</span>
        <span class="tcf-label">${filterLabel}</span>
        <span class="material-icons tcf-chevron">expand_more</span>
      </div>
      ${/* TASK-9414 — Project filter, right beside the company filter. Painted
           (with counts) by renderTicketsList → _repaintTicketProjectFilter. */''}
      <span class="tpf-slot" data-variant="desktop" style="display:none;align-items:center;gap:4px;flex-shrink:0;"></span>
      ${hasCustomerFilter ? `
        <div class="tcf-selected-chips">
          ${[...tcf].map(id => {
            const c = BillingState.customers.find(x => x.id === id);
            return c ? `<span class="tcf-chip">${escapeHtml(c.name)}<button onclick="event.stopPropagation();window.BillingModule.toggleTicketCustomer('${c.id}')"><span class="material-icons">close</span></button></span>` : '';
          }).join('')}
          <button class="tcf-clear" onclick="window.BillingModule.toggleTicketCustomer('all')">Clear all</button>
        </div>
      ` : ''}
      ${searchInputHtml}
      ${archivedToggleHtml}
      ${exportHtml}
      <div class="tcf-dropdown hidden" id="tcf-dropdown-panel" onclick="event.stopPropagation()">
        <div class="tcf-search-wrap">
          <span class="material-icons tcf-search-icon">search</span>
          <input type="text" class="tcf-search-input" placeholder="Search companies..." id="tcf-search" oninput="window.BillingModule.filterTcfDropdown(this.value, this)">
        </div>
        <div class="tcf-options" id="tcf-options">
          ${sortedCustomers.map(c => `
            <label class="tcf-option" data-name="${escapeHtml(c.name.toLowerCase())}">
              <input type="checkbox" ${tcf.has(c.id) ? 'checked' : ''} onchange="window.BillingModule.toggleTicketCustomer('${c.id}')">
              <span>${escapeHtml(c.name)}</span>
            </label>
          `).join('')}
        </div>
      </div>
    </div>`;

  // Daily date nav (only when daily filter active)
  const dailyNavHtml = BillingState.filter === 'daily' ? (() => {
    const d = BillingState.dailyDate;
    const isToday = d.toDateString() === new Date().toDateString();
    const isYesterday = (() => { const y = new Date(); y.setDate(y.getDate() - 1); return d.toDateString() === y.toDateString(); })();
    const dayName = isToday ? 'Today' : isYesterday ? 'Yesterday' : d.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const isoStr = formatDateISO(d);
    return `
      <div class="daily-date-nav">
        <button class="daily-nav-arrow" onclick="window.BillingModule.navigateDailyDate(-1)" title="Previous day">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="daily-nav-center" onclick="document.getElementById('daily-date-picker').showPicker?.()||document.getElementById('daily-date-picker').focus()" style="cursor:pointer;" title="Pick a date">
          <span class="daily-nav-day">${dayName}</span>
          <span class="daily-nav-date">${dateStr}</span>
          <input type="date" id="daily-date-picker" value="${isoStr}" onchange="window.BillingModule.dailyPickDate(this.value)"
            style="position:absolute;opacity:0;width:0;height:0;pointer-events:none;">
        </div>
        <button class="daily-nav-arrow" onclick="window.BillingModule.navigateDailyDate(1)" title="Next day">
          <span class="material-icons">chevron_right</span>
        </button>
        ${!isToday ? `<button class="daily-nav-today" onclick="window.BillingModule.dailyGoToToday()">Today</button>` : ''}
      </div>`;
  })() : '';

  // Mobile-compact: merge filter + customer + search into one tight block.
  // TASK-9340 — the phone gets the same compression in its OWN DOM (`.tcf-bar` is
  // display:none !important at <=768px, so nothing here can be shared with
  // band 2): back · company · search · Export on ONE row (search used to be a
  // second row), then the selected-company chips and Show archived (+ N hidden)
  // — every control the phone had, one row fewer.
  const mobileCompactHtml = `<div class="billing-mobile-compact">
    <div class="billing-mc-row1">
      <button class="btn btn-sm btn-secondary billing-mc-back" onclick="window.BillingModule.backToCalendar()">
        <span class="material-icons" style="font-size:16px;">arrow_back</span>
      </button>
      <div class="tcf-trigger billing-mc-company${hasCustomerFilter ? ' tcf-active' : ''}" onclick="window.BillingModule.toggleTcfDropdown(event)">
        <span class="material-icons" style="font-size:14px;">filter_list</span>
        <span class="tcf-label">${filterLabel}</span>
        <span class="material-icons tcf-chevron" style="font-size:16px;">expand_more</span>
      </div>
      <div class="billing-mc-search">
        <span class="material-icons" style="position:absolute;left:8px;top:50%;transform:translateY(-50%);font-size:16px;color:var(--text-secondary);pointer-events:none;">search</span>
        <input type="text" id="ticket-search-input-mobile" placeholder="Search\u2026" value="${window.escAttr(BillingState.ticketSearch || '')}"
          oninput="window.BillingModule.onTicketSearch(this.value)"
          style="width:100%;padding:6px 26px 6px 30px;border:1.5px solid #e2e8f0;border-radius:8px;font-size:16px;background:#fff;color:var(--text-primary);outline:none;"
          onfocus="this.style.borderColor='#3b82f6'" onblur="this.style.borderColor='#e2e8f0'">
        ${BillingState.ticketSearch ? `<button onclick="var i=this.parentNode.querySelector('input');if(i)i.value='';window.BillingModule.onTicketSearch('');" title="Clear search" style="position:absolute;right:4px;top:50%;transform:translateY(-50%);background:#e2e8f0;border:none;cursor:pointer;color:var(--text-secondary);padding:1px;line-height:1;border-radius:50%;width:18px;height:18px;display:flex;align-items:center;justify-content:center;">
          <span class="material-icons" style="font-size:12px;">close</span>
        </button>` : ''}
      </div>
      <button class="btn btn-sm billing-mc-export" onclick="window.BillingModule.exportFilteredTickets()" title="Export visible tickets to CSV">
        <span class="material-icons" style="font-size:16px;">download</span>
      </button>
    </div>
    ${/* TASK-9414 — phone: a plain full-width <select>, its own row (COD UX). */''}
    <span class="tpf-slot" data-variant="mobile" style="display:none;align-items:center;gap:6px;padding:4px 0 0;width:100%;"></span>
    ${hasCustomerFilter ? `<div class="tcf-selected-chips" style="padding:0;gap:4px;">
      ${[...tcf].map(id => {
        const c = BillingState.customers.find(x => x.id === id);
        return c ? `<span class="tcf-chip" style="font-size:0.7rem;padding:2px 6px;">${escapeHtml(c.name)}<button onclick="event.stopPropagation();window.BillingModule.toggleTicketCustomer('${c.id}')"><span class="material-icons">close</span></button></span>` : '';
      }).join('')}
      <button class="tcf-clear" style="font-size:0.7rem;" onclick="window.BillingModule.toggleTicketCustomer('all')">Clear</button>
    </div>` : ''}
    ${archivedToggleHtml ? `<div class="billing-mc-archived" style="display:flex;padding:4px 0 0;">${archivedToggleHtml}</div>` : ''}
    <!-- TASK-1232 (1): mobile copy of the company-filter dropdown gets its own ids —
         the duplicate id="tcf-dropdown-panel" made getElementById resolve the hidden
         desktop copy, which is why taps did nothing on mobile. -->
    <div class="tcf-dropdown hidden" id="tcf-dropdown-panel-m" onclick="event.stopPropagation()">
      <div class="tcf-search-wrap"><span class="material-icons tcf-search-icon">search</span>
        <input type="text" class="tcf-search-input" placeholder="Search companies..." id="tcf-search-m" oninput="window.BillingModule.filterTcfDropdown(this.value, this)">
      </div>
      <div class="tcf-options" id="tcf-options-m">
        ${sortedCustomers.map(c => `<label class="tcf-option" data-name="${escapeHtml(c.name.toLowerCase())}"><input type="checkbox" ${tcf.has(c.id) ? 'checked' : ''} onchange="window.BillingModule.toggleTicketCustomer('${c.id}')"><span>${escapeHtml(c.name)}</span></label>`).join('')}
      </div>
    </div>
  </div>`;

  _ensureTicketFilterBarStyles(); // TASK-4740 (A) + TASK-9340 (injects both style blocks)
  area.innerHTML = topBar + filterHtml + customerChipsHtml + mobileCompactHtml + dailyNavHtml + `<div class="tickets-content" style="margin-top:8px;"><div class="tickets-list" id="tickets-list"></div></div>`;

  // TASK-4740 (C) — renderTicketsList RE-RENDERS the delivery chip row with the
  // counts it measured off its own filtered population. It runs synchronously on
  // the next line, so the count-less first paint above never reaches the screen.
  renderTicketsList(countEl);
}

function switchFilter(filter) {
  BillingState.filter = filter;
  if (filter === 'daily') BillingState.dailyDate = new Date(); // reset to today when switching to daily
  clearTicketSelection();
  // Collapse all when switching to invoiced tab
  if (filter === 'invoiced') {
    BillingState.expandedCustomers = {};
    BillingState.expandedJobs = {};
    BillingState.expandedProjectFolders = {};
  }
  // TASK-857: these views must see the full ticket set, not just the bounded live
  // window — open A/R aging (invoiced & unpaid can be net-90+ old), paid +
  // written-off + archived history, AND the draft/ready "did I miss any unbilled
  // freight?" audit views (disputed loads / detention / fuel-surcharge holds
  // routinely sit unbilled 6-9 months in trucking). Load full history on demand.
  if (filter === 'invoiced' || filter === 'paid' || filter === 'written_off' ||
      filter === 'archived' || filter === 'draft' || filter === 'ready') {
    ensureFullTicketHistory();
  }
  renderBillingView();
}

function navigateDailyDate(offset) {
  const d = new Date(BillingState.dailyDate);
  d.setDate(d.getDate() + offset);
  BillingState.dailyDate = d;
  // TASK-857: navigating past the bounded window must pull full history, else the
  // day renders a false "no tickets" for a date that actually has tickets.
  if (_isBeforeTicketWindow(d)) ensureFullTicketHistory();
  renderBillingView();
}

function dailyGoToToday() {
  BillingState.dailyDate = new Date();
  renderBillingView();
}

function dailyPickDate(dateStr) {
  if (!dateStr) return;
  const parts = dateStr.split('-');
  BillingState.dailyDate = new Date(parts[0], parts[1] - 1, parts[2]);
  // TASK-857: picking a date older than the bounded window pulls full history.
  if (_isBeforeTicketWindow(BillingState.dailyDate)) ensureFullTicketHistory();
  renderBillingView();
}

function toggleTicketCustomer(customerId) {
  if (customerId === 'all') {
    BillingState.ticketCustomerFilter.clear();
  } else if (BillingState.ticketCustomerFilter.has(customerId)) {
    BillingState.ticketCustomerFilter.delete(customerId);
  } else {
    BillingState.ticketCustomerFilter.add(customerId);
  }
  renderBillingView();
}

function toggleTcfDropdown(e) {
  e.stopPropagation();
  // TASK-1232 (1) — ROOT CAUSE of the dead mobile filter: TWO copies of the
  // company-filter UI render simultaneously (desktop .tcf-bar + the mobile
  // .billing-mobile-compact strip) and both carried id="tcf-dropdown-panel".
  // getElementById always returned the FIRST copy in DOM order — the desktop
  // one — which sits inside .tcf-bar, display:none !important at <=768px.
  // So on mobile every tap toggled an invisible panel and looked dead.
  // Resolve the panel that belongs to the tapped trigger's own container.
  const trigger = (e.currentTarget && e.currentTarget.classList?.contains('tcf-trigger'))
    ? e.currentTarget
    : (e.target && e.target.closest ? e.target.closest('.tcf-trigger') : null);
  const root = trigger ? trigger.closest('.tcf-bar, .billing-mobile-compact') : null;
  const panel = (root && root.querySelector('.tcf-dropdown')) || document.getElementById('tcf-dropdown-panel');
  if (!panel) return;
  const isHidden = panel.classList.contains('hidden');
  panel.classList.toggle('hidden');
  if (isHidden) {
    const input = panel.querySelector('.tcf-search-input');
    // TASK-1244 — clear the search, but only steal focus on desktop. On mobile
    // input.focus() summoned the iOS keyboard on every open, covering half the
    // screen before the user could see the company list. The list is short —
    // tapping a name is the primary interaction; the search box stays usable,
    // it just no longer grabs focus (and the keyboard) on open.
    if (input) {
      input.value = '';
      if (window.innerWidth > 768) input.focus();
    }
    // Reset any previous search filtering on THIS panel's options
    panel.querySelectorAll('.tcf-option').forEach(opt => { opt.style.display = ''; });
    // Close on outside click
    const close = (ev) => {
      if (!panel.contains(ev.target) && !ev.target.closest('.tcf-trigger')) {
        panel.classList.add('hidden');
        document.removeEventListener('click', close);
      }
    };
    setTimeout(() => document.addEventListener('click', close), 0);
  }
}

function filterTcfDropdown(term, inputEl) {
  const lower = term.toLowerCase().trim();
  // TASK-1232 (1) — scope to the panel the typing is happening in (two copies
  // of the dropdown exist: desktop + mobile). Fallback keeps old behavior.
  const scope = inputEl && inputEl.closest ? inputEl.closest('.tcf-dropdown') : null;
  const opts = scope
    ? scope.querySelectorAll('.tcf-option')
    : document.querySelectorAll('#tcf-options .tcf-option');
  opts.forEach(opt => {
    const name = opt.dataset.name || '';
    opt.style.display = !lower || name.includes(lower) ? '' : 'none';
  });
}

let _searchDebounce = null;
function onTicketSearch(val) {
  BillingState.ticketSearch = val;
  // TASK-857: a search should span all history, not just the bounded live
  // window — pull the full set in the background on the first non-empty query.
  if (val && val.trim()) ensureFullTicketHistory();
  clearTimeout(_searchDebounce);
  _searchDebounce = setTimeout(() => {
    renderTicketsList();
  }, 200);
}

// TASK-1400 — the ticket search is a bare `includes(q)` against raw field
// values, so a leading/trailing space (paste from an email, a phone keyboard's
// auto-space after a number) made an exact ticket # match NOTHING. Every read
// site goes through this so the list, the cross-scope hint and the CSV export
// can't drift apart again.
//
// A whitespace-only box means "no search", not a query of " ": the old
// `if (BillingState.ticketSearch)` truthiness check let " " through and then
// filtered the list down to rows whose fields happen to contain a space.
//
// Internal runs are DELIBERATELY not collapsed. The matcher compares the query
// against unnormalized field values, so collapsing only the query side would
// BREAK a customer/job name that genuinely carries a double space — a
// regression, not a fix. Normalizing both sides is a different change.
function _ticketSearchQuery() {
  return String(BillingState.ticketSearch || '').trim().toLowerCase();
}

// ============================================
// RENDER TICKETS LIST (enhanced with totals)
// ============================================
function renderTicketsList(countEl) {
  const container = document.getElementById('tickets-list');
  if (!container) return;

  // TASK-368/369 pattern (2026-05-28) — distinguish "loading" from "no
  // tickets". Pre-fix this rendered "No tickets found" before the
  // onSnapshot first-snapshot landed, indistinguishable from a real empty
  // pipeline. Same shape as the HR Hiring fix + the Roster staff-login
  // flash fix. The calendar-mode loading-state at renderBillingView L4143
  // already gates correctly; this is the list-mode equivalent.
  if (!BillingState._ticketsLoaded) {
    // TASK-4740 (C) — no population has been measured yet, so the delivery chips
    // must print no numbers rather than a stale set from the previous tenant /
    // filter or a fabricated 0. Same reasoning as the loading skeleton itself.
    BillingState._deliveryCounts = null;
    _repaintTicketProjectFilter(null); // TASK-9414 — no counts before tickets land
    // Same for the row count: nothing measured yet, so print nothing.
    const _cEl = document.getElementById('tickets-count') || countEl;
    if (_cEl) _cEl.textContent = '';
    container.innerHTML = `
      <div class="billing-tickets-loading" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:36px;height:36px;border-radius:6px;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:50%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:30%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading tickets…</div>
      </div>
    `;
    if (countEl) countEl.textContent = '';
    return;
  }

  let filteredTickets = BillingState.tickets;

  // Apply job filter if drilling down
  if (BillingState.ticketJobFilter) {
    filteredTickets = filteredTickets.filter(t => t.jobId === BillingState.ticketJobFilter);
  }

  // TASK-9414 — an active project filter with jobs not yet landed would render
  // every ticket as "No project" (or none in the project): a plausible-looking
  // lie. Say it's loading instead; the jobs snapshot re-renders Tickets.
  if (BillingState.ticketProjectMode && !BillingState._jobsLoaded) {
    _flushSecondarySubs();
    _repaintTicketProjectFilter(null);
    if (!countEl) countEl = document.getElementById('tickets-count');
    if (countEl) countEl.textContent = '';
    container.innerHTML = `<div class="loading-state" style="padding:40px 0;text-align:center;">
        <div class="loading-spinner"></div>
        <p style="color:var(--text-secondary);margin-top:12px;">Loading projects…</p>
      </div>`;
    return;
  }

  // TASK-1618 trap 1 — project drill-down. THE SAME predicate runs in
  // exportFilteredTickets (via _ticketInProjectFilter), so the accountant's CSV
  // cannot contain a different set than the screen it came from.
  // TASK-9414 — now applied AFTER status + search (below), so the Project
  // control's counts can be measured off the population just before it. The
  // filters are pure restrictions, so the order doesn't change the result; the
  // two places that read the set earlier (archived-hidden count, cross-scope
  // search hint) apply the project predicate themselves.

  // Apply multi-select customer filter
  if (BillingState.ticketCustomerFilter.size > 0) {
    filteredTickets = filteredTickets.filter(t => BillingState.ticketCustomerFilter.has(t.customerId));
  }

  // TASK-1229 — reusable search predicate + a snapshot of the set BEFORE the date/
  // status scope, so a search that finds nothing IN SCOPE can look across all dates/
  // statuses (the "typed an exact ticket #, got 0, assumed it's gone / data loss" trap).
  // The deliberate job/customer drill-down applied above is preserved.
  const _ticketMatchesSearch = (t, q) => _ticketSearchFields(t).some(f => f && String(f).toLowerCase().includes(q));
  const _preScopeTickets = filteredTickets;

  // TASK-1345 — one shared filter, also used by exportFilteredTickets.
  // BillingState.showArchived unions archived/locked back into "Active".
  const _archivedHiddenCount = (BillingState.filter === 'all' && !BillingState.showArchived)
    ? countArchivedHidden(filteredTickets.filter(_ticketInProjectFilter)) : 0;
  BillingState._archivedHiddenCount = _archivedHiddenCount;
  // TASK-9654 — aging chip counts off the population the scope is about to cut
  // (before status), so a bucket's number is the rows that bucket shows once it
  // is the scope (later chips — search, delivery — narrow both alike).
  BillingState._qboAgingCounts = _qboAgingCounts(filteredTickets.filter(_ticketInProjectFilter));
  filteredTickets = _applyStatusOrAgingScope(filteredTickets); // TASK-9654 (status, or the QB aging bucket)

  // Apply search filter (TASK-1400 — trimmed; see _ticketSearchQuery)
  const _tsQuery = _ticketSearchQuery();
  const _tsLabel = String(BillingState.ticketSearch || '').trim();
  if (_tsQuery) {
    filteredTickets = filteredTickets.filter(t => _ticketMatchesSearch(t, _tsQuery));
  }

  // TASK-9414 — measure the Project control off THIS population (everything
  // except the project filter itself), then apply the one shared predicate.
  // So "Kittelson 20in (3)" means picking it shows 3 rows here.
  // (COD Code Reviewer) The count is taken AFTER the Missing-Permits and delivery
  // chips below too — else "Hamlin (5)" could show 3 rows with a chip on.
  const _preProjectPop = filteredTickets;
  filteredTickets = filteredTickets.filter(_ticketInProjectFilter);

  // TASK-4649 — "Missing Permits" chip drill-down: same predicate as the card
  // border, so the chip count and the filtered list can never disagree.
  if (BillingState.missingPermitFilter) {
    filteredTickets = filteredTickets.filter(_ticketMissingPermit);
  }

  // TASK-4662 — the delivery axis, applied AFTER the status filter so the two
  // compose ("Ready" + "Unsent" = the tickets she still owes a customer).
  //
  // TASK-4740 (C) — the chip tallies are taken HERE, off `filteredTickets` as it
  // stands one line before the delivery predicate runs. Delivery is the last
  // filter in the pipeline, so for the active chip the number the button prints
  // and the number of rows the list then renders are the same measurement.
  // Measured, not asserted: the counts move with Show archived, the company
  // multi-select, the search box and the status filter, because the population
  // they are drawn from is the one the user is looking at.
  BillingState._deliveryCounts = _ticketDeliveryCounts(filteredTickets);
  if (BillingState.deliveryFilter) {
    const _df = TICKET_DELIVERY_FILTERS.find(f => f.key === BillingState.deliveryFilter);
    if (_df) filteredTickets = filteredTickets.filter(_df.test);
  }
  // TASK-9414 — Project control counts: the same population minus the project
  // filter itself, with the permit + delivery chips applied like the list.
  {
    let _pop = _preProjectPop;
    if (BillingState.missingPermitFilter) _pop = _pop.filter(_ticketMissingPermit);
    const _pdf = BillingState.deliveryFilter ? TICKET_DELIVERY_FILTERS.find(f => f.key === BillingState.deliveryFilter) : null;
    if (_pdf) _pop = _pop.filter(_pdf.test);
    _repaintTicketProjectFilter(_pop);
  }
  // Repaint the chip row with the tallies just measured. The bar is built by
  // renderBillingView and NOT re-rendered by the many call sites that call
  // renderTicketsList alone (search input, status switch, ticket save…), so
  // without this the numbers would freeze at whatever the last full bar render
  // saw — the same stale-population bug in a slower form.
  const _dChipsEl = document.getElementById('tickets-delivery-chips');
  if (_dChipsEl) _dChipsEl.outerHTML = _ticketDeliveryFilterHtml();
  const _aChipsEl = document.getElementById('tickets-qbo-aging-chips'); // TASK-9654
  if (_aChipsEl) _aChipsEl.outerHTML = _qboAgingFilterHtml();
  const _rChipsEl = document.getElementById('tickets-review-chips'); // TASK-10041
  if (_rChipsEl) _rChipsEl.outerHTML = _reviewChipHtml();
  const _iChipsEl = document.getElementById('tickets-integrity-chips'); // TASK-10045 S2
  if (_iChipsEl) _iChipsEl.outerHTML = _integrityChip('toolbarChipHtml');

  // TASK-10041 — the review rows this view shows inline, and the chip filter:
  // ON = only the warnings (rows + the tickets they name).
  const _rvRows = _reviewRowsInView();
  const _rvOn = BillingState.reviewFilter === true || BillingState.noClockFilter === true; // TASK-10186
  if (_rvOn) {
    const _rvTicketIds = new Set();
    for (const r of _rvRows) { if (r.ticketId) _rvTicketIds.add(r.ticketId); if (r.offTicket) _rvTicketIds.add(r.offTicket.id); }
    filteredTickets = filteredTickets.filter(t => _rvTicketIds.has(t.id));
  }

  // Re-resolve the node, don't trust the caller's: renderBillingView captures
  // `#tickets-count`, then renderTicketsView rebuilds the bar via innerHTML and
  // hands the DETACHED node down — writing to it paints nothing (COD 2026-09-29).
  countEl = document.getElementById('tickets-count') || countEl;
  if (countEl) {
    // "shown", not "tickets": it counts the rows on screen after every filter,
    // so it must not read as a total (archived is excluded by default).
    countEl.textContent = `${filteredTickets.length} shown`;
  }

  if (filteredTickets.length === 0 && !_rvRows.length) {
    // TASK-1229 — self-healing search: if the query matched nothing IN this date/status
    // view but DOES match tickets loaded on other dates/statuses, say so and offer a
    // one-tap widen. Prevents "typed an exact ticket # → 0 results → looks like data
    // loss." Only searches the already-loaded set (never over-promises past the load
    // window — the "Load older" footer covers deeper history).
    let _crossScopeHtml = '';
    if (_tsQuery) {
      const _cross = _preScopeTickets.filter(t => _ticketInProjectFilter(t) && _ticketMatchesSearch(t, _tsQuery));
      if (_cross.length > 0) {
        const _hasActive = _cross.some(t => t.status !== 'archived' && t.status !== 'locked');
        const _target = _hasActive ? 'all' : 'archived';
        const _label = _hasActive ? 'Show all dates' : 'Show archived';
        _crossScopeHtml = `
          <div style="margin:14px auto 0;max-width:440px;padding:12px 14px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);font-size:0.85rem;color:var(--text-primary);text-align:center;">
            <strong>${_cross.length}</strong> ticket${_cross.length !== 1 ? 's' : ''} matching &ldquo;${escapeHtml(_tsLabel)}&rdquo; ${_cross.length !== 1 ? 'are' : 'is'} outside this view (other dates or statuses).
            <div style="margin-top:8px;"><button class="btn btn-sm btn-primary" onclick="window.BillingModule.switchFilter('${_target}')">${_label}</button></div>
          </div>`;
      }
    }
    // A project picked from the Project control with nothing in it: say WHY, so
    // an empty list never reads as lost tickets or a broken filter.
    if (!_tsQuery && BillingState.ticketProjectMode === 'project' && BillingState._jobsLoaded) {
      const _pid = BillingState.ticketProjectFilter;
      const _pname = escapeHtml(BillingState.ticketProjectFilterName || 'this project');
      const _hasJobs = BillingState.jobs.some(j => j.projectId === _pid);
      container.innerHTML = `
      <div class="empty-state">
        <span class="material-icons empty-state-icon">folder_special</span>
        <div class="empty-state-title">${_hasJobs ? `No tickets for ${_pname} in this view` : `No jobs linked to ${_pname} yet`}</div>
        <div class="empty-state-text">
          ${_hasJobs
            ? 'Its jobs have no tickets matching the filters above. Try the All filter or Show archived.'
            : 'Clear this filter, tick the tickets that belong to it, and choose <strong>Add to project…</strong> Their jobs join the project.'}
        </div>
        <div style="margin-top:12px;"><button class="btn btn-sm btn-secondary" onclick="window.BillingModule.setTicketProjectFilter('')">Show all projects</button></div>
      </div>` + _loadOlderTicketsFooterHtml();
      return;
    }
    container.innerHTML = `
      <div class="empty-state">
        <span class="material-icons empty-state-icon">receipt_long</span>
        <div class="empty-state-title">No tickets found</div>
        <div class="empty-state-text">
          ${_tsQuery
            ? `No tickets match &ldquo;${escapeHtml(_tsLabel)}&rdquo; in this view.`
            : BillingState.filter === 'daily'
            ? 'No tickets for this day.'
            : BillingState.filter === 'all' && !BillingState.showArchived
            ? 'No active tickets. Tick "Show archived" above, or use the Archived filter.'
            : 'No tickets match this filter.'}
        </div>
      </div>${_crossScopeHtml}
    ` + _loadOlderTicketsFooterHtml();
    return;
  }

  // Build accordion hierarchy
  const grouped = groupTicketsByCustomerJob(filteredTickets);
  // TASK-10041 — hang each review row under its customer → job, creating the
  // group when the job has no ticket in view (that is the whole point).
  for (const r of _rvRows) {
    const _cid = r.customerId || '_none';
    if (!grouped.has(_cid)) {
      const _lc = (BillingState.customers || []).find(c => c.id === _cid);
      grouped.set(_cid, { customerId: _cid === '_none' ? null : _cid, customerName: _lc?.name || r.customerName || 'No Customer', totalAmount: 0, ticketCount: 0, hasActive: false, unsentCount: 0, jobs: new Map() });
    }
    const _cg = grouped.get(_cid);
    _cg.reviewCount = (_cg.reviewCount || 0) + 1;
    const _jk = r.jobId || '_none';
    if (!_cg.jobs.has(_jk)) _cg.jobs.set(_jk, { jobName: r.jobTitle || 'No Job', totalAmount: 0, hasActive: false, tickets: [] });
    const _jg = _cg.jobs.get(_jk);
    (_jg.reviewRows = _jg.reviewRows || []).push(r);
  }
  let html = '';
  // TASK-9610 — project folders resolve jobs + projects from loaded state.
  const _jobById = new Map((BillingState.jobs || []).map(j => [j.id, j]));
  const _projById = new Map((BillingState.projects || []).map(p => [p.id, p]));
  const _canMoveJobs = _canLinkJobsToProjects() && BillingState._jobsLoaded === true;
  _ensureProjectFolderStyles();

  // Selection action bar
  html += `<div id="billing-selection-bar"></div>`;

  // Drag-to-paid drop zone (shown when invoiced filter or all filter)
  if (BillingState.filter === 'invoiced' || BillingState.filter === 'all') {
    html += `
      <div class="billing-paid-dropzone" id="billing-paid-dropzone"
        ondragover="event.preventDefault();this.classList.add('drag-over');"
        ondragleave="this.classList.remove('drag-over');"
        ondrop="event.preventDefault();this.classList.remove('drag-over');window.BillingModule.onDropMarkPaid(event);">
        <span class="material-icons">payments</span>
        <span>Drop ticket here to mark as <strong>Paid</strong></span>
      </div>`;
  }

  for (const [custId, custGroup] of grouped) {
    // TASK-4649 — the permit drill-down force-expands: a red border inside a
    // collapsed accordion is invisible, which defeats the whole triage chip.
    const custExpanded = BillingState.expandedCustomers[custId] === true || BillingState.missingPermitFilter || _rvOn; // default collapsed (TASK-10041: the warnings chip force-expands)
    const chevronClass = custExpanded ? 'expanded' : '';

    html += `<div class="billing-accordion">`;
    html += `
      <div class="billing-accordion-header" onclick="window.BillingModule.toggleCustomerExpand('${custId}')" style="border-left: ${_customerRailBorder(custId)};">
        <span class="material-icons accordion-chevron ${chevronClass}">chevron_right</span>
        <span class="material-icons">business</span>
        <strong style="font-size:0.95rem;color:var(--text-primary);">${escapeHtml(custGroup.customerName || 'No Customer')}</strong>
        ${custGroup.hasActive ? _onShiftBadgeHtml() : ''}
        ${_customerUnsentFactHtml(custGroup)}
        ${_reviewCustChipHtml(custGroup.reviewCount || 0)}
        <span class="billing-accordion-meta">${custGroup.ticketCount} ticket${custGroup.ticketCount !== 1 ? 's' : ''} &middot; ${invoiceFmtMoney(custGroup.totalAmount)}</span>
      </div>
    `;

    if (custExpanded) {
      html += `<div class="billing-accordion-body">`;
      // TASK-9610 — one job row (subheader + its tickets). Called for jobs inside
      // a project folder and for the loose jobs after the folders, so both render
      // exactly as a job always has.
      const _renderJobRow = (jobKey, jobGroup, _inFolder) => {
        const fullJobKey = `${custId}_${jobKey}`;
        const jobExpanded = BillingState.expandedJobs[fullJobKey] === true || BillingState.missingPermitFilter || _rvOn; // default collapsed (TASK-4649 / TASK-10041: chip drill-down force-expands)
        const jobChevronClass = jobExpanded ? 'expanded' : '';

        const isRealJob = jobKey !== '_none';
        // TASK-1221 — split-mode badge on the job header (resolve from the live
        // job doc by jobId; jobKey IS the jobId here). Only 'vehicle' in Phase 1.
        const _splitJob = isRealJob && BillingState.jobs ? BillingState.jobs.find(j => j.id === jobKey) : null;
        const _splitBadge = (_splitJob && _splitJob.splitBy && _splitJob.splitBy !== 'combined')
          ? `<span class="badge badge-sm" style="background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;font-size:11px;padding:2px 8px;border-radius:10px;margin-left:6px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;margin-right:2px;">call_split</span>Split · by ${escapeHtml(_splitJob.splitBy === 'vehicle' ? 'vehicle' : _splitJob.splitBy)}</span>`
          : '';
        html += `
          <div class="billing-accordion-subheader" data-job-id="${jobKey}" data-cust-id="${custId}"
            onclick="window.BillingModule.toggleJobExpand('${custId}','${jobKey}')"
            ondragover="window.BillingModule.onJobDragOver(event,'${custId}')"
            ondragleave="window.BillingModule.onJobDragLeave(event)"
            ondrop="window.BillingModule.onTicketDrop(event,'${jobKey}','${custId}')"
            style="border-left: ${_customerRailBorder(custId)};">
            <span class="material-icons accordion-chevron ${jobChevronClass}">chevron_right</span>
            ${_canMoveJobs && _splitJob && custId !== '_none' && jobGroup.tickets.length ? `<span class="material-icons billing-job-drag" draggable="true" data-job-id="${window.escAttr(jobKey)}" data-cust-id="${window.escAttr(custId)}"
              title="Drag into a project folder" aria-hidden="true" onclick="event.stopPropagation()"
              ondragstart="window.BillingModule.onJobDragStart(event)" ondragend="window.BillingModule.onJobDragEnd(event)">drag_indicator</span>` : ''}
            <span class="material-icons">work</span>
            <span class="billing-job-name" style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">${escapeHtml(jobGroup.jobName || 'No Job')}</span>${_splitBadge}${isRealJob && _splitJob && _splitJob.projectId && !_inFolder ? _jobProjectTagHtml(_splitJob.projectId) : ''}
            ${jobGroup.hasActive ? _onShiftBadgeHtml() : ''}
            ${_reviewJobChipHtml(jobGroup.reviewRows)}${isRealJob ? _integrityChip('jobChipHtml', jobKey) : ''}
            <span class="billing-accordion-meta">${jobGroup.tickets.length} ticket${jobGroup.tickets.length !== 1 ? 's' : ''} &middot; ${invoiceFmtMoney(jobGroup.totalAmount)}</span>
            ${isRealJob && jobGroup.tickets.length ? `
              <div class="billing-job-actions">
                ${_splitJob && custId !== '_none' ? `<button type="button" class="billing-job-action billing-job-action--move" data-job-id="${window.escAttr(jobKey)}" onclick="event.stopPropagation();window.BillingModule.openMoveJobToProject(this)" title="Move to project…" aria-label="Move to project">
                  <span class="material-icons">drive_file_move</span>
                </button>` : ''}
                <button class="billing-job-action" onclick="window.BillingModule.startJobRename('${custId}','${jobKey}',event)" title="Rename job">
                  <span class="material-icons">edit</span>
                </button>
                <button class="billing-job-action danger" onclick="window.BillingModule.deleteJob('${custId}','${jobKey}',event)" title="Delete job">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            ` : ''}
          </div>
        `;

        if (jobExpanded) {
          html += `<div class="billing-job-body">`;
          for (const r of (jobGroup.reviewRows || [])) html += _reviewCardHtml(r); // TASK-10041
          for (const ticket of jobGroup.tickets) {
            html += renderTicketCard(ticket, custId);
          }
          html += `</div>`;
        }
      };

      // TASK-9610 — Customer → Project folders → Jobs → Tickets; jobs not in a
      // project come after the folders. Folder totals are THIS VIEW's tickets
      // only (the loaded window + every filter above), and a multi-customer
      // project shows under each customer with that customer's part only.
      const _split = groupJobsIntoProjectFolders(custGroup.jobs, _jobById, _projById);
      const _shownFolderIds = new Set();
      for (const folder of _split.folders) {
        _shownFolderIds.add(folder.projectId);
        const fKey = projectFolderKey(custId, folder.projectId);
        const fExpanded = BillingState.expandedProjectFolders[fKey] === true || BillingState.missingPermitFilter || _rvOn
          || (BillingState.ticketProjectMode === 'project' && BillingState.ticketProjectFilter === folder.projectId);
        html += _projectFolderHeaderHtml(custId, folder, fExpanded, false);
        if (fExpanded) {
          // COD (Independent): the job rows inside an open folder are the big
          // target — a JOB dropped on them bubbles here and joins this folder.
          // Ticket drags are ignored here (the job rows handle those).
          html += `<div class="billing-project-folder-body" data-cust-id="${window.escAttr(custId)}" data-project-id="${window.escAttr(folder.projectId)}"
            ondragover="window.BillingModule.onFolderBodyDragOver(event)" ondrop="window.BillingModule.onFolderBodyDrop(event)">`;
          for (const [jobKey, jobGroup] of folder.jobs) _renderJobRow(jobKey, jobGroup, true);
          html += `</div>`;
        }
      }
      // Empty folders are drop targets only: hidden at rest, shown while a job
      // of THIS customer is being dragged (onJobDragStart). Never under the
      // permit drill-down (it hides folders with no flagged tickets).
      if (_canMoveJobs && custId !== '_none' && !BillingState.missingPermitFilter) {
        for (const p of jobMoveTargets(BillingState.projects, custId)) {
          if (_shownFolderIds.has(p.id)) continue;
          html += _projectFolderHeaderHtml(custId, {
            projectId: p.id, project: p, name: p.name || '(unnamed project)', poNumber: p.poNumber || '',
            multiCustomer: _isMultiCustomerProject(p), jobs: [], ticketCount: 0, totalAmount: 0
          }, false, true);
        }
      }
      for (const [jobKey, jobGroup] of _split.loose) _renderJobRow(jobKey, jobGroup, false);

      // TASK-9610 — job-drag drop zones (per customer, hidden until a JOB drag).
      if (_canMoveJobs && custId !== '_none') {
        html += `
        <div class="billing-job-drop-zones${_jobDragData && _jobDragData.custId === custId ? ' visible' : ''}" data-cust-id="${window.escAttr(custId)}">
          <div class="billing-job-dropzone billing-job-dropzone--unlink" data-cust-id="${window.escAttr(custId)}" data-action="unlink"
            ondragover="window.BillingModule.onJobZoneDragOver(event)"
            ondragleave="window.BillingModule.onJobDragLeave(event)"
            ondrop="window.BillingModule.onJobZoneDrop(event)">
            <span class="material-icons">folder_off</span>
            Remove from project
          </div>
          <div class="billing-job-dropzone billing-job-dropzone--new" data-cust-id="${window.escAttr(custId)}" data-action="new"
            ondragover="window.BillingModule.onJobZoneDragOver(event)"
            ondragleave="window.BillingModule.onJobDragLeave(event)"
            ondrop="window.BillingModule.onJobZoneDrop(event)">
            <span class="material-icons">create_new_folder</span>
            New project from this job
          </div>
        </div>`;
      }

      // Drop zones (per customer, hidden until drag)
      html += `
        <div class="billing-drop-zones" data-cust-id="${custId}">
          <div class="billing-unassigned-dropzone" data-cust-id="${custId}"
            ondragover="window.BillingModule.onJobDragOver(event,'${custId}')"
            ondragleave="window.BillingModule.onJobDragLeave(event)"
            ondrop="window.BillingModule.onTicketDrop(event,'_none','${custId}')">
            <span class="material-icons">link_off</span>
            Unassign from Job
          </div>
          <div class="billing-newjob-dropzone" data-cust-id="${custId}"
            ondragover="window.BillingModule.onJobDragOver(event,'${custId}')"
            ondragleave="window.BillingModule.onJobDragLeave(event)"
            ondrop="window.BillingModule.onTicketDropNewJob(event,'${custId}')">
            <span class="material-icons">add_circle</span>
            Create New Job
          </div>
        </div>
      `;

      html += `</div>`;
    }

    html += `</div>`;
  }

  container.innerHTML = html;

  // TASK-857: offer the long tail beyond the bounded live window.
  container.insertAdjacentHTML('beforeend', _loadOlderTicketsFooterHtml());

  // Re-check checkboxes for persisted selections
  for (const tid of BillingState.selectedTicketIds) {
    const cb = container.querySelector(`input[data-ticket-id="${tid}"]`);
    if (cb) cb.checked = true;
  }

  // Update selection bar
  updateSelectionActionBar();
}

// ============================================
// GROUP TICKETS BY CUSTOMER → JOB
// ============================================
// Check if a ticket's date is today (for showing Active badge on tickets by jobId match)
function _isTicketToday(ticket) {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return ticket.workDate === todayStr;
}

function groupTicketsByCustomerJob(tickets) {
  const map = new Map();

  for (const t of tickets) {
    const custId = t.customerId || '_none';
    // Group label resolution: prefer the LIVE customer doc name (BillingState.customers)
    // over the ticket's denormalized customerName field. Tickets cache customerName
    // at create-time, so a customer rename leaves stale labels on old tickets — the
    // group header would then read from whichever stale ticket happens to be first
    // in the map (TITS "Geo Pulse" bug, 2026-05-15). Falls back to ticket field for
    // tickets with a missing customer doc (e.g. deleted customer).
    const liveCustomer = custId !== '_none' && BillingState.customers
      ? BillingState.customers.find(c => c.id === custId)
      : null;
    const custName = liveCustomer?.name || t.customerName || 'No Customer';
    const jobId = t.jobId || '_none';
    const jobName = t.jobName || t.jobTitle || 'No Job';
    const amount = t.grandTotal || 0;

    if (!map.has(custId)) {
      map.set(custId, {
        customerId: custId === '_none' ? null : custId, // TASK-9588 — the row fact needs the customer's ticket-email flag
        customerName: custName,
        totalAmount: 0,
        ticketCount: 0,
        hasActive: false,
        unsentCount: 0, // TASK-9340 — same predicate as the "Unsent" chip
        jobs: new Map()
      });
    }
    const cg = map.get(custId);
    cg.totalAmount += amount;
    cg.ticketCount++;
    if (_ticketCountsAsUnsent(t)) cg.unsentCount++;
    const ticketActive = BillingState.activeTicketIds.has(t.id);
    // Also check customerName/customerId for ticketless clock-ins
    const custActive = BillingState.activeCustomerNames?.has(custName) || BillingState.activeCustomerIds?.has(custId);
    if (ticketActive || custActive) cg.hasActive = true;

    if (!cg.jobs.has(jobId)) {
      cg.jobs.set(jobId, {
        jobName,
        totalAmount: 0,
        hasActive: false,
        tickets: []
      });
    }
    const jg = cg.jobs.get(jobId);
    jg.totalAmount += amount;
    // Job group Active: check ticketId OR if any active clock entry is on this job
    const jobActive = jobId && BillingState.activeJobIds?.has(jobId);
    if (ticketActive || jobActive) jg.hasActive = true;
    jg.tickets.push(t);
  }

  return map;
}

// ============================================
// TASK-9340 — ROW BADGE, ROW FACT, QUIETER RAIL
// ============================================
// "Active" meant two unrelated things on one screen: the status FILTER "Active"
// (= every non-archived ticket, TASK-1345 — left alone on purpose) and this row
// badge (= someone is CLOCKED IN on that customer's / job's work right now,
// from BillingState.activeTicketIds / activeCustomerIds / activeJobIds). The
// badge now says what it measures: "On shift", with a live dot. Four sites use
// it — customer row, job row, and the Review Ticket employee line (card + table).
const ON_SHIFT_ROW_TITLE = 'Someone is clocked in on this work right now';
const ON_SHIFT_PERSON_TITLE = 'Clocked in right now';
function _onShiftBadgeHtml(title = ON_SHIFT_ROW_TITLE) {
  _ensureTickets9340Styles();
  return `<span class="badge-on-shift" title="${escapeHtml(title)}"><span class="on-shift-dot" aria-hidden="true"></span>On shift</span>`;
}

// Customer rows carry ONE more fact: how many of the rows under them have no
// delivery recorded. It is the exact predicate of the "Unsent" chip
// (TICKET_DELIVERY_FILTERS → _ticketDeliveryState(t).sent), counted over the
// same filtered rows the group is built from, so the number can be checked by
// expanding the row. Labelled as what it is — NOT money, NOT unbilled, NOT
// overdue (COD trap: "Unsent" = no delivery on any channel). Shown only on the
// views where an unsent ticket is still actionable; on Invoiced / Paid /
// Written Off / Archived it would be noise about closed work.
const _UNSENT_FACT_FILTERS = new Set(['all', 'daily', 'draft', 'ready']);
function _customerUnsentFactHtml(group) {
  if (!_UNSENT_FACT_FILTERS.has(BillingState.filter)) return '';
  const cid = group && group.customerId;
  // TASK-9588 — a customer marked "No ticket emails" (paper / hand-off) shows a
  // quiet crossed-out-mail chip instead of a count it will never work down.
  // Both chips open the per-customer setting, so it is changed from the row
  // that complains instead of a hunt through Customers. stopPropagation: the
  // row header itself toggles the accordion.
  const open = cid
    ? ` role="button" tabindex="0" onclick="event.stopPropagation();window.BillingModule.showCustomerTicketEmailPref('${window.escJsAttr(cid)}')"`
    : '';
  const n = group && group.unsentCount;
  if (cid && _customerTicketEmailsOff(cid)) {
    _ensureTickets9340Styles();
    // COD (Code Reviewer): the icon must not swallow a REAL count. For an off
    // customer unsentCount holds only what is still actionable (a failed send,
    // or the include-toggle is on), so show it beside the icon when non-zero.
    return `<span class="billing-cust-fact billing-cust-fact--noemail"${open} title="No ticket emails — click to change" aria-label="No ticket emails — click to change"><span class="material-icons" aria-hidden="true">unsubscribe</span></span>`
      + (n ? `<span class="billing-cust-fact" title="Still no delivery recorded — includes failed sends">${n} unsent</span>` : '');
  }
  if (!n) return '';
  _ensureTickets9340Styles();
  const tip = 'No delivery recorded on any channel — not emailed, and nobody logged handing it over'
    + (cid ? '. Click if this customer never gets ticket emails.' : '');
  return `<span class="billing-cust-fact${cid ? ' billing-cust-fact--click' : ''}"${open} title="${tip}">${n} unsent</span>`;
}

// TASK-9588 — the per-customer "No ticket emails" flag
// (`customers/{id}.ticketEmailsOff === true`). Absent / false = today's
// behaviour, and so does a ticket whose customer doc is missing. It is a
// DISPLAY preference only: nothing here blocks a send (a hand send still works
// and is still logged), and the driver close-out box stays governed by the
// contract's `fieldTicketSettings.requireEmailCopy`.
function _customerTicketEmailsOff(customerId) {
  if (!customerId) return false;
  const c = (BillingState.customers || []).find(x => x.id === customerId);
  return !!(c && c.ticketEmailsOff === true);
}
// A "paper ticket": never sent AND never a failed attempt, under a no-email
// customer. Sent / Awaiting / Edited / Email failed keep their own state and
// history — the flag only renames the "nothing recorded" state.
function _ticketIsPaper(t) {
  if (!t || !_customerTicketEmailsOff(t.customerId)) return false;
  // COD (Code Reviewer + Edge Case Hunter): a DRIVER close-out send writes only
  // fieldTicketData.emailSentAt, which _ticketDeliveryState does not count as
  // `sent`. That ticket WAS emailed — never relabel it "Paper ticket"; it keeps
  // its old state and stays countable.
  if (_ticketEmailDate(t.fieldTicketData && t.fieldTicketData.emailSentAt)) return false;
  const st = _ticketDeliveryState(t);
  return !st.sent && !st.failed;
}
// THE Unsent predicate — the chip, its count, the list, the project count, the
// CSV export and the customer-row fact all read this one function.
function _ticketCountsAsUnsent(t) {
  if (_ticketDeliveryState(t).sent) return false;
  return BillingState.unsentIncludeNoEmail === true || !_ticketIsPaper(t);
}

// The customer rail keeps its hash (getCustomerColor) so a customer keeps its
// colour, but it is thinner and softer so colour reads as identity, not alarm.
// Scoped to the two ACCORDION rails (customer row + the job rows nested under
// it); ticket cards and every other getCustomerColor caller are unchanged.
// '8C' = ~55% alpha on the palette's #RRGGBB.
function _customerRailBorder(custId) {
  const c = getCustomerColor(custId);
  return `3px solid ${/^#[0-9A-Fa-f]{6}$/.test(c) ? c + '8C' : c}`;
}

// TASK-9340 — the rules the two-band toolbar, the badge and the row fact need.
// Injected once from billing.js (like _ensureTicketFilterBarStyles) so the
// change ships with this module's cache-buster instead of depending on the
// styles.css one.
function _ensureTickets9340Styles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('tickets-9340-styles')) return;
  const el = document.createElement('style');
  el.id = 'tickets-9340-styles';
  el.textContent = `
.tickets-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 2px 0 10px; }
.tickets-toolbar > .tickets-status-seg { display: inline-flex; flex-wrap: wrap; justify-content: flex-start; gap: 2px; margin: 0; padding: 3px; min-width: 0; background: var(--bg-elevated); border: 1px solid var(--border-light); border-radius: var(--radius-md); }
.tickets-toolbar > .tickets-status-seg .filter-btn { padding: 5px 12px; gap: 0; border: none; background: transparent; box-shadow: none; border-radius: var(--radius-sm); color: var(--text-secondary); font-size: 0.8125rem; font-weight: 600; }
.tickets-toolbar > .tickets-status-seg .filter-btn:hover { background: #fff; color: var(--text-primary); box-shadow: none; }
.tickets-toolbar > .tickets-status-seg .filter-btn.active { background: var(--brand-blue); color: #fff; font-weight: 700; box-shadow: 0 1px 2px rgba(15,23,42,0.15); }
.tickets-toolbar > .tickets-status-seg .filter-btn:focus-visible { outline: 2px solid var(--brand-blue); outline-offset: 1px; }
.tickets-toolbar::after { content: ''; flex: 1 1 0; order: 1; }
.tickets-toolbar-right { order: 2; display: inline-flex; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 6px; min-width: 0; }
.tickets-count { font-size: 0.75rem; font-weight: 600; color: var(--text-secondary); white-space: nowrap; margin-right: 6px; }
.tickets-count:empty { display: none; }
.tickets-toolbar-right .permit-missing-chip { border-radius: var(--radius-sm); }
.tickets-toolbar .tickets-delivery-chips { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px; padding: 0; }
.tickets-delivery-label { font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-secondary); margin-right: 2px; }
.tickets-toolbar-chip { padding: 4px 10px; font-size: 0.75rem; }
.tickets-delivery-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px 4px 10px; border-radius: var(--radius-sm); border: 1px solid var(--border-medium); background: #fff; color: var(--text-primary); font-size: 0.75rem; font-weight: 600; white-space: nowrap; cursor: pointer; transition: all 0.15s ease; }
.tickets-delivery-chip .tdc-n { min-width: 18px; padding: 0 5px; border-radius: 4px; background: #FEF3C7; color: #92400E; font-size: 0.6875rem; font-weight: 700; line-height: 1.5; text-align: center; }
.tickets-delivery-chip.is-zero { background: transparent; border-color: var(--border-light); color: var(--text-secondary); }
.tickets-delivery-chip.is-zero .tdc-n { background: var(--bg-elevated); color: var(--text-secondary); }
.tickets-delivery-chip:hover { border-color: var(--brand-blue); color: var(--text-primary); }
.tickets-delivery-chip:focus-visible { outline: 2px solid var(--brand-blue); outline-offset: 1px; }
.tickets-delivery-chip.active { background: var(--brand-navy); border-color: var(--brand-navy); color: #fff; }
.tickets-delivery-chip.active .tdc-n { background: rgba(255,255,255,0.2); color: #fff; }
.tickets-delivery-chip.active .material-icons { color: #fff; }
.tcf-bar > .tcf-search-inline { position: relative; flex: 1 1 220px; min-width: 180px; max-width: 420px; }
.tcf-bar > .billing-export-btn { margin-left: auto; }
.tcf-bar > .tickets-archived-toggle + .billing-export-btn { margin-left: 0; }
.billing-mc-row1 > .billing-mc-company { flex: 1 1 0; }
.billing-mc-search { position: relative; flex: 1.3 1 0; min-width: 0; }
.badge-on-shift { display: inline-flex; align-items: center; gap: 4px; vertical-align: middle; margin-left: 4px; padding: 1px 7px; border-radius: 9999px; background: #DCFCE7; border: 1px solid #86EFAC; color: #166534; font-size: 0.6875rem; font-weight: 600; line-height: 1.5; white-space: nowrap; }
.on-shift-dot { width: 6px; height: 6px; border-radius: 50%; background: #16A34A; flex-shrink: 0; animation: onShiftPulse 2s ease-out infinite; }
@keyframes onShiftPulse { 0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.55); } 70% { box-shadow: 0 0 0 5px rgba(22,163,74,0); } 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); } }
@media (prefers-reduced-motion: reduce) { .on-shift-dot { animation: none; } }
.billing-cust-fact { display: inline-flex; align-items: center; margin-left: 6px; padding: 1px 7px; border-radius: 9999px; background: var(--bg-elevated); border: 1px solid var(--border-light); color: var(--text-secondary); font-size: 0.6875rem; font-weight: 600; white-space: nowrap; }
.billing-cust-fact--click, .billing-cust-fact--noemail { cursor: pointer; transition: border-color 0.15s ease; }
.billing-cust-fact--click:hover, .billing-cust-fact--noemail:hover, .billing-cust-fact--click:focus-visible, .billing-cust-fact--noemail:focus-visible { border-color: var(--brand-blue); outline: none; }
.billing-cust-fact--noemail { padding: 1px 6px; }
.billing-cust-fact--noemail .material-icons { font-size: 14px; line-height: 1; color: var(--text-secondary); }
/* TASK-9626 — depth: the list sits on a light-gray well so the white customer
   rows read as cards (was white rows on a white card on a white page). Scoped
   to .tickets-content (the Tickets list is its only user). Text on these light
   surfaces stays --text-primary / --text-secondary (never the muted token). */
.tickets-content { background: #F3F5F8; border-color: #E2E8F0; box-shadow: none; }
.tickets-content .billing-accordion { margin-bottom: 12px; }
.tickets-content .billing-accordion-header { background: #fff; }
.tickets-content .billing-accordion-header:hover { background: #F8FAFC; }
.tickets-content .ticket-card { background: var(--bg-card); }
.tickets-content .billing-accordion-meta { background: none; border: none; border-radius: 0; padding: 0; font-weight: 500; color: var(--text-secondary); }
.tickets-content .billing-accordion-header .billing-cust-fact { background: transparent; border-color: transparent; font-weight: 500; }
.tickets-content .billing-accordion-header .billing-cust-fact--click:hover, .tickets-content .billing-accordion-header .billing-cust-fact--noemail:hover, .tickets-content .billing-accordion-header .billing-cust-fact--click:focus-visible, .tickets-content .billing-accordion-header .billing-cust-fact--noemail:focus-visible { background: #fff; border-color: var(--brand-blue); color: var(--text-primary); }
.tickets-delivery-hidden { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border: none; background: transparent; color: var(--text-secondary); font-size: 0.72rem; font-weight: 600; white-space: nowrap; cursor: pointer; text-decoration: underline dotted; text-underline-offset: 3px; }
.tickets-delivery-hidden .material-icons { font-size: 14px; }
.tickets-delivery-hidden:hover { color: var(--brand-blue); }
@media (max-width: 768px) {
  .tickets-toolbar { gap: 6px; padding: 0 0 6px; }
  /* Phone: the segment WRAPS inside its container (no horizontal scroll —
     COD: a flex child at min-width:auto widens the page instead of scrolling,
     and a scroller hides Written Off/Archived). Right group drops below it. */
  .tickets-toolbar::after { display: none; }
  .tickets-toolbar > .tickets-status-seg { width: 100%; justify-content: flex-start; }
  .tickets-toolbar > .tickets-status-seg .filter-btn { padding: 5px 9px; font-size: 0.75rem; }
  .tickets-toolbar-right { width: 100%; justify-content: flex-start; }
  .billing-mobile-compact .billing-mc-export, .billing-mobile-compact .billing-mc-back { min-height: 44px !important; min-width: 44px !important; }
  .tickets-delivery-label { display: none; }
  .tickets-toolbar-chip, .tickets-delivery-chip { padding: 4px 8px; font-size: 0.7rem; }
  .billing-accordion-header { flex-wrap: wrap; row-gap: 4px; }
}
@media (max-width: 768px), (pointer: coarse) {
  .tickets-toolbar > .tickets-status-seg .filter-btn, .tickets-toolbar-right .tickets-delivery-chip, .tickets-toolbar-right .tickets-toolbar-chip { min-height: 44px; }
}`;
  document.head.appendChild(el);
}

// ============================================
// CUSTOMER COLOR PALETTE (matches dispatch.js)
// ============================================
const customerColors = [
  '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#0EA5E9', '#6366F1'
];
function getCustomerColor(customerIdOrName) {
  if (!customerIdOrName) return customerColors[0];
  let hash = 0;
  const str = String(customerIdOrName);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return customerColors[Math.abs(hash) % customerColors.length];
}

// ============================================
// READY-BY ATTRIBUTION (TASK-878)
// ============================================
// Resolve the CURRENT user's display name for stamping "who flipped this ticket
// to ready". Sourced ONLY from server-loaded identity — member display name for
// staff logins, Firebase Auth displayName / email for the legacy owner — NEVER a
// user-editable input field. Returns '' when nothing resolves (caller then skips
// readyByName so the badge renders gracefully as just "Ready").
function _resolveReadyByName() {
  const as = window.AppState || {};
  const ma = as.memberAccess;
  const fromMember = ma && typeof ma.displayName === 'string' ? ma.displayName.trim() : '';
  if (fromMember) return fromMember;
  // Owner / legacy single-login has no member doc → fall back to Auth identity.
  const authName = as.user?.displayName ? String(as.user.displayName).trim() : '';
  if (authName) return authName;
  const email = as.user?.email ? String(as.user.email).trim() : '';
  if (email) return email.split('@')[0];
  return '';
}

// TASK-1347 — "who did this", stamped on the TICKET DOC (not only the audit row).
// Delete attribution lived ONLY in tenants/{tid}/auditLog — a collection nobody
// thinks to query, and which the Review-modal delete path never wrote at all: of
// 96 deleted TITS tickets only 30 have a DELETE row and `deletedBy` is set on 0,
// so "who killed #100369?" ($340, TASK-1340) is unanswerable from the record.
// Same identity source as the ready-by / archived-by stamps — server-loaded
// identity ONLY, never a user-editable input field. Returns null (not undefined)
// on miss because Firestore rejects undefined field values outright, which would
// throw away the whole delete write rather than just the stamp.
// The name is only emitted alongside a uid: the rules pin lets deletedByName
// through only when deletedBy is the caller's own uid (or the name is null), so a
// name-without-uid stamp would be denied and take the ENTIRE delete write with it.
function _actorStamp() {
  const uid = window.AppState?.user?.uid || null;
  return { uid, name: uid ? (_resolveReadyByName() || null) : null };
}

// TASK-1347 — money for an AUDIT row. Missing/garbage becomes null, never 0: an
// audit record that says "$0" when it means "we don't know" is worse than silent,
// because a reconciliation reads it as a real zero-dollar ticket. A genuine 0 is
// preserved as 0. Also keeps NaN out of the addDoc.
function _auditAmount(v) {
  if (v === null || v === undefined || v === '') return null; // Number(null) is 0 — the exact conflation we're avoiding
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Render-side short form: "John Smith" → "J. Smith" (first initial + last name).
// Single-token names render as-is. Used next to the Ready badge where space is tight.
function _formatReadyByShort(name) {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0].charAt(0).toUpperCase()}. ${parts[parts.length - 1]}`;
}

// ============================================
// RENDER SINGLE TICKET CARD (with checkbox)
// ============================================
function renderTicketCard(ticket, customerId) {
  // TASK-1339 — an unnumbered ticket used to render as a silent blank, which is
  // how 29 of them went unnoticed on TITS. Say it out loud instead (COD: the
  // ticketNoPending flag is worthless if nothing surfaces it).
  const ticketNo = ticket.ticketNo
    ? `#${ticket.ticketNo}`
    : '<span title="This ticket has no ticket number — reach out if it needs one assigned." style="color:var(--status-warning);font-weight:600;">no #</span>';
  let empCount = ticket.employeeCount || ticket.employees?.length || 0;
  const storedHours = ticket.totalHours || (ticket.timeEntries?.reduce((sum, e) => {
    if (e.timeFrom && e.timeTo) {
      const from = parseTimeStr(e.timeFrom);
      const to = parseTimeStr(e.timeTo);
      return sum + Math.max(0, (to - from) / 3600000);
    }
    return sum;
  }, 0)) || 0;
  const totalHours = getTicketLiveHours(ticket.id, storedHours);
  const grandTotal = ticket.grandTotal || 0;
  // TASK-1567 — the card affordance for the $0-invoice block. Best-effort off the
  // stored grandTotal (the authoritative fresh-read gate is in generateInvoice); an
  // internal-time customer legitimately totals $0 so its Invoice button stays live.
  const _cardZeroBill = !(Number(grandTotal) > 0) && !_ticketCustomerIsInternalTime(ticket);
  const checked = BillingState.selectedTicketIds.has(ticket.id) ? 'checked' : '';
  const customerColor = getCustomerColor(customerId);

  // TASK-1346 sweep — books-integrity gate. Drag re-assigns a ticket to a
  // different job/customer; doing that after the work was billed desyncs the
  // books. Keyed on the boolean, the 110 invoice-marker-only tickets stayed
  // draggable.
  const isDraggable = ticket.status !== 'archived' && ticket.status !== 'locked' && !isTicketInvoiced(ticket);
  const dragAttrs = isDraggable
    ? `draggable="true" ondragstart="window.BillingModule.onTicketDragStart(event,'${ticket.id}','${customerId}','${ticket.jobId || '_none'}')" ondragend="window.BillingModule.onTicketDragEnd(event)"`
    : '';

  // TASK-1167 (AR/AP view) — prominent, low-vision-friendly read-only cue right on
  // the ticket card. The Review modal has this banner (A2b at renderReviewTicket),
  // but the AR/AP card list did not, so an archived/locked/invoiced ticket just
  // refused edits with no explanation (Steve, low-vision, mobile). Mirrors A2b's
  // palette + label logic and reuses the confirm-gated unarchiveFromReview. Label:
  // invoiced→'Invoiced', locked→'Locked', else 'Archived'. Unarchive button shows
  // ONLY for archived/locked (NOT invoiced — invoiced uses the uninvoice flow).
  // TASK-1346 sweep — narrow boolean, case (a). This whole banner block describes
  // and undoes the isReviewReadOnly lock, so every branch of it must read the SAME
  // predicate that lock uses. Widening the label would announce "Invoiced" as the
  // reason a ticket is frozen when the reason is actually `archived`, and widening
  // _cardCanUnarchive would delete the ONLY way back to editable for the 110
  // archived-with-an-invoice-number tickets — the exact lock-out we refused.
  const _cardReadOnly = isReviewReadOnly(ticket);
  const _cardLockLabel = ticket.invoiced ? 'Invoiced' : (ticket.status === 'locked' ? 'Locked' : 'Archived');
  const _cardCanUnarchive = !ticket.invoiced && (ticket.status === 'archived' || ticket.status === 'locked');
  // TASK-1345 (B) — an archived ticket with money and no invoice is the exact
  // trap: the Invoice button is hidden on archived tickets, so the only way to
  // bill it is a two-step nobody knew about. Surface it as one action. NOT by
  // re-rendering the Invoice button on an archived ticket — that would break
  // the isReviewReadOnly invariant TASK-1167 established.
  const _cardCanBill = _cardCanUnarchive && Number(ticket.grandTotal || 0) > 0
    && !isTicketInvoiced(ticket) && ticket.writtenOff !== true; // written-off debt is not re-billable (2nd-COD Domain)
  // narrow boolean, case (a) — must track _cardLockLabel above or the archive
  // timestamp would be printed next to an "Invoiced" heading.
  const _cardArchWhen = (!ticket.invoiced && ticket.archivedAt) ? formatTimestamp(ticket.archivedAt) : '';
  const _cardArchBy = (!ticket.invoiced && ticket.archivedByName) ? ` by ${escapeHtml(ticket.archivedByName)}` : '';
  const _cardLockedBanner = _cardReadOnly ? `
        <div class="ticket-card-locked-banner" style="background:linear-gradient(135deg,#475569,#64748B);color:#fff;padding:12px 14px;border-radius:var(--radius-sm);margin:0 0 10px;display:flex;flex-direction:column;gap:8px;">
          <div style="display:flex;align-items:center;gap:8px;font-weight:700;font-size:1rem;line-height:1.3;">
            <span style="font-size:1.15rem;line-height:1;">🔒</span>
            <span>${_cardLockLabel}${_cardArchWhen ? ` ${escapeHtml(_cardArchWhen)}` : ''}${_cardArchBy}</span>
          </div>
          <div style="font-size:0.85rem;opacity:0.92;">Editing is locked to protect your books.${_cardCanUnarchive ? ' Unarchive to edit.' : ''}</div>
          ${_cardCanUnarchive ? `<div style="display:flex;flex-wrap:wrap;gap:8px;align-self:flex-start;"><button class="btn" style="background:rgba(255,255,255,0.18);color:#fff;border:1.5px solid rgba(255,255,255,0.5);padding:10px 16px;font-size:0.95rem;font-weight:700;border-radius:8px;min-height:44px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;"
            onclick="event.stopPropagation();window.BillingModule.unarchiveFromReview('${ticket.id}', this)">
            <span class="material-icons" style="font-size:20px;vertical-align:middle;">lock_open</span>Unarchive to edit
          </button>${_cardCanBill ? `<button class="btn" style="background:#16A34A;color:#fff;border:1.5px solid rgba(255,255,255,0.35);padding:10px 16px;font-size:0.95rem;font-weight:700;border-radius:8px;min-height:44px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;"
            onclick="event.stopPropagation();window.BillingModule.unarchiveAndInvoice('${ticket.id}', this)">
            <span class="material-icons" style="font-size:20px;vertical-align:middle;">receipt</span>Unarchive &amp; Invoice
          </button>` : ''}${_cardCanBill && _canUnarchiveToExternal(ticket) ? _cardToExternalBtnHtml(ticket, 'unarchiveAndMarkBilledElsewhere', 'Unarchive &amp; mark Billed elsewhere', true) : ''}</div>` : ''}
        </div>` : '';

  // TASK-4649 — missing-permit flag. A red border alone is wallpaper (both
  // CODs), so the banner is ACTIONABLE: [Link Permit] opens the ticket detail
  // whose existing "Attach Load Permits" section handles upload + attach
  // (attachPermitToTicket), [Dismiss] waives via the SMS-shared field. Flag,
  // never block — no button is removed, no action is gated.
  const _permitMissing = _ticketMissingPermit(ticket);
  const _permitWaived = _ticketPermitWaived(ticket);
  // Locked/invoiced tickets: the border + message still render (the leak
  // already happened — billing should see it), but the action buttons don't:
  // both writes (the ticketLine attach AND the permitNotRequired stamp) are
  // rules-denied on a locked ticket (TASK-1176/4600 content lock), and a
  // button whose only outcome is a permission error is the TASK-428 class.
  // Unarchive first, like every other locked-ticket edit.
  const _permitFlagBanner = _permitMissing ? `
        <div class="ticket-permit-flag" onclick="event.stopPropagation();">
          <span class="material-icons" style="font-size:18px;">warning_amber</span>
          <span style="flex:1;font-weight:600;">Permit required — none attached</span>
          ${_cardReadOnly ? `<span style="font-size:0.75rem;opacity:0.85;">${_cardLockLabel} — read-only; unlock to attach or dismiss</span>` : `
          <button class="btn btn-sm" style="background:#DC2626;color:#fff;border:none;font-weight:600;" onclick="window.BillingModule.viewTicket('${ticket.id}')">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">add_link</span> Link Permit
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.dismissPermitFlag('${ticket.id}')" title="Record that no road permit was needed for this ticket">Dismiss</button>`}
        </div>` : (_permitWaived ? `
        <div style="display:flex;align-items:center;gap:6px;font-size:0.78rem;color:var(--text-secondary);margin:0 0 8px;" onclick="event.stopPropagation();">
          <span class="material-icons" style="font-size:14px;">remove_moderator</span>
          Permit flag waived${ticket.permitNotRequiredBy ? ` by ${escapeHtml(ticket.permitNotRequiredBy)}` : ''}
          ${_cardReadOnly ? '' : `<button class="btn btn-sm btn-secondary" style="padding:2px 8px;font-size:0.72rem;" onclick="window.BillingModule.undoDismissPermitFlag('${ticket.id}')">Undo</button>`}
        </div>` : '');

  return `
    <div class="ticket-card has-checkbox ${ticket.status}${isDraggable ? ' is-draggable' : ''}${_permitMissing ? ' ticket-card--permit-missing' : ''}" data-id="${ticket.id}" ${dragAttrs} style="border-left: 4px solid ${_permitMissing ? '#DC2626' : customerColor};">
      <div class="ticket-checkbox-wrapper" style="--customer-color: ${customerColor};" onclick="event.stopPropagation(); var cb=this.querySelector('input'); if(event.target!==cb){cb.checked=!cb.checked; cb.dispatchEvent(new Event('change'));}">
        <input type="checkbox" class="ticket-select-cb" data-ticket-id="${ticket.id}" ${checked}
          onchange="window.BillingModule.toggleTicketSelect('${ticket.id}', this.checked)">
      </div>${isDraggable ? `
      <div class="ticket-drag-handle" title="Drag to move between jobs">
        <span class="material-icons">drag_indicator</span>
      </div>` : ''}
      <div class="ticket-card-inner" onclick="window.BillingModule.viewTicket('${ticket.id}')">
        <div class="ticket-card-header">
          <div class="ticket-card-date">
            ${ticketNo ? `<span style="color:var(--brand-blue);font-weight:700;margin-right:8px;">${ticketNo}</span>` : ''}
            ${formatDate(ticket.date)}
          </div>
          <div>
            ${/* TASK-1346 sweep — "is this billed?" (display) */ isTicketInvoiced(ticket) && ticket.invoiceNumber ? `<span class="badge badge-invoice-num">${escapeHtml(ticket.invoiceNumber)}</span> ` : ''}
            ${ticket.writtenOff ? '<span class="badge badge-written-off"><span class="material-icons">money_off</span>Written Off</span> ' : ticket.paid ? (ticket.paymentNeedsReview ? _paidNeedsReviewBadge(ticket) : '<span class="badge badge-paid-billing"><span class="material-icons">payments</span>Paid</span> ') : ticket.paidShortfall ? _paymentShortfallBadge(ticket) : /* TASK-1346 sweep — "is this billed?" (display) */ isTicketInvoiced(ticket) ? `<span class="badge badge-invoiced"${_invoicedFromNonReady(ticket) ? ` title="${(window.escAttr || escapeHtml)('Invoiced while this ticket was still ' + String(ticket.statusAtInvoice || 'not Ready') + ' — it never went through Ready.')}"` : ''}><span class="material-icons">check_circle</span>Invoiced${_invoicedFromNonReady(ticket) ? ' ⚠' : ''}</span> ` : ''}
            ${ticket.invoiceEmailSentAt ? '<span class="badge badge-sm" style="background:#F0FDF4;color:#16a34a;border:1px solid #BBF7D0;font-size:11px;padding:2px 8px;border-radius:10px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;margin-right:2px;">email</span>Emailed</span> ' : ''}
            ${/* TASK-4662 — customer-delivery status. Reads ONLY the derived
                  ticketSendLast + ticketEmailDirtyAt fields on the ticket doc:
                  945 tickets on one tenant, so a per-row series fetch would be
                  an N+1 that crawls. Distinct from the invoice-emailed badge
                  above it, which means the INVOICE went out. */ _ticketDeliveryBadgeHtml(ticket)}
            <span class="badge badge-${ticket.status}">${capitalizeFirst(ticket.status)}</span>${ticket.status === 'ready' && ticket.readyByName ? `<span class="ticket-ready-by" title="Marked ready by ${escapeHtml(ticket.readyByName)}" style="font-size:11px;color:var(--text-secondary);margin-left:4px;max-width:90px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;vertical-align:middle;">${escapeHtml(_formatReadyByShort(ticket.readyByName))}</span>` : ''}
            ${(BillingState.activeTicketIds?.has(ticket.id) || (ticket.jobId && BillingState.activeJobIds?.has(ticket.jobId) && _isTicketToday(ticket))) ? '<span class="badge badge-active-pulse" style="background:#FEF2F2;color:#DC2626;font-size:11px;padding:3px 10px;border-radius:12px;font-weight:700;border:1.5px solid #FECACA;animation:activePulse 2s ease-in-out infinite;"><span class="material-icons" style="font-size:12px;vertical-align:middle;margin-right:3px;">radio_button_checked</span>Active</span>' : ''}
            ${ticket.fastTrax ? '<span class="badge badge-fasttrax"><span class="material-icons" style="font-size:12px;">bolt</span> FastTrax</span>' : ''}
            ${ticket.eTicketSubmissionId ? '<span class="eticket-source-badge"><span class="material-icons">description</span>E-Ticket</span>' : ''}
            ${_integrityChip('ticketChipHtml', ticket.id) /* TASK-10045 S2 */}
          </div>
        </div>
        ${_cardLockedBanner}
        ${_permitFlagBanner}
        ${_ticketDateOffChipHtml(ticket) /* TASK-10043 S1 */}
        <div class="ticket-card-body">
          ${ticket.wellName ? `
            <div class="ticket-card-well">
              <span class="material-icons">oil_barrel</span>
              ${escapeHtml(ticket.wellName)}${ticket.wellNumber ? ` #${escapeHtml(ticket.wellNumber)}` : ''}
            </div>
          ` : ''}
          <div class="ticket-card-employees">
            ${ticket.splitLabel ? `<span class="ticket-split-label" title="Split ticket" style="display:inline-flex;align-items:center;gap:3px;background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;margin-right:8px;"><span class="material-icons" style="font-size:12px;">local_shipping</span>${escapeHtml(ticket.splitLabel)}</span>` : ''}
            ${(ticket.splitBy === 'vehicle' && String(ticket.splitKey || '').indexOf('op:') === 0) ? `<span title="This rig ticket has no vehicle attached — the clock entry carried no equipment (e.g. remote clock-in). Check whether equipment lines are missing before invoicing." style="display:inline-flex;align-items:center;gap:3px;background:#FEF3C7;color:#92400E;border:1px solid #FDE68A;font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;margin-right:8px;"><span class="material-icons" style="font-size:12px;">warning_amber</span>No vehicle — check equipment</span>` : ''}
            ${empCount > 0 ? `<span class="material-icons">people</span> ${empCount} employee${empCount !== 1 ? 's' : ''}` : `<span class="material-icons" style="color:var(--text-secondary);">people</span> <span style="color:var(--text-secondary);font-style:italic;">No crew yet</span>`}
            ${totalHours > 0 ? `<span style="margin-left:auto;color:var(--text-secondary);">${totalHours.toFixed(1)} hrs</span>` : ''}
            ${grandTotal > 0 ? `<span style="font-weight:700;color:var(--text-primary);margin-left:8px;">${invoiceFmtMoney(grandTotal)}</span>` : ''}
          </div>
        </div>
        <div class="ticket-card-actions" onclick="event.stopPropagation();">
          <button class="btn btn-sm btn-primary" onclick="window.BillingModule.openReviewTicket('${ticket.id}')">
            <span class="material-icons">edit_note</span> Edit Ticket
          </button>
          ${/* TASK-1346 sweep — books-integrity gate: offering Invoice on a ticket
               that already carries an invoice number is an invitation to bill the
               same work twice. */
            !isTicketInvoiced(ticket) && ticket.status !== 'archived' && ticket.status !== 'locked' ? (
              /* TASK-1567 — a $0 ticket can't become a customer invoice. Disable the
                 one-click Invoice button and say why, instead of letting it stamp a
                 $0 invoice (INV-202608-055/059/062 reached customers this way). */
              /* Not a real `disabled` button: a disabled control fires no tap event,
                 so on a phone/tablet (no hover) there is no way to learn WHY it's
                 dead (COD UX ⚠️). Keep it visibly muted, never call generateInvoice,
                 and explain on tap via a toast that works on touch AND desktop. */
              _cardZeroBill ? `
              <button class="btn btn-sm" title="This ticket totals $0 — add the truck/asset or hours in Review Ticket before invoicing. A $0 invoice to a customer is never correct." style="background:#94A3B8;color:#fff;border:none;cursor:not-allowed;opacity:0.65;"
                onclick="event.stopPropagation();window.showToast&&window.showToast('This ticket totals $0 — add the truck/asset or hours in Review Ticket before invoicing. A $0 invoice to a customer is never correct.','error')">
                <span class="material-icons">receipt</span> Invoice
              </button>
          ` : `
              <button class="btn btn-sm" style="background:#2E7D32;color:#fff;border:none;" onclick="event.stopPropagation();window.BillingModule.generateInvoice('${ticket.id}')">
                <span class="material-icons">receipt</span> Invoice
              </button>
          `) + _cardBilledElsewhereBtnHtml(ticket) : ''}
          ${/* TASK-1346 sweep — "is this billed?": AR actions (Paid / Write Off)
               belong to every billed ticket, not just the flagged ones. An
               externally-billed ticket has no DD invoice to view. */
            isTicketInvoiced(ticket) ? `
              ${isBilledExternally(ticket)
                ? `<span class="btn btn-sm btn-secondary" style="cursor:default;opacity:0.85;" title="Recorded as billed outside DispatchDeck">
                    <span class="material-icons">assignment_turned_in</span> Billed elsewhere${ticket.externalInvoiceRef ? ` (${escapeHtml(ticket.externalInvoiceRef)})` : ''}
                  </span>`
                : `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.viewInvoice('${ticket.id}')">
                    <span class="material-icons">receipt</span> View Invoice
                  </button>${_switchToExternalBlockReason(ticket) || !_qbLinkRef(ticket) ? '' : _cardToExternalBtnHtml(ticket, 'switchToBilledElsewhere', 'Switch to Billed elsewhere')}`}
              ${ticket.writtenOff
                ? `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.unwriteOffTicket('${ticket.id}')" title="Undo write-off">
                    <span class="material-icons">undo</span> Undo
                  </button>`
                : /* TASK-4767 S2 one pay path — on an invoice record, Paid lives on the invoice */
                  (ticket.paid && _invoiceRecordIdOf(ticket) && !_qboSyncPaid(ticket))
                ? `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.openInvoicesPanel('${_invoiceIdAttr(ticket)}')" title="${escapeHtml(_onInvoiceRecordMsg(ticket))}">
                    <span class="material-icons">receipt_long</span> Paid on invoice
                  </button>`
                : ticket.paid
                ? `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.unmarkTicketPaid('${ticket.id}')" title="Unmark as paid">
                    <span class="material-icons">undo</span> Unpaid
                  </button>`
                : `${_invoiceRecordIdOf(ticket)
                  ? `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.openInvoicesPanel('${_invoiceIdAttr(ticket)}')" title="${escapeHtml(_onInvoiceRecordMsg(ticket))}">
                    <span class="material-icons">receipt_long</span> Pay on invoice
                  </button>`
                  : `<button class="btn btn-sm" style="background:#7C3AED;color:#fff;border:none;" onclick="event.stopPropagation();window.BillingModule.markTicketPaid('${ticket.id}')" title="Mark as paid">
                    <span class="material-icons">payments</span> Paid
                  </button>`}
                  <button class="btn btn-sm" style="background:#dc2626;color:#fff;border:none;" onclick="event.stopPropagation();window.BillingModule.writeOffTicket('${ticket.id}')" title="Write off as uncollectable">
                    <span class="material-icons">money_off</span> Write Off
                  </button>`}
          ` : ''}
          ${/* TASK-9654 — "QB #N" / "QB $X / DD $Y" (display only) */ (hasQboInvoiceLink(ticket) || ticket.qboAmountDiff) ? _qboLinkChipHTML(ticket, 'btn btn-sm btn-secondary') : ''}
          ${/* TASK-1346 sweep — "is this billed?" gates the .iif export. Excluded
               for externally-billed tickets: importing an EXT-<ref> invoice into
               QB Desktop would duplicate the invoice QuickBooks already holds.
               TASK-9654 — same for a ticket linked to its QuickBooks invoice. */
            isTicketInvoiced(ticket) && !isAlreadyInQuickBooks(ticket) ? `
            <button class="btn btn-sm" style="background:#0F172A;color:#fff;border:none;" onclick="event.stopPropagation();window.BillingModule.clearTicketSelection();window.BillingModule.toggleTicketSelect('${ticket.id}');window.BillingModule.exportToIIF()">
              <span class="material-icons">download</span> QB
            </button>
          ` : ''}
          ${ticket.status === 'archived' || ticket.status === 'locked' ? '' /* TASK-1167 — Unarchive now lives in the prominent locked banner above (confirm-gated unarchiveFromReview) */ : `
            ${/* TASK-1346 sweep — books-integrity gate (showSplitTicketModal
                 refuses billed tickets; keep the button consistent with it) */
              !isTicketInvoiced(ticket) && !ticket.paid && ticket.status !== 'sent' ? `
            <button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.showSplitTicketModal('${ticket.id}')">
              <span class="material-icons">call_split</span> Split
            </button>` : ''}
            <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.archiveTicket('${ticket.id}')">
              <span class="material-icons">inventory_2</span> Archive
            </button>
            <button class="btn-icon" onclick="window.BillingModule.deleteTicket('${ticket.id}')" title="Delete" style="color:var(--status-danger);">
              <span class="material-icons">delete</span>
            </button>
          `}
        </div>
      </div>
    </div>
  `;
}

// ============================================
// ACCORDION EXPAND/COLLAPSE
// ============================================
function toggleCustomerExpand(custId) {
  BillingState.expandedCustomers[custId] = !BillingState.expandedCustomers[custId];
  renderTicketsList();
}

function toggleJobExpand(custId, jobKey) {
  const fullKey = `${custId}_${jobKey}`;
  BillingState.expandedJobs[fullKey] = !BillingState.expandedJobs[fullKey];
  renderTicketsList();
}

// ============================================
// TICKET SELECTION & ACTION BAR
// ============================================
function toggleTicketSelect(ticketId, checked) {
  if (checked) {
    BillingState.selectedTicketIds.add(ticketId);
  } else {
    BillingState.selectedTicketIds.delete(ticketId);
  }
  updateSelectionActionBar();
}

function clearTicketSelection() {
  BillingState.selectedTicketIds.clear();
  document.querySelectorAll('.ticket-select-cb').forEach(cb => { cb.checked = false; });
  const bar = document.getElementById('billing-selection-bar');
  if (bar) bar.innerHTML = '';
}

function updateSelectionActionBar() {
  const bar = document.getElementById('billing-selection-bar');
  if (!bar) return;

  const count = BillingState.selectedTicketIds.size;
  if (count === 0) {
    bar.innerHTML = '';
    return;
  }

  const selectedTickets = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));

  // Check if combine is possible (2+ tickets, all same customer, all ready)
  let combineHtml = '';
  if (selectedTickets.length >= 2) {
    const firstCust = selectedTickets[0].customerId;
    const allSameCustomer = selectedTickets.every(t => t.customerId === firstCust);
    if (allSameCustomer) {
      const allReady = selectedTickets.every(t => t.status === 'ready');
      if (allReady) {
        combineHtml = `<button class="btn btn-sm" style="background:#6366F1;color:#fff;border:none;" onclick="window.BillingModule.combineSelectedTickets()">
          <span class="material-icons" style="font-size:16px;">call_merge</span> Combine Tickets
        </button>`;
      } else {
        const notReady = selectedTickets.filter(t => t.status !== 'ready').map(t => '#' + (t.ticketNo || '?')).join(', ');
        combineHtml = `<button class="btn btn-sm" style="background:#94A3B8;color:#fff;border:none;cursor:not-allowed;" onclick="window.showToast('All selected tickets must be in Ready status before combining','error')" title="Tickets not ready: ${notReady}">
          <span class="material-icons" style="font-size:16px;">call_merge</span> Combine Tickets
        </button>`;
      }
    }
  }

  bar.innerHTML = `
    <div class="billing-selection-bar">
      <div class="billing-selection-info">
        <span class="material-icons" style="font-size:18px;color:#3B82F6;">check_circle</span>
        <span>${count} ticket${count !== 1 ? 's' : ''} selected</span>
        <button class="btn-icon" onclick="window.BillingModule.clearTicketSelection()" title="Clear selection" style="margin-left:4px;">
          <span class="material-icons" style="font-size:16px;color:#64748B;">close</span>
        </button>
      </div>
      <div class="billing-selection-actions" style="flex-wrap:wrap;justify-content:flex-end;">
        ${/* TASK-9182 — bulk print. Read-only and the most-reached-for action on
             a multi-select, so it leads the bar. Title names the attachment
             tradeoff up front (the confirm repeats it) because a printed billing
             document that silently drops its photos is a support call. */''}
        <button class="btn btn-sm" style="background:#0891B2;color:#fff;border:none;" onclick="window.BillingModule.printSelectedTickets()" title="Print every selected ticket as one document, each starting on a new page. Photos are not included — print a ticket on its own for those.">
          <span class="material-icons" style="font-size:16px;">print</span> Print Tickets
        </button>
        ${combineHtml}
        ${/* TASK-1581 slice 1 — consolidate tickets scattered across duplicate job
             records onto one engagement. Rendered whenever anything is selected
             rather than pre-filtered on eligibility: the action itself partitions
             the selection and names what it skipped (same reasoning as "Billed
             elsewhere" below — a button that vanishes without explanation is how
             people conclude the feature doesn't exist). */''}
        <button class="btn btn-sm" style="background:#B45309;color:#fff;border:none;" onclick="window.BillingModule.moveSelectedTicketsToJob()" title="Move the selected tickets onto a different job for this customer">
          <span class="material-icons" style="font-size:16px;">drive_file_move</span> Move to job
        </button>
        ${/* TASK-1618 part 2 — the on-ramp. projects.js is 3,094 lines of fully
             built view sitting at ZERO usage (trippin-trucking: 0 projects)
             precisely because nothing creates a project where the work actually
             is. A button that merely NAVIGATES to an empty view would change
             nothing; this one names the engagement, consolidates the tickets onto
             one job, links that job to the new project, and lands the biller in
             the Projects view — which is why it belongs on the SELECTION and not
             the header. Rendered whenever anything is selected, same reasoning as
             "Move to job" above: the action itself partitions and reports. */''}
        ${/* TASK-9414 — the PRIMARY project action: link the selected tickets'
             JOBS to a project, jobs kept separate. The old merge stays, relabelled
             for what it actually does, as the secondary action. */''}
        <button id="billing-add-to-project-btn" class="btn btn-sm" style="background:#7E22CE;color:#fff;border:none;" onclick="window.BillingModule.addSelectedTicketsToProject()" title="Put the jobs these tickets are on into a project — the jobs stay separate">
          <span class="material-icons" style="font-size:16px;">create_new_folder</span> Add to project…
        </button>
        <button id="billing-group-project-btn" class="btn btn-sm" style="background:#fff;color:#6B21A8;border:1px solid #D8B4FE;" onclick="window.BillingModule.groupSelectedTicketsIntoProject()" title="Create a NEW project and pull every selected ticket onto ONE job">
          <span class="material-icons" style="font-size:16px;">call_merge</span> Merge onto one job (new project)
        </button>
        <button class="btn btn-sm" style="background:#2E7D32;color:#fff;border:none;" onclick="window.BillingModule.createGroupedInvoice()">
          <span class="material-icons" style="font-size:16px;">receipt</span> Create Invoice
        </button>
        ${/* TASK-1346 part 3 — the "I already billed these in QuickBooks" twin of
             Create Invoice, sitting right next to it. Rendered whenever anything is
             selected (not pre-filtered on eligibility): the action itself partitions
             the selection and names what it skipped, and a button that vanishes
             without explanation is how people conclude the feature doesn't exist and
             go back to mass-archiving. */''}
        <button class="btn btn-sm" style="background:#0EA5E9;color:#fff;border:none;" onclick="window.BillingModule.markInvoicedExternallyBulk()" title="Record these as already billed in QuickBooks or another system — no DispatchDeck invoice is created">
          <span class="material-icons" style="font-size:16px;">assignment_turned_in</span> Billed elsewhere
        </button>
        ${/* TASK-9735 follow-up — bulk Mark paid with one date + reference. Same
             render-always / partition-and-report shape as Billed elsewhere. */''}
        <button class="btn btn-sm" style="background:#7C3AED;color:#fff;border:none;" onclick="window.BillingModule.markSelectedTicketsPaid()" title="Mark the selected billed tickets paid, with the date the payment came in">
          <span class="material-icons" style="font-size:16px;">payments</span> Mark paid
        </button>
        <button class="btn btn-sm" style="background:#0F172A;color:#fff;border:none;" onclick="window.BillingModule.exportToIIF()">
          <span class="material-icons" style="font-size:16px;">download</span> Export QB (.iif)
        </button>
        <button class="btn btn-sm" style="background:var(--text-secondary);color:#fff;border:none;" onclick="window.BillingModule.exportToQuickBooks()">
          <span class="material-icons" style="font-size:16px;">download</span> Export CSV
        </button>
        <button class="btn btn-sm" style="background:#7C3AED;color:#fff;border:none;" onclick="window.BillingModule.exportOpenInvoiceBatch()" title="Export PIDX XML for OpenInvoice portal">
          <span class="material-icons" style="font-size:16px;">upload_file</span> OpenInvoice
        </button>
      </div>
    </div>
  `;
}

function consolidateSelected() {
  const selectedTickets = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));
  if (selectedTickets.length < 2) {
    window.showToast?.('Select at least 2 tickets to consolidate', 'info');
    return;
  }
  const first = selectedTickets[0];
  consolidateTickets(first.customerId, first.jobId || '', first.workDate);
  clearTicketSelection();
}

async function combineSelectedTickets() {
  const selectedTickets = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));
  if (selectedTickets.length < 2) {
    window.showToast?.('Select at least 2 tickets to combine', 'info');
    return;
  }
  if (!selectedTickets.every(t => t.status === 'ready')) {
    window.showToast?.('All selected tickets must be in Ready status before combining', 'error');
    return;
  }
  // TASK-1176 — an invoiced ticket keeps its ready status, so the check above lets it
  // through; combining rewrites content, which the books-integrity rules now deny.
  // TASK-1346 sweep — books-integrity gate.
  if (selectedTickets.some(t => isTicketInvoiced(t))) {
    window.showToast?.('Cannot combine a ticket that is already invoiced — uninvoice it first.', 'error');
    return;
  }
  const firstCust = selectedTickets[0].customerId;
  if (!selectedTickets.every(t => t.customerId === firstCust)) {
    window.showToast?.('All selected tickets must be from the same customer', 'error');
    return;
  }

  // Sort by ticketNo so lowest # becomes the target
  selectedTickets.sort((a, b) => (a.ticketNo || 9999) - (b.ticketNo || 9999));
  const target = selectedTickets[0];
  const sources = selectedTickets.slice(1);
  const custName = target.customerName || 'Unknown';

  if (!confirm(`Combine ${selectedTickets.length} tickets for ${custName} into ticket #${target.ticketNo || '?'}?\n\nAll line items, notes, and photos will be merged. This cannot be undone.`)) return;

  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

    for (const source of sources) {
      let _ppeTouchRef = null; // TASK-9725
      // 1. Copy all ticketLines from source → target
      const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines'));
      for (const lineDoc of linesSnap.docs) {
        const lineData = lineDoc.data();
        // TASK-9440 — never copy a FUEL_SURCHARGE line: it is server-owned and a %
        // of ITS ticket's charges. Dropped from the source; the server re-derives
        // the target's surcharge from the merged lines on its next pass.
        // TASK-9725 — same for a billed PPE line (server-owned rollup of the
        // signed sign-outs): dropped here, re-derived on the target once its
        // crew lines land.
        if (lineData.type === 'FUEL_SURCHARGE' || lineData.sourceType === 'safetyEquipment') {
          await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines', lineDoc.id));
          continue;
        }
        const newLineRef = await addDoc(collection(db, 'tenants', tenant, 'tickets', target.id, 'ticketLines'), lineData);
        if (lineData.type === 'EMPLOYEE' && !_ppeTouchRef) _ppeTouchRef = newLineRef; // TASK-9725
        // Update clockEntries referencing this line
        if (lineData.clockEntryId) {
          try {
            await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', lineData.clockEntryId), {
              ticketId: target.id,
              ticketLineId: newLineRef.id
            });
          } catch (e) { /* clockEntry may not exist */ }
        }
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines', lineDoc.id));
      }

      // 2. Re-point remaining clockEntries
      try {
        const entriesSnap = await getDocs(query(
          collection(db, 'tenants', tenant, 'clockEntries'),
          where('ticketId', '==', source.id)
        ));
        for (const entryDoc of entriesSnap.docs) {
          await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryDoc.id), {
            ticketId: target.id
          });
        }
      } catch (e) { console.warn('[Billing] Error updating clockEntries for combine:', e); }

      // 3. Merge notes
      if (source.notes || source.workPerformed) {
        const sourceNotes = source.notes || source.workPerformed || '';
        const targetNotes = target.notes || target.workPerformed || '';
        if (sourceNotes) {
          const merged = targetNotes ? targetNotes + '\n' + sourceNotes : sourceNotes;
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
            notes: merged,
            workPerformed: merged,
            ..._notesEditStamp('billing_combine') // TASK-10036 (a)
          });
          target.notes = merged;
          target.workPerformed = merged;
        }
      }

      // 4. Merge photos
      if (source.photos?.length > 0) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
          photos: arrayUnion(...source.photos)
        });
      }

      // 5. Delete source ticket
      await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id));
      // TASK-9725 — the source is gone: nudge the server to re-derive any billed
      // PPE line on the target (its sign-outs were attributed to the source).
      if (_ppeTouchRef) { try { await updateDoc(_ppeTouchRef, { ppeBillTouch: Timestamp.now() }); } catch (e) { console.warn('[Billing] ppe touch failed:', e?.message || e); } }
    }

    // 6. Recalculate target grand total — TASK-405: tax-aware (was tax-blind, which
    // undercharged tax-enabled tickets — sibling of CRIT-2). Mirrors calcReviewTax.
    const allLines = await getDocs(collection(db, 'tenants', tenant, 'tickets', target.id, 'ticketLines'));
    // Re-read the target doc for AUTHORITATIVE tax settings (the in-memory `target`
    // can be stale if another session changed the ticket's tax config — COD).
    const _tgtSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', target.id));
    const _tot = computeTicketTotalsFromLines(allLines.docs.map(d => d.data()), _tgtSnap.exists() ? _tgtSnap.data() : target);
    const grandTotal = _tot.grandTotal;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
      grandTotal,
      taxableSubtotal: _tot.taxableSubtotal,
      taxAmount: _tot.taxAmount,
      updatedAt: Timestamp.now()
    });

    window.showToast?.(`${sources.length + 1} tickets combined into #${target.ticketNo || ''}`, 'success');
    clearTicketSelection();
    renderTicketsList();
  } catch (err) {
    console.error('[Billing] Combine tickets error:', err);
    window.showToast?.('Error combining tickets', 'error');
  }
}

function parseTimeStr(str) {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

// ============================================
// NAVIGATION
// ============================================
function showJobTickets(jobId, jobName) {
  BillingState.ticketJobFilter = jobId;
  BillingState.ticketJobFilterName = jobName || 'Job';
  // renders-audit #9 — jobs are UNBOUNDED but the tickets listener is windowed:
  // drilling into a job that started before the window silently under-reported
  // its tickets (manual "Load older" existed, no auto-upgrade). If the job's own
  // start predates the window, pull full history up front.
  const _dj = BillingState.jobs.find(j => j.id === jobId);
  const _dStart = _dj && (_dj.startDate?.toDate?.() || (_dj.startDate ? new Date(_dj.startDate) : null));
  if (_dStart && _isBeforeTicketWindow(_dStart)) ensureFullTicketHistory();
  // TASK-1618 — the two drill-downs are mutually exclusive; entering one clears
  // the other, so they can never intersect into a set nobody asked for.
  BillingState.ticketProjectFilter = null;
  BillingState.ticketProjectFilterName = null;
  BillingState.ticketProjectMode = null;
  BillingState.filter = 'all';
  closeModal('billing-job');
  switchBillingView('tickets');
}

function showAllTickets(dateStr) {
  BillingState.ticketJobFilter = null;
  BillingState.ticketJobFilterName = null;
  // TASK-1618 — "all tickets for a date" must not silently stay inside a project.
  BillingState.ticketProjectFilter = null;
  BillingState.ticketProjectFilterName = null;
  BillingState.ticketProjectMode = null;
  BillingState.filter = 'daily';
  BillingState.dailyDate = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  // TASK-857: jumping to an old date pulls full history so the day isn't empty.
  if (_isBeforeTicketWindow(BillingState.dailyDate)) ensureFullTicketHistory();
  switchBillingView('tickets');
}

// TASK-1345 — now actually reachable (a checkbox renders on the Active view).
// The export reads the same flag through the shared filter, so the CSV can't
// disagree with the screen it came from. Selection is cleared because the
// visible set changes underneath it.
function toggleShowArchived(checked) {
  BillingState.showArchived = !!checked;
  // renders-audit #11 — an archived ticket is EXACTLY the kind that predates
  // the bounded live window (ticket-fiasco class: archived + old = invisible
  // everywhere). Asking to see archived means asking to see ALL of them, so
  // pull full history the moment the toggle turns on.
  if (checked) ensureFullTicketHistory();
  clearTicketSelection();
  renderTicketsList();
}

function backToCalendar() {
  switchBillingView('calendar');
}

// ============================================
// JOB DETAIL MODAL (Read-Only)
// ============================================
function showBillingJobDetail(jobId) {
  const job = BillingState.jobs.find(j => j.id === jobId);
  if (!job) return;

  BillingState.selectedJob = job;

  const titleEl = document.getElementById('billing-job-title');
  if (titleEl) {
    titleEl.innerHTML = escapeHtml(job.title || 'Job Details') +
      (job.permitSettings?.usedFastTrax ? ' <span class="badge badge-fasttrax" style="margin-left:8px;"><span class="material-icons" style="font-size:12px;">bolt</span> FastTrax</span>' : '');
  }

  const content = document.getElementById('billing-job-content');
  if (!content) return;

  // Find related jobDays
  const relatedJobDays = BillingState.jobDays
    .filter(jd => jd.jobId === jobId)
    .sort((a, b) => { const da = typeof a.date === 'string' ? a.date : a.date?.toDate ? a.date.toDate().toISOString().slice(0,10) : ''; const db = typeof b.date === 'string' ? b.date : b.date?.toDate ? b.date.toDate().toISOString().slice(0,10) : ''; return da.localeCompare(db); });

  // Customer info
  const customer = job.customerId ? BillingState.customers.find(c => c.id === job.customerId) : null;
  const customerName = customer?.name || job.customer || '-';

  let html = `<div class="ticket-detail">`;

  // Info grid
  html += `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);margin-bottom:var(--spacing-lg);">
      <div style="flex-direction:column;gap:2px;">
        <div class="detail-label">Customer</div>
        <div class="detail-value" style="text-align:left;">${escapeHtml(customerName)}</div>
      </div>
      ${job.location || job.address ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">Location</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(job.location || job.address)}</div>
        </div>
      ` : ''}
      ${jobPoList(job).length ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">PO #</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(jobPoList(job).join(', '))}</div>
        </div>
      ` : ''}
      ${job.vendorNumber ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">Vendor #</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(job.vendorNumber)}</div>
        </div>
      ` : ''}
      ${job.wellName ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">Well Name</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(job.wellName)}</div>
        </div>
      ` : ''}
      ${job.wellNumber ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">Well #</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(job.wellNumber)}</div>
        </div>
      ` : ''}
      ${job.startDate ? `
        <div style="flex-direction:column;gap:2px;">
          <div class="detail-label">Date Range</div>
          <div class="detail-value" style="text-align:left;">${formatJobDate(job.startDate)}${job.endDate ? ' to ' + formatJobDate(job.endDate) : ''}</div>
        </div>
      ` : ''}
    </div>
  `;

  // POC
  if (job.poc && job.poc.name) {
    const poc = job.poc;
    html += `
      <div style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;margin-bottom:var(--spacing-md);background:var(--bg-page);border-radius:var(--radius-sm);border:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);margin-top:1px;">contact_phone</span>
        <div style="flex:1;font-size:0.8125rem;line-height:1.5;">
          <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(poc.name)}${poc.role ? ` <span style="font-weight:400;color:var(--text-secondary);">(${escapeHtml(poc.role)})</span>` : ''}</div>
          <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:2px;">
            ${poc.phone ? `<a href="tel:${escapeHtml(poc.phone)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.phone)}</a>` : ''}
            ${poc.phone2 ? `<a href="tel:${escapeHtml(poc.phone2)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.phone2)}</a>` : ''}
            ${poc.email ? `<a href="mailto:${escapeHtml(poc.email)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.email)}</a>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // Schedule (jobDays)
  if (relatedJobDays.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">calendar_today</span>
          Schedule (${relatedJobDays.length} day${relatedJobDays.length !== 1 ? 's' : ''})
        </div>
        <div style="max-height:200px;overflow-y:auto;">
    `;
    // Show recent/upcoming 10 jobDays
    const todayStr = formatDateISO(new Date());
    const getDateStr = (d) => typeof d === 'string' ? d : d?.toDate ? formatDateISO(d.toDate()) : formatDateISO(new Date(d));
    const nearDays = relatedJobDays.filter(jd => getDateStr(jd.date) >= todayStr).slice(0, 10);
    const showDays = nearDays.length > 0 ? nearDays : relatedJobDays.slice(-10);

    for (const jd of showDays) {
      const crewCount = (jd.drivers?.length || 0) + (jd.helpers?.length || 0);
      const equipCount = (jd.trucks?.length || 0) + (jd.trailers?.length || 0) + (jd.assets?.length || 0);
      html += `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid var(--border-light);font-size:0.8125rem;">
          <span>${formatJobDate(jd.date)}</span>
          <span style="color:var(--text-secondary);">${crewCount} crew, ${equipCount} equip</span>
        </div>
      `;
    }

    if (relatedJobDays.length > showDays.length) {
      html += `<div style="padding:4px 0;font-size:0.75rem;color:var(--text-muted);">...and ${relatedJobDays.length - showDays.length} more</div>`;
    }
    html += `</div></div>`;
  }

  // Permits (TASK-9694: a driver Doc is not a permit)
  const _loadPermits = jobPermitEntries(job);
  if (_loadPermits.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">description</span>
          Load Permits (${_loadPermits.length})
        </div>
        <div class="permit-attach-list">
    `;
    for (const permit of _loadPermits) {
      const name = permit.name || permit.fileName || 'Permit';
      // TASK-9855 — open through the Storage SDK on the permit's path (rules-gated).
      const hasFile = !!window.StorageFile.fileRef(permit);
      html += `
        <div class="permit-attach-row">
          <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">picture_as_pdf</span>
          <span style="flex:1;font-size:0.8125rem;">${escapeHtml(name)}</span>
          ${hasFile ? `<a ${window.StorageFile.linkAttrs(permit)} class="btn btn-sm btn-secondary" style="text-decoration:none;">View</a>` : ''}
        </div>
      `;
    }
    html += `</div></div>`;
  }

  // Job Photos
  if (job.photos && job.photos.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">photo_library</span>
          Photos (${job.photos.length})
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;">
          ${job.photos.map(p => {
            // TASK-9855 — thumb + full open through the Storage SDK by path.
            const name = p.name || p.fileName || '';
            const isPdf = name.toLowerCase().endsWith('.pdf');
            if (isPdf) {
              return `<a ${window.StorageFile.linkAttrs(p)} style="width:80px;height:80px;border:1px solid var(--border-color);border-radius:var(--radius-sm);display:flex;flex-direction:column;align-items:center;justify-content:center;text-decoration:none;background:var(--bg-secondary);">
                <span class="material-icons" style="font-size:28px;color:var(--text-secondary);">picture_as_pdf</span>
                <span style="font-size:10px;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;max-width:70px;white-space:nowrap;">${escapeHtml(name)}</span>
              </a>`;
            }
            return `<a ${window.StorageFile.linkAttrs(p)} style="display:block;width:80px;height:80px;border:1px solid var(--border-color);border-radius:var(--radius-sm);overflow:hidden;">
              <img ${_sfThumbAttrs(p)} alt="${escapeHtml(name)}" style="width:100%;height:100%;object-fit:cover;">
            </a>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  // Inline ticket cards
  const jobTickets = BillingState.tickets.filter(t => t.jobId === jobId);
  // TASK-1414 — the SECOND door to the same duplicate. This modal offered a
  // primary create button whenever THIS job doc had no tickets, blind to the
  // sibling records the same real job is split across (COD Blast-Radius).
  // No date scoping here: this view is the whole job, not one day.
  const twinTickets = findTwinJobTickets(job, jobTickets, null);
  if (jobTickets.length > 0) {
    html += `<div class="detail-section">
      <div class="detail-section-title">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">receipt_long</span>
        Tickets (${jobTickets.length})
      </div>`;
    for (const t of jobTickets) {
      let empCount = t.employeeCount || t.employees?.length || 0;
      const hrs = getTicketLiveHours(t.id, t.totalHours || 0);
      const total = t.grandTotal || 0;
      html += `
        <div style="border:1px solid ${t.status === 'draft' ? '#FECACA' : 'var(--border-light)'};border-radius:var(--radius-sm);padding:10px 12px;margin-bottom:8px;background:${t.status === 'draft' ? '#FFFBFB' : 'var(--bg-page)'};">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">
              ${t.ticketNo ? '#' + t.ticketNo + ' — ' : ''}${formatDate(t.date)}
            </span>
            <div style="display:flex;gap:4px;align-items:center;">
              <span class="badge badge-${t.status}">${capitalizeFirst(t.status)}</span>
              ${BillingState.activeTicketIds?.has(t.id) ? '<span class="badge-active-shift">Active Clock-In</span>' : ''}
            </div>
          </div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">
            ${empCount} employee${empCount !== 1 ? 's' : ''} · ${hrs.toFixed(1)} hrs · $${total.toFixed(2)}
          </div>
          <div style="display:flex;gap:8px;">
            <button class="btn btn-sm btn-primary" onclick="window.BillingModule.openReviewTicket('${t.id}')">
              <span class="material-icons">edit_note</span> Edit Ticket
            </button>
            ${/* TASK-1346 sweep — books-integrity gate (double-bill) */ !isTicketInvoiced(t)
              ? `<button class="btn btn-sm btn-primary" onclick="window.BillingModule.generateInvoice('${t.id}')">
                  <span class="material-icons">receipt</span> Generate Invoice
                </button>`
              : `<button class="btn btn-sm btn-secondary" onclick="window.BillingModule.viewInvoice('${t.id}')">
                  <span class="material-icons">receipt</span> View Invoice
                </button>`
            }
            ${/* TASK-1346 sweep — books-integrity gate */ !isTicketInvoiced(t) && !t.paid && t.status !== 'sent' ? `<button class="btn btn-sm btn-secondary" onclick="event.stopPropagation();window.BillingModule.showSplitTicketModal('${t.id}')">
              <span class="material-icons">call_split</span> Split
            </button>` : ''}
          </div>
        </div>`;
    }
    html += `</div>`;
  }

  // TASK-1414 — this job doc has no tickets, but a sibling record does.
  if (jobTickets.length === 0) html += renderTwinTicketWarning(twinTickets, 'under another record for the same job');

  html += `</div>`;
  content.innerHTML = html;

  // Action buttons — show Generate Ticket only when no tickets exist
  const actionsEl = document.getElementById('billing-job-actions');
  if (actionsEl) {
    let actionBtns = `<button class="btn btn-secondary" onclick="window.BillingModule.closeModal('billing-job')">Close</button>`;
    if (jobTickets.length === 0) {
      // TASK-1414 — demoted (not removed) when a twin record already has
      // tickets; a second ticket is occasionally legitimate, so the human calls
      // it. Label also corrected: this button CREATES, it never edits.
      actionBtns += ` <button class="btn ${twinTickets.length ? 'btn-secondary' : 'btn-primary'}" onclick="window.BillingModule.generateTicketFromJob('${jobId}')" id="btn-generate-ticket"${twinTickets.length ? ' title="Another record for this job already has tickets — check those first"' : ''}>
        <span class="material-icons">add_circle</span> Create Ticket
      </button>`;
    }
    actionsEl.innerHTML = actionBtns;
    // TASK-1428 — the words-check is DAY-SCOPED and deliberately NOT run here.
    // This modal is the whole job, so there is no day to fetch clock notes for
    // and no date window to bound the match; running it would compare a bare
    // title against every ticket this customer ever had. The calendar day-cell
    // modal, which does have a date, carries the check. findTwinJobTickets
    // above still covers the sibling-record case on this screen.
  }

  document.getElementById('modal-billing-job')?.classList.remove('hidden');
}

// ============================================
// VIEW TICKET DETAIL (with ticketLines + permit attach)
// ============================================
// TASK-9855 — thumbnail <img> attrs through window.StorageFile: the thumb's own
// path/URL when the doc has one, else the full file. No stored token URL in src.
function _sfThumbAttrs(d) {
  if (d && typeof d === 'object' && (d.thumbUrl || d.thumbPath)) return window.StorageFile.imgAttrs(d, { urlKey: 'thumbUrl' });
  return window.StorageFile.imgAttrs(d, d && typeof d === 'object' && (d.fullUrl || d.fullPath) ? { urlKey: 'fullUrl' } : undefined);
}
// TASK-1220 — tap a ticket-photo thumbnail in the View Ticket gallery →
// full-size in-app lightbox (PDFs keep opening in a new tab via their <a>).
function showTicketPhoto(idx) {
  const t = BillingState.selectedTicket;
  const photo = t?.photos?.[idx];
  if (!photo) return;
  // TASK-9855 — bytes through the Storage SDK (rules-gated), then the lightbox.
  window.StorageFile.blobUrl(photo)
    .then((u) => { if (u) window.PhotoLightbox?.show(u); })
    .catch((err) => window.showToast?.(err?.sfReason === 'denied' ? "You don't have access to this photo." : 'Could not open the photo.', 'error'));
}

// TASK-1214 — biller recourse: delete an uploaded photo off an unlocked
// ticket. Routes through the shared window.TicketPhotos helper (confirm +
// arrayRemove of the exact stored element + photoCount recompute + jobAuditLog
// receipt). driverEditLocked deliberately does NOT block the biller (TASK-566);
// invoiced/archived/locked do (isReviewReadOnly + the helper's fresh-read).
async function deleteTicketPhoto(idx) {
  const ticket = BillingState.selectedTicket;
  if (!ticket) return;
  if (isReviewReadOnly(ticket)) {
    window.showToast?.("This ticket is locked — photos can't be deleted", 'error');
    return;
  }
  const photo = ticket.photos?.[idx];
  if (!photo) return;
  const url = photo.url || photo.downloadUrl || (typeof photo === 'string' ? photo : null);
  if (!url) return;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const res = await window.TicketPhotos?.removeUploaded({
    tenantId: tenant,
    ticketId: ticket.id,
    photo: { url, name: photo.name || photo.fileName || null },
    source: 'billing-view-ticket',
    respectDriverLock: false
  });
  if (!res?.ok) return;
  // Keep the in-memory list in sync, then re-render the modal (gallery + the
  // "Photos (N)" action button both re-derive from ticket.photos).
  ticket.photos = (ticket.photos || []).filter(p => (p?.url || p?.downloadUrl || (typeof p === 'string' ? p : null)) !== url);
  const listTicket = BillingState.tickets.find(t => t.id === ticket.id);
  if (listTicket && listTicket !== ticket) listTicket.photos = ticket.photos;
  viewTicket(ticket.id);
}

// ============================================================================
// TASK-4745 — TICKET DETAIL FOOTER
// ============================================================================
// The old footer painted NINE identical-weight buttons across two centred rows
// and let TWO of them claim primary: `Edit Ticket` was `btn-primary` blue while
// `Generate Invoice` carried an inline green. Two primaries means none. Worse,
// `Archive` sat in the middle of six grey pills wearing Print's exact coat, so a
// state change was one mis-tap from a print on a tablet in a truck cab.
//
// The shape now: ONE filled primary per state, pinned right; the outlined
// actions beside it; everything demoted — never deleted — behind a `⋯` that
// reuses the `.billing-overflow-*` pattern this file already ships. `Close`
// becomes a text link on the left, opposite the primary.
//
// 🔴 THREE THINGS THAT ARE NOT COSMETIC AND MUST NOT BE "TIDIED" LATER:
//   1. Print and Email STAY VISIBLE. TASK-4662's COD put Email beside Print
//      because that is where Lacey looks and she expects it to behave like
//      Print. Burying either re-opens a decision that was already made.
//   2. "Billed elsewhere" STAYS VISIBLE on a draft. TASK-1346 added it to stop
//      people using Archive to mean "billed"; hiding it behind the kebab while
//      Archive is one tap away would hand the misuse straight back. It is a
//      FORK away from Generate Invoice, not a sub-option of it, so it renders as
//      a quiet outline beside the primary rather than as another filled button.
//   3. There is not always a primary, and that is correct. A written-off ticket
//      has no forward money action; inventing one would be a lie in colour.
// ============================================================================

// The two rules the footer needs, injected ONCE from here — this task's surface
// is billing.js only, and both selectors are scoped to `#view-ticket-actions`.
//
// 🔴 Touch targets are keyed on `(pointer: coarse)`, NEVER a width query
// (TASK-4813): a 1024px cab tablet needs the 44px target and a 1024px mouse
// window does not, and a width query gets that exactly backwards on the one
// device this footer is actually used on. MEASURED, not asserted: 1024 coarse
// renders every footer control at 44px, 1024 fine leaves them at 36px.
//
// The `!important` on those four is not laziness. styles.css:9691 sets
// `.btn-secondary, .btn-sm { min-height: 40px !important; }` inside a WIDTH
// query, so on a phone the kebab measured 40px until this outranked it — the
// exact width-vs-pointer confusion TASK-4813 named, met head-on.
//
// Colours here were measured in a real Chromium against the real stylesheet,
// not eyeballed. The four that moved were all inherited and all failed WCAG AA
// at 4.5:1 once they became the loudest thing on the surface:
//   Sync to QB   #2CA01C -> #1E7A12  (3.41:1 -> 5.46:1 on white text)
//   Unarch&Inv   #16A34A -> #15803D  (3.30:1 -> 5.02:1)
//   Undo Payment #3B82F6 -> #1D4ED8  (3.36:1 -> 6.12:1 on the button face)
//   Rev Write-Off #DC2626 -> #B91C1C (4.41:1 -> 5.91:1)
function _ensureTicketFooterStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('vt-footer-styles')) return;
  const el = document.createElement('style');
  el.id = 'vt-footer-styles';
  el.textContent = `
/* Every selector starts #modal-view-ticket #view-ticket-actions (2,0,0). The
   shared styles.css block "#modal-view-ticket .form-actions" (1,1,0) and its
   ".btn" child rule (1,2,0) used to outrank the (1,0,0) rules this function
   shipped with, which is what Chris saw on 9/23: flex-wrap:wrap won, Close fell
   onto its own row as a shadowed pill, and Generate Invoice orphaned below.
   That block also styles #modal-billing-job and #modal-review-ticket, so it is
   outranked here rather than edited there.

   TWO FIXED ROWS above 640px, at every width, no width band in between (COD
   2026-09-23 picked this over widening the modal; styles.css ~33606 records the
   hr.js 720px footer breaking three times on width-banded layouts):
     row 1  tools     Print  Email  Edit Ticket  [View Invoice]  More
     row 2  decision  Grand Total  . . .  [fork / chip / Unarchive / Undo]  PRIMARY
   The widest state (archived + invoiced) measures well inside the 672px the
   720px modal leaves, so neither row can wrap.
   TASK-10044 (Chris 10/23): the Grand Total took the slot the footer Close had,
   so the money sits beside the decision and cannot be scrolled away; the header
   X is the way out at every width. Above 640px the tools go ghost (no fill, no
   border) so they read quieter than the outlined fork and the one filled
   primary; a hairline separates the two rows. */
#modal-view-ticket #view-ticket-actions { display: flex; flex-direction: column; align-items: stretch; flex-wrap: nowrap; gap: 10px; flex-shrink: 0; padding: 12px 24px 16px; }
#modal-view-ticket #view-ticket-actions .vt-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
#modal-view-ticket #view-ticket-actions .vt-decide { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
#modal-view-ticket #view-ticket-actions .vt-decide-actions { display: flex; flex-wrap: nowrap; align-items: center; justify-content: flex-end; gap: 8px; min-width: 0; margin-left: auto; }
#modal-view-ticket #view-ticket-actions .vt-decide-actions > .btn { flex-shrink: 0; }
#modal-view-ticket #view-ticket-actions .btn { padding: 8px 16px; min-height: 40px; font-size: 0.875rem; gap: 6px; box-shadow: none; white-space: nowrap; }
#modal-view-ticket #view-ticket-actions .btn .material-icons { font-size: 18px; }
#modal-view-ticket #view-ticket-actions .vt-primary { padding: 8px 20px; min-height: 42px; box-shadow: 0 2px 4px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04); }
/* The money. Display only, never a control. The label sits OVER the amount
   (button height, about 130px wide) rather than beside it (about 200px): measured
   in Chromium, beside-it pushed archived + invoiced + Switch to Billed elsewhere
   past the 672px row. If a state's buttons still cannot fit beside the total
   (archived + money + Unarchive and mark Billed elsewhere, which already overran
   with Close), .vt-decide wraps: total on its own line, buttons right-aligned
   under it, never drawn on top of each other. */
#modal-view-ticket #view-ticket-actions .vt-decide { flex-wrap: wrap; row-gap: 8px; }
#modal-view-ticket #view-ticket-actions .vt-total { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; flex: 0 0 auto; white-space: nowrap; }
#modal-view-ticket #view-ticket-actions .vt-total-label { display: inline-flex; align-items: baseline; gap: 6px; font-size: 0.75rem; font-weight: 600; line-height: 1.2; color: var(--text-secondary); }
#modal-view-ticket #view-ticket-actions .vt-total-tax { font-size: 0.6875rem; font-weight: 500; }
#modal-view-ticket #view-ticket-actions .vt-total-amount { font-size: 1.25rem; font-weight: 700; line-height: 1.2; color: var(--text-primary); font-variant-numeric: tabular-nums; }
/* The fork away from the primary: same size, deliberately quieter fill. */
#modal-view-ticket #view-ticket-actions .vt-btn-fork { background: transparent; color: var(--text-secondary); border: 2px solid var(--border-medium); box-shadow: none; }
#modal-view-ticket #view-ticket-actions .vt-btn-fork:hover { background: var(--bg-elevated); color: var(--text-primary); }
#modal-view-ticket #view-ticket-actions .vt-btn-fork .material-icons { color: var(--text-secondary); }
/* The kebab. .billing-overflow-wrap is display:none until the mobile media
   query, so the footer copy has to switch it back on at every width. It is the
   LAST tool, so its menu opens upward and anchors RIGHT: anchored left it would
   run past the modal edge, and .modal-content is overflow:hidden. */
#modal-view-ticket #view-ticket-actions .billing-overflow-wrap { display: inline-block !important; }
#modal-view-ticket #view-ticket-actions .billing-overflow-btn { min-width: 40px; min-height: 40px; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown { top: auto; bottom: 100%; margin-top: 0; margin-bottom: 6px; left: auto; right: 0; min-width: 220px; }
#modal-view-ticket #view-ticket-actions .vt-more-label { display: none; }
/* The chip carries a user-typed QuickBooks ref of any length. It is the ONE
   item in row 2 allowed to shrink, and it wraps inside itself (anywhere, since
   a ref has no spaces) rather than pushing the primary onto a line of its own. */
#modal-view-ticket #view-ticket-actions .vt-state-chip { background: transparent; box-shadow: none; border-style: dashed; color: var(--text-secondary); cursor: default; white-space: normal; overflow-wrap: anywhere; text-align: left; }
#modal-view-ticket #view-ticket-actions .vt-decide-actions > .vt-state-chip { flex: 0 1 auto; min-width: 0; }
#modal-view-ticket #view-ticket-actions .vt-state-chip:hover { transform: none; box-shadow: none; }
#modal-view-ticket #view-ticket-actions .vt-state-chip .material-icons { color: var(--text-secondary); }
#modal-view-ticket #view-ticket-actions .vt-menu-sep { height: 1px; background: var(--border-light); margin: 4px 0; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-warn { color: #92400E; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-warn .material-icons { color: #92400E; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-warn:hover { background: #FEF3C7; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-danger { color: #B91C1C; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-danger .material-icons { color: #B91C1C; }
#modal-view-ticket #view-ticket-actions .billing-overflow-dropdown .vt-menu-danger:hover { background: #FEF2F2; }
/* "Photos (N)" is an anchor onto a gallery already on screen. It earns a button
   only where the gallery is genuinely scrolled away; on desktop it lives in the
   menu, so the capability exists at BOTH widths and is duplicated at neither. */
#modal-view-ticket #view-ticket-actions .vt-only-narrow { display: none; }
/* TASK-10044 — desktop and tablet: the tools row goes ghost. Same 40px height
   (44px on a coarse pointer, below), same icon and label, no fill and no border,
   so Print / Email / Edit read lighter than the outlined fork and the filled
   primary. The phone toolbar cells keep their look (its stack is unchanged). */
@media (min-width: 641px) {
  #modal-view-ticket #view-ticket-actions .vt-tools .btn { background: transparent; border-color: transparent; color: var(--text-secondary); padding: 8px 12px; box-shadow: none; }
  #modal-view-ticket #view-ticket-actions .vt-tools .btn .material-icons { color: var(--text-secondary); }
  #modal-view-ticket #view-ticket-actions .vt-tools .btn:hover { background: var(--bg-elevated); border-color: var(--border-light); color: var(--text-primary); transform: none; box-shadow: none; }
  #modal-view-ticket #view-ticket-actions .vt-decide { border-top: 1px solid var(--border-light); padding-top: 10px; }
}
/* Phone. The decision stack on top, one toolbar row under it:
     Grand Total             1,360.00    (one compact line, TASK-10044)
     Generate Invoice                    (full width, 48px)
     Billed elsewhere                    (full width; any decision button alone on its line goes full width)
     Print | Email | Edit | ... | More   (equal cells, icon over a 12px label)
   was ~290-340px of footer in up to six ragged rows with a lone More and a lone
   Close; this is ~190px with no orphan, plus one short line for the total.
   There is no footer Close (TASK-10044 removed it at every width): the sheet
   header X runs the same closeModal and is raised to 44px below.
   NO BACKTICKS IN THIS COMMENT — it lives inside the injected CSS template
   literal, and one backtick here ends the template early. node --check still
   passes and the whole stylesheet silently becomes a fragment. */
@media (max-width: 640px) {
  #modal-view-ticket #view-ticket-actions { gap: 8px; }
  #modal-view-ticket #view-ticket-actions .vt-decide { order: -1; }
  #modal-view-ticket #view-ticket-actions .vt-decide { flex-wrap: wrap; row-gap: 6px; }
  #modal-view-ticket #view-ticket-actions .vt-total { flex: 1 1 100%; flex-direction: row; justify-content: space-between; align-items: baseline; }
  #modal-view-ticket #view-ticket-actions .vt-total-amount { font-size: 1.125rem; }
  #modal-view-ticket #view-ticket-actions .vt-decide-actions { width: 100%; margin-left: 0; flex-wrap: wrap; }
  #modal-view-ticket #view-ticket-actions .vt-decide-actions > .btn { flex: 1 1 calc(50% - 4px); justify-content: center; }
  #modal-view-ticket #view-ticket-actions .vt-decide-actions > .vt-primary { flex: 1 1 100%; order: -1; min-height: 48px !important; font-size: 1rem !important; }
  /* A state marker is not half of a button pair. */
  #modal-view-ticket #view-ticket-actions .vt-decide-actions > .vt-state-chip { flex: 1 1 100%; white-space: normal; }
  /* gap 4px, not more: six cells on a 320px phone must still clear 44px wide. */
  #modal-view-ticket #view-ticket-actions .vt-tools { flex-wrap: nowrap; align-items: stretch; gap: 4px; }
  #modal-view-ticket #view-ticket-actions .vt-tools > .btn,
  #modal-view-ticket #view-ticket-actions .vt-tools > .billing-overflow-wrap { flex: 1 1 0; min-width: 0; }
  #modal-view-ticket #view-ticket-actions .vt-tools .btn {
    flex-direction: column; gap: 2px; width: 100%; min-height: 56px !important;
    padding: 6px 2px !important; font-size: 12px !important; line-height: 1.15;
    white-space: normal; text-align: center;
  }
  #modal-view-ticket #view-ticket-actions .vt-tools .btn .material-icons { font-size: 20px !important; margin: 0; }
  #modal-view-ticket #view-ticket-actions .vt-tools .billing-overflow-btn { height: 100%; }
  #modal-view-ticket #view-ticket-actions .vt-more-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-primary); }
  #modal-view-ticket #view-ticket-actions .vt-only-narrow { display: inline-flex; }
  #modal-view-ticket #view-ticket-actions .vt-wide-only { display: none; }
}
@media (pointer: coarse) {
  #modal-view-ticket #view-ticket-actions .btn { min-height: 44px !important; }
  #modal-view-ticket #view-ticket-actions .billing-overflow-btn { min-width: 44px !important; min-height: 44px !important; }
  #modal-view-ticket #view-ticket-actions .billing-overflow-dropdown button { min-height: 44px !important; }
  /* The header X is the phone's Close (see above), so it gets the target too. */
  #modal-view-ticket .modal-header .btn-icon { width: 44px !important; height: 44px !important; }
}`;
  document.head.appendChild(el);
}

// One builder, so a new footer action cannot arrive wearing a coat nobody chose.
function _vtBtn({ cls = 'btn btn-secondary', style = '', onclick, icon, label, title = '' }) {
  return `<button class="${cls}"${style ? ` style="${style}"` : ''}${title ? ` title="${escapeHtml(title)}"` : ''} onclick="${onclick}">` +
    `${icon ? `<span class="material-icons">${icon}</span> ` : ''}${label}</button>`;
}

// TASK-10044 — the ticket's Grand Total, ONE formula for both places it shows:
// the body block at the bottom of the scroll area and the footer decision row.
// Stored grandTotal first (viewTicket heals it from the fresh lines before
// either renders); the line sum only when the header carries none.
function _vtGrandTotal(ticket, lines) {
  const stored = Number(ticket && ticket.grandTotal) || 0;
  if (stored) return stored;
  return (Array.isArray(lines) ? lines : []).reduce((s, l) => s + (l && l.type === 'DOWNTIME' ? -1 : 1) * (Number(l && l.lineAmount) || 0), 0);
}

// TASK-10044 — the money, in the footer row where the billing decision is made,
// so it cannot be scrolled out of sight (Chris 10/23). Display only: no button,
// no handler. "incl. tax" only when the ticket itself is taxed.
function _vtFooterTotalHtml(ticket, lines, fmt) {
  const money = typeof fmt === 'function' ? fmt : (v) => '$' + Number(v || 0).toFixed(2);
  const total = _vtGrandTotal(ticket, lines);
  const tax = ticket && ticket.taxEnabled ? '<span class="vt-total-tax">incl. tax</span>' : '';
  return `<div class="vt-total"><span class="vt-total-label">Grand Total${tax}</span>` +
    `<span class="vt-total-amount">${escapeHtml(money(total))}</span></div>`;
}

// fix/1017-small-followups — the Amount cell of a line the server is still
// pricing (TASK-9646 S2 `amountPending`). It used to read $0.00 here while the
// driver's view read a bare "…"; both now say the same plain thing. No money.
const _VT_PENDING_AMOUNT_HTML = '<span title="Price being calculated" aria-label="Price being calculated" style="color:var(--text-secondary);font-weight:500;white-space:nowrap;">… <span style="font-size:0.75rem;font-style:italic;">Price being calculated</span></span>';
function _vtAmountCell(l) {
  return l.amountPending === true ? _VT_PENDING_AMOUNT_HTML : '$' + (l.lineAmount || 0).toFixed(2);
}

// A `⋯` row. The menu closes FIRST, so an action that opens a confirm dialog
// (Archive, Write Off) does not paint it underneath a still-open dropdown.
function _vtMenuItem({ onclick, icon, label, cls = '', title = '' }) {
  return `<button${cls ? ` class="${cls}"` : ''}${title ? ` title="${escapeHtml(title)}"` : ''} ` +
    `onclick="document.getElementById('view-ticket-overflow-menu')?.classList.add('hidden');${onclick}">` +
    `<span class="material-icons">${icon}</span> ${label}</button>`;
}

// TASK-9646 S4a — the "Dispatch Notes" block on a ticket shows the JOB's own
// note (BillingState.jobs, the office jobs listener). The ticket no longer
// carries a `jobNotes` copy: crew sessions read every key on a ticket doc, and
// TASK-9694 S6b moves the job note behind jobsPrivate (its merged listener
// keeps `job.notes` readable here). No fallback to a legacy ticket copy (COD r1
// Adversary: a crew-written copy would render as "Dispatch Notes"); a copy that
// was the only record is archived server-side by the backfill. '' = nothing.
// TASK-9694 S6 — the ticket's job, when it has crew Updates (posts): the block
// then shows the shared job log (office notes, then a collapsed "Updates (N) ▾",
// filled by jobUpdates.js). No Updates = exactly today's block.
function _ticketJobLog(ticket) {
  const job = ticket && ticket.jobId ? (BillingState.jobs || []).find(j => j && j.id === ticket.jobId) : null;
  return job && (job.crewUpdates || []).some(e => e && e.kind !== 'change') ? job : null;
}
function _ticketDispatchNotes(ticket) {
  if (!ticket) return '';
  const job = ticket.jobId ? (BillingState.jobs || []).find(j => j && j.id === ticket.jobId) : null;
  const own = job && typeof job.notes === 'string' ? job.notes : '';
  return own.trim() ? own : '';
}

async function viewTicket(id) {
  const ticket = BillingState.tickets.find(t => t.id === id);
  if (!ticket) return;

  BillingState.selectedTicket = ticket;

  const titleEl = document.getElementById('view-ticket-title');
  if (titleEl) {
    titleEl.textContent = ticket.ticketNo ? `Ticket #${ticket.ticketNo}` : 'Ticket Details';
  }

  const content = document.getElementById('view-ticket-content');
  if (!content) return;
  content.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading ticket details...</p></div>';
  // TASK-10044 — the footer now carries a Grand Total. A re-open would show the
  // PREVIOUS ticket's money (and its buttons) under this one's spinner, so it is
  // emptied until this ticket's footer renders below.
  const _vtActionsEl = document.getElementById('view-ticket-actions');
  if (_vtActionsEl) _vtActionsEl.innerHTML = '';

  document.getElementById('modal-view-ticket')?.classList.remove('hidden');

  // Ensure roster data is loaded so getEquipmentDisplayName() can resolve names
  await loadReviewEquipment();

  // Auto-clean duplicate lines (from concurrent clock-out race conditions)
  if (window.ClockModule?.deduplicateTicketLines) {
    await window.ClockModule.deduplicateTicketLines(id);
  }

  // Load ticketLines from subcollection (no compound orderBy — sort client-side to avoid needing composite index)
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  let ticketLines = [];
  try {
    const linesSnap = await getDocs(
      collection(db, 'tenants', tenant, 'tickets', id, 'ticketLines')
    );
    ticketLines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ticketLines.sort((a, b) => {
      if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
      if (a.sortOrder !== undefined || b.sortOrder !== undefined) {
        return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
      }
      const aTime = a.fromTime?.toDate?.() || new Date(0);
      const bTime = b.fromTime?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
  } catch (err) {
    console.warn('[Billing] Error loading ticketLines (may not exist yet):', err);
  }

  // TASK-10044 (COD r2 Code Reviewer) — print and invoice trust this cache when
  // the selected id matches, so a slow ticket A must not overwrite ticket B's
  // lines after B was opened. Same "different ticket owns the modal" test as
  // the render guard below; nothing above this line wrote shared state.
  if (BillingState.selectedTicket && BillingState.selectedTicket.id !== id) return;
  BillingState.selectedTicketLines = ticketLines;

  // Resolve vehicleType + rates for equipment lines with $0 rate (self-heal)
  if (ticket.customerId) {
    await loadReviewContractRates(ticket.customerId);
    for (const line of ticketLines.filter(l => l.type === 'EQUIPMENT')) {
      let resolved = false;
      if (!line.vehicleType && line.assetId) {
        const found = (ReviewState.trucks || []).find(t => t.id === line.assetId)
          || (ReviewState.trailers || []).find(t => t.id === line.assetId)
          || (ReviewState.assets || []).find(a => a.id === line.assetId);
        if (found) { line.vehicleType = found.type || ''; resolved = true; }
      }
      if (line.rate === 0 && resolved && line.rentalDirection !== 'return' && ticket.returnEstimate !== true) {   // TASK-10107 COD: a $0 return line stays $0 (never priced by viewing it)
        const rateInfo = lookupReviewRate(ticket.customerId, 'EQUIPMENT', line.assetType || 'truck', line.assetName || '', line.vehicleType || '');
        if (rateInfo && rateInfo.rate > 0) {
          line.rate = rateInfo.rate;
          line.rateUnit = rateInfo.rateUnit || line.rateUnit || 'Hourly';
          line.lineAmount = parseFloat(((line.hours || line.quantity || 0) * rateInfo.rate).toFixed(2));
          // Persist fix to Firestore — but NOT on a locked ticket (TASK-1176: books
          // closed, the ticketLines rule denies it, and the un-awaited write would
          // unhandled-reject on every view). Display still heals in-memory.
          if (!isReviewReadOnly(ticket)) {
            try {
              updateDoc(doc(db, 'tenants', tenant, 'tickets', id, 'ticketLines', line.id), {
                vehicleType: line.vehicleType, rate: line.rate, rateUnit: line.rateUnit, lineAmount: line.lineAmount
              });
            } catch (_) {}
          }
        }
      }
      // Self-heal: apply half-hour rounding for old lines missing the flag.
      // TASK-9327 — SIXTH door, and this one lives in viewTicket, so merely
      // LOOKING at a ticket was enough to overwrite hand-typed hours and
      // updateDoc the result. Same human-ruled-on guard as the five in the
      // Review modal; `zeroHoursOk` was missing here too (predates this task).
      if (line.zeroHoursOk === true || line.hoursManual === true) { /* a human ruled on these hours */ }
      else if (line.roundToHalfHour !== true && line.fromTime && line.toTime) {
        const rateInfo = lookupReviewRate(ticket.customerId, 'EQUIPMENT', line.assetType || 'truck', line.assetName || '', line.vehicleType || '');
        // TASK-9489 (COD round 1) — a decision that came ONLY from the company
        // switch never re-rounds an EXISTING line on open: the switch applies to
        // lines written after it changed (and to times a biller edits). Turning
        // it on must not silently move hours a customer may already have seen.
        if (rateInfo?.roundToHalfHour && !rateInfo.roundFromCompanyDefault) {
          const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
          const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
          const rawHrs = Math.max(0, (to - from) / 3600000);
          const rounded = roundHalfHour(rawHrs);
          if (line.hours !== rounded) {
            // TASK-1410 (COD UX): stamp what it was BEFORE rounding. Hours
            // changing with no explanation reads as a glitch to a biller and is
            // exactly what makes someone pause billing over a suspected bug —
            // this is the breadcrumb a "8.5 (rounded up from 8.01)" hint reads.
            line.roundedFrom = parseFloat(rawHrs.toFixed(2));
            line.hours = rounded;
            line.lineAmount = parseFloat((rounded * (line.rate || 0)).toFixed(2));
            // Persist fix to Firestore — skip on a locked ticket (TASK-1176, as above).
            if (!isReviewReadOnly(ticket)) {
              try {
                updateDoc(doc(db, 'tenants', tenant, 'tickets', id, 'ticketLines', line.id), {
                  hours: rounded, lineAmount: line.lineAmount, roundToHalfHour: true
                });
              } catch (_) {}
            }
          }
        }
      }
    }
  }

  // Separate lines by type
  const empLines = ticketLines.filter(l => l.type === 'EMPLOYEE');
  const equipLines = ticketLines.filter(l => l.type === 'EQUIPMENT');

  // Self-heal: sync employeeCount + totalHours + grandTotal on ticket doc if stale
  const realEmpCount = empLines.filter(l => l.personId).length;
  const realTotalHours = parseFloat(empLines.reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0).toFixed(2));
  const healFields = {};
  if ((ticket.employeeCount || 0) !== realEmpCount) { ticket.employeeCount = realEmpCount; healFields.employeeCount = realEmpCount; }
  if ((ticket.totalHours || 0) !== realTotalHours) { ticket.totalHours = realTotalHours; healFields.totalHours = realTotalHours; }
  // grandTotal heal — TASK-1666: the line sum is authoritative over a stale header.
  // Historically this healed only non-taxed tickets, because realGrandTotal is the
  // bare line-sum (pre-tax) and healing a taxed header to it would STRIP the sales
  // tax (CRIT-2, 2026-05-31). computeTicketTotalsFromLines is tax-aware — the same
  // helper invoice generation uses for `gross` — so both modes now heal to the
  // number the invoice will actually carry, and a taxed ticket also gets its
  // subtotal/taxAmount realigned (a clock-side edit used to leave subtotal stale,
  // and the invoice reads `ticket.subtotal ||` FIRST when taxEnabled).
  const _authTot = computeTicketTotalsFromLines(ticketLines, ticket);
  if (Math.abs((ticket.grandTotal || 0) - _authTot.grandTotal) > 0.01) {
    ticket.grandTotal = _authTot.grandTotal; healFields.grandTotal = _authTot.grandTotal;
  }
  if (ticket.taxEnabled) {
    if (Math.abs((ticket.subtotal || 0) - _authTot.subtotal) > 0.01) {
      ticket.subtotal = parseFloat(_authTot.subtotal.toFixed(2)); healFields.subtotal = ticket.subtotal;
    }
    if (Math.abs((ticket.taxAmount || 0) - _authTot.taxAmount) > 0.01) {
      ticket.taxAmount = _authTot.taxAmount; healFields.taxAmount = _authTot.taxAmount;
    }
  }
  // TASK-1176 — never self-heal a LOCKED ticket's denorm (invoiced/archived/locked):
  // its books are closed, the rules now deny the write, and this un-awaited updateDoc
  // would otherwise unhandled-reject on every view of a locked ticket (errorLogs noise).
  // Blocking the grandTotal re-write here also closes a latent books-corruption path.
  if (Object.keys(healFields).length > 0 && !isReviewReadOnly(ticket)) {
    try { updateDoc(doc(db, 'tenants', tenant, 'tickets', id), healFields); } catch (_) {}
  }
  const downLines = ticketLines.filter(l => l.type === 'DOWNTIME');
  const standbyLines = ticketLines.filter(l => l.type === 'STANDBY');
  const permitLines = ticketLines.filter(l => l.type === 'PERMIT');
  const miscLines = ticketLines.filter(l => l.type === 'MISC');
  const fieldItemLines = ticketLines.filter(l => l.type === 'FIELD_ITEM');
  const potholeLines = ticketLines.filter(l => l.type === 'POTHOLE');

  const hasLines = ticketLines.length > 0;

  let html = `
    <div class="ticket-detail">
      ${/* TASK-4738 — the delivery ribbon, ABOVE the header grid ("towards the
           top", Chris's words). This modal is the one screen carrying the Email
           button and until now it said nothing about whether the ticket had
           already gone out, to whom, or when. The id is the handle
           _refreshTicketRibbon() repaints after a send, so the strip moves
           without closing the modal. */ ''}
      ${/* TASK-4683 — the ribbon says WHAT the customer answered; the decision
           block underneath says WHO and, on a rejection, WHY. They share the id
           so _refreshTicketRibbon() repaints both together and they can never
           tell two different stories. */ ''}
      <div id="view-ticket-ribbon">${_ticketDeliveryRibbonHtml(ticket)}${_ticketApprovalDecisionHtml(ticket)}</div>
      <!-- Header Info -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);margin-bottom:var(--spacing-lg);">
        <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
          <div class="detail-label">Date</div>
          <div class="detail-value" style="text-align:left;">${formatDate(ticket.date)}</div>
        </div>
        <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
          <div class="detail-label">Status</div>
          <div class="detail-value" style="text-align:left;">
            <span class="badge badge-${ticket.status}">${capitalizeFirst(ticket.status)}</span>
            ${BillingState.activeTicketIds?.has(ticket.id) ? ' <span class="badge-active-shift" style="font-size:11px;padding:2px 8px;">Active Clock-In</span>' : ''}
            ${/* TASK-1346 sweep — "is this billed?" (display) */ isTicketInvoiced(ticket) && ticket.invoiceNumber ? ` <span class="badge badge-invoice-num">${escapeHtml(ticket.invoiceNumber)}</span>` : ''}
            ${isTicketInvoiced(ticket) ? ' <span class="badge badge-invoiced"><span class="material-icons">check_circle</span>Invoiced</span>' : ''}
            ${ticket.paid ? ' <span class="badge badge-paid-billing"><span class="material-icons">payments</span>Paid</span>' : ''}
            ${ticket.writtenOff ? ' <span class="badge badge-written-off"><span class="material-icons">money_off</span>Written Off</span>' : ''}
            ${ticket.eTicketSubmissionId ? ' <span class="eticket-source-badge"><span class="material-icons">description</span>E-Ticket</span>' : ''}
            ${/* TASK-4854 — the delivery state BESIDE the status chips, not only
                 in the ribbon at the top of the modal. Chris, after accepting a
                 ticket himself: "I see the status like this: Status / Archived
                 EXT-2026-09-07 / ✓ Invoiced — I dont see any indicator that it
                 has been accepted? I see it on this [list row]." The Status
                 block is where a biller looks to answer "where is this ticket
                 at", and it answered every question except the one the customer
                 had just settled.

                 🔴 THE SAME COMPONENT HE ALREADY READS ON THE LIST ROW, on
                 purpose — `_ticketDeliveryBadgeHtml`, which derives from
                 `_ticketDeliveryState()` like the ribbon, the tracking panel,
                 the search keywords and the filter chips. A bespoke "Accepted"
                 chip written here would be a SECOND reader of the same fields,
                 which is exactly how the list row and the detail modal came to
                 disagree in the first place. It is also the thing he pointed at
                 and said "I see it on this", so the two screens now show him the
                 same pill for the same fact.

                 Deliberately NOT a replacement for the ribbon above: the ribbon
                 is the history (emailed → accepted → edited since) and the
                 decision block under it carries who signed and why. This is the
                 dense one-glance pill in the block he was reading. */ ''}
            ${_ticketDeliveryBadgeHtml(ticket)}
          </div>
        </div>
        <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
          <div class="detail-label">Customer</div>
          <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.customerName || '-')}</div>
        </div>
        ${ticket.jobName || ticket.jobTitle ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Job</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.jobName || ticket.jobTitle)}</div>
          </div>
        ` : ''}
        ${ticket.location ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Location</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.location)}</div>
          </div>
        ` : ''}
        ${ticket.poNumber ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">PO Number</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.poNumber)}</div>
          </div>
        ` : ''}
        ${ticket.vendorNumber ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Vendor Number</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.vendorNumber)}</div>
          </div>
        ` : ''}
        ${ticket.wellName ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Well</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.wellName)}${ticket.wellNumber ? ` #${escapeHtml(ticket.wellNumber)}` : ''}</div>
          </div>
        ` : ''}
        ${ticket.afeNumber ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">AFE / Cost Center</div>
            <div class="detail-value" style="text-align:left;">${escapeHtml(ticket.afeNumber)}</div>
          </div>
        ` : ''}
        ${ticket.workStartTime ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Work Start</div>
            <div class="detail-value" style="text-align:left;">${window.Fmt.shiftCells(ticket.workStartTime, ticket.workStopTime).fromHtml}</div>
          </div>
        ` : ''}
        ${ticket.workStopTime ? `
          <div class="detail-row" style="border:none;padding:0;flex-direction:column;gap:2px;">
            <div class="detail-label">Work Stop</div>
            <div class="detail-value" style="text-align:left;">${window.Fmt.shiftCells(ticket.workStartTime, ticket.workStopTime).toHtml}</div>
          </div>
        ` : ''}
        ${_ticketDispatchNotes(ticket) || _ticketJobLog(ticket) ? `
          <div style="border:none;padding:0;margin-top:4px;">
            <div style="background:#F8FAFC;border-radius:var(--radius-sm);border-left:3px solid var(--brand-blue);overflow:hidden;">
              <div onclick="this.nextElementSibling.classList.toggle('hidden');this.querySelector('.material-icons:last-child').textContent=this.nextElementSibling.classList.contains('hidden')?'expand_more':'expand_less'"
                style="padding:6px 10px;font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;display:flex;align-items:center;gap:4px;cursor:pointer;user-select:none;">
                <span class="material-icons" style="font-size:14px;">speaker_notes</span> Dispatch Notes
                <span class="material-icons" style="font-size:16px;margin-left:auto;">expand_more</span>
              </div>
              <div class="hidden" style="padding:0 10px 8px;font-size:0.85rem;color:var(--text-primary);white-space:pre-wrap;"${_ticketJobLog(ticket) ? ` data-joblog="${escapeHtml(ticket.jobId)}"` : ''}>${escapeHtml(_ticketDispatchNotes(ticket))}</div>
            </div>
          </div>
        ` : ''}
      </div>
  `;

  // POC from linked job
  if (ticket.jobId) {
    const linkedJob = BillingState.jobs.find(j => j.id === ticket.jobId);
    const poc = linkedJob?.poc;
    if (poc && poc.name) {
      html += `
        <div style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;margin-bottom:var(--spacing-md);background:var(--bg-page);border-radius:var(--radius-sm);border:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);margin-top:1px;">contact_phone</span>
          <div style="flex:1;font-size:0.8125rem;line-height:1.5;">
            <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(poc.name)}${poc.role ? ` <span style="font-weight:400;color:var(--text-secondary);">(${escapeHtml(poc.role)})</span>` : ''}</div>
            <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:2px;">
              ${poc.phone ? `<a href="tel:${escapeHtml(poc.phone)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.phone)}</a>` : ''}
              ${poc.phone2 ? `<a href="tel:${escapeHtml(poc.phone2)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.phone2)}</a>` : ''}
              ${poc.email ? `<a href="mailto:${escapeHtml(poc.email)}" style="color:var(--brand-blue);text-decoration:none;">${escapeHtml(poc.email)}</a>` : ''}
            </div>
          </div>
        </div>
      `;
    }
  }

  const canEdit = false; // View modal is read-only; edits happen in Review

  // TASK-511 — surface the customer's hide-by-default line types so the dispatcher
  // knows which lines will drop from the generated invoice + total. Informational
  // only (read-only view); the lines stay on the ticket for payroll/job-costing.
  {
    const _exTypes = getInvoiceExcludeLineTypes(ticket.customerId);
    if (_exTypes.length > 0) {
      const _labelOf = (t) => (EXCLUDABLE_LINE_TYPES.find(x => x.type === t)?.label || t);
      // Only show types that actually appear on this ticket
      const _present = _exTypes.filter(t => ticketLines.some(l => l.type === t && l.includeOnInvoice !== true));
      if (_present.length > 0) {
        html += `
          <div style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;margin-bottom:var(--spacing-md);background:#FEF9C3;border-radius:var(--radius-sm);border:1px solid #FDE68A;">
            <span class="material-icons" style="font-size:18px;color:#B45309;margin-top:1px;">visibility_off</span>
            <div style="flex:1;font-size:0.8125rem;line-height:1.5;color:#92400E;">
              <strong>Hidden on this customer's invoice:</strong> ${escapeHtml(_present.map(_labelOf).join(', '))}.
              These lines stay on the ticket for payroll &amp; job costing but are dropped from the invoice and the dollar total.
            </div>
          </div>`;
      }
    }
  }

  // Build ordered sections for viewTicket
  const _showTimes = showClockTimes();
  const _empColspan = _showTimes ? 6 : 4;
  const _vtSections = {};

  // Labor section
  if (hasLines && empLines.length > 0) {
    const empSubtotal = empLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    // TASK-4746 — a $0 man-hour line is only a WARNING when this customer's price
    // book says labour is supposed to be priced. Everywhere else it is the
    // intended shop-to-shop state, and the amber banner was a 100% false
    // positive. `loadReviewContractRates(ticket.customerId)` is awaited above, so
    // the cache is warm here and this costs no extra read.
    // TASK-511's per-customer "hide this line type on invoices" default is a
    // second, explicit statement of "this customer is not billed for labour" —
    // honour it, or the banner nags about the price of a section the customer's
    // invoice never shows.
    const _laborHiddenOnInvoice = getInvoiceExcludeLineTypes(ticket.customerId).includes('EMPLOYEE');
    const _pricedLaborItem = _laborHiddenOnInvoice ? null : pricedLaborPriceBookItem(
      ReviewState.contractRateCache[ticket.customerId]?.lineItems);
    // Line-level overrides mirror `equipmentLineUnpriced` exactly (:3521 server
    // twin): `includeOnInvoice: false` / `zeroHoursOk: true` are the biller's
    // RECORDED decision that this line is not billable, so an unpriced one is
    // not news.
    const _hasZeroRateEmp = !!_pricedLaborItem && empLines.some(l =>
      !l.rate && l.hours > 0 && l.includeOnInvoice !== false && l.zeroHoursOk !== true);
    _vtSections.labor = `
      <div class="detail-section">
        <div class="detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
          <span>
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">people</span>
            Employees
          </span>
          ${!namesOnTickets() ? `<label style="display:flex;align-items:center;gap:6px;font-weight:500;font-size:0.8rem;color:var(--text-primary);cursor:pointer;margin:0;">
            <input type="checkbox" id="view-ticket-show-names" ${_showEmployeeNames ? 'checked' : ''}
              onchange="window.BillingModule.toggleShowNames(this.checked)" style="accent-color:var(--brand-blue);">
            Show names
          </label>` : ''}
        </div>
        ${_hasZeroRateEmp ? `<div style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;margin-bottom:8px;background:#FEF3C7;border:1px solid #F59E0B;border-radius:8px;">
          <span class="material-icons" style="font-size:18px;color:#D97706;margin-top:1px;">info</span>
          <div style="font-size:0.8rem;color:#92400E;line-height:1.4;">
            ${window.AppState?.settings?.billLaborHours === true
              ? `<strong>Labor rate is $0</strong> — but this customer's price book prices labor (${escapeHtml(_pricedLaborItem.description || 'personnel item')} at $${(Number(_pricedLaborItem.rate) || 0).toFixed(0)}), so these hours should not be coming through unpriced. Labor bills only from an <strong>Hourly</strong> crew line that matches this person's role; check it in <a href="#" onclick="event.preventDefault();window.navigateToView?.('crm')" style="color:#92400E;text-decoration:underline;font-weight:600;">CRM → Contracts</a>.`
              : `<strong>Labor bills $0 here</strong> — this customer's price book prices labor (${escapeHtml(_pricedLaborItem.description || 'personnel item')} at $${(Number(_pricedLaborItem.rate) || 0).toFixed(0)}), but labor billing is off for your company. To bill crew hours from the price book, turn on <strong>Bill labor hours</strong> in Billing Settings.`}
          </div>
        </div>` : ''}
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead>
              <tr>
                <th>${namesOnTickets() ? 'Name' : 'Role'}</th>
                ${namesOnTickets() ? '<th>Role</th>' : ''}
                ${_showTimes ? '<th>From</th><th>To</th>' : ''}
                <th style="text-align:right;">Hours</th>
                <th style="text-align:right;">Rate</th>
                <th style="text-align:right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                return empLines.map(l => {
                  // "Active"/"Clocked In" is AUTHORITATIVE on the clock entry (clockOut == null),
                  // NOT inferred from a missing toTime — a Daily ($/day) / piece-rate line
                  // legitimately has no toTime and a build-ticket write can drop times on a
                  // closed line. Only an actually-open shift hides hours/amount behind "–".
                  const _isActive = isLineClockActive(l, ticket.id);
                  const _hasOpenShift = _isActive;
                  const _cardPu = !isTimeBasedUnit(l.rateUnit);
                  return `
                  <tr>
                    <td>${escapeHtml(getEmployeeDisplayName(l))}${_isActive ? ' <span class="badge-active-shift">Clocked In</span>' : ''}${l.revenueCategory ? ' <span style="font-size:0.7rem;padding:1px 6px;border-radius:4px;background:#EFF6FF;color:#1D4ED8;font-weight:500;">' + escapeHtml(l.revenueCategory) + '</span>' : ''}</td>
                    ${namesOnTickets() ? `<td>${escapeHtml(l.role || '-')}</td>` : ''}
                    ${_showTimes ? (_cardPu ? '<td style="color:var(--text-secondary);">—</td><td style="color:var(--text-secondary);">—</td>' : `<td>${lineFromHtml(l)}</td><td>${_hasOpenShift ? '<span style="color:var(--text-secondary);font-style:italic;">-</span>' : lineToHtml(l)}</td>`) : ''}
                    <td style="text-align:right;">${_hasOpenShift ? '<span style="color:var(--text-secondary);">-</span>' : (_cardPu ? ((l.quantity || 0) + ' ' + escapeHtml(l.rateUnit || '')) : (l.hours || 0).toFixed(2))}</td>
                    <td style="text-align:right;">$${(l.rate || 0).toFixed(0)}${_cardPu ? '<span style="font-size:0.75em;color:var(--text-secondary);">' + escapeHtml(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>
                    <td style="text-align:right;font-weight:600;">${_hasOpenShift ? '<span style="color:var(--text-secondary);">-</span>' : _vtAmountCell(l)}</td>
                  </tr>
                `; }).join('');
              })()}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="${_empColspan}" style="text-align:right;font-weight:600;">Employee Subtotal</td>
                <td style="text-align:right;font-weight:700;">$${empSubtotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    `;
  } else if (!hasLines && ticket.employees?.length > 0) {
    _vtSections.labor = `
      <div class="detail-section">
        <div class="detail-section-title">Employees</div>
        <ul class="detail-list">
          ${ticket.employees.map(e => `<li>${escapeHtml(getEmployeeDisplayName(e))}${namesOnTickets() ? ' (' + escapeHtml(e.role || 'N/A') + ')' : ''}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  // Equipment section
  if (equipLines.length > 0) {
    const equipSubtotal = equipLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    const _hasZeroRateEquip = equipLines.some(l => !l.rate && l.hours > 0);
    const _cardEqShowTimes = showClockTimes();
    _vtSections.equipment = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">local_shipping</span>
          Equipment
        </div>
        ${_hasZeroRateEquip ? `<div style="display:flex;align-items:flex-start;gap:8px;padding:8px 12px;margin-bottom:8px;background:#FEF3C7;border:1px solid #F59E0B;border-radius:8px;">
          <span class="material-icons" style="font-size:18px;color:#D97706;margin-top:1px;">info</span>
          <div style="font-size:0.8rem;color:#92400E;line-height:1.4;">
            <strong>Equipment rate is $0</strong> — contract may not match this vehicle type. Check <a href="#" onclick="event.preventDefault();window.navigateToView?.('crm')" style="color:#92400E;text-decoration:underline;font-weight:600;">CRM → Contracts</a> line item "Applies To" types.
          </div>
        </div>` : ''}
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Fleet #</th>
                ${_cardEqShowTimes ? '<th>From</th><th>To</th>' : ''}
                <th style="text-align:right;">QTY</th>
                <th style="text-align:right;">Rate</th>
                <th style="text-align:right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${equipLines.map(l => {
                const _pu = !isTimeBasedUnit(l.rateUnit);
                return `
                <tr>
                  <td>${escapeHtml(getEquipmentDisplayName(l))}${l.revenueCategory ? ' <span style="font-size:0.7rem;padding:1px 6px;border-radius:4px;background:#EFF6FF;color:#1D4ED8;font-weight:500;">' + escapeHtml(l.revenueCategory) + '</span>' : ''}</td>
                  <td>${escapeHtml(l.fleetNumber || '-')}</td>
                  ${_cardEqShowTimes ? (_pu ? '<td style="color:var(--text-secondary);">—</td><td style="color:var(--text-secondary);">—</td>' : `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>`) : ''}
                  <td style="text-align:right;">${_pu ? ((l.quantity || 0) + ' ' + escapeHtml(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>
                  <td style="text-align:right;">$${(l.rate || 0).toFixed(0)}${_pu ? '<span style="font-size:0.75em;color:var(--text-secondary);">' + escapeHtml(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>
                  <td style="text-align:right;font-weight:600;">${_vtAmountCell(l)}</td>
                </tr>
              `; }).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="${_cardEqShowTimes ? 6 : 4}" style="text-align:right;font-weight:600;">Equipment Subtotal</td>
                <td style="text-align:right;font-weight:700;">$${equipSubtotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    `;
  }

  // Downtime section
  if (downLines.length > 0) {
    _vtSections.downtime = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">pause_circle</span>
          Downtime Deduction
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead>
              <tr>
                <th>${namesOnTickets() ? 'Person' : 'Role'}</th>
                ${_showTimes ? '<th>From</th><th>To</th>' : ''}
                <th style="text-align:right;">Hours</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${downLines.map(l => `
                <tr>
                  <td>${escapeHtml(getEmployeeDisplayName(l))}</td>
                  ${_showTimes ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
                  <td style="text-align:right;">${(l.hours || 0).toFixed(2)}</td>
                  <td>${escapeHtml(l.notes || '-')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  // Standby section
  if (standbyLines.length > 0) {
    _vtSections.standby = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">hourglass_empty</span>
          Standby
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead>
              <tr>
                <th>${namesOnTickets() ? 'Person' : 'Role'}</th>
                ${_showTimes ? '<th>From</th><th>To</th>' : ''}
                <th style="text-align:right;">Hours</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${standbyLines.map(l => `
                <tr>
                  <td>${escapeHtml(getEmployeeDisplayName(l))}</td>
                  ${_showTimes ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
                  <td style="text-align:right;">${(l.hours || 0).toFixed(2)}</td>
                  <td>${escapeHtml(l.notes || '-')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  // Permit section
  if (permitLines.length > 0) {
    const permitSubtotal = permitLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    _vtSections.permit = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">description</span>
          Permits
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align:right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${permitLines.map(l => `
                <tr>
                  <td>${escapeHtml(l.description || '-')}</td>
                  <td style="text-align:right;font-weight:600;">${_vtAmountCell(l)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td style="text-align:right;font-weight:600;">Load Load Permit Subtotal</td>
                <td style="text-align:right;font-weight:700;">$${permitSubtotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    `;
  }

  // Field Items section (read-only)
  if (fieldItemLines.length > 0) {
    const fiSubtotal = fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    _vtSections.fieldItems = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">list_alt</span>
          Field Items
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead><tr><th>Description</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th></tr></thead>
            <tbody>
              ${fieldItemLines.map(l => `
                <tr>
                  <td>${escapeHtml(l.description || '-')}${_ppeRollupChip(l, id)}</td>
                  <td style="text-align:center;">${l.quantity || 0}</td>
                  <td style="text-align:right;">$${(l.rate || 0).toFixed(2)}/${escapeHtml(l.rateUnit || 'ea')}</td>
                  <td style="text-align:right;font-weight:600;">${_vtAmountCell(l)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot><tr><td colspan="3" style="text-align:right;font-weight:600;">Subtotal</td><td style="text-align:right;font-weight:700;">$${fiSubtotal.toFixed(2)}</td></tr></tfoot>
          </table>
        </div>
      </div>
    `;
  }

  // Miscellaneous section (read-only)
  if (miscLines.length > 0) {
    const miscSubtotal = miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    _vtSections.misc = `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">receipt</span>
          Miscellaneous
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead><tr><th>Description</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th></tr></thead>
            <tbody>
              ${miscLines.map(l => `
                <tr>
                  <td>${escapeHtml(l.description || '-')}</td>
                  <td style="text-align:center;">${l.quantity || 0}</td>
                  <td style="text-align:right;">$${(l.rate || 0).toFixed(2)}/${escapeHtml(l.rateUnit || 'ea')}</td>
                  <td style="text-align:right;font-weight:600;">${_vtAmountCell(l)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot><tr><td colspan="3" style="text-align:right;font-weight:600;">Subtotal</td><td style="text-align:right;font-weight:700;">$${miscSubtotal.toFixed(2)}</td></tr></tfoot>
          </table>
        </div>
      </div>
    `;
  }

  // Line Info (POTHOLE) section — collapsible
  if (potholeLines.length > 0) {
    let _plHtml = `
      <div class="detail-section">
        <div class="detail-section-title" style="cursor:pointer;display:flex;align-items:center;justify-content:space-between;"
          onclick="this.nextElementSibling.classList.toggle('hidden');this.querySelector('.expand-icon').textContent=this.nextElementSibling.classList.contains('hidden')?'expand_more':'expand_less'">
          <span style="display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">pin_drop</span>
            Line Info (${potholeLines.length})
          </span>
          <span class="material-icons expand-icon" style="font-size:18px;color:var(--text-secondary);">expand_less</span>
        </div>
        <div>`;
    for (const l of potholeLines) {
      const parts = (l.description || '').split(', ').filter(Boolean);
      const gpsStr = (l.gpsLat && l.gpsLng) ? `${l.gpsLat.toFixed(5)}, ${l.gpsLng.toFixed(5)}` : '';
      _plHtml += `
          <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:10px 12px;margin-bottom:6px;">
            <div style="display:flex;flex-wrap:wrap;gap:8px 16px;font-size:0.85rem;">
              ${parts.map(p => `<span style="color:var(--text-primary);font-weight:500;">${escapeHtml(p)}</span>`).join('')}
              ${gpsStr ? `<a href="https://maps.google.com/?q=${l.gpsLat},${l.gpsLng}" target="_blank" rel="noopener" style="color:var(--brand-blue);text-decoration:none;display:flex;align-items:center;gap:2px;font-size:0.8rem;">
                <span class="material-icons" style="font-size:14px;">location_on</span>${gpsStr}</a>` : ''}
            </div>
            ${l.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">${escapeHtml(l.notes)}</div>` : ''}
            ${(l.photos && l.photos.length > 0) ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;">${l.photos.map(p => {
              // TASK-9855 — Storage SDK by path, not the stored token URL.
              return window.StorageFile.fileRef(p) ? `<a ${window.StorageFile.linkAttrs(p)}><img ${window.StorageFile.imgAttrs(p)} style="width:48px;height:48px;object-fit:cover;border-radius:6px;border:1px solid var(--border-light);"></a>` : '';
            }).join('')}</div>` : ''}
          </div>`;
    }
    _plHtml += `</div></div>`;
    _vtSections.lineInfo = _plHtml;
  }

  // Custom ticket sections (TASK-747-C) — render captured data (single or repeatable)
  if (ticket.customSectionData) {
    const _csHtml = buildCustomSectionDataHtml(ticket.customSectionData, escapeHtml);
    // TASK-4845 — "Preview for the customer" for sections set up for the customer map.
    if (_csHtml) _vtSections.customSections = _csHtml + _cmTicketStripHtml(ticket);
  }

  html += buildOrderedSections(_vtSections);

  // Attach Job Permits section (renders whenever ticket has a parent job —
  // even if the job has zero permits yet, so the AR/AP reviewer can upload).
  // Upload writes back to jobs/{jobId}.permits[] so dispatch + driver views
  // see the permit too (the "bidirectional" path Chris asked for 2026-05-15).
  if (ticket.jobId) {
    const parentJob = BillingState.jobs.find(j => j.id === ticket.jobId);
    if (parentJob) {
      const jobPermits = parentJob.permits || [];
      const existingPermitIds = permitLines.map(l => l.jobPermitId).filter(Boolean);
      html += `
        <div class="detail-section">
          <div class="detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <span>
              <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">attach_file</span>
              Attach Load Permits
            </span>
            <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.uploadPermitFromBilling('${ticket.id}', '${ticket.jobId}')" title="Upload a new permit — pushes back to the job so dispatch + drivers see it">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">upload_file</span> Upload
            </button>
          </div>
          <div class="permit-attach-list">
      `;
      if (jobPermitEntries(parentJob).length === 0) {
        html += `<div style="font-size:0.78rem;color:var(--text-secondary);padding:8px 4px;">No permits on this job yet. Upload one above to attach it here and on the driver's job view.</div>`;
      }
      for (let i = 0; i < jobPermits.length; i++) {
        const permit = jobPermits[i];
        if (isJobDocEntry(permit, parentJob)) continue; // TASK-9694 — a driver Doc never becomes a PERMIT line (index i kept for permit_${i})
        const permitId = permit.id || `permit_${i}`;
        const permitName = permit.name || permit.fileName || `Permit ${i + 1}`;
        const isAttached = existingPermitIds.includes(permitId);
        html += `
          <div class="permit-attach-row">
            <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">description</span>
            <span style="flex:1;font-size:0.8125rem;">${escapeHtml(permitName)}</span>
            ${isAttached
              ? `<span class="badge badge-ready" style="font-size:0.7rem;">Attached</span>`
              : `<button class="btn btn-sm btn-secondary" onclick="window.BillingModule.attachPermitToTicket('${escJsAttr(ticket.id)}', '${escJsAttr(permitId)}', '${escJsAttr(permitName)}')">Attach</button>`
            }
          </div>
        `;
      }
      html += `</div></div>`;
    }
  }

  // Documents section (read-only display)
  if (ticket.documents?.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">attach_file</span>
          Documents (${ticket.documents.length})
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill, minmax(80px, 1fr));gap:10px;padding:8px 0;">
    `;
    for (const d of ticket.documents) {
      const isPdf = d.isPdf || d.mimeType === 'application/pdf';
      const printBadge = d.printWithInvoice ? '<div style="position:absolute;top:2px;right:2px;background:#3B82F6;color:#fff;font-size:0.55rem;padding:1px 4px;border-radius:3px;">Invoice</div>' : '';
      // TASK-9868 — type tag (bottom-left) + "Uploaded by …" on hover/long-press.
      // Old rows have neither, so they render exactly as before.
      const _typeLabel = ticketDocTypeLabel(d.docType);
      const typeTag = _typeLabel ? `<div style="position:absolute;bottom:2px;left:2px;background:#0F172A;color:#fff;font-size:0.55rem;padding:1px 4px;border-radius:3px;">${escapeHtml(_typeLabel)}</div>` : '';
      const _upLine = ticketDocUploadedLine(d, _ticketDocFmtTime);
      const upTitle = _upLine ? ` title="${escapeHtml(_upLine)}"` : '';
      if (isPdf) {
        html += `
          <a ${window.StorageFile.linkAttrs(d, { urlKey: 'fullUrl' })}${upTitle} style="display:block;position:relative;width:80px;height:80px;background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;text-align:center;padding-top:16px;text-decoration:none;">
            ${printBadge}${typeTag}
            <span class="material-icons" style="font-size:28px;color:#EF4444;">picture_as_pdf</span>
            <div style="font-size:0.6rem;color:#0F172A;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:0 4px;">${escapeHtml(d.name || 'PDF')}</div>
          </a>`;
      } else {
        html += `
          <a ${window.StorageFile.linkAttrs(d, { urlKey: 'fullUrl' })}${upTitle} style="display:block;position:relative;width:80px;height:80px;border-radius:8px;overflow:hidden;border:1px solid var(--border-light);">
            ${printBadge}${typeTag}
            <img ${_sfThumbAttrs(d)} style="width:100%;height:100%;object-fit:cover;" alt="${escapeHtml(d.name || 'Document')}">
          </a>`;
      }
    }
    html += `</div>`;
    // TASK-9868 — "Uploaded by …" under each stamped file's name. Old rows
    // (no uploadedBy) are not listed, so a ticket with only old rows looks as before.
    const _stamped = ticket.documents.filter(d => ticketDocUploadedLine(d, _ticketDocFmtTime));
    if (_stamped.length) {
      html += `<div style="display:flex;flex-direction:column;gap:2px;padding:0 0 8px;">${_stamped.map(d => `
        <div style="font-size:0.75rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          <span style="color:var(--text-primary);font-weight:500;">${escapeHtml(d.name || 'Document')}</span>${_ticketDocTypeChipHtml(d)} · ${escapeHtml(ticketDocUploadedLine(d, _ticketDocFmtTime))}
        </div>`).join('')}</div>`;
    }
    html += `</div>`;
  }

  // Legacy time entries
  if (!hasLines && ticket.timeEntries?.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">Time Entries</div>
        <ul class="detail-list">
          ${ticket.timeEntries.map(e => `
            <li>${e.timeFrom || '?'} - ${e.timeTo || '?'}: ${escapeHtml(e.description || 'No description')}</li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  // Legacy materials
  if (ticket.materials?.length > 0) {
    html += `
      <div class="detail-section">
        <div class="detail-section-title">Materials</div>
        <ul class="detail-list">
          ${ticket.materials.map(m => `
            <li>${escapeHtml(m.name)} - ${m.quantity || 0} ${escapeHtml(m.unit || '')}</li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  // Work performed (per-asset notes + general)
  {
    let _vpHtml = '';
    if (isPerAssetNotesEnabled()) {
      const _vpEqNotes = equipLines.filter(l => l.notes?.trim() && assetTypeGetsNotes(l.assetType));
      if (_vpEqNotes.length > 0) {
        _vpHtml += _vpEqNotes.map(l => `<div style="margin-bottom:8px;padding-left:10px;border-left:3px solid var(--brand-blue);">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(getEquipmentDisplayName(l) || l.assetName || 'Equipment')}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);white-space:pre-wrap;">${escapeHtml(l.notes)}</div>
        </div>`).join('');
      }
    }
    if (ticket.workPerformed) {
      if (_vpHtml) _vpHtml += '<div style="margin-top:8px;font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;">General Notes</div>';
      _vpHtml += `<div class="detail-section-content">${escapeHtml(ticket.workPerformed).replace(/\n/g, '<br>')}</div>`;
    }
    if (_vpHtml) {
      html += `
        <div class="detail-section">
          <div class="detail-section-title">Work Performed</div>
          ${_vpHtml}
        </div>
      `;
    }
  }

  // Photo Gallery — TASK-1220: image thumbnails open the in-app lightbox
  // (PDFs keep target=_blank). TASK-1214: biller recourse — a delete ✕ on each
  // tile when the ticket isn't locked (hidden ENTIRELY when it is, never a
  // disabled button; server rules + the shared helper's fresh-read re-check
  // are the real boundary).
  if (ticket.photos && ticket.photos.length > 0) {
    const canDeletePhotos = !isReviewReadOnly(ticket); // driver lock deliberately does NOT block the biller (TASK-566)
    html += `
      <div class="detail-section" id="ticket-photo-gallery">
        <div class="detail-section-title">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">photo_library</span>
          Photos (${ticket.photos.length})
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;">
          ${ticket.photos.map((photo, idx) => {
            const url = photo.url || photo.downloadUrl || photo;
            const name = photo.name || photo.fileName || '';
            // COD (Code Reviewer) — legacy entries can be raw URL strings with no
            // name; also sniff the storage path (before the ?token query) so a
            // PDF never renders as an <img>/lightbox tile.
            let urlIsPdf = false;
            try { urlIsPdf = decodeURIComponent(String(url).split('?')[0]).toLowerCase().endsWith('.pdf'); } catch (_) { /* keep false */ }
            const isPdf = name.toLowerCase().endsWith('.pdf') || urlIsPdf;
            const delBtn = canDeletePhotos ? `
              <button type="button" aria-label="Delete photo" onclick="event.stopPropagation();window.BillingModule.deleteTicketPhoto(${idx})" style="position:absolute;top:0;right:0;width:44px;height:44px;background:transparent;border:none;cursor:pointer;padding:0;display:flex;align-items:flex-start;justify-content:flex-end;">
                <span style="margin:2px 2px 0 0;background:rgba(0,0,0,0.6);border-radius:50%;width:22px;height:22px;display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:14px;color:#fff;">close</span></span>
              </button>` : '';
            if (isPdf) {
              return `
                <div style="position:relative;width:80px;height:80px;">
                  <a ${window.StorageFile.linkAttrs(photo)} style="width:100%;height:100%;border:1px solid var(--border-color);border-radius:var(--radius-sm);display:flex;flex-direction:column;align-items:center;justify-content:center;text-decoration:none;background:var(--bg-secondary);">
                    <span class="material-icons" style="font-size:28px;color:var(--text-secondary);">picture_as_pdf</span>
                    <span style="font-size:10px;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;max-width:70px;white-space:nowrap;">${escapeHtml(name)}</span>
                  </a>
                  ${delBtn}
                </div>
              `;
            }
            return `
              <div style="position:relative;width:80px;height:80px;">
                <div onclick="window.BillingModule.showTicketPhoto(${idx})" style="width:100%;height:100%;border:1px solid var(--border-color);border-radius:var(--radius-sm);overflow:hidden;cursor:pointer;">
                  <img ${window.StorageFile.imgAttrs(photo)} alt="${escapeHtml(name)}" style="width:100%;height:100%;object-fit:cover;">
                </div>
                ${delBtn}
              </div>
            `;
          }).join('')}
        </div>
        ${(() => {
          // TASK-9868 — the driver's stamp + type under each stamped photo's
          // name. Old photos carry neither and are not listed.
          const stamped = ticket.photos.filter(p => p && typeof p === 'object' && ticketDocUploadedLine(p, _ticketDocFmtTime));
          if (!stamped.length) return '';
          return `<div style="display:flex;flex-direction:column;gap:2px;padding:6px 0 0;">${stamped.map(p => `
            <div style="font-size:0.75rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
              <span style="color:var(--text-primary);font-weight:500;">${escapeHtml(p.name || p.fileName || 'Photo')}</span>${_ticketDocTypeChipHtml(p)} · ${escapeHtml(ticketDocUploadedLine(p, _ticketDocFmtTime))}
            </div>`).join('')}</div>`;
        })()}
      </div>
    `;
  }

  // Grand Total (with tax if applicable)
  if (hasLines) {
    const grandTotal = _vtGrandTotal(ticket, ticketLines); // TASK-10044 — same number the footer shows
    if (ticket.taxEnabled) {
      const subtotal = ticket.subtotal || (grandTotal - (ticket.taxAmount || 0));
      html += `
        <div style="margin-top:var(--spacing-lg);padding:var(--spacing-md);background:var(--bg-elevated);border-radius:var(--radius-md);">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-weight:600;font-size:0.95rem;color:var(--text-secondary);">Subtotal</span>
            <span style="font-weight:600;font-size:1.1rem;color:var(--text-primary);">$${subtotal.toFixed(2)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <span style="font-weight:600;font-size:0.95rem;color:var(--text-secondary);">Tax (${(ticket.taxRate || 0).toFixed(2)}%)</span>
            <span style="font-weight:600;font-size:1.1rem;color:var(--text-primary);">$${(ticket.taxAmount || 0).toFixed(2)}</span>
          </div>
          <div style="border-top:1px solid var(--border-light);padding-top:8px;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:700;font-size:1.125rem;color:var(--text-primary);">Grand Total</span>
            <span style="font-weight:700;font-size:1.5rem;color:var(--brand-blue);">$${grandTotal.toFixed(2)}</span>
          </div>
        </div>
      `;
    } else {
      html += `
        <div style="margin-top:var(--spacing-lg);padding:var(--spacing-md);background:var(--bg-elevated);border-radius:var(--radius-md);display:flex;justify-content:space-between;align-items:center;">
          <span style="font-weight:700;font-size:1.125rem;color:var(--text-primary);">Grand Total</span>
          <span style="font-weight:700;font-size:1.5rem;color:var(--brand-blue);">$${grandTotal.toFixed(2)}</span>
        </div>
      `;
    }
  }

  // Ticket Notes (customer-facing, editable for draft/ready)
  const ticketNotes = ticket.notes || ticket.workPerformed || '';
  html += `
    <div class="detail-section" style="margin-top:var(--spacing-lg);">
      <div class="detail-section-title">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">note</span>
        Ticket Notes <span style="font-size:0.75rem;font-weight:400;color:var(--text-secondary);">(customer-facing)</span>
      </div>
      <div style="color:var(--text-secondary);font-size:0.85rem;white-space:pre-wrap;">${ticketNotes ? escapeHtml(ticketNotes) : '<em>No ticket notes</em>'}</div>
    </div>
  `;

  // Shift Notes (internal, read-only, from linked clockEntries)
  html += `
    <div class="detail-section" style="margin-top:var(--spacing-md);">
      <div class="detail-section-title" style="cursor:pointer;" onclick="document.getElementById('shift-notes-list')?.classList.toggle('hidden')">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">speaker_notes</span>
        Shift Notes <span style="font-size:0.75rem;font-weight:400;color:var(--text-secondary);">(internal)</span>
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-left:4px;">expand_more</span>
      </div>
      <div id="shift-notes-list" class="hidden" style="margin-top:8px;">
        <div style="color:var(--text-secondary);font-size:0.8rem;">Loading...</div>
      </div>
    </div>
  `;

  // TASK-10047 — office notes (internal; never printed). Hidden until loaded.
  html += _officeNotesSectionHtml('office-notes-list', 'detail');

  html += '</div>';
  // TASK-10044 (COD r1 Edge Case) — this render ran after several awaits. If
  // another ticket was opened meanwhile, it owns the modal now: painting this
  // one's body and footer (its money, its Generate Invoice id) over it is the
  // wrong-ticket path. Only a DIFFERENT open ticket stops it.
  if (BillingState.selectedTicket && BillingState.selectedTicket.id !== id) return;
  content.innerHTML = html;

  // Load shift notes from clockEntries
  loadShiftNotesForTicket(ticket.id, undefined, ticket);
  loadOfficeNotesForTicket(ticket.id, 'office-notes-list');
  if (_ticketJobLog(ticket)) __ddChunkWrap(import('./jobUpdates.js?v=96946')).then(m => m.fillJobLogs(content, BillingState.jobs, { collapsed: true, tenantId: BillingState.tenantId }), () => {});

  // TASK-4683 — ONLY when there is a decision to explain. The signer's name and
  // the rejection reason live on the inbound `ticketSendLog` row and nowhere
  // else, so the decision block above needs the series; a ticket with no answer
  // has nothing to fetch and must not pay for a callable to learn that. The
  // cache short-circuits a repeat open, and `_refreshTicketRibbon` repaints when
  // it lands. Explicit sink: an unhandled rejection here would surface as a
  // pageerror on a screen the smoke test watches.
  if (ticket.ticketCustomerResponse) loadTicketSendLog(ticket.id).catch(() => {});

  // Update action buttons based on status — TASK-4745. One primary per state,
  // pinned right; Close a text link on the left; everything demoted (never
  // deleted) behind `⋯`. See the block comment on _ensureTicketFooterStyles.
  const actionsEl = document.getElementById('view-ticket-actions');
  if (actionsEl) {
    _ensureTicketFooterStyles();
    const photoCount = ticket.photos?.length || 0;
    // 🔴 `onclick="fn('${id}')"` is a JS string INSIDE an attribute, and no HTML
    // escaper fixes that context — the parser decodes the entity BEFORE the JS
    // compiles, so an apostrophe kills the handler outright and a crafted id
    // executes. escJsAttr escapes for the JS literal first, then the attribute.
    const tid = window.escJsAttr(ticket.id);

    let primary = '';   // at most ONE filled button, pinned right
    const visible = []; // row 1, the tools: Print, Email, Edit, View Invoice
    const decide = [];  // row 2, beside the primary: the fork, the chip, Unarchive, Undo, Reverse
    const menu = [];    // behind `⋯` — demoted, never deleted

    const _inv = isTicketInvoiced(ticket);
    const _ext = isBilledExternally(ticket);
    const _vtArchived = ticket.status === 'archived' || ticket.status === 'locked';

    // ── Always reachable, every state ───────────────────────────────────────
    visible.push(_vtBtn({ icon: 'print', label: 'Print', onclick: `window.BillingModule.printTicket('${tid}')` }));
    // TASK-4662 (COD UX) — Email sits next to Print because that is where Lacey
    // looks, and she expects it to behave like Print: pick who, send, done.
    visible.push(_vtBtn({ icon: 'send', label: 'Email', onclick: `window.BillingModule.showTicketEmailDialog('${tid}')` }));
    if (photoCount > 0) {
      // Not an action — an anchor onto a gallery ~200px up the same modal. A
      // button under 640px where that gallery really is scrolled away, a menu
      // row on desktop where it is already on screen. Reachable at both widths,
      // rendered twice at neither (`.vt-only-narrow` / `.vt-wide-only`).
      const _photoJump = `document.getElementById('ticket-photo-gallery')?.scrollIntoView({behavior:'smooth'})`;
      visible.push(_vtBtn({ cls: 'btn btn-secondary vt-only-narrow', icon: 'photo_library', label: `Photos (${photoCount})`, onclick: _photoJump }));
      menu.push(_vtMenuItem({ cls: 'vt-wide-only', icon: 'photo_library', label: `Jump to photos (${photoCount})`, onclick: _photoJump }));
    }
    // Demoted from btn-primary. Editing is what you do to a ticket, not the
    // thing the ticket is asking you to decide.
    visible.push(_vtBtn({ icon: 'edit_note', label: 'Edit Ticket', onclick: `window.BillingModule.openReviewTicket('${tid}')` }));
    // TASK-10107 — rentals went out on this ticket: draft the return ticket
    // (views/returnTicketMirror.js, lazy; it checks the roster and says so if none are rentals).
    // A visible tool, not a ⋯ item (COD r1 UX): it is the next step after a delivery.
    if (ticket.jobId && !ticket.mirrorOf && ticketLines.some(l => l.type === 'EQUIPMENT' && l.assetType !== 'truck' && l.assetType !== 'trailer' && l.rentalDirection !== 'return' && (l.rentalSlice || l.quantity > 0))) {
      visible.push(_vtBtn({ icon: 'assignment_return', label: 'Return ticket', onclick: `window.BillingModule.createReturnTicket('${tid}')` }));
    }

    // TASK-9654 — which QuickBooks invoice this ticket is, and whether the
    // QuickBooks total differs (the paid sync's qboAmountDiff). Display only.
    if (hasQboInvoiceLink(ticket) || ticket.qboAmountDiff) {
      decide.push(_qboLinkChipHTML(ticket, 'btn btn-secondary vt-state-chip'));
    }

    // ── The billing decision — the same six branches the old row 2 had ──────
    // TASK-1345 — archived/locked tickets do NOT get a bare Invoice button here
    // (the ticket card already hides it; this modal contradicted that). The
    // sanctioned path out of the archive trap is Unarchive & Invoice, which
    // unarchives first and then invoices in one action.
    if (_inv) {
      // TASK-1346 (COD Domain ⚠️) — a ticket billed in QuickBooks has no DD
      // invoice to view, and pushing a PIDX EDI file carrying EXT-<ref> into a
      // shipper's OpenInvoice portal would arrive with a number their AP can't
      // match against what QuickBooks actually sent. Show the reference instead.
      if (_ext) {
        decide.push(`<span class="btn btn-secondary vt-state-chip" style="cursor:default;" title="Recorded as billed outside DispatchDeck">` +
          `<span class="material-icons">assignment_turned_in</span> Billed elsewhere${ticket.externalInvoiceRef ? ` (${escapeHtml(ticket.externalInvoiceRef)})` : ''}</span>`);
      } else {
        visible.push(_vtBtn({ icon: 'receipt', label: 'View Invoice', onclick: `window.BillingModule.viewInvoice('${tid}')` }));
        // One integration's export, not a step in the common path. Demoted to
        // the menu; the label now says what it does rather than naming a portal.
        menu.push(_vtMenuItem({ icon: 'upload_file', label: 'Export for OpenInvoice', title: 'Export PIDX XML for OpenInvoice portal', onclick: `window.BillingModule.exportOpenInvoice('${tid}')` }));
        // hotfix/billed-elsewhere-1020 — a DD invoice that was really billed in QuickBooks.
        if (!_switchToExternalBlockReason(ticket)) {
          decide.push(_vtBtn({ cls: 'btn btn-secondary vt-btn-fork', icon: 'assignment_turned_in', label: 'Switch to Billed elsewhere', title: 'Billed in QuickBooks instead — retire the DispatchDeck invoice', onclick: `window.BillingModule.switchToBilledElsewhere('${tid}')` }));
        }
      }
    } else if (!_vtArchived) {
      // On a draft the money action is this one, and now it is the only filled
      // button on the screen.
      primary = _vtBtn({ cls: 'btn vt-primary', style: 'background:#2E7D32;color:#fff;border:2px solid #2E7D32;', icon: 'receipt', label: 'Generate Invoice', onclick: `window.BillingModule.generateInvoice('${tid}')` });
      // 🔴 TASK-1346 — for tenants who bill in QuickBooks: record the fact
      // without generating a DD invoice, so they stop using Archive to mean
      // "billed". STAYS VISIBLE (COD, five lanes): burying the honest path while
      // Archive is one tap away hands the misuse straight back. Quiet outline,
      // full size — a FORK away from the primary, not a sub-option of it.
      if (ticket.writtenOff !== true) {
        decide.push(_vtBtn({ cls: 'btn btn-secondary vt-btn-fork', icon: 'assignment_turned_in', label: 'Billed elsewhere', title: 'Already billed in QuickBooks or another system', onclick: `window.BillingModule.markInvoicedExternally('${tid}')` }));
      }
    } else if (Number(ticket.grandTotal || 0) > 0 && ticket.writtenOff !== true) {
      // Archived + unbilled + has money: the one action that gets it out.
      primary = _vtBtn({ cls: 'btn vt-primary', style: 'background:#15803D;color:#fff;border:2px solid #15803D;', icon: 'receipt', label: 'Unarchive &amp; Invoice', onclick: `window.BillingModule.unarchiveAndInvoice('${tid}', this)` });
      // hotfix/billed-elsewhere-1020 — its QuickBooks twin.
      if (_canUnarchiveToExternal(ticket)) {
        decide.push(_vtBtn({ cls: 'btn btn-secondary vt-btn-fork', icon: 'assignment_turned_in', label: 'Unarchive &amp; mark Billed elsewhere', title: 'Already billed in QuickBooks — record it, no DispatchDeck invoice', onclick: `window.BillingModule.unarchiveAndMarkBilledElsewhere('${tid}')` }));
      }
    }

    // ── AR band ─────────────────────────────────────────────────────────────
    // TASK-1346 sweep — "is this billed?": Mark Paid / Write Off are AR actions
    // on work that HAS been billed. Keyed on the boolean, the 110
    // invoice-marker-only tickets could never be marked paid or written off.
    if (_inv && !ticket.writtenOff) {
      if (ticket.paid) {
        // TASK-1346 (3rd-COD Blast ❌) — the payment .iif is keyed on
        // ticket.invoiceNumber; for an externally-billed ticket that's EXT-<ref>,
        // which matches no QuickBooks invoice. exportPaymentIIF refuses too.
        // TASK-9654 — a ticket linked to a QuickBooks invoice is in the books
        // already (and, once the paid sync marks it, so is its payment): no
        // .iif, no Sync to QB. Undo Payment is then the visible action below.
        const _inQb = _ext || hasQboInvoiceLink(ticket);
        const _iif = !_inQb;
        // TASK-1346 — an externally-billed ticket was invoiced IN QuickBooks;
        // pushing it back would create a duplicate invoice there (same reasoning
        // as the PIDX/OpenInvoice refusal part 1 shipped).
        // TASK-9589 — live truth when known; with no live answer yet, kick one and fall back to
        // the cache for this ACTION button only (the server refuses a push when not connected).
        const _qbTruth = getQBOConnectionTruth();
        if (_qbTruth === null && !_qboCheckInFlight) refreshQBOStatus();
        const _qb = (_qbTruth === null ? !!window.AppState?.tenantData?.qboConnected : _qbTruth) && !_inQb && window._qboLiveStatus?.readOnly === false; // TASK-9756 — read-only (the default) hides Sync to QB; the server refuses regardless
        // TASK-4767 S2 one pay path — Paid set by the ticket's invoice is undone on the invoice.
        const _undo = (_invoiceRecordIdOf(ticket) && !_qboSyncPaid(ticket))
          ? { icon: 'receipt_long', label: 'Paid on invoice — open it', title: _onInvoiceRecordMsg(ticket), onclick: `window.BillingModule.closeModal('view-ticket');window.BillingModule.openInvoicesPanel('${_invoiceIdAttr(ticket)}');` }
          : { icon: 'undo', label: 'Undo Payment', onclick: `window.BillingModule.unmarkTicketPaid('${tid}');window.BillingModule.closeModal('view-ticket');` };
        const _pushedId = qboPushedId(ticket); // TASK-9537 — pushed once; no Re-sync (it made a second invoice)
        if (_qb && _pushedId) {
          // Already in QuickBooks: say which invoice, offer no second push.
          const _docNo = ticket.qboDocNumber ? String(ticket.qboDocNumber) : '';
          const _qbLabel = _docNo ? `In QuickBooks · #${escapeHtml(_docNo)}` : (_pushedId !== '(unknown id)' ? `In QuickBooks · id ${escapeHtml(_pushedId)}` : 'In QuickBooks');
          primary = `<span class="vt-qb-pushed" title="Sent to QuickBooks. Make changes to that invoice in QuickBooks." style="display:inline-flex;align-items:center;gap:6px;padding:8px 12px;border:2px solid #1E7A12;border-radius:8px;color:#1E7A12;font-weight:600;background:#F0FDF4;"><span class="material-icons" style="font-size:18px;">cloud_done</span> ${_qbLabel}</span>`
            // TASK-9537 slice 3 — the server checks QuickBooks; nothing is cleared here.
            + `<button type="button" class="vt-qb-unlock" onclick="window.BillingModule.unlockVoidedQboPush('invoice','${tid}')" title="If this invoice was voided or deleted in QuickBooks, DispatchDeck checks QuickBooks and lets you send the ticket again." style="background:none;border:none;color:#1D4ED8;text-decoration:underline;font-size:13px;padding:8px 4px;min-height:40px;cursor:pointer;">Voided in QuickBooks? Check &amp; unlock</button>`;
          if (_iif) menu.push(_vtMenuItem({ icon: 'download', label: 'Payment .iif', onclick: `window.BillingModule.exportPaymentIIF('${tid}')` }));
          // The badge is not an action, so Undo Payment stays VISIBLE (never only in the menu).
          decide.push(_vtBtn({ style: 'border-color:#BFDBFE;color:#1D4ED8;', ..._undo }));
        } else if (_qb) {
          // The money is in; the remaining step is getting it into the books.
          primary = _vtBtn({ cls: 'btn vt-primary', style: 'background:#1E7A12;color:#fff;border:2px solid #1E7A12;', icon: 'cloud_upload', label: 'Sync to QB', onclick: `window.BillingModule.syncTicketToQBO('${tid}')` });
          if (_iif) menu.push(_vtMenuItem({ icon: 'download', label: 'Payment .iif', onclick: `window.BillingModule.exportPaymentIIF('${tid}')` }));
          menu.push(_vtMenuItem(_undo));
        } else if (_iif) {
          // No QBO connection: the .iif IS how this tenant gets the payment into
          // QuickBooks, so it takes the slot Sync would have had.
          primary = _vtBtn({ cls: 'btn vt-primary', style: 'background:#0F172A;color:#fff;border:2px solid #0F172A;', icon: 'download', label: 'Payment .iif', onclick: `window.BillingModule.exportPaymentIIF('${tid}')` });
          menu.push(_vtMenuItem(_undo));
        } else {
          // 🔴 Externally billed AND paid: .iif and QB both refuse, so Undo
          // Payment is the ONLY action this state has left. It stays VISIBLE.
          // The one rule the overflow must never break is hiding a state's last
          // remaining action behind a menu blue-collar users do not open.
          decide.push(_vtBtn({ style: 'border-color:#BFDBFE;color:#1D4ED8;', ..._undo }));
        }
      } else {
        primary = _invoiceRecordIdOf(ticket)
          // TASK-4767 S2 one pay path — a ticket on an invoice record is paid on its invoice.
          ? _vtBtn({ cls: 'btn vt-primary', style: 'background:#7C3AED;color:#fff;border:2px solid #7C3AED;', icon: 'receipt_long', label: 'Record payment on invoice', title: _onInvoiceRecordMsg(ticket), onclick: `window.BillingModule.closeModal('view-ticket');window.BillingModule.openInvoicesPanel('${_invoiceIdAttr(ticket)}');` })
          : _vtBtn({ cls: 'btn vt-primary', style: 'background:#7C3AED;color:#fff;border:2px solid #7C3AED;', icon: 'payments', label: 'Mark Paid', onclick: `window.BillingModule.markTicketPaid('${tid}');window.BillingModule.closeModal('view-ticket');` });
        // Writing off money is not a peer of collecting it. Menu, in red.
        menu.push(_vtMenuItem({ cls: 'vt-menu-danger', icon: 'money_off', label: 'Write off…', onclick: `window.BillingModule.writeOffTicket('${tid}')` }));
      }
    }
    if (ticket.writtenOff) {
      // No forward money action exists here, so no filled primary is invented.
      decide.push(_vtBtn({ style: 'border-color:#FECACA;color:#B91C1C;', icon: 'undo', label: 'Reverse Write-Off', onclick: `window.BillingModule.unwriteOffTicket('${tid}');window.BillingModule.closeModal('view-ticket');` }));
    }

    // ── Archive / Unarchive ─────────────────────────────────────────────────
    if (_vtArchived) {
      const _un = { icon: 'unarchive', label: 'Unarchive', onclick: `window.BillingModule.unarchiveFromView('${tid}', this)` };
      // If nothing else claimed the slot, getting this ticket back out of the
      // archive is the state's only forward action, so it takes it.
      if (!primary) primary = _vtBtn({ cls: 'btn vt-primary', style: 'background:#0F172A;color:#fff;border:2px solid #0F172A;', ..._un });
      else decide.push(_vtBtn(_un));
    } else {
      // TASK-1346 sweep — books-integrity gate (matches showSplitTicketModal).
      if (!_inv && !ticket.paid && ticket.status !== 'sent') {
        menu.push(_vtMenuItem({ icon: 'call_split', label: 'Split ticket…', onclick: `window.BillingModule.showSplitTicketModal('${tid}')` }));
      }
      // 🔴 THE ONE THAT MATTERED. Archive leaves the row of grey pills it shared
      // with Print and lands in the menu: amber, under a divider, ellipsis on
      // the label, behind archiveTickets()'s confirm, and on a handler that no
      // longer closes this modal out from under its own refusal dialog.
      if (menu.length) menu.push('<div class="vt-menu-sep"></div>');
      menu.push(_vtMenuItem({ cls: 'vt-menu-warn', icon: 'inventory_2', label: 'Archive ticket…', title: 'Takes it off the tickets list — this is NOT the same as billed', onclick: `window.BillingModule.archiveTicketFromView('${tid}')` }));
    }

    const menuHtml = menu.length ? `<div class="billing-overflow-wrap">` +
      `<button class="btn btn-secondary billing-overflow-btn" title="More actions" aria-label="More actions" onclick="window.BillingModule.toggleBillingOverflow(event)">` +
      `<span class="material-icons">more_vert</span><span class="vt-more-label">More</span></button>` +
      `<div class="billing-overflow-dropdown hidden" id="view-ticket-overflow-menu">${menu.join('')}</div>` +
      `</div>` : '';

    // Two fixed rows (see _ensureTicketFooterStyles): the tools with the kebab
    // LAST, then the Grand Total opposite the decision cluster and its primary.
    // TASK-10044 — no footer Close at any width: the header X closes the modal.
    // On a phone CSS lifts the decision row above the tools; the DOM order never changes.
    actionsEl.innerHTML =
      `<div class="vt-tools">${visible.join('')}${menuHtml}</div>` +
      `<div class="vt-decide">` +
        _vtFooterTotalHtml(ticket, ticketLines, invoiceFmtMoney) + // cents: formatCurrency drops them (Fmt.currency is $#,###)
        `<div class="vt-decide-actions">${decide.join('')}${primary}</div>` +
      `</div>`;
  }
}

// ============================================
// PERMIT ATTACHMENT
// ============================================

// TASK-4649 — missing-permit flag (NON-BLOCKING — Chris: "I don't want to
// prevent them from clocking out... AR/AP should just have a red border around
// tickets that require a permit"). Nothing here touches the driver clock-out
// path or gates invoicing; it flags, a human decides.
function _permitTrackingEnabled() {
  return window.AppState?.settings?.enablePermitTracking === true || window.AppState?.isDemo === true;
}

// A ticket is flagged when its job says permitRequired === true (tri-state:
// absent/false never flags — 675+ legacy jobs carry no decision and must stay
// silent) AND none of the three LIVE attachment signals shows a permit AND no
// human has waived it. The three signals are the existing plumbing, not a new
// model (Blast: "reconcile or the flag duplicates a signal AR/AP already has"):
//   1. job.permits[] — file uploads (dispatch job form + uploadPermitFromBilling);
//      attachPermitToTicket only ever attaches FROM this array, so a PERMIT
//      ticket line implies it and needs no per-ticket ticketLines read.
//   2. job.permitId — the legacy single-permit link into the permits collection.
//   3. permits collection docs carrying permit.jobId === job.id (the S1 model).
// Waive vocabulary is the SAME field the SMS clearance flow writes
// (smsInboxActions.js markPermitNotRequired): ticket.permitNotRequired === true.
function _ticketMissingPermit(ticket) {
  if (!_permitTrackingEnabled()) return false;
  if (!ticket || !ticket.jobId || ticket.status === 'deleted') return false;
  const job = BillingState.jobs.find(j => j.id === ticket.jobId);
  if (!job || job.permitRequired !== true) return false;
  if (ticket.permitNotRequired === true) return false;
  if (jobPermitEntries(job).length > 0) return false; // TASK-9694 — a driver Doc is not a permit
  if (job.permitId) return false;
  if (BillingState.permits.some(p => p.jobId === ticket.jobId)) return false;
  return true;
}

// True when a flag WOULD fire but a human waived it — the card shows a quiet
// "waived" note with an undo instead of a red border.
function _ticketPermitWaived(ticket) {
  if (!_permitTrackingEnabled()) return false;
  if (!ticket || !ticket.jobId || ticket.permitNotRequired !== true) return false;
  const job = BillingState.jobs.find(j => j.id === ticket.jobId);
  return !!job && job.permitRequired === true;
}

// Header-chip count over the active pipeline (archived/locked are out of
// triage; the card border still renders there if someone digs).
function _countMissingPermits() {
  return (BillingState.tickets || []).filter(t =>
    t.status !== 'archived' && t.status !== 'locked' && _ticketMissingPermit(t)).length;
}

function toggleMissingPermitFilter() {
  BillingState.missingPermitFilter = !BillingState.missingPermitFilter;
  renderBillingView();
}

// [Dismiss] on the card — "this job didn't need a permit after all". Writes the
// SAME fields the SMS clearance path writes so the two can never disagree and
// permit-signal.cjs skips it too. Reversible via the undo on the waived note.
async function dismissPermitFlag(ticketId) {
  if (typeof window.showConfirm === 'function') {
    const ok = await window.showConfirm(
      'Dismiss permit flag?',
      'This records that no road permit was needed for this ticket. You can undo it from the ticket card.',
      { confirmText: 'Dismiss', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' });
    if (!ok) return;
  }
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      permitNotRequired: true,
      permitNotRequiredBy: _resolveReadyByName() || null,
      permitNotRequiredAt: Timestamp.now()
    });
    window.logAudit?.('billing', 'PERMIT_FLAG_DISMISSED', { ticketId });
    window.showToast?.('Permit flag dismissed', 'success');
    renderBillingView();
  } catch (error) {
    console.error('[Billing] Error dismissing permit flag:', error);
    window.showToast?.('Error dismissing permit flag', 'error');
  }
}

async function undoDismissPermitFlag(ticketId) {
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // false (not delete) — smsInboxActions checks === true, so this reads the
    // same to both paths without importing deleteField here.
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      permitNotRequired: false,
      permitNotRequiredBy: null,
      permitNotRequiredAt: null
    });
    window.logAudit?.('billing', 'PERMIT_FLAG_DISMISS_UNDONE', { ticketId });
    renderBillingView();
  } catch (error) {
    console.error('[Billing] Error undoing permit flag dismiss:', error);
    window.showToast?.('Error restoring permit flag', 'error');
  }
}

async function attachPermitToTicket(ticketId, permitId, permitName) {
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    await addDoc(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'), {
      type: 'PERMIT',
      description: permitName,
      lineAmount: 0,
      jobPermitId: permitId,
      createdAt: Timestamp.now()
    });
    try { window.logAudit?.('ticket', 'ticket_line_edit', { type: 'ticket_line_edit', ticketId, added: [{ type: 'PERMIT', ..._auditCutAll(_lineAuditPick({ description: permitName, lineAmount: 0 })) }], editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown' }); } catch (_) { /* TASK-1406, best-effort */ }
    window.showToast?.('Load permit attached', 'success');
    // Re-open ticket detail to refresh
    viewTicket(ticketId);
  } catch (error) {
    console.error('[Billing] Error attaching permit:', error);
    window.showToast?.('Error attaching permit', 'error');
  }
}

// AR/AP-initiated permit upload — writes back to jobs/{jobId}.permits so
// dispatch + driver views see the permit. Mirrors dispatch.js uploadPermitFiles
// schema (id, fullUrl, name, mimeType, isPdf, sizeBytes, uploadedAt). After the
// upload, calls attachPermitToTicket so the current ticket gets the PERMIT line.
// See COD round 2026-05-15: cod-reviews/2026-05-15T15-21_cod-proposals-permit-bidirectional-upload-txt.md
async function uploadPermitFromBilling(ticketId, jobId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  if (!tenant || !jobId) {
    window.showToast?.('Missing tenant or job context', 'error');
    return;
  }

  // Open file picker (PDF or image)
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/pdf,image/*';
  input.style.display = 'none';
  document.body.appendChild(input);
  let file = await new Promise(resolve => {
    input.onchange = () => resolve(input.files?.[0] || null);
    input.click();
  });
  document.body.removeChild(input);
  if (!file) return;

  // Slice 14 (QA MEDIUM #12, 2026-05-16): client-side type/size pre-check
  // before upload starts. Mirrors dispatch.js:11959-11967 so users see a
  // clean toast instead of a cryptic Storage permission-denied if they
  // bypass the input.accept hint or pick a >10MB file. Storage rules at
  // storage.rules:288-294 still hard-enforce the same constraints
  // server-side; this is UX polish, not the security gate.
  const nameLower = file.name.toLowerCase();
  const isPdfPick = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
  const isImagePick = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));
  if (!isPdfPick && !isImagePick) {
    window.showToast?.('Only images and PDFs are allowed', 'error');
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    window.showToast?.('Permit must be 10 MB or smaller', 'error');
    return;
  }

  // TASK-9397 — an iPhone HEIC permit used to be stored raw (image/heic, or
  // labelled image/jpeg when the browser gave no type): only Safari could open
  // it. Make it a JPEG first; other files upload untouched, as before.
  if (!isPdfPick && (await detectHeic(file))) {
    try {
      const jpeg = await processPhoto(await toDrawablePhoto(file), { maxDimension: 2400, quality: 0.85, maxSizeKB: 3000 });
      file = new File([jpeg], file.name.replace(/\.[^.]*$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch (err) {
      console.error('[Billing] permit HEIC conversion failed:', err?.cause || err);
      window.showToast?.(photoErrorMessage(err), 'error');
      return;
    }
  }

  window.showToast?.('Uploading permit…', 'info');

  try {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const permitId = `permit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const isPdf = isPdfPick;
    const ext = isPdf ? 'pdf' : (file.name.split('.').pop() || 'jpg').toLowerCase();
    const contentType = isPdf ? 'application/pdf' : (file.type || 'image/jpeg');
    const basePath = `tenants/${tenant}/job-permits/${year}/${month}/${jobId}`;
    const fullPath = `${basePath}/${permitId}_full.${ext}`;

    // 1) Storage upload. TASK-9958 — customMetadata.uploadedByUid is the
    //    rule-pinned per-file record of who sent it (storage.rules).
    const _uploader = _ticketDocOfficeUploader();
    const fullRef = ref(storage, fullPath);
    await uploadBytes(fullRef, file, { contentType, customMetadata: fileUploadCustomMetadata(_uploader?.uid, 'ticket_edit') });
    const fullUrl = await getDownloadURL(fullRef);

    // 2) Build permit object — same schema as dispatch.js:12126 so dispatch's
    //    permit grid + driver job-board view render it identically.
    const permit = stampFileProvenance({
      id: permitId,
      fullUrl,
      thumbUrl: null,
      fullPath,
      thumbPath: null,
      name: file.name || `Permit ${now.toLocaleDateString()}`,
      mimeType: contentType,
      isPdf,
      sizeBytes: file.size,
      uploadedAt: new Date().toISOString(),
      uploadedFrom: 'ap-billing'
    }, _uploader, 'ticket_edit'); // TASK-9958 — who + how

    // 3) Re-read the job's current permits to avoid clobbering a concurrent
    //    dispatch upload (no arrayUnion on objects with timestamps is safe;
    //    we want a fresh snapshot + spread + write).
    const jobRef = doc(db, 'tenants', tenant, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    const existing = (jobSnap.exists() && Array.isArray(jobSnap.data().permits)) ? jobSnap.data().permits : [];
    const allPermits = [...existing, permit];

    await updateDoc(jobRef, {
      permits: allPermits,
      permitCount: allPermits.length,
      updatedAt: Timestamp.now()
    });

    // 4) Audit — mirror dispatch's JOB_MEDIA_SAVE event so this upload is
    //    visible in the same audit lane as dispatcher uploads.
    try {
      window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
        source: 'ap-billing-upload',
        jobId, mediaType: 'permit',
        count: allPermits.length,
        before: { permitCount: existing.length },
        after: { permitCount: allPermits.length },
        redFlags: { uploadFailures: 0, permitCountDrop: false, wipedAll: false }
      });
    } catch (_) { /* non-blocking */ }
    // TASK-9958 — the per-file "who put this here" row.
    try {
      window.logAudit?.('job', 'FILE_ADDED', fileAddedAuditPayload({
        entry: permit, target: 'job_permits', via: 'ticket_edit', jobId, ticketId,
        ticketNo: ReviewState.ticket?.ticketNo || ReviewState.ticket?.ticketNumber || undefined,
        uid: auth?.currentUser?.uid || window.AppState?.user?.uid || '',
      }));
    } catch (_) { /* non-blocking */ }

    // 5) Attach as PERMIT line on the current ticket (the user's view).
    //    Re-uses attachPermitToTicket so future structural changes apply once.
    await attachPermitToTicket(ticketId, permitId, permit.name);

    window.showToast?.('Permit uploaded — pushed to dispatch + driver views', 'success');
  } catch (err) {
    console.error('[Billing] Permit upload error:', err);
    window.logError?.(err, { module: 'billing', operation: 'permit-upload-from-ap', ticketId, jobId });
    window.showToast?.('Permit upload failed', 'error');
  }
}

// ============================================
// TASK-1587 — INVOICE-MARKER STAMPS GO THROUGH THE SERVER GATEWAY
//
// TASK-1583 built the `markInvoiced` op (functions/src/services/ticketGateway.js)
// as the server-authoritative twin of the client's $0 gate: it re-reads the ticket
// and ALL of its lines with the Admin SDK, recomputes the CUSTOMER-INVOICE total
// with the same math the invoice/PDF use, and refuses to stamp `invoiced:true` on
// a $0 total. That op is INERT until the client calls it — this block is the call.
//
// The client gates (_zeroHourGate / _zeroTotalGate) STAY exactly as they are: they
// own the dialog, the "Open Review Ticket" affordance and the wording. The server
// is the backstop that makes a raw `updateDoc(tickets/{id},{invoiced:true})` from
// devtools impossible, not a replacement for the UX.
//
// 🔴 ONLY THE STAMPING DIRECTION IS ROUTED. Un-marking / uninvoicing is never gated
// and stays on the direct client path — see markAsInvoiced's `invoiced === false`
// branch and uninvoiceTicket.
//
// Four stamp sites are routed (every write in this file that sets invoiced:true or
// an invoiceNumber): generateInvoiceNumber, markAsInvoiced, _applyInvoicedExternally,
// and createGroupedInvoice's per-ticket loop.
// ============================================

// Gateway idempotency keys. Third copy of clock.js:56 / jobboard.js:121 by design —
// Pitfall #1 forbids a cross-module import carrying a `?v=` buster (two busters =
// two module instances = two independent session maps, which would defeat the very
// dedup this exists for). A random session token per LOGICAL op, persisted across
// retries, suffixed with a hash of the exact payload: an identical retry replays
// server-side, a changed payload executes fresh. Cleared only on confirmed success
// (_gwOpDone) so a genuine re-do — uninvoice then re-mark — is NOT swallowed as a
// replay of the first stamp. Dedup key ONLY, never an auth input.
const _gwOpSessions = {};
function _gwOpKey(scope, payload) {
  if (!_gwOpSessions[scope]) {
    _gwOpSessions[scope] = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : (Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));
  }
  let h = 5381;
  const s = JSON.stringify(payload || {});
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return (_gwOpSessions[scope] + '_' + h.toString(36)).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 128);
}
function _gwOpDone(scope) { delete _gwOpSessions[scope]; }

// 🔴 DEMO KEEPS THE DIRECT WRITE — and this is a server gap, not a preference.
// The demo tenant is anonymous auth with NO tenants/demo/members/{uid} doc.
// firestore.rules lets every demo invoice stamp through via isDispatcherOrHigher's
// isDemo arm (rules:288), and the callable's requireTenantMember short-circuits on
// tenantId === 'demo' (index.js:170) — but ticketGateway's resolveBillingActor has
// NO demo arm, so it would answer "not a member of this tenant" and permission-deny
// EVERY invoice action in the demo. Marking a ticket invoiced is a core demo flow,
// so demo stays on the path rules already authorize (still scoped to the demo
// tenant — no cross-tenant reach is possible from here). Delete this carve-out the
// day resolveBillingActor grows `if (tenantId === 'demo') return { allowed: true }`.
function _invoiceStampUsesGateway(tenant) {
  return tenant !== 'demo';
}

// Server cap is 50 ticketIds per call (ticketGateway markInvoiced). Chunk smaller
// than the cap: the op is a sequential read-read-read-write per ticket inside a 30s
// function timeout, and a grouped QuickBooks week is routinely 20-40 tickets.
const _GW_MARK_INVOICED_CHUNK = 25;

// Turn a callable failure into something a biller can act on. `err.code` from the
// Functions client is namespaced ('functions/permission-denied'), so match on
// substrings.
function _gwInvoiceErrorMessage(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '');
  if (/deadline-exceeded/i.test(code) || /deadline exceeded/i.test(msg)) {
    // TASK-1160 — a client timeout is NOT evidence the server failed. Never retry
    // blind; the opKey makes a deliberate retry safe, but the user should look first.
    return 'that took too long — it may still have gone through; refresh and check before retrying';
  }
  if (/permission-denied/i.test(code)) {
    return msg || 'you do not have billing access to mark tickets invoiced';
  }
  if (/not-found/i.test(code)) return 'the billing service is unavailable — nothing was marked';
  if (/unavailable|internal|unknown/i.test(code)) {
    return 'could not reach the billing service — nothing was marked; check your connection and try again';
  }
  return msg || 'the billing service refused the stamp';
}

/**
 * The ONE call into the server's markInvoiced op.
 *
 * NEVER THROWS. A transport/permission failure on a chunk is converted into
 * per-ticket `blocked` rows carrying the user-facing reason, so a multi-chunk
 * batch can never end up half-stamped-and-unreported (which would leave tickets
 * invoiced with no revenue entry and nobody told). Callers treat "the server
 * refused it" and "we couldn't reach the server" the same way: that ticket is
 * still unbilled, say so, move on.
 *
 * @param {string} tenant  demo-mapped tenant id
 * @param {string[]} ticketIds
 * @param {object} extra   { invoiceNumber?, invoicedExternally?, externalInvoiceRef? }
 * @param {string} scope   stable idempotency scope for this logical action
 * @returns {Promise<{marked: string[], blocked: Array<{ticketId:string,ticketNo:?string,reason:string,total:?number,transport?:boolean}>}>}
 */
async function _gatewayMarkInvoiced(tenant, ticketIds, extra, scope) {
  const marked = [];
  const blocked = [];
  let ids = (ticketIds || []).filter(Boolean);
  if (ids.length === 0) return { marked, blocked };

  // 🔴 TASK-1587 — RULES-PARITY BACKFILL. Routing a stamp through a callable runs
  // it on the ADMIN SDK, which bypasses firestore.rules — so two denials the rules
  // made for us on the direct path are no longer enforced by anything:
  //   • status === 'deleted'  — TASK-1346 COD Adversary 🔴: soft-delete + stamp =
  //     invoiced AND invisible, reachable with LESS privilege than the archived
  //     path. firestore.rules:3468 denies it; ticketGateway's locked fence checks
  //     only invoiced/archived/locked.
  //   • writtenOff === true   — TASK-1345 COD Domain ⚠️: never re-bill written-off
  //     debt. firestore.rules:3469 denies it; the server op does not check it at all.
  // Reported to the server half; until it lands, keep the behaviour here so this
  // change is not a net loosening. A ticket outside the live window has no local
  // state to judge, so it is passed through rather than blocked on a guess.
  //
  // A THIRD parity gap — a stamp whose RESULTING status is archived/locked
  // (firestore.rules:3466-3467, the invoiced-AND-invisible state TASK-1345 exists to
  // end) — is deliberately NOT enforced here. It is enforced in markAsInvoiced's
  // gate loop instead, because that is the one entry point rules were the only
  // backstop for (the rules comment names it: "re-checking the invoice window's
  // 'mark as invoiced' box AFTER the ticket got archived in the main window is
  // denied — unarchive first"). Every other caller already refuses archived tickets
  // upstream, and unarchiveAndInvoice — the $47,340 archived-work recovery — stamps
  // a ticket that WAS archived a moment ago, so a filter here would race its
  // snapshot and trap exactly the flow TASK-1345 built.
  const _parityBlocked = [];
  ids = ids.filter((id) => {
    const t = BillingState.tickets.find(x => x.id === id);
    if (!t) return true;
    const why = t.status === 'deleted' ? 'deleted — restore it first'
      : (t.writtenOff === true ? 'written off — reverse the write-off first' : null);
    if (!why) return true;
    _parityBlocked.push({ ticketId: id, ticketNo: t.ticketNo ?? null, reason: why, total: null });
    return false;
  });
  for (const b of _parityBlocked) blocked.push(b);
  if (ids.length === 0) return { marked, blocked };
  // 120s client-side: the server window is 30s, but cold start + a slow office
  // connection sit ON TOP of it (same reasoning as clock.js GW_CLIENT_TIMEOUT_MS).
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 120000 });
  for (let i = 0; i < ids.length; i += _GW_MARK_INVOICED_CHUNK) {
    const chunk = ids.slice(i, i + _GW_MARK_INVOICED_CHUNK);
    const payload = { ticketIds: chunk, ...(extra || {}) };
    try {
      const res = await fn({
        op: 'markInvoiced',
        tenantId: tenant,
        payload,
        opKey: _gwOpKey(scope, payload)
      });
      const data = res?.data || {};
      for (const id of (Array.isArray(data.marked) ? data.marked : [])) marked.push(id);
      for (const b of (Array.isArray(data.blocked) ? data.blocked : [])) {
        blocked.push({
          ticketId: b?.ticketId || null,
          ticketNo: b?.ticketNo ?? null,
          reason: b?.reason || 'refused by the billing service',
          total: b?.total ?? null,
          // TASK-9988 S1 — marks an open-clock refusal (see the retire rule below)
          openClock: Array.isArray(b?.openClock) ? b.openClock : null
        });
      }
    } catch (err) {
      console.error('[Billing] markInvoiced gateway call failed for', chunk, err);
      const reason = _gwInvoiceErrorMessage(err);
      for (const id of chunk) {
        blocked.push({
          ticketId: id,
          ticketNo: BillingState.tickets.find(t => t.id === id)?.ticketNo ?? null,
          reason,
          total: null,
          transport: true,
          // hotfix/billed-elsewhere-1020 (2nd review) — only these MAY have committed
          // server-side; permission-denied / failed-precondition are definite refusals.
          maybeCommitted: /deadline|unavailable|internal|unknown|network/i.test(String(err?.code || '') + ' ' + String(err?.message || ''))
        });
      }
    }
  }
  // Only a run with nothing left hanging retires the idempotency session — a
  // partially-blocked batch keeps its token so a retry of the SAME payload still
  // dedups server-side.
  // TASK-9988 S1 — EXCEPT an open-clock refusal: its fix is "clock them out,
  // then try again", and the server caches a blocked answer under the opKey, so
  // keeping the token would replay "still clocked in" after the clock-out until
  // a reload. Retired only when every chunk got a definite server answer (no
  // transport failure); tickets marked meanwhile re-stamp harmlessly.
  if (blocked.length === 0 || (!blocked.some(b => b.transport) && blocked.some(b => b.openClock))) _gwOpDone(scope);
  return { marked, blocked };
}

// Shared wording for "these tickets were not marked invoiced". Used by both the
// client-side gates and the server's blocked[] so the biller reads ONE message.
function _notMarkedInvoicedToast(reasons) {
  // The "enter the hours" tail is the RIGHT advice for a gate block and the WRONG
  // advice for "you lack billing access" / "couldn't reach the service" — appending
  // it unconditionally would send a biller to hunt for missing hours that are fine.
  const dataFix = reasons.some(r => /0 hours|\$0 total|nothing to bill|could not verify/i.test(r));
  return `Not marked invoiced — ${reasons.join(' · ')}.` +
    (dataFix ? ' Enter the missing hours/amounts or mark lines non-billable in Review Ticket first.' : '');
}

// ============================================
// INVOICE NUMBERING
// ============================================
// TASK-1401 / TASK-1589 (the same defect, filed twice) — THE canonical invoice-
// number allocator. Both minting paths in this file (generateInvoiceNumber for a
// single ticket, createGroupedInvoice for a shared batch number) go through it.
//
// THE RACE IT REPLACES: allocation was `max(seq over the INV-YYYYMM- range) + 1`,
// computed client-side and handed to the server op as an input. A read-then-write
// max has no serialization, so two billers — or one biller in two tabs, or a
// retry racing its own first attempt — both read `INV-202608-054` and both mint
// `INV-202608-055`. Unlike a duplicate ticketNo, this one LEAVES THE BUILDING: it
// is the QuickBooks `DocNumber` (functions/src/qbo.js:616) and it is printed on
// the PDF the customer receives, so two different jobs end up billed under one
// invoice number in the customer's records and in the books.
//
// TWO NON-RACY DUPLICATE PATHS DIE HERE TOO, and they are the ones most likely to
// have already fired:
//   (1) The old code SWALLOWED its query error and fell through to `maxSeq = 0`,
//       so one transient read failure re-issued `-001` on top of a live invoice.
//   (2) `uninvoiceTicket` nulls `invoiceNumber`, which removes that number from
//       the max-probe's view — so invoice → uninvoice → invoice a DIFFERENT ticket
//       deterministically re-issued the number the first ticket had already been
//       shown/emailed/QB-synced under. The counter never moves backwards, so the
//       number is retired for good. Gaps in the sequence are the correct outcome
//       here: a skipped number costs nothing, a repeated one costs a customer
//       argument.
//
// THE FIX is the SAME MECHANISM the ticket-number allocator already ships
// (TASK-1354(b), getNextTicketNumber below): a counter doc under
// `tenants/{tid}/_counters/`, incremented inside runTransaction(). Firestore
// serializes transactions touching the same document, so two simultaneous
// billers get -055 and -056, never -055 twice. Deliberately NOT a second
// mechanism — consistency with the already-reviewed allocator is worth more
// than elegance here.
//
// 🔴 AND IT INHERITED THE TICKET ALLOCATOR'S BUG (TASK-4823). This header
// also used to say Firestore "re-runs the loser against the committed value".
// It does not, on the client: the `_counters` update rule is monotonic, so the
// loser is answered **permission-denied**, which the Web SDK will not retry.
// Two billers invoicing in the same second, and one gets an error instead of a
// number. Fixed by the same `_allocateCounterWithRetry` the ticket allocator
// uses — ONE loop for both, because three counters with the same shape and the
// same defect is a pattern, not three coincidences.
//
// PER-MONTH: invoice numbers reset each month (`INV-YYYYMM-NNN`), so the counter
// is ONE DOC PER MONTH — `_counters/invoiceNo-YYYYMM` — rather than a composite
// value in one doc. Reasons: a month's counter is seeded lazily from that month's
// own live max (identical shape to the ticket counter, no migration, no backfill);
// the existing `_counters/{counterId}` firestore.rules block already covers any
// counter id, so this needs NO RULES CHANGE and no rules deploy; and the
// monotonic + create-ceiling guards already in that block apply as-is (a sequence
// is a small int, nowhere near the 100,000,000 create ceiling or the +1,000,000
// per-write bound). A composite single doc would have needed a rules change to
// let `value` reset to 1 on a month boundary — i.e. it would have had to give up
// the monotonic guard, which is the whole protection.
//
// SEEDING / SELF-HEAL: same reasoning as getNextTicketNumber — the probe runs on
// EVERY call and the base is `max(counter, observedMax)`, because the counter is
// not the only thing that has ever written an invoice number (a browser still on
// the previous bundle during a rollout runs the old read-max allocator and bumps
// no counter; admin-SDK scripts and seeders set invoiceNumber directly). The probe
// is OUTSIDE the transaction — the Web SDK cannot run a query inside one — so the
// self-heal is EVENTUAL, not atomic, and the honest guarantee is two-part:
//   • Between any two callers of THIS allocator, collision is impossible.
//   • Against a writer that bypasses the counter (a stale bundle mid-rollout)
//     there is a one-allocation window that the next probe corrects.
// Treat duplicate invoice numbers as impossible only once the previous bundle is
// drained.
//
// FAIL CLOSED, BUT NEVER STRANDED: if there is no counter to read AND the probe
// failed, there is nothing safe to return, so this THROWS instead of guessing
// (guessing is exactly what produced duplicate `-001`s). Both callers catch it,
// tell the biller in plain words that nothing was invoiced and that retrying is
// the fix, and leave the ticket exactly as it was — no half-stamped state, no
// invoice window opened over a ticket that was never stamped.

// 🔴 TASK-4823 / TASK-4825 — THE RETRY IS NOT OPTIONAL, AND IT NOW LIVES IN ONE
// PLACE: `allocateCounterWithRetry` in utils/counterRetry.js, imported at the top
// of this file as `_allocateCounterWithRetry` so the two call sites below are
// unchanged from the reviewed version.
//
// The full argument — why the monotonic rule turns a lost race into
// permission-denied, why the Web SDK will not retry it, the measured
// per-allocator failure counts, why the backoff curve is narrow-and-jittered
// rather than textbook full jitter (MEASURED strictly worse, do not re-try it),
// why the second bound is a wall clock and not an attempt count, why 30s, and
// why the SERVER twins in functions/src/services/ticketGateway.js must NOT use
// this loop — all moved with the code, verbatim, into utils/counterRetry.js.
// Read it there before changing any of it.
//
// It was extracted because it had already drifted: this file shipped 64 attempts
// plus a 30s wall clock while dispatch.js shipped a private copy at 24 attempts
// with no wall clock, and 24 was measured leaving 17 of 50 callers with no number
// at all. Four allocators (ticketNo, invoiceNo-*, slips, jobNo) now share ONE loop.

const INVOICE_SEQ_SEED = 0; // → the first invoice of a month is -001

// The month prefix an invoice number minted `now` belongs to. Split out so the
// allocator is testable against a fixed clock.
function invoicePeriodPrefix(now) {
  const d = now || new Date();
  return `INV-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Highest sequence already present on a ticket for this month's prefix, or null
// if the month is empty. Same range query the old inline allocators used; the
// difference is that its FAILURE is now propagated instead of swallowed.
async function _probeMaxInvoiceSeq(tenant, prefix) {
  const snap = await getDocs(query(
    collection(db, 'tenants', tenant, 'tickets'),
    where('invoiceNumber', '>=', prefix + '-'),
    where('invoiceNumber', '<=', prefix + '-\uf8ff')
  ));
  let max = null;
  for (const d of snap.docs) {
    const seq = parseInt(String(d.data().invoiceNumber || '').split('-').pop(), 10);
    if (Number.isFinite(seq) && (max === null || seq > max)) max = seq;
  }
  return max;
}

// Allocates and RESERVES the next invoice number for `tenant`. Throws on failure
// — see FAIL CLOSED above. Callers must not stamp anything if this throws.
async function allocateInvoiceNumber(tenant, now) {
  const prefix = invoicePeriodPrefix(now);

  // Best-effort once the counter exists (the counter is then the authority) but
  // REQUIRED to seed it — a blind seed would restart a month already at -054 back
  // at -001 and collide on the very first invoice. One retry: a numbering failure
  // is nearly always a transient read.
  let observedMax = null;
  let probeErr = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      observedMax = await _probeMaxInvoiceSeq(tenant, prefix);
      probeErr = null;
      break;
    } catch (e) {
      probeErr = e;
      if (attempt === 0) await new Promise(r => setTimeout(r, 400));
    }
  }

  // TASK-4823 — the probe above ran ONCE and stays out of the retry loop: by the
  // time we are retrying, the counter has already moved PAST any max the probe
  // could have seen, so re-probing every round would cost a query per round and
  // change nothing.
  const next = await _allocateCounterWithRetry('invoice number',
    () => _allocateInvoiceNumberTxn(tenant, prefix, observedMax, probeErr));

  return `${prefix}-${String(next).padStart(3, '0')}`;
}

async function _allocateInvoiceNumberTxn(tenant, prefix, observedMax, probeErr) {
  const counterRef = doc(db, 'tenants', tenant, '_counters', `invoiceNo-${prefix.slice(4)}`);
  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? snap.data().value : null;
    const haveCounter = Number.isFinite(current);

    if (!haveCounter && probeErr) {
      // Nothing safe to return. Plain Error so the SDK treats it as fatal rather
      // than a retryable transaction error.
      throw new Error(
        'Could not reserve an invoice number: ' + (probeErr?.message || probeErr)
      );
    }

    // COD (Code Reviewer + Adversary) — the self-heal cannot be allowed to swallow
    // an ARBITRARY observed max. `_probeMaxInvoiceSeq` accepts any INV-YYYYMM-*
    // suffix, so one mistyped or imported `INV-202608-99999999` would become the
    // counter and permanently jump every future number; worse, a jump larger than
    // the +1,000,000 that firestore.rules allows on a single counter update gets
    // DENIED, which strands allocation behind an opaque permission error until an
    // admin deletes the counter doc.
    //
    // Seeding an EMPTY month from the live max is the legitimate case and stays
    // unbounded — that is what the seed is for. What is bounded is a heal on top of
    // an EXISTING counter: the honest reasons for a gap there are a stale bundle
    // mid-rollout or an admin backfill, and those move the sequence by single
    // digits. 10,000 is enormous next to real volume (TITS runs ~55 invoices a
    // month) and tiny next to the rules ceiling, so it cannot deny an honest heal.
    // Beyond it, refuse and NAME the number rather than mint a wrong one — the
    // biller can see which invoice is lying, which a silent jump would hide.
    const INVOICE_SELF_HEAL_MAX_JUMP = 10000;

    let base;
    if (haveCounter || observedMax != null) {
      if (haveCounter && observedMax != null && (observedMax - current) > INVOICE_SELF_HEAL_MAX_JUMP) {
        throw new Error(
          `Invoice numbering looks wrong for ${prefix}: an existing invoice is numbered ` +
          `${prefix}-${String(observedMax).padStart(3, '0')}, far above the current sequence ` +
          `(${String(current).padStart(3, '0')}). Nothing has been invoiced. Check that invoice ` +
          `number before billing anything else this month.`
        );
      }
      base = Math.max(
        haveCounter ? current : Number.NEGATIVE_INFINITY,
        observedMax != null ? observedMax : Number.NEGATIVE_INFINITY
      );
    } else {
      // Genuinely empty month — no counter, no invoice carrying this prefix.
      base = INVOICE_SEQ_SEED;
    }

    const value = base + 1;
    tx.set(counterRef, {
      value,
      updatedAt: Timestamp.now(),
      // seededFrom is forensics: it answers "did this month's counter start from
      // the live max or from an empty month" without re-deriving it later.
      ...(snap.exists() ? {} : { createdAt: Timestamp.now(), seededFrom: base })
    }, { merge: true });
    return value;
  });
}

// Plain-words failure for a biller whose number could not be reserved. Says what
// happened, states that NOTHING was billed, and names the way forward — a denial
// with no route out is how TASK-428 trapped drivers for 14 hours.
function _invoiceAllocFailedMessage(err) {
  const raw = String((err && err.message) || err || '');
  const offline = /offline|unavailable|network|deadline|failed to get document/i.test(raw);
  const denied = /permission|insufficient|denied/i.test(raw);
  const why = offline
    ? "couldn't reach the billing database"
    : (denied ? 'this account is not allowed to reserve invoice numbers' : (raw || 'the reservation failed'));
  return `Could not reserve an invoice number — ${why}. Nothing was invoiced and nothing was sent to the customer. `
    + (denied
      ? 'Ask an admin for billing access, then try again.'
      : 'Check your connection and try again in a moment — the ticket is untouched.');
}

async function generateInvoiceNumber(ticketId) {
  if (_moneyBlocked()) return null; // TASK-9491
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

  // TASK-1401/1589 — RESERVE the number transactionally instead of computing
  // `max + 1` locally. See allocateInvoiceNumber above for the race and for why
  // this mirrors the ticket-number counter rather than inventing a mechanism.
  // A failure here means NOTHING was stamped: return null (the contract the
  // TASK-1587 comment below already established) so generateInvoice does not open
  // an invoice window over an unstamped ticket, and say plainly what to do next.
  let invoiceNumber;
  try {
    invoiceNumber = await allocateInvoiceNumber(tenant);
  } catch (err) {
    console.error('[Billing] Invoice number allocation failed:', err);
    window.logError?.(err, { module: 'billing', operation: 'ALLOCATE_INVOICE_NUMBER', ticketId });
    window.showToast?.(_invoiceAllocFailedMessage(err), 'error');
    return null;
  }

  // Update ticket with invoice number.
  // TASK-1346 — `invoiced: true` belongs in the SAME write. Only markAsInvoiced()
  // used to set the boolean, and nothing forced it to run after this path, so
  // 110 live TITS tickets carry invoiceNumber + invoicedAt with invoiced still
  // false — and every report keyed on the boolean overstated unbilled work by
  // $274,339. Ongoing, not legacy: 71 of them landed in July alone.
  //
  // TASK-1587 — the server op takes the number as an INPUT; it does not allocate
  // one, and only the WRITE moved to it. TASK-1401 then moved the ALLOCATION into
  // allocateInvoiceNumber above, so the number handed to the server op is now a
  // transactionally reserved one rather than a local max+1.
  // The server stamps exactly the same five fields for a payload carrying an
  // invoiceNumber: invoiceNumber + invoiced + a fresh invoicedAt/invoicedBy + updatedAt.
  // 🔴 Returns null instead of throwing when the stamp is refused or unreachable —
  // the one caller (generateInvoice) must NOT open an invoice page for a ticket that
  // was never stamped.
  if (_invoiceStampUsesGateway(tenant)) {
    const res = await _gatewayMarkInvoiced(tenant, [ticketId], { invoiceNumber }, `markInvoiced:gen:${ticketId}`);
    if (!res.marked.includes(ticketId)) {
      const b = res.blocked.find(x => x.ticketId === ticketId);
      const t = BillingState.tickets.find(x => x.id === ticketId);
      window.showToast?.(
        `Ticket ${t?.ticketNo ? `#${t.ticketNo} ` : ''}was not invoiced — ${b?.reason || 'the billing service refused the stamp'}.`,
        'error'
      );
      return null;
    }
  } else {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      invoiceNumber,
      invoiced: true,
      invoicedAt: Timestamp.now(),
      invoicedBy: window.AppState?.user?.uid || 'unknown',
      updatedAt: Timestamp.now()
    });
  }

  // Record revenue entry. TASK-857: the ticket may be outside the bounded live
  // window — use _fetchTicketById (getDoc fallback) so the revenue entry ALWAYS
  // records when an invoice number is written. A plain BillingState.tickets.find
  // could return undefined for an out-of-window ticket → invoice-without-revenue.
  const ticket = await _fetchTicketById(ticketId);
  if (ticket) {
    // TASK-1587 — mirror the stamp into local state. A client updateDoc gets
    // latency compensation (the onSnapshot handler sees it before the server round
    // trip); a CALLABLE does not, so without this the caller's very next line —
    // openInvoicePage(), which reads BillingState.tickets synchronously — would
    // render the invoice with no invoice number on it. _fetchTicketById returns the
    // live BillingState object when the ticket is in the window, so this mutation
    // is the same object the render reads. invoicedAt/invoicedBy are deliberately
    // NOT faked here — the snapshot fills the real server values a moment later,
    // and isTicketInvoiced() already reads true off invoiceNumber.
    ticket.invoiceNumber = invoiceNumber;
    ticket.invoiced = true;
    // TASK-9942 — the revenue row is written by the SERVER, in the same commit
    // as the stamp above (ticketGateway markInvoiced), from the total it just
    // recomputed. The browser only records it on demo, whose stamp never
    // reaches the server (_invoiceStampUsesGateway).
    if (!_invoiceStampUsesGateway(tenant)) {
      // TASK-511 — ensure the customer's hide list is cached so recordRevenueEntry
      // recognizes revenue net of hidden lines (matches the invoice render).
      if (ticket.customerId) await loadReviewContractRates(ticket.customerId);
      // TASK-4595 — awaited: recordRevenueEntry RETURNS false on failure. Never
      // BLOCKS the invoice — it only stops the app claiming the books are complete.
      if ((await recordRevenueEntry(tenant, ticket, invoiceNumber)) === false) {
        window.showToast?.(`Invoice ${invoiceNumber} created, but your revenue reports did not update for ticket #${ticket.ticketNo || '?'}. The invoice is fine — BI will be short until it is re-run.`, 'warning');
      }
    }
  }

  return invoiceNumber;
}

async function markAsInvoiced(ticketIds, invoiced) {
  if (_moneyBlocked()) return; // TASK-9491
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  // TASK-1548 — marking invoiced is the write that made #100464 permanent. Gate it
  // the same as generating the invoice; fresh line read per ticket, fail closed on
  // a read error. Blocked tickets are SKIPPED with a message, clean ones proceed
  // (this runs from the invoice window's checkbox via window.opener — the dialog
  // renders in the main app, where the fix lives). UN-marking is never gated.
  // TASK-1587 — hoisted so the SERVER's blocked[] merges into the SAME toast.
  const _zhBlocked = [], _requested = ticketIds.length; // TASK-10164-B
  if (invoiced) {
    const _zhAllowed = [];
    for (const id of ticketIds) {
      try {
        // 🔴 TASK-1587 rules-parity — see _gatewayMarkInvoiced's note. Kept HERE,
        // not in that shared helper, so it can't race unarchiveAndInvoice.
        const _t = BillingState.tickets.find(x => x.id === id);
        if (_t && (_t.status === 'archived' || _t.status === 'locked')) {
          _zhBlocked.push(`#${_t.ticketNo || '?'} (archived or locked — unarchive it first)`);
          continue;
        }
        // TASK-9988 S1 — first-time stamps only, same as the server: the invoice
        // window's checkbox re-stamps a ticket generateInvoice already billed.
        const _oc = (_t && !isTicketInvoiced(_t)) ? _openClockOnTicket(id) : [];
        if (_oc.length) {
          _zhBlocked.push(`#${_t.ticketNo || '?'} (${_openClockReason(_oc)} — clock them out in Workforce first)`);
          continue;
        }
        const off = await _fetchZeroHourOffenders(id);
        if (off.length > 0) {
          const t = BillingState.tickets.find(x => x.id === id);
          _zhBlocked.push(`#${t?.ticketNo || '?'} (priced equipment, 0 hours: ${off.map(_zeroHourLineLabel).join(', ')})`);
        } else if ((await _zeroTotalOffense(id)).blocked) {
          // TASK-1567 — the whole ticket totals $0 and the customer is not
          // internal-time. A $0 invoice to a customer is never correct.
          const t = BillingState.tickets.find(x => x.id === id);
          _zhBlocked.push(`#${t?.ticketNo || '?'} (nothing to bill — $0 total)`);
        } else {
          _zhAllowed.push(id);
        }
      } catch (err) {
        console.error('[Billing] markAsInvoiced invoice-gate check failed for', id, err);
        _zhBlocked.push(`#${BillingState.tickets.find(x => x.id === id)?.ticketNo || '?'} (could not verify — try again)`);
      }
    }
    if (_zhAllowed.length === 0) {
      if (_zhBlocked.length > 0) window.showToast?.(_notMarkedInvoicedToast(_zhBlocked), 'error');
      return;
    }
    ticketIds = _zhAllowed;
  }
  let _blockedShown = false;
  try {
    // TASK-1587 — the write. `marked` is what actually landed; everything below
    // (local state, the audit row, the success toast) is keyed on IT, never on the
    // requested list, so a partially-blocked batch can't report itself as clean.
    let marked = ticketIds;
    if (!invoiced) {
      // 🔴 UN-MARKING IS NEVER GATED — deliberately still a direct write. The server
      // op only knows how to stamp; routing this would put a permission wall in
      // front of the one action a biller takes to UNDO a mistake, and there is no
      // money risk in clearing a flag. Do not "unify" these two branches.
      for (const id of ticketIds) {
        // TASK-4767 S2 one pay path — same as uninvoiceTicket: Paid its invoice set
        // comes off in this same write, re-read in a transaction.
        const _clearedPaid = await _uninvoiceWrite(tenant, id, {
          invoiced: false,
          // TASK-4597 — clearing ONLY the boolean left `invoiceNumber` and
          // `invoicedAt` behind, so an un-marked ticket still looked invoiced
          // everywhere those two are read. That is how Steve's Fri/Sat bore
          // tickets (#100540, #100548 on INV-202608-103) ended up reading
          // "invoiced" on one surface and "draft" on another — 9 tickets are in
          // that state on TITS right now.
          // `uninvoiceTicket` (:13745) already clears all three and is the
          // correct precedent; this loop was the one that did not. It
          // deliberately does NOT touch invoicedExternally / externalInvoiceRef
          // for the same reason that one does not: neither name is in the
          // firestore.rules locked-ticket allowlist, so including them would
          // make hasOnly() reject the WHOLE write.
          invoiceNumber: null,
          invoicedAt: null,
          updatedAt: Timestamp.now()
        }, false);
        await _voidRevenueRowsForTicket(tenant, id, 'unmarked_invoiced', _clearedPaid ? { paid: false, paidAt: null } : null); // TASK-9872
        if (_clearedPaid) {
          const _c = (BillingState.tickets || []).find(t => t.id === id);
          if (_c) { _c.paid = false; _c.paidAt = null; _c.paidBy = null; }
        }
      }
    } else if (!_invoiceStampUsesGateway(tenant)) {
      // Demo — see _invoiceStampUsesGateway.
      for (const id of ticketIds) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), {
          invoiced: true,
          updatedAt: Timestamp.now()
        });
      }
    } else {
      const res = await _gatewayMarkInvoiced(tenant, ticketIds, {}, `markInvoiced:mark:${ticketIds.join(',')}`);
      marked = res.marked;
      for (const b of res.blocked) {
        _zhBlocked.push(`#${b.ticketNo || BillingState.tickets.find(t => t.id === b.ticketId)?.ticketNo || '?'} (${b.reason})`);
      }
    }
    if (_zhBlocked.length > 0) { window.showToast?.(_notMarkedInvoicedToast(_zhBlocked), 'error'); _blockedShown = true; }
    if (marked.length === 0) return;
    // Update local state (a WRITE of the boolean, not a "is it billed?" read).
    // Also the callable's stand-in for latency compensation — see the note in
    // generateInvoiceNumber.
    for (const id of marked) {
      const t = BillingState.tickets.find(t => t.id === id);
      if (!t) continue;
      t.invoiced = !!invoiced;
      // TASK-4597 — mirror the FULL un-mark locally, not just the boolean. The
      // write now clears invoiceNumber/invoicedAt too, and `isTicketInvoiced()`
      // reads all three — so leaving the stale number on the local object meant
      // the biller's own list kept showing the ticket as billed until a snapshot
      // landed, i.e. the fix looked like it had not worked (COD Code Reviewer).
      if (!invoiced) { t.invoiceNumber = null; t.invoicedAt = null; }
    }
    // Audit rows die SILENTLY on an undefined field (logAudit swallows the Firestore
    // throw). `marked` is always an array and `count` always a number here.
    window.logAudit?.('ticket', invoiced ? 'MARK_INVOICED' : 'UNMARK_INVOICED', { ticketIds: marked, count: marked.length });
    window.showToast?.(invoiced ? 'Marked as invoiced' : 'Unmarked as invoiced', 'success');
    // Force ticket list re-render even if a modal is open (invoice page calls this via window.opener)
    const countEl = document.getElementById('tickets-count');
    renderTicketsList(countEl);
    updateBillingHeader();
    // TASK-10164-B — the invoice page reads this: true = every requested ticket
    // written, 'partial' = some were blocked (the toast names them).
    return marked.length === _requested || 'partial';
  } catch (err) {
    console.error('[Billing] Error marking invoiced:', err);
    // A client-side gate block that hasn't been shown yet must not be swallowed by
    // a later failure on the write path.
    if (_zhBlocked.length > 0 && !_blockedShown) window.showToast?.(_notMarkedInvoicedToast(_zhBlocked), 'error');
    window.showToast?.('Error updating invoice status', 'error');
  }
}

async function cleanupInvoiceNotes(ticketIds) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  let lastCleaned = null;
  for (const id of ticketIds) {
    const ticket = BillingState.tickets.find(t => t.id === id);
    if (!ticket) continue;
    const notes = ticket.notes || ticket.workPerformed || '';
    if (!notes.trim()) continue;
    try {
      const cleanupFn = httpsCallable(functions, 'cleanupShiftNotes');
      const result = await cleanupFn({ notes });
      const cleaned = result.data?.cleaned;
      if (!cleaned) continue;
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), {
        notes: cleaned,
        workPerformed: cleaned,
        ..._notesEditStamp('billing_cleanup'), // TASK-10036 (a)
        updatedAt: Timestamp.now()
      });
      if (ticket) { ticket.notes = cleaned; ticket.workPerformed = cleaned; }
      lastCleaned = cleaned;
    } catch (err) {
      console.error('[Billing] Error cleaning notes for ticket', id, err);
    }
  }
  if (lastCleaned) window.showToast?.('Notes cleaned up', 'success');
  return lastCleaned;
}

// Review Ticket "Clean Up Notes" (Chris 2026-09-23: "doesn't seem to be working").
// Three things made it look dead, all fixed here:
//   1. One call per note, awaited ONE AT A TIME (~10s each on a Pro model), so a
//      ticket with general + 4 equipment notes sat on "Cleaning..." for ~50s.
//      Calls now run in parallel (bounded) with a live "Cleaning N of M" label.
//   2. The GENERAL note was cleaned and then silently thrown away: it was put in
//      headerEdits, but renderReviewTicket() starts by copying the (still old)
//      #review-ticket-notes DOM value back over headerEdits. The cleaned text is
//      now written into the textarea itself, which is what render preserves.
//   3. Every outcome said roughly the same thing. Now: cleaned / already clean /
//      left as written (the server's safety check refused the rewrite) / failed
//      with the server's own reason.
// A note the biller edits while its call is in flight is left as they typed it.
const _REVIEW_CLEANUP_CONCURRENCY = 4;
// The run belongs to the ticket load it started on (_reviewLoadToken). A run left
// behind by a closed/switched ticket never blocks the next ticket's button.
let _reviewCleanupRunToken = null;
let _reviewCleanupLabel = '';

function _reviewCleanupButtons() {
  return ['review-cleanup-notes-btn', 'review-cleanup-general-btn']
    .map(id => document.getElementById(id)).filter(Boolean);
}

function _setReviewCleanupButtons(label, busy) {
  _reviewCleanupLabel = busy ? label : '';
  const idle = '<span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">auto_fix_high</span> Clean Up Notes';
  for (const b of _reviewCleanupButtons()) {
    b.disabled = !!busy;
    b.innerHTML = busy ? `<span class="btn-spinner"></span> ${escapeHtml(label)}` : idle;
  }
}

function _reviewCleanupErrorReason(err) {
  const code = String(err?.code || '').replace(/^functions\//, '');
  if (code === 'deadline-exceeded') return 'the AI took too long to answer';
  if (code === 'unavailable') return 'could not reach the server — check your connection';
  if (code === 'unauthenticated') return 'your session expired — sign in again';
  const msg = String(err?.message || '').trim();
  // A bare status word ("internal") says nothing to a biller.
  if (!msg || msg.toLowerCase() === code.replace(/-/g, ' ') || msg.toLowerCase() === code) return 'unexpected server error';
  return msg;
}

function _reviewEquipNoteTextarea(localId) {
  return document.querySelector(`#review-ticket-body textarea[data-eq-note-id="${localId}"]`);
}

async function handleReviewCleanupNotes(btnEl) {
  // Both buttons run the same job — never twice at once on the same ticket.
  if (_reviewCleanupRunToken !== null && _reviewCleanupRunToken === _reviewLoadToken) return;
  const ticketId = ReviewState.ticketId;
  if (!ticketId) { console.warn('[Billing] Cleanup notes: no ticketId'); return; }
  const loadToken = _reviewLoadToken;
  const stillThisTicket = () => loadToken === _reviewLoadToken && ReviewState.ticketId === ticketId;

  // What is on screen is the source — including typing that has not blurred yet.
  const jobs = [];
  const generalTa = document.getElementById('review-ticket-notes');
  const generalText = generalTa ? generalTa.value
    : (ReviewState.headerEdits.notes !== undefined ? ReviewState.headerEdits.notes
      : (ReviewState.ticket?.notes || ReviewState.ticket?.workPerformed || ''));
  if (generalText.trim()) jobs.push({ kind: 'general', sent: generalText });
  // Only the equipment notes the biller can see (the same filter the section
  // renders with) — never rewrite text that is not on screen.
  for (const line of ReviewState.equipmentLines) {
    if (!assetTypeGetsNotes(line.assetType)) continue;
    const ta = _reviewEquipNoteTextarea(line._localId);
    const text = ta ? ta.value : (line.notes || '');
    if (!text.trim()) continue;
    jobs.push({ kind: 'equip', line, sent: text });
  }
  if (jobs.length === 0) { window.showToast?.('No notes to clean up.', 'info'); return; }

  _reviewCleanupRunToken = loadToken;
  const total = jobs.length;
  let finished = 0;
  const progress = () => _setReviewCleanupButtons(
    total === 1 ? 'Cleaning…' : `Cleaning ${Math.min(finished + 1, total)} of ${total}…`, true);
  progress();

  const cleanupFn = httpsCallable(functions, 'cleanupShiftNotes');
  const tally = { changed: 0, unchanged: 0, refused: 0, edited: 0, failed: 0, reasons: [] };

  const applyResult = (job, cleaned) => {
    if (job.kind === 'general') {
      const ta = document.getElementById('review-ticket-notes');
      const current = ta ? ta.value : ReviewState.headerEdits.notes;
      if (current !== undefined && current !== job.sent) return false; // edited mid-flight
      if (ta) { ta.value = cleaned; ta.dispatchEvent(new Event('input', { bubbles: true })); }
      ReviewState.headerEdits.notes = cleaned;
      ReviewState.headerEdits.workPerformed = cleaned;
      return true;
    }
    if (!ReviewState.equipmentLines.includes(job.line)) return false; // line removed mid-flight
    const ta = _reviewEquipNoteTextarea(job.line._localId);
    const current = ta ? ta.value : (job.line.notes || '');
    if (current !== job.sent) return false; // edited mid-flight
    if (ta) {
      ta.value = cleaned;
      ta.style.height = 'auto'; ta.style.height = ta.scrollHeight + 'px';
    }
    updateReviewEquipmentNotes(job.line._localId, cleaned); // sets line.notes + dirty + math warning
    return true;
  };

  const runOne = async (job) => {
    try {
      const result = await cleanupFn({ notes: job.sent });
      if (!stillThisTicket()) return;
      const cleaned = typeof result.data?.cleaned === 'string' ? result.data.cleaned : '';
      if (result.data?.rejected === true) { tally.refused++; return; }
      if (!cleaned.trim() || cleaned.trim() === job.sent.trim()) { tally.unchanged++; return; }
      if (applyResult(job, cleaned)) tally.changed++; else tally.edited++;
    } catch (err) {
      console.error('[Billing] Clean Up Notes call failed:', err);
      tally.failed++;
      const reason = _reviewCleanupErrorReason(err);
      if (!tally.reasons.includes(reason)) tally.reasons.push(reason);
    } finally {
      finished++;
      if (stillThisTicket() && finished < total) progress();
    }
  };

  try {
    const queue = jobs.slice();
    // Stop dequeuing once the ticket is gone — no point spending calls on it.
    const worker = async () => { while (queue.length && stillThisTicket()) await runOne(queue.shift()); };
    await Promise.all(Array.from({ length: Math.min(_REVIEW_CLEANUP_CONCURRENCY, total) }, worker));
  } finally {
    if (_reviewCleanupRunToken === loadToken) _reviewCleanupRunToken = null;
    if (stillThisTicket()) _setReviewCleanupButtons('', false);
  }
  if (!stillThisTicket()) return; // modal closed / another ticket opened — say nothing

  if (tally.changed > 0) ReviewState.dirty = true;
  const n = (k, word = 'note') => `${k} ${word}${k === 1 ? '' : 's'}`;
  const extras = [];
  if (tally.refused) extras.push(`${n(tally.refused)} left as written — the cleanup would have dropped details`);
  if (tally.edited) extras.push(`${n(tally.edited)} you edited while cleaning ${tally.edited === 1 ? 'was' : 'were'} kept as typed`);
  const extraText = extras.length ? ` ${extras.join('. ')}.` : '';

  if (tally.failed === total) {
    window.showToast?.(`Couldn't clean up notes: ${tally.reasons.join('; ')}`, 'error');
  } else if (tally.failed > 0) {
    window.showToast?.(`Cleaned ${tally.changed} of ${total} notes. ${n(tally.failed)} couldn't be cleaned: ${tally.reasons.join('; ')}.${extraText}`
      + (tally.changed ? ' Review, then Save.' : ''), 'warning');
  } else if (tally.changed > 0) {
    window.showToast?.(`Cleaned up ${n(tally.changed)}.${extraText} Review, then Save.`, 'success');
  } else if (tally.refused || tally.edited) {
    window.showToast?.(`No notes changed.${extraText}`, 'info');
  } else {
    window.showToast?.('Notes already look clean — nothing to change.', 'info');
  }
}

// TASK-9872 — un-invoicing a ticket used to leave its revenue row counting in
// BI / P&L / the connector forever. Mark every row for the ticket superseded
// (never delete: a paid mark rides on it, and re-invoicing replaces the row —
// recordRevenueEntry's full set clears `voided`). The readers ALSO drop rows
// whose ticket is no longer invoiced (functions/src/shared/revenueEntryCanon.js),
// so a failure here is logged and never blocks the un-invoice.
async function _voidRevenueRowsForTicket(tenantId, ticketId, reason, extra) {
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'revenueEntries'), where('ticketId', '==', ticketId)));
    if (snap.empty) return true;
    const b = writeBatch(db);
    const now = Timestamp.now();
    // `extra` (TASK-4767 S2): only keys on the rules' revenueEntries client
    // allowlist (paid / paidAt) — any other key fails the whole batch.
    snap.docs.forEach(d => b.update(d.ref, {
      voided: true, voidedAt: now, voidReason: reason,
      voidedBy: window.AppState?.user?.uid || 'unknown',
      ...(extra || {})
    }));
    await b.commit();
    return true;
  } catch (err) {
    console.warn('[Billing] Could not mark revenue rows superseded for', ticketId, err);
    window.logError?.(err, { module: 'billing', operation: 'VOID_REVENUE_ROWS', ticketId, reason });
    return false;
  }
}

// TASK-9942 — DEMO ONLY. Every other tenant's revenue row is written by the
// server inside the invoice stamp (ticketGateway markInvoiced -> revenueEntries/
// tkt_<ticketId>, amount = the server-recomputed invoice total), and
// firestore.rules refuses a client create there. Demo's stamps never reach the
// server (_invoiceStampUsesGateway), so this browser writer stays for it alone.
async function recordRevenueEntry(tenantId, ticket, invoiceNumber) {
  if (_invoiceStampUsesGateway(tenantId)) return true; // the server already wrote it
  ticket = _emitTicketMoney(ticket); // TASK-9646 S3b — demo revenue row books the stored header / frozen money, never the live mirror
  try {
    const revenueData = {
      ticketId: ticket.id,
      invoiceNumber,
      amount: ticket.grandTotal || 0,
      customerId: ticket.customerId || null,
      customerName: ticket.customerName || 'Unknown',
      jobId: ticket.jobId || null,
      jobName: ticket.jobName || ticket.jobTitle || null,
      workDate: ticket.workDate || ticket.date || null,
      invoicedAt: Timestamp.now(),
      invoicedBy: window.AppState?.user?.uid || 'unknown',
      createdAt: Timestamp.now()
    };
    if (ticket.taxEnabled) {
      revenueData.subtotal = ticket.subtotal || 0;
      revenueData.taxAmount = ticket.taxAmount || 0;
      revenueData.taxRate = ticket.taxRate || 0;
    }
    // Build revenue breakdown from ticket lines (per-category amounts).
    // TASK-511: exclude the SAME lines the invoice omits (manual per-line
    // override OR per-customer hide-by-default) via the shared predicate, and
    // reduce recognized revenue to the actually-invoiced amount so BI matches
    // what the customer was billed — never recognize revenue we didn't invoice.
    try {
      const _exTypes = getInvoiceExcludeLineTypes(ticket.customerId);
      const tlSnap = await getDocs(collection(db, 'tenants', tenantId, 'tickets', ticket.id, 'ticketLines'));
      const catTotals = {};
      let _excluded = 0;
      for (const tlDoc of tlSnap.docs) {
        const tl = tlDoc.data();
        if (!isLineInvoiced(tl, _exTypes)) {
          _excluded += (tl.type === 'DOWNTIME' ? -1 : 1) * (tl.lineAmount || 0);
          continue;
        }
        const cat = tl.revenueCategory || 'Uncategorized';
        catTotals[cat] = (catTotals[cat] || 0) + (tl.lineAmount || 0);
      }
      if (_excluded !== 0) {
        revenueData.amount = parseFloat(((revenueData.amount || 0) - _excluded).toFixed(2));
        // Keep the stored subtotal consistent with the reduced amount on
        // tax-enabled rows (excluded lines are subtracted pre-tax, mirroring the
        // invoice render). taxAmount is left as-billed — recomputing tax on the
        // reduced base is out of scope and matches the prior exclusion behavior.
        if (ticket.taxEnabled && typeof revenueData.subtotal === 'number') {
          revenueData.subtotal = parseFloat((revenueData.subtotal - _excluded).toFixed(2));
        }
      }
      const breakdown = Object.entries(catTotals).map(([category, amount]) => ({ category, amount: parseFloat(amount.toFixed(2)) }));
      if (breakdown.length > 0) {
        revenueData.revenueBreakdown = breakdown;
        // Keep flat list for backward compat with BI queries
        revenueData.revenueCategories = [...new Set(breakdown.map(b => b.category).filter(c => c !== 'Uncategorized'))];
      }
    } catch (_) { /* best-effort — don't block invoicing if this fails */ }
    // TASK-950 — IDEMPOTENT revenue entry: exactly ONE entry per ticket, always
    // fresh. uninvoiceTicket clears the invoiced flag but never deletes
    // revenueEntries, so invoice → uninvoice → re-invoice used to create a SECOND
    // entry and double revenue in BI/P&L. Query any existing entries for this
    // ticket, carry a recorded payment forward (so re-invoicing never loses a
    // logged payment), delete them all, then add the single fresh entry. This
    // self-heals pre-existing duplicates too.
    // COD fix: delete-then-add is now ONE writeBatch (atomic) — a crash/network
    // loss between the deletes and the add would otherwise lose the BI revenue
    // entry entirely. The batch deletes all existing entries for this ticket and
    // sets the single fresh one in one commit.
    //
    // TASK-9872 — the query-then-batch above is NOT atomic: two overlapping calls
    // for one ticket (a double-clicked grouped invoice — INV-202608-078 wrote 4
    // tickets twice, 292ms apart — two tabs, a retry) each saw "no rows yet" and
    // each added its own auto-id row. The live row now has a DETERMINISTIC id
    // (`tkt_<ticketId>`, functions/src/shared/revenueEntryCanon.js rcDocId), so
    // every writer for a ticket lands on the same doc: a race overwrites, it can
    // no longer add. Re-invoice replaces the same doc (one row per ticket per
    // live invoice); legacy auto-id rows for the ticket are still deleted here.
    const _revCol = collection(db, 'tenants', tenantId, 'revenueEntries');
    const _liveRef = doc(_revCol, 'tkt_' + ticket.id);
    try {
      const _existingSnap = await getDocs(query(_revCol, where('ticketId', '==', ticket.id)));
      let _paidCarry = null;
      _existingSnap.forEach(d => {
        const ex = d.data();
        if (ex.paid && !_paidCarry) {
          _paidCarry = { paidAt: ex.paidAt || null, paymentMethod: ex.paymentMethod || null, paymentRef: ex.paymentRef || null };
        }
      });
      if (_paidCarry) {
        revenueData.paid = true;
        if (_paidCarry.paidAt) revenueData.paidAt = _paidCarry.paidAt;
        if (_paidCarry.paymentMethod) revenueData.paymentMethod = _paidCarry.paymentMethod;
        if (_paidCarry.paymentRef) revenueData.paymentRef = _paidCarry.paymentRef; // TASK-9735 f/u
      }
      const _batch = writeBatch(db);
      _existingSnap.docs.forEach(d => {
        if (d.id !== _liveRef.id) _batch.delete(doc(db, 'tenants', tenantId, 'revenueEntries', d.id));
      });
      _batch.set(_liveRef, revenueData); // TASK-9872 — full replace of the ticket's ONE row (also clears a `voided` mark)
      await _batch.commit();
    } catch (dedupeErr) {
      console.warn('[Billing] Error recording revenue entry (batch):', dedupeErr);
      // Fallback: at least record the entry so BI isn't silently missing revenue.
      // TASK-4595 — this inner catch used to swallow the fallback's own failure
      // and the function still `return true`d, so BOTH the batch AND the fallback
      // could fail and the caller was told the revenue landed. The awaits added
      // at the call sites would have inherited that lie. Now the fallback's
      // outcome decides the return value (COD Code Reviewer).
      try {
        // TASK-9872 — same deterministic doc, so a fallback can never add a second row.
        await setDoc(_liveRef, revenueData);
      } catch (fallbackErr) {
        console.warn('[Billing] Revenue fallback add ALSO failed:', fallbackErr);
        window.logError?.(fallbackErr, { module: 'billing', operation: 'RECORD_REVENUE_ENTRY_FALLBACK', ticketId: ticket?.id, invoiceNumber });
        return false;
      }
    }
    return true;
  } catch (err) {
    // TASK-1346 (integrated-diff COD ❌) — this swallows, so a caller that
    // relied on it THROWING would report a billed ticket as fully successful
    // while BI silently ran short. It still never blocks invoicing; it just
    // tells the truth about whether the revenue row landed.
    console.warn('[Billing] Error recording revenue entry:', err);
    // TASK-4595 — these two lines sat BELOW the `return false` and were therefore
    // unreachable. When TASK-1346 added the return it went in above the existing
    // logging instead of below it, which silently killed both the breadcrumb and
    // the only user-facing warning. So every revenue failure since then has been
    // invisible in the logs AND to the biller — which is exactly why the two
    // fire-and-forget call sites below went unnoticed for so long.
    window.logError?.(err, { module: 'billing', operation: 'RECORD_REVENUE_ENTRY', ticketId: ticket?.id, invoiceNumber });
    return false;
  }
}

// ============================================
// REVIEW TICKET (Mark as Ready)
// ============================================
async function reviewTicket(id) {
  try {
    // TASK-1548 — a priced equipment line with 0 hours blocks Ready. Authoritative
    // (fresh line read), not just the hidden button: every caller funnels here.
    if (!(await _zeroHourGate(id, 'mark this ticket Ready'))) return;
    // TASK-1405 — the mirror shape: equipment with real hours and NO rate. Same
    // fresh-read gate, same transition.
    if (!(await _unpricedGate(id, 'mark this ticket Ready'))) return;
    // TASK-1615-B — a Per Mile quantity auto-filled off a bad route coordinate
    // blocks Ready the same way. Fresh line + job read; cheap-exits on any ticket
    // with no priced per-mile line.
    if (!(await _routeMileageGate(id, 'mark this ticket Ready'))) return;
    // TASK-1572 Slices 3 + 3b — no billable line for a customer who is billed for
    // work, or a customer-required field (PO) left empty. Same fresh-read shape.
    if (!(await _ticketCompletenessGate(id, 'mark this ticket Ready'))) return;
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), {
      status: 'ready',
      reviewedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    window.showToast?.('Ticket marked as ready', 'success');
    // Refresh the detail view
    viewTicket(id);
  } catch (error) {
    console.error('[Billing] Error reviewing ticket:', error);
    window.showToast?.('Error updating ticket', 'error');
  }
}

// ============================================
// TICKET NOTES
// ============================================
async function saveTicketNotes(ticketId, notes) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      notes: notes.trim(),
      workPerformed: notes.trim(),
      ..._notesEditStamp('billing_ticket_view'), // TASK-10036 (a)
      updatedAt: Timestamp.now()
    });
  } catch (err) {
    console.error('[Billing] Error saving ticket notes:', err);
  }
}

// TASK-4621 (COD BINDING 2) — display fallback for shift notes stranded in
// `notes` with clockOutNotes EMPTY (114 of 451 TITS entries since 7/27: the
// jobboard clock-out path pre-TASK-4621 and gateway manual/office entries wrote
// `notes` only, and both renderers below filter on clockOutNotes, dropping the
// entry entirely). A blanket `clockOutNotes || notes` is NOT safe on this
// customer-invoiced surface: `notes` is also written by clock-IN flows
// (clock.js kiosk, dispatch PIN clock-in, jobboard standalone clock-in) and
// Time Clock edits, which can carry internal/clock-in context. So the fallback
// is gated to the audited driver-voice sources only; every other source
// renders exactly as before (clockOutNotes or nothing).
// TASK-4622 (COD Blast + Adversary): 'gateway:editClockEntry' removed — nothing
// ever persists that literal onto entry.source (it exists only as an audit
// changeSource), so the entry was dead code AND a client-stampable source value
// would have forced fallback display of arbitrary `notes` text.
const SHIFT_NOTE_FALLBACK_SOURCES = new Set(['jobboard_pin', 'jobboard_switch', 'gateway:manualEntry']);
function shiftNoteDisplayText(e) {
  if (e.clockOutNotes) return e.clockOutNotes;
  if (e.notes && SHIFT_NOTE_FALLBACK_SOURCES.has(e.source || '')) return e.notes;
  return '';
}

// TASK-9760 follow-up 2 — which clock entries' notes may show on this ticket.
// A driver-PIN session or clock-only member can point its OWN completed entry's
// ticketId at ANY ticket, so `where('ticketId', '==', T)` alone would print a
// forged entry's note on another customer's ticket. Same binding the crew banner
// uses (_crewEntryBoundToTicket: same real jobId OR same real customerId).
// `ticket` = the ticket as it was opened/loaded (jobId + customerId). No ticket
// = nothing is trusted. Pure; sorted by clock-in.
function _shiftNoteEntriesForTicket(entries, ticket) {
  if (!Array.isArray(entries) || !ticket) return [];
  return entries
    .filter(e => _crewEntryBoundToTicket(e, ticket))
    .filter(e => shiftNoteDisplayText(e)) // TASK-4621 — was e.clockOutNotes, which dropped stranded notes entirely
    .sort((a, b) => {
      const aTime = a.clockIn?.toDate?.() || new Date(0);
      const bTime = b.clockIn?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
}

// TASK-10047 — internal office notes (tickets/{id}/officeNotes). Written only by
// an approved Claude connector request (propose_ticket_fix, Admin SDK); read here
// by Billing / the owner (firestore.rules tkNoteReader — drivers never). The
// print, PDF, invoice and email paths never read this subcollection. The block
// stays hidden unless there is at least one note (and on a denied read).
function _officeNotesSectionHtml(listId, kind) {
  const wrapCls = kind === 'review' ? 'review-section' : 'detail-section';
  const titleCls = kind === 'review' ? 'review-section-title' : 'detail-section-title';
  return `
    <div class="${wrapCls}" id="${listId}-wrap" style="margin-top:8px;display:none;">
      <div class="${titleCls}">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">sticky_note_2</span>
        Office Notes <span style="font-size:0.75rem;font-weight:400;color:var(--text-secondary);">(internal · never printed · not shown to drivers)</span>
      </div>
      <div id="${listId}" style="margin-top:8px;"></div>
    </div>
  `;
}

async function loadOfficeNotesForTicket(ticketId, listId) {
  const wrap = document.getElementById(`${listId}-wrap`);
  const list = document.getElementById(listId);
  if (!wrap || !list || !ticketId) return;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'officeNotes'));
    const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
    if (!notes.length) return;
    list.innerHTML = notes.map(n => {
      const when = n.createdAt?.toDate?.();
      const whenTxt = when ? when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
      return `
        <div style="padding:8px 10px;background:var(--bg-secondary);border-radius:6px;margin-bottom:6px;">
          <div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(n.authorName || 'Someone')}${whenTxt ? ` · ${escapeHtml(whenTxt)}` : ''}</div>
          <div style="font-size:0.85rem;color:var(--text-primary);white-space:pre-wrap;margin-top:2px;">${escapeHtml(n.text || '')}</div>
        </div>`;
    }).join('');
    wrap.style.display = '';
  } catch (_) {
    // No Billing access (rules deny) or offline: the block stays hidden.
  }
}

async function loadShiftNotesForTicket(ticketId, containerId, ticket) {
  const container = document.getElementById(containerId || 'shift-notes-list');
  if (!container) return;

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const entriesSnap = await getDocs(query(
      collection(db, 'tenants', tenant, 'clockEntries'),
      where('ticketId', '==', ticketId)
    ));

    const entries = _shiftNoteEntriesForTicket(entriesSnap.docs.map(d => ({ id: d.id, ...d.data() })), ticket);

    if (entries.length === 0) {
      container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;font-style:italic;">No shift notes recorded</div>';
      return;
    }

    container.innerHTML = entries.map(e => {
      const clockIn = e.clockIn?.toDate?.() || new Date();
      const clockOut = e.clockOut?.toDate?.() || null;
      // TASK-9189 — shift-note headers printed a time-only range, so an
      // overnight shift read backwards here too. Shared formatter lives in
      // sharedFormat.js so this can never drift from the payroll panel.
      const timeRange = clockOut
        ? window.Fmt.shiftSpan(clockIn, clockOut).rangeHtml
        : window.Fmt.clockTime(clockIn) + ' - active';
      const reason = e.completionReason === 'job_switch' ? ' (job switch)' : '';
      return `
        <div style="padding:8px 10px;background:var(--bg-secondary);border-radius:6px;margin-bottom:6px;">
          <div style="font-size:0.75rem;color:var(--text-secondary);">
            <strong style="color:var(--text-primary);">${escapeHtml(e.personName || 'Unknown')}</strong> &middot; ${timeRange}${reason}
          </div>
          <div style="font-size:0.8rem;color:var(--text-primary);margin-top:4px;white-space:pre-wrap;">${escapeHtml(shiftNoteDisplayText(e))}</div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[Billing] Error loading shift notes:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;">Error loading shift notes</div>';
  }
}

// TASK-1287 — EDITABLE shift-notes renderer for the Edit/Review Ticket modal.
// Renders each linked clockEntry's driver clock-out note (clockOutNotes) in a
// per-entry textarea keyed by entryId. On Save Ticket, collectEditedShiftNotesForReview
// diffs each textarea vs. the value captured here and pushEditedShiftNotesForReview
// calls the gated editClockNotes gateway op per changed entry. Kept SEPARATE from the read-only
// loadShiftNotesForTicket (which still serves the View Ticket surface unchanged).
//
// readOnly (invoiced/archived/locked) disables every textarea HERE — the async
// render lands AFTER openReviewTicket's synchronous input-disable sweep, so this
// renderer cannot rely on that sweep and must disable itself. The demo sandbox
// (no server gateway) is likewise rendered read-only. Server-side editClockNotes
// re-enforces the ticket read-only state regardless of the UI.
let ReviewShiftNoteOriginals = null;
// TASK-10036 — entryId → person name, so a Save-time warning can say whose
// shift note is being cleared.
let ReviewShiftNoteNames = {};
// Which ticket the originals map above describes. Sent with every note write so
// the server can bind the entry to this ticket, and used to detect that the modal
// has moved on to a different ticket mid-save.
let ReviewShiftNoteTicketId = null;
async function loadEditableShiftNotesForReview(ticketId, containerId, readOnly, ticket) {
  const container = document.getElementById(containerId || 'review-shift-notes-list');
  if (!container) return;
  ReviewShiftNoteOriginals = {};
  ReviewShiftNoteNames = {}; // TASK-10036
  ReviewShiftNoteTicketId = ticketId || null;
  const isDemo = BillingState.tenantId === 'demo-tenant';
  const tenant = isDemo ? 'demo' : BillingState.tenantId;
  const disabled = readOnly === true || isDemo;
  try {
    const entriesSnap = await getDocs(query(
      collection(db, 'tenants', tenant, 'clockEntries'),
      where('ticketId', '==', ticketId)
    ));
    const entries = _shiftNoteEntriesForTicket(entriesSnap.docs.map(d => ({ id: d.id, ...d.data() })), ticket);

    if (entries.length === 0) {
      container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;font-style:italic;">No shift notes recorded</div>';
      return;
    }

    container.innerHTML = entries.map(e => {
      // TASK-4621 — the originals map holds the TRUE clockOutNotes ('' when the
      // textarea below is fallback-seeded from stranded `notes`), so an
      // UNTOUCHED Save Ticket still diffs as a change and converges the note
      // into clockOutNotes via editClockNotes — the desired convergence. The
      // server skips the Edited provenance stamp on that verbatim promotion, so
      // no false "Edited" badge lands on text the staffer never touched.
      ReviewShiftNoteOriginals[e.id] = e.clockOutNotes || '';
      ReviewShiftNoteNames[e.id] = e.personName || '';
      const noteText = shiftNoteDisplayText(e);
      const clockIn = e.clockIn?.toDate?.() || new Date();
      const clockOut = e.clockOut?.toDate?.() || null;
      // TASK-9189 — shift-note headers printed a time-only range, so an
      // overnight shift read backwards here too. Shared formatter lives in
      // sharedFormat.js so this can never drift from the payroll panel.
      const timeRange = clockOut
        ? window.Fmt.shiftSpan(clockIn, clockOut).rangeHtml
        : window.Fmt.clockTime(clockIn) + ' - active';
      const reason = e.completionReason === 'job_switch' ? ' (job switch)' : '';
      const editedBadge = e.clockOutNotesEditedAt
        ? `<span title="Edited after clock-out${e.clockOutNotesEditedBy ? ' by ' + escapeHtml(String(e.clockOutNotesEditedBy)) : ''}" style="font-size:0.65rem;font-weight:600;color:var(--action-warning,#B45309);border:1px solid var(--action-warning,#B45309);border-radius:8px;padding:0 5px;margin-left:6px;">Edited</span>`
        : '';
      return `
        <div style="padding:8px 10px;background:var(--bg-secondary);border-radius:6px;margin-bottom:6px;">
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:4px;">
            <strong style="color:var(--text-primary);">${escapeHtml(e.personName || 'Unknown')}</strong> &middot; ${timeRange}${reason}${editedBadge}
          </div>
          <textarea data-entry-id="${escapeHtml(e.id)}" rows="2" ${disabled ? 'disabled' : ''}
            aria-label="Shift note for ${escapeHtml(e.personName || 'this shift')}"
            style="width:100%;box-sizing:border-box;min-width:0;font-size:0.8rem;padding:6px 8px;border:1px solid var(--border-color,#CBD5E1);border-radius:6px;resize:vertical;color:var(--text-primary);background:var(--bg-primary,#fff);${disabled ? 'opacity:0.6;' : ''}">${escapeHtml(noteText)}</textarea>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[Billing] Error loading editable shift notes:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;">Error loading shift notes</div>';
  }
}

// TASK-1287 — collect edited shift notes SYNCHRONOUSLY, while the Review modal is
// still on screen and still showing THIS ticket.
//
// This is deliberately split from the push below. saveReviewTicket hides the modal,
// calls resetReviewState(), and re-opens the read-only view BEFORE it gets to the
// note save; reading the textareas at that point means reading DOM that the flow
// has already torn down and that a second ticket-open could have re-rendered
// underneath us (loadEditableShiftNotesForReview resets the module-level
// ReviewShiftNoteOriginals map on every render, so a re-render mid-save would
// diff the new ticket's notes against the old ticket's originals — the exact
// cross-attribution shape TASK-1327 exists to prevent). Snapshotting the entryId,
// the value, AND the ticketId up front makes the payload immune to anything the
// rest of the save does to the DOM.
//
// Returns { ticketId, changed: [{ entryId, val }] } — `changed` is [] when there
// is nothing to send. Never throws.
function collectEditedShiftNotesForReview() {
  const empty = { ticketId: null, changed: [] };
  try {
    if (BillingState.tenantId === 'demo-tenant') return empty; // no server gateway on the local demo
    const container = document.getElementById('review-shift-notes-list');
    if (!container || !ReviewShiftNoteOriginals) return empty;
    const ticketId = ReviewShiftNoteTicketId;
    if (!ticketId) return empty;
    const textareas = Array.from(container.querySelectorAll('textarea[data-entry-id]'));
    const changed = [];
    for (const ta of textareas) {
      if (ta.disabled) continue;
      const entryId = ta.getAttribute('data-entry-id');
      if (!entryId) continue;
      const orig = ReviewShiftNoteOriginals[entryId] ?? '';
      const val = ta.value ?? '';
      if (val !== orig) changed.push({ entryId, val });
    }
    return { ticketId, changed };
  } catch (e) {
    console.error('[Billing] collectEditedShiftNotesForReview error:', e);
    return empty;
  }
}

// TASK-10036 (a) — who + which screen changed the invoice notes, written in
// the SAME update as the notes. The server's ticket_notes_changed audit row
// (onTicketHeaderTotals) trusts it only when notesEditedAt moved in that write.
// integ/2114 — what a Review save writes to notes/workPerformed. Pure (tested
// in tests/task10036-review-notes-race.unit.cjs). Stored text is read the way
// clock.js saveInvoiceNotes reads it: notes when non-blank, else workPerformed.
//   unchanged since open -> the biller's edit, as typed
//   changed since open   -> merge(stored, open, edit): the newer line survives
//   changed, no merge fn -> refuse (throw) rather than overwrite
// No baseline (should not happen: the TASK-10036 guard drops the edit) merges
// against an empty baseline, which can add but never delete.
function _reviewNotesForSave({ storedNotes, storedWorkPerformed, open, edit, merge }) {
  const stored = (typeof storedNotes === 'string' && storedNotes.trim())
    ? storedNotes
    : (typeof storedWorkPerformed === 'string' ? storedWorkPerformed : '');
  const text = String(edit == null ? '' : edit);
  const base = (open === null || open === undefined) ? null : String(open);
  if (base !== null && stored === base) return { text, merged: false };
  if (stored === text) return { text, merged: false };
  if (typeof merge !== 'function') {
    throw new Error('The work notes changed while this ticket was open. Close and reopen it, then make your edit again.');
  }
  const m = merge(stored, base === null ? '' : base, text);
  return { text: m.text, merged: m.text !== text, foreign: m.foreign || 0 };
}

function _notesEditStamp(via) {
  return {
    // A uid, never an email: every driver session can read a ticket doc. The
    // server resolves it to a name/email for the audit row.
    notesEditedBy: window.AppState?.user?.uid ? `uid:${window.AppState.user.uid}` : 'unknown',
    notesEditedVia: String(via || 'billing'),
    notesEditedAt: Timestamp.now(),
  };
}

// TASK-10036 (#100918, 10/22) — has the work-notes box moved away from what it
// opened with? A save writes `notes` only when this is true, so a box that was
// never touched (or was disabled, or never rendered) cannot overwrite stored
// notes with whatever it happened to hold.
function _reviewNotesBoxEdited(val) {
  if (ReviewState.headerEdits.notes !== undefined) return true;
  const open = ReviewState._openNotesText;
  if (open === null || open === undefined) return false; // no baseline: never write blind
  return String(val ?? '') !== String(open);
}

// TASK-10036 — the things a Save is about to throw away that nobody gets back
// from the ticket afterwards: stored work notes cleared, a driver's shift note
// cleared, and lines removed whose time-clock entry still points at this ticket
// (the hours then bill on NO ticket unless someone adds them to another one).
// Pure: every input is passed in. Returns plain sentences, [] = nothing to ask.
function _reviewDestructiveSaveItems({ notesEdit, openNotes, shiftChanged, shiftOriginals, shiftNames, removedLines, boundEntryIds }) {
  const items = [];
  const was = (s) => {
    const t = String(s || '').trim().replace(/\s+/g, ' ');
    return t.length > 80 ? `${t.slice(0, 80)}…` : t;
  };
  if (notesEdit !== undefined && !String(notesEdit).trim() && String(openNotes || '').trim()) {
    items.push(`Clear the work notes (they said: "${was(openNotes)}").`);
  }
  for (const c of (shiftChanged || [])) {
    const orig = (shiftOriginals || {})[c.entryId] || '';
    if (String(c.val || '').trim() || !String(orig).trim()) continue;
    const who = (shiftNames || {})[c.entryId] || 'a crew member';
    items.push(`Clear ${who}'s shift note (it said: "${was(orig)}").`);
  }
  // null boundEntryIds = the clock read did not load: assume still bound (warn).
  const bound = (id) => !boundEntryIds || boundEntryIds.has(String(id));
  const byEntry = new Map();
  for (const r of (removedLines || [])) {
    if (!r || !r.clockEntryId || !bound(r.clockEntryId)) continue;
    const k = String(r.clockEntryId);
    if (!byEntry.has(k)) byEntry.set(k, []);
    byEntry.get(k).push(r);
  }
  for (const rows of byEntry.values()) {
    const crew = rows.find(r => r.type === 'EMPLOYEE');
    const equip = rows.filter(r => r.type !== 'EMPLOYEE');
    const parts = [];
    if (crew) parts.push(`${crew.label || 'a crew member'} (${Number(crew.hours) || 0} h)`);
    for (const e of equip) parts.push(`${e.label || 'equipment'} (${Number(e.hours) || 0} h)`);
    const whose = crew ? `${crew.label || 'This person'}'s time-clock entry` : 'The time-clock entry for this equipment';
    items.push(`Remove ${parts.join(' and ')}. ${whose} still points at this ticket, so these hours will not bill on any ticket unless they are on another one. Payroll hours do not change.`);
  }
  return items;
}

// TASK-1287 — persist the notes collected above.
// Best-effort + independent of the ticket write: a per-note failure toasts but
// never rolls back the ticket save (notes are secondary metadata), and this fn
// NEVER throws (so it can't turn a good ticket save into an "Error saving ticket"
// toast). Only CHANGED entries are sent, each through the gated editClockNotes
// gateway op (which self-enforces module authz, the entry↔ticket binding, and the
// ticket read-only state server-side).
//
// One call PER ENTRY, carrying that entry's own id — a note is one person's own
// words (TASK-1327), so there is no batched/shared write path here by design.
async function pushEditedShiftNotesForReview(collected) {
  try {
    const ticketId = collected?.ticketId;
    const changed = collected?.changed || [];
    if (!ticketId || changed.length === 0) return;

    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 30000 });
    let failed = 0;
    let diverged = 0;
    for (const c of changed) {
      try {
        const res = await fn({
          op: 'editClockNotes',
          tenantId: BillingState.tenantId,
          payload: { entryId: c.entryId, ticketId, clockOutNotes: c.val }
        });
        if (!res?.data?.ok) { failed++; continue; }
        // The server preserved a newer unsaved draft the person had typed rather
        // than overwriting it with this correction (their words stay theirs). Say
        // so plainly — their next save can supersede what was just written here.
        if (res?.data?.updated?.draftDiverged) diverged++;
        // Keep the originals map in step ONLY if it still describes this ticket —
        // otherwise the modal has moved on and the map belongs to another ticket.
        if (ReviewShiftNoteTicketId === ticketId && ReviewShiftNoteOriginals) {
          ReviewShiftNoteOriginals[c.entryId] = c.val;
        }
      } catch (e) {
        failed++;
        console.error('[Billing] shift-note save failed for entry', c.entryId, e);
      }
    }
    if (failed > 0) {
      window.showToast?.(`${failed} shift note${failed > 1 ? 's' : ''} could not be saved — check your access or the ticket's status.`, 'error');
    } else {
      window.showToast?.(`Shift note${changed.length > 1 ? 's' : ''} updated`, 'success');
    }
    if (diverged > 0) {
      window.showToast?.(`${diverged} of those shift${diverged > 1 ? 's' : ''} has a newer note the person hasn't submitted yet — their version may replace your edit when they save.`, 'warning');
    }
  } catch (e) {
    console.error('[Billing] pushEditedShiftNotesForReview error:', e);
  }
}

// ============================================
// TASK-9412 — REVIEW TICKET TIME EDIT → TIME CLOCK
// ============================================
// Jess (TITS office, 2026-09-28): "When I edit a time entry for an employee in
// the ticket, that it connects to their time clock and updates that as well."
// Before this, an EMPLOYEE line's in/out edited here wrote the ticket line ONLY;
// the clock entry — payroll — kept the old times and nothing said so. Now every
// moved EMPLOYEE window whose line names its clockEntryId goes through the
// existing gateway op (mutateTicketAndClockEntry → editClockEntry) with
// `fromTicketId`, which:
//   • runs the SAME closed-book gate the Workforce edit does (invoiced / invoice
//     markers / archived / locked / sent / qbSynced / deleted / split child all
//     refuse, inside the transaction, zero mutation) — only the DRIVER lock is
//     waived, because the biller is the one editing;
//   • refuses an entry billed on a DIFFERENT ticket (stray-line binding);
//   • writes clockEntryAuditLog before/after, source
//     gateway:editClockEntry:ticketReview;
//   • cascades the new window to the EMPLOYEE line and to EQUIPMENT lines still
//     on the old window (TASK-1654), so the money follows the hours.
// It runs BEFORE the ticket write. A refusal never becomes a silent desync: the
// biller is told which shift and why, and either goes back (nothing written) or
// saves the ticket only on purpose, which is audit-logged as a known mismatch.

function _reviewTsMs(v) {
  if (v == null || v === '') return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d.getTime()) ? null : d.getTime(); }
  if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function _stampReviewLineBaseline(line) {
  if (!line) return;
  line._origFromMs = _reviewTsMs(line.fromTime);
  line._origToMs = _reviewTsMs(line.toTime);
  line._origHours = Number(line.hours) || 0;
}

// TASK-1406 — ticket_edit diffs header fields only, so hours / crew / rates /
// line items were invisible in auditLog. These feed ONE `ticket_line_edit` row
// per save: changed fields only (from/to), normalized so a typed "45" vs a
// stored 45 is not a change. Times as ISO strings, never Timestamps.
const _LINE_AUDIT_FIELDS = ['hours', 'quantity', 'rate', 'rateUnit', 'lineAmount', 'fromTime', 'toTime', 'personId', 'personName', 'assetId', 'assetName', 'description', 'lineItemId', 'notes'];
function _lineAuditPick(l) {
  const o = {};
  for (const k of _LINE_AUDIT_FIELDS) {
    const v = l?.[k];
    if (v == null) continue;
    if (k === 'fromTime' || k === 'toTime') { const ms = _reviewTsMs(v); if (ms != null) o[k] = new Date(ms).toISOString(); }
    else if (k === 'hours' || k === 'quantity' || k === 'rate' || k === 'lineAmount') { const n = Number(v); o[k] = Number.isFinite(n) ? +n.toFixed(4) : String(v); }
    else o[k] = String(v);
  }
  return o;
}
// Re-baseline ONLY the keys a server/clock pass applied, so a biller's own edit to
// any other field of that line (rate, notes, item) still shows in the row.
function _auditRebase(l, keys) {
  if (!l?._auditBase) return;
  const p = _lineAuditPick(l);
  for (const k of keys) { if (k in p) l._auditBase[k] = p[k]; else delete l._auditBase[k]; }
}
const _AUDIT_TIME_KEYS = ['fromTime', 'toTime', 'hours', 'lineAmount'];
// A clock pass moved this line's window. If the biller ALSO changed something else
// on it (rate, unit, qty, notes), its hours/amount are partly hers: re-baseline the
// window only, so the row still shows the dollars that moved.
function _auditRebaseClock(l) {
  const p = _lineAuditPick(l);
  const mine = _LINE_AUDIT_FIELDS.some(k => !_AUDIT_TIME_KEYS.includes(k) && (k in p) && p[k] !== l._auditBase[k]);
  _auditRebase(l, mine ? ['fromTime', 'toTime'] : _AUDIT_TIME_KEYS);
}
// Free text is compared in FULL, cut only in the row (bounded size).
function _auditCut(v) { return typeof v === 'string' ? v.slice(0, 300) : v; }
function _auditCutAll(o) { for (const k in o) o[k] = _auditCut(o[k]); return o; }
// Pure. Changed fields only ({field:{from,to}}), or null when nothing moved. A key
// absent from `after` (null/undefined) is skipped: the save paths never write those
// (updateDoc keeps the stored value), so reporting to:null would be a false trail.
function _ticketLineAuditDiff(before, after) {
  const ch = {};
  for (const k of _LINE_AUDIT_FIELDS) if (k in after && before[k] !== after[k]) ch[k] = { from: _auditCut(before[k] ?? null), to: _auditCut(after[k]) };
  return Object.keys(ch).length ? ch : null;
}
// Pure. Review lines -> {edited, added}. Edited = an existing line whose open-time
// pick (_auditBase) differs; a line with no baseline is skipped (never a guess).
// Removals already write ticket_lines_removed. A clock Add-back is listed under added
// too: ticket_crew_restored keeps the hours as restored, not as finally saved.
function _ticketLineAuditRows(lines) {
  const edited = [], added = [];
  for (const l of lines) {
    if (!l) continue;
    if (l._isNew) { added.push({ type: l.type || null, ..._auditCutAll(_lineAuditPick(l)) }); continue; }
    const ch = l.id && l._auditBase && _ticketLineAuditDiff(l._auditBase, _lineAuditPick(l));
    if (ch) edited.push({ lineId: l.id, type: l.type || null, label: _auditCut(l.personName || l.assetName || l.description || ''), changes: ch });
  }
  return { edited, added };
}

// Pure. Which EMPLOYEE lines had their in/out MOVED in this session and carry a
// clock entry to move with them. A line added in this modal has no shift behind
// it; a line with no clockEntryId (legacy/hand-added) has nothing to update —
// both are left to the ticket write exactly as before. Two lines that name the
// same shift with DIFFERENT new windows cannot both be true: that shift is
// reported as a conflict and never sent.
function _reviewClockSyncPlan(employeeLines) {
  const byEntry = new Map();
  const conflicts = [];
  for (const l of (employeeLines || [])) {
    if (!l || l._isNew || !l.id || !l.clockEntryId) continue;
    if (l.type && l.type !== 'EMPLOYEE') continue; // other types fall back into employeeLines on load
    const fromMs = _reviewTsMs(l.fromTime);
    const toMs = _reviewTsMs(l.toTime);
    const inMoved = fromMs !== (l._origFromMs ?? null);
    const outMoved = toMs !== (l._origToMs ?? null);
    if (!inMoved && !outMoved) continue;
    const item = {
      localId: l._localId, lineId: l.id, entryId: String(l.clockEntryId),
      personName: l.personName || 'this employee',
      fromMs, toMs, oldFromMs: l._origFromMs ?? null, oldToMs: l._origToMs ?? null,
    };
    if (fromMs == null || toMs == null) {
      // A correction from a ticket always carries a full window; the server
      // refuses a half one (it would move only the clock-in).
      conflicts.push({ ...item, reason: fromMs == null ? 'the Clock In time is empty' : 'the Clock Out time is empty' });
      continue;
    }
    const prev = byEntry.get(item.entryId);
    if (prev) {
      if (prev.fromMs !== item.fromMs || prev.toMs !== item.toMs) {
        byEntry.delete(item.entryId);
        conflicts.push({ ...item, reason: 'two lines on this ticket point at the same shift with different times' });
      }
      continue;
    }
    if (!conflicts.some(c => c.entryId === item.entryId)) byEntry.set(item.entryId, item);
  }
  // COD round 2 (Edge Case Hunter) — ANY conflict on a shift withholds that
  // shift entirely, whatever order its lines arrived in.
  const blocked = new Set(conflicts.map(c => c.entryId));
  return { syncs: [...byEntry.values()].filter(s => !blocked.has(s.entryId)), conflicts };
}

// Pure (deps injected). Mirror the server's EQUIPMENT cascade into the modal's
// lines so the ticket write that follows doesn't put the OLD window back. Only a
// line the biller did NOT touch in this session takes it — one she edited keeps
// her numbers (an explicit human edit wins, same as the server skipping a
// hand-adjusted window). Returns { applied: [lineId], kept: [lineId] }.
function _applyClockCascadeToReviewEquipment(equipmentLines, updatedLines, fromMs, toMs, deps) {
  const applied = [];
  const kept = [];
  if (fromMs == null || toMs == null) return { applied, kept };
  for (const u of (updatedLines || [])) {
    const l = (equipmentLines || []).find(x => x && x.id && x.id === u?.lineId);
    if (!l) continue;
    const untouched = _reviewTsMs(l.fromTime) === (l._origFromMs ?? null)
      && _reviewTsMs(l.toTime) === (l._origToMs ?? null)
      && Math.abs((Number(l.hours) || 0) - (Number(l._origHours) || 0)) < 0.005;
    if (!untouched) { kept.push(l.id); continue; }
    l.fromTime = deps.toTs(fromMs);
    l.toTime = deps.toTs(toMs);
    if (!deps.recalc(l)) {
      // Not time-based (should not happen — the server skips per-unit lines);
      // fall back to the server's numbers rather than leaving a stale window.
      if (Number.isFinite(u.hours)) l.hours = u.hours;
      if (Number.isFinite(u.lineAmount)) l.lineAmount = u.lineAmount;
    }
    _stampReviewLineBaseline(l);
    if (l._auditBase) _auditRebaseClock(l); // TASK-1406 — the clock cascade moved the times, not the biller
    applied.push(l.id);
  }
  return { applied, kept };
}

function _gwClockSyncErrorMessage(err) {
  const code = String(err?.code || '');
  const msg = String(err?.message || '').replace(/^Validation failed:\s*/i, '');
  if (/deadline-exceeded/i.test(code)) {
    return 'no answer from the server in time — the time clock may or may not have changed; check the employee\'s time clock';
  }
  if (/unavailable|internal|unknown/i.test(code) && !msg) {
    return 'could not reach the time clock service';
  }
  if (/not-found/i.test(code)) return 'that shift no longer exists on the time clock';
  return msg || 'the time clock service refused the change';
}

// One gateway call per shift (each is its own transaction). Never throws.
async function _syncReviewTimesToClock(ticketId, syncs) {
  const done = [];
  const failed = [];
  if (!ticketId || !syncs?.length) return { done, failed };
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 120000 });
  for (const s of syncs) {
    try {
      const payload = { entryId: s.entryId, clockIn: new Date(s.fromMs).toISOString(), fromTicketId: ticketId };
      if (s.toMs != null) payload.clockOut = new Date(s.toMs).toISOString();
      // Release 1.15.1971 integration COD: the clock entry's window when this
      // Review opened. The server refuses when the time clock no longer shows it
      // (a newer Workforce fix). Not loaded (yet / failed) → nothing sent, no guard.
      const b = ReviewState.clockBaselines instanceof Map ? ReviewState.clockBaselines.get(s.entryId) : null;
      if (b && Number.isFinite(b.inMs)) payload.expectedClockIn = b.inMs;
      if (b && Number.isFinite(b.outMs)) payload.expectedClockOut = b.outMs;
      const res = await fn({ op: 'editClockEntry', tenantId: BillingState.tenantId, payload });
      if (!res?.data?.ok) { failed.push({ ...s, reason: 'the time clock service refused the change' }); continue; }
      done.push({ ...s, updated: res.data.updated || {} });
    } catch (err) {
      console.error('[Billing] ticket→clock time sync failed for entry', s.entryId, err);
      failed.push({ ...s, reason: _gwClockSyncErrorMessage(err) });
    }
  }
  return { done, failed };
}

// Release 1.15.1971 integration COD r2 — each EMPLOYEE line's clock entry
// window as it stands when the Review opens, read from the server. Never throws;
// an entry that can't be read (or answers from cache) simply gets no baseline.
async function _loadReviewClockBaselines(loadToken) {
  const map = new Map();
  ReviewState.clockBaselines = map;
  if (BillingState.tenantId === 'demo-tenant') return;
  // TASK-9982 — loaded even when the switch is OFF: a switch turned ON while this
  // Review is open must still send the stale-edit guard on the next Save.
  const ids = [...new Set((ReviewState.employeeLines || []).map(l => l && l.clockEntryId).filter(Boolean).map(String))];
  await Promise.all(ids.map(async (id) => {
    try {
      const snap = await getDoc(doc(db, 'tenants', BillingState.tenantId, 'clockEntries', id));
      if (loadToken !== _reviewLoadToken || ReviewState.clockBaselines !== map) return;
      if (!snap.exists() || (snap.metadata && snap.metadata.fromCache)) return;
      const d = snap.data() || {};
      // 1971 final COD — a Save that landed before this read already recorded the
      // window it sent; a late read must not overwrite it.
      if (map.has(id)) return;
      map.set(id, { inMs: _reviewTsMs(d.clockIn), outMs: _reviewTsMs(d.clockOut) });
    } catch (_) { /* no baseline → no stale guard for this shift, never a false refusal */ }
  }));
}

function _fmtSyncWindow(fromMs, toMs) {
  const f = (ms) => (ms == null ? '—' : new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }));
  return `${f(fromMs)} – ${f(toMs)}`;
}

// TASK-9982 — Billing Settings "Editing an employee's times on a ticket also
// updates their time clock (changes payroll)". OFF unless literally true:
// missing / unloaded settings read as OFF (pre-9412: ticket lines only).
function _ticketTimesUpdateClockOn() {
  return window.AppState?.settings?.ticketTimesUpdateClock === true;
}

// The Save-path step. Returns true to continue the ticket write, false to stop
// (the biller chose to go back; nothing of the ticket was written).
async function _reviewSyncClockBeforeSave(ticketId) {
  if (BillingState.tenantId === 'demo-tenant') return true; // no server gateway on the local demo
  // TASK-9982 — OFF (the default): the Review save writes the ticket lines only.
  // No editClockEntry, no clock audit row, no payroll change, no server cascade.
  if (!_ticketTimesUpdateClockOn()) return true;
  // COD round 1 (Code Reviewer) — pin the Review session. Everything below that
  // touches ReviewState runs only if the SAME ticket is still open afterwards.
  const sessionTicket = ReviewState.ticket;
  const { syncs, conflicts } = _reviewClockSyncPlan(ReviewState.employeeLines);
  if (syncs.length === 0 && conflicts.length === 0) return true;

  // TASK-9443 slice 2 — soft pay-period lock: moving a shift's times from the
  // ticket changes paid hours. If payroll for the old or new day was already
  // exported, say so once; Cancel = back to the modal, NOTHING written (same
  // contract as going back below). Soft: no lock module / any error = go on.
  if (syncs.length > 0) {
    try {
      const PL = window.PayrollRunLock;
      if (PL && typeof PL.confirmEditMany === 'function') {
        const ms = [];
        syncs.forEach(s => { [s.oldFromMs, s.fromMs].forEach(v => { if (Number.isFinite(v)) ms.push(v); }); });
        const go = await PL.confirmEditMany(ms, { what: 'Changing these times from the ticket', confirmText: 'Save anyway' });
        if (!go) { window.showToast?.('Nothing was saved.', 'info'); return false; }
        if (ReviewState.ticket !== sessionTicket || ReviewState.ticketId !== ticketId) return false;
      }
    } catch (_) { /* soft lock: never blocks the save */ }
  }

  const { done, failed } = await _syncReviewTimesToClock(ticketId, syncs);
  if (ReviewState.ticket !== sessionTicket || ReviewState.ticketId !== ticketId) {
    // The modal moved on while the calls were out. The server already applied
    // `done` to the clock AND this ticket's lines (one transaction each), so
    // nothing is out of step; just don't write another ticket's state.
    if (done.length > 0) window.showToast?.(`Time clock updated for ${[...new Set(done.map(d => d.personName))].join(', ')}`, 'success');
    return false;
  }

  let eqMirrored = 0;
  for (const d of done) {
    const l = ReviewState.employeeLines.find(x => x._localId === d.localId && x.id === d.lineId);
    // Baseline = the values we SENT (captured in the plan), never the line's
    // current values: an edit typed while the call was out must still read as
    // unsynced and go on the next save.
    if (l) { l._origFromMs = d.fromMs; l._origToMs = d.toMs; }
    // The clock entry now shows what we sent — that is the next save's baseline.
    if (ReviewState.clockBaselines instanceof Map) ReviewState.clockBaselines.set(d.entryId, { inMs: d.fromMs, outMs: d.toMs });
    // COD round 3 (Code Reviewer P1) — the server moved EVERY EMPLOYEE line that
    // names this clockEntryId. An untouched duplicate line on screen still holds
    // the old window, and the save loop would write it back. Mirror the move.
    for (const sib of ReviewState.employeeLines) {
      if (!sib || sib === l || !sib.id || String(sib.clockEntryId || '') !== d.entryId) continue;
      if (_reviewTsMs(sib.fromTime) !== (sib._origFromMs ?? null) || _reviewTsMs(sib.toTime) !== (sib._origToMs ?? null)) continue;
      sib.fromTime = Timestamp.fromMillis(d.fromMs);
      sib.toTime = Timestamp.fromMillis(d.toMs);
      _recalcLineFromTimes(sib);
      sib._origFromMs = d.fromMs; sib._origToMs = d.toMs;
      if (sib._auditBase) _auditRebaseClock(sib); // TASK-1406 — server mirror, not the biller
    }
    if (d.updated?.timesChanged) {
      eqMirrored += _applyClockCascadeToReviewEquipment(ReviewState.equipmentLines, d.updated.equipmentLinesUpdated,
        d.fromMs, d.toMs, { toTs: (ms) => Timestamp.fromMillis(ms), recalc: (line) => _recalcLineFromTimes(line) }).applied.length;
    }
  }
  // If the biller ends up going BACK to the modal below, it must show the
  // equipment windows the server just moved, not the stale ones.
  if (eqMirrored > 0) {
    const section = document.getElementById('review-equip-section');
    if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
    renderReviewTotals();
  }
  if (done.length > 0) {
    const names = [...new Set(done.map(d => d.personName))].join(', ');
    window.showToast?.(`Time clock updated for ${names}`, 'success');
  }

  // COD round 2 (Code Reviewer P1) — a time typed while the calls were out
  // would reach the ticket but not the clock. Re-plan against the submitted
  // baselines: anything new that WOULD sync stops the save (the next Save sends
  // it). Shifts already refused/conflicted are handled below, not here.
  const _handled = new Set([...conflicts, ...failed].map(p => p.entryId));
  const _late = _reviewClockSyncPlan(ReviewState.employeeLines).syncs.filter(s => !_handled.has(s.entryId));
  if (_late.length > 0) {
    window.showToast?.(`Times changed while saving (${[...new Set(_late.map(s => s.personName))].join(', ')}) — press Save again to send them to the time clock.`, 'warning');
    return false;
  }

  const problems = [...conflicts, ...failed];
  if (problems.length === 0) return true;

  const list = problems.map(p => `${p.personName} (${_fmtSyncWindow(p.fromMs, p.toMs)}): ${p.reason}`).join(' · ');
  // COD round 1 — say plainly what DID already change; "Go back" cannot undo it
  // (each shift that went through moved its clock entry and its ticket line
  // together, so those are in step either way).
  const doneNote = done.length > 0
    ? ` Already updated (time clock and ticket line together): ${[...new Set(done.map(d => d.personName))].join(', ')}.`
    : '';
  const saveAnyway = await window.showConfirm?.(
    'Time clock not updated',
    `${list}.${doneNote} Go back to fix it — or save the ticket only. If you save the ticket only, `
      + `${problems.length === 1 ? 'that shift keeps its' : 'those shifts keep their'} old times on the time clock (payroll), and the mismatch is recorded in the audit log under your name.`,
    { confirmText: 'Save ticket only', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true }
  );
  if (!saveAnyway) return false;
  if (ReviewState.ticket !== sessionTicket) return false;
  try {
    await window.logAudit?.('ticket', 'ticket_clock_sync_skipped', {
      type: 'ticket_clock_sync_skipped',
      ticketId,
      ticketNumber: ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '',
      shifts: problems.map(p => ({
        clockEntryId: p.entryId, ticketLineId: p.lineId, personName: p.personName,
        // What the ticket line showed when Review opened — NOT a verified read
        // of the clock entry (clockEntryAuditLog is the clock's own record).
        ticketLineTimesAtOpen: { from: p.oldFromMs, to: p.oldToMs },
        ticketTimesSaved: { from: p.fromMs, to: p.toMs },
        reason: p.reason,
      })),
      editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
    });
  } catch (auditErr) {
    console.warn('[Billing] clock-sync-skipped audit failed (non-blocking):', auditErr);
  }
  return true;
}

// ============================================
// ITEM CODES MANAGEMENT (Billing)
// ============================================
const BILLING_ITEM_CATEGORIES = ['Personnel', 'Heavy Haul', 'Hydrovac', 'Trucks & Trailer', 'Equipment', 'Other'];

function showItemCodesModal() {
  // If billing hasn't been initialized yet (e.g. called from CRM), set tenantId from AppState
  if (!BillingState.tenantId && window.AppState?.tenant) {
    BillingState.tenantId = window.AppState.tenant;
  }
  // Ensure the modal DOM exists (billing template may not be mounted yet)
  if (!document.getElementById('modal-billing-itemcodes')) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `
      <div class="modal hidden" id="modal-billing-itemcodes" style="z-index:1010;">
        <div class="modal-content" style="max-width:640px;max-height:85vh;max-height:85dvh;display:flex;flex-direction:column;">
          <div class="modal-header">
            <h3>Item Codes</h3>
            <button class="btn-icon" onclick="document.getElementById('modal-billing-itemcodes')?.classList.add('hidden')">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div id="billing-itemcodes-body" style="flex:1;overflow-y:auto;padding:var(--spacing-lg);min-height:0;"></div>
        </div>
      </div>`;
    document.body.appendChild(wrapper.firstElementChild);
  }
  // Force-reload item codes to get latest from Firestore
  ReviewState.itemCodes = [];
  ReviewState.customerRateCache = null; // clear customer rates cache
  loadReviewItemCodes().then(() => renderBillingItemCodesBody());
  document.getElementById('modal-billing-itemcodes')?.classList.remove('hidden');
}

function renderBillingItemCodesBody() {
  const body = document.getElementById('billing-itemcodes-body');
  if (!body) return;

  const codes = ReviewState.itemCodes || [];

  body.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-weight:600;color:var(--text-primary);">Manage Item Codes</div>
      <button class="btn btn-primary btn-sm" onclick="window.BillingModule.showBillingItemCodeForm()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Code
      </button>
    </div>
    <div id="billing-itemcode-form-area"></div>
    ${codes.length === 0 ? '<div style="text-align:center;color:var(--text-secondary);padding:24px;">No item codes yet. Add codes here to use in ticket line items.</div>' :
    `<div style="overflow-x:auto;"><table style="width:100%;border-collapse:collapse;font-size:14px;">
      <thead>
        <tr style="border-bottom:2px solid var(--border-light);">
          <th style="text-align:left;padding:6px;color:var(--text-secondary);font-weight:600;">Code</th>
          <th style="text-align:left;padding:6px;color:var(--text-secondary);font-weight:600;">Description</th>
          <th style="text-align:left;padding:6px;color:var(--text-secondary);font-weight:600;">Category</th>
          <th style="text-align:center;padding:6px;color:var(--text-secondary);font-weight:600;">Billable</th>
          <th style="text-align:right;padding:6px;color:var(--text-secondary);font-weight:600;">Rate</th>
          <th style="width:88px;"></th>
        </tr>
      </thead>
      <tbody>
        ${codes.map(ic => `
          <tr style="border-bottom:1px solid var(--border-light);">
            <td style="padding:6px;font-weight:500;color:var(--text-primary);">${escapeHtml(ic.code)}</td>
            <td style="padding:6px;color:var(--text-secondary);">${escapeHtml(ic.description || '')}</td>
            <td style="padding:6px;color:var(--text-secondary);font-size:13px;">${escapeHtml(ic.category || '')}</td>
            <td style="padding:6px;text-align:center;"><input type="checkbox" ${ic.billable !== false ? 'checked' : ''} onchange="window.BillingModule.toggleBillable('${window.escJsAttr(ic.id)}', this.checked)" style="accent-color:var(--brand-blue);cursor:pointer;"></td>
            <td style="padding:6px;text-align:right;font-weight:500;${ic.billable === false ? 'color:var(--text-secondary);text-decoration:line-through;opacity:0.6;' : 'color:var(--text-primary);'}">${ic.defaultRate != null ? '$' + ic.defaultRate + '/' + (ic.defaultRateUnit === 'Hourly' ? 'hr' : ic.defaultRateUnit === 'Daily' ? 'day' : 'mo') : ''}</td>
            <td style="padding:6px;text-align:right;white-space:nowrap;">
              <button class="btn-icon" onclick="window.BillingModule.showCustomerRates('${escJsAttr(ic.code)}', this, event)" title="Customer rates"><span class="material-icons" style="font-size:16px;color:var(--brand-blue);">people</span></button>
              <button class="btn-icon" onclick="window.BillingModule.showBillingItemCodeForm('${window.escJsAttr(ic.id)}')" title="Edit"><span class="material-icons" style="font-size:16px;">edit</span></button>
              <button class="btn-icon" onclick="window.BillingModule.deleteBillingItemCode('${window.escJsAttr(ic.id)}')" title="Delete"><span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button>
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table></div>`}
  `;
}

function showBillingItemCodeForm(itemCodeId) {
  const ic = itemCodeId ? (ReviewState.itemCodes || []).find(c => c.id === itemCodeId) : null;
  const area = document.getElementById('billing-itemcode-form-area');
  if (!area) return;

  area.innerHTML = `
    <div style="padding:12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-elevated);">
      <input type="hidden" id="billing-ic-id" value="${itemCodeId || ''}">
      <div style="display:grid;grid-template-columns:120px 1fr 120px;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Code *</label>
          <input type="text" id="billing-ic-code" class="form-control" value="${escapeHtml(ic?.code || '')}" ${ic ? 'readonly style="background:var(--bg-secondary);color:var(--text-primary);"' : ''}>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Description</label>
          <input type="text" id="billing-ic-desc" class="form-control" value="${escapeHtml(ic?.description || '')}">
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Category</label>
          <select id="billing-ic-category" class="form-control">
            <option value="">-- None --</option>
            ${BILLING_ITEM_CATEGORIES.map(c => `<option value="${c}" ${ic?.category === c ? 'selected' : ''}>${c}</option>`).join('')}
          </select>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:120px 120px auto 1fr;gap:8px;align-items:end;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Default Rate ($)</label>
          <input type="number" id="billing-ic-rate" class="form-control" value="${ic?.defaultRate || ''}" min="0" step="0.01" placeholder="0.00" ${ic?.billable === false ? 'disabled style="background:var(--bg-secondary);color:var(--text-secondary);"' : ''}>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Unit</label>
          <input type="text" id="billing-ic-rate-unit" list="ic-uom-suggestions" class="form-control" autocomplete="off" value="${escapeHtml(ic?.defaultRateUnit || 'Hourly')}">
          <datalist id="ic-uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
        </div>
        <div class="form-group" style="margin-bottom:8px;display:flex;align-items:center;padding-bottom:4px;">
          <label style="font-size:13px;color:var(--text-primary);display:flex;align-items:center;gap:6px;cursor:pointer;">
            <input type="checkbox" id="billing-ic-billable" ${ic?.billable !== false ? 'checked' : ''} onchange="
              const rateInput = document.getElementById('billing-ic-rate');
              if (!this.checked) { rateInput.value = '0'; rateInput.disabled = true; rateInput.style.background = 'var(--bg-secondary)'; rateInput.style.color = 'var(--text-secondary)'; }
              else { rateInput.disabled = false; rateInput.style.background = ''; rateInput.style.color = ''; }
            " style="accent-color:var(--brand-blue);cursor:pointer;"> Billable
          </label>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label for="billing-ic-round" style="font-size:13px;color:var(--text-primary);">Round to ½ hr (equipment)</label>
          ${_equipRoundSelectHtml('billing-ic-round', ic?.roundToHalfHour)}
        </div>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.renderBillingItemCodesBody()">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.BillingModule.saveBillingItemCode()">Save</button>
      </div>
    </div>
  `;
  setTimeout(() => document.getElementById(ic ? 'billing-ic-desc' : 'billing-ic-code')?.focus(), 100);
}

async function saveBillingItemCode() {
  if (_priceBookBlocked()) return; // TASK-9491
  const code = document.getElementById('billing-ic-code')?.value.trim();
  if (!code) { showToast('Item code is required', 'error'); return; }

  const existingId = document.getElementById('billing-ic-id')?.value;
  const billable = document.getElementById('billing-ic-billable')?.checked !== false;
  const rateVal = parseFloat(document.getElementById('billing-ic-rate')?.value);
  const data = {
    code,
    description: document.getElementById('billing-ic-desc')?.value.trim() || '',
    category: document.getElementById('billing-ic-category')?.value || '',
    billable,
    defaultRate: !billable ? 0 : (isNaN(rateVal) ? null : rateVal),
    defaultRateUnit: document.getElementById('billing-ic-rate-unit')?.value?.trim() || 'Hourly',
    // TASK-9489 — Company default / Always / Never (null / true / false).
    roundToHalfHour: _readEquipRoundSelect('billing-ic-round', null),
    updatedAt: Timestamp.now()
  };

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    // TASK-9672 follow-up — the default rate prices a tied ticket count when the
    // customer has no Price Book price: log old + new (read from the stored doc).
    let _before = null;
    let _savedId = existingId;
    const _icM = await _icMod(); // before any write: no audit helper, no save
    if (existingId) {
      try { const _s = await getDoc(doc(db, 'tenants', tenant, 'itemCodes', existingId)); if (_s.exists()) _before = _s.data(); }
      catch (_) { /* audit read best-effort; the save still runs */ }
      await updateDoc(doc(db, 'tenants', tenant, 'itemCodes', existingId), data);
    } else {
      const existing = (ReviewState.itemCodes || []).find(ic => ic.code === code);
      if (existing) { showToast('Item code already exists', 'error'); return; }
      data.createdAt = Timestamp.now();
      _savedId = (await addDoc(collection(db, 'tenants', tenant, 'itemCodes'), data)).id;
    }
    await _icM.logRateAudit(_before, data, { itemCodeId: _savedId, code, isNew: !existingId, source: 'billing-item-codes' }, (a) => window.logAudit?.('billing', 'ITEM_CODE_RATE_CHANGE', a));
    showToast(existingId ? 'Item code updated' : 'Item code added', 'success');
    // Reload item codes
    ReviewState.itemCodes = [];
    await loadReviewItemCodes();
    renderBillingItemCodesBody();
    // TASK-9672 follow-up — an open Price Book line form picks up the new code.
    _icMod().then(m => m.refillItemCodeSelect('li-form-itemcode', ReviewState.itemCodes, escapeHtml)).catch(() => {});
    // Re-render review ticket to pick up new item codes
    if (ReviewState.ticketId) renderReviewTicket();
  } catch (err) {
    console.error('[Billing] Save item code error:', err);
    showToast('Failed to save item code', 'error');
  }
}

async function deleteBillingItemCode(itemCodeId) {
  if (_priceBookBlocked()) return; // TASK-9491
  if (!confirm('Delete this item code?')) return;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    await deleteDoc(doc(db, 'tenants', tenant, 'itemCodes', itemCodeId));
    showToast('Item code deleted', 'success');
    ReviewState.itemCodes = [];
    await loadReviewItemCodes();
    renderBillingItemCodesBody();
    if (ReviewState.ticketId) renderReviewTicket();
  } catch (err) {
    console.error('[Billing] Delete item code error:', err);
    showToast('Failed to delete item code', 'error');
  }
}

async function toggleBillable(itemCodeId, checked) {
  if (_priceBookBlocked()) return; // TASK-9491
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const updates = checked
      ? { billable: true, updatedAt: Timestamp.now() }
      : { billable: false, defaultRate: 0, updatedAt: Timestamp.now() };
    // Old value from the STORED doc (2128 COD Code Reviewer), the list as fallback.
    let _before = (ReviewState.itemCodes || []).find(c => c.id === itemCodeId) || null;
    try { const _s = await getDoc(doc(db, 'tenants', tenant, 'itemCodes', itemCodeId)); if (_s.exists()) _before = _s.data(); }
    catch (_) { /* audit read best-effort */ }
    const _icM = await _icMod(); // before the write: no audit helper, no save
    await updateDoc(doc(db, 'tenants', tenant, 'itemCodes', itemCodeId), updates);
    // TASK-9672 follow-up — non-billable zeroes the default rate: log old + new.
    await _icM.logRateAudit(_before, { ...(_before || {}), ...updates }, { itemCodeId, code: _before?.code || null, isNew: false, source: 'billing-item-codes-billable' }, (a) => window.logAudit?.('billing', 'ITEM_CODE_RATE_CHANGE', a));
    showToast(checked ? 'Marked as billable' : 'Marked as non-billable', 'success');
    ReviewState.itemCodes = [];
    await loadReviewItemCodes();
    renderBillingItemCodesBody();
    if (ReviewState.ticketId) renderReviewTicket();
  } catch (err) {
    console.error('[Billing] Toggle billable error:', err);
    showToast('Failed to update item code', 'error');
  }
}

// ============================================
// CUSTOMER RATES POPOVER (per item code)
// ============================================

async function loadCustomerRateData() {
  if (ReviewState.customerRateCache) return ReviewState.customerRateCache;

  const tenant = getTenant();
  let results = [];

  try {
    // Load all customers
    const custSnap = await getDocs(collection(db, 'tenants', tenant, 'customers'));
    // P0 2026-10-06 — customer rates only; a vendor's rate card is a COST.
    const customers = window.BillableCustomer.billableCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    // Fan out the customer→contract→lineItem reads concurrently (TASK-864).
    // Was a sequential N+1 (per-customer active contracts, then per-contract line items),
    // i.e. hundreds of serial round-trips at ~50 customers. Promise.all preserves INPUT
    // order, so the flattened output is byte-for-byte identical to the old serial walk —
    // same customers→contracts→lineItems ordering, same fallbacks, same active-only filter.
    const perCustomer = await Promise.all(customers.map(async (cust) => {
      const contractsSnap = await getDocs(
        query(collection(db, 'tenants', tenant, 'customers', cust.id, 'contracts'),
          where('status', '==', 'Active'))
      );

      const perContract = await Promise.all(contractsSnap.docs.map(async (cDoc) => {
        const contract = { id: cDoc.id, ...cDoc.data() };
        const liSnap = await getDocs(
          collection(db, 'tenants', tenant, 'customers', cust.id, 'contracts', contract.id, 'lineItems')
        );

        const rows = [];
        for (const liDoc of liSnap.docs) {
          const li = liDoc.data();
          if (!li.itemCode) continue;
          rows.push({
            customerId: cust.id,
            customerName: cust.name || 'Unknown',
            contractId: contract.id,
            contractTitle: contract.title || 'Untitled',
            itemCode: li.itemCode,
            rate: li.rate,
            rateUnit: li.rateUnit || 'Hourly'
          });
        }
        return rows;
      }));

      return perContract.flat();
    }));

    results = perCustomer.flat();
  } catch (err) {
    console.error('[Billing] Error loading customer rate data:', err);
    // Don't poison the session cache with an incomplete/empty result on a transient
    // read failure — return what we have but let the next call retry (TASK-864 COD).
    return results;
  }

  ReviewState.customerRateCache = results;
  return results;
}

function showCustomerRates(itemCode, btnEl, evt) {
  evt?.stopPropagation();

  // Close any existing popover
  document.querySelector('.customer-rates-popover')?.remove();

  // Show loading popover immediately
  const rect = btnEl.getBoundingClientRect();
  const popover = document.createElement('div');
  popover.className = 'customer-rates-popover';
  popover.innerHTML = `
    <div class="crp-header">
      <span style="font-weight:600;color:var(--text-primary);">Customer Rates for ${escapeHtml(itemCode)}</span>
      <button class="btn-icon" onclick="this.closest('.customer-rates-popover').remove()" style="margin-left:auto;">
        <span class="material-icons" style="font-size:16px;">close</span>
      </button>
    </div>
    <div class="crp-body" style="text-align:center;padding:16px;color:var(--text-secondary);">
      <span class="btn-spinner"></span> Loading...
    </div>
  `;

  document.body.appendChild(popover);
  positionPopover(popover, rect);

  // Close on outside click
  const closeHandler = (e) => {
    if (!popover.contains(e.target) && e.target !== btnEl) {
      popover.remove();
      document.removeEventListener('click', closeHandler);
    }
  };
  setTimeout(() => document.addEventListener('click', closeHandler), 10);

  // Load data and render
  loadCustomerRateData().then(allRates => {
    const matches = allRates.filter(r => r.itemCode === itemCode);
    const masterCode = (ReviewState.itemCodes || []).find(ic => ic.code === itemCode);
    const masterRate = masterCode?.defaultRate != null
      ? `$${masterCode.defaultRate}/${masterCode.defaultRateUnit === 'Hourly' ? 'hr' : masterCode.defaultRateUnit === 'Daily' ? 'day' : 'mo'}`
      : 'Not set';

    const body = popover.querySelector('.crp-body');

    if (matches.length === 0) {
      body.innerHTML = `
        <div style="padding:12px;color:var(--text-secondary);font-size:13px;">
          No customers have overrides for this code.<br>
          All customers use the master rate: <strong style="color:var(--text-primary);">${masterRate}</strong>
        </div>
      `;
    } else {
      body.innerHTML = `
        <div style="max-height:200px;overflow-y:auto;">
          ${matches.map(m => {
            const rateLabel = m.rate != null ? escapeHtml(`$${m.rate}${formatUnitLabel(m.rateUnit)}`) : 'No rate';
            return `
              <div class="crp-row" onclick="window.BillingModule.navigateToCustomerContract('${escJsAttr(m.customerId)}', '${escJsAttr(m.contractId)}')" title="${escapeHtml(m.contractTitle)}">
                <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">business</span>
                <span style="flex:1;color:var(--text-primary);font-size:13px;">${escapeHtml(m.customerName)}</span>
                <span style="font-weight:600;color:var(--text-primary);font-size:13px;">${rateLabel}</span>
              </div>`;
          }).join('')}
        </div>
        <div style="padding:8px 12px;border-top:1px solid var(--border-light);font-size:12px;color:var(--text-secondary);">
          Master Rate: <strong style="color:var(--text-primary);">${masterRate}</strong>
        </div>
      `;
    }

    // Reposition after content changes
    positionPopover(popover, rect);
  });
}

function positionPopover(popover, btnRect) {
  const pad = 8;
  // Position below the button, right-aligned
  let top = btnRect.bottom + pad;
  let left = btnRect.right - 280;

  // Ensure it doesn't go off-screen
  if (left < pad) left = pad;
  if (top + 300 > window.innerHeight) {
    top = btnRect.top - popover.offsetHeight - pad;
    if (top < pad) top = pad;
  }

  popover.style.top = `${top}px`;
  popover.style.left = `${left}px`;
}

function navigateToCustomerContract(customerId, contractId) {
  // Close the popover
  document.querySelector('.customer-rates-popover')?.remove();

  if (window.CRMModule?.openCustomerDetail) {
    window.CRMModule.openCustomerDetail(customerId).then(() => {
      // Switch to contracts tab after detail modal opens
      setTimeout(() => {
        window.CRMModule.switchDetailTab?.('contracts');
      }, 200);
    });
  } else {
    showToast('Open CRM to view customer contracts', 'info');
  }
}

// ============================================
// LINE ITEMS MANAGEMENT (Contract Line Items)
// ============================================

function getTenant() {
  return BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
}

async function showLineItemsModal() {
  // If billing hasn't been initialized yet (e.g. called from CRM), set tenantId from AppState
  if (!BillingState.tenantId && window.AppState?.tenant) {
    BillingState.tenantId = window.AppState.tenant;
  }

  // Ensure the modal DOM exists (billing template may not be mounted yet)
  if (!document.getElementById('modal-billing-lineitems')) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `<div class="modal hidden" id="modal-billing-lineitems">
      <div class="modal-content modal-fullscreen">
        <div class="modal-header">
          <h3>Line Items</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeLineItemsModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="billing-lineitems-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;min-height:0;padding:var(--spacing-lg);">
          <div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>
        </div>
      </div>
    </div>`;
    document.body.appendChild(wrapper.firstElementChild);
  }

  // Ensure Quick Add Customer modal exists
  if (!document.getElementById('modal-li-quick-customer')) {
    const qc = document.createElement('div');
    qc.innerHTML = `<div class="modal hidden" id="modal-li-quick-customer">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Add Customer</h3>
          <button class="btn-icon" onclick="document.getElementById('modal-li-quick-customer').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-li-quick-customer" novalidate onsubmit="window.BillingModule.saveLiQuickCustomer(event)">
          <div style="padding:var(--spacing-lg);">
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Company Name *</label>
              <input type="text" id="li-qc-name" class="form-control" required placeholder="Acme Trucking">
            </div>
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Phone</label>
              <input type="tel" id="li-qc-phone" class="form-control" placeholder="555-123-4567">
            </div>
            <div class="form-group" style="margin-bottom:12px;">
              <label style="font-size:13px;color:var(--text-primary);">Email</label>
              <input type="email" id="li-qc-email" class="form-control" placeholder="billing@company.com">
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-li-quick-customer').classList.add('hidden')">Cancel</button>
            <button type="submit" class="btn btn-primary">Add Customer</button>
          </div>
        </form>
      </div>
    </div>`;
    document.body.appendChild(qc.firstElementChild);
  }

  // Load customers if not already loaded (when called from CRM, billing subscriptions aren't active)
  if (!BillingState.customers || BillingState.customers.length === 0) {
    const custSnap = await getDocs(query(
      collection(db, 'tenants', BillingState.tenantId, 'customers'),
      orderBy('name')
    ));
    BillingState.customers = custSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // Reset state
  LineItemsState.selectedCustomerId = null;
  LineItemsState.selectedContractId = null;
  LineItemsState.massUpdateMode = false;
  LineItemsState.contracts = [];
  LineItemsState.lineItems = [];
  LineItemsState.allLineItems = [];
  LineItemsState._pendingChanges = new Map();
  LineItemsState._pendingAdds = [];
  LineItemsState._allContracts = [];
  LineItemsState._hasUnsavedChanges = false;

  // Load item codes
  ReviewState.itemCodes = [];
  await loadReviewItemCodes();
  LineItemsState.itemCodes = ReviewState.itemCodes;

  renderLineItemsModalBody();
  document.getElementById('modal-billing-lineitems')?.classList.remove('hidden');
}

function closeLineItemsModal() {
  if (LineItemsState._hasUnsavedChanges) {
    if (!confirm('You have unsaved mass update changes. Discard and close?')) return;
  }
  document.getElementById('modal-billing-lineitems')?.classList.add('hidden');
  LineItemsState.selectedCustomerId = null;
  LineItemsState.selectedContractId = null;
  LineItemsState.massUpdateMode = false;
  LineItemsState.contracts = [];
  LineItemsState.lineItems = [];
  LineItemsState.allLineItems = [];
  LineItemsState._pendingChanges = new Map();
  LineItemsState._pendingAdds = [];
  LineItemsState._allContracts = [];
  LineItemsState._hasUnsavedChanges = false;
}

function renderLineItemsModalBody() {
  const body = document.getElementById('billing-lineitems-body');
  if (!body) return;

  const selectedCust = LineItemsState.selectedCustomerId;
  const selectedContract = LineItemsState.selectedContractId;
  const massMode = LineItemsState.massUpdateMode;

  let html = '';

  // Top row: Mass Update toggle — prominent segmented control
  html += `<div style="display:flex;gap:0;margin-bottom:16px;border:2px solid #e2e8f0;border-radius:12px;overflow:hidden;">
    <button onclick="window.BillingModule.toggleLiMassUpdate(false)" style="flex:1;padding:12px 16px;font-size:0.9rem;font-weight:${massMode ? '500' : '700'};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:all 0.15s;${massMode ? 'background:transparent;color:var(--text-secondary);' : 'background:var(--brand-blue);color:#fff;box-shadow:0 2px 8px rgba(59,130,246,0.3);'}">
      <span class="material-icons" style="font-size:18px;">edit_note</span> Single Contract
    </button>
    <button onclick="window.BillingModule.toggleLiMassUpdate(true)" style="flex:1;padding:12px 16px;font-size:0.9rem;font-weight:${massMode ? '700' : '500'};border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:all 0.15s;${massMode ? 'background:var(--brand-blue);color:#fff;box-shadow:0 2px 8px rgba(59,130,246,0.3);' : 'background:transparent;color:var(--text-secondary);'}">
      <span class="material-icons" style="font-size:18px;">sync_alt</span> Mass Update
    </button>
  </div>`;

  if (massMode) {
    // Mass update mode — no customer/contract pickers needed
    const items = LineItemsState.allLineItems || [];
    html += `<div id="li-lineitem-form-area"></div>`;

    // Info banner
    html += `<div style="background:linear-gradient(135deg,#EFF6FF,#DBEAFE);border:1px solid #BFDBFE;border-radius:10px;padding:14px 16px;margin-bottom:16px;display:flex;align-items:center;gap:10px;">
      <span class="material-icons" style="font-size:22px;color:var(--brand-blue);">info</span>
      <div style="font-size:13px;color:#1E40AF;line-height:1.4;">Changes apply to <strong>every contract</strong> containing each item. Edit rates below, then hit <strong>Save All</strong> to apply everywhere at once.</div>
    </div>`;

    // Invoice Column Settings (mass mode — applies to ALL contracts)
    html += buildInvoiceColumnsPanel(null);

    if (LineItemsState._massLoading) {
      html += `<div class="loading-state"><div class="loading-spinner"></div><p>Loading all line items...</p></div>`;
    } else if (items.length === 0 && LineItemsState._pendingAdds.length === 0) {
      html += `<div style="text-align:center;color:var(--text-secondary);padding:32px;">
        <span class="material-icons" style="font-size:48px;color:var(--border-light);margin-bottom:12px;display:block;">sync_alt</span>
        No line items found across any customer contracts.
      </div>`;
    } else {
      // Header row with count + add button
      html += `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
        <div style="font-size:14px;color:var(--text-primary);font-weight:600;">
          ${items.length} unique item${items.length !== 1 ? 's' : ''} found
        </div>
        <button class="btn btn-sm btn-primary" style="padding:6px 14px;border-radius:8px;font-size:13px;font-weight:600;display:flex;align-items:center;gap:4px;" onclick="window.BillingModule.showMassAddForm()">
          <span class="material-icons" style="font-size:16px;">add</span> Add Line Item to All
        </button>
      </div>`;

      // Pending add cards (green border)
      const pendingAdds = LineItemsState._pendingAdds || [];
      const contractCount = LineItemsState._allContracts.length;
      if (pendingAdds.length > 0) {
        html += `<div style="margin-bottom:16px;">`;
        pendingAdds.forEach(pa => {
          const unitLabel = escapeHtml(formatUnitLabel(pa.rateUnit));
          html += `
            <div class="card" style="padding:14px 16px;margin-bottom:6px;border-left:4px solid #22C55E;background:#F0FDF4;">
              <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
                <div style="flex:1;min-width:140px;">
                  <div style="font-weight:600;font-size:15px;color:var(--text-primary);line-height:1.3;margin-bottom:4px;">
                    ${pa.itemCode ? `<span style="font-size:12px;background:#DCFCE7;padding:2px 6px;border-radius:4px;color:#166534;margin-right:6px;font-weight:500;">${escapeHtml(pa.itemCode)}</span>` : ''}
                    ${escapeHtml(pa.description || 'Unnamed item')}
                    <span style="display:inline-block;font-size:11px;font-weight:700;color:#fff;background:#22C55E;padding:2px 8px;border-radius:6px;margin-left:8px;vertical-align:middle;">NEW</span>
                  </div>
                  <div style="font-size:13px;color:#166534;font-weight:500;">Will be added to ${contractCount} contract${contractCount !== 1 ? 's' : ''} on save</div>
                  <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">$${(pa.rate || 0).toFixed(2)}${unitLabel} &middot; ${escapeHtml(pa.category || 'Other')}</div>
                </div>
                <button class="btn btn-sm btn-secondary" style="padding:6px 14px;font-size:13px;font-weight:600;border-radius:8px;flex-shrink:0;" onclick="window.BillingModule.undoMassAdd('${pa.tempId}')">
                  <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;">undo</span> Undo
                </button>
              </div>
            </div>`;
        });
        html += `</div>`;
      }

      html += renderLiLineItems(items, true);

      // Sticky save bar when pending changes or adds exist
      const hasChanges = LineItemsState._pendingChanges.size > 0 || LineItemsState._pendingAdds.length > 0;
      if (hasChanges) {
        const pending = LineItemsState._pendingChanges;
        let modCount = 0, delCount = 0;
        pending.forEach(v => { if (v.deleted) delCount++; else modCount++; });
        const addCount = LineItemsState._pendingAdds.length;
        const totalCount = modCount + delCount + addCount;
        const parts = [];
        if (addCount) parts.push(`${addCount} new`);
        if (modCount) parts.push(`${modCount} modified`);
        if (delCount) parts.push(`${delCount} deleted`);
        html += `
          <div style="height:80px;"></div>
          <div id="mass-save-bar" style="position:sticky;bottom:-24px;z-index:10;padding:16px 24px 28px;border-top:2px solid var(--brand-blue);background:#fff;box-shadow:0 -8px 20px rgba(0,0,0,0.12);display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 -24px -24px;border-radius:0 0 16px 16px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">pending_actions</span>
              <span style="font-size:14px;color:var(--text-primary);font-weight:600;">${parts.join(', ')}</span>
            </div>
            <div style="display:flex;gap:8px;">
              <button class="btn btn-secondary" style="padding:10px 20px;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.08);background:#fff;border:1px solid #CBD5E1;" onclick="window.BillingModule.discardMassChanges()">Discard</button>
              <button id="btn-mass-save-all" class="btn btn-primary" style="padding:10px 20px;border-radius:8px;box-shadow:0 2px 8px rgba(59,130,246,0.3);" onclick="window.BillingModule.confirmMassChanges()">
                <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">save</span> Save All (${totalCount})
              </button>
            </div>
          </div>`;
      }
    }
  } else {
    // Normal mode — customer + contract pickers
    html += `<div style="margin-bottom:16px;">
      <label style="font-size:13px;color:var(--text-primary);font-weight:600;">Customer</label>
      <div class="input-with-action">
        <select class="form-control" id="li-customer-select" onchange="window.BillingModule.onLiCustomerChange(this.value)">
          <option value="">Select customer...</option>
          ${_billingSalesCustomerOptionsHtml(selectedCust || null) /* TASK-9548 — billing picker, no internal customers */}
        </select>
        <button class="btn-add-inline btn-add-inline-sm" onclick="window.BillingModule.showLiQuickAddCustomer()" title="Add Customer">
          <span class="material-icons">add</span>
        </button>
      </div>
    </div>`;

    if (selectedCust) {
      const _invInfo = (LineItemsState.invoiceContractInfo && LineItemsState.invoiceContractInfo.customerId === selectedCust) ? LineItemsState.invoiceContractInfo : null;
      html += `<div style="margin-bottom:16px;">
        <label style="font-size:13px;color:var(--text-primary);font-weight:600;">Contract</label>
        <div class="input-with-action">
          <select class="form-control" id="li-contract-select" onchange="window.BillingModule.onLiContractChange(this.value)">
            <option value="">Select contract...</option>
            ${LineItemsState.contracts.map(c => `<option value="${c.id}" ${selectedContract === c.id ? 'selected' : ''}>${escapeHtml(c.title || 'Untitled')} (${c.status || 'Draft'})${_ftEmailMisconfigured(c) ? ' ⚠ no email recipient' : ''}${_invInfo && _invInfo.activeCount >= 2 && _invInfo.invoiceContractId === c.id ? ' — used on invoices' : ''}</option>`).join('')}
          </select>
          ${selectedContract ? `<button class="btn-add-inline btn-add-inline-sm" onclick="window.BillingModule.showLiContractForm('${selectedContract}')" title="Edit Contract">
            <span class="material-icons" style="font-size:18px;">edit</span>
          </button>` : ''}
          <button class="btn-add-inline btn-add-inline-sm" onclick="window.BillingModule.showLiContractForm()" title="Add Contract">
            <span class="material-icons">add</span>
          </button>
        </div>
      </div>`;
      html += `<div id="li-contract-form-area"></div>`;
      // TASK-9668 (#5) — 2+ Active contracts: name the one the invoice reads.
      html += invoiceContractBannerHTML(_invInfo, selectedContract);

      // TASK-4714 — the misconfiguration made UNMISSABLE, not just marked. The
      // ⚠ in the picker above tells you WHICH contract; this says what is
      // actually broken and hands you the one button that fixes it. Rendered for
      // the SELECTED contract only, because a banner per contract in a dropdown
      // is a wall nobody reads.
      const _selC = LineItemsState.contracts.find(c => c.id === selectedContract);
      if (_selC && _ftEmailMisconfigured(_selC)) {
        html += `<div style="display:flex;gap:10px;align-items:flex-start;padding:10px 12px;margin-bottom:12px;border:1px solid #FCA5A5;background:#FEF2F2;border-radius:var(--radius-sm);">
          <span class="material-icons" style="font-size:18px;color:#B91C1C;flex-shrink:0;">error_outline</span>
          <div style="min-width:0;flex:1;">
            <div style="font-weight:600;font-size:13px;color:#B91C1C;">This contract asks for an emailed copy but has no recipient</div>
            <div style="font-size:12px;color:var(--text-primary);">Every field ticket the crew closes out on this contract is refused, and the driver is not told. Add the address the ticket should go to.</div>
          </div>
          <button class="btn btn-sm" style="background:#B91C1C;color:#fff;border:none;white-space:nowrap;flex-shrink:0;"
            onclick="window.BillingModule.showLiContractForm('${selectedContract}')">Set recipient</button>
        </div>`;
      }

      // Invoice Column Visibility panel (when contract selected)
      if (selectedContract) {
        html += buildInvoiceColumnsPanel(_selC);
      }
    }

    html += `<div id="li-lineitem-form-area"></div>`;

    if (!selectedCust) {
      html += `<div style="text-align:center;color:var(--text-secondary);padding:48px 24px;">
        <span class="material-icons" style="font-size:48px;color:var(--border-light);margin-bottom:12px;display:block;">list_alt</span>
        Select a customer to manage their contract line items.
      </div>`;
    } else if (!selectedContract) {
      html += `<div style="text-align:center;color:var(--text-secondary);padding:32px;">
        Select a contract to view and manage its line items.
      </div>`;
    } else {
      const items = LineItemsState.lineItems || [];
      html += `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div style="font-weight:600;color:var(--text-primary);">${items.length} Line Item${items.length !== 1 ? 's' : ''}</div>
        <button class="btn btn-primary btn-sm" onclick="window.BillingModule.showLiLineItemForm()">
          <span class="material-icons" style="font-size:16px;">add</span> Add Line Item
        </button>
      </div>`;
      if (items.length === 0) {
        html += `<div style="text-align:center;color:var(--text-secondary);padding:32px;">
          No line items yet. Click "Add Line Item" to get started.
        </div>`;
      } else {
        html += renderLiLineItems(items, false);
      }
    }
  }

  const scrollTop = body.scrollTop;
  body.innerHTML = html;
  if (scrollTop) requestAnimationFrame(() => body.scrollTop = scrollTop);
}

function renderLiLineItems(items, isMassUpdate) {
  // Group by appliesTo.kind (fall back to CATEGORY_KIND_MAP for legacy data)
  const grouped = {};
  items.forEach(li => {
    const kind = li.appliesTo?.kind || CATEGORY_KIND_MAP[li.category] || null;
    const groupKey = kind || 'other';
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(li);
  });

  // Stable ordering: person, truck, trailer, asset, other
  const kindOrder = ['person', 'truck', 'trailer', 'asset', 'misc', 'other'];
  const sortedEntries = Object.entries(grouped).sort((a, b) => {
    const ai = kindOrder.indexOf(a[0]); const bi = kindOrder.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sortedEntries.map(([groupKey, catItems]) => `
    <div style="margin-bottom:16px;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;padding:8px 12px;background:linear-gradient(90deg,#F1F5F9,transparent);border-left:3px solid var(--brand-blue);border-radius:0 6px 6px 0;">
        <span style="font-weight:700;font-size:14px;color:var(--text-primary);text-transform:uppercase;letter-spacing:0.5px;">${escapeHtml(KIND_LABELS[groupKey] || 'Other')}</span>
        <span style="font-size:12px;color:var(--text-secondary);font-weight:500;">${catItems.length} item${catItems.length !== 1 ? 's' : ''}</span>
      </div>
      ${catItems.map(li => {
        const inactive = li.active === false;
        if (isMassUpdate) {
          const matchCount = li._allMatches?.length || 1;
          const custCount = new Set((li._allMatches || []).map(m => m._customerId)).size || 1;
          const rates = (li._allMatches || []).map(m => m.rate ?? 0);
          const uniqueRates = [...new Set(rates)];
          const allSame = uniqueRates.length === 1;
          const rateMin = Math.min(...rates);
          const rateMax = Math.max(...rates);
          const rateHint = allSame ? '' : ` &mdash; Range: $${rateMin.toFixed(2)} – $${rateMax.toFixed(2)}`;
          const pendingKey = li.itemCode || li.description;
          const pendingChange = LineItemsState._pendingChanges.get(pendingKey);
          const isDeleted = li._pendingDelete || pendingChange?.deleted;
          const isModified = !!pendingChange && !isDeleted;
          const unitLabel = escapeHtml(formatUnitLabel(li.rateUnit));

          // Deleted items: strikethrough card with Undo
          if (isDeleted) {
            return `
              <div class="card" style="padding:14px 16px;margin-bottom:6px;opacity:0.5;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;border-left:4px solid #EF4444;">
                <div style="flex:1;min-width:140px;">
                  <div style="font-weight:600;font-size:15px;color:var(--text-primary);text-decoration:line-through;">
                    ${li.itemCode ? `<span style="font-size:12px;background:#F1F5F9;padding:2px 6px;border-radius:4px;color:var(--text-secondary);margin-right:6px;font-weight:500;">${escapeHtml(li.itemCode)}</span>` : ''}
                    ${escapeHtml(li.description || 'Unnamed item')}
                  </div>
                  <div style="font-size:13px;color:#EF4444;margin-top:4px;font-weight:500;">Will be deleted from ${matchCount} contract${matchCount !== 1 ? 's' : ''} on save</div>
                </div>
                <button class="btn btn-sm btn-secondary" style="padding:6px 14px;font-size:13px;font-weight:600;border-radius:8px;" onclick="window.BillingModule.undoMassDelete('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}')">
                  <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;">undo</span> Undo
                </button>
              </div>`;
          }

          // Modified styling
          const borderStyle = isModified ? 'border-left:4px solid var(--brand-blue);background:#FAFBFF;' : '';
          const modBadge = isModified ? `<span style="display:inline-block;font-size:11px;font-weight:700;color:#fff;background:var(--brand-blue);padding:2px 8px;border-radius:6px;margin-left:8px;vertical-align:middle;">MODIFIED</span>` : '';
          return `
            <div class="card" style="padding:14px 16px;margin-bottom:6px;${inactive ? 'opacity:0.5;' : ''}${borderStyle}">
              <div style="font-weight:600;font-size:15px;color:var(--text-primary);${inactive ? 'text-decoration:line-through;' : ''}line-height:1.3;margin-bottom:4px;">
                ${li.itemCode ? `<span style="font-size:12px;background:#F1F5F9;padding:2px 6px;border-radius:4px;color:var(--text-secondary);margin-right:6px;font-weight:500;">${escapeHtml(li.itemCode)}</span>` : ''}
                ${escapeHtml(li.description || 'Unnamed item')}${modBadge}${_priceBookStateBadge(li)}
              </div>
              <div style="font-size:13px;color:var(--text-secondary);margin-bottom:8px;display:flex;align-items:center;gap:4px;">
                <span class="material-icons" style="font-size:14px;">description</span>
                ${matchCount} contract${matchCount !== 1 ? 's' : ''} across ${custCount} customer${custCount > 1 ? 's' : ''}${rateHint}
              </div>
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
                <div style="display:flex;align-items:center;gap:2px;background:#F8FAFC;border:2px solid #E2E8F0;border-radius:10px;padding:6px 10px;${inactive ? 'opacity:0.5;pointer-events:none;' : ''}">
                  <span style="font-size:15px;font-weight:700;color:var(--text-primary);">$</span>
                  <input type="number" class="form-control" style="width:100px;padding:6px 8px;font-size:16px;font-weight:600;border:none;background:transparent;text-align:right;${inactive ? 'opacity:0.5;' : ''}"
                    ${inactive ? 'disabled' : (allSame ? `value="${uniqueRates[0].toFixed(2)}"` : '')} step="0.01" min="0" placeholder="${inactive ? '--' : (allSame ? '0.00' : 'Varies')}"
                    onchange="window.BillingModule.onMassUpdateRate('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}', this.value)">
                  <span style="font-size:13px;font-weight:600;color:var(--text-secondary);">${unitLabel}</span>
                </div>
                <div style="display:flex;gap:4px;flex-shrink:0;">
                  <button class="btn btn-sm" style="padding:6px 8px;border-radius:8px;border:1px solid #E2E8F0;" title="Edit across all contracts"
                    onclick="window.BillingModule.toggleMassEditForm('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}')">
                    <span class="material-icons" style="font-size:18px;color:var(--text-primary);">settings</span>
                  </button>
                  <button class="btn btn-sm" style="padding:6px 8px;border-radius:8px;border:1px solid #E2E8F0;" title="${inactive ? 'Reactivate across all contracts (restores the rate)' : 'Deactivate across all contracts — drops off driver close-out and Add Line; anything it still matches bills $0. To show an item at $0, edit it and tick Show at $0.'}"
                    onclick="window.BillingModule.onMassZeroOut('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}', ${!inactive})">
                    <span class="material-icons" style="font-size:20px;color:${inactive ? 'var(--text-secondary)' : 'var(--brand-blue)'};">${inactive ? 'toggle_off' : 'toggle_on'}</span>
                  </button>
                  <button class="btn btn-sm" style="padding:6px 8px;border-radius:8px;border:1px solid #FEE2E2;" title="Delete across all contracts"
                    onclick="window.BillingModule.onMassDelete('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}')">
                    <span class="material-icons" style="font-size:18px;color:#EF4444;">delete_outline</span>
                  </button>
                </div>
              </div>
            </div>
            <div id="mass-edit-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" style="display:none;padding:12px 16px;border:1px solid var(--border-light);border-top:2px solid var(--brand-blue);background:#FAFBFF;border-radius:0 0 10px 10px;margin-top:-6px;margin-bottom:16px;">
              <div style="font-size:12px;font-weight:600;color:var(--brand-blue);margin-bottom:8px;text-transform:uppercase;letter-spacing:0.5px;">Edit All Contracts</div>
              <div class="form-group" style="margin:0 0 10px 0;">
                <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Description</label>
                <input type="text" class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-desc-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" value="${escapeHtml(li.description || '')}">
              </div>
              <div class="form-group" style="margin:0 0 10px 0;">
                <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Revenue Category</label>
                <input type="text" list="mass-revcat-suggestions" class="form-control" autocomplete="off" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-revcat-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" value="${escapeHtml(li.revenueCategory || '')}" placeholder="e.g. Rentals, Services...">
                <datalist id="mass-revcat-suggestions">${getRevenueCategorySuggestions().map(c => `<option value="${c}">`).join('')}</datalist>
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px;">
                <div class="form-group" style="margin:0;">
                  <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Unit</label>
                  <input type="text" list="mass-edit-uom-suggestions" class="form-control" autocomplete="off" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-unit-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" value="${escapeHtml(li.rateUnit || 'Hourly')}">
                  <datalist id="mass-edit-uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
                </div>
                <div class="form-group" style="margin:0;">
                  <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Quantity</label>
                  <input type="number" class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-qty-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" min="0" value="${li.quantity || ''}">
                </div>
              </div>
              <div style="border-top:1px solid #E2E8F0;padding-top:10px;margin-bottom:10px;">
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;align-items:end;margin-bottom:8px;">
                <div class="form-group" style="margin:0;">
                  <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Applies To</label>
                  <select class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-kind-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}"
                    onchange="window.BillingModule.onMassEditKindChange('${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}', this.value)">
                    <option value="">Any</option>
                    <option value="person" ${li.appliesTo?.kind === 'person' ? 'selected' : ''}>Person</option>
                    <option value="truck" ${li.appliesTo?.kind === 'truck' ? 'selected' : ''}>Vehicle</option>
                    <option value="trailer" ${li.appliesTo?.kind === 'trailer' ? 'selected' : ''}>Trailer</option>
                    <option value="asset" ${li.appliesTo?.kind === 'asset' ? 'selected' : ''}>Asset</option>
                    <option value="misc" ${li.appliesTo?.kind === 'misc' ? 'selected' : ''}>Miscellaneous</option>
                    <option value="permit" ${li.appliesTo?.kind === 'permit' ? 'selected' : ''}>Load Permit</option>
                  </select>
                </div>
                <div class="form-group" style="margin:0;">
                  <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Type</label>
                  <select class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" id="mass-edit-type-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}">
                    <option value="">Any</option>
                  </select>
                </div>
              </div>
              <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;">
                <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;">
                  <input type="checkbox" id="mass-edit-ft-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" ${li.includeOnFieldTicket ? 'checked' : ''}>
                  Field Ticket
                </label>
                <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;">
                  <input type="checkbox" id="mass-edit-inv-${escapeHtml(li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_')}" ${li.includeOnInvoice !== false ? 'checked' : ''}>
                  Invoice
                </label>
                <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);">
                  Round to ½ hr
                  ${_equipRoundSelectHtml('mass-edit-rh-' + (li.itemCode || li.description || '').replace(/[^a-zA-Z0-9]/g, '_'), li.roundToHalfHour, li.appliesTo?.kind)}
                </label>
              </div>
              </div>
              <div style="display:flex;justify-content:flex-end;">
                <button class="btn btn-primary btn-sm" style="padding:6px 16px;border-radius:8px;font-size:13px;font-weight:600;" onclick="window.BillingModule.saveMassEdit('${escJsAttr(li.itemCode || '')}', '${escJsAttr(li.description || '')}')">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">check</span> Apply
                </button>
              </div>
            </div>
          `;
        }

        const custId = LineItemsState.selectedCustomerId;
        const contractId = LineItemsState.selectedContractId;
        return `
          <div class="card" style="padding:10px 12px;margin-bottom:4px;${inactive ? 'opacity:0.5;' : ''}display:flex;justify-content:space-between;align-items:center;">
            <div style="flex:1;">
              <div style="font-weight:500;font-size:14px;color:var(--text-primary);${inactive ? 'text-decoration:line-through;' : ''}">
                ${escapeHtml(li.description || 'Unnamed item')}
              </div>
              <div style="font-size:13px;color:var(--text-secondary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                <span>${inactive ? '--' : (_isNoChargeLine({ noCharge: li.noCharge, rate: li.rate }) ? 'N/C' : `$${(li.rate ?? 0).toFixed(2)}`)} / ${escapeHtml(li.rateUnit || 'Hourly')}${li.quantity ? ` x ${li.quantity}` : ''}</span>
                ${_priceBookStateBadge(li)}
                ${li.revenueCategory ? `<span style="font-size:11px;font-weight:600;background:#EFF6FF;color:#1D4ED8;padding:2px 8px;border-radius:6px;">${escapeHtml(li.revenueCategory)}</span>` : ''}
                ${li.appliesTo?.kind === 'permit' ? `<span style="font-size:11px;font-weight:600;background:#F1F5F9;color:var(--text-primary);padding:2px 8px;border-radius:6px;">${_normalizePermitMarkupPercent(li.markupPercent) !== null ? `+${_normalizePermitMarkupPercent(li.markupPercent)}% markup` : 'Company default markup'}</span>` : ''}
              </div>
            </div>
            <div style="display:flex;gap:4px;align-items:center;">
              <button class="btn btn-sm" style="padding:4px;" onclick="window.BillingModule.showLiLineItemForm('${window.escJsAttr(li.id)}')">
                <span class="material-icons" style="font-size:16px;color:var(--text-primary);">edit</span>
              </button>
              <button class="btn btn-sm" style="padding:4px;" title="Delete"
                onclick="window.BillingModule.deleteLiLineItem('${window.escJsAttr(custId)}','${window.escJsAttr(contractId)}','${window.escJsAttr(li.id)}','${escJsAttr(li.description || 'this item')}')">
                <span class="material-icons" style="font-size:16px;color:#EF4444;">delete</span>
              </button>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `).join('');
}

async function onLiCustomerChange(customerId) {
  LineItemsState.selectedCustomerId = customerId || null;
  LineItemsState.selectedContractId = null;
  LineItemsState.contracts = [];
  LineItemsState.lineItems = [];
  LineItemsState.allLineItems = [];

  if (customerId) {
    await loadLiContracts(customerId);
    if (LineItemsState.massUpdateMode) {
      await loadLiAllLineItems();
    }
  }
  renderLineItemsModalBody();
}

async function onLiContractChange(contractId) {
  LineItemsState.selectedContractId = contractId || null;
  LineItemsState.lineItems = [];

  if (contractId && LineItemsState.selectedCustomerId) {
    await loadLiLineItems(LineItemsState.selectedCustomerId, contractId);
  }
  renderLineItemsModalBody();
}

async function saveInvoiceColumns() {
  if (_priceBookBlocked()) return; // TASK-9491
  const customerId = LineItemsState.selectedCustomerId;
  const contractId = LineItemsState.selectedContractId;
  if (!customerId || !contractId) return;
  const panel = document.getElementById('li-invoice-columns-panel');
  if (!panel) return;

  const invoiceColumns = {};
  panel.querySelectorAll('[data-ic-section]').forEach(cb => {
    const sec = cb.dataset.icSection;
    const col = cb.dataset.icCol;
    if (!invoiceColumns[sec]) invoiceColumns[sec] = {};
    invoiceColumns[sec][col] = cb.checked;
  });
  // Clean up legacy 'equipment' key — replaced by vehicles/trailers/assets
  delete invoiceColumns.equipment;

  // TASK-511 — per-customer hide-by-default line types
  const invoiceExcludeLineTypes = Array.from(panel.querySelectorAll('[data-ic-exclude-type]:checked'))
    .map(cb => cb.dataset.icExcludeType);

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'customers', customerId, 'contracts', contractId), { invoiceColumns, invoiceExcludeLineTypes });
    // Update local LineItemsState contract
    const c = LineItemsState.contracts.find(c => c.id === contractId);
    if (c) { c.invoiceColumns = invoiceColumns; c.invoiceExcludeLineTypes = invoiceExcludeLineTypes; }
    // Invalidate rate cache so next invoice render picks up new columns
    delete ReviewState.contractRateCache[customerId];
    // Audit receipt (tech-support runbook) — who changed which customer's hide list to what
    const _cust = (BillingState.customers || []).find(c => c.id === customerId);
    window.logAudit?.('billing', 'CUSTOMER_INVOICE_PREFS_UPDATE', {
      customerId, contractId, customerName: _cust?.name || customerId, invoiceExcludeLineTypes
    });
    window.showToast?.('Invoice settings saved', 'success');
  } catch (err) {
    console.error('[Billing] Error saving invoice columns:', err);
    window.showToast?.('Error saving invoice settings', 'error');
  }
}

async function saveInvoiceColumnsMass() {
  // TASK-9668 — the single-contract save had this gate; the mass one (which
  // rewrites EVERY customer's contract) did not.
  if (_priceBookBlocked()) return; // TASK-9491
  const panel = document.getElementById('li-invoice-columns-panel');
  if (!panel) return;

  const invoiceColumns = {};
  panel.querySelectorAll('[data-ic-section]').forEach(cb => {
    const sec = cb.dataset.icSection;
    const col = cb.dataset.icCol;
    if (!invoiceColumns[sec]) invoiceColumns[sec] = {};
    invoiceColumns[sec][col] = cb.checked;
  });
  delete invoiceColumns.equipment;

  // TASK-511 — per-customer hide-by-default line types (mass = same list for all)
  const invoiceExcludeLineTypes = Array.from(panel.querySelectorAll('[data-ic-exclude-type]:checked'))
    .map(cb => cb.dataset.icExcludeType);

  const tenant = getTenant();
  const customers = _billableCustomers();
  if (customers.length === 0) {
    window.showToast?.('No customers found', 'error');
    return;
  }

  // Find save button and show spinner
  const btn = panel.querySelector('.btn-primary');
  const origHtml = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    let contractCount = 0;
    // Batch update all contracts for all customers
    const batch500 = [];
    let currentBatch = writeBatch(db);
    let opCount = 0;

    for (const cust of customers) {
      const contractsSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', cust.id, 'contracts'));
      for (const contractDoc of contractsSnap.docs) {
        currentBatch.update(contractDoc.ref, { invoiceColumns, invoiceExcludeLineTypes });
        contractCount++;
        opCount++;
        if (opCount >= 450) {
          batch500.push(currentBatch);
          currentBatch = writeBatch(db);
          opCount = 0;
        }
      }
    }
    if (opCount > 0) batch500.push(currentBatch);

    for (const b of batch500) { await b.commit(); }

    // Invalidate all rate caches + refresh local contract copies
    ReviewState.contractRateCache = {};
    (LineItemsState.contracts || []).forEach(c => { c.invoiceColumns = invoiceColumns; c.invoiceExcludeLineTypes = invoiceExcludeLineTypes; });
    window.logAudit?.('billing', 'CUSTOMER_INVOICE_PREFS_UPDATE', {
      scope: 'mass', contractCount, invoiceExcludeLineTypes
    });
    window.showToast?.(`Invoice settings applied to ${contractCount} contracts`, 'success');
  } catch (err) {
    console.error('[Billing] Error saving mass invoice columns:', err);
    window.showToast?.('Error saving invoice settings', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHtml; }
  }
}

async function toggleLiMassUpdate(checked) {
  // Guard: no-op if already in the requested mode
  if (checked === LineItemsState.massUpdateMode) return;
  // Guard: confirm if unchecking with pending changes
  if (!checked && LineItemsState._hasUnsavedChanges) {
    if (!confirm('You have unsaved mass update changes. Discard them?')) {
      // Re-check the checkbox since user cancelled
      renderLineItemsModalBody();
      return;
    }
    LineItemsState._pendingChanges = new Map();
    LineItemsState._pendingAdds = [];
    LineItemsState._hasUnsavedChanges = false;
  }

  LineItemsState.massUpdateMode = checked;
  LineItemsState.selectedContractId = null;
  LineItemsState.lineItems = [];
  LineItemsState.allLineItems = [];

  if (checked) {
    LineItemsState._massLoading = true;
    renderLineItemsModalBody();
    await loadLiAllLineItems();
    LineItemsState._massLoading = false;
  }
  renderLineItemsModalBody();
}

async function loadLiContracts(customerId) {
  const tenant = getTenant();
  try {
    const snap = await getDocs(
      query(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'), orderBy('title'))
    );
    LineItemsState.contracts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Billing] Error loading contracts:', err);
    LineItemsState.contracts = [];
  }
  LineItemsState.invoiceContractInfo = await loadInvoiceContractInfo(customerId);
}

// TASK-9668 (#5) — WHICH price book the invoice reads. Every invoice path —
// loadReviewContractRates (client), the server invoice/field-ticket renderers and
// ticketGateway's loadInvoiceExcludeLineTypesServer — runs the SAME query
// (contracts where status == 'Active', no orderBy) and takes docs[0] for the
// column + hidden-line-type settings. With 2+ Active contracts (Loenbro) that
// pick was invisible, so a hide list saved on the other contract silently did
// nothing. This runs that identical query and takes docs[0] the same way, so the
// Price Book screen names the contract the invoice actually reads. It does NOT
// change which one is read. null on error (no banner, never a wrong one).
async function loadInvoiceContractInfo(customerId) {
  if (!customerId) return null;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', getTenant(), 'customers', customerId, 'contracts'),
      where('status', '==', 'Active')
    ));
    return { customerId, ...invoiceContractInfoFromDocs(snap.docs.map(d => ({ id: d.id, ...d.data() }))) };
  } catch (err) {
    console.warn('[Billing] Error loading invoice contract info:', err);
    return null;
  }
}
function invoiceContractInfoFromDocs(activeDocs) {
  const docs = activeDocs || [];
  if (docs.length === 0) return { activeCount: 0, invoiceContractId: null, invoiceContractTitle: '', activeTitles: [] };
  return {
    activeCount: docs.length,
    invoiceContractId: docs[0].id,
    invoiceContractTitle: docs[0].title || 'Untitled',
    activeTitles: docs.map(d => d.title || 'Untitled'),
  };
}

// Banner for the Price Book screen when a customer has 2+ Active contracts.
// '' otherwise (one Active contract = nothing ambiguous to show).
function invoiceContractBannerHTML(info, selectedContractId) {
  if (!info || !(info.activeCount >= 2)) return '';
  const name = escapeHtml(info.invoiceContractTitle);
  const others = info.activeCount - 1;
  const onOther = selectedContractId && selectedContractId !== info.invoiceContractId;
  return `<div data-invoice-contract-banner style="display:flex;gap:10px;align-items:flex-start;padding:10px 12px;margin-bottom:12px;border:1px solid #FCD34D;background:#FFFBEB;border-radius:var(--radius-sm);">
    <span class="material-icons" style="font-size:18px;color:#B45309;flex-shrink:0;">warning_amber</span>
    <div style="min-width:0;flex:1;font-size:13px;color:var(--text-primary);line-height:1.45;">
      <div style="font-weight:600;color:#92400E;">This customer has ${info.activeCount} Active price books</div>
      Invoices take their column and hidden-line settings from <strong>${name}</strong> only${onOther ? ' — the invoice settings on the contract selected here are <strong>not used</strong> on invoices' : ''}. To avoid surprises, keep one Active price book per customer (set the other${others !== 1 ? 's' : ''} to a different status).
    </div>
    ${onOther ? `<button class="btn btn-sm btn-secondary" style="white-space:nowrap;flex-shrink:0;" onclick="window.BillingModule.onLiContractChange('${escJsAttr(info.invoiceContractId)}')">Open ${name}</button>` : ''}
  </div>`;
}

async function loadLiLineItems(customerId, contractId) {
  const tenant = getTenant();
  try {
    const snap = await getDocs(
      collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', contractId, 'lineItems')
    );
    LineItemsState.lineItems = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  } catch (err) {
    console.warn('[Billing] Error loading line items:', err);
    LineItemsState.lineItems = [];
  }
}

async function loadLiAllLineItems() {
  const tenant = getTenant();
  try {
    const customers = _billableCustomers();
    const allContractPairs = [];
    // Load all customers' contracts + line items in parallel (not sequential)
    const perCustomer = await Promise.all(customers.map(async (customer) => {
      const contractsSnap = await getDocs(
        collection(db, 'tenants', tenant, 'customers', customer.id, 'contracts')
      );
      // For each customer, load all contracts' line items in parallel
      const perContract = await Promise.all(contractsSnap.docs.map(async (contractDoc) => {
        const contract = { id: contractDoc.id, ...contractDoc.data() };
        allContractPairs.push({ customerId: customer.id, contractId: contract.id, contract });
        const liSnap = await getDocs(
          collection(db, 'tenants', tenant, 'customers', customer.id, 'contracts', contract.id, 'lineItems')
        );
        return liSnap.docs.map(d => ({
          id: d.id, ...d.data(),
          _customerId: customer.id, _customerName: customer.name,
          _contractId: contract.id, _contractTitle: contract.title || ''
        }));
      }));
      return perContract.flat();
    }));
    const allItems = perCustomer.flat();
    LineItemsState._allContracts = allContractPairs;

    // Deduplicate by itemCode (preferred) or description
    const deduped = new Map();
    allItems.forEach(item => {
      const key = item.itemCode || item.description || item.id;
      if (deduped.has(key)) {
        deduped.get(key)._contractCount++;
        deduped.get(key)._allMatches.push(item);
      } else {
        deduped.set(key, {
          ...item,
          _contractCount: 1,
          _allMatches: [item]
        });
      }
    });

    LineItemsState.allLineItems = Array.from(deduped.values());

    // Build lookup map for O(1) access in mass update functions
    LineItemsState._itemLookup = new Map();
    LineItemsState.allLineItems.forEach(item => {
      const key = item.itemCode || item.description;
      if (key) LineItemsState._itemLookup.set(key, item);
    });
  } catch (err) {
    console.warn('[Billing] Error loading all line items:', err);
    LineItemsState.allLineItems = [];
    LineItemsState._itemLookup = new Map();
  }
}

function findMassItem(itemCode, description) {
  const key = itemCode || description;
  if (LineItemsState._itemLookup && key) return LineItemsState._itemLookup.get(key);
  return LineItemsState.allLineItems.find(li =>
    (itemCode && li.itemCode === itemCode) || (!itemCode && li.description === description)
  );
}

function showLiLineItemForm(lineItemId) {
  const li = lineItemId ? (LineItemsState.lineItems || []).find(l => l.id === lineItemId) : null;
  renderLiLineItemFormUI(li);
}

function renderLiLineItemFormUI(li) {
  const area = document.getElementById('li-lineitem-form-area');
  if (!area) return;

  const isEdit = !!li;
  // TASK-9672 follow-up — Manage Item Codes reloads ReviewState.itemCodes into a
  // NEW array; read it first so a code added there shows here.
  const itemCodes = (ReviewState.itemCodes && ReviewState.itemCodes.length ? ReviewState.itemCodes : LineItemsState.itemCodes) || [];

  area.innerHTML = `
    <div style="padding:12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-elevated);">
      <div style="font-weight:600;margin-bottom:8px;color:var(--text-primary);">${isEdit ? 'Edit Line Item' : 'Add Line Item'}</div>
      <input type="hidden" id="li-form-id" value="${li?.id || ''}">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Applies To</label>
          <select id="li-form-applies-kind" class="form-control" onchange="window.BillingModule.onAppliesToKindChange()">
            <option value="" ${!li?.appliesTo?.kind ? 'selected' : ''}>Any</option>
            <option value="person" ${li?.appliesTo?.kind === 'person' ? 'selected' : ''}>Person</option>
            <option value="truck" ${li?.appliesTo?.kind === 'truck' ? 'selected' : ''}>Vehicle</option>
            <option value="trailer" ${li?.appliesTo?.kind === 'trailer' ? 'selected' : ''}>Trailer</option>
            <option value="asset" ${li?.appliesTo?.kind === 'asset' ? 'selected' : ''}>Asset</option>
            <option value="misc" ${li?.appliesTo?.kind === 'misc' ? 'selected' : ''}>Miscellaneous</option>
            <option value="permit" ${li?.appliesTo?.kind === 'permit' ? 'selected' : ''}>Load Permit</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Type</label>
          <select id="li-form-applies-type" class="form-control" ${!li?.appliesTo?.kind ? 'disabled' : ''}>
            <option value="">Any Type</option>
          </select>
        </div>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;color:var(--text-primary);">Description *</label>
        <input type="text" id="li-form-desc" class="form-control" value="${escapeHtml(li?.description || '')}">
      </div>
      <!-- TASK-9672 follow-up — the line's Item code (was a hidden input); rendered by the lazy chunk. -->
      <div id="li-form-itemcode-slot" class="form-group" style="margin-bottom:8px;"></div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;color:var(--text-primary);">Revenue Category</label>
        <input type="text" id="li-form-revenue-cat" list="revenue-cat-suggestions" class="form-control" placeholder="e.g. Rentals, Services, Materials..."
          value="${escapeHtml(li?.revenueCategory || '')}" autocomplete="off">
        <datalist id="revenue-cat-suggestions">${getRevenueCategorySuggestions().map(c => `<option value="${c}">`).join('')}</datalist>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Rate ($)</label>
          <input type="number" id="li-form-rate" class="form-control" step="0.01" min="0" value="${li?.noCharge === true ? '' : (li?.rate || '')}"
            placeholder="${li?.noCharge === true ? 'No charge' : 'Needs a price'}" ${li?.noCharge === true ? 'disabled' : ''}>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;color:var(--text-primary);">Unit</label>
          <input type="text" id="li-form-unit" list="uom-suggestions" class="form-control" value="${escapeHtml(li?.rateUnit || 'Hourly')}"
            autocomplete="off" onchange="window.BillingModule.onUomChange()">
          <datalist id="uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
        </div>
        <div class="form-group" id="li-form-qty-group" style="margin-bottom:8px;${isTimeBasedUnit(li?.rateUnit) ? 'display:none;' : ''}">
          <label style="font-size:13px;color:var(--text-primary);">Qty</label>
          <input type="number" id="li-form-qty" class="form-control" step="0.01" min="0" value="${li?.quantity || ''}">
        </div>
      </div>
      <!-- TASK-9666 — "Show at $0" is a deliberate state (item stays active, shows
           on driver close-out and Add Line). A blank rate is NOT $0 by choice:
           it saves flagged "Needs price". -->
      <div class="form-group" style="margin-bottom:8px;">
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
          <input type="checkbox" id="li-form-show-zero" ${li?.noCharge === true ? 'checked' : ''}
            onchange="(function(c){var r=document.getElementById('li-form-rate');if(r){r.disabled=c;if(c)r.value='';r.placeholder=c?'No charge':'Needs a price';}})(this.checked)">
          Show at $0 (no charge)
        </label>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Shows on the invoice at $0.00 — the customer sees what was used, but isn't charged.</div>
      </div>
      <div class="form-group" id="li-form-markup-group" style="margin-bottom:8px;${li?.appliesTo?.kind === 'permit' ? '' : 'display:none;'}">
        <label for="li-form-markup" style="font-size:13px;color:var(--text-primary);">Markup % on permit cost</label>
        <input type="number" id="li-form-markup" class="form-control" step="0.01" min="0" max="100" style="max-width:140px;"
          value="${li?.markupPercent !== undefined && li?.markupPercent !== null ? escapeHtml(String(li.markupPercent)) : ''}"
          placeholder="${escapeHtml(String(_normalizePermitMarkupPercent(window.AppState?.settings?.defaultPermitMarkupPercent) ?? 0))}">
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Added on top of the permit cost on this customer's tickets. Leave blank to use your company default (Billing Settings). You can still change it on any ticket.</div>
      </div>
      <div style="display:flex;align-items:center;gap:16px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="checkbox" id="li-form-field-ticket" ${li?.includeOnFieldTicket ? 'checked' : ''}>
          <label for="li-form-field-ticket" style="font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">Field Ticket</label>
        </div>
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="checkbox" id="li-form-invoice" ${li?.includeOnInvoice !== false ? 'checked' : ''}>
          <label for="li-form-invoice" style="font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;" title="Unticked: with Billing Settings → Hide individual Price Book items on, this item's field-ticket and misc lines stay off this customer's invoice. Taxable items can't be hidden per item yet — a priced line that would be taxed stays on.">Invoice</label>
        </div>
        <div id="li-form-round-group" style="display:flex;align-items:center;gap:6px;${!isTimeBasedUnit(li?.rateUnit) ? 'display:none;' : ''}">
          <label for="li-form-round-half-hour" style="font-size:13px;color:var(--text-primary);margin:0;">Round to ½ hr</label>
          ${_equipRoundSelectHtml('li-form-round-half-hour', li?.roundToHalfHour, li?.appliesTo?.kind)}
        </div>
      </div>
      ${window.AppState?.settings?.taxEnabled === true ? `<div style="font-size:12px;color:var(--text-secondary);margin:-4px 0 8px;">Sales tax is on: a priced item stays on the invoice even with Invoice unticked. Only $0 items can be hidden per item for now.</div>` : ''}
      <div id="li-form-stock-slot" class="form-group" style="margin-bottom:8px;"></div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.cancelLiForm()">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.BillingModule.saveLiLineItem()">Save</button>
      </div>
    </div>
  `;
  setTimeout(() => {
    document.getElementById('li-form-desc')?.focus();
    // Pre-fill type dropdown if editing an existing item with appliesTo
    if (li?.appliesTo?.kind) onAppliesToKindChange(li.appliesTo.type || '');
  }, 100);
  _fillLiStockAssetSelect(li);
  _icMod().then(m => m.renderItemCodeField('li-form-itemcode', li?.itemCode, itemCodes, escapeHtml))
    .catch(e => console.warn('[Billing] item code picker failed to load (code kept as is):', e?.message));
}

// TASK-9730 — the "Takes stock from" picker fills from the LAZY views/billingStockLink.js.
function _fillLiStockAssetSelect(li) {
  __ddChunkWrap(import('./billingStockLink.js?v=9730002')).then(m => m.fillLiStockAssetSelect(li, getTenant(), escapeHtml))
    .catch(e => console.warn('[Billing] stock picker failed to load (link kept as is):', e?.message));
}

function onAppliesToKindChange(preselect) {
  const kind = document.getElementById('li-form-applies-kind')?.value;
  const typeSelect = document.getElementById('li-form-applies-type');
  // TASK-9436 — the markup field belongs to permit items only.
  const _mkGroup = document.getElementById('li-form-markup-group');
  if (_mkGroup) _mkGroup.style.display = kind === 'permit' ? '' : 'none';
  if (!typeSelect) return;

  if (!kind || kind === 'misc' || kind === 'permit') {
    typeSelect.innerHTML = '<option value="">Any Type</option>';
    typeSelect.disabled = true;
    return;
  }

  const kindMap = {
    person: 'crewTypes',
    truck: 'truckTypes',
    trailer: 'trailerTypes',
    asset: 'assetCategories'
  };
  const typeList = window.getTypeList?.(kindMap[kind]) || [];
  typeSelect.innerHTML = '<option value="">Any Type</option>' +
    typeList.map(t => `<option value="${escapeHtml(t.slug)}">${escapeHtml(t.label)}</option>`).join('');
  typeSelect.disabled = false;

  if (preselect) typeSelect.value = preselect;
}

function onUomChange() {
  const unit = document.getElementById('li-form-unit')?.value?.trim() || 'Hourly';
  const _timeBased = isTimeBasedUnit(unit);
  const qtyGroup = document.getElementById('li-form-qty-group');
  const roundGroup = document.getElementById('li-form-round-group');
  if (qtyGroup) qtyGroup.style.display = _timeBased ? 'none' : '';
  if (roundGroup) roundGroup.style.display = _timeBased ? '' : 'none';
}

async function saveLiLineItem() {
  if (_priceBookBlocked()) return; // TASK-9491
  const custId = LineItemsState.selectedCustomerId;
  const contractId = LineItemsState.selectedContractId;
  if (!custId || !contractId) { window.showToast?.('No customer/contract selected', 'error'); return; }

  const desc = document.getElementById('li-form-desc')?.value.trim();
  if (!desc) { window.showToast?.('Description is required', 'error'); return; }

  const existingId = document.getElementById('li-form-id')?.value;
  const qtyVal = parseFloat(document.getElementById('li-form-qty')?.value);
  const rateUnit = document.getElementById('li-form-unit')?.value?.trim() || 'Hourly';

  // Applies To (kind + type)
  const appliesToKind = document.getElementById('li-form-applies-kind')?.value || '';
  const appliesToType = document.getElementById('li-form-applies-type')?.value || '';
  const includeOnFieldTicket = document.getElementById('li-form-field-ticket')?.checked || false;
  const includeOnInvoice = document.getElementById('li-form-invoice')?.checked ?? true;
  // TASK-9489 — three-way (null = Company default). A per-unit item never
  // rounds anyway; it saves Company default so a later switch to Hourly behaves.
  const roundToHalfHour = isTimeBasedUnit(rateUnit) ? _readEquipRoundSelect('li-form-round-half-hour', null) : null;

  const revenueCategory = document.getElementById('li-form-revenue-cat')?.value?.trim() || '';
  // TASK-9730 — "Takes stock from": written only when set, or to clear a link.
  const _prevLi = existingId ? (LineItemsState.lineItems || []).find(l => l.id === existingId) : null;
  const _stockSel = document.getElementById('li-form-stock-asset');
  const _stockAssetId = _stockSel ? (_stockSel.value || '') : (_prevLi?.stockAssetId || '');
  const _stockAssetName = _stockAssetId && _stockSel ? (_stockSel.selectedOptions?.[0]?.textContent || '').replace(/ \((not counted now|-?\d+ on hand)\)$/, '').slice(0, 100) : '';
  // TASK-9666 — rate + its state (priced / Show at $0 / needs a price).
  const _rateFields = _priceBookRateFields(
    document.getElementById('li-form-rate')?.value,
    document.getElementById('li-form-show-zero')?.checked === true,
    appliesToKind);

  const data = {
    category: KIND_TO_CATEGORY[appliesToKind] || 'Other',
    revenueCategory,
    // TASK-9672 follow-up — visible picker now. No picker on screen (chunk not
    // loaded yet / failed): null → dropped below, so the stored code is never
    // overwritten with a stale cached one (2128 COD Edge Case).
    itemCode: (() => { const s = document.getElementById('li-form-itemcode'); return s ? String(s.value || '').trim() : null; })(),
    description: desc,
    rate: _rateFields.rate,
    noCharge: _rateFields.noCharge,
    needsPrice: _rateFields.needsPrice,
    rateUnit,
    quantity: isNaN(qtyVal) ? null : qtyVal,
    active: true,
    appliesTo: appliesToKind ? { kind: appliesToKind, type: appliesToType || null } : null,
    includeOnFieldTicket,
    includeOnInvoice,
    roundToHalfHour,
    updatedAt: Timestamp.now()
  };
  if (data.itemCode === null) { if (existingId) delete data.itemCode; else data.itemCode = ''; }
  if (_stockAssetId) { data.stockAssetId = _stockAssetId; data.stockAssetName = _stockAssetName; }
  else if (_prevLi?.stockAssetId) { data.stockAssetId = null; data.stockAssetName = null; }

  // TASK-9436 — permit markup %, permit items only. Blank = use the company
  // default (stored as null so the resolver falls through); never a silent 0.
  if (appliesToKind === 'permit') {
    const _mkRaw = document.getElementById('li-form-markup')?.value?.trim() ?? '';
    if (_mkRaw === '') {
      data.markupPercent = null;
    } else {
      const _mk = _normalizePermitMarkupPercent(_mkRaw);
      if (_mk === null) { window.showToast?.('Markup must be between 0% and 100%', 'error'); return; }
      data.markupPercent = _mk;
    }
  }

  // Validate: no duplicate appliesTo match (same kind+type on another line item)
  // Skip for misc/permit — multiple line items are expected
  if (data.appliesTo?.kind && data.appliesTo.kind !== 'misc' && data.appliesTo.kind !== 'permit') {
    const conflict = (LineItemsState.lineItems || []).find(li => {
      if (existingId && li.id === existingId) return false; // skip self
      if (li.appliesTo?.kind !== data.appliesTo.kind) return false;
      // Exact type match or either is "Any" (null)
      const liType = li.appliesTo?.type || null;
      const newType = data.appliesTo?.type || null;
      return liType === newType || !liType || !newType;
    });
    if (conflict) {
      const conflictDesc = conflict.description || conflict.itemCode || 'another item';
      const typeLabel = data.appliesTo.type || 'Any';
      window.showToast?.(`Conflicts with "${conflictDesc}" — both apply to ${data.appliesTo.kind} / ${typeLabel}`, 'error');
      return;
    }
  }

  const tenant = getTenant();
  const basePath = `tenants/${tenant}/customers/${custId}/contracts/${contractId}/lineItems`;

  try {
    // TASK-9436 — old value read from the STORED doc (not the list cache) so
    // the audit row's `from` is what was actually in force.
    let _mkBefore = null;
    // TASK-9672 follow-up — the stored item code, same reason.
    let _icBefore = existingId ? String(_prevLi?.itemCode || '') : '';
    let _storedRead = !existingId;
    if (existingId) {
      try {
        const _bs = await getDoc(doc(db, basePath, existingId));
        if (_bs.exists()) {
          if ('markupPercent' in data) _mkBefore = _normalizePermitMarkupPercent(_bs.data().markupPercent);
          _icBefore = String(_bs.data().itemCode || '');
          _storedRead = true;
        }
      } catch (_) { /* audit reads best-effort; the save still runs */ }
    }
    // TASK-9672 follow-up — unreadable stored line (fail closed on a change),
    // ask before a code change (money), the same-code warning: the lazy chunk's
    // itemCodeSaveGate, shared with crm.js (2128 COD folds, rules in the chunk).
    const _ic = await _icMod();
    const _toast = (m, t) => window.showToast?.(m, t);
    const _g = await _ic.itemCodeSaveGate({ data, existingId, storedRead: _storedRead, before: _icBefore, cachedCode: _prevLi?.itemCode,
      ctx: { customerId: custId, contractId, lineItemId: existingId || null, description: desc, isNew: !existingId },
      toast: _toast, confirm: (a, b, c) => window.showConfirm?.(a, b, c) });
    if (!_g) return;
    const _icAudit = _g.audit;
    if (_icAudit || !existingId) _ic.warnItemCodeTwin(_ic.itemCodeTwin(LineItemsState.lineItems, data.itemCode, existingId || null), data.itemCode, _toast);
    let _savedId = existingId;
    if (existingId) {
      await updateDoc(doc(db, basePath, existingId), data);
    } else {
      data.createdAt = Timestamp.now();
      data.sortOrder = (LineItemsState.lineItems || []).length;
      const _newRef = await addDoc(collection(db, basePath), data);
      _savedId = _newRef.id;
      // Increment _lineItemCount on contract
      const contractRef = doc(db, 'tenants', tenant, 'customers', custId, 'contracts', contractId);
      const contractSnap = await getDoc(contractRef);
      if (contractSnap.exists()) {
        const currentCount = contractSnap.data()._lineItemCount || 0;
        await updateDoc(contractRef, { _lineItemCount: currentCount + 1 });
      }
    }
    // TASK-9730 — which stock a Price Book item draws: log old -> new.
    if ('stockAssetId' in data && (data.stockAssetId || null) !== (_prevLi?.stockAssetId || null)) {
      try {
        await window.logAudit?.('billing', 'PRICE_BOOK_STOCK_LINK_CHANGE', {
          customerId: custId, contractId, lineItemId: _savedId, description: desc,
          from: _prevLi?.stockAssetId || null, to: data.stockAssetId || null, isNew: !existingId,
        });
      } catch (auditErr) { console.warn('[Billing] Stock link audit failed (non-blocking):', auditErr); }
    }
    // TASK-9672 follow-up — money-governing: the item code logs old + new.
    if (_icAudit) {
      try { await window.logAudit?.('billing', 'PRICE_BOOK_ITEM_CODE_CHANGE', { ..._icAudit, lineItemId: _savedId, source: 'billing-price-book-line-form' }); }
      catch (auditErr) { console.warn('[Billing] Item code audit failed (non-blocking):', auditErr); }
    }
    // TASK-9436 — money-governing: a Price Book permit markup change logs old + new.
    if ('markupPercent' in data) {
      const _mkAudit = _permitMarkupPriceBookAudit(_mkBefore, data.markupPercent, {
        customerId: custId, contractId, lineItemId: _savedId, description: desc, isNew: !existingId,
      });
      if (_mkAudit) {
        try { await window.logAudit?.('billing', 'PRICE_BOOK_PERMIT_MARKUP_CHANGE', _mkAudit); }
        catch (auditErr) { console.warn('[Billing] Permit markup audit failed (non-blocking):', auditErr); }
      }
    }
    window.showToast?.(
      (existingId ? 'Line item updated' : 'Line item added')
        + (data.needsPrice ? ' — no price yet, flagged "Needs price". Tick "Show at $0" if it is meant to be free.' : ''),
      data.needsPrice ? 'warning' : 'success');
    cancelLiForm();
    await loadLiLineItems(custId, contractId);
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Save line item error:', err);
    window.showToast?.('Failed to save line item', 'error');
  }
}

async function toggleLiLineItem(custId, contractId, lineItemId, newActive) {
  if (_priceBookBlocked()) return; // TASK-9491
  const tenant = getTenant();
  try {
    const liRef = doc(db, 'tenants', tenant, 'customers', custId, 'contracts', contractId, 'lineItems', lineItemId);
    if (newActive) {
      // Restore: put originalRate back, mark active
      const snap = await getDoc(liRef);
      const d = snap.exists() ? snap.data() : {};
      const restored = d.originalRate != null ? d.originalRate : d.rate ?? 0;
      await updateDoc(liRef, { active: true, rate: restored, updatedAt: Timestamp.now() });
      window.showToast?.(`Restored to $${restored.toFixed(2)}`, 'success');
    } else {
      // Deactivate (TASK-9666: was labelled "No Charge", which it never was —
      // inactive items drop off driver close-out and Add Line). Saves the current
      // rate as originalRate and sets rate to $0, unchanged.
      const snap = await getDoc(liRef);
      const d = snap.exists() ? snap.data() : {};
      const currentRate = d.rate ?? 0;
      await updateDoc(liRef, { active: false, originalRate: currentRate, rate: 0, updatedAt: Timestamp.now() });
      window.showToast?.('Deactivated', 'success');
    }
    await loadLiLineItems(custId, contractId);
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Toggle line item error:', err);
    window.showToast?.('Failed to update line item', 'error');
  }
}

async function deleteLiLineItem(custId, contractId, lineItemId, description) {
  if (_priceBookBlocked()) return; // TASK-9491
  if (!confirm(`Delete "${description}"? This cannot be undone.`)) return;
  const tenant = getTenant();
  try {
    await deleteDoc(
      doc(db, 'tenants', tenant, 'customers', custId, 'contracts', contractId, 'lineItems', lineItemId)
    );
    window.showToast?.('Line item deleted', 'success');
    await loadLiLineItems(custId, contractId);
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Delete line item error:', err);
    window.showToast?.('Failed to delete line item', 'error');
  }
}

function cancelLiForm() {
  const liArea = document.getElementById('li-lineitem-form-area');
  if (liArea) liArea.innerHTML = '';
  const contractArea = document.getElementById('li-contract-form-area');
  if (contractArea) contractArea.innerHTML = '';
}

function onMassUpdateRate(itemCode, description, newRateStr) {
  const newRate = parseFloat(newRateStr);
  if (isNaN(newRate) || newRate < 0) { window.showToast?.('Invalid rate', 'error'); return; }

  const entry = findMassItem(itemCode, description);
  if (!entry || !entry._allMatches) return;

  const key = itemCode || description;
  const existing = LineItemsState._pendingChanges.get(key) || {};
  existing.newRate = newRate;
  LineItemsState._pendingChanges.set(key, existing);
  LineItemsState._hasUnsavedChanges = true;

  // Re-render to show MODIFIED badge on all changed items
  renderLineItemsModalBody();
}

function toggleMassEditForm(itemCode, description) {
  const key = (itemCode || description || '').replace(/[^a-zA-Z0-9]/g, '_');
  const el = document.getElementById(`mass-edit-${key}`);
  if (!el) return;
  const isHidden = el.style.display === 'none';
  el.style.display = isHidden ? 'block' : 'none';
  if (isHidden) {
    // Find the line item to get its saved appliesTo
    const entry = findMassItem(itemCode, description);
    // Populate type dropdown based on current kind
    const kindEl = document.getElementById(`mass-edit-kind-${key}`);
    if (kindEl && kindEl.value) {
      onMassEditKindChange(key, kindEl.value);
      // Pre-select saved type value
      const savedType = entry?.appliesTo?.type || '';
      if (savedType) {
        const typeEl = document.getElementById(`mass-edit-type-${key}`);
        if (typeEl) typeEl.value = savedType;
      }
    }
  }
}

function onMassEditKindChange(key, kind) {
  const typeEl = document.getElementById(`mass-edit-type-${key}`);
  if (!typeEl) return;
  typeEl.innerHTML = '<option value="">Any</option>';
  if (!kind || kind === 'misc' || kind === 'permit') return;
  const categoryMap = { truck: 'truckTypes', trailer: 'trailerTypes', person: 'crewTypes', asset: 'assetCategories' };
  const types = window.getTypeList ? window.getTypeList(categoryMap[kind] || '') : [];
  types.forEach(t => {
    const opt = document.createElement('option');
    opt.value = t.slug || t.value || t;
    opt.textContent = t.label || t.value || t;
    typeEl.appendChild(opt);
  });
}

function saveMassEdit(itemCode, description) {
  const entry = findMassItem(itemCode, description);
  if (!entry || !entry._allMatches) return;

  const safeKey = (itemCode || description || '').replace(/[^a-zA-Z0-9]/g, '_');

  // Core fields
  const newDesc = document.getElementById(`mass-edit-desc-${safeKey}`)?.value?.trim() || '';
  const newUnit = document.getElementById(`mass-edit-unit-${safeKey}`)?.value || 'Hourly';
  const newQtyStr = document.getElementById(`mass-edit-qty-${safeKey}`)?.value;
  const newQty = newQtyStr ? parseFloat(newQtyStr) : null;

  // Advanced fields
  const kind = document.getElementById(`mass-edit-kind-${safeKey}`)?.value || '';
  const type = document.getElementById(`mass-edit-type-${safeKey}`)?.value || '';
  const fieldTicket = document.getElementById(`mass-edit-ft-${safeKey}`)?.checked || false;
  const invoice = document.getElementById(`mass-edit-inv-${safeKey}`)?.checked ?? true;
  // TASK-9667 — the Invoice box here was read and then DROPPED by the save (it
  // never reached Firestore). Now it is written, but only when the biller
  // actually changed it from what the row showed: an all-contracts edit of the
  // description must not reset a per-customer hide on every contract.
  const invoiceChanged = invoice !== (entry.includeOnInvoice !== false);
  // TASK-9489 — three-way, and written only when the biller CHANGED it (same
  // reason as invoiceChanged above): an all-contracts edit of the description
  // must not reset a per-customer rounding choice on every contract.
  const _rhBefore = entry.roundToHalfHour === true ? true : (entry.roundToHalfHour === false ? false : null);
  const roundHalf = _readEquipRoundSelect(`mass-edit-rh-${safeKey}`, _rhBefore);
  const roundChanged = roundHalf !== _rhBefore;

  const newRevCat = document.getElementById(`mass-edit-revcat-${safeKey}`)?.value?.trim() || '';

  const appliesTo = kind ? { kind, ...(type ? { type } : {}) } : null;
  const editData = {
    includeOnFieldTicket: fieldTicket,
    includeOnInvoice: invoice,
    invoiceChanged,
    roundToHalfHour: roundHalf,
    roundChanged,
    appliesTo,
    description: newDesc,
    category: KIND_TO_CATEGORY[kind] || 'Other',
    revenueCategory: newRevCat,
    rateUnit: newUnit,
    quantity: isNaN(newQty) ? null : newQty
  };

  const pendingKey = itemCode || description;
  const existing = LineItemsState._pendingChanges.get(pendingKey) || {};
  existing.editData = editData;
  LineItemsState._pendingChanges.set(pendingKey, existing);
  LineItemsState._hasUnsavedChanges = true;

  // Update in-memory entry so it re-groups into the correct category immediately
  if (appliesTo) entry.appliesTo = appliesTo;
  if (newDesc) entry.description = newDesc;
  entry.category = editData.category;
  entry.rateUnit = newUnit;
  if (entry._allMatches) {
    entry._allMatches.forEach(m => {
      if (appliesTo) m.appliesTo = appliesTo;
      m.category = editData.category;
      m.rateUnit = newUnit;
    });
  }

  // Close the edit panel and re-render to show MODIFIED badge
  renderLineItemsModalBody();
  updateMassSaveBar();
}

function onMassZeroOut(itemCode, description, zeroOut) {
  const entry = findMassItem(itemCode, description);
  if (!entry || !entry._allMatches) return;

  const key = itemCode || description;
  const existing = LineItemsState._pendingChanges.get(key) || {};
  existing.zeroOut = zeroOut;
  LineItemsState._pendingChanges.set(key, existing);
  LineItemsState._hasUnsavedChanges = true;

  // Update in-memory entries so UI reflects change immediately
  if (zeroOut) {
    entry.active = false;
    entry._allMatches.forEach(m => {
      if (!m.hasOwnProperty('_origRate')) m._origRate = m.rate ?? 0;
      m.active = false;
      m.rate = 0;
    });
  } else {
    entry.active = true;
    entry._allMatches.forEach(m => { m.active = true; m.rate = m._origRate ?? m.originalRate ?? m.rate ?? 0; });
  }

  renderLineItemsModalBody();
}

function onMassDelete(itemCode, description) {
  const entry = findMassItem(itemCode, description);
  if (!entry || !entry._allMatches) return;

  const key = itemCode || description;
  const existing = LineItemsState._pendingChanges.get(key) || {};
  existing.deleted = true;
  LineItemsState._pendingChanges.set(key, existing);
  LineItemsState._hasUnsavedChanges = true;

  // Mark in-memory for strikethrough rendering
  entry._pendingDelete = true;
  renderLineItemsModalBody();
}

function undoMassDelete(itemCode, description) {
  const entry = findMassItem(itemCode, description);
  if (!entry) return;

  const key = itemCode || description;
  const existing = LineItemsState._pendingChanges.get(key);
  if (existing) {
    delete existing.deleted;
    // If no other pending changes remain for this key, remove it
    if (existing.newRate == null && existing.zeroOut == null && !existing.editData) {
      LineItemsState._pendingChanges.delete(key);
    }
  }
  LineItemsState._hasUnsavedChanges = LineItemsState._pendingChanges.size > 0 || (LineItemsState._pendingAdds || []).length > 0;

  entry._pendingDelete = false;
  renderLineItemsModalBody();
}

function updateMassSaveBar() {
  const pending = LineItemsState._pendingChanges;
  const addCount = (LineItemsState._pendingAdds || []).length;
  const existing = document.getElementById('mass-save-bar');
  if (pending.size === 0 && addCount === 0) {
    if (existing) existing.remove();
    return;
  }
  let modCount = 0, delCount = 0;
  pending.forEach(v => { if (v.deleted) delCount++; else modCount++; });
  const totalCount = modCount + delCount + addCount;
  const parts = [];
  if (addCount) parts.push(`${addCount} new`);
  if (modCount) parts.push(`${modCount} modified`);
  if (delCount) parts.push(`${delCount} deleted`);

  if (existing) {
    // Patch in-place (no full re-render — preserves input focus)
    existing.querySelector('div:first-child').textContent = parts.join(', ');
    const saveBtn = document.getElementById('btn-mass-save-all');
    if (saveBtn) saveBtn.innerHTML = `<span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">save</span> Save All (${totalCount})`;
  } else {
    // Bar doesn't exist yet — full re-render needed
    renderLineItemsModalBody();
  }
}

function confirmMassChanges() {
  const pending = LineItemsState._pendingChanges;
  const pendingAdds = LineItemsState._pendingAdds || [];
  if (pending.size === 0 && pendingAdds.length === 0) return;

  // Build summary of what will change per contract
  const contractChanges = new Map(); // contractKey → { customerName, contractTitle, changes[] }

  pending.forEach((change, key) => {
    const entry = LineItemsState._itemLookup?.get(key) || LineItemsState.allLineItems.find(li =>
      (li.itemCode && li.itemCode === key) || (!li.itemCode && li.description === key)
    );
    if (!entry || !entry._allMatches) return;

    for (const match of entry._allMatches) {
      const ck = `${match._customerId}||${match._contractId}`;
      if (!contractChanges.has(ck)) {
        contractChanges.set(ck, { customerName: match._customerName || 'Unknown', contractTitle: match._contractTitle || 'Contract', changes: [] });
      }
      const itemLabel = entry.itemCode ? `${entry.itemCode} — ${entry.description || ''}` : (entry.description || 'Line item');

      if (change.deleted) {
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'delete', detail: 'Will be removed' });
      } else if (change.zeroOut === true) {
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'zero', detail: `$${match.rate || 0} → $0 (Deactivated)` });
      } else if (change.zeroOut === false) {
        const restored = match._origRate ?? match.originalRate ?? match.rate ?? 0;
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'restore', detail: `Reactivated at $${restored}` });
      } else if (change.newRate != null && change.editData) {
        const parts = [];
        if (change.newRate !== match.rate) parts.push(`Rate: $${match.rate || 0} → $${change.newRate}`);
        if (change.editData.description && change.editData.description !== entry.description) parts.push(`Description updated`);
        if (change.editData.rateUnit && change.editData.rateUnit !== match.rateUnit) parts.push(`Unit: ${match.rateUnit || '?'} → ${change.editData.rateUnit}`);
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'edit', detail: parts.join(', ') || 'Metadata updated' });
      } else if (change.newRate != null) {
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'rate', detail: `$${match.rate || 0}${formatUnitLabel(match.rateUnit)} → $${change.newRate}${formatUnitLabel(match.rateUnit)}` });
      } else if (change.editData) {
        const parts = [];
        if (change.editData.description && change.editData.description !== entry.description) parts.push(`Description updated`);
        if (change.editData.rateUnit && change.editData.rateUnit !== match.rateUnit) parts.push(`Unit: ${match.rateUnit || '?'} → ${change.editData.rateUnit}`);
        contractChanges.get(ck).changes.push({ item: itemLabel, action: 'edit', detail: parts.join(', ') || 'Metadata updated' });
      }
    }
  });

  // Build adds summary
  const allContracts = LineItemsState._allContracts || [];
  let addsHtml = '';
  if (pendingAdds.length > 0) {
    const addItems = pendingAdds.map(pa => `<li style="margin-bottom:2px;"><strong>${escapeHtml(pa.description || 'New item')}</strong> — $${pa.rate || 0}${escapeHtml(formatUnitLabel(pa.rateUnit))}</li>`).join('');
    addsHtml = `
      <div style="margin-bottom:16px;padding:12px 16px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:10px;">
        <div style="font-weight:700;font-size:0.8rem;color:#166534;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;">add_circle</span> Adding to ALL ${allContracts.length} contracts:
        </div>
        <ul style="margin:0;padding-left:20px;font-size:0.8rem;color:var(--text-primary);">${addItems}</ul>
      </div>`;
  }

  // Build per-contract change list
  let contractsHtml = '';
  const actionColors = { delete: '#DC2626', zero: '#F59E0B', restore: '#16A34A', rate: '#3B82F6', edit: '#8B5CF6' };
  const actionIcons = { delete: 'delete', zero: 'money_off', restore: 'undo', rate: 'attach_money', edit: 'edit' };

  for (const [, info] of contractChanges) {
    const changeRows = info.changes.map(c => `
      <div style="display:flex;align-items:center;gap:8px;padding:4px 0;font-size:0.78rem;">
        <span class="material-icons" style="font-size:14px;color:${actionColors[c.action] || '#475569'};">${actionIcons[c.action] || 'edit'}</span>
        <span style="font-weight:600;color:var(--text-primary);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:200px;">${escapeHtml(c.item)}</span>
        <span style="color:var(--text-secondary);">→</span>
        <span style="color:${actionColors[c.action] || 'var(--text-primary)'};font-weight:500;">${escapeHtml(c.detail)}</span>
      </div>
    `).join('');

    contractsHtml += `
      <div style="margin-bottom:12px;padding:12px 16px;background:var(--bg-primary);border:1px solid var(--border-color);border-radius:10px;">
        <div style="font-weight:700;font-size:0.8rem;color:var(--text-primary);margin-bottom:6px;">${escapeHtml(info.customerName)}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-bottom:8px;">${escapeHtml(info.contractTitle)}</div>
        ${changeRows}
      </div>`;
  }

  const totalContracts = contractChanges.size + (pendingAdds.length > 0 ? allContracts.length : 0);

  // Show confirmation modal
  let overlay = document.getElementById('mass-confirm-overlay');
  if (overlay) overlay.remove();

  overlay = document.createElement('div');
  overlay.id = 'mass-confirm-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:16px;max-width:560px;width:100%;max-height:80vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,0.3);">
      <div style="padding:20px 24px 12px;border-bottom:1px solid var(--border-light);">
        <h3 style="margin:0;font-size:1rem;font-weight:700;color:var(--text-primary);display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:22px;color:#F59E0B;">warning</span>
          Review Changes Before Saving
        </h3>
        <p style="margin:8px 0 0;font-size:0.8rem;color:var(--text-secondary);">
          The following changes will be applied across <strong>${totalContracts} contract${totalContracts !== 1 ? 's' : ''}</strong>. Please review carefully.
        </p>
      </div>
      <div style="padding:16px 24px;overflow-y:auto;flex:1;">
        ${addsHtml}
        ${contractsHtml || '<div style="color:var(--text-secondary);font-size:0.85rem;">No contract-level changes.</div>'}
      </div>
      <div style="padding:16px 24px 20px;border-top:1px solid var(--border-light);display:flex;justify-content:flex-end;gap:10px;background:linear-gradient(to bottom,#F8FAFC,#fff);border-radius:0 0 16px 16px;">
        <button class="btn btn-secondary" style="padding:10px 20px;border-radius:8px;" onclick="document.getElementById('mass-confirm-overlay').remove()">Cancel</button>
        <button class="btn btn-primary" style="padding:10px 20px;border-radius:8px;box-shadow:0 2px 8px rgba(59,130,246,0.3);" onclick="document.getElementById('mass-confirm-overlay').remove();window.BillingModule.saveAllMassChanges();">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">check</span> Apply Changes
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

async function saveAllMassChanges() {
  if (_priceBookBlocked()) return; // TASK-9491
  const pending = LineItemsState._pendingChanges;
  const pendingAdds = LineItemsState._pendingAdds || [];
  if (pending.size === 0 && pendingAdds.length === 0) return;

  const btn = document.getElementById('btn-mass-save-all');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  const tenant = getTenant();
  try {
    // Collect all Firestore operations
    const ops = []; // { type: 'update'|'delete'|'set', ref, data, contractRef? }
    pending.forEach((change, key) => {
      const entry = LineItemsState._itemLookup?.get(key) || LineItemsState.allLineItems.find(li =>
        (li.itemCode && li.itemCode === key) || (!li.itemCode && li.description === key)
      );
      if (!entry || !entry._allMatches) return;

      for (const match of entry._allMatches) {
        const refPath = doc(db, 'tenants', tenant, 'customers', match._customerId, 'contracts', match._contractId, 'lineItems', match.id);

        if (change.deleted) {
          ops.push({ type: 'delete', ref: refPath });
        } else {
          const updateData = { updatedAt: Timestamp.now() };
          if (change.newRate != null) {
            updateData.rate = change.newRate;
            // TASK-9666 — a real price ends "Show at $0" / "Needs price".
            if (change.newRate > 0) { updateData.noCharge = false; updateData.needsPrice = false; }
          }
          if (change.zeroOut === true) {
            updateData.active = false;
            updateData.originalRate = match._origRate ?? match.originalRate ?? match.rate ?? 0;
            updateData.rate = 0;
          } else if (change.zeroOut === false) {
            updateData.active = true;
            const restored = match._origRate ?? match.originalRate ?? match.rate ?? 0;
            updateData.rate = restored;
          }
          if (change.editData) {
            updateData.includeOnFieldTicket = change.editData.includeOnFieldTicket;
            if (change.editData.roundChanged === true) updateData.roundToHalfHour = change.editData.roundToHalfHour; // TASK-9489
            if (change.editData.invoiceChanged === true) updateData.includeOnInvoice = change.editData.includeOnInvoice; // TASK-9667
            updateData.appliesTo = change.editData.appliesTo;
            if (change.editData.description) updateData.description = change.editData.description;
            if (change.editData.category) updateData.category = change.editData.category;
            if (change.editData.rateUnit) updateData.rateUnit = change.editData.rateUnit;
            if (change.editData.quantity !== undefined) updateData.quantity = change.editData.quantity;
          }
          ops.push({ type: 'update', ref: refPath, data: updateData });
        }
      }
    });

    // Process pending adds — add each new item to every contract
    const allContracts = LineItemsState._allContracts || [];
    for (const pa of pendingAdds) {
      const appliesToKind = pa.appliesToKind || '';
      const appliesToType = pa.appliesToType || '';
      const itemData = {
        category: KIND_TO_CATEGORY[appliesToKind] || 'Other',
        description: pa.description || '',
        rate: pa.rate || 0,
        noCharge: pa.noCharge === true,     // TASK-9666
        needsPrice: pa.needsPrice === true, // TASK-9666
        rateUnit: pa.rateUnit || 'Hourly',
        quantity: pa.quantity || null,
        active: true,
        appliesTo: appliesToKind ? { kind: appliesToKind, ...(appliesToType ? { type: appliesToType } : {}) } : null,
        includeOnFieldTicket: pa.includeOnFieldTicket || false,
        // TASK-9667 — the form's Invoice box was read and then dropped here.
        includeOnInvoice: pa.includeOnInvoice !== false,
        roundToHalfHour: pa.roundToHalfHour === true ? true : (pa.roundToHalfHour === false ? false : null), // TASK-9489
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      for (const cp of allContracts) {
        const newRef = doc(collection(db, 'tenants', tenant, 'customers', cp.customerId, 'contracts', cp.contractId, 'lineItems'));
        const contractRef = doc(db, 'tenants', tenant, 'customers', cp.customerId, 'contracts', cp.contractId);
        ops.push({ type: 'set', ref: newRef, data: itemData, contractRef });
      }
    }

    // Execute in 500-op batches (Firestore limit)
    // Each add op counts as 2 (set + increment), so use a conservative batch size
    let opIdx = 0;
    while (opIdx < ops.length) {
      const batch = writeBatch(db);
      let batchCount = 0;
      while (opIdx < ops.length && batchCount < 490) {
        const op = ops[opIdx];
        if (op.type === 'delete') {
          batch.delete(op.ref);
          batchCount++;
        } else if (op.type === 'update') {
          batch.update(op.ref, op.data);
          batchCount++;
        } else if (op.type === 'set') {
          batch.set(op.ref, op.data);
          batchCount++;
          if (op.contractRef) {
            batch.update(op.contractRef, { _lineItemCount: increment(1) });
            batchCount++;
          }
        }
        opIdx++;
      }
      await batch.commit();
    }

    const totalChanges = ops.length;
    const addedCount = pendingAdds.length * allContracts.length;
    const parts = [];
    if (totalChanges - addedCount > 0) parts.push(`${totalChanges - addedCount} change${(totalChanges - addedCount) !== 1 ? 's' : ''}`);
    if (addedCount > 0) parts.push(`${pendingAdds.length} item${pendingAdds.length !== 1 ? 's' : ''} added to ${allContracts.length} contract${allContracts.length !== 1 ? 's' : ''}`);
    window.showToast?.(parts.join(', '), 'success');

    LineItemsState._pendingChanges = new Map();
    LineItemsState._pendingAdds = [];
    LineItemsState._hasUnsavedChanges = false;

    // In-memory state already reflects the changes (applied in onMassZeroOut/onMassDelete/onMassRateChange).
    // Only do a full Firestore reload if items were added (need real doc IDs), otherwise just re-render.
    if (pendingAdds.length > 0) {
      LineItemsState._massLoading = true;
      renderLineItemsModalBody();
      await loadLiAllLineItems();
      LineItemsState._massLoading = false;
    }
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Save all mass changes error:', err);
    window.showToast?.('Failed to save some changes', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">save</span> Save All'; }
  }
}

// ============================================
// MASS ADD — Add new line item to all contracts
// ============================================
function showMassAddForm() {
  const area = document.getElementById('li-lineitem-form-area');
  if (!area) return;

  area.innerHTML = `
    <div style="padding:12px;border:2px solid #22C55E;border-radius:var(--radius-md);margin-bottom:12px;background:#F0FDF4;">
      <div style="font-weight:600;margin-bottom:8px;color:#166534;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;">add_circle</span> Add Line Item to All Contracts
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;color:var(--text-primary);">Description *</label>
        <input type="text" id="mass-add-desc" class="form-control" placeholder="e.g. Operator - Standard">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
        <div class="form-group" style="margin:0;">
          <label style="font-size:13px;color:var(--text-primary);">Unit</label>
          <input type="text" id="mass-add-unit" list="mass-add-uom-suggestions" class="form-control" autocomplete="off" value="Hourly" placeholder="Hourly, Per Load, Each...">
          <datalist id="mass-add-uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
        </div>
        <div class="form-group" style="margin:0;">
          <label style="font-size:13px;color:var(--text-primary);">Quantity</label>
          <input type="number" id="mass-add-qty" class="form-control" min="0" value="1">
        </div>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;color:var(--text-primary);">Rate ($)</label>
        <input type="number" id="mass-add-rate" class="form-control" step="0.01" min="0" placeholder="Needs a price" style="max-width:160px;">
        <!-- TASK-9666 — deliberate $0 vs. "no price yet" -->
        <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:6px 0 0;">
          <input type="checkbox" id="mass-add-show-zero"
            onchange="(function(c){var r=document.getElementById('mass-add-rate');if(r){r.disabled=c;if(c)r.value='';r.placeholder=c?'No charge':'Needs a price';}})(this.checked)">
          Show at $0 (no charge)
        </label>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Shows on the invoice at $0.00 — the customer sees what was used, but isn't charged.</div>
      </div>
      <div style="border-top:1px solid #BBF7D0;padding-top:8px;margin-bottom:8px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;align-items:end;margin-bottom:8px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Applies To</label>
            <select id="mass-add-applies-kind" class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" onchange="window.BillingModule.onMassAddAppliesToKindChange(this.value)">
              <option value="">Any</option>
              <option value="person">Person</option>
              <option value="truck">Vehicle</option>
              <option value="trailer">Trailer</option>
              <option value="asset">Asset</option>
              <option value="misc">Miscellaneous</option>
              <option value="permit">Load Permit</option>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:12px;color:var(--text-secondary);font-weight:600;">Type</label>
            <select id="mass-add-applies-type" class="form-control" style="padding:6px 8px;font-size:13px;border-radius:8px;" disabled>
              <option value="">Any</option>
            </select>
          </div>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;">
          <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;">
            <input type="checkbox" id="mass-add-field-ticket">
            Field Ticket
          </label>
          <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);cursor:pointer;">
            <input type="checkbox" id="mass-add-invoice" checked>
            Invoice
          </label>
          <label style="display:flex;align-items:center;gap:6px;font-size:13px;color:var(--text-primary);">
            Round to ½ hr
            ${_equipRoundSelectHtml('mass-add-round-half', null)}
          </label>
        </div>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.cancelMassAddForm()">Cancel</button>
        <button class="btn btn-sm" style="background:#22C55E;color:#fff;border:none;padding:6px 16px;border-radius:8px;font-weight:600;" onclick="window.BillingModule.addMassLineItem()">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">add</span> Add
        </button>
      </div>
    </div>
  `;
  setTimeout(() => document.getElementById('mass-add-desc')?.focus(), 100);
}

function addMassLineItem() {
  const desc = document.getElementById('mass-add-desc')?.value?.trim();
  if (!desc) { window.showToast?.('Description is required', 'error'); return; }

  const qtyVal = parseFloat(document.getElementById('mass-add-qty')?.value);

  const massAddKind = document.getElementById('mass-add-applies-kind')?.value || '';
  // TASK-9666 — rate + its state (priced / Show at $0 / needs a price).
  const _rf = _priceBookRateFields(
    document.getElementById('mass-add-rate')?.value,
    document.getElementById('mass-add-show-zero')?.checked === true,
    massAddKind);
  const item = {
    tempId: 'ma_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    category: KIND_TO_CATEGORY[massAddKind] || 'Other',
    description: desc,
    rate: _rf.rate,
    noCharge: _rf.noCharge,
    needsPrice: _rf.needsPrice,
    rateUnit: document.getElementById('mass-add-unit')?.value || 'Hourly',
    quantity: isNaN(qtyVal) ? null : qtyVal,
    appliesToKind: document.getElementById('mass-add-applies-kind')?.value || '',
    appliesToType: document.getElementById('mass-add-applies-type')?.value || '',
    includeOnFieldTicket: document.getElementById('mass-add-field-ticket')?.checked || false,
    includeOnInvoice: document.getElementById('mass-add-invoice')?.checked ?? true,
    roundToHalfHour: _readEquipRoundSelect('mass-add-round-half', null) // TASK-9489 three-way
  };

  LineItemsState._pendingAdds.push(item);
  LineItemsState._hasUnsavedChanges = true;

  // Clear form and re-render
  cancelMassAddForm();
  renderLineItemsModalBody();
  window.showToast?.(`"${desc}" queued — will be added to ${LineItemsState._allContracts.length} contracts on save`, 'success');
}

function cancelMassAddForm() {
  const area = document.getElementById('li-lineitem-form-area');
  if (area) area.innerHTML = '';
}

function undoMassAdd(tempId) {
  LineItemsState._pendingAdds = LineItemsState._pendingAdds.filter(pa => pa.tempId !== tempId);
  LineItemsState._hasUnsavedChanges = LineItemsState._pendingChanges.size > 0 || LineItemsState._pendingAdds.length > 0;
  renderLineItemsModalBody();
}

function onMassAddAppliesToKindChange(kind) {
  const typeSelect = document.getElementById('mass-add-applies-type');
  if (!typeSelect) return;
  typeSelect.innerHTML = '<option value="">Any Type</option>';
  typeSelect.disabled = !kind || kind === 'misc' || kind === 'permit';
  if (!kind || kind === 'misc' || kind === 'permit') return;
  const kindMap = { person: 'crewTypes', truck: 'truckTypes', trailer: 'trailerTypes', asset: 'assetCategories' };
  const typeList = window.getTypeList?.(kindMap[kind]) || [];
  typeList.forEach(t => {
    const opt = document.createElement('option');
    opt.value = t.slug || t.value || t;
    opt.textContent = t.label || t.value || t;
    typeSelect.appendChild(opt);
  });
}


async function discardMassChanges() {
  if (!confirm('Discard all pending changes and reload from server?')) return;
  LineItemsState._pendingChanges = new Map();
  LineItemsState._pendingAdds = [];
  LineItemsState._hasUnsavedChanges = false;
  LineItemsState._massLoading = true;
  renderLineItemsModalBody();
  await loadLiAllLineItems();
  LineItemsState._massLoading = false;
  renderLineItemsModalBody();
}

function showLiContractForm(contractId) {
  const contract = contractId ? (LineItemsState.contracts || []).find(c => c.id === contractId) : null;
  const area = document.getElementById('li-contract-form-area');
  if (!area) return;

  const isEdit = !!contract;

  area.innerHTML = `
    <div style="padding:16px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-elevated);">
      <div style="font-weight:600;margin-bottom:12px;color:var(--text-primary);font-size:15px;">${isEdit ? 'Edit Contract' : 'Add Contract'}</div>
      <input type="hidden" id="li-contract-form-id" value="${contract?.id || ''}">
      <div class="form-group" style="margin-bottom:10px;">
        <label style="font-size:13px;color:var(--text-primary);">Title *</label>
        <input type="text" id="li-contract-title" class="form-control" value="${escapeHtml(contract?.title || '')}" placeholder="Contract title" style="font-size:14px;padding:10px 12px;">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:13px;color:var(--text-primary);">Status</label>
          <select id="li-contract-status" class="form-control" style="font-size:14px;padding:10px 12px;"
            onchange="var n = document.getElementById('li-contract-approval-sig-inactive'); if (n) n.style.display = this.value === 'Active' ? 'none' : '';">
            <option value="Active" ${(contract?.status || 'Active') === 'Active' ? 'selected' : ''}>Active</option>
            <option value="Draft" ${contract?.status === 'Draft' ? 'selected' : ''}>Draft</option>
            <option value="Expired" ${contract?.status === 'Expired' ? 'selected' : ''}>Expired</option>
            <option value="Cancelled" ${contract?.status === 'Cancelled' ? 'selected' : ''}>Cancelled</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:13px;color:var(--text-primary);">Payment Terms</label>
          <select id="li-contract-terms" class="form-control" style="font-size:14px;padding:10px 12px;">
            <option value="">-- None --</option>
            ${LI_PAYMENT_TERMS.map(t => `<option value="${t}" ${contract?.paymentTerms === t ? 'selected' : ''}>${t}</option>`).join('')}
          </select>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;">
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:13px;color:var(--text-primary);">Start Date</label>
          <input type="date" id="li-contract-start" class="form-control" value="${contract?.startDate || ''}" style="font-size:14px;padding:10px 12px;">
        </div>
        <div class="form-group" style="margin-bottom:0;">
          <label style="font-size:13px;color:var(--text-primary);">End Date</label>
          <input type="date" id="li-contract-end" class="form-control" value="${contract?.endDate || ''}" style="font-size:14px;padding:10px 12px;">
        </div>
      </div>
      <div style="padding-top:10px;border-top:1px solid var(--border-light);">
        <div style="font-weight:600;font-size:13px;margin-bottom:8px;color:var(--text-primary);">Field Ticket Settings</div>
        <div style="display:flex;flex-direction:column;gap:6px;">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
            <input type="checkbox" id="li-contract-req-driver-sig" ${contract?.fieldTicketSettings?.requireDriverSignature ? 'checked' : ''}>
            Request driver signature
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
            <input type="checkbox" id="li-contract-req-customer-sig" ${contract?.fieldTicketSettings?.requireCustomerSignature ? 'checked' : ''}>
            Request customer signature
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
            <input type="checkbox" id="li-contract-req-stamp" ${contract?.fieldTicketSettings?.requireStamp ? 'checked' : ''}>
            Request stamp / photo
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
            <input type="checkbox" id="li-contract-show-costs" ${(contract?.fieldTicketSettings?.showCostsOnDriverTicket !== false) ? 'checked' : ''}>
            Show costs on driver ticket
          </label>
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
              <input type="checkbox" id="li-contract-req-email" ${contract?.fieldTicketSettings?.requireEmailCopy ? 'checked' : ''}
                onchange="document.getElementById('li-contract-default-email').style.display = this.checked ? '' : 'none';">
              Email copy on close-out
            </label>
            <input type="email" id="li-contract-default-email" class="form-control" placeholder="Default email" value="${escapeHtml(contract?.fieldTicketSettings?.defaultEmailRecipient || '')}"
              style="flex:1;font-size:13px;padding:6px 10px;min-width:150px;${contract?.fieldTicketSettings?.requireEmailCopy ? '' : 'display:none;'}">
          </div>
          ${_approvalSigSelectHtml('li-contract-approval-sig', contract?.fieldTicketSettings?.customerApprovalSignature, '', 'pb', contract?.status || 'Active')}
        </div>
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;">
        <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.cancelLiForm()">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.BillingModule.saveLiContract()">Save</button>
      </div>
    </div>
  `;
  setTimeout(() => document.getElementById('li-contract-title')?.focus(), 100);
}

// TASK-4714 — 🔴 A MIRROR OF THE SERVER'S OWN TEST, deliberately, byte for byte.
// `sendFieldTicketEmail` repairs an address (strip the wrapper/separator chars an
// address can never legitimately end in) and THEN requires a real alphabetic TLD;
// the driver's destination allowlist is built by running stored recipients through
// that same pair. If this validator were STRICTER than the server it would refuse
// addresses the send path accepts — including the messy-but-real ones live customer
// records actually hold (`Dee@Massiveservicesllc.com,`, `dcote@stallionis.com>`).
// If it were LOOSER it would happily save an address the server then refuses, and
// we would be back to a requirement armed with no way to satisfy it. Same regexes,
// same order. If index.js ever changes them, change them here in the same commit.
const _FT_EMAIL_TRIM_RE = /^[<"'\s]+|[>,;:"'\s.]+$/g;
const _FT_EMAIL_SHAPE_RE = /^[^\s@,;:<>"']+@[^\s@,;:<>"']+\.[A-Za-z]{2,}$/;
function _repairEmail(v) {
  return String(v || '').trim().replace(_FT_EMAIL_TRIM_RE, '');
}
function _isPlausibleEmail(v) {
  return _FT_EMAIL_SHAPE_RE.test(_repairEmail(v));
}

// Is this contract asking for an emailed copy it can never actually send?
// Named ONCE and read by the select label, the warning banner and the Invoice
// Builder, for the same reason `_ticketSearchFields` is named once: three
// hand-copied copies of a warning condition drift, and a warning that drifts is
// worse than none — the office stops believing it.
function _ftEmailMisconfigured(contract) {
  const fts = contract?.fieldTicketSettings || {};
  return !!fts.requireEmailCopy && !_isPlausibleEmail(fts.defaultEmailRecipient);
}

async function saveLiContract() {
  const custId = LineItemsState.selectedCustomerId;
  if (!custId) { window.showToast?.('No customer selected', 'error'); return; }

  const title = document.getElementById('li-contract-title')?.value.trim();
  if (!title) { window.showToast?.('Contract title is required', 'error'); return; }

  // TASK-4714 — 🔴 YOU CANNOT ARM THE EMAIL COPY WITHOUT SAYING WHERE IT GOES.
  // Measured on live prod 2026-09-08: 165 contracts fleet-wide, ZERO with a
  // recipient, TWO with `requireEmailCopy` on. Those two customers' drivers have
  // been closing tickets that ask for an emailed copy the server then refuses —
  // correctly, because the driver-side allowlist is built ONLY from this field
  // (index.js sendFieldTicketEmail) and an empty allowlist denies everything.
  // Fail-closed there is right; the bug is that this form let the office arm a
  // requirement it never gave the server a way to satisfy. So it is refused HERE,
  // at the only place with a person to tell.
  const _reqEmail = document.getElementById('li-contract-req-email')?.checked || false;
  const _defEmail = document.getElementById('li-contract-default-email')?.value?.trim() || '';
  if (_reqEmail && !_isPlausibleEmail(_defEmail)) {
    const el = document.getElementById('li-contract-default-email');
    if (el) { el.style.display = ''; el.style.borderColor = '#DC2626'; el.focus(); }
    window.showToast?.(_defEmail
      ? `"${_defEmail}" is not a valid email address — the driver's copy would be refused.`
      : 'Add the email address the field ticket should go to, or turn off "Email copy on close-out". Without it the driver\'s copy is refused and nobody is told.',
    'error');
    return;
  }

  const existingId = document.getElementById('li-contract-form-id')?.value;
  const data = {
    title,
    status: document.getElementById('li-contract-status')?.value || 'Active',
    paymentTerms: document.getElementById('li-contract-terms')?.value || '',
    startDate: document.getElementById('li-contract-start')?.value || '',
    endDate: document.getElementById('li-contract-end')?.value || '',
    fieldTicketSettings: {
      requireDriverSignature: document.getElementById('li-contract-req-driver-sig')?.checked || false,
      requireCustomerSignature: document.getElementById('li-contract-req-customer-sig')?.checked || false,
      requireStamp: document.getElementById('li-contract-req-stamp')?.checked || false,
      requireEmailCopy: _reqEmail,
      // Stored REPAIRED, so what the office sees on the contract is exactly the
      // string the server will match the driver's `emailTo` against.
      defaultEmailRecipient: _defEmail ? _repairEmail(_defEmail) : '',
      showCostsOnDriverTicket: document.getElementById('li-contract-show-costs')?.checked !== false,
      // TASK-4683 A3 — this map is written WHOLE, so the key must ride every save.
      customerApprovalSignature: _approvalSigMode(document.getElementById('li-contract-approval-sig')?.value)
    },
    updatedAt: Timestamp.now()
  };

  const tenant = getTenant();
  const basePath = `tenants/${tenant}/customers/${custId}/contracts`;

  try {
    let newId;
    if (existingId) {
      await updateDoc(doc(db, basePath, existingId), data);
      newId = existingId;
    } else {
      data.createdAt = Timestamp.now();
      data._lineItemCount = 0;
      const docRef = await addDoc(collection(db, basePath), data);
      newId = docRef.id;
      // Increment _contractCount on customer
      const custRef = doc(db, 'tenants', tenant, 'customers', custId);
      const custSnap = await getDoc(custRef);
      if (custSnap.exists()) {
        const currentCount = custSnap.data()._contractCount || 0;
        await updateDoc(custRef, { _contractCount: currentCount + 1 });
      }
    }
    window.showToast?.(existingId ? 'Contract updated' : 'Contract created', 'success');
    cancelLiForm();
    await loadLiContracts(custId);
    LineItemsState.selectedContractId = newId;
    await loadLiLineItems(custId, newId);
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Save contract error:', err);
    window.showToast?.('Failed to save contract', 'error');
  }
}

function showLiQuickAddCustomer() {
  document.getElementById('form-li-quick-customer')?.reset();
  document.getElementById('modal-li-quick-customer')?.classList.remove('hidden');
  setTimeout(() => document.getElementById('li-qc-name')?.focus(), 100);
}

async function saveLiQuickCustomer(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const name = document.getElementById('li-qc-name')?.value.trim();
  if (!name) { window.showToast?.('Company name is required', 'error'); return; }

  const data = {
    name,
    phone: document.getElementById('li-qc-phone')?.value.trim() || '',
    email: document.getElementById('li-qc-email')?.value.trim() || '',
    status: 'Active',
    _contractCount: 0,
    _siteCount: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };

  const tenant = getTenant();
  try {
    const docRef = await addDoc(collection(db, 'tenants', tenant, 'customers'), data);
    // Add to BillingState.customers so it appears in dropdown immediately
    BillingState.customers.push({ id: docRef.id, ...data });
    BillingState.customers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    document.getElementById('modal-li-quick-customer')?.classList.add('hidden');
    window.showToast?.('Customer added', 'success');

    // Auto-select the new customer
    LineItemsState.selectedCustomerId = docRef.id;
    LineItemsState.selectedContractId = null;
    LineItemsState.contracts = [];
    LineItemsState.lineItems = [];
    renderLineItemsModalBody();
  } catch (err) {
    console.error('[Billing] Quick add customer error:', err);
    window.showToast?.('Failed to add customer', 'error');
  }
}

// ============================================
// TICKET CONSOLIDATION
// ============================================
async function consolidateTickets(customerId, jobId, workDate) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

  // Find all matching tickets
  const matching = BillingState.tickets.filter(t =>
    t.customerId === customerId &&
    (t.jobId || '') === jobId &&
    t.workDate === workDate &&
    (t.status === 'draft' || t.status === 'ready') &&
    // TASK-1176 — never consolidate an already-invoiced ticket (books closed; rules
    // deny the content write). TASK-1346 sweep — books-integrity gate: keyed on the
    // boolean this silently swept invoice-marker-only tickets into a merge.
    !isTicketInvoiced(t)
  ).sort((a, b) => (a.ticketNo || 9999) - (b.ticketNo || 9999));

  if (matching.length < 2) {
    window.showToast?.('No tickets to consolidate', 'info');
    return;
  }

  const targetName = matching[0].customerName || 'Unknown';
  if (!confirm(`Merge ${matching.length} tickets for ${targetName} into ticket #${matching[0].ticketNo || '?'}? This cannot be undone.`)) return;

  try {
    const target = matching[0];
    const sources = matching.slice(1);

    for (const source of sources) {
      let _ppeTouchRef = null; // TASK-9725
      // 1. Copy all ticketLines from source → target
      const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines'));
      for (const lineDoc of linesSnap.docs) {
        const lineData = lineDoc.data();
        // TASK-9440 — never copy a FUEL_SURCHARGE line: it is server-owned and a %
        // of ITS ticket's charges. Dropped from the source; the server re-derives
        // the target's surcharge from the merged lines on its next pass.
        // TASK-9725 — same for a billed PPE line (server-owned rollup of the
        // signed sign-outs): dropped here, re-derived on the target once its
        // crew lines land.
        if (lineData.type === 'FUEL_SURCHARGE' || lineData.sourceType === 'safetyEquipment') {
          await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines', lineDoc.id));
          continue;
        }
        // Create in target subcollection
        const newLineRef = await addDoc(collection(db, 'tenants', tenant, 'tickets', target.id, 'ticketLines'), lineData);
        if (lineData.type === 'EMPLOYEE' && !_ppeTouchRef) _ppeTouchRef = newLineRef; // TASK-9725

        // Update any clockEntries that reference this line
        if (lineData.clockEntryId) {
          try {
            await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', lineData.clockEntryId), {
              ticketId: target.id,
              ticketLineId: newLineRef.id
            });
          } catch (e) { /* clockEntry may not exist */ }
        }

        // Delete from source
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id, 'ticketLines', lineDoc.id));
      }

      // 2. Update clockEntries that reference source ticket (catch-all for any not linked via ticketLine)
      try {
        const entriesSnap = await getDocs(query(
          collection(db, 'tenants', tenant, 'clockEntries'),
          where('ticketId', '==', source.id)
        ));
        for (const entryDoc of entriesSnap.docs) {
          await updateDoc(doc(db, 'tenants', tenant, 'clockEntries', entryDoc.id), {
            ticketId: target.id
          });
        }
      } catch (e) { console.warn('[Billing] Error updating clockEntries for consolidation:', e); }

      // 3. Merge notes
      if (source.notes || source.workPerformed) {
        const sourceNotes = source.notes || source.workPerformed || '';
        const targetNotes = target.notes || target.workPerformed || '';
        if (sourceNotes) {
          const merged = targetNotes ? targetNotes + '\n' + sourceNotes : sourceNotes;
          await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
            notes: merged,
            workPerformed: merged,
            ..._notesEditStamp('billing_combine') // TASK-10036 (a)
          });
          target.notes = merged;
          target.workPerformed = merged;
        }
      }

      // 4. Merge photos
      if (source.photos?.length > 0) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
          photos: arrayUnion(...source.photos)
        });
      }

      // 5. Delete source ticket
      await deleteDoc(doc(db, 'tenants', tenant, 'tickets', source.id));
      // TASK-9725 — the source is gone: nudge the server to re-derive any billed
      // PPE line on the target (its sign-outs were attributed to the source).
      if (_ppeTouchRef) { try { await updateDoc(_ppeTouchRef, { ppeBillTouch: Timestamp.now() }); } catch (e) { console.warn('[Billing] ppe touch failed:', e?.message || e); } }
    }

    // 6. Recalculate target grand total — TASK-405: tax-aware (was tax-blind, which
    // undercharged tax-enabled tickets — sibling of CRIT-2). Mirrors calcReviewTax.
    const allLines = await getDocs(collection(db, 'tenants', tenant, 'tickets', target.id, 'ticketLines'));
    // Re-read the target doc for AUTHORITATIVE tax settings (the in-memory `target`
    // can be stale if another session changed the ticket's tax config — COD).
    const _tgtSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', target.id));
    const _tot = computeTicketTotalsFromLines(allLines.docs.map(d => d.data()), _tgtSnap.exists() ? _tgtSnap.data() : target);
    const grandTotal = _tot.grandTotal;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', target.id), {
      grandTotal,
      taxableSubtotal: _tot.taxableSubtotal,
      taxAmount: _tot.taxAmount,
      updatedAt: Timestamp.now()
    });

    window.showToast?.(`${sources.length + 1} tickets consolidated into #${target.ticketNo || ''}`, 'success');

    // Refresh: the onSnapshot listener should fire, but let's force a re-render
    renderTicketsList();
  } catch (err) {
    console.error('[Billing] Consolidation error:', err);
    window.showToast?.('Error consolidating tickets', 'error');
  }
}

// ============================================
// SPLIT TICKET
// ============================================

async function showSplitTicketModal(ticketId) {
  console.log('[Billing] Split: called with ticketId:', ticketId);
  try {
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  // diagnostic — logs the RAW stored field on purpose (the guard below uses the predicate)
  console.log('[Billing] Split: found ticket:', ticket?.id, 'status:', ticket?.status, 'invoiced:', ticket?.invoiced, 'paid:', ticket?.paid);
  if (!ticket) { console.warn('[Billing] Split: ticket not found', ticketId); return; }

  // Block split on invoiced/paid/sent tickets.
  // TASK-1346 sweep — books-integrity gate; splitting rewrites line items.
  if (isTicketInvoiced(ticket) || ticket.paid || ticket.status === 'sent' || ticket.status === 'locked') {
    console.log('[Billing] Split: blocked by status guard');
    window.showToast?.('Cannot split an invoiced, paid, or sent ticket. Duplicate it manually if needed.', 'error');
    return;
  }

  const tenant = BillingState.tenantId;
  console.log('[Billing] Split: fetching lines for tenant:', tenant);

  const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
  const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  console.log('[Billing] Split: found', lines.length, 'lines, emp:', lines.filter(l => l.type === 'EMPLOYEE').length);
  const empLines = lines.filter(l => l.type === 'EMPLOYEE');

  if (empLines.length === 0) {
    window.showToast?.('No employee lines to split', 'info');
    return;
  }

  let empRowsHtml = '';
  for (const line of empLines) {
    const halfHours = parseFloat((line.hours / 2).toFixed(2));
    empRowsHtml += `
      <div style="display:flex;align-items:center;gap:12px;padding:8px 0;border-bottom:1px solid var(--border-color);">
        <div style="flex:1;font-weight:600;">${escapeHtml(line.personName || 'Unknown')}</div>
        <div style="color:var(--text-secondary);font-size:0.85rem;">${line.hours?.toFixed(2) || 0} hrs total</div>
        <div style="display:flex;align-items:center;gap:6px;">
          <label style="font-size:0.8rem;color:var(--text-secondary);">New ticket:</label>
          <input type="number" class="form-control split-hours-input" data-line-id="${line.id}"
            value="${halfHours}" min="0.25" max="${line.hours}" step="0.25"
            style="width:80px;text-align:center;font-weight:600;"
            oninput="window.BillingModule.onSplitHoursChange('${line.id}', this.value, ${line.hours})">
        </div>
        <div style="font-size:0.8rem;color:var(--text-secondary);width:90px;text-align:right;" id="split-remaining-${line.id}">
          Keeps: ${(line.hours - halfHours).toFixed(2)} hrs
        </div>
      </div>`;
  }

  const otherLines = lines.filter(l => l.type !== 'EMPLOYEE');
  let otherInfo = '';
  if (otherLines.length > 0) {
    otherInfo = `<div style="margin-top:12px;padding:10px;background:var(--bg-secondary);border-radius:8px;font-size:0.85rem;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">info</span>
      Equipment, permits, downtime, documents, and signatures stay on the original ticket. The new ticket will be a clean draft — move items manually if needed.
    </div>`;
  }

  // TASK-4591 — same relevance window as the review picker. A split always lands
  // on work that already happened, so it is anchored on the source ticket's date
  // and force-includes its current job ("Same job" is the default, but the job
  // must also be selectable by name). Gets its own "Show older" link — a hidden
  // option with no way to reveal it is the failure mode this whole change exists
  // to remove, and a split onto an older job is a real (if rare) case.
  const _splitJobsAll = _reviewJobFullList(ticket.customerId);
  const jobs = _reviewJobWindowJobs(_splitJobsAll, _reviewJobAnchor(ticket), ticket.jobId);
  const _splitOlder = Math.max(0, _splitJobsAll.length - jobs.length);
  // TASK-4660 — was a hand-rolled twin of _reviewJobOptionsHtml. Routed through
  // the shared builder so the dated labels land here too and there is exactly one
  // place that decides how a job reads in a picker. It emits its own leading
  // placeholder, so the hard-coded <option value="">Same job</option> that used to
  // sit above ${jobOptions} in the markup is gone with it — "Same job" (empty value
  // = don't re-point) is passed in as the placeholder instead of "No job".
  const jobOptions = _reviewJobOptionsHtml(jobs, ticket.jobId || null, 'Same job');

  const modalHtml = `
    <div class="modal" id="modal-split-ticket" onclick="if(event.target===this)this.classList.add('hidden')">
      <div class="modal-content" style="max-width:560px;">
        <div class="modal-header">
          <h2><span class="material-icons">call_split</span> Split Ticket #${ticket.ticketNo || '?'}</h2>
          <button class="modal-close" onclick="document.getElementById('modal-split-ticket').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding:16px;">
          <p style="margin-bottom:16px;color:var(--text-secondary);">
            Set hours for the <strong>new ticket</strong>. Remaining hours stay on the original.
          </p>
          <div style="margin-bottom:16px;">
            <label style="font-weight:600;margin-bottom:6px;display:block;"><span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">work</span> Job for new ticket</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="split-job-select" class="form-control" style="flex:1;" data-cust-id="${(window.escAttr || escapeHtml)(ticket.customerId || '')}">
                ${jobOptions}
              </select>
              <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.addJobFromSplit('${ticket.customerId}', '${ticketId}')" title="Pick the dispatched job for the new ticket">
                <span class="material-icons">add</span> New Job
              </button>
            </div>
            ${_splitOlder > 0 ? `<a href="#" id="split-job-show-older" onclick="window.BillingModule.showOlderSplitJobs();return false;" style="display:inline-block;padding:4px 0;font-size:11px;font-weight:600;color:var(--brand-blue);margin-top:3px;text-decoration:underline;">Show older jobs (${_splitOlder} more)</a>` : ''}
          </div>
          ${empRowsHtml}
          ${otherInfo}
        </div>
        <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;">
          <button class="btn btn-secondary" onclick="document.getElementById('modal-split-ticket').classList.add('hidden')">Cancel</button>
          <button class="btn btn-primary" onclick="window.BillingModule.executeSplitTicket('${ticketId}')">
            <span class="material-icons">call_split</span> Split Ticket
          </button>
        </div>
      </div>
    </div>`;

  let existing = document.getElementById('modal-split-ticket');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  // TASK-9609 — same grouping + filter as the review picker.
  _setJobPickerJobs(document.getElementById('split-job-select'), jobs, 'Same job');
  } catch (err) {
    console.error('[Billing] showSplitTicketModal error:', err);
    window.showToast?.('Error loading split modal', 'error');
  }
}

function onSplitHoursChange(lineId, value, totalHours) {
  const val = parseFloat(value) || 0;
  const remaining = (totalHours - Math.min(Math.max(val, 0), totalHours)).toFixed(2);
  const el = document.getElementById(`split-remaining-${lineId}`);
  if (el) el.textContent = `Keeps: ${remaining} hrs`;
}

async function addJobFromSplit(customerId, sourceTicketId) {
  // TASK-10043 S1 — start from the board: pick the dispatched job day for the
  // new ticket; work not on the board is put there (reason) by the picker,
  // never a billing-only job (the old inline addDoc made jobs with no days).
  const src = (BillingState.tickets || []).find(t => t.id === sourceTicketId) || null;
  const pick = await _pickJobDay({
    heading: 'Which dispatched job is the new ticket on?',
    customerId, lockCustomer: true, dateKey: src?.workDate || '', purpose: 'job',
  });
  if (!pick || !pick.jobId) return;
  const sel = document.getElementById('split-job-select');
  if (sel) {
    if (![...sel.options].some(o => o.value === pick.jobId)) {
      const job = BillingState.jobs.find(j => j.id === pick.jobId);
      const opt = document.createElement('option');
      opt.value = pick.jobId;
      opt.textContent = job ? (job.title || job.name || 'Job') : 'Job';
      sel.appendChild(opt);
    }
    sel.value = pick.jobId;
  }
  _offerPickedDayDate(pick, src?.workDate, false);
}

async function executeSplitTicket(sourceTicketId) {
  const tenant = BillingState.tenantId;
  const sourceTicket = BillingState.tickets.find(t => t.id === sourceTicketId);
  if (!sourceTicket) return;

  const inputs = document.querySelectorAll('.split-hours-input');
  const splitMap = {};
  let hasAnyHours = false;
  for (const inp of inputs) {
    const hrs = parseFloat(inp.value) || 0;
    if (hrs > 0) hasAnyHours = true;
    splitMap[inp.dataset.lineId] = hrs;
  }
  if (!hasAnyHours) {
    window.showToast?.('Enter hours for at least one employee on the new ticket', 'info');
    return;
  }

  const jobSelect = document.getElementById('split-job-select');
  // TASK-4660 — the job NAME used to be read off the selected <option>'s
  // textContent. That was already fragile (it assumed the option label was
  // byte-identical to the job's title, and sniffed the literal string 'Same job'
  // to detect the placeholder). Once the picker labels became dated — "Tufto
  // nitrogen purge — 8/24" — it would have BAKED THE DISPLAY DATE INTO BILLING
  // DATA and carried it onto the invoice. Resolve the job object by id instead,
  // the same way both move-to-job paths do (~:21758, ~:22810): title/name always
  // comes off the job doc, never off the DOM.
  // An empty select value is the "Same job" placeholder — keep the ticket's own
  // stored name in that case rather than re-deriving it, exactly as before.
  const _pickedJobId = jobSelect?.value || '';
  const newJobId = _pickedJobId || sourceTicket.jobId || '';
  const _splitTargetJob = _pickedJobId
    ? BillingState.jobs.find(j => j.id === _pickedJobId)
    : null;
  const newJobName = _splitTargetJob
    ? (_splitTargetJob.title || _splitTargetJob.name || '')
    : (sourceTicket.jobName || sourceTicket.jobTitle || '');

  try {
    // TASK-1339 — was a FOURTH allocator that scanned the stale client-side
    // BillingState.tickets cache (never even hit the server for the max), so a
    // split could mint a number a fresh ticket already took. Converged onto the
    // one allocator (COD Blast catch).
    const nextNo = await getNextTicketNumber(tenant);

    // Create new ticket
    const newTicketData = {
      date: sourceTicket.date || Timestamp.now(),
      workDate: sourceTicket.workDate || '',
      status: 'draft',
      customerId: sourceTicket.customerId || '',
      customerName: sourceTicket.customerName || '',
      jobId: newJobId || sourceTicket.jobId || '',
      jobName: newJobName,
      jobTitle: newJobName,
      poNumber: sourceTicket.poNumber || '',
      vendorNumber: sourceTicket.vendorNumber || '',
      location: sourceTicket.location || '',
      wellName: sourceTicket.wellName || '',
      wellNumber: sourceTicket.wellNumber || '',
      afeNumber: sourceTicket.afeNumber || '',
      workDateEnd: sourceTicket.workDateEnd || '',
      notes: '', workPerformed: '',
      ticketNo: nextNo, grandTotal: 0, totalHours: 0, employeeCount: 0,
      // TASK-405 Slice B: inherit tax config from the source so the split-off
      // ticket isn't tax-blind (its grandTotal is recomputed tax-aware below).
      taxEnabled: sourceTicket.taxEnabled || false,
      taxRate: sourceTicket.taxRate || 0,
      // Shallow copy so editing the split-off ticket's categories can't bleed into
      // the source's in-memory object via a shared reference (COD).
      taxCategories: sourceTicket.taxCategories ? { ...sourceTicket.taxCategories } : null,
      source: 'split',
      originalJobId: newJobId || sourceTicket.jobId || null,
      originalCustomerId: sourceTicket.customerId || null,
      originalWorkDate: sourceTicket.workDate || '',
      createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
      createdBy: window.AppState?.userId || '',
      splitFrom: sourceTicketId
    };
    const newRef = await addDoc(collection(db, 'tenants', tenant, 'tickets'), newTicketData);

    // Process lines
    const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', sourceTicketId, 'ticketLines'));
    let newTotal = 0, newHours = 0, newEmpCount = 0;
    let srcTotal = 0, srcHours = 0, srcEmpCount = 0;

    for (const lineDoc of linesSnap.docs) {
      const line = lineDoc.data();
      if (line.type === 'EMPLOYEE' && splitMap[lineDoc.id] > 0) {
        const splitHrs = Math.min(splitMap[lineDoc.id], line.hours || 0);
        const remainHrs = (line.hours || 0) - splitHrs;
        const rate = line.rate || 0;

        // New ticket line
        const newLine = { ...line };
        delete newLine.clockEntryId;
        newLine.hours = parseFloat(splitHrs.toFixed(4));
        newLine.lineAmount = parseFloat((splitHrs * rate).toFixed(2));
        newLine.createdAt = Timestamp.now();
        await addDoc(collection(db, 'tenants', tenant, 'tickets', newRef.id, 'ticketLines'), newLine);
        newTotal += newLine.lineAmount;
        newHours += newLine.hours;
        newEmpCount++;

        // Update source line
        const srcAmt = parseFloat((remainHrs * rate).toFixed(2));
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', sourceTicketId, 'ticketLines', lineDoc.id), {
          hours: parseFloat(remainHrs.toFixed(4)), lineAmount: srcAmt
        });
        srcTotal += srcAmt;
        srcHours += remainHrs;
        if (remainHrs > 0) srcEmpCount++;
      } else {
        const amt = (line.type === 'DOWNTIME' ? -1 : 1) * (line.lineAmount || 0);
        srcTotal += amt;
        if (line.type === 'EMPLOYEE') { srcHours += (line.hours || 0); srcEmpCount++; }
      }
    }

    // Update totals — TASK-405 Slice B: recompute BOTH tickets' grandTotal tax-aware
    // (the split distributed lines but summed grandTotal tax-blind). Re-read each
    // ticket's lines + tax config so tax-enabled splits charge correctly.
    const _newLines = (await getDocs(collection(db, 'tenants', tenant, 'tickets', newRef.id, 'ticketLines'))).docs.map(d => d.data());
    const _newTot = computeTicketTotalsFromLines(_newLines, newTicketData);
    const _srcLines = (await getDocs(collection(db, 'tenants', tenant, 'tickets', sourceTicketId, 'ticketLines'))).docs.map(d => d.data());
    const _srcSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', sourceTicketId));
    const _srcTot = computeTicketTotalsFromLines(_srcLines, _srcSnap.exists() ? _srcSnap.data() : sourceTicket);
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', newRef.id), {
      grandTotal: _newTot.grandTotal,
      taxableSubtotal: _newTot.taxableSubtotal,
      taxAmount: _newTot.taxAmount,
      totalHours: parseFloat(newHours.toFixed(2)),
      employeeCount: newEmpCount
    });
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', sourceTicketId), {
      grandTotal: _srcTot.grandTotal,
      taxableSubtotal: _srcTot.taxableSubtotal,
      taxAmount: _srcTot.taxAmount,
      totalHours: parseFloat(srcHours.toFixed(2)),
      employeeCount: srcEmpCount, updatedAt: Timestamp.now(),
      status: 'draft', invoiced: false
    });

    document.getElementById('modal-split-ticket')?.classList.add('hidden');
    window.showToast?.(`Ticket split! New ticket #${nextNo} created`, 'success');
    renderTicketsList();

    // Show side-by-side comparison
    showSplitComparison(sourceTicketId, sourceTicket, srcHours, srcTotal, srcEmpCount,
      newRef.id, nextNo, newHours, newTotal, newEmpCount, splitMap, linesSnap);
  } catch (err) {
    console.error('[Billing] Split ticket error:', err);
    window.showToast?.('Error splitting ticket', 'error');
  }
}

function showSplitComparison(srcId, srcTicket, srcHours, srcTotal, srcEmpCount,
  newId, newTicketNo, newHours, newTotal, newEmpCount, splitMap, linesSnap) {

  const lines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const empLines = lines.filter(l => l.type === 'EMPLOYEE');

  function buildCard(label, ticketNo, hours, total, empCount, lineDetails, ticketId, isNew) {
    const badge = isNew ? '<span style="background:var(--color-success);color:#fff;padding:2px 8px;border-radius:12px;font-size:0.75rem;margin-left:8px;">NEW</span>' : '';
    let rows = '';
    for (const ld of lineDetails) {
      rows += `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-color);font-size:0.9rem;">
        <span>${escapeHtml(ld.name)}</span>
        <span style="font-weight:600;">${ld.hours.toFixed(2)} hrs · $${ld.amount.toFixed(2)}</span>
      </div>`;
    }
    return `<div style="flex:1;min-width:240px;border:2px solid ${isNew ? 'var(--color-primary)' : 'var(--border-color)'};border-radius:12px;padding:16px;background:var(--bg-page);">
      <div style="font-weight:700;font-size:1.1rem;margin-bottom:12px;">${label} #${ticketNo || '?'}${badge}</div>
      <div style="display:flex;gap:16px;margin-bottom:12px;font-size:0.85rem;color:var(--text-secondary);">
        <span>${empCount} employee${empCount !== 1 ? 's' : ''}</span>
        <span>${hours.toFixed(2)} hrs</span>
        <span style="font-weight:600;color:var(--text-primary);">$${total.toFixed(2)}</span>
      </div>
      ${rows}
      <button class="btn btn-primary btn-sm" style="margin-top:12px;width:100%;" onclick="document.getElementById('modal-split-compare')?.classList.add('hidden');window.BillingModule.openReviewTicket('${ticketId}')">
        <span class="material-icons" style="font-size:16px;">edit_note</span> Open Ticket
      </button>
    </div>`;
  }

  // Build line details for original (remaining hours)
  const srcLines = empLines.map(l => {
    const splitHrs = splitMap[l.id] || 0;
    const remain = Math.max(0, (l.hours || 0) - Math.min(splitHrs, l.hours || 0));
    return { name: l.personName || 'Unknown', hours: remain, amount: remain * (l.rate || 0) };
  }).filter(l => l.hours > 0);

  // Build line details for new ticket (split hours)
  const newLines = empLines.filter(l => splitMap[l.id] > 0).map(l => {
    const splitHrs = Math.min(splitMap[l.id], l.hours || 0);
    return { name: l.personName || 'Unknown', hours: splitHrs, amount: splitHrs * (l.rate || 0) };
  });

  const html = `
    <div class="modal" id="modal-split-compare" onclick="if(event.target===this)this.classList.add('hidden')">
      <div class="modal-content" style="max-width:720px;">
        <div class="modal-header">
          <h2><span class="material-icons">call_split</span> Split Complete</h2>
          <button class="modal-close" onclick="document.getElementById('modal-split-compare').classList.add('hidden')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding:16px;">
          <div style="display:flex;gap:16px;flex-wrap:wrap;">
            ${buildCard('Original Ticket', srcTicket.ticketNo, srcHours, srcTotal, srcEmpCount, srcLines, srcId, false)}
            ${buildCard('New Ticket', newTicketNo, newHours, newTotal, newEmpCount, newLines, newId, true)}
          </div>
        </div>
        <div class="modal-footer" style="display:flex;justify-content:center;padding:12px 16px;">
          <button class="btn btn-secondary" onclick="document.getElementById('modal-split-compare').classList.add('hidden')">Close</button>
        </div>
      </div>
    </div>`;

  let existing = document.getElementById('modal-split-compare');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', html);
}

// Add new job from review modal — TASK-10043 S1: it starts from the board. Pick
// the dispatched job day this ticket belongs to; a job that is not on the board
// is put there (with a reason) by the picker, never made billing-only.
async function addJobFromReview() {
  const t = ReviewState;
  const custId = t.customerId || t.ticket?.customerId || t.headerEdits?.customerId;
  if (!t || !custId) { window.showToast?.("Select the customer first: the job list shows only that customer's jobs.", 'info'); return; }
  const _rid = t.ticketId;
  const pick = await _pickJobDay({
    heading: 'Which dispatched job is this ticket on?',
    customerId: custId, lockCustomer: true, dateKey: t.ticket?.workDate || '', purpose: 'job',
  });
  if (!pick || !pick.jobId || ReviewState.ticketId !== _rid) return; // same review ticket only (COD r14)
  if (pick.jobId === (t.jobId || t.ticket?.jobId)) { window.showToast?.('This ticket is already on that job.', 'info'); return; }
  ReviewState.headerExpanded = true; // TASK-9608 — UI only: keep the pickers open
  // The one job-change path (confirm by id + timecard note + staged re-derive).
  _selectReviewJob(pick.jobId);
  // Only when the move was accepted (updateReviewJob's confirm is synchronous
  // and leaves ticket.jobId unchanged on Cancel) — COD r4.
  if (ReviewState.ticket?.jobId === pick.jobId) _offerPickedDayDate(pick, ReviewState.ticket.workDate, true);
}
// COD r3 — a picked board day on another date must not silently leave the
// ticket dated off (billingJobPicker.js offerPickedDayDate; the picker chunk is
// loaded by then — the pick came from it).
function _offerPickedDayDate(pick, workDate, inReview) {
  return _jobPicker ? _jobPicker.offerPickedDayDate(pick, workDate, inReview) : null;
}

// ============================================
// TASK-10043 S1 — START FROM THE BOARD (job-day picker)
// ============================================
// Every Billing path that makes a ticket or a job opens the picker first:
// New Ticket (showAddTicket), the Review / Split "New Job" buttons, the drag
// zone "Create New Job", and New job in a project. The picker UI is the lazy
// billingJobPicker.js chunk (perf budget); its ranking is utils/jobDayPick.js.
// Work that is not on the board goes there through dispatch.js
// addJobDayFromBilling (window.JobOps — the board's own add-day path, guards
// and jobAuditLog rows), never a billing-only job.
let _jobPicker = null;
let _jobPickerLoading = null;
function _ensureJobPicker() {
  if (_jobPicker) return Promise.resolve(_jobPicker);
  if (!_jobPickerLoading) {
    _jobPickerLoading = __ddChunkWrap(import('./billingJobPicker.js?v=1')).then((m) => {
      m.initJobPicker({ BillingState, customerOptionsHtml: _billingCustomerOptionsHtml, loadFleet: _loadFullFleetRoster, flushBoard: _flushSecondarySubs, createJob: _createJobFromReview, noteBoardWork: _noteBoardWork, reviewTicketId: () => ReviewState.ticket && ReviewState.ticketId, setReviewDate: updateReviewDate,
        escapeHtml, isTicketInvoiced, _reviewRowsAll, _reviewRowById, _fmtReviewDate, _fmtReviewDateLong, _moneyViewOnly, _moneyBlocked, _ensureReviewStyles, reviewDecide, isReviewBusy: () => !!BillingState._reviewBusy });
      _jobPicker = m;
      return m;
    }).catch((e) => { _jobPickerLoading = null; throw e; });
  }
  return _jobPickerLoading;
}
async function _pickJobDay(opts) {
  try { return await (await _ensureJobPicker()).pickJobDay(opts); }
  catch (e) { window.showToast?.('Could not open the job picker — reload the page and try again.', 'error'); return null; }
}
// The listeners replace these arrays on their next tick; until then the ticket
// paths (showCalendarJobTickets / generateTicketFromJob / updateReviewJob) need
// the new job + day, so add them locally once (never a duplicate id).
function _noteBoardWork(jobId, jobDoc, jobDayId, jobDayData) {
  if (jobDoc && jobId && !BillingState.jobs.some(j => j.id === jobId)) BillingState.jobs.push({ id: jobId, ...jobDoc });
  if (jobDayData && jobDayId && !BillingState.jobDays.some(d => d.id === jobDayId)) BillingState.jobDays.push({ id: jobDayId, ...jobDayData });
}

// THE billing-side job create (the picker's "Brand-new job" + TASK-9810 "New
// job in this project"). TASK-10043 S1: the job is written TOGETHER WITH its
// dispatch job day (dispatch.js addJobDayFromBilling, one batch), so there is
// no billing-only job with no days any more. extra.dateKey (the work day) and
// extra.reason (why it was not on the board, 8+ chars) are required. Returns
// { id, jobDayId }; throws (Error.message is user-facing) on refusal/cancel.
// Only a Dispatch-access login passes the jobs + jobDays create rules — the
// picker says so. `extra.projectId` lands on the doc at create (rules:
// jobProjectLinkOk reads the project with getAfter — it must name this
// customer); `extra.poNumber` is the project's standing PO, written only when
// given (a job without one picks the project's up, same as changeJobProject).
async function _createJobFromReview(custId, jobName, extra = {}) {
  const tenant = BillingState.tenantId;
  const customer = BillingState.customers.find(c => c.id === custId);
  const add = window.JobOps?.addJobDayFromBilling;
  if (typeof add !== 'function') throw new Error('This page is out of date — reload it and try again.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(extra.dateKey || ''))) throw new Error('Pick the day the work was done.');
  // TASK-4659 — endDate written with startDate, both anchored noon local
  // (see addJobFromSplit note) — on the WORK day, which is the job's one day.
  const _jobDate = window.__coerceJobTimestamp?.(extra.dateKey) || Timestamp.fromDate(new Date(extra.dateKey + 'T12:00:00'));
  // TASK-4781 slice 2 — allocate the human job number BEFORE the write.
  // allocateJobNumberSafe never throws: on a failure it returns
  // { jobNumberPending: true } so the hole is QUERYABLE rather than invisible,
  // mirroring ticketNoPending. window.JobOps per Pitfall #1 — never a
  // cross-import with a ?v= buster.
  const _jn = await window.JobOps.allocateJobNumberSafe(tenant);
  const po = poNormalizeForSave(extra.poNumber);
  const data = {
    ...(_jn.jobNumberPending ? { jobNumberPending: true } : { jobNumber: _jn.jobNumber }),
    title: String(jobName || '').trim(), customerId: custId, customer: customer?.name || '',
    status: 'active', startDate: _jobDate, endDate: _jobDate, createdAt: Timestamp.now(),
    // TASK-1333 — REQUIRED by the jobs create rule (must equal request.auth.uid).
    createdBy: auth.currentUser?.uid || window.AppState?.user?.uid || null,
    createdByName: window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown',
    ...(typeof extra.projectId === 'string' && extra.projectId ? { projectId: extra.projectId } : {}),
    ...(po ? { poNumber: po } : {}),
    createReason: String(extra.reason || '').trim().slice(0, 300), // TASK-10043 — why it was not on the board
    source: 'billing' // made from billing — and, since TASK-10043, always with its board day
  };
  // tenantId: the writer refuses if the company switched while the number was allocated (COD r7).
  const out = await add({ newJob: { data }, dateKey: extra.dateKey, reason: extra.reason, tenantId: tenant });
  if (!out || !out.ok) {
    const e = new Error((out && out.error) || 'Could not create the job — try again.');
    e.cancelled = !!(out && out.cancelled);
    throw e;
  }
  if (BillingState.tenantId !== tenant) throw new Error('This page is out of date — reload it and try again.'); // company switched mid-save (COD r10)
  _noteBoardWork(out.jobId, data, out.jobDayId, out.jobDayData);
  return { id: out.jobId, jobDayId: out.jobDayId, warning: out.warning || null };
}

// TASK-10252 - under the Review Ticket job row when the ticket has no job: the
// list holds only the selected customer's jobs, so a wrong customer hides the
// right job. Says so instead of leaving the biller staring at an empty list.
function _reviewNoJobHintHtml(customerId) {
  const c = customerId ? (BillingState.customers || []).find(x => x.id === customerId || x.name === customerId) : null; // legacy name refs (Pitfall 5)
  const msg = c
    ? `No job on this ticket. Only ${escapeHtml(c.name || 'this customer')}'s jobs are listed. If the right job isn't there, check the customer above.`
    : "No job on this ticket. Pick the customer first: the job list shows only that customer's jobs.";
  return `<div id="review-no-job-hint" style="font-size:12px;color:var(--text-secondary);margin-top:3px;">${msg}</div>`;
}

// Rename existing job from review modal (changes the job title everywhere)
async function renameJobFromReview() {
  const t = ReviewState;
  const jobId = t.jobId || t.ticket?.jobId;
  // TASK-10252 - with no job the pen opens the same job-day picker as New Job
  // (re-point the ticket to a real job); it never types a job name onto the ticket.
  if (!jobId) return addJobFromReview();
  const currentName = t.ticket?.jobName || t.ticket?.jobTitle || '';
  const newName = await window.showInputModal('Rename job', `Rename "${currentName}" to:`, currentName);
  if (!newName || !newName.trim() || newName.trim() === currentName) return;
  const tenant = BillingState.tenantId;
  try {
    // TASK-412: one canonical rename path. This surface was the split-brain culprit
    // — it wrote ONLY job {title,name} with no clock-entry / ticket propagation, no
    // updatedAt, no audit. Now it names the job everywhere like the other two.
    const renamed = await window.JobOps.renameJob(tenant, jobId, newName.trim(), { source: 'billing-review' });
    if (!renamed) { window.showToast?.('Failed to rename job', 'error'); return; }
    // Update local state so the review modal reflects the change
    t.jobName = newName.trim(); t.jobTitle = newName.trim();
    ReviewState.headerEdits.jobName = newName.trim();
    ReviewState.headerEdits.jobTitle = newName.trim();
    t.ticket.jobName = newName.trim();
    t.ticket.jobTitle = newName.trim();
    // Update local jobs cache FIRST — the dropdown label is derived from it below.
    const cachedJob = BillingState.jobs.find(j => j.id === jobId);
    if (cachedJob) { cachedJob.title = newName.trim(); cachedJob.name = newName.trim(); }
    // Update the dropdown label. TASK-4660 — through the shared builder, or a
    // rename would silently strip the date off this one option and leave it the
    // only undated entry in a list where the date is what tells two same-named
    // jobs apart.
    const sel = document.getElementById('review-job-select');
    if (sel) {
      const opt = sel.querySelector(`option[value="${jobId}"]`);
      if (opt) opt.textContent = cachedJob ? _jobPickerLabel(cachedJob) : newName.trim();
    }
    window.showToast?.(`Job renamed to "${newName.trim()}"`, 'success');
  } catch (err) {
    console.error('Error renaming job:', err);
    window.showToast?.('Failed to rename job', 'error');
  }
}

// ============================================
// ARCHIVE / UNARCHIVE TICKETS
// ============================================
// TASK-1345 (COD mitigation iv) — a double-click used to fire two confirms.
const _archiveInFlight = new Set();

// 🔴 TASK-4645 — why a ticket may NOT be archived; null when it may.
//
// Chris set the rule ~2026-08-01: "tickets shouldn't get archived unless they are
// invoiced." Nothing in code enforced it. TASK-1345 (2026-08-05) got as far as a
// WARNING — a confirm whose button reads "Archive anyway" — and on 2026-08-18,
// thirteen days later, #100498 (Split Rock, $4,800, no invoice) went through it in
// the same MINUTE as its invoiced sibling #100510, followed by #100503 (T&E,
// $4,572.60). A soft confirm on the money path is a suggestion. Archiving is what
// makes that money BOTH un-billable (the Invoice button is hidden on archived
// tickets) and invisible (hidden from the default list), which is the entire
// ticket-fiasco mechanism. So this is now a REFUSAL, not a nag.
//
// ONE gate, shared by the single-ticket action and the bulk path, so the bulk path
// can never archive something the single path refuses — the same shape as
// _externalBillBlockReason() above, and mirrored server-side in firestore.rules'
// tkUpdOk archive-transition clause so a raw updateDoc cannot skip it.
//
// THE $0 EXCEPTION IS REAL AND LOAD-BEARING, not a hedge. Measured read-only across
// all 10 tenants holding tickets (977 docs): 55 of the 752 archived tickets are
// unbilled at $0 — safety-training and other no-charge rows that legitimately
// archive clean — and 143 non-archived tickets sit at $0 today. Gating on "not
// invoiced" alone would trap every one of them in the Active list forever. So the
// gate is "has money", not "every ticket".
//
// The other two escapes: `isTicketInvoiced` is the three-field predicate (:782), not
// the raw boolean — "Billed elsewhere" stamps `EXT-…` into invoiceNumber without
// setting `invoiced`, and refusing to archive that would break Lacey's normal
// QuickBooks day. `writtenOff` is bad debt: its own audited action, followed by an
// archive; denying it would strand uncollectable money in Active with no way out.
//
// THIS HALF IS STRUCTURAL ONLY — cheap, synchronous, no money. It returns null both
// for "settled, archive it" and for "still needs the money check"; _archiveMoneyMoot
// separates those. The money question is answered asynchronously against a FRESH
// line read, for the reason in the next comment.
function _archiveBlockReason(t) {
  if (!t) return 'no longer loaded';
  if (t.status === 'archived' || t.status === 'locked') return 'already archived';
  if (t.status === 'deleted') return 'deleted';
  return null;
}

// Books already closed on this ticket, so "does it have money on it" is moot and we
// can skip the line read entirely.
function _archiveMoneyMoot(t) {
  return isTicketInvoiced(t)       // billed here or in QuickBooks
    || t.writtenOff === true;      // bad debt, deliberately closed
}

// 🔴 THE MONEY CHECK READS THE LINES, NEVER THE STORED `grandTotal` (COD Code
// Reviewer + Adversary, both lanes, on the integrated diff — and they were right).
//
// The stored header LAGS. `generateInvoiceNumber` stamps invoice markers without
// rewriting grandTotal, and the header self-heals only when a human OPENS the
// ticket — `viewTicket`'s TASK-1666 denorm heal, and (TASK-9246) `openReviewTicket`'s
// heal-persist batch — both of which deliberately never heal a locked ticket. So
// any ticket nobody has opened can still carry a stale-LOW grandTotal while its
// lines really total thousands. Gating on the stale header would wave exactly that
// ticket through: real billable money archived silently, which is the bug this task
// exists to kill, not a corner of it. `_fetchTicketBillableTotal` is this file's
// established answer to the same problem (TASK-1548/1567) — the customer-INVOICE
// total, excluded line types already subtracted, so gate == PDF == displayed total.
//
// It THROWS on a read failure and this gate FAILS CLOSED on that: an archive we
// refuse costs one retry, an archive we wave through costs the money. Same stance as
// _zeroTotalGate above.
//
// Returns { blocked, total, staleHeader }. TASK-9988 S2: the server op re-checks
// the money from the lines and heals a stale header itself; `staleHeader` is
// informational only now.
async function _archiveMoneyOffense(t) {
  const total = Number(await _fetchTicketBillableTotal(t.id)) || 0;
  return {
    blocked: total > 0,
    total,
    staleHeader: total <= 0 && Number(t.grandTotal || 0) > 0,
  };
}

// 🔴 TASK-9988 S2 — THE WRITE IS THE SERVER'S. firestore.rules now refuse every
// client move into archived/locked, so archive is the ticketGateway
// `archiveTickets` op: it re-runs the open-clock stop, the TASK-4645 money check
// (from the lines, healing a stale-high header in the same commit — the old
// separate grandTotal:0 write is gone) and the state checks INSIDE one
// transaction per ticket, stamps archivedBy from the verified caller, and writes
// the ARCHIVE audit row. The gates above stay as the fast, named UX refusals.
//
// Returns { archived:[ids], already:[ids], blocked:[{ ticketId, ticketNo, code,
// reason, total, openClock }] }. A chunk whose call fails (timeout, offline) comes
// back as code 'error' per ticket — earlier chunks keep their real result, and a
// retry is safe (an archived ticket returns as `already`). Archived/already
// tickets are mirrored onto the in-memory ticket (TASK-4745: the view-ticket
// footer reads status back before the snapshot listener lands).
// 10 per call: each ticket is its own server transaction and the callable has 30s.
const _GW_ARCHIVE_CHUNK = 10;
async function _gatewayArchive(tenant, tickets) {
  const out = { archived: [], already: [], blocked: [] };
  const list = (tickets || []).filter(Boolean);
  if (!list.length) return out;
  const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 120000 });
  const byName = _resolveReadyByName() || null;
  for (let i = 0; i < list.length; i += _GW_ARCHIVE_CHUNK) {
    const chunk = list.slice(i, i + _GW_ARCHIVE_CHUNK);
    let res;
    try {
      res = await fn({ op: 'archiveTickets', tenantId: tenant, payload: { ticketIds: chunk.map(t => t.id) } });
    } catch (err) {
      console.error('[Billing] archiveTickets gateway call failed for', chunk.map(t => t.id), err);
      const denied = /permission-denied/i.test(String(err?.code || ''));
      for (const t of chunk) out.blocked.push({ ticketId: t.id, ticketNo: t.ticketNo ?? null, code: denied ? 'denied' : 'error', reason: denied ? (err?.message || 'not allowed') : 'try again', total: null });
      continue;
    }
    const d = res?.data || {};
    for (const id of (Array.isArray(d.archived) ? d.archived : [])) out.archived.push(id);
    for (const id of (Array.isArray(d.already) ? d.already : [])) out.already.push(id);
    for (const b of (Array.isArray(d.blocked) ? d.blocked : [])) out.blocked.push(b || {});
  }
  const now = Timestamp.now();
  for (const id of [...out.archived, ...out.already]) {
    const t = list.find(x => x.id === id);
    if (!t || t.status === 'archived' || t.status === 'locked') continue;
    t.status = 'archived';
    t.archivedAt = now;
    t.archivedBy = window.AppState?.user?.uid || null;
    t.archivedByName = byName;
  }
  return out;
}

// One ticket, for the billed-elsewhere re-archive. Throws unless it ended archived.
async function _applyArchive(tenant, t) {
  const r = await _gatewayArchive(tenant, [t]);
  if (r.archived.includes(t.id) || r.already.includes(t.id)) return;
  const b = r.blocked[0];
  const e = new Error(b?.reason || 'not archived');
  e._archiveBlocked = b || null;
  throw e;
}

// 🔴 TASK-4645 — bulk-capable archive. PARTITION, never all-or-nothing.
//
// Lacey archives in runs, and a refusal that failed the WHOLE batch because one
// ticket is unbilled would be worse than the bug it fixes — she'd be stuck, and the
// next move after "the button doesn't work" is a workaround that loses more money
// than the original sweep. So: archive every eligible ticket, then NAME the skipped
// ones with their numbers, their money, and what to do about it. Same contract as
// markInvoicedExternallyBulk() below.
//
// One bad write must not abandon the rest half-done and unreported, so the loop
// catches per ticket and reports failures separately from refusals — a refusal means
// "you still have to bill this", a failure means "try again".
async function archiveTickets(ids) {
  if (_moneyBlocked()) return; // TASK-9491
  const list = Array.isArray(ids) ? ids : [ids];
  const fresh = list.filter(id => !_archiveInFlight.has(id));
  if (fresh.length === 0) return;
  fresh.forEach(id => _archiveInFlight.add(id));
  try {
    // TASK-9988 S2 — the SERVER now refuses an archive while someone is clocked
    // in (archiveTickets op, in the same transaction as the write), so a clock
    // list that is still loading or errored no longer blocks archiving: the
    // local list only drives the friendlier stop dialog below.
    // TASK-9988 S1 — one ticket with someone on the clock: the full stop dialog,
    // with the Workforce clock-out as the way through.
    if (fresh.length === 1 && _openClockOnTicket(fresh[0]).length) {
      await _openClockGate(fresh[0], 'archive it');
      return;
    }
    const eligible = [];  // { t, healHeader }
    const skipped = [];   // { t, reason, amount? }
    for (const id of fresh) {
      const t = BillingState.tickets.find(x => x.id === id);
      const reason = _archiveBlockReason(t);
      if (reason) { skipped.push({ t, reason, id }); continue; }
      // TASK-9988 S1 — bulk: skip only this one and name who is on the clock.
      const _oc = _openClockOnTicket(id);
      if (_oc.length) { skipped.push({ t, reason: 'still clocked in', id, who: _openClockListLabel(t, _oc) }); continue; }
      if (_archiveMoneyMoot(t)) { eligible.push({ t, healHeader: false }); continue; }
      let money;
      try {
        money = await _archiveMoneyOffense(t);
      } catch (err) {
        // FAIL CLOSED. A ticket we could not price is a ticket we do not hide.
        console.error('[Billing] Archive gate: line read failed for', id, err);
        skipped.push({ t, reason: "couldn't be priced — check your connection and try again", id });
        continue;
      }
      if (money.blocked) { skipped.push({ t, reason: 'not invoiced yet', amount: money.total, id }); continue; }
      eligible.push({ t, healHeader: money.staleHeader });
    }

    // The refusal. Named tickets, named money, named way out — a bare "can't do
    // that" is what sends people back to a workaround.
    const unbilled = skipped.filter(s => s.reason === 'not invoiced yet');
    if (skipped.length > 0) {
      // The FRESH total, not the stored header — the message has to name the money
      // the biller will actually see on the invoice.
      const unbilledTotal = unbilled.reduce((sum, s) => sum + Number(s.amount || 0), 0);
      const nameList = (arr) => arr.map(s => '#' + (s.t?.ticketNo || '?')).join(', ');
      const parts = [];
      if (unbilled.length > 0) {
        parts.push(
          `${unbilled.length} ticket${unbilled.length !== 1 ? 's' : ''} ` +
          `(${nameList(unbilled)}) still ${unbilled.length !== 1 ? 'have' : 'has'} ` +
          `${formatCurrency(unbilledTotal)} on ${unbilled.length !== 1 ? 'them' : 'it'} and no invoice. ` +
          `Archiving hides the money AND removes the Invoice button, so that has to happen first: ` +
          `invoice ${unbilled.length !== 1 ? 'them' : 'it'} here, use "Billed elsewhere" if ` +
          `${unbilled.length !== 1 ? 'they were' : 'it was'} billed in QuickBooks, or write ` +
          `${unbilled.length !== 1 ? 'them' : 'it'} off.`
        );
      }
      // TASK-9988 S1 — clocked-in tickets by name; they leave the generic tally below.
      const _ocSkipped = skipped.filter(s => s.who);
      if (_ocSkipped.length > 0) {
        parts.push(`Still clocked in: ${_ocSkipped.map(s => s.who).join('; ')} — clock them out in Workforce first.`);
      }
      const others = skipped.filter(s => s.reason !== 'not invoiced yet' && !s.who);
      if (others.length > 0) {
        const byReason = {};
        for (const s of others) byReason[s.reason] = (byReason[s.reason] || 0) + 1;
        parts.push(`Also skipping ${Object.entries(byReason).map(([r, n]) => `${n} ${r}`).join(', ')}.`);
      }
      if (eligible.length === 0) {
        await window.showConfirm?.(
          unbilled.length > 0
            ? `Can't archive an unbilled ticket`
            : `Nothing here can be archived`,
          parts.join(' '),
          // hideCancel — nothing archived, so there is no choice to offer. A
          // Cancel button next to an OK on a pure refusal reads as "maybe there's
          // another way", which is how people go looking for the workaround.
          { confirmText: 'OK', hideCancel: true, icon: 'warning', iconColor: '#F59E0B' }
        );
        return;
      }
      const goOn = await window.showConfirm?.(
        `${skipped.length} ticket${skipped.length !== 1 ? 's' : ''} can't be archived`,
        `${parts.join(' ')} Archive the other ${eligible.length}?`,
        { confirmText: `Archive ${eligible.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
      );
      if (!goOn) return;
    } else {
      // 🔴 TASK-4745 — THE CONFIRM ARCHIVE NEVER HAD.
      //
      // The partial path above already asks a question ("Archive the other N?"),
      // so the only path that archived on a single unanswered click was the one
      // where EVERYTHING is archivable — which is every ordinary archive. In the
      // ticket-detail footer that click sat between Split and the primary row
      // wearing Print's exact styling, on a surface people drive with a thumb in
      // a truck. Archive is also the button TITS was abusing to mean "billed",
      // which is why the message names "Billed elsewhere" as the other road.
      //
      // Exactly ONE confirm per archive, both lanes (list row + modal + bulk):
      // this branch and the partial branch above are mutually exclusive.
      //
      // Guarded rather than optional-chained on purpose. `window.showConfirm?.()`
      // yields undefined when the helper is missing, and `if (!goOn) return`
      // would then silently refuse to archive anything at all.
      const _one = eligible.length === 1 ? eligible[0].t : null;
      const goOn = window.showConfirm
        ? await window.showConfirm(
            _one ? `Archive ticket #${_one.ticketNo || '?'}?` : `Archive ${eligible.length} tickets?`,
            `Archiving takes ${eligible.length === 1 ? 'it' : 'them'} off the tickets list and removes the Invoice button. ` +
            `It does NOT mean the work was billed — if it was billed in QuickBooks or another system, use "Billed elsewhere" instead. ` +
            `You can unarchive ${eligible.length === 1 ? 'it' : 'them'} again later.`,
            { confirmText: eligible.length === 1 ? 'Archive' : `Archive ${eligible.length}`, cancelText: 'Cancel', icon: 'inventory_2', iconColor: '#B45309' }
          )
        : true;
      if (!goOn) return;
    }

    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const failed = [];
    const lateClockIn = []; // refused by the server: someone is (now) clocked in
    const lateOther = [];   // refused by the server for another reason, named
    let res = { archived: [], already: [], blocked: [] };
    try {
      res = await _gatewayArchive(tenant, eligible.map(e => e.t));
    } catch (err) {
      // Transport / permission failure: nothing here is known to be archived.
      // A retry is safe — an already-archived ticket comes back as done.
      console.error('[Billing] archiveTickets gateway call failed', err);
      for (const { t } of eligible) failed.push(t);
    }
    const ok = res.archived.length + res.already.length;
    for (const b of res.blocked) {
      const t = eligible.find(e => e.t.id === b.ticketId)?.t || null;
      const no = '#' + (t?.ticketNo || b.ticketNo || '?');
      if (b.code === 'open_clock') {
        const who = (Array.isArray(b.openClock) ? b.openClock : []).map(e => String(e?.personName || '').trim() || 'Someone').join(', ');
        lateClockIn.push(`${no} — ${who || 'someone'}`);
      } else if (b.code === 'error') {
        if (t) failed.push(t);
      } else {
        lateOther.push(`${no} (${b.reason || 'refused'})`);
      }
    }
    const tail = [];
    if (lateClockIn.length > 0) tail.push(`Not archived — someone clocked in: ${lateClockIn.join('; ')}. Clock them out in Workforce first.`);
    if (lateOther.length > 0) tail.push(`Not archived: ${lateOther.join('; ')}.`);
    if (failed.length > 0) tail.push(`${failed.length} FAILED (${failed.map(t => '#' + (t.ticketNo || '?')).join(', ')}) — try again.`);
    if (tail.length > 0) {
      window.showToast?.(`${ok} archived. ${tail.join(' ')}`, 'error');
    } else if (ok > 0) {
      window.showToast?.(ok === 1 ? 'Ticket archived' : `${ok} tickets archived`, 'success');
    }
  } finally {
    fresh.forEach(id => _archiveInFlight.delete(id));
  }
}

// The single-ticket action (ticket card + view-ticket modal) is the degenerate bulk
// case, so the two can never drift apart.
async function archiveTicket(id) {
  return archiveTickets([id]);
}

// 🐛 TASK-4745 — the ticket-detail modal's archive click, and it exists to fix a
// real behaviour bug, not to fork a handler.
//
// The footer used to be wired `archiveTicket(id); closeModal('view-ticket');` —
// two statements, the first UN-AWAITED. archiveTickets() REFUSES a ticket that
// still carries unbilled money and explains why in a dialog, so the modal was
// already gone by the time the refusal painted: the user got a warning about a
// screen they had just lost, and the ticket they were told to invoice was no
// longer in front of them. The ticket-list row path never closed anything, so
// the modal was the inconsistent one.
//
// This awaits the SHARED handler (unchanged — `archiveTicket` is still the one
// implementation, still exported under the same name for the list row and the
// bulk lane) and closes only if the ticket actually reached the archive. It
// mirrors `unarchiveFromView`, which is the same "modal-shaped wrapper around a
// shared action" pattern this file already ships.
async function archiveTicketFromView(id) {
  await archiveTicket(id);
  const t = BillingState.tickets.find(x => x.id === id);
  if (t && (t.status === 'archived' || t.status === 'locked')) closeModal('view-ticket');
}

async function unarchiveTicket(id) {
  if (_moneyBlocked()) return; // TASK-9491
  if (_mergedCustomerBlocks(id)) return; // TASK-10030
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // TASK-1343 — a ticket coming back to draft must NOT keep the old approver
    // stamp. Leaving it is what made #100172 permanently unsaveable: the next
    // person's save re-stamped their own uid, which the (previously absolute)
    // immutability rule rejected forever. Clearing it is only possible because
    // the rule now has a reset path — and that path requires the ticket NOT be
    // invoiced, so an invoiced+archived ticket keeps its stamp rather than
    // having its unarchive denied outright.
    // TASK-1346 sweep — narrow boolean, case (c): this MIRRORS the firestore.rules
    // readyBy-reset condition, which reads the raw `invoiced` field. Widening it
    // here would skip the clear on the 110 invoice-marker-only tickets and hand
    // them back the permanently-unsaveable state TASK-1343 fixed.
    const _uaTicket = BillingState.tickets.find(t => t.id === id);
    const _clearReadyBy = _uaTicket?.invoiced !== true;
    const _uaUpdate = { status: 'draft', updatedAt: Timestamp.now() };
    if (_clearReadyBy) {
      _uaUpdate.readyByUid = null;
      _uaUpdate.readyByName = null;
      _uaUpdate.readyAt = null;
    }
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), _uaUpdate);
    // Keep the provenance the clear removes (COD Blast/Domain/Independent): the
    // approver we just dropped is recorded on the audit row, not lost.
    window.logAudit?.('ticket', 'UNARCHIVE', {
      ticketId: id,
      ...(_clearReadyBy && _uaTicket?.readyByUid
        ? { clearedReadyByUid: _uaTicket.readyByUid, clearedReadyByName: _uaTicket.readyByName || null }
        : {})
    });
    window.showToast?.('Ticket unarchived', 'success');
    return true; // TASK-1167 — signal success so callers only reopen editable on a real write
  } catch (error) {
    console.error('[Billing] Error unarchiving ticket:', error);
    window.showToast?.('Error unarchiving ticket', 'error');
    return false;
  }
}

// TASK-1167/1272 — confirm-gated unarchive, shared by the Review-modal banner, the
// AR ticket card, and the read-only view-ticket modal (COD Skeptic 2026-07-20: one
// helper, not three near-identical copies — kills the desynced-confirm-copy risk).
// Debounced via the button (double-click can't fire two unarchives/audits) and
// try/wrapped so a throw can't leave the button stuck-busy (COD Architect). On
// confirm: unarchive (status→draft + UNARCHIVE audit), then run the caller's
// onSuccess (reopen the ticket editable / close the source modal first).
async function _unarchiveWithConfirm(id, btnEl, onSuccess, confirmCopy) {
  if (_mergedCustomerBlocks(id)) return; // TASK-10030
  if (btnEl) {
    if (btnEl.dataset.busy === '1') return;
    btnEl.dataset.busy = '1'; btnEl.disabled = true; btnEl.style.opacity = '0.6';
  }
  const reEnable = () => { if (btnEl) { btnEl.dataset.busy = '0'; btnEl.disabled = false; btnEl.style.opacity = ''; } };
  try {
    // 3rd-COD Domain — the copy must describe what is ABOUT to happen. Routing
    // an archived ticket's "Generate Invoice" through here used to show the
    // edit-unlock wording, so a biller approving what they read as an unlock
    // would have silently cut an invoice.
    const ok = await window.showConfirm?.(
      confirmCopy?.title || 'Unarchive & edit?',
      confirmCopy?.message || 'This ticket was archived — it may already be invoiced in your accounting system. Unarchive and edit anyway?',
      { confirmText: confirmCopy?.confirmText || 'Unarchive', cancelText: 'Cancel' }
    );
    if (!ok) { reEnable(); return; }
    const done = await unarchiveTicket(id); // status→draft + UNARCHIVE audit; false on write failure
    if (done) {
      await onSuccess();
    } else {
      reEnable(); // write failed (toast already shown) — allow retry; ticket still archived
    }
  } catch (e) {
    console.error('[Billing] unarchive-with-confirm error:', e);
    reEnable();
  }
}

// From the Review modal's archived banner + the AR ticket card — reopen editable.
async function unarchiveFromReview(id, btnEl) {
  if (_moneyBlocked()) return; // TASK-9491
  return _unarchiveWithConfirm(id, btnEl, () => openReviewTicket(id));
}

// From the read-only view-ticket modal — close it, then open the ticket editable in Review.
async function unarchiveFromView(id, btnEl) {
  return _unarchiveWithConfirm(id, btnEl, async () => { closeModal('view-ticket'); await openReviewTicket(id); });
}

// TASK-1345 (B) — archived + has money + never invoiced: unarchive and go
// straight to invoicing, in one action. Archiving is what removed the Invoice
// button in the first place, and "unarchive first, then find the button again"
// was a recovery path nothing in the UI ever mentioned — which is how $47,340
// of TITS work ended up archived and never billed. Reuses the existing
// confirm-gated unarchive (its own debounce + failure handling) rather than
// rendering an Invoice button on a read-only ticket.
async function unarchiveAndInvoice(id, btnEl) {
  if (_moneyBlocked()) return; // TASK-9491
  // TASK-9988 S1 — refuse BEFORE the unarchive: generateInvoice's own gate would
  // fire only after the ticket was already back in draft, stranding it there.
  if (!(await _openClockGate(id, 'invoice it'))) return;
  return _unarchiveWithConfirm(id, btnEl, async () => {
    try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
    // 2nd-COD Adversary ⚠️ — generateInvoice reroutes to the GROUPED invoice
    // when a multi-selection is live, which would have stamped invoice fields
    // on every selected ticket (archived ones included) with no confirm and no
    // audit row. Clear the selection so this button only ever bills the ticket
    // whose banner was clicked.
    clearTicketSelection();
    await generateInvoice(id, { _unarchived: true });
  }, {
    title: 'Unarchive and invoice this ticket?',
    message: 'This ticket is archived. It will be unarchived and an invoice will be generated for it — check it is not already billed in your accounting system first.',
    confirmText: 'Unarchive & Invoice'
  });
}

// TASK-1346 — DD has to serve TWO worlds: tenants that invoice inside DD, and
// tenants that invoice in QuickBooks and only need to RECORD the fact. There
// was no way to express the second, so people clicked Archive to mean "I billed
// this elsewhere" — which is the behaviour behind the whole TASK-1345 archive
// trap. This stamps the same invoice markers, generates no DD invoice, and
// records the revenue so BI doesn't understate billed work.
// Why a ticket cannot be recorded as billed elsewhere — null when it can be.
// ONE gate, shared by the single-ticket action and the bulk action, so the bulk
// path can never mark something the single path refuses.
//
// The archived/locked check is not cosmetic: `invoicedExternally` and
// `externalInvoiceRef` are NOT in the firestore.rules locked-ticket lifecycle
// allowlist (`firestore.rules` ~:2618), so on an archived ticket this write is
// rejected wholesale — and if it ever were allowed it would produce the
// invoiced-AND-invisible state TASK-1345 exists to end. Unarchive first.
// ============================================
// TASK-9988 S1 — NOBODY BILLS OR ARCHIVES A TICKET SOMEONE IS CLOCKED IN ON
// ============================================
// TITS 10/20: Ethan Young was clocked onto a ticket at 1:47 PM when the office
// marked it billed elsewhere and archived it. His phone flipped to "Closed by
// the office" and nothing he typed after that saved. Delete was the only action
// that refused (deleteTicket, since 63bb27e92); invoicing, billed-elsewhere and
// archive all went straight through. Chris: "we used to have a stop warning".
//
// One rule, every entry point: generateInvoice (+ unarchiveAndInvoice),
// createGroupedInvoice, markAsInvoiced, markInvoicedExternally(+Bulk),
// archiveTickets (single + bulk). Bulk refuses ONLY the blocked tickets and
// names them. The server half is ticketGateway markInvoiced (every non-demo
// invoice stamp) and qbo.js qboPushInvoice, which refuse with the same words.
// Archive is a direct client write and firestore.rules cannot query clock
// entries, so archive is enforced here only (named in the TASK-9988 row).
//
// The way through for a forgotten clock-out is the EXISTING Workforce clock-out
// (_openWorkforceClockOut → ClockModule.openEntryForClockOut → the Edit Clock
// Entry modal, where the office sets the time they actually stopped). There is
// deliberately no "do it anyway" box: the driver's remaining time has to land
// somewhere first (TASK-9988 items 2/3 wait for Chris's steer).
//
// Reads BillingState.activeClockEntries (the live status=='active' listener in
// initBilling). Mirrors ticketGateway isOpenClockEntry: no clockOut, not closed.
const _CLOSED_CLOCK_STATUSES_9988 = new Set(['completed', 'deleted', 'cancelled', 'canceled', 'voided']);
function _openClockMs(e) {
  const v = e && e.clockIn;
  if (!v) return NaN;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return new Date(v).getTime();
}
// Pointer-only on purpose (same as the server): a ticket moved to another job
// AND customer mid-shift keeps its crew's timecards on the old job (TASK-9755),
// so a job/customer binding filter would let it be billed mid-shift. A
// mis-bound pointer costs a refusal with a way through, never a locked phone.
function _openClockOnTicket(ticketId) {
  if (!ticketId) return [];
  return (BillingState.activeClockEntries || [])
    .filter(e => e && e.ticketId === ticketId && !e.clockOut
      && !_CLOSED_CLOCK_STATUSES_9988.has(String(e.status == null ? '' : e.status).toLowerCase()))
    .sort((a, b) => (_openClockMs(a) || 0) - (_openClockMs(b) || 0));
}
// "1:47 PM" today, "Oct 20, 1:47 PM" for a shift that started on another day.
function _openClockSince(ms, nowMs) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const now = new Date(Number.isFinite(nowMs) ? nowMs : Date.now());
  if (d.toDateString() === now.toDateString()) return time;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}
// Same words as ticketGateway openClockReason, so the dialog and a server
// refusal read identically.
function _openClockReason(entries, nowMs) {
  const list = (entries || []).filter(Boolean);
  if (!list.length) return null;
  const one = (e) => {
    const name = String(e.personName || '').trim() || 'Someone';
    const since = _openClockSince(_openClockMs(e), nowMs);
    return { name, since: since ? ` (since ${since})` : '' };
  };
  if (list.length === 1) {
    const a = one(list[0]);
    return `${a.name} is still clocked in on this ticket${a.since}`;
  }
  const parts = list.map((e) => { const a = one(e); return `${a.name}${a.since}`; });
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]} are still clocked in on this ticket`;
}
// Bulk-list wording: "#100511 — Ethan Young (since 1:47 PM)".
function _openClockListLabel(t, entries) {
  const who = (entries || []).map((e) => {
    const since = _openClockSince(_openClockMs(e));
    return `${String(e.personName || '').trim() || 'Someone'}${since ? ` (since ${since})` : ''}`;
  }).join(', ');
  return `#${t?.ticketNo || '?'} — ${who}`;
}
function _openClockFirstName(e) {
  return String(e?.personName || '').trim().split(/\s+/)[0] || 'them';
}

// The single-ticket stop. Returns true when nobody is clocked in (go on).
// Otherwise explains, offers the Workforce clock-out, and returns false.
async function _openClockGate(ticketId, actionLabel) {
  const open = _openClockOnTicket(ticketId);
  if (!open.length) return true;
  const reason = _openClockReason(open);
  const msg = `${reason}. Doing this now would lock the ticket on their phone, and nothing more they enter would save. ` +
    `If they forgot to clock out, clock them out at the time they actually stopped, then come back and ${actionLabel}.`;
  if (typeof window.showConfirm !== 'function') {
    window.showToast?.(`${reason} — clock them out in Workforce first.`, 'error');
    return false;
  }
  const go = await window.showConfirm('Still clocked in', msg, {
    confirmText: `Clock ${_openClockFirstName(open[0])} out first`,
    cancelText: 'Not now',
    icon: 'timer',
    iconColor: '#F59E0B',
  });
  if (go === true) _openWorkforceClockOut(open[0].id);
  return false;
}

// The way through: the EXISTING Workforce clock-out for that entry. Lands on
// the Clock view and opens its Edit Clock Entry modal, where the office sets
// the real clock-out time. Window global, never a ?v= import (Pitfall #1).
function _openWorkforceClockOut(entryId) {
  try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
  const fallback = () => window.showToast?.('Open Clock → Workforce and clock them out there, then come back.', 'info');
  if (typeof window.navigateToView !== 'function') { fallback(); return; }
  window.navigateToView('clock');
  let tries = 0;
  const tick = () => {
    const fn = window.ClockModule?.openEntryForClockOut;
    if (typeof fn === 'function') { fn(entryId); return; }
    if (++tries < 40) setTimeout(tick, 150); else fallback();
  };
  setTimeout(tick, 50);
}

function _externalBillBlockReason(t) {
  if (!t) return 'no longer loaded';
  if (t.status === 'deleted') return 'deleted';
  if (t.status === 'archived' || t.status === 'locked') return 'archived or locked — unarchive it first';
  if (isTicketInvoiced(t)) return 'already invoiced';
  if (t.writtenOff === true) return 'written off — reverse the write-off first';
  return null;
}

// The write itself. Callers MUST have run _externalBillBlockReason() and preloaded
// the customer's contract rates. Throws on failure so batch callers can count it.
async function _applyInvoicedExternally(tenant, ticket, refRaw) {
  const _ref = String(refRaw ?? '').trim();
  const invoiceNumber = _ref ? `EXT-${_ref}` : `EXT-${new Date().toISOString().slice(0, 10)}`;
  // TASK-1587 — same server backstop as every other invoice-marker stamp. The op
  // carries `invoicedExternally` / `externalInvoiceRef` through as extra fields;
  // they are NOT in its locked-ticket allowlist (deliberately — they are not in
  // firestore.rules' allowlist either), so the server independently refuses them on
  // an archived/locked/already-invoiced ticket. That is the same refusal
  // _externalBillBlockReason() already makes client-side, so no legit flow changes.
  // Keeps this function's contract: THROWS on failure so the batch caller counts it.
  if (_invoiceStampUsesGateway(tenant)) {
    const res = await _gatewayMarkInvoiced(tenant, [ticket.id], {
      invoiceNumber,
      invoicedExternally: true,
      externalInvoiceRef: _ref || null
    }, `markInvoiced:ext:${ticket.id}`);
    if (!res.marked.includes(ticket.id)) {
      const b = res.blocked.find(x => x.ticketId === ticket.id);
      const err = new Error(b?.reason || 'the billing service refused the stamp');
      err._userMessage = `Can't record this as billed — ${b?.reason || 'the billing service refused the stamp'}.`;
      // hotfix/billed-elsewhere-1020 — a transport failure may still have committed
      // (deadline-exceeded); the switch flow must not roll back on a maybe.
      err._transport = !!b?.maybeCommitted;
      // A definite refusal is cached server-side under this key; drop it so a
      // retry after the fix really runs (COD r2).
      if (!err._transport) _gwOpDone(`markInvoiced:ext:${ticket.id}`);
      throw err;
    }
  } else {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticket.id), {
      invoiced: true,
      invoiceNumber,
      invoicedAt: Timestamp.now(),
      invoicedBy: window.AppState?.user?.uid || 'unknown',
      invoicedExternally: true,
      externalInvoiceRef: _ref || null,
      updatedAt: Timestamp.now()
    });
  }
  ticket.invoiced = true;
  ticket.invoiceNumber = invoiceNumber;
  ticket.invoicedExternally = true;
  ticket.externalInvoiceRef = _ref || null;
  // One audit row PER TICKET even in a batch — "who recorded this as billed, and
  // against which reference" has to stay answerable one ticket at a time.
  window.logAudit?.('ticket', 'MARK_INVOICED_EXTERNAL', {
    ticketId: ticket.id, ticketNo: ticket.ticketNo || null, externalInvoiceRef: _ref || null, grandTotal: Number(ticket.grandTotal || 0)
  });
  // COD Code Reviewer ❌ — the ticket doc is ALREADY invoiced by this point. If the
  // revenue write throws and we let it escape, the caller reports the ticket as
  // "still unbilled" (false) and a retry is refused as "already invoiced" — so the
  // ticket would be permanently billed with no revenue row and nobody told. Report
  // it as its own outcome instead. The invoicing is done either way; only BI lags,
  // and recordRevenueEntry is idempotent per ticketId so a manual re-run is safe.
  // TASK-9942 — off demo the server wrote the revenue row in the stamp's own
  // commit, so a marked ticket always has it (`marked` above proves the commit).
  let revenueRecorded = true;
  if (!_invoiceStampUsesGateway(tenant)) {
    try {
      // recordRevenueEntry catches its own errors and RETURNS false; keep the
      // try/catch too for anything it can't catch itself.
      revenueRecorded = (await recordRevenueEntry(tenant, ticket, invoiceNumber)) !== false;
    } catch (revErr) {
      revenueRecorded = false;
      console.error('[Billing] Marked billed externally but revenue entry failed for', ticket.id, revErr);
    }
  }
  return { invoiceNumber, revenueRecorded };
}

async function markInvoicedExternally(ticketId) {
  if (_moneyBlocked()) return; // TASK-9491
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (!ticket) return;
  const _blocked = _externalBillBlockReason(ticket);
  if (_blocked) {
    window.showToast?.(`Can't record this as billed — it is ${_blocked}.`, _blocked === 'already invoiced' ? 'info' : 'error');
    return;
  }
  // TASK-9988 S1 — never while someone is still clocked in on it (Ethan, 10/20).
  if (!(await _openClockGate(ticketId, 'record it as billed'))) return;
  // TASK-1548 — "billed elsewhere" still stamps the invoice markers and records
  // revenue; a $0 equipment line rides into the books exactly like a DD invoice.
  if (!(await _zeroHourGate(ticketId, 'record this ticket as billed'))) return;
  // TASK-1405 — same for equipment that ran with no rate: "billed elsewhere" books
  // the revenue, so an unpriced machine-day is lost in the books either way.
  if (!(await _unpricedGate(ticketId, 'record this ticket as billed'))) return;
  // TASK-1567 — "billed elsewhere" still stamps invoice markers and records
  // revenue; a $0 ticket rides into the books exactly like a $0 DD invoice.
  if (!(await _zeroTotalGate(ticketId, 'record this ticket as billed'))) return;
  // TASK-1615-B — "billed elsewhere" stamps the invoice markers and records the
  // revenue, so an inflated mileage rides into the books exactly like a DD invoice.
  if (!(await _routeMileageGate(ticketId, 'record this ticket as billed'))) return;
  const ref = await window.showInputModal?.(
    'Billed outside DispatchDeck',
    'Invoice or reference number from your accounting system (optional)',
    '',
    { okText: 'Mark billed' }
  );
  if (ref === null || ref === undefined) return; // cancelled
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // Match the DD-invoice paths: load the customer's hide-by-default line
    // settings BEFORE recording revenue, or BI over-recognizes what was billed
    // for customers who exclude line types (COD Code Reviewer ❌).
    if (ticket.customerId) await loadReviewContractRates(ticket.customerId);
    const { revenueRecorded } = await _applyInvoicedExternally(tenant, ticket, ref);
    window.showToast?.(revenueRecorded
      ? 'Marked as billed outside DispatchDeck'
      : 'Marked as billed — but your revenue reports did not update for it. The ticket is billed; the reports will be short until it is redone.',
      revenueRecorded ? 'success' : 'warning');
    try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
    renderTicketsList();
  } catch (error) {
    console.error('[Billing] Error marking externally invoiced:', error);
    // TASK-1587 — the server backstop's refusal reason is actionable ("nothing to
    // bill — $0 total", "billing access required"); a bare "could not mark" is not.
    window.showToast?.(error?._userMessage || 'Could not mark this ticket as billed', 'error');
  }
}

// ============================================
// hotfix/billed-elsewhere-1020 (Lacey, 10/20) — two tickets the Billed elsewhere
// button could not reach:
//   1. ARCHIVED + UNBILLED -> "Unarchive & mark Billed elsewhere" (the twin of
//      Unarchive & Invoice).
//   2. DD-INVOICED + UNPAID that was really billed in QuickBooks -> "Switch to
//      Billed elsewhere". 14 TITS tickets from 10/19 (INV-202610-006..022) sit
//      in exactly this state, each already linked to its QuickBooks invoice.
//
// The switch is uninvoice -> stamp billed elsewhere, both on paths that already
// exist and already pass firestore.rules for a billing member. The SERVER stamp
// (ticketGateway markInvoiced, wantsExternal) does the money work atomically:
// it moves the ticket's row off the old invoice record, clears invoiceId,
// replaces the ticket's ONE revenue row (revenueEntries/tkt_<id>) and writes
// INVOICE_RECORD_TICKET_MOVED + REVENUE_ROW_REBILLED. One revenue row per ticket
// before and after, so revenue is never counted twice.
//
// Ordering (each step names the state it leaves on failure):
//   a. read-only pre-checks the server would refuse on ($0, unpriced, priced
//      man-hour lines, still-being-priced) — refuse BEFORE any write;
//   b. archived -> unarchive (status draft);   fail: nothing changed
//   c. uninvoice (DD number off);              fail: re-archive, say so
//   d. stamp billed elsewhere (server);        fail (definite refusal): re-stamp
//      the old DD number, re-archive, say so. A TRANSPORT failure is never
//      rolled back — it may have committed; the biller is told to refresh.
//   e. was archived -> archive again.          fail: billed, just not archived
// Every outcome is a blocking dialog or a success toast — never a missable
// error toast.
// ============================================

// A QuickBooks link the paid sync trusts (TASK-9654 serverLinkOf shape).
function _qbLinkRef(t) {
  const l = t && t.qboLink;
  if (!l || typeof l !== 'object' || l.on !== true) return '';
  return typeof l.docNumber === 'string' ? l.docNumber.trim() : '';
}
const _refKey = (r) => String(r || '').replace(/\s+/g, ' ').trim().toLowerCase();

// Why a DD-invoiced ticket cannot be switched — null when it can.
function _switchToExternalBlockReason(t) {
  if (!t) return 'no longer loaded';
  if (t.status === 'deleted') return 'deleted';
  if (!isTicketInvoiced(t)) return 'not invoiced in DispatchDeck';
  if (isBilledExternally(t)) return 'already billed elsewhere';
  if (t.status === 'locked') return 'locked';
  if (t.writtenOff === true) return 'written off — reverse the write-off first';
  if (t.paid === true || t.partiallyPaid === true || (Number(t.amountPaidCents) || 0) > 0) return 'has a payment recorded — undo the payment first';
  if (!t.invoiceNumber || String(t.invoiceNumber).startsWith('EXT-')) return 'has no DispatchDeck invoice number';
  // COD r2 — an emailed DD invoice carries a Stripe pay link; a checkout opened
  // before a switch could still collect alongside QuickBooks. Not switchable here.
  if (t.invoiceEmailSentAt) return 'emailed to the customer with a DispatchDeck pay link — switching could let them pay twice; contact support';
  return null;
}

// Archived + unbilled tickets that Unarchive & mark Billed elsewhere can take.
function _canUnarchiveToExternal(t) {
  return !!t && t.status === 'archived' && !isTicketInvoiced(t) && t.writtenOff !== true;
}

// (a) The server's money refusals, checked on a FRESH line read before any
// write. Returns null or a sentence. Throws on read failure (caller fails closed).
async function _externalStampPrecheck(t) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', t.id, 'ticketLines'));
  const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (lines.some(l => l.amountPending === true || l.ratePending === true)) return 'a line is still being priced — try again in a moment';
  if (lines.some(equipmentLineNeedsHours)) return 'it has priced equipment with 0 hours';
  if (!_ticketCustomerIsInternalTime(t)) {
    if (lines.some(equipmentLineUnpriced)) return 'it has equipment that ran with no rate';
    if ((await _zeroTotalOffense(t.id)).blocked) return 'its lines total $0';
  }
  // Man-hour lines bill $0 (TITS bills the truck), and a $0 man-hour line is
  // never refused. Only a man-hour line that CARRIES money is — the server's
  // TASK-9942 rule — and not when the company bills labor on purpose (TASK-4875).
  if (window.AppState?.settings?.billLaborHours !== true &&
      lines.some(l => l.type === 'EMPLOYEE' && ((Number(l.rate) || 0) !== 0 || (Number(l.lineAmount) || 0) !== 0))) {
    return 'a man-hour line carries money (employee lines are never billed)';
  }
  return null;
}

async function _setTicketStatusQuiet(tenant, t, archived) {
  if (archived) { await _applyArchive(tenant, t); return; }
  await updateDoc(doc(db, 'tenants', tenant, 'tickets', t.id), { status: 'draft', updatedAt: Timestamp.now() });
  t.status = 'draft';
  window.logAudit?.('ticket', 'UNARCHIVE', { ticketId: t.id, reason: 'billed-elsewhere' });
}

// The one write sequence (b)-(e). Never throws: returns { ok, msg, transport? }
// where msg is the sentence for THIS ticket.
async function _toExternalCore(tenant, t, ref, { fromDdInvoice }) {
  const no = '#' + (t.ticketNo || '?');
  // 2nd review — decide from a FRESH read, not the cached list object (the paid
  // sync or another tab may have moved it since the dialog opened).
  try {
    const fs = await getDoc(doc(db, 'tenants', tenant, 'tickets', t.id));
    if (!fs.exists()) return { ok: false, msg: `${no}: no longer exists — nothing was changed.` };
    const fRaw = { ...fs.data(), id: t.id };
    const f = _withTicketMoney(fRaw); // TASK-9646 S3b
    const why = fromDdInvoice ? _switchToExternalBlockReason(f) : (_canUnarchiveToExternal(f) ? null : 'no longer archived and unbilled');
    if (why || (fromDdInvoice && f.invoiceNumber !== t.invoiceNumber)) return { ok: false, msg: `${no}: changed since you opened it (${why || 'its invoice number changed'}) — nothing was changed.` };
    Object.assign(t, f);
    // keep the stale check on the STORED header of this fresh read (COD r1 Code Reviewer)
    if (f !== fRaw) _billingHeaderMoney.set(t, _billingHeaderMoney.get(f)); else _billingHeaderMoney.delete(t);
  } catch (e) {
    return { ok: false, msg: `${no}: could not be read — check your connection; nothing was changed.` };
  }
  const wasArchived = t.status === 'archived';
  const oldNo = fromDdInvoice ? t.invoiceNumber : null;
  const reArchive = async () => {
    if (!wasArchived) return true;
    try { await _setTicketStatusQuiet(tenant, t, true); return true; } catch (_) { return false; }
  };
  if (wasArchived) {
    try { await _setTicketStatusQuiet(tenant, t, false); } catch (e) {
      console.error('[Billing] billed-elsewhere: unarchive failed', t.id, e);
      return { ok: false, msg: `${no}: could not unarchive it — nothing was changed.` };
    }
  }
  if (fromDdInvoice) {
    try {
      // Same write as uninvoiceTicket (lifecycle keys only — rules allowlist).
      await setDoc(doc(db, 'tenants', tenant, 'tickets', t.id), { invoiced: false, invoiceNumber: null, invoicedAt: null }, { merge: true });
      t.invoiced = false; t.invoiceNumber = null; t.invoicedAt = null;
    } catch (e) {
      console.error('[Billing] billed-elsewhere: uninvoice failed', t.id, e);
      const back = await reArchive();
      return { ok: false, msg: `${no}: could not take ${oldNo} off — nothing was changed${back ? '' : ' (it is now unarchived; archive it again)'}.` };
    }
    await _voidRevenueRowsForTicket(tenant, t.id, 'switched-to-billed-elsewhere');
  }
  try {
    const { revenueRecorded } = await _applyInvoicedExternally(tenant, t, ref);
    window.logAudit?.('ticket', fromDdInvoice ? 'SWITCH_TO_BILLED_EXTERNAL' : 'UNARCHIVE_MARK_BILLED_EXTERNAL', {
      ticketId: t.id, ticketNo: t.ticketNo || null, fromInvoiceNumber: oldNo,
      externalInvoiceRef: String(ref || '').trim() || null, wasArchived, grandTotal: Number(t.grandTotal || 0)
    });
    const back = await reArchive();
    const tail = [revenueRecorded ? '' : 'revenue reports did not update', back ? '' : 'it is billed but no longer archived'].filter(Boolean).join('; ');
    return { ok: true, msg: `${no}: billed elsewhere${ref ? ` (${ref})` : ''}${tail ? ` — ${tail}` : ''}.` };
  } catch (e) {
    console.error('[Billing] billed-elsewhere: stamp failed', t.id, e);
    const why = e?._userMessage || "Can't record this as billed — the billing service refused it.";
    if (e?._transport) {
      return { ok: false, transport: true, msg: `${no}: ${why} Refresh and check this ticket before trying again.` };
    }
    // 2nd review — a refused op is cached server-side by its key; drop the keys so
    // a retry (or the restore below, or a later plain Billed elsewhere) really runs.
    _gwOpDone(`markInvoiced:ext:${t.id}`);
    let restored = !fromDdInvoice;
    if (fromDdInvoice) {
      try {
        const res = await _gatewayMarkInvoiced(tenant, [t.id], { invoiceNumber: oldNo }, `markInvoiced:restore:${t.id}`);
        restored = res.marked.includes(t.id);
        _gwOpDone(`markInvoiced:restore:${t.id}`);
        if (!restored && res.blocked.some(b => b.maybeCommitted)) {
          return { ok: false, transport: true, msg: `${no}: ${why} Putting ${oldNo} back may not have finished — refresh and check this ticket.` };
        }
        if (restored) { t.invoiced = true; t.invoiceNumber = oldNo; }
      } catch (_) { restored = false; }
    }
    if (restored) {
      const back = await reArchive();
      if (!back) return { ok: false, msg: `${no}: ${why} ${fromDdInvoice ? `It is back on ${oldNo}, but` : 'It is still unbilled and'} it is now UNARCHIVED — archive it again or bill it.` };
      return { ok: false, msg: `${no}: ${why} ${fromDdInvoice ? `It is back on ${oldNo} (its invoice date is now today)` : 'Nothing was changed — it is still archived and unbilled'}.` };
    }
    // Uninvoiced and could not be put back: leave it UNARCHIVED so the work is
    // visible in the unbilled list, never archived-and-unbilled (TASK-1345).
    return { ok: false, msg: `${no}: ${why} ${oldNo} was taken off and could not be put back — the ticket is now UNBILLED and unarchived. Mark it billed elsewhere again, or invoice it.` };
  }
}

// A blocking, can't-miss message (never a toast) — she has to click OK.
function _beSay(title, msg, warn = true) {
  if (!window.showConfirm) { window.showToast?.(msg, warn ? 'error' : 'success'); return Promise.resolve(true); }
  return window.showConfirm(title, msg, { confirmText: 'OK', hideCancel: true, icon: warn ? 'warning' : 'check_circle', iconColor: warn ? '#F59E0B' : '#16A34A' });
}

async function _promptExternalRef(t, { title, intro, required }) {
  const linked = _qbLinkRef(t);
  const ref = await window.showInputModal?.(title,
    intro + (linked ? ` It is linked to QuickBooks invoice #${linked}.` : '') + ` QuickBooks invoice number${required ? '' : ' (optional)'}:`,
    linked || '', { okText: 'Mark billed elsewhere' });
  if (ref === null || ref === undefined) return null;
  const r = String(ref).trim();
  if (required && !r) { await _beSay('QuickBooks number needed', 'Type the QuickBooks invoice number this ticket was billed on. Nothing was changed.'); return null; }
  // The paid sync follows the link; a different typed number is a conflict it
  // stops syncing on (qboInvoiceLink.linkOf). Refuse here, where she typed it.
  if (linked && r && _refKey(r) !== _refKey(linked)) {
    await _beSay('That number does not match QuickBooks', `This ticket is linked to QuickBooks invoice #${linked}, not ${r}. Use #${linked}, or fix the QuickBooks link first. Nothing was changed.`);
    return null;
  }
  return r;
}

let _beSingleInFlight = false;
async function _toExternalSingle(ticketId, fromDdInvoice) {
  if (_moneyBlocked()) return;
  if (_beSingleInFlight || _bulkExternalInFlight) return; // one money action at a time
  _beSingleInFlight = true;
  try {
    const t = BillingState.tickets.find(x => x.id === ticketId);
    const blocked = fromDdInvoice ? _switchToExternalBlockReason(t) : (_canUnarchiveToExternal(t) ? null : 'not an archived, unbilled ticket');
    if (blocked) { await _beSay("Can't mark this billed elsewhere", `Ticket #${t?.ticketNo || '?'} is ${blocked}. Nothing was changed.`); return; }
    let pre;
    try { pre = await _externalStampPrecheck(t); } catch (e) {
      console.error('[Billing] billed-elsewhere precheck failed', ticketId, e);
      await _beSay("Couldn't check this ticket", 'The ticket lines could not be read — check your connection and try again. Nothing was changed.');
      return;
    }
    if (pre) { await _beSay("Can't mark this billed elsewhere", `Ticket #${t.ticketNo || '?'}: ${pre}. Fix it in Edit Ticket first. Nothing was changed.`); return; }
    const ref = await _promptExternalRef(t, fromDdInvoice
      ? { title: 'Switch to Billed elsewhere', intro: `DispatchDeck invoice ${t.invoiceNumber} will be retired and this ticket recorded as billed in QuickBooks.`, required: true }
      : { title: 'Unarchive & mark Billed elsewhere', intro: 'This archived ticket will be recorded as billed in QuickBooks and stay archived. No DispatchDeck invoice is created.', required: false });
    if (ref === null) return;
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    if (t.customerId) { try { await loadReviewContractRates(t.customerId); } catch (_) { /* server recomputes; demo only reads it */ } }
    const res = await _toExternalCore(tenant, t, ref, { fromDdInvoice });
    try { closeModal('view-ticket'); } catch (_) { /* not always open */ }
    renderTicketsList();
    if (res.ok) window.showToast?.(res.msg, 'success');
    else await _beSay(res.transport ? 'Not sure it went through' : 'Not marked billed elsewhere', res.msg);
  } finally {
    _beSingleInFlight = false;
  }
}
function switchToBilledElsewhere(ticketId) { return _toExternalSingle(ticketId, true); }
function unarchiveAndMarkBilledElsewhere(ticketId) { return _toExternalSingle(ticketId, false); }

// Bulk switch (Lacey's 14): each ticket uses ITS OWN QuickBooks link number —
// one typed reference cannot be right for 14 different QuickBooks invoices.
async function _switchToExternalBulk(list, skippedIn) {
  const ready = [];
  const skipped = [...skippedIn];
  for (const t of list) {
    if (!_qbLinkRef(t)) { skipped.push({ t, reason: 'not linked to a QuickBooks invoice — open it and use Switch to Billed elsewhere' }); continue; }
    try {
      const pre = await _externalStampPrecheck(t);
      if (pre) { skipped.push({ t, reason: pre }); continue; }
    } catch (_) { skipped.push({ t, reason: 'lines could not be read — try again' }); continue; }
    ready.push(t);
  }
  const skipTxt = skipped.length ? ` Skipping ${skipped.length}: ${skipped.map(s => `#${s.t?.ticketNo || '?'} (${s.reason})`).join('; ')}.` : '';
  if (ready.length === 0) { await _beSay('Nothing to switch', `None of the selected tickets can be switched.${skipTxt}`); return; }
  const go = await window.showConfirm?.(
    `Switch ${ready.length} to Billed elsewhere?`,
    'Each DispatchDeck invoice is retired and the ticket is recorded as billed on its linked QuickBooks invoice: ' +
    ready.map(t => `#${t.ticketNo || '?'} ${t.invoiceNumber} → QB #${_qbLinkRef(t)}`).join(', ') + `.${skipTxt}`,
    { confirmText: `Switch ${ready.length}`, cancelText: 'Cancel', icon: 'swap_horiz', iconColor: '#0EA5E9' });
  if (!go) return;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  await Promise.all([...new Set(ready.map(t => t.customerId).filter(Boolean))].map(cid => loadReviewContractRates(cid)));
  const ok = []; const bad = [];
  for (const t of ready) {
    const r = await _toExternalCore(tenant, t, _qbLinkRef(t), { fromDdInvoice: true });
    (r.ok ? ok : bad).push(r.msg);
  }
  clearTicketSelection();
  renderTicketsList();
  updateBillingHeader();
  await _beSay(bad.length ? `${bad.length} not switched` : `${ok.length} switched to Billed elsewhere`,
    [`${ok.length} switched.`, ...bad].join(' '), bad.length > 0);
}

// TASK-1346 part 3 — BULK "Billed elsewhere".
// A week of QuickBooks invoices is 20-40 tickets. Making the biller open each one
// and click through a modal is precisely the friction that made mass-Archive feel
// like the reasonable option — the behaviour this whole epic exists to design out.
// So the recording action has to be at least as cheap as the destructive one.
//
// Shape follows createGroupedInvoice deliberately: partition the selection, then a
// BLOCKING confirm naming the skipped count AND the dollar total (a toast is the
// wrong severity when money is being left behind — 3rd-COD Domain on TASK-1345),
// then ONE reference prompt for the batch (one QB invoice usually covers many
// tickets, exactly like the shared invoice number the grouped path assigns).
let _bulkExternalInFlight = false;
async function markInvoicedExternallyBulk() {
  if (_moneyBlocked()) return; // TASK-9491
  // Claimed BEFORE the first await (integrated-diff COD ❌): setting it after
  // the confirm let a double-click open two prompts, each holding its own
  // now-stale eligible list, and both would write.
  if (_bulkExternalInFlight || _beSingleInFlight) return; // double-click can't run the batch twice
  _bulkExternalInFlight = true;
  // Every early return below MUST release it — see _release().
  const _release = () => { _bulkExternalInFlight = false; };
  const ids = [...BillingState.selectedTicketIds];
  if (ids.length === 0) {
    window.showToast?.('Select tickets first', 'info');
    _release();
    return;
  }
  const selected = ids.map(id => ({ id, t: BillingState.tickets.find(x => x.id === id) }));

  const eligible = [];
  const skipped = []; // { t, reason }
  const switchable = []; // hotfix/billed-elsewhere-1020 — DD-invoiced, unpaid (archived too)
  for (const { id, t } of selected) {
    if (t && !_switchToExternalBlockReason(t)) { switchable.push(t); continue; }
    const reason = _externalBillBlockReason(t);
    if (reason) { skipped.push({ t: t || { id, grandTotal: 0 }, reason }); continue; }
    // TASK-9988 S1 — someone still clocked in: skip THIS one, name who, keep going.
    const _oc = _openClockOnTicket(id);
    if (_oc.length) { skipped.push({ t, reason: 'still clocked in', who: _openClockListLabel(t, _oc) }); continue; }
    // TASK-1548 — same zero-hour gate as every other invoice-marker writer. One
    // fresh line read per candidate (a QB week is 20–40 tickets — same cost class
    // as the contract-prefs prefetch below). Read failure = skipped, fail closed.
    try {
      const _zhOff = await _fetchZeroHourOffenders(id);
      if (_zhOff.length > 0) {
        skipped.push({ t, reason: 'carrying priced equipment with 0 hours — enter hours or mark non-billable' });
        continue;
      }
      // TASK-1567 — whole-ticket $0 (internal-time customers exempt). A $0
      // invoice to a customer is never correct.
      if ((await _zeroTotalOffense(id)).blocked) {
        skipped.push({ t, reason: 'nothing to bill ($0 total) — add the missing truck/asset or hours' });
        continue;
      }
    } catch (err) {
      console.error('[Billing] Bulk mark-billed invoice-gate check failed for', id, err);
      skipped.push({ t, reason: 'unverifiable (equipment lines could not be read) — try again' });
      continue;
    }
    eligible.push(t);
  }

  // hotfix/billed-elsewhere-1020 — tickets already carrying a DispatchDeck
  // invoice are SWITCHED (each to its own linked QuickBooks number). Mixed with
  // unbilled tickets, the one typed reference below would be wrong for one group
  // or the other: the unbilled ones go on as before and the invoiced ones are
  // skipped with a reason that says how to switch them.
  if (switchable.length > 0 && eligible.length === 0) {
    try { await _switchToExternalBulk(switchable, skipped); } finally { _release(); }
    return;
  }
  for (const t of switchable) skipped.push({ t, reason: 'already invoiced in DispatchDeck — select only those to switch them' });

  if (skipped.length > 0) {
    const skipTotal = skipped.reduce((sum, s) => sum + Number(s.t?.grandTotal || 0), 0);
    // Group by reason so the biller learns WHY, not just how many — the archived
    // ones are the line that matters, because those are recoverable money.
    const byReason = {};
    for (const s of skipped) byReason[s.reason] = (byReason[s.reason] || 0) + 1;
    const reasonList = Object.entries(byReason).map(([r, n]) => `${n} ${r}`).join(', ');
    // TASK-9988 S1 — the clocked-in ones by name, so the biller knows who to call.
    const _ocWho = skipped.filter(s => s.who).map(s => s.who);
    const _ocLine = _ocWho.length
      ? `Still clocked in: ${_ocWho.join('; ')} — clock them out in Workforce first. `
      : '';
    const goOn = await window.showConfirm?.(
      `${skipped.length} ticket${skipped.length !== 1 ? 's' : ''} can't be recorded as billed`,
      `${skipped.length} of the selected tickets will be SKIPPED (${formatCurrency(skipTotal)} total): ${reasonList}. ` +
      _ocLine +
      (eligible.length > 0
        ? `Record the remaining ${eligible.length} as billed elsewhere?`
        : 'Nothing in this selection can be recorded.'),
      eligible.length > 0
        ? { confirmText: `Record ${eligible.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
        : { confirmText: 'OK', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (!goOn) { _release(); return; }
  }
  if (eligible.length === 0) {
    window.showToast?.('Nothing left to record as billed in this selection.', 'info');
    _release();
    return;
  }

  const eligibleTotal = eligible.reduce((sum, t) => sum + Number(t.grandTotal || 0), 0);
  const ref = await window.showInputModal?.(
    `Billed outside DispatchDeck — ${eligible.length} ticket${eligible.length !== 1 ? 's' : ''}`,
    `Invoice or reference number from your accounting system (optional). It is applied to all ${eligible.length} tickets (${formatCurrency(eligibleTotal)}).`,
    '',
    { okText: `Mark ${eligible.length} billed` }
  );
  if (ref === null || ref === undefined) { _release(); return; } // cancelled

  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // Prefetch each distinct customer's contract prefs ONCE, same as
    // createGroupedInvoice — recordRevenueEntry reads them per customerId out of
    // ReviewState.contractRateCache, and without them BI over-recognizes revenue
    // for customers who hide line types.
    await Promise.all([...new Set(eligible.map(t => t.customerId).filter(Boolean))]
      .map(cid => loadReviewContractRates(cid)));

    let ok = 0;
    const failed = [];      // ticket write itself failed — still genuinely unbilled
    const revFailed = [];   // billed fine, but the revenue row didn't save
    // TASK-9988 S1 — the server's per-ticket reason (e.g. someone clocked in a
    // moment after the selection was checked), so "FAILED" says WHY.
    const failReason = new Map();
    for (const t of eligible) {
      try {
        const res = await _applyInvoicedExternally(tenant, t, ref);
        ok++;
        if (!res.revenueRecorded) revFailed.push(t);
      } catch (err) {
        // One bad ticket must not abandon the rest half-recorded and unreported.
        console.error('[Billing] Bulk mark-billed failed for', t.id, err);
        failed.push(t);
        if (err?._userMessage && err?.message) failReason.set(t.id, err.message);
      }
    }
    clearTicketSelection();
    renderTicketsList();
    updateBillingHeader();
    if (failed.length > 0 || revFailed.length > 0) {
      const failTotal = failed.reduce((sum, t) => sum + Number(t.grandTotal || 0), 0);
      const parts = [`${ok} recorded as billed.`];
      if (failed.length > 0) {
        parts.push(`${failed.length} FAILED (${formatCurrency(failTotal)}) — ${failed.map(t => '#' + (t.ticketNo || '?') + (failReason.has(t.id) ? ` (${failReason.get(t.id)})` : '')).join(', ')}. Those are still unbilled; try them again.`);
      }
      if (revFailed.length > 0) {
        parts.push(`${revFailed.length} were marked billed, but your revenue reports did not update for them (${revFailed.map(t => '#' + (t.ticketNo || '?')).join(', ')}). Those tickets ARE billed — the reports will be short by their amounts until it is redone.`);
      }
      await window.showConfirm?.(
        failed.length > 0
          ? `${failed.length} ticket${failed.length !== 1 ? 's' : ''} could not be recorded`
          : 'Recorded, with a revenue warning',
        parts.join(' '),
        { confirmText: 'OK', cancelText: 'Close', icon: 'warning', iconColor: failed.length > 0 ? '#DC2626' : '#F59E0B' }
      );
    } else {
      window.showToast?.(`${ok} ticket${ok !== 1 ? 's' : ''} recorded as billed outside DispatchDeck`, 'success');
    }
  } catch (error) {
    console.error('[Billing] Bulk mark-billed error:', error);
    window.showToast?.('Could not record these tickets as billed', 'error');
  } finally {
    _bulkExternalInFlight = false;
  }
}

// ============================================
// INVOICE GENERATION
// ============================================
async function generateInvoice(ticketId, opts) {
  try {
    const _t0 = BillingState.tickets.find(t => t.id === ticketId);
    // TASK-1345 (2nd-COD Domain ⚠️) — never re-bill written-off debt. The
    // uninvoice flow clears invoiced/invoiceNumber/invoicedAt but NOT
    // writtenOff, so a written-off ticket can read as unbilled and would
    // otherwise take a fresh invoice number silently.
    if (_t0?.writtenOff === true) {
      window.showToast?.('This ticket was written off. Reverse the write-off before invoicing it.', 'error');
      return;
    }
    // TASK-1345 (2nd-COD Code Reviewer ❌) — hiding the button is not a guard.
    // Every other invoice entry point (job detail modal, calendar job tickets,
    // bulk select) still called straight in here, so an archived ticket could
    // be stamped invoiced while STAYING archived — invoiced and invisible, the
    // exact state this task exists to end. Route it through the sanctioned
    // recovery instead of refusing: unarchive first, then invoice.
    if (!(opts && opts._unarchived) && (_t0?.status === 'archived' || _t0?.status === 'locked')) {
      return unarchiveAndInvoice(ticketId, null);
    }
    // If other tickets are selected, use grouped invoice for all selected
    if (BillingState.selectedTicketIds?.size > 1) {
      // Ensure the clicked ticket is included in the selection
      BillingState.selectedTicketIds.add(ticketId);
      return createGroupedInvoice();
    }

    const ticket = BillingState.tickets.find(t => t.id === ticketId);
    if (!ticket) return;

    // Assign invoice number if not yet assigned
    if (!ticket.invoiceNumber) {
      // TASK-9491 — stamping is a money change; VIEWING an invoiced ticket's
      // invoice (the else path) stays open to a view-only login.
      if (_moneyBlocked()) return;
      // TASK-9988 S1 — first, before any line read or number allocation: never
      // invoice a ticket someone is still clocked in on.
      if (!(await _openClockGate(ticketId, 'invoice it'))) return;
      // TASK-1548 — the moment before the invoice markers are stamped is the last
      // line of defense: a priced equipment line with 0 hours invoices real work
      // at $0 (#100464 / INV-202608-055, $375 of hauling). Fresh-read gate; a
      // ticket that already carries an invoiceNumber is being VIEWED, not billed,
      // so it deliberately does not re-gate here.
      if (!(await _zeroHourGate(ticketId, 'invoice this ticket'))) return;
      // TASK-1405 — the mirror shape, same last line of defense: a rate-0
      // equipment line with real hours rides to invoice on a ticket whose OTHER
      // lines carry money, so neither the gate above nor the $0 gate below sees it.
      if (!(await _unpricedGate(ticketId, 'invoice this ticket'))) return;
      // TASK-1567 — broad $0 backstop: block a whole-ticket $0 (or no-billable-
      // lines) invoice reaching an external customer. #100472/#100478 were
      // labor-only $0 tickets the zero-hour gate above cannot see (no priced
      // equipment line at all). internalTimeOnly customers are exempt.
      if (!(await _zeroTotalGate(ticketId, 'invoice this ticket'))) return;
      // TASK-1615-B — last line of defense before the invoice markers are stamped:
      // a Per Mile quantity that came off a garbage coordinate bills a customer for
      // miles nobody drove (or $0 for miles they did).
      if (!(await _routeMileageGate(ticketId, 'invoice this ticket'))) return;
      // TASK-4741 — an equipment ref that resolves to no vehicle in the fleet
      // reaches the customer as a line nobody can explain (and its rate could
      // never be matched from the price book). Cheap to fix here; a credit memo
      // and a phone call after. Allows on "could not check" by design — see the
      // gate's own header for why this one is not fail-closed.
      if (!(await _unresolvedRefGate(ticketId, 'invoice this ticket'))) return;
      // TASK-1572 Slice 3b — a customer-required field (PO) left empty. 17% of
      // real invoices skip Ready entirely, so the Ready gate alone would miss them.
      // requiredFields arm only: the $0 arm here is _zeroTotalGate above.
      if (!(await _ticketCompletenessGate(ticketId, 'invoice this ticket', null, { checkBillable: false }))) return;
      // TASK-1572 follow-up (Chris 2026-09-29) — not Ready is a WARNING, never a
      // block. Last, so it only asks about a ticket every hard gate above passed.
      // Re-read the ticket: the gates above await, and the live snapshot may have
      // replaced the object `ticket` points at (COD Edge Case). Skipped on the
      // Unarchive & Invoice road: unarchive itself forces status 'draft', and the
      // biller has just confirmed a dialog that says an invoice will be cut —
      // a second "not Ready" prompt there is an artefact of the unarchive, and a
      // Cancel would strand the ticket unarchived-but-unbilled (COD Independent).
      if (!(opts && opts._unarchived)) {
        const _fresh = BillingState.tickets.find(t => t.id === ticketId) || ticket;
        if (!(await _notReadyInvoiceWarning([_fresh], { reviewOnCancel: true }))) return;
        if (!(await _undecidedDaysInvoiceWarning([_fresh]))) return; // TASK-10041 — warn, never block
      }
      // TASK-1587 — the stamp is server-authoritative now, so it can legitimately
      // come back REFUSED (the backstop re-ran the $0 math on a fresh line read, or
      // the caller lacks the billing boundary) or unreachable. It returns null and
      // has already toasted why; do NOT fall through to openInvoicePage, which
      // would render an invoice for a ticket that was never stamped.
      if (!(await generateInvoiceNumber(ticketId))) return;
    }

    openInvoicePage(ticketId);
  } catch (error) {
    console.error('[Billing] Error generating invoice:', error);
    window.showToast?.('Error generating invoice', 'error');
  }
}

// TASK-1346 (2nd-COD Code Reviewer ❌) — hiding "View Invoice" on the ticket card
// and the view-ticket modal was not enough: the job-detail modal, the calendar
// job-tickets modal and the Review modal's invoiced banner all still offered it,
// and the invoice window it opens carries its OWN inline "Export QB (.iif)"
// button (exportSingleIIF) that never passes through exportToIIF's new guard. So
// the guard belongs at the funnel every one of those callers goes through.
// There is no DispatchDeck invoice to render for an externally-billed ticket —
// the document lives in QuickBooks.
function viewInvoice(ticketId) {
  openInvoicePage(ticketId);
}

// TASK-10030 — a billed ticket left on a customer that was MERGED into another
// stays billing history. Reopening it would re-price it from a customer whose
// price books moved to the keeper (laborLinePricing re-applies on every write),
// so un-invoice / unarchive are refused while the merge stands.
function _mergedCustomerBlocks(ticketId) {
  const t = (BillingState.tickets || []).find(x => x.id === ticketId);
  const c = t && t.customerId ? (BillingState.customers || []).find(x => x.id === t.customerId) : null;
  if (!c || !c.mergedIntoId) return false;
  window.showToast?.(`This ticket is billing history for "${c.name || 'a customer'}", which was merged into ${c.mergedIntoName || 'another customer'}. It can't be reopened while the merge stands — undo the merge in CRM first, or bill the correction under ${c.mergedIntoName || 'the customer it was merged into'}.`, 'warning');
  return true;
}

// TASK-4767 S2 one pay path — the un-invoice write, shared by Uninvoice and the
// bulk un-mark. Re-reads the ticket in a transaction and, when its Paid flag was
// set by the invoice it is leaving (paidBy invoice:<id>), clears paid / paidAt /
// paidBy in the SAME write — all three are on the rules' locked-ticket
// lifecycle allowlist, like the invoice keys. Returns the cleared paidBy, or null.
// `merge` keeps each caller's original write shape (setDoc merge vs updateDoc).
async function _uninvoiceWrite(tenant, ticketId, fields, merge) {
  const ref = doc(db, 'tenants', tenant, 'tickets', ticketId);
  return runTransaction(db, async (tx) => {
    const s = await tx.get(ref);
    const cur = s.exists() ? { id: ticketId, ...s.data() } : null;
    if (!cur && !merge) {
      const e = new Error('ticket not found'); e.code = 'not-found'; throw e;
    }
    const clearPaid = !!cur && _paidByItsInvoice(cur);
    const data = { ...fields, ...(clearPaid ? { paid: false, paidAt: null, paidBy: null } : {}) };
    if (merge) tx.set(ref, data, { merge: true }); else tx.update(ref, data);
    return clearPaid ? cur.paidBy : null;
  });
}

async function uninvoiceTicket(ticketId) {
  if (_moneyBlocked()) return; // TASK-9491
  if (_mergedCustomerBlocks(ticketId)) return; // TASK-10030
  // TASK-9989 — what the ticket carried before, for the UNINVOICE audit row
  // (Jess's 10/20 uninvoice of #100891 left only the UNARCHIVE row).
  const _uiBefore = (BillingState.tickets || []).find(t => t.id === ticketId) || null;
  const _uiPrev = {
    invoiced: _uiBefore?.invoiced === true,
    invoiceNumber: _uiBefore?.invoiceNumber || null,
    invoicedAt: _uiBefore?.invoicedAt?.toDate ? _uiBefore.invoicedAt.toDate().toISOString() : (_uiBefore?.invoicedAt || null),
  };
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // TASK-1346 — this write deliberately does NOT clear invoicedExternally /
    // externalInvoiceRef: neither name is in the firestore.rules locked-ticket
    // lifecycle allowlist (`firestore.rules` ~:2618), and uninvoice by definition
    // runs on an invoiced ticket, so adding them would make hasOnly() reject the
    // ENTIRE uninvoice write for every tenant. The stale marker is neutralized on
    // the READ side instead — see isBilledExternally().
    // TASK-4767 S2 one pay path — a ticket its invoice marked paid stops being
    // paid the moment it leaves that invoice, in the SAME write (re-read in a
    // transaction, never off the cached card). The payment stays on the invoice.
    // A ticket a person marked paid keeps its Paid, as before.
    const _uiClearedPaid = await _uninvoiceWrite(tenant, ticketId, {
      invoiced: false,
      invoiceNumber: null,
      invoicedAt: null,
    }, true);
    // TASK-9989 — written right after the ticket write, before anything that
    // can throw, so the uninvoice is never silent in the audit log.
    window.logAudit?.('ticket', 'UNINVOICE', {
      ticketId,
      ticketNumber: _uiBefore?.ticketNo ?? null,
      before: _uiPrev,
      after: { invoiced: false, invoiceNumber: null, invoicedAt: null },
      ...(_uiClearedPaid ? { paidCleared: { paidBy: _uiClearedPaid } } : {}), // TASK-4767 S2 one pay path
    });
    // TASK-9872 void; TASK-4767 S2 — and its revenue rows stop reading paid with it.
    await _voidRevenueRowsForTicket(tenant, ticketId, 'uninvoiced', _uiClearedPaid ? { paid: false, paidAt: null } : null);
    const ticket = BillingState.tickets.find(t => t.id === ticketId);
    if (ticket) {
      ticket.invoiced = false;
      ticket.invoiceNumber = null;
      ticket.invoicedAt = null;
      if (_uiClearedPaid) { ticket.paid = false; ticket.paidAt = null; ticket.paidBy = null; }
    }
    if (ReviewState.ticket?.id === ticketId) {
      ReviewState.ticket.invoiced = false;
      ReviewState.ticket.invoiceNumber = null;
      ReviewState.ticket.invoicedAt = null;
      renderReviewTicket();
    }
    window.showToast?.(_uiClearedPaid
      ? 'Invoice removed. Ticket is now unpaid; the payment stays on the old invoice.'
      : 'Invoice removed — ticket is now editable', 'success');
    renderBillingContent();
  } catch (err) {
    console.error('[Billing] Uninvoice error:', err);
    window.showToast?.('Failed to uninvoice', 'error');
  }
}

async function openInvoicePage(ticketId) {
  // Load ticket data — emitted money: stored header or frozen `billed` only (TASK-9646 S3b)
  const ticket = _emitTicketMoney(BillingState.tickets.find(t => t.id === ticketId));
  if (!ticket) return;
  // TASK-1346 (2nd-COD Code Reviewer ❌) — the guard belongs HERE, at the one
  // funnel every caller passes through. Hiding "View Invoice" on the ticket card
  // and the view-ticket modal was not enough: the job-detail modal, the calendar
  // job-tickets modal and the Review modal's invoiced banner all still offered it,
  // and generateInvoice() reaches this directly too. Worse, the window this opens
  // carries its OWN inline "Export QB (.iif)" button (exportSingleIIF) that never
  // passes through exportToIIF's external-billed skip — a live duplicate-invoice
  // path into QB Desktop. There is no DispatchDeck invoice to render for an
  // externally-billed ticket; the document lives in the other system.
  if (isBilledExternally(ticket)) {
    window.showToast?.(`This ticket was billed outside DispatchDeck${ticket.externalInvoiceRef ? ` (${ticket.externalInvoiceRef})` : ''} — open the invoice in that system.`, 'info');
    return;
  }

  // Ensure roster data is loaded so getEquipmentDisplayName() can resolve names
  await loadReviewEquipment();

  // Preload contract rates (for column visibility config)
  if (ticket.customerId) await loadReviewContractRates(ticket.customerId);

  // Load ticketLines (use cache if viewing same ticket)
  let lines = BillingState.selectedTicketLines;
  if (!lines || lines.length === 0 || BillingState.selectedTicket?.id !== ticketId) {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    try {
      // No compound orderBy — sort client-side to avoid composite index requirement
      const snap = await getDocs(
        collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines')
      );
      lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lines.sort((a, b) => {
        if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
        const aTime = a.fromTime?.toDate?.() || new Date(0);
        const bTime = b.fromTime?.toDate?.() || new Date(0);
        return aTime - bTime;
      });
    } catch (err) {
      console.warn('[Billing] Error loading ticketLines for invoice:', err);
      lines = [];
    }
  }

  // Company info — resolved from structured arrays with IB header prefs
  const { companyName, companyAddress, companyPhone, companyEmail, logoUrl } = resolveCompanyInfo();

  // Customer info
  const customer = ticket.customerId ? BillingState.customers.find(c => c.id === ticket.customerId) : null;

  // Job info
  const job = ticket.jobId ? BillingState.jobs.find(j => j.id === ticket.jobId) : null;

  const invoiceData = {
    ticket,
    lines,
    companyName,
    companyAddress,
    companyPhone,
    companyEmail,
    customer,
    job,
    logoUrl,
    projectName: await _invoiceProjectName([ticket]) // TASK-10203
  };

  const html = buildInvoiceHTML(invoiceData);
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

// ============================================
// INVOICE HELPERS (shared by single & grouped)
// ============================================
function invoiceEsc(s) {
  if (!s) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function invoiceFmtMoney(n) {
  return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * JSON for embedding inside an inline `<script>` block on a generated invoice
 * page. `invoiceEsc` is the WRONG tool there — inside a script element the
 * browser does not decode entities, so escaping would corrupt the data; the
 * actual hazard is the literal sequence `</script>` appearing in a JSON string
 * and ENDING the element early, after which the rest is parsed as HTML.
 *
 * 🔴 Found by TASK-4819's own tripwire, pre-existing and unrelated to the
 * partition fix: `buildInvoiceHTML` embeds the whole ticket and every line into
 * its `exportSingleIIF()` handler, so a stored `description` containing
 * `</script><img src=x onerror=...>` executed in the biller's authenticated
 * session as soon as they opened the invoice. `ticketNo` is tenant-writable and
 * firestore.rules has never validated it. Escaping "<" covers a closing script
 * tag and an HTML comment opener; U+2028/U+2029 are legal in JSON but are line
 * terminators to a JS parser. Output is byte-identical for data containing
 * none of them.
 */
function invoiceJson(value) {
  // The two line separators are written as ESCAPE SEQUENCES, never as literal
  // characters: a raw U+2028 in this source file would be invisible in every
  // editor and every diff, which is the exact failure mode
  // scripts/check-control-chars.cjs exists to catch.
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * Percent-encode the characters that can break OUT of an href/src attribute.
 *
 * \ud83d\udd34 Why this exists (TASK-9111, raised by COD's Code Reviewer against the fix,
 * verified before accepting it): the logo URL travels from this popup to
 * sendInvoiceEmail, and functions/src/index.js:32993 drops it into
 * `<img src="${customerLogo}">` with NO escaping, on purpose \u2014 its comment says
 * "Don't escape the logo URL \u2014 it contains & for query params that must stay
 * intact". invoiceEsc used to turn a `"` into &quot; on the way out, so that
 * sink was incidentally covered from this path. Switching to invoiceJson (which
 * is correct, and required, for the JS-string layer) round-trips the value
 * byte-exact and would have handed that sink a raw `"`.
 *
 * Escaping it as HTML here would be the same wrong-layer mistake this whole task
 * is about, so instead this NORMALISES THE URL, which is the right thing to do
 * at the source: none of these characters are legal raw in a URL anyway (they
 * are %22/%27/%3C/%3E/%60/%20 when they are meant), so every real logo URL \u2014
 * Firebase Storage's ?alt=media&token=\u2026 included \u2014 passes through UNCHANGED,
 * "&" very much included.
 *
 * \u26a0\ufe0f This does NOT close the server-side hole. functions/src/index.js:32993 is
 * still an unescaped attribute sink for anyone calling the sendInvoiceEmail
 * callable directly; it needs its own fix by whoever owns functions/.
 */
// TASK-9857 — for an <a href>: http(s) only (a javascript: link is a click-to-run
// script; an <img src> cannot run one, which is why invoiceSafeUrl alone is
// enough for images), then the same attribute-breakout normalising.
function invoiceSafeHref(u) {
  return (typeof u === 'string' && /^https?:\/\//i.test(u)) ? invoiceSafeUrl(u) : '#';
}

function invoiceSafeUrl(u) {
  if (!u) return '';
  return String(u)
    .replace(/"/g, '%22')
    .replace(/'/g, '%27')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E')
    .replace(/`/g, '%60')
    .replace(/\s/g, '%20');
}

/** Resolve company info from tenant data using IB company header preferences */
function resolveCompanyInfo(headerSettings) {
  const td = window.AppState?.tenantData || {};
  const ch = headerSettings || window.AppState?.settings?.companyHeader || {};

  const companyName = td.companyName || td.name || td.company || 'Your Company';

  // Build arrays with backward compat
  const phones = td.companyPhones && td.companyPhones.length > 0 ? td.companyPhones : (td.phone ? [{ label: 'Main', phone: td.phone }] : []);
  const emails = td.companyEmails && td.companyEmails.length > 0 ? td.companyEmails : (td.email ? [{ label: 'General', email: td.email }] : []);
  const addresses = td.companyAddresses && td.companyAddresses.length > 0 ? td.companyAddresses : (td.address ? [{ label: 'Main', address: td.address }] : []);

  // Pick by label preference or first entry
  const selectedPhone = ch.phoneLabel ? (phones.find(p => p.label === ch.phoneLabel)?.phone || phones[0]?.phone || '') : (phones[0]?.phone || '');
  const selectedEmail = ch.emailLabel ? (emails.find(e => e.label === ch.emailLabel)?.email || emails[0]?.email || '') : (emails[0]?.email || '');
  const selectedAddress = ch.addressLabel ? (addresses.find(a => a.label === ch.addressLabel)?.address || addresses[0]?.address || '') : (addresses[0]?.address || '');

  return {
    companyName: ch.showName !== false ? companyName : '',
    companyPhone: ch.showPhone !== false ? selectedPhone : '',
    companyEmail: ch.showEmail !== false ? selectedEmail : '',
    companyAddress: ch.showAddress !== false ? selectedAddress : '',
    logoUrl: td.companyLogoUrl || BillingState.companyLogoUrl || null,
    rawCompanyName: companyName // always available for title tags etc
  };
}

function buildLaborTableHTML(empLines, colConfig) {
  if (empLines.length === 0) return '';
  const cc = colConfig || {};
  const _showHours = cc.showHours !== false;
  const _showRate = cc.showRate !== false;
  const _showTotal = cc.showTotal !== false;
  const _showRole = cc.showRole !== false;
  const _showLineItem = cc.showLineItem === true;
  // If ALL display columns are off, hide entire section
  if (!_showHours && !_showRate && !_showTotal && !_showRole && !_showLineItem) return '';
  const subtotal = empLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const _founder = namesOnTickets(); // TASK-865 — names+Role-column display flag (was isFounder)
  const _anyPieceRate = empLines.some(l => !isTimeBasedUnit(l.rateUnit));
  const rows = empLines.map(l => {
    const _pu = !isTimeBasedUnit(l.rateUnit);
    return `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(getEmployeeDisplayName(l))}</td>
      ${_founder && _showRole ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.role || '-')}</td>` : ''}
      ${_showLineItem ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.lineItemDescription || '')}</td>` : ''}
      ${_showHours ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${_pu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
      ${_showRate ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}${_pu ? '<span style="font-size:0.75em;color:#64748B;">' + invoiceEsc(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>` : ''}
      ${_showTotal ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
    </tr>
  `; }).join('');
  let colCount = 1; // Name/Role col
  if (_founder && _showRole) colCount++;
  if (_showLineItem) colCount++;
  if (_showHours) colCount++;
  if (_showRate) colCount++;
  return `
    <div class="section-title">Labor</div>
    <table><thead><tr>
      <th>${_founder ? 'Name' : 'Role'}</th>${_founder && _showRole ? '<th>Role</th>' : ''}${_showLineItem ? '<th>Line Item</th>' : ''}${_showHours ? `<th class="right">${_anyPieceRate ? 'Hours / Qty' : 'Hours'}</th>` : ''}${_showRate ? '<th class="right">Rate</th>' : ''}${_showTotal ? '<th class="right">Amount</th>' : ''}
    </tr></thead><tbody>
      ${rows}
      ${_showTotal ? `<tr class="subtotal-row"><td colspan="${colCount}" style="text-align:right;">Labor Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>` : ''}
    </tbody></table>
  `;
}

function buildEquipTableHTML(equipLines, colConfig, sectionTitle) {
  if (equipLines.length === 0) return '';
  const cc = colConfig || {};
  const _showTimes = cc.showTimes === true;
  const _showHours = cc.showHours !== false;
  const _showRate = cc.showRate !== false;
  const _showTotal = cc.showTotal !== false;
  const _showLineItem = cc.showLineItem === true;
  // If ALL display columns are off, hide entire section
  if (!_showHours && !_showRate && !_showTotal && !_showLineItem) return '';
  const title = sectionTitle || 'Equipment';
  const subtotal = equipLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = equipLines.map(l => {
    const _pu = !isTimeBasedUnit(l.rateUnit);
    return `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(getEquipmentDisplayName(l))}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.fleetNumber || '-')}</td>
      ${_showTimes ? (_pu ? '<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;color:#94A3B8;">&mdash;</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;color:#94A3B8;">&mdash;</td>' : `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineFromHtml(l)}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineToHtml(l)}</td>`) : ''}
      ${_showLineItem ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.lineItemDescription || '')}</td>` : ''}
      ${_showHours ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${_pu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
      ${_showRate ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}${_pu ? '<span style="font-size:0.75em;color:#64748B;">' + invoiceEsc(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>` : ''}
      ${_showTotal ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
    </tr>
  `; }).join('');
  let subColspan = 2; // Asset + Fleet#
  if (_showTimes) subColspan += 2;
  if (_showLineItem) subColspan++;
  if (_showHours) subColspan++;
  if (_showRate) subColspan++;
  return `
    <div class="section-title">${invoiceEsc(title)}</div>
    <table><thead><tr>
      <th>Asset</th><th>Fleet #</th>${_showTimes ? '<th>From</th><th>To</th>' : ''}${_showLineItem ? '<th>Line Item</th>' : ''}${_showHours ? '<th class="right">QTY</th>' : ''}${_showRate ? '<th class="right">Rate</th>' : ''}${_showTotal ? '<th class="right">Amount</th>' : ''}
    </tr></thead><tbody>
      ${rows}
      ${_showTotal ? `<tr class="subtotal-row"><td colspan="${subColspan}" style="text-align:right;">${invoiceEsc(title)} Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>` : ''}
    </tbody></table>
  `;
}

function buildEquipSubTables(equipLines, customerId) {
  equipLines = equipLines.filter(l => l.includeOnInvoice !== false);
  // TASK-4819 — the SAME missing-bucket defect one level down, caught by COD's
  // Code Reviewer: this used to be `!l.assetType || l.assetType === 'asset'`, so
  // an EQUIPMENT line whose assetType was any other truthy string (a 'crane', a
  // future kind) reached the ticket total and none of the three tables. Live
  // data carries truck 1,004 / trailer 401 / asset 49 / undefined 32 / "" 5 —
  // `""` is falsy so it already landed here, which is why the output is
  // unchanged for every existing row. Assets is now the catch-all so the bucket
  // cannot be empty for a line that exists.
  const vehicleLines = equipLines.filter(l => l.assetType === 'truck');
  const trailerLines = equipLines.filter(l => l.assetType === 'trailer');
  const assetLines   = equipLines.filter(l => l.assetType !== 'truck' && l.assetType !== 'trailer');
  return [
    buildEquipTableHTML(vehicleLines, getInvoiceColumnConfig(customerId, 'vehicles'), 'Vehicles'),
    buildEquipTableHTML(trailerLines, getInvoiceColumnConfig(customerId, 'trailers'), 'Trailers'),
    buildEquipTableHTML(assetLines,   getInvoiceColumnConfig(customerId, 'assets'),   'Assets'),
  ].join('');
}

function buildPermitTableHTML(permitLines) {
  if (permitLines.length === 0) return '';
  const subtotal = permitLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = permitLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || '-')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>
    </tr>
  `).join('');
  return `
    <div class="section-title">Load Permits</div>
    <table><thead><tr>
      <th>Description</th><th class="right">Amount</th>
    </tr></thead><tbody>
      ${rows}
      <tr class="subtotal-row"><td style="text-align:right;">Load Permit Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>
    </tbody></table>
  `;
}

// TASK-9725 — the invoice's "Consumables used on this job" sub-group. The
// billed-PPE roll-up lines (`_isPpeRollupLine`: ppe_<assetId>, sourceType
// 'safetyEquipment', minted by services/ppeBilling.js) print in their own table
// right under Field Items; every other Field Item stays where it was. DISPLAY
// ONLY: the lines are already partitioned + filtered (isLineInvoiced, $0/hide
// rules) before they get here and the totals come from those partitions
// (invoiceTicketTotals), never from these tables, so no money moves. No billed
// PPE line → the one Field Items table, byte-identical to before.
// Server twin (emailed invoice PDF): fieldTicketDocument.js splitSectionLines.
// Portal twin: app.js loadPublicInvoicePage. Same label in all three.
const INVOICE_CONSUMABLES_LABEL = 'Consumables used on this job';
function splitInvoiceFieldItems(fieldItemLines) {
  const items = [], consumables = [];
  for (const l of fieldItemLines || []) (_isPpeRollupLine(l) ? consumables : items).push(l);
  return { items, consumables };
}

function buildFieldItemsTableHTML(fieldItemLines) {
  const { items, consumables } = splitInvoiceFieldItems(fieldItemLines);
  return buildFieldItemsGroupTableHTML(items, 'Field Items', 'Field Items Subtotal')
    + buildFieldItemsGroupTableHTML(consumables, INVOICE_CONSUMABLES_LABEL, 'Consumables Subtotal');
}

function buildFieldItemsGroupTableHTML(fieldItemLines, title, subtotalLabel) {
  if (fieldItemLines.length === 0) return '';
  const subtotal = fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = fieldItemLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || '-')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${l.quantity || 0}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>
    </tr>
  `).join('');
  return `
    <div class="section-title">${invoiceEsc(title)}</div>
    <table><thead><tr>
      <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
    </tr></thead><tbody>
      ${rows}
      <tr class="subtotal-row"><td colspan="3" style="text-align:right;">${invoiceEsc(subtotalLabel)}</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>
    </tbody></table>
  `;
}

function buildMiscTableHTML(miscLines) {
  if (miscLines.length === 0) return '';
  const subtotal = miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = miscLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || '-')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${l.quantity || 0}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>
    </tr>
  `).join('');
  return `
    <div class="section-title">Miscellaneous</div>
    <table><thead><tr>
      <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
    </tr></thead><tbody>
      ${rows}
      <tr class="subtotal-row"><td colspan="3" style="text-align:right;">Miscellaneous Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>
    </tbody></table>
  `;
}

// ============================================
// TASK-4819 — PARTITION: money cannot reach a total without reaching a row.
// ============================================
// THE DEFECT THIS CLOSES. Three renderers in this file each filtered lines by a
// HARDCODED LIST of type names and then totalled with `ticket.grandTotal ||
// sum(EVERY line)`. Any type outside a renderer's list contributed to the total
// the customer is billed without ever appearing as a row they can see:
//
//   buildGroupedInvoiceHTML  7 types — no POTHOLE, no MATERIAL, no unknown
//   buildInvoiceHTML         7 types — no POTHOLE, no MATERIAL, no unknown
//   buildTicketDetailHTML    8 types —              no MATERIAL, no unknown
//
// This is the same hole TASK-4756 closed in the EMAILED field ticket
// (functions/src/services/fieldTicketDocument.js), and the fix is the same
// shape on purpose: PARTITION the lines so every one lands in exactly one
// bucket, send anything unrecognised to "Other Charges", and total the sum of
// WHAT ACTUALLY RENDERED. Adding 'MATERIAL' to a list of seven would only move
// the discovery of the eighth type to a customer, later.
//
// MEASURED read-only across all 10 tenants holding tickets, 2026-09-08 (1,058
// non-deleted tickets, 960 with lines). The live vocabulary is NINE types and
// was derived from the data, not from this list: EMPLOYEE 1,495 · EQUIPMENT
// 1,489 · PERMIT 206 ($37,157.51) · FIELD_ITEM 139 · POTHOLE 29 · DOWNTIME 25 ·
// MISC 20 ($13,224.00) · STANDBY 15 · MATERIAL 1 ($143.00). Exposure TODAY is
// $143.00 on one show-account demo ticket (#1003, "Fresh water — 130 bbl") and
// ZERO real customer money — POTHOLE is $0.00 on all 29 rows, informational by
// design. The hole, not the balance, is the reason this is fixed: the moment a
// carrier bills a material or a permit through a path whose list is short, the
// charge vanishes from the document while staying in the total.
//
// 🔴 NOTHING HERE INVENTS OR SUPPRESSES A COST. Every row renders the STORED
// rate and amount. TITS man-hour lines carry no cost by design
// ([[feedback_man_hour_lines_never_carry_a_cost]]) and still render at $0.00
// here; 32 EMPLOYEE lines on OTHER tenants (demo, mmfo7b4no2fu0ypjl,
// mnc4z8d70evle7pzu) do carry a cost, and those render it. Equipment hours
// exceeding employee hours is CORRECT ([[reference_tits_billing_shop_to_shop]])
// and is never reconciled.

// Every type this file has a section for. 'OTHER' is not a stored type — it is
// the catch-all bucket, and its existence is what makes a missing branch
// impossible to hide.
// TASK-9440 — FUEL_SURCHARGE gets its own bucket; EVERY renderer below renders
// it (buildFuelSurchargeTableHTML / the detail copy's fuel block), so the
// "nothing reaches the total without reaching a row" invariant holds.
const INVOICE_SECTION_TYPES = ['EMPLOYEE', 'EQUIPMENT', 'DOWNTIME', 'STANDBY', 'PERMIT', 'FIELD_ITEM', 'POTHOLE', 'MISC', 'FUEL_SURCHARGE'];

/**
 * Split ticket lines into exactly one bucket each.
 *
 * @param {Array} lines      all ticketLines for one ticket
 * @param {Function} includeFn  the shared inclusion predicate — `isLineInvoiced`
 *        bound to the customer's exclude list on invoice copies, or `() => true`
 *        on the internal field-ticket copy. A line it rejects was DELIBERATELY
 *        hidden by the biller (TASK-511), so it lands in `dropped`: not rendered
 *        and not totalled, which is exactly what the old `excludedInvoiceAmount`
 *        subtraction achieved, minus the chance of the two drifting apart.
 * @returns {object} buckets keyed by type, plus OTHER and dropped.
 */
function partitionInvoiceLines(lines, includeFn) {
  const parts = { OTHER: [], dropped: [] };
  for (const t of INVOICE_SECTION_TYPES) parts[t] = [];
  for (const l of (lines || [])) {
    if (!l) continue;
    if (!includeFn(l)) { parts.dropped.push(l); continue; }
    const type = String(l.type == null ? '' : l.type);
    // 🔴 NO EQUIPMENT SPECIAL CASE HERE, and COD's Code Reviewer was right to
    // reject the first cut that had one. `buildEquipSubTables` (:14626) filters
    // `includeOnInvoice !== false` itself, so mirroring that looked like it kept
    // the bucket equal to the table. It does not: on the two INVOICE paths
    // `includeFn` is `isLineInvoiced`, which already drops those lines, making
    // the mirror dead code — and the ONLY case where it could fire is the
    // internal FIELD TICKET, whose equipment renderer is `_buildDtlEquipSub`
    // (:15195), which has never filtered `includeOnInvoice` at all. So the extra
    // drop could only ever have removed equipment from the driver's own copy of
    // the ticket, which no one asked for. The shared predicate is the only gate.
    parts[INVOICE_SECTION_TYPES.includes(type) ? type : 'OTHER'].push(l);
  }
  return parts;
}

/**
 * The sum of what the sections above actually render, sign-aware (DOWNTIME is a
 * deduction and is rendered as a negative). This is the pre-tax subtotal.
 *
 * POTHOLE is informational: it renders no Amount column while every one of its
 * lines is $0, and contributes 0 — matching both the printed ticket and
 * `_ticketLineTaxCategory`, which drops POTHOLE from the tax base. If a pothole
 * line ever DID carry money, buildLineInfoTableHTML shows an Amount column and
 * this sums it. That direction is deliberate: a visible row whose money counts
 * is a document that foots; the alternative is the bug being fixed here.
 *
 * 🔴 This does NOT re-derive the PERMIT upcharge that computeTicketTotalsFromLines
 * applies (baseCost * upchargePercent). It sums the stored `lineAmount`, which is
 * what buildPermitTableHTML puts on the page. Measured: 0 of 206 live PERMIT
 * lines carry a non-zero upchargePercent or a baseCost that differs from
 * lineAmount, so the two agree today — and where they ever disagree the
 * reconciliation notice below tells the biller instead of silently picking one.
 */
function invoiceRenderedSubtotal(parts) {
  let sum = 0;
  for (const key of INVOICE_SECTION_TYPES.concat(['OTHER'])) {
    for (const l of (parts[key] || [])) {
      sum += (l.type === 'DOWNTIME' ? -1 : 1) * (Number(l.lineAmount) || 0);
    }
  }
  return Math.round(sum * 100) / 100;
}

/**
 * WHICH TOTAL THE INVOICE TRUSTS: the rows. Always.
 *
 * The old code read `ticket.grandTotal || sum(lines)`, so the printed total was
 * a stored header the reader could not derive from the page. Those two disagree
 * on 33 live tickets — 32 show-account demo seed (stored totals against $0
 * lines) and ONE real: trippin-trucking #1088, stored $32.75 against $20.00 of
 * lines. Trusting the header would make footing impossible by construction, so
 * the rows win. That also matches what this file already declares elsewhere:
 * TASK-1666's self-heal writes the LINE SUM back over a stale header, and the
 * invoice-eligibility gate at :1168 reads lines and never the stored total.
 *
 * TAX IS NOT STRIPPED. The stored grandTotal is `subtotal + taxAmount`
 * (computeTicketTotalsFromLines), so a naive "sum the rows" total would have
 * silently dropped sales tax — the CRIT-2 regression of 2026-05-31. The rows
 * are the SUBTOTAL and the stored taxAmount is added back on top, so a taxed
 * invoice still foots as subtotal + tax = total. (0 of 1,058 live tickets have
 * taxEnabled today, so this path is dormant but must not rot.)
 *
 * NOTHING IS SILENTLY RECONCILED. When the header disagrees with the rows the
 * biller gets `notice` — rendered as a screen-only, never-printed block — naming
 * both numbers so the ticket can be fixed at the source. The customer's copy
 * always foots; the discrepancy is a DispatchDeck-side data problem and is
 * shown to the DispatchDeck user, not to their customer.
 */
function invoiceTicketTotals(ticket, lines, parts) {
  const subtotal = invoiceRenderedSubtotal(parts);
  const taxAmount = ticket.taxEnabled ? (Number(ticket.taxAmount) || 0) : 0;
  const total = Math.round((subtotal + taxAmount) * 100) / 100;
  // What this ticket WOULD have shown before TASK-4819, for the notice only.
  const storedRaw = Number(ticket.grandTotal) || (lines || []).reduce(
    (s, l) => s + (l.type === 'DOWNTIME' ? -1 : 1) * (Number(l.lineAmount) || 0), 0);
  const storedShown = Math.round((storedRaw - (parts.dropped || []).reduce(
    (s, l) => s + (l.type === 'DOWNTIME' ? -1 : 1) * (Number(l.lineAmount) || 0), 0)) * 100) / 100;
  const drifted = Math.abs(total - storedShown) > 0.005;
  // ══ TASK-9251 TELEMETRY — the invoice does not equal the stored ticket ══
  //
  // This IS the "does something DIFFERENT from what it told you" case: the
  // customer's invoice is added up from the line items, while the ticket card,
  // the billing list and BI all read the stored grandTotal. The biller gets a
  // screen-only yellow notice; nothing durable records WHICH tickets are wrong,
  // so "our revenue number doesn't match what we invoiced" has, until now, had
  // no answer short of re-footing tickets by hand.
  //
  // `logUserActionOnce` keyed on the ticket, because this runs on every invoice
  // RENDER — the same preview reopened five times is one defect, not five rows.
  // A multi-ticket invoice legitimately writes one row per drifted ticket; that
  // is the count support needs. Guarded: a diagnostic must never take down an
  // invoice.
  if (drifted) {
    try {
      window.Telemetry?.logUserActionOnce(String(ticket.id || ''), 'invoice_total_drift', {
        ticketId: ticket.id || null,
        ticketNo: ticket.ticketNo || null,
        storedTotal: storedShown,
        invoicedTotal: total,
        delta: Math.round((total - storedShown) * 100) / 100,
        lineCount: (lines || []).length,
        droppedCount: (parts?.dropped || []).length,
        taxEnabled: !!ticket.taxEnabled,
        status: ticket.status || null,
      });
    } catch (_) { /* never break an invoice over a breadcrumb */ }
  }
  return { subtotal, taxAmount, total, storedShown, drifted };
}

/**
 * Screen-only reconciliation notice — a message to the BILLER looking at the
 * preview window, never to the customer.
 *
 * 🔴 IT MUST BE RENDERED OUTSIDE `.invoice-page`, AND THAT IS NOT A STYLE
 * PREFERENCE. `.no-print` is a PRINT rule in invoiceStylesHTML, which keeps it
 * off paper and out of the PDF — but the single-ticket invoice page EMAILS
 * `document.querySelector('.invoice-page').outerHTML` to the customer wrapped in
 * its own small stylesheet, where a print rule means nothing. The first cut of
 * TASK-4819 put this block inside `.invoice-page`, which would have mailed
 * "the saved ticket total does not match its line items" straight to the
 * customer — an invoice dispute we started ourselves. COD's Code Reviewer caught
 * it. All three renderers now emit it outside the document, the email wrapper
 * also hides `.no-print` as a second line of defence, and
 * tests/invoice-line-type-partition.unit.cjs §11b holds the line.
 */
function invoiceReconcileNoticeHTML(rows) {
  if (!rows || rows.length === 0) return '';
  // 🔴 `label` comes from `ticket.ticketNo`, which is stored tenant data — it is
  // escaped, not interpolated raw (COD Code Reviewer). The amounts are numbers
  // computed above, but they are coerced anyway because `invoiceFmtMoney` calls
  // `.toLocaleString()` on whatever it is given and a STRING passes through it
  // unsanitised.
  const money = (n) => invoiceFmtMoney(Number(n) || 0);
  const items = rows.map(r =>
    `<li>Ticket ${invoiceEsc(r.label)} &mdash; the saved ticket total (${money(r.storedShown)}) does not match its line items (${money(r.total)}). This invoice was generated from the line items, <strong>${money(r.total)}</strong>.</li>`
  ).join('');
  return `<div class="no-print" style="max-width:8.5in;margin:0 auto 16px;padding:12px 16px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:6px;color:#0F172A;font-size:13px;line-height:1.6;">
    <div style="font-weight:700;margin-bottom:4px;">Heads up — this invoice was added up from the line items.</div>
    <ul style="margin:4px 0 4px 18px;">${items}</ul>
    <div style="color:#475569;">The invoice adds up to what is printed on it. If the saved total is the right one, open the ticket and fix the lines so the two agree. This box is never printed.</div>
  </div>`;
}

/**
 * Line Info (POTHOLE) for the invoice stylesheet. Columns mirror the printed
 * ticket's (billing.js `_dtlSections.lineInfo`) so the two copies of a ticket
 * do not disagree about what a pothole row says. Informational: the Amount
 * column appears only if some line actually carries money — 0 of 29 live rows
 * do — so today this renders exactly as print does and contributes $0.00.
 */
function buildLineInfoTableHTML(potholeLines) {
  if (!potholeLines || potholeLines.length === 0) return '';
  const money = potholeLines.some(l => (Number(l.lineAmount) || 0) !== 0);
  const rows = potholeLines.map(l => {
    // Coerced, not trusted: a stored gpsLat that is a STRING would throw on
    // `.toFixed`, taking the whole invoice window down with it.
    const lat = Number(l.gpsLat), lng = Number(l.gpsLng);
    const gpsStr = (Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng))
      ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : '-';
    return `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || '-')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:11px;">${invoiceEsc(gpsStr)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.notes || '-')}</td>
      ${money ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>` : ''}
    </tr>
  `; }).join('');
  const subtotal = potholeLines.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
  return `
    <div class="section-title">Line Info</div>
    <table><thead><tr>
      <th>Description</th><th>GPS</th><th>Notes</th>${money ? '<th class="right">Amount</th>' : ''}
    </tr></thead><tbody>
      ${rows}
      ${money ? `<tr class="subtotal-row"><td colspan="3" style="text-align:right;">Line Info Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>` : ''}
    </tbody></table>
  `;
}

/**
 * THE CATCH-ALL. Anything the eight named sections do not claim renders here
 * rather than nowhere. Columns mirror buildMiscTableHTML, because an
 * unclassified charge is shaped like a miscellaneous one: description, qty,
 * rate, amount. The line's own description carries the meaning the customer
 * needs ("Fresh water — 130 bbl"); the internal type name is not printed on a
 * customer document, and falls back in only when there is no description at all.
 */
function buildOtherChargesTableHTML(otherLines) {
  if (!otherLines || otherLines.length === 0) return '';
  const subtotal = otherLines.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
  // 🔴 EVERY interpolation here is escaped or numerically coerced, including
  // `quantity` — the sibling renderers interpolate it raw, and `invoiceFmtMoney`
  // passes a STRING straight through `.toLocaleString()` without sanitising it.
  // These lines carry a type no code path validated, so they are the least
  // trustworthy values on the page (COD Code Reviewer).
  const rows = otherLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || l.type || 'Charge')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceEsc(l.quantity || 0)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(Number(l.rate) || 0)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>
    </tr>
  `).join('');
  return `
    <div class="section-title">Other Charges</div>
    <table><thead><tr>
      <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
    </tr></thead><tbody>
      ${rows}
      <tr class="subtotal-row"><td colspan="3" style="text-align:right;">Other Charges Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>
    </tbody></table>
  `;
}

// TASK-9440 — the fuel surcharge on the customer's invoice. Description carries
// the % and base ("Fuel Surcharge 12% of $1,450.00 equipment"); the Amount is the
// stored lineAmount, which is exactly what invoiceRenderedSubtotal sums.
function buildFuelSurchargeTableHTML(fuelLines) {
  if (!fuelLines || fuelLines.length === 0) return '';
  const subtotal = fuelLines.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
  const rows = fuelLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || 'Fuel Surcharge')}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>
    </tr>
  `).join('');
  return `
    <div class="section-title">Fuel Surcharge</div>
    <table><thead><tr>
      <th>Description</th><th class="right">Amount</th>
    </tr></thead><tbody>
      ${rows}
      ${fuelLines.length > 1 ? `<tr class="subtotal-row"><td style="text-align:right;">Fuel Surcharge Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td></tr>` : ''}
    </tbody></table>
  `;
}

function buildWorkPerformedHTML(ticket, allLines) {
  const generalNotes = ticket.notes || ticket.workPerformed || '';
  const showNotes = ticket.showNotesOnInvoice !== false;

  // Per-asset notes from equipment lines
  let assetNotesHtml = '';
  if (isPerAssetNotesEnabled() && allLines) {
    const equipWithNotes = allLines.filter(l => l.type === 'EQUIPMENT' && l.notes?.trim() && assetTypeGetsNotes(l.assetType));
    if (equipWithNotes.length > 0) {
      assetNotesHtml = equipWithNotes.map(l => {
        const name = l.assetName || l.fleetNumber || 'Equipment';
        return `<div style="margin-bottom:8px;padding-left:10px;border-left:3px solid #3B82F6;">
          <div style="font-weight:600;font-size:13px;color:#0F172A;">${invoiceEsc(name)}</div>
          <div style="font-size:12px;color:#334155;white-space:pre-wrap;">${invoiceEsc(l.notes)}</div>
        </div>`;
      }).join('');
    }
  }

  if (!assetNotesHtml && (!generalNotes || !showNotes)) return '';

  let html = '<div style="margin-bottom:24px;"><div class="section-title">Work Performed</div>';
  if (assetNotesHtml) {
    html += assetNotesHtml;
  }
  if (generalNotes && showNotes) {
    if (assetNotesHtml) {
      html += '<div style="margin-top:10px;font-size:11px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;">General Notes</div>';
    }
    html += `<div id="invoice-notes-text" style="color:#334155;font-size:13px;white-space:pre-wrap;">${invoiceEsc(generalNotes)}</div>`;
  }
  html += '</div>';
  return html;
}

function buildDowntimeTableHTML(downLines, colConfig) {
  if (downLines.length === 0) return '';
  const cc = colConfig || {};
  const _showHours = cc.showHours !== false;
  const _showRate = cc.showRate !== false;
  const _showTotal = cc.showTotal !== false;
  const subtotal = downLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = downLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(getEmployeeDisplayName(l))}</td>
      ${_showHours ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${(l.hours || 0).toFixed(2)}</td>` : ''}
      ${_showRate ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
      ${_showTotal ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;color:#DC2626;">-${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${invoiceEsc(l.notes || '')}</td>
    </tr>
  `).join('');
  let subColspan = 1; // Person
  if (_showHours) subColspan++;
  if (_showRate) subColspan++;
  return `
    <div class="section-title">Downtime Deduction</div>
    <table><thead><tr>
      <th>Person</th>${_showHours ? '<th class="right">Hours</th>' : ''}${_showRate ? '<th class="right">Rate</th>' : ''}${_showTotal ? '<th class="right">Deduction</th>' : ''}<th>Notes</th>
    </tr></thead><tbody>
      ${rows}
      ${_showTotal ? `<tr class="subtotal-row"><td colspan="${subColspan}" style="text-align:right;">Downtime Deduction</td><td style="text-align:right;color:#DC2626;">-${invoiceFmtMoney(subtotal)}</td><td></td></tr>` : ''}
    </tbody></table>
  `;
}

function buildStandbyTableHTML(standbyLines, colConfig) {
  if (standbyLines.length === 0) return '';
  const cc = colConfig || {};
  const _showHours = cc.showHours !== false;
  const _showRate = cc.showRate !== false;
  const _showTotal = cc.showTotal !== false;
  const subtotal = standbyLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const rows = standbyLines.map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(getEmployeeDisplayName(l))}</td>
      ${_showHours ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${(l.hours || 0).toFixed(2)}</td>` : ''}
      ${_showRate ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
      ${_showTotal ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${invoiceEsc(l.notes || '')}</td>
    </tr>
  `).join('');
  let subColspan = 1; // Person
  if (_showHours) subColspan++;
  if (_showRate) subColspan++;
  return `
    <div class="section-title">Standby</div>
    <table><thead><tr>
      <th>Person</th>${_showHours ? '<th class="right">Hours</th>' : ''}${_showRate ? '<th class="right">Rate</th>' : ''}${_showTotal ? '<th class="right">Amount</th>' : ''}<th>Notes</th>
    </tr></thead><tbody>
      ${rows}
      ${_showTotal ? `<tr class="subtotal-row"><td colspan="${subColspan}" style="text-align:right;">Standby Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(subtotal)}</td><td></td></tr>` : ''}
    </tbody></table>
  `;
}

// TASK-9390 — the "Type" header row on tickets and invoices. AR/AP → Templates
// owns the switch: settings.headerFields = { jobType: { ticket, invoice } },
// default OFF on both. The value is the ticket's OWN snapshot (`ticket.jobType`,
// stamped at ticket creation) — never the live job — so the printed ticket, the
// invoice, the emailed PDF and the approval page read one value and one switch
// (the server twin is fieldTicketDocument.headerJobTypeFor; one fixture table
// drives both in tests/task9390-job-type.unit.cjs). Plain text; callers escape.
// `settingsOverride` = the builder's PENDING local settings (preview only).
function _ticketHeaderJobType(ticket, surface, settingsOverride) {
  const JT = window.JobTypes;
  if (!JT) return '';
  return JT.headerJobType(ticket, settingsOverride || window.AppState?.settings || {}, surface);
}

// TASK-10203 — settings.headerFields, normalised with EVERY leaf present (the
// writers save the whole map). jobType: default OFF on both. project: tickets
// default ON when the key is missing (the line printed always since 2108 — the
// server twin is services/ticketProjectName.js), invoices default OFF.
function _ibHeaderFields(h) {
  return {
    jobType: { ticket: h?.jobType?.ticket === true, invoice: h?.jobType?.invoice === true },
    project: { ticket: h?.project?.ticket !== false, invoice: h?.project?.invoice === true },
  };
}

// TASK-10203 — the Project line on a DD invoice. PURE. A ticket is in a project
// only through its JOB (ticketProjectOf); the project must still name the
// ticket's customer — its own customerId, else its job's, same as the server
// resolver (the firestore.rules jobProjectAdmits pairing, re-checked at print
// time). Returns the name only when EVERY ticket resolves to the SAME
// admitted project; any miss, mix, unknown job or unloaded project = '' (no
// line, never a list). Plain text; callers escape.
function invoiceProjectNameFor(tickets, jobs, projects) {
  const idx = buildJobProjectIndex(jobs);
  let pid = null;
  for (const t of (tickets || [])) {
    const p = ticketProjectOf(t, idx);
    if (!p || (pid && p !== pid)) return '';
    pid = p;
    const proj = (projects || []).find(x => x && x.id === p);
    const ids = proj && (proj.customerIds === undefined ? [] : proj.customerIds);
    const job = (jobs || []).find(j => j && j.id === t.jobId); // its own customer, else its job's (server twin)
    const c = [t.customerId, job && job.customerId].find(x => typeof x === 'string' && x) || null;
    if (!Array.isArray(ids) || proj.deleting === true || !(c ? ids.includes(c) : !ids.length)) return '';
  }
  const proj = pid && (projects || []).find(x => x && x.id === pid);
  return proj ? String(proj.name || '').replace(/\s+/g, ' ').trim().slice(0, 120) : '';
}

// TASK-10203 — the switch (settings.headerFields.project.invoice, default OFF)
// plus a FRESH projects read (customerIds / deleting can change after the
// cached load — COD). Never throws; '' = no line.
async function _invoiceProjectName(tickets) {
  if (window.AppState?.settings?.headerFields?.project?.invoice !== true) return '';
  try {
    await loadBillingProjects();
    if (BillingState._projectsLoadFailed) return '';
    return invoiceProjectNameFor(tickets, BillingState.jobs, BillingState.projects);
  } catch (_) { return ''; }
}

// TASK-9390 — the label a NEW ticket snapshots from its job. Null for the
// default "Job" type (the same rule the placard and the driver view use: the
// default reads as empty), so plain jobs print no Type row even when the
// template switch is on. A legacy free-text type snapshots as itself.
// TASK-9390 — the job type CODE (slug / legacy text as stored) a new ticket
// snapshots beside the label; null for the default type. Twin of clock.js.
function _ticketJobTypeCodeFromJob(job) {
  const JT = window.JobTypes;
  if (!job || !JT || JT.isDefault(job.type)) return null;
  return String(job.type);
}

function _ticketJobTypeFromJob(job) {
  const JT = window.JobTypes;
  if (!job || !JT || JT.isDefault(job.type)) return null;
  return JT.labelForJob(job, JT.list(window.AppState?.settings || {})) || null;
}

function buildTicketDetailHTML(ticket, lines, companyName, logoUrl, opts = {}) {
  const isFieldTicket = opts.isFieldTicket || false;
  // Field ticket = internal/driver copy → show everything. Invoice copy → apply
  // per-line override + per-customer hide-by-default via the shared predicate.
  const _exTypes = isFieldTicket ? [] : getInvoiceExcludeLineTypes(ticket.customerId);
  const _inv = (l) => isFieldTicket || isLineInvoiced(l, _exTypes);
  // TASK-4819 — partition, so a type with no branch below cannot reach the total
  // while missing the page. This renderer knew 8 of the 9 live types: MATERIAL
  // had no branch here either, and FIELD_ITEM / POTHOLE / MISC were read straight
  // off `lines` WITHOUT the `_inv` predicate, so a biller-excluded field item
  // rendered as a row whose money had already been subtracted from the total —
  // the same footing failure pointing the other way.
  const _parts = partitionInvoiceLines(lines, _inv);
  const empLines = _parts.EMPLOYEE;
  const equipLines = _parts.EQUIPMENT;
  const downLines = _parts.DOWNTIME;
  const standbyLines = _parts.STANDBY;
  const permitLines = _parts.PERMIT;
  const _totals = invoiceTicketTotals(ticket, lines, _parts);
  const grandTotal = _totals.total;

  const workDate = ticket.workDate
    ? new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      + (ticket.workDateEnd && ticket.workDateEnd !== ticket.workDate ? ' – ' + new Date(ticket.workDateEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '')
    : '';

  const job = ticket.jobId ? BillingState.jobs.find(j => j.id === ticket.jobId) : null;
  const customer = ticket.customerId ? BillingState.customers.find(c => c.id === ticket.customerId) : null;

  let html = `<div class="ticket-detail-page">`;

  // Header with company branding
  html += `<div class="detail-header" style="display:flex;justify-content:space-between;align-items:flex-start;">`;
  html += `<div style="display:flex;align-items:flex-start;gap:10px;">`;
  if (logoUrl) html += `<img src="${invoiceSafeUrl(logoUrl)}" style="max-height:50px;max-width:160px;flex-shrink:0;" alt="Logo">`;
  if (companyName) html += `<div style="font-size:14px;font-weight:700;color:#0F172A;">${invoiceEsc(companyName)}</div>`;
  html += `</div>`;
  html += `<div style="text-align:right;">`;
  html += `<div style="font-size:16px;font-weight:800;color:#3B82F6;">FIELD TICKET</div>`;
  // 🔴 escaped — `ticketNo` is tenant-writable and firestore.rules has never
  // validated it, so a raw interpolation here was stored XSS on the internal
  // field-ticket view (COD Adversary, round 2).
  if (ticket.ticketNo) html += `<div style="font-size:13px;font-weight:600;">Ticket #${invoiceEsc(ticket.ticketNo)}</div>`;
  html += `<div style="font-size:12px;color:#475569;">${workDate}</div>`;
  html += `</div></div>`;

  // Job info
  html += `<div class="detail-info-grid">`;
  html += `<div><span class="label">Customer:</span> <strong>${invoiceEsc(customer?.name || ticket.customerName || '-')}</strong></div>`;
  if (job?.title || ticket.jobName || ticket.jobTitle) {
    html += `<div><span class="label">Job:</span> ${invoiceEsc(job?.title || ticket.jobName || ticket.jobTitle)}</div>`;
  }
  // TASK-9390 — both callers pass isFieldTicket:true; an invoice-copy caller
  // (isFieldTicket false) reads the INVOICE switch instead.
  const _dtlJobType = _ticketHeaderJobType(ticket, isFieldTicket ? 'ticket' : 'invoice');
  if (_dtlJobType) {
    html += `<div><span class="label">Type:</span> ${invoiceEsc(_dtlJobType)}</div>`;
  }
  if (ticket.location || job?.location || job?.address) {
    html += `<div><span class="label">Location:</span> ${invoiceEsc(ticket.location || job?.location || job?.address)}</div>`;
  }
  if (ticket.poNumber) {
    html += `<div><span class="label">PO #:</span> ${invoiceEsc(ticket.poNumber)}</div>`;
  }
  if (ticket.vendorNumber) {
    html += `<div><span class="label">Vendor #:</span> ${invoiceEsc(ticket.vendorNumber)}</div>`;
  }
  if (ticket.wellName) {
    html += `<div><span class="label">Well:</span> ${invoiceEsc(ticket.wellName)}${ticket.wellNumber ? ' #' + invoiceEsc(ticket.wellNumber) : ''}</div>`;
  }
  if (ticket.afeNumber) {
    html += `<div><span class="label">AFE / CC:</span> ${invoiceEsc(ticket.afeNumber)}</div>`;
  }
  html += `</div>`;

  // Build sections via ordered helper
  const _dtlFounder = namesOnTickets(); // TASK-865 — names+Role-column display flag (was isFounder)
  const _dtlShowTimes = showClockTimes();

  const _dtlSections = {};

  // Notes section (per-asset + general)
  {
    let _notesHtml = '';
    if (isPerAssetNotesEnabled()) {
      const _eqWithNotes = lines.filter(l => l.type === 'EQUIPMENT' && l.notes?.trim() && assetTypeGetsNotes(l.assetType));
      if (_eqWithNotes.length > 0) {
        _notesHtml += _eqWithNotes.map(l => `<div style="margin-bottom:6px;padding-left:8px;border-left:3px solid #3B82F6;">
          <div style="font-weight:600;font-size:12px;color:#0F172A;">${invoiceEsc(getEquipmentDisplayName(l) || l.assetName || 'Equipment')}</div>
          <div style="font-size:11px;color:#334155;white-space:pre-wrap;">${invoiceEsc(l.notes)}</div>
        </div>`).join('');
      }
    }
    if (ticket.workPerformed && ticket.showNotesOnInvoice !== false) {
      if (_notesHtml) _notesHtml += '<div style="margin-top:6px;font-size:10px;font-weight:600;color:#64748B;text-transform:uppercase;">General Notes</div>';
      _notesHtml += `<div class="detail-notes">${invoiceEsc(ticket.workPerformed)}</div>`;
    }
    if (_notesHtml) {
      _dtlSections.notes = `<div class="detail-section">
        <div class="detail-section-title">Work Performed / Notes</div>
        ${_notesHtml}
      </div>`;
    }
  }

  // Labor
  if (empLines.length > 0) {
    const _laborCC = (!isFieldTicket && ticket.customerId) ? getInvoiceColumnConfig(ticket.customerId, 'labor') : {};
    const _laborShowHrs = _laborCC.showHours !== false;
    const _laborShowRate = _laborCC.showRate !== false;
    const _laborShowTotal = _laborCC.showTotal !== false;
    const laborSubtotal = empLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    const _dtlAnyPieceRate = empLines.some(l => !isTimeBasedUnit(l.rateUnit));
    let _dtlLaborColspan = 1; // Name/Role
    if (_dtlFounder) _dtlLaborColspan++; // Role col
    if (_dtlShowTimes) _dtlLaborColspan += 2; // Clock In/Out
    if (_laborShowHrs) _dtlLaborColspan++; // Hours
    if (_laborShowRate) _dtlLaborColspan++; // Rate
    let _lh = `<div class="detail-section">
      <div class="detail-section-title">Labor</div>
      <table class="detail-table"><thead><tr>
        <th>${_dtlFounder ? 'Name' : 'Role'}</th>${_dtlFounder ? '<th>Role</th>' : ''}${_dtlShowTimes ? '<th>Clock In</th><th>Clock Out</th>' : ''}${_laborShowHrs ? `<th class="right">${_dtlAnyPieceRate ? 'Hours / Qty' : 'Hours'}</th>` : ''}${_laborShowRate ? '<th class="right">Rate</th>' : ''}${_laborShowTotal ? '<th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
    for (const l of empLines) {
      const _dtlPu = !isTimeBasedUnit(l.rateUnit);
      _lh += `<tr>
        <td>${invoiceEsc(getEmployeeDisplayName(l))}</td>
        ${_dtlFounder ? `<td>${invoiceEsc(l.role || '-')}</td>` : ''}
        ${_dtlShowTimes ? (_dtlPu ? '<td style="color:#94A3B8;">—</td><td style="color:#94A3B8;">—</td>' : `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>`) : ''}
        ${_laborShowHrs ? `<td class="right">${_dtlPu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
        ${_laborShowRate ? `<td class="right">${invoiceFmtMoney(l.rate)}${_dtlPu ? '<span style="font-size:0.75em;color:#64748B;">' + invoiceEsc(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>` : ''}
        ${_laborShowTotal ? `<td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      </tr>`;
    }
    if (_laborShowTotal) {
      _lh += `<tr><td colspan="${_dtlLaborColspan}" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Labor Subtotal</td>
        <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(laborSubtotal)}</td></tr>`;
    }
    _lh += `</tbody></table></div>`;
    _dtlSections.labor = _lh;
  }

  // Equipment — split into Vehicles, Trailers, Assets sub-sections
  if (equipLines.length > 0) {
    const _eqShowTimes = showClockTimes();
    const _buildDtlEquipSub = (subLines, title, sectionKey) => {
      if (subLines.length === 0) return '';
      const cc = (!isFieldTicket && ticket.customerId) ? getInvoiceColumnConfig(ticket.customerId, sectionKey) : {};
      const _sH = cc.showHours !== false;
      const _sR = isFieldTicket || cc.showRate !== false;
      const _sT = isFieldTicket || cc.showTotal !== false;
      const _sLI = !isFieldTicket && cc.showLineItem === true;
      if (!_sH && !_sR && !_sT && !_sLI) return '';
      const sub = subLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      let cols = 2;
      if (_sLI) cols++;
      if (_eqShowTimes) cols += 2;
      if (_sH) cols++;
      if (_sR) cols++;
      let h = `<div class="detail-section">
        <div class="detail-section-title">${invoiceEsc(title)}</div>
        <table class="detail-table"><thead><tr>
          <th>Asset</th><th>Fleet #</th>${_sLI ? '<th>Line Item</th>' : ''}${_eqShowTimes ? '<th>From</th><th>To</th>' : ''}${_sH ? '<th class="right">QTY</th>' : ''}${_sR ? '<th class="right">Rate</th>' : ''}${_sT ? '<th class="right">Amount</th>' : ''}
        </tr></thead><tbody>`;
      for (const l of subLines) {
        const _pu = !isTimeBasedUnit(l.rateUnit);
        h += `<tr>
          <td>${invoiceEsc(getEquipmentDisplayName(l))}</td>
          <td>${invoiceEsc(l.fleetNumber || '-')}</td>
          ${_sLI ? `<td>${invoiceEsc(l.lineItemDescription || '')}</td>` : ''}
          ${_eqShowTimes ? (_pu ? '<td style="color:#94A3B8;">—</td><td style="color:#94A3B8;">—</td>' : `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>`) : ''}
          ${_sH ? `<td class="right">${_pu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
          ${_sR ? `<td class="right">${invoiceFmtMoney(l.rate)}${_pu ? '<span style="font-size:0.75em;color:#64748B;">' + invoiceEsc(formatUnitLabel(l.rateUnit)) + '</span>' : ''}</td>` : ''}
          ${_sT ? `<td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
        </tr>`;
      }
      if (_sT) {
        h += `<tr><td colspan="${cols}" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceEsc(title)} Subtotal</td>
          <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(sub)}</td></tr>`;
      }
      h += `</tbody></table></div>`;
      return h;
    };
    const _dtlEquipHtml = [
      _buildDtlEquipSub(equipLines.filter(l => l.assetType === 'truck'), 'Vehicles', 'vehicles'),
      _buildDtlEquipSub(equipLines.filter(l => l.assetType === 'trailer'), 'Trailers', 'trailers'),
      // Catch-all, for the same reason as buildEquipSubTables above — an
      // unrecognised assetType must not fall out of every table into the total.
      _buildDtlEquipSub(equipLines.filter(l => l.assetType !== 'truck' && l.assetType !== 'trailer'), 'Assets', 'assets'),
    ].join('');
    if (_dtlEquipHtml) _dtlSections.equipment = _dtlEquipHtml;
  }

  // Downtime
  if (downLines.length > 0) {
    const _downCC = (!isFieldTicket && ticket.customerId) ? getInvoiceColumnConfig(ticket.customerId, 'downtime') : {};
    const _downShowHrs = _downCC.showHours !== false;
    // TASK-4819 — the printed ticket showed Person / Hours / Notes and NO amount,
    // while the deduction was still taken out of the Ticket Total below. That is
    // the same footing failure as the hidden line types, pointing the other way:
    // the rows summed to MORE than the total. All 25 live DOWNTIME lines are
    // $0.00, so the column stays absent exactly as today and nothing moves; it
    // appears the moment a deduction actually carries money, which is the only
    // case where its absence could unbalance the page.
    const _downMoney = downLines.some(l => (Number(l.lineAmount) || 0) !== 0);
    const _downSub = downLines.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
    let _dh = `<div class="detail-section">
      <div class="detail-section-title">Downtime Deduction</div>
      <table class="detail-table"><thead><tr>
        <th>${_dtlFounder ? 'Person' : 'Role'}</th>${_dtlShowTimes ? '<th>From</th><th>To</th>' : ''}${_downShowHrs ? '<th class="right">Hours</th>' : ''}<th>Notes</th>${_downMoney ? '<th class="right">Deduction</th>' : ''}
      </tr></thead><tbody>`;
    for (const l of downLines) {
      _dh += `<tr>
        <td>${invoiceEsc(getEmployeeDisplayName(l))}</td>
        ${_dtlShowTimes ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
        ${_downShowHrs ? `<td class="right">${(l.hours || 0).toFixed(2)}</td>` : ''}
        <td>${invoiceEsc(l.notes || '')}</td>${_downMoney ? `<td class="right bold" style="color:#DC2626;">-${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>` : ''}
      </tr>`;
    }
    if (_downMoney) {
      const _downCols = 2 + (_dtlShowTimes ? 2 : 0) + (_downShowHrs ? 1 : 0);
      _dh += `<tr><td colspan="${_downCols}" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Downtime Deduction Subtotal</td>
        <td class="right bold" style="color:#DC2626;border-top:1px solid #CBD5E1;padding:6px 8px;">-${invoiceFmtMoney(_downSub)}</td></tr>`;
    }
    _dh += `</tbody></table></div>`;
    _dtlSections.downtime = _dh;
  }

  // Standby
  if (standbyLines.length > 0) {
    const _stbyCC = (!isFieldTicket && ticket.customerId) ? getInvoiceColumnConfig(ticket.customerId, 'standby') : {};
    const _stbyShowHrs = _stbyCC.showHours !== false;
    // TASK-4819 — STANDBY had the identical defect to DOWNTIME just above:
    // Person / Hours / Notes and NO amount, while the charge still landed in the
    // Ticket Total. COD Code Reviewer reproduced it at $25 billed against $0
    // visible. All 15 live STANDBY lines are $0.00, so the column stays absent
    // exactly as today and appears only once it would otherwise hide money.
    const _stbyMoney = standbyLines.some(l => (Number(l.lineAmount) || 0) !== 0);
    const _stbySub = standbyLines.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
    let _sh = `<div class="detail-section">
      <div class="detail-section-title">Standby</div>
      <table class="detail-table"><thead><tr>
        <th>${_dtlFounder ? 'Person' : 'Role'}</th>${_dtlShowTimes ? '<th>From</th><th>To</th>' : ''}${_stbyShowHrs ? '<th class="right">Hours</th>' : ''}<th>Notes</th>${_stbyMoney ? '<th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
    for (const l of standbyLines) {
      _sh += `<tr>
        <td>${invoiceEsc(getEmployeeDisplayName(l))}</td>
        ${_dtlShowTimes ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
        ${_stbyShowHrs ? `<td class="right">${(l.hours || 0).toFixed(2)}</td>` : ''}
        <td>${invoiceEsc(l.notes || '')}</td>${_stbyMoney ? `<td class="right bold">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>` : ''}
      </tr>`;
    }
    if (_stbyMoney) {
      const _stbyCols = 2 + (_dtlShowTimes ? 2 : 0) + (_stbyShowHrs ? 1 : 0);
      _sh += `<tr><td colspan="${_stbyCols}" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Standby Subtotal</td>
        <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(_stbySub)}</td></tr>`;
    }
    _sh += `</tbody></table></div>`;
    _dtlSections.standby = _sh;
  }

  // Permits
  if (permitLines.length > 0) {
    const permitSubtotal = permitLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let _ph = `<div class="detail-section">
      <div class="detail-section-title">Load Permits</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th class="right">Amount</th>
      </tr></thead><tbody>`;
    for (const l of permitLines) {
      _ph += `<tr>
        <td>${invoiceEsc(l.description || '-')}</td>
        <td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>
      </tr>`;
    }
    _ph += `<tr><td style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Load Permit Subtotal</td>
      <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(permitSubtotal)}</td></tr>`;
    _ph += `</tbody></table></div>`;
    _dtlSections.permit = _ph;
  }

  // Field Items
  const _dtlFieldItems = _parts.FIELD_ITEM;
  if (_dtlFieldItems.length > 0) {
    const fiSub = _dtlFieldItems.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let _fi = `<div class="detail-section">
      <div class="detail-section-title">Field Items</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
      </tr></thead><tbody>`;
    for (const l of _dtlFieldItems) {
      _fi += `<tr><td>${invoiceEsc(l.description || '-')}</td><td class="right">${l.quantity || 0}</td>
        <td class="right">${invoiceFmtMoney(l.rate)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
        <td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td></tr>`;
    }
    _fi += `<tr><td colspan="3" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Subtotal</td>
      <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(fiSub)}</td></tr>`;
    _fi += `</tbody></table></div>`;
    _dtlSections.fieldItems = _fi;
  }

  // TASK-9440 — Fuel Surcharge (its own bucket; must render, or its money would
  // be in the total with no row).
  if (_parts.FUEL_SURCHARGE.length > 0) {
    let _fs = `<div class="detail-section">
      <div class="detail-section-title">Fuel Surcharge</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th class="right">Amount</th>
      </tr></thead><tbody>`;
    for (const l of _parts.FUEL_SURCHARGE) {
      _fs += `<tr><td>${invoiceEsc(l.description || 'Fuel Surcharge')}</td>
        <td class="right bold">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td></tr>`;
    }
    _fs += `</tbody></table></div>`;
    _dtlSections.fuelSurcharge = _fs;
  }

  // Line Info (POTHOLE)
  const _dtlPotholeLines = _parts.POTHOLE;
  if (_dtlPotholeLines.length > 0) {
    // Informational, and priced only if it ever actually carries a price — 0 of
    // 29 live rows do. Without the conditional column a non-zero pothole line
    // would be a visible row with invisible money, which is the defect this task
    // exists to remove rather than relocate.
    const _pliMoney = _dtlPotholeLines.some(l => (Number(l.lineAmount) || 0) !== 0);
    let _pli = `<div class="detail-section">
      <div class="detail-section-title">Line Info</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th>GPS</th><th>Notes</th>${_pliMoney ? '<th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
    for (const l of _dtlPotholeLines) {
      // Coerced for the same reason as the invoice copy: a stored STRING coord
      // would throw on `.toFixed` and take the whole print window down (COD).
      const _pLat = Number(l.gpsLat), _pLng = Number(l.gpsLng);
      const gpsStr = (Number.isFinite(_pLat) && Number.isFinite(_pLng) && (_pLat || _pLng))
        ? `${_pLat.toFixed(5)}, ${_pLng.toFixed(5)}` : '-';
      _pli += `<tr>
        <td>${invoiceEsc(l.description || '-')}</td>
        <td style="font-size:11px;">${invoiceEsc(gpsStr)}</td>
        <td>${invoiceEsc(l.notes || '-')}</td>${_pliMoney ? `<td class="right bold">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td>` : ''}
      </tr>`;
    }
    _pli += `</tbody></table></div>`;
    _dtlSections.lineInfo = _pli;
  }

  // TASK-4819 — Other Charges. The catch-all for a stored type this file has no
  // branch for. MATERIAL is the live example (show-account #1003, $143.00) and
  // was previously invisible on EVERY copy of the ticket while still inside the
  // total. Rendered with the detail-table markup so it matches its neighbours.
  if (_parts.OTHER.length > 0) {
    const _ocSub = _parts.OTHER.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
    let _oc = `<div class="detail-section">
      <div class="detail-section-title">Other Charges</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
      </tr></thead><tbody>`;
    for (const l of _parts.OTHER) {
      // Escaped / coerced for the same reason as the invoice copy above.
      _oc += `<tr><td>${invoiceEsc(l.description || l.type || 'Charge')}</td><td class="right">${invoiceEsc(l.quantity || 0)}</td>
        <td class="right">${invoiceFmtMoney(Number(l.rate) || 0)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
        <td class="right bold">${invoiceFmtMoney(Number(l.lineAmount) || 0)}</td></tr>`;
    }
    _oc += `<tr><td colspan="3" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Subtotal</td>
      <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(_ocSub)}</td></tr>`;
    _oc += `</tbody></table></div>`;
    _dtlSections.otherCharges = _oc;
  }

  // Miscellaneous
  const _dtlMiscLines = _parts.MISC;
  if (_dtlMiscLines.length > 0) {
    const miscSub = _dtlMiscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let _ml = `<div class="detail-section">
      <div class="detail-section-title">Miscellaneous</div>
      <table class="detail-table"><thead><tr>
        <th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th>
      </tr></thead><tbody>`;
    for (const l of _dtlMiscLines) {
      _ml += `<tr><td>${invoiceEsc(l.description || '-')}</td><td class="right">${l.quantity || 0}</td>
        <td class="right">${invoiceFmtMoney(l.rate)}/${invoiceEsc(l.rateUnit || 'ea')}</td>
        <td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td></tr>`;
    }
    _ml += `<tr><td colspan="3" style="text-align:right;font-weight:600;border-top:1px solid #CBD5E1;padding:6px 8px;">Subtotal</td>
      <td class="right bold" style="border-top:1px solid #CBD5E1;padding:6px 8px;">${invoiceFmtMoney(miscSub)}</td></tr>`;
    _ml += `</tbody></table></div>`;
    _dtlSections.misc = _ml;
  }

  // Custom ticket sections (TASK-747-C) — single or repeatable, on the printed/field-ticket copy.
  // TASK-848: only the CUSTOMER-facing print filters out showOnInvoice:false sections.
  // The printed FIELD ticket is internal (driver copy) — it shows everything, same as
  // the in-app detail + review screens (re-COD Code Reviewer catch).
  if (ticket.customSectionData) {
    const _csHtml = buildCustomSectionDataHtml(ticket.customSectionData, invoiceEsc, { invoiceOnly: !isFieldTicket });
    if (_csHtml) _dtlSections.customSections = _csHtml;
  }

  html += buildOrderedSections(_dtlSections);

  // Attached Documents — filter source depends on caller:
  //   - opts.selectedDocIds (array): explicit picker selection from printTicket flow
  //   - else: legacy printWithInvoice flag (other print paths)
  // Rendering depends on opts.fullPageDocs:
  //   - true: each image gets its own full additional page (page-break-before)
  //   - false: 120x120 thumbnail grid (legacy behavior)
  // PDFs are NEVER rendered in the print output — picker surfaces them as
  // "Open PDF" links instead, since browser print of inline PDFs is unreliable.
  const printDocsAll = opts.selectedDocIds
    ? (ticket.documents || []).filter(d => opts.selectedDocIds.includes(d.id))
    : (ticket.documents || []).filter(d => d.printWithInvoice);
  const printDocs = printDocsAll.filter(d => !(d.isPdf || d.mimeType === 'application/pdf'));
  if (printDocs.length > 0) {
    if (opts.fullPageDocs) {
      // One full page per image, after the ticket main content
      for (const d of printDocs) {
        html += `<div style="page-break-before:always;text-align:center;padding:16px 0;">
          <div style="font-size:11px;color:#64748b;margin-bottom:8px;text-align:left;">Attachment: ${_ticketDocPrintName(d, invoiceEsc)}</div>
          <img src="${invoiceSafeUrl(d.fullUrl || d.thumbUrl)}" style="max-width:100%;max-height:9in;height:auto;width:auto;display:block;margin:0 auto;" alt="${invoiceEsc(d.name || 'Document')}">
        </div>`;
      }
    } else {
      // Legacy thumbnail grid
      html += `<div class="detail-section">
        <div class="detail-section-title">Attached Documents</div>
        <div style="display:flex;flex-wrap:wrap;gap:12px;padding:8px 0;">`;
      for (const d of printDocs) {
        html += _ticketDocPrintGridImg(d, 120, `<img src="${invoiceSafeUrl(d.fullUrl || d.thumbUrl)}" style="width:120px;height:120px;object-fit:cover;border-radius:6px;border:1px solid #E2E8F0;" alt="${invoiceEsc(d.name || 'Document')}">`);
      }
      html += `</div></div>`;
    }
  }

  // Ticket total (with tax if applicable)
  if (ticket.taxEnabled) {
    // TASK-4819 — the subtotal shown is the sum of the rows above it, not the
    // stored `ticket.subtotal`. Reading the stored one first is what let the
    // printed arithmetic stop matching the printed rows.
    const subtotal = _totals.subtotal;
    html += `<div style="margin-top:20px;padding:8px 16px;background:#F8FAFC;border-radius:4px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
        <span style="font-size:13px;color:#475569;">Subtotal</span>
        <span style="font-size:14px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(subtotal)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:13px;color:#475569;">Tax (${(ticket.taxRate || 0).toFixed(2)}%)</span>
        <span style="font-size:14px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(ticket.taxAmount || 0)}</span>
      </div>
    </div>`;
  }
  html += `<div class="detail-total">
    <span>Ticket Total</span>
    <span>${invoiceFmtMoney(grandTotal)}</span>
  </div>`;
  // Screen-only, never printed. See invoiceReconcileNoticeHTML.
  if (_totals.drifted) {
    html += invoiceReconcileNoticeHTML([{
      label: ticket.ticketNo ? '#' + ticket.ticketNo : (ticket.id || '').slice(0, 8),
      total: _totals.total, storedShown: _totals.storedShown,
    }]);
  }

  // Signatures section
  html += `<div style="margin-top:32px;display:flex;gap:48px;">`;
  // Driver signature
  html += `<div style="flex:1;">`;
  html += `<div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Driver Signature</div>`;
  if (ticket.driverSignatureUrl) {
    html += `<img src="${invoiceSafeUrl(ticket.driverSignatureUrl)}" style="max-width:250px;max-height:80px;display:block;margin-bottom:4px;" alt="Driver Signature">`;
    if (ticket.driverSignedBy) html += `<div style="font-size:12px;color:#475569;">${invoiceEsc(ticket.driverSignedBy)}</div>`;
  } else {
    html += `<div style="border-bottom:1px solid #0F172A;height:60px;margin-bottom:4px;"></div>`;
    html += `<div style="font-size:12px;color:#475569;">&nbsp;</div>`;
  }
  html += `</div>`;
  // Company representative signature
  html += `<div style="flex:1;">`;
  html += `<div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Company Representative</div>`;
  if (ticket.companyManSignatureUrl) {
    html += `<img src="${invoiceSafeUrl(ticket.companyManSignatureUrl)}" style="max-width:250px;max-height:80px;display:block;margin-bottom:4px;" alt="Company Rep Signature">`;
    if (ticket.companyManSignedBy) html += `<div style="font-size:12px;color:#475569;">${invoiceEsc(ticket.companyManSignedBy)}</div>`;
  } else {
    html += `<div style="border-bottom:1px solid #0F172A;height:60px;margin-bottom:4px;"></div>`;
    html += `<div style="font-size:12px;color:#475569;">&nbsp;</div>`;
  }
  html += `</div>`;
  html += `</div>`;

  // TASK-9437 — the customer's APPROVAL, on the printout. Only when the caller
  // resolved one (`_resolveTicketPrintApproval`, from the stored decision) —
  // absent on every unapproved / rejected ticket and on every non-print
  // caller, so those render byte-for-byte as before.
  if (opts.approval) html += _ticketPrintApprovalHtml(opts.approval);

  // TASK-10185 — the job's dispatch notes, only when AR/AP -> Templates
  // "Print dispatch job notes on tickets" is on (default off: every ticket
  // prints byte-for-byte as before). LAST, after the signatures, so the
  // ticket itself is not crowded and a long note continues on page 2. Read
  // from the job now (_ticketDispatchNotes), never copied onto the ticket.
  html += _ticketDispatchNotesPrintHtml(ticket);

  html += `</div>`;
  return html;
}

// TASK-10185 — pure-ish: the setting + the job's own note. '' when off / empty.
// Server twin: functions/src/services/fieldTicketDocument.js (model.dispatchNotes).
function _ticketDispatchNotesPrintHtml(ticket, settingsOverride) {
  const st = settingsOverride || window.AppState?.settings || {};
  if (st.showJobNotesOnTickets !== true) return '';
  // COD r1 (Code): same clean-up as the server (ticketDispatchNotes.cleanNotes):
  // CRLF -> LF, control characters dropped, capped at 6000 characters.
  let notes = String(_ticketDispatchNotes(ticket) || '').replace(/\r\n?/g, '\n').replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '').trim();
  if (notes.length > 6000) notes = notes.slice(0, 6000).trimEnd() + ' …';
  if (!notes) return '';
  return `<div class="detail-section" style="margin-top:24px;break-inside:auto;page-break-inside:auto;">
    <div class="detail-section-title">Dispatch Notes</div>
    <div class="detail-notes" style="white-space:pre-wrap;">${invoiceEsc(notes)}</div>
  </div>`;
}

// ---------------------------------------------------------------------------
// TASK-9437 — Print Ticket shows the customer's approval.
//
// Chris, TITS #100779: Lacey Christensen accepted it in the portal (typed name,
// no drawn signature), and Print Ticket printed the plain ticket with nothing
// on it saying so. A signed paper ticket carries its signature; this printout
// now carries the approval the same way.
//
// 🔴 READ, NEVER INVENT. "Accepted" comes from the ticket doc
// (`ticketCustomerResponse`, the latest answer — server-written, rules-pinned).
// WHO and the signature come from the inbound `ticketSendLog` row the portal
// wrote, via `listTicketSendLog` (the same allowlisted projection the approval
// panel reads). If that row cannot be loaded or does not match, the block says
// "accepted" from the ticket record and says the signer details are
// unavailable — it never prints a name it did not read.
// ---------------------------------------------------------------------------

/**
 * Pure. `rows` = the listTicketSendLog entries (any order), `null` when the
 * fetch failed. `ctx.respondedAt` = the ticket's response Date (fallback when
 * no row), `ctx.editedAfterAccept` / `ctx.resentSince` from
 * `_ticketDeliveryState`. Returns null for anything but an accepted ticket.
 */
function _ticketPrintApprovalFromRows(ticket, rows, ctx = {}) {
  const t = ticket || {};
  if (t.ticketCustomerResponse !== 'accepted') return null;
  const ts = (r) => Date.parse((r && (r.occurredAt || r.at)) || '') || 0;
  const inbound = Array.isArray(rows)
    ? rows.filter(r => r && r.direction === 'in' && r.outcome).sort((a, b) => ts(b) - ts(a))
    : [];
  // The NEWEST answer must be the acceptance. An older accept under a newer
  // reject is not this ticket's approval — and the ticket doc said accepted,
  // so a mismatch means the series and the doc disagree: print from the doc
  // and say the details are unavailable rather than pick one.
  let row = inbound.length && inbound[0].outcome === 'accepted' ? inbound[0] : null;
  // COD r1 — the row must BE the answer the ticket doc records. The server
  // writes `ticketCustomerResponseAt` from the same `occurredAt`, so a row
  // more than a few seconds away is a different (older) answer from a stale
  // series: never print its signer for this acceptance.
  // COD r2 — and it FAILS CLOSED: no verifiable response time means no signer.
  const respOk = ctx.respondedAt instanceof Date && !isNaN(ctx.respondedAt.getTime());
  if (row && (!respOk || Math.abs(ts(row) - ctx.respondedAt.getTime()) > 5000)) {
    row = null;
  }
  const a = (row && row.approval) || {};
  const inPortal = t.ticketCustomerResponseSource === 'customer';
  const rowAt = row ? new Date(row.occurredAt || row.at || '') : null;
  const at = rowAt && !isNaN(rowAt.getTime()) ? rowAt : (ctx.respondedAt || null);
  return {
    inPortal,
    signerName: String(a.signerName || '').trim().slice(0, 80),
    // A hand-recorded answer's row names the OFFICE person who logged it, never
    // the customer — so it is shown as "recorded by", not as the signer.
    recordedBy: (!inPortal && row) ? String(row.byName || '').trim().slice(0, 120) : '',
    at,
    signatureDataUrl: _isTicketSignatureDataUrl(a.signatureDataUrl) ? a.signatureDataUrl : '',
    detailsUnavailable: !row,
    editedAfterAccept: !!ctx.editedAfterAccept,
    resentSince: !!ctx.resentSince,
  };
}

/** Pure markup for the print approval block. '' when there is no approval. */
function _ticketPrintApprovalHtml(ap) {
  if (!ap) return '';
  const esc = invoiceEsc;
  const at = ap.at instanceof Date && !isNaN(ap.at.getTime()) ? ap.at : null;
  const whenStr = at
    ? at.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  const how = ap.inPortal
    ? 'Accepted in the approval portal'
    : `Acceptance recorded by hand${ap.recordedBy ? ' by ' + esc(ap.recordedBy) : ''}`;
  let html = `<div class="ticket-print-approval" style="margin-top:24px;padding:12px 16px;border:2px solid #15803D;border-radius:6px;background:#F0FDF4;page-break-inside:avoid;break-inside:avoid;color:#0F172A;">`;
  html += `<div style="font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Customer Approval</div>`;
  html += `<div style="font-size:17px;font-weight:800;color:#15803D;">APPROVED${ap.signerName ? ' — ' + esc(ap.signerName) : ''}</div>`;
  html += `<div style="font-size:12px;margin-top:2px;">${how}${whenStr ? ' · ' + esc(whenStr) : ''}</div>`;
  if (ap.signerName) {
    html += `<div style="font-size:12px;margin-top:6px;">Typed name: <strong>${esc(ap.signerName)}</strong></div>`;
  }
  if (ap.signatureDataUrl) {
    html += `<div style="margin-top:6px;"><div style="font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;">Signature</div>`
      + `<img src="${esc(ap.signatureDataUrl)}" alt="Customer signature" style="max-width:250px;max-height:80px;display:block;margin-top:2px;background:#fff;"></div>`;
  } else if (ap.inPortal && !ap.detailsUnavailable) {
    html += `<div style="font-size:12px;margin-top:4px;">No signature was drawn — approved by typed name.</div>`;
  }
  if (ap.detailsUnavailable) {
    html += `<div style="font-size:12px;margin-top:4px;">Signer details couldn't be loaded — this approval is from the ticket record.</div>`;
  }
  if (ap.editedAfterAccept) {
    html += `<div style="font-size:12px;margin-top:6px;padding:6px 8px;border:1px solid #FDE68A;background:#FFFBEB;border-radius:4px;color:#92400E;font-weight:600;">Edited after approval — this is the CURRENT ticket, which has changed since the customer accepted it. Their accepted copy is saved on the ticket ("Accepted copy").</div>`;
  }
  if (ap.resentSince) {
    html += `<div style="font-size:12px;margin-top:6px;color:#92400E;font-weight:600;">Sent to the customer again after this approval.</div>`;
  }
  html += `</div>`;
  return html;
}

/**
 * The approval for one ticket's printout, or null (never approved / rejected).
 * Fetches the series fresh (and refreshes the panel cache with it).
 * A failed fetch still prints the acceptance from the ticket record, flagged.
 */
async function _resolveTicketPrintApproval(ticket) {
  const t = ticket || {};
  if (t.ticketCustomerResponse !== 'accepted') return null;
  // Always a FRESH read (COD r1): the panel's cache can predate a later
  // portal answer, and a printout is a record. Only accepted tickets pay it.
  let rows = null;
  const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const fn = httpsCallable(functions, 'listTicketSendLog');
    const res = await fn({ tenantId, ticketId: t.id });
    rows = Array.isArray(res?.data?.entries) ? res.data.entries : [];
    _ticketSendLogCache[t.id] = rows;
  } catch (err) {
    console.warn('[Billing] print: could not load the approval for', t.id, err);
    rows = null;
  }
  const st = _ticketDeliveryState(t);
  return _ticketPrintApprovalFromRows(t, rows, {
    respondedAt: _ticketEmailDate(t.ticketCustomerResponseAt),
    editedAfterAccept: !!st.editedAfterAccept,
    resentSince: !!(st.sent && st.awaiting),
  });
}

function printReviewTicket() {
  if (ReviewState.ticketId) printTicket(ReviewState.ticketId);
}

/**
 * Print-time attachment picker. Returns a promise that resolves with an array
 * of selected doc IDs (images only) or null if cancelled.
 *
 * Images can be checked to include in the print as full additional pages.
 * PDFs get an "Open PDF" link to open in a new tab — they are NOT added to
 * the printed output (browser print of inline PDFs is unreliable across
 * browsers; surfacing them as a separate-tab open is honest UX).
 *
 * Default-checked state for image rows = the doc's existing printWithInvoice flag.
 */
function showTicketPrintPicker(documents) {
  return new Promise((resolve) => {
    // Singleton: bail if a picker is already open (defense vs double-click).
    if (document.getElementById('ticket-print-picker-overlay')) {
      resolve(null);
      return;
    }

    const docs = (documents || []).map(d => ({
      ...d,
      isPdf: d.isPdf || d.mimeType === 'application/pdf',
    }));
    const images = docs.filter(d => !d.isPdf);
    const pdfs = docs.filter(d => d.isPdf);

    // Index-based selection avoids sanitizing/round-tripping doc IDs through
    // a data-attr (Codex concern: escId() would mutate IDs and break filtering).
    // Url validation: only http/https/Firebase Storage URLs are linkable.
    const isSafeUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u);
    const safeUrlAttr = (u) => isSafeUrl(u) ? escapeHtml(u) : '';

    const imgRowsHtml = images.map((d, idx) => `
      <label style="display:flex;align-items:center;gap:10px;padding:8px;border:1px solid #E2E8F0;border-radius:6px;cursor:pointer;min-height:44px;">
        <input type="checkbox" data-img-idx="${idx}" ${d.printWithInvoice ? 'checked' : ''} style="width:16px;height:16px;flex-shrink:0;">
        ${window.StorageFile.fileRef(d, { urlKey: 'fullUrl' }) ? `<img ${_sfThumbAttrs(d)} style="width:48px;height:48px;object-fit:cover;border-radius:4px;flex-shrink:0;" alt="">` : `<div style="width:48px;height:48px;background:#E2E8F0;border-radius:4px;flex-shrink:0;"></div>`}
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;font-weight:600;color:#0F172A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(d.name || 'Image')}${_ticketDocTypeChipHtml(d)}</div>
          <div style="font-size:11px;color:#64748b;">Image — prints as a full page</div>
        </div>
      </label>
    `).join('');

    const pdfRowsHtml = pdfs.map(d => `
      <div style="display:flex;align-items:center;gap:10px;padding:8px;border:1px solid #FECACA;border-radius:6px;background:#FEF2F2;min-height:44px;">
        <div style="font-size:24px;color:#EF4444;flex-shrink:0;">&#128196;</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:13px;font-weight:600;color:#0F172A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(d.name || 'PDF')}${_ticketDocTypeChipHtml(d)}</div>
          <div style="font-size:11px;color:#64748b;">PDF — open separately to print (browsers won't reliably print embedded PDFs)</div>
        </div>
        ${window.StorageFile.fileRef(d, { urlKey: 'fullUrl' }) ? `<a ${window.StorageFile.linkAttrs(d, { urlKey: 'fullUrl' })} style="padding:10px 14px;background:#3B82F6;color:#fff;border-radius:4px;font-size:13px;font-weight:600;text-decoration:none;flex-shrink:0;min-height:44px;display:inline-flex;align-items:center;">Open PDF</a>` : '<span style="color:#64748b;font-size:11px;">URL unavailable</span>'}
      </div>
    `).join('');

    const overlay = document.createElement('div');
    overlay.id = 'ticket-print-picker-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.6);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:8px;max-width:520px;width:100%;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;">
        <div style="padding:16px 20px;border-bottom:1px solid #E2E8F0;">
          <div style="font-size:16px;font-weight:700;color:#0F172A;">Print ticket with attachments?</div>
          <div style="font-size:12px;color:#64748b;margin-top:4px;">Select which images to include with the printed ticket. PDFs print better in a new tab — use the "Open PDF" buttons below.</div>
        </div>
        <div style="padding:16px 20px;overflow-y:auto;display:flex;flex-direction:column;gap:8px;">
          ${images.length > 0 ? `<div style="font-size:11px;font-weight:700;text-transform:uppercase;color:#64748b;letter-spacing:0.5px;">Images (full-page in print)</div>${imgRowsHtml}` : ''}
          ${pdfs.length > 0 ? `<div style="font-size:11px;font-weight:700;text-transform:uppercase;color:#64748b;letter-spacing:0.5px;margin-top:${images.length > 0 ? '12px' : '0'};">PDFs (open separately)</div>${pdfRowsHtml}` : ''}
          ${images.length === 0 && pdfs.length === 0 ? '<div style="color:#64748b;text-align:center;padding:20px;">No attachments on this ticket.</div>' : ''}
        </div>
        <div style="padding:12px 20px;border-top:1px solid #E2E8F0;display:flex;gap:8px;justify-content:flex-end;">
          <button id="ticket-print-picker-cancel" style="padding:8px 16px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Cancel</button>
          <button id="ticket-print-picker-print" style="padding:8px 16px;background:#3B82F6;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Print</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const cleanup = () => { try { overlay.remove(); } catch (_) {} };

    overlay.querySelector('#ticket-print-picker-cancel').addEventListener('click', () => {
      cleanup();
      resolve(null);
    });
    overlay.querySelector('#ticket-print-picker-print').addEventListener('click', () => {
      // Index-based: map back to actual doc IDs (no sanitization round-trip).
      const ids = Array.from(overlay.querySelectorAll('input[type="checkbox"][data-img-idx]:checked'))
        .map(el => images[parseInt(el.getAttribute('data-img-idx'), 10)]?.id)
        .filter(Boolean);
      cleanup();
      resolve(ids);
    });
    // Outside-click does NOT close (per Edge Case Hunter — prevents accidental dismissal).
  });
}

async function printTicket(ticketId) {
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (!ticket) return;

  // If ticket has any attachments, show the print-time picker first.
  // Picker resolves with the array of selected doc IDs (images only — PDFs
  // are offered as "Open PDF" links in the picker since browser print of
  // inline PDFs is unreliable).
  const docs = ticket.documents || [];
  if (docs.length > 0) {
    const selectedDocIds = await showTicketPrintPicker(docs);
    if (selectedDocIds === null) return; // cancelled
    return printTicketInternal(ticketId, selectedDocIds);
  }
  return printTicketInternal(ticketId, []);
}

async function printTicketInternal(ticketId, selectedDocIds) {
  const ticket = _emitTicketMoney(BillingState.tickets.find(t => t.id === ticketId)); // TASK-9646 S3b emitted money
  if (!ticket) return;

  // Ensure roster data is loaded so getEquipmentDisplayName() can resolve names
  await loadReviewEquipment();

  // Load ticketLines (use cache if viewing same ticket)
  let lines = BillingState.selectedTicketLines;
  if (!lines || lines.length === 0 || BillingState.selectedTicket?.id !== ticketId) {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    try {
      const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'));
      lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('[Billing] Error loading ticketLines for print:', err);
      lines = [];
    }
  }

  const { companyName, logoUrl, rawCompanyName } = resolveCompanyInfo();

  // TASK-9437 — null unless the customer accepted this ticket.
  const approval = await _resolveTicketPrintApproval(ticket);

  const ticketHTML = buildTicketDetailHTML(ticket, lines, companyName || rawCompanyName, logoUrl, {
    isFieldTicket: true,
    selectedDocIds, // overrides the printWithInvoice filter when provided
    fullPageDocs: true, // render images as full additional pages, not thumbnails
    approval,
  });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Ticket ${invoiceEsc(ticket.ticketNo ? '#' + ticket.ticketNo : '')} - ${invoiceEsc(rawCompanyName)}</title>
  <style>${invoiceStylesHTML()}</style>
</head>
<body>
  <div class="no-print" style="text-align:center;margin-bottom:16px;">
    <button onclick="window.print()" style="padding:10px 24px;background:#3B82F6;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Print Ticket
    </button>
  </div>
  ${ticketHTML}
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

// ============================================
// BULK PRINT SELECTED TICKETS (TASK-9182)
// ============================================
// Chris: "the ability to print multiple tickets quickly." One dialog, N pages,
// in the order the tickets appear on screen.
//
// Deliberate scope calls — this is a loop around the EXISTING print, not a
// second print engine:
//   - Reuses buildTicketDetailHTML + invoiceStylesHTML verbatim, so a bulk page
//     is identical to the single-ticket print. `.ticket-detail-page` already
//     carries `page-break-before: always`, so concatenating N of them IS the
//     page break — no new print CSS, nothing to drift apart.
//   - ATTACHMENTS ARE OMITTED. printTicket() prompts a per-ticket photo picker;
//     prompting N times destroys the point of a bulk action, and a multi-ticket
//     picker is a different feature. Passing `selectedDocIds: []` takes the
//     explicit-selection branch in buildTicketDetailHTML with an empty set, so
//     no attachment renders. The button title AND the confirm say so out loud —
//     a silently-dropped photo on a billing document is not acceptable.
//   - CEILING 50. Measured on trippin-trucking 2026-09-11: across the last 400
//     tickets the largest customer+job group holds 20 tickets, the median ticket
//     carries 3 ticketLines (p90 6, max 11). 50 is ~2.5x the largest realistic
//     selection and ~10 rounds of reads; past that, refuse with a number instead
//     of building a multi-megabyte DOM and hanging the tab.
//   - `status === 'deleted'` is filtered out and the count is named, matching
//     exportToQuickBooks / exportToIIF.
const BULK_PRINT_MAX = 50;
const BULK_PRINT_CONCURRENCY = 5;

// Selected ids in the order they appear ON SCREEN (customer -> job -> card),
// not Set-insertion order — a printed stack that doesn't match the list is a
// stack nobody can check against the list. Any selected id with no rendered
// checkbox (collapsed/paged-out group) is appended rather than dropped.
function _selectedTicketIdsInListOrder() {
  const sel = BillingState.selectedTicketIds;
  const ordered = [];
  const seen = new Set();
  try {
    document.querySelectorAll('.ticket-select-cb[data-ticket-id]').forEach(cb => {
      const id = cb.getAttribute('data-ticket-id');
      if (id && sel.has(id) && !seen.has(id)) { seen.add(id); ordered.push(id); }
    });
  } catch (_) { /* non-DOM context (tests) — fall through to Set order */ }
  for (const id of sel) { if (!seen.has(id)) { seen.add(id); ordered.push(id); } }
  return ordered;
}

async function printSelectedTickets() {
  const ids = _selectedTicketIdsInListOrder();
  if (ids.length === 0) {
    showToast('Select tickets to print', 'error');
    return;
  }

  // Two different reasons a selected id may not print, counted separately —
  // COD Edge Case Hunter: selectedTicketIds survives a filter change / snapshot
  // refresh, so an id can be gone from BillingState.tickets entirely. Calling
  // that "deleted" in the confirm would be a lie about a billing document.
  const byId = new Map(BillingState.tickets.map(t => [t.id, t]));
  const resolved = ids.map(id => byId.get(id)).filter(Boolean);
  const missing = ids.length - resolved.length;
  const tickets = resolved.filter(t => t.status !== 'deleted');
  const deleted = resolved.length - tickets.length;
  if (tickets.length === 0) {
    showToast('Nothing printable in that selection', 'error');
    return;
  }
  if (tickets.length > BULK_PRINT_MAX) {
    showToast(`That's ${tickets.length} tickets — print ${BULK_PRINT_MAX} or fewer at a time.`, 'error');
    return;
  }

  const _n = tickets.length;
  const _confirmMsg = `Print ${_n} ticket${_n !== 1 ? 's' : ''}? Each one starts on a new page.`
    + (deleted > 0 ? `\n\n${deleted} deleted ticket${deleted !== 1 ? 's' : ''} skipped.` : '')
    + (missing > 0 ? `\n\n${missing} selected ticket${missing !== 1 ? 's are' : ' is'} no longer in the list and will not print.` : '')
    + `\n\nPhotos and attachments are NOT included — print a ticket on its own if you need its photos.`;
  if (!confirm(_confirmMsg)) return;

  // Opened INSIDE the click gesture (confirm() keeps it; the awaits below would
  // not) so the pop-up blocker doesn't eat the window. It doubles as the
  // progress surface — N tickets is N Firestore round trips, not instant.
  const win = window.open('', '_blank');
  if (!win) {
    showToast('Allow pop-ups for this site to print multiple tickets', 'error');
    return;
  }
  win.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Preparing tickets...</title></head>`
    + `<body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#E2E8F0;color:#0F172A;padding:64px 24px;text-align:center;">`
    + `<div id="bulk-print-progress" style="font-size:15px;font-weight:600;">Preparing ${_n} tickets...</div></body></html>`);
  win.document.close();
  const setProgress = (doneCount) => {
    try {
      const el = win.document.getElementById('bulk-print-progress');
      if (el) el.textContent = `Preparing ${_n} tickets... ${doneCount} of ${_n}`;
    } catch (_) { /* window closed or navigated */ }
  };
  setProgress(0);

  try {
    // Roster first, so getEquipmentDisplayName() can resolve names (same as single print).
    await loadReviewEquipment();

    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const linesById = new Map();
    const approvalsById = new Map(); // TASK-9437
    const failed = [];
    let cursor = 0;
    let doneCount = 0;
    const worker = async () => {
      while (cursor < tickets.length) {
        if (win.closed) return; // user bailed — stop burning reads
        const t = tickets[cursor++];
        let lines;
        if (BillingState.selectedTicket?.id === t.id && BillingState.selectedTicketLines?.length) {
          lines = BillingState.selectedTicketLines; // same cache reuse as printTicketInternal
        } else {
          try {
            const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', t.id, 'ticketLines'));
            lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          } catch (err) {
            console.warn('[Billing] bulk print: ticketLines load failed for', t.id, err);
            failed.push(t.ticketNo ? '#' + t.ticketNo : t.id);
            lines = null;
          }
        }
        if (lines !== null) linesById.set(t.id, lines);
        // TASK-9437 — same approval block as the single print. Null (no fetch)
        // for every ticket the customer has not accepted.
        if (lines !== null) approvalsById.set(t.id, await _resolveTicketPrintApproval(t));
        setProgress(++doneCount);
      }
    };
    await Promise.all(Array.from({ length: Math.min(BULK_PRINT_CONCURRENCY, tickets.length) }, () => worker()));

    if (win.closed) return;

    // COD Code Reviewer — do NOT print a ticket whose lines we could not read.
    // An empty ticketLines array renders a $0 field ticket that looks finished;
    // a stack of 30 is exactly where that goes unnoticed. Refuse, name them,
    // let the biller retry.
    if (failed.length > 0) {
      try { win.close(); } catch (_) { /* noop */ }
      showToast(`Could not load lines for ${failed.length} ticket${failed.length !== 1 ? 's' : ''} (${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '...' : ''}) — nothing printed. Try again.`, 'error');
      return;
    }

    const { companyName, logoUrl, rawCompanyName } = resolveCompanyInfo();
    const body = tickets.map(t => buildTicketDetailHTML(
      _emitTicketMoney(t), // TASK-9646 S3b emitted money
      linesById.get(t.id) || [],
      companyName || rawCompanyName,
      logoUrl,
      {
        isFieldTicket: true,
        selectedDocIds: [], // explicit EMPTY selection — renders no attachments
        fullPageDocs: true,
        approval: approvalsById.get(t.id) || null, // TASK-9437
      }
    )).join('\n');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${_n} Field Ticket${_n !== 1 ? 's' : ''} - ${invoiceEsc(rawCompanyName)}</title>
  <style>${invoiceStylesHTML()}</style>
</head>
<body>
  <div class="no-print" style="text-align:center;margin-bottom:16px;">
    <button onclick="window.print()" style="padding:10px 24px;background:#3B82F6;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Print ${_n} Ticket${_n !== 1 ? 's' : ''}
    </button>
    <div style="font-size:12px;color:#475569;margin-top:8px;">In list order — each ticket starts on a new page. Photos are not included.</div>
  </div>
  ${body}
</body>
</html>`;

    win.document.open();
    win.document.write(html);
    win.document.close();
  } catch (err) {
    console.error('[Billing] bulk print failed:', err);
    showToast('Could not build the print document - see console', 'error');
    try { if (!win.closed) win.close(); } catch (_) { /* noop */ }
  }
}

// TASK-10164-B — the invoice page's Save (single + grouped). Un-checking "Mark as
// Invoiced" then Save calls markAsInvoiced(ids, false), which clears invoiceNumber /
// invoicedAt and voids the revenue rows. Ask first, but ONLY when that Save would
// un-mark a ticket that is invoiced now (read live from the opener, so a Save made
// earlier on this page counts). Cancel writes nothing and resets the box. The
// dialog lives in THIS window (showConfirm's singleton is in the opener's DOM),
// built to the showConfirm pattern: icon, title, message, centered buttons.
// `all` = every ticket's narrow `invoiced` flag is true now — the same rule the
// page's checkbox renders from (TASK-1346). Cancel sets the box to it (COD r2):
// live truth, not a latch on a Save that markAsInvoiced may have refused.
function unmarkImpact(ticketIds) {
  let count = 0, all = true; const numbers = [];
  for (const id of ticketIds) {
    const t = BillingState.tickets.find(x => x.id === id);
    if (t?.invoiced !== true) all = false;
    if (t && !isTicketInvoiced(t)) continue; // not loaded → ask (fail toward the dialog)
    count++;
    if (t?.invoiceNumber && !numbers.includes(t.invoiceNumber)) numbers.push(t.invoiceNumber);
  }
  return { count, numbers, all };
}
function invoiceSaveScriptHTML(ticketIds) {
  // Template lines stay indented: column-0 `function`/`}` inside the template would
  // split this declaration for the test harnesses' top-level extractors. No comments
  // inside the template (they ship). COD r1 folds: Save stays disabled until the
  // opener's write settles, so a second Save cannot read pre-write state and skip
  // the dialog (Edge Case Hunter). Cancel sets the box to unmarkImpact's live `all`,
  // never forced checked: a box that started unchecked (partial group, number-only
  // ticket) would make the next Save STAMP them (Independent ❌, r1 + r2). Focus
  // goes to Cancel on open, back to Save on close; Escape = Cancel. "Saved!" only
  // when markAsInvoiced returns true (it resolves silently on a refusal).
  const b = 'padding:10px 24px;min-height:44px;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;';
  return `<div id="unmark-dialog" class="no-print" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:10000;justify-content:center;align-items:center">
  <div role="alertdialog" aria-modal="true" aria-labelledby="unmark-title" style="background:#fff;border-radius:12px;padding:24px;max-width:400px;width:90%;text-align:center">
  <div style="font-size:40px;color:#DC2626">&#9888;</div>
  <h3 id="unmark-title" style="margin:12px 0 8px;font-size:18px;color:#0F172A">Un-mark as invoiced?</h3>
  <p id="unmark-msg" style="margin:0 0 20px;font-size:14px;color:#475569"></p>
  <div style="display:flex;gap:12px;justify-content:center">
  <button id="unmark-cancel" style="${b}background:#fff;color:#475569;border:1px solid #CBD5E1">Cancel</button>
  <button id="unmark-ok" style="${b}background:#DC2626;color:#fff;border:none">Un-mark</button>
  </div></div></div>
  <script>
  var INVOICE_TICKET_IDS = ${invoiceJson(ticketIds)};
  function $i(id) { return document.getElementById(id); }
  function invoiceDoSave(checked) {
    var b = $i('invoice-save-btn'), bm = window.opener && window.opener.BillingModule, done = function(ok) { b.disabled = false; b.textContent = ok === true ? 'Saved!' : ok === 'partial' ? 'Partly saved' : 'Not saved'; setTimeout(function() { b.textContent = 'Save'; }, 2000); };
    if (!bm) { alert('Could not save — parent window closed. Please mark as invoiced from the main app.'); return; }
    b.disabled = true;
    Promise.resolve(bm.markAsInvoiced(INVOICE_TICKET_IDS, checked)).then(done, done);
  }
  function handleInvoiceSave() {
    var box = $i('invoice-mark-invoiced'), bm = window.opener && window.opener.BillingModule;
    if (!bm) { alert('Could not save — parent window closed. Please mark as invoiced from the main app.'); return; }
    var hit = box.checked ? null : bm.unmarkImpact(INVOICE_TICKET_IDS);
    if (!hit || !hit.count) { invoiceDoSave(box.checked); return; }
    var n = hit.count, nums = hit.numbers, dlg = $i('unmark-dialog'), close = function() { dlg.style.display = 'none'; $i('invoice-save-btn').focus(); };
    $i('unmark-msg').textContent = 'This clears ' + (nums.length ? 'invoice number' + (nums.length > 1 ? 's ' : ' ') + nums.join(', ') : 'the invoice number') +
      ' on ' + n + (n === 1 ? ' ticket and removes it' : ' tickets and removes them') + ' from revenue.';
    dlg.style.display = 'flex';
    $i('unmark-cancel').focus();
    $i('unmark-cancel').onclick = function() { close(); box.checked = bm.unmarkImpact(INVOICE_TICKET_IDS).all; };
    $i('unmark-ok').onclick = function() { close(); invoiceDoSave(false); };
    dlg.onkeydown = function(e) { if (e.key === 'Escape') $i('unmark-cancel').onclick(); };
  }
  </script>`;
}
function invoiceStylesHTML() {
  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #0F172A; line-height: 1.5; font-size: 14px;
    }
    @media screen {
      body { background: #E2E8F0; padding: 24px; }
      .invoice-page {
        max-width: 8.5in; margin: 0 auto; background: #fff;
        box-shadow: 0 4px 24px rgba(0,0,0,0.12); border-radius: 4px; padding: 48px;
      }
    }
    @media print {
      body { background: #fff; padding: 0; }
      .invoice-page { padding: 0.5in; }
      .no-print { display: none !important; }
      @page { size: portrait; margin: 0.25in; }
    }
    table { width: 100%; border-collapse: collapse; }
    th {
      text-align: left; padding: 8px; border-bottom: 2px solid #0F172A;
      font-size: 12px; text-transform: uppercase; color: #475569; font-weight: 600;
    }
    th.right { text-align: right; }
    .section-title {
      font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase;
      letter-spacing: 0.5px; margin: 24px 0 8px; padding-bottom: 4px;
      border-bottom: 1px solid #E2E8F0;
    }
    .subtotal-row td { padding: 8px; font-weight: 700; border-top: 1px solid #CBD5E1; }
    .ticket-divider {
      margin: 28px 0 16px; padding: 10px 16px; background: #F1F5F9;
      border-radius: 6px; font-size: 14px; font-weight: 700; color: #0F172A;
      border-left: 4px solid #3B82F6;
    }
    .ticket-section-page {
      page-break-before: always; padding: 48px;
    }
    .ticket-section-page:first-of-type {
      page-break-before: auto;
    }
    @media screen {
      .ticket-section-page {
        max-width: 8.5in; margin: 24px auto 0; background: #fff;
        box-shadow: 0 4px 24px rgba(0,0,0,0.12); border-radius: 4px;
      }
    }
    @media print {
      .ticket-section-page { padding: 0.5in; }
    }
    .invoice-footer { page-break-inside: avoid; break-inside: avoid; }
    .section-title { page-break-after: avoid; break-after: avoid; }
    table { page-break-inside: auto; break-inside: auto; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; break-inside: avoid; }
    .subtotal-row { page-break-before: avoid; break-before: avoid; }
    .ticket-detail-page {
      page-break-before: always;
      padding: 48px;
    }
    @media screen {
      .ticket-detail-page {
        max-width: 8.5in; margin: 24px auto 0; background: #fff;
        box-shadow: 0 4px 24px rgba(0,0,0,0.12); border-radius: 4px;
      }
    }
    @media print {
      .ticket-detail-page { padding: 0.5in; }
    }
    .detail-header {
      font-size: 18px; font-weight: 800; color: #0F172A; margin-bottom: 16px;
      padding-bottom: 8px; border-bottom: 2px solid #0F172A;
    }
    .detail-info-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px;
      font-size: 13px; margin-bottom: 16px;
    }
    .detail-info-grid .label { color: #64748B; }
    .detail-section { margin-top: 20px; }
    .detail-section-title {
      font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase;
      letter-spacing: 0.5px; margin-bottom: 6px; padding-bottom: 4px;
      border-bottom: 1px solid #E2E8F0;
    }
    .detail-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 8px; }
    .detail-table th {
      text-align: left; padding: 6px 8px; border-bottom: 1px solid #CBD5E1;
      font-size: 11px; text-transform: uppercase; color: #64748B; font-weight: 600;
    }
    .detail-table th.right { text-align: right; }
    .detail-table td { padding: 5px 8px; border-bottom: 1px solid #F1F5F9; }
    .detail-table td.right { text-align: right; }
    .detail-table td.bold { font-weight: 600; }
    .detail-total {
      margin-top: 20px; padding: 12px 16px; background: #F8FAFC; border-radius: 4px;
      display: flex; justify-content: space-between; align-items: center;
      font-weight: 700; font-size: 15px;
    }
    .detail-notes { margin-top: 16px; font-size: 12px; color: #475569; white-space: pre-wrap; }
  `;
}

function buildInvoiceHTML(data) {
  const { ticket, lines, companyName, companyAddress, companyPhone, companyEmail, customer, job, logoUrl, projectName } = data;
  const _invJobType = _ticketHeaderJobType(ticket, 'invoice'); // TASK-9390

  const _exTypes = getInvoiceExcludeLineTypes(ticket.customerId);
  const _inv = (l) => isLineInvoiced(l, _exTypes);
  // TASK-4819 — partitioned; POTHOLE and any unrecognised type reached this
  // invoice's total without reaching a row before this.
  const _parts = partitionInvoiceLines(lines, _inv);
  const empLines = _parts.EMPLOYEE;
  const equipLines = _parts.EQUIPMENT;
  const downLines = _parts.DOWNTIME;
  const standbyLines = _parts.STANDBY;
  const permitLines = _parts.PERMIT;
  const fieldItemLines = _parts.FIELD_ITEM;
  const miscLines = _parts.MISC;
  const _totals = invoiceTicketTotals(ticket, lines, _parts);
  const grandTotal = _totals.total;

  const invoiceDate = ticket.date instanceof Date
    ? ticket.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : new Date(ticket.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const workDate = ticket.workDate
    ? new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      + (ticket.workDateEnd && ticket.workDateEnd !== ticket.workDate ? ' – ' + new Date(ticket.workDateEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '')
    : invoiceDate;

  // Resolve email template merge fields for the send dialog
  const _emailTemplate = window.AppState?.settings?.invoiceEmailTemplate || {};
  const _amtStr = '$' + (grandTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const _dueStr = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); })();
  const _resolveMerge = (text) => (text || '')
    .replace(/\{\{customer\}\}/g, customer?.name || ticket.customerName || '')
    .replace(/\{\{companyName\}\}/g, companyName || '')
    .replace(/\{\{invoiceNumber\}\}/g, ticket.invoiceNumber || '')
    .replace(/\{\{amount\}\}/g, _amtStr)
    .replace(/\{\{poNumber\}\}/g, ticket.poNumber || '')
    .replace(/\{\{terms\}\}/g, 'Net 30')
    .replace(/\{\{date\}\}/g, invoiceDate)
    .replace(/\{\{dueDate\}\}/g, _dueStr);
  const _resolvedSubject = _resolveMerge(_emailTemplate.subject || 'Invoice {{invoiceNumber}} from {{companyName}}');
  const _resolvedBody = _resolveMerge(_emailTemplate.body || 'Please find the invoice details below.\n\nInvoice: {{invoiceNumber}}\nAmount Due: {{amount}}\nPO: {{poNumber}}\nPayment Terms: {{terms}}');
  const _resolvedPayment = _emailTemplate.paymentInstructions || '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice ${invoiceEsc(ticket.invoiceNumber || '')} - ${invoiceEsc(companyName)}</title>
  <style>${invoiceStylesHTML()}</style>
</head>
<body>
  <div class="no-print" style="text-align:center;margin-bottom:16px;display:flex;justify-content:center;align-items:center;gap:16px;flex-wrap:wrap;">
    <button onclick="window.print()" style="padding:10px 24px;background:#3B82F6;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Print Invoice
    </button>
    <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;font-weight:600;color:#0F172A;">
      <!-- TASK-1346 sweep — narrow boolean, case (b): Save writes markAsInvoiced()
           with whatever this box holds. Defaulting it from isTicketInvoiced would
           pre-check it on the 110 invoice-marker-only tickets, so an unrelated Save
           would stamp invoiced:true and lock them read-only — the un-announced
           backfill this task explicitly refused. -->
      <input type="checkbox" id="invoice-mark-invoiced" ${ticket.invoiced ? 'checked' : ''} style="width:18px;height:18px;cursor:pointer;">
      Mark as Invoiced
    </label>
    <button id="invoice-save-btn" onclick="handleInvoiceSave()" style="padding:10px 24px;background:#2E7D32;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Save
    </button>
    ${/* TASK-9654 — no .iif for a ticket already linked to its QuickBooks invoice */ hasQboInvoiceLink(ticket) ? '' : `<button onclick="exportSingleIIF()" style="padding:10px 24px;background:#0F172A;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Export QB (.iif)
    </button>`}
    ${ticket.invoiceEmailSentAt ? `<span style="display:inline-flex;align-items:center;gap:6px;padding:8px 14px;background:#F0FDF4;color:#16a34a;border:1px solid #BBF7D0;border-radius:6px;font-size:13px;font-weight:600;">
      <span style="font-size:16px;">&#10003;</span> Emailed ${(() => { try { const d = ticket.invoiceEmailSentAt?.toDate ? ticket.invoiceEmailSentAt.toDate() : new Date(ticket.invoiceEmailSentAt); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch(e) { return ''; } })()} to ${invoiceEsc(ticket.invoiceEmailSentTo?.[0] || '')}
    </span>` : ''}
    <button onclick="showEmailDialog()" style="padding:10px 24px;background:#7C3AED;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Email Invoice
    </button>
    <button onclick="window.close()" style="padding:10px 24px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Close
    </button>
  </div>
  <!-- Email confirm dialog -->
  <div id="email-dialog" class="no-print" style="display:none;position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;justify-content:center;align-items:center;">
    <div style="background:#fff;border-radius:12px;padding:24px;max-width:520px;width:90%;box-shadow:0 8px 32px rgba(0,0,0,0.2);max-height:90vh;overflow-y:auto;">
      <h3 style="margin:0 0 16px;font-size:18px;color:#0F172A;">Email Invoice</h3>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">To</label>
        <input type="email" id="email-to" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;" value="${invoiceEsc((customer?.emails?.[0]?.email || customer?.email || ''))}" placeholder="customer@email.com">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">CC <span style="font-weight:400;color:#94A3B8;">(optional)</span></label>
        <input type="text" id="email-cc" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;" value="${invoiceEsc(companyEmail || '')}" placeholder="your@email.com">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Subject</label>
        <input type="text" id="email-subject" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;" value="${invoiceEsc(_resolvedSubject)}">
      </div>
      <div style="margin-bottom:12px;">
        <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Message</label>
        <textarea id="email-body" rows="5" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;">${invoiceEsc(_resolvedBody)}</textarea>
      </div>
      ${_resolvedPayment ? `<div style="margin-bottom:12px;"><label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Payment Instructions</label><textarea id="email-payment" rows="3" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;">${invoiceEsc(_resolvedPayment)}</textarea></div>` : ''}
      <div style="display:flex;gap:12px;justify-content:flex-end;">
        <button onclick="document.getElementById('email-dialog').style.display='none'" style="padding:10px 20px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">Cancel</button>
        <button id="email-send-btn" onclick="sendInvoiceEmail()" style="padding:10px 20px;background:#7C3AED;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">Send</button>
      </div>
    </div>
  </div>
  ${invoiceSaveScriptHTML([ticket.id])}
  <script>
  function exportSingleIIF() {
    var T = '\\t';
    var ticket = ${invoiceJson({
      id: ticket.id,
      invoiceNumber: ticket.invoiceNumber || ticket.ticketNo || ticket.id,
      customerName: ticket.customerName,
      date: ticket.date?.toDate ? ticket.date.toDate().toISOString() : ticket.date,
      workDate: ticket.workDate?.toDate ? ticket.workDate.toDate().toISOString() : ticket.workDate,
      poNumber: ticket.poNumber || '',
      vendorNumber: ticket.vendorNumber || '',
      wellName: ticket.wellName || '',
      wellNumber: ticket.wellNumber || '',
      jobName: ticket.jobName || ticket.jobTitle || '',
      grandTotal: ticket.grandTotal || 0,
      taxEnabled: ticket.taxEnabled || false,
      taxAmount: ticket.taxAmount || 0,
      taxRate: ticket.taxRate || 0
    })};
    var qbCustomerName = ${invoiceJson(customer?.qbCustomerName || customer?.name || ticket.customerName || 'Unknown')};
    var ticketLines = ${invoiceJson((lines || []).filter(_inv).map(l => ({
      type: l.type,
      personName: l.personName || '',
      assetName: l.assetName || '',
      fleetNumber: l.fleetNumber || '',
      description: l.description || '',
      hours: l.hours || 0,
      quantity: l.quantity || 0,
      rate: l.rate || 0,
      lineAmount: l.lineAmount || 0
    })))};
    var settings = ${invoiceJson({
      qbItemMap: window.AppState?.settings?.qbItemMap || {},
      qbDefaultTerms: window.AppState?.settings?.qbDefaultTerms || 'Net 30',
      qbAccountsReceivable: window.AppState?.settings?.qbAccountsReceivable || 'Accounts Receivable',
      qbIncomeAccount: window.AppState?.settings?.qbIncomeAccount || 'Income'
    })};

    function esc(v) { return v == null ? '' : String(v).replace(/[\\t\\r\\n]/g, ' ').trim(); }
    function fmtDate(d) { if (!d) return ''; var dt = new Date(d); if (isNaN(dt)) return ''; return (dt.getMonth()+1).toString().padStart(2,'0')+'/'+dt.getDate().toString().padStart(2,'0')+'/'+dt.getFullYear(); }
    function addDaysStr(d, n) { if (!d) return ''; var dt = new Date(d); dt.setDate(dt.getDate()+n); return (dt.getMonth()+1).toString().padStart(2,'0')+'/'+dt.getDate().toString().padStart(2,'0')+'/'+dt.getFullYear(); }
    var defaults = {EMPLOYEE:'Labor',EQUIPMENT:'Equipment Rental',DOWNTIME:'Downtime',STANDBY:'Standby',POTHOLE:'Pothole',PERMIT:'Load Permits',FIELD_ITEM:'Field Items',MISC:'Miscellaneous'};
    var hasQBSettings = Object.keys(settings.qbItemMap).length > 0;
    function qbItem(type) { if (type === 'FUEL_SURCHARGE') return settings.qbItemMap.FUEL_SURCHARGE || 'Fuel Surcharge'; return hasQBSettings ? (settings.qbItemMap[type] || null) : (defaults[type] || type || 'Service'); }

    var terms = settings.qbDefaultTerms || 'Net 30';
    var termsDays = parseInt(terms.replace(/\\D/g,'')) || 30;
    var invDate = fmtDate(ticket.date);
    var dueDate = addDaysStr(ticket.date, termsDays);
    var memo = [ticket.poNumber ? 'PO: '+ticket.poNumber : '', ticket.wellName ? 'Well: '+ticket.wellName+(ticket.wellNumber ? ' #'+ticket.wellNumber : '') : '', ticket.jobName ? 'Job: '+ticket.jobName : ''].filter(Boolean).join(' | ');
    // QB Desktop DOCNUM max 11 chars — strip INV- prefix if too long
    var docNum = String(ticket.invoiceNumber || '');
    if (docNum.length > 11) docNum = docNum.replace(/^INV-/, '');

    var lines_out = [];
    lines_out.push(['!TRNS','TRNSID','TRNSTYPE','DATE','ACCNT','NAME','AMOUNT','DOCNUM','MEMO','TERMS','DUEDATE','PONUM'].join(T));
    lines_out.push(['!SPL','SPLID','TRNSTYPE','DATE','ACCNT','NAME','AMOUNT','DOCNUM','MEMO','QNTY','PRICE','INVITEM','EXTRA'].join(T));
    lines_out.push('!ENDTRNS');

    // TASK-9668 — ticketLines above are only the lines this customer's invoice
    // shows (isLineInvoiced). A line type with no QB item mapped used to be
    // skipped silently; now the biller is told and chooses.
    if (ticketLines.length === 0) {
      alert("Nothing to export: every line on this ticket is hidden on the customer invoice.");
      return;
    }
    var unmapped = {};
    var unmappedList = [];
    for (var u = 0; u < ticketLines.length; u++) {
      var ut = ticketLines[u].type;
      if (qbItem(ut)) continue;
      if (!unmapped[ut]) { unmapped[ut] = 0; unmappedList.push(ut); }
      unmapped[ut]++;
    }
    if (unmappedList.length > 0) {
      var umMsg = unmappedList.map(function(t){ return t + " (" + unmapped[t] + " line" + (unmapped[t] !== 1 ? "s" : "") + ", exports as " + (defaults[t] || t || "Service") + ")"; }).join("; ");
      if (!confirm("These invoice lines have no QuickBooks item mapped: " + umMsg + ". Export them under the item names shown? Make sure those items exist in QuickBooks. Cancel exports nothing.")) return;
    }

    var lineTotal = 0;
    var splRows = [];
    for (var i = 0; i < ticketLines.length; i++) {
      var ln = ticketLines[i];
      var mappedN = qbItem(ln.type);
      var itemN = mappedN || (defaults[ln.type] || ln.type || 'Service');
      var amt = ln.lineAmount || ((ln.hours || ln.quantity || 0) * (ln.rate || 0));
      var qty = ln.hours || ln.quantity || 0;
      var desc = ln.personName || ((ln.assetName || 'Equipment') + (ln.fleetNumber ? ' #'+ln.fleetNumber : '')) || ln.description || '';
      if (ln.type === 'PERMIT') { qty = 1; amt = ln.lineAmount || ln.rate || 0; desc = ln.description || 'Load Permit'; }
      // TASK-9668 — Field Item / Misc lines were labelled "Equipment" (the
      // fallback above). Use the item description, as the batch export does.
      if (ln.type === 'FIELD_ITEM') desc = ln.description || 'Field Item';
      if (ln.type === 'MISC') desc = ln.description || 'Miscellaneous';
      var prc = ln.rate;
      // Newly exported (unmapped, was dropped): carry the invoice amount -
      // stored lineAmount, DOWNTIME as a deduction (COD Code Reviewer).
      if (!mappedN) { amt = (ln.type === 'DOWNTIME' ? -1 : 1) * (Number(ln.lineAmount) || 0); if (ln.type === 'DOWNTIME') prc = -prc; }
      if (ln.type === 'FUEL_SURCHARGE') { qty = 1; amt = Number(ln.lineAmount) || 0; prc = amt; desc = ln.description || 'Fuel Surcharge'; } // TASK-9440
      lineTotal += amt;
      splRows.push(['SPL','','INVOICE',invDate,'',esc(qbCustomerName),(-amt).toFixed(2),esc(docNum),esc(desc),qty?(-qty).toFixed(2):'',prc?(-prc).toFixed(2):'',esc(itemN),''].join(T));
    }
    if (ticket.taxEnabled && ticket.taxAmount > 0) {
      lineTotal += ticket.taxAmount;
      splRows.push(['SPL','','INVOICE',invDate,'',esc(qbCustomerName),(-ticket.taxAmount).toFixed(2),esc(docNum),esc('Sales Tax ('+ticket.taxRate.toFixed(2)+'%)'),'','','Sales Tax',''].join(T));
    }
    lines_out.push(['TRNS','','INVOICE',invDate,esc(settings.qbAccountsReceivable),esc(qbCustomerName),lineTotal.toFixed(2),esc(docNum),esc(memo),esc(terms),dueDate,esc(ticket.poNumber)].join(T));
    splRows.forEach(function(r){lines_out.push(r)});
    lines_out.push('ENDTRNS');

    var blob = new Blob([lines_out.join('\\n')], {type:'text/plain;charset=utf-8;'});
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = (ticket.invoiceNumber || 'export') + '.iif';
    a.click();
    URL.revokeObjectURL(url);
  }
  function showEmailDialog() {
    document.getElementById('email-dialog').style.display = 'flex';
  }
  // TASK-1412 follow-up — set when a send came back "can't tell whether it went".
  // Send is not silently re-armed after that: the next click must get past a
  // confirm that names the duplicate risk. Apostrophes only inside DOUBLE-quoted
  // strings here: this <script> is a template literal, where \' emits a bare ' .
  var _invoiceSendUnconfirmed = false;
  function sendInvoiceEmail() {
    var btn = document.getElementById('email-send-btn');
    var toEmail = document.getElementById('email-to').value.trim();
    var ccEmails = document.getElementById('email-cc').value.trim();
    var subject = document.getElementById('email-subject').value.trim();
    var messageBody = (document.getElementById('email-body') || {}).value || '';
    var paymentInstructions = (document.getElementById('email-payment') || {}).value || '';
    if (!toEmail) { alert('Please enter a recipient email.'); return; }
    if (_invoiceSendUnconfirmed && !confirm("Your last send of this invoice could not be confirmed. It may already be in the customer's inbox. Check your Sent folder first. Send it again anyway?")) return;
    btn.disabled = true;
    btn.textContent = 'Sending...';
    // Get invoice HTML (the printable content)
    var invoiceContent = document.querySelector('.invoice-page').outerHTML;
    // Wrap in full HTML with styles
    var fullHtml = '<html><head><style>'
      + '.no-print{display:none!important}' // TASK-4819 - see invoiceReconcileNoticeHTML
      + 'body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#0F172A;margin:0;padding:20px;}'
      + 'table{border-collapse:collapse;width:100%}'
      + 'th,td{padding:6px 10px;text-align:left;font-size:13px}'
      + 'th{background:#F1F5F9;font-weight:600;color:#475569}'
      + 'td{border-bottom:1px solid #E2E8F0}'
      + '</style></head><body>' + invoiceContent + '</body></html>';
    if (window.opener && window.opener.BillingModule) {
      var mod = window.opener.BillingModule;
      mod.sendInvoiceEmail({
        ${''/* TASK-9111 — every value below is a JS STRING LITERAL INSIDE AN INLINE <script>,
             not markup. invoiceEsc was the wrong escaper here and it took the whole
             element down: it does not escape "'", so "Busy Bee's Hot Oil Inc" emitted
             companyName: 'Busy Bee's Hot Oil Inc' -> SyntaxError: Unexpected identifier 's'.
             An inline <script> dies WHOLE on one syntax error, so every handler DEFINED
             in it went with it: Save (handleInvoiceSave), Export QB (exportSingleIIF),
             Email Invoice (showEmailDialog) and Send (sendInvoiceEmail).

             Corrected vs the original ticket, which said "Print / Email / Send": Print
             SURVIVES. It is onclick="window.print()" at :16784 — a native call in an
             attribute, not a function from this script — and so does Close. That makes
             the failure worse to diagnose, not better: the biller clicks Print, it
             works, so the popup looks alive while Save and Email do nothing at all.
             Live on two real tenants when filed.

             The trap, and the reason this is NOT fixed by teaching invoiceEsc about "'":
             inside a <script> element the HTML parser does not decode character
             references at all, so "&#39;" would arrive at the JS compiler as the literal
             six characters and land in the DATA. Measured, not reasoned — window.escJsAttr
             (correct for onclick="fn('...')", which IS an attribute) parses fine here but
             corrupts: it yields companyName === "Busy Bee&#39;s Hot Oil Inc", and that
             string is what gets emailed to the customer. No HTML escaper is right here.

             invoiceJson is the escaper this context already has: it is ALREADY used
             six times in this same <script> block (:16877 ticketIds, :16888 ticket,
             :16904, :16905 ticketLines, :16916 settings) — the payload below was the
             only part of it still on an html escaper. It is exact on all of:
             apostrophe, double quote,
             backslash, "&", and the </script> breakout its own header describes. */}
        tenantId: ${invoiceJson(BillingState.tenantId || '')},
        invoiceHtml: fullHtml,
        recipientEmails: [toEmail],
        ccEmails: ccEmails ? ccEmails.split(',').map(function(e){return e.trim()}).filter(Boolean) : [],
        subject: subject,
        replyTo: ${invoiceJson((() => { const et = window.AppState?.settings?.invoiceEmailTemplate || {}; return et.replyTo || companyEmail || ''; })())},
        companyName: ${invoiceJson(companyName || '')},
        message: messageBody,
        paymentInstructions: paymentInstructions,
        ticketId: ${invoiceJson(ticket.id || '')},
        logoUrl: ${invoiceJson(invoiceSafeUrl(logoUrl))},
        invoiceNumber: ${invoiceJson(ticket.invoiceNumber || '')},
        grandTotal: ${Number(grandTotal) || 0},
        dueDate: ${invoiceJson(_dueStr || '')}
      }, { resendConfirmed: _invoiceSendUnconfirmed }).then(function() {
        _invoiceSendUnconfirmed = false;
        btn.textContent = 'Sent!';
        btn.style.background = '#16a34a';
        setTimeout(function() { document.getElementById('email-dialog').style.display = 'none'; btn.disabled = false; btn.textContent = 'Send'; btn.style.background = '#7C3AED'; }, 2000);
      }).catch(function(err) {
        btn.disabled = false;
        if (err && err.unconfirmed) {
          _invoiceSendUnconfirmed = true;
          btn.textContent = 'Resend anyway';
          btn.style.background = '#B45309';
          alert((err.userMessage || "We can't tell whether this invoice went out. Check your Sent folder before sending again.")
            + (err.message ? ' (Details: ' + err.message + ')' : ''));
        } else {
          // An EARLIER attempt may still be unconfirmed — keep saying so.
          btn.textContent = _invoiceSendUnconfirmed ? 'Resend anyway' : 'Send';
          alert('Error sending: ' + ((err && err.message) || 'Unknown error'));
        }
      });
    } else {
      alert('Could not send — parent window closed.');
      btn.disabled = false;
      btn.textContent = 'Send';
    }
  }
  </script>
  ${_totals.drifted ? invoiceReconcileNoticeHTML([{
    label: ticket.ticketNo ? '#' + ticket.ticketNo : String(ticket.id || '').slice(0, 8),
    total: _totals.total, storedShown: _totals.storedShown,
  }]) : ''}<div class="invoice-page">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        ${logoUrl ? `<img src="${invoiceSafeUrl(logoUrl)}" style="max-height:70px;max-width:200px;flex-shrink:0;" alt="Logo">` : ''}
        <div>
          <div style="font-size:18px;font-weight:800;color:#0F172A;">${invoiceEsc(companyName)}</div>
          ${companyAddress ? `<div style="color:#475569;font-size:13px;margin-top:2px;">${invoiceEsc(companyAddress)}</div>` : ''}
          ${companyPhone ? `<div style="color:#475569;font-size:13px;">${invoiceEsc(companyPhone)}</div>` : ''}
          ${companyEmail ? `<div style="color:#475569;font-size:13px;">${invoiceEsc(companyEmail)}</div>` : ''}
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:28px;font-weight:800;color:#3B82F6;letter-spacing:1px;">INVOICE</div>
        ${ticket.invoiceNumber ? `<div style="font-size:16px;font-weight:600;color:#0F172A;margin-top:4px;">${invoiceEsc(ticket.invoiceNumber)}</div>` : ''}
        <div style="color:#475569;margin-top:4px;">Date: ${invoiceDate}</div>
        ${ticket.ticketNo ? `<div style="color:#475569;">Ticket #${invoiceEsc(ticket.ticketNo)}</div>` : ''}
      </div>
    </div>

    <div style="margin-bottom:24px;padding:16px;background:#F8FAFC;border-radius:6px;">
      <div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Bill To</div>
      <div style="font-size:16px;font-weight:700;color:#0F172A;">${invoiceEsc(customer?.name || ticket.customerName || '-')}</div>
      ${customer?.address ? `<div style="color:#475569;margin-top:2px;">${invoiceEsc(customer.address)}</div>` : ''}
      ${customer?.phone ? `<div style="color:#475569;">${invoiceEsc(customer.phone)}</div>` : ''}
      ${customer?.email ? `<div style="color:#475569;">${invoiceEsc(customer.email)}</div>` : ''}
    </div>

    ${(job || ticket.location || ticket.poNumber || ticket.vendorNumber || ticket.wellName || ticket.afeNumber || _invJobType || projectName) ? `
      <div style="margin-bottom:24px;display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;font-size:13px;">
        ${job?.title ? `<div><span style="color:#64748B;">Job:</span> <strong>${invoiceEsc(job.title)}</strong></div>` : ''}${_invJobType ? `<div><span style="color:#64748B;">Type:</span> ${invoiceEsc(_invJobType)}</div>` : ''}${projectName ? `<div><span style="color:#64748B;">Project:</span> ${invoiceEsc(projectName)}</div>` : ''}
        ${ticket.location || job?.location || job?.address ? `<div><span style="color:#64748B;">Location:</span> ${invoiceEsc(ticket.location || job?.location || job?.address)}</div>` : ''}
        <div><span style="color:#64748B;">Work Date:</span> ${workDate}</div>
        ${ticket.poNumber ? `<div><span style="color:#64748B;">PO #:</span> ${invoiceEsc(ticket.poNumber)}</div>` : ''}
        ${ticket.vendorNumber ? `<div><span style="color:#64748B;">Vendor #:</span> ${invoiceEsc(ticket.vendorNumber)}</div>` : ''}
        ${ticket.wellName ? `<div><span style="color:#64748B;">Well:</span> ${invoiceEsc(ticket.wellName)}${ticket.wellNumber ? ' #' + invoiceEsc(ticket.wellNumber) : ''}</div>` : ''}
        ${ticket.afeNumber ? `<div><span style="color:#64748B;">AFE / CC:</span> ${invoiceEsc(ticket.afeNumber)}</div>` : ''}
      </div>
    ` : ''}

    ${buildOrderedSections({
      notes: buildWorkPerformedHTML(ticket, lines),
      labor: buildLaborTableHTML(empLines, getInvoiceColumnConfig(ticket.customerId, 'labor')),
      equipment: buildEquipSubTables(equipLines, ticket.customerId),
      downtime: buildDowntimeTableHTML(downLines, getInvoiceColumnConfig(ticket.customerId, 'downtime')),
      standby: buildStandbyTableHTML(standbyLines, getInvoiceColumnConfig(ticket.customerId, 'standby')),
      permit: buildPermitTableHTML(permitLines),
      fieldItems: buildFieldItemsTableHTML(fieldItemLines),
      misc: buildMiscTableHTML(miscLines),
      // TASK-9440 — appended (not in DEFAULT_SECTION_ORDER), right after misc.
      fuelSurcharge: buildFuelSurchargeTableHTML(_parts.FUEL_SURCHARGE),
      // TASK-4819 — the two sections that had no branch here. Neither key is in
      // DEFAULT_SECTION_ORDER on purpose: buildOrderedSections appends unknown
      // keys after the configured ones, so adding them cannot move an existing
      // section for the 3 tenants running a custom invoiceSectionOrder.
      lineInfo: buildLineInfoTableHTML(_parts.POTHOLE),
      otherCharges: buildOtherChargesTableHTML(_parts.OTHER)
    })}

    ${(() => {
      const _invPrintDocs = (ticket.documents || []).filter(d => d.printWithInvoice);
      if (_invPrintDocs.length === 0) return '';
      let _dhtml = '<div style="margin-top:24px;"><div style="font-size:14px;font-weight:700;color:#0F172A;margin-bottom:8px;">Attached Documents</div><div style="display:flex;flex-wrap:wrap;gap:12px;">';
      for (const d of _invPrintDocs) {
        if (d.isPdf || d.mimeType === 'application/pdf') {
          _dhtml += '<div style="width:120px;padding:12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;text-align:center;"><div style="font-size:32px;color:#EF4444;">&#128196;</div><div style="font-size:0.7rem;color:#0F172A;margin-top:4px;word-break:break-all;">' + invoiceEsc(d.name || 'PDF') + '</div></div>';
        } else {
          _dhtml += _ticketDocPrintGridImg(d, 120, '<img src="' + invoiceSafeUrl(d.fullUrl || d.thumbUrl) + '" style="width:120px;height:120px;object-fit:cover;border-radius:6px;border:1px solid #E2E8F0;" alt="' + invoiceEsc(d.name || 'Document') + '">');
        }
      }
      _dhtml += '</div></div>';
      return _dhtml;
    })()}

    ${ticket.taxEnabled ? `
    <div style="margin-top:32px;padding:16px;background:#F8FAFC;border-radius:6px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <span style="font-size:14px;color:#475569;">Subtotal</span>
        <span style="font-size:16px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(_totals.subtotal)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <span style="font-size:14px;color:#475569;">Tax (${(ticket.taxRate || 0).toFixed(2)}%)</span>
        <span style="font-size:16px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(ticket.taxAmount || 0)}</span>
      </div>
    </div>` : ''}

    <div class="invoice-footer">
      <div style="margin-top:${ticket.taxEnabled ? '8' : '32'}px;padding:16px;background:#0F172A;border-radius:6px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:18px;font-weight:700;color:#fff;">Grand Total</span>
        <span style="font-size:24px;font-weight:800;color:#fff;">${invoiceFmtMoney(grandTotal)}</span>
      </div>
      <div style="margin-top:8px;text-align:center;color:#64748B;font-size:11px;">
        Thank you for your business — <strong style="color:#475569;">${invoiceEsc(companyName)}</strong>
      </div>
    </div>
  </div>
</body>
</html>`;
}

// ============================================
// GROUPED INVOICE
// ============================================

// TASK-9393 — one invoice number is one bill to ONE customer. Before this,
// createGroupedInvoice stamped a shared number on every selected ticket with no
// customer check and rendered the document to the FIRST ticket's customer, so a
// multi-select across customers billed customer B's work on customer A's invoice
// (the server record just lists both in customerIds). Measured read-only
// 2026-09-24: 1 of 540 invoice numbers across all tenants spans two customers
// (trippin-trucking INV-202605-033, Office + Shop, 22 tickets).
//
// PURE — no BillingState, no DOM. Groups the tickets that are actually about to
// be invoiced by customerId; a ticket with no customer is its own group, because
// "no customer" on a bill addressed to A is the same defect. Returns
// { mixed, groups: [{ customerId, name, count }] }, groups in first-seen order.
function _invoiceCustomerGroups(tickets, customers) {
  const byId = new Map();
  for (const t of (tickets || [])) {
    if (!t) continue;
    const cid = t.customerId || null;
    const key = cid || '__none__';
    if (!byId.has(key)) {
      const c = cid ? (customers || []).find(x => x && x.id === cid) : null;
      const name = (c && c.name) || t.customerName || (cid ? cid : 'No customer');
      byId.set(key, { customerId: cid, name: String(name), count: 0 });
    }
    byId.get(key).count++;
  }
  const groups = [...byId.values()];
  return { mixed: groups.length > 1, groups };
}

// TASK-10164 — the grouped invoice page must be rendered from the POST-stamp
// ticket. ticketData holds the ticket objects captured from BillingState.tickets
// BEFORE the stamp; the snapshot listener REPLACES that array with new objects
// (on demo the updateDoc's latency-compensated snapshot; off demo the server
// write's snapshot can land before the callable returns), so the `_local`
// mirror in createGroupedInvoice can miss the captured object. The page's
// "Mark as Invoiced" box is checked only when every rendered ticket reads
// invoiced, and its Save calls markAsInvoiced(ids, checked) — so a pre-stamp
// render showed the box unchecked and one Save cleared the invoice number.
// PURE — returns new ticketData with the stamp merged in; never mutates input.
function _withGroupedInvoiceStamp(ticketData, invoiceNumber) {
  return (ticketData || []).map(td => (td && td.ticket)
    ? { ...td, ticket: { ...td.ticket, invoiced: true, invoiceNumber } }
    : td);
}

async function createGroupedInvoice(opts) {
  if (_moneyBlocked()) return; // TASK-9491
  const ids = [...BillingState.selectedTicketIds];
  if (ids.length === 0) {
    window.showToast?.('No tickets selected', 'info');
    return;
  }

  // Single ticket — fall through to existing flow
  if (ids.length === 1) {
    const ticket = BillingState.tickets.find(t => t.id === ids[0]);
    if (ticket) {
      await generateInvoice(ids[0]);
      clearTicketSelection();
    }
    return;
  }

  try {
    let selectedTickets = BillingState.tickets.filter(t => ids.includes(t.id));
    // TASK-1345 (2nd-COD Adversary ⚠️) — this path stamped invoiceNumber/
    // invoicedAt on EVERY selected ticket with no status check, so an archived
    // ticket swept into a multi-select came out invoiced while STAYING archived
    // (invoiced + invisible), and a written-off one could be re-billed. Skip
    // them and say so, rather than silently billing them.
    // TASK-1346 sweep — ALREADY-BILLED tickets join that skip list. The single-ticket
    // path only assigns a number `if (!ticket.invoiceNumber)`, so it can never
    // renumber; this loop stamps unconditionally, so one already-invoiced ticket
    // swept into a multi-select had its existing invoice number silently
    // overwritten — the customer's paid invoice and DispatchDeck stop agreeing.
    const _skipped = selectedTickets.filter(t => t.status === 'archived' || t.status === 'locked' || t.writtenOff === true || isTicketInvoiced(t));
    // TASK-9988 S1 — someone still clocked in on it: skip only those, by name.
    const _ocSkipped = selectedTickets.filter(t => !_skipped.includes(t) && _openClockOnTicket(t.id).length > 0);

    // TASK-9393 — REFUSE a selection whose invoiceable tickets span customers,
    // before any confirm, read, number allocation or stamp. Judged on the tickets
    // that would actually be invoiced (skipped ones are never stamped); every
    // later gate only REMOVES tickets, so it cannot introduce a second customer.
    {
      // TASK-9988 S1 (COD Code Reviewer): clocked-in tickets are skipped too, so
      // they must not count toward the customer mix either.
      const _cg = _invoiceCustomerGroups(selectedTickets.filter(t => !_skipped.includes(t) && !_ocSkipped.includes(t)), BillingState.customers);
      if (_cg.mixed) {
        const _cgList = _cg.groups.map(g => `${g.name} (${g.count})`).join(', ');
        window.showToast?.(
          `Can't put ${_cg.groups.length} customers on one invoice: ${_cgList}. Nothing was invoiced — select one customer's tickets and create an invoice for each.`,
          'error', null, 15000
        );
        return;
      }
    }

    if (_skipped.length > 0 || _ocSkipped.length > 0) {
      selectedTickets = selectedTickets.filter(t => !_skipped.includes(t) && !_ocSkipped.includes(t));
      const _allSkipped = [..._skipped, ..._ocSkipped];
      const _skipTotal = _allSkipped.reduce((sum, t) => sum + Number(t.grandTotal || 0), 0);
      // 3rd-COD Domain — a toast is the wrong severity here. Batch-invoicing is
      // exactly the scenario that produced the original $47,340 gap; a biller
      // running a big batch can miss a toast and believe the whole selection
      // was billed. Make them acknowledge the money being left behind.
      const _parts = [];
      if (_skipped.length > 0) {
        _parts.push(`${_skipped.length} of the selected tickets ${_skipped.length !== 1 ? 'are' : 'is'} archived, written off, or already invoiced and will be SKIPPED. Unarchive or uninvoice ${_skipped.length !== 1 ? 'them' : 'it'} first if ${_skipped.length !== 1 ? 'they still need' : 'it still needs'} billing.`);
      }
      if (_ocSkipped.length > 0) {
        // TASK-9988 S1 — named, so the biller knows who to call.
        _parts.push(`Still clocked in, SKIPPED: ${_ocSkipped.map(t => _openClockListLabel(t, _openClockOnTicket(t.id))).join('; ')} — clock them out in Workforce first.`);
      }
      if (selectedTickets.length === 0) {
        await window.showConfirm?.(
          `Nothing here can be invoiced`,
          `${_parts.join(' ')} (${formatCurrency(_skipTotal)} total)`,
          { confirmText: 'OK', hideCancel: true, icon: 'warning', iconColor: '#F59E0B' }
        );
        return;
      }
      const _goOn = await window.showConfirm?.(
        `${_allSkipped.length} ticket${_allSkipped.length !== 1 ? 's' : ''} can't be invoiced`,
        `${_parts.join(' ')} (${formatCurrency(_skipTotal)} total) Invoice the remaining ${selectedTickets.length}?`,
        { confirmText: `Invoice ${selectedTickets.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
      );
      if (!_goOn) return;
    }
    if (selectedTickets.length === 0) {
      window.showToast?.('Nothing left to invoice in this selection.', 'info');
      return;
    }

    // TASK-9811 S1 — tickets carrying different POs: WARN and offer one invoice
    // per PO, never block (a warning that can't load lets it through, like every
    // warning here). Each ticket keeps its own PO either way. Nothing is read,
    // numbered or stamped yet, so Cancel leaves it all as is. Split = each PO
    // group through this same path, every gate included.
    if (!(opts && opts.poSplitChild) && new Set(selectedTickets.map(t => poGroupKey(poNormalizeForSave(t.poNumber)))).size > 1) {
      const _poPick = await _loadJobPoUi().then(m => m.groupedPoMixChoice(selectedTickets, { isStrayMultiClick: _isStrayMultiClick }), () => 'together');
      if (!_poPick) return;
      if (Array.isArray(_poPick.split)) {
        // COD (Code Reviewer / Domain / Adversary): STOP at the first group that
        // did not get invoiced (cancelled at a gate, or failed) and leave it and
        // every later group SELECTED, saying how far it got — never a silent
        // half-split that reads as done.
        const _groups = _poPick.split;
        let _made = 0;
        for (let gi = 0; gi < _groups.length; gi++) {
          BillingState.selectedTicketIds = new Set(_groups[gi]);
          await createGroupedInvoice({ poSplitChild: true });
          const _billed = _groups[gi].some(id => isTicketInvoiced(BillingState.tickets.find(x => x.id === id) || {}));
          if (!_billed) {
            const _left = _groups.slice(gi).flat();
            BillingState.selectedTicketIds = new Set(_left);
            if (BillingState.viewMode === 'tickets') renderTicketsList();
            updateSelectionActionBar();
            window.showToast?.(`Split stopped: ${_made} of ${_groups.length} PO invoices made. The other ${_left.length} ticket${_left.length !== 1 ? 's are' : ' is'} still selected.`, 'warning', null, 12000);
            return;
          }
          _made++;
        }
        clearTicketSelection();
        return;
      }
    }

    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

    // TASK-1401/1589 — the shared invoice number is no longer computed here from a
    // `max + 1` read. It is RESERVED transactionally (allocateInvoiceNumber) and the
    // reservation deliberately moved DOWN, to just before the stamp: every gate below
    // can still bail out or be cancelled, and burning a reserved number on a batch
    // nobody ended up invoicing would leave a hole in the sequence for no reason.
    // Gaps are cheap, duplicates are not — but there is no reason to make gaps either.

    // Load all ticketLines in parallel
    const linePromises = selectedTickets.map(async (t) => {
      try {
        const snap = await getDocs(
          collection(db, 'tenants', tenant, 'tickets', t.id, 'ticketLines')
        );
        const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        lines.sort((a, b) => {
          if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
          const aTime = a.fromTime?.toDate?.() || new Date(0);
          const bTime = b.fromTime?.toDate?.() || new Date(0);
          return aTime - bTime;
        });
        return { ticket: t, lines };
      } catch (err) {
        console.warn('[Billing] Error loading ticketLines for', t.id, err);
        // TASK-1548 (COD Code Reviewer ❌) — a failed line read is UNKNOWN, not
        // clean. The zero-hour partition below fails CLOSED on this flag; without
        // it, "couldn't read the lines" invoiced straight past the gate.
        return { ticket: t, lines: [], linesFailed: true };
      }
    });
    let ticketData = await Promise.all(linePromises);

    // TASK-1548 — priced-equipment-with-no-hours gate, batch form. The lines are
    // already in hand (loaded above), so this adds zero reads. Same skip pattern as
    // the archived/written-off partition earlier in this function: a BLOCKING
    // confirm naming the tickets and the fix, never a silent drop — and nothing has
    // been stamped yet, so Cancel leaves every ticket untouched. NOTE: a line load
    // that failed above yields lines:[] which this check cannot see through — but
    // that ticket's invoice detail page would render empty too, so the biller sees
    // the same failure either way.
    {
      const _zhBlocked = (td) => td.linesFailed === true || _zeroHourOffenders(td.lines).length > 0;
      const _zhBad = ticketData.filter(_zhBlocked);
      if (_zhBad.length > 0) {
        const _zhKeep = ticketData.filter(td => !_zhBlocked(td));
        const _zhNames = _zhBad.map(td => `#${td.ticket.ticketNo || '?'} (${td.linesFailed ? 'lines could not be read — try again' : _zeroHourOffenders(td.lines).map(_zeroHourLineLabel).join(', ')})`).join(' · ');
        if (_zhKeep.length === 0) {
          window.showToast?.(`Can't invoice — priced equipment with no hours on ${_zhNames}. Enter hours or mark the lines non-billable first.`, 'error');
          return;
        }
        const _zhGoOn = await window.showConfirm?.(
          `${_zhBad.length} ticket${_zhBad.length !== 1 ? 's have' : ' has'} equipment hours missing`,
          `${_zhNames}. A priced equipment line with 0 hours invoices real work at $0. ` +
          `These tickets will be SKIPPED — enter the hours or mark the lines non-billable in Review Ticket, then invoice them. ` +
          `Invoice the remaining ${_zhKeep.length}?`,
          { confirmText: `Invoice ${_zhKeep.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
        );
        if (!_zhGoOn) return;
        ticketData = _zhKeep;
        const _zhBadIds = new Set(_zhBad.map(td => td.ticket.id));
        selectedTickets = selectedTickets.filter(t => !_zhBadIds.has(t.id));
      }
    }

    // TASK-1615-B — mileage-off-a-bad-route gate, batch form. Without it a biller
    // walks straight past the single-ticket gate by selecting the tickets and
    // hitting Invoice. The lines are already in hand, so the only added cost is one
    // job read per DISTINCT job among the tickets that actually carry per-mile
    // money — normally zero. Same skip-with-a-blocking-confirm shape as the
    // zero-hour block above; nothing is stamped yet, so Cancel leaves every ticket
    // untouched. A job read that FAILS is UNKNOWN, not clean: that ticket is
    // skipped, mirroring the linesFailed treatment above.
    {
      const _rmBlocked = [];
      const _rmNeeds = ticketData.filter(td => _rmHasPerMileMoney(td.lines));
      if (_rmNeeds.length > 0) {
        const _rmJobBad = new Map();
        await Promise.all(
          [...new Set(_rmNeeds.map(td => td.ticket && td.ticket.jobId).filter(Boolean))].map(async (jid) => {
            try {
              _rmJobBad.set(jid, await _rmFetchJobBadStops(jid));
            } catch (err) {
              console.warn('[Billing] Batch mileage gate: job read failed for', jid, err);
              _rmJobBad.set(jid, null); // null = unknown, never "clean"
            }
          })
        );
        for (const td of _rmNeeds) {
          const jid = td.ticket && td.ticket.jobId;
          const bad = jid ? _rmJobBad.get(jid) : [];
          if (bad === null) { _rmBlocked.push({ td, why: 'route could not be read — try again' }); continue; }
          const off = _rmSuspectLines(td.lines, Array.isArray(bad) && bad.length > 0);
          if (off.length > 0) _rmBlocked.push({ td, why: off.map(_rmLineLabel).join(', ') });
        }
      }
      if (_rmBlocked.length > 0) {
        const _rmBadIds = new Set(_rmBlocked.map(b => b.td.ticket.id));
        const _rmKeep = ticketData.filter(td => !_rmBadIds.has(td.ticket.id));
        const _rmNames = _rmBlocked.map(b => `#${b.td.ticket.ticketNo || '?'} (${b.why})`).join(' · ');
        if (_rmKeep.length === 0) {
          window.showToast?.(`Can't invoice — mileage came from a bad location on ${_rmNames}. Correct or confirm the miles in Review Ticket first.`, 'error');
          return;
        }
        const _rmGoOn = await window.showConfirm?.(
          `${_rmBlocked.length} ticket${_rmBlocked.length !== 1 ? 's bill' : ' bills'} mileage from a bad location`,
          `${_rmNames}. The Per Mile quantity was auto-filled from a route whose destination coordinates aren't a real point on earth, so it can be thousands of miles off — or zero. ` +
          `These tickets will be SKIPPED — correct or confirm the miles in Review Ticket, then invoice them. ` +
          `Invoice the remaining ${_rmKeep.length}?`,
          { confirmText: `Invoice ${_rmKeep.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
        );
        if (!_rmGoOn) return;
        ticketData = _rmKeep;
        selectedTickets = selectedTickets.filter(t => !_rmBadIds.has(t.id));
      }
    }

    // TASK-511/1567 — preload each customer's contract prefs BEFORE the $0 gate so
    // getInvoiceExcludeLineTypes() resolves their hide list. Moved above the gate
    // (was below) because the gate must judge the CUSTOMER-INVOICE total, not the
    // raw line sum — an all-excluded positive ticket still bills $0. Keyed on
    // ticketData's customers (superset of the post-gate selection); loadReviewContractRates
    // caches, so buildGroupedInvoiceHTML + recordRevenueEntry reuse it (no double read).
    await Promise.all([...new Set(ticketData.map(td => td.ticket && td.ticket.customerId).filter(Boolean))]
      .map(cid => loadReviewContractRates(cid)));

    // TASK-1567 — whole-ticket $0 gate, batch form. Lines are already in hand
    // (loaded above and the zero-hour block already dropped linesFailed tickets),
    // so this adds ZERO reads. Uses the SAME invoice-inclusion math as the
    // single-ticket path + the render (gross − excludedInvoiceAmount), so the batch
    // gate == PDF == total. Same skip-with-confirm shape as the partitions above;
    // nothing has been stamped yet, so Cancel leaves every ticket untouched.
    // internalTimeOnly customers are exempt (legit $0 internal time).
    {
      const _ztInvoiceTotal = (td) => (Number(computeTicketTotalsFromLines(td.lines, td.ticket).grandTotal) || 0)
        - (Number(excludedInvoiceAmount(td.lines, getInvoiceExcludeLineTypes(td.ticket && td.ticket.customerId))) || 0);
      const _ztBlocked = (td) => td.linesFailed !== true
        && !_ticketCustomerIsInternalTime(td.ticket)
        && !(_ztInvoiceTotal(td) > 0);
      const _ztBad = ticketData.filter(_ztBlocked);
      if (_ztBad.length > 0) {
        const _ztKeep = ticketData.filter(td => !_ztBad.includes(td));
        const _ztNames = _ztBad.map(td => `#${td.ticket.ticketNo || '?'}`).join(' · ');
        if (_ztKeep.length === 0) {
          window.showToast?.(`Can't invoice — nothing to bill ($0 total) on ${_ztNames}. Add the missing truck/asset or hours first.`, 'error');
          return;
        }
        const _ztGoOn = await window.showConfirm?.(
          `${_ztBad.length} ticket${_ztBad.length !== 1 ? 's have' : ' has'} nothing to bill`,
          `${_ztNames} total $0 — a $0 invoice to a customer is never correct (the truck/asset is likely missing, or hours were never entered). ` +
          `These will be SKIPPED. Invoice the remaining ${_ztKeep.length}?`,
          { confirmText: `Invoice ${_ztKeep.length}`, cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
        );
        if (!_ztGoOn) return;
        ticketData = _ztKeep;
        const _ztBadIds = new Set(_ztBad.map(td => td.ticket.id));
        selectedTickets = selectedTickets.filter(t => !_ztBadIds.has(t.id));
      }
    }

    // (contract prefs already preloaded above the $0 gate — TASK-511/1567.)

    // TASK-1572 follow-up (Chris 2026-09-29) — not Ready is a WARNING, never a
    // block. After every skip partition, so it names only tickets that will
    // actually be invoiced, and before the number is reserved, so "Go back"
    // burns nothing and leaves the selection intact.
    // A batch the partitions shrank to ONE ticket gets the single-ticket copy
    // ("Review it first"), so that button must actually open Review (COD Code
    // Reviewer). Statuses re-read from the live snapshot for the same reason as
    // the single path.
    {
      const _nrFresh = selectedTickets.map(t => BillingState.tickets.find(x => x.id === t.id) || t);
      if (!(await _notReadyInvoiceWarning(_nrFresh, { reviewOnCancel: _nrFresh.length === 1 }))) return;
      if (!(await _undecidedDaysInvoiceWarning(_nrFresh))) return; // TASK-10041 — warn, never block
    }

    // TASK-1401/1589 — RESERVE the shared number now that the selection is final.
    // Every ticket on this invoice carries the SAME number by design, so this is one
    // allocation, not one per ticket. A failure here stamps NOTHING: no ticket is
    // marked invoiced, no revenue entry is written, no invoice window opens, and the
    // biller is told in plain words that the batch is untouched and retrying is safe.
    let invoiceNumber;
    try {
      invoiceNumber = await allocateInvoiceNumber(tenant);
    } catch (err) {
      console.error('[Billing] Grouped invoice number allocation failed:', err);
      window.logError?.(err, { module: 'billing', operation: 'ALLOCATE_INVOICE_NUMBER', ticketIds: selectedTickets.map(t => t.id) });
      window.showToast?.(_invoiceAllocFailedMessage(err), 'error');
      return;
    }

    // Mark each ticket with shared invoice number.
    // TASK-1346 — same fix as the single-ticket path: set the boolean here, in
    // the same write, instead of relying on markAsInvoiced() running later.
    //
    // TASK-1587 — this is the FOURTH invoice-marker stamp in this file and it was
    // not in the task's named list, but leaving it on updateDoc would leave the
    // server backstop with its widest hole: a grouped QuickBooks week is 20-40
    // tickets in one write loop. Same op, same payload, batched.
    if (_invoiceStampUsesGateway(tenant)) {
      const _gRes = await _gatewayMarkInvoiced(
        tenant, selectedTickets.map(t => t.id), { invoiceNumber }, `markInvoiced:grouped:${invoiceNumber}`
      );
      if (_gRes.blocked.length > 0) {
        // A blocked ticket is a per-ticket SKIP, not a whole-invoice failure — same
        // semantics as the archived/zero-hour/$0 partitions above. But unlike those,
        // nothing was shown yet and money is being left behind, so it gets the same
        // blocking dialog severity the skip partitions use, not a toast.
        const _names = _gRes.blocked.map(b => `#${b.ticketNo || '?'} (${b.reason})`).join(' · ');
        await window.showConfirm?.(
          `${_gRes.blocked.length} ticket${_gRes.blocked.length !== 1 ? 's' : ''} could not be invoiced`,
          `${_names}. ${_gRes.marked.length > 0
            ? `They were left OFF invoice ${invoiceNumber} and are still unbilled — fix them and invoice them separately.`
            : 'Nothing was invoiced.'}`,
          { confirmText: 'OK', cancelText: 'Close', icon: 'warning', iconColor: '#DC2626' }
        );
      }
      if (_gRes.marked.length === 0) return;
      const _markedSet = new Set(_gRes.marked);
      selectedTickets = selectedTickets.filter(t => _markedSet.has(t.id));
      ticketData = ticketData.filter(td => _markedSet.has(td.ticket && td.ticket.id));
      for (const t of selectedTickets) {
        const _local = BillingState.tickets.find(x => x.id === t.id);
        if (_local) { _local.invoiced = true; _local.invoiceNumber = invoiceNumber; }
      }
    } else {
      // Demo — see _invoiceStampUsesGateway.
      for (const t of selectedTickets) {
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', t.id), {
          invoiceNumber,
          invoiced: true,
          invoicedAt: Timestamp.now(),
          invoicedBy: window.AppState?.user?.uid || 'unknown',
          updatedAt: Timestamp.now()
        });
        const _local = BillingState.tickets.find(x => x.id === t.id);
        if (_local) { _local.invoiced = true; _local.invoiceNumber = invoiceNumber; }
      }
    }

    // TASK-10164 — render from the post-stamp state (see _withGroupedInvoiceStamp).
    ticketData = _withGroupedInvoiceStamp(ticketData, invoiceNumber);

    // TASK-4599 — the grouped path stamped every ticket invoiced but never wrote a
    // MARK_INVOICED audit row, so "who invoiced this?" was unanswerable for any
    // ticket that rode a grouped invoice (INV-202608-103 / #100525, #100540,
    // #100548 had none). Same shape as MARK_INVOICED_EXTERNAL just above in this
    // file: one row PER TICKET even in a batch, never one row carrying a ticketIds
    // array — a biller resolving a dispute greps by ticketId, not by array
    // membership. logAudit strips undefined fields before the Firestore write
    // (TASK-1479 in app.js), so `ticketNo` — optional on some tickets — is coalesced
    // to `null` here rather than left undefined, but nothing below can actually be
    // undefined: ticketId comes from BillingState.tickets (always has an id),
    // invoiceNumber was just allocated and already gates the whole function on
    // failure above, and the actor is stamped by logAudit itself from
    // AppState.user.email, not passed here.
    for (const t of selectedTickets) {
      window.logAudit?.('ticket', 'MARK_INVOICED', {
        ticketId: t.id,
        ticketNo: t.ticketNo || null,
        invoiceNumber
      });
    }

    // Record revenue entries for each ticket
    // TASK-4595 — same fire-and-forget defect as the single-invoice path, and
    // worse here: this loop covers a whole grouped invoice, so a silent failure
    // understates BI by the entire group. Collect and report by ticket number —
    // a biller can act on "#100261 and #100255 did not reach revenue", not on
    // "something failed".
    // TASK-9942 — off demo the server wrote every marked ticket's revenue row
    // in the stamp's own commit (ticketGateway markInvoiced); only demo, whose
    // stamp stays in the browser, records it here.
    const _revFailed = [];
    if (!_invoiceStampUsesGateway(tenant)) {
      for (const t of selectedTickets) {
        if ((await recordRevenueEntry(tenant, t, invoiceNumber)) === false) _revFailed.push(t);
      }
    }
    if (_revFailed.length > 0) {
      window.showToast?.(
        `Invoice ${invoiceNumber} created, but your revenue reports did not update for ${_revFailed.length} ticket${_revFailed.length !== 1 ? 's' : ''} (${_revFailed.map(t => '#' + (t.ticketNo || '?')).join(', ')}). The invoice is fine — BI will be short until those are re-run.`,
        'warning', null, 15000
      );
    }

    // Build grouped invoice — resolved from structured arrays with IB header prefs
    const { companyName, companyAddress, companyPhone, companyEmail, logoUrl } = resolveCompanyInfo();

    // Customer from first ticket
    const firstTicket = selectedTickets[0];
    const customer = firstTicket.customerId ? BillingState.customers.find(c => c.id === firstTicket.customerId) : null;

    const html = buildGroupedInvoiceHTML({
      invoiceNumber,
      // TASK-9646 S3b emitted money — read-only copies, AFTER the stamp mirrored into local state
      ticketData: ticketData.map(td => (td && td.ticket ? { ...td, ticket: _emitTicketMoney(td.ticket) } : td)),
      companyName,
      companyAddress,
      companyPhone,
      companyEmail,
      logoUrl,
      customer,
      projectName: await _invoiceProjectName(selectedTickets) // TASK-10203
    });

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }

    clearTicketSelection();
    window.showToast?.(`Invoice ${invoiceNumber} created for ${selectedTickets.length} tickets`, 'success');
  } catch (err) {
    console.error('[Billing] Error creating grouped invoice:', err);
    window.showToast?.('Error creating grouped invoice', 'error');
  }
}

function buildGroupedInvoiceHTML(data) {
  const { invoiceNumber, ticketData, companyName, companyAddress, companyPhone, companyEmail, logoUrl, customer, projectName } = data;

  const invoiceDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  let grandTotal = 0;

  // Sort tickets oldest → newest by work date
  ticketData.sort((a, b) => {
    const aDate = a.ticket.workDate || '';
    const bDate = b.ticket.workDate || '';
    return aDate.localeCompare(bDate);
  });

  // Build per-ticket sections
  let ticketSections = '';
  let _ticketIdx = 0;
  // TASK-4819 — computed once per ticket in the loop below, then reused by the
  // summary table so the summary line and the detail page cannot disagree. They
  // were two independent copies of the same expression before this.
  const _ticketTotals = new Map();
  const _reconcile = [];
  for (const { ticket, lines } of ticketData) {
    const _exTypes = getInvoiceExcludeLineTypes(ticket.customerId);
    const _inv = (l) => isLineInvoiced(l, _exTypes);
    // TASK-4819 — this renderer knew 7 of the 9 live line types and totalled all
    // of them, so POTHOLE and MATERIAL (and any type added later) were billed
    // without being shown. Partitioned: every line lands in one bucket, the
    // unrecognised ones land in Other Charges, and the total below is the sum of
    // what rendered.
    const _parts = partitionInvoiceLines(lines, _inv);
    const empLines = _parts.EMPLOYEE;
    const equipLines = _parts.EQUIPMENT;
    const downLines = _parts.DOWNTIME;
    const standbyLines = _parts.STANDBY;
    const permitLines = _parts.PERMIT;
    const fieldItemLines = _parts.FIELD_ITEM;
    const miscLines = _parts.MISC;
    const _totals = invoiceTicketTotals(ticket, lines, _parts);
    const ticketTotal = _totals.total;
    _ticketTotals.set(ticket.id, _totals);
    if (_totals.drifted) {
      _reconcile.push({
        label: ticket.ticketNo ? '#' + ticket.ticketNo : String(ticket.id || '').slice(0, 8),
        total: _totals.total, storedShown: _totals.storedShown,
      });
    }
    grandTotal += ticketTotal;

    const workDate = ticket.workDate
      ? new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + (ticket.workDateEnd && ticket.workDateEnd !== ticket.workDate ? ' – ' + new Date(ticket.workDateEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '')
      : '';
    const jobName = ticket.jobName || ticket.jobTitle || '';
    const job = ticket.jobId ? BillingState.jobs.find(j => j.id === ticket.jobId) : null;
    // TASK-9390 — per TICKET section, so a grouped invoice spanning jobs of
    // different types prints each ticket's own type (never one guessed type).
    const _grpJobType = _ticketHeaderJobType(ticket, 'invoice');

    ticketSections += `
      <div class="ticket-section-page">
      <div class="ticket-divider">
        Ticket ${ticket.ticketNo ? '#' + invoiceEsc(ticket.ticketNo) : ''} ${workDate ? '&mdash; ' + workDate : ''}
      </div>
      ${(jobName || ticket.poNumber || ticket.vendorNumber || ticket.wellName || ticket.afeNumber || _grpJobType) ? `
        <div style="margin-bottom:12px;display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;font-size:13px;">
          ${jobName ? `<div><span style="color:#64748B;">Job:</span> <strong>${invoiceEsc(jobName)}</strong></div>` : ''}${_grpJobType ? `<div><span style="color:#64748B;">Type:</span> ${invoiceEsc(_grpJobType)}</div>` : ''}
          ${ticket.location || job?.location || job?.address ? `<div><span style="color:#64748B;">Location:</span> ${invoiceEsc(ticket.location || job?.location || job?.address)}</div>` : ''}
          ${ticket.poNumber ? `<div><span style="color:#64748B;">PO #:</span> ${invoiceEsc(ticket.poNumber)}</div>` : ''}
          ${ticket.vendorNumber ? `<div><span style="color:#64748B;">Vendor #:</span> ${invoiceEsc(ticket.vendorNumber)}</div>` : ''}
          ${ticket.wellName ? `<div><span style="color:#64748B;">Well:</span> ${invoiceEsc(ticket.wellName)}${ticket.wellNumber ? ' #' + invoiceEsc(ticket.wellNumber) : ''}</div>` : ''}
          ${ticket.afeNumber ? `<div><span style="color:#64748B;">AFE / CC:</span> ${invoiceEsc(ticket.afeNumber)}</div>` : ''}
        </div>
      ` : ''}
      ${buildOrderedSections({
        notes: buildWorkPerformedHTML(ticket, lines),
        labor: buildLaborTableHTML(empLines, getInvoiceColumnConfig(ticket.customerId, 'labor')),
        equipment: buildEquipSubTables(equipLines, ticket.customerId),
        downtime: buildDowntimeTableHTML(downLines, getInvoiceColumnConfig(ticket.customerId, 'downtime')),
        standby: buildStandbyTableHTML(standbyLines, getInvoiceColumnConfig(ticket.customerId, 'standby')),
        permit: buildPermitTableHTML(permitLines),
        fieldItems: buildFieldItemsTableHTML(fieldItemLines),
        misc: buildMiscTableHTML(miscLines),
        fuelSurcharge: buildFuelSurchargeTableHTML(_parts.FUEL_SURCHARGE), // TASK-9440
        // TASK-4819 — the two missing sections. Not added to
        // DEFAULT_SECTION_ORDER so nothing already on the page moves.
        lineInfo: buildLineInfoTableHTML(_parts.POTHOLE),
        otherCharges: buildOtherChargesTableHTML(_parts.OTHER)
      })}
      ${(() => {
        const _gpDocs = (ticket.documents || []).filter(d => d.printWithInvoice);
        if (_gpDocs.length === 0) return '';
        let _gh = '<div style="margin-top:12px;"><div style="font-size:12px;font-weight:700;color:#0F172A;margin-bottom:6px;">Attached Documents</div><div style="display:flex;flex-wrap:wrap;gap:8px;">';
        for (const d of _gpDocs) {
          if (d.isPdf || d.mimeType === 'application/pdf') {
            _gh += '<div style="width:80px;padding:8px;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;text-align:center;"><div style="font-size:24px;color:#EF4444;">&#128196;</div><div style="font-size:0.6rem;color:#0F172A;margin-top:2px;word-break:break-all;">' + invoiceEsc(d.name || 'PDF') + '</div></div>';
          } else {
            _gh += _ticketDocPrintGridImg(d, 80, '<img src="' + invoiceSafeUrl(d.fullUrl || d.thumbUrl) + '" style="width:80px;height:80px;object-fit:cover;border-radius:6px;border:1px solid #E2E8F0;" alt="' + invoiceEsc(d.name || 'Document') + '">');
          }
        }
        _gh += '</div></div>';
        return _gh;
      })()}
      ${ticket.taxEnabled ? `
      <div style="margin-top:12px;padding:10px 16px;background:#F8FAFC;border-radius:4px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
          <span style="font-size:13px;color:#475569;">Subtotal</span>
          <span style="font-size:14px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(_totals.subtotal)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
          <span style="font-size:13px;color:#475569;">Tax (${(ticket.taxRate || 0).toFixed(2)}%)</span>
          <span style="font-size:14px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(ticket.taxAmount || 0)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;border-top:1px solid #E2E8F0;padding-top:4px;">
          <span style="font-size:14px;font-weight:600;color:#475569;">Ticket Total</span>
          <span style="font-size:16px;font-weight:700;color:#0F172A;">${invoiceFmtMoney(ticketTotal)}</span>
        </div>
      </div>` : `
      <div style="margin-top:12px;padding:10px 16px;background:#F8FAFC;border-radius:4px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:14px;font-weight:600;color:#475569;">Ticket Subtotal</span>
        <span style="font-size:16px;font-weight:700;color:#0F172A;">${invoiceFmtMoney(ticketTotal)}</span>
      </div>`}
      </div>
    `;
    _ticketIdx++;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice ${invoiceEsc(invoiceNumber)} - ${invoiceEsc(companyName)}</title>
  <style>${invoiceStylesHTML()}</style>
</head>
<body>
  <div class="no-print" style="text-align:center;margin-bottom:16px;display:flex;justify-content:center;align-items:center;gap:16px;flex-wrap:wrap;">
    <button onclick="window.print()" style="padding:10px 24px;background:#3B82F6;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Print Invoice
    </button>
    <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;font-weight:600;color:#0F172A;">
      <!-- TASK-1346 sweep — narrow boolean, case (b); see the single-invoice page. -->
      <input type="checkbox" id="invoice-mark-invoiced" ${ticketData.every(d => d.ticket.invoiced) ? 'checked' : ''} style="width:18px;height:18px;cursor:pointer;">
      Mark as Invoiced
    </label>
    <button id="invoice-save-btn" onclick="handleInvoiceSave()" style="padding:10px 24px;background:#2E7D32;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Save
    </button>
    <button onclick="exportGroupedIIF()" style="padding:10px 24px;background:#0F172A;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Export QB (.iif)
    </button>
    <button onclick="window.close()" style="padding:10px 24px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
      Close
    </button>
  </div>
  ${invoiceSaveScriptHTML(ticketData.map(d => d.ticket.id))}
  <script>
  function exportGroupedIIF() {
    if (window.opener && window.opener.BillingModule) {
      var mod = window.opener.BillingModule;
      mod.clearTicketSelection();
      var ids = ${invoiceJson(ticketData.map(d => d.ticket.id))};
      ids.forEach(function(id) { mod.toggleTicketSelect(id); });
      mod.exportToIIF();
    } else {
      alert('Could not export — parent window closed. Use the Export QB button from the main billing list.');
    }
  }
  </script>
  ${invoiceReconcileNoticeHTML(_reconcile)}<div class="invoice-page">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;">
      <div style="display:flex;align-items:flex-start;gap:12px;">
        ${logoUrl ? `<img src="${invoiceSafeUrl(logoUrl)}" style="max-height:70px;max-width:200px;flex-shrink:0;" alt="Logo">` : ''}
        <div>
          <div style="font-size:18px;font-weight:800;color:#0F172A;">${invoiceEsc(companyName)}</div>
          ${companyAddress ? `<div style="color:#475569;font-size:13px;margin-top:2px;">${invoiceEsc(companyAddress)}</div>` : ''}
          ${companyPhone ? `<div style="color:#475569;font-size:13px;">${invoiceEsc(companyPhone)}</div>` : ''}
          ${companyEmail ? `<div style="color:#475569;font-size:13px;">${invoiceEsc(companyEmail)}</div>` : ''}
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:28px;font-weight:800;color:#3B82F6;letter-spacing:1px;">INVOICE</div>
        <div style="font-size:16px;font-weight:600;color:#0F172A;margin-top:4px;">${invoiceEsc(invoiceNumber)}</div>
        <div style="color:#475569;margin-top:4px;">Date: ${invoiceDate}</div>
        <div style="color:#475569;">${ticketData.length} tickets</div>
      </div>
    </div>

    <div style="margin-bottom:24px;padding:16px;background:#F8FAFC;border-radius:6px;">
      <div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Bill To</div>
      <div style="font-size:16px;font-weight:700;color:#0F172A;">${invoiceEsc(customer?.name || ticketData[0]?.ticket?.customerName || '-')}</div>
      ${customer?.address ? `<div style="color:#475569;margin-top:2px;">${invoiceEsc(customer.address)}</div>` : ''}
      ${customer?.phone ? `<div style="color:#475569;">${invoiceEsc(customer.phone)}</div>` : ''}
      ${customer?.email ? `<div style="color:#475569;">${invoiceEsc(customer.email)}</div>` : ''}${projectName ? `<div style="color:#475569;margin-top:6px;"><span style="color:#64748B;">Project:</span> ${invoiceEsc(projectName)}</div>` : ''}
    </div>

    <!-- Ticket summary table -->
    <table style="margin-bottom:16px;">
      <thead><tr>
        <th>Ticket</th><th>Date</th><th>Job</th><th class="right">Amount</th>
      </tr></thead>
      <tbody>
      ${ticketData.map(({ ticket }) => {
        // TASK-4819 — reuse the number the detail page actually printed instead
        // of recomputing it here. This was a duplicate of the per-ticket
        // expression above, so the summary row and the detail page could drift
        // apart the moment either one changed. Falls back to 0 only if the loop
        // above never ran for this ticket, which cannot happen — same source array.
        const tTotal = _ticketTotals.get(ticket.id)?.total ?? 0;
        const tDate = ticket.workDate ? new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
        return `<tr>
          <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${ticket.ticketNo ? '#' + invoiceEsc(ticket.ticketNo) : '-'}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${tDate}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(ticket.jobName || ticket.jobTitle || '-')}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(tTotal)}</td>
        </tr>`;
      }).join('')}
      </tbody>
    </table>

    ${(() => {
      const totalTax = ticketData.reduce((s, { ticket }) => s + (ticket.taxEnabled ? (ticket.taxAmount || 0) : 0), 0);
      const anyTax = ticketData.some(({ ticket }) => ticket.taxEnabled);
      if (anyTax) {
        const totalSubtotal = grandTotal - totalTax;
        return `<div style="padding:16px;background:#F8FAFC;border-radius:6px;margin-bottom:8px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <span style="font-size:14px;color:#475569;">Combined Subtotal</span>
            <span style="font-size:16px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(totalSubtotal)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="font-size:14px;color:#475569;">Total Tax</span>
            <span style="font-size:16px;font-weight:600;color:#0F172A;">${invoiceFmtMoney(totalTax)}</span>
          </div>
        </div>`;
      }
      return '';
    })()}

    <div class="invoice-footer">
      <div style="padding:16px;background:#0F172A;border-radius:6px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:18px;font-weight:700;color:#fff;">Grand Total</span>
        <span style="font-size:24px;font-weight:800;color:#fff;">${invoiceFmtMoney(grandTotal)}</span>
      </div>
      <div style="margin-top:8px;text-align:center;color:#64748B;font-size:11px;">
        Thank you for your business — <strong style="color:#475569;">${invoiceEsc(companyName)}</strong>
      </div>
    </div>
  </div>

  ${ticketSections}
</body>
</html>`;
}

// ============================================
// CRUD OPERATIONS
// ============================================
async function showAddTicket() {
  // TASK-10043 S1 — a new ticket starts from the board. This path used to make
  // a BLANK ticket with no job and no customer (the AR/AP "New Ticket" button),
  // which is how one day's work ended up on two jobs (TITS J-1289 / J-1282,
  // #100923). Now: pick the dispatched job day (search by customer, date, crew,
  // truck), or put the work on the board with a reason; then the EXISTING
  // job-day flow takes over — showCalendarJobTickets: opens the day's ticket if
  // it has one, else the twin-ticket warning + words check + "Create Ticket"
  // (generateTicketFromJob → createTicketChecked's per-day dedup). No new
  // ticket writer.
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const pick = await _pickJobDay({ heading: 'New ticket — which dispatched job?', dateKey: todayKey, purpose: 'ticket' });
  if (!pick || !pick.jobId || !pick.dateKey) return;
  if (!(BillingState.jobs || []).some(j => j.id === pick.jobId)) {
    window.showToast?.('That job is not loaded in AR/AP yet — try again in a moment.', 'warning');
    return;
  }
  showCalendarJobTickets(pick.jobId, pick.dateKey);
}

function editTicket(id) {
  // Consolidated: all editing goes through the Review Ticket modal
  openReviewTicket(id);
  return;

  // --- Legacy code below (unreachable, kept for reference) ---
  const ticket = BillingState.tickets.find(t => t.id === id);
  if (!ticket) return;

  if (ticket.status === 'archived' || ticket.status === 'locked' || ticket.status === 'sent') {
    viewTicket(id);
    return;
  }

  BillingState.selectedTicket = ticket;

  document.getElementById('modal-ticket-title').textContent = 'Edit Ticket';
  document.getElementById('ticket-id').value = id;
  document.getElementById('ticket-job-id').value = ticket.jobId || '';
  document.getElementById('ticket-date').value = formatDateInput(ticket.date);
  document.getElementById('ticket-status').value = ticket.status || 'draft';
  document.getElementById('ticket-customer').value = ticket.customerId || '';
  document.getElementById('ticket-po').value = ticket.poNumber || '';
  document.getElementById('ticket-vendor-number').value = ticket.vendorNumber || '';
  document.getElementById('ticket-well-name').value = ticket.wellName || '';
  document.getElementById('ticket-well-number').value = ticket.wellNumber || '';
  document.getElementById('ticket-location').value = ticket.location || '';
  document.getElementById('ticket-work').value = ticket.workPerformed || '';

  updateCustomerDropdown();

  const employeesContainer = document.getElementById('ticket-employees');
  employeesContainer.innerHTML = '';
  if (ticket.employees && ticket.employees.length > 0) {
    ticket.employees.forEach(emp => addEmployeeRow(emp));
  } else {
    addEmployeeRow();
  }

  const timeContainer = document.getElementById('ticket-time-entries');
  timeContainer.innerHTML = '';
  if (ticket.timeEntries && ticket.timeEntries.length > 0) {
    ticket.timeEntries.forEach(entry => addTimeEntry(entry));
  } else {
    addTimeEntry();
  }

  const materialsContainer = document.getElementById('ticket-materials');
  materialsContainer.innerHTML = '';
  if (ticket.materials && ticket.materials.length > 0) {
    ticket.materials.forEach(mat => addMaterialRow(mat));
  }

  document.getElementById('modal-ticket').classList.remove('hidden');
}

async function saveTicket(e) {
  e.preventDefault();
  if (_ticketContentBlocked()) return; // TASK-9491
  if (!window.validateRequiredFields(e.target)) return;

  const id = document.getElementById('ticket-id').value;
  const customerId = document.getElementById('ticket-customer').value;
  const customer = BillingState.customers.find(c => c.id === customerId);

  const employees = [];
  document.querySelectorAll('#ticket-employees .employee-row').forEach(row => {
    const personId = row.querySelector('.employee-select')?.value;
    const role = row.querySelector('.employee-role')?.value;
    if (personId) {
      const person = BillingState.people.find(p => p.id === personId);
      employees.push({
        personId,
        personName: person?.name || 'Unknown',
        role: role || 'NON_CDL'
      });
    }
  });

  const timeEntries = [];
  document.querySelectorAll('#ticket-time-entries .time-entry-row').forEach(row => {
    const timeFrom = row.querySelector('.time-from')?.value;
    const timeTo = row.querySelector('.time-to')?.value;
    const description = row.querySelector('.time-desc')?.value;
    if (timeFrom || timeTo || description) {
      timeEntries.push({ timeFrom, timeTo, description });
    }
  });

  const materials = [];
  document.querySelectorAll('#ticket-materials .material-row').forEach(row => {
    const name = row.querySelector('.material-name')?.value;
    const quantity = parseFloat(row.querySelector('.material-qty')?.value) || 0;
    const unit = row.querySelector('.material-unit')?.value;
    if (name) {
      materials.push({ name, quantity, unit });
    }
  });

  const jobId = document.getElementById('ticket-job-id')?.value || null;

  const data = {
    date: Timestamp.fromDate(new Date(document.getElementById('ticket-date').value)),
    status: document.getElementById('ticket-status').value,
    customerId: customerId || null,
    customerName: customer?.name || '',
    poNumber: poNormalizeForSave(document.getElementById('ticket-po').value),
    vendorNumber: document.getElementById('ticket-vendor-number').value.trim(),
    wellName: document.getElementById('ticket-well-name').value.trim(),
    wellNumber: document.getElementById('ticket-well-number').value.trim(),
    location: document.getElementById('ticket-location').value.trim(),
    workPerformed: document.getElementById('ticket-work').value.trim(),
    employees,
    timeEntries,
    materials,
    updatedAt: Timestamp.now()
  };

  // Link to source job if generated from one
  if (jobId) {
    data.jobId = jobId;
    const sourceJob = BillingState.jobs.find(j => j.id === jobId);
    if (sourceJob) data.jobName = sourceJob.title || '';
  }

  // TASK-1548 — the edit-ticket modal's Status select is a third road to 'ready'.
  // Same gate as reviewTicket/saveReviewTicket; fresh line read (this modal never
  // loads ticketLines). New tickets (no id) have no lines yet — nothing to check.
  if (id && data.status === 'ready') {
    const _orig = BillingState.tickets.find(t => t.id === id);
    if ((_orig?.status || 'draft') !== 'ready'
        && !(await _zeroHourGate(id, 'mark this ticket Ready'))) return;
    // TASK-1405 — same third road to 'ready', same unpriced-equipment gate.
    if ((_orig?.status || 'draft') !== 'ready'
        && !(await _unpricedGate(id, 'mark this ticket Ready'))) return;
    // TASK-1615-B — same third road to 'ready', same mileage gate.
    if ((_orig?.status || 'draft') !== 'ready'
        && !(await _routeMileageGate(id, 'mark this ticket Ready'))) return;
    // TASK-1572 Slices 3 + 3b — same third road. This modal WRITES the header
    // fields in this very save, so judge the ticket as it will be (the PO the
    // biller just typed counts), not as the fresh read last saw it.
    if ((_orig?.status || 'draft') !== 'ready'
        && !(await _ticketCompletenessGate(id, 'mark this ticket Ready', {
          customerId: data.customerId, customerName: data.customerName,
          poNumber: data.poNumber, vendorNumber: data.vendorNumber,
          wellName: data.wellName, wellNumber: data.wellNumber,
          location: data.location, workPerformed: data.workPerformed
        }))) return;
  }
  // TASK-1572 Slice 3b (COD Code Reviewer ❌) — a NEW hand-entered ticket can be
  // created straight into Ready from this modal. It has no lines yet (so the
  // billable arm has nothing to judge — same as the three gates above, which skip
  // new tickets), but its customer's required fields are right here on the form.
  if (!id && data.status === 'ready') {
    const _newMissing = ticketMissingRequiredFields(data, customer || null, []);
    if (_newMissing.length > 0) {
      window.showToast?.(`${customer?.name || 'This customer'} requires ${_newMissing.map(k => _TC_REQUIRED_FIELD_LABELS[k]).join(', ')} before a ticket can be Ready. Fill it in, or save as Draft.`, 'error');
      return;
    }
    // COD round 3 (Code Reviewer ❌): a new ticket has no lines, so for a customer
    // whose Price Book bills for work it has nothing to bill — it cannot be Ready
    // yet. Same derived rule as ticketHasNoBillableLine; fails closed on a read error.
    if (customerId && !_ticketCustomerIsInternalTime({ customerId })) {
      const _pb = await loadReviewContractRates(customerId);
      if (!_pb) {
        window.showToast?.('Couldn\'t check this customer\'s Price Book — save as Draft, or try again.', 'error');
        return;
      }
      if (priceBookBillsSomething(_pb.lineItems)) {
        window.showToast?.('A new ticket has no lines yet, so it has nothing to bill. Save it as Draft, add the work, then mark it Ready.', 'error');
        return;
      }
    }
  }

  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

    if (id) {
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), data);
      window.showToast?.('Ticket updated', 'success');
    } else {
      data.createdAt = Timestamp.now();
      data.createdBy = window.AppState?.user?.uid || 'unknown';
      // TASK-1339 — hand-entered tickets never got a number: getNextTicketNumber
      // had exactly one caller (generateTicketFromJob), so 29 of 744 TITS tickets
      // are numberless — invisible to every ticketNo-ordered list and report.
      // Same allocator as the job-generated path, so the sequence stays shared.
      // If allocation fails we do NOT create the ticket: an unnumbered ticket is
      // exactly the silent-money-loss state this fixes.
      data.ticketNo = await getNextTicketNumber(tenant);
      await addDoc(collection(db, 'tenants', tenant, 'tickets'), data);
      window.showToast?.(`Ticket #${data.ticketNo} created`, 'success');
    }

    closeModal('ticket');
  } catch (error) {
    console.error('[Billing] Error saving ticket:', error);
    window.logError?.(error, { module: 'billing', operation: 'SAVE_TICKET', ticketId: id || 'new' });
    window.showToast?.('Error saving ticket: ' + (error.message || 'Unknown error'), 'error');
  }
}

// ============================================
// TASK-10036 (f) — DELETING A TICKET THAT CARRIES HOURS NEEDS A REASON
// ============================================
// #100887 / #100886 were deleted from the list with no confirm and no reason,
// and the clock entries still point at the deleted tickets — those hours bill
// nowhere and nothing says why. Every client delete path (list + Review) now
// reads the clocked hours and the crew/equipment lines on the ticket, names
// them, and for a ticket that has any requires a typed reason, stored on the
// ticket (`deletedReason`) and in the DELETE audit row. A ticket with no hours
// gets a plain confirm. (The gateway's own soft-deletes are repoints and
// already carry a system reason; no connector tool deletes tickets.)
async function _ticketDeleteExposure(tenant, ticketId) {
  const out = { items: [], hoursTotal: 0, readFailed: false };
  try {
    const [linesSnap, ceSnap] = await Promise.all([
      getDocs(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines')),
      getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'), where('ticketId', '==', ticketId))),
    ]);
    const seenEntries = new Set();
    for (const d of linesSnap.docs) {
      const l = d.data() || {};
      if (l.type !== 'EMPLOYEE' && l.type !== 'EQUIPMENT') continue;
      const h = Number(l.hours) || 0;
      const label = l.type === 'EMPLOYEE' ? (l.personName || l.name || 'Crew member') : (getEquipmentDisplayName(l) || l.assetName || 'Equipment');
      if (l.type === 'EMPLOYEE' && l.clockEntryId) seenEntries.add(String(l.clockEntryId));
      out.items.push({ label, hours: h, type: l.type });
      out.hoursTotal += h;
    }
    for (const d of ceSnap.docs) {
      const e = d.data() || {};
      if (seenEntries.has(d.id) || e.status === 'deleted') continue;
      const h = Number(e.totalHours ?? e.hours) || 0;
      out.items.push({ label: `${e.personName || 'Crew member'} (time clock)`, hours: h, type: 'CLOCK' });
      out.hoursTotal += h;
    }
  } catch (err) {
    console.warn('[Billing] TASK-10036 delete exposure read failed:', err?.message || err);
    out.readFailed = true; // unknown = treat as carrying hours (ask for the reason)
  }
  return out;
}

// Resolves { reason, exposure } on Delete, or null on Cancel. `reason` is ''
// only when the ticket carries no hours (plain confirm).
async function _askTicketDeleteReason(ticket, tenant) {
  const exp = await _ticketDeleteExposure(tenant, ticket.id);
  const needsReason = exp.readFailed || exp.items.length > 0;
  const label = ticket.ticketNo ? `Ticket #${ticket.ticketNo}` : 'this ticket';
  if (!needsReason) {
    const ok = await window.showConfirm?.('Delete Ticket', `Delete ${label}? It has no crew or equipment hours. You can undo right after.`,
      { confirmText: 'Delete', cancelText: 'Cancel', danger: true });
    return ok ? { reason: '', exposure: exp } : null;
  }
  return new Promise((resolve) => {
    document.getElementById('ticket-delete-reason-modal')?.remove();
    const modal = document.createElement('div');
    modal.id = 'ticket-delete-reason-modal';
    modal.className = 'modal';
    const list = exp.readFailed
      ? '<li>The hours on this ticket could not be read. Assume it carries hours.</li>'
      : exp.items.map(i => `<li>${escapeHtml(i.label)} — ${Math.round(i.hours * 100) / 100} h</li>`).join('');
    modal.innerHTML = `
      <div class="modal-content modal-small" style="padding:var(--spacing-xl);">
        <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);text-align:center;">
          <span class="material-icons" style="font-size:48px;color:#DC2626;">delete_forever</span>
          <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Delete ${escapeHtml(label)}?</h3>
        </div>
        <p style="color:var(--text-primary);margin:0 0 6px;line-height:1.5;">These hours are on this ticket. Once it is deleted they will not bill on any ticket unless you move them first:</p>
        <ul style="margin:0 0 var(--spacing-md) 18px;color:var(--text-primary);line-height:1.5;">${list}</ul>
        <div class="form-group" style="margin-bottom:var(--spacing-md);">
          <label for="ticket-delete-reason-input" style="font-weight:600;font-size:13px;">Why are you deleting it? (required)</label>
          <textarea id="ticket-delete-reason-input" class="form-control" rows="2" maxlength="300" placeholder="e.g. Duplicate of #100923, hours are billed there" style="resize:vertical;width:100%;"></textarea>
        </div>
        <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
          <button type="button" class="btn btn-secondary" style="flex:1;max-width:140px;" id="btn-cancel-ticket-delete">Cancel</button>
          <button type="button" class="btn" style="flex:1;max-width:180px;background:#DC2626;color:#fff;border:none;opacity:0.45;cursor:not-allowed;" id="btn-confirm-ticket-delete" disabled>Delete</button>
        </div>
      </div>`;
    const done = (v) => { modal.remove(); resolve(v); };
    modal.onclick = (e) => { if (e.target === modal) done(null); };
    document.body.appendChild(modal);
    modal.classList.remove('hidden');
    const input = document.getElementById('ticket-delete-reason-input');
    const go = document.getElementById('btn-confirm-ticket-delete');
    input.oninput = () => {
      go.disabled = input.value.trim().length < 3;
      go.style.opacity = go.disabled ? '0.45' : '1';
      go.style.cursor = go.disabled ? 'not-allowed' : 'pointer';
    };
    document.getElementById('btn-cancel-ticket-delete').onclick = () => done(null);
    go.onclick = () => {
      const reason = input.value.trim().replace(/\s+/g, ' ').slice(0, 300);
      if (reason.length < 3) return;
      // A client may never mint a server repoint marker (firestore.rules TASK-4577).
      done({ reason: /^repoint_/i.test(reason) ? `note: ${reason}` : reason, exposure: exp });
    };
    setTimeout(() => input.focus(), 50);
  });
}

async function deleteTicket(id) {
  if (_moneyBlocked()) return; // TASK-9491
  const ticket = BillingState.tickets.find(t => t.id === id);
  if (!ticket) return;

  // TASK-10036 (COD r2 Independent) — same books-integrity gate the Review
  // delete has always had: deleting a ticket carrying an invoice orphans the
  // invoice (and the locked-lifecycle rule would refuse the reason stamp).
  if (isTicketInvoiced(ticket)) {
    window.showToast?.('Cannot delete an invoiced ticket', 'error');
    return;
  }

  if (ticket.status === 'archived' || ticket.status === 'locked' || ticket.status === 'sent') {
    window.showToast?.('Cannot delete archived or sent tickets', 'error');
    return;
  }

  // Block deletion of tickets with active clock entries
  if (BillingState.activeTicketIds?.has(id)) {
    window.showToast?.('Cannot delete — someone is still clocked in on this ticket', 'error');
    return;
  }

  const prevStatus = ticket.status || null;
  // TASK-10036 (f) — confirm, naming the hours; a reason when there are any.
  const _delAsk = await _askTicketDeleteReason(ticket, BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId);
  if (!_delAsk) return;
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // TASK-1347 — stamp the actor on the DOC, not just the audit row.
    const _actor = _actorStamp();
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), {
      status: 'deleted',
      deletedAt: Timestamp.now(),
      deletedBy: _actor.uid,
      deletedByName: _actor.name,
      _prevStatus: prevStatus,
      ...(_delAsk.reason ? { deletedReason: _delAsk.reason } : {}), // TASK-10036 (f)
      updatedAt: Timestamp.now()
    });
    // TASK-1347 — every value coalesced: logAudit does an addDoc, and a single
    // undefined field value makes the WHOLE addDoc throw (the SDK is not
    // configured with ignoreUndefinedProperties). That throw is swallowed by
    // logAudit's own catch, so a ticket missing e.g. customerName silently lost
    // its DELETE row — the second half of the 30-of-96 coverage gap.
    window.logAudit?.('ticket', 'DELETE', {
      ticketId: id,
      ticketNo: ticket.ticketNo ?? null,
      prevStatus,
      jobName: ticket.jobName ?? null,
      customerName: ticket.customerName ?? null,
      // COD Code Reviewer ⚠️ — do NOT coalesce money to 0: a missing amount and a
      // real $0 ticket must stay distinguishable in an audit record. null = unknown.
      grandTotal: _auditAmount(ticket.grandTotal),
      // TASK-10036 (f) — why, and the hours that were on it.
      reason: _delAsk.reason || null,
      hoursOnTicket: _delAsk.exposure.items.map(i => ({ label: i.label, hours: i.hours, type: i.type })),
      hoursReadFailed: _delAsk.exposure.readFailed === true
    });
    window.showToast?.('Ticket deleted', 'success', {
      label: 'Undo',
      callback: () => undoDeleteTicket(id, prevStatus)
    });
  } catch (error) {
    console.error('[Billing] Error deleting ticket:', error);
    window.showToast?.('Error deleting ticket', 'error');
  }
}

async function undoDeleteTicket(id, prevStatus) {
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', id), {
      status: prevStatus || 'draft',
      deletedAt: null,
      deletedBy: null,      // TASK-1347 — a restored ticket is not deleted by anyone
      deletedByName: null,
      _prevStatus: null,
      deletedReason: null, // TASK-10036 — a restored ticket carries no delete reason
      updatedAt: Timestamp.now()
    });
    window.logAudit?.('ticket', 'RESTORE', { ticketId: id, restoredTo: prevStatus || 'draft' });
    window.showToast?.('Ticket restored', 'success');
  } catch (error) {
    console.error('[Billing] Error restoring ticket:', error);
    window.showToast?.('Error restoring ticket', 'error');
  }
}

// ============================================
// WORKFORCE (opens Clock module's workforce tab)
// ============================================
function openWorkforce() {
  if (window.navigateToView) {
    window.navigateToView('clock');
    setTimeout(() => {
      if (window.ClockModule?.switchTab) {
        window.ClockModule.switchTab('workforce');
      }
    }, 300);
  }
}

// ============================================
// DYNAMIC FORM SECTIONS
// ============================================
function addEmployeeRow(data = {}) {
  const container = document.getElementById('ticket-employees');
  const row = document.createElement('div');
  row.className = 'employee-row form-row';
  row.innerHTML = `
    <div class="form-group">
      <select class="employee-select">
        <option value="">Select employee...</option>
        ${BillingState.people.map(p =>
          `<option value="${p.id}" ${data.personId === p.id ? 'selected' : ''}>${escapeHtml(p.name)}</option>`
        ).join('')}
      </select>
    </div>
    <div class="form-group">
      <select class="employee-role">
        <option value="CDL" ${data.role === 'CDL' ? 'selected' : ''}>CDL</option>
        <option value="NON_CDL" ${data.role === 'NON_CDL' || !data.role ? 'selected' : ''}>Non-CDL</option>
      </select>
    </div>
    <button type="button" class="btn-icon btn-remove" onclick="this.parentElement.remove()">
      <span class="material-icons">close</span>
    </button>
  `;
  container.appendChild(row);
}

function addTimeEntry(data = {}) {
  const container = document.getElementById('ticket-time-entries');
  const row = document.createElement('div');
  row.className = 'time-entry-row';
  row.innerHTML = `
    <div class="form-row">
      <div class="form-group form-group-sm">
        <input type="time" class="time-from" value="${data.timeFrom || ''}" placeholder="Start">
      </div>
      <div class="form-group form-group-sm">
        <input type="time" class="time-to" value="${data.timeTo || ''}" placeholder="End">
      </div>
      <div class="form-group flex-grow">
        <input type="text" class="time-desc" value="${escapeHtml(data.description || '')}" placeholder="Description">
      </div>
      <button type="button" class="btn-icon btn-remove" onclick="this.parentElement.parentElement.remove()">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  container.appendChild(row);
}

function addMaterialRow(data = {}) {
  const container = document.getElementById('ticket-materials');
  const row = document.createElement('div');
  row.className = 'material-row';
  row.innerHTML = `
    <div class="form-row">
      <div class="form-group flex-grow">
        <input type="text" class="material-name" value="${escapeHtml(data.name || '')}" placeholder="Material name">
      </div>
      <div class="form-group form-group-sm">
        <input type="number" class="material-qty" value="${data.quantity || ''}" placeholder="Qty" min="0" step="0.1">
      </div>
      <div class="form-group form-group-sm">
        <input type="text" class="material-unit" value="${escapeHtml(data.unit || '')}" placeholder="Unit">
      </div>
      <button type="button" class="btn-icon btn-remove" onclick="this.parentElement.parentElement.remove()">
        <span class="material-icons">close</span>
      </button>
    </div>
  `;
  container.appendChild(row);
}

// ============================================
// MODAL HELPERS
// ============================================
function closeModal(type) {
  document.getElementById(`modal-${type}`)?.classList.add('hidden');
  if (type === 'ticket') {
    BillingState.selectedTicket = null;
  }
  // Snapshots that arrive while a billing modal is open are dropped by the
  // modal-open guard in renderBillingView, so the list goes stale (e.g. a
  // status saved in Review then closed through the re-opened view modal).
  // renderBillingView self-guards: it no-ops if another billing modal is
  // still visible or the billing view isn't mounted.
  renderBillingView();
}

// ============================================
// UTILITIES
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js); escapes the
// identical set (&<>"', '->&#39;). Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// HOTFIX (stored XSS) — escapeHtml is NOT a fix inside onclick="fn('${…}')": the
// parser decodes &#39; back to ' before the handler compiles. That position takes
// escJsAttr (sharedFormat.js, TASK-4838). Hoisted wrapper, window lookup deferred
// to call time (Pitfall #1 — never import sharedFormat).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// HOTFIX (stored XSS) — a URL from a stored doc goes into href/src only if it is
// https (blocks javascript:, data:, protocol-relative); callers still escape it.
function _safeHttpsUrl(u) {
  const s = String(u == null ? '' : u).trim();
  return /^https:\/\//i.test(s) ? s : '';
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDate(date) {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatDateInput(date) {
  if (!date) return '';
  // Handle Firestore Timestamps + guard against Invalid Dates so a bad/edited
  // ticket date (e.g. updateReviewDate stashes a Timestamp into ReviewState.ticket.date)
  // can't throw `RangeError: Invalid time value` and abort the whole review render.
  // Matches sibling formatters formatJobDate/formatTimestamp below.
  const d = (date && typeof date.toDate === 'function')
    ? date.toDate()
    : (date instanceof Date ? date : new Date(date));
  return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
}

function formatJobDate(val) {
  if (!val) return '-';
  if (typeof val === 'string') {
    // Already a string like "2026-02-10"
    const d = new Date(val + 'T00:00:00');
    return isNaN(d.getTime()) ? val : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = val?.toDate ? val.toDate() : new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(ts) {
  if (!ts) return '-';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ============================================
// TASK-9197 — OVERNIGHT-SAFE TICKET-LINE TIME CELLS
// ============================================
// `formatTimestamp` / `invoiceFmtTime` / the three local `fmtTime` closures all
// printed ONE timestamp, TIME-ONLY, and every ticket-line "From"/"To" column pair
// in this file was built from two independent calls to one of them. A line whose
// window crosses midnight therefore rendered a range that reads backwards —
// "10:45 PM | 2:19 AM" beside 4.00 hrs and a $940 amount — on ticket review, on
// the invoice, and on the printed field ticket.
//
// MEASURED on TITS 2026-09-14: 59 of the 2,456 ticketLines that carry BOTH times
// cross a local calendar day, on 33 tickets, 17 of which are already invoiced.
//
// A CELL cannot be fixed by looking at its own timestamp — the day offset only
// exists relative to the PARTNER end — so these two helpers take the LINE and
// thread the pair through the one shared day-offset implementation
// (`window.Fmt.shiftCells`, sharedFormat.js, which shares its calendar-day math
// with TASK-9189's `shiftSpan`). Reached through `window.*`, never a cross-module
// `?v=` import (Pitfall #1).
//
// When a line does NOT cross a day — 2,397 of 2,456 — the output is
// byte-identical to what the old formatters produced. Nothing else moves.
function lineFromHtml(line) {
  return window.Fmt.shiftCells(line && line.fromTime, line && line.toTime).fromHtml || '-';
}
function lineToHtml(line) {
  return window.Fmt.shiftCells(line && line.fromTime, line && line.toTime).toHtml || '-';
}

function truncate(str, maxLength) {
  if (!str) return '';
  return str.length > maxLength ? str.slice(0, maxLength) + '...' : str;
}

// ============================================
// DELETE / EDIT TICKET LINES
// ============================================
async function deleteTicketLine(ticketId, lineId) {
  if (_ticketContentBlocked()) return; // TASK-9491
  if (!confirm('Delete this line item?')) return;
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    // Snapshot the line before deleting for audit
    const lineSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId));
    const lineData = lineSnap.exists() ? lineSnap.data() : {};
    await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId));
    window.logAudit?.('ticket', 'DELETE_LINE', { ticketId, lineId, lineType: lineData.type, personName: lineData.personName, assetName: lineData.assetName, hours: lineData.hours, amount: lineData.lineAmount });
    window.showToast?.('Line deleted', 'success');
    // Refresh the ticket detail view
    viewTicket(ticketId);
  } catch (error) {
    console.error('[Billing] Error deleting ticket line:', error);
    window.showToast?.('Error deleting line', 'error');
  }
}

function editTicketLine(ticketId, lineId) {
  const line = BillingState.selectedTicketLines.find(l => l.id === lineId);
  if (!line) return;

  // Build edit modal HTML based on line type
  const fromTime = line.fromTime?.toDate?.() || (line.fromTime ? new Date(line.fromTime) : null);
  const toTime = line.toTime?.toDate?.() || (line.toTime ? new Date(line.toTime) : null);
  // clock-dst-all — COMPANY zone, keyed per box so an unchanged repeated-hour
  // time re-saves as the stored instant (saveTicketLineEdit reads the same keys).
  // TASK-9971: called even with no time, so an empty box forgets the last line's slot.
  const fromVal = window.TenantLocalTime.formatLocal(fromTime || null, { key: 'edit-line-from' });
  const toVal = window.TenantLocalTime.formatLocal(toTime || null, { key: 'edit-line-to' });

  let fieldsHtml = `
    <div class="form-group">
      <label>From</label>
      <input type="datetime-local" id="edit-line-from" class="form-control" value="${fromVal}">
    </div>
    <div class="form-group">
      <label>To</label>
      <input type="datetime-local" id="edit-line-to" class="form-control" value="${toVal}">
    </div>
    <div class="form-group">
      <label>Rate ($/hr)</label>
      <input type="number" id="edit-line-rate" class="form-control" step="0.01" value="${line.rate || 0}">
    </div>
  `;
  if (line.type === 'DOWNTIME' || line.type === 'STANDBY') {
    fieldsHtml += `
      <div class="form-group">
        <label>Notes</label>
        <input type="text" id="edit-line-notes" class="form-control" value="${escapeHtml(line.notes || '')}">
      </div>
    `;
  }

  // Use a simple modal overlay
  let modal = document.getElementById('modal-edit-ticketline');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-edit-ticketline';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <div class="modal-header">
        <h3><span class="material-icons">edit</span> Edit ${escapeHtml(line.type === 'EMPLOYEE' ? (line.personName || 'Employee') : line.type === 'EQUIPMENT' ? (line.assetName || 'Equipment') : (line.type || ''))} Entry</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-edit-ticketline').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">${fieldsHtml}</div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-edit-ticketline').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" onclick="window.BillingModule.saveTicketLineEdit('${window.escJsAttr(ticketId)}','${window.escJsAttr(lineId)}')">Save</button>
      </div>
    </div>
  `;
}

async function saveTicketLineEdit(ticketId, lineId) {
  if (_ticketContentBlocked()) return; // TASK-9491
  try {
    const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    const fromInput = document.getElementById('edit-line-from')?.value;
    const toInput = document.getElementById('edit-line-to')?.value;
    const rate = parseFloat(document.getElementById('edit-line-rate')?.value || '0');
    const notesEl = document.getElementById('edit-line-notes');

    if (!fromInput || !toInput) {
      window.showToast?.('From and To times are required', 'error');
      return;
    }

    // clock-dst-all — company zone; repeated-hour time asked (with hours),
    // nonexistent time / unreadable zone refused — nothing written.
    const _tlt = window.TenantLocalTime;
    const _res = await _tlt.resolveAll([
      { str: fromInput, key: 'edit-line-from', label: 'From', optional: false },
      { str: toInput, key: 'edit-line-to', label: 'To', optional: false },
    ], { describe: (it, ms) => {
      const isFrom = it.key === 'edit-line-from';
      const other = isFrom ? _tlt.peekMs(toInput, { key: 'edit-line-to' }) : _tlt.peekMs(fromInput, { key: 'edit-line-from' });
      const h = isFrom ? (other - ms) / 3600000 : (ms - other) / 3600000;
      return Number.isFinite(h) && h >= 0 ? ` — ${_tlt.hm(h)}` : '';
    } });
    if (!_res.ok) { window.showToast?.(_res.message, 'error'); return; }
    const fromTime = new Date(_res.ms[0]);
    const toTime = new Date(_res.ms[1]);
    const hours = Math.max(0, (toTime - fromTime) / 3600000);
    const lineAmount = hours * rate;

    const updateData = {
      fromTime: Timestamp.fromDate(fromTime),
      toTime: Timestamp.fromDate(toTime),
      hours: parseFloat(hours.toFixed(4)),
      rate,
      lineAmount: parseFloat(lineAmount.toFixed(2))
    };
    if (notesEl) updateData.notes = notesEl.value.trim();

    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId), updateData);
    // TASK-1406 — this door changed hours/rate/times with no trail. Non-blocking.
    try {
      const _old = BillingState.selectedTicketLines.find(l => l.id === lineId) || {};
      const _ch = _ticketLineAuditDiff(_lineAuditPick(_old), _lineAuditPick({ ..._old, ...updateData }));
      if (_ch) window.logAudit?.('ticket', 'ticket_line_edit', { type: 'ticket_line_edit', ticketId, lines: [{ lineId, type: _old.type || null, label: _old.personName || _old.assetName || _old.description || '', changes: _ch }], editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown' });
    } catch (_) { /* audit is best-effort */ }

    document.getElementById('modal-edit-ticketline')?.classList.add('hidden');
    window.showToast?.('Line updated', 'success');
    viewTicket(ticketId);
  } catch (error) {
    console.error('[Billing] Error updating ticket line:', error);
    window.showToast?.('Error saving changes', 'error');
  }
}

// ============================================
// REVIEW TICKET — DATA LOADING
// ============================================
async function loadReviewItemCodes() {
  if (ReviewState.itemCodes.length > 0) return ReviewState.itemCodes;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'itemCodes'));
    ReviewState.itemCodes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Billing] Error loading item codes:', err);
    ReviewState.itemCodes = [];
  }
  return ReviewState.itemCodes;
}

async function loadReviewEquipment() {
  if (ReviewState.trucks.length > 0 || ReviewState.trailers.length > 0 || ReviewState.assets.length > 0) return;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const [trucksSnap, trailersSnap, assetsSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'trucks'), where('active', '==', true))),
      getDocs(query(collection(db, 'tenants', tenant, 'trailers'), where('active', '==', true))),
      getDocs(query(collection(db, 'tenants', tenant, 'assets'), where('active', '==', true)))
    ]);
    ReviewState.trucks = trucksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ReviewState.trailers = trailersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ReviewState.assets = assetsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Billing] Error loading equipment:', err);
  }
}

async function loadReviewContractRates(customerId) {
  if (!customerId) return null;
  const cached = ReviewState.contractRateCache[customerId];
  if (cached && (Date.now() - cached.loadedAt) < 600000) {
    ReviewState.contractLineItems = cached.lineItems || [];
    return cached;
  }

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const contractsSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'),
        where('status', '==', 'Active')
      )
    );
    let lineItems = [];
    let permanentPoNumber = '';
    for (const cDoc of contractsSnap.docs) {
      const cData = cDoc.data();
      if (!permanentPoNumber && cData.permanentPo && cData.permanentPoNumber) {
        permanentPoNumber = cData.permanentPoNumber;
      }
      const linesSnap = await getDocs(
        collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', cDoc.id, 'lineItems')
      );
      lineItems.push(...linesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    ReviewState.contractLineItems = lineItems;
    const contractData = contractsSnap.docs[0]?.data() || {};
    const entry = { lineItems, contractData, permanentPoNumber, loadedAt: Date.now() };
    ReviewState.contractRateCache[customerId] = entry;
    return entry;
  } catch (err) {
    console.warn('[Billing] Error loading contract rates:', err);
    return null;
  }
}

// ===========================================================================
// TASK-1674 — EQUIPMENT rate matcher CORE. TRIPLICATED — KEEP BYTE-IDENTICAL.
// Twins: public/src/views/clock.js · public/src/views/billing.js ·
//        functions/src/services/ticketGateway.js  (search _equipRateMatchCore).
// A browser module cannot import from functions/, and cross-importing a
// `?v=`-busted client module mints a SECOND module instance (Pitfall #1), so
// three copies is the accepted shape here — same call the _resolveFleetRef
// twins make. tests/task1674-equipment-rate-match.unit.cjs extracts all three
// and fails if either the SOURCE TEXT or the fixture-table output diverges.
// Edit one → edit all three → run that test.
//
// WHY IT REPORTS HOW IT MATCHED. The three old copies returned only {rate,...}
// and threw away the reason, so a last-resort `.find()` over a description
// regex was indistinguishable from an exact price-book hit. Every TITS contract
// carries a "Winch Truck" @ $150 line, so any vehicle the price book did not
// cover — a $285 hydrovac, a semi, a rental vac — silently priced as a winch
// truck and then passed review looking fully priced, because the only existing
// badge fires on rate === 0. Read-only probe of live TITS, 2026-08-20: 166
// EQUIPMENT lines ($142,350 billed) resolve to a guess — 76 off the description
// regex, 7 off a seeded item code, 83 off an ambiguous appliesTo key.
// `guessed` is how a caller tells the two apart. Nothing here sets policy; it
// only reports what it did, and returns null rather than inventing a number.
//
// WHY candidateCount EXISTS. An appliesTo hit is not automatically trustworthy
// either: TITS binds TEN different line items to {kind:"truck",type:"trucking"}
// (Equipment $170 … SuperLoads+Jeep+Booster $235) and `.find()` returned
// whichever sorted first by Firestore doc ID — so a hand-added line with a
// random ID could silently become a customer's default truck rate. More than
// one candidate means the price book cannot answer the question. Say so.
// ===========================================================================
function _equipRateMatchCore(lineItems, itemCodes, subtype, vehicleType, assetName) {
  const lines = Array.isArray(lineItems) ? lineItems : [];
  const codes = (Array.isArray(itemCodes) ? itemCodes : []).filter((c) => c && c.billable !== false);
  const kind = subtype === "truck" ? "truck" : subtype === "trailer" ? "trailer" : "asset";
  const vt = String(vehicleType || "").trim();
  const usable = (l) => !!l && (l.active === false || l.rate != null);
  const finish = (via, line, code, count) => ({
    matchedVia: count > 1 ? via + "-ambiguous" : via,
    line: line || null,
    itemCode: code || null,
    matchedDescription: line
      ? (line.description || line.itemCode || "")
      : (code ? (code.description || code.name || code.code || "") : ""),
    candidateCount: count,
    guessed: count > 1 || via === "category-regex" || via === "item-code",
  });

  if (lines.length > 0) {
    // [1] v2 appliesTo — exact kind+type, then a kind-only line ("any truck").
    //     COUNTED, never `.find()`-ed: see the candidateCount note above.
    let cands = vt ? lines.filter((l) => l.appliesTo && l.appliesTo.kind === kind && l.appliesTo.type === vt) : [];
    let via = "appliesTo";
    if (cands.length === 0) {
      cands = lines.filter((l) => l.appliesTo && l.appliesTo.kind === kind && !l.appliesTo.type);
      via = "appliesTo-any";
    }
    if (cands.length > 0 && usable(cands[0])) return finish(via, cands[0], null, cands.length);

    // [2] The asset's own name inside a line description ("Pelican #13" → a
    //     line described "Pelican"). Specific enough to trust, so not a guess.
    const nameWords = String(assetName || "").toLowerCase().split(/[\s_/-]+/).filter((w) => w.length > 2);
    const nameCats = subtype === "truck"
      ? ["Trucks & Trailer", "Heavy Haul", "Hydrovac"]
      : (subtype === "trailer" ? [] : ["Equipment", "Other"]);
    if (nameWords.length > 0 && nameCats.length > 0) {
      const hit = lines.find((l) => nameCats.indexOf(l.category) >= 0 &&
        nameWords.some((w) => String(l.description || "").toLowerCase().includes(w)));
      if (usable(hit)) return finish("name", hit, null, 1);
    }

    // [3] Legacy (pre-appliesTo) contracts — by vehicle type, then by category.
    if (subtype === "truck") {
      if (vt) {
        const vtl = vt.toLowerCase();
        // ⚠️ THIS ARM IS THE HYDROVAC FIX. A tenant's own "hydrovac" type and
        // the built-in "vac" type are the same truck. billing.js had this
        // rescue; clock.js and the server did not — so a `vac`-typed hydrovac
        // fell to the winch-truck regex below and billed $150 instead of $285.
        let hit = /hydrovac|vac/i.test(vtl) ? lines.find((l) => l.category === "Hydrovac") : null;
        if (!hit) {
          hit = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
            String(l.description || "").toLowerCase().includes(vtl));
        }
        if (usable(hit)) return finish("vehicle-type", hit, null, 1);
      }
      const guess = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
        /truck|cab|sleeper|daycab|pickup|winch|bobtail|tanker|water/i.test(l.description || l.itemCode || ""));
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    } else if (subtype === "trailer") {
      const guess = lines.find((l) => (l.category === "Trucks & Trailer" || l.category === "Heavy Haul") &&
        /trailer|flatbed|stepdeck|lowboy|rgn|gooseneck|drop.?deck|hot.?shot/i.test(l.description || l.itemCode || ""));
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    } else {
      const guess = lines.find((l) => l.category === "Equipment" && l.rate != null);
      if (usable(guess)) return finish("category-regex", guess, null, 1);
    }
  }

  // [4] Seeded item-code defaults. TT-DAYCAB ships at $150 — the same wrong
  //     number the winch-truck regex lands on. Both roads are a guess.
  if (codes.length > 0) {
    const wanted = subtype === "truck"
      ? ["TT-DAYCAB", "TT-SLEEPER"]
      : (subtype === "trailer" ? ["TT-FLATBED", "TT-STEPDECK"] : ["EQ-MISC", "EQ-GENERATOR"]);
    const code = codes.find((c) => wanted.indexOf(c.code) >= 0);
    if (code && code.defaultRate > 0) return finish("item-code", null, code, 1);
  }

  // [5] Nothing in the price book covers this vehicle. REFUSE. The caller
  //     decides what to do with that, and every caller has to say so out loud.
  return null;
}

// TASK-1674 — twin of the same name in clock.js / billing.js / ticketGateway.js.
// KEEP BYTE-IDENTICAL (same test pins it). Turns a _equipRateMatchCore result
// into the {rate, rateUnit, ...} shape the three lookups have always returned,
// plus the provenance fields (`matchedVia` / `matchedDescription` /
// `candidateCount` / `rateGuessed`) that let a caller refuse to bill silently.
function _shapeEquipRateMatch(m) {
  if (!m || (!m.line && !m.itemCode)) return null;
  const isLine = !!m.line;
  const src = isLine ? m.line : m.itemCode;
  return {
    rate: isLine ? (src.active === false ? 0 : src.rate) : src.defaultRate,
    rateUnit: (isLine ? src.rateUnit : src.defaultRateUnit) || "Hourly",
    roundToHalfHour: src.roundToHalfHour === true,
    roundSetting: src.roundToHalfHour === true ? true : (src.roundToHalfHour === false ? false : null),
    includeOnInvoice: isLine ? src.includeOnInvoice !== false : true,
    revenueCategory: (isLine ? src.revenueCategory : "") || "",
    matchedVia: m.matchedVia,
    matchedDescription: m.matchedDescription,
    candidateCount: m.candidateCount,
    rateGuessed: m.guessed === true,
  };
}

function lookupReviewRate(customerId, type, subtype, assetName = '', vehicleType = '') {
  // Check contract line items first
  const cached = ReviewState.contractRateCache[customerId];
  const lineItems = cached?.lineItems || [];

  // TASK-1674 — EQUIPMENT runs the shared matcher (_equipRateMatchCore above),
  // byte-identical to the clock.js and ticketGateway.js copies. It returns null
  // rather than inventing a rate, and every result says HOW it matched so the
  // review banner can tell a price-book hit from a guess. EMPLOYEE is untouched
  // on purpose: a man-hour line carries no customer rate here
  // ([[feedback_man_hour_lines_never_carry_a_cost]]) and this task must not
  // move that number in either direction.
  if (type === 'EQUIPMENT') {
    const _eqMatch = _shapeEquipRateMatch(
      _equipRateMatchCore(lineItems, (ReviewState.itemCodes || []), subtype, vehicleType, assetName));
    if (_eqMatch) {
      // TASK-9489 — Always / Never on the line wins; an unset line or item code
      // follows the company switch. `roundFromCompanyDefault` lets a heal skip
      // a decision that came only from the switch (never on a locked ticket).
      _eqMatch.roundToHalfHour = _billingResolveEquipRound(_eqMatch.roundSetting, _billingCompanyRoundsEquipment());
      _eqMatch.roundFromCompanyDefault = _eqMatch.roundSetting == null && _eqMatch.roundToHalfHour === true;
      return _eqMatch;
    }
    // Nothing in the price book covers it. The tenant's own configured default
    // is the last legitimate answer (it is a deliberate setting, not a guess);
    // on a tenant that never set one it is 0, which the "unpriced equipment"
    // badge already catches. We do NOT fall back to a truckish line here.
    const _dr = getDefaultRates();
    const _drRate = subtype === 'truck' ? _dr.truck : (subtype === 'trailer' ? _dr.trailer : _dr.asset);
    // TASK-9489 — no price book at all (a brand-new customer): the company
    // switch decides. Shop never rounds.
    const _coRound = !!customerId && customerId !== '__shop__' && _billingCompanyRoundsEquipment();
    return {
      rate: _drRate, roundToHalfHour: _coRound, rateUnit: 'Hourly', revenueCategory: '', includeOnInvoice: true,
      matchedVia: 'tenant-default', matchedDescription: '', candidateCount: 0, rateGuessed: false,
      roundSetting: null, roundFromCompanyDefault: _coRound
    };
  }

  if (lineItems.length > 0) {
    let matched = null;

    if (type === 'EMPLOYEE' && subtype) {
      matched = lineItems.find(l => l.appliesTo?.kind === 'person' && l.appliesTo?.type === subtype);
      if (!matched) {
        matched = lineItems.find(l => l.appliesTo?.kind === 'person' && !l.appliesTo?.type);
      }
      if (matched) {
        if (matched.active === false) return { rate: 0, roundToHalfHour: false, rateUnit: matched.rateUnit || 'Hourly', revenueCategory: matched.revenueCategory || '', includeOnInvoice: matched.includeOnInvoice !== false };
        if (matched.rate != null) return { rate: matched.rate, roundToHalfHour: matched.roundToHalfHour === true, rateUnit: matched.rateUnit || 'Hourly', revenueCategory: matched.revenueCategory || '', includeOnInvoice: matched.includeOnInvoice !== false };
      }
      matched = null;
    }

    // Legacy matching (regex/category based)
    if (type === 'EMPLOYEE') {
      // Check if subtype is a crew type slug (new system)
      const crewItemCode = CREW_TYPE_ITEM_CODE_MAP[subtype];
      if (crewItemCode) {
        // Try matching by item code in contract line items
        matched = lineItems.find(l => l.category === 'Personnel' && (l.itemCode === crewItemCode || new RegExp(subtype.replace(/_/g, '.?'), 'i').test(l.description || '')));
        if (!matched) {
          // Fall through to CDL/NON_CDL matching based on crew type category
          if (['cdl_driver', 'foreman', 'operator'].includes(subtype)) {
            matched = lineItems.find(l => l.category === 'Personnel' && /foreman|operator|cdl|driver/i.test(l.description || l.itemCode || ''));
          } else {
            matched = lineItems.find(l => l.category === 'Personnel' && /laborer|swamper|helper|non.?cdl/i.test(l.description || l.itemCode || ''));
          }
        }
      } else if (subtype === 'CDL') {
        matched = lineItems.find(l => l.category === 'Personnel' && /foreman|operator|cdl|driver/i.test(l.description || l.itemCode || ''));
      } else {
        matched = lineItems.find(l => l.category === 'Personnel' && /laborer|swamper|helper|non.?cdl/i.test(l.description || l.itemCode || ''));
      }
    }
    // TASK-1674 — the EQUIPMENT arm that stood here (name → vehicleType →
    // last-resort description regex) moved into _equipRateMatchCore, which every
    // copy of this matcher now shares. Leaving a second copy behind is exactly
    // how clock.js and the server ended up WITHOUT the /hydrovac|vac/i rescue
    // that lived only here, and how a vac-typed hydrovac billed $150.
    if (matched) {
      if (matched.active === false) return { rate: 0, roundToHalfHour: false, rateUnit: matched.rateUnit || 'Hourly', revenueCategory: matched.revenueCategory || '', includeOnInvoice: matched.includeOnInvoice !== false };
      if (matched.rate != null) return { rate: matched.rate, roundToHalfHour: matched.roundToHalfHour === true, rateUnit: matched.rateUnit || 'Hourly', revenueCategory: matched.revenueCategory || '', includeOnInvoice: matched.includeOnInvoice !== false };
    }
  }

  // Check item code default rates (skip non-billable codes)
  const itemCodes = (ReviewState.itemCodes || []).filter(ic => ic.billable !== false);
  if (itemCodes.length > 0) {
    let matchedCode = null;
    if (type === 'EMPLOYEE') {
      // Try crew type item code first
      const crewCode = CREW_TYPE_ITEM_CODE_MAP[subtype];
      if (crewCode) {
        matchedCode = itemCodes.find(ic => ic.code === crewCode);
      }
      // Fall back to CDL/NON_CDL matching
      if (!matchedCode) {
        const isCdlType = subtype === 'CDL' || ['cdl_driver', 'foreman', 'operator'].includes(subtype);
        matchedCode = itemCodes.find(ic => isCdlType ? (ic.code === 'CREW-CDL' || ic.code === 'CREW-FOREMAN') : (ic.code === 'CREW-NONCDL' || ic.code === 'CREW-LABORER'));
      }
    }
    // (EQUIPMENT item codes are handled by _equipRateMatchCore — see above.)
    if (matchedCode?.defaultRate > 0) return { rate: matchedCode.defaultRate, roundToHalfHour: matchedCode.roundToHalfHour === true, rateUnit: matchedCode.defaultRateUnit || 'Hourly', includeOnInvoice: true };
  }

  // Fall back to tenant default rates
  const rates = getDefaultRates();
  if (type === 'EMPLOYEE') {
    const isCdlType = subtype === 'CDL' || ['cdl_driver', 'foreman', 'operator'].includes(subtype);
    return { rate: isCdlType ? rates.cdl : rates.nonCdl, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '', includeOnInvoice: true };
  }
  // (EQUIPMENT tenant defaults are returned from the early branch at the top.)
  return { rate: 0, roundToHalfHour: false, rateUnit: 'Hourly', revenueCategory: '', includeOnInvoice: true };
}

// ============================================
// REVIEW TICKET — OPEN
// ============================================
// TASK-1106 — PO fallback precedence, promoted to a LOCAL helper (do NOT
// cross-module import this into clock.js — cache-buster Pitfall #1; small
// logic is duplicated locally where needed instead). Precedence (TASK-4580):
// parent job PO (most specific — dispatcher set it for this specific job) →
// parent job's PROJECT standing PO (corridor-wide) → customer contract
// permanent PO (customer-wide default). Returns '' when nothing resolves or a
// needed collection isn't readable yet (refuse-don't-guess — the result
// persists).
function _resolveFallbackPoNumber(jobId, customerId) {
  // TASK-4828 (COD round 2, Code Reviewer) — every return below is a value that
  // openReviewTicket PERSISTS onto the live ticket doc, so this is a PO WRITE
  // path, not a read. It used to trim with String().trim(), which leaves NBSP
  // and zero-width edges intact and forks the resolved PO into a second value.
  // TASK-1364 — 🔑 before the jobs snapshot lands, BillingState.jobs is []. A
  // ticket WITH a parent job would then find no parentJobPo and fall through to
  // the customer-wide contract PO — which openReviewTicket PERSISTS onto the
  // live ticket doc. That's a wrong PO on a real ticket. Never reachable before
  // this task (the modal only opened from an already-loaded Billing view); the
  // clock deep link opens it moments after initBilling. If we can't see the
  // jobs yet we don't guess — the ticket keeps its blank PO and resolves on the
  // next open. Precedence itself is unchanged once jobs are loaded.
  // The `.some()` half matters because openTicketDeepLink prefetches the one
  // parent job it needs: having THAT job in hand is the real precondition, and
  // the listener not having landed yet is then irrelevant.
  if (jobId && !BillingState._jobsLoaded && !BillingState.jobs.some(j => j.id === jobId)) return '';
  const parentJob = jobId ? BillingState.jobs.find(j => j.id === jobId) : null;
  const parentJobPo = parentJob?.poNumber;
  if (poNormalizeForSave(parentJobPo)) return poNormalizeForSave(parentJobPo);

  // TASK-4580 — middle tier: the parent job's PROJECT standing PO (TASK-4613
  // stamps it onto the job at pick/link time, but jobs linked before 4613 — or
  // whose project got its PO after the job was created — arrive here with a
  // blank job PO). More specific than the customer-wide contract PO: field POs
  // name a corridor/project, never a customer (23 concurrent Loenbro POs).
  // 🔑 Same refusal discipline as the TASK-1364 jobs guard above: projects are
  // a DEFERRED one-shot read (loadBillingProjects, Projects view only), so on
  // the review path they are usually NOT loaded. If the job points at a project
  // we cannot see yet, we don't guess — falling through to the contract PO
  // would PERSIST the less-specific number onto the ticket (openReviewTicket's
  // updateDoc fires only while the stored poNumber is null, so a wrong
  // fall-through is one-shot and permanent). Kick off the load and resolve on
  // the next open instead.
  if (parentJob?.projectId) {
    // _projectsLoadFailed: a FAILED read also refuses (COD) — _projectsLoaded
    // goes true on failure for the UI's sake, but "authoritatively empty" and
    // "couldn't read" must not resolve the same way when the result persists.
    if (!BillingState._projectsLoaded || BillingState._projectsLoadFailed) {
      loadBillingProjects(); // fire-and-forget; guarded + idempotent
      return '';
    }
    const projPo = BillingState.projects.find(p => p.id === parentJob.projectId)?.poNumber;
    if (poNormalizeForSave(projPo)) return poNormalizeForSave(projPo);
  }

  const permPo = customerId
    ? ReviewState.contractRateCache[customerId]?.permanentPoNumber
    : null;
  return poNormalizeForSave(permPo);
}

// TASK-1232 (3) — Review-modal load watchdog. PRESENTATION ONLY: the fetches
// themselves are untouched. On 5G the "Loading ticket details..." spinner
// could sit for minutes with zero feedback; now a skeleton paints immediately,
// a "still loading" note appears after 5s, and after 20s a Retry/Close
// affordance replaces the skeleton. The in-flight load keeps running — if it
// lands late it still renders; a tapped Retry bumps _reviewLoadToken so the
// stale run aborts instead of double-mutating ReviewState.
let _reviewLoadToken = 0;
let _reviewLoadTimers = [];
function _clearReviewLoadWatchdog() {
  _reviewLoadTimers.forEach(t => clearTimeout(t));
  _reviewLoadTimers = [];
}
function _reviewLoadingHtml() {
  return `
    <div class="rvw-load-state">
      <div class="loading-state" style="padding:28px 20px 4px;">
        <div class="loading-spinner"></div>
        <p style="margin:0;">Loading ticket details...</p>
        <p class="rvw-load-still" id="review-load-still">Still loading &mdash; slow connection. Hang tight&hellip;</p>
      </div>
      <div class="rvw-skel" aria-hidden="true">
        <div class="rvw-skel-bar lg"></div>
        <div class="rvw-skel-bar" style="width:60%;"></div>
        <div class="rvw-skel-bar" style="width:85%;"></div>
        <div class="rvw-skel-bar lg"></div>
        <div class="rvw-skel-bar" style="width:70%;"></div>
        <div class="rvw-skel-bar" style="width:90%;"></div>
        <div class="rvw-skel-bar" style="width:55%;"></div>
      </div>
    </div>`;
}
function _reviewLoadErrorHtml(ticketId, title, sub) {
  const safeId = String(ticketId).replace(/[^A-Za-z0-9_-]/g, '');
  return `
    <div class="loading-state rvw-load-error" style="padding:40px 20px;">
      <span class="material-icons" style="font-size:44px;color:var(--action-warning);margin-bottom:8px;">wifi_off</span>
      <p style="color:var(--text-primary);font-weight:600;margin:0 0 4px;text-align:center;">${title}</p>
      <p style="color:var(--text-secondary);font-size:0.85rem;margin:0 0 16px;text-align:center;">${sub}</p>
      <div style="display:flex;gap:10px;">
        <button class="btn btn-secondary" style="min-height:44px;" onclick="window.BillingModule.closeReviewModal(true)">Close</button>
        <button class="btn btn-primary" style="min-height:44px;" onclick="window.BillingModule.openReviewTicket('${safeId}')">
          <span class="material-icons" style="font-size:18px;vertical-align:-4px;margin-right:4px;">refresh</span> Retry
        </button>
      </div>
    </div>`;
}
function _startReviewLoadWatchdog(ticketId) {
  _clearReviewLoadWatchdog();
  const token = _reviewLoadToken;
  _reviewLoadTimers.push(setTimeout(() => {
    if (token !== _reviewLoadToken) return;
    const el = document.getElementById('review-load-still');
    if (el) el.style.display = 'block';
  }, 5000));
  _reviewLoadTimers.push(setTimeout(() => {
    if (token !== _reviewLoadToken) return;
    const body = document.getElementById('review-ticket-body');
    // Only take over if the skeleton is still what's on screen
    if (!body || !body.querySelector('.rvw-load-state')) return;
    body.innerHTML = _reviewLoadErrorHtml(ticketId,
      'This is taking longer than it should',
      "Slow or dropped connection — the ticket hasn't loaded yet.");
  }, 20000));
}

// TASK-1364 — give the just-mounted view a beat to settle its listeners before
// opening the modal on top of it (the BI deep link below does the same with a
// flat 300ms). CORRECTNESS does not depend on this window: openTicketDeepLink
// fetches both the ticket and its parent job directly if the snapshots haven't
// landed. Short cap so a cold/slow tenant doesn't stare at Billing wondering
// whether the tap registered. Bails if the view was unmounted while waiting —
// opening a modal into a wiped #main-content is the exact bug this task fixes.
// TASK-9671 S2 — #/billing?dd_open=ticket&ticket=<id> (app.js parks it as
// AppState.pendingFocus, window.OpenLinks). takeHint consumes it once (stale /
// other company = null); a driver, link or PIN session drops it. Access
// re-check + open: the LAZY openLinkRecordDrain.js. Opens viewTicket (the read
// view), never the Review editor. TASK-9671 S3 — #/billing?dd_open=invoice&
// ticket=<id> (a ticket ON the invoice) opens the same read view; View Invoice
// there is the user's own tap (openInvoicePage is a popup).
function _drainBillingOpenHint() {
  const OL = window.OpenLinks;
  if (!OL || window.AppState?.pendingFocus?.view !== 'billing') return;
  const hint = OL.takeHint('billing');
  if (!hint) return;
  if (window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin) {
    window.showToast?.('Links from Claude open from the office login.', 'info');
    return;
  }
  const tenant = window.AppState?.tenant || null;
  const gen = _billingMountGen;
  const host = document.getElementById('modal-view-ticket');
  const still = () => gen === _billingMountGen && !!host && document.getElementById('modal-view-ticket') === host;
  const waitReady = async () => {
    _flushSecondarySubs();
    for (let i = 0; i < 30 && !BillingState._ticketsLoaded && still(); i++) await new Promise(r => setTimeout(r, 100));
  };
  const openTicket = async (t) => {
    const idx = BillingState.tickets.findIndex(x => x.id === t.id);
    if (idx < 0) BillingState.tickets.push(t); // out of the live window (TASK-857): viewTicket reads this array
    await viewTicket(t.id);
  };
  // TASK-9671 S3 — an invoice link opens the same read view of a ticket on that
  // invoice; its state (read from this company's ticket) picks the read-back.
  const invoiceState = (t) => (isBilledExternally(t) ? 'external' : (isTicketInvoiced(t) ? 'invoiced' : 'not_invoiced'));
  const open = hint.kind === 'invoice' ? 'openInvoiceHint' : 'openTicketHint';
  __ddChunkWrap(import('./openLinkRecordDrain.js?v=2'))
    .then((m) => m[open](hint, { tenant, still, waitReady, fetchTicket: _fetchTicketById, openTicket, invoiceState }))
    .catch((e) => { console.warn('[Billing] open link failed', e?.message); window.showToast?.('Could not open that ticket from the link. Find it in Billing.', 'error'); });
}

function _openTicketDeepLinkWhenReady(ticketId, startedAt) {
  const started = startedAt || Date.now();
  // TASK-1245 — this path is the one place that genuinely wants the jobs
  // snapshot EARLY, so don't make it sit out phase 2's normal trigger: bring
  // the jobs/jobDays/people/timeOff listeners up right now. Without this the
  // 3s budget below could expire before jobs even started loading on LTE.
  _flushSecondarySubs();
  setTimeout(() => {
    if (!document.getElementById('modal-review-ticket')) return; // view left
    if (!BillingState._jobsLoaded && Date.now() - started < 3000) {
      _openTicketDeepLinkWhenReady(ticketId, started);
      return;
    }
    openTicketDeepLink(ticketId);
  }, 100);
}

// TASK-1364 — open one ticket after arriving here from the Clock/Workforce
// receipt button. Owns the read-only-vs-editable decision (it used to live in
// clock.js, which had to guess at billing's lock semantics from across a module
// boundary) and seeds BillingState.tickets, because viewTicket() reads straight
// off that array and the live listener is bounded to a rolling window
// (TASK-857) — an out-of-window ticket would make the read-only path silently
// no-op exactly the way the button itself used to.
async function openTicketDeepLink(ticketId) {
  if (!ticketId) return;
  try {
    const ticket = await _fetchTicketById(ticketId);
    if (!ticket) { window.showToast?.('Ticket not found', 'error'); return; }
    const idx = BillingState.tickets.findIndex(t => t.id === ticketId);
    if (idx >= 0) BillingState.tickets[idx] = ticket;
    else BillingState.tickets.push(ticket);

    // TASK-1364 (COD Code Reviewer, round 4) — GUARANTEE the parent job is in
    // BillingState.jobs before opening. openReviewTicket() bridges that job's
    // permits + photos into ReviewState.documents and saveReviewTicket()
    // persists that array wholesale, so opening without the job and then
    // saving would strip the job's permits off the ticket. Waiting on the jobs
    // snapshot can't be the guarantee — a slow or failed listener still has to
    // open eventually — so fetch the ONE job we need directly. Also what makes
    // the PO precedence in _resolveFallbackPoNumber correct on this path.
    // Harmless duplication-wise: the listener replaces the whole array on its
    // next tick.
    if (ticket.jobId && !BillingState.jobs.some(j => j.id === ticket.jobId)) {
      try {
        const _t = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
        const jobSnap = await getDoc(doc(db, 'tenants', _t, 'jobs', ticket.jobId));
        if (jobSnap.exists()) {
          const jobData = { id: jobSnap.id, ...jobSnap.data() };
          const jIdx = BillingState.jobs.findIndex(j => j.id === jobData.id);
          if (jIdx >= 0) BillingState.jobs[jIdx] = jobData;
          else BillingState.jobs.push(jobData);
        }
      } catch (e) {
        console.warn('[Billing] TASK-1364 parent job prefetch failed:', e?.message || e);
        // Say so rather than opening a quietly-incomplete ticket. NOT forcing
        // read-only (COD Code Reviewer round 5 suggested it): nothing is lost by
        // saving here — permits already persisted onto the ticket come back via
        // ticket.documents, and ones that were never bridged still live on the
        // JOB, which owns them, and re-bridge on the next open. The failure mode
        // is "not added this time", not "removed".
        window.showToast?.("Couldn't load this ticket's job — attached job permits and photos may not show", 'warning');
      }
    }

    // Status list carried over verbatim from the old clock.js branch so the
    // fix doesn't quietly change which tickets open read-only. Deliberately
    // WIDER than isReviewReadOnly() (which is invoiced/archived/locked only) —
    // 'ready' and 'sent' tickets have been view-only from this entry point
    // since it shipped.
    const lockedStatuses = ['ready', 'invoiced', 'sent', 'archived'];
    if (lockedStatuses.includes(ticket.status) || ticket.invoiced) {
      await viewTicket(ticketId);
      window.showToast?.('Ticket is locked — marked as ' + (ticket.invoiced ? 'invoiced' : ticket.status), 'info');
      return;
    }
    await openReviewTicket(ticketId);
  } catch (err) {
    console.error('[Billing] TASK-1364 ticket deep link failed:', err);
    window.showToast?.('Could not open ticket', 'error');
  }
}

async function openReviewTicket(ticketId, opts) {
  // TASK-1245 (COD Code-Reviewer ⚠️) — the Review modal builds its per-line
  // employee <select>s straight off BillingState.people, and renderBillingView()
  // deliberately skips re-rendering while a modal is open. So a user who opens a
  // ticket before phase 2 has landed would get EMPTY employee dropdowns that
  // never self-heal until the modal is reopened. Bring phase 2 up now. (The
  // window existed before this task too — people attached at mount, so a tap
  // within the first few hundred ms hit the same hole — but deferring would
  // widen it to BILLING_SECONDARY_SUBS_MAX_DELAY_MS, so close it here.)
  _flushSecondarySubs();
  // TASK-9983 (COD diff r2) — pick up pay changes made while Billing stayed
  // open; the Review banner re-renders itself if this ticket gained a flag.
  _loadPayChanges(BillingState.tenantId);
  // TASK-1232 (3): paint the loading state + open the modal BEFORE the first
  // fetch — previously a hang inside _fetchTicketById left the tap looking
  // completely dead (no modal, no spinner).
  _reviewLoadToken++;
  const _loadToken = _reviewLoadToken;
  {
    const titleEl0 = document.getElementById('review-ticket-title');
    if (titleEl0) titleEl0.textContent = 'Review Ticket';
    const body0 = document.getElementById('review-ticket-body');
    if (body0) body0.innerHTML = _reviewLoadingHtml();
    document.getElementById('modal-review-ticket')?.classList.remove('hidden');
    _startReviewLoadWatchdog(ticketId);
  }

  // TASK-857: a just-created arbitrary-date ticket (addDayFromReview) or any
  // out-of-window ticket may not be in the bounded BillingState.tickets — fall
  // back to a direct fetch so the review modal still opens instead of silently
  // returning.
  // TASK-9735 — the after-save re-hydrate reads the ticket fresh from Firestore,
  // never the BillingState.tickets cache (patched from headerUpdate, not a read).
  const ticket = opts?.fresh === true ? await _fetchTicketFresh(ticketId) : await _fetchTicketById(ticketId);
  if (_loadToken !== _reviewLoadToken) return; // stale run (Retry/close superseded it)
  if (!ticket) {
    _clearReviewLoadWatchdog();
    const body0 = document.getElementById('review-ticket-body');
    if (body0) body0.innerHTML = _reviewLoadErrorHtml(ticketId,
      "Couldn't load this ticket",
      'It may not have synced yet, or the connection dropped.');
    return;
  }

  resetReviewState();
  ReviewState.ticketId = ticketId;
  ReviewState.ticket = { ...ticket };
  // Snapshot original values for audit trail on save
  ReviewState._originalSnapshot = {
    jobId: ticket.jobId, jobName: ticket.jobName || ticket.jobTitle,
    customerName: ticket.customerName, customerId: ticket.customerId,
    poNumber: ticket.poNumber, vendorNumber: ticket.vendorNumber,
    wellName: ticket.wellName, wellNumber: ticket.wellNumber,
    afeNumber: ticket.afeNumber, workDate: ticket.workDate,
    date: ticket.date, status: ticket.status,
    grandTotal: ticket.grandTotal, notes: ticket.notes,
    // TASK-846-B — deep clone (ReviewState.ticket is a shallow spread, so the
    // nested object is shared with the tickets cache); lets the undo toast
    // restore the driver-captured data verbatim after a count edit.
    customSectionData: ticket.customSectionData ? JSON.parse(JSON.stringify(ticket.customSectionData)) : undefined,
    // TASK-9735 — the custom-section poll compares against this. It was never
    // captured, so any old stamp ended the poll on its first tick. READ-ONLY:
    // the Undo revert strips it (a server-owned field, never written back).
    derivedLinesReconciledAt: ticket.derivedLinesReconciledAt ?? null
  };
  // TASK-10036 — the work-notes text the box OPENED with (the same expression
  // renderReviewTicket shows). A save writes notes only when the box moved away
  // from this; kept OUT of _originalSnapshot so the Undo revert never writes it.
  ReviewState._openNotesText = ticket.notes || ticket.workPerformed || '';

  // Load existing documents from ticket
  ReviewState.documents = (ticket.documents || []).map(d => ({ ...d }));
  // Hidden bridged job permits — ones the user explicitly removed from this ticket.
  // The job still owns the permit (dispatch + driver views see it); it just doesn't
  // re-appear on this ticket's docs every time the modal opens.
  ReviewState.hiddenJobPermitIds = Array.isArray(ticket.hiddenJobPermitIds) ? [...ticket.hiddenJobPermitIds] : [];

  // ══ TASK-9355 S1 — SELF-HEAL bridged job docs, then bridge the CURRENT job. ══
  //
  // The bridge below copies the parent job's permits[]/photos[] onto the ticket
  // as `job_permit` / `job_photo` documents, and Save persists those copies. It
  // only ever APPENDED: when a ticket's job was changed (review dropdown, bulk
  // move, drag, groupTicketsIntoProject) the OLD job's copies stayed and the new
  // job's landed on top — KSI #100754 showed 4 permits, 2 from Go Tong; Go
  // Daniel 7/03 printed another job's permit on a customer invoice.
  //
  // Rule (COD 2026-09-23, replaces a path-parse that would have dropped every
  // "Copy to New Job" permit): a bridged doc is STALE iff its jobPermitId /
  // jobPhotoId is not among the CURRENT job's permits[]/photos[] ids. A doc with
  // no id to judge by is left alone. When the ticket has NO job, every bridged
  // job doc is stale (nothing owns it); when the job is not in the cache we
  // cannot judge, so nothing is touched (and nothing is bridged — as before).
  //
  // PERSISTED on unlocked tickets (TASK-9246 pattern: repair, record, write,
  // never `dirty`) so a stale permit cannot print on an invoice just because the
  // biller looked and closed without saving. Locked / archived / invoiced: the
  // books are closed and firestore.rules refuse the write — display-only.
  const parentJob = ticket.jobId ? BillingState.jobs.find(j => j.id === ticket.jobId) : null;
  const _healJudgeJob = !ticket.jobId ? _NO_JOB_FOR_BRIDGE : (parentJob || null);
  let _staleBridged = [];
  if (_healJudgeJob) {
    const part = _partitionStaleBridgedDocs(ReviewState.documents, _healJudgeJob);
    if (part.dropped.length) {
      _staleBridged = part.dropped;
      ReviewState.documents = part.keep;
      // Keep the persisted-truth mirror honest so saveReviewTicket's
      // `originalDoc` lookups and the deletedDocIds cleanup judge the same list.
      ReviewState.ticket.documents = part.keep.map(d => ({ ...d }));
    }
  }
  if (_staleBridged.length) {
    const _canPersistHeal = !isReviewReadOnly(ticket) && ticket.status !== 'deleted';
    let _healPersisted = false;
    if (_canPersistHeal) {
      try {
        const _healTenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
        await updateDoc(doc(db, 'tenants', _healTenant, 'tickets', ticketId), {
          documents: ReviewState.ticket.documents,
          updatedAt: Timestamp.now()
        });
        _healPersisted = true;
      } catch (err) {
        // Non-fatal: the modal still shows the healed list; the biller's Save
        // (which writes `documents` from ReviewState) persists it.
        console.warn('[Billing] TASK-9355 bridged-doc heal write failed:', err?.message || err);
      }
    }
    if (_loadToken !== _reviewLoadToken) return; // stale run — a Retry/close superseded us
    // Reversible trail: WHICH copies came off, from which job, and whether the
    // write landed. Non-blocking — an audit failure never takes down a repair.
    try {
      await window.logAudit?.('ticket', 'ticket_bridged_docs_selfhealed', {
        type: 'ticket_bridged_docs_selfhealed',
        ticketId,
        ticketNumber: ticket.ticketNo || ticket.ticketNumber || '',
        currentJobId: ticket.jobId || null,
        dropped: _staleBridged.map(d => ({ id: d.id, source: d.source, name: d.name || '', jobPermitId: d.jobPermitId || null, jobPhotoId: d.jobPhotoId || null, fromJobId: d.jobId || null })),
        persisted: _healPersisted,
        openedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown'
      });
    } catch (ae) { console.warn('[Billing] TASK-9355 heal audit failed:', ae?.message || ae); }
    if (_loadToken !== _reviewLoadToken) return; // stale run
    window.showToast?.(_staleBridgedDocsToast(_staleBridged, _healPersisted, _canPersistHeal), _healPersisted ? 'info' : 'warning');
  }

  // Bridge the CURRENT job's permits + photos as documents (stamped with the
  // job they came from, so a future move can be judged without path-parsing).
  if (parentJob) _bridgeJobDocsInto(ReviewState.documents, parentJob, ReviewState.hiddenJobPermitIds);

  // Bridge clock-out photos: ticket.photos[] → documents with source 'clock_photo'
  if (ticket.photos?.length) {
    const existingPhotoUrls = new Set(ReviewState.documents.map(d => d.fullUrl));
    for (const photo of ticket.photos) {
      const photoUrl = photo.url || photo.fullUrl;
      if (photoUrl && !existingPhotoUrls.has(photoUrl)) {
        ReviewState.documents.push({
          id: `cp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
          fullUrl: photoUrl,
          thumbUrl: photo.thumbUrl || null,
          fullPath: null,
          thumbPath: null,
          name: photo.name || 'Clock-out Photo',
          mimeType: 'image/jpeg',
          isPdf: false,
          sizeBytes: 0,
          uploadedAt: photo.uploadedAt || null,
          source: 'clock_photo',
          jobPermitId: null,
          printWithInvoice: false,
          // TASK-9868 — the driver's stamp + type ride along when present
          // (old photos have neither and stay that way).
          ...(photo.uploadedBy && typeof photo.uploadedBy === 'object' ? { uploadedBy: { ...photo.uploadedBy } } : {}),
          ...(photo.uploadedVia ? { uploadedVia: photo.uploadedVia } : {}), // TASK-9958
          ...(photo.docType ? { docType: normalizeTicketDocType(photo.docType) } : {})
        });
      }
    }
  }

  // Close the view-ticket modal first
  document.getElementById('modal-view-ticket')?.classList.add('hidden');

  // Show review modal with loading (TASK-1232: skeleton, not a bare spinner)
  const titleEl = document.getElementById('review-ticket-title');
  if (titleEl) titleEl.textContent = ticket.ticketNo ? `Review Ticket #${ticket.ticketNo}` : 'Review Ticket';
  const body = document.getElementById('review-ticket-body');
  if (body) body.innerHTML = _reviewLoadingHtml();

  // Reset footer buttons (may be hidden from a previously-viewed invoiced ticket)
  const saveBtn = document.getElementById('btn-save-review');
  if (saveBtn) saveBtn.style.display = '';
  const footer = document.getElementById('review-ticket-footer');
  if (footer) {
    const delBtn = footer.querySelector('button[onclick*="deleteReview"]');
    if (delBtn) delBtn.style.display = '';
    footer.querySelectorAll('button').forEach(btn => { btn.disabled = false; btn.style.opacity = ''; btn.style.pointerEvents = ''; });
  }

  document.getElementById('modal-review-ticket')?.classList.remove('hidden');

  _reviewLinesLoadFailedFor = null; // TASK-9735
  // Load ticketLines (no compound orderBy — sort client-side to avoid needing composite index)
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  let ticketLines = [];
  try {
    const linesSnap = await getDocs(
      collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines')
    );
    ticketLines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    ticketLines.sort((a, b) => {
      if (a.type !== b.type) return (a.type || '').localeCompare(b.type || '');
      if (a.sortOrder !== undefined || b.sortOrder !== undefined) {
        return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
      }
      const aTime = a.fromTime?.toDate?.() || new Date(0);
      const bTime = b.fromTime?.toDate?.() || new Date(0);
      return aTime - bTime;
    });
  } catch (err) {
    console.warn('[Billing] Error loading ticketLines for review:', err);
    _reviewLinesLoadFailedFor = ticketId; // TASK-9735 — the after-save re-hydrate must not call this "Saved"
  }
  if (_loadToken !== _reviewLoadToken) return; // TASK-1232: stale run, a Retry took over

  // TASK-842-A — rental tickets: load this ticket's slice records so the equipment
  // grid can show each line's billed [start–end] span + a non-blocking "rental
  // still active" indicator. rentalSlices is the SERVER-ONLY watermark ledger
  // (firestore.rules: billing/admin read, no client writes) — strictly read-only
  // here; single-equality query, no composite index needed. Best-effort: a read
  // failure only degrades the span/still-out display, never blocks the review.
  ReviewState.rentalSlices = [];
  ReviewState._rentalSlicesFailed = false;
  if (ticket.rentalTicket === true) {
    try {
      const sliceSnap = await getDocs(query(
        collection(db, 'tenants', tenant, 'rentalSlices'),
        where('ticketId', '==', ticketId)
      ));
      if (_loadToken !== _reviewLoadToken) return; // stale run
      ReviewState.rentalSlices = sliceSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      // COD round 8 (Code Reviewer ❌): a superseded open whose read rejects must
      // not touch the ticket now on screen — check the token BEFORE mutating.
      if (_loadToken !== _reviewLoadToken) return; // stale run
      console.warn('[Billing] Could not load rental slices (span display degraded):', err);
      ReviewState._rentalSlicesFailed = true; // TASK-9258 — template check says "could not check"
    }
  }

  // Separate by type and assign local IDs
  for (const line of ticketLines) {
    line._localId = nextLocalId();
    // TASK-9412 — the times/hours as LOADED, so Save can tell which EMPLOYEE in/out
    // the biller actually moved (those go to the time clock) and which EQUIPMENT
    // lines she left alone (those may take the clock cascade). Underscore = never
    // persisted by the save loop.
    _stampReviewLineBaseline(line);
    switch (line.type) {
      case 'EMPLOYEE':
        ReviewState.employeeLines.push(line);
        break;
      case 'EQUIPMENT':
        ReviewState.equipmentLines.push(line);
        break;
      case 'PERMIT':
        line._baseCost = line.baseCost || line.lineAmount || 0;
        line._upchargePercent = line.upchargePercent || 0;
        // TASK-9436 — what the markup was when opened (null = never set), for the
        // save's audit row; and the raw unit fee the Cost column edits. Legacy
        // lines have no baseRate: an AI-parsed line has no rate/qty at all
        // (qty 1 × its fee); a hand-added one's rate IS the raw fee.
        line._loadedUpchargePercent = (line.upchargePercent === undefined || line.upchargePercent === null) ? null : (Number(line.upchargePercent) || 0);
        if (line.baseRate === undefined || line.baseRate === null) {
          const _q = Number(line.quantity) > 0 ? Number(line.quantity) : 1;
          const _r = Number(line.rate) || 0;
          line._displayBaseRate = (!line._upchargePercent && _r > 0 && Math.abs(Math.round(_q * _r * 100) / 100 - line._baseCost) < 0.005)
            ? _r
            : Math.round((line._baseCost / _q) * 100) / 100;
        }
        // A stored markup whose lineAmount was never marked up (the trap the
        // task names) heals here, so the row, the totals and the next save agree.
        if (line._upchargePercent) line.lineAmount = _permitMarkedUpAmount(line._baseCost, line._upchargePercent);
        ReviewState.permitLines.push(line);
        break;
      case 'DOWNTIME':
        ReviewState.downtimeLines.push(line);
        break;
      case 'STANDBY':
        ReviewState.standbyLines.push(line);
        break;
      case 'POTHOLE':
        ReviewState.potholeLines.push(line);
        break;
      case 'FIELD_ITEM':
        ReviewState.fieldItemLines.push(line);
        break;
      case 'MISC':
        ReviewState.miscLines.push(line);
        break;
      case 'FUEL_SURCHARGE':
        // TASK-9440 — its own bucket. The `default` below would have filed it
        // as an EMPLOYEE line, priced it at a labor rate and saved $0 over it.
        ReviewState.fuelSurchargeLines.push(line);
        break;
      default:
        // Other types go to employee as fallback
        ReviewState.employeeLines.push(line);
        break;
    }
  }

  // TASK-9355 S3 — a PERMIT line stamped with a jobId that is not this ticket's
  // job was priced from a job the ticket has since left (drag / bulk / the
  // callable cannot mark the row — they never load lines). Marked on EVERY
  // open so the Load Permits table shows it until a human rules on it. Display
  // only (underscore field, never persisted); never deleted.
  _flagPermitLinesFromOtherJob(ReviewState.permitLines, null, ticket.jobId || '');

  // Parallel load supporting data
  await Promise.all([
    loadReviewItemCodes(),
    loadReviewEquipment(),
    ticket.customerId ? loadReviewContractRates(ticket.customerId) : Promise.resolve()
  ]);
  if (_loadToken !== _reviewLoadToken) return; // TASK-1232: stale run, a Retry took over

  // TASK-1615-B — resolve this job's garbage-coordinate stops ONCE per open, from
  // a FRESH job read, so the Review banner and the Ready/invoice gates judge the
  // same route. Deliberately not BillingState.jobs: that is a deferred phase-2
  // listener, so a banner keyed on it can render "clean" purely because the
  // snapshot hasn't landed — a warning that is absent when you need it most.
  // Skipped entirely unless the ticket carries priced, invoiced per-mile money:
  // zero extra reads on the overwhelming majority of tickets. A failed read leaves
  // it null = NOT RESOLVED, which is never treated as clean; the fresh-read gate
  // on Ready/invoice fails CLOSED on the same error.
  ReviewState.routeBadStops = null;
  if (_rmHasPerMileMoney(ticketLines)) {
    try {
      ReviewState.routeBadStops = await _rmFetchJobBadStops(ticket.jobId);
    } catch (err) {
      console.warn('[Billing] Could not read the job route for the mileage check:', err);
    }
    if (_loadToken !== _reviewLoadToken) return; // stale run, a Retry took over
  }

  // TASK-4741 — resolve every EQUIPMENT ref on this ticket against the CURRENT
  // fleet, once per open, on the same contract as routeBadStops above: a read
  // that fails leaves status 'could_not_check', which is never treated as clean.
  // Deliberately the FULL roster, not ReviewState.trucks/trailers/assets — those
  // come from loadReviewEquipment's `active==true` query, and 12 live refs
  // (measured) point at vehicles that carry no `active` field at all. Judging
  // them against the active-only list would invent 12 false alarms.
  // Only the ROSTER is loaded here; renderReviewEquipmentSection runs the audit
  // itself on every render so it always judges the lines as they stand now.
  {
    await _reviewFullRoster();
    if (_loadToken !== _reviewLoadToken) return; // stale run, a Retry took over
  }

  // TASK-9258 — the job's jobDays, for the "quantity came from a template and
  // nobody confirmed it" check. Same contract as the two reads above: skipped
  // unless the ticket carries an asset or rental line (zero extra reads for every
  // truck-and-crew ticket), and a failed read is an envelope with loaded:false,
  // which the banner reports as "could not check", never as clean.
  // COD round 7 (Code Reviewer ❌): await into LOCALS, check the load token,
  // THEN assign. Assigning first let a slow, superseded open overwrite the
  // ticket actually on screen with another ticket's (or an empty) template
  // evidence — and the next quantity edit then erased its warning.
  ReviewState._tplDays = null;
  ReviewState._tplDeployTx = null;
  if ((ticket.jobId || ticket.originalJobId) && _ticketHasTemplateCandidateLines(ticketLines)) {
    const _tplDaysLocal = await _loadTemplateCheckJobDays([ticket.jobId, ticket.originalJobId]);
    if (_loadToken !== _reviewLoadToken) return; // stale run, a Retry took over
    // Rental tickets: the deploy ledger rows ARE the provenance (see header).
    let _tplTxLocal = null;
    if (ticket.rentalTicket === true && (ReviewState.rentalSlices || []).length > 0) {
      _tplTxLocal = await _loadTemplateDeployTx(ReviewState.rentalSlices);
      if (_loadToken !== _reviewLoadToken) return; // stale run, a Retry took over
    }
    ReviewState._tplDays = _tplDaysLocal;
    ReviewState._tplDeployTx = _tplTxLocal;
  }

  // Auto-populate PO if the ticket's PO is blank. Precedence (TASK-1106 helper
  // _resolveFallbackPoNumber): parent job PO (most specific — dispatcher set
  // this for this specific job) → project standing PO (TASK-4580 — the job's
  // project/corridor number) → customer contract permanent PO (customer-wide
  // default). Tickets created from dispatch_pin clock-outs before the dispatcher set a PO on
  // the job arrive here with poNumber:null; without this fallback they keep showing the
  // "Required for {customer}" warning even after the dispatcher fills in the PO upstream.
  if (!poNormalizeForSave(ReviewState.ticket.poNumber)) {
    const resolved = _resolveFallbackPoNumber(ticket.jobId, ticket.customerId);
    if (resolved) {
      ReviewState.ticket.poNumber = resolved;
      ReviewState.headerEdits.poNumber = resolved;
      // TASK-1106 — persist the reconciled PO onto the TICKET doc, not just
      // ReviewState: previously the fallback lived only in the review modal,
      // so the work-order/field-ticket the driver + customer see stayed blank
      // unless the biller happened to save. 🔑 GUARD: write ONLY when the
      // stored poNumber is null/undefined (clock-created-missing) — NEVER
      // when it's '' (billing manual edit saves a deliberately-cleared PO as
      // empty string; a human cleared it on purpose, don't clobber).
      // TASK-1176 — don't persist a reconciled PO onto a locked (invoiced/archived)
      // ticket; its books are closed and the rules deny the write.
      if ((ticket.poNumber === null || ticket.poNumber === undefined) && !isReviewReadOnly(ticket)) {
        try {
          const _tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
          await updateDoc(doc(db, 'tenants', _tenant, 'tickets', ticketId), {
            poNumber: resolved,
            updatedAt: Timestamp.now()
          });
          // Tickets audit to jobAuditLog (not the general auditLog).
          try {
            await addDoc(collection(db, 'tenants', _tenant, 'jobAuditLog'), {
              action: 'TICKET_PO_RECONCILED',
              source: 'billing-review',
              timestamp: Timestamp.now(),
              user: window.AppState?.user?.email || 'unknown',
              ticketId,
              ticketNo: ticket.ticketNo || null,
              jobId: ticket.jobId || null,
              customerName: ticket.customerName || null,
              details: { from: ticket.poNumber === undefined ? 'missing' : 'null', to: resolved }
            });
          } catch (ae) { console.warn('[Billing] PO-reconcile audit log failed:', ae); }
        } catch (err) {
          // Non-fatal — the review modal still opens with the resolved PO in
          // ReviewState; the biller's save will persist it.
          console.warn('[Billing] TASK-1106 PO reconcile write failed:', err?.message || err);
        }
      }
    }
  }

  // Resolve vehicleType from roster for equipment lines missing it (older tickets)
  const _resolvedVehicleTypes = new Set();
  for (const line of ReviewState.equipmentLines) {
    if (!line.vehicleType && line.assetId) {
      const found = ReviewState.trucks.find(t => t.id === line.assetId)
        || ReviewState.trailers.find(t => t.id === line.assetId)
        || ReviewState.assets.find(a => a.id === line.assetId);
      if (found) {
        line.vehicleType = found.type || '';
        _resolvedVehicleTypes.add(line._localId || line.id);
      }
    }
  }

  // Apply contract rates ONLY to lines that don't already have a saved rate.
  // Saved lines (with Firestore ID) keep their rates — the user may have manually adjusted them.
  // Exception: re-evaluate $0 rate lines whose vehicleType was just resolved from roster.
  if (ticket.customerId) {
    for (const line of ReviewState.employeeLines) {
      if (line.id && line.rate !== undefined) continue; // already saved with a rate
      const rateInfo = lookupReviewRate(ticket.customerId, 'EMPLOYEE', line.role);
      if (rateInfo) {
        line.rate = rateInfo.rate;
        line.lineAmount = parseFloat(((line.hours || 0) * rateInfo.rate).toFixed(2));
      }
    }
    for (const line of ReviewState.equipmentLines) {
      const wasJustResolved = _resolvedVehicleTypes.has(line._localId || line.id);
      // Re-evaluate if: new line (no id), OR saved line with $0 rate whose vehicleType we just resolved
      if (line.id && line.rate !== undefined && (line.rentalDirection === 'return' || ticket.returnEstimate === true || !(line.rate === 0 && wasJustResolved))) continue;   // TASK-10107 COD: return lines stay $0
      const rateInfo = lookupReviewRate(ticket.customerId, 'EQUIPMENT', line.subtype || line.equipmentType || 'truck', line.assetName || '', line.vehicleType || '');
      if (rateInfo) {
        line.rate = rateInfo.rate;
        line.rateUnit = rateInfo.rateUnit || line.rateUnit || 'Hourly';
        line.lineAmount = parseFloat(((line.hours || line.quantity || 0) * rateInfo.rate).toFixed(2));
      }
    }
  }

  // ══ TASK-9246 — SELF-HEAL ON OPEN: repair, record, PERSIST. Never `dirty`. ══
  //
  // The three heals below fix real bad data: a line that carries clock times but
  // `hours: 0` bills $0, so on #100694 that was Zaxary's 4.88 h and Lance's
  // 4.40 h going out unbilled. They stay.
  //
  // What changed is ATTRIBUTION. Each used to set `ReviewState.dirty = true`,
  // which is the flag `closeReviewModal` reads to ask "You have unsaved changes.
  // Discard them?" — so merely OPENING #100694 accused the biller of edits they
  // had not made. Worse, it trains people to click through a guard that exists
  // to protect real work, so the next time it fires for a genuine reason they
  // dismiss it out of habit. `dirty` now means one thing only: A HUMAN EDITED
  // SOMETHING. App repairs land in `ReviewState.systemHeals` instead.
  //
  // And because the repair is no longer riding on the biller's Save, it is
  // PERSISTED here — the same pattern viewTicket's heals have always used
  // (write the line, re-derive the ticket denorm, skip a locked ticket, never
  // let a failure take the modal down). That is what keeps the promise the old
  // prompt was accidentally keeping: a healed line cannot quietly snap back to
  // $0 the moment the modal closes.
  const _healFields = ['hours', 'lineAmount', 'roundToHalfHour', 'roundedFrom', 'zeroHoursOk'];
  const _healSnap = (line) => { const s = {}; for (const f of _healFields) s[f] = line[f]; return s; };
  const _recordHeal = (line, before, kind) => {
    const changed = {};
    for (const f of _healFields) {
      if (line[f] !== before[f] && line[f] !== undefined) changed[f] = line[f];
    }
    if (Object.keys(changed).length === 0) return; // heal ran, nothing actually moved
    ReviewState.systemHeals.push({
      lineId: line.id || null,
      type: line.type || '',
      label: line.type === 'EMPLOYEE'
        ? (getEmployeeDisplayName(line) || 'Crew line')
        : (getEquipmentDisplayName(line) || line.assetName || 'Equipment line'),
      kind,
      fromHours: Number(before.hours) || 0,
      toHours: Number(line.hours) || 0,
      // A rounding heal can stamp `roundToHalfHour` without moving the hours at
      // all. That still has to be WRITTEN, but the banner must not claim hours
      // were recalculated when they weren't — hence the separate flag.
      hoursChanged: (Number(before.hours) || 0) !== (Number(line.hours) || 0),
      changed
    });
  };

  // Self-heal: calculate hours from fromTime/toTime for equipment lines with 0
  // hours. TASK-1646 — routed through _recalcLineFromTimes (one copy of the
  // rounding-aware math); skipIfZero preserves the old rawHrs>0 no-op.
  for (const line of ReviewState.equipmentLines) {
    // 🔴 COD (Independent Code Reviewer + Edge Case Hunter) — `zeroHoursOk` is a
    // biller's RECORDED DECISION that this line really is non-billable
    // (TASK-1548's mark; `equipmentLineNeedsHours` above skips it for exactly
    // this reason). Healing it re-derives hours from the clock, and
    // `_recalcLineFromTimes` then CLEARS the mark — so before this guard, opening
    // Review would have silently revoked a deliberate "yes, this is free" and, now
    // that the heal persists, WRITTEN the reversal. The heal repairs data nobody
    // has ruled on; it does not overrule a human.
    if (line.zeroHoursOk === true) continue;
    // 2026-09-22 — a biller TYPED this number (see updateReviewEquipmentLine).
    // The heal exists to repair hours nobody has ruled on; a hand-entered 0 has
    // been ruled on. Without this the modal re-derived it from the clock and,
    // since TASK-9246, WROTE the reversal back.
    if (line.hoursManual === true) continue;
    if (line.fromTime && line.toTime && (!line.hours || line.hours === 0) && isTimeBasedUnit(line.rateUnit || 'Hourly')) {
      const _b = _healSnap(line);
      if (_recalcLineFromTimes(line, { skipIfZero: true })) _recordHeal(line, _b, 'hours-from-clock');
    }
  }

  // Same for employee lines with 0 hours but valid fromTime/toTime.
  // 💵 Rate is deliberately untouched: this shop bills the truck, never the
  // person, so a crew line legitimately ends up at hours × $0. The 0 HOURS are
  // the bug; the $0 amount is not.
  for (const line of ReviewState.employeeLines) {
    if (line.zeroHoursOk === true) continue; // same recorded-decision guard as above
    if (line.hoursManual === true) continue; // hand-entered hours are a decision, not bad data
    if (line.fromTime && line.toTime && (!line.hours || line.hours === 0)) {
      const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
      const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
      const rawHrs = Math.max(0, (to - from) / 3600000);
      if (rawHrs > 0) {
        const _b = _healSnap(line);
        line.hours = parseFloat(rawHrs.toFixed(2));
        line.lineAmount = parseFloat((line.hours * (line.rate || 0)).toFixed(2));
        _recordHeal(line, _b, 'hours-from-clock');
      }
    }
  }

  // Self-heal: apply half-hour rounding to equipment lines where contract says roundToHalfHour
  // but the ticket line was written before the flag was stamped (pre-v1.14.540)
  if (ticket.customerId) {
    for (const line of ReviewState.equipmentLines) {
      // 🔴 COD ROUND 2 (Edge Case Hunter + Independent Code Reviewer, both lanes,
      // and they were right): the guard belongs on ALL THREE doors, not the two
      // obvious ones. A marked-free line that ALSO has clock times and a contract
      // carrying roundToHalfHour fell through here and got hours + a positive
      // lineAmount written — and `_ticketLineBillable` folds lineAmount straight
      // into grandTotal, so write-on-open would have BILLED the line the biller
      // marked free. Round 1's browser test missed it because its marked-free
      // fixture had no rounding contract behind it.
      if (line.zeroHoursOk === true) continue;
      // 2026-09-22 — THIRD DOOR, same guard, for the same reason COD ROUND 2
      // insisted zeroHoursOk cover all three: this heal does not just stamp the
      // flag, it REPLACES line.hours with the clock-derived rounded value. A
      // biller who typed 3.7 h (or 0) on a line whose contract rounds would have
      // had it overwritten on the next open and the overwrite persisted.
      if (line.hoursManual === true) continue;
      if (!line.fromTime || !line.toTime) continue;
      if (line.roundToHalfHour === true) continue; // already flagged, already rounded
      // Check if contract line item has roundToHalfHour
      const rateInfo = lookupReviewRate(ticket.customerId, 'EQUIPMENT', line.assetType || 'truck', line.assetName || '', line.vehicleType || '');
      // TASK-9489 (COD round 1) — same rule as viewTicket: the company switch
      // alone never re-rounds an existing line on load.
      if (rateInfo?.roundToHalfHour && !rateInfo.roundFromCompanyDefault) {
        const _b = _healSnap(line);
        line.roundToHalfHour = true;
        const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
        const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
        const rawHrs = Math.max(0, (to - from) / 3600000);
        const rounded = roundHalfHour(rawHrs);
        if (line.hours !== rounded) {
          line.hours = rounded;
          line.lineAmount = parseFloat((rounded * (line.rate || 0)).toFixed(2));
        }
        _recordHeal(line, _b, 'half-hour-rounding');
      }
    }
  }

  // TASK-9412 (COD round 2, Edge Case Hunter) — re-baseline AFTER the load-time
  // heals: a healed 0-hour line is not a biller edit, so the clock cascade
  // mirror must still treat it as untouched. (Safe whether or not the heal
  // persists below: an unpersisted heal leaves server hours that fail the
  // gateway's own hours match, so that line is never in its cascade report.)
  for (const _l of [...ReviewState.employeeLines, ...ReviewState.equipmentLines]) _stampReviewLineBaseline(_l);
  // TASK-1406 — the line-audit baseline, also AFTER the heals (a heal is not the biller's edit).
  for (const _l of [...ReviewState.employeeLines, ...ReviewState.equipmentLines, ...ReviewState.permitLines, ...ReviewState.downtimeLines, ...ReviewState.standbyLines, ...ReviewState.miscLines]) if (_l?.id && !_l._isNew) _l._auditBase = _lineAuditPick(_l);
  // Release 1.15.1971 integration COD r2 — the stale-edit guard compares against
  // the CLOCK ENTRY as it stood when this Review opened (a ticket line can
  // legitimately differ from its entry). Background load; see _loadReviewClockBaselines.
  _loadReviewClockBaselines(_loadToken);
  // TASK-9755 (#100852) — who the CLOCK says is on this ticket. Background
  // load; when it lands, the employee section re-renders with the "clocked to
  // this ticket but not on it" banner if anyone is missing.
  ReviewState.crewRemovedEntryIds = Array.isArray(ticket.crewRemovedEntryIds) ? ticket.crewRemovedEntryIds.map(String) : [];
  _loadReviewTicketClockEntries(ticketId, _loadToken, ticket).then(() => {
    if (_loadToken !== _reviewLoadToken || ReviewState.ticketId !== ticketId) return;
    if (_reviewMissingCrew().length > 0 || _reviewRuledOffCrew().length > 0 || _reviewPlanVsRan().length > 0) _rerenderReviewEmployeeSection(); // TASK-9763 S2 adds plan vs ran
    // TASK-9264 — a shift on this ticket carries a crew "Materials Used" report: load the review banner.
    if ((ReviewState.ticketClockEntries || []).some(e => e && e.consumablesReported)) _ensureMaterialsUi().then((m) => { if (_loadToken === _reviewLoadToken) { m.reset(); _rerenderEquipSection(); } }).catch((e) => console.warn('[Billing] materials review unavailable:', e?.message));
    // TASK-9866 — the crew's hours TODAY on a sister job of the same customer
    // (ZL 10/14: both clock entries sat on the 5 PM job while the ticket was
    // the 8 AM one). Runs after the ticket's own entries landed so the
    // "clocked here but not on this job's crew" mirror reads the same list.
    _loadReviewSisterHours(ticketId, _loadToken, ticket).then(() => {
      if (_loadToken !== _reviewLoadToken || ReviewState.ticketId !== ticketId) return;
      if ((ReviewState.sisterHours || []).length > 0 || (ReviewState.offCrewClocked || []).length > 0) _rerenderReviewEmployeeSection();
    });
  });

  // TASK-9246 — persist what the heals just repaired. Mirrors viewTicket's
  // long-standing heal writes: per-line updateDoc, then the ticket denorm
  // (totalHours / grandTotal / subtotal / taxAmount) re-derived from the SAME
  // line array the heals mutated, so the list card, BI and the invoice can't
  // disagree with what the modal is showing.
  let _healOutcome = null;   // null = persisted (or nothing to persist)
  let _healHeader = null;
  let _healErrMsg = null;    // TASK-9251 — carried to the telemetry row below
  if (ReviewState.systemHeals.length > 0) {
    if (isReviewReadOnly(ticket)) {
      // Locked / archived / invoiced: the books are closed and firestore.rules
      // denies the write. The repair stays display-only and the banner says so
      // in those words — it does NOT become a prompt, because there is nothing
      // the biller could do about it from here.
      _healOutcome = 'locked';
    } else {
      try {
        const _healTenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
        // 🔴 COD (Architect + Code Reviewer + Edge Case Hunter) — ONE writeBatch,
        // not N independent updateDocs. Two reasons, both real:
        //   • An EQUIPMENT line can be healed TWICE (0-hour derivation, then the
        //     rounding stamp), so two writes could land on the same lineId in
        //     whatever order the network chose — leaving Firestore on the
        //     unrounded hours while the header below was summed from the rounded
        //     ones. Merging per lineId is what makes the last word deterministic.
        //   • Lines succeeding while the header write fails leaves a stale total
        //     that a REOPEN can never retry, because the second open finds
        //     nothing left to heal. All-or-nothing removes that state entirely.
        const _healByLine = new Map();
        for (const h of ReviewState.systemHeals) {
          if (!h.lineId) continue;
          _healByLine.set(h.lineId, { ...(_healByLine.get(h.lineId) || {}), ...h.changed });
        }
        // Denorm. `ticketLines` holds the same objects the heals mutated (the
        // ReviewState.*Lines arrays were push()ed by reference), so this sums
        // the healed numbers, not the stale ones. Same shared helper the invoice
        // and viewTicket's TASK-1666 heal use — never a local copy.
        const _healTot = computeTicketTotalsFromLines(ticketLines, ticket);
        _healHeader = {};
        const _healEmpHours = parseFloat(ticketLines
          .filter(l => l.type === 'EMPLOYEE')
          .reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0).toFixed(2));
        if ((ticket.totalHours || 0) !== _healEmpHours) _healHeader.totalHours = _healEmpHours;
        if (Math.abs((ticket.grandTotal || 0) - _healTot.grandTotal) > 0.01) _healHeader.grandTotal = _healTot.grandTotal;
        if (ticket.taxEnabled) {
          if (Math.abs((ticket.subtotal || 0) - _healTot.subtotal) > 0.01) _healHeader.subtotal = parseFloat(_healTot.subtotal.toFixed(2));
          if (Math.abs((ticket.taxAmount || 0) - _healTot.taxAmount) > 0.01) _healHeader.taxAmount = _healTot.taxAmount;
        }
        // 🔴 COD ROUND 2 (Code Reviewer) — snapshot the audit payload BEFORE the
        // await. Reading `ReviewState.systemHeals` after the commit lets a ticket
        // switch during that await write ticket B's repairs into an audit row
        // stamped with ticket A's id — a trail that lies is worse than none.
        const _healAuditLines = ReviewState.systemHeals.map(h => ({
          lineId: h.lineId, type: h.type, label: h.label, kind: h.kind,
          fromHours: h.fromHours, toHours: h.toHours
        }));
        const _healBatch = writeBatch(db);
        for (const [_lineId, _fields] of _healByLine) {
          _healBatch.update(doc(db, 'tenants', _healTenant, 'tickets', ticketId, 'ticketLines', _lineId), _fields);
        }
        if (Object.keys(_healHeader).length > 0) {
          _healBatch.update(doc(db, 'tenants', _healTenant, 'tickets', ticketId), { ..._healHeader, updatedAt: Timestamp.now() });
        } else {
          _healHeader = null;
        }
        await _healBatch.commit();
        // Reversible trail: WHO opened it, WHICH lines moved, from what to what.
        // Non-blocking — an audit failure must never take down a repair.
        try {
          await window.logAudit?.('ticket', 'ticket_hours_selfhealed', {
            type: 'ticket_hours_selfhealed',
            ticketId,
            ticketNumber: ticket.ticketNo || ticket.ticketNumber || '',
            lines: _healAuditLines,
            openedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown'
          });
        } catch (ae) { console.warn('[Billing] TASK-9246 heal audit failed:', ae?.message || ae); }
      } catch (err) {
        // The ONLY branch that still interrupts the close — these lines are
        // sitting on the biller's screen at their corrected hours while the
        // stored ticket still bills them at $0, and nobody has been told.
        _healOutcome = 'error';
        _healHeader = null;
        _healErrMsg = String(err?.code || err?.message || err || '').slice(0, 200);
        console.warn('[Billing] TASK-9246 heal persist failed:', err?.message || err);
      }
    }
  }
  // 🔴 COD (Edge Case Hunter) — the stale check comes BEFORE anything above is
  // committed to ReviewState. Writing `healUnpersisted` first meant ticket A's
  // failed heal could arm the "$0" close prompt on ticket B, which the user had
  // already opened. Everything the persist learned is held in locals until here.
  if (_loadToken !== _reviewLoadToken) return; // stale run — a Retry/close superseded us
  ReviewState.healUnpersisted = _healOutcome;
  // ══ TASK-9251 TELEMETRY — the app repaired billable hours it could not store ══
  //
  // The SUCCESS path already writes `ticket_hours_selfhealed` (category
  // 'ticket'), so this is deliberately the failure twin and nothing else. When
  // `_healOutcome` is set, the biller is looking at corrected hours on screen
  // while Firestore still holds the $0 line — screen and database disagree
  // about money, and until now the only record of it was a console.warn on a
  // machine nobody can read. 'locked' means the books were already closed, so
  // the ticket will invoice at the wrong number permanently unless someone
  // reopens it; 'error' means the batch write itself failed.
  //
  // Placed AFTER the stale-run guard on purpose: a superseded load must not
  // stamp ticket A's failure onto a row read as ticket B's.
  // PII: ids, counts and hours only — no crew or equipment NAMES (the `label`
  // the sibling audit row carries is a person's name; it is not repeated here).
  //
  // 🔴 COD (Independent Code Reviewer) — ONCE per ticket per page, not per open.
  // 'locked' is a property of the TICKET, not of this open: an invoiced ticket
  // with a 0-hour clocked line re-heals display-only on every single open,
  // forever. Measured read-only on live TITS: 102 of 894 tickets are in exactly
  // that state, so an un-deduped row would fire every time a biller browsed an
  // old invoice and would push `invoice_email_failed` straight off the 100-row
  // Telemetry pane — the precise failure mode this task exists to avoid.
  if (_healOutcome) {
    try {
      const _hs = ReviewState.systemHeals || [];
      window.Telemetry?.logUserActionOnce(`${ticketId}:${_healOutcome}`, 'ticket_heal_unpersisted', {
        ticketId,
        ticketNo: ticket.ticketNo || ticket.ticketNumber || null,
        outcome: _healOutcome,                         // 'locked' | 'error'
        healCount: _hs.length,
        lineIds: _hs.map(h => h.lineId).filter(Boolean).slice(0, 20),
        kinds: [...new Set(_hs.map(h => h.kind).filter(Boolean))],
        hoursDelta: parseFloat(_hs.reduce((s, h) => s + ((Number(h.toHours) || 0) - (Number(h.fromHours) || 0)), 0).toFixed(2)),
        ticketStatus: ticket.status || null,
        errorMessage: _healErrMsg ? (window.Telemetry?.scrubPii?.(_healErrMsg) || null) : null,
      });
    } catch (_) { /* a diagnostic must never become a second failure */ }
  }
  if (_healHeader) {
    Object.assign(ticket, _healHeader);
    Object.assign(ReviewState.ticket, _healHeader);
  }

  // Init tax state: ticket fields → tenant settings → off
  const _tenantTaxSettings = window.AppState?.settings || {};
  if (ticket.taxEnabled !== undefined) {
    ReviewState.taxEnabled = ticket.taxEnabled;
    ReviewState.taxRate = ticket.taxRate || 0;
    ReviewState.taxCategories = ticket.taxCategories || { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true };
  } else if (_tenantTaxSettings.taxEnabled) {
    ReviewState.taxEnabled = true;
    ReviewState.taxRate = _tenantTaxSettings.defaultTaxRate || 0;
    ReviewState.taxCategories = _tenantTaxSettings.defaultTaxCategories || { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true };
  }

  // Auto-populate workStartTime / workStopTime from billable line items if blank
  if (!ticket.workStartTime && !ticket.workStopTime) {
    const _billableLines = [...ReviewState.employeeLines, ...ReviewState.equipmentLines];
    let _earliest = null, _latest = null;
    for (const l of _billableLines) {
      const ft = l.fromTime?.toDate ? l.fromTime.toDate() : (l.fromTime ? new Date(l.fromTime) : null);
      const tt = l.toTime?.toDate ? l.toTime.toDate() : (l.toTime ? new Date(l.toTime) : null);
      if (ft && !isNaN(ft.getTime()) && (!_earliest || ft < _earliest)) _earliest = ft;
      if (tt && !isNaN(tt.getTime()) && (!_latest || tt > _latest)) _latest = tt;
    }
    if (_earliest) {
      const ts = Timestamp.fromDate(_earliest);
      ReviewState.headerEdits.workStartTime = ts;
      ReviewState.ticket.workStartTime = ts;
    }
    if (_latest) {
      const ts = Timestamp.fromDate(_latest);
      ReviewState.headerEdits.workStopTime = ts;
      ReviewState.ticket.workStopTime = ts;
    }
  }

  // Load Ship Slips (if enabled for tenant) — non-fatal on failure
  if (window.AppState?.settings?.enableShipSlips === true) {
    try {
      ReviewState.shipSlips = await slipGetForTicket(BillingState.tenantId, ticketId);
    } catch (err) {
      console.warn('[Billing] Failed to load ship slips:', err);
      ReviewState.shipSlips = [];
    }
  }

  if (_loadToken !== _reviewLoadToken) return; // TASK-1232: stale run, a Retry took over
  // TASK-1291 — render INSIDE try/catch. Previously the watchdog was cleared and
  // then renderReviewTicket() ran unguarded; a render throw (e.g. the undefined
  // equip-group anchor deref) became an unhandled rejection AFTER the watchdog was
  // gone, freezing the modal on the skeleton forever with no Retry/Close. Now a
  // render failure repaints the recovery affordance and is logged.
  try {
    renderReviewTicket();
    _clearReviewLoadWatchdog();
  } catch (err) {
    _clearReviewLoadWatchdog();
    window.logError?.(err, { module: 'billing', operation: 'RENDER_REVIEW_TICKET', ticketId });
    const _body = document.getElementById('review-ticket-body');
    if (_body) _body.innerHTML = _reviewLoadErrorHtml(ticketId,
      "Couldn't display this ticket",
      'Something went wrong rendering it. Tap Retry, or close and let support know.');
  }
}

// ============================================
// REVIEW TICKET — RENDER
// ============================================
// TASK-566 — 🔒 "Lock to driver (read-only)" row in the Review Ticket modal.
// Renders the current lock state + toggle button. Kept in a helper so
// toggleDriverEditLock can surgically re-render just this row (a full
// renderReviewTicket() would risk dropping in-progress line edits).
// ============================================
// TASK-4662 — THE FIELD-TICKET SEND LOG + ITS DERIVED STATUS
// ============================================
// Origin, and it moved once: Chris relayed Lacey wanting to email the TICKET
// rather than an invoice — "let me look at the ticket, then send it." Then the
// sharper version of the same complaint: she ALREADY emails tickets today, by
// printing to PDF and attaching in Gmail, and "she is having a hard time
// keeping track of what went out and what didn't."
//
// THE RECORD IS THE PRODUCT HERE; THE SEND BUTTON IS THE CONVENIENCE. Built in
// that order.
//
// A boolean could not carry her actual day — "emailed 8/28, customer said they
// never got it, re-sent 9/1 to accounting@" — so the record is an APPEND-ONLY
// SERIES in `tickets/{id}/ticketSendLog`, deliberately the same shape as the
// §391.23 good-faith contact-attempt log (TASK-1374, `peiAttempts`): rows never
// edited in place, time and actor stamped SERVER-side, a constrained `channel`
// enum, a required free-text note, and the SINGULAR derived fields left on the
// parent ticket doc.
//
// 🔴 EVERYTHING ON A LIST ROW READS THE DERIVED PARENT FIELDS AND NOTHING ELSE.
// One tenant has 945 tickets; a per-row series fetch is an N+1 that would crawl
// and would cost a read per ticket. That is the entire reason the derived
// fields exist. `_ticketDeliveryState()` below touches only fields that live on
// the ticket doc. The series is fetched when someone opens ONE ticket.
//
// WHAT THIS IS NOT: not a 6th ticket status, and not coupled to Ready. Status is
// load-bearing across this module (combine requires all 'ready', Mark-Ready runs
// the zero-hour / unpriced / route-mileage gates, invoicing stamps
// statusAtInvoice) and sending happens BEFORE billing is finalised. Delivery is
// an ORTHOGONAL axis — a ticket can be `ready` AND unsent, and "ready and
// unsent" is her most valuable query — so it lives in its own filter
// (`BillingState.deliveryFilter`), never inside the status enum.
//
// AND IT IS NOT ACCEPTANCE. "Sent" never reads as the customer agreeing to
// anything. The customer's ANSWER is TASK-4683's job; this log is what WE did.

// The same repair + shape test the callable applies, so what a dialog shows her
// is what the server will actually use. Real stored customer addresses carry
// trailing commas and '>' (`Dee@Massiveservicesllc.com,`,
// `dcote@stallionis.com>` are live TITS records) — malformed is the NORM here.
const _TICKET_EMAIL_TRIM_RE = /^[<"'\s]+|[>,;:"'\s.]+$/g;
const _TICKET_EMAIL_SHAPE_RE = /^[^\s@,;:<>"']+@[^\s@,;:<>"']+\.[A-Za-z]{2,}$/;
function _normTicketEmail(v) { return String(v || '').trim().replace(_TICKET_EMAIL_TRIM_RE, ''); }
function _isTicketEmailShaped(v) { return _TICKET_EMAIL_SHAPE_RE.test(v); }
function _ticketEmailDate(v) {
  if (!v) return null;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}
const _TICKET_SEND_CHANNEL_LABELS = {
  email: 'Email', phone: 'Phone call', fax: 'Fax', mail: 'Mail',
  hand: 'Handed over on site', portal: 'Customer portal', other: 'Other'
};

// 🔴 STALENESS IS STAMPED, NEVER DERIVED FROM `updatedAt`.
// toggleDriverEditLock() bumps updatedAt without changing one thing the customer
// can see, so an `updatedAt > sentAt` comparison would false-flag "the customer
// has an old version" on every single lock — the one action a biller takes right
// after sending. `ticketEmailDirtyAt` is stamped by saveReviewTicket() and
// cleared server-side by every outbound log row.
//
// RESIDUAL, STATED PLAINLY — and this paragraph is now NARROWER than it was,
// because TASK-4699 closed the big hole it described. The driver surfaces DO
// stamp now: jobboard.js and clock.js (TASK-4699, 16 writers) and the public
// clock / driver timesheet in app.js (pubSaveTicketLine, pubDeleteTicketLine,
// pubSaveEditShift, pubSaveEditTicket, pubDeleteShift's keep-the-ticket branch,
// pubUploadClockOutPhotos, TicketPhotos.removeUploaded).
//
// WHAT IS STILL NOT COVERED, so nobody reads the flag as an audit claim:
//  - a hand-rolled SDK write, or an admin script, can skip the stamp entirely;
//  - a ticket that is EMAILED and then DELETED leaves no doc to carry a flag;
//  - clock.js autoSaveOnClose covers an amended shift note but not dumps /
//    waterRefills changed and abandoned without a Save press;
//  - a second driver clocking IN on an already-emailed ticket moves the crew
//    count on the customer's copy before any clock-out stamps it.
// So this stays a UI hint — a POSITIVE claim only. Every string in this file
// says "this ticket HAS been edited since it went out"; not one of them says an
// unflagged ticket is proven current, and none of them may start to. Absence of
// the flag is not evidence of a matching copy, and the wording must not imply it
// until the residual above is empty.
//
// THREE STATES, NOT TWO. "Accepted or not" is really accepted / rejected / NO
// ANSWER YET, and no-answer is the state Lacey is actually chasing. Under an
// accept-or-reject-only model it is indistinguishable from never-sent, which is
// the confusion being fixed — so `sent` and `response` are separate facts here
// and `awaiting` is derived from both.
//
// `verified` vs a logged claim is the other distinction that must survive:
// source 'app' means SendGrid accepted the message and we hold the address and
// timestamp (EVIDENCE); source 'manual' means a person says they sent it
// themselves (an ASSERTION). Anything that must ever prove a customer was
// notified reads `verified`, never `sent`.
function _ticketDeliveryState(t) {
  const ti = t || {};
  const at = _ticketEmailDate(ti.ticketSendLastAt);
  const dirtyAt = _ticketEmailDate(ti.ticketEmailDirtyAt);
  const response = ti.ticketCustomerResponse || null; // TASK-4683 writes this

  // TASK-4714 — THE FAILED SEND, a fourth fact and not a flavour of the other
  // three. `sent` stays exactly what it was (something went out); a refused or
  // bounced attempt sets its own family server-side and NEVER ticketSendLast*,
  // because a failure that lit up "sent" would be the badge lying about notice.
  //
  // A later GOOD send clears the alarm without erasing the history — the row
  // stays in the series, the badge stops shouting. "Later" has to consider BOTH
  // successes: the office send writes `ticketSendLastAt` (it appends to the log),
  // while the DRIVER close-out send writes only `fieldTicketData.emailSentAt`
  // (index.js ~:30350 branches on `_isOffice`). Reading only the first would
  // leave the failure badge burning on a ticket the driver successfully re-sent
  // five minutes later — which is the exact flow this task is about.
  const failedAt = _ticketEmailDate(ti.ticketSendLastFailedAt);
  const driverSentAt = _ticketEmailDate(ti.fieldTicketData?.emailSentAt);
  const lastGood = [at, driverSentAt].filter(Boolean).sort((a, b) => b - a)[0] || null;
  const failure = failedAt && (!lastGood || failedAt.getTime() > lastGood.getTime())
    ? {
      failed: true,
      failedAt,
      failedTo: ti.ticketSendLastFailedTo || '',
      failedReason: ti.ticketSendLastFailedReason || '',
      failedCount: ti.ticketSendFailCount || 1
    }
    : { failed: false, failedAt: null, failedTo: '', failedReason: '', failedCount: 0 };

  // TASK-4855 — 🔴 "N SENDS" HAS TO COUNT SENDS.
  // `ticketSendLogCount` is the ROW TOTAL of the series: services/ticketSendLog.js
  // increments it on EVERY write — outbound, failed, and the customer's own
  // inbound answer. On Chris's own accepted ticket (#900001: one direction:"out"
  // row + one direction:"in" acceptance) that made the ribbon read
  // "Emailed 9/9 · 2 sends" when the office had sent it exactly once. A customer
  // answering is not us sending, and this surface exists because a badge lied
  // about notice once already.
  //
  // `ticketEmailSentCount` is incremented on the outbound APP-SEND branch only
  // (same file), which is exactly the claim the word "sends" makes.
  //
  // 🔴 AND NOTHING COUNTS A HAND-LOGGED OUTBOUND ROW. There is no server counter
  // for `source:'manual'`, and the row total cannot be repaired into one from
  // the ticket doc (no inbound counter to subtract, and a ticket can be answered
  // more than once). So a hand-logged send contributes 0 here and the `logged`
  // segment claims no count at all — an uncertain number on the face of a badge
  // is worse than no number. The full history is one click away in the Review
  // Ticket tracking panel, which reads the series itself.
  const sends = Number(ti.ticketEmailSentCount) || 0;

  if (!at) {
    return { sent: false, verified: false, stale: false, awaiting: false, response, at: null, to: '', by: '', channel: '', count: 0, resentAfterAnswer: false, answeredInPortal: false, ...failure };
  }
  // TASK-4856 — AWAITING IS A COMPARISON, NOT A NULL CHECK.
  // `!response` is permanently false once a customer has answered ONCE, so after
  // the reject → correct → resend loop (the flow the reject-dead-end fix opened
  // up) a ticket carrying a FRESH, UNANSWERED link still counted as answered: it
  // fell out of the "Awaiting Response" chip and out of the search keywords, and
  // the one question this whole axis exists to answer — "who hasn't answered
  // me?" — stopped including the tickets Lacey has just chased.
  //
  // `ticketCustomerResponse*` is "the LATEST answer", not "the answer" (see the
  // inbound branch of services/ticketSendLog.js), so the honest test is whether
  // the last thing that went out is newer than the last thing that came back.
  // A response with no timestamp is NOT treated as stale — we cannot prove a
  // newer send, and inventing "awaiting" out of missing data is the same class
  // of lie in the other direction.
  const respAt = _ticketEmailDate(ti.ticketCustomerResponseAt);
  const awaiting = !response || !!(respAt && respAt.getTime() < at.getTime());
  // TASK-4683 A2 — "EDITED SINCE" IS MEASURED FROM THE NEWEST FACT, not always
  // from the send. Once the customer has answered (and nothing newer went out),
  // the version that matters is the one they ANSWERED ABOUT — and the portal's
  // hash gate refuses an answer on anything but the current ticket, so an edit
  // made between the send and the answer is part of what they accepted. Keying
  // on the send would paint "Accepted, then edited" on a ticket nobody touched
  // after the customer signed it.
  //
  // 🔴 STILL STAMPED, NEVER DERIVED FROM `updatedAt`: `ticketEmailDirtyAt` is
  // written by saveReviewTicket() and the driver edit paths (TASK-4699), and
  // NOT by toggleDriverEditLock() nor by the server's accept-time auto-lock —
  // both of which move `updatedAt`/`driverEditLocked*` without changing a thing
  // the customer saw. Same positive-claim-only residual as the block above.
  // PORTAL answers only: the hash-gate argument above holds for an answer given
  // in the approval portal. A hand-recorded answer never passed that gate — the
  // customer may have answered about the emailed copy from before an edit — so
  // it keeps measuring from the send, exactly as before this task.
  const answeredLast = !!(response && !awaiting && respAt && ti.ticketCustomerResponseSource === 'customer');
  const staleSince = answeredLast ? respAt : at;
  const stale = !!(dirtyAt && dirtyAt.getTime() > staleSince.getTime());
  return {
    ...failure,
    sent: true,
    verified: ti.ticketSendLastSource === 'app',
    at,
    to: ti.ticketSendLastTo || '',
    by: ti.ticketSendLastBy || '',
    channel: ti.ticketSendLastChannel || 'email',
    stale,
    response,
    awaiting,
    // "Accepted, then edited": the customer's acceptance is the newest answer
    // and the ticket has been edited after it. The accepted copy is frozen
    // server-side (approvalReceipts), so this is a pointer to it, not a loss.
    editedAfterAccept: !!(stale && answeredLast && response === 'accepted'),
    count: sends,
    // TASK-4852 — whether a send went out AFTER the latest answer.
    // `at` is the LATEST send, so when this is true it post-dates the
    // answer, and printing it in front of the answer puts the strip out of
    // order. Strictly a comparison of two stamps; a response with no timestamp
    // is never treated as re-sent (same rule as `awaiting` above).
    resentAfterAnswer: !!(response && respAt && respAt.getTime() < at.getTime()),
    // A portal answer needs a link we sent, so it proves a send came first.
    answeredInPortal: !!(response && ti.ticketCustomerResponseSource === 'customer')
  };
}

// ONE searchable field list for a ticket, used by BOTH the list
// (_ticketMatchesSearch) and the CSV export. There were two hand-copied copies
// before this task and the file's own TASK-1345 comment describes what that
// cost: "the accountant's CSV could legitimately contain a different set than
// the screen". Adding a third copy for delivery keywords would have rebuilt the
// same bug, so the list is named once here instead.
function _ticketSearchFields(t) {
  return [
    t.customerName, t.jobName, t.jobTitle, t.wellName, t.wellNumber,
    t.ticketNo ? `#${t.ticketNo}` : '', t.ticketNo,
    t.invoiceNumber, t.poNumber,
    ...(t.employees || []).map(e => e.name || e.personName || ''),
    t.date ? formatDate(t.date) : '',
    _ticketDeliveryKeywords(t),
    ..._ticketJobSearchFields(t)
  ];
}

// TASK-10257 B — the ticket's JOB as search text: job number ("J-1345" and the
// bare 1345), the job's POs (main + extras) and its project's name. Read only
// from the jobs listener and the projects read already in BillingState; no
// reads here. A job or project not loaded yet adds nothing. Maps are cached per
// array (jobs is replaced per snapshot, but deep links push onto it: length).
let _tsJobIdx = { jobs: null, n: -1, projects: null, byId: new Map(), proj: new Map() };
function _ticketJobSearchFields(t) {
  const jobs = BillingState.jobs || [], projects = BillingState.projects || [];
  if (_tsJobIdx.jobs !== jobs || _tsJobIdx.n !== jobs.length || _tsJobIdx.projects !== projects) {
    _tsJobIdx = { jobs, n: jobs.length, projects,
      byId: new Map(jobs.filter(j => j && j.id).map(j => [j.id, j])),
      proj: new Map(projects.filter(p => p && p.id).map(p => [p.id, p])) };
  }
  const j = t && t.jobId ? _tsJobIdx.byId.get(t.jobId) : null;
  if (!j) return [];
  const n = j.jobNumber != null ? String(j.jobNumber).trim() : '';
  const p = typeof j.projectId === 'string' && j.projectId ? _tsJobIdx.proj.get(j.projectId) : null;
  return [n ? `J-${n}` : '', n, ...jobPoList(j), p && p.deleting !== true ? p.name : ''];
}

// Delivery state as SEARCH TEXT, folded into the Tickets view's existing search
// box rather than given a control of its own. Reads the derived parent fields
// only, like everything else on a list row.
//
// Chris asked for this in his own vocabulary — "search tickets that have been
// 'sent', by customer, by job, or 'approved' or 'denied'" — so "approved" and
// "denied" are indexed as synonyms of accepted and rejected. Somebody typing
// what they mean should not have to learn our nouns.
function _ticketDeliveryKeywords(t) {
  const st = _ticketDeliveryState(t);
  const words = [];
  if (_ticketIsPaper(t)) {
    // TASK-9588 — a no-email customer's never-sent ticket is not "unsent".
    words.push('paper', 'paper ticket', 'no email');
  } else if (!st.sent) {
    words.push('unsent', 'not sent', 'never sent');
  } else {
    words.push('sent');
    if (st.verified) words.push('emailed'); else words.push('logged');
    if (st.stale) words.push('edited since sent', 'stale');
    if (st.editedAfterAccept) words.push('accepted then edited', 'edited after acceptance');
    if (st.awaiting) words.push('awaiting response', 'no response', 'waiting');
  }
  if (st.response === 'accepted') words.push('accepted', 'approved');
  if (st.response === 'rejected') words.push('rejected', 'denied');
  // TASK-4714 — searchable, so "which ones failed" is one box away and does not
  // need a control of its own. Same reasoning as the delivery words above.
  if (st.failed) words.push('failed', 'email failed', 'not delivered', 'refused', 'bounced');
  return words.join(' ');
}

// The delivery badge — the status column on the ticket row. Rendered for EVERY
// ticket including the never-sent ones, because "which ones did I miss" is a
// question about absence and a feed of actions taken can never answer it.
//
// Deliberately distinct from the green "Emailed" badge earlier in the same row,
// which means "the INVOICE went out". Colour AND icon separate an app send from
// a hand-logged one, so evidence and assertion are legible without a hover.
function _ticketDeliveryBadgeHtml(t) {
  const st = _ticketDeliveryState(t);
  const esc = window.escAttr || escapeHtml;
  const pill = (bg, fg, border, icon, label, tip) =>
    `<span class="badge badge-sm" title="${esc(tip)}" style="background:${bg};color:${fg};border:${border};font-size:11px;padding:2px 8px;border-radius:10px;white-space:nowrap;"><span class="material-icons" style="font-size:12px;vertical-align:middle;margin-right:2px;">${icon}</span>${label}</span> `;

  // TASK-4714 — FIRST, ahead of every other state. A send that was tried and
  // refused is more urgent than one nobody has got to yet: somebody is waiting
  // on a ticket they think went out, and the fix (a recipient on the contract)
  // is an office action nobody knew was needed. Red, not the grey "Not sent".
  if (st.failed) {
    const whenF = st.failedAt ? st.failedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    return pill('#FEF2F2', '#B91C1C', '1px solid #FCA5A5', 'error_outline', 'Email failed',
      `Tried ${whenF}${st.failedTo ? ' to ' + st.failedTo : ''} and it did not go out${st.failedCount > 1 ? ` (${st.failedCount} attempts)` : ''}. ${st.failedReason || ''}`.trim());
  }
  // TASK-9588 — the customer is marked "No ticket emails": the never-sent
  // state is the expected one, so it reads as a neutral fact, not a to-do.
  if (_ticketIsPaper(t)) {
    return pill('#F8FAFC', '#475569', '1px solid #E2E8F0', 'description', 'Paper ticket',
      'This customer is set to no ticket emails, so this ticket is not counted as unsent. You can still email it by hand.');
  }
  if (!st.sent) {
    return pill('#F8FAFC', '#475569', '1px dashed #CBD5E1', 'mail_outline', 'Not sent',
      'This field ticket has not been sent to the customer, and nothing has been logged for it.');
  }
  const when = st.at ? st.at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const how = st.verified
    ? 'Emailed from DispatchDeck '
    : (_TICKET_SEND_CHANNEL_LABELS[st.channel] || 'Sent') + ' — logged by hand, DispatchDeck did not send it ';
  const base = how + when + (st.to ? ' to ' + st.to : '') + (st.by ? ' by ' + st.by : '');

  // TASK-4683 A2 — ahead of the generic "Edited since sent": the fact that
  // matters on this row is that the customer signed a version the ticket no
  // longer matches. Amber like every staleness state, but it names the answer.
  if (st.editedAfterAccept) {
    return pill('#FFFBEB', '#B45309', '1px solid #FDE68A', 'rule', 'Accepted, then edited',
      base + ' — the customer accepted it, and the ticket has been edited since. What they see no longer matches what they accepted.');
  }
  if (st.stale) {
    return pill('#FFFBEB', '#B45309', '1px solid #FDE68A', 'mark_email_unread', 'Edited since sent',
      base + ' — the ticket has been edited since. The customer is looking at the older version.');
  }
  // 🔴 TASK-4856 — `!st.awaiting` GATES BOTH ANSWERS, and it is not decoration.
  // After answer → correct → RESEND, the resend clears `ticketEmailDirtyAt`, so
  // `st.stale` above is false and this branch would paint "Accepted" on a ticket
  // whose newest copy nobody has answered — a pill claiming an agreement the
  // customer has not given for what they are now holding. `st.awaiting` is the
  // one fact that knows the send is newer than the answer, so the pill falls
  // through to "Sent — no answer yet" and agrees with the Awaiting Response
  // chip, which counts exactly the same ticket. The HISTORY is not lost: the
  // ribbon in the ticket detail still reads Emailed → Accepted → Awaiting.
  if (st.response === 'accepted' && !st.awaiting) {
    return pill('#F0FDF4', '#15803D', '1px solid #BBF7D0', 'task_alt', 'Accepted', base + ' — the customer accepted it.');
  }
  if (st.response === 'rejected' && !st.awaiting) {
    return pill('#FEF2F2', '#B91C1C', '1px solid #FECACA', 'cancel', 'Rejected', base + ' — the customer rejected it.');
  }
  if (!st.verified) {
    return pill('#F1F5F9', '#334155', '1px dashed #94A3B8', 'drafts', 'Sent · logged', base + ' — no answer yet.');
  }
  return pill('#EFF6FF', '#1D4ED8', '1px solid #BFDBFE', 'mark_email_read', 'Sent', base + ' — no answer yet.');
}

// ---------------------------------------------------------------------------
// THE DELIVERY RIBBON — one strip that reads left→right as history.
// ---------------------------------------------------------------------------
// TASK-4738 (Slice 0 of TASK-4683). Chris, after his own test send of #100642:
// "i don't see anything that shows that it has been emailed (i got a toast that
// said it was emailed) but I was hoping for a ribbon towards the top that says
// 'emailed' … the ribbon design should be such that another ribbon will fit
// nicely into it, the next ribbon should say 'accepted or rejected' to form a
// long ribbon that is easy to read and get the history (emailed, accepted)."
//
// THE BUG IT CLOSES: `viewTicket()` is the ONE screen carrying the Email button
// and it rendered no delivery state at all. Press Email → the send succeeds →
// the screen you are standing on is byte-identical to before, and the only
// evidence is a toast that vanishes. You then have to close the modal and hunt
// the row in the list to find out whether it worked.
//
// 🔴 WHY THIS IS AN ARRAY AND NOT A BADGE. `_ticketDeliverySegments()` returns
// N segments and the renderer joins however many it is handed, so a new fact
// EXTENDS THE SAME OBJECT instead of parking another floating pill beside it.
// TASK-4683 A1 fills segment 2 (the customer's answer) and A3 adds signature;
// a one-segment badge here would have forced 4683 to land a second ribbon next
// to this one, which is precisely the "row of loose pills" Chris is asking us
// not to build.
//
// 🔴 IT READS NO FIELD OF ITS OWN. Every value comes from
// `_ticketDeliveryState()` — the same reader behind the list pill, the tracking
// block, the search keywords and the filter chips. A second reader of the same
// fields is how two screens come to disagree about whether a customer was
// notified, and this ribbon exists because two screens already disagreed.
//
// WORDING IS INHERITED, NOT REDESIGNED (TASK-4662's comment block above argues
// each of these out, and a prettier ribbon that drops them ships a false claim):
//  · `verified` (source 'app', SendGrid accepted it — EVIDENCE) and hand-logged
//    (source 'manual', a person says they sent it — an ASSERTION) stay legible
//    apart WITHOUT a hover: different word on the face, solid vs dashed rail.
//  · Staleness is only ever a POSITIVE claim. Nothing here says an unflagged
//    ticket is proven current, because absence of `ticketEmailDirtyAt` does not
//    prove it (hand-rolled SDK writes and a second driver clocking in bypass the
//    stamp). Never derived from `updatedAt` — `toggleDriverEditLock` bumps that.
//  · The never-sent segment describes what WE HOLD ("nothing recorded"), not
//    what the world did.
//
// ── THE AWAITING-RESPONSE SLOT — NOW ON (TASK-4683, 2026-09-09) ─────────────
// The acceptance segment below is wired and DATA-DRIVEN: the moment
// `ticketCustomerResponse` holds a value the ribbon grows a second segment with
// no change to this file.
//
// 🔴 THE PREMISE THIS FLAG WAS FALSE UNDER IS DEAD, and this comment used to
// say the opposite. It read "NOTHING WRITES THAT FIELD TODAY … there is no
// portal, no link and no reply path, so a customer currently has no way to
// answer." That was true when TASK-4738 shipped and it is not true now:
//
//   • `ticketApprovalSubmit` (functions/src/index.js) writes the customer's own
//     answer through `buildTicketSendLogWrite`, whose inbound branch
//     (services/ticketSendLog.js — `else if (outcome)`) sets
//     `ticketCustomerResponse` / `…At` / `…Source` on the ticket doc. That is
//     the field's real, executed writer.
//   • The customer reaches it from `public/ticket-approval.html`, opened with a
//     30-day server-minted token that `sendFieldTicketEmail` mails when the
//     office picks "Send for approval" in `showTicketEmailDialog()` below.
//
// So all three states now occur: sent-and-awaiting, accepted, rejected. The
// ghost slot TASK-4738's board row argued for — three states, never two,
// because "who hasn't answered me" is Lacey's real question — is no longer a
// promise the product cannot keep. It is the answer to the question she is
// actually asking, and it is the ONLY segment that distinguishes "I asked and
// they haven't answered" from "I never asked".
//
// 🔴 IT STILL RENDERS ONLY WHEN `st.sent` IS TRUE. An unsent ticket gets no
// awaiting slot, because you cannot be waiting on an answer you never asked
// for, and a permanent grey "Awaiting response" on a ticket nobody has emailed
// would be exactly the lying badge this whole surface exists to remove.
//
// The flag stays a NAMED CONSTANT rather than being inlined: it is the one
// switch that turns the slot off again if the portal is ever pulled, and
// `tests/task4738-ticket-delivery-ribbon.unit.cjs` +
// `tests/task4683-send-for-approval.unit.cjs` both pin it by name.
const _TICKET_RIBBON_SHOW_AWAITING = true;

function _ticketDeliverySegments(t) {
  const st = _ticketDeliveryState(t);
  const day = (d) => (d ? d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '');
  const who = (st.to ? ' to ' + st.to : '') + (st.by ? ' by ' + st.by : '');
  const segs = [];

  // ── SEGMENT 1 — WHAT WE DID ───────────────────────────────────────────────
  // Branch order mirrors `_ticketDeliveryBadgeHtml` exactly (failure, then
  // never-sent, then the send itself) so the ribbon and the list pill cannot
  // tell two different stories about the same ticket.
  if (st.failed) {
    // TASK-4714 — ahead of everything. Somebody is waiting on a ticket they
    // think went out, and the fix is an office action nobody knew was needed.
    segs.push({
      key: 'failed', icon: 'error_outline', label: `Email failed ${day(st.failedAt)}`.trim(),
      bg: '#FEF2F2', fg: '#B91C1C', border: '#FCA5A5',
      detail: `Tried${st.failedTo ? ' to ' + st.failedTo : ''} and it did not go out${st.failedCount > 1 ? ` — ${st.failedCount} attempts` : ''}.${st.failedReason ? ' ' + st.failedReason : ''}`
    });
  } else if (_ticketIsPaper(t)) {
    // TASK-9588 — never sent, under a "No ticket emails" customer: expected,
    // so neutral grey with a solid rail, not the dashed "nothing yet" look.
    segs.push({
      key: 'paper', icon: 'description', label: 'Paper ticket',
      bg: '#F8FAFC', fg: '#475569', border: '#E2E8F0',
      detail: 'This customer is set to no ticket emails. Nothing has been emailed or logged; you can still email it by hand.'
    });
  } else if (!st.sent) {
    // The honest empty state, and it must RENDER — "which ones did I miss" is a
    // question about absence, and a blank space is the original bug wearing a
    // new coat. Dashed grey reads as "nothing here yet", not as a red failure.
    segs.push({
      key: 'unsent', icon: 'mail_outline', label: 'Not sent',
      bg: '#F8FAFC', fg: '#475569', border: '#CBD5E1', dashed: true,
      detail: 'Nothing recorded — DispatchDeck has not emailed this ticket and nobody has logged sending it another way.'
    });
  } else if (st.resentAfterAnswer) {
    // TASK-4852 — SEQUENCE. The ticket doc holds only the LATEST send, and here
    // that send came AFTER the customer's answer, so its date and its source
    // describe the re-send (segment "resent" below), not the first send.
    // This segment renders only when the doc PROVES a send BEFORE the answer:
    // the customer answered in the PORTAL, which is reachable only from a link
    // we sent. A send COUNT proves nothing about order (COD r1/r2: a
    // hand-recorded answer followed by two app sends has count 2 and nothing
    // before the answer). No date, and not "Emailed" — the latest send's source
    // cannot vouch for how the first one went. Full series: the tracking panel.
    if (st.answeredInPortal) {
      segs.push({
        key: 'sent', icon: 'send', label: 'Sent',
        bg: '#F8FAFC', fg: '#334155', border: '#CBD5E1',
        detail: 'Sent earlier (the full send history is in the tracking panel).'
      });
    }
  } else if (st.verified) {
    // "Emailed" is the settled word (TASK-4662, re-locked by TASK-4683 decision
    // 9) and it is Chris's own. Solid blue rail = EVIDENCE: SendGrid accepted
    // the message and we hold the address and the timestamp.
    segs.push({
      key: 'emailed', icon: 'mark_email_read',
      label: `Emailed ${day(st.at)}${st.count > 1 ? ` · ${st.count} sends` : ''}`,
      bg: '#EFF6FF', fg: '#1D4ED8', border: '#BFDBFE',
      detail: `Sent from DispatchDeck${who}.`
    });
  } else {
    // A DIFFERENT CLAIM and it has to look different without a hover: a person
    // says they sent it themselves. Dashed slate, and the word "logged" on the
    // face of the segment — never "Emailed", which would dress an assertion up
    // as evidence.
    // TASK-4855 — no send count on this branch, ever. `st.count` counts APP
    // sends (nothing on the ticket doc counts hand-logged ones), so printing it
    // beside "Phone 9/9 · logged" would attach a number to a channel it does not
    // describe. The Review Ticket tracking panel reads the whole series and is
    // the one surface that can show a mixed history honestly.
    const ch = _TICKET_SEND_CHANNEL_LABELS[st.channel] || 'Sent';
    segs.push({
      key: 'logged', icon: 'drafts',
      label: `${ch} ${day(st.at)} · logged`,
      bg: '#F1F5F9', fg: '#334155', border: '#94A3B8', dashed: true,
      detail: `Logged by hand — DispatchDeck did not send this${who}.`
    });
  }

  // ── SEGMENT 2 — WHAT THE CUSTOMER DID (TASK-4683) ─────────────────────────
  // Unreachable until 4683 ships (see the block comment above); it is wired
  // rather than stubbed so that landing 4683 is a data change, not an edit here.
  if (st.sent && st.response === 'accepted') {
    segs.push({
      key: 'accepted', icon: 'task_alt', label: `Accepted ${day(_ticketEmailDate(t.ticketCustomerResponseAt))}`.trim(),
      bg: '#F0FDF4', fg: '#15803D', border: '#BBF7D0',
      detail: `The customer accepted it${t.ticketCustomerResponseSource === 'customer' ? ' in the portal' : ' — recorded by hand'}.`
    });
  } else if (st.sent && st.response === 'rejected') {
    segs.push({
      key: 'rejected', icon: 'cancel', label: `Rejected ${day(_ticketEmailDate(t.ticketCustomerResponseAt))}`.trim(),
      bg: '#FEF2F2', fg: '#B91C1C', border: '#FECACA',
      // TASK-4852 — a rejection is a BRANCH, not a step: the office corrects and
      // re-sends, and the customer can accept after. The renderer breaks the
      // strip around it (own corners, a gap) so it never reads as progress.
      branch: true,
      detail: `The customer rejected it${t.ticketCustomerResponseSource === 'customer' ? ' in the portal' : ' — recorded by hand'}.`
    });
  }
  // TASK-4852 — the RE-SEND after an answer, in its place in time: after the
  // answer it followed, before the "Awaiting response" it opened. Evidence vs
  // assertion keeps the same faces as segment 1 (blue "Re-sent" for an app
  // send, dashed "… logged" for a hand-logged one). Not on a failure strip:
  // the failure segment already leads and owns segment 1.
  // 🔴 KNOWN LIMIT (COD r1): a newer FAILED send leads the strip and this
  // segment is skipped, so a failure after a good re-send reads
  // "Email failed | Rejected | Awaiting" — the failure-first rule of
  // `_ticketDeliveryBadgeHtml` wins over strict time order, on purpose.
  if (st.sent && st.resentAfterAnswer && !st.failed) {
    if (st.verified) {
      // `st.count` is the LIFETIME app-send count (it includes the first send),
      // so it lives in the detail line as a total, never on the face where
      // "Re-sent · 2 sends" would read as two re-sends (COD r1). "Re-sent" only
      // when a send before the answer is proven (portal answer, see above).
      const earlier = st.answeredInPortal;
      segs.push({
        key: 'resent', icon: 'mark_email_read', newRun: true,
        label: `${earlier ? 'Re-sent' : 'Emailed'} ${day(st.at)}`,
        bg: '#EFF6FF', fg: '#1D4ED8', border: '#BFDBFE',
        detail: `${earlier ? 'Sent again' : 'Sent'} from DispatchDeck${who} after the customer answered${st.count > 1 ? ` (${st.count} sends from DispatchDeck in all)` : ''}.`
      });
    } else {
      const ch2 = _TICKET_SEND_CHANNEL_LABELS[st.channel] || 'Sent';
      segs.push({
        key: 'resent', icon: 'drafts', newRun: true,
        label: `${ch2} ${day(st.at)} · logged`,
        bg: '#F1F5F9', fg: '#334155', border: '#94A3B8', dashed: true,
        detail: `Logged by hand after the customer answered — DispatchDeck did not send this${who}.`
      });
    }
  }
  // TASK-4856 — 🔴 A SEPARATE `if`, NOT AN `else`. After reject → correct →
  // resend, BOTH facts are true and both matter: they rejected it on the 9th,
  // AND a new link went out that nobody has answered. `st.awaiting` is false
  // whenever the answer is the newest fact, so for every state that existed
  // before this task the strip is byte-identical; the only ticket that grows a
  // third segment is the one that was re-sent after an answer.
  if (st.sent && st.awaiting && _TICKET_RIBBON_SHOW_AWAITING) {
    // 🔴 The detail says MORE than "no answer", on purpose. We cannot tell from
    // the ticket doc whether the send that went out carried Accept/Reject
    // buttons (the outbound log row does not record the mode — see the note on
    // showTicketEmailDialog), so a bare "Awaiting response" on a plain email
    // would quietly imply the customer has a way to answer when he may not.
    // Naming the approval send here is both the honest caveat and the next
    // action: it is what the biller does about this segment.
    segs.push({
      key: 'awaiting', icon: 'hourglass_empty', label: 'Awaiting response',
      bg: '#FFFFFF', fg: '#475569', border: '#CBD5E1', dashed: true,
      detail: 'No answer from the customer yet. Only a "Send for approval" email gives them Accept and Reject buttons to answer with.'
    });
  }

  // ── SEGMENT 3 — WHAT HAPPENED AFTER ───────────────────────────────────────
  // Last, because it is the newest fact. TASK-4683 A2 wants the strip to read
  // "Accepted, then edited", so staleness sits AFTER the response segment
  // rather than swallowing segment 1 the way the dense list pill has to.
  if (st.sent && st.editedAfterAccept) {
    // TASK-4683 A2 — the strip now reads "Accepted 9/23 | Then edited", and the
    // visible detail line spells the phrase out. `stale` here is measured from
    // the ACCEPTANCE, not the send (see _ticketDeliveryState), and is stamped —
    // a driver-lock toggle can never produce it.
    segs.push({
      key: 'editedAfterAccept', icon: 'rule', label: 'Then edited',
      bg: '#FFFBEB', fg: '#B45309', border: '#FDE68A',
      detail: 'Accepted, then edited — the ticket has changed since the customer accepted it, so it no longer matches what they signed. Use "Accepted copy" below to see the version they signed.'
    });
  } else if (st.sent && st.stale) {
    segs.push({
      key: 'stale', icon: 'mark_email_unread', label: 'Edited since',
      bg: '#FFFBEB', fg: '#B45309', border: '#FDE68A',
      detail: 'The ticket has been edited since it went out — the customer is looking at the older version.'
    });
  }

  return segs;
}

// Display only. There is deliberately no onclick anywhere in here: nothing in
// this ribbon is tappable, so there is no `onclick="fn('${x}')"` JS-string-
// inside-an-attribute context (which no HTML escaper fixes) and no 44px touch
// target to hit. The detail line under the strip carries who/when/to as VISIBLE
// text rather than a title tooltip alone, because a phone has no hover and this
// modal is read on one.
function _ticketDeliveryRibbonHtml(t) {
  const segs = _ticketDeliverySegments(t);
  if (!segs.length) return '';
  const esc = escapeHtml; // file-local wrapper → window.escapeHtml (sharedFormat.js): escapes &<>"' and IS attribute-safe.

  // TASK-4852 — RUNS. A BRANCH segment (a rejection) stands apart as its own
  // run, and whatever follows it starts a new one, so a "Rejected" never reads
  // as one more step of progress. Each run is its own box and the outer row
  // wraps BETWEEN runs with a gap, so on a 390px phone a line break lands at a
  // run boundary instead of leaving a half-joined chip at the start of a line.
  const runs = [];
  segs.forEach((s, i) => {
    // A re-send also opens a new run (COD r3): it is a new round, and on the
    // accept path nothing else would split a ~480px strip at 390px.
    if (i === 0 || s.branch || s.newRun || segs[i - 1].branch) runs.push([]);
    runs[runs.length - 1].push(s);
  });

  const chip = (s, i, run) => {
    // The segments butt together into ONE object: shared corners, borders
    // collapsed with a -1px pull. That is what makes it read as a strip you
    // follow left→right instead of a row of loose pills.
    const end = run.length - 1;
    const radius = run.length === 1 ? '8px'
      : i === 0 ? '8px 0 0 8px'
        : i === end ? '0 8px 8px 0' : '0';
    return `<span data-seg="${esc(s.key)}" title="${esc(s.detail)}" style="display:inline-flex;align-items:center;gap:5px;box-sizing:border-box;min-height:30px;padding:5px 11px;background:${s.bg};color:${s.fg};border:1px ${s.dashed ? 'dashed' : 'solid'} ${s.border};border-radius:${radius};${i > 0 ? 'margin-left:-1px;' : ''}font-size:0.78rem;font-weight:600;line-height:1.2;white-space:nowrap;">
      <span class="material-icons" style="font-size:15px;line-height:1;">${esc(s.icon)}</span>${esc(s.label)}
    </span>`;
  };

  return `
    <div class="ticket-delivery-ribbon" style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;">${runs.map(run => `<span style="display:inline-flex;flex-wrap:wrap;align-items:center;">${run.map((s, i) => chip(s, i, run)).join('')}</span>`).join('')}</div>
      <div style="margin-top:4px;font-size:0.72rem;line-height:1.45;color:var(--text-primary);overflow-wrap:anywhere;">${segs.map(s => esc(s.detail)).join(' ')}</div>
    </div>`;
}

// ---------------------------------------------------------------------------
// THE DECISION DETAIL — who answered, and on a rejection, why.
// ---------------------------------------------------------------------------
// TASK-4683. The ribbon above says WHAT the answer was and WHEN, because those
// two facts are derived onto the ticket doc (`ticketCustomerResponse` / `…At` /
// `…Source`) and a list row can read them without opening anything.
//
// 🔴 EVERYTHING ELSE LIVES ON THE LOG ROW, NOT THE TICKET. The signer's name,
// the rejection reason and its quick-pick code, the signature image, the IP,
// the user-agent and the `ticketVersionHash` are all on the ONE inbound
// `ticketSendLog` row `ticketApprovalSubmit` writes, and `listTicketSendLog`
// projects them under `approval`. Deliberately: that subcollection is
// server-only in firestore.rules both ways, the signature alone can be 200KB,
// and copying any of it onto the ticket doc would need a rules change plus a
// second writer of the same fact. So this block reads the SERIES.
//
// A biller's first question on a rejection is "who said no and why", and
// neither half of that answer is on the ticket. A ribbon that says "Rejected
// 9/9" and stops is a screen she has to leave to use.
const _TICKET_REJECT_REASON_LABELS = {
  hours: 'Hours are wrong',
  rate: 'Rate is wrong',
  well: 'Wrong well or location',
  docs: 'Missing documents',
  other: 'Other'
};

// The one inbound decision row, out of the cached series. At most ONE can exist
// per ticket — `ticketApprovalSubmit` is idempotent and keyed on
// `ticketCustomerResponse` inside a transaction — so `find` is not a guess.
//
// Returns null for BOTH "not fetched yet" and "fetched, nothing there", and the
// caller must not treat those as the same thing as "no decision": the decision
// itself is a ticket-doc fact and is stated with or without this row. An empty
// probe result is "did not run", never "found nothing".
function _ticketApprovalDecisionRow(ticketId) {
  const rows = _ticketSendLogCache[ticketId];
  if (!Array.isArray(rows)) return null;
  return rows.find(r => r && r.direction === 'in' && r.approval) || null;
}

// A customer-supplied signature is an <img> whose src comes from a PUBLIC,
// UNAUTHENTICATED page. Gate it to a data:image/ URL rather than trusting the
// server's cap alone — `javascript:` in an src is the whole reason this check
// is a function and not an inline template hole.
function _isTicketSignatureDataUrl(v) {
  return typeof v === 'string' && /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=\s]+$/.test(v);
}

/**
 * Renders under the delivery ribbon in viewTicket(). Empty string when the
 * customer has not answered — nothing to explain, no chrome.
 *
 * 🔴 REJECTION MUST NOT READ AS SILENCE. The ribbon already separates them by
 * colour; this block separates them by CONTENT: a rejection carries a reason a
 * person wrote, and silence has nothing to carry. Nothing in the rejection
 * branch may use the words "no answer" or "awaiting".
 *
 * No fetch, no await, no `onclick` — so no `onclick="fn('${x}')"` JS-string-
 * inside-an-attribute context exists for an apostrophe in a signer's name to
 * break ([[lesson_escapehtml_is_not_attribute_safe]]).
 */
function _ticketApprovalDecisionHtml(t) {
  const ti = t || {};
  const resp = ti.ticketCustomerResponse;
  if (resp !== 'accepted' && resp !== 'rejected') return '';
  const esc = escapeHtml; // → window.escapeHtml (sharedFormat.js): full &<>"' set, attribute-safe.
  const row = _ticketApprovalDecisionRow(ti.id);
  const a = (row && row.approval) || {};
  const signer = String(a.signerName || row?.byName || '').trim();
  const reason = String(a.reason || '').trim();
  const reasonLabel = _TICKET_REJECT_REASON_LABELS[a.reasonCode] || '';
  const inPortal = ti.ticketCustomerResponseSource === 'customer';
  const when = _ticketEmailDate(ti.ticketCustomerResponseAt);
  const whenStr = when ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const rejected = resp === 'rejected';

  const skin = rejected
    ? { bg: '#FEF2F2', border: '#FECACA', fg: '#B91C1C', icon: 'cancel' }
    : { bg: '#F0FDF4', border: '#BBF7D0', fg: '#15803D', icon: 'task_alt' };

  // The headline states the fact from the TICKET DOC, so it is correct before
  // the series has loaded and stays correct if the series fails to load.
  const head = `${rejected ? 'Rejected' : 'Accepted'}${signer ? ' by ' + esc(signer) : ''}${whenStr ? ' · ' + esc(whenStr) : ''}`;

  // Then the detail the SERIES carries. Three honest states, never one blank:
  // not fetched, fetched-and-empty, and fetched-with-a-row.
  const rows = _ticketSendLogCache[ti.id];
  let body;
  if (rejected) {
    if (reason) {
      body = `<div style="font-weight:600;">${esc(reasonLabel || 'Reason')}</div>
        <div style="margin-top:2px;white-space:pre-wrap;overflow-wrap:anywhere;">${esc(reason)}</div>`;
    } else if (rows === undefined) {
      body = `<div>Loading the reason they gave…</div>`;
    } else if (rows === null) {
      // No Retry link here on purpose: this block is display-only, so it emits
      // no `onclick="fn('${id}')"` — a JS string inside an attribute, which no
      // HTML escaper fixes. The Retry that DOES exist lives one modal over, in
      // the Review Ticket tracking panel, where the series is the subject.
      body = `<div>Couldn't load the reason they gave. Close and reopen this ticket, or open Review Ticket to retry.</div>`;
    } else {
      // Fetched and there is genuinely nothing. Say that, rather than leaving a
      // blank that reads as "no reason was required".
      body = `<div>No reason was recorded with this rejection.</div>`;
    }
    body += `<div style="margin-top:4px;font-weight:600;">Fix the ticket and send it for approval again — that cancels this link and issues a new one.</div>`;
  } else {
    body = `<div>${inPortal ? 'Signed in the approval portal.' : 'Recorded by hand — the customer did not sign this in the portal.'} Driver edits are locked; unlock them from Review Ticket if you need a correction.</div>`;
    // TASK-4683 A2 — THE ACCEPTED COPY. A frozen PDF of the ticket exactly as
    // the customer accepted it, served from `approvalReceipts` by
    // getTicketApprovalReceiptPdf — never from the live ticket, so it still
    // shows what they signed after any edit. Deliberately NOT a row in the
    // Documents list (locked decision 15: that list is user-deletable and
    // capped at 10). Data attributes + one delegated listener, no inline
    // onclick, so no id is ever a JS string inside an attribute.
    if (inPortal && row && row.id) {
      const st = _ticketDeliveryState(ti);
      const savedAt = row.snapshot && row.snapshot.state === 'ready' && row.snapshot.at ? new Date(row.snapshot.at) : null;
      const savedStr = savedAt && !isNaN(savedAt.getTime())
        ? ' · saved ' + savedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
      body += `${st.editedAfterAccept ? `<div style="margin-top:4px;font-weight:600;color:#B45309;">Accepted, then edited — the ticket has changed since they accepted it. The accepted copy still shows the version they signed.</div>` : ''}
        <div style="margin-top:6px;display:flex;flex-wrap:wrap;align-items:center;gap:6px;">
          <button type="button" class="btn btn-secondary btn-sm" data-approval-copy-ticket="${esc(ti.id)}" data-approval-copy-row="${esc(row.id)}" style="min-height:36px;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">picture_as_pdf</span>Accepted copy
          </button>
          <span style="font-size:0.72rem;color:var(--text-secondary);">The ticket as they accepted it${esc(savedStr)}</span>
        </div>`;
    }
  }

  const sig = _isTicketSignatureDataUrl(a.signatureDataUrl)
    ? `<div style="margin-top:6px;"><div style="font-size:0.68rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.06em;font-weight:700;">Signature</div>
        <img alt="Customer signature" src="${esc(a.signatureDataUrl)}" style="max-width:220px;max-height:70px;background:#fff;border:1px solid ${skin.border};border-radius:4px;padding:2px;margin-top:2px;">
      </div>`
    : '';

  return `
    <div class="ticket-approval-decision" style="margin:0 0 var(--spacing-md);padding:10px 12px;background:${skin.bg};border:1px solid ${skin.border};border-radius:var(--radius-sm);">
      <div style="display:flex;align-items:flex-start;gap:8px;">
        <span class="material-icons" style="font-size:18px;color:${skin.fg};flex-shrink:0;">${skin.icon}</span>
        <div style="min-width:0;font-size:0.78rem;line-height:1.5;color:var(--text-primary);">
          <div style="font-weight:700;color:${skin.fg};font-size:0.85rem;">${head}</div>
          ${body}
          ${sig}
        </div>
      </div>
    </div>`;
}

// TASK-4683 A2 — open the accepted copy. ONE delegated listener (the TASK-4607
// `data-tsf-view` pattern): the ids travel in data attributes, never in an
// inline onclick string. The tab is opened SYNCHRONOUSLY inside the click so a
// popup blocker sees a user gesture; the PDF is poured into it after the
// callable returns. Every failure lands as a visible toast, never a dead button.
let _approvalCopyDelegated = false;
function _installApprovalCopyDelegate() {
  if (_approvalCopyDelegated || typeof document === 'undefined') return;
  _approvalCopyDelegated = true;
  document.addEventListener('click', async (ev) => {
    const btn = ev.target && ev.target.closest ? ev.target.closest('[data-approval-copy-row]') : null;
    if (!btn) return;
    ev.preventDefault();
    if (btn.disabled) return;
    const ticketId = btn.getAttribute('data-approval-copy-ticket') || '';
    const rowId = btn.getAttribute('data-approval-copy-row') || '';
    const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
    if (!ticketId || !rowId || !tenantId) return;
    const win = window.open('', '_blank');
    const prev = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Opening…';
    let url = null;
    try {
      const fn = httpsCallable(functions, 'getTicketApprovalReceiptPdf');
      const res = await fn({ tenantId, ticketId, rowId });
      const d = res?.data || {};
      if (!d.ok || !d.base64) throw new Error(d.error || 'The accepted copy came back empty.');
      const bin = atob(d.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win && !win.closed) {
        win.location.href = url;
      } else if (!window.open(url, '_blank')) {
        window.showToast?.('Your browser blocked the popup. Allow popups for this site to open the accepted copy.', 'warning');
      }
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
    } catch (err) {
      if (win && !win.closed) { try { win.close(); } catch (_) {} }
      if (url) { try { URL.revokeObjectURL(url); } catch (_) {} }
      console.warn('[Billing] accepted copy failed:', err?.message);
      window.showToast?.(err?.message || 'Could not open the accepted copy.', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = prev;
    }
  }, true);
}
_installApprovalCopyDelegate();

// Surgical repaint for the view-ticket modal, the twin of
// `_refreshTicketTracking` for the review modal. Re-running `viewTicket()` after
// a send would re-await the ticketLines fetch and jump the photo gallery's
// scroll position out from under whoever is reading it.
function _refreshTicketRibbon(ticketId) {
  const el = document.getElementById('view-ticket-ribbon');
  if (!el) return;
  // Paint ONLY if the open modal is this ticket. The node survives a modal
  // close (closeModal hides, it does not clear), so without this a send fired
  // from the Review modal could repaint a stale view-ticket header with another
  // ticket's delivery state — a badge lying about notice, which is the one
  // thing this whole surface exists to prevent.
  const t = (BillingState.tickets || []).find(x => x.id === ticketId)
    || (BillingState.selectedTicket && BillingState.selectedTicket.id === ticketId ? BillingState.selectedTicket : null);
  if (!t) return;
  if (!BillingState.selectedTicket || BillingState.selectedTicket.id !== ticketId) return;
  el.innerHTML = _ticketDeliveryRibbonHtml(t) + _ticketApprovalDecisionHtml(t);
}

// ---------------------------------------------------------------------------
// The Review Ticket tracking block — the DETAIL behind a row.
// ---------------------------------------------------------------------------
// Companion to the driver-lock row directly above it. Own helper for the same
// reason the lock row is one: re-rendering the whole modal mid-edit would drop
// in-progress line edits.
const _ticketSendLogCache = {};

function _ticketTrackingHtml(t) {
  const st = _ticketDeliveryState(t);
  const esc = escapeHtml;
  const id = esc(t.id);
  const rows = _ticketSendLogCache[t.id];

  const head = (() => {
    // TASK-4714 — the failure states what happened AND what to do about it. The
    // reason string is authored server-side on the refusal path and names the
    // actual fix ("set a recipient on this customer's contract"), because the
    // person reading this block is the only one who can perform it.
    if (st.failed) {
      const whenF = st.failedAt ? st.failedAt.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '';
      return `<div style="font-weight:600;font-size:0.85rem;color:#B91C1C;">Email failed ${esc(whenF)}${st.failedTo ? ' to ' + esc(st.failedTo) : ''}${st.failedCount > 1 ? ` — ${esc(String(st.failedCount))} attempts` : ''}</div>
        <div style="font-size:0.72rem;color:var(--text-primary);overflow-wrap:anywhere;">${esc(st.failedReason || 'The customer did not get this ticket.')}</div>`;
    }
    if (_ticketIsPaper(t)) {
      // TASK-9588 — "No ticket emails" customer: say so, and that a hand send still works.
      return `<div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Paper ticket — this customer doesn't get ticket emails</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);">It isn't counted as unsent. You can still email it or log a hand-off below.</div>`;
    }
    if (!st.sent) {
      return `<div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Not sent to the customer yet</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);">Sending does not change the ticket's status, and it is not the customer accepting it.</div>`;
    }
    const when = st.at ? st.at.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '';
    const how = st.verified ? 'Emailed' : (_TICKET_SEND_CHANNEL_LABELS[st.channel] || 'Sent') + ' (logged by hand)';
    // TASK-4683 — WHO answered and, on a rejection, WHY. Both live on the
    // inbound log row, which this panel has already fetched into
    // `_ticketSendLogCache`; neither is on the ticket doc. Before this the tail
    // read "— customer rejected." and stopped, which sent the biller hunting
    // the series below for the one thing she opened the panel to find out.
    const _decRow = _ticketApprovalDecisionRow(t.id);
    const _decName = _decRow ? String(_decRow.approval?.signerName || _decRow.byName || '').trim() : '';
    const _decReason = _decRow ? String(_decRow.approval?.reason || '').trim() : '';
    const _decLabel = _decRow ? (_TICKET_REJECT_REASON_LABELS[_decRow.approval?.reasonCode] || '') : '';
    const tail = st.stale
      ? ' — and this ticket has been edited since, so the customer is looking at the older version.'
      : (st.response
        ? ` — customer ${esc(st.response)}${_decName ? ' — signed ' + esc(_decName) : ''}${t.ticketCustomerResponseSource === 'customer' ? ' (in the portal)' : ' (recorded by hand)'}.${st.awaiting ? ' It has gone out again since — no answer to the latest copy yet.' : ''}`
        : ' — no answer from the customer yet.');
    // 🔴 A REJECTION MUST NOT LOOK LIKE SILENCE. Same words in the same grey as
    // "no answer yet" is how a rejection gets read as an unanswered ticket and
    // sits for a week. Red headline, and the reason on its own line.
    // 🔴 `!st.awaiting` on BOTH (TASK-4856): after answer → correct → resend the
    // resend clears `ticketEmailDirtyAt`, so `st.stale` is false and a headline
    // gated only on the response would announce a settled answer over a copy
    // nobody has answered. Same gate as the list pill, so the two cannot
    // disagree; the tail below still names the earlier answer.
    const rejectedHead = st.response === 'rejected' && !st.awaiting;
    // TASK-4854 — AND NEITHER MUST AN ACCEPTANCE. Same failure mode, opposite
    // sign: before this, an accepted ticket wore the identical grey skin and
    // headline as one nobody had answered, and the only difference was a clause
    // at the end of a 0.72rem grey line. That is the fact that says this money
    // is agreed — it is what a biller opens the panel to find.
    //
    // Staleness still WINS over it (the amber "accepted, then edited" state):
    // an agreement against a copy the customer no longer holds is the more
    // urgent thing, and green there would read as settled when it is not.
    const acceptedHead = st.response === 'accepted' && !st.stale && !st.awaiting;
    const headFg = rejectedHead ? '#B91C1C' : (acceptedHead ? '#15803D' : 'var(--text-primary)');
    const headPrefix = rejectedHead ? 'Rejected by the customer — ' : (acceptedHead ? 'Accepted by the customer — ' : '');
    return `<div style="font-weight:600;font-size:0.85rem;color:${headFg};">${headPrefix}${esc(how)} ${esc(when)}${st.to ? ' to ' + esc(st.to) : ''}</div>
      <div style="font-size:0.72rem;color:var(--text-secondary);overflow-wrap:anywhere;">${st.by ? 'by ' + esc(st.by) : ''}${tail}</div>
      ${rejectedHead && _decReason ? `<div style="margin-top:3px;font-size:0.72rem;color:#B91C1C;overflow-wrap:anywhere;white-space:pre-wrap;">${_decLabel ? `<strong>${esc(_decLabel)}:</strong> ` : ''}${esc(_decReason)}</div>` : ''}`;
  })();

  const listHtml = rows === undefined
    ? `<div style="font-size:0.72rem;color:var(--text-secondary);padding:6px 0;">Loading history…</div>`
    : rows === null
      ? `<div style="font-size:0.72rem;color:var(--text-primary);padding:6px 0;">Couldn't load the history. <a href="#" onclick="event.preventDefault();window.BillingModule.loadTicketSendLog('${id}', true);" style="color:var(--brand-blue);font-weight:600;">Retry</a></div>`
      : rows.length === 0
        ? `<div style="font-size:0.72rem;color:var(--text-secondary);padding:6px 0;">Nothing recorded yet.</div>`
        : `<div style="max-height:190px;overflow-y:auto;min-height:0;display:flex;flex-direction:column;gap:6px;padding-right:4px;">
            ${rows.map(r => _ticketSendLogRowHtml(r)).join('')}
          </div>`;

  // 🔴 min-height:0 on the scroll container above is not decoration — a
  // scrollable area inside a flex column silently refuses to scroll without it.
  // TASK-4683 — a rejection tints the whole panel red, ahead of staleness. Every
  // other state here is something WE did or did not do; a rejection is the one
  // where a customer is waiting on us, and it is the state most easily mistaken
  // for "nobody has got back to me".
  //
  // TASK-4854 — and an ACCEPTANCE tints it green, in the same palette the ribbon
  // segment and the list pill already use for accepted (#F0FDF4 / #BBF7D0 /
  // #15803D), so one fact wears one colour everywhere it appears. Ordered AFTER
  // staleness on purpose: "accepted, then edited" stays amber, because a green
  // panel on a ticket the customer no longer holds a matching copy of would read
  // as settled.
  // 🔴 `_rejected` is deliberately NOT gated on `!st.awaiting`, unlike the green
  // and unlike the headline. Green is a claim that the money is agreed, so it
  // has to yield the moment a newer copy is out; red is an ALARM that the
  // customer refused this ticket, and a correction that has been sent but not
  // yet answered has not cleared it. The headline still stops asserting a
  // settled answer and the tail says a new copy is out — the panel keeps the
  // urgency without the false claim.
  const _rejected = st.response === 'rejected';
  const _accepted = st.response === 'accepted' && !st.stale && !st.awaiting;
  const _skinBorder = _rejected ? '#FECACA' : (st.stale ? '#FDE68A' : (_accepted ? '#BBF7D0' : 'var(--border-color)'));
  const _skinBg = _rejected ? '#FEF2F2' : (st.stale ? '#FFFBEB' : (_accepted ? '#F0FDF4' : '#F8FAFC'));
  const _skinFg = _rejected ? '#B91C1C' : (st.stale ? '#B45309' : (_accepted ? '#15803D' : (st.sent ? '#1D4ED8' : 'var(--text-secondary)')));
  const _skinIcon = _rejected ? 'cancel' : (st.stale ? 'mark_email_unread' : (_accepted ? 'task_alt' : (st.sent ? 'mark_email_read' : 'mail_outline')));
  return `
    <div style="padding:10px 14px;border:1px solid ${_skinBorder};background:${_skinBg};border-radius:var(--radius-sm);margin-bottom:8px;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap;">
        <div style="display:flex;align-items:flex-start;gap:10px;min-width:0;">
          <span class="material-icons" style="font-size:20px;color:${_skinFg};">${_skinIcon}</span>
          <div style="min-width:0;">
            <div style="font-size:0.68rem;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:2px;">Customer tracking</div>
            ${head}
          </div>
        </div>
        <div style="display:flex;gap:6px;flex-shrink:0;flex-wrap:wrap;">
          <button type="button" class="btn btn-sm btn-secondary" style="white-space:nowrap;"
            onclick="window.BillingModule.recordTicketSend('${id}')">
            <span class="material-icons" style="font-size:15px;vertical-align:-3px;margin-right:3px;">history_edu</span>Log a send
          </button>
          <button type="button" class="btn btn-sm ${st.stale ? '' : 'btn-secondary'}" style="white-space:nowrap;${st.stale ? 'background:#B45309;color:#fff;border:none;' : ''}"
            onclick="window.BillingModule.showTicketEmailDialog('${id}')">
            <span class="material-icons" style="font-size:15px;vertical-align:-3px;margin-right:3px;">send</span>${st.sent ? 'Resend' : 'Email ticket'}
          </button>
        </div>
      </div>
      <div style="margin-top:8px;border-top:1px solid var(--border-color);padding-top:6px;">${listHtml}</div>
    </div>`;
}

function _ticketSendLogRowHtml(r) {
  const esc = escapeHtml;
  const when = r.occurredAt || r.at;
  let stamp = '';
  try {
    const d = new Date(when);
    stamp = isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { stamp = ''; }
  const inbound = r.direction === 'in';
  // TASK-4714 — a failed attempt reads as a failure, never as a send. It shares
  // the series (one story per ticket) but nothing else: red rail, "Refused"
  // instead of a channel label, and it never claims DispatchDeck sent anything.
  const failed = r.failed === true;
  const verified = !failed && r.source === 'app';
  // TASK-4683 — a REJECTION is not a green "customer replied". Before this the
  // inbound branch painted every answer #15803D, so a rejection and an
  // acceptance were the same colour and the same shape, and the only difference
  // was one word. Rejections get the red rail and the red word, matching the
  // ribbon and the list pill; two surfaces agreeing is the whole point of the
  // one-reader rule this series was built under.
  const rejectedIn = inbound && r.outcome === 'rejected';
  const tag = failed
    ? `<span style="color:#B91C1C;font-weight:700;">Email failed</span>`
    : inbound
      ? `<span style="color:${rejectedIn ? '#B91C1C' : '#15803D'};font-weight:700;">Customer ${esc(r.outcome || 'replied')}</span>`
      : `<span style="color:#1D4ED8;font-weight:700;">${esc(_TICKET_SEND_CHANNEL_LABELS[r.channel] || r.channel || 'Sent')}</span>`;

  // TASK-4683 — the `approval` object `listTicketSendLog` projects. Present on
  // exactly one row per ticket (the submit is idempotent) and null on the ~1,000
  // outbound rows that already exist, so this is additive to every old row.
  //
  // 🔴 EVERY FIELD BELOW IS CUSTOMER-AUTHORED, TYPED INTO A PUBLIC PAGE THAT
  // NEEDS NO ACCOUNT. Escaped without exception, and the signature src is gated
  // to a data:image/ URL rather than trusted because the server capped its size.
  const ap = r.approval || null;
  const apName = ap ? String(ap.signerName || '').trim() : '';
  const apReason = ap ? String(ap.reason || '').trim() : '';
  const apReasonLabel = ap ? (_TICKET_REJECT_REASON_LABELS[ap.reasonCode] || '') : '';
  const approvalHtml = !ap ? '' : `
    ${apName ? `<div style="margin-top:1px;"><span style="color:var(--text-secondary);">Signed</span> <strong>${esc(apName)}</strong></div>` : ''}
    ${apReason ? `<div style="margin-top:1px;color:${rejectedIn ? '#B91C1C' : 'var(--text-primary)'};overflow-wrap:anywhere;white-space:pre-wrap;">${apReasonLabel ? `<strong>${esc(apReasonLabel)}:</strong> ` : ''}${esc(apReason)}</div>` : ''}
    ${_isTicketSignatureDataUrl(ap.signatureDataUrl) ? `<img alt="Customer signature" src="${esc(ap.signatureDataUrl)}" style="display:block;margin-top:3px;max-width:180px;max-height:56px;background:#fff;border:1px solid var(--border-color);border-radius:3px;padding:2px;">` : ''}
    ${ap.ticketVersionHash ? `<div style="margin-top:1px;color:var(--text-secondary);font-size:0.66rem;">Version signed: ${esc(String(ap.ticketVersionHash).slice(0, 12))}${ap.ip ? ` &middot; from ${esc(ap.ip)}` : ''}</div>` : ''}`;
  // 🔴 Every field here is customer- or biller-authored free text rendered on a
  // surface that can show many tickets at once. Escaped without exception.
  return `<div style="border-left:3px solid ${failed ? '#DC2626' : (rejectedIn ? '#DC2626' : (inbound ? '#16A34A' : (verified ? '#3B82F6' : '#94A3B8')))};padding:2px 0 2px 8px;font-size:0.72rem;color:var(--text-primary);">
    <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:baseline;">
      <span style="font-weight:600;">${esc(stamp)}</span>
      ${tag}
      ${failed ? '<span style="color:#B91C1C;">the customer did not get it</span>'
    : (inbound
      // EVIDENCE vs ASSERTION, the same distinction the outbound rows carry:
      // channel 'portal' + source 'customer' is the customer's own click on a
      // tokenized link; anything else inbound is somebody in the office saying
      // they were told. Gated on the fields rather than assumed from `inbound`,
      // because `recordTicketSendEntry` can mint other channels.
      ? (r.channel === 'portal' && r.source === 'customer'
        ? '<span style="color:var(--text-secondary);">in the approval portal</span>'
        : '<span style="color:var(--text-secondary);">recorded by hand</span>')
      : (verified ? '<span style="color:#1D4ED8;">sent by DispatchDeck</span>' : '<span style="color:var(--text-secondary);">logged by hand</span>'))}
      ${r.to ? `<span style="color:var(--text-secondary);overflow-wrap:anywhere;">${esc(r.to)}</span>` : ''}
      ${r.byName ? `<span style="color:var(--text-secondary);">${esc(r.byName)}</span>` : ''}
    </div>
    ${r.note ? `<div style="font-style:italic;margin-top:1px;color:var(--text-primary);overflow-wrap:anywhere;">${esc(r.note)}</div>` : ''}
    ${approvalHtml}
  </div>`;
}

function _refreshTicketTracking(ticketId) {
  const row = document.getElementById('review-ticket-tracking');
  if (row && ReviewState.ticketId === ticketId && ReviewState.ticket) {
    row.innerHTML = _ticketTrackingHtml(ReviewState.ticket);
  }
}

// Fetch the series for ONE ticket. Never called from a list render — the derived
// fields on the ticket doc exist precisely so a list never needs this.
async function loadTicketSendLog(ticketId, force) {
  if (!force && Array.isArray(_ticketSendLogCache[ticketId])) { _refreshTicketTracking(ticketId); return; }
  if (force) { delete _ticketSendLogCache[ticketId]; _refreshTicketTracking(ticketId); }
  const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  if (!tenantId) return;
  try {
    const fn = httpsCallable(functions, 'listTicketSendLog');
    const res = await fn({ tenantId, ticketId });
    _ticketSendLogCache[ticketId] = Array.isArray(res?.data?.entries) ? res.data.entries : [];
  } catch (err) {
    console.error('[Billing] listTicketSendLog failed', err);
    _ticketSendLogCache[ticketId] = null;
  }
  _refreshTicketTracking(ticketId);
  // TASK-4683 — the signer's name and the rejection reason arrive with THIS
  // fetch and live nowhere else, so the view-ticket decision block has to be
  // repainted when it lands. Same one-place reasoning as _applyTicketSendLocal:
  // wiring it at the call sites instead guarantees a third one lands without it.
  _refreshTicketRibbon(ticketId);
}

function _applyTicketSendLocal(ticketId, patch) {
  const apply = (obj) => { if (obj) Object.assign(obj, patch); };
  apply((BillingState.tickets || []).find(x => x.id === ticketId));
  if (ReviewState.ticketId === ticketId) apply(ReviewState.ticket);
  _refreshTicketTracking(ticketId);
  // TASK-4738 — ONE place, so both send paths (the office email dialog and the
  // hand-logged reconciler) move the ribbon. Wiring it at the two call sites
  // instead would guarantee a third one lands without it.
  _refreshTicketRibbon(ticketId);
}

function _ticketLookup(ticketId) {
  return (ReviewState.ticketId === ticketId && ReviewState.ticket)
    ? ReviewState.ticket
    : (BillingState.tickets || []).find(x => x.id === ticketId);
}

function emailReviewTicket() {
  if (ReviewState.ticketId) showTicketEmailDialog(ReviewState.ticketId);
}

// ---------------------------------------------------------------------------
// LOG A SEND — the back-catalogue reconciler.
// ---------------------------------------------------------------------------
// Without this, on the morning this ships every ticket Lacey has already emailed
// through Gmail reads "Not sent" — the exact state she is complaining about with
// a new badge on it. She would re-send things customers already have, or keep
// her own list anyway, and we would not have helped.
//
// The write goes through `recordTicketSendEntry`, never a raw client write: the
// callable validates `channel` against a fixed enum, requires and caps `note`,
// stamps the time, the actor and `source` itself, and REFUSES a client-supplied
// `source` outright so a member can never mint a row claiming DispatchDeck sent
// something.
function recordTicketSend(ticketId) {
  if (document.getElementById('ticket-email-overlay')) return;
  const t = _ticketLookup(ticketId);
  if (!t) { window.showToast?.('Ticket not found', 'error'); return; }
  const esc = escapeHtml;
  const escA = window.escAttr || escapeHtml;
  const customer = (BillingState.customers || []).find(c => c.id === t.customerId) || {};
  const prefillTo = _normTicketEmail(customer.emails?.[0]?.email || customer.email || t.fieldTicketData?.emailSentTo || '');
  const todayStr = formatDateISO(new Date());
  const ticketNo = t.ticketNo || String(t.id || '').slice(0, 8);
  const chanOpts = Object.keys(_TICKET_SEND_CHANNEL_LABELS)
    .filter(k => k !== 'portal')
    .map(k => `<option value="${escA(k)}"${k === 'email' ? ' selected' : ''}>${esc(_TICKET_SEND_CHANNEL_LABELS[k])}</option>`).join('');

  const overlay = document.createElement('div');
  overlay.id = 'ticket-email-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.6);z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:8px;max-width:480px;width:100%;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;">
      <div style="padding:16px 20px;border-bottom:1px solid #E2E8F0;">
        <div style="font-size:16px;font-weight:700;color:#0F172A;">Log a send for ticket #${esc(ticketNo)}</div>
        <div style="font-size:12px;color:#64748b;margin-top:4px;">For anything you sent outside DispatchDeck &mdash; Gmail, a phone call, a printed copy handed over on site.</div>
      </div>
      <div style="padding:16px 20px;overflow-y:auto;min-height:0;display:flex;flex-direction:column;gap:12px;">
        <div style="padding:10px 12px;background:#F1F5F9;border:1px dashed #94A3B8;border-radius:6px;font-size:12px;color:#334155;">
          This records what you tell it. DispatchDeck does not send anything and has no delivery record for it, so it shows as <strong>logged by hand</strong>, never as a DispatchDeck send.
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">How</label>
          <select id="ticket-log-channel" style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;background:#fff;color:#0F172A;">${chanOpts}</select>
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Address <span style="font-weight:400;color:#94A3B8;">(optional)</span></label>
          <input type="email" id="ticket-log-to" value="${escA(prefillTo)}" placeholder="customer@email.com"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;">
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">When</label>
          <input type="date" id="ticket-log-date" value="${escA(todayStr)}" max="${escA(todayStr)}"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;">
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">What happened</label>
          <textarea id="ticket-log-note" rows="3" placeholder="Emailed the PDF to Dee. She said accounting handles it now."
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;"></textarea>
          <div style="font-size:11px;color:#64748b;margin-top:4px;">Required. This is the part a checkbox could never hold.</div>
        </div>
        <div id="ticket-log-error" style="display:none;padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;font-size:12px;color:#991B1B;"></div>
      </div>
      <div style="padding:12px 20px;border-top:1px solid #E2E8F0;display:flex;gap:8px;justify-content:flex-end;">
        <button id="ticket-log-cancel" style="padding:10px 16px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Cancel</button>
        <button id="ticket-log-save" style="padding:10px 20px;background:#334155;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Log it</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const cleanup = () => { try { overlay.remove(); } catch (_) {} };
  const showErr = (msg) => {
    const el = overlay.querySelector('#ticket-log-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  };
  overlay.querySelector('#ticket-log-cancel').addEventListener('click', cleanup);

  overlay.querySelector('#ticket-log-save').addEventListener('click', async () => {
    const btn = overlay.querySelector('#ticket-log-save');
    const channel = String(overlay.querySelector('#ticket-log-channel')?.value || 'email');
    const to = _normTicketEmail(overlay.querySelector('#ticket-log-to')?.value);
    if (to && !_isTicketEmailShaped(to)) { showErr('That does not look like an email address. Leave it blank if there was no email.'); return; }
    const note = String(overlay.querySelector('#ticket-log-note')?.value || '').trim();
    if (!note) { showErr('Say what happened — that is the whole point of the log.'); return; }
    const occurredAt = String(overlay.querySelector('#ticket-log-date')?.value || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(occurredAt)) { showErr('Pick the date it went out.'); return; }

    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      const tenantId = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
      const fn = httpsCallable(functions, 'recordTicketSendEntry');
      await fn({ tenantId, ticketId, channel, to, note, occurredAt });
      cleanup();
      // Optimistic derived stamp, mirroring exactly what the server writes.
      const [y, m, d] = occurredAt.split('-').map(Number);
      _applyTicketSendLocal(ticketId, {
        ticketSendLastAt: Timestamp.fromDate(new Date(Date.UTC(y, m - 1, d, 12, 0, 0))),
        ticketSendLastTo: to,
        ticketSendLastBy: window.AppState?.user?.email || window.AppState?.user?.uid || '',
        ticketSendLastChannel: channel,
        ticketSendLastSource: 'manual',
        ticketSendLogCount: ((_ticketLookup(ticketId) || {}).ticketSendLogCount || 0) + 1,
        ticketEmailDirtyAt: null
      });
      await loadTicketSendLog(ticketId, true);
      window.showToast?.('Logged', 'success');
    } catch (err) {
      console.error('[Billing] recordTicketSendEntry failed:', err);
      showErr(err?.message ? `Not saved — ${err.message}` : 'Not saved. Try again.');
      btn.disabled = false; btn.textContent = 'Log it';
    }
  });
}

// ---------------------------------------------------------------------------
// TASK-4761 — THE ATTACHMENT PICKER, and why it is a picker and not a checkbox.
// ---------------------------------------------------------------------------
// The photos and the scale ticket ARE the backup for a disputed line, so the
// interim copy that used to sit in this dialog — telling the biller documents
// were excluded and to print instead — cost the office a hand re-send every time
// a customer argued. (The spec asserts that sentence is gone from this whole
// file, so it is deliberately not quoted here.)
//
// The reason this is not simply "attach everything" is measured, not defensive.
// Read-only probe of live prod 2026-09-08 (87 tenants, 1,056 tickets, 1,028
// Storage objects sized from bucket metadata): total attachment bytes per ticket
// are p50 0.42 MB but p90 48 MB and max 289 MB, and per FILE p50 3.2 MB / max
// 12.5 MB — raw phone photos, never resized. After base64 (+33%) 42 of the 243
// attachment-carrying tickets blow a 30 MB SendGrid message cap outright. Attach
// blindly and roughly one ticket in six bounces and the customer gets NOTHING.
//
// So the send is all-attached or all-linked, never a mix — a partial set is
// worse than a link, because the recipient cannot tell a photo is missing — and
// this meter tells the biller WHICH she is about to do before she clicks.
const FT_ATTACH_BUDGET_BYTES = Math.floor(20 * 1024 * 1024 * 3 / 4); // server budgets 20MB of BASE64

/**
 * TASK-4761 — every file this ticket can send, using the SAME ids the server
 * resolves (`index.js` `_ftCollectTicketAttachables`): a `documents[]` row by its
 * own id, a bare `photos[]` row as `photo:<index>`.
 *
 * 🔴 `photos[]` is not optional. Clock-out photos are bridged into `documents[]`
 * by openReviewTicket (:17622) and only PERSIST if a biller opens and saves the
 * review modal — so on a ticket nobody reviewed, the driver's photos exist only
 * on `photos[]`. Measured: 18 of the 243 attachment-carrying tickets are in
 * exactly that state, and reading `documents[]` alone would send those with zero
 * evidence while reporting success.
 *
 * If this list and the server's ever drift, the server DROPS the unknown id and
 * returns the count, and the send result below says so. Visible, never silent.
 */
function _ftTicketAttachables(t) {
  const out = [];
  const seen = new Set();
  const docs = Array.isArray(t && t.documents) ? t.documents : [];
  docs.forEach((d, i) => {
    if (!d || typeof d !== 'object') return;
    const url = d.fullUrl ? String(d.fullUrl) : '';
    if (url) seen.add(url);
    out.push({
      id: String(d.id || `doc:${i}`),
      name: String(d.name || 'Document'),
      isPdf: !!d.isPdf || d.mimeType === 'application/pdf',
      bytes: Number(d.sizeBytes) > 0 ? Number(d.sizeBytes) : 0, // 0 = not yet known
      url
    });
  });
  const photos = Array.isArray(t && t.photos) ? t.photos : [];
  photos.forEach((p, i) => {
    if (!p || typeof p !== 'object') return;
    const url = String(p.url || p.fullUrl || '');
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push({ id: `photo:${i}`, name: String(p.name || `Photo ${i + 1}`), isPdf: false, bytes: 0, url });
  });
  return out;
}

function _ftBytesLabel(n) {
  if (!n) return 'size unknown';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0) + ' MB';
}

/**
 * TASK-4761 — what the office is about to do, in words, before she clicks Send.
 * `unknown > 0` is its own state and is NOT rounded down to "it will fit": a
 * clock-out photo carries `sizeBytes: 0` on the ticket row (733 of 1,028 live
 * files), so claiming a total from the known half would under-report by an order
 * of magnitude on exactly the tickets that blow the cap.
 */
function _ftAttachMeter(items, selectedIds) {
  const sel = items.filter(i => selectedIds.includes(i.id));
  const known = sel.filter(i => i.bytes > 0);
  const unknown = sel.length - known.length;
  const bytes = known.reduce((s, i) => s + i.bytes, 0);
  if (sel.length === 0) {
    return { mode: 'none', bytes: 0, unknown: 0, count: 0,
      text: 'Nothing selected — the ticket goes out on its own.' };
  }
  if (bytes > FT_ATTACH_BUDGET_BYTES) {
    return { mode: 'linked', bytes, unknown, count: sel.length,
      text: `${sel.length} file${sel.length === 1 ? '' : 's'} · ${_ftBytesLabel(bytes)}${unknown ? '+' : ''} — too big to attach. The customer gets secure links to all ${sel.length}, never a partial set.` };
  }
  if (unknown > 0) {
    return { mode: 'unknown', bytes, unknown, count: sel.length,
      text: `${sel.length} file${sel.length === 1 ? '' : 's'} · at least ${_ftBytesLabel(bytes || 1)} (${unknown} not sized yet). Anything that fits under 15 MB is attached; if the set is bigger, all of them go as secure links instead.` };
  }
  return { mode: 'attached', bytes, unknown, count: sel.length,
    text: `${sel.length} file${sel.length === 1 ? '' : 's'} · ${_ftBytesLabel(bytes)} — attached to the email.` };
}

/**
 * TASK-4761 — the one line the office reads after a send. It reports the MODE,
 * not just the count, because "4 photos attached" and "4 photos sent as links"
 * are different answers when the customer calls back, and because a dropped or
 * refused file has to be visible at the moment it happens rather than discovered
 * later from an argument.
 */
function _ftSendResultSuffix(a) {
  if (!a || !a.mode || a.mode === 'none') {
    const lost = (a && (a.dropped || a.blocked || a.unreadable)) || 0;
    return lost ? ` — ${lost} attachment(s) could not be sent` : '';
  }
  const bits = [];
  if (a.mode === 'attached') bits.push(`${a.count} document(s) attached`);
  if (a.mode === 'linked') bits.push(`${a.count} document(s) too large to attach — sent as links`);
  // Plain English, not status codes (COD UX Lead): a biller reads this once, on a
  // toast, and has to know whether to do anything about it.
  if (a.dropped) bits.push(`${a.dropped} is no longer on this ticket`);
  if (a.blocked) bits.push(`${a.blocked} could not be sent — the file isn't stored with this ticket`);
  if (a.unreadable) bits.push(`${a.unreadable} could not be opened`);
  return bits.length ? ` — ${bits.join('; ')}` : '';
}

/**
 * TASK-4761 — fill in the sizes the ticket row does not carry, by HEADing the
 * object. Deliberately best-effort: the bucket CORS policy exposes
 * `Content-Length` to the app origins (cors.json) but a failure here is not a
 * failure of the send — the meter falls back to "not sized yet" and the SERVER
 * makes the attach-or-link decision either way. Capped and aborted so a ticket
 * with 30 photos cannot hang the dialog open on a slow office connection.
 */
async function _ftResolveAttachSizes(items, overlay, paint) {
  const pending = items
    .map((d, i) => ({ d, i }))
    .filter(x => !x.d.bytes && /^https?:\/\//i.test(x.d.url))
    .slice(0, 30);
  if (pending.length === 0) return;
  await Promise.allSettled(pending.map(async ({ d, i }) => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 6000);
    try {
      const res = await fetch(d.url, { method: 'HEAD', signal: ctl.signal });
      const len = Number(res.headers.get('content-length'));
      if (res.ok && len > 0) {
        d.bytes = len;
        const cell = overlay.querySelector(`[data-ft-size-idx="${i}"]`);
        if (cell) cell.textContent = `${d.isPdf ? 'PDF' : 'Photo'} · ${_ftBytesLabel(len)}`;
      }
    } catch (_) {
      // size stays unknown — the meter says so rather than guessing low
    } finally {
      clearTimeout(timer);
    }
  }));
  if (document.body.contains(overlay)) paint();
}

function _ftAttachRowsHtml(items) {
  const esc = escapeHtml;
  // Index-based selection, same reason showTicketPrintPicker uses it: a doc id
  // must never be round-tripped through an attribute and come back mutated.
  return items.map((d, idx) => `
    <label style="display:flex;align-items:center;gap:10px;padding:6px 8px;border:1px solid #E2E8F0;border-radius:6px;cursor:pointer;min-height:40px;">
      <input type="checkbox" data-ft-doc-idx="${idx}" checked style="width:16px;height:16px;flex-shrink:0;">
      <div style="flex:1;min-width:0;">
        <div style="font-size:13px;font-weight:600;color:#0F172A;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(d.name)}</div>
        <div style="font-size:11px;color:#64748b;" data-ft-size-idx="${idx}">${d.isPdf ? 'PDF' : 'Photo'} &middot; ${esc(_ftBytesLabel(d.bytes))}</div>
      </div>
    </label>`).join('');
}

// ---------------------------------------------------------------------------
// SEND — the convenience half.
// ---------------------------------------------------------------------------
// The send is the EXISTING `sendFieldTicketEmail` callable, which already built
// the whole field-ticket email and already let an office member send anywhere
// (its destination allowlist sits inside the driver-session branch). Nothing
// about the truck-side auto-copy at close-out changes. On success the server
// appends an `source:'app'` row to the same series a hand-logged send lands in.
// TASK-4757 — "Send me a copy". Who the sender is, for the checkbox label.
// Only a signed-in, non-anonymous user with a VERIFIED login email qualifies:
// the server takes the address from the verified ID token (never from the
// request) and refuses an unverified one, so offering the box to anyone else
// would be a checkbox that silently does nothing. PIN drivers are anonymous
// and have no email, so the box never renders for them.
function _ticketSelfCopyAddress(user) {
  if (!user || user.isAnonymous || user.emailVerified !== true) return '';
  const v = _normTicketEmail(user.email || '');
  return _isTicketEmailShaped(v) ? v : '';
}
// Remembered per user, default OFF. Per-viewer convenience only: every read and
// write is wrapped, and a throwing or empty storage simply means "unticked".
function _ticketSelfCopyPrefKey(user) {
  return `dd.ticketEmail.ccSelf.${(user && user.uid) || 'anon'}`;
}
function _readTicketSelfCopyPref(user) {
  try { return window.localStorage.getItem(_ticketSelfCopyPrefKey(user)) === '1'; } catch (_) { return false; }
}
function _writeTicketSelfCopyPref(user, on) {
  try { window.localStorage.setItem(_ticketSelfCopyPrefKey(user), on ? '1' : '0'); } catch (_) { /* storage blocked — not remembered, still works */ }
}
// PURE. What the toast says about the self-copy. `res` is the server's
// `ccSelf` echo; it is ABSENT from a server that predates TASK-4757, which is
// reported as unconfirmed rather than assumed delivered.
function _ticketSelfCopyResultNote(wanted, res) {
  if (!wanted) return { text: '', warn: false };
  if (!res || typeof res !== 'object') return { text: 'Your copy was not confirmed — check your inbox.', warn: true };
  if (res.added === true || res.reason === 'already-a-recipient') return { text: 'A copy went to you.', warn: false };
  if (res.reason === 'unverified') return { text: 'Your copy was NOT sent — your login email is not verified.', warn: true };
  return { text: 'Your copy was NOT sent.', warn: true };
}

function showTicketEmailDialog(ticketId) {
  // Singleton — defence against a double click, same as showTicketPrintPicker.
  if (document.getElementById('ticket-email-overlay')) return;

  const t = _ticketLookup(ticketId);
  if (!t) { window.showToast?.('Ticket not found', 'error'); return; }

  const st = _ticketDeliveryState(t);
  const esc = escapeHtml;
  const escA = window.escAttr || escapeHtml;
  const customer = (BillingState.customers || []).find(c => c.id === t.customerId) || {};

  // Prefill order: where it went last time (she is usually resending to the same
  // person), then the customer record, then the driver's auto-copy address.
  // Repaired through the server's own normaliser and shown back.
  const rawTo = st.to || customer.emails?.[0]?.email || customer.email || t.fieldTicketData?.emailSentTo || '';
  const prefillTo = _normTicketEmail(rawTo);
  const wasRepaired = !!rawTo && prefillTo !== String(rawTo).trim();

  // CC defaults to the CARRIER's own billing address off the tenant doc — the
  // same source the printed ticket is branded from. 🔴 Never a DispatchDeck
  // address: this is a carrier→customer message and routes by tenant only.
  const ci = (typeof resolveCompanyInfo === 'function') ? resolveCompanyInfo() : {};
  const prefillCc = _normTicketEmail(ci.companyEmail || '');
  // TASK-4757 — the sender's own address, or '' (then no checkbox at all).
  const _selfUser = window.AppState?.user || null;
  const selfCopyTo = _ticketSelfCopyAddress(_selfUser);
  const selfCopyOn = selfCopyTo ? _readTicketSelfCopyPref(_selfUser) : false;
  const companyName = ci.rawCompanyName || ci.companyName || '';
  const ticketNo = t.ticketNo || String(t.id || '').slice(0, 8);
  const prefillSubject = `Field Ticket #${ticketNo}${companyName ? ' — ' + companyName : ''}`;
  // 🔴 TWO DEFAULTS, BECAUSE THE TWO SENDS ASK THE CUSTOMER FOR DIFFERENT THINGS.
  // The plain send is a copy for their records. The approval send puts Accept and
  // Reject buttons in front of them — so the one default ("Let us know if anything
  // needs correcting") was telling an AP clerk to write back while the message she
  // is holding has the two buttons that answer it. The note has to name the
  // buttons or it argues with the document it sits on top of.
  const _ftJobLine = t.jobTitle ? '\nJob: ' + t.jobTitle : '';
  const FT_MSG_OFFICE = `Field ticket #${ticketNo} for your records.${_ftJobLine}\n\nLet us know if anything needs correcting.`;
  const FT_MSG_APPROVAL = `Field ticket #${ticketNo} for your review.${_ftJobLine}\n\nAccept it below if it's right. If something's off, Reject it and tell us what — we'll fix it and resend.`;
  const prefillMessage = FT_MSG_OFFICE;

  const unlocked = t.driverEditLocked !== true;

  // TASK-4761 — the evidence behind the bill. Default is EVERYTHING checked:
  // a disputed line is argued from the photos, so the useful default is "send
  // the backup", and the meter below says what that will actually cost.
  const ftDocs = _ftTicketAttachables(t);

  const overlay = document.createElement('div');
  overlay.id = 'ticket-email-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.6);z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:8px;max-width:560px;width:100%;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;">
      <div style="padding:16px 20px;border-bottom:1px solid #E2E8F0;">
        <div style="font-size:16px;font-weight:700;color:#0F172A;">${st.sent ? 'Resend' : 'Email'} field ticket #${esc(ticketNo)}</div>
        <div style="font-size:12px;color:#64748b;margin-top:4px;">Sends the ticket itself, not an invoice. It does not change the ticket's status, and it is not the customer accepting it.</div>
      </div>
      <div style="padding:16px 20px;overflow-y:auto;min-height:0;display:flex;flex-direction:column;gap:12px;">
        ${st.stale ? `<div style="padding:10px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;font-size:12px;color:#92400E;">This ticket has been edited since it last went out on ${esc(st.at ? st.at.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '')}. Sending now sends the current version.</div>` : ''}
        ${wasRepaired ? `<div style="padding:10px 12px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:6px;font-size:12px;color:#1E40AF;">The stored address was <code>${esc(String(rawTo).trim())}</code>. It will be sent to <strong>${esc(prefillTo)}</strong> &mdash; check it before you send.</div>` : ''}
        ${/* ── TASK-4683 — THE SEND MODE ────────────────────────────────────
             This is the office half of the acceptance portal, and it is a
             CHOICE rather than a second button because of what the other two
             shapes would have cost:

             · A separate "Send for Approval" button somewhere else on the
               ticket screen splits one decision ("get this ticket to the
               customer") across two surfaces, and duplicates the To / CC /
               attachment picker Lacey has already filled in. She is standing in
               this dialog when she decides.
             · Making the existing Email button send an approval request turns
               the thing she presses fifty times a week into something that
               mints a signing link and mails Accept/Reject buttons to a
               customer. That is a surprise with a customer on the other end of
               it, so the plain email stays FIRST and stays CHECKED, and the
               approval choice never comes pre-selected.

             The mode is read at click time into `source`, which the server
             branches on: `_isApproval = _isOffice && data.source === 'approval'`
             (functions/src/index.js). With 'office' every approval variable
             server-side stays empty and the message SendGrid gets is
             byte-identical to what it has always been. */ ''}
        <div id="ticket-email-mode" role="radiogroup" aria-label="How to send this ticket" style="display:flex;flex-direction:column;gap:6px;">
          <label data-ft-mode-card="office" style="display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;cursor:pointer;background:#F8FAFC;">
            <input type="radio" name="ticket-email-mode" value="office" checked style="width:16px;height:16px;margin-top:2px;flex-shrink:0;">
            <span style="min-width:0;">
              <span style="display:block;font-size:13px;font-weight:700;color:#0F172A;">Just email the ticket</span>
              <span style="display:block;font-size:12px;color:#64748b;margin-top:2px;">Sends the ticket as it has always been sent. Nothing for them to click, and no answer is expected back.</span>
            </span>
          </label>
          <label data-ft-mode-card="approval" style="display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;cursor:pointer;background:#fff;">
            <input type="radio" name="ticket-email-mode" value="approval" style="width:16px;height:16px;margin-top:2px;flex-shrink:0;">
            <span style="min-width:0;">
              <span style="display:block;font-size:13px;font-weight:700;color:#0F172A;">Send for approval</span>
              <span style="display:block;font-size:12px;color:#64748b;margin-top:2px;">The same ticket, plus <strong>Accept</strong> and <strong>Reject</strong> buttons and a signing link that works for 30 days. Accepting locks driver edits; rejecting asks them what is wrong.</span>
            </span>
          </label>
        </div>
        <div id="ticket-email-approval-note" style="display:none;padding:10px 12px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:6px;font-size:12px;color:#1E40AF;">
          A new approval link is created when you send, and <strong>any approval link already out for this ticket stops working</strong> — so if you have sent one before, whoever is holding it will need this new one.
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">To</label>
          <input type="email" id="ticket-email-to" value="${escA(prefillTo)}" placeholder="customer@email.com"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;">
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">CC <span style="font-weight:400;color:#94A3B8;">(optional, comma separated)</span></label>
          <input type="text" id="ticket-email-cc" value="${escA(prefillCc)}" placeholder="you@yourcompany.com"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;">
          ${prefillCc ? `<div id="ticket-email-cc-note" style="font-size:12px;color:#475569;margin-top:4px;">Pre-filled with your company&rsquo;s billing address. Delete it if they should not get a copy.</div>` : ''}
          ${selfCopyTo ? `
          <label style="display:flex;align-items:center;gap:8px;margin-top:8px;cursor:pointer;font-size:13px;color:#0F172A;">
            <input type="checkbox" id="ticket-email-cc-self" ${selfCopyOn ? 'checked' : ''} style="width:16px;height:16px;flex-shrink:0;">
            <span style="min-width:0;overflow-wrap:anywhere;">Send me a copy (${esc(selfCopyTo)})</span>
          </label>` : ''}
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Subject</label>
          <input type="text" id="ticket-email-subject" value="${escA(prefillSubject)}"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;">
        </div>
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Message</label>
          <textarea id="ticket-email-message" rows="5"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;">${esc(prefillMessage)}</textarea>
        </div>
        ${ftDocs.length > 0 ? `
        <div style="padding:10px 12px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:6px;">
          <div style="font-size:13px;font-weight:600;color:#475569;margin-bottom:6px;">Photos &amp; documents <span style="font-weight:400;color:#94A3B8;">&mdash; the backup for a disputed line</span></div>
          <div style="display:flex;flex-direction:column;gap:6px;max-height:200px;overflow-y:auto;">${_ftAttachRowsHtml(ftDocs)}</div>
          <div id="ticket-email-attach-meter" style="margin-top:8px;font-size:12px;color:#475569;"></div>
        </div>` : `
        <div style="padding:10px 12px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:6px;font-size:12px;color:#475569;">
          There are no photos or documents on this ticket, so this sends the lines, signatures and stamp only.
        </div>`}
        ${unlocked ? `
        <div style="padding:10px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;font-size:12px;color:#92400E;">
          Sending locks this ticket to drivers — once the customer has it, drivers can't change it. You can still edit it, and unlock it for a driver under "Lock to driver".
        </div>` : ''}
        <div id="ticket-email-error" style="display:none;padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;font-size:12px;color:#991B1B;"></div>
      </div>
      <div style="padding:12px 20px;border-top:1px solid #E2E8F0;display:flex;gap:8px;justify-content:flex-end;">
        <button id="ticket-email-cancel" style="padding:10px 16px;background:#fff;color:#475569;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Cancel</button>
        <button id="ticket-email-send" style="padding:10px 20px;background:#2E7D32;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">${st.sent ? 'Resend' : 'Send'}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const cleanup = () => { try { overlay.remove(); } catch (_) {} };
  const showErr = (msg) => {
    const el = overlay.querySelector('#ticket-email-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  };

  overlay.querySelector('#ticket-email-cancel').addEventListener('click', cleanup);
  // TASK-4757 — remember the tick per user the moment it changes, so closing the
  // dialog with Cancel still keeps the preference.
  overlay.querySelector('#ticket-email-cc-self')?.addEventListener('change', (e) => {
    _writeTicketSelfCopyPref(_selfUser, !!e.target.checked);
  });

  // TASK-4683 — the mode. Read from the DOM at click time, never cached into a
  // closure variable: the radios are the source of truth and a stale copy is
  // how a dialog sends one thing while showing another.
  const ftMode = () => (overlay.querySelector('input[name="ticket-email-mode"]:checked')?.value === 'approval')
    ? 'approval' : 'office';
  const ftPaintMode = () => {
    const approval = ftMode() === 'approval';
    const btn = overlay.querySelector('#ticket-email-send');
    // 🔴 THE BUTTON SAYS WHAT IT WILL DO. "Send" and "Send for approval" are
    // different actions with a customer on the far end, and the label is the
    // last thing she reads before pressing it.
    if (btn) btn.textContent = approval ? 'Send for approval' : (st.sent ? 'Resend' : 'Send');
    const note = overlay.querySelector('#ticket-email-approval-note');
    if (note) note.style.display = approval ? 'block' : 'none';
    // Swap the default note to match the mode — but ONLY while it is still
    // untouched. 🔴 The biller's own typing is never clobbered: the moment the
    // textarea holds anything other than one of the two defaults, this stops
    // rewriting it. Toggling the radio back and forth must not eat a sentence
    // she wrote, and "she flipped the radio after typing" is the ordinary case,
    // not the edge case.
    const msg = overlay.querySelector('#ticket-email-message');
    if (msg && (msg.value === FT_MSG_OFFICE || msg.value === FT_MSG_APPROVAL)) {
      msg.value = approval ? FT_MSG_APPROVAL : FT_MSG_OFFICE;
    }
    overlay.querySelectorAll('[data-ft-mode-card]').forEach((card) => {
      const on = card.getAttribute('data-ft-mode-card') === (approval ? 'approval' : 'office');
      card.style.background = on ? '#F8FAFC' : '#fff';
      card.style.borderColor = on ? '#0F172A' : '#CBD5E1';
    });
  };
  overlay.querySelectorAll('input[name="ticket-email-mode"]').forEach(el => el.addEventListener('change', ftPaintMode));
  ftPaintMode();

  // The success state for an approval send. It REPLACES the dialog rather than
  // closing it, because the link comes back exactly once: `sendFieldTicketEmail`
  // returns `approval: { url, expiresDays }` on this response and the token is
  // never re-readable afterwards (it is minted server-side, stored hashed-by-id
  // in `accessTokens`, and the outbound log row deliberately does not carry it).
  // Close this and the URL is gone — the only way back is another send, which
  // revokes the one you just mailed. So it is shown, and it is copyable.
  const showApprovalLink = (url, days, to) => {
    const card = overlay.firstElementChild;
    if (!card) { cleanup(); return; }
    card.innerHTML = `
      <div style="padding:16px 20px;border-bottom:1px solid #E2E8F0;">
        <div style="font-size:16px;font-weight:700;color:#15803D;display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:20px;">mark_email_read</span>Approval request sent
        </div>
        <div style="font-size:12px;color:#64748b;margin-top:4px;">Field ticket #${esc(ticketNo)} went to <strong>${esc(to)}</strong> with Accept and Reject buttons.</div>
      </div>
      <div style="padding:16px 20px;overflow-y:auto;min-height:0;display:flex;flex-direction:column;gap:12px;">
        <div>
          <label style="font-size:13px;font-weight:600;color:#475569;display:block;margin-bottom:4px;">Their approval link</label>
          <input type="text" id="ticket-email-approval-link" readonly value="${escA(url)}"
            style="width:100%;padding:10px 12px;border:1px solid #CBD5E1;border-radius:6px;font-size:13px;box-sizing:border-box;background:#F8FAFC;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">
          <div style="font-size:12px;color:#64748b;margin-top:6px;">
            Good for ${esc(String(days || 30))} days. Copy it now if you want to text it or paste it into another email &mdash;
            <strong>it is not shown again</strong>, and sending another approval request for this ticket cancels it.
          </div>
        </div>
        <div style="padding:10px 12px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:6px;font-size:12px;color:#475569;">
          Anyone with this link can accept or reject this ticket, so send it the way you would send the ticket itself.
        </div>
      </div>
      <div style="padding:12px 20px;border-top:1px solid #E2E8F0;display:flex;gap:8px;justify-content:flex-end;">
        <button id="ticket-email-copy-link" style="padding:10px 16px;background:#fff;color:#0F172A;border:1px solid #CBD5E1;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Copy link</button>
        <button id="ticket-email-done" style="padding:10px 20px;background:#2E7D32;color:#fff;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;">Done</button>
      </div>`;
    // Listeners, not inline onclick — a URL inside an `onclick="fn('...')"` is a
    // JS string inside an attribute, and no HTML escaper makes that safe
    // ([[lesson_escapehtml_is_not_attribute_safe]]).
    card.querySelector('#ticket-email-done')?.addEventListener('click', cleanup);
    card.querySelector('#ticket-email-copy-link')?.addEventListener('click', async () => {
      const btnC = card.querySelector('#ticket-email-copy-link');
      try {
        await navigator.clipboard.writeText(url);
        if (btnC) btnC.textContent = 'Copied';
      } catch (_) {
        // clipboard is permission-gated and fails on http:// origins. Select the
        // text instead of claiming a copy that did not happen.
        const inp = card.querySelector('#ticket-email-approval-link');
        if (inp) { inp.focus(); inp.select(); }
        if (btnC) btnC.textContent = 'Press Ctrl+C';
      }
    });
  };

  // TASK-4761 — the meter. Index-based lookup back into `ftDocs`, same reason
  // showTicketPrintPicker uses it: a doc id must never be round-tripped through
  // an attribute and come back mutated.
  const ftSelectedIds = () => Array.from(overlay.querySelectorAll('input[data-ft-doc-idx]:checked'))
    .map(el => ftDocs[parseInt(el.getAttribute('data-ft-doc-idx'), 10)]?.id)
    .filter(Boolean);
  const ftPaintMeter = () => {
    const el = overlay.querySelector('#ticket-email-attach-meter');
    if (!el) return;
    const m = _ftAttachMeter(ftDocs, ftSelectedIds());
    el.textContent = m.text;
    el.style.color = m.mode === 'linked' ? '#92400E' : '#475569';
  };
  overlay.querySelectorAll('input[data-ft-doc-idx]').forEach(el => el.addEventListener('change', ftPaintMeter));
  ftPaintMeter();
  // 🔴 733 of the 1,028 live attachment rows carry no usable `sizeBytes` — every
  // clock-out photo is bridged with 0 — so the meter has to go and ask, or it
  // under-reports by an order of magnitude on exactly the tickets that blow the
  // cap. Best-effort and abortable: if it cannot size a file the meter says
  // "not sized yet" rather than guessing, and the server decides regardless.
  // Fire-and-forget with an explicit sink: an unhandled rejection here would be
  // an "Uncaught (in promise)" on a page the smoke test watches for pageerrors.
  _ftResolveAttachSizes(ftDocs, overlay, ftPaintMeter).catch(() => {});

  overlay.querySelector('#ticket-email-send').addEventListener('click', async () => {
    const btn = overlay.querySelector('#ticket-email-send');
    const to = _normTicketEmail(overlay.querySelector('#ticket-email-to')?.value);
    if (!_isTicketEmailShaped(to)) { showErr('That does not look like an email address.'); return; }
    const cc = String(overlay.querySelector('#ticket-email-cc')?.value || '')
      .split(/[,;]/).map(_normTicketEmail).filter(v => _isTicketEmailShaped(v));
    const subject = String(overlay.querySelector('#ticket-email-subject')?.value || '').trim();
    const message = String(overlay.querySelector('#ticket-email-message')?.value || '');
    // TASK-4757 — a FLAG, never an address. The server resolves the copy's
    // address from the caller's verified ID token; the client does not append
    // its own email to `cc`.
    const wantSelfCopy = !!overlay.querySelector('#ticket-email-cc-self')?.checked;
    // TASK-4683 — read the radios, not a cached value.
    const sendMode = ftMode();

    btn.disabled = true;
    const _label = btn.textContent;
    btn.textContent = 'Sending…';
    try {
      const fn = httpsCallable(functions, 'sendFieldTicketEmail');
      // `force` is always true from here: this is a deliberate human click on an
      // address she is looking at, so the driver-side double-send guard (which
      // exists because the phone auto-fires on both Save and Clock Out) must not
      // silently swallow it. The server refuses `force` from a driver session.
      // TASK-4761 — IDS ONLY. The server resolves them against this ticket's own
      // `documents[]`/`photos[]`; it never accepts a storage path or a URL from
      // here, because a doc row is client-writable (the driver arm of tkUpdOk has
      // no field allowlist) and a path from the client is an arbitrary bucket
      // read wearing the office's credentials.
      const docIds = ftSelectedIds();
      // TASK-4683 — `source` is the mode the biller chose. 'office' is the
      // unchanged path (and stays the default); 'approval' is what unlocks the
      // server's `_isApproval` branch, which mints the 30-day token, revokes any
      // prior live link for this ticket, and appends Accept/Reject buttons to
      // the same email body.
      const res = await fn({ ticketId, emailTo: to, cc, subject, message, source: sendMode, force: true, docIds, ...(wantSelfCopy ? { ccSelf: true } : {}) });
      const d = (res && res.data) || {};
      if (d.skipped) {
        // Should be unreachable with force:true — but a skip must NEVER render as
        // a green "Sent". Say what happened.
        showErr('Not sent — the server reported this ticket was already emailed to that address.');
        btn.disabled = false; btn.textContent = _label;
        return;
      }
      // TASK-4683 — the approval URL comes back on THIS response and is never
      // readable again. So on an approval send the dialog is not torn down: it
      // becomes the panel that shows the link. Everything else below (the
      // optimistic stamp, the series reload, the toast, the lock) runs
      // identically either way — the send already happened and the ribbon must
      // move whether or not she has closed the panel yet.
      const _approvalUrl = (d.approval && typeof d.approval.url === 'string') ? d.approval.url : '';
      if (_approvalUrl) showApprovalLink(_approvalUrl, d.approval.expiresDays, to);
      else cleanup();
      // Optimistic derived stamp, mirroring exactly what the server writes.
      _applyTicketSendLocal(ticketId, {
        ticketSendLastAt: Timestamp.now(),
        ticketSendLastTo: to,
        ticketSendLastBy: window.AppState?.user?.email || window.AppState?.user?.uid || '',
        ticketSendLastChannel: 'email',
        ticketSendLastSource: 'app',
        ticketSendLogCount: ((_ticketLookup(ticketId) || {}).ticketSendLogCount || 0) + 1,
        ticketEmailSentTo: [to],
        ticketEmailSentAt: Timestamp.now(),
        ticketEmailSentCount: ((_ticketLookup(ticketId) || {}).ticketEmailSentCount || 0) + 1,
        ticketEmailDirtyAt: null
      });
      await loadTicketSendLog(ticketId, true);
      // TASK-4761 — say what the CUSTOMER got. A green "Sent" that hides a
      // downgrade to links, a dropped id or a path the server refused is the
      // same silent success TASK-4714 spent a whole task removing from the
      // failure path; it does not get to come back in on the success one.
      //
      // 🔴 TASK-4683 — AND A SILENT DOWNGRADE THE OTHER WAY. If she picked
      // approval and the response carries no `approval.url`, the ticket went out
      // as a PLAIN email with no buttons on it: the server's `_isApproval` is
      // `_isOffice && source === 'approval'`, so a driver-shaped session (or a
      // deploy where that branch is not live yet) silently sends the ordinary
      // message. She is told, because she will otherwise sit waiting on an
      // answer nobody was asked for.
      const _a = d.attachments || {};
      const _approvalMissed = sendMode === 'approval' && !_approvalUrl;
      // TASK-4757 — a ticked box whose copy did not go is said, not swallowed.
      const _selfNote = _ticketSelfCopyResultNote(wantSelfCopy, d.ccSelf);
      window.showToast?.((_approvalMissed
        ? `Emailed to ${to}, but WITHOUT the Accept/Reject buttons — no approval link came back. Send it again for approval.`
        : `Field ticket ${_approvalUrl ? 'sent for approval to' : 'emailed to'} ${to}${_ftSendResultSuffix(_a)}`) + (_selfNote.text ? ` ${_selfNote.text}` : ''),
      (_approvalMissed || _selfNote.warn || _a.dropped || _a.blocked || _a.unreadable) ? 'warning' : 'success');

      // TASK-10026 — no "lock when I send" box any more: a successful send
      // locks driver edits on the server (sentLockPatch), every time.
    } catch (err) {
      // A SendGrid failure must say so — never a silent green.
      console.error('[Billing] Field ticket email failed:', err);
      // 🔴 TASK-4683 — the error box may no longer BE there. On an approval send
      // the dialog is replaced by the link panel the moment the callable
      // returns, so anything that throws after that point (the series reload,
      // the driver-edit lock) would call showErr() into a node that has been
      // torn out, print nothing, and leave a green panel over a half-finished
      // action. When the box is gone the failure goes to a toast instead, and it
      // does NOT claim the email failed — by then it did not.
      // Checked against the LIVE document, not the (possibly detached) overlay:
      // on a plain send `cleanup()` has already removed the whole subtree, and
      // `overlay.querySelector` happily finds the error box inside a node nobody
      // can see — which is how a failure gets written to an invisible div.
      const errBox = document.body.contains(overlay) ? overlay.querySelector('#ticket-email-error') : null;
      if (!errBox) {
        window.showToast?.(`The ticket went out, but something after it failed${err?.message ? ' — ' + err.message : '.'}`, 'warning');
        return;
      }
      showErr(err?.message ? `Not sent — ${err.message}` : 'Not sent — the email could not be delivered. Try again, or print the ticket and attach it.');
      if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = _label; }
    }
  });
  // Outside-click does NOT close (matches showTicketPrintPicker) — an accidental
  // dismissal after typing a message is pure loss.
}

// ---------------------------------------------------------------------------
// The delivery filter — an ORTHOGONAL axis, deliberately not a status.
// ---------------------------------------------------------------------------
// 🔴 `BillingState.filter` is the STATUS enum and `filterTicketsByStatus`
// switches on it. Delivery state is a SEPARATE axis: a ticket can be `ready`
// AND unsent, and "ready and unsent" is the most valuable question Lacey asks.
// Folding delivery into the status enum would make that query unaskable. Same
// orthogonal-chip pattern as TASK-4649's Missing Permits chip, applied at the
// same point in renderTicketsList.
//
// 🔴 TASK-4856 — ALL THREE NOW DERIVE FROM `_ticketDeliveryState()`.
// They used to hand-test the raw fields, and the `awaiting` one drifted: it read
// `!!t.ticketSendLastAt && !t.ticketCustomerResponse`, which is permanently
// false once a customer has answered ONCE. After the reject → correct → resend
// loop, a ticket carrying a FRESH link nobody has answered still counted as
// answered and vanished from the chip — the one query this axis exists for
// ("who hasn't answered me?") stopped listing the tickets Lacey had just
// chased. The reader knows the rule (the last send is newer than the last
// answer); a fourth hand-rolled copy of the fields is how it got lost.
//
// 🔴 TASK-4740 — "RELABEL IT SENT" IS SUPERSEDED. DO NOT APPLY IT.
// TASKBOARD TASK-4740 (B) carries a ✅ decision from Chris on 2026-09-08 to
// rename "Awaiting Response" to "Sent" and drop the response half of the
// predicate. That decision was correct FOR ITS OWN PREMISE — on that date
// `ticketCustomerResponse` had no writer, `direction:"in"` existed nowhere in
// the repo, and the chip was a counter that could only go up. The premise is
// dead: TASK-4683 shipped the acceptance portal on 2026-09-09
// (`functions/src/services/ticketApproval.js` writes `direction:'in'` +
// `outcome`), and TASK-4856 replaced the `!response` null-check with the
// send-newer-than-answer comparison below. The chip clears now. The 4740 row
// says so itself: *"When TASK-4683 lands, 'Sent' splits into Sent / Awaiting /
// Answered — 4683 owns that, not this task."* It landed. Renaming this to
// "Sent" today would delete the one distinction 4683 was built to create.
// Verified read-only against live `trippin-trucking` 2026-09-10: 4 tickets
// carry `ticketSendLastAt`, 0 carry `ticketCustomerResponse` — so the answer
// path is live but nobody has exercised it on TITS yet.
const TICKET_DELIVERY_FILTERS = [
  // Chris, 2026-09-08: *"I don't know that the UI makes sense to show 'unsent'
  // since not all tickets even get emailed."* Correct, and the title now says
  // what the predicate actually tests — a ticket handed over on site and logged
  // by hand (TASK-4662's `source:'manual'`, any of the seven channels) is NOT
  // unsent. This counts the absence of ANY recorded delivery, which is the
  // question the chip exists to answer, and after TASK-4740 (C) it counts it
  // over the rows on screen rather than over every ticket ever loaded.
  // TASK-9588 — and it leaves out the never-sent tickets of customers marked
  // "No ticket emails" (paper customers), unless the "N hidden" toggle beside
  // the chip puts them back. `_ticketCountsAsUnsent` is the one predicate.
  { key: 'unsent', icon: 'mail_outline', label: 'Unsent',
    title: 'No delivery recorded on any channel — not emailed, and nobody logged handing it over. Customers marked "No ticket emails" are left out.',
    test: (t) => _ticketCountsAsUnsent(t) },
  { key: 'awaiting', icon: 'hourglass_empty', label: 'Awaiting Response',
    title: 'Sent to the customer, no answer to the latest send yet — clears when they Accept or Reject (TASK-4683)',
    test: (t) => _ticketDeliveryState(t).awaiting },
  { key: 'edited', icon: 'mark_email_unread', label: 'Edited Since Sent',
    title: 'Edited after it went out — the customer is looking at an older version',
    test: (t) => _ticketDeliveryState(t).stale }
];

// TASK-4740 (C) — 🔴 THE CHIP AND THE LIST MUST COUNT THE SAME POPULATION.
// This used to count `(BillingState.tickets || []).filter(t => t.status !== 'deleted')`
// — every loaded ticket — while the list applied the same predicate to the set
// that survives the job / project / customer / status+archived / search /
// missing-permit pipeline. On live `trippin-trucking` (measured read-only
// 2026-09-10, 998 docs) that made the bar read **"Unsent (868)"** beside
// **"847 archived hidden"** while clicking it returned **24 rows**: 868 − 844
// archived-and-unsent = 24, exactly. Chris's 2026-09-08 screenshot is the same
// arithmetic one week earlier — 784 vs 739 hidden vs ~45 rows.
//
// It was never only the archive. Selecting one company, typing in the search
// box, or switching the status filter each moved the list without moving the
// number; archived just dominated because 847 of 872 non-deleted TITS tickets
// are archived. Two predicates over two populations — the count picked the
// bigger one.
//
// THE FIX IS THE COUNTING POINT, NOT THE NUMBER. `renderTicketsList` snapshots
// the population immediately BEFORE it applies the delivery predicate — which
// is the LAST filter in the pipeline — and stores the per-chip tallies. So for
// the ACTIVE chip, count === the row count that follows, by construction and
// not by a second implementation that has to be kept in sync. This is the
// invariant TASK-4649's Missing Permits chip states in its own comment: "same
// predicate as the card border, so the chip count and the filtered list can
// never disagree."
//
// NO NUMBER IS PRINTED WHEN NO POPULATION HAS BEEN MEASURED. During the loading
// skeleton `renderTicketsList` returns early and nulls the tallies; rendering
// "(0)" there would be the same class of lie in the other direction. Labels
// alone are honest, and the real counts land on the same synchronous tick.
function _ticketDeliveryCounts(pop) {
  const src = pop || [];
  const counts = {};
  for (const f of TICKET_DELIVERY_FILTERS) counts[f.key] = src.filter(f.test).length;
  // TASK-9588 — how many never-sent tickets the Unsent chip leaves out because
  // their customer is marked "No ticket emails". Same population as the chips.
  counts.unsentNoEmail = src.filter(_ticketIsPaper).length;
  return counts;
}

function _ticketDeliveryFilterHtml() {
  const active = BillingState.deliveryFilter;
  const esc = window.escAttr || escapeHtml;
  const counts = BillingState._deliveryCounts || null;
  // TASK-9340 — rendered INSIDE `.tickets-toolbar` (the status row), so layout
  // comes from _ensureTickets9340Styles, not an inline full-width row. Compact
  // neutral chips: each keeps its own count and its own click; none is merged
  // into a dropdown or summed with another (Awaiting and Edited overlap).
  //
  // 2026-09-29 restyle: no icons, and a chip at 0 goes QUIET (muted text, faint
  // outline) — still visible and clickable, because "did anything go unsent?"
  // is answered by seeing the 0 (TASK-4662). Three guards, all from COD:
  //  - quiet only on a MEASURED 0; while counts are null (loading) the chip is
  //    neutral, never painted as a fake zero;
  //  - an ACTIVE chip is never quiet, even at 0, or a still-applied filter would
  //    look switched off and the next click would silently clear it;
  //  - the label + wrapper stay inside this returned string, because
  //    renderTicketsList swaps `#tickets-delivery-chips` by outerHTML.
  _ensureTickets9340Styles();
  return `<div class="tickets-delivery-chips" id="tickets-delivery-chips" role="group" aria-label="Customer delivery">
    <span class="tickets-delivery-label" aria-hidden="true">Delivery</span>
    ${TICKET_DELIVERY_FILTERS.map(f => {
      const on = active === f.key;
      const n = counts ? counts[f.key] : null;
      const quiet = !on && n === 0;
      return `
      <button type="button" class="tickets-delivery-chip${on ? ' active' : ''}${quiet ? ' is-zero' : ''}" aria-pressed="${on}" title="${esc(f.title)}"
        onclick="window.BillingModule.switchDeliveryFilter('${f.key}')">${esc(f.label)}${n !== null ? `<span class="tdc-n">${n}</span>` : ''}${on ? '<span class="material-icons" style="font-size:14px;margin-left:2px;">close</span>' : ''}</button>${f.key === 'unsent' ? _unsentNoEmailToggleHtml(counts) : ''}`;
    }).join('')}
  </div>`;
}

// TASK-9588 — THE CHIP SAYS WHAT IT HIDES. Unsent leaves out the never-sent
// tickets of "No ticket emails" customers; this link beside it prints how many
// and puts them back on click, so a flag flip can never make work vanish
// silently. Renders nothing when no measured row is affected.
function _unsentNoEmailToggleHtml(counts) {
  const n = counts && counts.unsentNoEmail;
  if (!n) return '';
  const on = BillingState.unsentIncludeNoEmail === true;
  const label = on ? `Hide ${n} no-email` : `${n} hidden · no-email customers`;
  const tip = on
    ? `Unsent includes ${n} ticket${n !== 1 ? 's' : ''} from customers marked "No ticket emails". Click to leave them out again.`
    : `${n} never-sent ticket${n !== 1 ? 's' : ''} belong to customers marked "No ticket emails" and are left out of Unsent. Click to include them.`;
  return `<button type="button" class="tickets-delivery-hidden" title="${escapeHtml(tip)}" onclick="window.BillingModule.toggleUnsentIncludeNoEmail()"><span class="material-icons" aria-hidden="true">${on ? 'visibility_off' : 'unsubscribe'}</span>${escapeHtml(label)}</button>`;
}

function toggleUnsentIncludeNoEmail() {
  BillingState.unsentIncludeNoEmail = !BillingState.unsentIncludeNoEmail;
  clearTicketSelection();
  renderBillingView();
}

// TASK-9588 — can this user edit the customer doc? UI gate only; the real
// boundary is the firestore.rules customers update arm (crm / dispatch module,
// or dispatcher+). Access = modules: owner, legacy admin (no modules field),
// or a member holding `crm` or `dispatch`. No member doc = legacy single login.
function _canEditCustomerTicketEmailPref() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  const m = A.memberAccess;
  if (m) {
    // COD (Code Reviewer): a blocked or view-and-upload-only member is refused
    // by the rules, so the modal must not offer Save to them.
    if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
    if (m.role === 'owner') return true;
    if (m.viewUploadOnly === true) return false;
    if (m.role === 'admin' && (!Array.isArray(m.modules) || m.modules.length === 0)) return true;
    return Array.isArray(m.modules) && (m.modules.includes('crm') || m.modules.includes('dispatch'));
  }
  return true;
}

// TASK-9588 — the per-customer "No ticket emails" setting, opened from the
// customer row on the Tickets page (the "N unsent" fact or the no-email icon).
// Same field + same audit as the checkbox in the CRM and Dispatch customer
// editors; this is the shortcut so the biller doesn't have to leave Tickets.
function showCustomerTicketEmailPref(customerId) {
  const cust = (BillingState.customers || []).find(c => c.id === customerId);
  if (!cust) { showToast('Customer not found', 'error'); return; }
  document.getElementById('modal-ticket-email-pref')?.remove();
  const canEdit = _canEditCustomerTicketEmailPref();
  const off = cust.ticketEmailsOff === true;
  const overlay = document.createElement('div');
  overlay.id = 'modal-ticket-email-pref';
  overlay.className = 'modal';
  overlay.dataset.customerId = customerId;
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:9999;';
  overlay.innerHTML = `
    <div class="modal-content modal-small" style="max-width:440px;padding:var(--spacing-xl);">
      <div style="display:flex;align-items:center;gap:var(--spacing-md);margin-bottom:var(--spacing-lg);">
        <span class="material-icons" style="font-size:28px;color:var(--brand-blue);">unsubscribe</span>
        <h3 style="font-size:1.125rem;font-weight:600;margin:0;color:var(--text-primary);overflow-wrap:anywhere;">Ticket emails — ${escapeHtml(cust.name || 'Customer')}</h3>
      </div>
      <label style="display:flex;align-items:center;gap:10px;min-height:44px;cursor:${canEdit ? 'pointer' : 'not-allowed'};color:var(--text-primary);font-weight:500;">
        <input type="checkbox" id="tep-off" ${off ? 'checked' : ''} ${canEdit ? '' : 'disabled'} style="width:20px;height:20px;">
        <span>Don't email tickets to this customer</span>
      </label>
      <p style="font-size:0.8rem;color:var(--text-secondary);margin:4px 0 0 30px;line-height:1.45;">Hides the 'unsent' count for this customer. You can still email a ticket by hand.</p>
      <p id="tep-contract-note" style="display:none;font-size:0.8rem;color:#92400E;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-sm);padding:6px 10px;margin:10px 0 0;"></p>
      ${canEdit ? '' : `<p style="font-size:0.8rem;color:var(--text-primary);margin:10px 0 0;">You need Customers (CRM) or Dispatch access to change this.</p>`}
      <div style="display:flex;gap:var(--spacing-md);justify-content:flex-end;margin-top:var(--spacing-xl);">
        <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-ticket-email-pref').remove()">${canEdit ? 'Cancel' : 'Close'}</button>
        ${canEdit ? `<button type="button" class="btn btn-primary" id="tep-save" onclick="window.BillingModule.saveCustomerTicketEmailPref()">Save</button>` : ''}
      </div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  _fillTicketEmailContractNote(customerId, 'tep-contract-note', 'tep-off', () => document.getElementById('modal-ticket-email-pref')?.dataset.customerId === customerId);
}

// TASK-9588 (spec item 7) — the flag does not touch the driver close-out box,
// which the contract's `fieldTicketSettings.requireEmailCopy` still governs. If
// a contract asks drivers for an email copy, say so next to the checkbox.
async function _fillTicketEmailContractNote(customerId, noteId, boxId, stillOpen) {
  try {
    const snap = await getDocs(collection(db, 'tenants', getTenant(), 'customers', customerId, 'contracts'));
    if (!stillOpen()) return;
    const names = snap.docs.map(d => d.data())
      .filter(c => c && c.fieldTicketSettings && c.fieldTicketSettings.requireEmailCopy === true)
      .map(c => c.title || c.name || 'a contract');
    const note = document.getElementById(noteId);
    const box = document.getElementById(boxId);
    if (!note || !box || !names.length) return;
    note.textContent = `Drivers are still asked to email a copy on ${names.length === 1 ? 'contract ' + names[0] : names.length + ' contracts (' + names.join(', ') + ')'}.`;
    const sync = () => { note.style.display = box.checked ? '' : 'none'; };
    box.addEventListener('change', sync);
    sync();
  } catch (e) {
    console.warn('[Billing] ticket-email contract note skipped:', e && e.message);
  }
}

async function saveCustomerTicketEmailPref() {
  const overlay = document.getElementById('modal-ticket-email-pref');
  const customerId = overlay && overlay.dataset.customerId;
  if (!customerId) return;
  const cust = (BillingState.customers || []).find(c => c.id === customerId);
  const from = !!(cust && cust.ticketEmailsOff === true);
  const to = document.getElementById('tep-off')?.checked === true;
  if (from === to) { overlay.remove(); return; }
  const btn = document.getElementById('tep-save');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await updateDoc(doc(db, 'tenants', getTenant(), 'customers', customerId), { ticketEmailsOff: to, updatedAt: Timestamp.now() });
    // Customer id + old/new boolean only — no name, no addresses (no PII).
    window.logAudit?.('crm', 'CUSTOMER_TICKET_EMAILS_TOGGLE', { customerId, from, to });
    overlay.remove();
    showToast(to ? 'Ticket emails off for this customer' : 'Ticket emails back on for this customer', 'success');
  } catch (e) {
    console.error('[Billing] save ticketEmailsOff failed:', e);
    if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
    showToast(e && e.code === 'permission-denied'
      ? 'You need Customers (CRM) or Dispatch access to change this'
      : 'Could not save — check your connection and try again', 'error');
  }
}

// ---------------------------------------------------------------------------
// TASK-9654 — QuickBooks AGING filter (Chris's ask 2: "old invoices that need
// to be paid"). COD UX: a filter on the receivables list, not a table inside
// the paid-sync modal. The buckets are READ from QuickBooks by the paid sync
// (balance + QBO DueDate on its last run, report.aging) — never re-computed
// from DD data here. A partly-paid invoice is open with its remaining balance.
// Linked tickets are mostly archived, so while a bucket is on it replaces the
// status/archived scope (an open QuickBooks invoice is open whatever DD's
// status says); every other filter (company, project, search, delivery) still
// composes, and the CSV export runs the same predicate.
// ---------------------------------------------------------------------------
const QBO_AGING_FILTERS = [
  { key: 'current', label: 'Current', title: 'Open in QuickBooks, not yet past its due date' },
  { key: '1-30', label: '1–30', title: 'Open in QuickBooks, 1–30 days past due' },
  { key: '31-60', label: '31–60', title: 'Open in QuickBooks, 31–60 days past due' },
  { key: '61-90', label: '61–90', title: 'Open in QuickBooks, 61–90 days past due' },
  { key: '90+', label: '90+', title: 'Open in QuickBooks, more than 90 days past due' },
];
// ticketId -> aging row, from the paid sync's last report. null = not loaded.
function _qboAgingRowOf(t) {
  const m = BillingState._qboAgingByTicket;
  return m && t ? (m.get(t.id) || null) : null;
}
function _ticketInQboAgingFilter(t) {
  const k = BillingState.qboAgingFilter;
  if (!k) return true;
  const r = _qboAgingRowOf(t);
  return !!r && r.bucket === k;
}
// The list's status/archived scope, or — while an aging bucket is on — the
// aging predicate instead. ONE function for the list and the CSV export.
function _applyStatusOrAgingScope(tickets) {
  if (BillingState.qboAgingFilter) return tickets.filter(_ticketInQboAgingFilter);
  return (BillingState.filter === 'archived')
    ? tickets.filter(t => t.status === 'archived' || t.status === 'locked')
    : filterTicketsByStatus(tickets, BillingState.filter, { showArchived: BillingState.showArchived });
}
function _qboAgingCounts(tickets) {
  const m = BillingState._qboAgingByTicket;
  if (!m) return null;
  const c = {};
  for (const f of QBO_AGING_FILTERS) c[f.key] = 0;
  for (const t of tickets || []) { const r = m.get(t.id); if (r && c[r.bucket] != null) c[r.bucket] += 1; }
  return c;
}
// Rendered only once a report with aging exists (QuickBooks connected + the
// paid sync has run). Same chip classes as Delivery, so the same touch rules.
function _qboAgingFilterHtml() {
  if (!BillingState._qboAgingByTicket) return '<div id="tickets-qbo-aging-chips" hidden></div>';
  const active = BillingState.qboAgingFilter;
  const counts = BillingState._qboAgingCounts || null;
  const asOf = BillingState._qboAgingAsOf ? ` — as of ${BillingState._qboAgingAsOf}` : '';
  const bal = BillingState._qboAgingBalance || {};
  return `<div class="tickets-delivery-chips" id="tickets-qbo-aging-chips" role="group" aria-label="QuickBooks aging">
    <span class="tickets-delivery-label" aria-hidden="true" title="Open invoices in QuickBooks by days past due, read by the paid sync${escapeHtml(asOf)}">QB aging</span>
    ${QBO_AGING_FILTERS.map(f => {
      const on = active === f.key;
      const n = counts ? counts[f.key] : null;
      const quiet = !on && n === 0;
      const b = bal[f.key];
      const tip = `${f.title}${b && b.invoices ? ` · ${b.invoices} invoice${b.invoices !== 1 ? 's' : ''}, ${formatCurrency(b.balance)} open` : ''}${asOf}`;
      return `<button type="button" class="tickets-delivery-chip${on ? ' active' : ''}${quiet ? ' is-zero' : ''}" aria-pressed="${on}" title="${escapeHtml(tip)}"
        onclick="window.BillingModule.switchQboAgingFilter('${f.key}')">${escapeHtml(f.label)}${n !== null ? `<span class="tdc-n">${n}</span>` : ''}${on ? '<span class="material-icons" style="font-size:14px;margin-left:2px;">close</span>' : ''}</button>`;
    }).join('')}
  </div>`;
}
let _qboAgingLoading = false;
// One read per Billing mount: the paid sync's stored report (server callable;
// integrations/* is not client-readable). Silent when QuickBooks is not
// connected or the login lacks billing/bi — the chips simply never appear.
async function _loadQboAging() {
  if (_qboAgingLoading || BillingState._qboAgingByTicket || BillingState._qboAgingTried) return;
  if (!window.AppState?.tenantData?.qboConnected) return;
  _qboAgingLoading = true;
  BillingState._qboAgingTried = true;
  try {
    const res = await httpsCallable(functions, 'qboPaidSyncStatus')({});
    const rep = res && res.data && res.data.report;
    if (!rep || rep.status !== 'ok' || !Array.isArray(rep.aging)) return;
    const m = new Map();
    for (const a of rep.aging) for (const id of (Array.isArray(a.ticketIds) ? a.ticketIds : [])) m.set(id, a);
    BillingState._qboAgingByTicket = m;
    BillingState._qboAgingAsOf = rep.agingAsOf || (rep.ranAt ? String(rep.ranAt).slice(0, 10) : '');
    BillingState._qboAgingBalance = (rep.summary && rep.summary.aging) || {};
    renderTicketsList();
  } catch (e) {
    console.warn('[Billing] QuickBooks aging not loaded:', e && e.code);
  } finally {
    _qboAgingLoading = false;
  }
}
function switchQboAgingFilter(key) {
  BillingState.qboAgingFilter = (BillingState.qboAgingFilter === key) ? null : key;
  clearTicketSelection();
  // Open QuickBooks invoices from months back sit on tickets past the live window.
  if (BillingState.qboAgingFilter) ensureFullTicketHistory();
  renderBillingView();
}

// Click the active chip again to clear it — no filter is the default, so a
// one-truck owner-operator never has to dismiss anything to see all their work.
function switchDeliveryFilter(key) {
  BillingState.deliveryFilter = (BillingState.deliveryFilter === key) ? null : key;
  clearTicketSelection();
  // A ticket emailed months ago and still unbilled is exactly what these views
  // are for; the bounded live window would render a false "none".
  if (BillingState.deliveryFilter) ensureFullTicketHistory();
  renderBillingView();
}

function _driverLockRowHtml(t) {
  const dLocked = t.driverEditLocked === true;
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 14px;border:1px solid ${dLocked ? '#FCA5A5' : 'var(--border-color)'};background:${dLocked ? '#FEF2F2' : '#F8FAFC'};border-radius:var(--radius-sm);margin-bottom:8px;">
      <div style="display:flex;align-items:center;gap:10px;min-width:0;">
        <span class="material-icons" style="font-size:20px;color:${dLocked ? '#DC2626' : 'var(--text-secondary)'};">${dLocked ? 'lock' : 'lock_open'}</span>
        <div style="min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Lock to driver (read-only)</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);">${dLocked
            ? (t.driverEditLockedBy === 'ticket-sent'
              ? 'Locked because it was sent to the customer. Drivers can no longer edit it. You can still edit it here.'
              : 'Drivers can no longer edit this ticket. You can still edit it here.')
            : 'Drivers can still edit this ticket while driver edits are enabled. Lock it once you start reviewing.'}</div>
        </div>
      </div>
      <button type="button" class="btn btn-sm ${dLocked ? 'btn-secondary' : ''}" style="white-space:nowrap;flex-shrink:0;${dLocked ? '' : 'background:#DC2626;color:#fff;border:none;'}"
        onclick="window.BillingModule.toggleDriverEditLock('${t.id}')">
        <span class="material-icons" style="font-size:15px;vertical-align:-3px;margin-right:3px;">${dLocked ? 'lock_open' : 'lock'}</span>${dLocked ? 'Unlock' : 'Lock'}
      </button>
    </div>`;
}

// TASK-566 — flip the per-ticket driver lock. Writes immediately (not via
// headerEdits/save) so the firestore.rules gate takes effect the moment the
// biller clicks Lock — the whole point is locking the driver out WHILE the
// biller continues editing. Audits to jobAuditLog (tickets audit there, not
// the general auditLog).
async function toggleDriverEditLock(ticketId) {
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const listTicket = BillingState.tickets.find(t => t.id === ticketId);
  const cur = (ReviewState.ticketId === ticketId ? ReviewState.ticket?.driverEditLocked : listTicket?.driverEditLocked) === true;
  const newVal = !cur;
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      driverEditLocked: newVal,
      driverEditLockedAt: newVal ? Timestamp.now() : null,
      driverEditLockedBy: newVal ? (window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown') : null,
      updatedAt: Timestamp.now()
    });
    if (listTicket) listTicket.driverEditLocked = newVal;
    if (ReviewState.ticketId === ticketId && ReviewState.ticket) ReviewState.ticket.driverEditLocked = newVal;
    // Surgical row refresh — don't re-render the whole modal mid-edit
    const row = document.getElementById('review-driver-lock-row');
    if (row && ReviewState.ticketId === ticketId) row.innerHTML = _driverLockRowHtml(ReviewState.ticket);
    try {
      await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
        action: newVal ? 'TICKET_DRIVER_LOCK' : 'TICKET_DRIVER_UNLOCK',
        source: 'billing-review',
        timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        ticketId,
        ticketNo: (ReviewState.ticket?.ticketNo ?? listTicket?.ticketNo) || null,
        jobId: (ReviewState.ticket?.jobId ?? listTicket?.jobId) || null,
        customerName: (ReviewState.ticket?.customerName ?? listTicket?.customerName) || null
      });
    } catch (ae) { console.warn('[Billing] Driver-lock audit log failed:', ae); }
    window.showToast?.(newVal ? 'Ticket locked to drivers (read-only)' : 'Ticket unlocked — drivers can edit again', 'success');
  } catch (err) {
    console.error('[Billing] Error toggling driver edit lock:', err);
    window.showToast?.('Error updating driver lock', 'error');
  }
}

// TASK-870 — soft visual cue (not a block, not an auto-pass) on a billable line that
// will invoice the customer $0 because no rate resolved (no contract + no defaultRates).
// Passive by design: the biller's review of every ticket is the control — this only
// makes a $0 line easier for the eye to catch; it does NOT approve, gate, or replace
// that review. Returns the class only for a line with real hours/qty but a $0 rate, so
// legitimately-$0 lines (downtime/standby/inactive) don't light up.
function _reviewRateZeroClass(l) {
  if (!l) return '';
  const billable = (Number(l.hours) || 0) > 0 || (Number(l.quantity) || 0) > 0;
  return (billable && !(Number(l.rate) || 0)) ? ' review-rate-zero' : '';
}

// TASK-1615-B — "this mileage came off a bad location" warning. It lands at the TOP
// of Review Ticket (slot A4, above the key fields) because this is the one screen
// where the invoiced quantity is actually read and changed. The dispatch map header
// — where TASK-1615 counts dropped stops — is not a screen a biller ever opens, and
// a console warning is not a warning. Deliberately covers EMPLOYEE and EQUIPMENT
// per-mile lines in a SINGLE banner: the employee section has no banner slot, and
// splitting the message across two sections is how one half gets missed.
//
// It renders on locked/invoiced tickets too (without the action button) — if an
// inflated mileage already went out the door, the biller opening that ticket to ask
// "why is this invoice so big" is exactly who needs to see this.
//
// Factored out of renderReviewTicket so _refreshRouteMileageBanner below can also
// re-run it in place after a line edit. Returns '' when there is nothing to warn
// about.
function _renderRouteMileageBannerHtml() {
  const t = ReviewState.ticket;
  if (!t) return '';
  const _rmBad = Array.isArray(ReviewState.routeBadStops) ? ReviewState.routeBadStops : [];
  const _rmOffenders = _rmSuspectLines(
    [...ReviewState.employeeLines, ...ReviewState.equipmentLines],
    _rmBad.length > 0
  );
  if (_rmOffenders.length === 0) return '';
  const _rmReadOnly = isReviewReadOnly(t);
  const _rmWhere = _rmBad.length > 0
    ? `${_rmBad.length === 1 ? 'Destination' : 'Destinations'} <strong>${escapeHtml(_rmBadStopNames(_rmBad))}</strong> on this job ${_rmBad.length === 1 ? 'has a coordinate that isn\'t a real point on earth' : 'have coordinates that aren\'t real points on earth'}`
    : 'This ticket was built from a route with a bad destination coordinate';
  const _rmRows = _rmOffenders.map(l => {
    const _rmName = l.type === 'EMPLOYEE'
      ? getEmployeeDisplayName(l)
      : (getEquipmentDisplayName(l) || l.assetName || 'Equipment');
    const _rmQty = Number(l.quantity) || 0;
    return `
      <div style="margin-top:8px;">
        <div><strong>${escapeHtml(_rmName)}</strong> is set to <strong>${_rmQty.toLocaleString()} mi</strong> at $${Number(l.rate) || 0}/${escapeHtml(l.rateUnit || 'Per Mile')}${_rmQty === 0 ? ' — the route was too broken to measure at all, so it bills $0' : ''}. Type the real miles on the line below, or confirm this number is right.</div>
        ${_rmReadOnly ? '' : `<button style="margin-top:6px;padding:4px 12px;font-size:0.78rem;font-weight:600;background:transparent;color:#b45309;border:1px solid #b45309;border-radius:6px;cursor:pointer;"
          onclick="window.BillingModule.markRouteMileageOk(${l._localId})">Mileage is correct</button>`}
      </div>`;
  }).join('');
  return `
    <div id="review-route-mileage-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid #f59e0b;border-left:4px solid #f59e0b;border-radius:var(--radius-sm);background:#fffbeb;color:#92400e;font-size:0.82rem;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
        <span class="material-icons" style="font-size:18px;">wrong_location</span>
        Mileage came from a bad location
      </div>
      <div style="margin-top:4px;">${_rmWhere}, so the Per Mile quantity that was filled in from it can be thousands of miles off — in either direction. Nothing has been changed for you; the number below is exactly what was auto-filled.${_rmReadOnly ? ' This ticket is locked, so if it was already invoiced the correction has to go out as a credit.' : ' This ticket can\'t be marked Ready or invoiced until the mileage is corrected or confirmed.'}</div>
      ${_rmRows}
    </div>
  `;
}

// Re-evaluate the banner after a line edit, without a full modal re-render. A
// warning that stays up after the biller has ALREADY fixed the number is worse than
// no warning — it is exactly what teaches people to stop reading warnings. Three
// outcomes, all of which have to work: it disappears (they typed the real miles —
// the intended fix, and it must cost them no extra click), it updates (they typed a
// different number that is still the auto-filled one), or it APPEARS (they priced a
// per-mile line that was $0, or switched a line's unit to Per Mile, on a job whose
// route is bad). The re-insert anchors on .review-key-fields because that block is
// what immediately follows slot A4 in renderReviewTicket.
function _refreshRouteMileageBanner() {
  if (!ReviewState.ticket) return;
  let html = '';
  try {
    html = _renderRouteMileageBannerHtml();
  } catch (err) {
    // Leave whatever is on screen rather than blanking a money warning on a throw.
    console.warn('[Billing] route-mileage banner refresh failed:', err);
    return;
  }
  const existing = document.getElementById('review-route-mileage-banner');
  if (existing) {
    if (html) existing.outerHTML = html; else existing.remove();
    return;
  }
  if (!html) return;
  const anchor = document.querySelector('#review-ticket-body .review-key-fields');
  if (anchor) anchor.insertAdjacentHTML('beforebegin', html);
}

// TASK-9246 — say out loud what the app repaired on open.
//
// The old design told the user about the heal by accusing them of it ("You have
// unsaved changes") at the moment they tried to LEAVE. This says it plainly, at
// the moment it happens, in the modal, next to the numbers it changed — and in
// the past tense, because by the time this renders the repair is already saved.
// It only appears when something actually moved; a silent no-op heal renders
// nothing, so this is not a banner billers learn to scroll past.
// One entry per LINE, not per heal pass. An EQUIPMENT line can be healed twice
// on the same open (0-hour derivation, then the rounding stamp), and counting it
// twice would make the banner — and the failed-save prompt — overstate what
// happened. A banner that inflates its own numbers is the same sin as a prompt
// that invents an edit. First `fromHours` seen, last `toHours` written.
function _mergedSystemHeals() {
  const out = new Map();
  for (const h of (Array.isArray(ReviewState.systemHeals) ? ReviewState.systemHeals : [])) {
    const key = h.lineId || `local:${h.label}:${h.type}`;
    const prev = out.get(key);
    if (prev) {
      prev.toHours = h.toHours;
      prev.hoursChanged = prev.fromHours !== h.toHours;
      if (h.kind === 'half-hour-rounding') prev.kind = 'half-hour-rounding';
    } else {
      out.set(key, { ...h });
    }
  }
  return [...out.values()];
}

function _renderHoursHealBannerHtml() {
  const moved = _mergedSystemHeals().filter(h => h.hoursChanged);
  if (moved.length === 0) return '';
  const _state = ReviewState.healUnpersisted; // null | 'locked' | 'error'
  const _rows = moved.map(h => `
    <div style="margin-top:4px;">
      <strong>${escapeHtml(h.label)}</strong> — ${h.fromHours === 0 ? 'had clock times but 0 hours' : `was ${h.fromHours} h`}, now <strong>${h.toHours} h</strong>${h.kind === 'half-hour-rounding' ? ' (half-hour rounding from the price book)' : ' (from its clock in/out)'}.
    </div>`).join('');
  // Palette: light background, so an explicit DARK ink — never --text-muted.
  const _skin = _state === 'error'
    ? { bg: '#fef2f2', edge: '#dc2626', ink: '#991b1b', icon: 'error_outline' }
    : _state === 'locked'
      ? { bg: '#f8fafc', edge: '#64748B', ink: '#334155', icon: 'lock' }
      : { bg: '#eff6ff', edge: '#3B82F6', ink: '#1e3a8a', icon: 'auto_fix_high' };
  const _head = _state === 'error'
    ? `Recalculated ${moved.length === 1 ? 'an hours line' : `${moved.length} hours lines`} — but could NOT save it`
    : _state === 'locked'
      ? `Recalculated ${moved.length === 1 ? 'an hours line' : `${moved.length} hours lines`} for display only`
      : `Recalculated ${moved.length === 1 ? '1 line' : `${moved.length} lines`} from the clock times`;
  const _why = _state === 'error'
    ? 'The corrected hours are on screen but the save failed, so the stored ticket still bills these lines at $0. Press Save to write them, or reopen the ticket to retry.'
    : _state === 'locked'
      ? 'This ticket is locked, so the correction could not be written. If it was already invoiced, the difference has to go out as a credit.'
      : 'These lines were stored with 0 hours, which bills $0. The corrected hours have been saved to the ticket — you did not change anything and there is nothing to discard.';
  return `
    <div id="review-hours-heal-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid ${_skin.edge};border-left:4px solid ${_skin.edge};border-radius:var(--radius-sm);background:${_skin.bg};color:${_skin.ink};font-size:0.82rem;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
        <span class="material-icons" style="font-size:18px;">${_skin.icon}</span>
        ${_head}
      </div>
      ${_rows}
      <div style="margin-top:6px;">${_why}</div>
    </div>
  `;
}

function renderReviewTicket() {
  const body = document.getElementById('review-ticket-body');
  if (!body) return;

  // Preserve unsaved textarea values before re-render (onchange only fires on blur)
  // TASK-10036 — only from a box the user could type in, and only once it
  // differs from what it opened with (or was already edited). Copying the DOM
  // value in unconditionally made EVERY save write notes, edited or not.
  const existingNotes = document.getElementById('review-ticket-notes');
  if (existingNotes && existingNotes.value !== undefined && !existingNotes.disabled
      && _reviewNotesBoxEdited(existingNotes.value)) {
    ReviewState.headerEdits.notes = existingNotes.value;
    ReviewState.headerEdits.workPerformed = existingNotes.value;
    ReviewState.ticket.notes = existingNotes.value;
    ReviewState.ticket.workPerformed = existingNotes.value;
  }

  const t = ReviewState.ticket;
  let html = '';

  // A. Ticket identity banner
  const eTicketBadge = t.eTicketSubmissionId ? ' <span class="eticket-source-badge"><span class="material-icons">description</span>E-Ticket</span>' : '';
  // Normalize ONLY truly-rogue statuses (e.g. legacy "pending") to "draft".
  // TASK-1167 — archived/locked MUST pass through untouched: the old
  // `!draft && !ready ⇒ draft` coercion silently turned an archived (often already
  // QBO-invoiced) ticket into an editable draft that saved back as draft/ready with
  // no warning (Simonson #100239). Keep every known status; the read-only lock below
  // (isReviewReadOnly) governs whether it can be edited.
  const KNOWN_TICKET_STATUSES = ['draft', 'ready', 'archived', 'locked'];
  const normalizedStatus = KNOWN_TICKET_STATUSES.includes(t.status) ? t.status : 'draft';
  if (normalizedStatus !== t.status) {
    // TASK-9246 — the APP is normalizing a rogue status here, not the biller.
    // Staged identically; it just no longer arms the discard prompt. See
    // _setReviewHeaderFieldSystem for the measurement behind this.
    _setReviewHeaderFieldSystem('status', normalizedStatus);
    t.status = normalizedStatus;
  }
  const statusClass = normalizedStatus === 'ready' ? 'review-status-ready' : 'review-status-draft';
  // Compute the review date once; formatDateInput now returns '' for a missing/invalid
  // date, so render an em-dash instead of the literal "Invalid Date" string.
  const reviewDateStr = formatDateInput(t.date);
  const reviewDateDisplay = reviewDateStr
    ? new Date(reviewDateStr + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—';
  // TASK-1166 — the banner now carries the running grand total + customer/job so a
  // biller sees "what is this ticket worth / for whom" WITHOUT scrolling to the
  // bottom bar. Computed up-front (calcReview* are hoisted fn declarations); the
  // live figure is kept in sync by renderReviewTotals() writing #review-banner-total.
  const _bannerSubtotal = calcReviewSubtotal();
  const _bannerTaxRes = calcReviewTax();
  const _bannerGrandTotal = _bannerSubtotal + _bannerTaxRes.taxAmount;
  const _bannerCustomerName = t.customerId ? (BillingState.customers.find(c => c.id === t.customerId)?.name || '') : '';
  const _bannerJobObj = t.jobId ? BillingState.jobs.find(j => j.id === t.jobId) : null;
  const _bannerJobName = _bannerJobObj ? (_bannerJobObj.title || _bannerJobObj.name || '') : '';
  html += `
    <div class="review-ticket-banner">
      <div class="review-banner-top">
        <div class="review-banner-left">
          <span class="review-ticket-number">#${t.ticketNo || '-'}</span>
          ${eTicketBadge}
          <select id="review-status" class="review-status-pill ${statusClass}"
            aria-label="Ticket status" title="Ticket status — Draft or Ready"
            onchange="window.BillingModule.updateReviewHeaderField('status', this.value); this.className='review-status-pill '+(this.value==='ready'?'review-status-ready':'review-status-draft');">
            <option value="draft" ${normalizedStatus === 'draft' ? 'selected' : ''}>Draft</option>
            <option value="ready" ${normalizedStatus === 'ready' ? 'selected' : ''}>Ready</option>
          </select>
          ${normalizedStatus === 'ready' && t.readyByName ? `<span class="review-ready-by" title="Marked ready by ${escapeHtml(t.readyByName)}" style="font-size:0.8rem;font-weight:600;color:rgba(255,255,255,0.85);max-width:130px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;vertical-align:middle;">${escapeHtml(_formatReadyByShort(t.readyByName))}</span>` : ''}
        </div>
        <div class="review-banner-right" style="display:flex;align-items:center;gap:8px;">
          <span id="review-date-display" style="font-size:0.95rem;font-weight:600;color:#fff;cursor:pointer;text-decoration:underline dotted rgba(255,255,255,0.5);text-underline-offset:3px;" onclick="window.BillingModule.toggleReviewDateEdit()" title="Click to change date">${reviewDateDisplay}</span>
          <input type="date" id="review-date-input" class="control-auto" value="${reviewDateStr}" style="display:none;font-size:0.85rem;font-weight:600;padding:4px 8px;border-radius:6px;border:1px solid rgba(255,255,255,0.4);background:rgba(255,255,255,0.15);color:#fff;" onchange="window.BillingModule.updateReviewDate(this.value)" onblur="window.BillingModule.toggleReviewDateEdit(false)">
          <button onclick="window.BillingModule.addDayFromReview()" style="background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;border-radius:6px;padding:4px 10px;font-size:0.78rem;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:4px;" title="Add another day to this job">
            <span class="material-icons" style="font-size:14px;">add</span> Add Day
          </button>
        </div>
      </div>
      <div class="review-banner-bottom">
        <div class="review-banner-context">
          <span class="review-banner-customer">${_bannerCustomerName ? escapeHtml(_bannerCustomerName) : 'No customer selected'}</span>
          ${_bannerJobName ? `<span class="review-banner-sep">·</span><span class="review-banner-job">${escapeHtml(_bannerJobName)}</span>` : ''}
        </div>
        <div class="review-banner-total">
          <span class="review-banner-total-label">Total</span>
          <span class="review-banner-total-amount" id="review-banner-total">$${_bannerGrandTotal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  `;

  // A2. Invoiced banner — ticket is locked for editing.
  // TASK-1346 sweep — narrow boolean, case (a). This banner asserts "editing is
  // locked" and offers Uninvoice as the way out; both are only TRUE when
  // isReviewReadOnly is firing on `invoiced`. On the 110 invoice-marker-only
  // tickets the biller can still edit, so showing it would be a lie.
  // TASK-4600 — widened in LOCKSTEP with isReviewReadOnly above. This banner is
  // the only thing that explains the lock and the only route to Uninvoice; on the
  // raw boolean it would not render for the ~104 invoice-marker-only tickets, so
  // widening the lock alone would freeze them with no reason shown and no way
  // out. The comment this replaces said showing it would "be a lie" because
  // those tickets were still editable — that is exactly what changed.
  if (isTicketInvoiced(t)) {
    html += `
      <div style="background:linear-gradient(135deg,#059669,#10B981);color:#fff;padding:12px 16px;border-radius:var(--radius-sm);margin-bottom:8px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;">
          <div style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:0.9rem;">
            <span class="material-icons" style="font-size:20px;">lock</span> Invoiced${t.invoiceNumber ? ` — ${escapeHtml(t.invoiceNumber)}` : ''}${t.paid ? ' — Paid' : ''}${t.writtenOff ? ' — Written Off' : ''}
          </div>
          <button class="btn" style="background:rgba(255,255,255,0.2);color:#fff;border:1px solid rgba(255,255,255,0.3);padding:4px 12px;font-size:0.8rem;border-radius:6px;"
            onclick="window.BillingModule.viewInvoice('${t.id}')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">receipt_long</span>View Invoice
          </button>
        </div>
        <div style="font-size:0.8rem;opacity:0.9;">Editing is locked to preserve invoice integrity. To make changes, uninvoice first.</div>
        <button class="btn" style="background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.3);padding:4px 12px;font-size:0.78rem;border-radius:6px;margin-top:6px;"
          onclick="if(confirm('This will remove the invoice number and mark the ticket as not invoiced. Continue?')){window.BillingModule.uninvoiceTicket('${t.id}')}">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">lock_open</span>Uninvoice to Edit
        </button>
      </div>
    `;
  }

  // A2c. TASK-9983 — a Workforce pay change landed after this ticket closed
  // and asks billing to check the invoice (or added hours to this job).
  html += _reviewPayChangeBannerHtml(t.id);

  // A2b. TASK-1167 — Archived / locked banner (read-only, like invoiced). NOT shown
  // when invoiced (that banner + uninvoice flow wins). At TITS, archive is the
  // post-QBO-invoice marker, so this ticket may already be billed — editing silently
  // desyncs the books. The single [Unarchive to edit] button is the deliberate,
  // confirm-gated way back to editable.
  // TASK-1346 sweep — narrow boolean, case (a): mutual exclusion with A2 above.
  // Both must read the same field or a ticket gets two banners or none.
  if (!t.invoiced && (t.status === 'archived' || t.status === 'locked')) {
    const _archLbl = t.status === 'locked' ? 'Locked' : 'Archived';
    const _archWhen = t.archivedAt ? formatTimestamp(t.archivedAt) : '';
    const _archBy = t.archivedByName ? ` by ${escapeHtml(t.archivedByName)}` : '';
    html += `
      <div style="background:linear-gradient(135deg,#475569,#64748B);color:#fff;padding:12px 16px;border-radius:var(--radius-sm);margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:0.9rem;margin-bottom:6px;">
          <span class="material-icons" style="font-size:20px;">inventory_2</span> ${_archLbl}${_archWhen ? ` ${escapeHtml(_archWhen)}` : ''}${_archBy} — may already be invoiced in your accounting system.
        </div>
        <div style="font-size:0.8rem;opacity:0.9;margin-bottom:8px;">Editing is locked to protect your books. Unarchive to edit.</div>
        <button class="btn" style="background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.3);padding:4px 12px;font-size:0.78rem;border-radius:6px;"
          onclick="window.BillingModule.unarchiveFromReview('${t.id}', this)">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">lock_open</span>Unarchive to edit
        </button>
      </div>
    `;
  }

  // A2d. TASK-10036 — a view-only login could type into every box and only
  // learned at Save (one toast) that nothing would be kept. Say it up front.
  if (!isReviewReadOnly(t) && _ticketContentViewOnly()) {
    html += `
      <div id="review-view-only-banner" style="background:#F1F5F9;color:var(--text-primary);border:1px solid var(--border-color);border-left:4px solid #475569;padding:10px 14px;border-radius:var(--radius-sm);margin-bottom:8px;display:flex;align-items:center;gap:8px;font-size:0.85rem;">
        <span class="material-icons" style="font-size:20px;color:#475569;">visibility</span>
        <span><strong>View only.</strong> Your login can look at this ticket but not change it, so nothing you type here will be saved. Ask the owner if you need to make changes.</span>
      </div>
    `;
  }

  // A3. TASK-566 — per-ticket driver lock (manual). The biller flips it; the
  // ticket goes read-only for driver-PIN sessions (firestore.rules enforces it
  // server-side) while the biller keeps editing here. SEPARATE field from
  // status==='locked' (which blocks the biller too) and from invoiced (always
  // hard-locked, so the control is hidden on invoiced tickets). TASK-1167 — also
  // hidden when read-only (archived/locked), since the whole ticket is frozen.
  if (!isReviewReadOnly(t)) {
    html += `<div id="review-driver-lock-row">${_driverLockRowHtml(t)}</div>`;
  }

  // A3b. TASK-4662 — the customer-tracking block, deliberately a COMPANION to
  // the lock row above rather than part of it. Rendered even on a read-only
  // ticket: once a ticket is invoiced or archived, "did the customer ever get
  // this?" is exactly the question support gets asked, so the record must not
  // disappear with the edit controls.
  html += `<div id="review-ticket-tracking">${_ticketTrackingHtml(t)}</div>`;

  // A4. TASK-1615-B — mileage-off-a-bad-route warning. Body + rationale live in
  // _renderRouteMileageBannerHtml (also re-run in place by _refreshRouteMileageBanner
  // when a line edit changes the answer).
  html += _renderRouteMileageBannerHtml();

  // A4b. TASK-9246 — what the app repaired while opening this ticket. Sits with
  // the other money warnings, above the fields, so the biller reads it before
  // they read the numbers it changed.
  html += _renderHoursHealBannerHtml();

  // B. Key fields — Customer + Job (most important for filling out)
  // TASK-4591 — the Job picker is windowed to the ticket's own work date; the
  // rest are one click away under the select. Counted here so the link can say
  // how many are hidden (a bare "Show older" reads like it might do nothing).
  const _revJobsAll = _reviewJobFullList(t.customerId);
  const _revJobsWin = _reviewJobWindowJobs(_revJobsAll, _reviewJobAnchor(t), t.jobId);
  const _revOlderCount = Math.max(0, _revJobsAll.length - _revJobsWin.length);
  // TASK-9608 — a ticket whose customer AND job both resolve reads as one summary
  // line + "Change"; the pickers below stay in the DOM (hidden), same ids and
  // handlers. Anything unresolved, or a read-only ticket, renders as before.
  const _hdrSummary = ReviewState.headerExpanded ? null
    : reviewHeaderSummary(t, BillingState.customers, BillingState.jobs, isReviewReadOnly(t));
  const _hdrCust = t.customerId ? (BillingState.customers || []).find(c => c && c.id === t.customerId) : null;
  // The "Office/Shop = internal time" hint is gone from this header; the cue
  // moves onto the ticket itself as a chip when its customer IS internal time.
  const _hdrInternal = _hdrCust?.internalTimeOnly === true;
  // TASK-9810 — the ticket's project (through its job) on the summary line AND
  // as its own field under the pickers. Read-only (the project is on the job);
  // the button opens the one dialog that changes it. Projects load lazily —
  // kick the read here so the label fills in on the next paint.
  const _hdrProject = reviewTicketProject(t.jobId, BillingState.jobs, BillingState.projects, BillingState._projectsLoaded === true);
  if (t.jobId && !BillingState._projectsLoaded && !BillingState._projectsLoading) loadBillingProjects();
  if (_hdrSummary) _hdrSummary.project = _hdrProject;
  if (_hdrSummary) html += _reviewHeaderSummaryHtml(_hdrSummary);
  html += `
    <div class="review-key-fields" id="review-header-pickers"${_hdrSummary ? ' style="display:none;"' : ''}>
      <div class="review-key-field">
        <label for="review-customer"><span class="material-icons">business</span> Customer</label>
        <select id="review-customer" class="form-control"
          onchange="window.BillingModule.updateReviewCustomer(this.value)">
          <option value="">Select customer...</option>
          ${_billingCustomerOptionsHtml(t.customerId)}
        </select>
        ${_hdrInternal ? `<div>${REVIEW_INTERNAL_CHIP_HTML}</div>` : ''}
      </div>
      <div class="review-key-field">
        <label for="review-job-select"><span class="material-icons">work</span> Job</label>
        <div style="display:flex;gap:6px;align-items:center;">
          <select id="review-job-select" class="form-control" style="flex:1;"
            onchange="window.BillingModule.updateReviewJob(this.value)">
            ${_reviewJobOptionsHtml(_revJobsWin, t.jobId)}
          </select>
          <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.renameJobFromReview()" title="${t.jobId ? 'Rename current job' : 'No job yet: pick the dispatched job'}" aria-label="${t.jobId ? 'Rename current job' : 'No job yet: pick the dispatched job'}" style="padding:4px 8px;min-width:0;">
            <span class="material-icons" style="font-size:16px;">edit</span>
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.addJobFromReview()" title="Pick the dispatched job (or put new work on the board)" style="white-space:nowrap;">
            <span class="material-icons" style="font-size:16px;">add</span> New Job
          </button>
        </div>
        ${t.jobId ? '' : _reviewNoJobHintHtml(t.customerId)}
        ${_revOlderCount > 0 ? `<a href="#" id="review-job-show-older" onclick="window.BillingModule.showOlderReviewJobs();return false;" style="display:inline-block;padding:4px 0;font-size:11px;font-weight:600;color:var(--brand-blue);margin-top:3px;text-decoration:underline;">Show older jobs (${_revOlderCount} more)</a>` : ''}
        ${_mce ? _mce.boxHtml() : (_mceLoad(), '')}
      </div>
      ${_reviewProjectFieldHtml(_hdrProject, !!t.customerId)}
    </div>
  `;

  // C. Job details card (PO, Location, Well) — with mismatch warnings from dispatch job
  const _reviewJob = t.jobId ? BillingState.jobs.find(j => j.id === t.jobId) : null;
  function _mismatchHint(field, label, ticketVal, jobVal) {
    // TASK-337 2026-05-21 — guard relaxed: hint fires when ticket field is
    // EMPTY and job has a value (previously dropped out when ticketVal was
    // falsy). Covers two prod bugs same week: TITS Ticket #100032 (permit
    // ghost, v1.15.706) and #100028 (PO blank, v1.15.713) — both root-caused
    // to ticket holding a denormalized snapshot of job data at create-time
    // that drifts when dispatcher edits the job. The Sync hint puts the
    // recovery in the dispatcher's hands (Skeptic 🟡 catch — never silently
    // overwrite an intentionally cleared field on open).
    if (!_reviewJob || !jobVal) return '';
    if (String(ticketVal || '').trim().toLowerCase() === String(jobVal).trim().toLowerCase()) return '';
    // HOTFIX (stored XSS): jobVal is free text any job writer controls (app,
    // SMS bot, MCP connector). It used to be spliced into this onclick as
    // '${escapeHtml(jobVal)}' — the HTML parser decodes &#39; back to ' before
    // the handler compiles, so a PO like  ';alert(1);'  executed. The value now
    // rides in data-* attributes (escapeHtml is attribute-safe there) and the
    // handler body is a CONSTANT that reads this.dataset — nothing user-owned
    // is ever compiled as JavaScript.
    return `<div style="font-size:0.7rem;color:#D97706;margin-top:2px;display:flex;align-items:center;gap:4px;">
      <span class="material-icons" style="font-size:13px;">warning</span>
      Job has: ${escapeHtml(jobVal)}
      <button type="button" class="btn btn-sm" style="font-size:0.65rem;padding:1px 6px;border:1px solid #D97706;color:#D97706;background:transparent;border-radius:4px;cursor:pointer;"
        data-sync-field="${escapeHtml(field)}" data-sync-label="${escapeHtml(label)}" data-sync-value="${escapeHtml(jobVal)}"
        onclick="var d=this.dataset,el=document.getElementById(d.syncField);if(el)el.value=d.syncValue;window.BillingModule.updateReviewHeaderField(d.syncLabel,d.syncValue);this.closest('div[style]').remove();">Sync</button>
    </div>`;
  }
  html += `
    <div class="review-details-card">
      <div class="review-details-header" onclick="this.parentElement.classList.toggle('collapsed')">
        <span><span class="material-icons">info_outline</span> Job Details</span>
        <span class="material-icons review-details-chevron">expand_less</span>
      </div>
      <div class="review-details-body">
        <div class="review-details-grid">
          <div class="form-group">
            <label for="review-po">PO Number</label>
            <input type="text" id="review-po" value="${escapeHtml(t.poNumber || '')}" placeholder="PO-12345"
              oninput="window.BillingModule.onReviewPoInput()"
              onchange="window.BillingModule.updateReviewHeaderField('poNumber', this.value)">
            <div id="review-po-mismatch"${(Array.isArray(_reviewJob?.extraPoNumbers) && poGroupKey(t.poNumber) && _reviewJob.extraPoNumbers.some(p => poGroupKey(p) === poGroupKey(t.poNumber))) ? ' style="display:none"' : ''}>${_mismatchHint('review-po', 'poNumber', t.poNumber, _reviewJob?.poNumber)}</div>
            <div id="review-po-chips"></div>
            <div id="review-po-hint"></div>
          </div>
          <div class="form-group">
            <label for="review-vendor-number">Vendor Number</label>
            <input type="text" id="review-vendor-number" value="${escapeHtml(t.vendorNumber || '')}" placeholder="Vendor #"
              onchange="window.BillingModule.updateReviewHeaderField('vendorNumber', this.value)">
            ${_mismatchHint('review-vendor-number', 'vendorNumber', t.vendorNumber, _reviewJob?.vendorNumber)}
          </div>
          <div class="form-group">
            <label for="review-location">Location</label>
            <input type="text" id="review-location" value="${escapeHtml(t.location || '')}" placeholder="Location"
              onchange="window.BillingModule.updateReviewHeaderField('location', this.value)">
            ${_mismatchHint('review-location', 'location', t.location, _reviewJob?.location || _reviewJob?.address)}
          </div>
          <div class="form-group">
            <label for="review-well-name">Well Name</label>
            <input type="text" id="review-well-name" value="${escapeHtml(t.wellName || '')}" placeholder="Well name"
              onchange="window.BillingModule.updateReviewHeaderField('wellName', this.value)">
            ${_mismatchHint('review-well-name', 'wellName', t.wellName, _reviewJob?.wellName)}
          </div>
          <div class="form-group">
            <label for="review-well-number">Well #</label>
            <input type="text" id="review-well-number" value="${escapeHtml(t.wellNumber || '')}" placeholder="001"
              onchange="window.BillingModule.updateReviewHeaderField('wellNumber', this.value)">
            ${_mismatchHint('review-well-number', 'wellNumber', t.wellNumber, _reviewJob?.wellNumber)}
          </div>
          <div class="form-group">
            <label for="review-afe" title="Authorization for Expenditure / Cost Center — used for OpenInvoice submission">AFE / Cost Center</label>
            <input type="text" id="review-afe" value="${escapeHtml(t.afeNumber || '')}" placeholder="AFE-001"
              onchange="window.BillingModule.updateReviewHeaderField('afeNumber', this.value)">
            ${_mismatchHint('review-afe', 'afeNumber', t.afeNumber, _reviewJob?.afeNumber)}
          </div>
        </div>
        ${_ticketDispatchNotes(t) ? `
          <div style="margin-top:8px;background:#F8FAFC;border-radius:var(--radius-sm);border-left:3px solid var(--brand-blue);overflow:hidden;">
            <div onclick="this.nextElementSibling.classList.toggle('hidden');this.querySelector('.material-icons:last-child').textContent=this.nextElementSibling.classList.contains('hidden')?'expand_more':'expand_less'"
              style="padding:6px 12px;font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;display:flex;align-items:center;gap:4px;cursor:pointer;user-select:none;">
              <span class="material-icons" style="font-size:14px;">speaker_notes</span> Dispatch Notes
              <span class="material-icons" style="font-size:16px;margin-left:auto;">expand_more</span>
            </div>
            <div class="hidden" style="padding:0 12px 8px;font-size:0.85rem;color:var(--text-primary);white-space:pre-wrap;">${escapeHtml(_ticketDispatchNotes(t))}</div>
          </div>
        ` : ''}
      </div>
    </div>
    <!-- Item codes managed internally via Price Books -->
  `;

  // C. Employee Hours Section
  html += renderReviewEmployeeSection();

  // D. Equipment Section
  html += renderReviewEquipmentSection();

  // D2. Ship Slips Section (only when tenant has enabled the feature)
  if (window.AppState?.settings?.enableShipSlips === true) {
    html += renderReviewShipSlipSection();
  }

  // E. Downtime/Standby (editable notes + deletable)
  if (ReviewState.downtimeLines.length > 0) {
    const totalDowntime = ReviewState.downtimeLines.reduce((s, l) => s + (l.hours || 0), 0);
    html += `
      <div class="review-section">
        <div class="review-section-title">
          <span style="display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">pause_circle</span> Downtime Deduction
            <span style="font-weight:400;color:var(--text-secondary);font-size:0.8rem;">(${totalDowntime.toFixed(2)} hrs)</span>
          </span>
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead><tr><th>${namesOnTickets() ? 'Person' : 'Role'}</th>${showClockTimes() ? '<th>From</th><th>To</th>' : ''}<th style="text-align:right;">Hours</th><th>Notes</th><th style="width:36px;"></th></tr></thead>
            <tbody>
              ${ReviewState.downtimeLines.map(l => `
                <tr>
                  <td>${escapeHtml(getEmployeeDisplayName(l))}</td>
                  ${showClockTimes() ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
                  <td style="text-align:right;">${(l.hours || 0).toFixed(2)}</td>
                  <td><input type="text" class="form-control" value="${escapeHtml(l.notes || '')}" placeholder="Notes..."
                    style="font-size:0.8rem;padding:4px 8px;min-width:120px;"
                    onchange="window.BillingModule.updateReviewDowntimeNotes(${l._localId}, this.value)"></td>
                  <td><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('DOWNTIME', ${l._localId})" title="Remove downtime">
                    <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  if (ReviewState.standbyLines.length > 0) {
    html += `
      <div class="review-section">
        <div class="review-section-title">
          <span class="material-icons" style="font-size:16px;">hourglass_empty</span> Standby
        </div>
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead><tr><th>${namesOnTickets() ? 'Person' : 'Role'}</th>${showClockTimes() ? '<th>From</th><th>To</th>' : ''}<th style="text-align:right;">Hours</th><th>Notes</th><th style="width:36px;"></th></tr></thead>
            <tbody>
              ${ReviewState.standbyLines.map(l => `
                <tr>
                  <td>${escapeHtml(getEmployeeDisplayName(l))}</td>
                  ${showClockTimes() ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
                  <td style="text-align:right;">${(l.hours || 0).toFixed(2)}</td>
                  <td><input type="text" class="form-control" value="${escapeHtml(l.notes || '')}" placeholder="Notes..."
                    style="font-size:0.8rem;padding:4px 8px;min-width:120px;"
                    onchange="window.BillingModule.updateReviewStandbyNotes(${l._localId}, this.value)"></td>
                  <td><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('STANDBY', ${l._localId})" title="Remove standby">
                    <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  // F. Field Activity / Line Info (POTHOLE lines)
  if (ReviewState.potholeLines.length > 0) {
    html += `
      <div class="review-section">
        <div class="review-section-title" style="cursor:pointer;" onclick="const b=document.getElementById('review-pothole-body');b.classList.toggle('hidden');this.querySelector('.expand-icon').textContent=b.classList.contains('hidden')?'expand_more':'expand_less'">
          <span style="display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">pin_drop</span> Line Info (${ReviewState.potholeLines.length})
          </span>
          <span class="material-icons expand-icon" style="font-size:18px;color:var(--text-secondary);">expand_less</span>
        </div>
        <div id="review-pothole-body">
          ${ReviewState.potholeLines.map(l => {
            const parts = (l.description || '').split(', ').filter(Boolean);
            const gpsStr = (l.gpsLat && l.gpsLng) ? `${l.gpsLat.toFixed(5)}, ${l.gpsLng.toFixed(5)}` : '';
            return `<div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:10px 12px;margin-bottom:6px;">
              <div style="display:flex;flex-wrap:wrap;gap:8px 16px;font-size:0.85rem;">
                ${parts.map(p => `<span style="color:var(--text-primary);font-weight:500;">${escapeHtml(p)}</span>`).join('')}
                ${gpsStr ? `<a href="https://maps.google.com/?q=${l.gpsLat},${l.gpsLng}" target="_blank" rel="noopener" style="color:var(--brand-blue);text-decoration:none;display:flex;align-items:center;gap:2px;font-size:0.8rem;">
                  <span class="material-icons" style="font-size:14px;">location_on</span>${gpsStr}</a>` : ''}
              </div>
              ${l.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">${escapeHtml(l.notes)}</div>` : ''}
              ${(l.photos && l.photos.length > 0) ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;">${l.photos.map(p => {
                // TASK-9855 — Storage SDK by path, not the stored token URL.
                return window.StorageFile.fileRef(p) ? `<a ${window.StorageFile.linkAttrs(p)}><img ${window.StorageFile.imgAttrs(p)} style="width:48px;height:48px;object-fit:cover;border-radius:6px;border:1px solid var(--border-light);"></a>` : '';
              }).join('')}</div>` : ''}
            </div>`;
          }).join('')}
        </div>
      </div>
    `;
  }

  // F2. TASK-846-B — captured custom sections, EDITABLE (upgrades TASK-848's
  // read-only echo). The biller fixes a forgotten/wrong count here; saving writes
  // customSectionData on the ticket and the 846-A server reconciler
  // (reconcileDerivedTicketLines) re-derives the auto-billed FIELD_ITEM lines.
  // Invoiced/driver-locked tickets fall back to the read-only echo (the reconciler
  // skips them). Clock-out Review & Confirm + in-app detail stay read-only.
  // NO invoiceOnly filter here — the review screen always shows every section,
  // including ones flagged off the customer invoice.
  html += renderReviewCustomSections(t);

  // G. Permits Section
  html += renderReviewPermitSection();

  // G2. Field Items Section
  if (ReviewState.fieldItemLines.length > 0) {
    const fiSubtotal = ReviewState.fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    html += `
      <div class="review-section" id="review-field-items-section">
        <div class="review-section-title">
          <span class="material-icons" style="font-size:18px;">list_alt</span> Field Items
          <span class="review-subtotal" id="review-field-item-subtotal">$${fiSubtotal.toFixed(2)}</span>
        </div>
        <table class="review-table"><thead><tr>
          <th style="text-align:left;padding:6px 10px;font-weight:600;">Description</th>
          <th style="text-align:center;padding:6px 10px;font-weight:600;">Qty</th>
          <th style="text-align:right;padding:6px 10px;font-weight:600;">Rate</th>
          <th style="text-align:right;padding:6px 10px;font-weight:600;">Amount</th>
        </tr></thead><tbody>
        ${ReviewState.fieldItemLines.map(l => {
          // TASK-846-A — derived-from-binding lines are auto-calculated (server-managed,
          // read-only); flag $0/no-match (rateUnresolved) so AR sets a Price Book rate.
          const isDerived = !!(l.derivedFromBinding && l.derivedFromBinding.csId);
          const unresolved = l.rateUnresolved === true;
          const dupe = l.possibleDuplicate === true;
          return `
          <tr${(unresolved || dupe) ? ' style="background:rgba(220,38,38,0.06);"' : ''}>
            <td style="padding:6px 10px;">${escapeHtml(l.description || '')}${isDerived ? ' <span title="Auto-calculated from a Custom Section field — edit the Custom Section to change" style="font-size:0.7rem;color:var(--text-tertiary);"><span class="material-icons" style="font-size:12px;vertical-align:-2px;">lock</span>auto</span>' : ''}${unresolved ? _tapTag('⚠ no price', _derivedNoPriceTitle(l)) : ''}${dupe ? _tapTag('⚠ possible duplicate', 'Another line already bills this item code — review to avoid double-billing') : ''}${isDerived ? '' : _reviewPriceBookLineTags(l)}${_ppeRollupChip(l, ReviewState.ticketId)}</td>
            <td style="padding:6px 10px;text-align:center;">${l.quantity || 0}</td>
            <td style="padding:6px 10px;text-align:right;">${_isNoChargeLine(l) ? '<span title="Show at $0 — on the invoice at $0.00, not charged">N/C</span>' : `$${(l.rate || 0).toFixed(2)}/${escapeHtml(l.rateUnit || 'ea')}`}</td>
            <td style="padding:6px 10px;text-align:right;font-weight:600;">$${(l.lineAmount || 0).toFixed(2)}</td>
          </tr>`;
        }).join('')}
        </tbody></table>
      </div>
    `;
  }

  // G2b. Miscellaneous Line Items (editable)
  html += renderReviewMiscSection();

  // G2c. TASK-9440 — Fuel Surcharge (read-only; set in Billing Settings)
  html += renderReviewFuelSurchargeSection();

  // G3. Documents Section
  html += renderReviewDocumentsSection();

  // H. Tax Section + Grand Total Bar
  const _taxSubtotal = calcReviewSubtotal();
  const _taxResult = calcReviewTax();
  const _taxGrandTotal = _taxSubtotal + _taxResult.taxAmount;
  const TAX_CATEGORY_LABELS = { labor: 'Labor', equipment: 'Equipment', permits: 'Load Permits', fieldItems: 'Field Items', misc: 'Miscellaneous', downtime: 'Downtime', standby: 'Standby' };

  html += `
    <div style="margin-top:16px;padding:12px 16px;background:var(--bg-secondary);border-radius:var(--radius-md);border:1px solid var(--border-light);">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <label style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:0.9rem;color:var(--text-primary);cursor:pointer;margin:0;">
          <input type="checkbox" id="review-tax-toggle" ${ReviewState.taxEnabled ? 'checked' : ''}
            onchange="window.BillingModule.toggleReviewTax(this.checked)" style="accent-color:var(--brand-blue);">
          Apply Tax
        </label>
        ${ReviewState.taxEnabled ? `
          <div style="display:flex;align-items:center;gap:6px;">
            <input type="number" id="review-tax-rate" class="form-control" value="${ReviewState.taxRate}" step="0.01" min="0" max="100"
              style="width:80px;font-size:0.85rem;padding:4px 8px;text-align:right;"
              onchange="window.BillingModule.updateReviewTaxRate(parseFloat(this.value)||0)">
            <span style="font-size:0.85rem;font-weight:600;color:var(--text-secondary);">%</span>
          </div>
        ` : ''}
      </div>
      ${ReviewState.taxEnabled ? `
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;">
          ${Object.entries(TAX_CATEGORY_LABELS).map(([key, label]) => `
            <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;color:var(--text-primary);cursor:pointer;background:var(--bg-elevated);padding:4px 10px;border-radius:var(--radius-sm);border:1px solid var(--border-light);">
              <input type="checkbox" ${ReviewState.taxCategories[key] !== false ? 'checked' : ''}
                onchange="window.BillingModule.toggleReviewTaxCategory('${key}', this.checked)" style="accent-color:var(--brand-blue);">
              ${label}
            </label>
          `).join('')}
        </div>
        <div style="margin-top:12px;border-top:1px solid var(--border-light);padding-top:8px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
            <span style="font-size:0.85rem;color:var(--text-secondary);">Subtotal</span>
            <span style="font-size:0.95rem;font-weight:600;color:var(--text-primary);" id="review-subtotal-amount">$${_taxSubtotal.toFixed(2)}</span>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;">
            <span style="font-size:0.85rem;color:var(--text-secondary);">Tax (${ReviewState.taxRate.toFixed(2)}%) <span style="font-size:0.7rem;color:var(--text-secondary);" id="review-taxable-label">on $${_taxResult.taxableSubtotal.toFixed(2)}</span></span>
            <span style="font-size:0.95rem;font-weight:600;color:var(--text-primary);" id="review-tax-amount">$${_taxResult.taxAmount.toFixed(2)}</span>
          </div>
        </div>
      ` : ''}
    </div>
    <div class="review-grand-total" id="review-grand-total-bar">
      <span class="review-gt-label">Grand Total</span>
      <span class="review-gt-amount" id="review-grand-total-amount">$${_taxGrandTotal.toFixed(2)}</span>
    </div>
  `;

  // H2. Work Start/Stop Times (editable)
  {
    const _wst = ReviewState.headerEdits.workStartTime !== undefined ? ReviewState.headerEdits.workStartTime : t.workStartTime;
    const _wet = ReviewState.headerEdits.workStopTime !== undefined ? ReviewState.headerEdits.workStopTime : t.workStopTime;
    // clock-dst-all — company zone (same formatter as the line time boxes).
    const _fmtDTL = (ts) => toDatetimeLocal(ts);
    html += `
      <div class="review-section" style="margin-top:16px;">
        <div class="review-section-title">
          <span class="material-icons" style="font-size:16px;">timer</span> Work Start / Stop
        </div>
        <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;">
          <div class="form-group" style="flex:1;min-width:180px;">
            <label style="font-size:0.8rem;">Start Time</label>
            <input type="datetime-local" id="review-work-start-time" class="form-control" value="${_fmtDTL(_wst)}"
              onchange="window.BillingModule.updateReviewWorkTime('workStartTime', this.value)">
          </div>
          <div class="form-group" style="flex:1;min-width:180px;">
            <label style="font-size:0.8rem;">Stop Time</label>
            <input type="datetime-local" id="review-work-stop-time" class="form-control" value="${_fmtDTL(_wet)}"
              onchange="window.BillingModule.updateReviewWorkTime('workStopTime', this.value)">
          </div>
        </div>
      </div>
    `;
  }

  // I. Per-Asset Notes + Ticket Notes (editable)
  const ticketNotes = ReviewState.headerEdits.notes !== undefined ? ReviewState.headerEdits.notes : (t.notes || t.workPerformed || '');
  const showNotesOnInvoice = ReviewState.headerEdits.showNotesOnInvoice !== undefined ? ReviewState.headerEdits.showNotesOnInvoice : (t.showNotesOnInvoice !== false);

  // Per-asset notes section
  if (isPerAssetNotesEnabled()) {
    const noteableLines = ReviewState.equipmentLines.filter(l => assetTypeGetsNotes(l.assetType));
    if (noteableLines.length > 0) {
      html += `
        <div class="review-section" style="margin-top:16px;">
          <div class="review-section-title">
            <span class="material-icons" style="font-size:16px;">note_add</span> Equipment Notes
          </div>
      `;
      for (const l of noteableLines) {
        const label = getEquipmentDisplayName(l) || l.assetName || l.fleetNumber || 'Equipment';
        const typeTag = l.assetType === 'trailer' ? ' (Trailer)' : l.assetType === 'asset' ? ' (Asset)' : '';
        const noteVal = l.notes || '';
        // TASK-1636 — the customer reads this note on the invoice. It should say what
        // the machine DID (loads hauled, task, location), never restate our rate math.
        // Derivation rationale gets its own internal field below, which never prints.
        const mathWarn = noteReadsAsBillingDerivation(noteVal);
        const internalVal = l.internalNotes || '';
        html += `
          <div style="margin-bottom:var(--spacing-sm);">
            <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:2px;">${escapeHtml(label)}${typeTag} <span style="font-weight:400;color:var(--text-secondary);">(prints on the customer invoice)</span></label>
            <textarea class="form-control textarea-auto-expand" rows="1" style="width:100%;font-size:0.85rem;"
              placeholder="What did ${window.escAttr(label)} do? Loads hauled, task, location..."
              oninput="this.style.height='auto';this.style.height=this.scrollHeight+'px'"
              data-eq-note-id="${l._localId}"
              onchange="window.BillingModule.updateReviewEquipmentNotes(${l._localId}, this.value)">${escapeHtml(noteVal)}</textarea>
            <div id="eq-note-mathwarn-${l._localId}" style="display:${mathWarn ? 'flex' : 'none'};align-items:flex-start;gap:4px;margin-top:2px;font-size:0.75rem;color:#B45309;">
              <span class="material-icons" style="font-size:14px;margin-top:1px;">warning_amber</span>
              <span>This reads like billing math &mdash; the customer sees this note. Describe what the machine did; put rate/derivation reasoning in the internal note below.</span>
            </div>
            <input type="text" class="form-control" style="width:100%;font-size:0.8rem;margin-top:4px;background:var(--bg-page);color:var(--text-secondary);border-style:dashed;"
              placeholder="Internal note &mdash; why this line bills the way it does (never prints)"
              value="${window.escAttr(internalVal)}"
              onchange="window.BillingModule.updateReviewEquipmentInternalNotes(${l._localId}, this.value)">
          </div>
        `;
      }
      const _hasAnyEquipNotes = noteableLines.some(l => (l.notes || '').trim());
      if (_hasAnyEquipNotes) {
        html += `<button id="review-cleanup-notes-btn" onclick="window.BillingModule.handleReviewCleanupNotes(this)" style="margin-top:8px;padding:6px 16px;background:#7C3AED;color:#fff;border:none;border-radius:6px;font-size:0.8rem;cursor:pointer;font-weight:600;">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">auto_fix_high</span> Clean Up Notes
          </button>`;
      }
      html += `</div>`;
    }

    // Collapsible general ticket notes
    html += `
      <div class="review-details-card" style="margin-top:8px;">
        <div class="review-details-header" onclick="this.parentElement.classList.toggle('collapsed')">
          <span><span class="material-icons">receipt_long</span> General Ticket Notes <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">(customer-facing)</span></span>
          <div style="display:flex;align-items:center;gap:8px;">
            <label style="display:flex;align-items:center;gap:4px;font-size:0.75rem;font-weight:400;color:var(--text-secondary);cursor:pointer;" onclick="event.stopPropagation()">
              <input type="checkbox" id="review-show-notes-invoice" ${showNotesOnInvoice ? 'checked' : ''}
                onchange="window.BillingModule.updateReviewHeaderField('showNotesOnInvoice', this.checked)">
              Show on Invoice
            </label>
            <span class="material-icons review-details-chevron">expand_less</span>
          </div>
        </div>
        <div class="review-details-body">
          <textarea id="review-ticket-notes" rows="3" class="form-control" style="width:100%;font-size:0.85rem;" placeholder="Notes for the customer invoice..."
            oninput="window.BillingModule.updateReviewHeaderField('notes', this.value); window.BillingModule.updateReviewHeaderField('workPerformed', this.value);">${escapeHtml(ticketNotes)}</textarea>
          ${ticketNotes.trim() ? `<button id="review-cleanup-general-btn" onclick="window.BillingModule.handleReviewCleanupNotes(this)" style="margin-top:8px;padding:6px 16px;background:#7C3AED;color:#fff;border:none;border-radius:6px;font-size:0.8rem;cursor:pointer;font-weight:600;">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">auto_fix_high</span> Clean Up Notes
          </button>` : ''}
        </div>
      </div>
    `;
  } else {
    html += `
      <div class="review-section" style="margin-top:16px;">
        <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
          <div>
            <span class="material-icons" style="font-size:16px;">note</span> Ticket Notes
            <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);margin-left:4px;">(customer-facing)</span>
          </div>
          <label style="display:flex;align-items:center;gap:4px;font-size:0.75rem;font-weight:400;color:var(--text-secondary);cursor:pointer;">
            <input type="checkbox" id="review-show-notes-invoice" ${showNotesOnInvoice ? 'checked' : ''}
              onchange="window.BillingModule.updateReviewHeaderField('showNotesOnInvoice', this.checked)">
            Show notes on Invoice
          </label>
        </div>
        <textarea id="review-ticket-notes" rows="3" class="form-control" style="width:100%;font-size:0.85rem;" placeholder="Notes for the customer invoice..."
          oninput="window.BillingModule.updateReviewHeaderField('notes', this.value); window.BillingModule.updateReviewHeaderField('workPerformed', this.value);">${escapeHtml(ticketNotes)}</textarea>
        ${ticketNotes.trim() ? `<button id="review-cleanup-notes-btn" onclick="window.BillingModule.handleReviewCleanupNotes(this)" style="margin-top:8px;padding:6px 16px;background:#7C3AED;color:#fff;border:none;border-radius:6px;font-size:0.8rem;cursor:pointer;font-weight:600;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">auto_fix_high</span> Clean Up Notes
        </button>` : ''}
      </div>
    `;
  }

  // Init auto-expand textareas after render
  setTimeout(() => {
    document.querySelectorAll('.textarea-auto-expand').forEach(ta => {
      if (ta.value) { ta.style.height = 'auto'; ta.style.height = ta.scrollHeight + 'px'; }
    });
  }, 50);

  // J. Shift Notes (TASK-1287 — editable in the Edit Ticket modal; the driver's
  // clock-out note is a wage-hour record the AR/AP reviewer may correct. Locked
  // to read-only when the ticket is invoiced/archived/locked, enforced both here
  // and server-side in the editClockNotes gateway op.)
  html += `
    <div class="review-section" style="margin-top:8px;">
      <div class="review-section-title" style="cursor:pointer;" onclick="document.getElementById('review-shift-notes-list')?.classList.toggle('hidden')">
        <span class="material-icons" style="font-size:16px;">speaker_notes</span> Shift Notes
        <span style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);margin-left:4px;">(internal${isReviewReadOnly(t) ? '' : ' · editable'})</span>
        <span class="material-icons" style="font-size:16px;margin-left:4px;">expand_more</span>
      </div>
      <div id="review-shift-notes-list" class="hidden" style="margin-top:8px;">
        <div style="color:var(--text-secondary);font-size:0.8rem;">Loading...</div>
      </div>
    </div>
  `;
  html += _acctNoteSlotHtml(); // TASK-10088 — accounting-only notes (lazy chunk)
  // TASK-10047 — office notes (internal; never printed). Hidden until loaded.
  html += _officeNotesSectionHtml('review-office-notes-list', 'review');

  body.innerHTML = html;
  if (ReviewState.ticketId) loadOfficeNotesForTicket(ReviewState.ticketId, 'review-office-notes-list');
  // TASK-9609 — project groups + type-to-filter on the Job picker. BEFORE the
  // read-only lock below, so a locked ticket's filter box is disabled with it.
  _setJobPickerJobs(document.getElementById('review-job-select'), _revJobsWin, 'No job');
  // A re-render mid-cleanup (e.g. a line removed) paints idle buttons — put the
  // busy state back so the run stays visible and cannot be double-started.
  if (_reviewCleanupRunToken !== null && _reviewCleanupRunToken === _reviewLoadToken) {
    _setReviewCleanupButtons(_reviewCleanupLabel, true);
  }

  // TASK-4662 — load THIS ticket's send history. One ticket, on open; never per
  // row of a list (the derived fields on the ticket doc exist so a list never
  // needs the series). Fire-and-forget: the block renders "Loading history…"
  // and swaps itself in, so a slow or failed call never blocks the modal.
  if (ReviewState.ticketId) loadTicketSendLog(ReviewState.ticketId);

  // TASK-1234 — subtle "more content this way" scroll affordance on the lines
  // tables (Chris, field dogfood: nothing hinted that the table scrolls
  // sideways on a phone, so the amount column just looked cut off).
  _enhanceScrollHintsIn(body);

  // Hydrate number inputs that can't be set via HTML value attribute
  hydrateEquipmentHours();

  // Load shift notes for review ticket — EDITABLE renderer (TASK-1287); passes
  // the ticket's read-only state so notes lock in lockstep with the rest of the
  // modal. (The bare read-only renderer loadShiftNotesForTicket still serves the
  // View Ticket surface, unchanged.)
  // TASK-9760 follow-up 2 — bind against the ticket AS IT OPENED (the snapshot
  // the clock entries were bound to), not an in-session job/customer change.
  loadEditableShiftNotesForReview(ReviewState.ticketId, 'review-shift-notes-list', isReviewReadOnly(t), ReviewState._originalSnapshot || t);

  // Lock all inputs if the ticket is read-only — invoiced, driver-locked, OR
  // archived/locked (TASK-1167) — to preserve books integrity. openReviewTicket
  // resets the footer + button state on every open, so this only-hides pattern is safe.
  if (isReviewReadOnly(t)) {
    body.querySelectorAll('input, select, textarea').forEach(el => { el.disabled = true; el.style.opacity = '0.6'; });
    body.querySelectorAll('button').forEach(btn => {
      // Keep the escape hatches functional: View Invoice / Uninvoice / Unarchive.
      const text = btn.textContent || '';
      if (text.includes('View Invoice') || text.includes('Uninvoice') || text.includes('Unarchive')) return;
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    });
    // Hide save/delete in footer, keep print and cancel
    const saveBtn = document.getElementById('btn-save-review');
    if (saveBtn) saveBtn.style.display = 'none';
    const footer = document.getElementById('review-ticket-footer');
    if (footer) {
      const delBtn = footer.querySelector('button[onclick*="deleteReview"]');
      if (delBtn) delBtn.style.display = 'none';
    }
  }

  // Apply customer required field warnings after render
  setTimeout(applyTicketRequiredFieldWarnings, 50);
}

// TASK-1234 — wrap each overflow-x table wrapper in a positioned shell and
// toggle edge-shadow classes from scroll state. The shadows (styles.css
// .sx-shell::before/::after) only show when content actually hides in that
// direction, so a table that fits gets zero chrome. Re-renders wipe the DOM,
// so this re-runs after every body.innerHTML — the _sxHinted flag only guards
// against double-wrapping within one render pass.
function _enhanceScrollHintsIn(root) {
  if (!root) return;
  root.querySelectorAll('div[style*="overflow-x"]').forEach(w => {
    if (w._sxHinted) return;
    w._sxHinted = true;
    const shell = document.createElement('div');
    shell.className = 'sx-shell';
    w.parentNode.insertBefore(shell, w);
    shell.appendChild(w);
    const update = () => {
      const canLeft = w.scrollLeft > 4;
      const canRight = w.scrollLeft + w.clientWidth < w.scrollWidth - 4;
      shell.classList.toggle('sx-left', canLeft);
      shell.classList.toggle('sx-right', canRight);
    };
    w.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(update);
  });

  // TASK-1234b (Chris: "not sure the subtle swipe fade is enough") — one-time
  // "peek" nudge: the first scrollable table slides ~34px and springs back, so
  // the eye catches the motion; the edge shadows stay as the passive cue.
  // Once per ticket (re-renders during editing must not re-nudge), skipped for
  // reduced-motion users.
  const tid = ReviewState.ticketId;
  if (tid && _sxNudgedFor !== tid && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const first = root.querySelector('.sx-shell > div');
    if (first && first.scrollWidth > first.clientWidth + 8) {
      _sxNudgedFor = tid;
      setTimeout(() => {
        if (!first.isConnected || first.scrollLeft > 4) return; // user already scrolled
        first.scrollTo({ left: 34, behavior: 'smooth' });
        setTimeout(() => { if (first.isConnected && first.scrollLeft <= 40) first.scrollTo({ left: 0, behavior: 'smooth' }); }, 480);
      }, 650);
    }
  }
}
let _sxNudgedFor = null;

// Required Fields — highlight empty fields based on customer requirements
const TICKET_REQUIRED_FIELD_MAP = {
  poNumber: 'review-po',
  vendorNumber: 'review-vendor-number',
  afeNumber: 'review-afe',
  wellName: 'review-well-name',
  wellNumber: 'review-well-number',
  location: 'review-location',
  workPerformed: 'review-ticket-notes',
};

function applyTicketRequiredFieldWarnings() {
  // Clear all existing warnings
  document.querySelectorAll('#modal-review-ticket .field-required-warn').forEach(el => el.classList.remove('field-required-warn'));
  document.querySelectorAll('#modal-review-ticket .field-required-hint').forEach(el => el.remove());

  // TASK-4828 — refresh the PO hint here, ABOVE the early returns below, and
  // deliberately so. This function is the one hook that runs both after the
  // review modal renders AND after the customer changes, and the PO vocabulary
  // is per-customer. Putting the call at the bottom would silently skip it for
  // every ticket whose customer has no requiredFields (the majority), which is
  // exactly the shape of a warning that "shipped" and never fires.
  onReviewPoInput();

  const customerId = ReviewState.ticket?.customerId;
  if (!customerId) return;
  const customer = BillingState.customers.find(c => c.id === customerId);
  if (!customer?.requiredFields?.length) return;

  const customerName = customer.name || 'customer';
  for (const key of customer.requiredFields) {
    const elId = TICKET_REQUIRED_FIELD_MAP[key];
    if (!elId) continue;
    const el = document.getElementById(elId);
    if (!el) continue;
    if (!el.value?.trim()) {
      el.classList.add('field-required-warn');
      if (!el.parentElement.querySelector('.field-required-hint')) {
        const hint = document.createElement('div');
        hint.className = 'field-required-hint';
        hint.innerHTML = `<span class="material-icons">info_outline</span> Required for ${escapeHtml(customerName)}`;
        el.parentElement.appendChild(hint);
      }
    }
  }
}

// ============================================
// TASK-1234 — MOBILE CARD MODE (Steve's AR/AP loop, low-vision first-class)
// On phones the Employee / Equipment line tables are REPLACED by stacked
// per-line cards: label:value rows, 16px+ inputs AND 14px+ labels (the old
// perverse state was 16px values under 10.4-13px labels), thumb-size targets,
// and an INLINE per-row outlier warning at the point of entry (the COD-endorsed
// shape — the passive sticky total already failed for this failure mode).
// Decided at render time; every section re-render re-evaluates, so rotating a
// phone picks the right mode on the next render. Card mode renders NO
// overflow-x wrapper, so _enhanceScrollHintsIn (which keys off those wrappers)
// naturally never fires for these sections — the scroll-hint mechanism stays
// intact for desktop and for the other tables (downtime/standby/ship slips).
function _reviewCardMode() {
  return !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
}

// Single source of truth for a line's plausible-hours ceiling — used by BOTH
// the inline card warning and the TASK-1231 save gate so they can never drift.
// Priority: server flag (equipmentLineEnrichment's entryCeilingHours) → the
// line's own clock window (×1.25, min +1h) → 24h absolute bar. Returns null
// for per-unit lines (they bill quantity, not hours).
function _lineHoursCeilingInfo(l) {
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return null;
  const flagCeil = parseFloat(l._billingReviewFlag?.reason === 'hours_exceed_clock_entry'
    ? l._billingReviewFlag?.entryCeilingHours : NaN);
  if (flagCeil > 0) return { ceiling: flagCeil, source: 'clock' };
  if (l.fromTime && l.toTime) {
    const f = l.fromTime?.toDate ? l.fromTime.toDate() : new Date(l.fromTime);
    const t = l.toTime?.toDate ? l.toTime.toDate() : new Date(l.toTime);
    if (!isNaN(f.getTime()) && !isNaN(t.getTime()) && t > f) {
      const durH = (t - f) / 3600000;
      return { ceiling: Math.round(Math.max(durH * 1.25, durH + 1) * 100) / 100, source: 'window' };
    }
  }
  return { ceiling: 24, source: 'default' };
}

function _hoursWarnText(hours, info) {
  const h = Math.round(hours * 100) / 100;
  return info.source === 'default'
    ? `${h} h · over 24 h — check entry`
    : `${h} h · clock max ${info.ceiling} h`;
}

// Inline per-row outlier warning, rendered adjacent to the hours input the
// biller is thumb-typing into. Always present (hidden) for time-based lines so
// _refreshHoursWarn can flip it live without a re-render.
function _hoursWarnHtml(kind, l) {
  const info = _lineHoursCeilingInfo(l);
  if (!info) return '';
  const hours = parseFloat(l.hours) || 0;
  const exceeds = hours > 0 && hours > info.ceiling;
  return `<div class="rvc-hours-warn${exceeds ? '' : ' hidden'}" id="rvc-warn-${kind}-${l._localId}">
      <span class="material-icons">error</span><span class="rvc-warn-text">${exceeds ? _hoursWarnText(hours, info) : ''}</span>
    </div>`;
}

// Live update after an hours/time edit — no-op when the card warning isn't in
// the DOM (desktop table mode).
function _refreshHoursWarn(kind, line) {
  const el = document.getElementById(`rvc-warn-${kind}-${line._localId}`);
  if (!el) return;
  const info = _lineHoursCeilingInfo(line);
  const hours = parseFloat(line.hours) || 0;
  const exceeds = !!info && hours > 0 && hours > info.ceiling;
  el.classList.toggle('hidden', !exceeds);
  const txt = el.querySelector('.rvc-warn-text');
  if (txt) txt.textContent = exceeds ? _hoursWarnText(hours, info) : '';
  const input = document.getElementById(`review-${kind}-hours-${line._localId}`);
  if (input) input.classList.toggle('rvc-input-alert', exceeds);
}

// TASK-1234 — stacked per-line EMPLOYEE cards (phone). Same input ids /
// classes / handlers as the table so every surgical updater
// (updateReviewEmployeeLine, updateReviewTime, renderReviewTotals,
// toggleReviewTimeEdit) works unchanged.
function _renderReviewEmployeeCards(lines, showTimes, founderNames, hasPieceRate) {
  // Steve/AR-AP fix (2026-07-20): on a read-only ticket (archived/locked/invoiced)
  // the per-line time pills + hours/rate inputs stayed tappable in card mode, so a
  // biller could type into them on a phone and the change silently no-op'd (the save
  // handlers already `return` on read-only — the DATA was safe, the UX lied). Dim +
  // disable interaction on the whole section so it reads as locked even when the
  // archived banner has scrolled off above. Unarchive is via the card/banner button.
  const _roStyle = isReviewReadOnly(ReviewState.ticket) ? ' style="pointer-events:none;opacity:0.6;"' : '';
  let html = `
    <div class="review-section" id="review-emp-section"${_roStyle}>
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">people</span> Employee ${hasPieceRate ? 'Hours / Piece Rate' : 'Hours'}
        </span>
        ${!founderNames ? `<label class="rvc-shownames">
          <input type="checkbox" id="review-show-names" ${_showEmployeeNames ? 'checked' : ''}
            onchange="window.BillingModule.toggleShowNames(this.checked)" style="accent-color:var(--brand-blue);">
          Show names
        </label>` : ''}
      </div>
      ${_reviewMissingCrewBannerHtml()}${_reviewSisterHoursBannerHtml()}${_reviewPlanVsRanHtml()}
  `;

  if (lines.length === 0) {
    html += `<div class="rvc-empty">No employee lines yet</div>`;
  }

  for (const l of lines) {
    const isActive = isLineClockActive(l, ReviewState.ticket?.id);
    const hoursDisplay = isActive ? '–' : (l.hours || 0).toFixed(2);
    const amountDisplay = isActive ? '–' : '$' + (l.lineAmount || 0).toFixed(2);
    const isPiece = !isTimeBasedUnit(l.rateUnit);
    const warnInfo = (!isActive && !isPiece) ? _lineHoursCeilingInfo(l) : null;
    const warnNow = !!warnInfo && (parseFloat(l.hours) || 0) > warnInfo.ceiling;
    const outEditable = l._isNew || !isActive;

    const nameHtml = (l._isNew && !l._restoredFromClock) // TASK-9755
      ? `<select class="review-select rvc-name-select" onchange="window.BillingModule.onReviewEmployeeSelect(${l._localId}, this.value)">
          <option value="">Select employee...</option>
          ${BillingState.people.map(p => `<option value="${p.id}" ${l.personId === p.id ? 'selected' : ''}>${escapeHtml(p.name)}</option>`).join('')}
        </select>`
      : (founderNames
        ? `${escapeHtml(l.personName || '-')}${l.role && CREW_TYPE_ITEM_CODE_MAP[l.role] ? ` <span class="rvc-role">${escapeHtml(window.getTypeLabel?.(l.role, 'crewTypes') || l.role)}</span>` : (l.role && l.role !== 'CDL' && l.role !== 'NON_CDL' ? ` <span class="rvc-role">${escapeHtml(l.role)}</span>` : '')}`
        : escapeHtml(getEmployeeDisplayName(l)));

    const timesHtml = showTimes ? `
      <div class="rvc-times">
        <div class="rvc-time">
          <span class="rvc-lbl">Clock In</span>
          <span class="review-time-display review-time-pill" id="review-emp-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-in', ${l._localId})" title="Tap to edit time">${lineFromHtml(l)}</span>
          <input type="datetime-local" class="review-time-input hidden" id="review-emp-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime)}"
            onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'fromTime', this.value)">
        </div>
        <div class="rvc-time">
          <span class="rvc-lbl">Clock Out</span>
          <span class="review-time-display${outEditable ? ' review-time-pill' : ''}" id="review-emp-out-display-${l._localId}"${outEditable ? ` onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-out', ${l._localId})" title="Tap to edit time"` : ''}>${lineToHtml(l)}</span>
          <input type="datetime-local" class="review-time-input hidden" id="review-emp-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime)}"
            onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'toTime', this.value)">
        </div>
      </div>` : '';

    const hoursField = isActive
      ? `<div class="rvc-field"><span class="rvc-lbl">Hours</span><span class="rvc-static">${hoursDisplay}</span></div>`
      : `<div class="rvc-field"><span class="rvc-lbl">Hours</span>
          <input type="number" class="review-input rvc-num${warnNow ? ' rvc-input-alert' : ''}" id="review-emp-hours-${l._localId}" value="${l._isNew ? (l.hours || '') : (l.hours || 0).toFixed(2)}" step="0.25" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'hours', this.value)"></div>`;

    const qtyField = isPiece ? `<div class="rvc-field"><span class="rvc-lbl">Qty · ${escapeHtml(formatUnitLabel(l.rateUnit))}</span>
        <input type="number" class="review-input rvc-num" value="${l._isNew ? (l.quantity || '') : (l.quantity || 0)}" step="1" min="0" onfocus="this.select()"
          onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'quantity', this.value)"></div>` : '';

    const rateField = `<div class="rvc-field"><span class="rvc-lbl">Rate</span>
        <input type="number" class="review-input rvc-num${_reviewRateZeroClass(l)}" value="${l._isNew ? (l.rate || '') : (l.rate || 0)}" step="1" min="0" onfocus="this.select()"${_reviewLaborRateLockAttr()}
          onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'rate', this.value)"></div>`;

    const amountField = `<div class="rvc-field rvc-amt"><span class="rvc-lbl">Amount</span><span class="rvc-amt-val" id="review-emp-amount-${l._localId}">${amountDisplay}</span></div>`;

    html += `
      <div class="rvc-card" data-local-id="${l._localId}">
        <div class="rvc-head">
          <div class="rvc-title">${nameHtml}${isActive ? ' ' + _onShiftBadgeHtml(ON_SHIFT_PERSON_TITLE) : ''}</div>
          <button class="btn-icon rvc-del" onclick="window.BillingModule.removeReviewLine('EMPLOYEE', ${l._localId})" title="Remove">
            <span class="material-icons">delete_outline</span></button>
        </div>
        ${timesHtml}
        <div class="rvc-grid">${hoursField}${qtyField}${rateField}${(!isActive && !isPiece) ? _hoursWarnHtml('emp', l) : ''}${amountField}</div>
      </div>`;
  }

  const empSubtotal = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  html += `
      <div class="rvc-subtotal"><span>Labor Subtotal</span><span class="rvc-subtotal-amt" id="review-emp-subtotal">$${empSubtotal.toFixed(2)}</span></div>
      <button class="review-add-btn" onclick="window.BillingModule.addReviewEmployeeRow()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Employee
      </button>
    </div>
  `;
  return html;
}

function renderReviewEmployeeSection() {
  const lines = ReviewState.employeeLines;
  const itemCodeOpts = buildItemCodeOptions('Personnel');
  const hasItemCodes = false; // Hidden from review UI — rate matching handled by Price Books
  const _revShowTimes = showClockTimes();
  const _revFounder = namesOnTickets(); // TASK-865 — names display flag (was isFounder)
  const _hasPieceRate = lines.some(l => !isTimeBasedUnit(l.rateUnit));
  // TASK-1234 — phones get stacked cards instead of the scrolling table.
  if (_reviewCardMode()) return _renderReviewEmployeeCards(lines, _revShowTimes, _revFounder, _hasPieceRate);
  const baseCols = _revShowTimes ? 7 : 5; // Employee, [In, Out], Hours/Qty, Rate, Amount, actions
  const colCount = _hasPieceRate ? baseCols + 1 : baseCols;
  // Read-only gate (desktop table mirror of the card-mode gate) — dim + disable
  // interaction on archived/locked/invoiced tickets so edits can't silently no-op.
  const _roStyle = isReviewReadOnly(ReviewState.ticket) ? ' style="pointer-events:none;opacity:0.6;"' : '';

  let html = `
    <div class="review-section" id="review-emp-section"${_roStyle}>
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">people</span> Employee ${_hasPieceRate ? 'Hours / Piece Rate' : 'Hours'}
        </span>
        ${!_revFounder ? `<label style="display:flex;align-items:center;gap:6px;font-weight:500;font-size:0.8rem;color:var(--text-primary);cursor:pointer;margin:0;">
          <input type="checkbox" id="review-show-names" ${_showEmployeeNames ? 'checked' : ''}
            onchange="window.BillingModule.toggleShowNames(this.checked)" style="accent-color:var(--brand-blue);">
          Show names
        </label>` : ''}
      </div>
      ${_reviewMissingCrewBannerHtml()}${_reviewSisterHoursBannerHtml()}${_reviewPlanVsRanHtml()}
      <div style="overflow-x:auto;">
        <table class="billing-lines-table">
          <thead>
            <tr>
              <th>Employee</th>
              ${_revShowTimes ? '<th>In</th><th>Out</th>' : ''}
              <th style="text-align:right;">Hours</th>
              ${_hasPieceRate ? '<th style="text-align:right;">Qty</th>' : ''}
              <th style="text-align:right;">Rate</th>
              <th style="text-align:right;">Amount</th>
              ${hasItemCodes ? '<th>Item Code</th>' : ''}
              <th></th>
            </tr>
          </thead>
          <tbody>
  `;

  if (lines.length === 0) {
    html += `<tr><td colspan="${colCount}" style="text-align:center;color:var(--text-secondary);font-style:italic;">No employee lines yet</td></tr>`;
  }

  for (const l of lines) {
    // "Active" is AUTHORITATIVE on the clock entry (clockOut == null), NOT inferred from a
    // missing toTime. A Daily ($/day) / piece-rate line legitimately has no toTime, and a
    // build-ticket write can drop times on a closed line — neither means "still clocked in".
    const isActive = isLineClockActive(l, ReviewState.ticket?.id);
    // Only an actually-open shift hides hours/amount behind a live "–". Closed lines (incl.
    // Daily/piece-rate with no toTime) show their real stored hours/amount.
    const hasOpenShift = isActive;
    const hoursDisplay = hasOpenShift ? '-' : (l.hours || 0).toFixed(2);
    const amountDisplay = hasOpenShift ? '-' : '$' + (l.lineAmount || 0).toFixed(2);

    if (l._isNew && !l._restoredFromClock) { // TASK-9755 — a line restored from the clock renders as a normal crew row
      // New row with selectable employee
      html += `
        <tr data-local-id="${l._localId}">
          <td>
            <select class="review-select" onchange="window.BillingModule.onReviewEmployeeSelect(${l._localId}, this.value)">
              <option value="">Select employee...</option>
              ${BillingState.people.map(p => `<option value="${p.id}" ${l.personId === p.id ? 'selected' : ''}>${escapeHtml(p.name)}</option>`).join('')}
            </select>
          </td>
          ${_revShowTimes ? `<td>
            <span class="review-time-display review-time-pill" id="review-emp-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-in', ${l._localId})" title="Click to edit time">${lineFromHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-emp-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime)}"
              onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'fromTime', this.value)">
          </td>
          <td>
            <span class="review-time-display review-time-pill" id="review-emp-out-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-out', ${l._localId})" title="Click to edit time">${lineToHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-emp-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime)}"
              onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'toTime', this.value)">
          </td>` : ''}
          <td style="text-align:right;"><input type="number" class="review-input" id="review-emp-hours-${l._localId}" value="${l.hours || ''}" step="0.25" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'hours', this.value)"></td>
          ${_hasPieceRate ? `<td style="text-align:right;">${!isTimeBasedUnit(l.rateUnit) ? `<input type="number" class="review-input" value="${l.quantity || ''}" step="1" min="0" style="width:60px;display:inline-block;" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'quantity', this.value)">
            <span style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(formatUnitLabel(l.rateUnit))}</span>` : '<span style="color:var(--text-secondary);">—</span>'}</td>` : ''}
          <td style="text-align:right;"><input type="number" class="review-input${_reviewRateZeroClass(l)}" value="${l.rate || ''}" step="1" min="0" onfocus="this.select()"${_reviewLaborRateLockAttr()}
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'rate', this.value)"></td>
          <td style="text-align:right;font-weight:600;" id="review-emp-amount-${l._localId}">${amountDisplay}</td>
          ${hasItemCodes ? `<td><select class="review-select" onchange="window.BillingModule.onReviewItemCodeChange('EMPLOYEE', ${l._localId}, this.value)">
            <option value="">-</option>${itemCodeOpts}</select></td>` : ''}
          <td><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('EMPLOYEE', ${l._localId})" title="Remove">
            <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
        </tr>
      `;
    } else {
      // Existing row — show role as primary for non-founder, name for founder
      const empDisplayCell = _revFounder
        ? `${escapeHtml(l.personName || '-')}${l.role && CREW_TYPE_ITEM_CODE_MAP[l.role] ? ` <span style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(window.getTypeLabel?.(l.role, 'crewTypes') || l.role)}</span>` : (l.role && l.role !== 'CDL' && l.role !== 'NON_CDL' ? ` <span style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(l.role)}</span>` : '')}`
        : `${escapeHtml(getEmployeeDisplayName(l))}${(_showEmployeeNames && l.role) ? '' : (l.personName && _showEmployeeNames ? '' : '')}`;
      html += `
        <tr data-local-id="${l._localId}">
          <td>${empDisplayCell}${isActive ? ' ' + _onShiftBadgeHtml(ON_SHIFT_PERSON_TITLE) : ''}</td>
          ${_revShowTimes ? `<td>
            <span class="review-time-display review-time-pill" id="review-emp-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-in', ${l._localId})" title="Click to edit time">${lineFromHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-emp-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime)}"
              onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'fromTime', this.value)">
          </td>
          <td>
            <span class="review-time-display${!isActive ? ' review-time-pill' : ''}" id="review-emp-out-display-${l._localId}"${!isActive ? ` onclick="window.BillingModule.toggleReviewTimeEdit('review-emp-out', ${l._localId})" title="Click to edit time"` : ''}>${lineToHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-emp-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime)}"
              onchange="window.BillingModule.updateReviewTime('EMPLOYEE', ${l._localId}, 'toTime', this.value)">
          </td>` : ''}
          <td style="text-align:right;">${isActive
            ? hoursDisplay
            : `<input type="number" class="review-input" id="review-emp-hours-${l._localId}" value="${(l.hours || 0).toFixed(2)}" step="0.25" min="0" onfocus="this.select()"
              onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'hours', this.value)">`
          }</td>
          ${_hasPieceRate ? `<td style="text-align:right;">${!isTimeBasedUnit(l.rateUnit) ? `<input type="number" class="review-input" value="${l.quantity || 0}" step="1" min="0" style="width:60px;display:inline-block;" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'quantity', this.value)">
            <span style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(formatUnitLabel(l.rateUnit))}</span>` : '<span style="color:var(--text-secondary);">—</span>'}</td>` : ''}
          <td style="text-align:right;"><input type="number" class="review-input${_reviewRateZeroClass(l)}" value="${l.rate || 0}" step="1" min="0" onfocus="this.select()"${_reviewLaborRateLockAttr()}
            onchange="window.BillingModule.updateReviewEmployeeLine(${l._localId}, 'rate', this.value)"></td>
          <td style="text-align:right;font-weight:600;" id="review-emp-amount-${l._localId}">${amountDisplay}</td>
          ${hasItemCodes ? `<td><select class="review-select" onchange="window.BillingModule.onReviewItemCodeChange('EMPLOYEE', ${l._localId}, this.value)">
            <option value="">-</option>${itemCodeOpts.replace(`value="${l.itemCodeId}"`, `value="${l.itemCodeId}" selected`)}</select></td>` : ''}
          <td><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('EMPLOYEE', ${l._localId})" title="Remove">
            <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
        </tr>
      `;
    }
  }

  const empSubtotal = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  html += `
          </tbody>
          <tfoot>
            <tr>
              <td colspan="${colCount - 2}" style="text-align:right;font-weight:600;">Labor Subtotal</td>
              <td style="text-align:right;font-weight:700;" id="review-emp-subtotal">$${empSubtotal.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <button class="review-add-btn" onclick="window.BillingModule.addReviewEmployeeRow()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Employee
      </button>
    </div>
  `;

  return html;
}

// ============================================
// SHIP SLIPS — review section (driver mobile + admin desktop)
// Lists existing slips on the ticket + Add Ship Slip button
// ============================================
function renderReviewShipSlipSection() {
  const slips = ReviewState.shipSlips || [];
  const totals = slipGetRunningTotals(slips);
  const ticketId = ReviewState.ticketId;

  const slipRows = slips.map(s => {
    const isVoid = s.status === 'void';
    const isLocked = s.status === 'locked';
    const created = s.createdAt?.toDate ? s.createdAt.toDate() : null;
    const dateStr = created ? created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + created.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
    const wellLabel = [s.leaseName, s.wellName].filter(Boolean).join(' — ');
    const bbls = s.bblsTransferred != null && s.bblsTransferred !== '' ? `${s.bblsTransferred} bbls` : '—';
    const statusBadge = isVoid
      ? `<span style="font-size:0.7rem;background:var(--status-danger);color:#fff;padding:2px 6px;border-radius:4px;">VOID</span>`
      : isLocked
        ? `<span style="font-size:0.7rem;background:var(--text-muted);color:#fff;padding:2px 6px;border-radius:4px;">LOCKED</span>`
        : '';
    return `
      <tr style="${isVoid ? 'opacity:0.55;' : ''}">
        <td style="font-weight:600;white-space:nowrap;">${escapeHtml(s.slipNumber)} ${statusBadge}</td>
        <td>${escapeHtml(wellLabel || '—')}</td>
        <td style="text-align:right;white-space:nowrap;">${escapeHtml(bbls)}</td>
        <td style="font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;">${escapeHtml(dateStr)}</td>
        <td style="text-align:right;white-space:nowrap;">
          <button class="btn-icon" title="Print slip" onclick="window.BillingModule.printShipSlip('${escapeHtml(s.slipId)}')">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">print</span>
          </button>
          ${!isVoid && !isLocked ? `<button class="btn-icon" title="Edit slip" onclick="window.BillingModule.openShipSlipForm('${escapeHtml(ticketId)}','${escapeHtml(s.slipId)}')">
            <span class="material-icons" style="font-size:18px;">edit</span>
          </button>` : ''}
          ${!isVoid ? `<button class="btn-icon" title="Void slip" onclick="window.BillingModule.voidShipSlip('${escapeHtml(s.slipId)}')">
            <span class="material-icons" style="font-size:18px;color:var(--status-danger);">block</span>
          </button>` : ''}
        </td>
      </tr>
    `;
  }).join('');

  const summary = totals.slipCount > 0
    ? `<span style="font-weight:400;color:var(--text-secondary);font-size:0.8rem;">(${totals.slipCount} slip${totals.slipCount === 1 ? '' : 's'} · ${totals.bblsTotal} bbls total${totals.voidCount > 0 ? ' · ' + totals.voidCount + ' void' : ''})</span>`
    : '';

  const arriveAt = ReviewState.ticket?.lastArriveSiteAt;
  const departAt = ReviewState.ticket?.lastDepartSiteAt;
  // TASK-9197 — arrive/depart is a PAIR, and a crew that arrives at 10 PM and
  // departs at 2 AM had these two buttons reading "Arrived 10:00 PM" /
  // "Departed 2:00 AM" with nothing to say the second is the next day. Same
  // shared cell formatter as the ticket lines, TEXT form because the labels go
  // through escapeHtml on their way into the button.
  const _ping = window.Fmt.shiftCells(arriveAt, departAt);
  const arriveLabel = arriveAt ? `Arrived ${_ping.fromText}` : 'Arrive on Site';
  const departLabel = departAt ? `Departed ${_ping.toText}` : 'Depart Site';

  return `
    <div class="review-section" id="review-ship-slips-section">
      <div class="review-section-title">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">receipt_long</span> Ship Slips
          ${summary}
        </span>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
        <button class="btn btn-sm" style="flex:1;min-width:140px;background:#F59E0B;color:#fff;border:none;display:inline-flex;align-items:center;gap:6px;justify-content:center;" onclick="window.BillingModule.fireSitePing('${escapeHtml(ticketId)}','arrive_site')">
          <span class="material-icons" style="font-size:18px;">place</span> ${escapeHtml(arriveLabel)}
        </button>
        <button class="btn btn-sm" style="flex:1;min-width:140px;background:#FCD34D;color:#1F2937;border:none;display:inline-flex;align-items:center;gap:6px;justify-content:center;" onclick="window.BillingModule.fireSitePing('${escapeHtml(ticketId)}','depart_site')">
          <span class="material-icons" style="font-size:18px;">flag</span> ${escapeHtml(departLabel)}
        </button>
      </div>
      ${slips.length > 0 ? `
        <div style="overflow-x:auto;">
          <table class="billing-lines-table">
            <thead><tr><th>Slip #</th><th>Lease / Well</th><th style="text-align:right;">Bbls</th><th>Created</th><th></th></tr></thead>
            <tbody>${slipRows}</tbody>
          </table>
        </div>
      ` : `
        <div style="padding:16px;color:var(--text-secondary);font-size:0.9rem;text-align:center;background:var(--bg-secondary);border-radius:8px;">
          No ship slips yet. Tap "Add Ship Slip" below to create one.
        </div>
      `}
      <button class="review-add-btn" onclick="window.BillingModule.openShipSlipForm('${escapeHtml(ticketId)}')" style="margin-top:8px;">
        <span class="material-icons">add</span> Add Ship Slip
      </button>
    </div>
  `;
}

function openPrinterSetup() {
  const ua = navigator.userAgent || '';
  const isIOS = /iP(hone|ad|od)/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';

  const iosSteps = `
    <ol style="margin:0;padding-left:20px;line-height:1.6;font-size:0.9rem;">
      <li>Power on your Bluetooth thermal printer (most beep or blink an LED when ready to pair).</li>
      <li>Open <strong>Settings → Bluetooth</strong> on the iPhone or iPad.</li>
      <li>Wait for the printer to appear under <em>Other Devices</em>, then tap it. If asked for a PIN, try <code>0000</code> or <code>1234</code> — those are the manufacturer defaults for almost every receipt printer.</li>
      <li>Once paired, the printer shows up in <em>My Devices</em>. Leave that screen.</li>
      <li>Come back to DispatchDeck, open any ship slip and tap <strong>Print</strong>. iOS will show the AirPrint dialog — your paired printer will be in the list.</li>
      <li><strong>Tip:</strong> if your printer is older Bluetooth (BLE-only), it may not show in AirPrint. In that case, install your printer's free companion app (e.g. EpsonConnect, Star Print) to bridge — print still goes through the iOS share sheet.</li>
    </ol>
  `;
  const androidSteps = `
    <ol style="margin:0;padding-left:20px;line-height:1.6;font-size:0.9rem;">
      <li>Power on your Bluetooth thermal printer.</li>
      <li>Open <strong>Settings → Connected devices → Pair new device</strong>.</li>
      <li>Tap your printer when it appears. PIN is usually <code>0000</code> or <code>1234</code> if asked.</li>
      <li>Once paired, install your printer brand's print plugin from Google Play (Star, Epson, Brother, MUNBYN — search the brand). The plugin bridges the printer into Android's Print framework.</li>
      <li>Return to DispatchDeck, open a slip and tap <strong>Print</strong>. Android's print dialog will list the printer.</li>
      <li><strong>Tip:</strong> set <em>Paper size: 80 mm × Receipt</em> (or 58 mm depending on printer) before printing. Some printers also need <em>scale: 100%</em> to prevent shrinking.</li>
    </ol>
  `;
  const desktopSteps = `
    <ol style="margin:0;padding-left:20px;line-height:1.6;font-size:0.9rem;">
      <li>This page is most useful from a phone or tablet — that's what your drivers will use to print slips in the field. Open dispatchdeck.app on the device that has the Bluetooth thermal printer paired.</li>
      <li>If you're printing slips from this desktop, just hit Print — your default OS printer will be used.</li>
    </ol>
  `;

  const stepsHTML = platform === 'ios' ? iosSteps : platform === 'android' ? androidSteps : desktopSteps;
  const platformLabel = platform === 'ios' ? 'iPhone / iPad' : platform === 'android' ? 'Android' : 'Desktop';

  const existing = document.getElementById('modal-printer-setup');
  if (existing) existing.remove();
  const modal = document.createElement('div');
  modal.id = 'modal-printer-setup';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.5);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px;';
  modal.innerHTML = `
    <div style="background:#fff;border-radius:12px;max-width:620px;width:100%;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;">
      <div style="background:linear-gradient(135deg,#3B82F6,#1D4ED8);color:#fff;padding:14px 20px;display:flex;align-items:center;justify-content:space-between;">
        <h3 style="margin:0;color:#fff;display:flex;align-items:center;gap:8px;font-size:1.05rem;">
          <span class="material-icons">print</span> Printer Setup — ${platformLabel}
        </h3>
        <button onclick="document.getElementById('modal-printer-setup').remove()" style="background:transparent;border:none;color:#fff;cursor:pointer;padding:4px;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:20px;overflow-y:auto;flex:1;">
        <p style="font-size:0.88rem;color:var(--text-secondary);margin:0 0 14px;">
          DispatchDeck prints through your phone's built-in print dialog (AirPrint on iOS, Print framework on Android). Pair your Bluetooth thermal printer once at the OS level and it's available everywhere.
        </p>
        <div style="background:#F8FAFC;border-left:3px solid #3B82F6;border-radius:6px;padding:12px 14px;margin-bottom:16px;">
          ${stepsHTML}
        </div>
        <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:10px 12px;margin-bottom:16px;font-size:0.82rem;color:#78350F;">
          <strong>Once paired, test it:</strong> tap the button below and try printing the sample slip. If your printer doesn't appear in the OS print dialog, the pairing didn't take — repeat the steps above.
        </div>
      </div>
      <div style="padding:14px 20px;border-top:1px solid var(--border-light);display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;">
        <button onclick="document.getElementById('modal-printer-setup').remove()" style="padding:8px 14px;background:#F1F5F9;border:1px solid var(--border-light);border-radius:6px;cursor:pointer;font-size:0.85rem;">Close</button>
        <button onclick="window.BillingModule.runTestPrint()" style="padding:8px 14px;background:#10B981;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;display:inline-flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">print</span> Test Print
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function runTestPrint() {
  const sampleSlip = {
    slipNumber: 'TEST-PRINT',
    leaseName: 'Sample Lease',
    wellName: 'Test #1',
    materialType: 'Sample',
    tankInUnit: 'bbls',
    tankInValue: 100,
    tankOutUnit: 'bbls',
    tankOutValue: 50,
    bblsTransferred: 50,
    customerRepName: '',
    notes: 'This is a test print from DispatchDeck. If you can see this on paper, your printer is set up correctly.',
    photoUrls: [],
    lineItemRefs: [],
    driverName: window.AppState?.userName || '',
    truckName: '',
    gpsCoords: null,
    status: 'open',
    createdAt: new Date()
  };
  const tenantData = window.AppState?.tenantData || {};
  slipPrint({
    slip: sampleSlip,
    ticket: { invoiceNumber: 'TEST' },
    customer: { name: 'Test Customer' },
    company: {
      name: tenantData.name || 'DispatchDeck',
      address: tenantData.address || '',
      phone: tenantData.phone || '',
      email: tenantData.email || ''
    },
    runningTotal: 50,
    includeRunningTotal: true,
    template: window.AppState?.settings?.slipTemplate || null,
  });
}

async function fireSitePing(ticketId, eventType) {
  if (!['arrive_site', 'depart_site'].includes(eventType)) return;
  const tenant = BillingState.tenantId;
  if (!tenant || !ticketId) return;
  try {
    const ping = await gpsCapturePing({
      eventType,
      tenant,
      ticketId,
    });
    // Stamp on the ticket so the UI can show "Arrived 1:23 PM" without re-querying pings.
    const stampField = eventType === 'arrive_site' ? 'lastArriveSiteAt' : 'lastDepartSiteAt';
    try {
      await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
        [stampField]: Timestamp.now(),
      });
      if (ReviewState.ticket) {
        ReviewState.ticket[stampField] = Timestamp.now();
      }
    } catch (e) { /* ticket stamp is best-effort — ping log is source of truth */ }
    const label = eventType === 'arrive_site' ? 'Arrival' : 'Departure';
    if (ping?.ok && ping.gpsAvailable === false) {
      window.showToast?.(`${label} logged — GPS unavailable (${ping.reason || 'no fix'})`, 'warn');
    } else if (ping?.ok) {
      window.showToast?.(`${label} pinged`, 'success');
    } else {
      window.showToast?.(`${label} couldn't be pinged: ${ping?.reason || 'unknown'}`, 'error');
    }
    renderReviewTicket();
  } catch (e) {
    console.warn('[fireSitePing]', eventType, e?.message || e);
    window.showToast?.(`Couldn't capture GPS — check location permission`, 'error');
  }
}

// ============================================
// SHIP SLIPS — Form, save, print, void
// ============================================
function ensureShipSlipModal() {
  if (document.getElementById('modal-ship-slip')) return;
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="modal hidden" id="modal-ship-slip">
      <div class="modal-content modal-fullscreen" id="ship-slip-modal-content">
        <div class="modal-header">
          <h3 id="ship-slip-modal-title">Add Ship Slip</h3>
          <button class="btn-icon" onclick="window.BillingModule.closeShipSlipForm()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-ship-slip" novalidate onsubmit="window.BillingModule.saveShipSlip(event)">
          <div id="ship-slip-form-body" style="max-height:75vh;overflow-y:auto;padding:var(--spacing-lg);">
            <!-- populated by openShipSlipForm -->
          </div>
          <div class="form-actions" style="position:sticky;bottom:0;background:var(--bg-primary);border-top:1px solid var(--border-light);padding:var(--spacing-md);display:flex;gap:8px;flex-wrap:wrap;">
            <button type="button" class="btn btn-secondary" onclick="window.BillingModule.closeShipSlipForm()">Cancel</button>
            <button type="submit" class="btn btn-primary" style="flex:1;min-width:120px;">
              <span class="material-icons" style="font-size:18px;vertical-align:middle;">save</span> Save Slip
            </button>
            <button type="submit" class="btn btn-primary" name="save-and-print" value="1" style="flex:1;min-width:140px;background:#10b981;border-color:#10b981;">
              <span class="material-icons" style="font-size:18px;vertical-align:middle;">print</span> Save &amp; Print
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(wrap.firstElementChild);
}

async function openShipSlipForm(ticketId, slipId = null) {
  ensureShipSlipModal();
  const ticket = ReviewState.ticket;
  const tenant = BillingState.tenantId;
  let slip = null;
  if (slipId) {
    slip = (ReviewState.shipSlips || []).find(s => s.slipId === slipId);
  }

  // Pull current ticket lines from ReviewState — both employee + equipment + misc
  const ticketLineCandidates = [
    ...ReviewState.employeeLines.map(l => ({ id: l.id || l._localId, type: 'EMPLOYEE', description: getEmployeeDisplayName(l), qty: (l.hours || 0).toFixed(2), unit: 'hrs' })),
    ...ReviewState.equipmentLines.map(l => ({ id: l.id || l._localId, type: 'EQUIPMENT', description: l.assetName || l.description || 'Equipment', qty: (l.hours || l.qty || 0).toFixed(2), unit: l.unit || 'hrs' })),
    ...(ReviewState.miscLines || []).map(l => ({ id: l.id || l._localId, type: 'MISC', description: l.description || 'Misc', qty: l.qty || '', unit: l.unit || '' }))
  ];

  const checkedRefIds = new Set((slip?.lineItemRefs || []).map(r => r.id));
  const lineItemsHTML = ticketLineCandidates.length === 0
    ? `<div style="padding:8px;color:var(--text-muted);font-size:0.85rem;">No ticket line items yet. Add some in the ticket builder, then re-open this slip.</div>`
    : ticketLineCandidates.map(li => `
        <label style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
          <input type="checkbox" class="ship-slip-line-cb" data-line-id="${escapeHtml(String(li.id))}" data-type="${escapeHtml(li.type)}" data-desc="${escapeHtml(li.description)}" data-qty="${escapeHtml(String(li.qty))}" data-unit="${escapeHtml(li.unit)}" ${checkedRefIds.has(li.id) ? 'checked' : ''}>
          <span style="flex:1;">${escapeHtml(li.description)}</span>
          <span style="color:var(--text-secondary);font-size:0.85rem;">${escapeHtml(String(li.qty))} ${escapeHtml(li.unit)}</span>
        </label>
      `).join('');

  const tankInUnit = slip?.tankInUnit || 'bbls';
  const tankOutUnit = slip?.tankOutUnit || 'bbls';

  const body = document.getElementById('ship-slip-form-body');
  body.innerHTML = `
    <input type="hidden" id="ship-slip-ticket-id" value="${escapeHtml(ticketId)}">
    <input type="hidden" id="ship-slip-slip-id" value="${escapeHtml(slipId || '')}">

    <div class="form-group" style="margin-bottom:14px;">
      <label style="font-weight:600;">Lease / Well</label>
      <input type="text" id="ship-slip-lease" class="form-control" placeholder="Smith Federal #4H" value="${escapeHtml(slip?.leaseName || '')}">
    </div>

    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;">
      <div class="form-group" style="flex:1;min-width:140px;">
        <label style="font-size:0.85rem;color:var(--text-secondary);">Well Name</label>
        <input type="text" id="ship-slip-well" class="form-control" placeholder="optional" value="${escapeHtml(slip?.wellName || '')}">
      </div>
      <div class="form-group" style="flex:1;min-width:140px;">
        <label style="font-size:0.85rem;color:var(--text-secondary);">API #</label>
        <input type="text" id="ship-slip-api" class="form-control" placeholder="optional" value="${escapeHtml(slip?.wellApiNumber || '')}">
      </div>
      <div class="form-group" style="flex:1;min-width:140px;">
        <label style="font-size:0.85rem;color:var(--text-secondary);">Material</label>
        <input type="text" id="ship-slip-material" class="form-control" placeholder="Hot Oil, Crude, etc." value="${escapeHtml(slip?.materialType || '')}">
      </div>
    </div>

    <div class="review-section-title" style="margin:16px 0 8px 0;font-size:0.95rem;">Tank Readings</div>

    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
      <div style="flex:1;min-width:200px;">
        <label style="font-size:0.85rem;color:var(--text-secondary);">Tank In</label>
        <div style="display:flex;gap:6px;align-items:center;">
          <select id="ship-slip-tank-in-unit" class="form-control" style="width:90px;" onchange="window.BillingModule.toggleShipSlipTankUnit('in'); window.BillingModule.recomputeShipSlipBbls();">
            <option value="bbls" ${tankInUnit === 'bbls' ? 'selected' : ''}>bbls</option>
            <option value="ft_in" ${tankInUnit === 'ft_in' ? 'selected' : ''}>ft+in</option>
          </select>
          <div id="ship-slip-tank-in-bbls-wrap" style="${tankInUnit === 'bbls' ? '' : 'display:none;'}flex:1;">
            <input type="number" step="0.01" id="ship-slip-tank-in-bbls" class="form-control" placeholder="bbls" value="${escapeHtml(slip?.tankInValue ?? '')}" oninput="window.BillingModule.recomputeShipSlipBbls()">
          </div>
          <div id="ship-slip-tank-in-ftin-wrap" style="${tankInUnit === 'ft_in' ? '' : 'display:none;'}display:${tankInUnit === 'ft_in' ? 'flex' : 'none'};gap:4px;flex:1;">
            <input type="number" id="ship-slip-tank-in-ft" class="form-control" placeholder="ft" value="${escapeHtml(slip?.tankInFeet ?? '')}" style="width:70px;" oninput="window.BillingModule.recomputeShipSlipBbls()">
            <input type="number" step="0.01" id="ship-slip-tank-in-inches" class="form-control" placeholder="in" value="${escapeHtml(slip?.tankInInches ?? '')}" style="width:70px;" oninput="window.BillingModule.recomputeShipSlipBbls()">
          </div>
        </div>
      </div>

      <div style="flex:1;min-width:200px;">
        <label style="font-size:0.85rem;color:var(--text-secondary);">Tank Out</label>
        <div style="display:flex;gap:6px;align-items:center;">
          <select id="ship-slip-tank-out-unit" class="form-control" style="width:90px;" onchange="window.BillingModule.toggleShipSlipTankUnit('out'); window.BillingModule.recomputeShipSlipBbls();">
            <option value="bbls" ${tankOutUnit === 'bbls' ? 'selected' : ''}>bbls</option>
            <option value="ft_in" ${tankOutUnit === 'ft_in' ? 'selected' : ''}>ft+in</option>
          </select>
          <div id="ship-slip-tank-out-bbls-wrap" style="${tankOutUnit === 'bbls' ? '' : 'display:none;'}flex:1;">
            <input type="number" step="0.01" id="ship-slip-tank-out-bbls" class="form-control" placeholder="bbls" value="${escapeHtml(slip?.tankOutValue ?? '')}" oninput="window.BillingModule.recomputeShipSlipBbls()">
          </div>
          <div id="ship-slip-tank-out-ftin-wrap" style="display:${tankOutUnit === 'ft_in' ? 'flex' : 'none'};gap:4px;flex:1;">
            <input type="number" id="ship-slip-tank-out-ft" class="form-control" placeholder="ft" value="${escapeHtml(slip?.tankOutFeet ?? '')}" style="width:70px;" oninput="window.BillingModule.recomputeShipSlipBbls()">
            <input type="number" step="0.01" id="ship-slip-tank-out-inches" class="form-control" placeholder="in" value="${escapeHtml(slip?.tankOutInches ?? '')}" style="width:70px;" oninput="window.BillingModule.recomputeShipSlipBbls()">
          </div>
        </div>
      </div>
    </div>

    <div class="form-group" style="margin-bottom:14px;">
      <label style="font-weight:600;">Bbls Transferred (this slip)</label>
      <input type="number" step="0.01" id="ship-slip-bbls" class="form-control" placeholder="Auto-calculated; override if needed" value="${escapeHtml(slip?.bblsTransferred ?? '')}">
      <p style="font-size:0.75rem;color:var(--text-muted);margin-top:2px;">Leave blank to auto-calculate from Tank In − Tank Out (bbls mode only).</p>
    </div>

    <div class="form-group" style="margin-bottom:14px;">
      <label style="font-weight:600;">Customer Representative</label>
      <input type="text" id="ship-slip-cust-rep" class="form-control" placeholder="Name on the slip" value="${escapeHtml(slip?.customerRepName || '')}">
    </div>

    <div class="form-group" style="margin-bottom:14px;">
      <label style="font-weight:600;">Notes</label>
      <textarea id="ship-slip-notes" class="form-control" rows="2" placeholder="Optional">${escapeHtml(slip?.notes || '')}</textarea>
    </div>

    <div class="review-section-title" style="margin:16px 0 8px 0;font-size:0.95rem;">Photos</div>
    <div id="ship-slip-photos-grid" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px;"></div>
    <input type="file" id="ship-slip-photo-input" accept="image/*" capture="environment" style="display:none;" onchange="window.BillingModule.handleShipSlipPhotoPick(event)">
    <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('ship-slip-photo-input').click()" style="margin-bottom:14px;display:inline-flex;align-items:center;gap:6px;">
      <span class="material-icons" style="font-size:18px;">photo_camera</span> Add Photo
    </button>

    <div class="review-section-title" style="margin:16px 0 8px 0;font-size:0.95rem;">Line Items on This Slip</div>
    <div style="background:var(--bg-secondary);padding:8px 12px;border-radius:8px;">
      ${lineItemsHTML}
    </div>
  `;

  // Hydrate photo state from existing slip URLs (if editing). Photos uploaded during
  // this form session get appended; user can remove pre-existing or new ones.
  ShipSlipFormState.photos = (slip?.photoUrls || []).map(u => ({ url: u, storagePath: null }));
  renderShipSlipPhotosGrid();

  document.getElementById('ship-slip-modal-title').textContent = slipId ? `Edit ${slip?.slipNumber || 'Ship Slip'}` : 'Add Ship Slip';
  document.getElementById('modal-ship-slip').classList.remove('hidden');
}

const ShipSlipFormState = { photos: [], uploading: false };

function renderShipSlipPhotosGrid() {
  const grid = document.getElementById('ship-slip-photos-grid');
  if (!grid) return;
  if (ShipSlipFormState.photos.length === 0) {
    grid.innerHTML = `<div style="font-size:0.8rem;color:var(--text-muted);">No photos yet — tap Add Photo to attach proof of load, meter reads, well condition, etc.</div>`;
    return;
  }
  grid.innerHTML = ShipSlipFormState.photos.map((p, i) => `
    <div style="position:relative;width:88px;height:88px;border-radius:8px;overflow:hidden;border:1px solid var(--border-light);background:#000;">
      <img ${window.StorageFile.imgAttrs(p)} data-sf-open="1" alt="" style="width:100%;height:100%;object-fit:cover;cursor:pointer;">
      <button type="button" onclick="window.BillingModule.removeShipSlipPhoto(${i})" title="Remove" style="position:absolute;top:2px;right:2px;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:50%;width:22px;height:22px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;">
        <span class="material-icons" style="font-size:14px;">close</span>
      </button>
    </div>
  `).join('');
}

async function handleShipSlipPhotoPick(event) {
  const file = event.target.files?.[0];
  event.target.value = ''; // allow re-pick of the same file
  if (!file) return;
  if (ShipSlipFormState.uploading) {
    window.showToast?.('Still adding the last photo — try again in a moment.', 'info');
    return;
  }
  const valid = validateImageFile(file);
  if (!valid.valid) {
    window.showToast?.(valid.error || 'Invalid image', 'error');
    return;
  }
  ShipSlipFormState.uploading = true;
  const tenant = BillingState.tenantId;
  const ticketId = document.getElementById('ship-slip-ticket-id')?.value;
  if (!tenant || !ticketId) {
    window.showToast?.('Missing context — close and reopen the slip form', 'error');
    ShipSlipFormState.uploading = false;
    return;
  }
  // openShipSlipForm replaces photos[]; a photo finishing after the form moved
  // to another slip must not be attached to it.
  const slipPhotos = ShipSlipFormState.photos;
  try {
    // TASK-9397 — a HEIC this browser can't draw is converted first (it used
    // to die in processPhoto with a bare "Failed to load image").
    const drawable = await toDrawablePhoto(file);
    const blob = await processPhoto(drawable);
    const ts = Date.now();
    const safeName = (drawable.name || file.name || 'photo.jpg').replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `tenants/${tenant}/photos/tickets/${ticketId}/slip-photos/${ts}_${safeName}`;
    const storageRefObj = ref(storage, storagePath);
    await uploadBytes(storageRefObj, blob, { contentType: 'image/jpeg' });
    const url = await getDownloadURL(storageRefObj);
    if (ShipSlipFormState.photos !== slipPhotos) return;
    ShipSlipFormState.photos.push({ url, storagePath });
    renderShipSlipPhotosGrid();
  } catch (err) {
    console.error('[Billing] handleShipSlipPhotoPick failed:', err?.cause || err);
    window.showToast?.(photoErrorMessage(err, err?.message || 'Photo upload failed'), 'error');
  } finally {
    ShipSlipFormState.uploading = false;
  }
}

function removeShipSlipPhoto(index) {
  if (index < 0 || index >= ShipSlipFormState.photos.length) return;
  ShipSlipFormState.photos.splice(index, 1);
  renderShipSlipPhotosGrid();
}

function closeShipSlipForm() {
  document.getElementById('modal-ship-slip')?.classList.add('hidden');
}

function toggleShipSlipTankUnit(side) {
  const unitEl = document.getElementById(`ship-slip-tank-${side}-unit`);
  if (!unitEl) return;
  const unit = unitEl.value;
  const bblsWrap = document.getElementById(`ship-slip-tank-${side}-bbls-wrap`);
  const ftinWrap = document.getElementById(`ship-slip-tank-${side}-ftin-wrap`);
  if (bblsWrap) bblsWrap.style.display = unit === 'bbls' ? '' : 'none';
  if (ftinWrap) ftinWrap.style.display = unit === 'ft_in' ? 'flex' : 'none';
}

function recomputeShipSlipBbls() {
  const inUnit = document.getElementById('ship-slip-tank-in-unit')?.value || 'bbls';
  const outUnit = document.getElementById('ship-slip-tank-out-unit')?.value || 'bbls';
  const bblsField = document.getElementById('ship-slip-bbls');
  if (!bblsField) return;
  // Only auto-calc when both are bbls and user hasn't manually entered a value
  if (bblsField.value !== '' && bblsField.dataset.userOverride === '1') return;
  if (inUnit === 'bbls' && outUnit === 'bbls') {
    const tin = parseFloat(document.getElementById('ship-slip-tank-in-bbls')?.value);
    const tout = parseFloat(document.getElementById('ship-slip-tank-out-bbls')?.value);
    if (!isNaN(tin) && !isNaN(tout)) {
      const diff = Math.round((tin - tout) * 100) / 100;
      bblsField.value = diff;
    }
  }
}

async function saveShipSlip(event) {
  event.preventDefault();
  // TASK-9397 — a photo still converting/uploading isn't in photos[] yet;
  // saving now would drop it. (Conversion made this window seconds long.)
  if (ShipSlipFormState.uploading) {
    window.showToast?.('Still adding the photo — one moment, then press Save.', 'info');
    return;
  }
  const submitter = event.submitter;
  const printAfter = submitter && submitter.name === 'save-and-print';

  const ticketId = document.getElementById('ship-slip-ticket-id').value;
  const editingSlipId = document.getElementById('ship-slip-slip-id').value || null;

  const tankInUnit = document.getElementById('ship-slip-tank-in-unit').value;
  const tankOutUnit = document.getElementById('ship-slip-tank-out-unit').value;

  const formData = {
    leaseName: document.getElementById('ship-slip-lease').value.trim(),
    wellName: document.getElementById('ship-slip-well').value.trim(),
    wellApiNumber: document.getElementById('ship-slip-api').value.trim(),
    materialType: document.getElementById('ship-slip-material').value.trim(),
    tankInUnit,
    tankInValue: tankInUnit === 'bbls' ? parseFloatOrNull(document.getElementById('ship-slip-tank-in-bbls').value) : null,
    tankInFeet: tankInUnit === 'ft_in' ? parseFloatOrNull(document.getElementById('ship-slip-tank-in-ft').value) : null,
    tankInInches: tankInUnit === 'ft_in' ? parseFloatOrNull(document.getElementById('ship-slip-tank-in-inches').value) : null,
    tankOutUnit,
    tankOutValue: tankOutUnit === 'bbls' ? parseFloatOrNull(document.getElementById('ship-slip-tank-out-bbls').value) : null,
    tankOutFeet: tankOutUnit === 'ft_in' ? parseFloatOrNull(document.getElementById('ship-slip-tank-out-ft').value) : null,
    tankOutInches: tankOutUnit === 'ft_in' ? parseFloatOrNull(document.getElementById('ship-slip-tank-out-inches').value) : null,
    bblsTransferred: parseFloatOrNull(document.getElementById('ship-slip-bbls').value),
    customerRepName: document.getElementById('ship-slip-cust-rep').value.trim(),
    notes: document.getElementById('ship-slip-notes').value.trim(),
    photoUrls: ShipSlipFormState.photos.map(p => p.url),
  };

  // Collect checked line items
  const lineItemRefs = [];
  document.querySelectorAll('.ship-slip-line-cb:checked').forEach(cb => {
    lineItemRefs.push({
      id: cb.dataset.lineId,
      type: cb.dataset.type,
      description: cb.dataset.desc,
      qty: cb.dataset.qty,
      unit: cb.dataset.unit
    });
  });
  formData.lineItemRefs = lineItemRefs;

  if (!formData.leaseName) {
    window.showToast?.('Lease / Well is required', 'error');
    return;
  }

  // TASK-4832 — WHICH SIDE OF THE WRITE ARE WE ON?
  // This try block does not end at the write: the re-read, the re-render and
  // the print all run AFTER the slip is already in Firestore. So the catch must
  // not say "nothing was saved" unconditionally — on those paths it would send a
  // biller to re-enter a slip that exists, which is a duplicate on a money
  // surface. `persisted` is flipped the instant createSlip / updateSlip resolves
  // and never before: the slip number is allocated BEFORE addDoc, and a rejected
  // addDoc rolls its local mutation back, so every path up to that point leaves
  // Firestore untouched and there is never a half-written slip.
  let persisted = false;
  let savedSlip;
  try {
    if (editingSlipId) {
      await slipUpdate(BillingState.tenantId, ticketId, editingSlipId, formData);
      persisted = true;
      // Re-fetch updated slip
      const updatedList = await slipGetForTicket(BillingState.tenantId, ticketId);
      ReviewState.shipSlips = updatedList;
      savedSlip = updatedList.find(s => s.slipId === editingSlipId);
      window.showToast?.(`Slip ${savedSlip?.slipNumber || ''} saved`, 'success');
    } else {
      // Create slip first
      savedSlip = await slipCreate(BillingState.tenantId, ticketId, {
        ...formData,
        driverId: ReviewState.ticket?.personId || null,
        driverName: ReviewState.ticket?.personName || null
      });
      persisted = true;
      // Fire GPS ping with slipId reference (best-effort, non-blocking on failure)
      try {
        await gpsCapturePing({
          eventType: 'slip_created',
          tenant: BillingState.tenantId,
          ticketId,
          slipId: savedSlip.slipId
        });
      } catch (_) { /* best-effort; pings collection writes are non-fatal */ }
      const updatedList = await slipGetForTicket(BillingState.tenantId, ticketId);
      ReviewState.shipSlips = updatedList;
      savedSlip = updatedList.find(s => s.slipId === savedSlip.slipId) || savedSlip;
      window.showToast?.(`Slip ${savedSlip?.slipNumber || ''} created`, 'success');
    }

    closeShipSlipForm();
    renderReviewTicket();

    if (printAfter && savedSlip) {
      printShipSlip(savedSlip.slipId);
    }
  } catch (err) {
    console.error('[Billing] saveShipSlip failed:', err);
    // TASK-4832 — the raw FirebaseError used to go straight into the toast, so an
    // EXHAUSTED number allocation read as "Error saving slip: Missing or
    // insufficient permissions." and sent the biller hunting an access bug that
    // does not exist. Measured against real rules in
    // tests/slip-counter-self-heal.spec.js (THE COPY BASELINE).
    window.logError?.(err, { module: 'billing', operation: 'SAVE_SHIP_SLIP', ticketId, persisted });
    window.showToast?.(slipSaveFailedMessage(err, {
      persisted,
      editing: !!editingSlipId,
      slipNumber: savedSlip?.slipNumber || '',
    }), 'error');
  }
}

function printShipSlip(slipId) {
  const slip = (ReviewState.shipSlips || []).find(s => s.slipId === slipId);
  if (!slip) {
    window.showToast?.('Slip not found', 'error');
    return;
  }
  const ticket = ReviewState.ticket;
  const customer = BillingState.customers?.find(c => c.id === ticket?.customerId) || { name: ticket?.customerName };
  const tenantData = window.AppState?.tenantData || {};
  const company = {
    name: tenantData.name || '',
    address: tenantData.address || '',
    phone: tenantData.phone || '',
    email: tenantData.email || ''
  };
  const totals = slipGetRunningTotals(ReviewState.shipSlips);
  slipPrint({
    slip,
    ticket,
    customer,
    company,
    runningTotal: totals.bblsTotal,
    includeRunningTotal: true,
    template: window.AppState?.settings?.slipTemplate || null,
  });
}

async function voidShipSlip(slipId) {
  const slip = (ReviewState.shipSlips || []).find(s => s.slipId === slipId);
  if (!slip) return;
  const confirmed = window.confirm(`Void slip ${slip.slipNumber}? It will stay in history with a VOID watermark.`);
  if (!confirmed) return;
  try {
    await slipVoid(BillingState.tenantId, ReviewState.ticketId, slipId);
    const updatedList = await slipGetForTicket(BillingState.tenantId, ReviewState.ticketId);
    ReviewState.shipSlips = updatedList;
    renderReviewTicket();
    window.showToast?.(`Slip ${slip.slipNumber} voided`, 'success');
  } catch (err) {
    console.error('[Billing] voidShipSlip failed:', err);
    window.showToast?.('Error voiding slip', 'error');
  }
}

function parseFloatOrNull(v) {
  if (v == null || v === '') return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

// TASK-1165 S2 — "Unpriced equipment" detection: the same asset carrying BOTH a
// priced line (rate > 0) AND a $0 line on one ticket is the visible fingerprint
// of the jobboard-vs-clock.js dual-writer bug (#100240: the TASK-442 dedup
// buckets by assetId|rate, so a $0 and a $285 line for the SAME truck never
// merge → silent under-billing). The server now prices those lines at creation
// (equipmentLineEnrichment); this badge catches the residue — historical
// tickets, price-book misses — and hands the biller a one-click,
// human-confirmed fix. NEVER auto-adopts a rate silently (COD: auto-adopt was
// rejected as an over-billing exploit — the human confirms, then Saves).
// Returns Map<assetId, { priced: line[], zeros: line[] }> for flagged assets.
function _mixedRateEquipGroups() {
  const groups = new Map();
  for (const l of ReviewState.equipmentLines) {
    if (!l.assetId) continue;
    if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) continue; // per-unit merge guard stays as-is
    const g = groups.get(l.assetId) || { priced: [], zeros: [] };
    if ((Number(l.rate) || 0) > 0) g.priced.push(l);
    else if ((Number(l.hours) || 0) > 0
      || l._billingReviewFlag?.reason === 'unpriced_equipment_same_asset') g.zeros.push(l);
    groups.set(l.assetId, g);
  }
  // TASK-1165 (2nd-COD Adversary) -- lines whose HOURS exceed their clock
  // entry's window are server-flagged (reconcile refuses to bake them into the
  // total); surface them in the same review banner so a biller corrects hours.
  const hoursFlagged = ReviewState.equipmentLines.filter(l => l._billingReviewFlag?.reason === 'hours_exceed_clock_entry');
  // TASK-1399 — equipment window outside every clock entry on the ticket. The driver
  // is no longer blocked from clocking out over this (that block trapped Ethan Young
  // on the clock overnight), so the times land as typed and THIS badge is how a biller
  // learns to reconcile them. Server-stamped only (equipmentLineEnrichment.js).
  const windowFlagged = ReviewState.equipmentLines.filter(l => l._billingReviewFlag?.reason === 'equipment_outside_clock_window');
  // TASK-1635 — the operator TIED this asset at clock-in but dispatch never
  // listed it on that day's jobDay. The fill now bills it (instead of silently
  // dropping it — the Steve hand-add fiasco); this badge is the human check
  // that it belongs on the ticket. Stamped at line CREATE by the clock-out fills.
  const dayFlagged = ReviewState.equipmentLines.filter(l => l._billingReviewFlag?.reason === 'equipment_not_on_jobday');
  const flagged = new Map();
  for (const l of hoursFlagged) {
    const g = flagged.get(l.assetId) || { priced: [], zeros: [], hoursFlags: [], windowFlags: [], dayFlags: [] };
    (g.hoursFlags = g.hoursFlags || []).push(l);
    flagged.set(l.assetId, g);
  }
  for (const l of windowFlagged) {
    const g = flagged.get(l.assetId) || { priced: [], zeros: [], hoursFlags: [], windowFlags: [], dayFlags: [] };
    (g.windowFlags = g.windowFlags || []).push(l);
    flagged.set(l.assetId, g);
  }
  for (const l of dayFlagged) {
    const g = flagged.get(l.assetId) || { priced: [], zeros: [], hoursFlags: [], windowFlags: [], dayFlags: [] };
    (g.dayFlags = g.dayFlags || []).push(l);
    flagged.set(l.assetId, g);
  }
  for (const [assetId, g] of groups) {
    if (g.priced.length > 0 && g.zeros.length > 0) {
      // TASK-1291 — carry any hours-flags for this asset into the priced/$0 group
      // instead of clobbering them: the same asset can be BOTH an unpriced-mix and
      // hours-exceed-clock. The banner loop renders both rows off the one merged group.
      const prior = flagged.get(assetId);
      if (prior?.hoursFlags?.length) g.hoursFlags = prior.hoursFlags;
      if (prior?.windowFlags?.length) g.windowFlags = prior.windowFlags;
      if (prior?.dayFlags?.length) g.dayFlags = prior.dayFlags;
      flagged.set(assetId, g);
    }
  }
  return flagged;
}

// One-click resolve for a flagged asset (anchored by the priced line's
// _localId — numeric, injection-safe in the onclick). Both modes only mutate
// ReviewState + mark dirty; the money change persists when the biller SAVES.
//   mode 'rate'  — price the $0 line(s) at the priced sibling's rate.
//   mode 'merge' — interval-UNION the $0 line(s)' windows into the priced line
//                  (TASK-442 semantics: overlap counted once, gaps unbilled)
//                  and remove them. Guards: same time-based rateUnit + same
//                  includeOnInvoice treatment, both windows present.
// TASK-1674 S2 — which EQUIPMENT lines on this ticket were priced by a GUESS.
//
// DERIVED at render time, not read off a stored flag, and that is the whole
// point. A guess leaves no trace: the server's enrichment pass writes the
// number with no marker, and every line written before this task predates the
// stamp. Asking the price book again, right now, is the only way to catch the
// 46 live lines already on TITS. Lines written by the clock-out fills DO carry
// `_billingReviewFlag.reason === 'equipment_rate_guessed'` (clock.js) — the two
// paths agree, this one just also covers everything else.
//
// A line drops off this list the moment a human fixes it: correcting the rate
// makes it stop equalling what the matcher would have guessed, and the "Rate is
// correct" button stamps `rateGuessAcknowledged`. Nothing has to be cleared by
// hand, so the badge cannot go stale (COD Code Reviewer).
//
// Returns [{ line, match }] — never blocks Ready or invoice. A hard refusal
// would drop the legacy-shaped tenants (`mmwwgj1lkr941cytz`,
// `moc4nl9n1u7vs2sc4`) to $0 on every truck and trap a biller mid-close.
function _equipRateGuessLines() {
  const out = [];
  const cid = ReviewState.ticket?.customerId;
  if (!cid || !ReviewState.contractRateCache?.[cid]) return out;
  // Closed books stay closed. 166 live TITS lines match this shape across all
  // history; badging the invoiced ones would be noise a biller cannot act on,
  // and repricing history is explicitly out of scope (Chris's call).
  if (isReviewReadOnly(ReviewState.ticket || {})) return out;
  for (const l of (ReviewState.equipmentLines || [])) {
    if (!l.assetId) continue;
    if (l.lineItemId) continue;                       // a human picked this line by hand
    // A human already confirmed the number. TASK-1674 (COD Adversary 🔴):
    // `rateGuessAcknowledged` is a SUPPRESSION switch — it stops the biller ever
    // being asked about this line again — so it is pinned biller-only in
    // firestore.rules on all four member/driver arms, exactly like zeroHoursOk.
    // Unpinned, the clock-only member or driver-PIN session that created the
    // guessed line could raw-SDK self-stamp it and silence the review of their
    // own money line. Trusting this field here is only safe BECAUSE of that pin.
    if (l.rateGuessAcknowledged) continue;
    if ((Number(l.rate) || 0) <= 0) continue;         // $0 is the existing unpriced badge's job
    let m = null;
    try {
      m = lookupReviewRate(cid, 'EQUIPMENT', l.assetType || 'truck', l.assetName || '', l.vehicleType || '');
    } catch (_) { continue; }
    if (!m || m.rateGuessed !== true) continue;
    // Rate no longer matches what the guess would produce → a biller corrected it.
    if (Math.abs((Number(m.rate) || 0) - (Number(l.rate) || 0)) > 0.005) continue;
    out.push({ line: l, match: m });
  }
  return out;
}

// TASK-1674 — "Rate is correct": the biller confirms the guessed number rather
// than changing it. Recorded on the line (not underscore-prefixed, so it
// persists) so the badge never nags again, and so a later reader can tell a
// confirmed rate from an unexamined one. Persists on SAVE like every other
// review edit — this only mutates ReviewState.
function acknowledgeGuessedRate(localId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const line = (ReviewState.equipmentLines || []).find(l => l._localId === localId);
  if (!line) return;
  line.rateGuessAcknowledged = true;
  line.rateResolvedBy = 'human:confirmed-fallback';
  ReviewState.dirty = true;
  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  renderReviewTotals();
}

function resolveUnpricedEquipment(anchorLocalId, mode) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const anchor = ReviewState.equipmentLines.find(l => l._localId === anchorLocalId);
  if (!anchor) return;
  const g = _mixedRateEquipGroups().get(anchor.assetId);
  if (!g || !g.priced.includes(anchor)) return;

  if (mode === 'rate') {
    for (const z of g.zeros) {
      z.rate = anchor.rate;
      z.rateUnit = anchor.rateUnit || z.rateUnit || 'Hourly';
      z.lineAmount = parseFloat(((z.hours || 0) * z.rate).toFixed(2));
      if (z._billingReviewFlag?.reason === 'unpriced_equipment_same_asset') z._flagClearPending = true;
    }
    ReviewState.dirty = true;
    renderReviewTicket();
    window.showToast?.(`Applied $${anchor.rate}/${anchor.rateUnit || 'Hourly'} to ${g.zeros.length} unpriced line(s) — review, then Save`, 'success');
    return;
  }

  if (mode === 'merge') {
    const _invTreat = (l) => l.includeOnInvoice !== false;
    const mergeable = g.zeros.filter(z =>
      (z.rateUnit || 'Hourly') === (anchor.rateUnit || 'Hourly')
      && _invTreat(z) === _invTreat(anchor)
      && z.fromTime && z.toTime && anchor.fromTime && anchor.toTime);
    if (mergeable.length === 0) {
      window.showToast?.('These lines can\'t be merged automatically (different unit, invoice treatment, or missing times) — set the rate instead', 'error');
      return;
    }
    const iv = [];
    const pushIv = (l) => {
      const f = l.fromTime?.toDate ? l.fromTime.toDate() : new Date(l.fromTime);
      const t = l.toTime?.toDate ? l.toTime.toDate() : new Date(l.toTime);
      if (!isNaN(f.getTime()) && !isNaN(t.getTime()) && t > f) iv.push({ from: +f, to: +t });
    };
    pushIv(anchor);
    mergeable.forEach(pushIv);
    iv.sort((a, b) => a.from - b.from);
    let unionMs = 0; let earliest = null; let latest = null; let curF = null; let curT = null;
    for (const x of iv) {
      earliest = earliest == null ? x.from : Math.min(earliest, x.from);
      latest = latest == null ? x.to : Math.max(latest, x.to);
      if (curF == null) { curF = x.from; curT = x.to; continue; }
      if (x.from <= curT) { if (x.to > curT) curT = x.to; }
      else { unionMs += curT - curF; curF = x.from; curT = x.to; }
    }
    if (curF != null) unionMs += curT - curF;
    let hrs = unionMs / 3600000;
    hrs = shouldRoundLine(anchor) ? roundHalfHour(hrs) : parseFloat(hrs.toFixed(2));
    if (earliest != null) anchor.fromTime = Timestamp.fromDate(new Date(earliest));
    if (latest != null) anchor.toTime = Timestamp.fromDate(new Date(latest));
    anchor.hours = hrs;
    anchor.lineAmount = parseFloat((hrs * (anchor.rate || 0)).toFixed(2));
    for (const z of mergeable) {
      if (z.id && !z._isNew) {
        ReviewState.deletedLineIds.push(z.id);
        // TASK-9755 (COD Blast) — the second deletedLineIds pusher; its
        // deletions get the same ticket_lines_removed row as removeReviewLine's.
        ReviewState.removedLinesAudit.push({
          lineId: z.id, type: 'EQUIPMENT', label: getEquipmentDisplayName(z) || z.assetName || '',
          personId: null, clockEntryId: z.clockEntryId || null,
          hours: Number(z.hours) || 0, lineAmount: Number(z.lineAmount) || 0, mergedInto: anchor.id || null,
        });
      }
      const idx = ReviewState.equipmentLines.findIndex(l => l._localId === z._localId);
      if (idx !== -1) ReviewState.equipmentLines.splice(idx, 1);
    }
    ReviewState.dirty = true;
    renderReviewTicket();
    window.showToast?.(`Merged ${mergeable.length} unpriced line(s) into ${getEquipmentDisplayName(anchor) || anchor.assetName || 'the priced line'} (${hrs} hrs) — review, then Save`, 'success');
  }
}

// TASK-1548 — per-row badge for the priced-line-no-hours state, shared by the
// desktop table and the phone cards. Two faces:
//   • HOURS NEEDED (amber) — the line matches equipmentLineNeedsHours(): it will
//     bill $0 for real work and blocks Ready/invoice. Never renders as complete.
//   • $0 OK (grey) — the biller marked it deliberately non-billable; the title
//     carries who/when/why, and clicking it un-marks (hours required again).
function _zeroHourBadge(l) {
  if (l._isNew) return '';
  if (equipmentLineNeedsHours(l)) {
    // window.escAttr, not escapeHtml — rateUnit is biller-entered free text landing
    // in an ATTRIBUTE, and escapeHtml does not escape quotes (lesson: TASK-917 family).
    return ` <span title="${window.escAttr(`Priced at $${Number(l.rate) || 0}/${l.rateUnit || 'Hourly'} with no hours — bills $0 as-is. Enter hours or mark non-billable (banner above); blocks Ready/invoice until then.`)}" style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:#fef3c7;color:#92400e;border:1px solid #f59e0b;font-size:0.68rem;font-weight:700;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">hourglass_empty</span>HOURS NEEDED</span>`;
  }
  // TASK-1405 — the mirror face. NO RATE (amber): the hours are real, the rate is
  // $0, so the machine's whole day bills nothing. Same block, same one-click
  // override as above (the mark is the SAME zeroHoursOk field, biller-pinned in
  // firestore.rules — a second, unpinned flag would be a new hole).
  if (equipmentLineUnpriced(l)) {
    return ` <span title="${window.escAttr(`Ran ${Number(l.hours) || 0} hrs at $0/${l.rateUnit || 'Hourly'} — bills $0 as-is. Enter the rate or mark non-billable (banner above); blocks Ready/invoice until then.`)}" style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:#fef3c7;color:#92400e;border:1px solid #f59e0b;font-size:0.68rem;font-weight:700;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">money_off</span>NO RATE</span>`;
  }
  // The "$0 OK" face only shows while the mark is doing something (line would
  // otherwise be flagged) — once the missing half is filled in the mark is inert
  // and the badge goes. BOTH flagged shapes qualify: priced-with-no-hours
  // (TASK-1548) and hours-with-no-rate (TASK-1405).
  const _wouldFlag = l.type === 'EQUIPMENT'
    && isTimeBasedUnit(l.rateUnit || 'Hourly')
    && !((Number(l.lineAmount) || 0) > 0)
    && l.includeOnInvoice !== false
    && (
      ((Number(l.rate) || 0) > 0 && !((Number(l.hours) || 0) > 0))     // TASK-1548 shape
      || (!((Number(l.rate) || 0) > 0) && (Number(l.hours) || 0) > 0)  // TASK-1405 shape
    );
  if (l.zeroHoursOk === true && _wouldFlag) {
    const _by = l.zeroHoursMarkedByName ? ` by ${l.zeroHoursMarkedByName}` : '';
    const _why = l.zeroHoursReason ? ` — ${l.zeroHoursReason}` : '';
    // TASK-1405 — name the half that's actually missing: one mark releases two
    // shapes, and "hours will be required again" is wrong on the unpriced one.
    const _missing = (Number(l.rate) || 0) > 0 ? 'hours' : 'a rate';
    // window.escAttr — the reason is biller-entered free text in an ATTRIBUTE.
    return ` <span title="${window.escAttr(`Marked deliberately non-billable${_by}${_why}. Click to un-mark (${_missing} will be required again).`)}" onclick="window.BillingModule.markEquipmentZeroHours(${l._localId})" style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:var(--bg-secondary, #f3f4f6);color:var(--text-secondary, #6b7280);border:1px solid var(--border-color, #d1d5db);font-size:0.68rem;font-weight:700;vertical-align:middle;cursor:pointer;"><span class="material-icons" style="font-size:12px;">money_off</span>$0 OK</span>`;
  }
  return '';
}

// TASK-4564 — the rounding marker. INTERNAL SURFACES ONLY.
//
// Chris's question was the whole ticket: *"are you suggesting we put like a
// little 'rounding on' tag in ticket view?"* — because a biller looking at a line
// that ends 3:18 PM and bills 7.5 hrs has nothing on screen explaining the gap,
// and an unexplained number is exactly what makes someone pause billing over a
// suspected bug. This is that tag.
//
// 🔴 NOT on the customer invoice and NOT in the field-ticket email. Telling a
// customer "rounded up from 7.13" invites a dispute over 22 minutes on a line
// they have been paying for years. Both call sites below are the Review screen
// (renderReviewEquipmentSection / _renderReviewEquipmentCards); the invoice and
// email renderers are separate functions and stay untouched.
//
// Two faces: a DELTA ("rounded up from 7.13") when the raw hours actually moved,
// and "rounding on" when the window happened to land on an exact half hour — the
// second one matters because it tells the biller the rule is armed on this line
// even on the day it changed nothing.
function _roundingMarker(l) {
  if (!l || l._isNew) return '';
  if (l.type !== 'EQUIPMENT') return '';
  if (!isTimeBasedUnit(l.rateUnit || 'Hourly')) return '';
  // Ask the SAME pure decision the recalc asks — the marker can never claim
  // rounding the math is not applying (or hide rounding the math IS applying).
  if (!decideLineRounding(l, ReviewState.contractLineItems).round) return '';
  const rf = Number(l.roundedFrom);
  const h = Number(l.hours);
  const hasDelta = Number.isFinite(rf) && rf > 0 && Number.isFinite(h) && (h - rf) > 0.005;
  const label = hasDelta ? `rounded up from ${rf.toFixed(2)}` : 'rounding on';
  const title = hasDelta
    ? `Billed ${h.toFixed(2)} hrs — the clock window was ${rf.toFixed(2)} hrs and this line's price-book item rounds UP to the next half hour. Internal only; the customer invoice shows ${h.toFixed(2)} hrs.`
    : `This line's price-book item rounds UP to the next half hour. The clock window landed on an exact half hour, so nothing moved. Internal only.`;
  // window.escAttr, not escapeHtml — escapeHtml leaves quotes alone and this
  // lands in an ATTRIBUTE (TASK-917 family).
  return ` <span title="${window.escAttr(title)}" style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:#eff6ff;color:#1e40af;border:1px solid #93c5fd;font-size:0.68rem;font-weight:600;white-space:nowrap;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">expand_less</span>${escapeHtml(label)}</span>`;
}

// TASK-1548 — the "mark non-billable" affordance. Toggles `zeroHoursOk` on the
// line IN MEMORY (persists on Save, like every other review edit). Marking asks
// for an optional reason and stamps who/when so a deliberate $0 (support unit,
// hours consolidated onto another ticket — the Dove #03 pattern) is forever
// distinguishable in data from "nobody typed the hours". Un-marking is one click.
async function markEquipmentZeroHours(localId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;

  const _rerenderEquip = () => {
    const section = document.getElementById('review-equip-section');
    if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
    renderReviewTotals();
  };

  if (line.zeroHoursOk === true) {
    // Un-mark. `false`, not null — the line-save loop drops null values, and the
    // stored `true` must be overwritten, not left behind.
    line.zeroHoursOk = false;
    ReviewState.dirty = true;
    _rerenderEquip();
    // TASK-1405 — the same mark releases two shapes; say which half is missing.
    const _missing = (Number(line.rate) || 0) > 0 ? 'hours' : 'a rate';
    window.showToast?.(`Un-marked — this line needs ${_missing} (or a new mark) before Ready/invoice. Save to keep.`, 'info');
    return;
  }

  const name = getEquipmentDisplayName(line) || line.assetName || 'this line';
  const reason = await window.showInputModal?.(
    'Mark non-billable',
    `${name} will stay on the ticket at $0 on purpose and stop blocking Ready/invoice. Why is it $0? (e.g. "support unit", "hours billed on #100460") — optional`,
    '',
    { okText: 'Mark $0 non-billable' }
  );
  if (reason === null || reason === undefined) return; // cancelled
  line.zeroHoursOk = true;
  const _r = String(reason).trim();
  if (_r) line.zeroHoursReason = _r;
  line.zeroHoursMarkedBy = window.AppState?.user?.uid || 'unknown';
  const _byName = _resolveReadyByName();
  if (_byName) line.zeroHoursMarkedByName = _byName;
  line.zeroHoursMarkedAt = Timestamp.now();
  ReviewState.dirty = true;
  _rerenderEquip();
  window.showToast?.('Marked non-billable — Save the ticket to keep it', 'success');
}

// TASK-1615-B — the biller's "I checked this number" mark. Records who/when/why
// ON THE LINE (in memory; persists on Save, like every other review edit) so a
// mileage that was accepted off a broken route stays forever distinguishable in
// data from one nobody looked at — the same reason zeroHoursOk carries a reason.
// There is deliberately NO auto-fix button: nothing in this app knows the real
// mileage, so the only honest options are "type the number" and "state that the
// number is right". Typing the number needs no button at all — a quantity that
// differs from routeMilesAutoQty is the biller's, and clears the flag by itself.
async function markRouteMileageOk(localId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const line = [...ReviewState.employeeLines, ...ReviewState.equipmentLines]
    .find(l => l._localId === localId);
  if (!line) return;
  const name = line.type === 'EMPLOYEE'
    ? getEmployeeDisplayName(line)
    : (getEquipmentDisplayName(line) || line.assetName || 'this line');
  const qty = Number(line.quantity) || 0;
  const reason = await window.showInputModal?.(
    'Confirm this mileage',
    `${name} will bill ${qty.toLocaleString()} miles at $${Number(line.rate) || 0}/${line.rateUnit || 'Per Mile'}, even though this job's route has a destination with a bad location. How do you know it's right? (e.g. "odometer 412 mi", "agreed flat run") — optional`,
    '',
    { okText: 'Confirm mileage' }
  );
  if (reason === null || reason === undefined) return; // cancelled
  line.routeMilesOk = true;
  const _r = String(reason).trim();
  if (_r) line.routeMilesReason = _r;
  line.routeMilesMarkedBy = window.AppState?.user?.uid || 'unknown';
  const _byName = _resolveReadyByName();
  if (_byName) line.routeMilesMarkedByName = _byName;
  line.routeMilesMarkedAt = Timestamp.now();
  ReviewState.dirty = true;
  renderReviewTicket();
  window.showToast?.('Mileage confirmed — Save the ticket to keep it', 'success');
}

// TASK-842-A — format a rental-slice bound (epoch ms) for span display.
// Operational bounds (deploy/return dates) come from Date.parse('YYYY-MM-DD') =
// UTC MIDNIGHT — those must format in UTC or US timezones show the previous day.
// An OPEN slice's sweep end is a real server instant (not midnight-aligned) —
// that one formats in local time. Year shown only when it isn't the current year.
function _fmtRentalSliceDate(ms) {
  const n = Number(ms);
  if (ms == null || !Number.isFinite(n)) return '';
  const utcMidnight = (n % 86400000) === 0;
  const d = new Date(n);
  const opts = { month: 'short', day: 'numeric' };
  if (utcMidnight) opts.timeZone = 'UTC';
  const y = utcMidnight ? d.getUTCFullYear() : d.getFullYear();
  if (y !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

// TASK-9258 — the template banner, built on its own so a quantity edit can
// refresh just this box (updateReviewEquipmentLine) without re-rendering the
// equipment table under the biller's cursor. Returns '' when there is nothing
// to say. Every interpolated value is escaped or coerced to a number.
function _renderTemplateBannerHtml(lines) {
  if (isReviewReadOnly(ReviewState.ticket || {}) || !ReviewState._tplDays) return '';
  let _tplBanner = '';
  const _tplRows = [];
  if (ReviewState._tplDays.loaded === true) {
    const _tplGroups = templateUnconfirmedEquipGroups(lines, ReviewState._tplDays.days,
      _reviewTemplateWorkDate(), _reviewSliceProvenance(lines), _reviewTemplateBirthJobId());
    for (const _tg of _tplGroups) {
      const _tl = _tg.lines[0];
      const _tName = escapeHtml(getEquipmentDisplayName(_tl) || _tl.assetName || 'Asset');
      const _tTpl = _tg.templateName ? `the <strong>${escapeHtml(_tg.templateName)}</strong> job template` : 'a job template';
      const _tQty = _tg.proposedQty != null ? ` (it set <strong>${Number(_tg.proposedQty) || 0}</strong>)` : '';
      const _tWhat = _tg.kind === 'unchecked'
        ? `is a rental, and its deploy record couldn't be loaded, so whether a job template set this count was <strong>not</strong> checked. Reopen this ticket before invoicing.`
        : _tg.rental
        ? `was sent out by ${_tTpl}${_tg.proposedQty != null ? ` as <strong>${Number(_tg.proposedQty) || 0}</strong> at a time` : ''} and nobody on dispatch confirmed that count. This line bills that count for every day it is out &mdash; check it against what actually went out before invoicing.`
        : _tg.kind === 'removed'
        ? `still bills the <strong>${Number(_tg.proposedQty) || 0}</strong> that ${_tTpl} filled in, but dispatch took it off that day after this ticket was made. Remove the line, or confirm it really went out.`
        : _tg.kind === 'stale'
        ? `still bills the <strong>${Number(_tg.proposedQty) || 0}</strong> that ${_tTpl} filled in, but dispatch changed that day to <strong>${Number(_tg.dayQty) || 0}</strong> after this ticket was made. Fix the quantity on this line, or confirm the ticket's number is the right one.`
        : `came from ${_tTpl}${_tQty} and nobody on dispatch confirmed the count. Check it against what actually went out before invoicing.`;
      // No "Quantity is right" on a row that could not be checked — there is
      // nothing yet to confirm.
      _tplRows.push(_tg.kind === 'unchecked' ? `
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
          <span style="font-weight:600;">${_tName}</span>
          <span>${_tWhat}</span>
        </div>` : `
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
          <span style="font-weight:600;">${_tName}</span>
          <span>${_tWhat}</span>
          <button style="min-height:36px;padding:6px 12px;font-size:0.78rem;font-weight:600;background:transparent;color:#1E293B;border:1px dashed #475569;border-radius:6px;cursor:pointer;"
            onclick="window.BillingModule.acknowledgeTemplateQty(${Number(_tl._localId) || 0})">Quantity is right</button>
        </div>`);
    }
  } else if (_ticketHasTemplateCandidateLines(lines)) {
    // "I could not check" is not "nothing to check" — an empty probe result
    // means the check did not run.
    _tplRows.push(`
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="font-weight:600;">Template check didn't run</span>
        <span>The job's days couldn't be loaded, so the assets on this ticket were <strong>not</strong> checked for quantities a job template filled in. Reopen this ticket before invoicing.</span>
      </div>`);
  }
  if (_tplRows.length > 0) {
    _tplBanner = `
      <div id="review-equip-template-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px dashed #475569;border-left:4px solid #475569;border-radius:8px;background:#E2E8F0;color:#1E293B;font-size:0.82rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
          <span class="material-icons" style="font-size:18px;">content_copy</span>
          Quantity from a job template &mdash; check it
        </div>
        <div style="margin-top:4px;">Nothing here holds the ticket. The number still bills; this is the one line a template would otherwise make look routine.</div>
        ${_tplRows.join('')}
      </div>`;
  }
  return _tplBanner;
}

// Re-render the template box after an edit — it can disappear (6→4) AND come
// back (4→6), because renderReviewEquipmentSection always emits the slot.
// A throw leaves whatever is on screen rather than blanking a money warning.
function _refreshTemplateBanner() {
  const slots = document.querySelectorAll('#review-equip-template-slot');
  if (!slots.length) return;
  let html = '';
  try { html = _renderTemplateBannerHtml(ReviewState.equipmentLines); }
  catch (err) { console.warn('[Billing] TASK-9258 template banner refresh failed:', err); return; }
  slots.forEach((el) => { el.innerHTML = html; });
}

function renderReviewEquipmentSection() {
  const lines = ReviewState.equipmentLines;
  const _revEqShowTimes = showClockTimes();
  // Equipment, Unit #, [In, Out], QTY, Rate, Unit, Amount, actions.
  // 🔴 This was 9/7 against an EIGHT-column header (no separate Unit column), so
  // the tfoot's `colspan="${colCount - 2}"` + amount + spacer made the footer row
  // NINE cells wide against an eight-cell thead. The browser invented a phantom
  // 9th column, which pushed "Equipment Total $X" one full column to the RIGHT of
  // the Amount values it sums — measured 814-889 vs the Amount column at 740-814
  // (Chris field screenshot 2026-09-22). Splitting the rate UNIT into its own
  // column makes 9/7 true rather than just changing the number. Count the <th>s
  // above before you touch this.
  const colCount = _revEqShowTimes ? 9 : 7;

  // TASK-842-A — still-out indicator (UX spec: NON-BLOCKING inline banner, never a
  // confirm dialog — mid-rental billing is standard; this is a heads-up only).
  // OPEN slices on this rental ticket = equipment still out in the field; the
  // billed-through date is the furthest swept end across the ticket's slices.
  const _rentalSlices = (ReviewState.ticket?.rentalTicket === true) ? (ReviewState.rentalSlices || []) : [];
  const _openSlices = _rentalSlices.filter(s => s && s.open === true);
  const _openByLineId = new Set(_openSlices.map(s => s.ticketLineId).filter(Boolean));
  let _rentalBanner = '';
  if (_openSlices.length > 0) {
    const _throughMs = Math.max(..._rentalSlices.map(s => Number(s.sliceEndMs) || 0));
    const _openAssetIds = [...new Set(_openSlices.map(s => s.assetId).filter(Boolean))];
    _rentalBanner = `
      <div id="review-equip-rental-active-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid #93c5fd;border-left:4px solid #3b82f6;border-radius:8px;background:#eff6ff;color:#1e40af;font-size:0.82rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
          <span class="material-icons" style="font-size:18px;">schedule</span>
          Rental still active — billing through ${_fmtRentalSliceDate(_throughMs)}
        </div>
        <div style="margin-top:4px;">${_openAssetIds.length} rental asset${_openAssetIds.length === 1 ? '' : 's'} still out. This ticket bills the span shown on each line; generate again later to sweep the remaining days, and the closing ticket bills through the return.</div>
      </div>`;
  }

  // TASK-1165 S2 — high-visibility "Unpriced equipment" banner + per-row badge
  // when the same asset has both a priced and a $0 line (see _mixedRateEquipGroups).
  const _mixedGroups = _mixedRateEquipGroups();
  let _mixedBanner = '';
  const _bannerRows = [];
  if (_mixedGroups.size > 0) {
    for (const [, g] of _mixedGroups) {
      // A group reaches _mixedGroups two ways: a priced/$0 MIX (has g.priced[0]),
      // OR a pure hours-exceed-clock-entry flag (g.hoursFlags only, priced:[]).
      // TASK-1291 — the pure-hours group has NO priced anchor; dereferencing
      // g.priced[0] here (getEquipmentDisplayName(undefined).assetId) is what threw
      // during renderReviewTicket and left the modal on an eternal "Loading ticket
      // details…" skeleton (watchdog already cleared → no recovery). Guard the anchor
      // and render each flag type off its own array.
      const anchor = g.priced[0];
      if (anchor) {
        const name = escapeHtml(getEquipmentDisplayName(anchor) || anchor.assetName || 'Equipment');
        const canMerge = g.zeros.some(z =>
          (z.rateUnit || 'Hourly') === (anchor.rateUnit || 'Hourly')
          && (z.includeOnInvoice !== false) === (anchor.includeOnInvoice !== false)
          && z.fromTime && z.toTime && anchor.fromTime && anchor.toTime);
        _bannerRows.push(`
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
            <span style="font-weight:600;">${name}</span>
            <span>has ${g.zeros.length} $0 line(s) alongside a $${anchor.rate}/${escapeHtml(anchor.rateUnit || 'Hourly')} line for the same unit.</span>
            <button style="padding:3px 10px;font-size:0.78rem;font-weight:600;background:#b45309;color:#fff;border:none;border-radius:6px;cursor:pointer;"
              onclick="window.BillingModule.resolveUnpricedEquipment(${anchor._localId}, 'rate')">Use $${anchor.rate} rate</button>
            ${canMerge ? `<button style="padding:3px 10px;font-size:0.78rem;font-weight:600;background:transparent;color:#b45309;border:1px solid #b45309;border-radius:6px;cursor:pointer;"
              onclick="window.BillingModule.resolveUnpricedEquipment(${anchor._localId}, 'merge')">Merge into priced line</button>` : ''}
          </div>`);
      }
      // Hours-exceed-clock-entry flags (TASK-1165 2nd-COD). No one-click fix —
      // informational so the biller corrects the hours before invoicing.
      const _hf = g.hoursFlags || [];
      if (_hf.length > 0) {
        const _hfLine = _hf[0];
        const _hfName = escapeHtml(getEquipmentDisplayName(_hfLine) || _hfLine.assetName || 'Equipment');
        _bannerRows.push(`
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
            <span style="font-weight:600;">${_hfName}</span>
            <span>${_hf.length} line(s) show more hours than the clock entry — review the hours before invoicing.</span>
          </div>`);
      }
      // TASK-1399 — equipment-window-outside-any-clock-entry flags. Also no one-click
      // fix: the driver's typed times are the field truth, and only a human knows
      // whether to widen the clock entry, trim the machine window, or bill it as-is.
      const _wf = g.windowFlags || [];
      if (_wf.length > 0) {
        const _wfLine = _wf[0];
        const _wfName = escapeHtml(getEquipmentDisplayName(_wfLine) || _wfLine.assetName || 'Equipment');
        const _outside = Number(_wfLine._billingReviewFlag?.outsideHours) || 0;
        _bannerRows.push(`
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
            <span style="font-weight:600;">${_wfName}</span>
            <span>${_wf.length} line(s) run ${_outside > 0 ? `about ${_outside.toFixed(2)}h ` : ''}outside every clock entry on this ticket — the operator entered real machine time he couldn't fit inside his shift. Confirm the hours (and whether a clock entry needs correcting) before invoicing.</span>
          </div>`);
      }
      // TASK-1635 — tied at clock-in but not on that day's dispatch list. The
      // line bills (the operator was standing next to the rig — the plan is the
      // stale side more often than the driver); this is the human confirmation
      // the old silent drop never offered. No one-click fix: keep it, or delete
      // the line if it truly was a stray pick.
      const _df = g.dayFlags || [];
      if (_df.length > 0) {
        const _dfLine = _df[0];
        const _dfName = escapeHtml(getEquipmentDisplayName(_dfLine) || _dfLine.assetName || 'Equipment');
        _bannerRows.push(`
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
            <span style="font-weight:600;">${_dfName}</span>
            <span>was tied by the operator at clock-in but wasn't on that day's job — confirm it belongs on this ticket (and add it to the job day) before invoicing.</span>
          </div>`);
      }
    }
  }

  // TASK-1674 S2 — "Rate check" rows. The price book has no rule covering this
  // vehicle, so the number on the line came from a description regex, a seeded
  // item-code default, or a tie between several equally-matching lines. It
  // still bills; this is the biller's chance to catch it before the customer
  // does. Plain English on purpose — a biller does not know what a
  // "vehicleType" is, and "priced at the Winch Truck rate" is the sentence that
  // makes the mistake obvious. Drivers never see this: clock-out has no banner.
  //
  // 🔴 EVERY interpolated value here is escaped. `matchedDescription` is a
  // contract line's free-text description, editable by any CRM user with
  // contract rights — raw interpolation is stored XSS against this tenant's own
  // billers (COD Adversary). escapeHtml for text nodes; nothing lands in an
  // attribute (escapeHtml does not escape quotes — [[lesson_escapehtml_is_not_attribute_safe]]).
  for (const { line: _gl, match: _gm } of _equipRateGuessLines()) {
    const _gName = escapeHtml(getEquipmentDisplayName(_gl) || _gl.assetName || 'Equipment');
    const _gRate = Number(_gm.rate) || 0;
    const _gUnit = escapeHtml(_gm.rateUnit || 'Hourly');
    const _gWhat = escapeHtml(_gl.vehicleType || _gl.assetType || 'vehicle');
    const _gVia = String(_gm.matchedVia || '');
    let _gWhy;
    if (_gVia.indexOf('ambiguous') >= 0) {
      _gWhy = `this contract has ${Number(_gm.candidateCount) || 2} different lines that all cover a `
        + `<strong>${_gWhat}</strong>, so there is no single right answer — pick the line on the job, `
        + `or set the rate here.`;
    } else if (_gVia === 'item-code') {
      _gWhy = `this contract has no line for a <strong>${_gWhat}</strong>, so it fell back to the `
        + `default item code <strong>${escapeHtml(_gm.matchedDescription || '')}</strong>.`;
    } else {
      _gWhy = `this contract has no line for a <strong>${_gWhat}</strong> — that is just the first `
        + `truck line on the contract.`;
    }
    _bannerRows.push(`
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="font-weight:600;">${_gName}</span>
        <span>is priced at the <strong>${escapeHtml(_gm.matchedDescription || 'default')}</strong> rate
          ($${_gRate}/${_gUnit}) — ${_gWhy} Check it before invoicing.</span>
        <button style="padding:3px 10px;font-size:0.78rem;font-weight:600;background:transparent;color:#b45309;border:1px solid #b45309;border-radius:6px;cursor:pointer;"
          onclick="window.BillingModule.acknowledgeGuessedRate(${Number(_gl._localId) || 0})">Rate is correct</button>
      </div>`);
  }

  // TASK-1674 S4 — the dispatch reference never resolved to a roster vehicle, so
  // the line has no vehicleType and CANNOT match the price book by type. Its
  // rate is a guess by construction; say which reference failed so someone can
  // fix the jobDay instead of re-guessing every week.
  //
  // TASK-4741 — this arm used to read ONLY `_billingReviewFlag.reason`, a stamp
  // written once at ticket-generation time (:29859). Three ways that goes wrong,
  // all of them silent: a line born before that stamp shipped carries nothing; a
  // line whose vehicle was renamed or deleted AFTER the ticket was cut still
  // reads clean; and a stamp that has since been fixed keeps shouting forever.
  // So the flag is now DERIVED LIVE off the current roster, with the stamp kept
  // only as the fallback for when the roster could not be read. Live-derived
  // rows win; a stamped row that resolves fine today is dropped on purpose.
  //
  // It is recomputed on EVERY render off the CURRENT lines (COD Code Reviewer),
  // never read from a snapshot taken when the modal opened: the biller fixes an
  // unresolvable ref by picking the right unit on the line right here, and a
  // cached verdict would leave the warning on screen after the thing it warns
  // about is gone — which is how a banner teaches people to ignore it. Only the
  // roster envelope is cached (one read per modal open) and auditEquipmentRefs
  // is pure, so recomputing costs nothing.
  const _refRoster = ReviewState._refRoster || null;
  const _refAudit = _refRoster
    ? auditEquipmentRefs(_equipmentRefsFromLines(lines, ''), _refRoster.roster, _refRoster.loaded)
    : null;
  if (_refAudit && _refAudit.status === 'ok') {
    for (const _u of _refAudit.unresolved) {
      const _uName = escapeHtml(_u.ref || 'Equipment');
      _bannerRows.push(`
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
          <span style="font-weight:600;">${_uName}</span>
          <span>isn't matched to a vehicle in your fleet, so its rate couldn't be looked up from the
            price book. Pick the right unit on this line (and fix it on the job day) before invoicing.</span>
        </div>`);
    }
  } else {
    // 🔴 The roster could not be read. "I could not check this" is NOT "this is
    // fine" — say so plainly rather than rendering a clean ticket, and fall back
    // to whatever birth-time stamps exist so we degrade to the old behaviour
    // instead of to silence.
    for (const _ul of lines) {
      if (_ul._billingReviewFlag?.reason !== 'equipment_ref_unresolved') continue;
      const _uName = escapeHtml(_ul._billingReviewFlag?.ref || _ul.assetName || 'Equipment');
      _bannerRows.push(`
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
          <span style="font-weight:600;">${_uName}</span>
          <span>isn't matched to a vehicle in your fleet, so its rate couldn't be looked up from the
            price book. Pick the right unit on this line (and fix it on the job day) before invoicing.</span>
        </div>`);
    }
    if (_refAudit) {
      _bannerRows.push(`
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
          <span style="font-weight:600;">Vehicle check didn't run</span>
          <span>Your fleet list couldn't be loaded, so the equipment on this ticket was
            <strong>not</strong> checked against it. Reopen this ticket before invoicing.</span>
        </div>`);
    }
  }

  // TASK-1548 — the "priced line, no hours" arm of the same banner. This is the
  // job_generate birth defect made VISIBLE: the line was created with hours:0, no
  // clockEntryId, and nothing ever fills it, yet it rendered exactly like a
  // deliberate $0. Each row carries the "Mark non-billable" affordance so a real
  // support-unit / consolidated-onto-another-ticket $0 (the Dove #03 pattern) is a
  // one-click, RECORDED decision (`zeroHoursOk` + who/when/why) instead of an
  // indistinguishable blank. Hours are never auto-derived — the human types them.
  for (const l of lines) {
    if (!equipmentLineNeedsHours(l) || l._isNew) continue;
    const _zhName = escapeHtml(getEquipmentDisplayName(l) || l.assetName || 'Equipment');
    _bannerRows.push(`
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="font-weight:600;">${_zhName}</span>
        <span>is priced at $${Number(l.rate) || 0}/${escapeHtml(l.rateUnit || 'Hourly')} with <strong>no hours</strong> — it bills $0 as-is. Enter the hours, or mark it non-billable. This ticket can't be marked Ready or invoiced until one of those happens.</span>
        <button style="padding:3px 10px;font-size:0.78rem;font-weight:600;background:transparent;color:#b45309;border:1px solid #b45309;border-radius:6px;cursor:pointer;"
          onclick="window.BillingModule.markEquipmentZeroHours(${l._localId})">Mark non-billable</button>
      </div>`);
  }

  // TASK-1405 — the mirror arm: the machine RAN (real hours) and carries no rate.
  // The existing TASK-1165 "UNPRICED" badge only fires when the SAME asset also
  // has a priced line on the ticket, so a lone unpriced asset showed nothing at
  // all while the ticket read complete. This row is where the biller's block
  // LANDS: type the rate on the line below, or record the $0 as deliberate.
  for (const l of lines) {
    if (!equipmentLineUnpriced(l) || l._isNew) continue;
    const _upName = escapeHtml(getEquipmentDisplayName(l) || l.assetName || 'Equipment');
    _bannerRows.push(`
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="font-weight:600;">${_upName}</span>
        <span>ran <strong>${Number(l.hours) || 0} hrs</strong> with <strong>no rate</strong> — it bills $0 as-is. Enter the rate, or mark it non-billable. This ticket can't be marked Ready or invoiced until one of those happens.</span>
        <button style="padding:3px 10px;font-size:0.78rem;font-weight:600;background:transparent;color:#b45309;border:1px solid #b45309;border-radius:6px;cursor:pointer;"
          onclick="window.BillingModule.markEquipmentZeroHours(${l._localId})">Mark non-billable</button>
      </div>`);
  }

  // TASK-9275 — the asset on this line is sitting BELOW ZERO in the roster.
  //
  // The deduct in saveReviewTicket (~:28140) already detects this and toasts, then
  // writes anyway — and that is the right call: a crew really can consume stock
  // nobody booked in, and refusing the write would strand the biller mid-ticket the
  // way TASK-428/489 stranded drivers mid-clock-out. This does NOT change that. It
  // fixes the other half: the toast is gone the moment it fades, seen only by
  // whoever was on that screen, so `trippin-trucking` walked one item -1 → -3 → -11
  // → -17 with no screen ever saying so again. This row is the durable half of the
  // same signal — it comes back on every open of every ticket that bills the item,
  // and it disappears on its own the moment someone receives the stock.
  //
  // NOT a block on Ready/invoice, for the same reason the write is not blocked: the
  // hours and the rate on the line are still correct, and the money is still owed.
  // The count is what is wrong, and the count is fixed in Roster, not here.
  //
  // 🔴 AND IT GETS ITS OWN BOX, not a row in the amber "Equipment — review needed"
  // banner. COD's independent reviewer caught this and was right: every OTHER row
  // in that banner is a thing that stops the ticket being marked Ready, and the
  // banner says so. Dropping a non-blocking row in beside them teaches a biller
  // either to hold a correct invoice or — worse — to start skimming a box that
  // also carries the real blocks. Same reasoning as TASK-4658's separate
  // check-failed banner: a different KIND of statement gets a different box.
  // Red, matching the roster banner and the picker pill, so one colour means one
  // thing (the count is below zero) everywhere in the product.
  const _stockRows = [];
  for (const { line: _nl, asset: _na } of reviewNegativeStockLines(lines)) {
    const _nName = escapeHtml(getEquipmentDisplayName(_nl) || _nl.assetName || 'Asset');
    const _nQty = Number(_na.quantity || 0);
    _stockRows.push(`
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:6px;">
        <span style="font-weight:600;">${_nName}</span>
        <span>is at <strong>${_nQty}</strong> on hand &mdash; more has been consumed than was ever booked in.
          Receive the missing stock (or adjust the count) under Roster &rsaquo; Assets.</span>
      </div>`);
  }
  // TASK-9258 — "a template put this quantity here and nobody checked it."
  // Its OWN box, not a row in the amber "review needed" banner, for the reason
  // TASK-9275 spelled out right above: that banner is where Ready-blockers live,
  // and this is not one. Slate + DASHED border, the exact mark dispatch puts on
  // the same row before Save (dispatch.js _JT_MARK_*), so one look means one
  // thing on both sides of the handoff. Literal hex, never var(): an undefined
  // custom property silently kills the whole declaration, and this box existing
  // is the entire mitigation. Skipped on closed books (same as rate-guess) —
  // an invoiced ticket is not a place a biller can act.
  // Always emit the slot (empty when there is nothing to say) so an edit can
  // put the box back — COD round 4: 6→4 removed it, 4→6 could not restore it.
  const _tplBanner = `<div id="review-equip-template-slot">${_renderTemplateBannerHtml(lines)}</div>`;

  let _stockBanner = '';

  if (_stockRows.length > 0) {
    _stockBanner = `
      <div id="review-equip-negative-stock-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid #FECACA;border-left:4px solid #DC2626;border-radius:8px;background:#FEF2F2;color:#991B1B;font-size:0.82rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
          <span class="material-icons" style="font-size:18px;">inventory_2</span>
          Inventory count is off &mdash; this ticket still bills
        </div>
        <div style="margin-top:4px;">Nothing here holds the ticket. The hours and the rate below are unaffected; only the yard count is wrong.</div>
        ${_stockRows.join('')}
      </div>`;
  }

  // TASK-4658 — the server's automated billing pass DIED on this ticket. Until
  // now that failure was invisible: onTicketLineWrite swallowed the throw into a
  // log line, the review flag it was about to stamp never landed, and this modal
  // rendered the ticket as CLEAN. A biller cannot be asked to trust a check that
  // silently didn't run, so say so, in plain English, at the top of the section.
  //
  // Deliberately NOT a block on Ready/invoice: the failure is usually transient
  // (a Firestore blip), and hard-blocking would stop a biller from closing real
  // money over a trigger that will succeed on the next line write. This is a
  // "look at every line yourself" warning, not a gate. The marker self-clears the
  // moment a pass completes, so it cannot go stale.
  let _checkFailBanner = '';
  const _bcf = ReviewState.ticket?._billingCheckFailure;
  if (_bcf && _bcf.stage) {
    const _stageWords = {
      'acquire-lock': "couldn't start",
      'employee-line-money-guard': 'the employee-line money guard',
      'equipment-rate-enrichment': 'the equipment rate lookup',
      'server-side-dedup': 'the duplicate-line merge',
      'stray-and-cross-job-guard': 'the double-bill / wrong-job check',
      // integ/2099 (TASK-9725) — the billed-PPE charge line runs BEFORE the
      // pass chain and never blocks it, so its wording must not say "the checks
      // after it never ran": they did; the supply charge line is what is missing.
      'ppe-billing': 'the billed-PPE charge line',
    };
    const _what = _stageWords[_bcf.stage] || escapeHtml(String(_bcf.stage));
    _checkFailBanner = `
      <div id="review-billing-check-failed-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid #ef4444;border-left:4px solid #dc2626;border-radius:8px;background:#fef2f2;color:#991b1b;font-size:0.82rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
          <span class="material-icons" style="font-size:18px;">error_outline</span>
          Automatic checks did not finish on this ticket
        </div>
        <div style="margin-top:4px;">
          ${_bcf.stage === 'acquire-lock'
            ? 'The server pass that prices $0 equipment lines, merges duplicates and flags double-bills <strong>never ran</strong> on this ticket.'
            : _bcf.stage === 'ppe-billing'
              ? 'The <strong>billed-PPE charge line</strong> for this ticket could not be written (the other checks ran). A supply charge the customer owes may be missing below; it is retried on the next line write.'
              : `The server pass stopped at <strong>${_what}</strong>, so the checks after it never ran.`}
          Anything it would normally have caught &mdash; an unpriced truck, a duplicate line, hours that don't fit the shift, a line billing on the wrong job &mdash;
          <strong>is not flagged below.</strong> Read every line yourself before invoicing. This clears itself once the checks complete.
        </div>
      </div>`;
  }

  if (_bannerRows.length > 0) {
    _mixedBanner = `
      <div id="review-equip-mixed-rate-banner" style="margin:6px 0 10px;padding:10px 12px;border:1px solid #f59e0b;border-left:4px solid #f59e0b;border-radius:8px;background:#fffbeb;color:#92400e;font-size:0.82rem;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
          <span class="material-icons" style="font-size:18px;">warning_amber</span>
          Equipment — review needed
        </div>
        ${_bannerRows.join('')}
      </div>`;
  }
  // TASK-4658 — prepended, not folded into the amber banner: a check that did
  // not RUN is a different (and louder) statement than a check that fired. Goes
  // through _mixedBanner so both the desktop table and the phone card path get
  // it without threading a new argument through _renderReviewEquipmentCards.
  if (_checkFailBanner) _mixedBanner = _checkFailBanner + _mixedBanner;
  // TASK-9258 — appended after the amber banner, same reasoning as TASK-9275:
  // informational, must never push a real Ready-blocker off the top. Threaded
  // through _mixedBanner so the phone card path gets it too.
  if (_tplBanner) _mixedBanner = _mixedBanner + _tplBanner;
  // TASK-9275 — APPENDED, and after the amber banner on purpose: a wrong yard
  // count is the quieter of the two statements, and it must not push a real
  // Ready-blocker off the top of the section. Threaded through _mixedBanner so
  // the phone card path (_renderReviewEquipmentCards) gets it too, without a new
  // argument — same trick TASK-4658 used.
  if (_stockBanner) _mixedBanner = _mixedBanner + _stockBanner;
  if (_matUi) _mixedBanner += _matUi.bannerHtml(); // TASK-9264 crew-reported materials (lazy materialsReview.js)

  // TASK-1234 — phones get stacked per-asset cards instead of the deliberate
  // min-width-640 scrolling table (TASK-1026): the scroll mechanism protected
  // the numbers by scrolling but collapsed the QTY field itself on a phone
  // (width:100% of a near-zero residual cell = an empty pill, mrscsc 2026-07-17).
  if (_reviewCardMode()) {
    return _renderReviewEquipmentCards(lines, _revEqShowTimes, _rentalBanner, _mixedBanner, _mixedGroups, _openByLineId);
  }

  // Read-only gate (desktop table mirror of the card-mode gate).
  const _roStyle = isReviewReadOnly(ReviewState.ticket) ? ' style="pointer-events:none;opacity:0.6;"' : '';
  let html = `
    <div class="review-section" id="review-equip-section"${_roStyle}>
      <datalist id="review-equip-uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
      <div class="review-section-title">
        <span class="material-icons" style="font-size:16px;">local_shipping</span> Equipment
      </div>
      ${_rentalBanner}
      ${_mixedBanner}
      <div style="overflow-x:auto;">
        <table class="billing-lines-table">
          <thead>
            <tr>
              <th style="min-width:100px;">Equipment</th>
              <th style="width:45px;">Unit #</th>
              ${_revEqShowTimes ? '<th style="width:80px;">In</th><th style="width:80px;">Out</th>' : ''}
              <th style="text-align:right;width:80px;">QTY</th>
              <th style="text-align:right;width:72px;">Rate</th>
              <th style="width:88px;">Unit</th>
              <th style="text-align:right;width:80px;">Amount</th>
              <th style="width:40px;"></th>
            </tr>
          </thead>
          <tbody>
  `;

  if (lines.length === 0) {
    html += `<tr><td colspan="${colCount}" style="text-align:center;color:var(--text-secondary);font-style:italic;">No equipment lines yet</td></tr>`;
  }

  for (const l of lines) {
    // Fallback: resolve missing/bad asset names from roster data
    // Also re-resolve when fleetNumber looks like a Firestore doc ID (20+ alphanumeric chars)
    const needsResolve = !l.assetName || (l.fleetNumber && /^[a-zA-Z0-9]{15,}$/.test(l.fleetNumber));
    if (needsResolve && l.assetId) {
      const found = ReviewState.trucks.find(t => t.id === l.assetId)
        || ReviewState.trailers.find(t => t.id === l.assetId)
        || ReviewState.assets.find(a => a.id === l.assetId);
      if (found) {
        l.assetName = window.getUnitDisplay ? window.getUnitDisplay(found) : (found.unit || found.name || found.partNumber || '');
        l.fleetNumber = found.unit || found.partNumber || '';
        if (!l.assetType) l.assetType = ReviewState.trucks.find(t => t.id === l.assetId) ? 'truck' : ReviewState.trailers.find(t => t.id === l.assetId) ? 'trailer' : 'asset';
      }
    }
    const typeLabel = l.assetType === 'trailer' ? ' <span style="font-size:0.7rem;color:var(--text-secondary);font-weight:500;">(Trailer)</span>'
      : l.assetType === 'asset' ? ' <span style="font-size:0.7rem;color:var(--text-secondary);font-weight:500;">(Asset)</span>' : '';
    const rateOpts = buildEquipmentRateOptions(l);
    const _isPerUnit = !isTimeBasedUnit(l.rateUnit);

    // Ensure hours is computed from times if missing.
    // 🔴 TASK-9327 (COD Independent Code Reviewer found this; I had missed it) —
    // this is a FOURTH heal door, and it is the one that actually defeated the
    // fix. `openReviewTicket` runs its three guarded heals and THEN calls this
    // render, which mutates the same ReviewState line objects in place; the next
    // Save persists whatever it painted. So guarding only openReviewTicket left
    // #100738 still showing 7 over the biller's saved 0.
    // It also never carried the `zeroHoursOk` guard the other three have, so a
    // line the biller explicitly marked non-billable was being overwritten here
    // too — that half predates this task.
    if (l.zeroHoursOk === true || l.hoursManual === true) { /* a human ruled on these hours */ }
    else if (l.fromTime && l.toTime && (!l.hours || l.hours === 0) && !_isPerUnit) {
      const _from = l.fromTime?.toDate ? l.fromTime.toDate() : new Date(l.fromTime);
      const _to = l.toTime?.toDate ? l.toTime.toDate() : new Date(l.toTime);
      const _rawHrs = Math.max(0, (_to - _from) / 3600000);
      if (_rawHrs > 0) {
        const _sr = shouldRoundLine(l);
        l.hours = _sr ? roundHalfHour(_rawHrs) : parseFloat(_rawHrs.toFixed(2));
        l.lineAmount = parseFloat((l.hours * (l.rate || 0)).toFixed(2));
      }
    }

    const amountDisplay = '$' + (l.lineAmount || 0).toFixed(2);
    const idx = lines.indexOf(l);
    const moveBtns = lines.length > 1 ? `<span style="display:inline-flex;flex-direction:column;gap:0;margin-right:2px;vertical-align:middle;">${
      idx > 0 ? `<button class="btn-icon" onclick="window.BillingModule.moveReviewEquipmentLine(${l._localId},-1)" title="Move up" style="padding:0;line-height:1;">
        <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">arrow_drop_up</span></button>` : '<span style="width:24px;height:10px;display:block;"></span>'
    }${
      idx < lines.length - 1 ? `<button class="btn-icon" onclick="window.BillingModule.moveReviewEquipmentLine(${l._localId},1)" title="Move down" style="padding:0;line-height:1;">
        <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">arrow_drop_down</span></button>` : '<span style="width:24px;height:10px;display:block;"></span>'
    }</span>` : '';

    if (l._isNew) {
      const equipOptions = buildEquipmentOptions(l.assetType, l.assetId);
      html += `
        <tr data-local-id="${l._localId}">
          <td>
            <select class="review-select" onchange="window.BillingModule.onReviewEquipmentSelect(${l._localId}, this.value)">
              <option value="">Select equipment...</option>
              ${equipOptions}
            </select>${rateOpts ? `<br><select class="review-select" style="font-size:10px;max-width:140px;margin-top:4px;" onchange="window.BillingModule.onReviewEquipmentRateSelect(${l._localId}, this.value)">
              <option value="">Manual</option>${rateOpts}</select>` : ''}
          </td>
          <td id="review-equip-fleet-${l._localId}">${escapeHtml(l.fleetNumber || '-')}</td>
          ${_revEqShowTimes ? (_isPerUnit ? '<td style="color:var(--text-secondary);text-align:center;">—</td><td style="color:var(--text-secondary);text-align:center;">—</td>' : `<td>
            <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-in', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Click to edit time'}">${lineFromHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-equip-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime) || _seedDatetimeLocal()}"
              onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'fromTime', this.value)">
          </td>
          <td>
            <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-out-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-out', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Click to edit time'}">${lineToHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-equip-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime) || _seedDatetimeLocal()}"
              onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'toTime', this.value)">
          </td>`) : ''}
          ${_isPerUnit ? `<td style="text-align:right;"><input type="number" class="review-input" id="review-equip-hours-${l._localId}" value="${l.quantity || ''}" step="0.01" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'quantity', this.value)"></td>` : `<td style="text-align:right;"><input type="number" class="review-input" id="review-equip-hours-${l._localId}" value="${(l.hours || 0).toFixed(2)}" step="0.25" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'hours', this.value)"><span id="review-equip-round-${l._localId}">${_roundingMarker(l)}</span></td>`}
          <td style="text-align:right;"><input type="number" class="review-input${_reviewRateZeroClass(l)}" value="${l.rate || ''}" step="1" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'rate', this.value)"></td>
          <td><input type="text" class="review-input review-unit-cell" list="review-equip-uom-suggestions" value="${escapeHtml(l.rateUnit || 'Hourly')}" autocomplete="off" title="Rate unit (per-hour / per-day / per-mat / flat...)" onfocus="this.select()" onchange="window.BillingModule.updateReviewEquipmentUnit(${l._localId}, this.value)"></td>
          <td style="text-align:right;font-weight:600;white-space:nowrap;" id="review-equip-amount-${l._localId}">${amountDisplay}</td>
          <td style="white-space:nowrap;">${moveBtns}<button class="btn-icon" onclick="window.BillingModule.removeReviewLine('EQUIPMENT', ${l._localId})" title="Remove">
            <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
        </tr>
      `;
    } else {
      // TASK-1165 S2 — per-row badge on a $0 line whose asset also has a priced
      // line on this ticket (the banner above carries the one-click fixes).
      // TASK-1291 — only badge a $0 row when its asset genuinely has a priced/$0
      // MIX (a resolvable-in-the-banner case). A pure hours-flag group is also in
      // _mixedGroups but has no priced sibling, so `.has()` alone would mislabel it.
      const _badgeGrp = _mixedGroups.get(l.assetId);
      const _unpricedBadge = (_badgeGrp?.priced?.length > 0 && _badgeGrp?.zeros?.length > 0 && !(Number(l.rate) || 0))
        ? ` <span title="Unpriced — this unit also has a priced line on this ticket. Use the banner above to resolve." style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:#fef3c7;color:#92400e;border:1px solid #f59e0b;font-size:0.68rem;font-weight:700;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">warning_amber</span>UNPRICED</span>`
        : '';
      // TASK-842-A — span-on-line: a rental-engine slice line shows the exact
      // [start – end] window it bills (UX spec: no black-box sweep). "still out"
      // marks a line whose slice is OPEN (equipment not yet returned).
      const _rentalSpanPill = (l.rentalSlice === true && l.sliceStartMs != null)
        ? `<br><span title="Rental period billed by this line (from the server slice ledger)" style="display:inline-flex;align-items:center;gap:3px;margin-top:3px;padding:1px 7px;border-radius:8px;background:#eff6ff;color:#1e40af;border:1px solid #93c5fd;font-size:0.68rem;font-weight:600;white-space:nowrap;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">date_range</span>${_fmtRentalSliceDate(l.sliceStartMs)} – ${_fmtRentalSliceDate(l.sliceEndMs)}${_openByLineId.has(l.id) ? ' · still out' : ''}</span>`
        : '';
      html += `
        <tr data-local-id="${l._localId}">
          <td style="white-space:normal;word-break:break-word;">${escapeHtml(getEquipmentDisplayName(l))}${typeLabel}${_unpricedBadge}${_zeroHourBadge(l)}${_rentalSpanPill}${rateOpts ? `<br><select class="review-select" style="font-size:10px;max-width:140px;margin-top:4px;" onchange="window.BillingModule.onReviewEquipmentRateSelect(${l._localId}, this.value)">
              <option value="">Manual</option>${rateOpts}</select>` : ''}</td>
          <td id="review-equip-fleet-${l._localId}">${escapeHtml(l.fleetNumber || '-')}</td>
          ${_revEqShowTimes ? (_isPerUnit ? '<td style="color:var(--text-secondary);text-align:center;">—</td><td style="color:var(--text-secondary);text-align:center;">—</td>' : `<td>
            <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-in', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Click to edit time'}">${lineFromHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-equip-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime) || _seedDatetimeLocal()}"
              onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'fromTime', this.value)">
          </td>
          <td>
            <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-out-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-out', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Click to edit time'}">${lineToHtml(l)}</span>
            <input type="datetime-local" class="review-time-input hidden" id="review-equip-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime) || _seedDatetimeLocal()}"
              onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'toTime', this.value)">
          </td>`) : ''}
          ${_isPerUnit ? `<td style="text-align:right;"><input type="number" class="review-input" id="review-equip-hours-${l._localId}" value="${l.quantity || ''}" step="0.01" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'quantity', this.value)"></td>` : `<td style="text-align:right;"><input type="number" class="review-input" id="review-equip-hours-${l._localId}" value="${(l.hours || 0).toFixed(2)}" step="0.25" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'hours', this.value)"><span id="review-equip-round-${l._localId}">${_roundingMarker(l)}</span></td>`}
          <td style="text-align:right;"><input type="number" class="review-input${_reviewRateZeroClass(l)}" value="${l.rate || 0}" step="1" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'rate', this.value)"></td>
          <td><input type="text" class="review-input review-unit-cell" list="review-equip-uom-suggestions" value="${escapeHtml(l.rateUnit || 'Hourly')}" autocomplete="off" title="Rate unit (per-hour / per-day / per-mat / flat...)" onfocus="this.select()" onchange="window.BillingModule.updateReviewEquipmentUnit(${l._localId}, this.value)"></td>
          <td style="text-align:right;font-weight:600;white-space:nowrap;" id="review-equip-amount-${l._localId}">${amountDisplay}</td>
          <td style="white-space:nowrap;">${moveBtns}<button class="btn-icon" onclick="window.BillingModule.removeReviewLine('EQUIPMENT', ${l._localId})" title="Remove">
            <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
        </tr>
      `;
    }
  }

  const equipSubtotal = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  html += `
          </tbody>
          <tfoot>
            <tr>
              <td colspan="${colCount - 2}" style="text-align:right;font-weight:600;">Equipment Total</td>
              <td style="text-align:right;font-weight:700;" id="review-equip-subtotal">$${equipSubtotal.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <button class="review-add-btn" onclick="window.BillingModule.addReviewEquipmentRow()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Equipment
      </button>
    </div>
  `;

  return html;
}

// TASK-1234 — stacked per-asset EQUIPMENT cards (phone). Shares the section id,
// banners, input ids/classes/handlers with the table path so every surgical
// updater (updateReviewEquipmentLine/Unit, updateReviewTime, renderReviewTotals,
// onReviewEquipmentSelect, hydrateEquipmentHours) works unchanged. The QTY/Hours
// input — the field that produced the $317K prepend — gets a full-width
// thumb-size row with a 14px label, and the inline outlier warning renders
// directly beneath it (point-of-entry signal, per COD).
function _renderReviewEquipmentCards(lines, showTimes, rentalBanner, mixedBanner, mixedGroups, openByLineId) {
  // Steve/AR-AP fix (2026-07-20): mirror the employee-card read-only gate — dim +
  // disable interaction when the ticket is archived/locked/invoiced so equipment
  // time/qty/rate inputs can't be silently typed into on a read-only ticket.
  const _roStyle = isReviewReadOnly(ReviewState.ticket) ? ' style="pointer-events:none;opacity:0.6;"' : '';
  let html = `
    <div class="review-section" id="review-equip-section"${_roStyle}>
      <datalist id="review-equip-uom-suggestions">${UOM_SUGGESTIONS.map(u => `<option value="${u}">`).join('')}</datalist>
      <div class="review-section-title">
        <span class="material-icons" style="font-size:16px;">local_shipping</span> Equipment
      </div>
      ${rentalBanner}
      ${mixedBanner}
  `;

  if (lines.length === 0) {
    html += `<div class="rvc-empty">No equipment lines yet</div>`;
  }

  for (const l of lines) {
    // Same roster name-resolve as the table path (idempotent state normalization)
    const needsResolve = !l.assetName || (l.fleetNumber && /^[a-zA-Z0-9]{15,}$/.test(l.fleetNumber));
    if (needsResolve && l.assetId) {
      const found = ReviewState.trucks.find(t => t.id === l.assetId)
        || ReviewState.trailers.find(t => t.id === l.assetId)
        || ReviewState.assets.find(a => a.id === l.assetId);
      if (found) {
        l.assetName = window.getUnitDisplay ? window.getUnitDisplay(found) : (found.unit || found.name || found.partNumber || '');
        l.fleetNumber = found.unit || found.partNumber || '';
        if (!l.assetType) l.assetType = ReviewState.trucks.find(t => t.id === l.assetId) ? 'truck' : ReviewState.trailers.find(t => t.id === l.assetId) ? 'trailer' : 'asset';
      }
    }
    const typeLabel = l.assetType === 'trailer' ? ' <span class="rvc-role">(Trailer)</span>'
      : l.assetType === 'asset' ? ' <span class="rvc-role">(Asset)</span>' : '';
    const rateOpts = buildEquipmentRateOptions(l);
    const _isPerUnit = !isTimeBasedUnit(l.rateUnit);

    // Ensure hours is computed from times if missing (same as table path)
    // TASK-9327 — FIFTH door, same guard as the table twin above. Phones render
    // cards instead of the table, so leaving this one open would have made the
    // resurrection mobile-only — the hardest possible version to reproduce.
    if (l.zeroHoursOk === true || l.hoursManual === true) { /* a human ruled on these hours */ }
    else if (l.fromTime && l.toTime && (!l.hours || l.hours === 0) && !_isPerUnit) {
      const _from = l.fromTime?.toDate ? l.fromTime.toDate() : new Date(l.fromTime);
      const _to = l.toTime?.toDate ? l.toTime.toDate() : new Date(l.toTime);
      const _rawHrs = Math.max(0, (_to - _from) / 3600000);
      if (_rawHrs > 0) {
        const _sr = shouldRoundLine(l);
        l.hours = _sr ? roundHalfHour(_rawHrs) : parseFloat(_rawHrs.toFixed(2));
        l.lineAmount = parseFloat((l.hours * (l.rate || 0)).toFixed(2));
      }
    }

    const amountDisplay = '$' + (l.lineAmount || 0).toFixed(2);
    const idx = lines.indexOf(l);
    const warnInfo = !_isPerUnit ? _lineHoursCeilingInfo(l) : null;
    const warnNow = !!warnInfo && (parseFloat(l.hours) || 0) > warnInfo.ceiling;

    // TASK-1291 — genuine priced/$0 mix only (see desktop badge note).
    const _badgeGrp = mixedGroups.get(l.assetId);
    const _unpricedBadge = (!l._isNew && _badgeGrp?.priced?.length > 0 && _badgeGrp?.zeros?.length > 0 && !(Number(l.rate) || 0))
      ? ` <span title="Unpriced — this unit also has a priced line on this ticket. Use the banner above to resolve." style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:8px;background:#fef3c7;color:#92400e;border:1px solid #f59e0b;font-size:0.75rem;font-weight:700;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">warning_amber</span>UNPRICED</span>`
      : '';
    const _rentalSpanPill = (!l._isNew && l.rentalSlice === true && l.sliceStartMs != null)
      ? `<span title="Rental period billed by this line (from the server slice ledger)" style="display:inline-flex;align-items:center;gap:3px;margin-top:3px;padding:1px 7px;border-radius:8px;background:#eff6ff;color:#1e40af;border:1px solid #93c5fd;font-size:0.75rem;font-weight:600;white-space:nowrap;vertical-align:middle;"><span class="material-icons" style="font-size:12px;">date_range</span>${_fmtRentalSliceDate(l.sliceStartMs)} – ${_fmtRentalSliceDate(l.sliceEndMs)}${openByLineId.has(l.id) ? ' · still out' : ''}</span>`
      : '';

    const nameHtml = l._isNew
      ? `<select class="review-select rvc-name-select" onchange="window.BillingModule.onReviewEquipmentSelect(${l._localId}, this.value)">
          <option value="">Select equipment...</option>
          ${buildEquipmentOptions(l.assetType, l.assetId)}
        </select>`
      : `${escapeHtml(getEquipmentDisplayName(l))}${typeLabel}`;

    const moveBtns = lines.length > 1 ? `
      ${idx > 0 ? `<button class="btn-icon rvc-move" onclick="window.BillingModule.moveReviewEquipmentLine(${l._localId},-1)" title="Move up"><span class="material-icons">arrow_upward</span></button>` : ''}
      ${idx < lines.length - 1 ? `<button class="btn-icon rvc-move" onclick="window.BillingModule.moveReviewEquipmentLine(${l._localId},1)" title="Move down"><span class="material-icons">arrow_downward</span></button>` : ''}` : '';

    const presetHtml = rateOpts ? `
      <div class="rvc-preset">
        <span class="rvc-lbl">Price Book rate</span>
        <select class="review-select rvc-preset-select" onchange="window.BillingModule.onReviewEquipmentRateSelect(${l._localId}, this.value)">
          <option value="">Manual</option>${rateOpts}
        </select>
      </div>` : '';

    const timesHtml = (showTimes && !_isPerUnit) ? `
      <div class="rvc-times">
        <div class="rvc-time">
          <span class="rvc-lbl">Clock In</span>
          <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-in-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-in', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Tap to edit time'}">${lineFromHtml(l)}</span>
          <input type="datetime-local" class="review-time-input hidden" id="review-equip-in-input-${l._localId}" value="${toDatetimeLocal(l.fromTime) || _seedDatetimeLocal()}"
            onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'fromTime', this.value)">
        </div>
        <div class="rvc-time">
          <span class="rvc-lbl">Clock Out</span>
          <span class="review-time-display review-time-pill${l._timesMirroredFrom ? ' review-time-mirrored' : ''}" id="review-equip-out-display-${l._localId}" onclick="window.BillingModule.toggleReviewTimeEdit('review-equip-out', ${l._localId})" title="${l._timesMirroredFrom ? window.escAttr('Copied from ' + l._timesMirroredFrom + ' — edit if different') : 'Tap to edit time'}">${lineToHtml(l)}</span>
          <input type="datetime-local" class="review-time-input hidden" id="review-equip-out-input-${l._localId}" value="${toDatetimeLocal(l.toTime) || _seedDatetimeLocal()}"
            onchange="window.BillingModule.updateReviewTime('EQUIPMENT', ${l._localId}, 'toTime', this.value)">
        </div>
      </div>` : '';

    const qtyHoursField = _isPerUnit
      ? `<div class="rvc-field rvc-field-wide"><span class="rvc-lbl">QTY</span>
          <input type="number" class="review-input rvc-num" id="review-equip-hours-${l._localId}" value="${l.quantity || ''}" step="0.01" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'quantity', this.value)"></div>`
      : `<div class="rvc-field rvc-field-wide"><span class="rvc-lbl">Hours</span>
          <input type="number" class="review-input rvc-num${warnNow ? ' rvc-input-alert' : ''}" id="review-equip-hours-${l._localId}" value="${(l.hours || 0).toFixed(2)}" step="0.25" min="0" onfocus="this.select()"
            onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'hours', this.value)"><span id="review-equip-round-${l._localId}">${_roundingMarker(l)}</span></div>`;

    const rateField = `<div class="rvc-field rvc-field-rate"><span class="rvc-lbl">Rate</span>
        <div class="review-rate-group"><input type="number" class="review-input${_reviewRateZeroClass(l)}" value="${l._isNew ? (l.rate || '') : (l.rate || 0)}" step="1" min="0" onfocus="this.select()"
          onchange="window.BillingModule.updateReviewEquipmentLine(${l._localId}, 'rate', this.value)"><span class="review-rate-sep">/</span><input type="text" class="review-input review-unit-input" list="review-equip-uom-suggestions" value="${escapeHtml(l.rateUnit || 'Hourly')}" autocomplete="off" title="Rate unit (per-hour / per-day / per-mat / flat...)" onfocus="this.select()" onchange="window.BillingModule.updateReviewEquipmentUnit(${l._localId}, this.value)"></div></div>`;

    const amountField = `<div class="rvc-field rvc-amt"><span class="rvc-lbl">Amount</span><span class="rvc-amt-val" id="review-equip-amount-${l._localId}">${amountDisplay}</span></div>`;

    html += `
      <div class="rvc-card" data-local-id="${l._localId}">
        <div class="rvc-head">
          <div class="rvc-title">${nameHtml}${_unpricedBadge}${_zeroHourBadge(l)}
            <span class="rvc-unitno">Unit <span id="review-equip-fleet-${l._localId}">${escapeHtml(l.fleetNumber || '-')}</span></span>
            ${_rentalSpanPill}
          </div>
          <div class="rvc-head-actions">${moveBtns}
            <button class="btn-icon rvc-del" onclick="window.BillingModule.removeReviewLine('EQUIPMENT', ${l._localId})" title="Remove">
              <span class="material-icons">delete_outline</span></button>
          </div>
        </div>
        ${presetHtml}
        ${timesHtml}
        <div class="rvc-grid">${qtyHoursField}${!_isPerUnit ? _hoursWarnHtml('equip', l) : ''}${rateField}${amountField}</div>
      </div>`;
  }

  const equipSubtotal = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  html += `
      <div class="rvc-subtotal"><span>Equipment Total</span><span class="rvc-subtotal-amt" id="review-equip-subtotal">$${equipSubtotal.toFixed(2)}</span></div>
      <button class="review-add-btn" onclick="window.BillingModule.addReviewEquipmentRow()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Equipment
      </button>
    </div>
  `;
  return html;
}

function renderReviewPermitSection() {
  const lines = ReviewState.permitLines;
  const hasLines = lines.length > 0;
  const permitSubtotal = lines.reduce((s, l) => s + (l.lineAmount || 0), 0);

  // Get configured permit line items from contract
  const permitLiOptions = (ReviewState.contractLineItems || []).filter(li =>
    li.active !== false && li.appliesTo?.kind === 'permit'
  );
  const hasPermitOptions = permitLiOptions.length > 0;

  let addBtn;
  if (hasPermitOptions) {
    addBtn = `
      <select id="review-permit-add-select" class="form-control" style="font-size:0.75rem;padding:4px 8px;width:auto;min-width:130px;"
        onchange="if(this.value){window.BillingModule.addReviewPermitRow(this.value);this.value='';}">
        <option value="">+ Add Load Permit</option>
        ${permitLiOptions.map(li => `<option value="${li.id}">${escapeHtml(li.description)} — $${(li.rate || 0).toFixed(2)}/${escapeHtml(li.rateUnit || 'Per')}</option>`).join('')}
        <option value="__custom__">Custom permit...</option>
      </select>`;
  } else {
    addBtn = `
      <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.addReviewPermitRow()" style="font-size:0.75rem;padding:4px 10px;">
        <span class="material-icons" style="font-size:14px;">add</span> Add Load Permit
      </button>`;
  }

  return `
    <div class="review-section" id="review-permit-section">
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:18px;">description</span> Load Permits
          ${hasLines ? `<span class="review-subtotal" id="review-permit-subtotal">$${permitSubtotal.toFixed(2)}</span>` : ''}
        </span>
        ${addBtn}
      </div>
      ${!hasLines ? `<p style="font-size:0.75rem;color:var(--text-secondary);margin:4px 0 0;">Oversize/overweight transport permit costs</p>` : ''}
      ${hasLines ? `
        <div style="overflow-x:auto;">
          <table class="review-table"><thead><tr>
            <th style="text-align:left;padding:6px 10px;font-weight:600;">Description</th>
            <!-- TASK-1413 — Qty 70px/Rate 90px clipped their own inputs: the number
                 input is width:100%, so the content box was 90 - 20 (td padding)
                 - 16 (input padding) = 54px minus Chrome's spinner reservation,
                 and "222.00" at 0.8rem did not fit (rendered "222.0("). Stored
                 value was always correct; this is display-only. Both tables sit
                 in overflow-x:auto so mobile just scrolls. -->
            <th style="text-align:center;padding:6px 10px;font-weight:600;width:80px;">Qty</th>
            <th style="text-align:right;padding:6px 10px;font-weight:600;width:120px;" title="Your cost per permit, before markup">Cost</th>
            <th style="text-align:right;padding:6px 10px;font-weight:600;width:90px;" title="Markup on the permit cost. Starts from this customer's Price Book (or your company default) and can be changed for this ticket.">Markup %</th>
            <th style="text-align:right;padding:6px 10px;font-weight:600;width:90px;">Amount</th>
            <th style="width:36px;"></th>
          </tr></thead><tbody>
          ${lines.map(l => `
            <tr>
              <td style="padding:4px 10px;"><input type="text" class="form-control" value="${escapeHtml(l.description || '')}" placeholder="Permit description..."
                style="font-size:0.8rem;padding:4px 8px;width:100%;"
                onchange="window.BillingModule.updateReviewPermitLine(${l._localId}, 'description', this.value)">${l._fromOtherJob ? `
                <div class="review-permit-other-job" style="font-size:0.7rem;color:#D97706;margin-top:2px;display:flex;align-items:center;gap:4px;">
                  <span class="material-icons" style="font-size:13px;">warning</span>
                  Priced from ${l._fromOtherJob.fromJobName ? `"${escapeHtml(l._fromOtherJob.fromJobName)}"` : 'another job'} — this ticket no longer carries that permit. Keep or remove.
                </div>` : ''}</td>
              <td style="padding:4px 10px;"><input type="number" class="form-control" value="${l.quantity || 1}" min="0" step="1"
                style="font-size:0.8rem;padding:4px 8px;text-align:center;width:100%;"
                onchange="window.BillingModule.updateReviewPermitLine(${l._localId}, 'quantity', parseFloat(this.value)||0)"></td>
              <td style="padding:4px 10px;"><input type="number" class="form-control" value="${(Number(l.baseRate ?? l._displayBaseRate ?? l.rate) || 0).toFixed(2)}" min="0" step="0.01"
                style="font-size:0.8rem;padding:4px 8px;text-align:right;width:100%;"
                onchange="window.BillingModule.updateReviewPermitLine(${l._localId}, 'baseRate', parseFloat(this.value)||0)"></td>
              <td style="padding:4px 10px;"><input type="number" class="form-control review-permit-markup" value="${Number(l.upchargePercent) || 0}" min="0" max="100" step="0.01"
                style="font-size:0.8rem;padding:4px 8px;text-align:right;width:100%;" aria-label="Markup percent"
                onchange="window.BillingModule.updateReviewPermitLine(${l._localId}, 'upchargePercent', this.value)"></td>
              <td style="padding:4px 10px;text-align:right;font-weight:600;">$${(l.lineAmount || 0).toFixed(2)}</td>
              <td style="padding:4px 4px;"><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('PERMIT', ${l._localId})" title="Remove permit">
                <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
            </tr>
          `).join('')}
          </tbody></table>
        </div>
      ` : ''}
      ${_permitCheckSlotHtml()}
    </div>
  `;
}

// TASK-10043 S2 — the permit check (permits recorded for the job, cost on the
// ticket, truck / job / route warnings) lives in the lazily loaded
// ticketPermitCheckUi.js chunk (perf budget); this is only its slot + loader.
let _permitCheckUi = null;
function _permitCheckSlotHtml() {
  if (!ReviewState.ticket?.jobId) return '';
  setTimeout(() => {
    const el = document.getElementById('review-permit-check');
    if (!el) return;
    (_permitCheckUi ? Promise.resolve(_permitCheckUi) : __ddChunkWrap(import('./ticketPermitCheckUi.js?v=10292950')).then((m) => (_permitCheckUi = m)))
      .then((m) => m.mountPermitCheck(el, { ReviewState, BillingState, escapeHtml, httpsCallable, functions,
        readOnly: () => isReviewReadOnly(ReviewState.ticket) || _ticketContentViewOnly(),
        addLine: (preset) => addReviewPermitRow(null, preset) }))
      .catch(() => {});
  }, 0);
  return '<div id="review-permit-check"></div>';
}

// TASK-10088 — accounting-only ticket notes (owner + Billing module only, by
// firestore.rules; drivers cannot read them). They live in the lazily loaded
// ticketAcctNoteUi.js chunk (perf budget); this is only its slot + loader. A
// login the rules refuse gets an empty slot.
// (A dynamic import() resolves to the same cached module after the first load.)
function _acctNoteSlotHtml() {
  if (!ReviewState.ticketId) return '';
  setTimeout(() => __ddChunkWrap(import('./ticketAcctNoteUi.js?v=1'))
    .then((m) => m.mountAcctNote(document.getElementById('review-acct-notes'), { BillingState, ReviewState, escapeHtml }))
    .catch(() => {}), 0);
  return '<div id="review-acct-notes"></div>';
}

// ============================================
// REVIEW TICKET — DOCUMENTS SECTION
// ============================================
function renderReviewDocumentsSection() {
  const allDocs = [...ReviewState.documents, ...ReviewState.pendingDocFiles.map(p => ({ ...p, _pending: true }))];
  const count = allDocs.length;

  let html = `
    <div class="review-section" id="review-documents-section">
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span>
          <span class="material-icons" style="font-size:16px;">attach_file</span> Documents${count > 0 ? ` (${count})` : ''}
        </span>
        <button class="btn btn-sm btn-secondary" onclick="document.getElementById('review-doc-upload-input').click()" style="font-size:0.75rem;padding:4px 10px;" ${count >= 10 ? 'disabled title="Maximum 10 documents"' : ''}>
          <span class="material-icons" style="font-size:14px;">add</span> Upload
        </button>
      </div>
      <input type="file" id="review-doc-upload-input" accept="image/*,.heic,.heif,application/pdf" multiple style="display:none;" onchange="window.BillingModule.handleReviewDocUpload(event)">
  `;

  if (count === 0) {
    html += `<div style="padding:12px;text-align:center;color:var(--text-secondary);font-size:0.8125rem;">No documents attached. Click Upload to add permits, photos, or PDFs.</div>`;
  } else {
    for (const d of allDocs) {
      const isPending = d._pending;
      const isPdf = d.isPdf || d.mimeType === 'application/pdf';
      const thumbSrc = isPdf ? null : (d.thumbUrl || d.previewUrl || d.fullUrl);
      const sourceBadge = d.source === 'job_permit'
        ? '<span style="display:inline-block;padding:1px 6px;font-size:0.65rem;background:#DBEAFE;color:#1E40AF;border-radius:4px;margin-left:6px;">Job Permit</span>'
        : d.source === 'clock_photo'
          ? '<span style="display:inline-block;padding:1px 6px;font-size:0.65rem;background:#E0E7FF;color:#3730A3;border-radius:4px;margin-left:6px;">Clock Photo</span>'
          : isPending
            ? '<span style="display:inline-block;padding:1px 6px;font-size:0.65rem;background:#DCFCE7;color:#166534;border-radius:4px;margin-left:6px;">Uploaded</span>'
            : '';
      const printChecked = d.printWithInvoice !== false ? 'checked' : '';

      html += `
        <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <div style="width:40px;height:40px;border-radius:6px;overflow:hidden;background:var(--bg-elevated);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            ${isPdf
              ? '<span class="material-icons" style="font-size:24px;color:#EF4444;">picture_as_pdf</span>'
              : (thumbSrc ? `<img ${isPending ? `src="${escapeHtml(thumbSrc)}"` : _sfThumbAttrs(d)} style="width:40px;height:40px;object-fit:cover;" alt="">` : '<span class="material-icons" style="font-size:24px;color:var(--text-secondary);">image</span>')
            }
          </div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:0.8125rem;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--text-primary);">${escapeHtml(d.name || 'Document')}${sourceBadge}</div>
            ${_ticketDocUploadedHtml(d)}
            <div style="display:flex;align-items:center;flex-wrap:wrap;gap:4px 12px;margin-top:2px;">
              ${d.source === 'job_permit' ? '' : _reviewDocTypeSelectHtml(d)}
              <label style="display:flex;align-items:center;gap:4px;font-size:0.7rem;color:var(--text-secondary);cursor:pointer;">
                <input type="checkbox" ${printChecked} onchange="window.BillingModule.toggleDocPrintWithInvoice('${d.id}', this.checked)" style="width:14px;height:14px;">
                Print with invoice
              </label>
            </div>
          </div>
          ${!isPending && (d.fullUrl || d.fullPath) ? `<a ${window.StorageFile.linkAttrs(d, { urlKey: 'fullUrl' })} class="btn btn-icon-secondary btn-sm" title="View"><span class="material-icons" style="font-size:16px;">open_in_new</span></a>` : ''}
          <button class="btn btn-icon-secondary btn-sm" onclick="window.BillingModule.removeReviewDoc('${d.id}')" title="Remove" style="color:#EF4444;">
            <span class="material-icons" style="font-size:16px;">close</span>
          </button>
        </div>
      `;
    }
  }

  html += `</div>`;
  return html;
}

// TASK-9397 — pick-time photo work saveReviewTicket must wait out (a HEIC
// conversion can take seconds; a Save during it would drop the document).
const _reviewPhotoPrep = createPhotoPrepTracker();

function handleReviewDocUpload(event) {
  return _reviewPhotoPrep.track(_handleReviewDocUpload(event));
}

async function _handleReviewDocUpload(event) {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  event.target.value = '';
  // Opening a ticket resets pendingDocFiles to a NEW array; a file finishing
  // after the biller moved to another ticket must not land on that one.
  const pendingList = ReviewState.pendingDocFiles;
  const pendingBefore = pendingList.length;

  const totalDocs = ReviewState.documents.length + ReviewState.pendingDocFiles.length;
  const remaining = 10 - totalDocs;
  if (remaining <= 0) {
    window.showToast?.('Maximum 10 documents per ticket', 'warning');
    return;
  }

  const toProcess = files.slice(0, remaining);
  if (files.length > remaining) {
    window.showToast?.(`Only ${remaining} more document${remaining !== 1 ? 's' : ''} allowed`, 'warning');
  }

  for (const file of toProcess) {
    const nameLower = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
    const isImage = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));

    if (!isPdf && !isImage) {
      window.showToast?.(`${file.name}: Only images and PDFs allowed`, 'error');
      continue;
    }

    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.(`${file.name}: File too large (max 10MB)`, 'error');
      continue;
    }

    const id = `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      if (isPdf) {
        ReviewState.pendingDocFiles.push({
          id,
          blob: file,
          thumbBlob: null,
          previewUrl: null,
          name: file.name,
          mimeType: 'application/pdf',
          isPdf: true,
          printWithInvoice: true,
          docType: 'other' // TASK-9868 — default; the row's picker changes it
        });
      } else {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          window.showToast?.(validation.error, 'error');
          continue;
        }

        // TASK-9397 — HEIC used to be parked RAW here, then uploaded as
        // `_full.jpg` with contentType image/jpeg: HEIC bytes under a JPEG
        // label, broken on every screen and in the invoice PDF. Now: convert
        // when this browser can't draw it, then the usual JPEG + thumbnail.
        const drawable = await toDrawablePhoto(file);
        const processed = await processPhoto(drawable, { maxSizeKB: 200 });
        const thumbnail = await generateThumbnail(drawable);
        if (ReviewState.pendingDocFiles !== pendingList) return;

        ReviewState.pendingDocFiles.push({
          id,
          blob: processed,
          thumbBlob: thumbnail,
          previewUrl: URL.createObjectURL(thumbnail),
          name: drawable.name || file.name,
          mimeType: 'image/jpeg',
          isPdf: false,
          printWithInvoice: true,
          docType: 'other' // TASK-9868 — default; the row's picker changes it
        });
      }
    } catch (error) {
      console.error('[Billing] Document processing error:', error?.cause || error);
      window.showToast?.(`${file.name}: ${photoErrorMessage(error, 'Failed to process this file')}`, 'error');
    }
  }
  if (ReviewState.pendingDocFiles !== pendingList) return;

  ReviewState.dirty = true;
  // Re-render just the documents section
  const section = document.getElementById('review-documents-section');
  if (section) {
    section.outerHTML = renderReviewDocumentsSection();
  }
  const added = ReviewState.pendingDocFiles.length - pendingBefore;
  if (added > 0) window.showToast?.(`${added} document${added !== 1 ? 's' : ''} added`, 'success');
}

// TASK-9355 S0 — document sources that are COPIES of a file this ticket does
// not own. `job_permit` / `job_photo` are bridged from the parent job's
// permits[] / photos[] (same fullPath as the job's own file); `clock_photo`
// is the driver's clock-out photo on ticket.photos[]. Removing one from a
// ticket must never `deleteObject` its Storage path — the job (or the clock
// entry) still points at it. Before this predicate both cleanup sites listed
// only job_permit + clock_photo by hand, so a removed job_photo fell through
// and the JOB lost its photo file (COD Edge Case Hunter + Code Reviewer +
// Independent, 2026-09-23). ONE list, read at both sites.
const _BRIDGED_DOC_SOURCES = new Set(['job_permit', 'job_photo', 'clock_photo']);
function _isBridgedReviewDoc(d) {
  return !!d && _BRIDGED_DOC_SOURCES.has(d.source);
}

// ═══ TASK-9355 — bridged JOB docs: identity, staleness, bridging. ═══
//
// The review modal surfaces the parent job's permits[] / photos[] on the
// ticket as `job_permit` / `job_photo` document COPIES, and Save persists them
// onto tickets/{id}.documents. These three helpers are the ONE place that
// knows (a) what a job permit/photo's id is, (b) when a persisted copy no
// longer belongs to the ticket's job, and (c) how a copy is minted. Used by
// openReviewTicket's self-heal (S1) and every job-change path (S2). Pure —
// no DOM, no Firestore — so they run in the unit tripwire.
//
// 🔴 STALENESS IS ID MEMBERSHIP, NEVER PATH-PARSING (COD 2026-09-23). Dispatch's
// "Copy to New Job" carries permits with the SAME ids and the SOURCE job's
// fullPath on purpose, so a path-parse would drop every legitimate permit off
// every copied job. A bridged doc is stale iff its jobPermitId / jobPhotoId is
// not among the job's CURRENT permit / photo ids. A copy with no id to judge by
// is left alone.

/** The "job" a ticket with NO job is judged against — nothing owns a bridged doc. */
const _NO_JOB_FOR_BRIDGE = Object.freeze({ id: null, permits: [], photos: [] });

/** Id of the i-th entry of job.permits / job.photos, with the bridge's index fallback. */
function _jobDocEntryId(entry, idx, kind) {
  return (entry && entry.id) || `${kind === 'photos' ? 'photo' : 'permit'}_${idx}`;
}

/** Set of the job's current permit ids (`kind:'permits'`) or photo ids (`kind:'photos'`). */
function _jobDocIds(job, kind) {
  const arr = Array.isArray(job?.[kind]) ? job[kind] : [];
  return new Set(arr.map((e, i) => _jobDocEntryId(e, i, kind)));
}

/**
 * Split `documents` into { keep, dropped } against `job`. Only `job_permit` /
 * `job_photo` copies are ever judged; manual uploads and clock photos always
 * keep. `job` null/undefined → cannot judge → everything keeps.
 */
function _partitionStaleBridgedDocs(documents, job) {
  const keep = [];
  const dropped = [];
  if (!job) return { keep: (documents || []).slice(), dropped };
  const permitIds = _jobDocIds(job, 'permits');
  const photoIds = _jobDocIds(job, 'photos');
  for (const d of (documents || [])) {
    let stale = false;
    if (d?.source === 'job_permit' && d.jobPermitId) stale = !permitIds.has(d.jobPermitId);
    else if (d?.source === 'job_photo' && d.jobPhotoId) stale = !photoIds.has(d.jobPhotoId);
    (stale ? dropped : keep).push(d);
  }
  return { keep, dropped };
}

/**
 * Bridge `job`'s permits[] + photos[] into `documents` (mutates; returns how
 * many were added). Skips ids already present and permit ids the biller hid
 * (`hiddenPermitIds`). Every copy is stamped `jobId` so a later move can be
 * judged by id, not by path. printWithInvoice defaults OFF on a bridged copy.
 */
/**
 * TASK-9958 — the provenance fields of a job file, copied onto its bridged
 * ticket document: uploadedBy (TASK-9868 shape), uploadedVia, and the two
 * older explicit markers (the Claude link's `source` lands as uploadedVia, AP
 * billing's uploadedFrom). Only what the job row actually records — {} for an
 * old row. Never invents a value.
 */
function _bridgedProvenance(f) {
  const out = {};
  if (!f || typeof f !== 'object') return out;
  if (f.uploadedBy && typeof f.uploadedBy === 'object' && f.uploadedBy.uid) out.uploadedBy = { ...f.uploadedBy };
  if (f.uploadedVia) out.uploadedVia = f.uploadedVia;
  else if (f.source === 'claude-upload-link') out.uploadedVia = 'claude_link';
  if (f.uploadedFrom) out.uploadedFrom = f.uploadedFrom;
  return out;
}

function _bridgeJobDocsInto(documents, job, hiddenPermitIds) {
  if (!job || !Array.isArray(documents)) return 0;
  let added = 0;
  const hidden = new Set(hiddenPermitIds || []);
  if (Array.isArray(job.permits) && job.permits.length) {
    const existing = new Set(documents.filter(d => d.source === 'job_permit').map(d => d.jobPermitId));
    job.permits.forEach((permit, idx) => {
      if (isJobDocEntry(permit, job)) return; // TASK-9694 — a driver Doc is not a job permit (idx kept for permit_${idx})
      const permitId = _jobDocEntryId(permit, idx, 'permits');
      if (hidden.has(permitId) || existing.has(permitId)) return;
      documents.push({
        id: `jp_${permitId}`,
        fullUrl: permit.fullUrl,
        thumbUrl: permit.thumbUrl || null,
        fullPath: permit.fullPath || null,
        thumbPath: permit.thumbPath || null,
        name: permit.name || permit.fileName || 'Job Permit',
        mimeType: permit.mimeType || (permit.isPdf ? 'application/pdf' : 'image/jpeg'),
        isPdf: permit.isPdf || false,
        sizeBytes: permit.sizeBytes || 0,
        uploadedAt: permit.uploadedAt || null,
        source: 'job_permit',
        jobPermitId: permitId,
        jobId: job.id || null,
        printWithInvoice: false,
        // TASK-9958 — who/how the JOB's file got there rides along (old rows: none).
        ..._bridgedProvenance(permit)
      });
      added++;
    });
  }
  if (Array.isArray(job.photos) && job.photos.length) {
    const existing = new Set(documents.filter(d => d.source === 'job_photo').map(d => d.jobPhotoId));
    job.photos.forEach((photo, idx) => {
      const photoId = _jobDocEntryId(photo, idx, 'photos');
      if (existing.has(photoId)) return;
      documents.push({
        id: `jph_${photoId}`,
        fullUrl: photo.fullUrl,
        thumbUrl: photo.thumbUrl || null,
        fullPath: photo.fullPath || null,
        thumbPath: photo.thumbPath || null,
        name: photo.name || photo.fileName || 'Job Photo',
        mimeType: photo.mimeType || 'image/jpeg',
        isPdf: false,
        sizeBytes: photo.sizeBytes || 0,
        uploadedAt: photo.uploadedAt || null,
        source: 'job_photo',
        jobPhotoId: photoId,
        jobId: job.id || null,
        printWithInvoice: false,
        ..._bridgedProvenance(photo) // TASK-9958
      });
      added++;
    });
  }
  return added;
}

/** "Removed 2 permits and 1 photo from another job" — the S1 self-heal toast. */
function _staleBridgedDocsToast(dropped, persisted, couldPersist) {
  const p = dropped.filter(d => d.source === 'job_permit').length;
  const ph = dropped.filter(d => d.source === 'job_photo').length;
  const parts = [];
  if (p) parts.push(`${p} permit${p !== 1 ? 's' : ''}`);
  if (ph) parts.push(`${ph} photo${ph !== 1 ? 's' : ''}`);
  const what = parts.join(' and ') || `${dropped.length} document${dropped.length !== 1 ? 's' : ''}`;
  let tail = '';
  if (!couldPersist) tail = ' — display only, this ticket is locked';
  else if (!persisted) tail = ' — could not save the cleanup, Save this ticket to keep it';
  return `Removed ${what} that belong to another job${tail}.`;
}

// ═══ TASK-9355 S2 — a job change re-derives EVERYTHING job-derived on the ticket. ═══
//
// Twins: public/src/views/billing.js (this) · functions/src/services/ticketJobChange.js
// (server, used by groupTicketsIntoProject). tests/task9355-s2-apply-job-change.unit.cjs
// runs both on the same fixtures and fails if they diverge. Keep them in lockstep.
//
// Five writers re-point a ticket's jobId — updateReviewJob (review dropdown),
// moveSelectedTicketsToJob (bulk), onTicketDrop (drag onto a job),
// onTicketDropNewJob (drag to a new job) and the server callable — and every
// one of them used to write jobId/jobName/jobTitle ONLY. Chris (2026-09-23):
// the office ticket-move is the OFFICIAL correction for a wrong-job clock-in,
// so it has to carry everything. All five now call this ONE helper.
//
// RULES (COD 2026-09-23 — Skeptic vs UX resolved, do not re-litigate):
//   • bridged permits/photos: drop the copies the NEW job does not own (id
//     membership via _partitionStaleBridgedDocs, never path-parsing)
//   • fastTrax follows the job outright (jobNotes is no longer copied — TASK-9646 S4a)
//   • PO / vendor / well name / well # / AFE / location: a ticket value that
//     still EQUALS the old job's (or is empty) was INHERITED → follows the new
//     job silently (null when the new job has none); a value that DIFFERS was
//     HAND-TYPED → kept and listed in ONE post-move summary. No per-field
//     modals — drag and bulk cannot have them.
//   • originalJobId is never touched (duplicate detection keys off it)
//   • ticket LINES are not touched here; the PERMIT-line flag is S3 and is
//     surfaced, never auto-deleted (money)
// Pure: no DOM, no Firestore. Callers own eligibility and the write.
const _JOB_DERIVED_FIELDS = Object.freeze([
  Object.freeze({ field: 'poNumber', label: 'PO', jobKeys: ['poNumber'] }),
  Object.freeze({ field: 'vendorNumber', label: 'Vendor #', jobKeys: ['vendorNumber'] }),
  Object.freeze({ field: 'wellName', label: 'Well name', jobKeys: ['wellName'] }),
  Object.freeze({ field: 'wellNumber', label: 'Well #', jobKeys: ['wellNumber'] }),
  Object.freeze({ field: 'afeNumber', label: 'AFE', jobKeys: ['afeNumber'] }),
  Object.freeze({ field: 'location', label: 'Location', jobKeys: ['location', 'address'] }),
  // TASK-9390 — the job type LABEL. Resolved WITHOUT the tenant list (the server
  // twin has none): see _jobTypeFieldValue.
  Object.freeze({ field: 'jobType', label: 'Type', jobKeys: ['typeLabel', 'type'], jobType: true })
]);
// TASK-9390 — a job's type LABEL for the job-change rule: '' for the default type
// (tickets never snapshot "Job"); else the tenant list's CURRENT label for the
// slug when a list is in hand (a renamed type must compare as its new name, the
// name a freshly created ticket snapshots — COD Code Reviewer); else the
// `typeLabel` saveJob stamps; else a default slug's label; else legacy free text
// as-is. Twin of ticketJobChange.js jobTypeFieldValue.
const _DEFAULT_JOB_TYPE_LABELS = Object.freeze({ job: 'Job', shop_day: 'Shop Day', meeting: 'Meeting' });
function _jobTypeFieldValue(job, typeList) {
  const raw = job && job.type != null ? String(job.type).trim() : '';
  if (raw === '' || raw.toLowerCase() === 'job') return '';
  if (Array.isArray(typeList)) {
    const exact = typeList.find(t => t && String(t.value) === String(job.type));
    const hit = exact || typeList.find(t => t && String(t.value == null ? '' : t.value).trim().toLowerCase() === raw.toLowerCase());
    const hitLabel = hit && hit.label != null ? String(hit.label).replace(/\s+/g, ' ').trim().slice(0, 40) : '';
    if (hitLabel) return hitLabel;
  }
  const stamped = job.typeLabel != null ? String(job.typeLabel).trim() : '';
  if (stamped) return stamped;
  return _DEFAULT_JOB_TYPE_LABELS[raw.toLowerCase()] || raw;
}
// Same edge-whitespace class as utils/poField.js normalizePoForSave — NBSP and
// zero-width chars are not stripped by .trim(), and an invisible difference is a
// second PO. Written values are edge-trimmed only; internal spacing is kept.
const _FIELD_EDGE_WS_RE = /^[\s  -​﻿]+|[\s  -​﻿]+$/g;
function _edgeTrimField(v) {
  return String(v == null ? '' : v).replace(_FIELD_EDGE_WS_RE, '');
}
/** Comparison key: case + whitespace folded; punctuation stays significant (PO-1 ≠ PO1). */
function _fieldCmpKey(v) {
  return String(v == null ? '' : v).normalize('NFKC').toLowerCase().replace(/[\s  -​﻿]+/g, ' ').trim();
}
/** The job's value for a derived field ('' when it has none). location → job.location, then job.address. */
// TASK-9390 (COD round 2) — the job TYPE is judged by its CODE, not its label.
// A ticket snapshots both `jobType` (label) and `jobTypeCode` (the job's slug)
// at creation. A later rename changes the label, so comparing the ticket's
// snapshotted LABEL against the old job's CURRENT label called a renamed,
// inherited type 'hand-kept'. Rule:
//   ticket has a code  → inherited iff it equals the old job's code
//   legacy (label only) → inherited iff the label matches the old job's type
//                         label at ANY point we can see: the tenant list's
//                         current label, the job's own typeLabel stamp, a
//                         default slug's label, or its legacy free text
//   ticket empty        → inherited (nothing to keep)
// Inherited → both follow the new job (cleared when it has the default type).
// Twin of functions/src/services/ticketJobChange.js applyJobTypeChange — keep them identical.
function _jobTypeCodeOf(job) {
  const raw = job && job.type != null ? String(job.type).trim() : '';
  return (raw === '' || raw.toLowerCase() === 'job') ? '' : String(job.type);
}
function _jobTypeLabelsSeen(job, typeList) {
  const out = [];
  if (!job) return out;
  const raw = job.type != null ? String(job.type).trim() : '';
  if (raw === '' || raw.toLowerCase() === 'job') return out;
  out.push(_jobTypeFieldValue(job, typeList));
  if (job.typeLabel != null) out.push(String(job.typeLabel));
  out.push(_DEFAULT_JOB_TYPE_LABELS[raw.toLowerCase()] || '');
  out.push(raw);
  return out.map(_fieldCmpKey).filter((k) => k !== '');
}
function _applyJobTypeChange(t, oldJob, nj, typeList, updates, summary) {
  const label = 'Type';
  const ticketVal = t.jobType == null ? '' : String(t.jobType);
  const ticketCode = t.jobTypeCode == null ? '' : String(t.jobTypeCode);
  const newVal = nj ? _jobTypeFieldValue(nj, typeList) : '';
  const newCode = _jobTypeCodeOf(nj);
  const tKey = _fieldCmpKey(ticketVal);
  const nKey = _fieldCmpKey(newVal);
  const tCode = _fieldCmpKey(ticketCode);
  const nCode = _fieldCmpKey(newCode);
  if (tKey === nKey && (tCode === '' || tCode === nCode)) return; // already the new job's type
  let inherited;
  if (tKey === '' && tCode === '') inherited = true;
  else if (!oldJob) inherited = false; // old job unknown: only an empty ticket counts as inherited
  else if (tCode !== '') inherited = tCode === _fieldCmpKey(_jobTypeCodeOf(oldJob));
  else inherited = _jobTypeLabelsSeen(oldJob, typeList).includes(tKey);
  if (inherited) {
    if (nKey === '') {
      updates.jobType = null;
      if (tCode !== '') updates.jobTypeCode = null;
      if (tKey !== '') summary.cleared.push({ field: 'jobType', label, from: ticketVal });
    } else {
      updates.jobType = _edgeTrimField(newVal);
      updates.jobTypeCode = newCode || null;
      if (tKey !== nKey) summary.followed.push({ field: 'jobType', label, from: ticketVal, to: _edgeTrimField(newVal) });
    }
  } else if (nKey !== '' && tKey !== nKey) {
    summary.kept.push({ field: 'jobType', label, ticketValue: ticketVal, jobValue: _edgeTrimField(newVal) });
  }
}

function _jobFieldValue(job, spec, typeList) {
  if (!job) return '';
  if (spec.jobType) return _jobTypeFieldValue(job, typeList);
  for (const k of spec.jobKeys) {
    const v = job[k];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
}

/**
 * Re-derive every job-derived field of `ticket` for `newJob`.
 * @param ticket  the ticket as stored (documents[], jobNotes, poNumber, …)
 * @param oldJob  the job it is leaving (null = unknown / none)
 * @param newJob  the job it moves to (null = unassigned, unless opts.newJobId)
 * @param opts.newJobId  a target id whose job doc is NOT in hand — jobId is
 *                       written, nothing else is judged (summary.unknownJob)
 * @param opts.skipPo    the caller ran its own PO decision (bulk move) — PO untouched
 * @returns { updates, summary } — `updates.documents` only when a copy dropped;
 *          cleared inherited fields are written as null.
 */
function _applyJobChangeToTicket(ticket, oldJob, newJob, opts) {
  const o = opts || {};
  // TASK-9390 — the tenant's job type list (opts.jobTypes wins; else the live
  // settings in the browser; null in a bare vm, where the server twin gets it
  // passed explicitly by groupTicketsIntoProject).
  const _jtList = Array.isArray(o.jobTypes) ? o.jobTypes
    : ((typeof window !== 'undefined' && window.JobTypes) ? window.JobTypes.list(window.AppState?.settings || {}) : null);
  const t = ticket || {};
  const nj = newJob || null;
  const newJobId = nj ? String(nj.id || '') : String(o.newJobId || '');
  const newJobName = nj ? String(nj.title || nj.name || '') : '';
  const unknownJob = !nj && !!newJobId;
  const updates = { jobId: newJobId, jobName: newJobName, jobTitle: newJobName };
  const summary = {
    newJobId, newJobName,
    oldJobId: t.jobId || null,
    oldJobName: oldJob ? String(oldJob.title || oldJob.name || '') : String(t.jobName || t.jobTitle || ''),
    unknownJob,
    droppedPermits: 0, droppedPhotos: 0,
    followed: [], cleared: [], kept: [],
    notesChanged: false, fastTraxChanged: false,
    permitLinesFlagged: []
  };
  if (unknownJob) return { updates, summary };

  const part = _partitionStaleBridgedDocs(Array.isArray(t.documents) ? t.documents : [], nj || _NO_JOB_FOR_BRIDGE);
  if (part.dropped.length) {
    updates.documents = part.keep;
    summary.droppedPermits = part.dropped.filter(d => d.source === 'job_permit').length;
    summary.droppedPhotos = part.dropped.filter(d => d.source === 'job_photo').length;
  }

  // TASK-9646 S4a — jobNotes is no longer a ticket field (twin of
  // functions/src/services/ticketJobChange.js); notesChanged stays false.
  const newFastTrax = !!(nj && nj.permitSettings && nj.permitSettings.usedFastTrax);
  if (!!t.fastTrax !== newFastTrax) { updates.fastTrax = newFastTrax; summary.fastTraxChanged = true; }

  for (const spec of _JOB_DERIVED_FIELDS) {
    if (spec.field === 'poNumber' && o.skipPo) continue;
    if (spec.jobType) continue; // TASK-9390 — judged by CODE, below
    const ticketVal = t[spec.field] == null ? '' : String(t[spec.field]);
    const newVal = _jobFieldValue(nj, spec, _jtList);
    const tKey = _fieldCmpKey(ticketVal);
    const nKey = _fieldCmpKey(newVal);
    if (tKey === nKey) continue;
    const oldKey = oldJob ? _fieldCmpKey(_jobFieldValue(oldJob, spec, _jtList)) : null;
    const inherited = tKey === '' || (oldKey !== null && tKey === oldKey);
    if (inherited) {
      if (nKey === '') {
        updates[spec.field] = null;
        summary.cleared.push({ field: spec.field, label: spec.label, from: ticketVal });
      } else {
        updates[spec.field] = _edgeTrimField(newVal);
        summary.followed.push({ field: spec.field, label: spec.label, from: ticketVal, to: _edgeTrimField(newVal) });
      }
    } else if (nKey !== '') {
      summary.kept.push({ field: spec.field, label: spec.label, ticketValue: ticketVal, jobValue: _edgeTrimField(newVal) });
    }
  }
  _applyJobTypeChange(t, oldJob, nj, _jtList, updates, summary);
  return { updates, summary };
}

/** ONE post-move summary line: what moved, what dropped, what followed, what was kept. */
function _jobChangeSummaryText(summary) {
  const s = summary || {};
  const head = s.newJobName ? `Moved to "${s.newJobName}"` : 'Unassigned from job';
  const bits = [];
  if (s.droppedPermits || s.droppedPhotos) {
    const w = [];
    if (s.droppedPermits) w.push(`${s.droppedPermits} permit${s.droppedPermits !== 1 ? 's' : ''}`);
    if (s.droppedPhotos) w.push(`${s.droppedPhotos} photo${s.droppedPhotos !== 1 ? 's' : ''}`);
    bits.push(`${w.join(' + ')} from ${s.oldJobName ? `"${s.oldJobName}"` : 'the old job'} removed`);
  }
  for (const f of (s.followed || [])) bits.push(`${f.label} → ${f.to}`);
  for (const f of (s.cleared || [])) bits.push(`${f.label} cleared (was ${f.from})`);
  for (const k of (s.kept || [])) bits.push(`${k.label} kept as ${k.ticketValue} (job has ${k.jobValue})`);
  for (const p of (s.permitLinesFlagged || [])) bits.push(`permit line $${(Number(p.lineAmount) || 0).toFixed(2)} was priced from "${p.fromJobName || 'the old job'}" — review it`);
  if (s.unknownJob) bits.push('job details not loaded — open the ticket to check permits and PO');
  // TASK-9755 (#100852) — the ticket keeps the crew's hours; their timecards
  // stay on the job they clocked to.
  if (s.crewOnOtherJob && s.crewOnOtherJob.length) {
    const c = s.crewOnOtherJob;
    bits.push(`${_crewNamesText(c)} (${_crewHoursText(c)} h) ${c.length === 1 ? 'stays' : 'stay'} clocked to ${_crewJobsText(c, s.oldJobName)} (timecards don't move)`);
  }
  return bits.length ? `${head} — ${bits.join(' · ')}.` : `${head}.`;
}

/** Roll many per-ticket summaries into the counts a bulk toast reports. */
function _aggregateJobChangeSummaries(summaries) {
  const agg = { tickets: 0, droppedDocs: 0, ticketsWithDroppedDocs: 0, keptFields: 0, ticketsWithKeptFields: 0, permitLinesFlagged: 0 };
  for (const s of (summaries || [])) {
    if (!s) continue;
    agg.tickets++;
    const dropped = (s.droppedPermits || 0) + (s.droppedPhotos || 0);
    if (dropped) { agg.droppedDocs += dropped; agg.ticketsWithDroppedDocs++; }
    if (s.kept && s.kept.length) { agg.keptFields += s.kept.length; agg.ticketsWithKeptFields++; }
    if (s.permitLinesFlagged && s.permitLinesFlagged.length) agg.permitLinesFlagged += s.permitLinesFlagged.length;
  }
  return agg;
}

/** The bulk-move toast tail for an aggregate (empty string when nothing to say). */
function _jobChangeAggregateTail(agg) {
  const a = agg || {};
  const bits = [];
  if (a.droppedDocs) bits.push(`${a.droppedDocs} permit/photo cop${a.droppedDocs !== 1 ? 'ies' : 'y'} from old jobs removed.`);
  if (a.ticketsWithKeptFields) bits.push(`${a.ticketsWithKeptFields} ticket${a.ticketsWithKeptFields !== 1 ? 's' : ''} kept a hand-typed PO/well/location that differs from the job — open ${a.ticketsWithKeptFields !== 1 ? 'them' : 'it'} to check.`);
  if (a.permitLinesFlagged) bits.push(`${a.permitLinesFlagged} permit line${a.permitLinesFlagged !== 1 ? 's' : ''} priced from an old job — review before invoicing.`);
  return bits.length ? ' ' + bits.join(' ') : '';
}

// ═══ TASK-9355 S3 — a PERMIT line priced from a job the ticket is LEAVING. ═══
//
// clock.js _findOrCreateTicketInner writes the job's AI-parsed permit cost onto
// the new ticket as a PERMIT line — `source:'ai_parsed'`, `jobId` = the job it
// was parsed from, `lineAmount` = job.permitParsedCost. Nothing else stamps a
// jobId on a PERMIT line (verified: the review "Add Load Permit" row carries
// lineItemId, attachPermitToTicket carries jobPermitId). So after a job change
// that line is a BILLED dollar figure for a permit this ticket no longer
// carries — Go Tong's permit cost on the Exxon invoice.
//
// This is money, so it is FLAGGED, never auto-deleted: the biller may well want
// to keep it (the permit was bought, someone pays), and a silent delete is the
// archive-trap shape (TASK-1345) — hiding money without a human ruling on it.
// Rule: a PERMIT line is "from another job" iff
//   (a) it carries a jobId and that jobId !== the ticket's (new) jobId, or
//   (b) it has no jobId, is `ai_parsed`, and its amount equals the OLD job's
//       permitParsedCost (> 0) — the legacy shape from before jobId was stamped.
// Returns the flagged lines as summary entries; the review path ALSO marks the
// in-memory row (`_fromOtherJob`, underscore = never persisted) so the Load
// Permits table shows it, and the marking is repeated on every open so a
// ticket moved by drag / bulk / the callable shows the flag when next reviewed.
function _permitLinesFromOtherJob(lines, oldJob, newJobId, jobNameById) {
  const out = [];
  const nid = newJobId || '';
  const oldCost = Number(oldJob?.permitParsedCost) || 0;
  for (const l of (lines || [])) {
    if (!l || l.type !== 'PERMIT') continue;
    const amt = Number(l.lineAmount) || 0;
    let matched = false;
    let fromJobId = null;
    if (l.jobId) {
      if (l.jobId !== nid) { matched = true; fromJobId = l.jobId; }               // (a)
    } else if (l.source === 'ai_parsed' && oldCost > 0
      // TASK-9436 — a marked-up AI line's lineAmount is the fee PLUS markup; the
      // raw fee lives in baseCost. Match either, or the flag goes blind.
      && (Math.abs(amt - oldCost) < 0.005 || Math.abs((Number(l.baseCost) || 0) - oldCost) < 0.005)) {
      matched = true; fromJobId = (oldJob && oldJob.id) || null;                 // (b)
    }
    if (!matched) continue;
    let fromJobName = '';
    if (fromJobId && typeof jobNameById === 'function') fromJobName = jobNameById(fromJobId) || '';
    if (!fromJobName && oldJob && (fromJobId === null || fromJobId === oldJob.id)) fromJobName = String(oldJob.title || oldJob.name || '');
    out.push({ lineId: l.id || null, description: l.description || 'Load permit', lineAmount: amt, fromJobId, fromJobName });
  }
  return out;
}

/** Review modal: mark the in-memory PERMIT rows (never persisted) and return the summary entries. */
function _flagPermitLinesFromOtherJob(lines, oldJob, newJobId) {
  const nameOf = (id) => { const j = BillingState.jobs.find(x => x.id === id); return j ? String(j.title || j.name || '') : ''; };
  const flagged = _permitLinesFromOtherJob(lines, oldJob, newJobId, nameOf);
  for (const l of (lines || [])) {
    if (!l || l.type !== 'PERMIT') continue;
    const f = flagged.find(x => (x.lineId && x.lineId === l.id) || (!x.lineId && x.lineAmount === (Number(l.lineAmount) || 0) && x.description === (l.description || 'Load permit')));
    if (f) l._fromOtherJob = { fromJobId: f.fromJobId, fromJobName: f.fromJobName };
    else delete l._fromOtherJob;
  }
  return flagged;
}

/** Drag / bulk paths: one bounded read of the ticket's PERMIT lines. Best-effort — a failed read flags nothing. */
async function _fetchPermitLinesFromOtherJob(tenant, ticketId, oldJob, newJobId) {
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'), where('type', '==', 'PERMIT')));
    const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const nameOf = (id) => { const j = BillingState.jobs.find(x => x.id === id); return j ? String(j.title || j.name || '') : ''; };
    return _permitLinesFromOtherJob(lines, oldJob, newJobId, nameOf);
  } catch (err) {
    console.warn('[Billing] TASK-9355 permit-line check failed (nothing flagged):', err?.message || err);
    return [];
  }
}

function removeReviewDoc(docId) {
  // Check pending files first
  const pendingIdx = ReviewState.pendingDocFiles.findIndex(p => p.id === docId);
  if (pendingIdx !== -1) {
    const pending = ReviewState.pendingDocFiles[pendingIdx];
    if (pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    ReviewState.pendingDocFiles.splice(pendingIdx, 1);
  } else {
    // Find in existing documents
    const docIdx = ReviewState.documents.findIndex(d => d.id === docId);
    if (docIdx !== -1) {
      const removed = ReviewState.documents[docIdx];
      // Only track for Storage cleanup if it was an upload this ticket owns —
      // never a bridged job permit / job photo / clock photo (TASK-9355 S0).
      if (!_isBridgedReviewDoc(removed)) {
        ReviewState.deletedDocIds.push(removed.id);
      }
      // Bridged job permits live on the job — record the hide so the bridge in
      // openReviewTicket doesn't re-add them next time the modal opens.
      if (removed.source === 'job_permit' && removed.jobPermitId) {
        if (!ReviewState.hiddenJobPermitIds.includes(removed.jobPermitId)) {
          ReviewState.hiddenJobPermitIds.push(removed.jobPermitId);
        }
      }
      ReviewState.documents.splice(docIdx, 1);
    }
  }

  ReviewState.dirty = true;
  const section = document.getElementById('review-documents-section');
  if (section) {
    section.outerHTML = renderReviewDocumentsSection();
  }
}

// ═══ TASK-9868 — who uploaded a ticket document, and what it is. ═══
// Old rows (no uploadedBy / no docType) render exactly as before: no line, no
// chip, no print label. Nothing is backfilled or guessed.
function _ticketDocFmtTime(v) {
  const dt = v?.toDate ? v.toDate() : new Date(v);
  if (!dt || isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
/** "Uploaded by Sean Wood · Oct 14, 1:49 PM" under the file name; '' for old rows. */
function _ticketDocUploadedHtml(d) {
  const line = ticketDocUploadedLine(d, _ticketDocFmtTime);
  return line ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:1px;">${escapeHtml(line)}</div>` : '';
}
/** Read-only type chip ('' when the row has no type). */
function _ticketDocTypeChipHtml(d) {
  const label = ticketDocTypeLabel(d?.docType);
  return label ? `<span class="badge badge-other" style="margin-left:6px;">${escapeHtml(label)}</span>` : '';
}
/** Review-modal type picker. A row with no type gets a blank first option so
 *  merely opening and saving never stamps "Other" onto an old document. */
function _reviewDocTypeSelectHtml(d) {
  const has = d.docType != null && d.docType !== '';
  return `<select aria-label="Document type" onchange="window.BillingModule.setReviewDocType('${window.escJsAttr(String(d.id))}', this.value)" style="font-size:0.7rem;padding:2px 4px;border:1px solid var(--border-light);border-radius:4px;background:var(--bg-surface, #fff);color:var(--text-primary);">
    ${has ? '' : '<option value="" selected>Type…</option>'}${ticketDocTypeOptionsHtml(has ? d.docType : null)}
  </select>`;
}
/** Printed above an attachment on the invoice packet: "BOL — sean 1.jpeg". */
function _ticketDocPrintName(d, esc) {
  const label = ticketDocPrintLabel(d?.docType);
  const name = d?.name || 'Document';
  return label ? `${esc(label)} — ${esc(name)}` : esc(name);
}
/** Invoice-packet thumbnail: the old <img> untouched, plus a printed type
 *  caption ("BOL") under it when the row has a printable type. */
function _ticketDocPrintGridImg(d, size, imgHtml) {
  const label = ticketDocPrintLabel(d?.docType);
  if (!label) return imgHtml;
  return `<div style="display:inline-flex;flex-direction:column;align-items:center;width:${size}px;">${imgHtml}<div style="font-size:10px;font-weight:600;color:#0F172A;margin-top:2px;">${invoiceEsc(label)}</div></div>`;
}
/** The office uploader stamp — from the signed-in session, never typed. */
function _ticketDocOfficeUploader() {
  const ma = window.AppState?.memberAccess || null;
  const u = window.AppState?.user || null;
  const cu = auth?.currentUser || null;
  let name = ma?.displayName || '';
  if (!name && ma?.personId) {
    const p = (BillingState.people || []).find(x => x.id === ma.personId);
    if (p?.name) name = p.name;
  }
  if (!name) name = cu?.displayName || u?.displayName || cu?.email || u?.email || '';
  return buildTicketDocUploader({ uid: cu?.uid || u?.uid || '', name, personId: ma?.personId || '' });
}

function setReviewDocType(docId, value) {
  const d = ReviewState.documents.find(x => x.id === docId)
    || ReviewState.pendingDocFiles.find(p => p.id === docId);
  if (!d) return;
  if (value === '' || value == null) return; // "Type…" placeholder — leave the row as it was
  d.docType = normalizeTicketDocType(value);
  ReviewState.dirty = true;
}

function toggleDocPrintWithInvoice(docId, checked) {
  const doc = ReviewState.documents.find(d => d.id === docId)
    || ReviewState.pendingDocFiles.find(p => p.id === docId);
  if (doc) {
    doc.printWithInvoice = checked;
    ReviewState.dirty = true;
  }
}

async function uploadReviewDocFiles(tenantId, ticketId) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const basePath = `tenants/${tenantId}/photos/tickets/${year}/${month}/${ticketId}/documents`;
  // TASK-9868 — who is uploading, from the signed-in session (null = no uid:
  // store nothing rather than a guess).
  const uploadedBy = _ticketDocOfficeUploader();
  // TASK-9958 — rule-pinned uploader on the Storage object itself.
  const _meta = fileUploadCustomMetadata(uploadedBy?.uid, 'ticket_edit');

  const uploaded = [];

  for (const pending of ReviewState.pendingDocFiles) {
    const ext = pending.isPdf ? 'pdf' : 'jpg';
    const contentType = pending.isPdf ? 'application/pdf' : 'image/jpeg';
    const fullPath = `${basePath}/${pending.id}_full.${ext}`;

    const fullRef = ref(storage, fullPath);
    await uploadBytes(fullRef, pending.blob, { contentType, customMetadata: _meta });
    const fullUrl = await getDownloadURL(fullRef);

    let thumbUrl = null;
    let thumbPath = null;
    if (!pending.isPdf && pending.thumbBlob) {
      thumbPath = `${basePath}/${pending.id}_thumb.jpg`;
      const thumbRef = ref(storage, thumbPath);
      await uploadBytes(thumbRef, pending.thumbBlob, { contentType: 'image/jpeg', customMetadata: _meta });
      thumbUrl = await getDownloadURL(thumbRef);
    }

    uploaded.push({
      id: pending.id,
      fullUrl,
      thumbUrl,
      fullPath,
      thumbPath,
      name: pending.name,
      mimeType: pending.mimeType,
      isPdf: pending.isPdf,
      sizeBytes: pending.blob.size,
      uploadedAt: new Date().toISOString(),
      source: 'upload',
      jobPermitId: null,
      printWithInvoice: pending.printWithInvoice !== false,
      // TASK-9868 — type picked in the review modal (default Other) + uploader.
      docType: normalizeTicketDocType(pending.docType),
      ...(uploadedBy ? { uploadedBy } : {}),
      uploadedVia: 'ticket_edit' // TASK-9958 — how it got here
    });
  }

  ReviewState.pendingDocFiles = [];
  return uploaded;
}

function buildItemCodeOptions(category) {
  return ReviewState.itemCodes
    .filter(ic => !category || ic.category === category || !ic.category)
    .map(ic => `<option value="${ic.id}">${escapeHtml(ic.code)} - ${escapeHtml(ic.description || '')}</option>`)
    .join('');
}

function buildEquipmentRateOptions(line) {
  const lineItems = ReviewState.contractLineItems || [];
  if (lineItems.length === 0) return '';
  const kind = line.assetType || 'asset';
  const vType = line.vehicleType || '';
  // Find all active line items matching this equipment's kind + type
  const matches = lineItems.filter(li => {
    if (!li.active && li.active !== undefined) return false;
    if (li.appliesTo?.kind !== kind) return false;
    // Match: same type, or line item has no type (wildcard)
    if (li.appliesTo?.type && vType && li.appliesTo.type !== vType) return false;
    if (li.rate == null || li.rate <= 0) return false;
    return true;
  });
  if (matches.length === 0) return '';
  const selectedId = line.lineItemId || '';
  return matches.map(li => {
    const desc = li.description || li.itemCode || 'Unnamed';
    const unit = escapeHtml(formatUnitLabel(li.rateUnit));
    return `<option value="${escapeHtml(li.id)}" ${li.id === selectedId ? 'selected' : ''}>${escapeHtml(desc)} — $${(li.rate || 0).toFixed(2)}${unit}</option>`;
  }).join('');
}

function onReviewEquipmentRateSelect(localId, lineItemId) {
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;
  const li = (ReviewState.contractLineItems || []).find(l => l.id === lineItemId);
  if (li) {
    line.rate = li.rate;
    line.rateUnit = li.rateUnit || 'Hourly';
    // TASK-9489 — Always / Never / Company default, one decision.
    line.roundToHalfHour = _billingResolveEquipRound(li.roundToHalfHour, _billingCompanyRoundsEquipment());
    line.revenueCategory = li.revenueCategory || '';
    line.includeOnInvoice = li.includeOnInvoice !== false;
    line.lineItemId = li.id;
    line.lineItemDescription = li.description || li.itemCode || '';
    // TASK-4673 (COD r1 Code Reviewer / Skeptic / Domain; r2 Independent / Edge)
    // — a money editor's explicit pick IS the rounding decision. A different
    // lineItemId already retires the server stamp; a SAME-item re-pick (price
    // book flipped Always -> Never, then re-picked to settle a dispute) would
    // leave a still-bound stamp overruling it. So the pick RE-STAMPS the line
    // with the biller's own decision -- never just clears it: a cleared stamp
    // hands the line back to the member-writable flag (r2), and no server pass
    // re-stamps a priced line. Only for a money editor: firestore.rules lets
    // canEditMoney write the stamp and refuses it on every other arm, so a
    // non-biller save must never carry this key. saveReviewTicket writes it.
    if (typeof window.canEditMoney === 'function' && window.canEditMoney() === true) {
      if (!('_roundingProvenanceBefore' in line)) { // first pick this session = the stored value (audit "from")
        const _o = line.roundingProvenance;
        line._roundingProvenanceBefore = (_o && typeof _o === 'object')
          ? { round: _o.round === true, lineItemId: _o.lineItemId || null, by: _o.by || null } : null;
      }
      line.roundingProvenance = { by: 'human:reviewRatePick', round: line.roundToHalfHour === true, lineItemId: li.id };
      line._reviewRoundingStamp = true;
    }
    const _isPerUnit = !isTimeBasedUnit(line.rateUnit);
    // Clear the opposite field when switching modes so the input renders blank
    if (_isPerUnit) {
      line.quantity = null;  // blank qty — user fills in
    } else if (line.fromTime && line.toTime) {
      const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
      const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
      const rawHrs = Math.max(0, (to - from) / 3600000);
      if (line.roundToHalfHour) {
        // Apply half-hour rounding if line item has roundToHalfHour and we have raw times
        line.hours = roundHalfHour(rawHrs);
      } else if (!(line.hoursManual === true)
          && Math.abs((Number(line.hours) || 0) - roundHalfHour(rawHrs)) < 0.005
          && Math.abs(roundHalfHour(rawHrs) - rawHrs) >= 0.005) {
        // TASK-4673 (COD r2) — a re-pick to a NON-rounding item un-rounds hours
        // that were the window's round-up; typed hours (hoursManual) and any
        // other hand value are left alone.
        line.hours = parseFloat(rawHrs.toFixed(2));
      }
    }
    line.lineAmount = _isPerUnit
      ? parseFloat(((line.quantity || 0) * li.rate).toFixed(2))
      : parseFloat(((line.hours || 0) * li.rate).toFixed(2));
    ReviewState.dirty = true;
  }
  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  renderReviewTotals();
}

function buildEquipmentOptions(selectedAssetType, selectedAssetId) {
  const display = (item) => window.getUnitDisplay ? escapeHtml(window.getUnitDisplay(item)) : escapeHtml(item.unit || item.name || item.id);
  let opts = '';
  if (ReviewState.trucks.length > 0) {
    opts += `<optgroup label="Vehicles">`;
    opts += ReviewState.trucks.map(t => `<option value="truck_${t.id}" ${selectedAssetType === 'truck' && selectedAssetId === t.id ? 'selected' : ''}>${display(t)}</option>`).join('');
    opts += `</optgroup>`;
  }
  if (ReviewState.trailers.length > 0) {
    opts += `<optgroup label="Trailers">`;
    opts += ReviewState.trailers.map(t => `<option value="trailer_${t.id}" ${selectedAssetType === 'trailer' && selectedAssetId === t.id ? 'selected' : ''}>${display(t)}</option>`).join('');
    opts += `</optgroup>`;
  }
  if (ReviewState.assets.length > 0) {
    opts += `<optgroup label="Assets">`;
    // TASK-9275 — an asset already below zero says so in the picker itself. An
    // <option> cannot carry styling that every browser honours, so the count goes
    // in the text where it cannot be missed or silently dropped.
    opts += ReviewState.assets.map(a => `<option value="asset_${a.id}" ${selectedAssetType === 'asset' && selectedAssetId === a.id ? 'selected' : ''}>${escapeHtml(a.partNumber || a.name || a.id)}${reviewAssetStockIsNegative(a) ? escapeHtml(` ⚠ ${Number(a.quantity || 0)} on hand`) : ''}</option>`).join('');
    opts += `</optgroup>`;
  }
  return opts;
}

// ============================================
// REVIEW TICKET — TIME EDITING
// ============================================
// clock-dst-all — the review time boxes show COMPANY time (window.TenantLocalTime),
// never the biller's browser zone; updateReviewTime reads them back the same way.
function toDatetimeLocal(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '';
  return window.TenantLocalTime.formatLocal(d);
}

// clock-dst-all — read ONE typed review time as an instant in the company zone.
// Repeated fall-back hour → asks which one (with the hours each gives against
// `partner`); nonexistent time / unreadable zone → refused with a toast.
// Resolves the Date, or null (refused / cancelled — caller changes nothing).
async function _reviewReadTypedTime(value, label, partner, isEnd) {
  const T = window.TenantLocalTime;
  const partnerMs = partner ? (partner.toMillis ? partner.toMillis() : (partner.toDate ? partner.toDate().getTime() : new Date(partner).getTime())) : NaN;
  const r = await T.resolveAll([{ str: value, label, optional: false }], {
    describe: (_it, ms) => {
      if (!Number.isFinite(partnerMs)) return '';
      const h = isEnd ? (ms - partnerMs) / 3600000 : (partnerMs - ms) / 3600000;
      return h >= 0 ? ` — ${T.hm(h)}` : '';
    },
  });
  if (!r.ok) { window.showToast?.(r.message, 'error'); return null; }
  return new Date(r.ms[0]);
}

function toggleReviewTimeEdit(prefix, localId) {
  const display = document.getElementById(`${prefix}-display-${localId}`);
  const input = document.getElementById(`${prefix}-input-${localId}`);
  if (!display || !input) return;
  const isHidden = input.classList.contains('hidden');
  display.classList.toggle('hidden', isHidden);
  input.classList.toggle('hidden', !isHidden);
  if (isHidden) input.focus();
}

// TASK-1646 — ONE place derives a line's hours + lineAmount from an explicit
// fromTime/toTime pair. updateReviewTime, the equipment load self-heal, and the
// Add Equipment time-mirror all route through it (the math previously lived in
// three near-copies; a fourth was about to ship). Rounding rules ride along:
// shouldRoundLine covers both the line flag and the contract line item.
// `skipIfZero` preserves the self-heal's old no-op on a zero-width window (it
// never clobbered a 0-hours line that carried a hand-set lineAmount).
// TASK-1646 slice 3 — when a picker has NO real value, seed its DEFAULT to the
// ticket's date at a neutral 8am so the biller only touches the time (the date
// half is what Chris called painful). State stays null — the pill still shows
// "-", HOURS NEEDED still fires, and save serializes ReviewState (never DOM
// input values), so an untouched seed can never reach Firestore.
function _seedDatetimeLocal() {
  const td = ReviewState.ticket?.date;
  if (!td) return '';
  const d = td?.toDate ? td.toDate() : new Date(td);
  if (isNaN(d.getTime())) return '';
  // clock-dst-all — the ticket's day in the COMPANY zone, not the browser's.
  const key = window.TenantLocalTime.dateKey(d);
  return key ? `${key}T08:00` : '';
}

function _recalcLineFromTimes(line, opts) {
  if (!line.fromTime || !line.toTime || !isTimeBasedUnit(line.rateUnit || 'Hourly')) return false;
  const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
  const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
  // COD (Code Reviewer) — a truthy but MALFORMED persisted time would otherwise
  // write NaN hours/amount (the old self-heal's rawHrs>0 guard skipped those).
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return false;
  const rawHrs = Math.max(0, (to - from) / 3600000);
  if (opts?.skipIfZero && rawHrs === 0) return false;
  const _shouldRound = shouldRoundLine(line);
  line.hours = _shouldRound ? roundHalfHour(rawHrs) : parseFloat(rawHrs.toFixed(2));
  line.lineAmount = parseFloat(((line.hours || 0) * (line.rate || 0)).toFixed(2));
  // TASK-4564 — PERSIST the decision. Recomputing rounding on every read is what
  // let a rounded line silently un-round the moment the price-book lookup or the
  // absent flag disagreed with the snapshot. Once a biller touches the times, the
  // line carries its own answer forever and no future reader has to re-derive it.
  // `roundedFrom` is refreshed to the NEW raw hours (a stale one would make the
  // "rounded up from …" marker lie about a window the biller just changed).
  // Neither field is underscore-prefixed, so saveReviewTicket's clean-copy loop
  // writes them both. RATE IS NOT TOUCHED HERE — an EMPLOYEE/crew line ends this
  // function with exactly the rate it arrived with (hours × 0 = $0), because this
  // shop bills the truck and never the person.
  if (_shouldRound) {
    line.roundToHalfHour = true;
    line.roundedFrom = parseFloat(rawHrs.toFixed(2));
  }
  // TASK-1548 (2nd-COD) — datetime-derived hours supersede a "deliberately $0"
  // mark exactly like manually typed hours do (the clear at updateReviewLine).
  // TASK-1405 — but ONLY on a PRICED line. Hours only overrule the mark when they
  // make the line bill money; on a rate-0 line the hours are exactly what makes it
  // a leak, so clearing the mark here would silently revoke the biller's recorded
  // "yes, this really is free" and re-block them with no visible cause.
  if (line.hours > 0 && (Number(line.rate) || 0) > 0 && line.zeroHoursOk === true) line.zeroHoursOk = false;
  return true;
}

async function updateReviewTime(lineType, localId, field, value) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return; // TASK-1167 — read-only tickets don't mutate
  // TASK-9412 (COD round 3) — times are frozen while a Save is in flight: the
  // clock sync already sent the values on screen, so a change now would reach
  // the ticket but not the time clock.
  if (ReviewState._saving === true) { window.showToast?.('Saving — wait a moment, then change the time.', 'info'); _repaintReviewTimeInput(lineType, localId, field); return; }
  const lines = lineType === 'EMPLOYEE' ? ReviewState.employeeLines : ReviewState.equipmentLines;
  const line = lines.find(l => l._localId === localId);
  if (!line) return;

  // clock-dst-all — company zone; a repeated-hour time is asked, never guessed.
  if (!value) return;
  const newDate = await _reviewReadTypedTime(value, field === 'fromTime' ? 'Start' : 'End',
    field === 'fromTime' ? line.toTime : line.fromTime, field !== 'fromTime');
  if (!newDate || isNaN(newDate.getTime())) { _repaintReviewTimeInput(lineType, localId, field); return; }
  // The picker is an await: a Save may have started, or the line may be gone.
  if (ReviewState._saving === true || !lines.includes(line)) { _repaintReviewTimeInput(lineType, localId, field); return; }
  line[field] = Timestamp.fromDate(newDate);
  ReviewState.dirty = true;
  // TASK-1646 slice 2 — the first hand-edit of either time clears the
  // "mirrored" provenance: the value is now measured, not derived.
  if (line._timesMirroredFrom) delete line._timesMirroredFrom;

  // Update the display text
  // TASK-9197 — BOTH pills are repainted, not just the edited one. A cell date
  // marker only exists relative to its PARTNER, so moving the "in" time can put
  // the "out" time on a different calendar day (or take it off one) without the
  // "out" value changing at all. Repainting only the edited pill would leave a
  // stale — and now WRONG — date beside it. `innerHTML` because a crossing cell
  // carries a date <span>; that markup is built entirely from our own toLocale*
  // output plus an escAttr'd title, never from line data.
  const prefix = lineType === 'EMPLOYEE' ? 'review-emp' : 'review-equip';
  const _cells = window.Fmt.shiftCells(line.fromTime, line.toTime);
  [['in', _cells.fromHtml], ['out', _cells.toHtml]].forEach(([_k, _html]) => {
    const el = document.getElementById(`${prefix}-${_k}-display-${localId}`);
    if (!el) return;
    el.innerHTML = _html || '-';
    el.classList.remove('review-time-mirrored');
    el.title = 'Click to edit time';
  });

  // Recalculate hours if both fromTime and toTime exist (only for time-based units)
  if (line.fromTime && line.toTime && isTimeBasedUnit(line.rateUnit)) {
    const from = line.fromTime?.toDate ? line.fromTime.toDate() : new Date(line.fromTime);
    const to = line.toTime?.toDate ? line.toTime.toDate() : new Date(line.toTime);
    if ((to - from) <= 0 && to < from) {
      window.showToast?.('Clock In must be before Clock Out', 'error');
    }
    _recalcLineFromTimes(line);
    line._originalHours = line.hours;

    const hoursEl = document.getElementById(`${prefix}-hours-${localId}`);
    if (hoursEl) hoursEl.value = line.hours.toFixed(2);
    const amountEl = document.getElementById(`${prefix}-amount-${localId}`);
    if (amountEl) amountEl.textContent = '$' + line.lineAmount.toFixed(2);
    // TASK-4564 — this path repaints hours/amount in place instead of re-rendering
    // the section, so the rounding marker has to be repainted with them. Without
    // this it would keep showing the PREVIOUS window's "rounded up from …" next to
    // freshly recalculated hours — a marker that lies is worse than no marker.
    if (lineType === 'EQUIPMENT') {
      const roundEl = document.getElementById(`review-equip-round-${localId}`);
      if (roundEl) roundEl.innerHTML = _roundingMarker(line);
    }
  }

  // TASK-1234 — a time edit changes both hours and the ceiling window
  _refreshHoursWarn(lineType === 'EMPLOYEE' ? 'emp' : 'equip', line);
  renderReviewTotals();
}

// ============================================
// REVIEW TICKET — INLINE EDITING
// ============================================
// TASK-9412 — put a refused (frozen-while-saving) time input back to the line's value.
function _repaintReviewTimeInput(lineType, localId, field) {
  try {
    const lines = lineType === 'EMPLOYEE' ? ReviewState.employeeLines : ReviewState.equipmentLines;
    const line = lines.find(l => l._localId === localId);
    const prefix = lineType === 'EMPLOYEE' ? 'review-emp' : 'review-equip';
    const el = document.getElementById(`${prefix}-${field === 'fromTime' ? 'in' : 'out'}-input-${localId}`);
    if (el && line) el.value = toDatetimeLocal(line[field]) || '';
  } catch (_) { /* display only */ }
}

function updateReviewEmployeeLine(localId, field, value) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return; // TASK-1167 — read-only tickets don't mutate
  const line = ReviewState.employeeLines.find(l => l._localId === localId);
  if (!line) return;

  if (field === 'hours') {
    const newHours = parseFloat(value) || 0;
    line.hours = newHours;
    // If user manually edits hours, update the original baseline too
    line._originalHours = newHours;
    // 🔴 2026-09-22 — a biller who types hours OWNS them, including 0. Without
    // this stamp TASK-9246's "hours: 0 but the clock says otherwise" self-heal
    // re-derives them from fromTime/toTime on the next open AND PERSISTS the
    // reversal, so a zeroed line silently comes back. Measured on TITS from the
    // `ticket_hours_selfhealed` audit rows: #100738's #103 went 0 → 7 on three
    // consecutive opens in five minutes, #100741/#100742 did it to Ethan Young's
    // crew hours, #100715 to Sean Wood's. `zeroHoursOk` is NOT the field for
    // this — that one is the "this line is deliberately non-billable" mark and
    // it also releases the Ready gate, so auto-setting it on a typed 0 would
    // hide a real $0-on-priced-work leak. This records provenance only: who set
    // the number, not what it means.
    line.hoursManual = true;
  }
  if (field === 'rate' && window.AppState?.settings?.billLaborHours === true) return; // TASK-4875 — the Price Book owns it
  if (field === 'rate') line.rate = parseFloat(value) || 0;
  if (field === 'quantity') line.quantity = parseFloat(value) || 0;
  if (field === 'rateUnit') {
    line.rateUnit = value || 'Hourly';
    // Re-render entire section when unit type changes (shows/hides qty vs hours)
    const section = document.getElementById('review-emp-section');
    if (section) section.outerHTML = renderReviewEmployeeSection();
    renderReviewTotals();
    return;
  }
  const _empPieceRate = !isTimeBasedUnit(line.rateUnit);
  line.lineAmount = _empPieceRate
    ? parseFloat(((line.quantity || 0) * (line.rate || 0)).toFixed(2))
    : parseFloat(((line.hours || 0) * (line.rate || 0)).toFixed(2));
  ReviewState.dirty = true;

  // Update DOM without re-render
  const amountEl = document.getElementById(`review-emp-amount-${localId}`);
  if (amountEl) amountEl.textContent = '$' + line.lineAmount.toFixed(2);
  _refreshHoursWarn('emp', line); // TASK-1234 — live inline outlier warning (card mode)
  _refreshRouteMileageBanner();   // TASK-1615-B — typing the real miles clears the warning
  renderReviewTotals();
}

function updateReviewEquipmentLine(localId, field, value) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return; // TASK-1167 — read-only tickets don't mutate
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;

  if (field === 'hours') {
    line.hours = parseFloat(value) || 0;
    // TASK-1548 — real hours supersede a "deliberately $0" mark. Clearing it here
    // (false, not null — the save loop drops nulls) keeps the stored data honest:
    // a PRICED line with hours is billed on its hours, full stop.
    // TASK-1405 — the rate>0 guard: on a rate-0 line the hours are the leak, not
    // the fix, so typing them must not silently revoke the biller's mark.
    if (line.hours > 0 && (Number(line.rate) || 0) > 0 && line.zeroHoursOk === true) line.zeroHoursOk = false;
    // 2026-09-22 — provenance, same reason as the employee twin above: a typed
    // number (0 included) is a decision, and the TASK-9246 self-heal must not
    // overwrite it on the next open.
    line.hoursManual = true;
  }
  if (field === 'rate') line.rate = parseFloat(value) || 0;
  if (field === 'quantity') line.quantity = parseFloat(value) || 0;
  const _isPerUnit = !isTimeBasedUnit(line.rateUnit);
  line.lineAmount = _isPerUnit
    ? parseFloat(((line.quantity || 0) * (line.rate || 0)).toFixed(2))
    : parseFloat(((line.hours || 0) * (line.rate || 0)).toFixed(2));
  ReviewState.dirty = true;

  const amountEl = document.getElementById(`review-equip-amount-${localId}`);
  if (amountEl) amountEl.textContent = '$' + line.lineAmount.toFixed(2);
  _refreshHoursWarn('equip', line); // TASK-1234 — live inline outlier warning (card mode)
  _refreshRouteMileageBanner();     // TASK-1615-B — typing the real miles clears the warning
  _refreshTemplateBanner();         // TASK-9258 — retyping a stale template quantity clears it
  renderReviewTotals();
}



// TASK-730a: per-line rate-unit editing in the review/ticket editor. The biller can switch a
// line's unit (per-day / per-mat/each / flat / hourly...) so seeded rental mats aren't locked
// to the looked-up unit. Switching between time-based (Hourly/Daily/Monthly) and per-unit
// re-renders the section so the QTY-vs-Hours column flips and the amount recomputes correctly.
function updateReviewEquipmentUnit(localId, value) {
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;
  const newUnit = (value || '').trim() || 'Hourly';
  const wasPerUnit = !isTimeBasedUnit(line.rateUnit);
  line.rateUnit = newUnit;
  const isPerUnit = !isTimeBasedUnit(newUnit);
  // Seed a quantity when switching INTO per-unit with none set, so the line doesn't read $0.
  if (isPerUnit && (!line.quantity || line.quantity === 0)) line.quantity = 1;
  line.lineAmount = isPerUnit
    ? parseFloat(((line.quantity || 0) * (line.rate || 0)).toFixed(2))
    : parseFloat(((line.hours || 0) * (line.rate || 0)).toFixed(2));
  ReviewState.dirty = true;
  // Re-render only when the time-based/per-unit mode flipped (the QTY/Hours column changes).
  if (wasPerUnit !== isPerUnit) {
    const section = document.getElementById('review-equip-section');
    if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  } else {
    const amountEl = document.getElementById(`review-equip-amount-${localId}`);
    if (amountEl) amountEl.textContent = '$' + (line.lineAmount || 0).toFixed(2);
  }
  // TASK-1615-B — switching a line's unit INTO or OUT OF Per Mile changes whether it
  // is in scope for the mileage warning at all.
  _refreshRouteMileageBanner();
  renderReviewTotals();
}

// TASK-1636 — equipment-line notes are CUSTOMER-FACING (they print on the invoice,
// per-asset). The note standard: describe what the machine DID — loads hauled, task,
// location — never our billing derivation ("2.5 hrs @ $215/hr", "standard 2.00h per
// the … practice", "Less 2.00h transferred to …"). This detector flags derivation
// language so the Review UI can warn the biller BEFORE it reaches the customer.
// High-precision patterns only — "2 crews 2 hours each" or "5 loads hauled" must NOT
// flag. Non-blocking: a warning, never a gate.
function noteReadsAsBillingDerivation(text) {
  const s = String(text || '');
  if (!s.trim()) return false;
  return [
    /@\s*\$\s*\d/,                                   // "2.5 hrs @ $215/hr"
    /\$\s*\d[\d,.]*\s*(?:\/|per\s+)h(?:ou)?rs?\b/i,  // "$215/hr", "$215 per hour"
    /\bper\s+(?:the|our)\s+\S+(?:\s+\S+){0,3}\s+practice\b/i, // "per the Core Oilfield practice"
    /\bstandard\s+\d+(?:\.\d+)?\s*h(?:ou)?rs?\b/i,   // "standard 2.00h"
    /\btransferred\s+(?:to|from)\b/i,                // "transferred to the crew truck"
    /^\s*less\s+\d+(?:\.\d+)?\s*h/i,                 // "Less 2.00h …" (accounting deduction)
    /\bbilled?\s+(?:at|as|per)\b/i                   // "billed at the winch rate"
  ].some(re => re.test(s));
}

function updateReviewEquipmentNotes(localId, value) {
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;
  // TASK-1636 — '' not null: the saveReviewTicket clean-copy loop skips null/undefined
  // fields, so a note CLEARED to null silently reverted on reload ("prefer empty over
  // wrong" was impossible — a biller blanking a bad note got it back). Empty string
  // persists through updateDoc and every render/print site trims it away.
  line.notes = value || '';
  ReviewState.dirty = true;
  // Toggle the derivation-language warning for this line (render computes initial state).
  const warnEl = document.getElementById(`eq-note-mathwarn-${localId}`);
  if (warnEl) warnEl.style.display = noteReadsAsBillingDerivation(value) ? 'flex' : 'none';
}

// TASK-1636 — internal (never-prints) companion to the customer-facing note: where
// "why we billed it this way" lives. Excluded from every invoice render (they read
// `.notes`) and stripped server-side from the public invoice payload (getPublicInvoice).
function updateReviewEquipmentInternalNotes(localId, value) {
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line) return;
  line.internalNotes = (value || '').trim() ? value.trim() : '';
  ReviewState.dirty = true;
}

function addReviewPermitRow(lineItemId, preset) {
  let desc = '', rate = 0, rateUnit = 'Per', qty = 1, liId = null;
  // TASK-10043 S2 — the permit check's "Add permit line" (user-confirmed cost).
  const _pre = preset && typeof preset === 'object' ? preset : null;
  if (_pre) { desc = String(_pre.description || ''); rate = Number(_pre.baseRate) || 0; }

  if (lineItemId && lineItemId !== '__custom__') {
    const li = (ReviewState.contractLineItems || []).find(l => l.id === lineItemId);
    if (li) {
      desc = li.description || '';
      rate = li.rate || 0;
      rateUnit = li.rateUnit || 'Per';
      qty = li.quantity || 1;
      liId = li.id;
    }
  }

  // TASK-9436 — the customer's Price Book permit markup (the picked item's own %
  // first), else the company default, else 0. Rate on the Price Book item is the
  // RAW fee; the markup rides on top and the biller can change it on the row.
  const _mk = _resolvePermitMarkupPercent(ReviewState.contractLineItems, window.AppState?.settings || {}, liId);
  const newLine = {
    _localId: nextLocalId(),
    _isNew: true,
    type: 'PERMIT',
    description: desc,
    quantity: qty,
    baseRate: rate,
    upchargePercent: _mk.percent,
    upchargeSource: _mk.source,
    rateUnit: rateUnit,
    lineItemId: liId,
    ...(_pre ? { roadPermitId: _pre.roadPermitId || null, permitNumber: _pre.permitNumber || null, jobId: _pre.jobId || null, source: 'permit_record' } : {})
  };
  _applyPermitLineMath(newLine);
  ReviewState.permitLines.push(newLine);
  ReviewState.dirty = true;

  const section = document.getElementById('review-permit-section');
  if (section) section.outerHTML = renderReviewPermitSection();
  renderReviewTotals();

  // Focus the description input of the new row
  setTimeout(() => {
    const inputs = document.querySelectorAll('#review-permit-section input[type="text"]');
    if (inputs.length > 0) inputs[inputs.length - 1].focus();
  }, 50);
}

function updateReviewPermitLine(localId, field, value) {
  const line = ReviewState.permitLines.find(l => l._localId === localId);
  if (!line) return;
  // TASK-9436 — money fields: quantity, baseRate (the raw "Cost" per unit) and
  // upchargePercent (the markup). `rate` is DERIVED (customer-facing unit price),
  // so a legacy caller passing 'rate' edits the raw cost.
  if (field === 'rate') field = 'baseRate';
  if (field === 'quantity' || field === 'baseRate' || field === 'upchargePercent') {
    line._permitMoneyEdited = true; // save keeps the biller's values (no server re-read)
    if (line.baseRate === undefined || line.baseRate === null) {
      line.baseRate = line._displayBaseRate !== undefined ? line._displayBaseRate : (Number(line.rate) || 0);
    }
    if (line.quantity === undefined || line.quantity === null) line.quantity = 1;
    // COD r1 — an AI-parsed line the server hasn't priced yet has NO % decided.
    // Editing its Cost/Qty must not freeze it at 0% (the server pass skips any
    // line with a %): seed it with the customer's markup, same rule the server uses.
    if (field !== 'upchargePercent' && (line.upchargePercent === undefined || line.upchargePercent === null)) {
      const _mk = _resolvePermitMarkupPercent(ReviewState.contractLineItems, window.AppState?.settings || {}, line.lineItemId || null);
      line.upchargePercent = _mk.percent;
      line.upchargeSource = _mk.source;
    }
    if (field === 'upchargePercent') {
      const p = _normalizePermitMarkupPercent(value === '' ? 0 : value);
      if (p === null) {
        window.showToast?.('Markup must be between 0% and 100%', 'error');
        const section = document.getElementById('review-permit-section');
        if (section) section.outerHTML = renderReviewPermitSection();
        return;
      }
      line.upchargePercent = p;
      line.upchargeSource = 'ticket';
    } else {
      line[field] = Number(value) || 0;
    }
    _applyPermitLineMath(line);
    const section = document.getElementById('review-permit-section');
    if (section) section.outerHTML = renderReviewPermitSection();
  } else {
    line[field] = value;
  }
  ReviewState.dirty = true;
  renderReviewTotals();
}

function updateReviewHeaderField(field, value) {
  // TASK-4828 — the PO takes the money-field normalizer, not the generic trim.
  // `.trim()` does NOT strip NBSP or the zero-width characters, and TASK-4782
  // measured 18 live documents whose PO differs from a correct one by nothing
  // but invisible edge whitespace — two values instead of one, so the billing
  // never groups. Same single implementation the job form uses; never truncates.
  const _norm = (field === 'poNumber' && typeof value === 'string')
    ? poNormalizeForSave(value)
    : ((typeof value === 'string') ? value.trim() : value);
  ReviewState.headerEdits[field] = _norm;
  ReviewState.ticket[field] = _norm;
  ReviewState.dirty = true;
}

// TASK-9246 — the same staging, WITHOUT arming the discard prompt.
//
// `updateReviewHeaderField` is a user-event handler at 19 of its 20 call sites.
// The twentieth is renderReviewTicket's status normalizer, which runs during the
// FIRST PAINT of the modal: any ticket whose status isn't one of draft / ready /
// archived / locked gets coerced to 'draft', and that call armed `dirty` before
// the biller had done anything — a second, independent source of the same false
// "You have unsaved changes" this task exists to kill. Measured, not assumed:
// 132 of 1,024 live TITS tickets carry `status: 'deleted'`, so every one of them
// arms the prompt on open.
//
// The staging is deliberately UNCHANGED — the normalized status still goes into
// headerEdits, so a save the biller makes for any other reason writes it exactly
// as it does today. What is dropped is only the claim that THEY changed it. It
// is deliberately NOT persisted on its own either: coercing a `deleted` ticket
// to `draft` behind the user's back would un-delete it.
function _setReviewHeaderFieldSystem(field, value) {
  ReviewState.headerEdits[field] = value;
  ReviewState.ticket[field] = value;
}

// The PO vocabulary already in use for THIS ticket's customer.
//
// HONEST LIMIT, same one the job form carries: BillingState.tickets/jobs are
// windowed streams, so this is "POs used recently for this customer", not the
// customer's whole history. That is the right corpus anyway — PO codes carry
// the year inside them (ND25… rolls to ND26… every January), so an expired
// corridor is not something to suggest.
function getReviewPoVocabulary() {
  const customerId = ReviewState.ticket?.customerId || '';
  // No customer, no vocabulary. A PO belongs to one customer's AP department;
  // offering another customer's is both noise and a way to type the wrong one.
  if (!customerId) return [];
  const editingId = ReviewState.ticketId || null;
  const seen = new Map(); // group key -> most recent exact spelling
  const push = (raw) => {
    const po = poNormalizeForSave(raw);
    if (!po) return;
    const key = poGroupKey(po);
    if (key && !seen.has(key)) seen.set(key, po);
  };
  for (const t of (BillingState.tickets || [])) {
    if (t.id === editingId) continue;
    if ((t.customerId || '') !== customerId) continue;
    push(t.poNumber);
  }
  for (const j of (BillingState.jobs || [])) {
    if ((j.customerId || '') !== customerId) continue;
    jobPoList(j).forEach(push); // TASK-9811 S2 — extras are this customer's POs too
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

// TASK-4828 — the ticket-side half of the PO warning. TASK-4782 shipped this on
// the job form only, which left the LAST field anyone touches before an invoice
// goes out with no guidance at all. WARN, NEVER BLOCK here: the biller can save
// and print this ticket exactly as typed. The QuickBooks push is the surface
// that refuses (functions/src/qbo.js) — a wrong PO on a real invoice is worse
// than a refusal the biller can see.
function onReviewPoInput() {
  const input = document.getElementById('review-po');
  // TASK-9608 — the collapsed header's "PO …" follows the field as it is typed.
  _syncReviewHeaderSummaryPo(input ? input.value : null);
  const host = document.getElementById('review-po-hint');
  if (!input || !host) return;
  const issues = poEntryIssues(input.value, getReviewPoVocabulary());
  host.innerHTML = renderPoHintHTML(issues, {
    escapeHtml,
    escAttr: window.escAttr,
    onUseExisting: 'window.BillingModule.useExistingReviewPoSpelling(this.dataset.po)'
  });
  _reviewPoChips(input.value);
}

// TASK-9811 S1 — a job with extra POs: its POs as one-tap choices by the PO
// field + "This job has 2 POs" (clock tickets inherit the main PO, so the 2nd
// only bills right if someone picks it). UI in jobExtraPos.js, lazy.
let _jobPoUi = null;
function _loadJobPoUi() {
  if (!_jobPoUi) _jobPoUi = __ddChunkWrap(import('./jobExtraPos.js?v=9811003')).catch((e) => { _jobPoUi = null; throw e; });
  return _jobPoUi;
}
function _reviewPoChips(current) {
  const t = ReviewState.ticket;
  const j = t && t.jobId && (BillingState.jobs || []).find(x => x.id === t.jobId);
  if (!t || !t.jobId || (j && !(j.extraPoNumbers || []).length)) { const h = document.getElementById('review-po-chips'); if (h) h.textContent = ''; return; }
  const tid = ReviewState.ticketId;
  _loadJobPoUi().then(m => m.reviewChips({
    tenantId: BillingState.tenantId, jobId: t.jobId, cached: j || null, current,
    readJob: (id) => getDoc(doc(db, 'tenants', BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId, 'jobs', id)),
    still: () => ReviewState.ticketId === tid,
    pick: (po) => { const i = document.getElementById('review-po'); if (!i) return; i.value = po; updateReviewHeaderField('poNumber', po); onReviewPoInput(); },
  })).catch((e) => console.warn('[Billing] job PO choices unavailable:', e?.message));
}

// The dispatcher clicks "Use it" or ignores it. We never swap the value for
// them — that is the difference between an offer and a rewrite.
function useExistingReviewPoSpelling(po) {
  const input = document.getElementById('review-po');
  if (!input) return;
  input.value = po;
  updateReviewHeaderField('poNumber', po);
  onReviewPoInput();
}

async function updateReviewWorkTime(field, value) {
  if (value) {
    // clock-dst-all — company zone; repeated-hour time asked, gap / bad zone refused.
    const isEnd = field === 'workStopTime';
    const partner = isEnd ? ReviewState.ticket?.workStartTime : ReviewState.ticket?.workStopTime;
    const _ticketBefore = ReviewState.ticket; // TASK-9971 (COD r1): the picker is an await
    const d = await _reviewReadTypedTime(value, isEnd ? 'Stop Time' : 'Start Time', partner, isEnd);
    // A different ticket opened, a Save started, or the ticket went read-only while
    // the picker was up: never write this time into whatever is open now.
    if (ReviewState.ticket !== _ticketBefore || ReviewState._saving === true || isReviewReadOnly(ReviewState.ticket || {})) return;
    if (!d) {
      const el = document.getElementById(isEnd ? 'review-work-stop-time' : 'review-work-start-time');
      if (el) el.value = toDatetimeLocal(ReviewState.ticket?.[field]) || '';
      return;
    }
    const ts = Timestamp.fromDate(d);
    ReviewState.headerEdits[field] = ts;
    ReviewState.ticket[field] = ts;
  } else {
    ReviewState.headerEdits[field] = null;
    ReviewState.ticket[field] = null;
  }
  ReviewState.dirty = true;
}

function toggleReviewDateEdit(show) {
  const display = document.getElementById('review-date-display');
  const input = document.getElementById('review-date-input');
  if (!display || !input) return;
  if (show === false || display.style.display === 'none') {
    display.style.display = '';
    input.style.display = 'none';
  } else {
    display.style.display = 'none';
    input.style.display = '';
    input.focus();
  }
}

function updateReviewDate(dateStr) {
  if (!dateStr) return;
  // Warn if date moves significantly
  const origDate = ReviewState._originalSnapshot?.workDate;
  if (origDate && origDate !== dateStr) {
    const origMs = new Date(origDate + 'T12:00:00').getTime();
    const newMs = new Date(dateStr + 'T12:00:00').getTime();
    const daysDiff = Math.abs(Math.round((newMs - origMs) / 86400000));
    const origMonth = origDate.substring(0, 7);
    const newMonth = dateStr.substring(0, 7);
    if (daysDiff > 7 || origMonth !== newMonth) {
      const warn = daysDiff > 7
        ? `Date is moving ${daysDiff} days (${origDate} → ${dateStr}). This may affect invoicing.`
        : `Date is crossing a month boundary (${origDate} → ${dateStr}). This may affect invoicing.`;
      if (!confirm(warn + '\n\nContinue?')) {
        toggleReviewDateEdit(false);
        return;
      }
    }
  }
  const d = new Date(dateStr + 'T12:00:00');
  ReviewState.headerEdits.workDate = dateStr;
  ReviewState.headerEdits.date = Timestamp.fromDate(d);
  ReviewState.ticket.workDate = dateStr;
  ReviewState.ticket.date = Timestamp.fromDate(d);
  ReviewState.dirty = true;
  const display = document.getElementById('review-date-display');
  if (display) display.textContent = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  toggleReviewDateEdit(false);
}

function getFilteredJobsForCustomer(customerId) {
  if (!customerId) return [];
  return BillingState.jobs
    .filter(j => j.customerId === customerId)
    .sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
}

// ============================================
// TASK-4591 — relevance window on the biller-facing job pickers
// ============================================
// getFilteredJobsForCustomer above filters by customer and NOTHING else: no
// status, no date. Cherry Creek renders all 18 of its jobs in the review modal
// — 12 of them `finished`, nine of them one-day February "Bore support" records
// — and across TITS the average picker carries 87 options. The nine identical
// labels are nine real job docs; there is no string fix, only a relevance one.
//
// Same pattern as the driver-PIN Record Expense picker (TASK-1513): window on
// SPAN OVERLAP (a long-running job that started 60 days ago and ends today must
// stay), drop cancelled, keep dateless jobs, always force-include the ticket's
// own job, and put a "Show older jobs" text link under the select. Reused
// through `window.JobBoardModule` — NEVER a cross-import with ?v= (Pitfall #1).
//
// 🔴 PRESENTATION-ONLY. This trims what the <select> renders. It must never be
// the thing that decides what a ticket may point at — `updateReviewJob` writes
// whatever id the select hands it, and the bulk re-point path revalidates
// server-side (:20880). A window that could block a legitimate save would be a
// bug; one that merely hides an option the user can still reveal is not.
//
// Width: the DRIVER window (14 back / 7 fwd). Measured on 596 TITS tickets from
// the last 120 days, anchored on each ticket's own workDate:
//     14/7  -> avg 87 to 13.8 options, worst 40, force-include fired 2.7%
//     30/30 -> avg 87 to 32.2 options, worst 74, force-include fired 1.8%
// One-line tunable to EXPENSE_JOB_WINDOW_ADMIN if 14/7 turns out too tight.
function _reviewJobWindowJobs(allJobs, anchorYmd, forceIncludeId) {
  const win = window.JobBoardModule?.EXPENSE_JOB_WINDOW_DRIVER || { backDays: 14, fwdDays: 7 };
  const fn = window.JobBoardModule?.filterExpenseJobWindow;
  // Graceful fallback = unwindowed. jobboard.js is statically imported by
  // app.js so in practice it is always present; a missing module must degrade
  // to the old (noisy) list, never to an empty picker.
  if (typeof fn !== 'function') return allJobs;
  // Anchor on the ticket's own work date. Noon local so a 'YYYY-MM-DD' string
  // can't shift a day under UTC (the jobDay noon-anchor rule, same reason).
  let anchorMs = null;
  if (anchorYmd) {
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(String(anchorYmd))
      ? new Date(`${anchorYmd}T12:00:00`).getTime()
      : new Date(anchorYmd).getTime();
    if (!isNaN(parsed)) anchorMs = parsed;
  }
  return fn(allJobs, { ...win, anchorMs, forceIncludeId: forceIncludeId || null });
}

// The ticket date the window centres on. `workDate` is the billing-truth field
// (`date` and workDate legitimately differ on a split — TASK-1390); fall back to
// `date` for pre-workDate imports, and to today when a draft has neither.
function _reviewJobAnchor(t) {
  if (!t) return null;
  if (t.workDate) return t.workDate;
  const d = t.date?.toDate?.() || (t.date ? new Date(t.date) : null);
  if (!d || isNaN(d.getTime())) return null;
  // Local Y/M/D, NOT toISOString() — a 7pm CDT ticket is already the next day in
  // UTC, which would anchor the window a day late (COD Code Reviewer).
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The full (unwindowed) list the "Show older" links fall back to, and the list
// the "N more" count is measured against. It must drop cancelled jobs, because
// the WINDOWED list drops them (filterExpenseJobWindow does) — without this,
// clicking "Show older" would ADD cancelled jobs that the default view correctly
// hid, and the count would be inflated by jobs nobody should pick (COD run 2,
// Code Reviewer). Deliberately NOT folded into getFilteredJobsForCustomer: that
// helper also feeds the bulk re-point and group-into-project pickers, which are
// out of scope here.
function _reviewJobFullList(customerId) {
  return getFilteredJobsForCustomer(customerId).filter(j => j && j.status !== 'cancelled');
}

// TASK-4660 — THE job-picker label builder. All three job pickers read through
// it: the review picker and the split-ticket picker via _reviewJobOptionsHtml
// below, and the move-to-job modal directly. A biller looking at "Tufto nitrogen
// purge" twice in one dropdown (live on TITS — 8/24 and 8/26, byte-identical
// titles, same PO) has no way to tell them apart, and picking the wrong one puts
// the hours on the wrong invoice. Dating the label is the whole fix; do NOT
// hand-roll this label at a fourth call site.
//
// Label chain: title first. On TITS `title` is populated on all 798 job docs and
// `name` on 39 — the reverse chain rendered a raw doc id. NEVER fall back to
// j.id as a user-visible label.
//
// Returns PLAIN TEXT. Escaping is the caller's job because the contexts differ:
// option TEXT wants escapeHtml, a `value=` ATTRIBUTE wants escAttr (escapeHtml
// does not escape quotes — [[lesson_escapehtml_is_not_attribute_safe]]).
function _jobPickerLabel(j) {
  const name = String(j?.title || j?.name || 'Untitled');
  // job.startDate/endDate are Timestamps on healthy docs but NOT on all of them
  // (dispatch.js TASK-4610 warns about raw string/Date survivors), so coerce
  // defensively and fall back to a bare title rather than rendering "Invalid Date".
  const _d = v => {
    if (!v) return null;
    try {
      // A bare 'YYYY-MM-DD' parses as UTC MIDNIGHT, and getMonth()/getDate()
      // below read local time — so in every US timezone a legacy string-dated
      // job would label one day EARLY (an 8/24 job reading "8/23"). That is the
      // exact ambiguity this label exists to kill, on a money surface, so
      // date-only strings anchor at noon local — the same rule jobDay dates
      // follow ([[lesson_jobday_date_anchors_to_noon_local]]) and the same one
      // _reviewJobWindowJobs already uses for its anchor.
      const d = v?.toDate?.() || (v instanceof Date ? v
        : new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? `${v}T12:00:00` : v));
      return (d && !isNaN(d.getTime())) ? d : null;
    } catch { return null; }
  };
  const _md = d => `${d.getMonth() + 1}/${d.getDate()}`;
  const s = _d(j?.startDate);
  const e = _d(j?.endDate);
  // A dateless job renders title-only — never "Untitled — " with a dangling dash.
  if (!s && !e) return name;
  const a = s || e;
  const b = e || s;
  const left = _md(a);
  const right = _md(b);
  return left === right ? `${name} — ${left}` : `${name} — ${left}–${right}`;
}

// ============================================
// TASK-9609 — ticket job pickers: group by project + type-to-filter
// ============================================
// Chris 2026-10-05, Edit Ticket on a Loenbro ticket: "the drop down selector for
// jobs is very very long". Loenbro = 223 jobs; even windowed (TASK-4591) the
// picker still shows ~20 near-identical rows. Presentation-only changes:
//   1. The project rides on each job's OWN row ("LOSTWOOD — 10/3 · Lostwood
//      ND26UA2-U0"). 🔴 NOT <optgroup>s — P0 2026-10-06 (Lacey, TITS billing):
//      the first cut grouped by project, and TITS names its projects like its
//      jobs ("HUNT ND25TU4-U1" project vs "HUNT OIL ND25TU4-U1" job), so the
//      bold project HEADERS read as the jobs she wanted — and an optgroup label
//      is not selectable, so "it didn't let her click anything, it just gives
//      this weird hierarchical list". Every row in this list must be a job she
//      can pick. Do not reintroduce optgroups here.
//   2. More than JOB_PICKER_FILTER_MIN jobs -> a type-to-filter box BELOW the
//      select (job #, title, customer, project, dated label). Below, not above:
//      above, it sat where the Job field belongs and looked like the field.
// 🔴 PRESENTATION-ONLY, same contract as the window above. Filtering rebuilds
// the option list (option.hidden is ignored by iOS Safari, so hiding is not an
// option) but it NEVER changes sel.value: the selected job is always rendered,
// matching or not, and no change event fires. A silent re-point on save would
// bill the wrong job (COD Domain lane). Values are the same job ids as before.
const JOB_PICKER_FILTER_MIN = 12;

// The project a job is in, for its row label + the filter. '' when projects
// aren't loaded or the job's project doc is gone (never "undefined").
function _jobPickerProjectName(j) {
  if (!j || !j.projectId || !BillingState._projectsLoaded) return '';
  const p = (BillingState.projects || []).find(x => x && x.id === j.projectId);
  return p ? (String(p.name || '').trim() || 'Untitled project') : '';
}

// Pure. Every whitespace-separated word must appear in one of the job's fields.
// A leading '#' is dropped so "#4123" finds job number 4123.
function jobPickerMatches(j, query, extra) {
  const words = String(query || '').toLowerCase().split(/\s+/)
    .map(w => w.replace(/^#+/, '')).filter(Boolean);
  if (!words.length) return true;
  const hay = [j?.jobNumber, j?.title, j?.name, j?.customer, j?.customerName,
    extra?.customerName, extra?.projectName, extra?.label]
    .filter(v => v != null && v !== '').map(v => String(v).toLowerCase()).join('\n');
  return words.every(w => hay.includes(w));
}

// Pure. The jobs to render for a query. The selected job is ALWAYS kept.
function jobPickerFilterJobs(jobs, query, selectedId, extraFor) {
  const list = (Array.isArray(jobs) ? jobs : []).filter(Boolean);
  if (!String(query || '').trim()) return list.slice();
  return list.filter(j => (selectedId && j.id === selectedId)
    || jobPickerMatches(j, query, typeof extraFor === 'function' ? extraFor(j) : null));
}

// `value=` is ATTRIBUTE context, so escAttr; option TEXT is escapeHtml.
// opts.emptyText adds a disabled "nothing matches" row (no value, so it can
// never become the selection). FLAT: every row is a selectable job — the project
// is a suffix on the job's own row, never an <optgroup> header (P0 2026-10-06,
// see the TASK-9609 block above).
function _reviewJobOptionsHtml(jobsArr, selectedId, placeholder = 'No job', opts = {}) {
  const escA = window.escAttr || escapeHtml;
  const opt = j => {
    const label = _jobPickerLabel(j);
    const proj = _jobPickerProjectName(j);
    const text = proj ? `${label} · ${proj}` : label;
    return `<option value="${escA(j.id)}"${selectedId && j.id === selectedId ? ' selected' : ''}>${escapeHtml(text)}</option>`;
  };
  const body = (Array.isArray(jobsArr) ? jobsArr : []).filter(Boolean).map(opt).join('');
  const empty = opts && opts.emptyText
    ? `<option disabled data-nomatch="1">${escapeHtml(opts.emptyText)}</option>` : '';
  return `<option value="">${escapeHtml(placeholder)}</option>` + body + empty;
}

// Search context for one job: customer + project names and the dated label.
function _jobPickerExtra(j) {
  const cust = j?.customerId ? (BillingState.customers || []).find(c => c.id === j.customerId) : null;
  return { customerName: cust?.name || '', projectName: _jobPickerProjectName(j), label: _jobPickerLabel(j) };
}

// Hand a picker its job list and (re)render it. Every writer of a ticket job
// picker's options goes through here, so the filter survives "Show older" and a
// customer change. opts.selectedId overrides the value to keep (default: the
// select's current value); opts.resetFilter clears the typed text.
function _setJobPickerJobs(sel, jobs, placeholder, opts = {}) {
  if (!sel) return;
  // Off-list options (New Job / cancelled) carry over a "Show older"; a customer
  // change (resetFilter) drops them — they belonged to the old customer.
  const prevExtra = !opts.resetFilter && sel._ddJobPicker?.extra ? sel._ddJobPicker.extra : [];
  sel._ddJobPicker = { jobs: (Array.isArray(jobs) ? jobs : []).filter(Boolean), placeholder: placeholder || 'No job', extra: prevExtra };
  const input = _ensureJobPickerFilter(sel);
  if (input && opts.resetFilter) input.value = '';
  _renderJobPickerOptions(sel, opts.selectedId);
}

function _renderJobPickerOptions(sel, selectedIdOverride) {
  const st = sel?._ddJobPicker;
  if (!st) return;
  const input = document.getElementById(`${sel.id}-filter`);
  const query = input && !input.closest('[hidden]') ? input.value : '';
  const curOpt = sel.selectedIndex >= 0 ? sel.options[sel.selectedIndex] : null;
  const keep = selectedIdOverride !== undefined ? (selectedIdOverride || '') : (sel.value || '');
  const keepText = curOpt && curOpt.value === keep ? curOpt.textContent : '';
  const shown = jobPickerFilterJobs(st.jobs, query, keep, _jobPickerExtra);
  const matches = query.trim()
    ? st.jobs.filter(j => jobPickerMatches(j, query, _jobPickerExtra(j))).length : st.jobs.length;
  const olderLink = document.getElementById(sel.id === 'split-job-select' ? 'split-job-show-older' : 'review-job-show-older');
  const emptyText = query.trim() && matches === 0
    ? `No jobs match "${query.trim()}"${olderLink ? ' (try Show older jobs)' : ''}` : '';
  sel.innerHTML = _reviewJobOptionsHtml(shown, keep || null, st.placeholder, { emptyText });
  // The selected job must survive even when it is not in the list at all (a job
  // just made with "New Job", or a cancelled one the full list drops): put its
  // existing option back, right under the placeholder. Only an option that WAS
  // on screen is restored — never invented from an id alone. It is REMEMBERED
  // on the picker (st.extra) so it also survives the user moving off it and
  // filtering again: a cancelled "Move this ticket…?" confirm reverts the select
  // to that id, which must still exist (COD Code Reviewer).
  const has = v => [...sel.options].some(o => o.value === v && !o.disabled);
  st.extra = st.extra || [];
  if (keep && keepText && !st.jobs.some(j => j.id === keep) && !st.extra.some(x => x.value === keep)) {
    st.extra.push({ value: keep, text: keepText });
  }
  for (const x of st.extra.slice().reverse()) {
    if (has(x.value)) continue;
    const o = document.createElement('option');
    o.value = x.value;
    o.textContent = x.text;
    sel.insertBefore(o, sel.firstElementChild ? sel.firstElementChild.nextSibling : null);
  }
  sel.value = keep && has(keep) ? keep : '';
  const status = document.getElementById(`${sel.id}-filter-status`);
  if (status) status.textContent = query.trim() ? `${matches} of ${st.jobs.length} jobs match` : '';
}

// The filter box, built with DOM APIs (no string HTML). Created once per select
// and only when the list is long; hidden again if a customer change shortens it.
function _ensureJobPickerFilter(sel) {
  const st = sel._ddJobPicker;
  const id = `${sel.id}-filter`;
  const needed = st.jobs.length > JOB_PICKER_FILTER_MIN;
  let input = document.getElementById(id);
  if (!input) {
    if (!needed) return null;
    const row = sel.parentElement;   // the flex row holding the select + buttons
    if (!row || !row.parentElement) return null;
    const wrap = document.createElement('div');
    wrap.id = `${id}-wrap`;
    wrap.style.cssText = 'margin-top:6px;';
    input = document.createElement('input');
    input.type = 'search';
    input.id = id;
    input.className = 'form-control';
    input.placeholder = 'Type to filter: job #, name, customer';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('aria-label', 'Filter jobs');
    input.setAttribute('aria-controls', sel.id);
    input.setAttribute('enterkeyhint', 'search');
    // 16px stops iOS zooming the modal on focus; 44px is the tap-target floor.
    input.style.cssText = 'width:100%;font-size:16px;min-height:44px;';
    const status = document.createElement('div');
    status.id = `${id}-status`;
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'font-size:11px;color:var(--text-secondary);min-height:0;margin-top:2px;';
    input.addEventListener('input', () => _renderJobPickerOptions(sel));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        // Move to the list; never auto-pick (picking is what re-points a ticket).
        e.preventDefault();
        e.stopPropagation();
        sel.focus();
        if (e.key === 'Enter') { try { sel.showPicker?.(); } catch (_) { /* not supported */ } }
      } else if (e.key === 'Escape' && input.value) {
        e.preventDefault();
        e.stopPropagation();   // first Escape clears the filter, not the modal
        input.value = '';
        _renderJobPickerOptions(sel);
      }
    });
    wrap.append(input, status);
    // BELOW the select row (P0 2026-10-06): above it, the box sat where the Job
    // field belongs and read as the field itself.
    row.parentElement.insertBefore(wrap, row.nextSibling);
  }
  const wrap = document.getElementById(`${id}-wrap`);
  if (wrap) wrap.hidden = !needed;
  if (!needed) input.value = '';
  input.disabled = !!sel.disabled;
  return input;
}

// ============================================
// TASK-9608 — ticket review header: summary line + "Change"
// ============================================
// Lacey (via Chris 2026-10-05): the always-open Customer select + hint + Job
// select + rename + New Job block is clutter on a ticket that is already set.
// 172 of 178 recent TITS tickets carry a job, so the common case collapses to
// one line. 🔴 PRESENTATION-ONLY: the selects are hidden, never removed, keep
// their ids + onchange handlers, and nothing here touches headerEdits or the
// ticket — Save writes exactly what it wrote before.
const REVIEW_INTERNAL_CHIP_HTML = '<span class="review-internal-chip" style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:999px;background:#FEF3C7;color:#78350F;font-size:0.75rem;font-weight:700;white-space:nowrap;"><span class="material-icons" style="font-size:14px;" aria-hidden="true">home_repair_service</span>Internal — not billed</span>';

// Pure. null = render the pickers as before (customer or job missing or not
// resolvable in the loaded lists, or the ticket is read-only). Otherwise the
// plain-text pieces of the summary line (caller escapes).
function reviewHeaderSummary(t, customers, jobs, readOnly) {
  if (!t || readOnly) return null;
  if (!t.customerId || !t.jobId) return null;
  const cust = (Array.isArray(customers) ? customers : []).find(c => c && c.id === t.customerId);
  const job = (Array.isArray(jobs) ? jobs : []).find(j => j && j.id === t.jobId);
  if (!cust || !job) return null;
  return {
    customerName: String(cust.name || '').trim() || 'Unnamed customer',
    jobLabel: _jobPickerLabel(job),
    po: String(t.poNumber || '').trim(),
    internal: cust.internalTimeOnly === true,
  };
}

// Text via escapeHtml only; the one onclick carries no data (no names in a JS
// string). The Change button is a 44px tap target.
function _reviewHeaderSummaryHtml(s) {
  return `
    <div id="review-header-summary" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);background:#fff;margin-bottom:var(--spacing-md);">
      <div style="flex:1;min-width:0;display:flex;align-items:center;gap:6px;flex-wrap:wrap;color:var(--text-primary);font-size:0.9375rem;">
        <span class="material-icons" style="font-size:16px;color:var(--brand-blue);" aria-hidden="true">business</span>
        <strong id="review-header-summary-customer">${escapeHtml(s.customerName)}</strong>
        ${s.internal ? REVIEW_INTERNAL_CHIP_HTML : ''}
        <span aria-hidden="true" style="color:var(--text-secondary);">·</span>
        <span id="review-header-summary-job">${escapeHtml(s.jobLabel)}</span>
        <span id="review-header-summary-po-wrap" style="color:var(--text-secondary);${s.po ? '' : 'display:none;'}">· PO <span id="review-header-summary-po" style="color:var(--text-primary);font-weight:600;">${escapeHtml(s.po)}</span></span>
        ${_reviewHeaderSummaryProjectHtml(s.project)}
      </div>
      <button type="button" id="review-header-change" class="btn btn-sm btn-secondary" style="min-height:44px;min-width:44px;padding:6px 14px;"
        aria-controls="review-header-pickers" aria-expanded="false"
        onclick="window.BillingModule.expandReviewHeader()">Change</button>
    </div>`;
}

// "Change" — reveal today's pickers in place (no re-render, no state change
// beyond the UI flag that keeps them open across later re-renders).
function expandReviewHeader() {
  ReviewState.headerExpanded = true;
  const pickers = document.getElementById('review-header-pickers');
  if (pickers) pickers.style.display = '';
  document.getElementById('review-header-summary')?.remove();
  try { document.getElementById('review-customer')?.focus({ preventScroll: true }); } catch (_) { /* focus is a nicety */ }
}

function _syncReviewHeaderSummaryPo(value) {
  const wrap = document.getElementById('review-header-summary-po-wrap');
  const span = document.getElementById('review-header-summary-po');
  if (!wrap || !span || value == null) return;
  const po = String(value).trim();
  span.textContent = po;
  wrap.style.display = po ? '' : 'none';
}

// Projects can land after a picker rendered flat (one-shot getDocs). Regroup any
// open picker in place; values and selection are untouched.
function _refreshOpenJobPickers() {
  for (const id of ['review-job-select', 'split-job-select']) {
    const sel = document.getElementById(id);
    if (sel && sel._ddJobPicker) _renderJobPickerOptions(sel);
  }
}

// "Show older jobs" — re-render the SAME <select>'s options from the full
// customer list, window removed. Replacing innerHTML keeps the element, so its
// inline onchange keeps firing and the current selection is preserved.
function showOlderReviewJobs() {
  const sel = document.getElementById('review-job-select');
  if (!sel) return;
  const keep = sel.value || ReviewState.headerEdits?.jobId || ReviewState.ticket?.jobId || '';
  const custId = ReviewState.ticket?.customerId;
  // TASK-9609 — remove the link FIRST so the "try Show older" hint drops with it.
  document.getElementById('review-job-show-older')?.remove();
  _setJobPickerJobs(sel, _reviewJobFullList(custId), 'No job', { selectedId: keep || '' });
}

// Split-ticket twin of the above. Its placeholder is "Same job" (an empty value
// means "don't re-point"), NOT "No job" — do not collapse these two into one
// helper without carrying that difference.
// The customer id is read off the select's data attribute rather than passed
// through the inline onclick: escAttr is ATTRIBUTE-safe, and the browser decodes
// entities before evaluating an `onclick` as JS, so an escaped value inside a
// quoted JS string literal can still break out of it (COD Code Reviewer).
function showOlderSplitJobs() {
  const sel = document.getElementById('split-job-select');
  if (!sel) return;
  const customerId = sel.dataset.custId || '';
  const keep = sel.value || '';
  document.getElementById('split-job-show-older')?.remove();
  _setJobPickerJobs(sel, _reviewJobFullList(customerId), 'Same job', { selectedId: keep });
}

// ============================================
// TASK-1581 SLICE 1 — BULK RE-POINT TICKETS TO A JOB
// ============================================
//
// The problem this exists for, measured on live data: 22 separate job records for
// ONE Loenbro Lostwood engagement (" Bridger Lostwood" x9, "EOG lostwood",
// "Stanley/Wyzetta", "Lostwood Pipeline"...). Only 11 are even string-similar, so
// no matcher can group them. 234 of 303 active jobs (77%) are stale. And it feeds
// itself: 79 active Loenbro jobs -> the job picker caps "other" at 10 -> the
// dispatcher can't find the right one -> creates a new one -> 80.
//
// The field names jobs for WAYFINDING; billing wants ONE stable engagement name.
// One text field, two audiences, and the field wins because they type it last.
// That is correct behaviour by the field — this does not try to prevent it, it
// gives the biller a way to consolidate after the fact.
//
// 🔴 RE-POINT, NEVER RENAME. 693 tickets carry BOTH `jobId` and `jobName`. Writing
// only the name desyncs the label from the pointer.

// Mirror of the drag eligibility rule at :6854 — archived, locked and invoiced
// tickets are not movable. Kept as one predicate so the action bar, the confirm
// dialog and the write path can never disagree about what is eligible.
function _ticketMoveEligible(t) {
  return t && t.status !== 'archived' && t.status !== 'locked' && !isTicketInvoiced(t);
}

// Firestore caps a batch at 500 ops. Chunk well below it and commit sequentially —
// there is deliberately NO transaction across chunks, so partial success is
// possible and is reported honestly rather than hidden. 100 rather than 400 so the
// pre-write freshness reads fan out in bounded waves and a failure loses less.
const _MOVE_CHUNK = 100;

async function moveSelectedTicketsToJob() {
  const tenant = BillingState.tenantId;
  if (!tenant) { showToast('Not loaded yet — refresh and try again.', 'error'); return; }

  const selected = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));
  if (!selected.length) return;

  // 🔴 HARD-VALIDATE SAME CUSTOMER. firestore.rules let any writing member set
  // jobId to ANOTHER customer's job — the same-customer constraint lives only
  // here, so this check IS the constraint, not a convenience.
  const custId = selected[0].customerId;
  if (!selected.every(t => t.customerId === custId)) {
    showToast('Select tickets from one customer at a time — a job belongs to a single customer.', 'error');
    return;
  }

  const movable = selected.filter(_ticketMoveEligible);
  const skipped = selected.filter(t => !_ticketMoveEligible(t));
  if (!movable.length) {
    showToast('None of the selected tickets can be moved — invoiced, archived and locked tickets are fixed.', 'error');
    return;
  }

  // Target picker, scoped to this customer so another customer's job can never
  // be offered (the customer-filter fix pulled forward from slice 3).
  const jobs = getFilteredJobsForCustomer(custId)
    .filter(j => j.id !== (movable[0].jobId || '_none'));
  if (!jobs.length) {
    showToast('No other job on this customer to move these tickets to.', 'error');
    return;
  }

  const targetJobId = await _pickTargetJobModal(jobs, movable.length, skipped.length);
  if (!targetJobId) return;

  // 🔴 REVALIDATE THE TARGET AGAINST THE SERVER. Filtering the picker is not the
  // check — the picker returns an id, and by the time it does, that id has passed
  // through the DOM. A tampered data-job attribute, a BillingState.jobs snapshot
  // that went stale while the modal was open, or a job reassigned to another
  // customer mid-session all end the same way: tickets silently re-billed under a
  // DIFFERENT customer's job. firestore.rules do NOT constrain jobId at all (the
  // unlocked-ticket arm has no field allowlist), so this client check IS the
  // constraint. Caught by COD on the real diff — the picker filter alone read as
  // sufficient and was not.
  let targetJob;
  try {
    const jobSnap = await getDoc(doc(db, 'tenants', tenant, 'jobs', targetJobId));
    if (!jobSnap.exists()) { showToast('That job no longer exists — reload and try again.', 'error'); return; }
    targetJob = { id: jobSnap.id, ...jobSnap.data() };
  } catch (err) {
    console.error('[BulkMove] target job read failed:', err);
    showToast('Could not verify the target job — nothing was moved.', 'error');
    return;
  }
  if (targetJob.customerId !== custId) {
    showToast('That job belongs to a different customer — nothing was moved.', 'error');
    return;
  }
  const newJobName = targetJob.title || targetJob.name || '';

  // 🔴 THE UNNAMED RISK COD FOUND — PO does NOT follow the move.
  // onTicketDrop writes jobId/jobName/jobTitle and never touches poNumber, and PO
  // inheritance (TASK-1106) runs at ticket create/review time only. So bulk-moving
  // 14 tickets onto a consolidated job leaves every one carrying its OLD job's PO,
  // and pipeline customers AUTO-REJECT invoices whose PO doesn't match their
  // records. Chris's own framing of the use case was "change the name AND po".
  // Never silently rewrite a PO — show the mismatch and let the biller choose.
  const targetPo = poNormalizeForSave(targetJob?.poNumber);
  const poMismatched = movable.filter(t => poNormalizeForSave(t.poNumber) !== targetPo);
  let alsoSetPo = false;
  if (poMismatched.length) {
    const choice = await _confirmPoReconcile(poMismatched, targetPo, newJobName);
    if (choice === null) return;      // cancelled — nothing moves
    alsoSetPo = choice === 'set';
  }

  // 🔴 NO OPTIMISTIC PRE-UPDATE. Writing BillingState up front and then committing
  // chunks sequentially means a later chunk failure leaves the tree showing tickets
  // as moved that never committed — and the next thing the biller does is invoice
  // against that lie. Local state is updated per COMMITTED chunk instead, so what
  // is on screen is always a subset of what actually landed.
  BillingState.expandedJobs[`${custId}_${targetJobId}`] = true;

  let written = 0, staleSkipped = 0, movedTotal = 0;
  const _moveSummaries = []; // TASK-9355 S2 — per-ticket job-change summaries for the ONE final toast
  try {
    for (let i = 0; i < movable.length; i += _MOVE_CHUNK) {
      const slice = movable.slice(i, i + _MOVE_CHUNK);

      // 🔴 RE-VERIFY THE TARGET JOB EVERY CHUNK, not just once before the loop.
      // Nothing in firestore.rules stops a writing member editing
      // jobs/{id}.customerId, and a 400-ticket move is several seconds of wall
      // clock across multiple waves. Verifying once at the top leaves later chunks
      // writing jobId onto a job that now belongs to a DIFFERENT customer — the
      // per-ticket re-assert below can't catch it, because it only compares each
      // ticket against the ORIGINAL custId. Same cross-customer split the target
      // revalidation was added to prevent, reached from the other side.
      // Costs one read per chunk; found by COD's Adversary lane on round 2.
      const jobNow = await getDoc(doc(db, 'tenants', tenant, 'jobs', targetJobId));
      if (!jobNow.exists() || jobNow.data()?.customerId !== custId) {
        renderTicketsList();
        showToast(`Stopped after ${written} ticket${written !== 1 ? 's' : ''} — the target job changed customer mid-move. Reload before invoicing.`, 'error');
        return;
      }

      // Re-read immediately before writing. The in-memory eligibility check ran
      // against a snapshot that may be seconds or minutes old — another biller can
      // invoice or lock a ticket while the picker and PO dialogs are open, and a
      // blind batch.update would quietly move an invoiced ticket off its job.
      const fresh = await Promise.all(slice.map(async t => {
        try {
          const s = await getDoc(doc(db, 'tenants', tenant, 'tickets', t.id));
          return s.exists() ? { id: s.id, ...s.data() } : null;
        } catch { return null; }
      }));

      const batch = writeBatch(db);
      const committing = [];
      for (const cur of fresh) {
        // Re-assert BOTH gates on server truth: still eligible, and still this
        // customer's ticket (a ticket whose customer changed must not be dragged
        // onto a job that belongs to the old one).
        if (!cur || !_ticketMoveEligible(cur) || cur.customerId !== custId) { staleSkipped++; continue; }
        // TASK-9355 S2 — the move carries everything job-derived (bridged
        // permits/photos, notes, fastTrax, vendor/well/AFE/location by the
        // inherited-follows rule). PO is EXCLUDED from the helper here because
        // the biller already answered _confirmPoReconcile above — that decision
        // is applied verbatim, never second-guessed.
        const _oldJob = cur.jobId ? (BillingState.jobs.find(j => j.id === cur.jobId) || null) : null;
        const { updates, summary } = _applyJobChangeToTicket(cur, _oldJob, targetJob, { skipPo: true });
        if (alsoSetPo) updates.poNumber = targetPo;
        batch.update(doc(db, 'tenants', tenant, 'tickets', cur.id), updates);
        committing.push({ cur, updates, summary, oldJob: _oldJob });
      }
      if (!committing.length) continue;

      await batch.commit();

      // S3 — one bounded PERMIT-line read per moved ticket, in parallel per
      // chunk; surfaced in the final toast, never deleted. Best-effort: a failed
      // read flags nothing.
      await Promise.all(committing.map(async ({ cur, summary, oldJob: _oldJob }) => {
        summary.permitLinesFlagged = await _fetchPermitLinesFromOtherJob(tenant, cur.id, _oldJob, targetJobId);
      }));

      // Only now is it true locally.
      for (const { cur, updates, summary } of committing) {
        _moveSummaries.push(summary);
        const t = BillingState.tickets.find(x => x.id === cur.id);
        if (!t) continue;
        Object.assign(t, updates);
        movedTotal += Number(t.grandTotal) || 0;
        // 🔴 originalJobId is DELIBERATELY NOT TOUCHED. It is the immutable
        // original (see the duplicate-detection helper's own comment): a ticket
        // reassigned to another job keeps it, and that is the alias sticky-ticket
        // dedup relies on to still recognise the sibling. Rewriting it here would
        // silently break duplicate detection for every consolidated ticket.
      }
      written += committing.length;
      renderTicketsList();
    }
  } catch (err) {
    console.error('[BulkMove] failed after', written, 'of', movable.length, err);
    renderTicketsList();
    showToast(`Moved ${written} of ${movable.length} tickets before failing — reload and check before invoicing.`, 'error');
    return;
  }

  clearTicketSelection();
  renderTicketsList();
  const money = movedTotal ? ` ($${movedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : '';
  const skipTail = skipped.length ? ` ${skipped.length} skipped (invoiced/archived/locked).` : '';
  const staleTail = staleSkipped ? ` ${staleSkipped} changed while you were choosing and ${staleSkipped !== 1 ? 'were' : 'was'} left alone.` : '';
  // TASK-9355 S2/S3 — ONE post-move summary for the whole batch.
  const _agg = _aggregateJobChangeSummaries(_moveSummaries);
  const changeTail = _jobChangeAggregateTail(_agg);
  showToast(`Moved ${written} ticket${written !== 1 ? 's' : ''}${money} to "${newJobName}".${skipTail}${staleTail}${changeTail}`,
    written ? ((_agg.ticketsWithKeptFields || _agg.permitLinesFlagged) ? 'warning' : 'success') : 'error');
}

// 🔴 STRAY-CLICK GUARD — a modal that opens UNDER THE POINTER must not eat the
// second click of a double-click.
//
// Found by driving the DEPLOYED app, not by reading. Rapid double-click on
// "Create project", measured: click 1 lands at t=14194.8ms, the grouping modal is
// removed at t=14195.3, _confirmPoReconcile opens at t=14195.6 — 0.8ms later —
// and click 2 arrives at t=14200.9 onto the PO dialog's BACKDROP, which resolves
// null and cancels the whole operation. Nothing created, nothing moved, and NO
// message to the user: the "I clicked it and nothing happened, so it must be
// broken" that mass-archiving was born from.
//
// Worse is the geometry-dependent twin. The PO dialog's three choices are stacked
// full-width at the bottom of its content box, whose height varies with how many
// mismatched tickets it lists. Shift the layout — bigger viewport, longer list,
// different zoom — and that same stray click lands on "Move and set PO to X",
// silently rewriting the PO on every moved ticket. That is exactly the books
// change _confirmPoReconcile was built as a THREE-way dialog to prevent.
//
// THE SIGNAL IS event.detail, NOT A TIMER. The first version of this fix ignored
// all clicks for 400ms after the modal opened, and it swallowed a legitimately
// fast click on the very next test run — silently, which is the same failure I
// was fixing. Measured on the deployed build instead: click 1 carries detail:1,
// the stray click 2 carries detail:2. So drop clicks that are part of a
// multi-click sequence and nothing else. No window, no false positives, and a
// programmatic .click() (detail:0) still works.
//
// Applies to all three hand-off modals, including slice 1's already-shipped
// picker + PO pair, which have the same shape and the same hole.
function _isStrayMultiClick(e) {
  return !!e && typeof e.detail === 'number' && e.detail >= 2;
}

// Target-job picker. Purpose-built rather than reusing showInputModal because the
// biller needs to SEE the candidate jobs — typing a name is what created 22
// duplicates of one engagement in the first place.
function _pickTargetJobModal(jobs, movableCount, skippedCount) {
  return new Promise(resolve => {
    const id = 'billing-move-job-modal';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    el.innerHTML = `
      <div class="modal-content" style="max-width:520px;width:92%;height:auto;max-height:80vh;overflow:auto;">
        <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">Move ${movableCount} ticket${movableCount !== 1 ? 's' : ''} to a job</h3>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);">
          Only jobs for this customer are listed.${skippedCount ? ` ${skippedCount} selected ticket${skippedCount !== 1 ? 's are' : ' is'} invoiced, archived or locked and will be skipped.` : ''}
        </p>
        <div style="display:flex;flex-direction:column;gap:6px;">
          ${jobs.map(j => `
            <button type="button" class="btn btn-secondary" data-job="${window.escAttr(j.id)}"
                    style="text-align:left;justify-content:flex-start;padding:10px 12px;">
              <!-- TASK-4660: same dated label as the two <select> pickers, via the
                   shared builder. This is a <button>, not an <option>, so it takes
                   the plain-text label and escapes it itself. -->
              <span style="font-weight:600;">${escapeHtml(_jobPickerLabel(j))}</span>
              ${jobPoLabel(j) ? `<span style="font-size:0.78rem;color:var(--text-secondary);margin-left:8px;">PO ${escapeHtml(jobPoLabel(j))}</span>` : ''}
            </button>`).join('')}
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:var(--spacing-md);">
          <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };
    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;   // see _isStrayMultiClick
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      const btn = e.target.closest('[data-job]');
      if (btn) done(btn.getAttribute('data-job'));
    });
  });
}

// Pre-commit PO warning. Resolves 'set' (adopt the job's PO), 'keep' (move only),
// or null (cancel the whole move).
//
// Deliberately a purpose-built THREE-way modal rather than showConfirm:
//  - showConfirm is two-way, so "Cancel" would have to mean either "don't move" or
//    "move without touching PO". Collapsing those is how a biller cancels a dialog
//    and discovers 14 tickets moved anyway.
//  - showConfirm renders its message with textContent (app.js), so the per-ticket
//    PO list has to be real DOM here, not a markup string.
//
// Three-way because BOTH silent options are wrong: rewriting a PO is a books change
// nobody asked for, and leaving a stale one gets the invoice auto-rejected by the
// customer's portal.
function _confirmPoReconcile(mismatched, targetPo, jobName) {
  return new Promise(resolve => {
    const id = 'billing-move-po-modal';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    const rows = mismatched.slice(0, 6)
      .map(t => `<div>#${escapeHtml(String(t.ticketNo || '?'))} — PO ${escapeHtml((t.poNumber || '').trim() || '(none)')}</div>`)
      .join('');
    const more = mismatched.length > 6
      ? `<div style="opacity:0.75;">…and ${mismatched.length - 6} more</div>` : '';
    el.innerHTML = `
      <div class="modal-content" style="max-width:520px;width:92%;height:auto;max-height:80vh;overflow:auto;">
        <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">Update the PO too?</h3>
        <p style="color:var(--text-primary);margin:0 0 var(--spacing-sm);">
          ${mismatched.length} of these tickets carry a PO that doesn't match
          <strong>${escapeHtml(jobName)}</strong>${targetPo ? ` (PO ${escapeHtml(targetPo)})` : ' — which has no PO'}.
        </p>
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">${rows}${more}</div>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);">
          Customers often auto-reject an invoice whose PO doesn't match their records.
        </p>
        <div style="display:flex;flex-direction:column;gap:8px;">
          <button type="button" class="btn btn-primary" data-choice="set">
            ${targetPo ? `Move and set PO to ${escapeHtml(targetPo)}` : 'Move and clear the PO'}
          </button>
          <button type="button" class="btn btn-secondary" data-choice="keep">Move, keep each ticket's own PO</button>
          <button type="button" class="btn btn-tertiary" data-choice="cancel">Cancel — don't move anything</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };
    el.addEventListener('click', e => {
      // 🔴 THE ONE THAT WAS ACTUALLY BITING. This dialog opens 0.8ms after the
      // click that dismissed the modal before it, directly under the pointer.
      if (_isStrayMultiClick(e)) return;   // see _isStrayMultiClick
      if (e.target === el) return done(null);
      const btn = e.target.closest('[data-choice]');
      if (!btn) return;
      const c = btn.getAttribute('data-choice');
      done(c === 'cancel' ? null : c);
    });
  });
}

// ============================================
// TASK-1618 — AR/AP PROJECTS VIEW + "GROUP INTO PROJECT"
// ============================================
//
// The measured problem (8/13 ticket audit — do not re-derive): 22 job records for
// ONE Loenbro Lostwood engagement, only 11 even string-similar, so no matcher can
// group them — the grouping exists only in Lacey's head. 234 of 303 active jobs
// (77%) are stale. Meanwhile projects.js is 3,094 lines of fully-built view at
// ZERO usage (trippin-trucking: 0 projects), because nothing creates a project
// where the work actually is. This is a finish-and-surface job plus a money spine.
//
// TWO LENSES, ONE COLLECTION. Dispatch's Projects view owns the operational
// detail — timeline, inventory, docs, notes, change orders. It has no money in it
// at all. This view is the AR lens: what is unbilled, what is out, what came back.
// None of the Dispatch detail is duplicated here; the row links out to it.
//
// 🔴 window.ProjectsModule.* ONLY — NEVER `import` from projects.js. Its importers
//   carry a ?v= cache buster, and a busted cross-import instantiates a SECOND
//   module with its own empty ProjectState (CLAUDE.md pitfall #1). The link-out
//   below goes through the window handle after navigating, which is also the only
//   thing that works: ProjectsModule is assigned inside initProjects, so it does
//   not exist until the Projects view has mounted.

// One-shot read of the tenant's projects. Deliberately getDocs, not an eighth
// live onSnapshot: the AR/AP mount already opens seven listeners totalling 2.66MB
// (TASK-1245) and this view is nowhere near the first-paint path. Re-read after a
// grouping so the new row appears.
//
// No permission work: firestore.rules:4763 is already `allow read: if
// canReadTenant(tenantId)`. Unordered + sorted in memory on purpose — an
// orderBy('createdAt') would silently DROP every project written before that field
// existed, which is exactly the kind of invisible omission this task is about.
async function loadBillingProjects() {
  const tenant = BillingState.tenantId;
  if (!tenant || BillingState._projectsLoading) return;
  // TASK-9651 — project read is the 'projects' module (firestore.rules
  // projRead). Without it: no read, an empty list, and _projectsLoadFailed set
  // so the PO resolver keeps REFUSING the project tier (never falls through to
  // the less specific contract PO and persists it) — same as a failed read,
  // minus the "check your connection" toast that would be a lie here.
  if (window.canAccessProjects?.() === false) {
    BillingState.projects = [];
    BillingState._projectsLoaded = true;
    BillingState._projectsLoadFailed = true;
    return;
  }
  BillingState._projectsLoading = true;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'projects'));
    BillingState.projects = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BillingState._projectsLoaded = true;
    BillingState._projectsLoadFailed = false; // TASK-4580 — a real read clears the resolver hold
  } catch (err) {
    console.error('[Billing] Error loading projects:', err);
    BillingState.projects = [];
    BillingState._projectsLoaded = true;   // never leave the spinner spinning
    // TASK-4580 (COD Code Reviewer) — the PO resolver must NOT treat a FAILED
    // read as "projects are authoritatively empty": openReviewTicket persists
    // the resolved PO while ticket.poNumber is null, so falling through to the
    // contract PO after a transient failure stamps the wrong number
    // permanently. _projectsLoaded stays true for the UI (no eternal spinner);
    // this flag keeps the resolver refusing until a read actually succeeds.
    BillingState._projectsLoadFailed = true;
    window.showToast?.('Could not load projects — check your connection and refresh.', 'error');
  } finally {
    BillingState._projectsLoading = false;
    // TASK-9414 — Tickets shows the Project control + job tags off this list too.
    if (BillingState.viewMode === 'projects' || BillingState.viewMode === 'tickets') renderBillingView();
    // TASK-9609 — an open ticket job picker regroups by project in place.
    _refreshOpenJobPickers();
    // TASK-9810 — the Review header's project label was "Loading…" if the
    // ticket opened before this read landed; fill it in place (COD Code Reviewer).
    _refreshReviewProjectLabel();
  }
}

// TASK-9810 — repaint the open Review modal's project label(s) from state, no
// re-render. Safe when the modal is closed (nothing found, nothing done).
function _refreshReviewProjectLabel() {
  const cur = document.getElementById('review-project-current');
  if (!cur || !ReviewState.ticket) return;
  const info = reviewTicketProject(ReviewState.ticket.jobId, BillingState.jobs, BillingState.projects, BillingState._projectsLoaded === true);
  const text = reviewProjectText(info);
  const has = !!info.projectId;
  cur.textContent = text;
  cur.style.color = has ? 'var(--text-primary)' : 'var(--text-secondary)';
  cur.style.fontWeight = has ? '600' : '';
  const sum = document.getElementById('review-header-summary-project');
  if (sum) { sum.textContent = text; sum.style.color = has ? 'var(--text-primary)' : ''; sum.style.fontWeight = has ? '600' : ''; }
  const btn = document.getElementById('review-project-change');
  if (btn) {
    const icon = btn.querySelector('.material-icons');
    btn.textContent = '';
    if (icon) btn.appendChild(icon);
    btn.appendChild(document.createTextNode(' ' + (has ? 'Change project' : 'Put in a project')));
  }
}

// Money + hours rollup for one project, computed off the tickets already in state.
//
// BUCKET DEFINITIONS MIRROR filterTicketsByStatus (:819) EXACTLY, so a project row
// and the ticket tab it drills into can never disagree:
//   Paid     = paid === true && !writtenOff              (the Paid tab)
//   Invoiced = isTicketInvoiced && !paid && !writtenOff   (the Invoiced tab — open A/R)
//   Unbilled = (draft|ready) && !isTicketInvoiced         (the Draft + Ready tabs)
// Written-off work is in NONE of the three; that is the point of writing it off.
//
// ARCHIVED-BUT-UNBILLED is counted SEPARATELY rather than folded into Unbilled or
// silently dropped. Archiving is what made $46,756 of unbilled tickets both
// un-billable and invisible ("the ticket fiasco"); a project row that quietly
// omitted them would rebuild the same blind spot one level up. It renders as a
// warning chip on the row.
//
// HOURS come from ticket.totalHours, which is derived from clock entries at ticket
// build/review time (:7740 self-heals it from the employee lines). Deliberately
// NOT projects.js loadProjectClockHours: that would be a per-project fan-out of
// `in`-chunked clockEntries queries on a list view, it is not exported on
// ProjectsModule, and it would answer a DIFFERENT question than the money columns
// beside it — these hours are the hours on the tickets the money is computed from.
// The clock-truth lens lives in the Dispatch project detail, one click away.
function _billingProjectRollup(projectId) {
  const jobIds = new Set(
    BillingState.jobs.filter(j => j.projectId === projectId).map(j => j.id)
  );
  const roll = {
    jobIds, unbilled: 0, invoiced: 0, paid: 0, hours: 0,
    ticketCount: 0, archivedUnbilled: 0, archivedUnbilledCount: 0
  };
  if (!jobIds.size) return roll;
  for (const t of BillingState.tickets) {
    if (!t.jobId || !jobIds.has(t.jobId)) continue;
    if (t.status === 'deleted') continue;
    roll.ticketCount++;
    roll.hours += Number(t.totalHours) || 0;
    const amt = Number(t.grandTotal) || 0;
    if (t.writtenOff === true) continue;
    if (t.paid === true) { roll.paid += amt; continue; }
    if (isTicketInvoiced(t)) { roll.invoiced += amt; continue; }
    if (t.status === 'draft' || t.status === 'ready') { roll.unbilled += amt; continue; }
    if (t.status === 'archived' || t.status === 'locked') {
      roll.archivedUnbilled += amt;
      roll.archivedUnbilledCount++;
    }
  }
  return roll;
}

// TASK-1618 trap 3 — the schema is multi-customer by design (createProject writes
// customerIds[] + primaryCustomerId, projects.js:408-425) but multi-customer is
// DEFERRED behind the portal IDOR. Dispatch can still create one, so this view
// must refuse to OPERATE on it: display-only, "manage in Dispatch". One predicate
// so every entry point asks the same question.
function _isMultiCustomerProject(p) {
  return Array.isArray(p && p.customerIds) && p.customerIds.length > 1;
}

// ── TASK-9414 pure project-filter helpers ─────────────────────────────────────
// PURE (no BillingState, no DOM) so tests/task9414-arap-project-filter.unit.cjs
// can run them in node:vm. Keep them that way.
//
// A ticket is in a project only through its JOB (jobs.projectId). So:
//   'project' = ticket has a jobId AND that job's projectId is the target.
//   'none'    = ticket has NO jobId, OR its job has no projectId, OR its job doc
//               is not loaded/known (COD Edge Case #3: the old job-id Set dropped
//               every jobless ticket, so "No project" could not reuse it).
function buildJobProjectIndex(jobs) {
  const idx = new Map();
  for (const j of (jobs || [])) {
    if (j && j.id && typeof j.projectId === 'string' && j.projectId) idx.set(j.id, j.projectId);
  }
  return idx;
}
function ticketProjectOf(t, idx) {
  if (!t || !t.jobId || !idx) return null;
  return idx.get(t.jobId) || null;
}
function ticketMatchesProjectFilter(t, mode, projectId, idx) {
  if (mode === 'none') return !ticketProjectOf(t, idx);
  if (mode === 'project') return !!projectId && ticketProjectOf(t, idx) === projectId;
  return true; // no filter
}
// Signature of every job → project link. The jobs snapshot re-renders Tickets
// only when this changes (a link/unlink from Dispatch), not on every job edit.
function jobProjectSignature(jobs) {
  const parts = [];
  for (const j of (jobs || [])) {
    if (j && j.id && typeof j.projectId === 'string' && j.projectId) parts.push(j.id + ':' + j.projectId);
  }
  return parts.sort().join('|');
}
// The dropdown's rows. `tickets` is the population the list shows WITHOUT the
// project filter, so every count is the number of rows picking it would show.
// selectedCustomerIds (Set) narrows the list to those companies' projects;
// empty = every live project, plus `currentProjectId` so the active choice never
// disappears from its own control. A project with no tickets here is still
// listed (Chris 10/5: "no projects in the drop down" read as broken when every
// project just lacked tickets) and carries `unlinked` when NO job is linked to
// it at all, so the control can say why it is empty. Multi-customer projects
// are listed LOCKED (AR/AP never operates on them — TASK-1618 trap 3).
// Deleting / archived / completed projects with no tickets here are left out.
function projectFilterOptions(tickets, projects, idx, selectedCustomerIds, currentProjectId) {
  const linked = new Set(idx ? idx.values() : []);
  const counts = new Map();
  let none = 0;
  let total = 0;
  for (const t of (tickets || [])) {
    total++;
    const pid = ticketProjectOf(t, idx);
    if (pid) counts.set(pid, (counts.get(pid) || 0) + 1);
    else none++;
  }
  const sel = selectedCustomerIds && selectedCustomerIds.size ? selectedCustomerIds : null;
  const items = [];
  for (const p of (projects || [])) {
    if (!p || !p.id) continue;
    const count = counts.get(p.id) || 0;
    const custIds = Array.isArray(p.customerIds) ? p.customerIds : [];
    const isCurrent = p.id === currentProjectId;
    if (sel) {
      const forSelected = custIds.some(c => sel.has(c)) || (p.primaryCustomerId && sel.has(p.primaryCustomerId));
      if (!forSelected && !count && !isCurrent) continue;
    }
    if (!count && !isCurrent && (p.deleting === true || p.status === 'archived' || p.status === 'completed')) continue;
    items.push({ id: p.id, name: String(p.name || '(unnamed project)'), count, locked: custIds.length > 1, unlinked: !linked.has(p.id) });
  }
  // Projects with tickets first (most first), then linked-but-none-here, then unlinked.
  items.sort((a, b) => (b.count - a.count) || (a.unlinked - b.unlinked) || a.name.localeCompare(b.name));
  return { total, none, items };
}
// ── end TASK-9414 pure helpers ────────────────────────────────────────────────

// Cached per jobs snapshot: BillingState.jobs is REPLACED (never mutated) on
// every snapshot, so array identity is the cache key.
let _jobProjectIdxCache = { jobs: null, idx: new Map() };
function _billingJobProjectIndex() {
  if (_jobProjectIdxCache.jobs !== BillingState.jobs) {
    _jobProjectIdxCache = { jobs: BillingState.jobs, idx: buildJobProjectIndex(BillingState.jobs) };
  }
  return _jobProjectIdxCache.idx;
}

// The shared project drill-down predicate. Used by BOTH renderTicketsList and
// exportFilteredTickets — see trap 1. TASK-9414: derived from the LIVE jobs
// every call (no captured Set), and knows the 'none' mode.
function _ticketInProjectFilter(t) {
  const mode = BillingState.ticketProjectMode;
  if (!mode) return true;
  return ticketMatchesProjectFilter(t, mode, BillingState.ticketProjectFilter, _billingJobProjectIndex());
}

// TASK-9414 — the project filter needs BOTH jobs (the link lives on the job) and
// the projects list (names). Until jobs land, every ticket would read "No project"
// — the silent lie renderBillingProjectsView was built to avoid — so callers show
// "Loading projects…" instead of a count.
function _projectFilterReady() {
  return !!(BillingState._jobsLoaded && BillingState._projectsLoaded);
}

// One entry point for the Tickets-view control, the job-subheader tag and the
// Projects-tab row click. value: '' = all, '__none__' = no project, else a projectId.
function setTicketProjectFilter(value) {
  const v = String(value || '');
  if (!v) {
    BillingState.ticketProjectMode = null;
    BillingState.ticketProjectFilter = null;
    BillingState.ticketProjectFilterName = null;
  } else if (v === '__none__') {
    BillingState.ticketProjectMode = 'none';
    BillingState.ticketProjectFilter = null;
    BillingState.ticketProjectFilterName = 'No project';
  } else {
    const p = BillingState.projects.find(x => x.id === v);
    if (!p) { window.showToast?.('That project is no longer here — refresh.', 'error'); return; }
    // Trap 3 belt — a replayed onclick string is trivial to forge.
    if (_isMultiCustomerProject(p)) {
      window.showToast?.('This project spans several customers — open it in Dispatch.', 'info');
      return;
    }
    BillingState.ticketProjectMode = 'project';
    BillingState.ticketProjectFilter = v;
    BillingState.ticketProjectFilterName = p.name || 'Project';
  }
  if (BillingState.ticketProjectMode) {
    // Mutual exclusion with the calendar/date job drill-down (both ways).
    BillingState.ticketJobFilter = null;
    BillingState.ticketJobFilterName = null;
    // A project spans months; the bounded ticket window would under-report it.
    // "No project" stays on the loaded window (independent review: pulling the
    // whole history for the most common pick is a heavy read, and it made the
    // count on the option disagree with the rows it then showed).
    if (BillingState.ticketProjectMode === 'project') ensureFullTicketHistory();
    _flushSecondarySubs();
    if (!BillingState._projectsLoaded) loadBillingProjects();
  }
  // Rows now hidden must not stay selected (same as a status switch).
  clearTicketSelection();
  if (BillingState.viewMode !== 'tickets') switchBillingView('tickets');
  else renderBillingView();
}

// The control itself. One builder, placed twice (desktop band 2 / phone compact
// strip), and REPAINTED by renderTicketsList with the counts it just measured —
// same pattern as the delivery chips. A plain <select> on both: 12+ projects as
// chips would wrap band 2 to a third row and be unscrollable at 375px (COD UX).
function _ticketProjectFilterHtml(opts, variant) {
  const mode = BillingState.ticketProjectMode;
  const cur = mode === 'none' ? '__none__' : (mode === 'project' ? BillingState.ticketProjectFilter : '');
  const active = !!mode;
  const mobile = variant === 'mobile';
  const selStyle = `max-width:${mobile ? '100%' : '240px'};${mobile ? 'width:100%;font-size:16px;' : 'font-size:0.85rem;'}padding:6px 8px;border-radius:8px;border:1.5px solid ${active ? '#3b82f6' : '#e2e8f0'};background:${active ? '#EFF6FF' : '#fff'};color:var(--text-primary);font-weight:${active ? '600' : '500'};`;
  if (!opts) {
    // Jobs or projects still loading — never print a number we haven't measured.
    // TASK-9414: an ACTIVE filter keeps its name visible while it loads, so a
    // narrowed list is never unlabelled.
    const label = active ? `${escapeHtml(BillingState.ticketProjectFilterName || 'Project')} — loading…` : 'Loading projects…';
    return `<select class="tpf-select" disabled aria-label="Project filter" style="${selStyle}opacity:0.75;"><option>${label}</option></select>`;
  }
  const o = [];
  o.push(`<option value="" ${cur === '' ? 'selected' : ''}>All projects (${opts.total})</option>`);
  o.push(`<option value="__none__" ${cur === '__none__' ? 'selected' : ''}>No project (${opts.none})</option>`);
  for (const it of opts.items) {
    o.push(`<option value="${window.escAttr(it.id)}" ${cur === it.id ? 'selected' : ''} ${it.locked ? 'disabled' : ''}>${escapeHtml(it.name)} ${it.unlinked ? '— no jobs linked yet' : `(${it.count})`}${it.locked ? ' — multi-customer, manage in Dispatch' : ''}</option>`);
  }
  // A filter on a project that has since vanished must still SHOW as active.
  if (mode === 'project' && !opts.items.some(it => it.id === cur)) {
    o.push(`<option value="${window.escAttr(cur)}" selected>${escapeHtml(BillingState.ticketProjectFilterName || 'Project')} (0)</option>`);
  }
  const clearBtn = active
    ? `<button type="button" class="tpf-clear" title="Show all projects" aria-label="Clear project filter" onclick="window.BillingModule.setTicketProjectFilter('')" style="background:#e2e8f0;border:none;border-radius:50%;width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;"><span class="material-icons" style="font-size:14px;color:var(--text-secondary);">close</span></button>`
    : '';
  return `<span class="material-icons" style="font-size:16px;color:${active ? '#2563EB' : 'var(--text-secondary)'};flex-shrink:0;">folder_special</span>
    <select class="tpf-select${active ? ' tpf-active' : ''}" aria-label="Project filter" title="${BillingState._ticketsFullHistory ? 'Counts are for the tickets in this view' : 'Counts are for the tickets loaded here — picking a project loads its older tickets too'}" onchange="window.BillingModule.setTicketProjectFilter(this.value)" style="${selStyle}">${o.join('')}</select>${clearBtn}`;
}

// Should the control render at all? Tenants with no projects (most of them)
// don't get an empty control — unless a filter is somehow active, which must
// always be visible.
function _ticketProjectControlShown() {
  if (BillingState.ticketProjectMode) return true;
  if (!BillingState._projectsLoaded) return true;   // "Loading projects…" until we know
  return BillingState.projects.length > 0;
}

// Repaint both copies of the control from renderTicketsList.
function _repaintTicketProjectFilter(population) {
  const slots = document.querySelectorAll('.tpf-slot');
  if (!slots.length) return;
  const shown = _ticketProjectControlShown();
  let opts = null;
  // population === null = not measured yet (tickets or jobs still loading).
  if (shown && _projectFilterReady() && Array.isArray(population)) {
    opts = projectFilterOptions(population, BillingState.projects, _billingJobProjectIndex(),
      BillingState.ticketCustomerFilter, BillingState.ticketProjectMode === 'project' ? BillingState.ticketProjectFilter : null);
  }
  slots.forEach(slot => {
    slot.style.display = shown ? 'inline-flex' : 'none';
    if (shown) slot.innerHTML = _ticketProjectFilterHtml(opts, slot.getAttribute('data-variant'));
  });
}

// The small folder tag on a linked job's subheader. Click = filter to it.
function _jobProjectTagHtml(projectId) {
  if (!projectId) return '';
  const p = BillingState.projects.find(x => x.id === projectId);
  const name = p ? (p.name || 'Project') : (BillingState._projectsLoaded ? 'Project' : '…');
  const locked = p && _isMultiCustomerProject(p);
  const base = 'display:inline-flex;align-items:center;gap:2px;margin-left:6px;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;background:#F3E8FF;color:#6B21A8;border:1px solid #E9D5FF;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
  if (!p || locked) {
    return `<span class="billing-job-project-tag" style="${base}" title="${locked ? 'Multi-customer project — manage in Dispatch' : 'Project'}"><span class="material-icons" style="font-size:12px;">folder_special</span>${escapeHtml(name)}</span>`;
  }
  return `<button type="button" class="billing-job-project-tag" style="${base}cursor:pointer;" title="Show only this project's tickets"
    onclick="event.stopPropagation();window.BillingModule.setTicketProjectFilter('${window.escJsAttr(projectId)}')"><span class="material-icons" style="font-size:12px;">folder_special</span>${escapeHtml(name)}</button>`;
}

function renderBillingProjectsView(area) {
  if (!area) return;

  // 🔴 TRAP 2 — a project's LIFETIME Invoiced/Paid computed off the bounded live
  // window is simply wrong, and wrong SILENTLY. The ticket listener is capped at
  // BILLING_TICKET_WINDOW_DAYS/BILLING_TICKET_LIMIT (:4974); invoiced-and-unpaid
  // is routinely net-90+, and unbilled freight sits 6-9 months in trucking. Same
  // upgrade the invoiced/paid/written-off/archived/draft/ready tabs already do.
  ensureFullTicketHistory();
  // Jobs are a DEFERRED phase-2 listener (TASK-1245) and this whole view is keyed
  // on jobs.projectId — without them every project reads as $0 with no tickets,
  // which looks exactly like real data. Flush rather than render a lie.
  _flushSecondarySubs();

  // 🔴 GATE ON JOBS, NOT JUST PROJECTS. Every number in this table is derived by
  // walking jobs.projectId, so rendering before the jobs snapshot lands shows a
  // full table of "0 jobs · $0" — indistinguishable from a project that genuinely
  // has no work on it. A wrong number that looks right is worse than a spinner.
  // (The jobs listener re-renders this view on arrival; see _runSecondarySubs.)
  if (!BillingState._projectsLoaded || !BillingState._jobsLoaded) {
    if (!BillingState._projectsLoaded) loadBillingProjects();
    area.innerHTML = `<div class="loading-state" style="padding:60px 0;text-align:center;">
        <div class="loading-spinner"></div>
        <p style="color:var(--text-secondary);margin-top:12px;">Loading projects…</p>
      </div>`;
    return;
  }

  _loadProjectsPrefs();
  const rows = _billingProjectRows();

  if (!rows.length) {
    area.innerHTML = `
      <div class="billing-projects-empty" style="max-width:620px;margin:48px auto;text-align:center;padding:0 16px;">
        <span class="material-icons" style="font-size:44px;color:var(--brand-blue);">folder_special</span>
        <h3 style="margin:12px 0 8px;color:var(--text-primary);">No projects yet</h3>
        <p style="color:var(--text-secondary);font-size:0.9rem;line-height:1.5;margin:0 0 var(--spacing-md);">
          A project is one customer engagement, however many job records the field
          created for it. Open <strong>Tickets</strong>, tick the tickets that belong
          to the same engagement, and choose <strong>Add to project…</strong> —
          their jobs are grouped (each job stays as it is) and the money rolls up here.
        </p>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">
          <button class="btn btn-primary bpv-new" onclick="window.BillingModule.newBillingProject()">
            <span class="material-icons">create_new_folder</span> New project
          </button>
          <button class="btn btn-secondary" onclick="window.BillingModule.switchBillingView('tickets')">
            <span class="material-icons">receipt_long</span> Go to Tickets
          </button>
        </div>
      </div>`;
    return;
  }

  // Lacey's ask: projects grouped by customer, with filters. The toolbar is
  // painted ONCE here and the table below it is repainted by
  // _renderBillingProjectsTable, so typing in the search box never loses focus.
  const groupsAll = _billingProjectGroups(rows);
  const curCust = BillingState.projectsCustomer;
  const custOpts = groupsAll.map(g =>
    `<option value="${window.escAttr(g.key)}" ${g.key === curCust ? 'selected' : ''}>${escapeHtml(g.label)} (${g.rows.length})</option>`
  ).join('');

  area.innerHTML = `
    <div class="billing-projects-view" style="margin-top:12px;">
      <div class="bpv-toolbar" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
        <div class="bpv-chips" style="display:flex;gap:6px;flex-wrap:wrap;"></div>
        <select class="bpv-customer" aria-label="Customer"
                onchange="window.BillingModule.setProjectsCustomer(this.value)"
                style="max-width:240px;font-size:0.85rem;padding:6px 8px;border-radius:8px;border:1.5px solid ${curCust ? '#3b82f6' : '#e2e8f0'};background:${curCust ? '#EFF6FF' : '#fff'};color:var(--text-primary);">
          <option value="">All customers (${groupsAll.length})</option>${custOpts}
        </select>
        <div style="position:relative;flex:1 1 180px;max-width:280px;">
          <span class="material-icons" style="position:absolute;left:8px;top:50%;transform:translateY(-50%);font-size:16px;color:var(--text-secondary);">search</span>
          <input type="search" class="bpv-search" placeholder="Search project or PO" aria-label="Search projects"
                 value="${window.escAttr(BillingState.projectsSearch)}"
                 oninput="window.BillingModule.setProjectsSearch(this.value)"
                 style="width:100%;box-sizing:border-box;font-size:0.85rem;padding:6px 8px 6px 30px;border-radius:8px;border:1.5px solid #e2e8f0;background:#fff;color:var(--text-primary);">
        </div>
        ${/* TASK-9414 C — make a project right here (createBillingProject). */''}
        <button type="button" class="btn btn-sm btn-primary bpv-new" onclick="window.BillingModule.newBillingProject()"
                title="Create a project for one customer, then pick its jobs" style="margin-left:auto;">
          <span class="material-icons" style="font-size:16px;">create_new_folder</span> New project
        </button>
      </div>
      <div style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:10px;">
        Unbilled = draft + ready · Invoiced = billed, not yet paid · Paid = money in. Written-off work is in none of them.
      </div>
      <div class="bpv-table"></div>
    </div>`;
  _renderBillingProjectsTable(rows);
}

// Every project with its money rolled up, money-first. Shared by the view and
// its filter repaints so both read the same numbers.
function _billingProjectRows() {
  return BillingState.projects
    .map(p => ({ p, roll: _billingProjectRollup(p.id) }))
    .sort((a, b) => {
      // Money first — the biller opens this to find what is still owed.
      const d = (b.roll.unbilled + b.roll.invoiced) - (a.roll.unbilled + a.roll.invoiced);
      if (d) return d;
      return String(a.p.name || '').localeCompare(String(b.p.name || ''));
    });
}

// TASK-9700 — the shared status predicate (missing status = Active), so this list
// and Dispatch -> Projects can never disagree about which projects are active.
function _billingProjectIsOpen(p) {
  return window.ProjectStatusFilter.matches(p, 'active');
}

// Owed = anything not yet in the bank: unbilled, invoiced, or archived-unbilled.
function _billingProjectOwed(roll) {
  return roll.unbilled + roll.invoiced + roll.archivedUnbilled;
}

// One group per customer — TASK-9700: the SAME key, label and A–Z order as
// Dispatch -> Projects (window.ProjectStatusFilter), "No customer" last. The one
// AR/AP-only difference: a multi-customer project goes in its own "Multiple
// customers" group (just before "No customer") — it is locked here (trap 3) and
// its money spans customers, so it must not inflate one customer's subtotal.
const BILLING_MULTI_GROUP_KEY = '__multi__';
function _billingProjectGroups(rows) {
  const PSF = window.ProjectStatusFilter;
  const customerById = new Map(BillingState.customers.map(c => [c.id, c]));
  const nameById = id => customerById.get(id)?.name || customerById.get(id)?.company || '';
  const groups = new Map();
  for (const r of rows) {
    const multi = _isMultiCustomerProject(r.p);
    const key = multi ? BILLING_MULTI_GROUP_KEY : PSF.customerGroupKey(r.p);
    if (!groups.has(key)) groups.set(key, { key, label: multi ? 'Multiple customers' : '', rows: [] });
    groups.get(key).rows.push(r);
  }
  // Header from the WHOLE group (shared with Dispatch), never the first row —
  // rows here sort money-first, Dispatch's by run state.
  for (const g of groups.values()) {
    if (g.key !== BILLING_MULTI_GROUP_KEY) g.label = PSF.customerGroupLabelFor(g.rows.map(r => r.p), nameById);
  }
  return [...groups.values()].sort((a, b) => PSF.compareGroups(a, b, [BILLING_MULTI_GROUP_KEY]));
}

function _renderBillingProjectsTable(rowsIn) {
  const host = document.querySelector('.billing-projects-view .bpv-table');
  if (!host) return;
  const rows = rowsIn || _billingProjectRows();

  // Customer + search narrow the set first; the chip counts are measured on
  // what is left, so a count never promises rows the next click won't show.
  const q = String(BillingState.projectsSearch || '').trim().toLowerCase();
  const custKey = BillingState.projectsCustomer;
  const groupKeyOf = new Map();
  for (const g of _billingProjectGroups(rows)) for (const r of g.rows) groupKeyOf.set(r.p.id, g.key);
  const narrowed = rows.filter(r => {
    if (custKey && groupKeyOf.get(r.p.id) !== custKey) return false;
    if (q) {
      const hay = `${r.p.name || ''} ${r.p.poNumber || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // TASK-9700 — Active / Completed / Archived / All are the SHARED chips (same
  // words, order, default and predicate as Dispatch -> Projects); "Money owed"
  // is the AR/AP-only extra, after them.
  const PSF = window.ProjectStatusFilter;
  const tests = { owed: r => _billingProjectOwed(r.roll) > 0 };
  for (const c of PSF.CHIPS) tests[c.key] = r => PSF.matches(r.p, c.key);
  const mode = _billingProjectsMode();
  const chipDefs = [
    ...PSF.CHIPS.map(c => [c.key, c.label, c.tip]),
    ['owed', 'Money owed', 'Unbilled or invoiced, not yet paid']
  ];
  const chipsEl = document.querySelector('.billing-projects-view .bpv-chips');
  if (chipsEl) {
    chipsEl.innerHTML = chipDefs.map(([k, label, tip]) => {
      const on = k === mode;
      const n = narrowed.filter(tests[k]).length;
      return `<button type="button" title="${tip}" data-status-chip="${k}" aria-pressed="${on ? 'true' : 'false'}" onclick="window.BillingModule.setProjectsFilter('${k}')"
        style="padding:5px 12px;border-radius:999px;font-size:0.8rem;font-weight:600;cursor:pointer;border:1.5px solid ${on ? '#2563EB' : '#e2e8f0'};background:${on ? '#2563EB' : '#fff'};color:${on ? '#fff' : 'var(--text-primary)'};">${label} <span style="opacity:${on ? '0.85' : '0.6'};font-weight:500;">${n}</span></button>`;
    }).join('');
  }

  const shown = narrowed.filter(tests[mode]);
  // TASK-9700 — "Money owed" (incl. a remembered legacy default) hides every $0
  // project. Say so above the table, with a one-tap way back to Active.
  const owedNote = mode === 'owed' && narrowed.length > shown.length
    ? `<div class="bpv-owed-note" style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:0.8rem;color:var(--text-primary);background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;padding:6px 10px;margin-bottom:8px;">
        <span class="material-icons" style="font-size:16px;color:#B45309;">filter_alt</span>
        Showing only projects with money owed — ${narrowed.length - shown.length} more hidden.
        <button type="button" onclick="window.BillingModule.setProjectsFilter('active')"
          style="background:none;border:none;color:#2563EB;font-weight:600;cursor:pointer;padding:0;min-height:32px;">Show Active</button>
      </div>`
    : '';
  if (!shown.length) {
    const hidden = rows.length - shown.length;
    host.innerHTML = `
      ${owedNote}<div style="padding:36px 16px;text-align:center;color:var(--text-secondary);background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);">
        No projects match these filters.${hidden ? ` ${hidden} hidden —` : ''}
        <button type="button" onclick="window.BillingModule.setProjectsFilter('all', true)"
          style="background:none;border:none;color:#2563EB;font-weight:600;cursor:pointer;padding:0;">show all</button>
      </div>`;
    return;
  }

  const groups = _billingProjectGroups(shown);
  const tot = shown.reduce((s, r) => ({
    unbilled: s.unbilled + r.roll.unbilled,
    invoiced: s.invoiced + r.roll.invoiced,
    paid: s.paid + r.roll.paid,
    hours: s.hours + r.roll.hours
  }), { unbilled: 0, invoiced: 0, paid: 0, hours: 0 });

  const statusChip = (st) => {
    const s = PSF.statusOf({ status: st });
    const color = s === 'active' ? '#16A34A' : (s === 'completed' ? '#0EA5E9' : '#64748B');
    return `<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:0.72rem;font-weight:600;background:${color};color:#fff;">${escapeHtml(PSF.statusLabel({ status: st }))}</span>`;
  };
  const num = 'padding:10px 12px;text-align:right;';

  const projectRow = ({ p, roll }) => {
    const multi = _isMultiCustomerProject(p);
    const jobCount = roll.jobIds.size;
    // Read-only rows are not clickable at all (trap 3) — the affordance and the
    // refusal have to agree, or the user learns the app is broken.
    const clickable = !multi;
    const sub = jobCount || roll.ticketCount
      ? `${jobCount} job${jobCount !== 1 ? 's' : ''} · ${roll.ticketCount} ticket${roll.ticketCount !== 1 ? 's' : ''}`
      : 'No work linked yet';
    return `
      <tr class="billing-project-row" data-project="${window.escAttr(p.id)}"
          style="${clickable ? 'cursor:pointer;' : ''}border-bottom:1px solid var(--border-light);"
          ${multi ? 'title="Locked here: this project covers more than one customer. Open it in Dispatch to manage it."' : ''}
          ${clickable ? `onclick="window.BillingModule.showProjectTickets('${window.escAttr(p.id)}')"` : ''}>
        <td style="padding:10px 12px 10px 36px;">
          <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(p.name || '(unnamed project)')}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${sub}</div>
          ${roll.archivedUnbilledCount ? `
            <div style="font-size:0.72rem;color:#B45309;margin-top:3px;" title="Archived tickets can't be invoiced until they're unarchived">
              <span class="material-icons" style="font-size:13px;vertical-align:-2px;">inventory_2</span>
              ${roll.archivedUnbilledCount} archived, ${formatCurrency(roll.archivedUnbilled)} not billed
            </div>` : ''}
          ${multi ? `
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:3px;">
              <span class="material-icons" style="font-size:13px;vertical-align:-2px;">lock</span>
              ${p.customerIds.length} customers — manage in Dispatch
            </div>` : ''}
        </td>
        <td style="${num}font-weight:600;color:${roll.unbilled > 0 ? '#B45309' : 'var(--text-secondary)'};">${formatCurrency(roll.unbilled)}</td>
        <td style="${num}color:${roll.invoiced > 0 ? 'var(--text-primary)' : 'var(--text-secondary)'};">${formatCurrency(roll.invoiced)}</td>
        <td style="${num}color:${roll.paid > 0 ? 'var(--text-primary)' : 'var(--text-secondary)'};">${formatCurrency(roll.paid)}</td>
        <td style="${num}color:var(--text-primary);">${roll.hours ? roll.hours.toFixed(1) : '—'}</td>
        <td style="padding:10px 12px;color:var(--text-primary);">${escapeHtml((p.poNumber || '').trim() || '—')}</td>
        <td style="padding:10px 12px;">${statusChip(p.status)}</td>
        <td style="padding:10px 12px;text-align:right;white-space:nowrap;">
          ${multi ? '' : `
          <button class="btn btn-sm btn-secondary bpv-add-jobs" title="Put more of this customer’s jobs into this project — the jobs stay separate"
                  onclick="event.stopPropagation();window.BillingModule.addJobsToBillingProject('${window.escJsAttr(p.id)}')">
            <span class="material-icons" style="font-size:15px;">playlist_add</span> Add jobs
          </button>
          <button class="btn btn-sm btn-secondary bpv-edit" title="Rename the project or change its PO"
                  onclick="event.stopPropagation();window.BillingModule.editBillingProject('${window.escJsAttr(p.id)}')">
            <span class="material-icons" style="font-size:15px;">edit</span> Edit
          </button>`}
          <button class="btn btn-sm btn-secondary" title="Open the full project in Dispatch — timeline, docs, notes, change orders"
                  onclick="event.stopPropagation();window.BillingModule.openProjectInDispatch('${window.escJsAttr(p.id)}')">
            <span class="material-icons" style="font-size:15px;">open_in_new</span> Open
          </button>
        </td>
      </tr>`;
  };

  const body = groups.map(g => {
    // A search must never be hidden by a remembered collapse.
    const collapsed = !q && BillingState.projectsCollapsed.has(g.key);
    const sum = g.rows.reduce((s, r) => ({
      unbilled: s.unbilled + r.roll.unbilled,
      invoiced: s.invoiced + r.roll.invoiced,
      paid: s.paid + r.roll.paid,
      hours: s.hours + r.roll.hours
    }), { unbilled: 0, invoiced: 0, paid: 0, hours: 0 });
    const head = `
      <tr class="billing-project-group" style="background:var(--bg-elevated);border-top:2px solid var(--border-light);cursor:pointer;"
          onclick="window.BillingModule.toggleProjectsGroup('${window.escJsAttr(g.key)}')"
          aria-expanded="${collapsed ? 'false' : 'true'}">
        <td style="padding:10px 12px;font-weight:700;color:var(--text-primary);">
          <span class="material-icons" style="font-size:18px;vertical-align:-4px;color:var(--text-secondary);">${collapsed ? 'chevron_right' : 'expand_more'}</span>
          ${escapeHtml(g.label)}
          <span style="font-weight:500;font-size:0.78rem;color:var(--text-secondary);margin-left:4px;">${g.rows.length} project${g.rows.length !== 1 ? 's' : ''}</span>
        </td>
        <td style="${num}font-weight:700;color:${sum.unbilled > 0 ? '#B45309' : 'var(--text-secondary)'};">${formatCurrency(sum.unbilled)}</td>
        <td style="${num}font-weight:700;color:var(--text-primary);">${formatCurrency(sum.invoiced)}</td>
        <td style="${num}font-weight:700;color:var(--text-primary);">${formatCurrency(sum.paid)}</td>
        <td style="${num}font-weight:700;color:var(--text-primary);">${sum.hours ? sum.hours.toFixed(1) : '—'}</td>
        <td colspan="3"></td>
      </tr>`;
    return head + (collapsed ? '' : g.rows.map(projectRow).join(''));
  }).join('');

  const th = (label, right) => `<th style="padding:10px 12px;color:var(--text-secondary);font-weight:600;${right ? 'text-align:right;' : ''}">${label}</th>`;
  const hiddenCount = rows.length - shown.length;
  host.innerHTML = `${owedNote}
    <div style="overflow-x:auto;background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);">
      <table style="width:100%;border-collapse:collapse;font-size:0.875rem;min-width:940px;">
        <thead>
          <tr style="text-align:left;border-bottom:1px solid var(--border-light);">
            ${th('Customer / Project')}${th('Unbilled', 1)}${th('Invoiced', 1)}${th('Paid', 1)}${th('Hours', 1)}${th('PO')}${th('Status')}<th></th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
        <tfoot>
          <tr style="background:var(--bg-elevated);font-weight:700;border-top:2px solid var(--border-light);">
            <td style="padding:10px 12px;color:var(--text-primary);">Total
              <span style="font-weight:500;font-size:0.78rem;color:var(--text-secondary);">${shown.length} project${shown.length !== 1 ? 's' : ''}${hiddenCount ? ` · ${hiddenCount} hidden by filters` : ''}</span>
            </td>
            <td style="${num}color:var(--text-primary);">${formatCurrency(tot.unbilled)}</td>
            <td style="${num}color:var(--text-primary);">${formatCurrency(tot.invoiced)}</td>
            <td style="${num}color:var(--text-primary);">${formatCurrency(tot.paid)}</td>
            <td style="${num}color:var(--text-primary);">${tot.hours ? tot.hours.toFixed(1) : '—'}</td>
            <td colspan="3"></td>
          </tr>
        </tfoot>
      </table>
    </div>`;
}

// `reset` (from the empty state's "show all") also clears customer + search.
// Filter + collapsed groups survive a reload (per browser, view-only).
const PROJECTS_PREF_KEY = 'dd_billing_projects_prefs';
function _saveProjectsPrefs() {
  try {
    localStorage.setItem(PROJECTS_PREF_KEY, JSON.stringify({
      filter: BillingState.projectsFilter,
      collapsed: [...BillingState.projectsCollapsed]
    }));
  } catch (_) { /* private mode — fine */ }
}
function _loadProjectsPrefs() {
  if (BillingState._projectsPrefsLoaded) return;
  BillingState._projectsPrefsLoaded = true;
  try {
    const p = JSON.parse(localStorage.getItem(PROJECTS_PREF_KEY) || 'null');
    // TASK-9700 — a remembered 'owed' is kept (its chip shows selected + the
    // "N more hidden" note); the retired 'closed' falls back to the default.
    if (p && typeof p.filter === 'string') BillingState.projectsFilter = window.ProjectStatusFilter.resolve(p.filter, BILLING_EXTRA_PROJECT_CHIPS);
    // Group keys moved to the shared format ('id:<customerId>'); carry a
    // remembered bare-id collapse across so nothing re-expands unexpectedly.
    if (p && Array.isArray(p.collapsed)) {
      BillingState.projectsCollapsed = new Set(p.collapsed.map(String).map(k =>
        (k.startsWith('id:') || k.startsWith('name:') || k.startsWith('__')) ? k : 'id:' + k));
    }
  } catch (_) { /* corrupt or blocked — defaults */ }
}

const BILLING_EXTRA_PROJECT_CHIPS = ['owed'];
function _billingProjectsMode() {
  return window.ProjectStatusFilter.resolve(BillingState.projectsFilter, BILLING_EXTRA_PROJECT_CHIPS);
}

function setProjectsFilter(mode, reset) {
  BillingState.projectsFilter = window.ProjectStatusFilter.resolve(mode, BILLING_EXTRA_PROJECT_CHIPS);
  _saveProjectsPrefs();
  if (reset) {
    BillingState.projectsCustomer = '';
    BillingState.projectsSearch = '';
    const area = document.querySelector('.billing-projects-view')?.parentElement;
    if (area) { renderBillingProjectsView(area); return; }
  }
  _renderBillingProjectsTable();
}

function setProjectsCustomer(key) {
  BillingState.projectsCustomer = key || '';
  const sel = document.querySelector('.billing-projects-view .bpv-customer');
  if (sel) {
    sel.style.borderColor = key ? '#3b82f6' : '#e2e8f0';
    sel.style.background = key ? '#EFF6FF' : '#fff';
  }
  _renderBillingProjectsTable();
}

function setProjectsSearch(text) {
  BillingState.projectsSearch = text || '';
  _renderBillingProjectsTable();
}

function toggleProjectsGroup(key) {
  const s = BillingState.projectsCollapsed;
  if (s.has(key)) s.delete(key); else s.add(key);
  _saveProjectsPrefs();
  _renderBillingProjectsTable();
}

// Row click → the existing ticket list, filtered to every job on this project.
function showProjectTickets(projectId) {
  const p = BillingState.projects.find(x => x.id === projectId);
  if (!p) { window.showToast?.('That project is no longer here — refresh.', 'error'); return; }
  // Trap 3 belt (Blast: "verify no other entry point bypasses that check"). The
  // row is already unclickable for a multi-customer project; this covers a
  // replayed onclick string, which is trivial to forge.
  if (_isMultiCustomerProject(p)) {
    window.showToast?.('This project spans several customers — open it in Dispatch.', 'info');
    return;
  }
  // TASK-9414 — same state + same predicate as the Tickets-view control. Status
  // resets to Active here (as before) because the row click means "show me this
  // project's work"; the control itself leaves the status pill alone.
  BillingState.filter = 'all';
  setTicketProjectFilter(projectId);
}

// Link-out to the heavy Dispatch detail (timeline / inventory / docs / notes /
// change orders). NOTHING of it is duplicated here.
//
// TASK-9744 — this used to navigateToView('projects'), a view that does not
// exist: the router fell to its default and painted "Coming Soon". Projects is a
// sub-view of Dispatch (painted into #dispatch-content), so navigate to
// Dispatch, wait for its handles, then reuse the board's own
// switch-to-Projects-and-open path (DispatchModule.openProjectFromBoard) — the
// TASK-9736 pattern (crm.js openProjectFromCrm).
//
// 🔴 window handles only: window.ProjectsModule is assigned INSIDE initProjects
//   and `import`ing projects.js / dispatch.js from here with a ?v= buster would
//   create a second module instance with empty state (pitfall #1).
// 🔴 #dispatch-content is the ready signal — it exists only while Dispatch is
//   painted, so a ProjectsModule left over from an earlier Dispatch visit can't
//   fire while Billing is still on screen. AppState.currentView is NOT a signal:
//   a programmatic navigateToView doesn't update it.
const BILLING_PROJECT_OPEN_WAIT_MS = 20000;
function openProjectInDispatch(projectId) {
  if (!projectId) return false;
  if (window.canAccessProjects?.() === false) { // TASK-9651
    window.showToast?.('This login does not have Projects. Ask an admin to turn on Projects for you.', 'warning');
    return false;
  }
  const access = typeof window.checkModuleAccess === 'function' ? window.checkModuleAccess('dispatch') : true;
  // TASK-9779 — an 811-only login gets the Dispatch view for the 811 window only.
  if (access === false || window.isMember811Only?.() === true) {
    window.showToast?.('Projects open in Dispatch, and this login does not have Dispatch.', 'warning');
    return false;
  }
  if (typeof window.navigateToView !== 'function') {
    window.showToast?.('Could not open the project — open it from Dispatch → Projects.', 'error');
    return false;
  }
  const ready = () => !!document.getElementById('dispatch-content')
    && typeof window.DispatchModule?.openProjectFromBoard === 'function'
    && typeof window.ProjectsModule?.openProjectDetail === 'function';
  if (!ready()) window.navigateToView('dispatch');
  const started = Date.now();
  const tick = () => {
    if (ready()) {
      Promise.resolve(window.DispatchModule.openProjectFromBoard(projectId))
        .catch(err => console.error('[Billing] openProjectFromBoard failed:', err));
      return;
    }
    // The user went somewhere else while we waited — don't yank them back.
    const h = String(window.location.hash || '');
    if (h && !/^#\/(dispatch|billing|ap)(\/|\?|$)/.test(h)) return;
    // The nav gate can refuse (trial wall, module access, PIN) and shows its
    // own message; stop polling rather than spin forever.
    if (Date.now() - started > BILLING_PROJECT_OPEN_WAIT_MS) return;
    setTimeout(tick, 150);
  };
  setTimeout(tick, 0);
  return true;
}

// ---- Part 2: "Group into project" -----------------------------------------
//
// Select N tickets → name the engagement, pick the job they all land on, set the
// PO once → ONE callable creates the project, links the job, and re-points the
// tickets. The write discipline is TASK-1581 slice 1's, and it now lives on the
// SERVER (functions/src/index.js groupTicketsIntoProject) because the operation
// crosses two dispatch-walled collections — see that header for why this is not a
// rules arm. Nothing here is optimistic: the live ticket listener brings the truth
// back, so what is on screen is always a subset of what actually landed.

function _newIdempotencyKey() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

async function groupSelectedTicketsIntoProject() {
  const tenant = BillingState.tenantId;
  if (!tenant) { window.showToast?.('Not loaded yet — refresh and try again.', 'error'); return; }
  // TASK-9651 — it makes a project and lands on Projects; without the module
  // the biller could never see what it made. (The callable is billing-gated.)
  if (window.canAccessProjects?.() === false) {
    window.showToast?.('Making a project needs Projects access on your login — ask the owner to add it.', 'warning', null, 7000);
    return;
  }
  // 🔴 TRAP 4, UI half — a double-click must not create two projects. This guard
  // (not the button's disabled attribute) is the durable one: updateSelectionActionBar
  // rebuilds the bar's innerHTML on every selection change, which would wipe a
  // disabled flag. The callable's idempotency key is the other half, and BOTH are
  // needed — the UI half alone loses to a retry or a flaky network.
  if (BillingState._groupBusy) return;

  const selected = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));
  if (!selected.length) return;

  // 🔴 SINGLE CUSTOMER. Enforced again on the server (which is what actually
  // makes it a constraint); this is the early, legible refusal.
  const custId = selected[0].customerId;
  if (!custId || !selected.every(t => t.customerId === custId)) {
    window.showToast?.('Select tickets from one customer at a time — a project belongs to a single customer.', 'error');
    return;
  }

  const movable = selected.filter(_ticketMoveEligible);
  const skipped = selected.filter(t => !_ticketMoveEligible(t));
  if (!movable.length) {
    window.showToast?.('None of the selected tickets can be grouped — invoiced, archived and locked tickets are fixed.', 'error');
    return;
  }

  // The job list and the already-on-a-project check both need jobs, which are a
  // deferred phase-2 listener.
  _flushSecondarySubs();
  const jobs = getFilteredJobsForCustomer(custId);
  if (!jobs.length) {
    window.showToast?.('No job on this customer to hang a project on.', 'error');
    return;
  }

  // 🔴 TRAP 5 — a ticket whose job ALREADY belongs to another project must not be
  // silently re-parented. Named in the dialog here, re-checked on server truth in
  // the callable, and counted in the result toast. Same partition-and-report shape
  // slice 1 uses for ineligible tickets.
  const jobById = new Map(BillingState.jobs.map(j => [j.id, j]));
  const alreadyOnProject = movable.filter(t => {
    const j = jobById.get(t.jobId);
    return !!(j && j.projectId);
  });
  const willMove = movable.filter(t => !alreadyOnProject.includes(t));

  const customerName = BillingState.customers.find(c => c.id === custId)?.name || '';
  const choice = await _groupIntoProjectModal({
    jobs,
    tickets: movable,
    willMoveCount: willMove.length,
    skippedCount: skipped.length,
    alreadyOnProject,
    customerName
  });
  if (!choice) return;

  const { projectName, targetJobId } = choice;
  const projectPo = poNormalizeForSave(choice.poNumber);

  // Re-validate the picked job against state before spending a round trip. The
  // server re-verifies it (and re-verifies it per chunk) — this is the friendly
  // refusal, not the constraint.
  const targetJob = BillingState.jobs.find(j => j.id === targetJobId);
  if (!targetJob) { window.showToast?.('That job is no longer here — reload and try again.', 'error'); return; }
  if (targetJob.customerId !== custId) {
    window.showToast?.('That job belongs to a different customer — nothing was changed.', 'error');
    return;
  }
  if (targetJob.projectId) {
    window.showToast?.('That job is already part of a project — pick another, or open it in Dispatch.', 'error');
    return;
  }

  // 🔴 NEVER SILENTLY REWRITE A PO. Same three-way dialog slice 1 uses: adopting
  // the project PO is a books change, and leaving a stale one gets the invoice
  // auto-rejected by the customer's portal. Both silent options are wrong.
  const poMismatched = willMove.filter(t => poNormalizeForSave(t.poNumber) !== projectPo);
  let setPoOnTickets = false;
  if (poMismatched.length) {
    const poChoice = await _confirmPoReconcile(poMismatched, projectPo, projectName);
    if (poChoice === null) return;      // cancelled — nothing is created
    setPoOnTickets = poChoice === 'set';
  }

  BillingState._groupBusy = true;
  const btn = document.getElementById('billing-group-project-btn');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; btn.style.cursor = 'wait'; }
  try {
    const call = httpsCallable(functions, 'groupTicketsIntoProject');
    // tenantId is NOT sent — the callable binds it from the auth claim.
    const res = await call({
      projectName,
      customerId: custId,
      targetJobId,
      ticketIds: movable.map(t => t.id),
      poNumber: projectPo,
      setPoOnTickets,
      idempotencyKey: _newIdempotencyKey()
    });
    const r = (res && res.data) || {};

    clearTicketSelection();
    // Force a re-read so the new row is actually there when we land on it.
    BillingState._projectsLoaded = false;
    await loadBillingProjects();

    const money = r.movedTotal
      ? ` ($${Number(r.movedTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
      : '';
    const tails = [];
    if (skipped.length) tails.push(`${skipped.length} skipped (invoiced/archived/locked).`);
    if (r.skippedIneligible?.length) tails.push(`${r.skippedIneligible.length} became invoiced/archived while you were choosing and ${r.skippedIneligible.length !== 1 ? 'were' : 'was'} left alone.`);
    if (r.skippedOtherProject?.length) tails.push(`${r.skippedOtherProject.length} already belong to another project and ${r.skippedOtherProject.length !== 1 ? 'were' : 'was'} left alone.`);
    if (r.staleSkipped) tails.push(`${r.staleSkipped} changed underneath the selection.`);
    if (r.stoppedEarly) tails.push('Stopped early — the target job changed customer mid-move. Reload before invoicing.');
    if (r.reused) tails.push('(This matched a project you just created, so nothing was duplicated.)');
    // TASK-9355 S2 — the server twin re-derived the job-derived fields; report it the same way the bulk move does.
    if (r.jobChange) { const _jt = _jobChangeAggregateTail(r.jobChange); if (_jt) tails.push(_jt.trim()); }

    // 🔴 A mid-move customer drift is NOT a success, even though tickets landed.
    // The server returns ok:true with stoppedEarly so the counts are honest; the
    // toast has to be honest too, or the next thing the biller does is invoice
    // against a half-moved set (COD Code Reviewer, on the real diff).
    const toastKind = r.stoppedEarly ? 'error' : ((r.moved || r.reused) ? 'success' : 'error');
    window.showToast?.(
      `"${r.projectName || projectName}" created — ${r.moved || 0} ticket${(r.moved || 0) !== 1 ? 's' : ''}${money} grouped.${tails.length ? ' ' + tails.join(' ') : ''}`,
      toastKind
    );
    switchBillingView('projects');
  } catch (err) {
    console.error('[Billing] groupTicketsIntoProject failed:', err);
    // Honest partial-failure reporting — the callable's `aborted` message already
    // says how many landed, so surface it verbatim rather than a generic failure.
    window.showToast?.(err?.message || 'Could not group these tickets — nothing was changed.', 'error');
  } finally {
    BillingState._groupBusy = false;
    const b = document.getElementById('billing-group-project-btn');
    if (b) { b.disabled = false; b.style.opacity = ''; b.style.cursor = ''; }
  }
}

// ============================================
// TASK-9414 — ADD JOBS TO A PROJECT (the jobs stay separate)
// ============================================
// Lacey (9/25): "tickets getting out of hand". Dispatch makes one job per day
// (HAMLIN PROJECT ×4 …); "Group into project" MERGES them onto one job, which is
// the structure she wants to keep. This links each job to a project instead —
// jobs keep their names, days and tickets; the project filter then finds them.
//
// SLICE C — ONE WRITE PATH for AR/AP: the billing callables (functions/src/
// index.js, slice B). createBillingProject (B2) makes the project, then
// linkJobsToProject (B1) links each job in its own server transaction — it
// re-reads the job + project, refuses a deleting / multi-customer / wrong-
// customer project, a job someone else moved, a job already on another
// project, and MORE invoiced tickets than the biller confirmed; fills the
// project PO only into a job with none; writes PROJECT_JOB_LINK /
// PROJECT_PO_INHERITED in the same commit. Edit (name / PO) is
// updateBillingProject (B3). Nothing here writes jobs.projectId or a project.
// tenantId is never sent — every callable binds it from the auth claim.
//
// ACCESS = MODULES: _canManageBillingProjects mirrors the server gate
// (_billingProjectsGate): window.canEditMoney (owner, or `billing` and not
// view-only) AND window.canAccessProjects (owner, or `projects`). So a
// billing-only biller WITH Projects can now do this without Dispatch.
//
// _canLinkJobsToProjects (dispatch module) stays for the TASK-9610 / 9810 move
// paths (re-parent, unlink), which still go through ProjectsModule.
function _canLinkJobsToProjects() {
  const st = window.AppState || {};
  if (st.isPublicAccess || st.driverMode || st.driverModePin) return false;
  if (st.isDemo || st.tenant === 'demo') return true;
  // TASK-9651 — the link reads the project (changeJobProject + the picker), and
  // project read is the 'projects' module (firestore.rules projRead).
  if (window.canAccessProjects?.() === false) return false;
  const ma = st.memberAccess;
  if (ma == null) return true; // no member doc = legacy single login (the owner)
  if (ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  if (ma.role === 'owner') return true;
  // TASK-9491 — view-only binds; the jobs rule refuses it too.
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
  // Legacy admin whose member doc pre-dates modules = full access (TASK-319).
  if (ma.role === 'admin' && (!Array.isArray(ma.modules) || ma.modules.length === 0)) return true;
  return Array.isArray(ma.modules) && ma.modules.includes('dispatch');
}
const LINK_NEEDS_DISPATCH_MSG = 'Putting jobs into a project needs Dispatch and Projects access on your login — ask the owner to add them.';

// TASK-9414 C — the client mirror of the callables' gate (see header).
function _canManageBillingProjects() {
  const st = window.AppState || {};
  if (st.isPublicAccess || st.driverMode || st.driverModePin) return false;
  if (typeof window.canEditMoney !== 'function' || typeof window.canAccessProjects !== 'function') return false;
  return window.canEditMoney() === true && window.canAccessProjects() === true;
}
const PROJECTS_NEEDS_ACCESS_MSG = 'Managing projects here needs Billing and Projects access on your login — ask the owner to add Projects.';
// Refuse with the plain sentence; true = blocked.
function _billingProjectsBlocked() {
  if (_canManageBillingProjects()) return false;
  const msg = window.canEditMoney?.() === true
    ? PROJECTS_NEEDS_ACCESS_MSG
    : (window.MONEY_VIEW_ONLY_MSG || 'View only — ask the owner to make this change.');
  window.showToast?.(msg, 'warning', null, 7000);
  return true;
}

// The three slice-B callables. Returns res.data; throws the HttpsError (its
// message is written for the biller — surface it verbatim).
async function _callBillingProjects(name, payload) {
  const call = httpsCallable(functions, name);
  const res = await call(payload);
  return (res && res.data) || {};
}

// PURE. Exact mirror of projects.js countBilledTickets (TASK-9289: invoiced /
// paid / invoiceNumber / invoicedAt / status archived|locked|sent|invoiced;
// deleted never counts). NOT a cross-import (Pitfall #1). Parity is asserted by
// tests/task9414-arap-project-filter.unit.cjs against the projects.js source, so
// a change to one without the other fails a test instead of drifting. The
// server twin (functions/src/shared/jobProjectLink.js) is held to the same
// table by tests/task9414-job-project-link.unit.cjs.
const LINK_BILLED_STATUSES = ['archived', 'locked', 'sent', 'invoiced'];
function linkBilledTicketCount(tickets) {
  let n = 0;
  for (const t of (tickets || [])) {
    if (!t || t.status === 'deleted') continue;
    if (t.invoiced === true || t.paid === true || !!t.invoiceNumber || !!t.invoicedAt ||
        LINK_BILLED_STATUSES.includes(t.status)) n++;
  }
  return n;
}

// PURE. Which of these jobs can be linked, and why the rest can't. Returns
// { eligible: job[], skipped: [{job|null, name, reason}], customerId }.
// One customer at a time (AR/AP projects are single-customer, trap 3); a job
// already on a project is refused (v1 never re-parents — unlink stays a
// Dispatch action); a customer-less job is refused (set a customer first).
function partitionJobsForLink(jobs) {
  const eligible = [];
  const skipped = [];
  let customerId = null;
  for (const j of (jobs || [])) {
    if (!j) continue;
    const name = String(j.title || j.name || '(untitled job)');
    if (j.projectId) { skipped.push({ job: j, name, reason: 'already in a project' }); continue; }
    if (!j.customerId) { skipped.push({ job: j, name, reason: 'has no customer' }); continue; }
    if (customerId === null) customerId = j.customerId;
    if (j.customerId !== customerId) { skipped.push({ job: j, name, reason: 'different customer' }); continue; }
    eligible.push(j);
  }
  return { eligible, skipped, customerId };
}

// PURE. Projects a job for `customerId` may be linked to: single-customer,
// naming that customer (jobProjectAdmits), not being deleted, not archived.
function linkableProjectsForCustomer(projects, customerId) {
  return (projects || []).filter(p => p && p.id
    && Array.isArray(p.customerIds) && p.customerIds.length === 1 && p.customerIds[0] === customerId
    && p.deleting !== true && p.status !== 'archived')
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// PURE. The biller's words for each linkJobsToProject refusal reason.
const LINK_REFUSAL_TEXT = {
  changed: 'someone else just put it in a project',
  'other-project': 'already in another project',
  customer: 'customer doesn’t match the project',
  'multi-customer': 'the project has several customers',
  'missing-project': 'the project is gone',
  deleting: 'the project is being deleted',
  billed: 'more invoiced tickets appeared on it — try again',
  missing: 'the job is gone',
  error: 'couldn’t save — try again'
};
function linkRefusalText(reason) {
  return LINK_REFUSAL_TEXT[reason] || 'not changed';
}

// PURE. Chunk job ids for linkJobsToProject (the server caps one call at 200).
const LINK_JOBS_PER_CALL = 200;
function chunkJobIdsForLink(ids, size = LINK_JOBS_PER_CALL) {
  const out = [];
  for (let i = 0; i < (ids || []).length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

async function _projectsLinkGate() {
  try {
    if (typeof window.ensureProjectsModule === 'function') {
      const pm = await window.ensureProjectsModule();
      if (pm && typeof pm.changeJobProject === 'function') return pm;
    }
  } catch (err) {
    console.warn('[Billing] ensureProjectsModule failed:', err);
  }
  const pm = window.ProjectsModule;
  return (pm && typeof pm.changeJobProject === 'function') ? pm : null;
}

// TASK-9610 (COD Code Reviewer) — loadBillingProjects() returns at once when a
// load is already running, so a move started mid-load could read a half-filled
// list and refuse a valid target. Load if needed, then wait out any in-flight load.
async function _ensureBillingProjectsSettled() {
  if (!BillingState._projectsLoaded) await loadBillingProjects();
  for (let i = 0; i < 100 && BillingState._projectsLoading; i++) await new Promise(r => setTimeout(r, 100));
}

// Selection bar → "Add to project…": the selected tickets' JOBS are linked.
async function addSelectedTicketsToProject() {
  if (_billingProjectsBlocked()) return;
  const selected = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id));
  if (!selected.length) return;
  _flushSecondarySubs();
  if (!BillingState._jobsLoaded) { window.showToast?.('Jobs are still loading — try again in a moment.', 'info'); return; }
  const jobById = new Map(BillingState.jobs.map(j => [j.id, j]));
  const noJob = selected.filter(t => !t.jobId || !jobById.has(t.jobId));
  const jobIds = [...new Set(selected.filter(t => t.jobId && jobById.has(t.jobId)).map(t => t.jobId))];
  const extraSkipped = noJob.length
    ? [{ job: null, name: `${noJob.length} ticket${noJob.length !== 1 ? 's' : ''} with no job`, reason: 'nothing to add — move them onto a job first' }]
    : [];
  await _addJobsToProjectFlow(jobIds.map(id => jobById.get(id)), { selectedIds: new Set(selected.map(t => t.id)), extraSkipped });
}

// Job subheader → folder+ icon: link just this job.
async function addJobToProject(jobId) {
  if (_billingProjectsBlocked()) return;
  _flushSecondarySubs();
  const job = BillingState.jobs.find(j => j.id === jobId);
  if (!job) { window.showToast?.('That job is no longer here — refresh.', 'error'); return; }
  await _addJobsToProjectFlow([job], { selectedIds: null, extraSkipped: [] });
}

// opts.fixedProjectId — the Projects tab's "Add jobs": the project is already
// chosen, so the dialog only confirms (no picker, no "New project").
async function _addJobsToProjectFlow(jobs, { selectedIds, extraSkipped, fixedProjectId = null }) {
  if (BillingState._linkBusy) return;
  const tenant = BillingState.tenantId;
  if (!tenant) return;
  const { eligible, skipped, customerId } = partitionJobsForLink(jobs);
  const allSkipped = [...skipped, ...(extraSkipped || [])];
  if (!eligible.length) {
    const why = allSkipped.slice(0, 4).map(s => `${s.name} (${s.reason})`).join('; ');
    window.showToast?.(`Nothing to add to a project${why ? ' — ' + why : ''}.`, 'error', null, 8000);
    return;
  }
  BillingState._linkBusy = true;
  try {
    await _ensureBillingProjectsSettled();
    let fixedProject = null;
    if (fixedProjectId) {
      fixedProject = linkableProjectsForCustomer(BillingState.projects, customerId).find(p => p.id === fixedProjectId) || null;
      if (!fixedProject) { window.showToast?.('Those jobs can’t go into that project — its customer doesn’t match, or it is locked.', 'error', null, 8000); return; }
    }
    // Server truth for the two numbers the dialog states: every ticket on these
    // jobs (not just the loaded window) — the blast line and the billed count.
    const byJob = new Map(eligible.map(j => [j.id, []]));
    try {
      const ids = eligible.map(j => j.id);
      for (let i = 0; i < ids.length; i += 30) {
        const snap = await getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('jobId', 'in', ids.slice(i, i + 30))));
        snap.docs.forEach(d => { const t = { id: d.id, ...d.data() }; if (byJob.has(t.jobId)) byJob.get(t.jobId).push(t); });
      }
    } catch (err) {
      // Fail CLOSED, like the gate: "could not look" is not "none".
      console.warn('[Billing] add-to-project ticket read failed:', err);
      window.showToast?.('Could not check these jobs’ tickets — nothing was changed. Try again.', 'error');
      return;
    }
    let otherTickets = 0;
    let billedTotal = 0;
    const billedByJob = {};
    for (const j of eligible) {
      const ts = (byJob.get(j.id) || []).filter(t => t.status !== 'deleted');
      if (selectedIds) otherTickets += ts.filter(t => !selectedIds.has(t.id)).length;
      billedByJob[j.id] = linkBilledTicketCount(ts);
      billedTotal += billedByJob[j.id];
    }
    const customerName = BillingState.customers.find(c => c.id === customerId)?.name || '';
    const choice = await _addToProjectModal({
      jobs: eligible, skipped: allSkipped, customerId, customerName,
      projects: fixedProject ? [fixedProject] : linkableProjectsForCustomer(BillingState.projects, customerId),
      fixed: !!fixedProject,
      otherTickets: selectedIds ? otherTickets : null, billedTotal
    });
    if (!choice) return;

    let projectId = choice.projectId;
    let created = false;
    let reusedProject = false;
    if (!projectId) {
      try {
        const r = await _callBillingProjects('createBillingProject', {
          name: choice.newName, customerId, poNumber: choice.newPo || '', idempotencyKey: _newIdempotencyKey()
        });
        projectId = r.projectId || null;
        created = r.created === true;
        reusedProject = r.reused === true;
      } catch (err) {
        console.error('[Billing] createBillingProject failed:', err);
        window.showToast?.(err?.message || 'Could not create the project — nothing was changed.', 'error', null, 8000);
        return;
      }
      if (!projectId) { window.showToast?.('Could not create the project — nothing was changed.', 'error'); return; }
    }

    // One server transaction per job; up to 200 jobs per call.
    const results = [];
    let callError = null;
    for (const ids of chunkJobIdsForLink(eligible.map(j => j.id))) {
      try {
        const r = await _callBillingProjects('linkJobsToProject', {
          projectId,
          jobIds: ids,
          expectedPrevious: Object.fromEntries(ids.map(id => [id, null])),
          // The dialog stated (and, when > 0, the biller ticked) this many
          // invoiced tickets per job; the server re-counts inside each job's
          // transaction and refuses a job that has MORE now.
          billedConfirmed: Object.fromEntries(ids.map(id => [id, billedByJob[id] || 0])),
          idempotencyKey: _newIdempotencyKey()
        });
        for (const x of (r.results || [])) results.push(x);
      } catch (err) {
        console.error('[Billing] linkJobsToProject failed:', err);
        callError = err;
        break;
      }
    }
    const jobById = new Map(eligible.map(j => [j.id, j]));
    const linked = results.filter(r => r.status === 'ok');
    const unchanged = results.filter(r => r.status === 'unchanged');
    const refused = results.filter(r => r.status === 'refused');
    const unsent = eligible.filter(j => !results.some(r => r.jobId === j.id));
    try {
      window.logAudit?.('billing', 'ARAP_JOBS_ADDED_TO_PROJECT', {
        projectId, created, linked: linked.length, unchanged: unchanged.length, refused: refused.length + unsent.length,
        billedTicketCount: billedTotal, billedConfirmed: billedTotal > 0, via: 'linkJobsToProject'
      });
    } catch (_) { /* audit is best-effort; the server's per-job PROJECT_JOB_LINK rows are the trail */ }

    if (selectedIds) clearTicketSelection();
    BillingState._projectsLoaded = false;
    await loadBillingProjects();
    const pname = BillingState.projects.find(p => p.id === projectId)?.name || choice.newName || 'the project';
    const tails = [];
    if (unchanged.length) tails.push(`${unchanged.length} already there.`);
    const inherited = linked.filter(r => r.poInherited);
    if (inherited.length) tails.push(`PO ${inherited[0].poInherited} filled into ${inherited.length} job${inherited.length !== 1 ? 's' : ''} that had none.`);
    const nameOf = id => { const j = jobById.get(id); return (j && (j.title || j.name)) || 'job'; };
    const notDone = [
      ...refused.map(r => `${nameOf(r.jobId)} (${linkRefusalText(r.reason)})`),
      ...unsent.map(j => `${j.title || j.name || 'job'} (${callError ? (callError.message || 'not saved') : 'not saved'})`),
      ...allSkipped.map(s => `${s.name} (${s.reason})`)
    ];
    if (notDone.length) tails.push(`Not added: ${notDone.slice(0, 5).join('; ')}${notDone.length > 5 ? `; and ${notDone.length - 5} more` : ''}.`);
    // COD r1 — the server's 10-minute same-name window adopted a project made a
    // moment ago (same customer + same PO only); say so instead of implying a new one.
    if (reusedProject) tails.push(`(Matched the “${pname}” project made a few minutes ago — nothing duplicated.)`);
    if (created && !linked.length && !unchanged.length) tails.push(`“${pname}” was created but has no jobs yet.`);
    window.showToast?.(
      `${linked.length} job${linked.length !== 1 ? 's' : ''} added to “${pname}”.${tails.length ? ' ' + tails.join(' ') : ''}`,
      linked.length ? (notDone.length ? 'warning' : 'success') : 'error', null, notDone.length ? 12000 : 6000);
  } finally {
    BillingState._linkBusy = false;
  }
}

// The Add-to-project dialog. Resolves { projectId } | { projectId:null, newName, newPo } | null.
// opts.fixed — one pre-chosen project, shown but not changeable, no "New project".
function _addToProjectModal(opts) {
  const { jobs, skipped, customerName, projects, otherTickets, billedTotal, fixed } = opts;
  return new Promise(resolve => {
    const id = 'billing-add-to-project-modal';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    const n = jobs.length;
    const jobList = jobs.slice(0, 8).map(j => `<li>${escapeHtml(j.title || j.name || '(untitled job)')}</li>`).join('')
      + (jobs.length > 8 ? `<li>and ${jobs.length - 8} more</li>` : '');
    const skipHtml = skipped.length
      ? `<p style="font-size:0.82rem;color:#B45309;margin:0 0 8px;">Not included: ${skipped.slice(0, 6).map(s => `${escapeHtml(s.name)} (${escapeHtml(s.reason)})`).join('; ')}${skipped.length > 6 ? `; and ${skipped.length - 6} more` : ''}.</p>` : '';
    const blastHtml = otherTickets
      ? `<p style="font-size:0.85rem;color:var(--text-primary);margin:0 0 8px;"><strong>This adds the whole job${n !== 1 ? 's' : ''}</strong>, including ${otherTickets} other ticket${otherTickets !== 1 ? 's' : ''} you didn’t check.</p>` : '';
    const billedHtml = billedTotal
      ? `<label style="display:flex;gap:8px;align-items:flex-start;font-size:0.85rem;color:var(--text-primary);background:#FFF7ED;border:1px solid #FED7AA;border-radius:var(--radius-sm);padding:8px 10px;margin:0 0 10px;cursor:pointer;">
          <input type="checkbox" id="atp-billed-ok" style="margin-top:2px;">
          <span>${billedTotal} ticket${billedTotal !== 1 ? 's' : ''} on ${n !== 1 ? 'these jobs' : 'this job'} ${billedTotal !== 1 ? 'are' : 'is'} already invoiced. Adding moves ${billedTotal !== 1 ? 'them' : 'it'} into this project’s billed totals. Nothing on the invoice changes.</span>
        </label>` : '';
    const opt = projects.map(p => `<option value="${window.escAttr(p.id)}">${escapeHtml(p.name || '(unnamed project)')}${p.poNumber ? ' · PO ' + escapeHtml(p.poNumber) : ''}</option>`).join('');
    el.innerHTML = `
      <div class="modal-content" style="max-width:520px;width:92%;height:auto;max-height:85vh;max-height:85dvh;overflow:auto;padding:20px;">
        <h3 style="margin:0 0 var(--spacing-xs);color:var(--text-primary);">Add ${n} job${n !== 1 ? 's' : ''} to ${fixed ? '“' + escapeHtml(projects[0]?.name || 'the project') + '”' : 'a project'}</h3>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 8px;">
          ${customerName ? escapeHtml(customerName) + ' — t' : 'T'}he jobs stay separate: same names, days and tickets. The project just groups them so you can filter by it.
        </p>
        <ul style="margin:0 0 10px;padding-left:20px;font-size:0.85rem;color:var(--text-primary);">${jobList}</ul>
        ${blastHtml}${skipHtml}
        <label for="atp-project" style="display:${fixed ? 'none' : 'block'};font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Project</label>
        <select id="atp-project" style="display:${fixed ? 'none' : 'block'};width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">
          ${opt}
          ${fixed ? '' : `<option value="__new__" ${projects.length ? '' : 'selected'}>+ New project…</option>`}
        </select>
        <div id="atp-new" style="display:${fixed || projects.length ? 'none' : 'block'};">
          <label for="atp-name" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">New project name</label>
          <input type="text" id="atp-name" maxlength="120" placeholder="e.g. Hamlin" value="${window.escAttr(String(jobs[0]?.title || jobs[0]?.name || '').replace(/\s+\d{1,2}[./-]\d{1,2}([./-]\d{2,4})?\s*$/, '').trim())}" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">
          <label for="atp-po" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">PO number (optional)</label>
          <input type="text" id="atp-po" maxlength="64" placeholder="Customer PO for this project" style="width:100%;padding:9px 10px;margin-bottom:4px;font-size:16px;">
          <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 10px;">A job that has no PO of its own picks this one up. A job’s own PO is never changed.</p>
        </div>
        ${billedHtml}
        <div style="display:flex;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
          <button type="button" class="btn btn-primary" data-add="1">Add to project</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };
    const sel = el.querySelector('#atp-project');
    sel.addEventListener('change', () => { el.querySelector('#atp-new').style.display = sel.value === '__new__' ? 'block' : 'none'; });
    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-add]')) return;
      if (billedTotal && !el.querySelector('#atp-billed-ok')?.checked) {
        window.showToast?.('Tick the box to confirm moving the invoiced tickets into this project’s totals.', 'error');
        return;
      }
      if (sel.value !== '__new__') return done({ projectId: sel.value });
      const name = String(el.querySelector('#atp-name')?.value || '').trim();
      if (!name) { window.showToast?.('Give the new project a name.', 'error'); return; }
      if (name.length > 120) { window.showToast?.('Project name is too long (120 characters max).', 'error'); return; }
      done({ projectId: null, newName: name, newPo: poNormalizeForSave(el.querySelector('#atp-po')?.value) });
    });
  });
}

// ============================================
// TASK-9414 C — PROJECTS TAB: New project · Edit · Add jobs
// ============================================
// Same callables, same gate. Multi-customer rows stay locked ("manage in
// Dispatch"); the server refuses them too.

// PURE. Customers a new AR/AP project may belong to: billable (never a vendor /
// subcontractor), active, not the internal Shop customer. A–Z.
function billingProjectCustomerChoices(customers) {
  return (customers || []).filter(c => c && c.id && !_isVendorType(c) && !_isInactiveCustomer(c) && c.internalTimeOnly !== true)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// PURE. Jobs the "Add jobs" picker offers for a single-customer project: this
// customer's jobs with no project, newest first, each with its ticket count
// (non-deleted tickets in state — the Projects tab loads full history).
function addJobsPickerRows(jobs, tickets, customerId) {
  const counts = new Map();
  for (const t of (tickets || [])) {
    if (!t || !t.jobId || t.status === 'deleted') continue;
    counts.set(t.jobId, (counts.get(t.jobId) || 0) + 1);
  }
  const ms = j => {
    for (const v of [j.startDate, j.date, j.createdAt]) {
      if (!v) continue;
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (typeof v.seconds === 'number') return v.seconds * 1000;
      const n = new Date(v).getTime();
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  return (jobs || [])
    .filter(j => j && j.id && j.customerId === customerId && !j.projectId && j.deleted !== true && j.status !== 'deleted')
    .map(j => ({ job: j, ms: ms(j), tickets: counts.get(j.id) || 0 }))
    .sort((a, b) => (b.ms - a.ms) || String(a.job.title || '').localeCompare(String(b.job.title || '')));
}

// PURE. The Edit dialog's PO consequence sentence from B3's preview counts.
// newPo — what the PO field now holds. COD r1 (Edge Case): a BLANK new PO does
// not land on those tickets; _resolveFallbackPoNumber falls through to the
// customer's contract PO, so the sentence says that instead.
function projectPoImpactText(impact, oldPo, newPo) {
  if (!impact) return 'Couldn’t count the jobs and tickets this PO reaches — check before saving.';
  const n = impact.linkedJobs || 0;
  if (!n) {
    // COD r2 (Edge Case) — a cleared PO is inherited by nobody; say so.
    const clearedEmpty = newPo !== undefined && !String(newPo || '').trim();
    return clearedEmpty
      ? 'No jobs are in this project yet. With no project PO, jobs added later keep whatever PO they have (or the customer’s contract PO).'
      : 'No jobs are in this project yet, so changing the PO changes nothing today. Jobs added later pick it up if they have no PO.';
  }
  const parts = [];
  const own = impact.jobsKeepOwnPo || 0;
  if (own) {
    const oldNote = impact.jobsKeepOldProjectPo
      ? ` (${impact.jobsKeepOldProjectPo} of them still carry the old project PO${oldPo ? ' ' + oldPo : ''})` : '';
    parts.push(`${own} of ${n} job${n !== 1 ? 's' : ''} ${own !== 1 ? 'have their' : 'has its'} own PO and keep${own !== 1 ? '' : 's'} it${oldNote}.`);
  } else {
    parts.push(`None of the ${n} job${n !== 1 ? 's' : ''} has its own PO.`);
  }
  const k = impact.ticketsWillTakeNewPo || 0;
  const cleared = newPo !== undefined && !String(newPo || '').trim();
  parts.push(!k
    ? (cleared ? 'No open ticket is affected.' : 'No open ticket will pick up the new PO.')
    : cleared
      ? `${k} open ticket${k !== 1 ? 's' : ''} with no PO will take the customer’s contract PO instead (if there is one) the next time ${k !== 1 ? 'they are' : 'it is'} opened in Review.`
      : `${k} open ticket${k !== 1 ? 's' : ''} with no PO will pick up the new PO the next time ${k !== 1 ? 'they are' : 'it is'} opened in Review.`);
  parts.push('Invoiced tickets never change.');
  return parts.join(' ');
}

async function newBillingProject() {
  if (_billingProjectsBlocked()) return;
  if (BillingState._linkBusy) return;
  // Through the vendor rule (_billableCustomers → window.BillableCustomer) first.
  const choices = billingProjectCustomerChoices(_billableCustomers());
  if (!choices.length) { window.showToast?.('Add a customer first — a project belongs to one customer.', 'error'); return; }
  const cur = String(BillingState.projectsCustomer || '');
  const preCust = cur.startsWith('id:') ? cur.slice(3) : '';
  const form = await _billingProjectFormModal({
    title: 'New project', submit: 'Create project', customers: choices, customerId: preCust, name: '', po: ''
  });
  if (!form) return;
  BillingState._linkBusy = true;
  let r;
  try {
    r = await _callBillingProjects('createBillingProject', {
      name: form.name, customerId: form.customerId, poNumber: form.po, idempotencyKey: _newIdempotencyKey()
    });
  } catch (err) {
    console.error('[Billing] createBillingProject failed:', err);
    window.showToast?.(err?.message || 'Could not create the project — nothing was changed.', 'error', null, 8000);
    return;
  } finally {
    BillingState._linkBusy = false;
  }
  BillingState._projectsLoaded = false;
  await loadBillingProjects();
  window.showToast?.(r.created ? `“${form.name}” created. Now pick its jobs.` : `“${form.name}” already existed — it was reused, nothing duplicated.`, 'success');
  if (r.projectId) addJobsToBillingProject(r.projectId);
}

async function editBillingProject(projectId) {
  if (_billingProjectsBlocked()) return;
  if (BillingState._linkBusy) return;
  const p = BillingState.projects.find(x => x.id === projectId);
  if (!p) { window.showToast?.('That project is no longer here — refresh.', 'error'); return; }
  if (_isMultiCustomerProject(p)) { window.showToast?.('This project spans several customers — edit it in Dispatch.', 'info'); return; }
  BillingState._linkBusy = true;
  let pv;
  try {
    // Server truth: the current name / PO and B3's two counts, before any edit.
    pv = await _callBillingProjects('updateBillingProject', { projectId, preview: true });
  } catch (err) {
    console.error('[Billing] updateBillingProject preview failed:', err);
    window.showToast?.(err?.message || 'Could not open this project — try again.', 'error', null, 8000);
    return;
  } finally {
    BillingState._linkBusy = false;
  }
  const form = await _billingProjectFormModal({
    title: 'Edit project', submit: 'Save', name: pv.currentName || p.name || '', po: pv.currentPoNumber || '',
    poImpact: projectPoImpactText(pv.impact, pv.currentPoNumber), impact: pv.impact || null, oldPo: pv.currentPoNumber || ''
  });
  if (!form) return;
  const payload = { projectId };
  if (form.name !== (pv.currentName || '')) payload.name = form.name;
  if (form.po !== (pv.currentPoNumber || '')) { payload.poNumber = form.po; payload.expectedPoNumber = pv.currentPoNumber || ''; }
  if (!('name' in payload) && !('poNumber' in payload)) { window.showToast?.('Nothing changed.', 'info'); return; }
  BillingState._linkBusy = true;
  try {
    const r = await _callBillingProjects('updateBillingProject', payload);
    BillingState._projectsLoaded = false;
    await loadBillingProjects();
    const bits = [];
    if (r.changed?.name) bits.push('renamed');
    if (r.changed?.poNumber) bits.push(form.po ? `PO is now ${form.po}` : 'PO cleared');
    window.showToast?.(bits.length ? `Project ${bits.join(', ')}.` : 'Nothing changed.', 'success');
  } catch (err) {
    console.error('[Billing] updateBillingProject failed:', err);
    window.showToast?.(err?.message || 'Could not save the project — nothing was changed.', 'error', null, 9000);
  } finally {
    BillingState._linkBusy = false;
  }
}

// Shared New / Edit form. Resolves { name, customerId?, po } | null.
function _billingProjectFormModal(o) {
  return new Promise(resolve => {
    const id = 'billing-project-form-modal';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    const custSel = o.customers
      ? `<label for="bpf-customer" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Customer</label>
         <select id="bpf-customer" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">
           <option value="">Pick a customer…</option>
           ${/* integ/2107: the shared grouped builder (TASK-9548 tripwire) — A–Z, vendors and internal-time customers out unless already selected */ window.PickerGroups.customerOptionsHtml(o.customers, { selectedId: o.customerId || null, nameOf: c => c.name || c.company || c.id, excludeInternal: true })}
         </select>` : '';
    el.innerHTML = `
      <div class="modal-content" style="max-width:480px;width:92%;height:auto;max-height:85vh;max-height:85dvh;overflow:auto;padding:20px;">
        <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">${escapeHtml(o.title)}</h3>
        <label for="bpf-name" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Project name</label>
        <input type="text" id="bpf-name" maxlength="120" value="${window.escAttr(o.name || '')}" placeholder="e.g. Hamlin" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">
        ${custSel}
        <label for="bpf-po" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">PO number (optional)</label>
        <input type="text" id="bpf-po" maxlength="64" value="${window.escAttr(o.po || '')}" placeholder="Customer PO for this project" style="width:100%;padding:9px 10px;margin-bottom:4px;font-size:16px;">
        <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 10px;">A job with no PO of its own uses the project’s. A job’s own PO is never changed.</p>
        ${o.poImpact != null ? `<p id="bpf-po-impact" style="display:none;font-size:0.82rem;color:var(--text-primary);background:#FFF7ED;border:1px solid #FED7AA;border-radius:var(--radius-sm);padding:8px 10px;margin:0 0 10px;">${escapeHtml(o.poImpact)}</p>` : ''}
        <div style="display:flex;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
          <button type="button" class="btn btn-primary" data-save="1">${escapeHtml(o.submit)}</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };
    const poEl = el.querySelector('#bpf-po');
    const impactEl = el.querySelector('#bpf-po-impact');
    // The PO consequence shows the moment the PO differs from what was loaded,
    // so the two counts are always on screen before Save (COD Edge Case #1).
    const syncImpact = () => {
      if (!impactEl) return;
      const nv = poNormalizeForSave(poEl.value);
      impactEl.style.display = nv !== (o.oldPo || '') ? 'block' : 'none';
      if (o.impact !== undefined) impactEl.textContent = projectPoImpactText(o.impact, o.oldPo, nv);
    };
    poEl.addEventListener('input', syncImpact);
    syncImpact();
    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-save]')) return;
      const name = String(el.querySelector('#bpf-name')?.value || '').trim();
      if (!name) { window.showToast?.('Give the project a name.', 'error'); return; }
      const out = { name, po: poNormalizeForSave(poEl.value) };
      if (o.customers) {
        out.customerId = String(el.querySelector('#bpf-customer')?.value || '');
        if (!out.customerId) { window.showToast?.('Pick the customer this project is for.', 'error'); return; }
      }
      done(out);
    });
  });
}

async function addJobsToBillingProject(projectId) {
  if (_billingProjectsBlocked()) return;
  _flushSecondarySubs();
  if (!BillingState._jobsLoaded) { window.showToast?.('Jobs are still loading — try again in a moment.', 'info'); return; }
  const p = BillingState.projects.find(x => x.id === projectId);
  if (!p) { window.showToast?.('That project is no longer here — refresh.', 'error'); return; }
  if (_isMultiCustomerProject(p)) { window.showToast?.('This project spans several customers — manage it in Dispatch.', 'info'); return; }
  const custId = Array.isArray(p.customerIds) && p.customerIds.length === 1 ? p.customerIds[0] : null;
  if (!custId) { window.showToast?.('This project has no customer yet — set one in Dispatch first.', 'info', null, 7000); return; }
  const rows = addJobsPickerRows(BillingState.jobs, BillingState.tickets, custId);
  const customerName = BillingState.customers.find(c => c.id === custId)?.name || p.primaryCustomerName || '';
  if (!rows.length) { window.showToast?.(`Every ${customerName ? customerName + ' ' : ''}job is already in a project.`, 'info'); return; }
  const picked = await _addJobsPickerModal({ project: p, rows, customerName });
  if (!picked || !picked.length) return;
  const byId = new Map(rows.map(r => [r.job.id, r.job]));
  await _addJobsToProjectFlow(picked.map(id => byId.get(id)).filter(Boolean), { selectedIds: null, extraSkipped: [], fixedProjectId: projectId });
}

// The "Add jobs" picker: this customer's unlinked jobs, newest first, search,
// ticket count per job. Resolves [jobId] | null.
function _addJobsPickerModal({ project, rows, customerName }) {
  return new Promise(resolve => {
    const id = 'billing-add-jobs-picker-modal';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    const fmt = ms => ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const rowHtml = rows.map(r => `
      <label class="ajp-row" data-hay="${window.escAttr(String((r.job.title || r.job.name || '') + ' ' + jobPoList(r.job).join(' ')).toLowerCase())}"
             style="display:flex;gap:10px;align-items:center;padding:9px 6px;border-bottom:1px solid var(--border-light);cursor:pointer;min-height:44px;">
        <input type="checkbox" class="ajp-cb" value="${window.escAttr(r.job.id)}" style="width:18px;height:18px;flex:0 0 auto;">
        <span style="flex:1;min-width:0;">
          <span style="display:block;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(r.job.title || r.job.name || '(untitled job)')}</span>
          <span style="display:block;font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(fmt(r.ms))}${r.ms ? ' · ' : ''}${r.tickets} ticket${r.tickets !== 1 ? 's' : ''}${jobPoLabel(r.job) ? ' · PO ' + escapeHtml(jobPoLabel(r.job)) : ''}</span>
        </span>
      </label>`).join('');
    el.innerHTML = `
      <div class="modal-content" style="max-width:560px;width:94%;height:auto;max-height:88vh;max-height:88dvh;display:flex;flex-direction:column;padding:16px;">
        <h3 style="margin:0 0 4px;color:var(--text-primary);">Add jobs to “${escapeHtml(project.name || 'project')}”</h3>
        <p style="font-size:0.82rem;color:var(--text-secondary);margin:0 0 8px;">${customerName ? escapeHtml(customerName) + ' jobs' : 'Jobs'} not in any project, newest first. They stay separate.</p>
        <input type="search" id="ajp-search" placeholder="Search jobs or PO" aria-label="Search jobs"
               style="width:100%;box-sizing:border-box;padding:9px 10px;margin-bottom:8px;font-size:16px;border:1.5px solid #e2e8f0;border-radius:8px;">
        <div id="ajp-list" style="flex:1 1 auto;overflow:auto;min-height:120px;border:1px solid var(--border-light);border-radius:8px;">${rowHtml}</div>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:10px;flex-wrap:wrap;">
          <span id="ajp-count" style="font-size:0.82rem;color:var(--text-secondary);">0 selected</span>
          <span style="display:flex;gap:8px;">
            <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
            <button type="button" class="btn btn-primary" data-next="1" disabled>Add selected</button>
          </span>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };
    const next = el.querySelector('[data-next]');
    const count = el.querySelector('#ajp-count');
    const refresh = () => {
      const n = el.querySelectorAll('.ajp-cb:checked').length;
      count.textContent = `${n} selected`;
      next.disabled = n === 0;
    };
    el.querySelector('#ajp-search').addEventListener('input', e => {
      const q = String(e.target.value || '').trim().toLowerCase();
      el.querySelectorAll('.ajp-row').forEach(r => { r.style.display = !q || r.dataset.hay.includes(q) ? 'flex' : 'none'; });
    });
    el.addEventListener('change', e => { if (e.target.closest('.ajp-cb')) refresh(); });
    el.addEventListener('click', e => {
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-next]') || next.disabled) return;
      if (_isStrayMultiClick(e)) return;
      done([...el.querySelectorAll('.ajp-cb:checked')].map(cb => cb.value));
    });
  });
}

// The grouping dialog. Purpose-built rather than a chain of showInputModal /
// showConfirm calls, for the same reason slice 1's job picker is: the biller has
// to SEE the candidate jobs. Typing a name is what created 22 duplicates of one
// engagement in the first place. Resolves { projectName, targetJobId, poNumber }
// or null.
function _groupIntoProjectModal(opts) {
  const { jobs, willMoveCount, skippedCount, alreadyOnProject, customerName } = opts;
  return new Promise(resolve => {
    const id = 'billing-group-project-modal';
    document.getElementById(id)?.remove();

    // Default the target to the job the most selected tickets already sit on, and
    // exclude jobs already claimed by another project (the server refuses them).
    const counts = new Map();
    for (const t of (opts.tickets || [])) {
      if (t.jobId) counts.set(t.jobId, (counts.get(t.jobId) || 0) + 1);
    }
    const candidates = jobs.filter(j => !j.projectId);
    const defaultJob = candidates.slice().sort((a, b) => (counts.get(b.id) || 0) - (counts.get(a.id) || 0))[0];
    const defaultName = defaultJob ? (defaultJob.title || defaultJob.name || '') : '';
    const defaultPo = poNormalizeForSave(defaultJob?.poNumber);

    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';

    if (!candidates.length) {
      el.innerHTML = `
        <div class="modal-content" style="max-width:520px;width:92%;height:auto;">
          <h3 style="margin:0 0 var(--spacing-sm);color:var(--text-primary);">No job available</h3>
          <p style="color:var(--text-primary);margin:0 0 var(--spacing-md);">
            Every job on this customer already belongs to a project. Open the project
            in Dispatch to move work between them.
          </p>
          <div style="display:flex;justify-content:flex-end;">
            <button type="button" class="btn btn-secondary" data-cancel="1">Close</button>
          </div>
        </div>`;
    } else {
      const skipNote = skippedCount
        ? `${skippedCount} selected ticket${skippedCount !== 1 ? 's are' : ' is'} invoiced, archived or locked and will be skipped.` : '';
      const projNote = alreadyOnProject.length
        ? `${alreadyOnProject.length} ticket${alreadyOnProject.length !== 1 ? 's' : ''} already belong${alreadyOnProject.length !== 1 ? '' : 's'} to another project and will be left alone: ${
          alreadyOnProject.slice(0, 6).map(t => '#' + escapeHtml(String(t.ticketNo || '?'))).join(', ')
        }${alreadyOnProject.length > 6 ? `, and ${alreadyOnProject.length - 6} more` : ''}.` : '';

      el.innerHTML = `
        <div class="modal-content" style="max-width:560px;width:92%;height:auto;max-height:85vh;overflow:auto;">
          <h3 style="margin:0 0 var(--spacing-xs);color:var(--text-primary);">Group ${willMoveCount} ticket${willMoveCount !== 1 ? 's' : ''} into a project</h3>
          <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);">
            ${customerName ? escapeHtml(customerName) + ' — o' : 'O'}ne customer, one engagement. The tickets are pulled onto
            the job you pick, and that job is linked to the new project.
          </p>
          ${skipNote ? `<p style="font-size:0.82rem;color:var(--text-secondary);margin:0 0 8px;">${skipNote}</p>` : ''}
          ${projNote ? `<p style="font-size:0.82rem;color:#B45309;margin:0 0 8px;">${projNote}</p>` : ''}

          <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Project name</label>
          <input type="text" id="gip-name" maxlength="120" value="${window.escAttr(defaultName)}"
                 placeholder="e.g. Loenbro Lostwood"
                 style="width:100%;padding:9px 10px;margin-bottom:var(--spacing-md);">

          <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Land the tickets on this job</label>
          <div id="gip-jobs" style="display:flex;flex-direction:column;gap:5px;max-height:220px;overflow:auto;margin-bottom:var(--spacing-md);">
            ${candidates.map(j => `
              <label style="display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);cursor:pointer;">
                <input type="radio" name="gip-job" value="${window.escAttr(j.id)}" data-po="${window.escAttr(poNormalizeForSave(j.poNumber))}" ${j.id === defaultJob.id ? 'checked' : ''}>
                <span style="font-weight:600;color:var(--text-primary);">${escapeHtml(j.title || j.name || '(untitled job)')}</span>
                ${counts.get(j.id) ? `<span style="font-size:0.75rem;color:var(--text-secondary);">${counts.get(j.id)} selected here</span>` : ''}
                ${jobPoLabel(j) ? `<span style="font-size:0.75rem;color:var(--text-secondary);margin-left:auto;">PO ${escapeHtml(jobPoLabel(j))}</span>` : ''}
              </label>`).join('')}
          </div>

          <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">PO number (optional)</label>
          <input type="text" id="gip-po" maxlength="64" value="${window.escAttr(defaultPo)}"
                 placeholder="Customer PO for this engagement"
                 style="width:100%;padding:9px 10px;">
          <p style="font-size:0.78rem;color:var(--text-secondary);margin:6px 0 var(--spacing-md);">
            Stored on the project and resolved at invoice time. You'll be asked
            separately before any ticket's own PO is changed.
          </p>

          <div style="display:flex;justify-content:flex-end;gap:8px;">
            <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
            <button type="button" class="btn btn-primary" data-create="1">Create project</button>
          </div>
        </div>`;
    }

    document.body.appendChild(el);
    const done = v => { el.remove(); resolve(v); };

    // Keep the PO in step with the picked job until the biller edits it by hand.
    let poDirty = false;
    el.querySelector('#gip-po')?.addEventListener('input', () => { poDirty = true; });
    el.querySelectorAll('input[name="gip-job"]').forEach(r => {
      r.addEventListener('change', () => {
        if (poDirty) return;
        const po = el.querySelector('#gip-po');
        if (po) po.value = r.getAttribute('data-po') || '';
      });
    });

    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;   // see _isStrayMultiClick
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-create]')) return;
      const name = String(el.querySelector('#gip-name')?.value || '').trim();
      if (!name) { window.showToast?.('Give the project a name.', 'error'); return; }
      if (name.length > 120) { window.showToast?.('Project name is too long (120 characters max).', 'error'); return; }
      const picked = el.querySelector('input[name="gip-job"]:checked');
      if (!picked) { window.showToast?.('Pick the job the tickets should land on.', 'error'); return; }
      done({
        projectName: name,
        targetJobId: picked.value,
        // TASK-4828 (COD Blast-Radius) — the SIXTH PO writer, and the one this
        // lane missed on its first pass. This value becomes the project standing
        // PO and, when the biller confirms, is stamped onto every ticket in the
        // group. Same money-field normalizer as every other PO path; a bare
        // .trim() here leaves NBSP/zero-width edges that fork the PO in two.
        poNumber: poNormalizeForSave(el.querySelector('#gip-po')?.value)
      });
    });
  });
}

// ============================================
// JOB MANAGEMENT: RENAME, DELETE, DRAG & DROP
// ============================================

function startJobRename(custId, jobKey, event) {
  event.stopPropagation();
  const subheader = event.target.closest('.billing-accordion-subheader');
  if (!subheader) return;
  const job = BillingState.jobs.find(j => j.id === jobKey);
  const currentName = job?.title || job?.name || '';

  // Replace the subheader content with inline edit
  const nameSpan = subheader.querySelector('.billing-job-name');
  const actionsDiv = subheader.querySelector('.billing-job-actions');
  if (nameSpan) {
    nameSpan.outerHTML = `
      <div class="billing-inline-edit" onclick="event.stopPropagation()">
        <input type="text" value="${escapeHtml(currentName)}" id="rename-job-input-${jobKey}"
          onkeydown="if(event.key==='Enter')window.BillingModule.saveJobRename('${custId}','${jobKey}',event);if(event.key==='Escape')window.BillingModule.cancelJobRename(event);">
        <button class="save-rename" onclick="window.BillingModule.saveJobRename('${custId}','${jobKey}',event)" title="Save">
          <span class="material-icons">check</span>
        </button>
        <button class="cancel-rename" onclick="window.BillingModule.cancelJobRename(event)" title="Cancel">
          <span class="material-icons">close</span>
        </button>
      </div>`;
    if (actionsDiv) actionsDiv.style.display = 'none';
    // Focus and select
    setTimeout(() => {
      const input = document.getElementById(`rename-job-input-${jobKey}`);
      if (input) { input.focus(); input.select(); }
    }, 0);
  }
}

async function saveJobRename(custId, jobKey, event) {
  event.stopPropagation();
  const input = document.getElementById(`rename-job-input-${jobKey}`);
  if (!input) return;
  const newName = input.value.trim();
  if (!newName) {
    showToast('Job name cannot be empty', 'error');
    return;
  }
  const tenant = BillingState.tenantId;
  if (!tenant) return;

  try {
    // TASK-412: one canonical rename path (job doc + clock entries + draft/ready
    // tickets + audit, chunked). Replaces the old partial inline propagation that
    // missed `ready` tickets + updatedAt + audit.
    const updated = await window.JobOps.renameJob(tenant, jobKey, newName, { source: 'billing-list' });
    if (!updated) { showToast('Failed to rename job', 'error'); return; }

    // Refresh local state from the canonical result.
    const localJob = BillingState.jobs.find(j => j.id === jobKey);
    if (localJob) { localJob.title = newName; localJob.name = newName; }
    BillingState.tickets.forEach(t => {
      if (t.jobId === jobKey && (t.status === 'draft' || t.status === 'ready')) {
        t.jobName = newName;
        t.jobTitle = newName;
      }
    });

    showToast(`Job renamed to "${newName}"`, 'success');
    renderTicketsList();
  } catch (err) {
    console.error('Error renaming job:', err);
    showToast('Failed to rename job', 'error');
  }
}

function cancelJobRename(event) {
  event.stopPropagation();
  renderTicketsList();
}

async function deleteJob(custId, jobKey, event) {
  event.stopPropagation();
  const tenant = BillingState.tenantId;
  if (!tenant) return;

  const job = BillingState.jobs.find(j => j.id === jobKey);
  const jobName = job?.title || job?.name || 'this job';

  // Check for tickets referencing this job (query Firestore for accuracy)
  try {
    // TASK-4816 — this precheck used to run
    //     where('jobId','==',jobKey) + where('status','not-in',['deleted'])
    // which needs a `tickets: jobId+status` COMPOSITE index that does not
    // exist. `firestore.indexes.json` declares exactly three `tickets` indexes
    // — status+date, status+createdAt, customerId+date — and none of them is
    // keyed on `jobId`. So the query threw FAILED_PRECONDITION on EVERY run,
    // the outer catch below rendered 'Failed to delete job', and nothing was
    // ever deleted: **deleting a job from Billing has never worked in
    // production.** Everything downstream of this line — the TASK-4734 audit
    // row, the TASK-4735 `deletedBy` breadcrumb — has therefore never fired
    // for a real user. It failed CLOSED, so no data was lost; it was simply a
    // dead button.
    //
    // Re-confirmed at HEAD on 2026-09-08 by running this exact two-clause
    // query against the LIVE project `dispatch-deck-be88c` (read-only):
    //   9 FAILED_PRECONDITION: The query requires an index.
    // while the single-clause `jobId ==` form below returned in the same run.
    //
    // 🔴 THE FIX IS NOT TO ADD THE INDEX. This is the identical defect
    // TASK-959 fixed on the dispatch path after it orphaned a draft ticket and
    // 17.14h of clock time on 2026-07-02 (dispatch.js `deleteJobFromDetails`
    // ~:13721). Its proven pattern: query by `jobId` ALONE — a single-field
    // query, always indexed, structurally incapable of failing on a missing
    // composite — and classify the status client-side.
    //
    // THE REFUSAL IS PRESERVED EXACTLY. `not-in ['deleted']` refused the delete
    // when ANY ticket carried a status other than the literal 'deleted', and so
    // does this. It is deliberately NOT loosened to let `archived`/`invoiced`
    // through, even though those read as terminal: measured live 2026-09-08
    // across all 87 tenants / 1,056 tickets, the entire `tickets.status`
    // vocabulary is archived(863) deleted(135) draft(37) invoiced(14) ready(7)
    // — no legacy capitalisation anywhere in this collection, and 0 tickets
    // with a missing, null or empty status. Treating archived+invoiced as
    // terminal would NEWLY ALLOW deleting a job out from under 877 billed
    // tickets. That is a product decision, not a bug fix; it does not ride
    // along here.
    //
    // ONE DELIBERATE DIVERGENCE, in the fail-CLOSED direction: Firestore
    // `not-in` silently drops documents that have no `status` field, so the old
    // query would have ignored a status-less ticket entirely; this counts it as
    // live and refuses. 0 live tickets are affected today, and refusing is the
    // right way to be wrong about a destructive control.
    //
    // FAIL-CLOSED ON ERROR: this read stays inside the single outer `try`, so a
    // network failure lands in the catch below and NOTHING is deleted. There is
    // deliberately no inner catch here — an inner catch that "proceeded" is
    // precisely what made the dispatch-path version of this bug fail OPEN and
    // destroy data (TASK-959).
    const ticketSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'tickets'),
        where('jobId', '==', jobKey)
      )
    );
    const liveTicketCount = ticketSnap.docs
      .filter(d => ((d.data() || {}).status || '') !== 'deleted').length;
    if (liveTicketCount > 0) {
      showToast(`Cannot delete "${jobName}" — it has tickets. Reassign them first.`, 'error');
      return;
    }

    if (!confirm(`Delete job "${jobName}"? This also removes all job days for this job.`)) return;

    // TASK-9467 S1 — outside the demo the SERVER deletes: firestore.rules deny
    // the client job delete. deleteJobLedgered re-runs the ticket check (and a
    // clocked-in check), takes the actor from request.auth, and writes the
    // ledger row + JOB_DELETE breadcrumb + every day + the job in one batch.
    // A refusal arrives as the callable's message; nothing was deleted.
    if (!(window.AppState?.isDemo === true) && tenant !== 'demo') {
      await httpsCallable(functions, 'deleteJobLedgered')({ tenantId: tenant, jobId: jobKey, via: 'billing-job-list' });
      BillingState.jobs = BillingState.jobs.filter(j => j.id !== jobKey);
      BillingState.jobDays = BillingState.jobDays.filter(jd => jd.jobId !== jobKey);
      showToast(`Job "${jobName}" deleted`, 'success');
      renderTicketsList();
      return;
    }

    // Read the jobDays BEFORE deleting anything — they are needed for both the
    // audit snapshot and the delete itself, and once the job doc is gone a
    // snapshot listener can empty BillingState out from under us.
    const jobDaySnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'jobDays'),
        where('jobId', '==', jobKey)
      )
    );

    // TASK-4734 — audit BEFORE the delete, so the snapshot survives it. This is
    // the third job-delete path in the app and, until now, the second one that
    // recorded nothing: a job deleted from Billing left no trace of who removed
    // it, when, or what was on it. Same row shape as dispatch.js
    // deleteJobFromDetails so one query over `jobAuditLog` answers "who deleted
    // this job" regardless of which screen it went through.
    // Best-effort: a failed audit write must never block the delete the user
    // asked for, but it IS logged loudly rather than swallowed.
    try {
      await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
        action: 'JOB_DELETE',
        source: 'billing-job-list',
        timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        jobId: jobKey,
        jobTitle: job?.title || job?.name || null,
        customerName: job?.customer || job?.customerName || null,
        customerId: custId || null,
        jobSnapshot: job || null,
        jobDaysDeleted: jobDaySnap.size,
        // Snapshot the WHOLE jobDay. `assignments` does not exist on these docs
        // (0 of 40 live jobDays carried it, measured 2026-09-04) — the crew and
        // equipment are in drivers/helpers/trucks/trailers/assets — so storing
        // `{date, assignments}` preserves nothing but the date.
        jobDaysSnapshot: jobDaySnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))
      });
    } catch (ae) {
      console.warn('[Billing] TASK-4734 job-delete audit write failed:', ae);
    }

    // TASK-4735 slice 2a — the `deletedBy` BREADCRUMB, stamped immediately
    // before the delete so the SERVER-side trigger row can name a person.
    //
    // Slice 1 moved the JOB_DELETE row onto a Firestore onDelete trigger so a
    // raw devtools `deleteDoc` can no longer remove a job with no trail. That
    // trigger CANNOT NAME THE ACTOR — firebase-functions@4.9.0
    // lib/v1/cloud-functions.d.ts:73, `authType` is null "for all except
    // Realtime Database" — so the client row is currently the only carrier of
    // an identity. This stamp is what makes deleting that client row (slice 2b)
    // safe. firestore.rules pins `deletedBy` to request.auth.uid the way
    // `createdBy` is pinned (jobDeletedByOk, rules ~:768). A raw-SDK deleter
    // never stamps and the row lands with `deletedBy: null` — the row still
    // exists, and the absent breadcrumb is itself evidence.
    //
    // Deliberately DUPLICATED from dispatch.js `_stampJobDeletedBy` rather than
    // imported: cross-importing a view module under a `?v=` buster gives two
    // module instances (repo pitfall #1), and the JOB_DELETE audit row itself is
    // already duplicated across these two files for the same reason. The canonical
    // explanation lives at dispatch.js ~:13516 — change both or neither.
    //
    // Best effort: a failed stamp must never block the delete the user asked
    // for. Its outcome (a row with no actor) is exactly the raw-delete outcome.
    let _deletedByStamped = false;
    const _delUid = auth.currentUser?.uid || window.AppState?.user?.uid || null;
    if (_delUid) {
      try {
        await updateDoc(doc(db, 'tenants', tenant, 'jobs', jobKey), {
          deletedBy: _delUid,
          deletedAt: Timestamp.now(),
          deletedVia: 'billing-job-list',
        });
        _deletedByStamped = true;
      } catch (se) {
        console.warn('[Billing] TASK-4735 deletedBy stamp failed — delete proceeds, row will have no actor:', se);
      }
    }

    // Delete job doc
    try {
      await deleteDoc(doc(db, 'tenants', tenant, 'jobs', jobKey));
    } catch (delErr) {
      // Stamp landed, delete did not: the job is STILL LIVE and now marked
      // deleted-by-someone. Roll it back or a later raw delete of this job
      // pins the blame on whoever last TRIED to delete it. The trigger's
      // freshness check on `deletedAt` is the backstop if this too fails.
      if (_deletedByStamped) {
        try {
          await updateDoc(doc(db, 'tenants', tenant, 'jobs', jobKey), {
            deletedBy: null, deletedAt: null, deletedVia: null,
          });
        } catch (re) {
          console.error('[Billing] TASK-4735 deletedBy rollback FAILED — job ' + jobKey +
            ' is live and still stamped:', re);
        }
      }
      throw delErr;
    }

    if (jobDaySnap.size > 0) {
      const batch = writeBatch(db);
      jobDaySnap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }

    // Update local state
    BillingState.jobs = BillingState.jobs.filter(j => j.id !== jobKey);
    BillingState.jobDays = BillingState.jobDays.filter(jd => jd.jobId !== jobKey);

    showToast(`Job "${jobName}" deleted`, 'success');
    renderTicketsList();
  } catch (err) {
    console.error('Error deleting job:', err);
    // TASK-9467 S1 — a server refusal (tickets, someone clocked in) says why.
    const _srv = String(err?.code || '').startsWith('functions/') && err?.message;
    showToast(_srv ? err.message : 'Failed to delete job', 'error');
  }
}

// ============================================
// DRAG & DROP TICKETS BETWEEN JOBS
// ============================================

let _dragData = null; // { ticketId, custId, jobId }

// TASK-9610 — ticket drags and JOB drags carry their own MIME types so neither
// set of drop handlers can act on the other's payload (a job id dropped on the
// "mark paid" zone used to reach markTicketPaid via text/plain).
const TICKET_DRAG_MIME = 'application/x-dd-ticket';
const JOB_DRAG_MIME = 'application/x-dd-job';
// PURE. Does this DataTransfer carry the given MIME type? (dragover can read
// `types` but not the data.)
function dragHasType(dataTransfer, mime) {
  const types = dataTransfer && dataTransfer.types;
  if (!types) return false;
  if (typeof types.includes === 'function') return types.includes(mime);
  if (typeof types.contains === 'function') return types.contains(mime);
  return Array.prototype.indexOf.call(types, mime) !== -1;
}
// WebKit (Safari) does not list custom MIME types in dataTransfer.types during
// dragover (it shows text/plain, text/uri-list, text/html) and reports
// effectAllowed "all" — so a "must carry my MIME" test never passes there and no
// drop is ever accepted (found by tests/task9610-project-folders.spec.js on
// WebKit). The page's own drag state (_dragData / _jobDragData, set only by this
// page's dragstart handlers and never both at once) is the real guard; the MIME
// type is only used to REJECT a drag that explicitly carries the OTHER kind.
function _isTicketDrag(event) {
  return !!_dragData && !dragHasType(event && event.dataTransfer, JOB_DRAG_MIME);
}

function onTicketDragStart(event, ticketId, custId, jobId) {
  _jobDragData = null;
  _dragData = { ticketId, custId, jobId };
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(TICKET_DRAG_MIME, ticketId);
  event.dataTransfer.setData('text/plain', ticketId);
  event.target.closest('.ticket-card')?.classList.add('dragging');

  // Show drop zones for this customer
  document.querySelectorAll(`.billing-drop-zones[data-cust-id="${custId}"]`)
    .forEach(el => el.classList.add('visible'));

  // Show paid dropzone if ticket is invoiced.
  // TASK-1346 sweep — "is this billed?". NOTE: currently unreachable — isDraggable
  // in renderTicketCard excludes billed tickets, so a billed ticket never starts a
  // drag. Converted anyway so the two stay consistent if drag gating changes.
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (isTicketInvoiced(ticket) && !ticket?.paid) {
    document.getElementById('billing-paid-dropzone')?.classList.add('visible');
  }
}

function onTicketDragEnd(event) {
  event.target.closest('.ticket-card')?.classList.remove('dragging');
  document.querySelectorAll('.billing-drop-zones').forEach(el => el.classList.remove('visible'));
  document.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));
  // Also hide paid dropzone highlight
  document.getElementById('billing-paid-dropzone')?.classList.remove('drag-over');
  _dragData = null;
}

function onDropMarkPaid(event) {
  // TASK-9610 — a TICKET drag only (its own MIME type + the drag this page
  // started). A job drag, or text dragged in from elsewhere, does nothing.
  if (!_isTicketDrag(event)) return;
  // COD (Independent + Domain): the drop must CARRY the dragged ticket's id, not just
  // coincide with a live _dragData — a stale _dragData (dragend never fired) plus text
  // dropped from elsewhere must not mark that ticket paid. WebKit may not return the
  // custom type on drop, so text/plain (set to the same id by onTicketDragStart) is the fallback.
  const dt = event.dataTransfer;
  const ticketId = (dt && (dt.getData(TICKET_DRAG_MIME) || dt.getData('text/plain'))) || '';
  if (!ticketId || ticketId !== _dragData.ticketId) return;
  markTicketPaid(ticketId);
}

function onJobDragOver(event, targetCustId) {
  if (!_isTicketDrag(event) || _dragData.custId !== targetCustId) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  event.currentTarget.classList.add('drag-over');
}

function onJobDragLeave(event) {
  event.currentTarget.classList.remove('drag-over');
}

async function onTicketDrop(event, targetJobId, targetCustId) {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');

  if (!_isTicketDrag(event) || _dragData.custId !== targetCustId) return; // TASK-9610 — ticket MIME only
  if (_dragData.jobId === targetJobId) return; // dropped on same job

  const { ticketId } = _dragData;
  const tenant = BillingState.tenantId;
  if (!tenant) return;

  const newJobId = (targetJobId && targetJobId !== '_none') ? targetJobId : '';
  const newJob = newJobId ? (BillingState.jobs.find(j => j.id === newJobId) || null) : null;

  // TASK-9355 S2 — a drag re-derives everything job-derived (bridged docs,
  // notes, fastTrax, PO/vendor/well/AFE/location), so the write now carries
  // `documents` and header fields, not just the name. Those come from the
  // ticket as STORED, so re-read it first: BillingState's copy can be minutes
  // old and a blind write of a stale `documents` array would erase an upload
  // another biller just made. Same freshness discipline as the bulk move, and
  // the same eligibility re-assert — a ticket that was invoiced while it sat
  // on screen is fixed, not moved.
  let fresh;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
    fresh = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('Error reading ticket before move:', err);
    window.showToast?.('Could not verify the ticket — nothing was moved.', 'error');
    return;
  }
  if (!fresh) { window.showToast?.('That ticket no longer exists — reload.', 'error'); return; }
  if (!_ticketMoveEligible(fresh)) {
    window.showToast?.('This ticket is invoiced, archived or locked — it stays on its job.', 'error');
    renderTicketsList();
    return;
  }
  if (fresh.customerId !== targetCustId) {
    window.showToast?.('This ticket belongs to a different customer now — reload before moving it.', 'error');
    return;
  }
  const oldJob = fresh.jobId ? (BillingState.jobs.find(j => j.id === fresh.jobId) || null) : null;
  const { updates, summary } = _applyJobChangeToTicket(fresh, oldJob, newJob, { newJobId });
  // S3 — a PERMIT line priced from the old job's parsed cost is money that no
  // longer has a permit behind it. One bounded read; surfaced, never deleted.
  summary.permitLinesFlagged = await _fetchPermitLinesFromOtherJob(tenant, ticketId, oldJob, newJobId);

  // Optimistic local update
  const ticket = BillingState.tickets.find(t => t.id === ticketId);
  if (ticket) Object.assign(ticket, updates);

  // Auto-expand target job group
  const targetKey = `${targetCustId}_${targetJobId || '_none'}`;
  BillingState.expandedJobs[targetKey] = true;

  renderTicketsList();

  // Firestore update
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), updates);
    window.showToast?.(_jobChangeSummaryText(summary), (summary.kept.length || summary.permitLinesFlagged.length) ? 'warning' : 'success');
  } catch (err) {
    console.error('Error moving ticket:', err);
    window.showToast?.('Failed to save ticket move', 'error');
  }
}

async function onTicketDropNewJob(event, targetCustId) {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');

  if (!_isTicketDrag(event) || _dragData.custId !== targetCustId) return; // TASK-9610 — ticket MIME only

  const { ticketId } = _dragData;
  const tenant = BillingState.tenantId;
  if (!tenant) return;

  // TASK-10043 S1 — start from the board: the drop zone picks the dispatched
  // job day (or puts new work on the board with a reason); it no longer makes a
  // billing-only job with no days.
  const _dropTicket = (BillingState.tickets || []).find(t => t.id === ticketId) || null;
  const pick = await _pickJobDay({
    heading: 'Which dispatched job is this ticket on?',
    customerId: targetCustId, lockCustomer: true, dateKey: _dropTicket?.workDate || '', purpose: 'job',
  });
  if (!pick || !pick.jobId) return;
  const newJobId = pick.jobId;
  const jobDoc = BillingState.jobs.find(j => j.id === newJobId) || null;
  if (!jobDoc) { window.showToast?.('That job is not loaded yet — try again in a moment.', 'warning'); return; }
  if (_dropTicket && _dropTicket.jobId === newJobId) { window.showToast?.('This ticket is already on that job.', 'info'); return; }
  _offerPickedDayDate(pick, _dropTicket?.workDate, false);
  const trimmedName = String(jobDoc.title || jobDoc.name || 'Job');

  try {
    // TASK-9355 S2 — a brand-new job owns no permits, photos, notes or PO, so
    // every bridged copy drops and every INHERITED header value clears (a
    // hand-typed one is kept and listed). Read the ticket as STORED first —
    // the write carries `documents`, and BillingState's copy may be stale.
    let fresh = null;
    try {
      const snap = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId));
      fresh = snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (err) {
      console.warn('[Billing] fresh ticket read before move failed:', err?.message || err);
    }
    if (!fresh) { window.showToast?.(`Job "${trimmedName}" is ready, but the ticket could not be read — reload and move it by hand.`, 'error'); return; }
    if (!_ticketMoveEligible(fresh)) { window.showToast?.(`Job "${trimmedName}" is ready — the ticket is invoiced, archived or locked and stays on its job.`, 'error'); return; }
    // S2b (COD) — same customer gate as onTicketDrop, on the FRESH read: the new
    // job was just created under targetCustId, and a ticket whose customer changed
    // while it sat on screen must not land on another customer's job.
    if (fresh.customerId !== targetCustId) {
      window.showToast?.('This ticket belongs to a different customer now — reload before moving it.', 'error');
      return;
    }
    const oldJob = fresh.jobId ? (BillingState.jobs.find(j => j.id === fresh.jobId) || null) : null;
    const newJob = { id: newJobId, ...jobDoc };
    const { updates, summary } = _applyJobChangeToTicket(fresh, oldJob, newJob);
    summary.permitLinesFlagged = await _fetchPermitLinesFromOtherJob(tenant, ticketId, oldJob, newJobId); // S3

    // Optimistic local update for ticket
    const ticket = BillingState.tickets.find(t => t.id === ticketId);
    if (ticket) Object.assign(ticket, updates);

    // Auto-expand the new job group
    BillingState.expandedJobs[`${targetCustId}_${newJobId}`] = true;

    renderTicketsList();

    // Update ticket in Firestore
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), updates);
    window.showToast?.(`Job "${trimmedName}" — ${_jobChangeSummaryText(summary).replace(/^Moved to "[^"]*"/, 'ticket moved')}`,
      (summary.kept.length || summary.permitLinesFlagged.length) ? 'warning' : 'success');
  } catch (err) {
    console.error('Error moving ticket to the picked job:', err);
    showToast('Failed to move the ticket', 'error');
  }
}

// ============================================
// TASK-9610 — PROJECT FOLDERS IN THE TICKETS TREE + MOVE A JOB BETWEEN PROJECTS
// ============================================
// Customer → Project folders → Jobs → Tickets. A job moves into, between or out
// of a project by drag (desktop) or the job row's "Move to project…" button (the
// touch path). BOTH triggers end in moveJobToProject, the one link function, and
// it writes nothing itself: ProjectsModule.changeJobProject /
// unlinkJobFromProject (reached through window — Pitfall #1, no cross-import)
// re-read the job + project in a transaction, refuse a job someone else moved
// (expectedPreviousProjectId), and firestore.rules jobProjectLinkOk /
// jobProjectAdmits stay the authority. No rules change.
//
// Why not _addJobsToProjectFlow (COD Independent ❌): it skips already-linked
// jobs (never re-parents), passes expectedPreviousProjectId:null, has no
// unlink, and only offers single-customer targets.

// PURE. The folder expand key — its own map, never the expandedJobs one.
function projectFolderKey(custId, projectId) {
  return `${custId}_proj_${projectId}`;
}

// PURE. Split one customer's job groups (Map jobKey → group, as built by
// groupTicketsByCustomerJob) into project folders + loose jobs, resolving each
// job's project from the loaded jobs/projects maps. A folder's counts are the
// groups it holds — i.e. the tickets IN THIS VIEW for this customer only.
function groupJobsIntoProjectFolders(jobEntries, jobById, projById) {
  const folders = new Map();
  const loose = [];
  for (const [jobKey, jg] of (jobEntries || [])) {
    const job = jobKey && jobKey !== '_none' && jobById ? jobById.get(jobKey) : null;
    const pid = job && typeof job.projectId === 'string' && job.projectId ? job.projectId : null;
    if (!pid) { loose.push([jobKey, jg]); continue; }
    if (!folders.has(pid)) {
      const p = (projById && projById.get(pid)) || null;
      folders.set(pid, {
        projectId: pid,
        project: p,
        name: p ? (p.name || '(unnamed project)') : 'Project',
        poNumber: (p && p.poNumber) || '',
        multiCustomer: !!(p && Array.isArray(p.customerIds) && p.customerIds.length > 1),
        jobs: [],
        ticketCount: 0,
        totalAmount: 0
      });
    }
    const f = folders.get(pid);
    f.jobs.push([jobKey, jg]);
    f.ticketCount += (jg && jg.tickets ? jg.tickets.length : 0);
    f.totalAmount += (jg && Number(jg.totalAmount)) || 0;
  }
  const list = [...folders.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return { folders: list, loose };
}

// PURE. Exact mirror of projects.js projectAdoptsCustomer (TASK-9599): a project
// naming NO customer (and no other primary) may adopt this job's customer, with
// a confirm. Parity asserted by tests/task9610-ticket-project-folders.unit.cjs.
function projectAdoptEligibleForCustomer(project, customerId) {
  if (!project || project.deleting === true) return false;
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  if (!(ids.length === 0 && typeof customerId === 'string' && customerId !== '')) return false;
  return !project.primaryCustomerId || project.primaryCustomerId === customerId;
}

// PURE. Projects a job of `customerId` may move into: the project names that
// customer (multi-customer included — jobProjectAdmits admits it), or names no
// customer yet (adopt on confirm). Never a deleting or archived project.
function jobMoveTargets(projects, customerId) {
  if (typeof customerId !== 'string' || !customerId) return [];
  return (projects || []).filter(p => p && p.id && p.deleting !== true && p.status !== 'archived'
    && ((Array.isArray(p.customerIds) && p.customerIds.includes(customerId)) || projectAdoptEligibleForCustomer(p, customerId)))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// PURE. May this job (as loaded, projectId = where the caller believes it is)
// go to `toProjectId` (null = take it out)? `project` is the target resolved
// from loaded state by id — never from a drag payload.
// Returns { ok, unchanged?, adopt?, reason?, message? }.
function jobProjectMoveCheck(job, toProjectId, project) {
  if (!job || !job.id) return { ok: false, reason: 'missing', message: 'That job is no longer here — refresh.' };
  const from = job.projectId || null;
  const to = toProjectId || null;
  if (to === from) return { ok: true, unchanged: true };
  if (!to) return { ok: true, adopt: false };
  if (!job.customerId) return { ok: false, reason: 'no-customer', message: 'This job has no customer — set its customer first.' };
  if (!project || project.id !== to) return { ok: false, reason: 'missing-project', message: 'That project is no longer here — refresh.' };
  if (project.deleting === true) return { ok: false, reason: 'deleting', message: 'That project is being deleted.' };
  if (project.status === 'archived') return { ok: false, reason: 'archived', message: 'That project is archived — reopen it in Projects first.' };
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  if (ids.includes(job.customerId)) return { ok: true, adopt: false };
  if (projectAdoptEligibleForCustomer(project, job.customerId)) return { ok: true, adopt: true };
  return {
    ok: false, reason: 'customer',
    message: 'That project is for a different customer. A job can only go into a project for its own customer — add the customer in Edit Project first.'
  };
}

// PURE. The invoiced-ticket question, in the Add-to-project dialog's words.
function jobMoveBilledMessage(title, billedCount, fromName, toName, fromId, toId) {
  const n = `${billedCount} ticket${billedCount !== 1 ? 's' : ''}`;
  const are = billedCount !== 1 ? 'are' : 'is';
  const it = billedCount !== 1 ? 'them' : 'it';
  let move;
  if (fromId && toId) move = `Moving it takes ${it} out of ${fromName}’s billed totals and into ${toName}’s.`;
  else if (fromId) move = `Taking it out removes ${it} from ${fromName}’s billed totals.`;
  else move = `Adding moves ${it} into ${toName}’s billed totals.`;
  return `${n} on “${title || 'this job'}” ${are} already invoiced. ${move} Nothing on the invoice changes.`;
}

function _billingProjectLabel(projectId) {
  if (!projectId) return 'no project';
  const p = (BillingState.projects || []).find(x => x.id === projectId);
  return p && p.name ? `“${p.name}”` : 'the project';
}

// One folder row. Ids live in data-* attributes only; every handler reads them
// back off the element (no ids inside inline onclick strings).
function _projectFolderHeaderHtml(custId, folder, expanded, empty) {
  const p = folder.project;
  const noCustomer = !!(p && !(Array.isArray(p.customerIds) && p.customerIds.length));
  const nJobs = folder.jobs.length;
  const visible = empty && _jobDragData && _jobDragData.custId === custId ? ' visible' : '';
  const meta = empty
    ? `<span class="billing-accordion-meta">Drop a job here</span>`
    : `<span class="billing-accordion-meta" title="Counts and dollars are only the tickets shown with the filters above${folder.multiCustomer ? ', for this customer only — the project also has other customers' : ''}.">${nJobs} job${nJobs !== 1 ? 's' : ''} · ${folder.ticketCount} ticket${folder.ticketCount !== 1 ? 's' : ''} &middot; ${invoiceFmtMoney(folder.totalAmount)} in this view${folder.multiCustomer ? ' (this customer’s part)' : ''}</span>`;
  const filterBtn = !empty && p && !folder.multiCustomer
    ? `<button type="button" class="billing-job-action" data-project-id="${window.escAttr(folder.projectId)}" onclick="event.stopPropagation();window.BillingModule.filterToProjectFolder(this)" title="Show only this project's tickets" aria-label="Show only this project's tickets"><span class="material-icons">filter_alt</span></button>`
    : '';
  return `
    <div class="billing-project-folder${empty ? ' billing-project-folder--empty' + visible : ''}" data-cust-id="${window.escAttr(custId)}" data-project-id="${window.escAttr(folder.projectId)}"
      ${empty ? '' : `role="button" tabindex="0" aria-expanded="${expanded ? 'true' : 'false'}" onclick="window.BillingModule.toggleProjectFolder(this)" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.BillingModule.toggleProjectFolder(this);}"`}
      ondragover="window.BillingModule.onFolderDragOver(event)"
      ondragleave="window.BillingModule.onFolderDragLeave(event)"
      ondrop="window.BillingModule.onFolderDrop(event)"
      style="border-left: ${_customerRailBorder(custId)};">
      ${empty ? '' : `<span class="material-icons accordion-chevron ${expanded ? 'expanded' : ''}">chevron_right</span>`}
      <span class="material-icons">${expanded ? 'folder_open' : 'folder'}</span>
      <span class="billing-project-folder-name">${escapeHtml(folder.name)}</span>
      ${folder.poNumber ? `<span class="billing-project-folder-po">PO ${escapeHtml(folder.poNumber)}</span>` : ''}
      ${noCustomer ? `<span class="billing-project-folder-po" title="Dropping a job here asks to add its customer to the project">no customer yet</span>` : ''}
      ${meta}
      ${filterBtn}
    </div>`;
}

function toggleProjectFolder(el) {
  const custId = el && el.dataset ? el.dataset.custId : '';
  const pid = el && el.dataset ? el.dataset.projectId : '';
  if (!custId || !pid || el.classList.contains('billing-project-folder--empty')) return;
  const key = projectFolderKey(custId, pid);
  BillingState.expandedProjectFolders[key] = !BillingState.expandedProjectFolders[key];
  renderTicketsList();
}

function filterToProjectFolder(el) {
  const pid = el && el.dataset ? el.dataset.projectId : '';
  if (pid) setTicketProjectFilter(pid);
}

function _ensureProjectFolderStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('tickets-9610-styles')) return;
  const el = document.createElement('style');
  el.id = 'tickets-9610-styles';
  el.textContent = `
.billing-project-folder { display: flex; align-items: center; gap: 8px; padding: 9px 14px; margin-bottom: 6px; background: #FAF5FF; border: 1px solid #E9D5FF; border-radius: var(--radius-md); cursor: pointer; user-select: none; transition: background 0.15s, box-shadow 0.15s; min-width: 0; }
.billing-project-folder:hover { background: #F3E8FF; }
.billing-project-folder:focus-visible { outline: 2px solid var(--brand-blue); outline-offset: 1px; }
.billing-project-folder .material-icons { font-size: 18px; color: #7E22CE; }
.billing-project-folder .accordion-chevron { color: var(--text-secondary); }
.billing-project-folder-name { font-size: 0.85rem; font-weight: 700; color: var(--text-primary); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.billing-project-folder-po { font-size: 0.72rem; font-weight: 600; color: #6B21A8; background: #fff; border: 1px solid #E9D5FF; border-radius: 10px; padding: 1px 7px; white-space: nowrap; max-width: 160px; overflow: hidden; text-overflow: ellipsis; }
.billing-project-folder .billing-accordion-meta { margin-left: auto; }
.billing-project-folder .billing-job-action .material-icons { color: var(--text-secondary); font-size: 16px; }
.billing-project-folder-body { margin: 0 0 6px 14px; padding-left: 8px; border-left: 2px solid #E9D5FF; }
.billing-project-folder--empty { display: none; border-style: dashed; background: #fff; cursor: default; }
.billing-project-folder--empty.visible { display: flex; }
.billing-project-folder.drag-over { background: #F3E8FF; border-color: #9333EA; box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.2); }
.billing-project-folder.drag-refuse { border-color: #DC2626; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15); }
.billing-job-drag { cursor: grab; color: var(--text-secondary); }
.billing-job-drag:active { cursor: grabbing; }
.billing-accordion-subheader.dragging { opacity: 0.5; }
.billing-job-drop-zones { display: none; gap: 12px; margin: 12px 0; }
.billing-job-drop-zones.visible { display: flex; }
.billing-job-dropzone { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 16px; min-height: 52px; border: 3px dashed #C084FC; border-radius: 12px; background: #FAF5FF; color: #6B21A8; font-size: 0.9rem; font-weight: 700; transition: all 0.15s; }
.billing-job-dropzone .material-icons { font-size: 24px; }
.billing-job-dropzone--unlink { border-color: #94A3B8; background: #F1F5F9; color: #334155; }
.billing-job-dropzone.is-hidden { display: none; }
.billing-job-dropzone.drag-over { border-color: #9333EA; background: #F3E8FF; box-shadow: 0 0 0 4px rgba(147, 51, 234, 0.2); }
@media (max-width: 640px) {
  .billing-project-folder { flex-wrap: wrap; padding: 8px 10px; }
  .billing-project-folder .billing-accordion-meta { margin-left: 0; flex-basis: 100%; }
  .billing-project-folder-body { margin-left: 6px; padding-left: 6px; }
}
@media (hover: none) {
  .billing-job-actions { opacity: 1; }
  .billing-job-drag { display: none; }
}
`;
  document.head.appendChild(el);
}

// ── Drag (desktop trigger) ──────────────────────────────────────────────────
let _jobDragData = null; // { jobId, custId, fromProjectId } — set only by onJobDragStart

function _isJobDrag(event) {
  return !!_jobDragData && !dragHasType(event && event.dataTransfer, TICKET_DRAG_MIME); // see _isTicketDrag: WebKit hides custom types
}

function _revealJobDropTargets(custId, linked) {
  document.querySelectorAll('.billing-project-folder--empty, .billing-job-drop-zones').forEach(n => {
    if (n.dataset.custId === custId) n.classList.add('visible');
  });
  document.querySelectorAll('.billing-job-dropzone--unlink').forEach(n => n.classList.toggle('is-hidden', !linked));
}

function onJobDragStart(event) {
  const el = event.currentTarget;
  const jobId = el && el.dataset ? el.dataset.jobId : '';
  const custId = el && el.dataset ? el.dataset.custId : '';
  const job = jobId ? (BillingState.jobs || []).find(j => j.id === jobId) : null;
  if (!job || !custId || !_canLinkJobsToProjects()) { event.preventDefault(); return; }
  _dragData = null; // never both at once
  const drag = { jobId: job.id, custId, fromProjectId: job.projectId || null };
  _jobDragData = drag;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(JOB_DRAG_MIME, job.id);
  const row = el.closest('.billing-accordion-subheader');
  if (row) {
    try { event.dataTransfer.setDragImage?.(row, 16, 16); } catch (_) { /* default image */ }
    row.classList.add('dragging');
  }
  // Reveal after the drag has begun: changing layout inside dragstart can
  // cancel the drag in some browsers.
  setTimeout(() => { if (_jobDragData === drag) _revealJobDropTargets(custId, !!drag.fromProjectId); }, 0);
}

function onJobDragEnd() {
  _jobDragData = null;
  document.querySelectorAll('.billing-project-folder--empty.visible, .billing-job-drop-zones.visible').forEach(n => n.classList.remove('visible'));
  document.querySelectorAll('.billing-accordion-subheader.dragging').forEach(n => n.classList.remove('dragging'));
  document.querySelectorAll('.billing-project-folder.drag-over, .billing-project-folder.drag-refuse, .billing-job-dropzone.drag-over')
    .forEach(n => n.classList.remove('drag-over', 'drag-refuse'));
}

function onFolderDragOver(event) {
  const isJob = _isJobDrag(event);
  const isTicket = !isJob && _isTicketDrag(event);
  if (!isJob && !isTicket) return;
  event.preventDefault(); // accept, so a misplaced TICKET gets told why on drop
  event.dataTransfer.dropEffect = 'move';
  event.currentTarget.classList.add(isJob ? 'drag-over' : 'drag-refuse');
}

function onFolderDragLeave(event) {
  event.currentTarget.classList.remove('drag-over', 'drag-refuse');
}

function onFolderDrop(event) {
  const el = event.currentTarget;
  el.classList.remove('drag-over', 'drag-refuse');
  if (_isTicketDrag(event)) {
    event.preventDefault();
    window.showToast?.('Drop it on a job inside the project — tickets move between jobs, jobs move into projects.', 'info');
    return;
  }
  if (!_isJobDrag(event)) return;
  event.preventDefault();
  const drag = _jobDragData;
  const folderCust = el.dataset.custId || '';
  const pid = el.dataset.projectId || '';
  if (!pid) return;
  if (folderCust !== drag.custId) {
    window.showToast?.('A job can only go into a project under its own customer.', 'error');
    return;
  }
  moveJobToProject({ jobId: drag.jobId, toProjectId: pid, fromProjectId: drag.fromProjectId, source: 'arap-drag' });
}

// The open folder's body: job drags only (a ticket over a job row inside it is
// the job row's business, and must never reach a project move).
function onFolderBodyDragOver(event) {
  if (!_isJobDrag(event)) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
}

function onFolderBodyDrop(event) {
  if (!_isJobDrag(event)) return;
  event.preventDefault();
  const el = event.currentTarget;
  const drag = _jobDragData;
  const pid = el.dataset.projectId || '';
  if (!pid) return;
  if ((el.dataset.custId || '') !== drag.custId) {
    window.showToast?.('A job can only go into a project under its own customer.', 'error');
    return;
  }
  moveJobToProject({ jobId: drag.jobId, toProjectId: pid, fromProjectId: drag.fromProjectId, source: 'arap-drag' });
}

function onJobZoneDragOver(event) {
  if (!_isJobDrag(event) || event.currentTarget.dataset.custId !== _jobDragData.custId) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
  event.currentTarget.classList.add('drag-over');
}

function onJobZoneDrop(event) {
  const el = event.currentTarget;
  el.classList.remove('drag-over');
  if (!_isJobDrag(event)) return;
  event.preventDefault();
  const drag = _jobDragData;
  if (el.dataset.custId !== drag.custId) return;
  if (el.dataset.action === 'unlink') {
    if (!drag.fromProjectId) return;
    moveJobToProject({ jobId: drag.jobId, toProjectId: null, fromProjectId: drag.fromProjectId, source: 'arap-drag' });
  } else if (el.dataset.action === 'new') {
    _promptMoveJob(drag.jobId, { preselect: '__new__', fromProjectId: drag.fromProjectId, source: 'arap-drag' });
  }
}

// ── Button (touch trigger) ──────────────────────────────────────────────────
function openMoveJobToProject(el) {
  const jobId = el && el.dataset ? el.dataset.jobId : '';
  if (jobId) _promptMoveJob(jobId, { source: 'arap-move-button' });
}

async function _promptMoveJob(jobId, { preselect = null, fromProjectId, source = 'arap-move-button' } = {}) {
  if (!_canLinkJobsToProjects()) { window.showToast?.(LINK_NEEDS_DISPATCH_MSG, 'warning', null, 7000); return; }
  if (BillingState._linkBusy) return;
  _flushSecondarySubs();
  if (!BillingState._jobsLoaded) { window.showToast?.('Jobs are still loading — try again in a moment.', 'info'); return; }
  const job = (BillingState.jobs || []).find(j => j.id === jobId);
  if (!job) { window.showToast?.('That job is no longer here — refresh.', 'error'); return; }
  if (!job.customerId) { window.showToast?.('This job has no customer — set its customer first.', 'error'); return; }
  if (!BillingState._projectsLoaded) await _ensureBillingProjectsSettled();
  // The project the job is in as the dialog opens — what the write expects.
  const from = fromProjectId === undefined ? (job.projectId || null) : (fromProjectId || null);
  const current = from ? (BillingState.projects.find(p => p.id === from) || { id: from, name: '' }) : null;
  const targets = jobMoveTargets(BillingState.projects, job.customerId).filter(p => p.id !== from);
  const customerName = BillingState.customers.find(c => c.id === job.customerId)?.name || '';
  const choice = await _moveJobToProjectModal({ job, current, targets, preselect, customerName });
  if (!choice) return;
  await moveJobToProject({
    jobId: job.id,
    toProjectId: choice.toProjectId || null,
    fromProjectId: from,
    newProject: choice.newProject || null,
    source
  });
}

// The Move dialog. Resolves { toProjectId } | { toProjectId:null } (take it
// out) | { toProjectId:null, newProject:{ name, po } } | null.
let _moveJobModalResolve = null; // COD (Code Reviewer): a superseded dialog resolves null, never hangs
function _moveJobToProjectModal({ job, current, targets, preselect, customerName }) {
  return new Promise(resolve => {
    const id = 'billing-move-job-project-modal';
    if (_moveJobModalResolve) { const prev = _moveJobModalResolve; _moveJobModalResolve = null; prev(null); }
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    const title = job.title || job.name || '(untitled job)';
    const label = p => {
      const ids = Array.isArray(p.customerIds) ? p.customerIds : [];
      const tail = !ids.length ? ' — no customer yet (adds this customer)' : (ids.length > 1 ? ' — shared with other customers' : '');
      return `${escapeHtml(p.name || '(unnamed project)')}${p.poNumber ? ' · PO ' + escapeHtml(p.poNumber) : ''}${tail}`;
    };
    const def = preselect || (targets.length ? targets[0].id : '__new__');
    const opts = targets.map(p => `<option value="${window.escAttr(p.id)}" ${p.id === def ? 'selected' : ''}>${label(p)}</option>`).join('')
      + (current ? `<option value="__none__" ${def === '__none__' ? 'selected' : ''}>No project — take it out of ${escapeHtml(current.name || 'its project')}</option>` : '')
      + `<option value="__new__" ${def === '__new__' ? 'selected' : ''}>+ New project…</option>`;
    const suggested = String(title).replace(/\s+\d{1,2}[./-]\d{1,2}([./-]\d{2,4})?\s*$/, '').trim();
    el.innerHTML = `
      <div class="modal-content" style="max-width:520px;width:92%;height:auto;max-height:85vh;max-height:85dvh;overflow:auto;padding:20px;">
        <h3 style="margin:0 0 var(--spacing-xs);color:var(--text-primary);">Move “${escapeHtml(title)}” to a project</h3>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 10px;">
          ${customerName ? escapeHtml(customerName) + ' — t' : 'T'}he job keeps its name, days and tickets; all of its tickets move with it.
          ${current ? `Now in <strong style="color:var(--text-primary);">${escapeHtml(current.name || 'a project')}</strong>.` : 'Not in a project yet.'}
        </p>
        <label for="mjp-project" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Project</label>
        <select id="mjp-project" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">${opts}</select>
        <div id="mjp-new" style="display:${def === '__new__' ? 'block' : 'none'};">
          <label for="mjp-name" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">New project name</label>
          <input type="text" id="mjp-name" maxlength="120" placeholder="e.g. Hamlin" value="${window.escAttr(suggested)}" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">
          <label for="mjp-po" style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">PO number (optional)</label>
          <input type="text" id="mjp-po" maxlength="64" placeholder="Customer PO for this project" style="width:100%;padding:9px 10px;margin-bottom:4px;font-size:16px;">
          <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 10px;">A job that has no PO of its own picks this one up. A job’s own PO is never changed.</p>
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
          <button type="button" class="btn btn-primary" data-move="1">Move</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { if (_moveJobModalResolve === done) _moveJobModalResolve = null; el.remove(); resolve(v); };
    _moveJobModalResolve = done;
    const sel = el.querySelector('#mjp-project');
    sel.addEventListener('change', () => { el.querySelector('#mjp-new').style.display = sel.value === '__new__' ? 'block' : 'none'; });
    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-move]')) return;
      if (sel.value === '__none__') return done({ toProjectId: null });
      if (sel.value !== '__new__') return done({ toProjectId: sel.value });
      const name = String(el.querySelector('#mjp-name')?.value || '').trim();
      if (!name) { window.showToast?.('Give the new project a name.', 'error'); return; }
      if (name.length > 120) { window.showToast?.('Project name is too long (120 characters max).', 'error'); return; }
      done({ toProjectId: null, newProject: { name, po: poNormalizeForSave(el.querySelector('#mjp-po')?.value) } });
    });
  });
}

const MOVE_JOB_READ_TIMEOUT_MS = 15000;
// PURE. Reject with { moveJobTimeout: true } if `promise` has not settled in `ms`.
function _withTimeout(promise, ms) {
  let timer;
  const t = new Promise((_, reject) => {
    timer = setTimeout(() => { const e = new Error('timed out after ' + ms + 'ms'); e.moveJobTimeout = true; reject(e); }, ms);
  });
  return Promise.race([promise, t]).finally(() => clearTimeout(timer));
}

// ── THE link function (both triggers) ───────────────────────────────────────
// { jobId, toProjectId|null, fromProjectId, newProject?, source }:
// resolve from loaded state → access + move check → invoiced-ticket count on
// server truth + one confirm → ProjectsModule.changeJobProject (link,
// re-parent, adopt) or unlinkJobFromProject (out), with
// expectedPreviousProjectId = fromProjectId so a job someone else moved is
// refused, never overwritten.
async function moveJobToProject({ jobId, toProjectId = null, fromProjectId, newProject = null, source = 'arap-move' } = {}) {
  if (!_canLinkJobsToProjects()) { window.showToast?.(LINK_NEEDS_DISPATCH_MSG, 'warning', null, 7000); return { ok: false, reason: 'access' }; }
  if (BillingState._linkBusy) return { ok: false, reason: 'busy' };
  const tenant = BillingState.tenantId;
  if (!tenant) return { ok: false, reason: 'no-tenant' };
  const loaded = (BillingState.jobs || []).find(j => j.id === jobId) || null;
  if (!loaded) { window.showToast?.('That job is no longer here — refresh.', 'error'); return { ok: false, reason: 'missing' }; }
  const from = fromProjectId === undefined ? (loaded.projectId || null) : (fromProjectId || null);
  const job = { ...loaded, projectId: from };
  const title = job.title || job.name || 'this job';

  BillingState._linkBusy = true;
  let created = null;
  try {
    if (!BillingState._projectsLoaded) await _ensureBillingProjectsSettled();
    let to = newProject ? null : (toProjectId || null);
    if (newProject) {
      if (!job.customerId) { window.showToast?.('This job has no customer — set its customer first.', 'error'); return { ok: false, reason: 'no-customer' }; }
    } else {
      const project = to ? (BillingState.projects.find(p => p.id === to) || null) : null;
      const check = jobProjectMoveCheck(job, to, project);
      if (!check.ok) { window.showToast?.(check.message, 'error', null, 8000); return { ok: false, reason: check.reason }; }
      if (check.unchanged) { window.showToast?.(`“${title}” is already ${to ? 'in ' + _billingProjectLabel(to) : 'out of any project'}.`, 'info'); return { ok: true, unchanged: true }; }
    }

    // Invoiced tickets move between rollups with the job — count them on
    // server truth (every ticket on the job, not the loaded window) and ask
    // once. Fail CLOSED: "could not look" is not "none".
    let billed = 0;
    try {
      // Bounded: a hung read must end in a message, never an endless silent wait.
      // Nothing has been written at this point (the read precedes every create/link).
      const snap = await _withTimeout(
        getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('jobId', '==', job.id))),
        MOVE_JOB_READ_TIMEOUT_MS);
      billed = linkBilledTicketCount(snap.docs.map(d => d.data()));
    } catch (err) {
      console.warn('[Billing] move-job ticket read failed:', err);
      window.showToast?.(err && err.moveJobTimeout
        ? 'Couldn’t move the job — check your connection and try again.'
        : 'Could not check this job’s tickets — nothing was changed. Try again.', 'error');
      return { ok: false, reason: 'unverified' };
    }
    const toName = newProject ? `“${newProject.name}”` : _billingProjectLabel(to);
    if (billed > 0) {
      const msg = jobMoveBilledMessage(title, billed, _billingProjectLabel(from), toName, from, newProject ? '__new__' : to);
      const yes = typeof window.showConfirm === 'function'
        ? await window.showConfirm('Move invoiced tickets?', msg, { confirmText: 'Move job', cancelText: 'Cancel' })
        : confirm(msg);
      if (!yes) return { ok: false, reason: 'declined' };
    }

    const pm = await _projectsLinkGate();
    if (!pm) { window.showToast?.('Projects could not be loaded — nothing was changed. Refresh and try again.', 'error'); return { ok: false, reason: 'no-projects-module' }; }

    if (newProject) {
      if (typeof pm.createProject !== 'function') { window.showToast?.('Projects could not be loaded — nothing was changed.', 'error'); return { ok: false, reason: 'no-projects-module' }; }
      const customerName = BillingState.customers.find(c => c.id === job.customerId)?.name || job.customer || '';
      created = await pm.createProject({
        name: newProject.name,
        customerIds: [job.customerId],
        primaryCustomerId: job.customerId,
        primaryCustomerName: customerName,
        poNumber: newProject.po || ''
      });
      if (!created) return { ok: false, reason: 'create-failed' }; // createProject already said why
      to = created;
    }

    let res;
    try {
      res = to
        ? await pm.changeJobProject(job.id, to, {
          expectedPreviousProjectId: from,
          adoptCustomer: 'ask',
          billedConfirmed: billed,
          source,
          quiet: true
        })
        : await pm.unlinkJobFromProject(job.id, {
          expectedPreviousProjectId: from,
          billedConfirmed: billed,
          source,
          quiet: true
        });
    } catch (err) {
      console.warn('[Billing] job project change threw:', err);
      res = { ok: false, reason: 'error' };
    }
    res = res || { ok: false, reason: 'error' };
    if (!res.ok) {
      if (created && typeof pm.discardUnusedProject === 'function') {
        try { await pm.discardUnusedProject(created); } catch (_) { /* an empty project is harmless */ }
      }
      // changeJobProject already told the user why (a declined question needs nothing more).
      return res;
    }

    // Show the result where it landed, then let the jobs snapshot confirm it.
    if (to) BillingState.expandedProjectFolders[projectFolderKey(job.customerId, to)] = true;
    if (!res.unchanged) loaded.projectId = to;
    BillingState._projectsLoaded = false;
    await loadBillingProjects();
    const toLabel = _billingProjectLabel(to);
    const po = res.poApplied && res.poApplied.poNumber ? ` PO ${res.poApplied.poNumber} applied from the project.` : '';
    const text = !to
      ? `“${title}” taken out of ${_billingProjectLabel(from)}.`
      : (from ? `“${title}” moved from ${_billingProjectLabel(from)} to ${toLabel}.` : `“${title}” moved into ${toLabel}.`);
    window.showToast?.(`${text}${po}`, 'success');
    renderTicketsList();
    return res;
  } finally {
    BillingState._linkBusy = false;
  }
}

// ============================================
// TASK-9810 — put THIS ticket into a project from the Review Ticket modal
// ============================================
// A ticket is in a project only through its job (jobs.projectId — never a
// tickets field; see ticketProjectOf). So "put this ticket in project P" means
// "put this ticket on a job in P", through the one job-change path the Job
// picker already uses (updateReviewJob → _applyJobChangeToTicket, staged until
// Save). "Just this ticket" is the DEFAULT. The second choice — the whole job
// and its other tickets — goes through the one job-link function
// (_promptMoveJob → moveJobToProject), which writes at once; the dialog says
// so. No second write path, no tickets.projectId.

// PURE. The project a ticket is in, through its job. loaded=false → the name is
// not known yet (projects not read), which is not "No project".
function reviewTicketProject(jobId, jobs, projects, loaded) {
  const job = jobId ? (Array.isArray(jobs) ? jobs : []).find(j => j && j.id === jobId) : null;
  const pid = job && typeof job.projectId === 'string' && job.projectId ? job.projectId : null;
  if (!pid) return { projectId: null, name: '', loaded: loaded !== false };
  if (loaded === false) return { projectId: pid, name: '', loaded: false };
  const p = (Array.isArray(projects) ? projects : []).find(x => x && x.id === pid);
  return { projectId: pid, name: p ? (String(p.name || '').trim() || 'Untitled project') : '(project not found)', loaded: true };
}

// PURE. The words for the Project field and the summary line.
function reviewProjectText(info) {
  if (!info) return 'No project';
  if (info.projectId && !info.loaded) return 'Loading…';
  return info.projectId ? info.name : 'No project';
}

// Summary-line piece (text via escapeHtml only, no handlers).
function _reviewHeaderSummaryProjectHtml(info) {
  const has = !!(info && info.projectId);
  return `<span id="review-header-summary-project-wrap" style="color:var(--text-secondary);">· <span class="material-icons" style="font-size:14px;vertical-align:-2px;${has ? 'color:var(--brand-blue);' : ''}" aria-hidden="true">folder</span> <span id="review-header-summary-project" style="${has ? 'color:var(--text-primary);font-weight:600;' : ''}">${escapeHtml(reviewProjectText(info))}</span></span>`;
}

// The Project field under the Customer/Job pickers: read-only text + the one
// button (the read-only sweep in renderReviewTicket disables it like the rest).
function _reviewProjectFieldHtml(info, hasCustomer) {
  const has = !!(info && info.projectId);
  return `
      <div class="review-key-field" id="review-project-field">
        <label for="review-project-change"><span class="material-icons">folder</span> Project</label>
        <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
          <span id="review-project-current" style="flex:1;min-width:0;font-size:0.9rem;${has ? 'color:var(--text-primary);font-weight:600;' : 'color:var(--text-secondary);'}">${escapeHtml(reviewProjectText(info))}</span>
          <button type="button" id="review-project-change" class="btn btn-sm btn-secondary" style="white-space:nowrap;min-height:36px;"
            onclick="window.BillingModule.changeReviewTicketProject()"${hasCustomer ? '' : ' disabled title="Select a customer first"'}>
            <span class="material-icons" style="font-size:16px;">drive_file_move</span> ${has ? 'Change project' : 'Put in a project'}
          </button>
        </div>
      </div>`;
}

// PURE. Does picking `newJobId` need the "Move this ticket…?" question? By ID,
// never by name — two same-named day-jobs are two jobs.
function reviewJobChangeNeedsConfirm(originalJobId, newJobId) {
  return !!originalJobId && !!newJobId && String(originalJobId) !== String(newJobId);
}

// PURE. Other live tickets on the job: this one and deleted ones excluded.
function reviewOtherTicketCount(tickets, ticketId) {
  let n = 0;
  for (const x of (Array.isArray(tickets) ? tickets : [])) {
    if (x && x.id !== ticketId && x.status !== 'deleted') n++;
  }
  return n;
}

// PURE. What the dialog offers for ONE ticket of `customerId`: every live
// project that names this customer or already holds one of its jobs, with the
// jobs (this customer's, not cancelled, newest first) the ticket could move
// onto. canCreate = "+ New job in this project" is offered: Dispatch access AND
// the project names the customer (the jobs create rule, jobProjectLinkOk,
// admits nothing else).
function reviewProjectChoices(projects, jobs, customerId, opts = {}) {
  if (typeof customerId !== 'string' || !customerId) return [];
  const ms = j => {
    const v = j && j.startDate;
    const d = v && typeof v.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : (v ? new Date(v) : null));
    return d && !isNaN(d.getTime()) ? d.getTime() : 0;
  };
  const byProject = new Map();
  for (const j of (Array.isArray(jobs) ? jobs : [])) {
    if (!j || !j.id || j.customerId !== customerId || j.status === 'cancelled') continue;
    if (typeof j.projectId !== 'string' || !j.projectId) continue;
    if (!byProject.has(j.projectId)) byProject.set(j.projectId, []);
    byProject.get(j.projectId).push(j);
  }
  const out = [];
  for (const p of (Array.isArray(projects) ? projects : [])) {
    if (!p || !p.id || p.deleting === true || p.status === 'archived') continue;
    const names = Array.isArray(p.customerIds) && p.customerIds.includes(customerId);
    const pj = byProject.get(p.id) || [];
    if (!names && !pj.length) continue;
    out.push({
      id: p.id,
      name: String(p.name || '').trim() || 'Untitled project',
      po: String(p.poNumber || '').trim(),
      jobs: pj.slice().sort((a, b) => ms(b) - ms(a)),
      canCreate: names && opts.canCreate === true
    });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

// PURE. PO differences to SHOW before the move — nothing is rewritten here.
// The job-change path keeps a hand-typed ticket PO and lists it; an empty or
// inherited one follows the new job's (TASK-9355 S2). `originalJobPo` = the PO
// of the job the ticket is saved on, which is what makes a PO "inherited".
function reviewProjectPoNotes(ticketPo, originalJobPo, job, project) {
  const t = String(ticketPo || '').trim();
  const o = String(originalJobPo || '').trim();
  const jp = String((job && job.poNumber) || '').trim();
  const pp = String((project && project.poNumber) || '').trim();
  const eq = (a, b) => a.toLowerCase() === b.toLowerCase();
  const handTyped = !!t && !(o && eq(t, o));
  const notes = [];
  if (t && jp && !eq(t, jp) && !(handTyped && jobHasPo(job, t))) { // TASK-9811 S2 — an extra PO of the new job is not a mismatch
    notes.push(handTyped
      ? `PO mismatch: this ticket's PO ${t} is not the job's PO ${jp}. The ticket keeps ${t} — change it by hand if that's wrong.`
      : `PO ${t} came from the current job — it will follow the new job to ${jp}.`);
  } else if (t && !jp && pp && !eq(t, pp)) {
    notes.push(`The project's standing PO is ${pp}; this ticket has ${t}${handTyped ? ' and keeps it' : ''}.`);
  } else if (!t && jp) {
    notes.push(`This ticket has no PO — it will take the job's PO ${jp}.`);
  } else if (!t && !jp && pp) {
    notes.push(`This ticket has no PO. The project's standing PO is ${pp}; the job has none.`);
  }
  return notes;
}

// PURE. The whole-job option's words.
function reviewWholeJobText(jobLabel, others) {
  const n = `${others} other ticket${others === 1 ? '' : 's'}`;
  return `This whole job — “${jobLabel || 'this job'}” and its ${n}`;
}

const NEW_JOB_NEEDS_DISPATCH_MSG = 'Starting a new job in a project needs Dispatch access on your login — pick one of the project’s jobs, or ask the owner to add it.';

// Button → dialog → the existing path for whichever scope was chosen.
async function changeReviewTicketProject() {
  const t = ReviewState.ticket || {};
  if (!ReviewState.ticketId) return;
  // Same gates as the Job picker: an invoiced / locked / archived ticket moves nowhere.
  if (isReviewReadOnly(t)) { window.showToast?.('This ticket is invoiced or locked — its job and project can’t change. Uninvoice it first.', 'warning'); return; }
  const custId = t.customerId || ReviewState.headerEdits?.customerId || '';
  if (!custId) { window.showToast?.('Select a customer first', 'info'); return; }
  if (BillingState._reviewProjectBusy) return;
  const tenant = BillingState.tenantId;
  if (!tenant) return;
  BillingState._reviewProjectBusy = true;
  try {
    _flushSecondarySubs();
    if (!BillingState._jobsLoaded) { window.showToast?.('Jobs are still loading — try again in a moment.', 'info'); return; }
    await _ensureBillingProjectsSettled();
    if (BillingState._projectsLoadFailed) { window.showToast?.('Could not load projects — check your connection and try again.', 'error'); return; }
    const jobId = t.jobId || '';
    const job = jobId ? (BillingState.jobs.find(j => j.id === jobId) || null) : null;
    // Other tickets on this job — a FRESH tenant-scoped read, never the loaded
    // window. Fail CLOSED: "could not look" is not "none".
    let others = 0;
    if (job) {
      try {
        const snap = await _withTimeout(
          getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('jobId', '==', job.id))),
          MOVE_JOB_READ_TIMEOUT_MS);
        others = reviewOtherTicketCount(snap.docs.map(d => ({ id: d.id, ...d.data() })), ReviewState.ticketId);
      } catch (err) {
        console.warn('[Billing] review project: ticket read failed:', err);
        window.showToast?.('Could not check this job’s other tickets — nothing was changed. Try again.', 'error');
        return;
      }
    }
    const canDispatch = _canLinkJobsToProjects();
    const choices = reviewProjectChoices(BillingState.projects, BillingState.jobs, custId, { canCreate: canDispatch });
    const current = reviewTicketProject(jobId, BillingState.jobs, BillingState.projects, true);
    // The whole-job option moves the job this ticket is SAVED on. With a staged,
    // unsaved job change it would move the wrong job — ask for a Save first.
    const savedJobId = ReviewState._originalSnapshot?.jobId || '';
    const jobUnsaved = Object.prototype.hasOwnProperty.call(ReviewState.headerEdits || {}, 'jobId')
      && (ReviewState.headerEdits.jobId || '') !== savedJobId;
    // PO notes judge "inherited" against the job the ticket is on NOW (staged
    // or saved) — the same baseline updateReviewJob → _applyJobChangeToTicket
    // uses, so a note never promises what the move won't do (COD Code Reviewer).
    const choice = await _reviewProjectModal({
      ticket: t, job, others, choices, current, canDispatch, jobUnsaved,
      originalJobPo: job?.poNumber || ''
    });
    if (!choice) return;
    if (choice.scope === 'job') {
      // Whole job: the existing Move dialog → moveJobToProject (writes now).
      BillingState._reviewProjectBusy = false;
      await _promptMoveJob(job.id, { source: 'review-ticket' });
      if (ReviewState.ticketId) renderReviewTicket(); // the header shows where the job landed
      return;
    }
    let targetJobId = choice.jobId;
    if (targetJobId === '__new__') {
      const project = choices.find(p => p.id === choice.projectId);
      if (!project || !project.canCreate) { window.showToast?.(NEW_JOB_NEEDS_DISPATCH_MSG, 'warning', null, 7000); return; }
      // TASK-10043 S1 — a new job goes on the board for the ticket's day, with
      // a reason (the picker's new-job form → _createJobFromReview).
      const pick = await _pickJobDay({
        heading: `New job in ${project.name || 'this project'}`, newOnly: true,
        customerId: custId, lockCustomer: true, dateKey: t.workDate || ReviewState.ticket?.workDate || '',
        purpose: 'job', extra: { projectId: project.id, poNumber: project.po },
      });
      if (!pick || !pick.jobId) return;
      const ref = { id: pick.jobId };
      const newTitle = BillingState.jobs.find(j => j.id === ref.id)?.title || 'New job';
      // The jobs listener replaces the whole array on its next tick; until then
      // the job-change path needs the doc to read the PO from (TASK-1364 precedent).
      if (!BillingState.jobs.some(j => j.id === ref.id)) {
        try {
          const snap = await getDoc(doc(db, 'tenants', tenant, 'jobs', ref.id));
          if (snap.exists() && !BillingState.jobs.some(j => j.id === ref.id)) BillingState.jobs.push({ id: snap.id, ...snap.data() });
        } catch (_) { /* updateReviewJob says "job details not loaded" */ }
      }
      targetJobId = ref.id;
      window.showToast?.(`Job "${newTitle}" created in ${project.name} and put on the Dispatch board.`, 'info');
    }
    if (!targetJobId) return;
    if (targetJobId === jobId) { window.showToast?.('This ticket is already on that job.', 'info'); return; }
    _selectReviewJob(targetJobId);
  } finally {
    BillingState._reviewProjectBusy = false;
  }
}

// Pick `jobId` in the review Job picker and run the one job-change path
// (confirm by id + timecard note + staged re-derive). The picker may be
// windowed without this job — give it the option so a cancelled confirm reverts
// to something real; renderReviewTicket force-includes the ticket's job after.
function _selectReviewJob(jobId) {
  const sel = document.getElementById('review-job-select');
  if (sel) {
    if (![...sel.options].some(o => o.value === jobId)) {
      const job = BillingState.jobs.find(j => j.id === jobId);
      const opt = document.createElement('option');
      opt.value = jobId;
      opt.textContent = job ? _jobPickerLabel(job) : 'New job';
      sel.appendChild(opt);
    }
    sel.value = jobId;
  }
  updateReviewJob(jobId);
}

// The dialog. Resolves { scope:'ticket', projectId, jobId|'__new__' } |
// { scope:'job' } | null. Ids ride in option values; text is escaped; no
// user-owned string is compiled as JavaScript.
let _reviewProjectModalResolve = null; // a superseded dialog resolves null, never hangs
function _reviewProjectModal({ ticket, job, others, choices, current, canDispatch, jobUnsaved, originalJobPo }) {
  return new Promise(resolve => {
    const id = 'billing-review-project-modal';
    if (_reviewProjectModalResolve) { const prev = _reviewProjectModalResolve; _reviewProjectModalResolve = null; prev(null); }
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.className = 'modal';
    el.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:1100;';
    const escA = window.escAttr || escapeHtml;
    const ticketNo = ticket.ticketNo || ticket.ticketNumber || '';
    const jobLabel = job ? _jobPickerLabel(job) : '';
    const curId = current && current.projectId;
    const hasScope = !!job && others > 0;
    const wholeOff = !canDispatch || jobUnsaved;
    // Defaults: a project that is not the current one, then a job that is not
    // this ticket's; else "+ New job"; else the current (button stays off).
    const defProject = (choices.find(p => p.id !== curId) || choices[0] || {}).id || '';
    const jobOptsFor = pid => {
      const p = choices.find(x => x.id === pid);
      if (!p) return '<option value="" disabled selected>Pick a project first</option>';
      const other = p.jobs.find(j => !job || j.id !== job.id);
      const def = other ? other.id : (p.canCreate ? '__new__' : (p.jobs[0] ? p.jobs[0].id : ''));
      const rows = p.jobs.map(j => `<option value="${escA(j.id)}"${j.id === def ? ' selected' : ''}>${escapeHtml(_jobPickerLabel(j))}${job && j.id === job.id ? ' (this ticket’s job now)' : ''}</option>`).join('');
      const create = p.canCreate ? `<option value="__new__"${def === '__new__' ? ' selected' : ''}>+ New job in this project…</option>` : '';
      return (rows + create) || '<option value="" disabled selected>No jobs for this customer in this project yet</option>';
    };
    const projOpts = choices.map(p => `<option value="${escA(p.id)}"${p.id === defProject ? ' selected' : ''}>${escapeHtml(p.name)}${p.po ? ' · PO ' + escapeHtml(p.po) : ''}${p.id === curId ? ' (current)' : ''}</option>`).join('');
    const lbl = 'display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;';
    const card = 'display:flex;gap:10px;align-items:flex-start;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card, #fff);cursor:pointer;';
    el.innerHTML = `
      <div class="modal-content" style="max-width:540px;width:92%;height:auto;max-height:85vh;max-height:85dvh;overflow:auto;padding:20px;">
        <h3 style="margin:0 0 var(--spacing-xs);color:var(--text-primary);">Put ${ticketNo ? 'ticket #' + escapeHtml(String(ticketNo)) : 'this ticket'} in a project</h3>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 12px;">
          ${curId ? `Now in <strong style="color:var(--text-primary);">${escapeHtml(current.name)}</strong>${jobLabel ? ' on “' + escapeHtml(jobLabel) + '”' : ''}.` : (jobLabel ? `On “${escapeHtml(jobLabel)}”, not in a project.` : 'Not on a job yet.')}
          A ticket is in a project through its job.
        </p>
        ${hasScope ? `
        <fieldset style="border:0;padding:0;margin:0 0 12px;min-width:0;">
          <legend style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:6px;padding:0;">What moves?</legend>
          <label style="${card}margin-bottom:6px;">
            <input type="radio" name="rtp-scope" value="ticket" id="rtp-scope-ticket" checked style="margin-top:3px;flex:none;">
            <span><strong style="color:var(--text-primary);">Just this ticket</strong><br><span style="font-size:0.8rem;color:var(--text-secondary);">Moves this one ticket onto a job in the project. The other ${others} stay${others === 1 ? 's' : ''} put. Saved when you save the ticket.</span></span>
          </label>
          <label style="${card}${wholeOff ? 'opacity:0.7;cursor:default;' : ''}">
            <input type="radio" name="rtp-scope" value="job" id="rtp-scope-job" style="margin-top:3px;flex:none;"${wholeOff ? ' disabled' : ''}>
            <span><strong style="color:var(--text-primary);">${escapeHtml(reviewWholeJobText(jobLabel, others))}</strong><br><span style="font-size:0.8rem;color:var(--text-secondary);">${jobUnsaved ? 'Save this ticket first — its job change isn’t saved yet.' : (canDispatch ? 'Moves the job and every ticket on it. <strong>Saves right away</strong> — not on this ticket’s Save button.' : escapeHtml(LINK_NEEDS_DISPATCH_MSG))}</span></span>
          </label>
        </fieldset>` : ''}
        <div id="rtp-ticket-fields">
          <label for="rtp-project" style="${lbl}">Project</label>
          ${choices.length
            ? `<select id="rtp-project" style="width:100%;padding:9px 10px;margin-bottom:10px;font-size:16px;">${projOpts}</select>`
            : `<p id="rtp-no-projects" style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 10px;">No projects for this customer yet${canDispatch ? (hasScope ? ' — start one with the whole-job option above, or in Dispatch → Projects.' : ' — start one in Dispatch → Projects.') : '.'}</p>`}
          <label for="rtp-job" style="${lbl}">Job in that project</label>
          <select id="rtp-job" style="width:100%;padding:9px 10px;margin-bottom:8px;font-size:16px;"${choices.length ? '' : ' disabled'}>${jobOptsFor(defProject)}</select>
          ${!canDispatch && choices.length ? `<p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 8px;">${escapeHtml(NEW_JOB_NEEDS_DISPATCH_MSG)}</p>` : ''}
          <div id="rtp-po-note" style="display:none;font-size:0.8rem;color:#78350F;background:#FEF3C7;border-radius:var(--radius-md);padding:8px 10px;margin:0 0 10px;"></div>
        </div>
        <div id="rtp-job-fields" style="display:none;font-size:0.85rem;color:var(--text-secondary);margin:0 0 10px;">Next you’ll pick the project for the whole job. All ${others + 1} tickets on it move together.</div>
        <div style="display:flex;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-tertiary" data-cancel="1">Cancel</button>
          <button type="button" class="btn btn-primary" id="rtp-go" data-go="1">Move ticket</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    const done = v => { if (_reviewProjectModalResolve === done) _reviewProjectModalResolve = null; el.remove(); resolve(v); };
    _reviewProjectModalResolve = done;
    const scope = () => el.querySelector('input[name="rtp-scope"]:checked')?.value || 'ticket';
    const projSel = () => el.querySelector('#rtp-project');
    const jobSel = () => el.querySelector('#rtp-job');
    const sync = () => {
      const s = scope();
      el.querySelector('#rtp-ticket-fields').style.display = s === 'job' ? 'none' : '';
      el.querySelector('#rtp-job-fields').style.display = s === 'job' ? '' : 'none';
      const go = el.querySelector('#rtp-go');
      if (s === 'job') { go.textContent = 'Choose project & move job now'; go.disabled = false; return; }
      const pid = projSel()?.value || '';
      const jid = jobSel()?.value || '';
      go.textContent = jid === '__new__' ? 'Create job & move ticket' : 'Move ticket';
      go.disabled = !pid || !jid || (!!job && jid === job.id);
      const p = choices.find(x => x.id === pid) || null;
      const tj = jid === '__new__' ? { poNumber: p ? p.po : '' } : ((p && p.jobs.find(j => j.id === jid)) || null);
      const notes = (pid && jid) ? reviewProjectPoNotes(ticket.poNumber, originalJobPo, tj, p ? { poNumber: p.po } : null) : [];
      const note = el.querySelector('#rtp-po-note');
      note.textContent = notes.join(' ');
      note.style.display = notes.length ? '' : 'none';
    };
    projSel()?.addEventListener('change', e => { jobSel().innerHTML = jobOptsFor(e.target.value); sync(); });
    jobSel().addEventListener('change', sync);
    el.querySelectorAll('input[name="rtp-scope"]').forEach(r => r.addEventListener('change', sync));
    sync();
    el.addEventListener('click', e => {
      if (_isStrayMultiClick(e)) return;
      if (e.target === el || e.target.closest('[data-cancel]')) return done(null);
      if (!e.target.closest('[data-go]')) return;
      if (scope() === 'job') return done({ scope: 'job' });
      const pid = projSel()?.value || '';
      const jid = jobSel()?.value || '';
      if (!pid || !jid) return;
      done({ scope: 'ticket', projectId: pid, jobId: jid });
    });
  });
}

// TASK-9355 S5 — "Also move the linked clock entry's hours" box: lazy chunk
// (perf budget), fetched on the first Review header paint so it is there by
// the time the job is changed; helpers passed in positionally (never a
// cross-import). A failed load = no box (today's behaviour).
let _mce = null;
const _mceLoad = () => __ddChunkWrap(import('./billingMoveClock.js?v=1')).then((m) => { m.init([ReviewState, BillingState, escapeHtml, isReviewReadOnly, _crewOnOtherJob, _crewNamesText, _crewHoursText, httpsCallable, functions]); _mce = m; }, () => {});

function updateReviewJob(jobId) {
  // TASK-9608 — the biller is working the pickers; keep them open on re-render
  // (set before the confirm, so a cancelled move doesn't collapse them either).
  ReviewState.headerExpanded = true;
  const oldJobName = ReviewState._originalSnapshot?.jobName || ReviewState.ticket?.jobName || '';
  // TASK-9810 — compare job IDs, not names: two same-named day-jobs ("Tufto
  // nitrogen purge" 8/24 and 8/26) used to skip BOTH the confirm and the
  // TASK-9755 timecard warning because their titles matched.
  const oldJobId = ReviewState._originalSnapshot?.jobId || ReviewState.ticket?.jobId || '';
  if (jobId && oldJobId && reviewJobChangeNeedsConfirm(oldJobId, jobId)) {
    const job = BillingState.jobs.find(j => j.id === jobId);
    const oldJob = BillingState.jobs.find(j => j.id === oldJobId);
    // Dated labels (TASK-4660) so two same-named jobs read apart in the question.
    const newName = job ? _jobPickerLabel(job) : '';
    const oldLabel = oldJob ? _jobPickerLabel(oldJob) : (oldJobName || 'its current job');
    {
      // TASK-9755 (#100852) — the crew's TIMECARDS do not move with the
      // ticket. Say whose hours stay on which job before the biller commits.
      const _offJob = _crewOnOtherJob(ReviewState.ticketClockEntries, jobId);
      const _crewNote = _offJob.length
        ? `\n\n${_crewNamesText(_offJob)} (${_crewHoursText(_offJob)} h) ${_offJob.length === 1 ? 'is' : 'are'} clocked to ${_crewJobsText(_offJob, oldJobName)}. This ticket keeps their hours; their timecards stay on that job.`
        : '';
      if (!confirm(`Move this ticket from "${oldLabel}" to "${newName}"?\n\nThis changes which job this ticket bills under.${_crewNote}`)) {
        // Revert dropdown
        const sel = document.getElementById('review-job-select');
        if (sel) sel.value = ReviewState.ticket.jobId || '';
        return;
      }
    }
  }
  // TASK-9355 S2 — the job change re-derives EVERYTHING job-derived, not just
  // the name: bridged permits/photos, dispatch notes, fastTrax, and the
  // PO/vendor/well/AFE/location fields (inherited follows, hand-typed is kept
  // and listed). Staged into headerEdits like any other header edit, so Save
  // writes it; the modal re-renders NOW so the biller sees what they will save.
  const oldJob = ReviewState.ticket?.jobId ? (BillingState.jobs.find(j => j.id === ReviewState.ticket.jobId) || null) : null;
  const newJob = jobId ? (BillingState.jobs.find(j => j.id === jobId) || null) : null;
  const { updates, summary } = _applyJobChangeToTicket(ReviewState.ticket || {}, oldJob, newJob, { newJobId: jobId || '' });
  // `documents` is owned by the LIVE list below (it also holds the unpersisted
  // bridged copies) and saveReviewTicket writes it from there — never via headerEdits.
  delete updates.documents;
  for (const [k, v] of Object.entries(updates)) {
    ReviewState.headerEdits[k] = v;
    ReviewState.ticket[k] = v;
  }
  if (!summary.unknownJob) {
    const part = _partitionStaleBridgedDocs(ReviewState.documents, newJob || _NO_JOB_FOR_BRIDGE);
    ReviewState.documents = part.keep;
    if (newJob) _bridgeJobDocsInto(ReviewState.documents, newJob, ReviewState.hiddenJobPermitIds);
    // S3 — a PERMIT line priced from the OLD job's parsed permit cost is a
    // billed dollar figure for a permit this ticket no longer carries. Flag it
    // on the row + in the summary; never auto-delete (money).
    summary.permitLinesFlagged = _flagPermitLinesFromOtherJob(ReviewState.permitLines, oldJob, jobId || '');
  }
  // TASK-9755 — name the crew whose timecards stay on another job.
  summary.crewOnOtherJob = _crewOnOtherJob(ReviewState.ticketClockEntries, jobId || null);
  ReviewState.dirty = true;
  renderReviewTicket();
  window.showToast?.(_jobChangeSummaryText(summary), (summary.kept.length || summary.crewOnOtherJob.length || summary.permitLinesFlagged.length) ? 'warning' : 'info');
}

function updateReviewCustomer(customerId) {
  ReviewState.headerExpanded = true; // TASK-9608 — see updateReviewJob
  const oldCustName = ReviewState._originalSnapshot?.customerName || ReviewState.ticket?.customerName || '';
  const customer = BillingState.customers.find(c => c.id === customerId);
  const newCustName = customer?.name || '';
  if (oldCustName && newCustName && oldCustName !== newCustName) {
    if (!confirm(`Move this ticket from "${oldCustName}" to "${newCustName}"?\n\nThis changes which customer gets invoiced.`)) {
      // TASK-9608 — was 'review-customer-select', an id that never existed, so a
      // cancelled confirm left the dropdown on the NEW customer while state kept
      // the old one.
      const sel = document.getElementById('review-customer');
      if (sel) sel.value = ReviewState.ticket.customerId || '';
      return;
    }
  }
  ReviewState.customerId = customerId;
  ReviewState.headerEdits.customerId = customerId;
  ReviewState.headerEdits.customerName = newCustName;
  ReviewState.ticket.customerId = customerId;
  ReviewState.ticket.customerName = newCustName;
  ReviewState.dirty = true;

  // Refresh job dropdown for new customer
  const jobSelect = document.getElementById('review-job-select');
  if (jobSelect) {
    // TASK-4591 — same window as the initial render, or switching customer would
    // silently restore the unbounded list. No force-include: the job selection is
    // cleared below because it belonged to the OLD customer.
    const jobsAll = _reviewJobFullList(customerId);
    const jobsWin = _reviewJobWindowJobs(jobsAll, _reviewJobAnchor(ReviewState.ticket), null);
    // TASK-9609 — through the picker so groups + filter follow the new customer;
    // the old customer's filter text is cleared.
    _setJobPickerJobs(jobSelect, jobsWin, 'No job', { selectedId: '', resetFilter: true });
    // Rebuild the "Show older jobs" link for the NEW customer — it lives outside
    // the <select>, so replacing the options alone would leave a stale count (or
    // no link at all where the new customer has hidden jobs).
    document.getElementById('review-job-show-older')?.remove();
    const older = Math.max(0, jobsAll.length - jobsWin.length);
    if (older > 0) {
      const link = document.createElement('a');
      link.href = '#';
      link.id = 'review-job-show-older';
      link.textContent = `Show older jobs (${older} more)`;
      link.style.cssText = 'display:inline-block;padding:4px 0;font-size:11px;font-weight:600;color:var(--brand-blue);margin-top:3px;text-decoration:underline;';
      link.onclick = (e) => { e.preventDefault(); showOlderReviewJobs(); return false; };
      jobSelect.closest('.review-key-field')?.appendChild(link);
    }
    // Clear current job selection since customer changed
    jobSelect.value = '';
    updateReviewJob('');
  }
  // Re-apply required field warnings for new customer
  setTimeout(applyTicketRequiredFieldWarnings, 50);
}

async function addDayFromReview() {
  const _startTid = BillingState.tenantId; // TASK-10043 — the company this add started in (COD r8)
  const ticket = ReviewState.ticket;
  const jobId = ticket.jobId;
  if (!jobId) {
    window.showToast?.('No job linked to this ticket', 'error');
    return;
  }

  const job = BillingState.jobs.find(j => j.id === jobId);
  const jobName = job?.title || ticket.jobName || 'this job';

  // Show date picker modal
  const dateStr = await showBillingDatePicker('Add Day', `Select a date to add a new day to "${escapeHtml(jobName)}". A blank ticket will be created for you to fill in.`);
  if (!dateStr) return;

  // Check if jobDay already exists for this date
  const exists = BillingState.jobDays.some(jd => jd.jobId === jobId && toDateStr(jd.date) === dateStr);
  if (exists) {
    window.showToast?.('A dispatch day already exists for this date', 'info');
    return;
  }

  // TASK-10043 S1 — this path used to write the jobDay itself (no double-add
  // claim, no 811 stop, no jobAuditLog row, startDate never widened). It now
  // goes through the board's own add-day path, with a reason for work that was
  // not dispatched.
  const add = window.JobOps?.addJobDayFromBilling;
  if (typeof add !== 'function') { window.showToast?.('This page is out of date — reload it and try again.', 'error'); return; }
  const reason = await window.showInputModal('Why isn’t this day on the board?', 'Reason (8+ characters)', '', { multiline: true });
  if (reason == null) return;

  try {
    const newDate = new Date(dateStr + 'T12:00:00');
    const tenantId = BillingState.tenantId;
    const dateLabel = newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    const out = await add({ jobId, dateKey: dateStr, reason: String(reason).trim(), tenantId: _startTid });
    if (!out || !out.ok) { if (!(out && out.cancelled)) window.showToast?.((out && out.error) || 'Error adding day', 'error'); return; }
    if (out.status === 'exists') { window.showToast?.('A dispatch day already exists for this date', 'info'); return; }
    if (BillingState.tenantId !== _startTid) return; // company switched mid-save (COD r8) — never continue into the other company
    _noteBoardWork(jobId, null, out.jobDayId, out.jobDayData);
    if (out.warning) window.showToast?.(out.warning, 'warning', null, 9000);

    // Create a blank draft ticket for the new day
    const newTicketData = {
      jobId,
      jobName: ticket.jobName || jobName,
      jobTitle: ticket.jobTitle || jobName,
      customerId: ticket.customerId || '',
      customer: ticket.customer || '',
      date: Timestamp.fromDate(new Date(dateStr + 'T00:00:00')),
      workDate: dateStr,
      status: 'draft',
      grandTotal: 0,
      totalHours: 0,
      source: 'add_day',
      originalJobId: jobId || null,
      originalCustomerId: ticket.customerId || null,
      originalWorkDate: dateStr,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    // Copy over job detail fields if they exist
    if (ticket.poNumber) newTicketData.poNumber = ticket.poNumber;
    if (ticket.vendorNumber) newTicketData.vendorNumber = ticket.vendorNumber;
    if (ticket.location) newTicketData.location = ticket.location;
    if (ticket.wellName) newTicketData.wellName = ticket.wellName;
    if (ticket.wellNumber) newTicketData.wellNumber = ticket.wellNumber;
    if (ticket.afeNumber) newTicketData.afeNumber = ticket.afeNumber;

    // TASK-1339 — this "Add Day" draft was another create path with no number
    // (1 such ticket live on TITS). Same allocator as every other path.
    newTicketData.ticketNo = await getNextTicketNumber(tenantId);

    const ticketRef = await addDoc(collection(db, 'tenants', tenantId, 'tickets'), newTicketData);

    window.showToast?.(`Day added: ${dateLabel} — blank ticket created`, 'success');

    // Close current review and open the new ticket
    closeReviewModal();
    // Small delay to let state settle, then open the new ticket
    setTimeout(() => openReviewTicket(ticketRef.id), 300);

  } catch (err) {
    console.error('[Billing] Add day from review error:', err);
    window.showToast?.('Error adding day', 'error');
  }
}

function showBillingDatePicker(title, message) {
  return new Promise((resolve) => {
    const modalHtml = `
      <div id="billing-date-picker-modal" class="modal" style="z-index:10001;">
        <div class="modal-content" style="max-width:380px;text-align:center;padding:var(--spacing-xl);">
          <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
            <span class="material-icons" style="font-size:40px;color:var(--brand-blue);">event</span>
            <h3 style="font-size:1.15rem;font-weight:700;margin:0;">${title}</h3>
          </div>
          <p style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);line-height:1.5;font-size:0.9rem;">${message}</p>
          <input type="date" id="billing-date-picker-input" class="form-control" style="font-size:1rem;padding:12px;margin-bottom:var(--spacing-lg);">
          <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
            <button type="button" class="btn btn-secondary" id="billing-date-picker-cancel" style="flex:1;max-width:140px;">Cancel</button>
            <button type="button" class="btn btn-primary" id="billing-date-picker-confirm" style="flex:1;max-width:140px;">Add Day</button>
          </div>
        </div>
      </div>
    `;
    const container = document.createElement('div');
    container.innerHTML = modalHtml;
    document.body.appendChild(container.firstElementChild);

    const modal = document.getElementById('billing-date-picker-modal');
    const input = document.getElementById('billing-date-picker-input');
    const cancelBtn = document.getElementById('billing-date-picker-cancel');
    const confirmBtn = document.getElementById('billing-date-picker-confirm');

    // Default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    input.value = formatDateISO(tomorrow);

    const cleanup = () => modal.remove();

    cancelBtn.onclick = () => { cleanup(); resolve(null); };
    confirmBtn.onclick = () => { const v = input.value; cleanup(); resolve(v || null); };
    modal.onclick = (e) => { if (e.target === modal) { cleanup(); resolve(null); } };
  });
}

function onReviewEmployeeSelect(localId, personId) {
  const line = ReviewState.employeeLines.find(l => l._localId === localId);
  if (!line) return;

  const person = BillingState.people.find(p => p.id === personId);
  if (!person) return;

  line.personId = personId;
  line.personName = person.name;

  // Determine role: CDL if person has cdlLicensed flag or role tags, else NON_CDL
  const isCdl = person.cdlLicensed === true ||
    (person.roles || []).some(r => /cdl|foreman|operator|driver/i.test(r));
  line.role = isCdl ? 'CDL' : 'NON_CDL';

  // Lookup rate
  const _empLookup = lookupReviewRate(ReviewState.ticket.customerId, 'EMPLOYEE', line.role);
  line.rate = _empLookup.rate;
  line.rateUnit = _empLookup.rateUnit;
  line.includeOnInvoice = _empLookup.includeOnInvoice;
  const _empPR = !isTimeBasedUnit(_empLookup.rateUnit);
  if (_empPR) line.quantity = line.quantity || 0;
  line.lineAmount = _empPR
    ? parseFloat(((line.quantity || 0) * _empLookup.rate).toFixed(2))
    : parseFloat(((line.hours || 0) * _empLookup.rate).toFixed(2));
  ReviewState.dirty = true;

  // Re-render the employee section to reflect changes
  const section = document.getElementById('review-emp-section');
  if (section) section.outerHTML = renderReviewEmployeeSection();
  renderReviewTotals();
}

function moveReviewEquipmentLine(localId, direction) {
  const lines = ReviewState.equipmentLines;
  const idx = lines.findIndex(l => l._localId === localId);
  if (idx < 0) return;
  const newIdx = idx + direction;
  if (newIdx < 0 || newIdx >= lines.length) return;
  // Swap
  [lines[idx], lines[newIdx]] = [lines[newIdx], lines[idx]];
  ReviewState.dirty = true;
  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
}

function onReviewEquipmentSelect(localId, equipmentKey) {
  const line = ReviewState.equipmentLines.find(l => l._localId === localId);
  if (!line || !equipmentKey) return;

  const [type, id] = equipmentKey.split('_');
  let asset = null;
  let subtype = 'asset';

  if (type === 'truck') {
    asset = ReviewState.trucks.find(t => t.id === id);
    subtype = 'truck';
    line.assetType = 'truck';
  } else if (type === 'trailer') {
    asset = ReviewState.trailers.find(t => t.id === id);
    subtype = 'trailer';
    line.assetType = 'trailer';
  } else {
    asset = ReviewState.assets.find(a => a.id === id);
    subtype = 'asset';
    line.assetType = 'asset';
  }

  if (!asset) return;

  line.assetId = id;
  line.assetName = asset.unit || asset.name || asset.partNumber || id;
  line.fleetNumber = asset.unit || asset.fleetNumber || '';
  line.vehicleType = asset.type || '';

  // Lookup rate (with type-aware matching)
  const _eqLookup = lookupReviewRate(ReviewState.ticket.customerId, 'EQUIPMENT', subtype, line.assetName, line.vehicleType);
  line.rate = _eqLookup.rate;
  line.rateUnit = _eqLookup.rateUnit;
  line.includeOnInvoice = _eqLookup.includeOnInvoice;
  const _isPerUnit = !isTimeBasedUnit(line.rateUnit);
  // TASK-1646 slice 4 — if the resolved price-book unit is per-unit, MIRRORED
  // times are meaningless: clear them so unused times never persist and the row
  // renders "—" like every other per-unit line. Gated on _timesMirroredFrom so
  // a biller's hand-entered times are never wiped by an asset pick.
  if (_isPerUnit && line._timesMirroredFrom) {
    line.fromTime = null; line.toTime = null; line.hours = 0;
    delete line._timesMirroredFrom;
  }
  line.lineAmount = _isPerUnit
    ? parseFloat(((line.quantity || 0) * _eqLookup.rate).toFixed(2))
    : parseFloat(((line.hours || 0) * _eqLookup.rate).toFixed(2));
  ReviewState.dirty = true;

  // Re-render equipment section
  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  renderReviewTotals();
}

function onReviewItemCodeChange(lineType, localId, itemCodeId) {
  const lists = {
    EMPLOYEE: ReviewState.employeeLines,
    EQUIPMENT: ReviewState.equipmentLines
  };
  const line = (lists[lineType] || []).find(l => l._localId === localId);
  if (!line) return;

  line.itemCodeId = itemCodeId;
  ReviewState.dirty = true;

  // If rate is 0 or default rate, try auto-filling from item code
  const ic = ReviewState.itemCodes.find(c => c.id === itemCodeId);
  if (ic?.defaultRate > 0) {
    const rates = getDefaultRates();
    const isDefaultRate = !line.rate || line.rate === 0 ||
      (lineType === 'EMPLOYEE' && (line.rate === rates.cdl || line.rate === rates.nonCdl)) ||
      (lineType === 'EQUIPMENT' && (line.rate === rates.truck || line.rate === rates.trailer || line.rate === rates.asset));
    if (isDefaultRate) {
      line.rate = ic.defaultRate;
      if (ic.defaultRateUnit) line.rateUnit = ic.defaultRateUnit;
      const _icPerUnit = !isTimeBasedUnit(line.rateUnit);
      line.lineAmount = _icPerUnit
        ? parseFloat(((line.quantity || 0) * line.rate).toFixed(2))
        : parseFloat(((line.hours || 0) * line.rate).toFixed(2));
      // Re-render the section to show updated rate
      if (lineType === 'EMPLOYEE') {
        const section = document.getElementById('review-emp-section');
        if (section) section.outerHTML = renderReviewEmployeeSection();
      } else {
        const section = document.getElementById('review-equip-section');
        if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
      }
      renderReviewTotals();
    }
  }
}

function calcReviewSubtotal() {
  const empSubtotal = ReviewState.employeeLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const equipSubtotal = ReviewState.equipmentLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const permitSubtotal = ReviewState.permitLines.reduce((s, l) => s + _reviewPermitBillable(l), 0); // TASK-9436
  const fieldItemSubtotal = ReviewState.fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const miscSubtotal = ReviewState.miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const downtimeSubtotal = ReviewState.downtimeLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const standbySubtotal = ReviewState.standbyLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const fuelSubtotal = _reviewFuelSurchargeView()?.amount || 0; // TASK-9440 (0 when the ticket has no surcharge line)
  return empSubtotal + equipSubtotal + permitSubtotal + fieldItemSubtotal + miscSubtotal - downtimeSubtotal + standbySubtotal + fuelSubtotal;
}

function calcReviewTax() {
  if (!ReviewState.taxEnabled || !ReviewState.taxRate) return { taxableSubtotal: 0, taxAmount: 0 };
  const cats = ReviewState.taxCategories || {};
  let taxable = 0;
  if (cats.labor !== false) taxable += ReviewState.employeeLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  if (cats.equipment !== false) taxable += ReviewState.equipmentLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  if (cats.permits !== false) taxable += ReviewState.permitLines.reduce((s, l) => s + _reviewPermitBillable(l), 0); // TASK-9436
  if (cats.fieldItems !== false) taxable += ReviewState.fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  if (cats.misc !== false) taxable += ReviewState.miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  if (cats.downtime !== false) taxable -= ReviewState.downtimeLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  if (cats.standby !== false) taxable += ReviewState.standbyLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  taxable += _reviewFuelSurchargeView()?.taxableAmount || 0; // TASK-9440 — only when the surcharge is stamped taxable
  const taxAmount = parseFloat((taxable * ReviewState.taxRate / 100).toFixed(2));
  return { taxableSubtotal: taxable, taxAmount };
}

function calcReviewGrandTotal() {
  return calcReviewSubtotal() + calcReviewTax().taxAmount;
}

// TASK-405: category-aware ticket totals computed from LINE DOCS — a faithful mirror
// of the in-memory authoritative math (calcReviewSubtotal + calcReviewTax) so the
// merge / combine / consolidate / split paths recompute grandTotal WITH tax instead
// of tax-blind (which silently undercharged tax-enabled tickets — sibling of CRIT-2).
// `ticket` supplies taxEnabled / taxRate / taxCategories (persisted on the ticket doc).
function _ticketLineBillable(ld) {
  // Permits carry an optional upcharge; mirror the ReviewState load mapping
  // (_baseCost = baseCost || lineAmount, then base + base*upcharge%).
  if (ld.type === 'PERMIT') {
    // Exact truthy fallback the ReviewState loader uses (baseCost || lineAmount),
    // so a stored baseCost:0 falls back to lineAmount identically (COD).
    // TASK-9436 — whole cents via the shared formula, so this equals the stored
    // lineAmount the invoice sums (pct 0 → base, unchanged).
    const base = ld.baseCost || ld.lineAmount || 0;
    return _permitMarkedUpAmount(base, ld.upchargePercent || 0);
  }
  return ld.lineAmount || 0;
}

// ═══ TASK-9436 — permit markup %: Price Book per customer → company default → 0. ═══
// Server twins (byte-identical bodies, no `_` prefix) live in
// functions/src/services/permitMarkup.js; tests/task9436-permit-markup.unit.cjs
// fails if they diverge. A marked-up PERMIT line stores baseCost = raw fee,
// lineAmount = _permitMarkedUpAmount(baseCost, pct), rate = customer-facing unit
// price, baseRate = raw unit fee (see the server header for why).
// No hardcoded percentage anywhere: an unset Price Book + unset default = 0.
function _normalizePermitMarkupPercent(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100) / 100;
}

function _permitMarkedUpAmount(base, pct) {
  const b = Number(base) || 0;
  const p = Number(pct) || 0;
  if (!p) return b;
  const cents = Math.round(b * 100);
  return Math.round(cents * (100 + p) / 100) / 100;
}

function _resolvePermitMarkupPercent(lineItems, settings, lineItemId) {
  const items = (Array.isArray(lineItems) ? lineItems : [])
    .filter((li) => li && li.active !== false && li.appliesTo && li.appliesTo.kind === "permit");
  if (lineItemId) {
    const picked = items.find((li) => li.id === lineItemId);
    if (picked) {
      const pp = _normalizePermitMarkupPercent(picked.markupPercent);
      if (pp !== null) return { percent: pp, source: "price_book", lineItemId: picked.id };
      const pd = _normalizePermitMarkupPercent(settings && settings.defaultPermitMarkupPercent);
      return { percent: pd === null ? 0 : pd, source: pd === null ? "none" : "company_default", lineItemId: null };
    }
  }
  const ordered = items.slice().sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
  for (const li of ordered) {
    const p = _normalizePermitMarkupPercent(li.markupPercent);
    if (p !== null) return { percent: p, source: "price_book", lineItemId: li.id || null };
  }
  const d = _normalizePermitMarkupPercent(settings && settings.defaultPermitMarkupPercent);
  if (d !== null) return { percent: d, source: "company_default", lineItemId: null };
  return { percent: 0, source: "none", lineItemId: null };
}

// Review row math: quantity × baseRate = baseCost (raw), then the markup.
// Writes both the persisted fields and the in-memory _baseCost/_upchargePercent
// the Review totals read, so the row, the totals and the save can't disagree.
function _applyPermitLineMath(line) {
  const q = Number(line.quantity) || 0;
  const br = Number(line.baseRate) || 0;
  const pct = _normalizePermitMarkupPercent(line.upchargePercent) || 0;
  const base = Math.round(q * br * 100) / 100;
  const amount = _permitMarkedUpAmount(base, pct);
  line.upchargePercent = pct;
  line.baseCost = base;
  line.lineAmount = amount;
  line.rate = pct && q > 0 ? Math.round((amount / q) * 100) / 100 : br;
  line._baseCost = base;
  line._upchargePercent = pct;
  return line;
}

// Price Book permit markup audit payload, or null when nothing changed. A new
// item that sets no markup is not a change (it inherits the company default).
function _permitMarkupPriceBookAudit(before, after, meta) {
  const from = _normalizePermitMarkupPercent(before);
  const to = _normalizePermitMarkupPercent(after);
  if (from === to) return null;
  const m = meta || {};
  return {
    entityType: 'priceBookLineItem',
    entityId: m.lineItemId || null,
    source: 'price-book-line-item-form',
    customerId: m.customerId || null,
    contractId: m.contractId || null,
    description: m.description || '',
    isNew: !!m.isNew,
    before: { markupPercent: from },
    after: { markupPercent: to },
  };
}

function _reviewPermitBillable(l) {
  return _permitMarkedUpAmount(l._baseCost || 0, l._upchargePercent || 0);
}

// Audit input for the ticket save: every permit line whose markup differs from
// what was loaded (new lines count from null, and only when they carry one).
function _permitMarkupAuditChanges(permitLines) {
  const out = [];
  for (const l of (permitLines || [])) {
    if (!l) continue;
    const to = _normalizePermitMarkupPercent(l.upchargePercent) || 0;
    const from = l._isNew ? null : (l._loadedUpchargePercent === undefined ? null : l._loadedUpchargePercent);
    const fromN = from === null ? 0 : from;
    if (l._isNew ? to === 0 : fromN === to) continue;
    out.push({
      lineId: l.id || null,
      description: l.description || 'Load permit',
      baseCost: Number(l.baseCost) || Number(l._baseCost) || 0,
      from,
      to,
      lineAmount: Number(l.lineAmount) || 0,
    });
  }
  return out;
}
function _ticketLineTaxCategory(type) {
  switch (type) {
    case 'EQUIPMENT': return 'equipment';
    case 'PERMIT': return 'permits';
    case 'DOWNTIME': return 'downtime';   // subtracted, like ReviewState
    case 'STANDBY': return 'standby';
    case 'FIELD_ITEM': return 'fieldItems';
    case 'MISC': return 'misc';
    case 'POTHOLE': return null;           // informational (lineAmount 0); excluded, matching calcReviewSubtotal
    case 'FUEL_SURCHARGE': return 'fuelSurcharge'; // TASK-9440 — its own category; taxed only by its own flag
    case 'EMPLOYEE':
    default: return 'labor';               // unknown types fall back to employee/labor (matches the loader's default)
  }
}
function computeTicketTotalsFromLines(lineDocs, ticket) {
  let subtotal = 0;
  let taxable = 0;
  const taxOn = !!(ticket && ticket.taxEnabled && ticket.taxRate);
  const cats = (ticket && ticket.taxCategories) || {};
  for (const ld of (lineDocs || [])) {
    const cat = _ticketLineTaxCategory(ld.type);
    if (cat === null) continue;            // pothole / excluded
    const signed = (cat === 'downtime' ? -1 : 1) * _ticketLineBillable(ld);
    subtotal += signed;
    // TASK-9440 — the fuel surcharge is taxed by its own stamped flag, never by
    // ticket.taxCategories (server twin: ticketGateway.computeTicketTotals).
    if (taxOn && (cat === 'fuelSurcharge' ? ld.fuelSurchargeTaxable === true : cats[cat] !== false)) taxable += signed;
  }
  const taxAmount = taxOn ? parseFloat((taxable * ticket.taxRate / 100).toFixed(2)) : 0;
  return {
    subtotal,
    taxableSubtotal: taxOn ? parseFloat(taxable.toFixed(2)) : 0, // match saveReviewTicket's rounded persist
    taxAmount,
    grandTotal: parseFloat((subtotal + taxAmount).toFixed(2)),
  };
}

// ═══ TASK-9440 — FUEL SURCHARGE (slice 1: flat %, OFF by default). ═══
// ONE calculation: window.FuelSurcharge is GENERATED (scripts/build-shared.cjs)
// from functions/src/shared/fuelSurcharge.js, the file the server line pass
// runs — read that file's header for the design. Read at CALL time, never a
// cross-`?v=` import (Pitfall #1). The SERVER owns the line (onTicketLineWrite +
// setFuelSurchargeSettings); this file only re-derives its AMOUNT from the lines
// on screen at the STAMPED % so the Review total and the saved header match what
// the server will compute. The client never resolves a % itself.
// If the shared module did not load, every helper fails SAFE: the ticket reads
// as closed, so Review shows exactly the stored line and re-derives nothing.
function _fuelShared() { return window.FuelSurcharge || null; }
function _normalizeFuelSurchargePercent(v) {
  const F = _fuelShared();
  return F ? F.normalizeFuelSurchargePercent(v) : null;
}
function _fuelSurchargeTicketState(t) {
  const F = _fuelShared();
  return F ? F.fuelSurchargeTicketState(t) : 'closed';
}
function _planFuelSurcharge(ticket, lines, resolved, categoryFn, billableFn, stamp) {
  const F = _fuelShared();
  if (!F) return { state: 'closed', changed: false, set: null, deleteIds: [], before: null, after: null };
  return F.planFuelSurcharge(ticket, lines, resolved, categoryFn, billableFn, stamp);
}

// TASK-9440 — every Review bucket that is a real ticket line (the fuel lines
// themselves excluded; _planFuelSurcharge skips them from its own base anyway).
function _reviewFuelBaseLines() {
  return [].concat(ReviewState.employeeLines, ReviewState.equipmentLines, ReviewState.permitLines,
    ReviewState.downtimeLines, ReviewState.standbyLines, ReviewState.potholeLines,
    ReviewState.fieldItemLines, ReviewState.miscLines);
}
// Review billable: identical to calcReviewSubtotal's per-line math (a PERMIT row
// reads its live _baseCost/_upchargePercent), which is what the server's
// ticketLineBillable computes from the same fields once they are saved.
function _reviewFuelBillable(l) {
  return l && l.type === 'PERMIT' ? _reviewPermitBillable(l) : ((l && l.lineAmount) || 0);
}
// The surcharge as it will stand after this Review saves: the STAMPED % on the
// kept line, the amount re-derived from the lines on screen (frozen semantics —
// the client never resolves a new %; the server does that for open drafts).
// Read-only / closed tickets show exactly what is stored. null = no line.
function _reviewFuelSurchargeView() {
  const fuel = ReviewState.fuelSurchargeLines || [];
  if (!fuel.length) return null;
  const stored = () => {
    const amount = fuel.reduce((s, l) => s + (Number(l.lineAmount) || 0), 0);
    const taxableAmount = fuel.reduce((s, l) => s + (l.fuelSurchargeTaxable === true ? (Number(l.lineAmount) || 0) : 0), 0);
    return { amount, taxableAmount, description: fuel.map(l => l.description || 'Fuel Surcharge').join('; '), set: null, readOnly: true };
  };
  const t = ReviewState.ticket || {};
  // closed (rules-locked) and settled (accepted / written off / sent…) tickets
  // show exactly what is stored; the client never re-derives them.
  if (ReviewState._fuelRefreshFailed || isReviewReadOnly(t) || ['closed', 'settled'].includes(_fuelSurchargeTicketState(t))) return stored();
  const plan = _planFuelSurcharge({ status: 'ready' }, _reviewFuelBaseLines().concat(fuel), null, _ticketLineTaxCategory, _reviewFuelBillable);
  if (!plan.after) return stored();
  const keepId = plan.set ? plan.set.id : (fuel.find(l => !plan.deleteIds.includes(l.id)) || fuel[0]).id;
  const kept = fuel.find(l => l.id === keepId) || fuel[0];
  const data = plan.set ? plan.set.data : kept;
  return {
    amount: plan.after.lineAmount,
    taxableAmount: data.fuelSurchargeTaxable === true ? plan.after.lineAmount : 0,
    description: data.description || 'Fuel Surcharge',
    set: plan.set,
    readOnly: false,
  };
}
function renderReviewFuelSurchargeSection() {
  const v = _reviewFuelSurchargeView();
  if (!v) return '';
  return `
    <div class="review-section" id="review-fuel-section">
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:18px;">local_gas_station</span> Fuel Surcharge
          <span class="review-subtotal" id="review-fuel-amount">$${v.amount.toFixed(2)}</span>
        </span>
      </div>
      <div style="padding:6px 10px;font-size:0.85rem;color:var(--text-primary);" id="review-fuel-desc">${escapeHtml(v.description)}</div>
      <div style="padding:0 10px 8px;font-size:0.75rem;color:var(--text-secondary);">
        ${v.readOnly ? 'Billed as shown.' : 'Follows this ticket&#39;s charges. Set in AR/AP &rarr; Settings &rarr; Fuel Surcharge; the % locks once the ticket is marked ready or sent.'}
      </div>
    </div>`;
}

// Post-render: set number input values via JS (HTML value attr on type=number is unreliable after innerHTML)
function hydrateEquipmentHours() {
  document.querySelectorAll('[data-init-value]').forEach(input => {
    const val = input.dataset.initValue;
    if (val) input.value = val;
  });
}

function renderReviewTotals() {
  const empSubtotal = ReviewState.employeeLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const equipSubtotal = ReviewState.equipmentLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const permitSubtotal = ReviewState.permitLines.reduce((s, l) => s + _reviewPermitBillable(l), 0); // TASK-9436
  const fieldItemSubtotal = ReviewState.fieldItemLines.reduce((s, l) => s + (l.lineAmount || 0), 0);

  const el1 = document.getElementById('review-emp-subtotal');
  if (el1) el1.textContent = '$' + empSubtotal.toFixed(2);

  const el2 = document.getElementById('review-equip-subtotal');
  if (el2) el2.textContent = '$' + equipSubtotal.toFixed(2);

  const el3 = document.getElementById('review-permit-subtotal');
  if (el3) el3.textContent = '$' + permitSubtotal.toFixed(2);

  const el5 = document.getElementById('review-field-item-subtotal');
  if (el5) el5.textContent = '$' + fieldItemSubtotal.toFixed(2);

  const miscSubtotal = ReviewState.miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const el6 = document.getElementById('review-misc-subtotal');
  if (el6) el6.textContent = '$' + miscSubtotal.toFixed(2);

  // TASK-9440 — the surcharge follows the lines on screen at its stamped %.
  const _fuelV = _reviewFuelSurchargeView();
  const elFuel = document.getElementById('review-fuel-amount');
  if (elFuel && _fuelV) elFuel.textContent = '$' + _fuelV.amount.toFixed(2);
  const elFuelDesc = document.getElementById('review-fuel-desc');
  if (elFuelDesc && _fuelV) elFuelDesc.textContent = _fuelV.description;

  // Tax-aware totals
  const subtotal = calcReviewSubtotal();
  const taxResult = calcReviewTax();
  const grandTotal = subtotal + taxResult.taxAmount;

  const elSub = document.getElementById('review-subtotal-amount');
  if (elSub) elSub.textContent = '$' + subtotal.toFixed(2);

  const elTax = document.getElementById('review-tax-amount');
  if (elTax) elTax.textContent = '$' + taxResult.taxAmount.toFixed(2);

  const elTaxLabel = document.getElementById('review-taxable-label');
  if (elTaxLabel) elTaxLabel.textContent = 'on $' + taxResult.taxableSubtotal.toFixed(2);

  const el4 = document.getElementById('review-grand-total-amount');
  if (el4) el4.textContent = '$' + grandTotal.toFixed(2);

  // TASK-1166 — keep the sticky banner total in lock-step with the bottom bar.
  const elBanner = document.getElementById('review-banner-total');
  if (elBanner) elBanner.textContent = '$' + grandTotal.toFixed(2);
}

function toggleReviewTax(checked) {
  ReviewState.taxEnabled = checked;
  if (checked && !ReviewState.taxRate) {
    const settings = window.AppState?.settings || {};
    ReviewState.taxRate = settings.defaultTaxRate || 0;
    ReviewState.taxCategories = settings.defaultTaxCategories || { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true };
  }
  ReviewState.dirty = true;
  renderReviewTicket();
}

function updateReviewTaxRate(rate) {
  ReviewState.taxRate = rate;
  ReviewState.dirty = true;
  renderReviewTotals();
}

function toggleReviewTaxCategory(cat, checked) {
  ReviewState.taxCategories[cat] = checked;
  ReviewState.dirty = true;
  renderReviewTotals();
}

// ============================================
// REVIEW TICKET — ADD / REMOVE
// ============================================
function addReviewEmployeeRow() {
  // TASK-1646 — match every sibling mutator: the button is hidden on read-only
  // tickets, but the function itself had no guard.
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  const newLine = {
    _localId: nextLocalId(),
    _isNew: true,
    type: 'EMPLOYEE',
    personId: '',
    personName: '',
    role: 'NON_CDL',
    hours: 0,
    rate: 0,
    lineAmount: 0,
    fromTime: null,
    toTime: null,
    itemCodeId: ''
  };
  ReviewState.employeeLines.push(newLine);
  ReviewState.dirty = true;

  const section = document.getElementById('review-emp-section');
  if (section) section.outerHTML = renderReviewEmployeeSection();
  renderReviewTotals();
}

function addReviewEquipmentRow() {
  if (isReviewReadOnly(ReviewState.ticket || {})) return; // TASK-1646 — match sibling mutators
  const newLine = {
    _localId: nextLocalId(),
    _isNew: true,
    type: 'EQUIPMENT',
    assetId: '',
    assetName: '',
    assetType: '',
    fleetNumber: '',
    vehicleType: '',
    hours: 0,
    rate: 0,
    lineAmount: 0,
    fromTime: null,
    toTime: null,
    itemCodeId: ''
  };
  // TASK-1646 (Chris) — the ticket is already date-oriented, so a new equipment
  // row MIRRORS the In/Out of the last TIMED equipment line instead of opening
  // two empty pickers ("9/10 the user wouldn't need to change anything").
  // Walk BACKWARDS past per-unit lines (they legitimately carry no times —
  // strict previous-row would silently no-op and feel random). Hours + amount
  // derive immediately via _recalcLineFromTimes — deliberately NOT left at 0:
  // the load-time self-heal would derive them silently on the next open anyway,
  // so deriving at click time is the honest version (COD Domain, adjudicated).
  // Provenance rides on _timesMirroredFrom (underscore = never persisted) and
  // renders as a "mirrored" pill until the first hand-edit.
  for (let i = ReviewState.equipmentLines.length - 1; i >= 0; i--) {
    const src = ReviewState.equipmentLines[i];
    if (src.fromTime && src.toTime && isTimeBasedUnit(src.rateUnit || 'Hourly')) {
      newLine.fromTime = src.fromTime;
      newLine.toTime = src.toTime;
      newLine._timesMirroredFrom = getEquipmentDisplayName(src) || src.assetName || 'previous line';
      _recalcLineFromTimes(newLine);
      break;
    }
  }
  ReviewState.equipmentLines.push(newLine);
  ReviewState.dirty = true;

  const section = document.getElementById('review-equip-section');
  if (section) { section.outerHTML = renderReviewEquipmentSection(); hydrateEquipmentHours(); }
  renderReviewTotals();
}

// ============================================
// TASK-9755 — crew clocked to a ticket but missing from it (#100852)
// ============================================
// TITS #100852 (2026-10-08): both EMPLOYEE lines were removed in Review while the
// biller was editing the truck's shop-to-shop hours, and nothing said so — the
// two clock entries still named the ticket (ticketId) but the ticket showed
// "No crew yet", 19.86 h gone from the ticket and its invoice labor detail. The
// clock is the truth for who worked; the ticket must never silently hold fewer
// crew than the clock puts on it.

// Pure. A clock entry that is gone, voided or still open is never "missing" —
// an open shift's line is written at clock-in and finishes at clock-out.
function _crewEntryCountable(e) {
  if (!e || !e.id || !e.personId) return false;
  if (e.deleted === true || e.voided === true || e.status === 'deleted' || e.status === 'voided') return false;
  if (!e.clockOut) return false;
  return true;
}

// Pure. Hours on a completed clock entry, the canonical pair first.
function _crewEntryHours(e) {
  const h = Number(e && (e.totalHours != null ? e.totalHours : e.hours));
  return Number.isFinite(h) && h > 0 ? Math.round(h * 100) / 100 : 0;
}

// Pure. Which completed clock entries point at this ticket but have no EMPLOYEE
// line carrying their clockEntryId. A LEGACY line (no clockEntryId) for the
// same person covers that person's entries — old tickets predate the link and
// must not light up. `excludedIds` = entries a biller deliberately took off
// this ticket (ticket.crewRemovedEntryIds), so a decision never nags.
function _missingCrewFromEntries(entries, employeeLines, excludedIds) {
  const lines = (employeeLines || []).filter(l => l && (!l.type || l.type === 'EMPLOYEE'));
  const linked = new Set(lines.map(l => l.clockEntryId).filter(Boolean).map(String));
  const legacyPeople = new Set(lines.filter(l => !l.clockEntryId && l.personId).map(l => String(l.personId)));
  const excluded = new Set((excludedIds || []).map(String));
  const out = [];
  for (const e of (entries || [])) {
    if (!_crewEntryCountable(e)) continue;
    const id = String(e.id);
    if (linked.has(id) || excluded.has(id)) continue;
    if (legacyPeople.has(String(e.personId))) continue;
    out.push({ entryId: id, personId: String(e.personId), personName: e.personName || 'Unknown', hours: _crewEntryHours(e), jobId: e.jobId || null, jobTitle: e.jobTitle || '' });
  }
  // Stable, readable order — Firestore query order is not.
  return out.sort((a, b) => String(a.personName).localeCompare(String(b.personName)) || a.entryId.localeCompare(b.entryId));
}

// Pure. Crew whose clock entries on this ticket sit on a DIFFERENT job than
// `newJobId` — what a job change leaves behind on the timecards.
function _crewOnOtherJob(entries, newJobId) {
  const target = newJobId || null;
  const out = [];
  for (const e of (entries || [])) {
    // Open shifts count here too: a job change leaves them on the old job as well.
    if (!e || !e.id || !e.personId) continue;
    if (e.deleted === true || e.voided === true || e.status === 'deleted' || e.status === 'voided') continue;
    const j = e.jobId || null;
    if (!j || j === target) continue;
    out.push({ entryId: String(e.id), personName: e.personName || 'Unknown', hours: _crewEntryHours(e), jobId: j, jobTitle: e.jobTitle || '' });
  }
  return out.sort((a, b) => String(a.personName).localeCompare(String(b.personName)) || a.entryId.localeCompare(b.entryId));
}

// Pure. "Steven Alandt and Kayden Daniels" / "A, B and 2 others".
function _crewNamesText(list) {
  const names = [...new Set((list || []).map(c => c.personName || 'Unknown'))];
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} others`;
}

// Pure. Total hours, 2 decimals, as a string ("19.86").
// Pure. Every job the crew is clocked to, quoted: "A" / "A" and "B" / "A", "B", "C".
function _crewJobsText(list, fallback) {
  const titles = [...new Set((list || []).map(c => c.jobTitle || fallback || 'the old job'))];
  return titles.map(t => `"${t}"`).join(titles.length === 2 ? ' and ' : ', ');
}

function _crewHoursText(list) {
  const t = (list || []).reduce((s, c) => s + (Number(c.hours) || 0), 0);
  return (Math.round(t * 100) / 100).toFixed(2);
}

// Pure. The EMPLOYEE line a restored clock entry becomes. Same shape the clock
// writers mint (rate 0 / lineAmount 0 — TASK-4538: people are never billed on
// tickets); `_isNew` so Save addDoc's it, `_restoredFromClock` so it renders as
// a normal crew row (not the blank "Select employee" row of a hand-added line).
function _employeeLineFromClockEntry(e) {
  return {
    type: 'EMPLOYEE',
    clockEntryId: String(e.id),
    personId: e.personId,
    personName: e.personName || '',
    role: e.role || '',
    fromTime: e.clockIn || null,
    toTime: e.clockOut || null, // the window totalHours was computed from (COD Domain r2)
    hours: _crewEntryHours(e),
    rate: 0,
    rateUnit: 'Hourly',
    lineAmount: 0,
    revenueCategory: e.revenueCategory || '',
    notes: null,
    _isNew: true,
    _restoredFromClock: true,
  };
}

// Pure. Is this clock entry's ticketId pointer one the office may trust? A
// clock-only member or a driver-PIN session can write its OWN entry's ticketId to
// ANY ticket in the tenant (firestore.rules clockEntries self arm leaves ticketId
// unfrozen so the PIN close-out can stamp it after completing the entry —
// tests/rules/clockentry-ticket-pin.test.mjs). So the pointer alone proves
// nothing. Trust it only when the entry is bound to the ticket the SAME way the
// server backfill (employeeLineBackfill.js entryBoundToTicket) and the driver
// line rule (driverOwnsTicketLine) do: same jobId OR same customerId,
// undefined == null. Deliberately STRICTER than the server copy (COD Code
// Reviewer r1+r2): each arm needs a real value (undefined / null / '' are
// "none"). There, null == null lets a no-job entry match a no-job ticket of
// ANOTHER customer, or a no-customer entry match any no-customer ticket; here
// neither binds. The banner only OFFERS a line, so stricter costs nothing but
// a manual Add Employee in those corners. jobId/customerId are frozen on a
// completed entry, so a forged pointer onto another customer's ticket never
// reaches the banner, the ruled-off list, Add back or the job-change crew note.
// (An ACTIVE entry can still be rebound before clock-out — the same trust
// driverOwnsTicketLine grants; tests/rules/clockentry-ticket-pin.test.mjs.)
function _crewEntryBoundToTicket(e, ticket) {
  if (!e || !ticket) return false;
  const n = (v) => (v === undefined || v === '' ? null : v);
  const job = n(e.jobId);
  const cust = n(e.customerId);
  return (job !== null && job === n(ticket.jobId)) || (cust !== null && cust === n(ticket.customerId));
}

// Loads the clock entries that point at this ticket. Best-effort: a failed read
// leaves `null` (unknown), which renders nothing — never a false "missing".
// `ticket` = the ticket as Review opened it (before any in-session job change),
// the snapshot the clock entries were bound against.
async function _loadReviewTicketClockEntries(ticketId, loadToken, ticket) {
  ReviewState.ticketClockEntries = null;
  if (BillingState.tenantId === 'demo-tenant' || !ticketId) return;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', BillingState.tenantId, 'clockEntries'),
      where('ticketId', '==', ticketId)
    ));
    if (loadToken !== _reviewLoadToken || ReviewState.ticketId !== ticketId) return;
    ReviewState.ticketClockEntries = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => _crewEntryBoundToTicket(e, ticket));
  } catch (err) {
    console.warn('[Billing] crew-gap: could not load clock entries for ticket', ticketId, err?.message || err);
  }
}

function _reviewMissingCrew() {
  if (!Array.isArray(ReviewState.ticketClockEntries)) return [];
  const removedNow = ReviewState.crewRemovedEntryIds || [];
  return _missingCrewFromEntries(ReviewState.ticketClockEntries, ReviewState.employeeLines, removedNow);
}

// Banner inside #review-emp-section (both table and card modes) so the section's
// outerHTML re-render keeps it in step with the lines.
// Shifts a biller ruled "not on this ticket" — listed quietly so the ruling is
// visible and reversible (COD r2 Code Reviewer), never as a warning.
function _reviewRuledOffCrew() {
  if (!Array.isArray(ReviewState.ticketClockEntries)) return [];
  const ruled = new Set((ReviewState.crewRemovedEntryIds || []).map(String));
  if (ruled.size === 0) return [];
  return _missingCrewFromEntries(ReviewState.ticketClockEntries, ReviewState.employeeLines, []).filter(m => ruled.has(m.entryId));
}
function _reviewRuledOffCrewHtml() {
  const off = _reviewRuledOffCrew();
  if (off.length === 0) return '';
  const ro = isReviewReadOnly(ReviewState.ticket || {}) || _moneyViewOnly();
  return `
      <div id="review-crew-ruled-off" style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;display:flex;flex-wrap:wrap;gap:6px 10px;align-items:center;">
        <span>Marked not on this ticket:</span>
        ${off.map(m => `<span style="display:inline-flex;align-items:center;gap:6px;color:var(--text-primary);">${escapeHtml(m.personName)} · ${m.hours.toFixed(2)} h${ro ? '' : ` <button type="button" class="btn btn-sm btn-secondary" style="min-height:36px;padding:2px 10px;" onclick="window.BillingModule.restoreMissingCrew('${(window.escJsAttr || escapeHtml)(m.entryId)}')">Add back</button>`}</span>`).join('')}
      </div>`;
}

function _reviewMissingCrewBannerHtml() {
  const missing = _reviewMissingCrew();
  if (missing.length === 0) return _reviewRuledOffCrewHtml();
  // Locked/invoiced tickets still SHOW the gap (COD Domain) — read-only. So does a
  // view-only login: ticketLines create needs canEditMoney (COD AuthZ), so the
  // buttons would only ever end in permission-denied.
  const locked = isReviewReadOnly(ReviewState.ticket || {});
  const viewOnly = !locked && _moneyViewOnly();
  const ro = locked || viewOnly;
  const isAre = missing.length === 1 ? 'is' : 'are';
  const rows = missing.map(m => `
        <div class="crew-gap-row" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-top:1px solid #FDE68A;">
          <span style="flex:1 1 180px;min-width:0;font-weight:600;color:var(--text-primary);">${escapeHtml(m.personName)} <span style="font-weight:400;color:var(--text-secondary);">· ${m.hours.toFixed(2)} h</span></span>
          ${ro ? '' : `<button type="button" class="btn btn-sm btn-primary" style="min-height:44px;" onclick="window.BillingModule.restoreMissingCrew('${(window.escJsAttr || escapeHtml)(m.entryId)}')">Add back</button>
          <button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;" onclick="window.BillingModule.keepCrewOffTicket('${(window.escJsAttr || escapeHtml)(m.entryId)}')">Not on this ticket</button>`}
        </div>`).join('');
  return `
      <div id="review-crew-gap" class="crew-gap-banner" role="status" style="background:#FFFBEB;border:1px solid #F59E0B;border-radius:8px;padding:10px 12px;margin:0 0 10px;color:var(--text-primary);font-size:0.85rem;">
        <div style="display:flex;align-items:flex-start;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#B45309;">warning_amber</span>
          <span><strong>${escapeHtml(_crewNamesText(missing))} ${isAre} clocked to this ticket but not on it</strong> (${_crewHoursText(missing)} h). Their timecards still have the hours; this ticket and its invoice do not.${locked ? ' Unlock the ticket to add them back.' : (viewOnly ? ' Ask someone who can edit billing to add them back.' : '')}</span>
        </div>
        ${missing.length > 1 && !ro ? `<div style="padding:4px 0 2px;"><button type="button" class="btn btn-sm btn-primary" style="min-height:44px;" onclick="window.BillingModule.restoreMissingCrew('*')">Add all back</button></div>` : ''}
        ${rows}
      </div>${_reviewRuledOffCrewHtml()}`;
}

function _rerenderReviewEmployeeSection() {
  const section = document.getElementById('review-emp-section');
  if (section) section.outerHTML = renderReviewEmployeeSection();
  if (typeof renderReviewTotals === 'function') renderReviewTotals();
}

// "Add back" — re-creates the EMPLOYEE line from the clock entry. Staged like any
// Review edit; Save writes it (and the ticket_crew_restored audit row).
function restoreMissingCrew(entryId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  if (_ticketContentBlocked() || _moneyBlocked()) return;
  // "Add all back" restores only what the banner lists (COD r3: never a ruled-off
  // shift). A single Add back may name a ruled-off shift — that is how a ruling
  // is undone.
  const pick = entryId === '*'
    ? _reviewMissingCrew()
    : _missingCrewFromEntries(ReviewState.ticketClockEntries || [], ReviewState.employeeLines, []).filter(m => m.entryId === String(entryId));
  if (pick.length === 0) return;
  ReviewState.crewRestored = ReviewState.crewRestored || [];
  for (const m of pick) {
    const e = (ReviewState.ticketClockEntries || []).find(x => String(x.id) === m.entryId);
    if (!e) continue;
    const line = _employeeLineFromClockEntry(e);
    line._localId = nextLocalId();
    _stampReviewLineBaseline(line);
    ReviewState.employeeLines.push(line);
    ReviewState.crewRestored.push({ entryId: m.entryId, personName: m.personName, hours: m.hours });
    // A biller who removed this person earlier in the session and now adds them
    // back has changed their mind — drop the pending removal stamp.
    ReviewState.crewRemovedEntryIds = (ReviewState.crewRemovedEntryIds || []).filter(id => id !== m.entryId);
    // ...and a ruling made earlier in THIS session is withdrawn, so Save never
    // audits one shift as both ruled off and restored (COD r3).
    ReviewState.crewRemovedDecisions = (ReviewState.crewRemovedDecisions || []).filter(d => d.entryId !== m.entryId);
  }
  ReviewState.dirty = true;
  _rerenderReviewEmployeeSection();
  window.showToast?.(`${_crewNamesText(pick)} added back (${_crewHoursText(pick)} h) — Save to keep it.`, 'info');
}

// "Not on this ticket" — the biller says this shift does not bill here. Recorded
// on the ticket (crewRemovedEntryIds, written on Save) so the banner stops
// asking; the clock entry itself is not touched.
function keepCrewOffTicket(entryId) {
  if (isReviewReadOnly(ReviewState.ticket || {})) return;
  if (_ticketContentBlocked() || _moneyBlocked()) return;
  const id = String(entryId || '');
  if (!id) return;
  const ids = new Set(ReviewState.crewRemovedEntryIds || []);
  ids.add(id);
  ReviewState.crewRemovedEntryIds = [...ids];
  ReviewState.crewRemovedDecisions = ReviewState.crewRemovedDecisions || [];
  const e = (ReviewState.ticketClockEntries || []).find(x => String(x.id) === id);
  ReviewState.crewRemovedDecisions.push({ entryId: id, personName: e?.personName || '', hours: _crewEntryHours(e), via: 'banner' });
  ReviewState.dirty = true;
  _rerenderReviewEmployeeSection();
}

// ============================================
// TASK-9866 — the crew's hours today are on a SISTER job (TITS ZL 10/14)
// ============================================
// Ticket #100884 (J-1248, the 8 AM run) carried Sean's and Mike's BOLs while
// both of their clock entries sat on J-1247 (the 5 PM run, same customer,
// same PO). Nothing on either side said so; Chris heard it by word of mouth.
// Review now reads the crew's clock entries for the ticket's work date and
// says, in one line per person, whose hours sit on which sister job — with
// "Move to this job" (the SAME gateway op a Workforce edit uses: times pinned,
// fingerprint-guarded, ticket line follows). Same customer only: a person on
// two customers' jobs in one day is two runs, not a mistake. Read-only
// detection; the move is an explicit tap. The mirror — "clocked here but not
// on this job's crew" — reads the ticket's own entries against the job day.

// Pure. Who counts as "the crew" of this ticket: everyone on an EMPLOYEE line
// plus everyone on the job day (ids, or legacy name refs resolved via people).
function _reviewCrewIdsFrom(employeeLines, dayRefs, people) {
  const ids = new Set();
  (employeeLines || []).forEach(l => { if (l && l.personId && (!l.type || l.type === 'EMPLOYEE')) ids.add(String(l.personId)); });
  const byName = new Map((people || []).filter(p => p && p.id && p.name).map(p => [String(p.name).trim().toLowerCase(), String(p.id)]));
  const byId = new Set((people || []).filter(p => p && p.id).map(p => String(p.id)));
  (dayRefs || []).forEach(r => {
    if (r == null || r === '') return;
    const s = String(r).trim();
    if (byId.has(s)) { ids.add(s); return; }
    const hit = byName.get(s.toLowerCase());
    if (hit) ids.add(hit);
  });
  return [...ids];
}

// Pure. The crew's completed entries that day on ANOTHER job of the SAME
// customer, for people who have no time on this job that day. Entries already
// pointing at this ticket, deleted/voided ones and open shifts never count.
function _sisterJobHoursFromEntries(entries, ticket, crewIds) {
  const t = ticket || {};
  if (!t.customerId || !t.jobId) return [];
  const crew = new Set((crewIds || []).map(String));
  const live = (entries || []).filter(e => e && e.id && e.personId && crew.has(String(e.personId))
    && !(e.deleted === true || e.voided === true || e.status === 'deleted' || e.status === 'voided'));
  const onThisJob = new Set(live.filter(e => (e.jobId || null) === t.jobId || (t.id && e.ticketId === t.id)).map(e => String(e.personId)));
  const out = [];
  for (const e of live) {
    if (!e.clockOut) continue;
    if (onThisJob.has(String(e.personId))) continue;
    if ((e.customerId || null) !== t.customerId) continue;
    if (!e.jobId || e.jobId === t.jobId) continue;
    // `entry` = the clock entry as read: the move payload's fingerprint needs
    // its status / updatedAt / qbSynced / ticketId (COD Code Reviewer).
    out.push({ entryId: String(e.id), personId: String(e.personId), personName: e.personName || 'Unknown', hours: _crewEntryHours(e), jobId: e.jobId, jobTitle: e.jobTitle || '', ticketId: e.ticketId || null, entry: e });
  }
  return out.sort((a, b) => String(a.personName).localeCompare(String(b.personName)) || a.entryId.localeCompare(b.entryId));
}

// Pure. The mirror: shifts clocked to THIS ticket by someone the job day does
// not list. An empty crew list is "no claim", never "everyone is off-crew".
function _offCrewClockedFromEntries(entries, dayRefs, people) {
  const refs = (dayRefs || []).filter(r => r != null && r !== '');
  if (!refs.length) return [];
  const crew = new Set(_reviewCrewIdsFrom([], refs, people));
  const names = new Set(refs.map(r => String(r).trim().toLowerCase()));
  const out = [];
  for (const e of (entries || [])) {
    if (!_crewEntryCountable(e)) continue;
    if (crew.has(String(e.personId))) continue;
    if (e.personName && names.has(String(e.personName).trim().toLowerCase())) continue;
    out.push({ entryId: String(e.id), personId: String(e.personId), personName: e.personName || 'Unknown', hours: _crewEntryHours(e) });
  }
  return out.sort((a, b) => String(a.personName).localeCompare(String(b.personName)) || a.entryId.localeCompare(b.entryId));
}

// Pure. The gateway payload for moving ONE finished shift onto this ticket's
// job: times pinned to what is stored, the target, and the fingerprint the
// gateway re-checks inside its transaction (ticketGateway.clockEntryFingerprint,
// mirrored field for field — a shift edited since Review opened refuses).
function _sisterMovePayload(entry, ticket, job) {
  const ms = (v) => (v && typeof v.toMillis === 'function') ? v.toMillis() : (v && typeof v.toDate === 'function' ? v.toDate().getTime() : (v ? new Date(v).getTime() : null));
  const inMs = ms(entry.clockIn); const outMs = ms(entry.clockOut);
  return {
    entryId: entry.id,
    clockIn: new Date(inMs).toISOString(),
    clockOut: outMs != null ? new Date(outMs).toISOString() : null,
    customerId: ticket.customerId,
    customerName: ticket.customerName || (job && (job.customer || job.customerName)) || null,
    jobId: ticket.jobId,
    jobTitle: (job && (job.title || job.name)) || ticket.jobName || ticket.jobTitle || null,
    expectedEntry: {
      status: entry.status == null ? null : String(entry.status),
      clockInMs: inMs, clockOutMs: outMs,
      updatedAtMs: ms(entry.updatedAt),
      customerId: entry.customerId || null, jobId: entry.jobId || null,
      ticketId: entry.ticketId || null, personId: entry.personId || null,
      qbSynced: entry.qbSynced === true,
    },
  };
}

function _sisterJobLabel(row) {
  const j = (BillingState.jobs || []).find(x => x.id === row.jobId);
  const no = j && j.jobNumber != null ? `J-${j.jobNumber}` : '';
  const title = (j && (j.title || j.name)) || row.jobTitle || row.jobId;
  return no ? `${no} · ${title}` : title;
}

// Best-effort loader. A failed read leaves sisterHours null (nothing rendered —
// never a false "hours are on another job").
async function _loadReviewSisterHours(ticketId, loadToken, ticket) {
  ReviewState.sisterHours = null;
  ReviewState.offCrewClocked = [];
  ReviewState.crewDayRefs = null;
  if (BillingState.tenantId === 'demo-tenant' || !ticketId || !ticket) return;
  const t = { ...(ticket || {}), id: ticketId };
  const workDate = typeof t.workDate === 'string' ? t.workDate.slice(0, 10) : '';
  if (!t.customerId || !t.jobId || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return;
  try {
    const tenant = BillingState.tenantId;
    const dayEnv = await _loadTemplateCheckJobDays(t.jobId);
    if (loadToken !== _reviewLoadToken || ReviewState.ticketId !== ticketId) return;
    const day = dayEnv.loaded ? dayEnv.days.find(d => d._dayStr === workDate) : null;
    const dayRefs = day ? [...(Array.isArray(day.drivers) ? day.drivers : []), ...(Array.isArray(day.helpers) ? day.helpers : [])] : [];
    ReviewState.crewDayRefs = day ? dayRefs : null;
    const crewIds = _reviewCrewIdsFrom(ReviewState.employeeLines, dayRefs, BillingState.people).slice(0, 15);
    let entries = [];
    if (crewIds.length) {
      const start = new Date(`${workDate}T00:00:00`);
      const end = new Date(start.getTime()); end.setDate(end.getDate() + 1);
      const snaps = await Promise.all(crewIds.map(pid => getDocs(query(
        collection(db, 'tenants', tenant, 'clockEntries'),
        where('personId', '==', pid),
        where('clockIn', '>=', Timestamp.fromDate(start)),
        where('clockIn', '<', Timestamp.fromDate(end)),
        orderBy('clockIn', 'desc')
      ))));
      if (loadToken !== _reviewLoadToken || ReviewState.ticketId !== ticketId) return;
      entries = snaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    ReviewState.sisterHours = _sisterJobHoursFromEntries(entries, t, crewIds);
    ReviewState.offCrewClocked = _offCrewClockedFromEntries(ReviewState.ticketClockEntries || [], ReviewState.crewDayRefs, BillingState.people);
  } catch (err) {
    console.warn('[Billing] TASK-9866 — sister-job hours read failed; nothing is shown:', err?.message || err);
    ReviewState.sisterHours = null;
    ReviewState.offCrewClocked = [];
  }
}

// TASK-9763 S2 — "Ran #22, planned #19". The plan is SERVER-stamped on the
// clock entry (onClockEntryAssetValidate → assetProvenance.plannedAssetIds,
// from the day's crew↔truck pairing); assetIds is what the driver confirmed.
// A quiet note, advisory only — no billing math reads it. Entries with no plan
// (no pairing that day, or the person wasn't paired) say nothing.
function _reviewSameIds(a, b) {
  const x = a.map(String).sort();
  const y = b.map(String).sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

function _reviewPlanVsRan() {
  const entries = Array.isArray(ReviewState.ticketClockEntries) ? ReviewState.ticketClockEntries : [];
  const out = [];
  for (const e of entries) {
    const planned = e?.assetProvenance?.plannedAssetIds;
    if (!Array.isArray(planned) || planned.length === 0 || !Array.isArray(e.assetIds)) continue;
    if (_reviewSameIds(e.assetIds, planned)) continue;
    out.push({ entryId: String(e.id || ''), personName: e.personName || 'Someone', ran: e.assetIds.map(String), planned: planned.map(String) });
  }
  return out;
}

function _reviewUnitLabel(id) {
  const pools = [ReviewState.trucks, ReviewState.trailers, ReviewState.assets, window.AppState?.fleetTrucks];
  for (const pool of pools) {
    const u = (Array.isArray(pool) ? pool : []).find(x => x && String(x.id) === String(id));
    if (u) return (window.getUnitDisplay ? window.getUnitDisplay(u) : '') || u.nickname || u.unit || u.name || 'a unit';
  }
  return 'a unit not on the roster';
}

function _reviewPlanVsRanHtml() {
  const rows = _reviewPlanVsRan();
  if (!rows.length) return '';
  const list = ids => ids.length ? ids.map(id => escapeHtml(_reviewUnitLabel(id))).join(' + ') : 'no equipment';
  return `
      <div id="review-plan-vs-ran" role="note" style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">
        ${rows.map(r => `<div class="review-plan-vs-ran-row" data-entry="${(window.escAttr || escapeHtml)(r.entryId)}" style="display:flex;align-items:center;gap:6px;padding:2px 0;">
          <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">swap_horiz</span>
          <span><strong style="color:var(--text-primary);">${escapeHtml(r.personName)}</strong> · Ran ${list(r.ran)}, planned ${list(r.planned)}</span>
        </div>`).join('')}
      </div>`;
}

function _reviewSisterHoursBannerHtml() {
  const rows = Array.isArray(ReviewState.sisterHours) ? ReviewState.sisterHours : [];
  const off = Array.isArray(ReviewState.offCrewClocked) ? ReviewState.offCrewClocked : [];
  if (!rows.length && !off.length) return '';
  const locked = isReviewReadOnly(ReviewState.ticket || {});
  const ro = locked || _moneyViewOnly();
  // The move rewrites a shift's job — a Workforce edit (COD Adversary/AuthZ:
  // the gateway's billing arm would admit a billing-only login). Others see
  // the line and are pointed at Workforce.
  let wf = false;
  try { wf = typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('workforce', true) === true; } catch (_) { wf = false; }
  const escJ = window.escJsAttr || escapeHtml;
  let html = '';
  if (rows.length) {
    const isAre = rows.length === 1 ? 'is' : 'are';
    html += `
      <div id="review-sister-hours" class="crew-gap-banner" role="status" style="background:#FFFBEB;border:1px solid #F59E0B;border-radius:8px;padding:10px 12px;margin:0 0 10px;color:var(--text-primary);font-size:0.85rem;">
        <div style="display:flex;align-items:flex-start;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#B45309;">warning_amber</span>
          <span><strong>${escapeHtml(_crewNamesText(rows))}'${rows.length === 1 && !/s$/i.test(rows[0].personName || '') ? 's' : ''} hours today ${isAre} on a different job</strong> of this customer. Their timecards (and the other job's ticket) have the hours. If they worked this run, move the shift here; a shift that covered both runs is split in Workforce.${locked ? ' Unlock this ticket to move them here.' : (ro ? ' Ask someone who can edit billing to move them.' : '')}</span>
        </div>
        ${rows.map(r => `
        <div class="crew-gap-row" data-sister-entry="${(window.escAttr || escapeHtml)(r.entryId)}" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-top:1px solid #FDE68A;">
          <span style="flex:1 1 220px;min-width:0;color:var(--text-primary);"><strong>${escapeHtml(r.personName)}</strong> · ${r.hours.toFixed(2)} h on <span style="white-space:nowrap;">${escapeHtml(_sisterJobLabel(r))}</span></span>
          ${ro ? '' : (wf ? `<button type="button" class="btn btn-sm btn-primary" style="min-height:44px;" onclick="window.BillingModule.moveSisterHoursHere('${escJ(r.entryId)}')">Move to this job</button>` : '<span style="font-size:0.8rem;color:var(--text-secondary);">Move it in Workforce</span>')}
        </div>`).join('')}
      </div>`;
  }
  if (off.length) {
    html += `
      <div id="review-off-crew" role="status" style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;display:flex;flex-wrap:wrap;gap:6px 10px;align-items:center;">
        <span class="material-icons" style="font-size:16px;color:#B45309;" aria-hidden="true">info</span>
        <span>Clocked here but not on this job's crew:</span>
        ${off.map(o => `<span style="color:var(--text-primary);">${escapeHtml(o.personName)} · ${o.hours.toFixed(2)} h</span>`).join('')}
      </div>`;
  }
  return html;
}

// "Move to this job" — the shift's job + customer become this ticket's, through
// the clock gateway (editClockEntry → repoint moves its EMPLOYEE line off the
// sister ticket and onto this job's open ticket; one audit row). Re-opens the
// Review afterwards so lines, entries and both banners are read fresh.
async function moveSisterHoursHere(entryId) {
  const ticket = ReviewState.ticket || {};
  const ticketId = ReviewState.ticketId;
  if (!ticketId || isReviewReadOnly(ticket)) return;
  if (_ticketContentBlocked() || _moneyBlocked()) return;
  try { if (typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('workforce', true) !== true) { window.showToast?.('Moving a shift needs the Workforce module. Move it in Workforce.', 'warning'); return; } } catch (_) { return; }
  const row = (ReviewState.sisterHours || []).find(r => r.entryId === String(entryId));
  if (!row) return;
  if (ReviewState.dirty) { window.showToast?.('Save or cancel your changes on this ticket first, then move the hours.', 'warning'); return; }
  const job = (BillingState.jobs || []).find(j => j.id === ticket.jobId) || null;
  const hereLabel = job ? _jobPickerLabel(job) : (ticket.jobName || 'this job');
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm(`Move ${row.personName}'s hours to this job?`, `${row.hours.toFixed(2)} h move from ${_sisterJobLabel(row)} to ${hereLabel}. Only do this if the shift was worked on this run. The clock times do not change; their ticket line follows the hours. This is recorded in the shift's edit history. A shift that covered both runs is split in Workforce instead.`, { confirmText: 'Move hours', cancelText: 'Cancel', danger: false })
    : confirm(`Move ${row.personName}'s ${row.hours.toFixed(2)} h from ${_sisterJobLabel(row)} to this job?`);
  if (!ok) return;
  // TASK-9443 soft pay-period lock — same ask the Workforce edit makes (COD Edge Case Hunter).
  try {
    const PL = window.PayrollRunLock;
    const _ci = row.entry?.clockIn?.toDate?.() || (row.entry?.clockIn ? new Date(row.entry.clockIn) : null);
    if (PL && typeof PL.confirmEditMany === 'function' && _ci && !(await PL.confirmEditMany([_ci.getTime()], { what: 'Moving this shift' }))) return;
  } catch (_) { /* the lock informs, it never traps */ }
  const btn = document.querySelector(`[data-sister-entry="${CSS.escape(String(entryId))}"] button`);
  if (btn) { btn.disabled = true; btn.textContent = 'Moving…'; }
  try {
    if (!row.entry || !row.entry.id) throw new Error('Could not read that shift. Reopen the ticket and try again.');
    const payload = _sisterMovePayload(row.entry, { ...ticket, id: ticketId }, job);
    const fn = httpsCallable(functions, 'mutateTicketAndClockEntry', { timeout: 120000 });
    const opKey = `t9866_${String(entryId).replace(/[^A-Za-z0-9_-]/g, '')}_${Date.now().toString(36)}`;
    const result = await fn({ op: 'editClockEntry', tenantId: BillingState.tenantId, payload, opKey });
    if (!result?.data?.ok) throw new Error('The move was not confirmed by the server.');
    window.showToast?.(`${row.personName}'s ${row.hours.toFixed(2)} h moved to this job.`, 'success');
    // Fresh read of everything: the gateway may have minted/attached the line here.
    openReviewTicket(ticketId);
  } catch (e) {
    const code = String(e?.code || e?.details?.code || '');
    const msg = /failed-precondition|permission-denied/i.test(code) || /failed-precondition|permission-denied/i.test(String(e?.message))
      ? (e?.message || 'The server refused the move.')
      : (e?.message || 'Could not move the hours. Try again, or move the shift in Workforce.');
    window.showToast?.(msg, 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Move to this job'; }
  }
}

function removeReviewLine(type, localId) {
  const lists = {
    EMPLOYEE: ReviewState.employeeLines,
    EQUIPMENT: ReviewState.equipmentLines,
    PERMIT: ReviewState.permitLines,
    DOWNTIME: ReviewState.downtimeLines,
    STANDBY: ReviewState.standbyLines,
    MISC: ReviewState.miscLines
  };
  const list = lists[type];
  if (!list) return;

  const idx = list.findIndex(l => l._localId === localId);
  if (idx === -1) return;

  const removed = list[idx];

  // Confirm before deleting any line item
  if (type === 'EMPLOYEE') {
    const empName = removed.name || removed.personName || 'this employee';
    const hrs = removed.hours ? ` (${parseFloat(removed.hours).toFixed(2)} hrs)` : '';
    // TASK-9755 (#100852) — say what removal does to a CLOCKED shift: the
    // timecard keeps the hours, the ticket and its invoice stop showing them.
    const clockNote = removed.clockEntryId
      ? `\n\nTheir timecard keeps these hours (payroll is not affected), but this ticket and its invoice will no longer show them. Unless they are on another ticket, these hours will not be billed.` // TASK-10036
      : '';
    if (!confirm(`Remove ${empName}${hrs} from this ticket?${clockNote}`)) return;
    if (removed.clockEntryId && !removed._isNew) {
      const ids = new Set(ReviewState.crewRemovedEntryIds || []);
      ids.add(String(removed.clockEntryId));
      ReviewState.crewRemovedEntryIds = [...ids];
    }
  } else if (type === 'EQUIPMENT') {
    const label = getEquipmentDisplayName(removed) || removed.assetName || removed.fleetNumber || 'this equipment';
    const amt = removed.lineAmount ? ` ($${parseFloat(removed.lineAmount).toFixed(2)})` : '';
    const noteWarn = removed.notes?.trim() ? `\n\nNotes will also be deleted:\n"${removed.notes.trim().substring(0, 120)}${removed.notes.trim().length > 120 ? '...' : ''}"` : '';
    // TASK-10036 — a clocked equipment line's hours bill nowhere once removed.
    const clockWarn = (removed.clockEntryId && !removed._isNew)
      ? `\n\nThese hours came from a time-clock entry. Unless they are on another ticket, they will not be billed.`
      : '';
    if (!confirm(`Remove ${label}${amt}?${noteWarn}${clockWarn}`)) return;
  } else if (type === 'PERMIT') {
    const desc = removed.description || removed.permitType || 'this permit';
    if (!confirm(`Remove ${desc}?`)) return;
  } else if (type === 'DOWNTIME' || type === 'STANDBY') {
    if (!confirm(`Remove this ${type.toLowerCase()} entry?`)) return;
  } else if (type === 'MISC') {
    const desc = removed.description || 'this line item';
    if (!confirm(`Remove ${desc}?`)) return;
  }

  // If it has a Firestore ID, track it for deletion
  if (removed.id && !removed._isNew) {
    ReviewState.deletedLineIds.push(removed.id);
    // TASK-9755 — Review deletions wrote no audit row at all, so #100852's
    // two removed crew lines were only recoverable from point-in-time reads.
    // Snapshot what is going so Save can record it (ticket_lines_removed).
    ReviewState.removedLinesAudit.push({
      lineId: removed.id, type,
      label: type === 'EMPLOYEE' ? (removed.personName || removed.name || '')
        : (type === 'EQUIPMENT' ? (getEquipmentDisplayName(removed) || removed.assetName || '') : (removed.description || removed.permitType || '')),
      personId: removed.personId || null,
      clockEntryId: removed.clockEntryId || null,
      hours: Number(removed.hours) || 0,
      lineAmount: Number(removed.lineAmount) || 0,
    });
  } else if (removed._restoredFromClock) {
    // Un-did an "Add back" before saving — nothing to delete, nothing restored.
    ReviewState.crewRestored = (ReviewState.crewRestored || []).filter(r => r.entryId !== String(removed.clockEntryId));
  }
  list.splice(idx, 1);
  ReviewState.dirty = true;

  // Re-render appropriate section
  if (type === 'EMPLOYEE') {
    const section = document.getElementById('review-emp-section');
    if (section) section.outerHTML = renderReviewEmployeeSection();
  } else if (type === 'EQUIPMENT') {
    // Full re-render to also remove the Equipment Notes section for this line
    renderReviewTicket();
    return; // renderReviewTicket handles totals
  } else if (type === 'PERMIT') {
    const section = document.getElementById('review-permit-section');
    if (section) section.outerHTML = renderReviewPermitSection();
  } else if (type === 'DOWNTIME' || type === 'STANDBY') {
    // Full re-render since these sections don't have stable IDs
    renderReviewTicket();
    return;
  } else if (type === 'MISC') {
    const section = document.getElementById('review-misc-section');
    if (section) section.outerHTML = renderReviewMiscSection();
  }
  renderReviewTotals();
}

// ============================================
// REVIEW TICKET — DOWNTIME/STANDBY NOTE EDITING
// ============================================
function updateReviewDowntimeNotes(localId, value) {
  const line = ReviewState.downtimeLines.find(l => l._localId === localId);
  if (!line) return;
  line.notes = value || '';
  ReviewState.dirty = true;
}

function updateReviewStandbyNotes(localId, value) {
  const line = ReviewState.standbyLines.find(l => l._localId === localId);
  if (!line) return;
  line.notes = value || '';
  ReviewState.dirty = true;
}

// ============================================
// REVIEW TICKET — MISCELLANEOUS LINE ITEMS
// ============================================
function renderReviewMiscSection() {
  const miscSubtotal = ReviewState.miscLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
  const hasMiscLines = ReviewState.miscLines.length > 0;

  // Get configured misc line items from contract
  const miscLiOptions = (ReviewState.contractLineItems || []).filter(li =>
    li.active !== false && li.appliesTo?.kind === 'misc'
  );
  const hasMiscOptions = miscLiOptions.length > 0;

  // Build the add button/dropdown
  let addBtn;
  if (hasMiscOptions) {
    addBtn = `
      <select id="review-misc-add-select" class="form-control" style="font-size:0.75rem;padding:4px 8px;width:auto;min-width:130px;"
        onchange="if(this.value){window.BillingModule.addReviewMiscRow(this.value);this.value='';}">
        <option value="">+ Add Line</option>
        ${miscLiOptions.map(li => `<option value="${li.id}">${escapeHtml(li.description)} — ${_isNoChargeLine({ noCharge: li.noCharge, rate: li.rate }) ? 'N/C' : `$${(li.rate || 0).toFixed(2)}/${escapeHtml(li.rateUnit || 'ea')}`}</option>`).join('')}
        <option value="__custom__">Custom line...</option>
      </select>`;
  } else {
    addBtn = `
      <button class="btn btn-secondary btn-sm" onclick="window.BillingModule.addReviewMiscRow()" style="font-size:0.75rem;padding:4px 10px;">
        <span class="material-icons" style="font-size:14px;">add</span> Add Line
      </button>`;
  }

  return `
    <div class="review-section" id="review-misc-section">
      <div class="review-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <span style="display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:18px;">receipt</span> Miscellaneous
          ${hasMiscLines ? `<span class="review-subtotal" id="review-misc-subtotal">$${miscSubtotal.toFixed(2)}</span>` : ''}
        </span>
        ${addBtn}
      </div>
      ${hasMiscLines ? `
        <div style="overflow-x:auto;">
          <table class="review-table"><thead><tr>
            <th style="text-align:left;padding:6px 10px;font-weight:600;">Description</th>
            <!-- TASK-1413 — Qty 70px/Rate 90px clipped their own inputs: the number
                 input is width:100%, so the content box was 90 - 20 (td padding)
                 - 16 (input padding) = 54px minus Chrome's spinner reservation,
                 and "222.00" at 0.8rem did not fit (rendered "222.0("). Stored
                 value was always correct; this is display-only. Both tables sit
                 in overflow-x:auto so mobile just scrolls. -->
            <th style="text-align:center;padding:6px 10px;font-weight:600;width:80px;">Qty</th>
            <th style="text-align:right;padding:6px 10px;font-weight:600;width:120px;">Rate</th>
            <th style="text-align:right;padding:6px 10px;font-weight:600;width:90px;">Amount</th>
            <th style="width:36px;"></th>
          </tr></thead><tbody>
          ${ReviewState.miscLines.map(l => `
            <tr>
              <td style="padding:4px 10px;"><input type="text" class="form-control" value="${escapeHtml(l.description || '')}" placeholder="Description..."
                style="font-size:0.8rem;padding:4px 8px;width:100%;"
                onchange="window.BillingModule.updateReviewMiscLine(${l._localId}, 'description', this.value)">${_reviewPriceBookLineTags(l)}</td>
              <td style="padding:4px 10px;"><input type="number" class="form-control" value="${l.quantity || 1}" min="0" step="0.01"
                style="font-size:0.8rem;padding:4px 8px;text-align:center;width:100%;"
                onchange="window.BillingModule.updateReviewMiscLine(${l._localId}, 'quantity', parseFloat(this.value)||0)"></td>
              <td style="padding:4px 10px;"><input type="number" class="form-control" value="${(l.rate || 0).toFixed(2)}" min="0" step="0.01"
                style="font-size:0.8rem;padding:4px 8px;text-align:right;width:100%;"
                onchange="window.BillingModule.updateReviewMiscLine(${l._localId}, 'rate', parseFloat(this.value)||0)"></td>
              <td style="padding:4px 10px;text-align:right;font-weight:600;">$${(l.lineAmount || 0).toFixed(2)}</td>
              <td style="padding:4px 4px;"><button class="btn-icon" onclick="window.BillingModule.removeReviewLine('MISC', ${l._localId})" title="Remove line">
                <span class="material-icons" style="font-size:16px;color:var(--status-danger);">delete_outline</span></button></td>
            </tr>
          `).join('')}
          </tbody></table>
        </div>
      ` : `<p style="font-size:0.8rem;color:var(--text-muted);margin:8px 0 0;">No miscellaneous charges.${hasMiscOptions ? ' Select from the dropdown to add one.' : ''}</p>`}
    </div>
  `;
}

function addReviewMiscRow(lineItemId) {
  let desc = '', rate = 0, rateUnit = 'ea', qty = 1, liId = null, pbFlags = {};

  if (lineItemId && lineItemId !== '__custom__') {
    const li = (ReviewState.contractLineItems || []).find(l => l.id === lineItemId);
    if (li) {
      desc = li.description || '';
      rate = li.rate || 0;
      rateUnit = li.rateUnit || 'ea';
      qty = li.quantity || 1;
      liId = li.id;
      pbFlags = _priceBookLineTicketFlags(li); // TASK-9666/9667 — empty unless the item uses them
    }
  }

  const newLine = {
    _localId: nextLocalId(),
    _isNew: true,
    type: 'MISC',
    description: desc,
    quantity: qty,
    rate: rate,
    rateUnit: rateUnit,
    lineAmount: parseFloat((qty * rate).toFixed(2)),
    lineItemId: liId,
    ...pbFlags
  };
  ReviewState.miscLines.push(newLine);
  ReviewState.dirty = true;

  const section = document.getElementById('review-misc-section');
  if (section) section.outerHTML = renderReviewMiscSection();
  renderReviewTotals();
}

function updateReviewMiscLine(localId, field, value) {
  const line = ReviewState.miscLines.find(l => l._localId === localId);
  if (!line) return;
  line[field] = value;
  if (field === 'quantity' || field === 'rate') {
    line.lineAmount = parseFloat(((line.quantity || 0) * (line.rate || 0)).toFixed(2));
    // Update the amount cell in the DOM
    const section = document.getElementById('review-misc-section');
    if (section) section.outerHTML = renderReviewMiscSection();
  }
  ReviewState.dirty = true;
  renderReviewTotals();
}

// ============================================
// REVIEW TICKET — SAVE
// ============================================
// TASK-1231 — money-integrity save gate (ticket #100264: "10" typed into a
// prefilled "12.22" persisted 1012.22 h → a $320,315.70 grandTotal). Collects
// every time-based employee/equipment line whose HOURS exceed the clock-window
// ceiling. Ceiling priority: (1) the server-stamped
// `_billingReviewFlag.entryCeilingHours` (equipmentLineEnrichment.js), else
// (2) the SAME formula derived client-side from the line's own fromTime/toTime
// window — max(duration × 1.25, duration + 1 h) — which covers the FIRST write,
// before the server flagger has ever seen the value; else (3) a 24 h sanity
// bar for lines with no window at all. NEVER mutates the value (COD Domain,
// BINDING: auto-capping converts a flag into a silent billing alteration) —
// the biller either goes back and fixes the hours or explicitly verifies.
function _reviewHoursViolations() {
  const out = [];
  // TASK-1234 — ceiling logic now lives in _lineHoursCeilingInfo (shared with
  // the inline card warning) so the point-of-entry signal and this save gate
  // can never drift apart. Behavior identical to the original TASK-1231 check:
  // flag ceiling → clock-window ×1.25 (min +1h) → 24h bar; per-unit lines skip.
  const check = (l, label) => {
    const info = _lineHoursCeilingInfo(l);
    if (!info) return; // per-unit lines bill quantity, not hours
    const hours = parseFloat(l.hours) || 0;
    if (hours <= 0) return;
    if (hours > info.ceiling) out.push({ label, hours, ceiling: info.source === 'default' ? null : info.ceiling });
  };
  for (const l of ReviewState.employeeLines) check(l, getEmployeeDisplayName(l) || 'Employee line');
  for (const l of ReviewState.equipmentLines) check(l, getEquipmentDisplayName(l) || 'Equipment line');
  return out;
}

async function saveReviewTicket() {
  if (_ticketContentBlocked()) return; // TASK-9491
  // TASK-9397 — a document still converting would miss the upload list.
  // COD: a Save that waited must not persist a DIFFERENT ticket opened meanwhile.
  const _saveTicketRef = ReviewState.ticket;
  if (!(await settlePhotoPrepBeforeSave(_reviewPhotoPrep, 'btn-save-review'))) return;
  if (ReviewState.ticket !== _saveTicketRef) return;
  // TASK-1167 — hard stop: never persist edits to a read-only ticket (archived /
  // locked / invoiced — NOT driver-locked, which the biller may still edit). The
  // footer Save is hidden for these, but a stale DOM, a double-submit, or a direct
  // call must still be refused — this is the books-integrity backstop behind the UI
  // lock (Simonson #100239). Concurrent archive-after-open is closed by TASK-1176
  // (firestore.rules member-arm immutability).
  if (isReviewReadOnly(ReviewState.ticket || {})) {
    window.showToast?.('This ticket is locked — unarchive it first to make changes.', 'error');
    return;
  }

  // TASK-1287 — snapshot any edited driver shift notes NOW, while the modal is
  // still open and still showing this ticket. The actual write happens near the
  // end of this function, long after the modal has been torn down and re-rendered
  // (see collectEditedShiftNotesForReview's header for why reading the DOM there
  // is unsafe).
  const _editedShiftNotes = collectEditedShiftNotesForReview();

  // TASK-10036 (#100918) — an untouched notes box never writes: drop a notes
  // edit that ended up equal to what the box opened with.
  if (ReviewState.headerEdits.notes !== undefined
      && ReviewState._openNotesText !== null && ReviewState._openNotesText !== undefined
      && String(ReviewState.headerEdits.notes) === String(ReviewState._openNotesText)) {
    delete ReviewState.headerEdits.notes;
    delete ReviewState.headerEdits.workPerformed;
  }
  // TASK-10036 — say what this Save throws away (cleared notes, cleared shift
  // notes, clocked lines whose hours would bill nowhere) and let the user back
  // out with NOTHING written. #100918 lost all three in one silent save.
  let _confirmedRemovals = [];
  {
    const _tce = Array.isArray(ReviewState.ticketClockEntries) ? ReviewState.ticketClockEntries : null;
    const _items = _reviewDestructiveSaveItems({
      notesEdit: ReviewState.headerEdits.notes,
      openNotes: ReviewState._openNotesText,
      shiftChanged: _editedShiftNotes.changed,
      shiftOriginals: ReviewShiftNoteOriginals || {},
      shiftNames: ReviewShiftNoteNames || {},
      removedLines: (ReviewState.removedLinesAudit || []).filter(r => (ReviewState.deletedLineIds || []).includes(r.lineId)),
      boundEntryIds: _tce ? new Set(_tce.map(e => String(e?.id || ''))) : null,
    });
    if (_items.length > 0) {
      const _ok = await window.showConfirm?.(
        'This save removes information',
        `Saving will: ${_items.join(' ')} Go back if you did not mean to do this.`,
        { confirmText: 'Save anyway', cancelText: 'Go back', danger: true, safeDefault: true }
      );
      if (!_ok) return;
      if (ReviewState.ticket !== _saveTicketRef) return;
      _confirmedRemovals = _items;
    }
  }

  // TASK-1231 — flag save-block: hours over the clock-window ceiling cannot
  // persist silently. Blocking, explicit-verify confirm (house showConfirm
  // pattern, same as the delete gate below). Cancel returns to the modal with
  // nothing written; the value is never auto-corrected.
  const _hourViolations = _reviewHoursViolations();
  if (_hourViolations.length > 0) {
    const _vList = _hourViolations.map(v => `${v.label}: ${v.hours} h`
      + (v.ceiling != null ? ` (clock window allows at most ${v.ceiling} h)` : ' (over 24 h with no clock window)')).join(' · ');
    // TASK-1342 — the attestation lives in the BODY COPY, not the button label.
    // "Save anyway — I verified these hours" wrapped to five lines inside a 140px
    // button; the button text was never the evidence anyway — the
    // `ticket_hours_override` audit row written below is. `safeDefault: true`
    // makes "Go back & fix" the solid primary and the override a quiet red
    // outline, because this dialog is a VALIDATOR interrupting the biller, not a
    // destructive action they deliberately asked for.
    const _ok = await window.showConfirm?.(
      'Billed hours look impossible',
      `${_vList}. This usually means a typo landed in front of a prefilled number. Go back and fix the hours — or save anyway ONLY if you have personally verified these hours are correct. Saving anyway is recorded in the audit log under your name.`,
      { confirmText: 'Save anyway', cancelText: 'Go back & fix', danger: true, safeDefault: true }
    );
    if (!_ok) return;
    // COD (Adversary + AuthZ + Domain, 2026-07-17): an un-stamped override makes a
    // padded-hours save indistinguishable from a normal edit later. Audit-stamp it.
    // Goes to auditLog (the ticket_edit shape below), NOT onto the line doc — the
    // line write path strips underscore-prefixed fields, and _billingReviewFlag's
    // rules pin means server-adjacent fields aren't the client's to write anyway.
    // Non-blocking: an audit failure must never eat a biller's verified save.
    //
    // TASK-1495 — goes through the SHARED writer (window.logAudit), not a direct
    // addDoc. logAudit stamps category/action/timestamp/user and strips undefined
    // before the write; a row written around it is one conditional-undefined away
    // from silently disappearing (TASK-1479) and, without `timestamp`, can never
    // match the `where('timestamp','>=',…)` range the "who changed X" viewer runs.
    // `type` is kept in the payload verbatim so existing rows and new rows still
    // share one filterable key. window.* (not import) per Pitfall #1.
    try {
      await window.logAudit?.('ticket', 'ticket_hours_override', {
        type: 'ticket_hours_override',
        ticketId: ReviewState.ticketId,
        ticketNumber: ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '',
        violations: _hourViolations.map(v => ({ label: v.label, hours: v.hours, ceiling: v.ceiling ?? null })),
        editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
      });
    } catch (auditErr) {
      console.warn('[Billing] Hours-override audit failed (non-blocking):', auditErr);
    }
  }

  // TASK-1548 — block the draft→ready TRANSITION while a priced equipment line has
  // no hours (and no non-billable mark). In-memory lines are the truth here: the
  // biller may have just typed the hours in this very modal. Deliberately NOT a
  // whole-save block — "Save as Draft" keeps every other edit (house pattern from
  // TASK-1409: warn and demote, never strand the operator's work).
  if (ReviewState.headerEdits.status === 'ready'
      && (ReviewState._originalSnapshot?.status || ReviewState.ticket?.status) !== 'ready') {
    const _zhOff = _zeroHourOffenders(ReviewState.equipmentLines);
    if (_zhOff.length > 0) {
      const _zhList = _zhOff.map(_zeroHourLineLabel).join(' · ');
      const _asDraft = await window.showConfirm?.(
        'Equipment hours are missing',
        `${_zhList}. A priced equipment line with 0 hours bills $0 for real work. ` +
        `Enter the hours, or use "Mark non-billable" in the Equipment section, before marking this ticket Ready. ` +
        `Save your other changes as a Draft instead?`,
        { confirmText: 'Save as Draft', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true }
      );
      if (!_asDraft) return;
      ReviewState.headerEdits.status = 'draft';
      const _statusSel = document.getElementById('review-status');
      if (_statusSel) {
        _statusSel.value = 'draft';
        _statusSel.className = 'review-status-pill review-status-draft';
      }
    }
  }

  // TASK-1405 — the same draft→ready block for the mirror shape: equipment with
  // real hours and NO rate. In-memory lines are the truth here too (the biller may
  // have just typed the rate in this very modal), and it demotes to Draft rather
  // than stranding the rest of their edits, exactly like the zero-hour block above.
  if (ReviewState.headerEdits.status === 'ready'
      && (ReviewState._originalSnapshot?.status || ReviewState.ticket?.status) !== 'ready') {
    const _upOff = _unpricedOffenders(ReviewState.equipmentLines);
    if (_upOff.length > 0) {
      const _upList = _upOff.map(_unpricedLineLabel).join(' · ');
      const _asDraft = await window.showConfirm?.(
        'Equipment has no rate',
        `${_upList}. The hours say the machine worked, so those hours bill $0 as-is. ` +
        `Enter the rate, or use "Mark non-billable" in the Equipment section, before marking this ticket Ready. ` +
        `Save your other changes as a Draft instead?`,
        { confirmText: 'Save as Draft', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true }
      );
      if (!_asDraft) return;
      ReviewState.headerEdits.status = 'draft';
      const _statusSel = document.getElementById('review-status');
      if (_statusSel) {
        _statusSel.value = 'draft';
        _statusSel.className = 'review-status-pill review-status-draft';
      }
    }
  }

  // TASK-1615-B — the same draft→ready block for a Per Mile quantity that came off
  // a bad route coordinate. In-memory lines are the truth here: the biller may have
  // just typed the real miles in this very modal, which clears it with no extra
  // click. Demotes to Draft rather than stranding the rest of their edits, exactly
  // like the zero-hour block above.
  if (ReviewState.headerEdits.status === 'ready'
      && (ReviewState._originalSnapshot?.status || ReviewState.ticket?.status) !== 'ready') {
    const _rmOff = _rmSuspectLines(
      [...ReviewState.employeeLines, ...ReviewState.equipmentLines],
      Array.isArray(ReviewState.routeBadStops) && ReviewState.routeBadStops.length > 0
    );
    if (_rmOff.length > 0) {
      const _asDraft = await window.showConfirm?.(
        'Mileage came from a bad location',
        `${_rmOff.map(_rmLineLabel).join(' · ')}. This job's route has a destination whose coordinates aren't a real point on earth, so the auto-filled Per Mile quantity can be thousands of miles off — or zero. ` +
        `Type the real miles, or use "Mileage is correct" in the banner at the top, before marking this ticket Ready. ` +
        `Save your other changes as a Draft instead?`,
        { confirmText: 'Save as Draft', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true }
      );
      if (!_asDraft) return;
      ReviewState.headerEdits.status = 'draft';
      const _statusSel = document.getElementById('review-status');
      if (_statusSel) {
        _statusSel.value = 'draft';
        _statusSel.className = 'review-status-pill review-status-draft';
      }
    }
  }

  // TASK-1572 Slices 3 + 3b — the same draft→ready block for an INCOMPLETE ticket:
  // no billable line for a customer whose Price Book bills for work, or a
  // customer-required field (PO) left empty. In-memory is the truth here (the
  // biller may have just typed the PO or added the truck in this very modal), and it
  // demotes to Draft rather than stranding the rest of their edits, exactly like the
  // three blocks above. Gross subtotal (no invoice-exclusion subtraction): lenient
  // at Ready on purpose — the invoice's own $0 gate and the server backstop still
  // subtract exclusions before anything reaches a customer.
  if (ReviewState.headerEdits.status === 'ready'
      && (ReviewState._originalSnapshot?.status || ReviewState.ticket?.status) !== 'ready') {
    const _tcProblems = await _reviewCompletenessProblems();
    if (_tcProblems.length > 0) {
      const _asDraft = await window.showConfirm?.(
        'Ticket is not complete',
        `${_tcProblems.join(' ')} Fix it before marking this ticket Ready. ` +
        `Save your other changes as a Draft instead?`,
        { confirmText: 'Save as Draft', cancelText: 'Go back', icon: 'warning', iconColor: '#F59E0B', safeDefault: true }
      );
      if (!_asDraft) return;
      ReviewState.headerEdits.status = 'draft';
      const _statusSel = document.getElementById('review-status');
      if (_statusSel) {
        _statusSel.value = 'draft';
        _statusSel.className = 'review-status-pill review-status-draft';
      }
    }
  }

  const btn = document.getElementById('btn-save-review');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const ticketId = ReviewState.ticketId;
  // TASK-9412 (COD round 3) — freeze time edits for the whole save, so what the
  // clock received is what the ticket writes. Cleared in finally below.
  ReviewState._saving = true;
  const _mceGo = _mce && _mce.snapshot(tenant, ticketId); // TASK-9355 S5 — the ticked move, frozen before any await

  try {
    // 0. TASK-9412 — moved EMPLOYEE in/out times go to the time clock FIRST,
    // through the gated gateway. A refusal stops here with nothing of the ticket
    // written unless the biller deliberately saves the ticket only (audited).
    if (!(await _reviewSyncClockBeforeSave(ticketId))) return;
    // The sync awaited the network; a modal closed/reopened meanwhile must not
    // have ITS state written under this ticket id (TASK-9397 pattern above).
    if (ReviewState.ticket !== _saveTicketRef) return;

    // 1. Update ticket header fields
    const headerUpdate = {
      updatedAt: Timestamp.now()
    };
    for (const [key, val] of Object.entries(ReviewState.headerEdits)) {
      headerUpdate[key] = val;
    }
    // Persist hidden bridged-permit ids so the openReviewTicket bridge skips them
    // on reopen. Without this, removing a job-bridged permit silently snaps back.
    headerUpdate.hiddenJobPermitIds = [...ReviewState.hiddenJobPermitIds];

    // TASK-846-B — persist biller edits to the captured custom-section data.
    // This field is the SOURCE of truth for auto-billed lines: the 846-A server
    // reconciler (reconcileDerivedTicketLines) fires on this write and re-derives
    // the derivedFromBinding FIELD_ITEM lines — including deleting a line when a
    // bound count was set to 0 (designed behavior; no client special-casing).
    // Cleanup mirrors clock.js collectCustomSectionData (empty instances/sections
    // drop out, shapes preserved, never writes undefined).
    // TASK-10036 (a) — stamp who/which screen when this save writes the notes.
    if (headerUpdate.notes !== undefined || headerUpdate.workPerformed !== undefined) {
      Object.assign(headerUpdate, _notesEditStamp('billing_review'));
    }
    if (ReviewState.csdDirty && ReviewState.csdEdits) {
      headerUpdate.customSectionData = _cleanCustomSectionDataForSave(ReviewState.csdEdits);
    }

    // 2. Delete removed lines
    const _deletedOk = new Set(); // TASK-9755 — the audit row lists only lines that really went
    for (const lineId of ReviewState.deletedLineIds) {
      try {
        await deleteDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', lineId));
        _deletedOk.add(lineId);
      } catch (err) {
        console.warn('[Billing] Error deleting line:', lineId, err);
      }
    }
    // TASK-9755 (COD Blast + Code Reviewer) — "Add back" must be idempotent:
    // another tab (or a second click before Save) may already have restored the
    // shift. Re-read this ticket's EMPLOYEE lines and drop a restored line whose
    // clockEntryId is already there, instead of minting a duplicate.
    if (ReviewState.employeeLines.some(l => l && l._restoredFromClock && l._isNew)) {
      try {
        const _freshEmp = await getDocs(query(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'), where('type', '==', 'EMPLOYEE')));
        const _haveDocs = new Map();
        _freshEmp.docs.forEach(d => { const ce = d.data()?.clockEntryId; if (ce && !_haveDocs.has(String(ce))) _haveDocs.set(String(ce), { id: d.id, ...d.data() }); });
        const _dupes = ReviewState.employeeLines.filter(l => l && l._restoredFromClock && l._isNew && _haveDocs.has(String(l.clockEntryId)));
        if (_dupes.length) {
          // Swap the staged copy for the line already on the server (not drop it),
          // so the header's employeeCount / totalHours below still count the worker.
          ReviewState.employeeLines = ReviewState.employeeLines.map(l => {
            if (!_dupes.includes(l)) return l;
            const keep = { ..._haveDocs.get(String(l.clockEntryId)), _localId: l._localId };
            _stampReviewLineBaseline(keep);
            return keep;
          });
          ReviewState.crewRestored = (ReviewState.crewRestored || []).filter(r => !_dupes.some(d => String(d.clockEntryId) === r.entryId));
        }
      } catch (_) { /* best-effort: the server EMPLOYEE dedup (personId) is the backstop */ }
    }

    // TASK-9436 (COD r3) — a PERMIT line the biller did NOT touch takes its money
    // from the server, not from the open-time copy. The server may have applied
    // the customer's markup while this modal was open; writing the stale copy
    // back would put the raw fee over a marked-up line (and the header total
    // below would be computed from it). Re-read before the line writes AND the
    // totals, so neither can be stale. Edited lines keep the biller's values.
    for (const _pl of ReviewState.permitLines) {
      if (!_pl || !_pl.id || _pl._isNew || _pl._permitMoneyEdited) continue;
      try {
        const _fs = await getDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', _pl.id));
        if (!_fs.exists()) continue;
        const _fd = _fs.data() || {};
        for (const k of ['lineAmount', 'rate', 'quantity', 'baseRate', 'baseCost', 'upchargePercent', 'upchargeSource', 'upchargeResolvedBy', 'upchargeResolvedAt', 'upchargeLineItemId']) {
          if (_fd[k] !== undefined) _pl[k] = _fd[k]; else delete _pl[k];
        }
        _pl._baseCost = _fd.baseCost || _fd.lineAmount || 0;
        _pl._upchargePercent = _fd.upchargePercent || 0;
        _pl._loadedUpchargePercent = (_fd.upchargePercent === undefined || _fd.upchargePercent === null) ? null : (Number(_fd.upchargePercent) || 0);
        // TASK-1406 — the server's money is not the biller's edit; a description edit still is.
        if (_pl._auditBase) _auditRebase(_pl, ['rate', 'quantity', 'lineAmount']);
      } catch (_) { /* best-effort; the server reconcile pass is the backstop */ }
    }

    // 3. Update/create editable lines (preserve order via sortOrder)
    const allLines = [
      ...ReviewState.employeeLines,
      ...ReviewState.equipmentLines,
      ...ReviewState.permitLines,
      ...ReviewState.downtimeLines,
      ...ReviewState.standbyLines,
      ...ReviewState.miscLines
    ];

    for (let i = 0; i < allLines.length; i++) {
      const line = allLines[i];
      // Build clean data (remove internal fields)
      const data = {};
      for (const [k, v] of Object.entries(line)) {
        if (k.startsWith('_') || k === 'id') continue;
        if (v !== undefined && v !== null) data[k] = v;
      }
      // TASK-9667 (COD Code Reviewer) — the invoice-hide REQUEST is born with a
      // new line only. Writing it back from this modal's open-time copy could
      // resurrect a marker the server already consumed (e.g. while the setting
      // was off) and hide an existing line retroactively.
      if (!line._isNew) delete data.invoiceHidePending;
      // TASK-9665 — same for the server-pricing request: a biller's Review save
      // is the biller's own price, never a re-read of the Price Book.
      if (!line._isNew) delete data.priceBookRatePending;
      // TASK-9646 S5 — same for the rate-authority request (`ratePending`): a
      // biller's save is the biller's price. (The server keeps a non-zero rate
      // under a stale marker anyway; this stops the marker riding along.)
      if (!line._isNew) delete data.ratePending;
      // TASK-4673 — a money editor's explicit price-book pick re-stamped the
      // rounding decision (onReviewEquipmentRateSelect, canEditMoney-gated). The
      // loop above already copied it; stamp the save time. A line that pick did
      // not touch carries its stored stamp through UNCHANGED (rules diff-based).
      if (line._reviewRoundingStamp === true && data.roundingProvenance) {
        data.roundingProvenance = { ...data.roundingProvenance, at: Timestamp.now() };
      }
      data.sortOrder = i;
      // TASK-1165 S3/S2 — clear the backfill's "unpriced equipment" review flag
      // once the biller has priced the line (via the badge's one-click fix OR a
      // manual rate edit). Null = cleared; the server flaggers treat a falsy
      // flag as unflagged and would only re-flag if the mixed-rate condition
      // itself recurred. (_billingReviewFlag is underscore-prefixed so the
      // clean-copy loop above never writes it back — this is the ONLY place a
      // review save touches it.)
      if (line.type === 'EQUIPMENT' && line.id && !line._isNew
          && line._billingReviewFlag?.reason === 'unpriced_equipment_same_asset'
          && (Number(line.rate) || 0) > 0) {
        data._billingReviewFlag = null;
      }
      // TASK-1674 — same treatment for a GUESSED equipment rate: once a human
      // has confirmed it ("Rate is correct") or changed the number away from
      // what the matcher would have guessed, the stored flag is done. Without
      // this the badge would be derived-clear but the doc would stay flagged,
      // which is how a "fixed" line goes on nagging every reader forever.
      if (line.type === 'EQUIPMENT' && line.id && !line._isNew
          && line._billingReviewFlag?.reason === 'equipment_rate_guessed'
          && (line.rateGuessAcknowledged === true
            || Math.abs((Number(line._billingReviewFlag.rate) || 0) - (Number(line.rate) || 0)) > 0.005)) {
        data._billingReviewFlag = null;
      }

      if (line._isNew) {
        // TASK-9258 (COD round 3, Code Reviewer) — a line the biller ADDED is a
        // human decision. `_isNew` is stripped by the clean-copy loop above, so
        // without this the same line would read as template-placed on reopen.
        if (line.type === 'EQUIPMENT' && line.assetType === 'asset' && data.templateQtyAcknowledged !== true) {
          data.templateQtyAcknowledged = true;
          data.templateQtyAcknowledgedBy = window.AppState?.user?.email || 'unknown';
          data.templateQtyAcknowledgedAt = Timestamp.now();
        }
        // New line — addDoc
        data.createdAt = Timestamp.now();
        if (line._restoredFromClock && line.clockEntryId) {
          // TASK-9755 (COD r3) — a restored crew line gets a DETERMINISTIC id per
          // clock entry, so two tabs restoring the same shift write one doc, not two.
          await setDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', `crew_${String(line.clockEntryId).replace(/[^A-Za-z0-9_-]/g, '')}`), data);
        } else {
          await addDoc(collection(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines'), data);
        }
      } else if (line.id) {
        // Existing line — updateDoc
        await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', line.id), data);
      }
    }
    // TASK-1406 — every line write above landed (any throw skips this); diff now.
    let _lineAudit = null;
    try {
      _lineAudit = _ticketLineAuditRows(allLines);
      _lineAudit.docsRemoved = (ReviewState.ticket.documents || []).filter(d => ReviewState.deletedDocIds.includes(d.id)).map(d => d.name || d.fileName || d.id);
    } catch (_) { /* audit is best-effort */ }

    // 3b. Deduct inventory for newly added quantity-tracked asset lines
    const newAssetLines = ReviewState.equipmentLines.filter(l => l._isNew && l.assetType === 'asset' && l.assetId && l.sourceType !== 'safetyEquipment'); // TASK-9725 — a billed PPE line never moves stock (the signed issue did)
    // TASK-10067b — collect first, then ONE transaction (dispatch.js
    // _deductConsumablesTx via window.*) re-reads each asset and deducts from the
    // STORED count: the old per-line `quantity: <cached> - qty` write put back
    // units an org transfer had moved out. Deduct + ledger row land together;
    // an item deleted / now stock-tracked / no longer consumable is skipped and named.
    const _reviewConsumableAdds = [];
    const _reviewRunQty = new Map(); // two new lines for one asset chain off the cached count
    for (const line of newAssetLines) {
      const asset = ReviewState.assets.find(a => a.id === line.assetId);
      // TASK-9720 — a stock-tracked item is counted by the server ledger only
      // (rules refuse a client quantity write on it); the ticket does not move it (S8).
      if (!asset || !asset.consumable || asset.stockTracked) continue;
      const qty = parseInt(line.quantity) || 1;
      const prevQty = _reviewRunQty.has(asset.id) ? _reviewRunQty.get(asset.id) : (asset.quantity || 0);
      const newQty = prevQty - qty;
      _reviewRunQty.set(asset.id, newQty);
      if (newQty < 0) {
        window.showToast?.(`Warning: ${asset.name || asset.partNumber} inventory is now negative (${newQty})`, 'error');
      }
      _reviewConsumableAdds.push({ assetId: asset.id, asset, qty, prevQty, newQty });
    }
    if (_reviewConsumableAdds.length > 0) {
      try {
        if (typeof window._deductConsumablesTx !== 'function') throw new Error('inventory deduct helper not loaded');
        await window._deductConsumablesTx(tenant, _reviewConsumableAdds, (it) => ({
          assetId: it.assetId,
          assetName: it.asset.name || it.asset.partNumber || '',
          partNumber: it.asset.partNumber || '',
          type: 'ticket',
          quantity: -it.qty,
          // TASK-9575 slice D — no unitCost / totalCost on a ledger row: every driver session reads this collection; cost lives in assetCosts (TASK-9730 owns the real cost stamp).
          ticketId,
          ticketNumber: ReviewState.ticket?.ticketNumber || '',
          note: `Deducted via ticket #${ReviewState.ticket?.ticketNumber || ticketId}`,
          issuedBy: window.AppState?.user?.uid || 'unknown',
          createdAt: Timestamp.now()
        }));
      } catch (err) {
        console.error('[Billing] Error deducting asset inventory:', err);
        const _names = [...new Set(_reviewConsumableAdds.map(it => it.asset.name || it.asset.partNumber || 'asset'))].join(', ');
        window.logError?.(err, { module: 'billing', operation: 'INVENTORY_DEDUCTION', assetIds: _reviewConsumableAdds.map(it => it.assetId), ticketId });
        window.showToast?.(err?.code === 'unavailable'
          ? `No connection. The ticket saved, but the inventory deduct for ${_names} could not be confirmed. Check the count in Roster before changing it.` // TASK-10067 item 2 (COD r1): a lost response may have committed — never invite a second deduct
          : `Warning: Failed to deduct inventory for ${_names}`, 'error', null, 9000); // COD r1 UX: long enough to read which items to fix
      }
    }

    // 4. Handle documents: upload pending, delete removed, persist to ticket
    // Filter out deleted docs from existing
    let finalDocs = ReviewState.documents.filter(d => !ReviewState.deletedDocIds.includes(d.id));

    // Upload pending doc files
    // TASK-9958 — kept so the FILE_ADDED rows fire only after the ticket write
    // below commits (a file that never landed on the ticket is not "added").
    let _docsAddedThisSave = [];
    if (ReviewState.pendingDocFiles.length > 0) {
      const uploaded = await uploadReviewDocFiles(tenant, ticketId);
      finalDocs = [...finalDocs, ...uploaded];
      _docsAddedThisSave = uploaded;
    }

    // Delete Storage files for removed upload docs. Bridged copies (job_permit /
    // job_photo / clock_photo) are skipped — the file belongs to the job or the
    // clock entry, not this ticket (TASK-9355 S0: job_photo used to fall through
    // here and the job lost its photo).
    for (const docId of ReviewState.deletedDocIds) {
      const originalDoc = (ReviewState.ticket.documents || []).find(d => d.id === docId);
      if (originalDoc && !_isBridgedReviewDoc(originalDoc)) {
        try {
          if (originalDoc.fullPath) await deleteObject(ref(storage, originalDoc.fullPath));
          if (originalDoc.thumbPath) await deleteObject(ref(storage, originalDoc.thumbPath));
        } catch (err) {
          console.warn('[Billing] Error deleting doc file:', docId, err);
        }
      }
    }

    // Save clean doc objects (strip internal fields)
    headerUpdate.documents = finalDocs.map(d => {
      const clean = {};
      for (const [k, v] of Object.entries(d)) {
        if (!k.startsWith('_') && v !== undefined && v !== null) clean[k] = v;
      }
      return clean;
    });

    // 4b. TASK-9440 — refresh the fuel surcharge line's AMOUNT at its stamped %
    // so the line and the header written below agree the moment this save lands
    // (an invoice cut seconds later must not read a stale surcharge). Never
    // creates or deletes a surcharge line and never changes its % — the server
    // (onTicketLineWrite) owns that and re-checks this write. Best-effort: a
    // failure leaves the server pass to converge, and is logged.
    {
      const _fuelSave = _reviewFuelSurchargeView();
      if (_fuelSave && _fuelSave.set && !_fuelSave.set.isNew) {
        try {
          await setDoc(doc(db, 'tenants', tenant, 'tickets', ticketId, 'ticketLines', _fuelSave.set.id),
            { ..._fuelSave.set.data, updatedAt: Timestamp.now() }, { merge: true });
        } catch (err) {
          // COD r1 (Code Reviewer): the header below must not carry an amount
          // the line does not. From here the Review totals read the STORED
          // surcharge, so the header equals the stored line set; the
          // onTicketLineWrite pass (fired by the line writes above) re-derives it.
          ReviewState._fuelRefreshFailed = true;
          console.warn('[Billing] TASK-9440 fuel surcharge line refresh failed (header uses the stored amount; server will recompute):', err?.message || err);
        }
      }
    }

    // 5. Recalculate grand total + tax + employee count and update ticket
    const _subtotal = calcReviewSubtotal();
    const _taxResult = calcReviewTax();
    const grandTotal = _subtotal + _taxResult.taxAmount;
    headerUpdate.subtotal = parseFloat(_subtotal.toFixed(2));
    headerUpdate.grandTotal = parseFloat(grandTotal.toFixed(2));
    headerUpdate.totalHours = parseFloat(ReviewState.employeeLines.reduce((sum, l) => sum + (parseFloat(l.hours) || 0), 0).toFixed(2));
    headerUpdate.employeeCount = ReviewState.employeeLines.filter(l => l.personId).length;
    headerUpdate.taxEnabled = ReviewState.taxEnabled;
    headerUpdate.taxRate = ReviewState.taxRate;
    headerUpdate.taxCategories = ReviewState.taxCategories;
    headerUpdate.taxableSubtotal = parseFloat(_taxResult.taxableSubtotal.toFixed(2));
    headerUpdate.taxAmount = parseFloat(_taxResult.taxAmount.toFixed(2));

    // TASK-878 — stamp WHO flipped this ticket draft→ready, FIRST transition only.
    // Guarded on the ORIGINAL status being 'draft' (from the open-time snapshot) so
    // later edits to an already-ready ticket never overwrite the original approver.
    // Name is server-loaded identity (never a user input); uid is verified against
    // request.auth.uid in firestore.rules to block forging another person's id.
    if (headerUpdate.status === 'ready' && ReviewState._originalSnapshot?.status === 'draft') {
      const _readyByName = _resolveReadyByName();
      if (_readyByName) headerUpdate.readyByName = _readyByName;
      headerUpdate.readyByUid = window.AppState?.user?.uid || null;
      headerUpdate.readyAt = Timestamp.now();
    }
    // TASK-1343 — the reverse transition: un-readying a ticket clears the
    // approver stamp in the SAME write. A draft ticket carrying someone else's
    // stamp is the trap that made #100172 unsaveable for everyone.
    if (headerUpdate.status === 'draft' && ReviewState._originalSnapshot?.status === 'ready') {
      headerUpdate.readyByUid = null;
      headerUpdate.readyByName = null;
      headerUpdate.readyAt = null;
    }

    // TASK-4662 — 🔴 STALENESS IS STAMPED HERE AND ONLY HERE, never derived.
    // This function rewrites header, lines, docs and totals — everything the
    // customer can see on the copy they were emailed. toggleDriverEditLock also
    // bumps updatedAt without changing anything customer-visible, so an
    // `updatedAt > ticketEmailSentAt` comparison would false-flag every lock;
    // that is the exact trap this stamp exists to avoid.
    //
    // Only stamped when the ticket has ALREADY been emailed — absent means
    // "never sent", and there is no backfill. On the next (re)send the callable
    // clears it with the admin SDK.
    // Keyed on the DERIVED LATEST (ticketSendLastAt), not on ticketEmailSentAt:
    // a hand-logged send sets the former and never the latter, so keying on the
    // app-send field alone would mean a ticket Lacey emailed from Gmail could be
    // edited afterwards and never flip to "Edited since sent" — silently telling
    // her the customer holds the current copy when they do not. ticketEmailSentAt
    // stays in the OR for the (impossible today, cheap tomorrow) case of an app
    // send with no derived field.
    if (ReviewState.ticket?.ticketSendLastAt || ReviewState.ticket?.ticketEmailSentAt) {
      headerUpdate.ticketEmailDirtyAt = Timestamp.now();
    }

    // TASK-9755 — the shifts a biller ruled "not on this ticket" (removed a
    // clocked crew line, or "Not on this ticket" on the banner). An "Add back"
    // takes the id out again. Written only when it moved, so an untouched save
    // never invents the field.
    // Written as DELTAS (arrayUnion in the header write, arrayRemove right after)
    // so a ruling made in another tab meanwhile is never overwritten (COD r2).
    let _crewRmRemoved = [];
    {
      const _crewRmBefore = new Set(Array.isArray(ReviewState.ticket?.crewRemovedEntryIds) ? ReviewState.ticket.crewRemovedEntryIds.map(String) : []);
      const _crewRmAfter = new Set((ReviewState.crewRemovedEntryIds || []).map(String));
      const _crewRmAdded = [..._crewRmAfter].filter(id => !_crewRmBefore.has(id));
      _crewRmRemoved = [..._crewRmBefore].filter(id => !_crewRmAfter.has(id));
      if (_crewRmAdded.length) headerUpdate.crewRemovedEntryIds = arrayUnion(..._crewRmAdded);
    }
    const _crewAuditSnap = {
      removedLines: (ReviewState.removedLinesAudit || []).filter(r => _deletedOk.has(r.lineId)),
      restored: (ReviewState.crewRestored || []).slice(),
      ruledOff: (ReviewState.crewRemovedDecisions || []).slice(),
    };

    // integ/2114 (COD 🔴 data loss, dispatcher 10/23) — a notes edit is written
    // INSIDE a transaction that re-reads the stored notes. If a driver (or
    // anyone) changed them after Review opened, the biller's edit is MERGED in
    // (ClockModule.mergeInvoiceNoteText, the same merge the clock-out uses)
    // instead of overwriting the new line. Unchanged since open = the edit is
    // written as-is (Billing may still clear notes outright).
    const _ticketRef = doc(db, 'tenants', tenant, 'tickets', ticketId);
    if (headerUpdate.notes !== undefined || headerUpdate.workPerformed !== undefined) {
      const _edit = String(headerUpdate.notes !== undefined ? headerUpdate.notes : headerUpdate.workPerformed);
      let _notesResult = null;
      await runTransaction(db, async (tx) => {
        const _snap = await tx.get(_ticketRef);
        const _d = _snap.exists() ? (_snap.data() || {}) : {};
        _notesResult = _reviewNotesForSave({
          storedNotes: _d.notes, storedWorkPerformed: _d.workPerformed,
          open: ReviewState._openNotesText, edit: _edit,
          merge: window.ClockModule?.mergeInvoiceNoteText,
        });
        headerUpdate.notes = _notesResult.text;
        headerUpdate.workPerformed = _notesResult.text;
        tx.update(_ticketRef, headerUpdate);
      });
      if (_notesResult?.merged) {
        ReviewState._openNotesText = _notesResult.text;
        window.showToast?.('Someone added to the work notes while you had this ticket open. Their note was kept alongside your edit.', 'info');
      }
    } else {
      await updateDoc(_ticketRef, headerUpdate);
    }
    if (_docsAddedThisSave.length) {
      try {
        const _uid = auth?.currentUser?.uid || window.AppState?.user?.uid || '';
        const _tno = ReviewState.ticket?.ticketNo || ReviewState.ticket?.ticketNumber || undefined;
        for (const d of _docsAddedThisSave) {
          window.logAudit?.('ticket', 'FILE_ADDED', fileAddedAuditPayload({
            entry: d, target: 'ticket_documents', via: 'ticket_edit', ticketId, ticketNo: _tno,
            jobId: ReviewState.ticket?.jobId || undefined, uid: _uid,
          }));
        }
      } catch (_) { /* audit is best-effort, like ticket_edit */ }
    }
    if (_crewRmRemoved.length) {
      try { await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), { crewRemovedEntryIds: arrayRemove(..._crewRmRemoved) }); }
      catch (e) { console.warn('[Billing] crew ruling undo failed (non-blocking):', e?.message || e); }
    }

    // Audit log — track what changed on this ticket
    try {
      const changes = {};
      const original = ReviewState._originalSnapshot || {};
      // TASK-1343 — readyBy* joins the tracked set: the approver stamp can now
      // be cleared/re-stamped, so who approved (and who un-approved) has to
      // leave a trail instead of just silently changing.
      const trackFields = ['jobId', 'jobName', 'customerName', 'customerId', 'poNumber', 'vendorNumber', 'wellName', 'wellNumber', 'afeNumber', 'workDate', 'date', 'status', 'grandTotal', 'notes', 'readyByUid', 'readyByName'];
      for (const field of trackFields) {
        const oldVal = original[field];
        const newVal = headerUpdate[field];
        if (newVal !== undefined && String(newVal) !== String(oldVal)) {
          changes[field] = { from: oldVal ?? null, to: newVal };
        }
      }
      // TASK-846-B — customSectionData is a nested object; the String() compare
      // above can't diff it. JSON-compare against the open-time snapshot and log
      // the full before/after map like any other header-field change (this same
      // snapshot also powers the undo toast below).
      if (headerUpdate.customSectionData !== undefined) {
        const _csdFrom = original.customSectionData ?? null;
        if (JSON.stringify(_csdFrom) !== JSON.stringify(headerUpdate.customSectionData)) {
          changes.customSectionData = { from: _csdFrom, to: headerUpdate.customSectionData };
        }
      }
      // TASK-9436 — a permit markup is a money-governing field: log every line
      // whose % moved (old + new), not just the grand total it produced.
      const _permitMk = _permitMarkupAuditChanges(ReviewState.permitLines);
      if (_permitMk.length > 0) changes.permitMarkup = _permitMk;
      // TASK-4673 — the rounding stamp is money-governing too: every line a
      // biller's price-book pick re-stamped, old + new.
      const _rpChanges = (ReviewState.equipmentLines || [])
        .filter(l => l && l._reviewRoundingStamp === true)
        .map(l => ({
          lineId: l.id || null,
          asset: l.assetName || null,
          from: l._roundingProvenanceBefore ?? null,
          to: l.roundingProvenance ? { round: l.roundingProvenance.round === true, lineItemId: l.roundingProvenance.lineItemId || null, by: l.roundingProvenance.by || null } : null,
        }));
      if (_rpChanges.length > 0) changes.roundingProvenance = _rpChanges;
      if (Object.keys(changes).length > 0) {
        // TASK-1495 — shared writer, not a direct addDoc. This is the 2nd-busiest
        // action in the whole log (~820 rows), so it is also the one that would
        // hurt most to lose: logAudit stamps timestamp/user/category/action and
        // strips undefined first. `changes` is spread through untouched (the
        // diff loop above already coerces every `from` to `?? null`).
        await window.logAudit?.('ticket', 'ticket_edit', {
          type: 'ticket_edit',
          ticketId,
          ticketNumber: ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '',
          changes,
          editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
          // TASK-10036 — the removal warning the user saw and accepted, verbatim.
          ...(_confirmedRemovals.length ? { confirmedRemovals: _confirmedRemovals } : {}),
        });
      }
    } catch (auditErr) {
      console.warn('[Billing] Audit log failed (non-blocking):', auditErr);
    }
    // TASK-9755 — line removals / crew restores from Review leave a trail.
    // #100852's two crew lines were deleted by this save path with NO row; the
    // only evidence left was a point-in-time read. Non-blocking, like the above.
    try {
      const _who = window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown';
      const _tNo = ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '';
      if (_crewAuditSnap.removedLines.length > 0) {
        await window.logAudit?.('ticket', 'ticket_lines_removed', {
          type: 'ticket_lines_removed', ticketId, ticketNumber: _tNo, lines: _crewAuditSnap.removedLines, editedBy: _who,
          ...(_confirmedRemovals.length ? { confirmedRemovals: _confirmedRemovals } : {}), // TASK-10036
        });
      }
      if (_crewAuditSnap.restored.length > 0) {
        await window.logAudit?.('ticket', 'ticket_crew_restored', {
          type: 'ticket_crew_restored', ticketId, ticketNumber: _tNo, crew: _crewAuditSnap.restored, editedBy: _who,
        });
      }
      if (_crewAuditSnap.ruledOff.length > 0) {
        await window.logAudit?.('ticket', 'ticket_crew_ruled_off', {
          type: 'ticket_crew_ruled_off', ticketId, ticketNumber: _tNo, crew: _crewAuditSnap.ruledOff, editedBy: _who,
        });
      }
    } catch (auditErr) {
      console.warn('[Billing] crew-gap audit failed (non-blocking):', auditErr);
    }
    // TASK-1406 — line-level trail (hours, crew, equipment, rates, items, removed files).
    try {
      const _la = _lineAudit;
      if (_la && (_la.edited.length || _la.added.length || _la.docsRemoved.length)) {
        window.logAudit?.('ticket', 'ticket_line_edit', { // not awaited: a hung write must not hold the Saving spinner
          type: 'ticket_line_edit', ticketId, ticketNumber: ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '',
          lines: _la.edited, added: _la.added, docsRemoved: _la.docsRemoved,
          editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
        });
      }
    } catch (auditErr) {
      console.warn('[Billing] line audit failed (non-blocking):', auditErr);
    }

    // Immediately sync local cache so viewTicket shows fresh data
    // (onSnapshot fires asynchronously and may not have updated yet)
    const cachedTicket = BillingState.tickets.find(t => t.id === ticketId);
    if (cachedTicket) {
      for (const [key, val] of Object.entries(headerUpdate)) {
        if (key === 'crewRemovedEntryIds') continue; // TASK-9755 — a FieldValue sentinel; set below
        if (key === 'date' && val?.toDate) {
          cachedTicket.date = val.toDate();
        } else {
          cachedTicket[key] = val;
        }
      }
      // TASK-9755 — the local copy gets the resolved list, never the sentinel.
      cachedTicket.crewRemovedEntryIds = [...new Set((ReviewState.crewRemovedEntryIds || []).map(String))];
    }

    // Grand total change warning (non-blocking)
    const origTotal = ReviewState._originalSnapshot?.grandTotal;
    if (origTotal && origTotal > 0) {
      const newTotal = headerUpdate.grandTotal;
      const pctChange = Math.abs((newTotal - origTotal) / origTotal) * 100;
      if (pctChange > 15) {
        const dir = newTotal > origTotal ? 'increased' : 'decreased';
        window.showToast?.(`Total ${dir} ${pctChange.toFixed(0)}% ($${origTotal.toFixed(2)} → $${newTotal.toFixed(2)})`, 'info');
      }
    }

    // Build undo data from the audit changes we just logged
    const undoSnapshot = { ...ReviewState._originalSnapshot };
    const undoTicketId = ticketId;
    const undoTenant = tenant;
    const undoTicketNo = ReviewState.ticket?.ticketNumber || ReviewState.ticket?.ticketNo || '';
    const hadCsdEdits = ReviewState.csdDirty === true;
    const hasChanges = Object.keys(ReviewState.headerEdits).length > 0 || hadCsdEdits;
    const csdBeforeStamp = ReviewState._originalSnapshot?.derivedLinesReconciledAt ?? null;
    delete undoSnapshot.derivedLinesReconciledAt; // TASK-9735 — server-owned, never reverted

    // TASK-9735 (Lacey) — Save used to close this modal and open the read-only
    // view: "it kicks you out". Now the modal STAYS on this ticket and is
    // re-hydrated from storage by _saveReviewTicketGuarded AFTER everything
    // below (shift notes, toasts) has run. Only a save that left the ticket
    // locked (approved / invoiced / archived) keeps the old close + view.
    if (isReviewReadOnly({ ...(ReviewState.ticket || {}), ...headerUpdate })) {
      document.getElementById('modal-review-ticket')?.classList.add('hidden');
      resetReviewState();
      viewTicket(undoTicketId);
    } else {
      _reviewSaveStayOn = undoTicketId;
    }

    // TASK-846-B (re-COD Code Reviewer): custom-section edits derive billing lines
    // via an ASYNC server trigger — a fast "Generate Invoice" from the reopened
    // view could fire before the derived FIELD_ITEM lines land. Poll briefly for
    // the reconciler's stamp and refresh the cached ticket + open view once it
    // advances (silently gives up after ~6s; review re-sum stays authoritative).
    if (hadCsdEdits) {
      (async () => {
        for (let i = 0; i < 6; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          try {
            const snap = await getDoc(doc(db, 'tenants', undoTenant, 'tickets', undoTicketId));
            if (!snap.exists()) return;
            const fresh = { id: snap.id, ...snap.data() };
            const stamp = fresh.derivedLinesReconciledAt;
            const advanced = stamp && (!csdBeforeStamp || (stamp.toMillis?.() ?? 0) !== (csdBeforeStamp.toMillis?.() ?? 0));
            if (advanced) {
              const idx = (BillingState.tickets || []).findIndex((t) => t.id === undoTicketId);
              if (idx >= 0) BillingState.tickets[idx] = _withTicketMoney(fresh); // TASK-9646 S3b
              // TASK-9735 — the review modal now stays open after Save: re-hydrate it
              // (never over unsaved typing; wait out a save/re-hydrate in flight).
              if (_reviewOpenOn(undoTicketId)) {
                if (_reviewSaveInFlight) continue;
                if (!ReviewState.dirty) await _rehydrateReviewTicket(undoTicketId);
                else window.showToast?.('The custom-section billing lines just updated on this ticket. Save or close, then reopen it so the totals include them.', 'warning');
                return;
              }
              // Only re-render if the user is still looking at this ticket.
              if (BillingState.selectedTicket?.id === undoTicketId
                  && !document.getElementById('modal-view-ticket')?.classList.contains('hidden')) {
                viewTicket(undoTicketId);
              }
              return;
            }
          } catch (_) { return; }
        }
      })();
    }

    // TASK-1287 — persist the driver shift notes snapshotted at the top of this
    // function (independent of the ticket write; best-effort, never throws). Runs
    // before the success toast so a note-save failure surfaces its own toast
    // alongside the ticket result.
    await pushEditedShiftNotesForReview(_editedShiftNotes);

    // Show undo toast (15s window)
    if (hasChanges) {
      window.showToast?.(`Ticket ${undoTicketNo ? '#' + undoTicketNo : ''} updated`, 'success', {
        label: 'Undo',
        callback: async () => {
          try {
            // Write back original values
            const revert = {};
            for (const [key, val] of Object.entries(undoSnapshot)) {
              if (val !== undefined && val !== null) revert[key] = val;
            }
            revert.updatedAt = Timestamp.now();
            await updateDoc(doc(db, 'tenants', undoTenant, 'tickets', undoTicketId), revert);
            // Log the undo as its own audit entry.
            // TASK-1495 — shared writer (stamps timestamp/user/category/action +
            // strips undefined). `undoTenant` is `tenant` captured above, which is
            // window.AppState.tenant, the same tenant logAudit resolves.
            await window.logAudit?.('ticket', 'ticket_undo', {
              type: 'ticket_undo',
              ticketId: undoTicketId,
              ticketNumber: undoTicketNo,
              revertedFields: Object.keys(undoSnapshot),
              editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
            });
            // Refresh the view
            const cachedTicket = BillingState.tickets.find(t => t.id === undoTicketId);
            if (cachedTicket) {
              for (const [key, val] of Object.entries(revert)) {
                cachedTicket[key] = val;
              }
            }
            // TASK-9735 — the review modal may still be open on this ticket.
            if (_reviewOpenOn(undoTicketId)) {
              if (!ReviewState.dirty && !_reviewSaveInFlight) {
                await _rehydrateReviewTicket(undoTicketId);
                window.showToast?.('Changes undone', 'success');
              } else {
                window.showToast?.('Changes undone. You have unsaved edits open, so the screen was not refreshed. Close and reopen the ticket to see the undone values.', 'warning');
              }
            } else {
              viewTicket(undoTicketId);
              window.showToast?.('Changes undone', 'success');
            }
          } catch (err) {
            console.error('[Billing] Undo failed:', err);
            window.showToast?.('Undo failed: ' + err.message, 'error');
          }
        }
      });
    } else {
      window.showToast?.('Ticket updated', 'success');
    }
    // TASK-9355 S5 — the ticket's new job is written; the ticked box moves the
    // crew's clock entries to it LAST (plan frozen at the top of this save), so
    // nothing after these network calls reads a Review that may have changed.
    _mceGo && await _mce.apply(_mceGo);

  } catch (error) {
    console.error('[Billing] Error saving review:', error);
    window.logError?.(error, { module: 'billing', operation: 'SAVE_REVIEW_TICKET', ticketId, ticketNo: ReviewState.ticket?.ticketNo });
    window.logOperation?.('billing', 'SAVE_REVIEW_TICKET', { status: 'error', ticketId, ticketNo: ReviewState.ticket?.ticketNo, error });
    window.showToast?.('Error saving ticket: ' + (error.message || 'Unknown error'), 'error');
  } finally {
    // resetReviewState may have replaced the object; clear on whatever is live.
    ReviewState._saving = false;
    if (btn && !_reviewSaveStayOn) { // TASK-9735 — staying: the re-hydrate owns the button
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">save</span> Save Ticket';
    }
  }
}

// ============================================
// TASK-9735 — SAVE STAYS ON THE TICKET
// ============================================
// Lacey (TITS, 2026-10-08): "when you are editing a ticket and you hit 'save
// ticket' it kicks you out of the modal, it should stay on the same screen with
// your saved changes." The footer Save (and window.BillingModule.saveReviewTicket)
// now runs through this guard:
//  - ONE save at a time. A double-click used to start two saves (the button is
//    only disabled after the gate dialogs), which could add new lines twice.
//  - No Save while the modal is still loading (half-loaded lines are not the ticket).
//  - On success the modal is RE-HYDRATED from storage via openReviewTicket(id,
//    {fresh:true}): real line ids (a 2nd Save updates, never re-creates),
//    deletedLineIds cleared, _originalSnapshot = the saved values (audit diff +
//    Undo baseline). Never patched in place — that is the duplicate-line path.
//  - Footer shows "Saved" + "Close" until the next edit (dirty setter below).
let _reviewSaveInFlight = false;
let _reviewSaveStayOn = null;

function _reviewOpenOn(ticketId) {
  const m = document.getElementById('modal-review-ticket');
  return !!(m && !m.classList.contains('hidden') && ReviewState.ticketId === ticketId);
}

let _reviewLinesLoadFailedFor = null;

// Success means the header, the lines AND the render all landed (COD Code
// Reviewer: openReviewTicket swallows a failed line read and a render throw, and
// a half-loaded modal must never read "Saved" or take another Save). On failure
// the save itself already stands, so fall back to the old close + read-only view.
async function _rehydrateReviewTicket(ticketId) {
  const body = document.getElementById('review-ticket-body');
  const top = body ? body.scrollTop : 0;
  await openReviewTicket(ticketId, { fresh: true });
  if (!_reviewOpenOn(ticketId)) return false; // closed / another ticket opened meanwhile
  const b2 = document.getElementById('review-ticket-body');
  const ok = _reviewLinesLoadFailedFor !== ticketId
    && !!b2 && !b2.querySelector('.rvw-load-state, .rvw-load-error');
  if (!ok) {
    _reviewLoadToken++;
    _clearReviewLoadWatchdog();
    document.getElementById('modal-review-ticket')?.classList.add('hidden');
    resetReviewState();
    viewTicket(ticketId);
    window.showToast?.("Saved, but the ticket couldn't be reloaded for editing. Showing the saved ticket — open it again to keep editing.", 'warning');
    return false;
  }
  if (top) b2.scrollTop = top;
  return true;
}

function _setReviewSavedUi() {
  const save = document.getElementById('btn-save-review');
  if (save) {
    save.disabled = false;
    save.innerHTML = '<span class="material-icons">check_circle</span> Saved';
    save.setAttribute('aria-label', 'Saved. Save Ticket again');
  }
  const cancel = document.querySelector('#review-ticket-footer .review-footer-cancel');
  if (cancel) cancel.textContent = 'Close';
  ReviewState._savedUi = true;
  _syncReviewSavedActions(true);
}

// TASK-9735 follow-up (Lacey, 10/19) — before 10/16 Save Ticket landed on the
// read-only view, where Generate Invoice and Billed elsewhere were one click
// away. Save now stays on the edit screen, so the "Saved" footer carries those
// two decisions itself. Shown ONLY while the footer reads Saved (the first edit
// removes them with the Saved state) and only for a ticket the cached list says
// can still be billed — the actions read that cache, so a ticket missing from
// it gets no buttons rather than a button that silently does nothing.
// Inserted as two plain buttons (no wrapper) so the phone footer grid, whose
// inner div is display:contents, lays them out as normal grid cells.
function _reviewSavedCanBill(t) {
  if (!t) return false;
  if (typeof isTicketInvoiced === 'function' && isTicketInvoiced(t)) return false;
  if (t.status === 'archived' || t.status === 'locked' || t.status === 'deleted') return false;
  if (t.writtenOff === true) return false;
  if (typeof isReviewReadOnly === 'function' && isReviewReadOnly(t)) return false;
  return true;
}

function _syncReviewSavedActions(show) {
  const footer = document.getElementById('review-ticket-footer');
  if (!footer || typeof footer.querySelectorAll !== 'function') return;
  footer.querySelectorAll('.review-saved-action').forEach((b) => b.remove());
  if (!show) return;
  const id = ReviewState.ticketId;
  const t = (typeof BillingState !== 'undefined' && Array.isArray(BillingState.tickets))
    ? BillingState.tickets.find((x) => x.id === id) : null;
  if (!_reviewSavedCanBill(t)) return;
  const save = document.getElementById('btn-save-review');
  const row = save && save.parentElement;
  if (!row) return;
  const tid = window.escJsAttr(id);
  const tpl = document.createElement('template');
  tpl.innerHTML =
    `<button type="button" class="btn review-saved-action review-saved-invoice" style="background:#2E7D32;color:#fff;border:none;" title="Create the DispatchDeck invoice for this ticket" onclick="window.BillingModule.reviewSavedInvoice('${tid}')">` +
      '<span class="material-icons">receipt</span> Invoice</button>' +
    `<button type="button" class="btn btn-secondary review-saved-action review-saved-billed-elsewhere" title="Already billed in QuickBooks or another system — no DispatchDeck invoice is created" onclick="window.BillingModule.reviewSavedBilledElsewhere('${tid}')">` +
      '<span class="material-icons">assignment_turned_in</span> Billed elsewhere</button>';
  row.insertBefore(tpl.content, save);
}

// Leave the saved edit screen for the read-only view of the same ticket — the
// exact place Save used to land before 10/16 — so cancelling the follow-on
// prompt leaves the biller on the ticket, not dumped on the list. Refuses when
// there is anything unsaved (the buttons are removed on the first edit; this is
// the guard for a stale click).
async function _leaveSavedReviewFor(ticketId) {
  if (!_reviewOpenOn(ticketId)) return false;
  if (ReviewState.dirty || ReviewState._saving || _reviewSaveInFlight || ReviewState._savedUi !== true) {
    window.showToast?.('Save your changes first, then choose Invoice or Billed elsewhere.', 'info');
    return false;
  }
  _reviewLoadToken++;
  _clearReviewLoadWatchdog();
  document.getElementById('modal-review-ticket')?.classList.add('hidden');
  resetReviewState();
  // AWAIT the view (COD r1): viewTicket runs the grandTotal / $0-rate self-heal,
  // which must land while the ticket is still unlocked. Before 10/16 nobody
  // could stamp until the view had loaded; this keeps that order.
  try {
    await viewTicket(ticketId);
    // viewTicket fires its grandTotal / $0-rate heal updateDocs without awaiting
    // them; after the stamp the locked-ticket rules would refuse them. Let every
    // queued write reach the server first (bounded: offline must not hang here).
    await Promise.race([waitForPendingWrites(db), new Promise((r) => setTimeout(r, 8000))]);
  } catch (e) {
    console.error('[Billing] TASK-9735 f/u: ticket view failed to load', e);
    window.showToast?.('The ticket could not be opened. Nothing was billed — open it again and retry.', 'error');
    return false;
  }
  return true;
}

let _reviewSavedActInFlight = false;
async function _reviewSavedAct(ticketId, act) {
  if (_moneyBlocked()) return;
  if (_reviewSavedActInFlight) return;
  _reviewSavedActInFlight = true;
  try {
    if (!(await _leaveSavedReviewFor(ticketId))) return;
    return await act(ticketId);
  } finally {
    _reviewSavedActInFlight = false;
  }
}
function reviewSavedInvoice(ticketId) { return _reviewSavedAct(ticketId, generateInvoice); }
function reviewSavedBilledElsewhere(ticketId) { return _reviewSavedAct(ticketId, markInvoicedExternally); }

function _clearReviewSavedUi(force) {
  if (!force && ReviewState._savedUi !== true) return;
  ReviewState._savedUi = false;
  const save = document.getElementById('btn-save-review');
  if (save && !ReviewState._saving) {
    save.innerHTML = '<span class="material-icons">save</span> Save Ticket';
    save.removeAttribute('aria-label');
  }
  const cancel = document.querySelector('#review-ticket-footer .review-footer-cancel');
  if (cancel) cancel.textContent = 'Cancel';
  _syncReviewSavedActions(false);
}

// `dirty` as an accessor: the first human edit after a save flips the footer
// back to Save Ticket / Cancel, without touching the ~50 sites that arm it.
{
  let _dirty = ReviewState.dirty === true;
  Object.defineProperty(ReviewState, 'dirty', {
    get() { return _dirty; },
    set(v) { _dirty = v; if (v) _clearReviewSavedUi(); },
    enumerable: true, configurable: true,
  });
}

async function _saveReviewTicketGuarded() {
  if (_reviewSaveInFlight) return;
  if (document.querySelector('#review-ticket-body .rvw-load-state')) {
    window.showToast?.('Still loading this ticket. Save again in a moment.', 'info');
    return;
  }
  _reviewSaveInFlight = true;
  _reviewSaveStayOn = null;
  try {
    await saveReviewTicket();
    const stayOn = _reviewSaveStayOn;
    if (stayOn && _reviewOpenOn(stayOn)) {
      if (await _rehydrateReviewTicket(stayOn)) _setReviewSavedUi();
    }
  } finally {
    _reviewSaveStayOn = null;
    _reviewSaveInFlight = false;
    const btn = document.getElementById('btn-save-review');
    if (btn && btn.disabled && !ReviewState._saving) {
      btn.disabled = false;
      if (ReviewState._savedUi !== true) btn.innerHTML = '<span class="material-icons">save</span> Save Ticket';
    }
  }
}

// ============================================
// REVIEW TICKET — DELETE
// ============================================
async function deleteReviewTicket() {
  const ticket = ReviewState.ticket;
  if (!ticket?.id) return;

  const ticketLabel = ticket.ticketNo ? `Ticket #${ticket.ticketNo}` : 'this ticket';

  // Block deletion of invoiced tickets.
  // TASK-1346 sweep — books-integrity gate: a ticket carrying an invoice number
  // is billed work, whatever the boolean says. Deleting it orphans the invoice.
  if (isTicketInvoiced(ticket)) {
    window.showToast?.('Cannot delete an invoiced ticket', 'error');
    return;
  }

  // TASK-1347 — read the RAW status off the loaded ticket, not ReviewState's copy.
  // renderReviewTicket() coerces any status outside ['draft','ready','archived',
  // 'locked'] to 'draft' and writes it back onto ReviewState.ticket, so a 'sent' or
  // 'invoiced' ticket reads as 'draft' here. Both the guard below and _prevStatus
  // would otherwise act on a fabricated status — and a restore would resurrect a
  // sent ticket as a draft.
  const rawStatus = BillingState.tickets?.find(t => t.id === ticket.id)?.status ?? ticket.status ?? null;

  // TASK-1347 (COD Code Reviewer ⚠️ + Domain) — align this path with the list
  // delete, which has always refused archived/locked/sent. This one refused only
  // `invoiced`, so an ARCHIVED ticket — which at TITS means "already invoiced in
  // QuickBooks" — could be soft-deleted from the Review modal even though the
  // ticket list blocks the identical action. The modal hides the footer Delete on
  // read-only tickets, but a hidden button is not a gate, and the rules lifecycle
  // arm permits the write for any dispatcher/billing member.
  if (rawStatus === 'archived' || rawStatus === 'locked' || rawStatus === 'sent') {
    window.showToast?.('Cannot delete archived or sent tickets', 'error');
    return;
  }

  // Block deletion of tickets with active clock entries
  if (BillingState.activeTicketIds?.has(ticket.id)) {
    window.showToast?.('Cannot delete — someone is still clocked in on this ticket', 'error');
    return;
  }

  // TASK-10036 (f) — same ask as the list delete: name the hours on the
  // ticket and require a reason when there are any.
  const _delAsk = await _askTicketDeleteReason(ticket, getTenant());
  if (!_delAsk) return;

  try {
    // TASK-1347 — getTenant() maps the demo tenant ('demo-tenant' → 'demo') the
    // way every other write in this file does; the raw BillingState.tenantId this
    // used to pass wrote to a tenant path that doesn't exist in demo.
    const tenantId = getTenant();
    // TASK-1347 — this delete path recorded NOTHING: no audit row, no actor, no
    // _prevStatus. It is why only 30 of 96 deleted tickets are attributable. It
    // now writes the same shape as the list-delete path above.
    const prevStatus = rawStatus;
    const _actor = _actorStamp();
    await updateDoc(doc(db, 'tenants', tenantId, 'tickets', ticket.id), {
      status: 'deleted',
      deletedAt: Timestamp.now(),
      deletedBy: _actor.uid,
      deletedByName: _actor.name,
      _prevStatus: prevStatus,
      ...(_delAsk.reason ? { deletedReason: _delAsk.reason } : {}), // TASK-10036 (f)
      updatedAt: Timestamp.now()
    });
    window.logAudit?.('ticket', 'DELETE', {
      ticketId: ticket.id,
      ticketNo: ticket.ticketNo ?? null,
      prevStatus,
      jobName: ticket.jobName ?? null,
      customerName: ticket.customerName ?? null,
      grandTotal: _auditAmount(ticket.grandTotal),
      via: 'review-modal',
      reason: _delAsk.reason || null, // TASK-10036 (f)
      hoursOnTicket: _delAsk.exposure.items.map(i => ({ label: i.label, hours: i.hours, type: i.type })),
      hoursReadFailed: _delAsk.exposure.readFailed === true
    });

    // Remove from local state
    BillingState.tickets = (BillingState.tickets || []).filter(t => t.id !== ticket.id);

    window.showToast?.(`${ticketLabel} deleted`, 'success');
    document.getElementById('modal-review-ticket')?.classList.add('hidden');
    resetReviewState();
    renderBillingView();
    _maybeReturnToClock(); // TASK-9411 — deleted from the time clock hop: go back there
  } catch (err) {
    console.error('[Billing] Delete ticket error:', err);
    window.showToast?.('Error deleting ticket', 'error');
  }
}

// ============================================
// REVIEW TICKET — CLOSE WITH GUARD
// ============================================
function closeReviewModal(fromUser) {
  // TASK-9411 — `fromUser` is passed ONLY by the modal's own X / Cancel / Close
  // buttons. Programmatic closes (Add Day → open the new ticket) must never
  // bounce the biller back to the time clock mid-flow.
  // `dirty` is armed ONLY by a human edit (TASK-9246 — the three self-heals in
  // openReviewTicket used to arm it too, which is what made this prompt fire on
  // a ticket nobody had touched). Wording and behaviour here are unchanged:
  // losing a dispatcher's real work is far worse than a spurious prompt, so the
  // guard on genuine edits stays exactly as it was.
  // The unsaved REPAIR, if the write failed. Not an accusation — a report. The
  // corrected hours are on screen while the stored lines still carry the old
  // ones, so closing walks away from money. COD (Edge Case Hunter) — this is
  // appended to the edit prompt rather than being an `else`: when the biller has
  // ALSO edited something, the generic prompt alone would hide the failed repair
  // behind a question about their own typing.
  const _failedHeals = ReviewState.healUnpersisted === 'error'
    ? _mergedSystemHeals().filter(h => h.hoursChanged)
    : [];
  // COD (Code Reviewer) — worded off what ACTUALLY moved. A rounding-only repair
  // is not a "$0 line", and claiming it was is the same false-prompt sin in a new
  // costume.
  const _zeroHealed = _failedHeals.filter(h => h.fromHours === 0).length;
  const _healWarn = _failedHeals.length === 0 ? '' : (
    ` We also recalculated hours on ${_failedHeals.length} line${_failedHeals.length === 1 ? '' : 's'}`
    + (_zeroHealed > 0 ? ` (${_zeroHealed} of them stored with 0 hours, so ${_zeroHealed === 1 ? 'it bills' : 'they bill'} $0)` : '')
    + ", but saving that failed — closing leaves the stored ticket as it was."
  );

  if (ReviewState.dirty) {
    if (!confirm('You have unsaved changes. Discard them?' + _healWarn)) return;
  } else if (_healWarn) {
    if (!confirm(_healWarn.trim())) return;
  }
  // TASK-1232 (3): invalidate any in-flight load + its watchdog timers so a
  // slow fetch can't repaint a modal the user already closed.
  _reviewLoadToken++;
  _clearReviewLoadWatchdog();
  const _closingId = ReviewState.ticketId; // TASK-9735
  document.getElementById('modal-review-ticket')?.classList.add('hidden');
  resetReviewState();
  // TASK-9735 — Save no longer hops to the read-only view, so a view left open
  // underneath may predate the save: repaint it on the way out.
  if (_closingId && BillingState.selectedTicket?.id === _closingId
      && !document.getElementById('modal-view-ticket')?.classList.contains('hidden')) {
    viewTicket(_closingId);
  }
  // Re-render the list now that the modal-open guard in renderBillingView no
  // longer blocks it — snapshots received mid-edit (e.g. a status change saved
  // from this modal) otherwise leave the row stale until a manual refresh.
  // TASK-9411 (COD round 2) — render FIRST, so a return hop the nav gate refuses
  // still leaves a live Billing list behind, never a blank screen.
  renderBillingView();
  // TASK-9411 — opened from an employee's time clock? Go back there.
  if (fromUser === true) _maybeReturnToClock();
}

// ============================================
// TASK-9411 — BACK TO THE TIME CLOCK AFTER A TICKET
// ============================================
// Jess (TITS office, 2026-09-28): opening a ticket from Workforce → employee →
// time clock navigated to Billing (TASK-1364 has to — the ticket modals only
// exist inside the Billing view), and closing the ticket left her on the AR/AP
// list. She then re-walked Workforce → find the employee → page back to the
// pay period, every time. clock.js now hands a `returnTo` snapshot along with
// the deep link (employee, pay period, week, sub-tab, scroll); closing the
// ticket with its own X / Close / Cancel puts her back on it.
//
// Scope: ONLY a ticket session that started from the clock carries a return.
// A ticket opened from the Billing list behaves exactly as before. The return
// fires only on a USER close with no ticket modal left open (TASK-9735: Save
// keeps the review modal open, so the return waits for its Close).
function _anyTicketModalOpen() {
  return ['modal-review-ticket', 'modal-view-ticket'].some((id) => {
    const m = document.getElementById(id);
    return !!(m && !m.classList.contains('hidden'));
  });
}

function _clockReturnLabel(r) {
  // First name only — COD UX: the full name crowds the modal header on a phone.
  const first = r && r.personName ? String(r.personName).trim().split(/\s+/)[0] : '';
  return first ? `Back to ${first}'s time clock` : 'Back to Workforce';
}

// Adds / removes a visible "Back to …" button in both ticket modal headers so
// the way back is discoverable, not just a side effect of the X.
function _syncClockReturnButtons() {
  const r = BillingState._clockReturn;
  [['modal-review-ticket', 'window.BillingModule.closeReviewModal(true)'],
    ['modal-view-ticket', 'window.BillingModule.closeViewTicketModal()']].forEach(([id, onclick]) => {
    const header = document.querySelector(`#${id} .modal-header`);
    if (!header) return;
    let btn = header.querySelector('.clock-return-btn');
    if (!r) { btn?.remove(); return; }
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-secondary btn-sm clock-return-btn';
      btn.style.cssText = 'margin-left:auto;margin-right:8px;display:inline-flex;align-items:center;gap:4px;white-space:nowrap;';
      btn.setAttribute('onclick', onclick);
      const closeBtn = header.querySelector('.btn-icon');
      header.insertBefore(btn, closeBtn || null);
    }
    btn.innerHTML = `<span class="material-icons" style="font-size:16px;">arrow_back</span> ${escapeHtml(_clockReturnLabel(r))}`;
  });
}

function _maybeReturnToClock() {
  const r = BillingState._clockReturn;
  if (!r) return false;
  if (_anyTicketModalOpen()) return false;
  if (typeof window.navigateToView !== 'function') return false;
  // COD round 1 (Edge Case Hunter) — do NOT clear the return target here. The
  // nav gate can refuse (a cancelled PIN challenge leaves Billing mounted); the
  // Back button must still be there. A successful hop unmounts Billing, and
  // initBilling resets _clockReturn on the next mount anyway.
  window._clockReturnRestore = { ...r, at: Date.now() };
  window.navigateToView(r.view === 'workforce' ? 'workforce' : 'clock');
  return true;
}

// The view modal's own X / Close. Programmatic closeModal('view-ticket') calls
// (view → edit transitions, archive, pay) deliberately stay on closeModal.
function closeViewTicketModal() {
  closeModal('view-ticket');
  _maybeReturnToClock();
}

// ============================================
// QUICKBOOKS CSV EXPORT
// ============================================

function csvEscape(val) {
  if (val == null) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function downloadCsv(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDateMMDDYYYY(dateVal) {
  if (!dateVal) return '';
  const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function addDays(dateVal, days) {
  const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

async function exportPaymentIIF(ticketId) {
  const ticket = _emitTicketMoney(BillingState.tickets.find(t => t.id === ticketId)); // TASK-9646 S3b emitted money
  if (!ticket) {
    showToast('Ticket not found', 'error');
    return;
  }
  if (!ticket.paid) {
    showToast('Ticket is not marked as paid', 'error');
    return;
  }
  // TASK-1346 (3rd-COD Blast ❌) — this writes a QB-importable PAYMENT record keyed
  // on ticket.invoiceNumber. For an externally-billed ticket that number is
  // `EXT-<ref>`, which matches no invoice in QuickBooks, so the payment imports
  // against nothing (or against the wrong doc) while the real invoice stays open.
  // Record the payment in the system that cut the invoice.
  if (isBilledExternally(ticket)) {
    showToast('This ticket was billed outside DispatchDeck — record the payment in that system instead.', 'error');
    return;
  }
  // TASK-9654 — linked to a QuickBooks invoice with ITS OWN number: a payment
  // keyed on the DD INV- number would land on nothing there.
  if (hasQboInvoiceLink(ticket)) {
    showToast('This ticket is linked to a QuickBooks invoice — record the payment against that invoice in QuickBooks.', 'error');
    return;
  }

  const T = '\t';
  const customer = getQBCustomerName(ticket);
  const invoiceNo = iifEscape(ticket.invoiceNumber || ticket.ticketNo || ticket.id);
  const amount = parseFloat(ticket.grandTotal || 0);
  const paidDate = formatDateMMDDYYYY(ticket.paidAt || new Date());
  const settings = window.AppState?.settings || {};
  const arAccount = iifEscape(settings.qbAccountsReceivable || 'Accounts Receivable');
  const depositAccount = iifEscape(settings.qbDepositAccount || 'Undeposited Funds');

  const lines = [];
  lines.push(['!TRNS', 'TRNSID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO'].join(T));
  lines.push(['!SPL', 'SPLID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO'].join(T));
  lines.push('!ENDTRNS');
  lines.push([
    'TRNS', '', 'PAYMENT', paidDate, depositAccount, customer,
    amount.toFixed(2), invoiceNo, 'Payment received'
  ].join(T));
  lines.push([
    'SPL', '', 'PAYMENT', paidDate, arAccount, customer,
    (-amount).toFixed(2), invoiceNo, 'Payment received'
  ].join(T));
  lines.push('ENDTRNS');

  downloadIIF(`${invoiceNo}-payment.iif`, lines.join('\n'));
  showToast('Payment IIF downloaded', 'success');
}

// `opts.visibleListDump` — called by exportFilteredTickets(), i.e. "give me a CSV of
// exactly what is on screen", NOT "build me a QuickBooks import".
//
// TASK-1346 (3rd-COD Code Reviewer ❌) vs TASK-1345 (binding mitigation i) pull in
// opposite directions here and only one function serves both callers:
//   • As a QB import artifact the CSV is invoice-shaped (InvoiceNo, Terms, DueDate)
//     and sits next to the .iif button, so externally-billed tickets must be dropped
//     or the customer gets billed twice.
//   • As the accountant's export, TASK-1345 made it a rule that the CSV contains
//     EXACTLY the on-screen set — silently dropping rows would re-create the
//     export-disagrees-with-the-screen bug that made "what is unbilled?" unanswerable.
// Split by INTENT, which the caller knows: the selection-bar export is someone
// hand-picking tickets to push into QuickBooks (guarded); the filtered export is a
// dump of the visible list (not guarded — and note it already includes drafts and
// unbilled tickets, so it was never a curated import set to begin with).
async function exportToQuickBooks(opts) {
  const ids = [...BillingState.selectedTicketIds];
  if (ids.length === 0) {
    showToast('Select tickets to export', 'error');
    return;
  }

  const _raw = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id) && t.status !== 'deleted').map(_emitTicketMoney); // TASK-9646 S3b emitted money
  const selectedTickets = (opts && opts.visibleListDump)
    ? _raw
    : await _dropExternallyBilledForQb(_raw, 'QuickBooks (CSV)');
  if (selectedTickets === null) return; // cancelled
  if (selectedTickets.length === 0) {
    showToast('No matching tickets found', 'error');
    return;
  }

  showToast('Preparing QB export...', 'info');

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const headers = ['InvoiceNo','Customer','InvoiceDate','DueDate','Terms','Item','ItemDescription','ItemQuantity','ItemRate','ItemAmount','ServiceDate','Memo'];
  const rows = [headers.map(csvEscape).join(',')];

  for (const ticket of selectedTickets) {
    const invoiceNo = ticket.invoiceNumber || ticket.ticketNo || ticket.id;
    const customer = ticket.customerName || '';
    const invoiceDate = formatDateMMDDYYYY(ticket.date);
    const dueDate = addDays(ticket.date, 30);
    const serviceDate = formatDateMMDDYYYY(ticket.workDate || ticket.date);
    const memoParts = [];
    if (ticket.poNumber) memoParts.push('PO: ' + ticket.poNumber);
    if (ticket.vendorNumber) memoParts.push('Vendor: ' + ticket.vendorNumber);
    if (ticket.wellName) memoParts.push('Well: ' + ticket.wellName + (ticket.wellNumber ? ' #' + ticket.wellNumber : ''));
    if (ticket.jobName || ticket.jobTitle) memoParts.push('Job: ' + (ticket.jobName || ticket.jobTitle));
    // TASK-1346 (4th-COD Domain ⚠️ + Adversary ⚠️, independently) — on the
    // visible-list path these rows are NOT dropped (TASK-1345 (i): the CSV must be
    // exactly what's on screen), so the warning has to live IN THE ARTIFACT. A
    // caller-intent split is invisible to the bookkeeper holding the file and to
    // the QuickBooks importer, which will happily create a new invoice under
    // `InvoiceNo=EXT-<ref>`. Lead the memo so it is unmissable in the spreadsheet.
    if (isBilledExternally(ticket)) memoParts.unshift('*** ALREADY BILLED ELSEWHERE — DO NOT IMPORT ***');
    const memo = memoParts.join(' | ');

    // Fetch ticketLines subcollection
    let ticketLines = [];
    try {
      const linesSnap = await getDocs(
        collection(db, 'tenants', tenant, 'tickets', ticket.id, 'ticketLines')
      );
      ticketLines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('[Billing] Error loading ticketLines for export:', err);
    }

    if (ticketLines.length === 0) {
      // Summary row for ticket with no lines
      const row = [
        invoiceNo, customer, invoiceDate, dueDate, 'Net 30',
        'Service', ticket.jobName || ticket.jobTitle || 'Service',
        '1', ticket.grandTotal || 0, ticket.grandTotal || 0,
        serviceDate, memo
      ];
      rows.push(row.map(csvEscape).join(','));
    } else {
      for (const line of ticketLines) {
        let item = '', description = '', qty = 0, rate = 0, amount = 0;

        switch (line.type) {
          case 'EMPLOYEE':
            item = line.description || line.itemCode || line.role || 'Labor';
            description = getEmployeeDisplayName(line);
            qty = line.hours || 0;
            rate = line.rate || 0;
            amount = line.lineAmount || (qty * rate);
            break;
          case 'EQUIPMENT': {
            const vTypeLabel = line.vehicleType ? (window.getTypeLabel?.(line.vehicleType, line.assetType === 'truck' ? 'truckTypes' : line.assetType === 'trailer' ? 'trailerTypes' : 'assetCategories') || line.vehicleType) : '';
            item = line.description || vTypeLabel || line.assetName || line.itemCode || 'Equipment';
            description = (line.assetName || 'Equipment') + (line.fleetNumber ? ' #' + line.fleetNumber : '');
            qty = line.hours || 0;
            rate = line.rate || 0;
            amount = line.lineAmount || (qty * rate);
            break;
          }
          case 'PERMIT':
            item = line.description || 'Permit';
            description = line.description || 'Permit';
            qty = 1;
            rate = line.lineAmount || line.baseCost || 0;
            amount = line.lineAmount || rate;
            break;
          case 'DOWNTIME':
          case 'STANDBY':
          case 'POTHOLE':
            item = line.description || line.type.charAt(0) + line.type.slice(1).toLowerCase();
            description = getEmployeeDisplayName(line);
            qty = line.hours || 0;
            rate = line.rate || 0;
            amount = line.lineAmount || (qty * rate);
            break;
          case 'FIELD_ITEM':
            item = line.description || line.itemCode || 'Field Item';
            description = line.description || 'Field Item';
            qty = line.quantity || 1;
            rate = line.rate || 0;
            amount = line.lineAmount || (qty * rate);
            break;
          case 'MISC':
            item = line.description || 'Miscellaneous';
            description = line.description || 'Miscellaneous';
            qty = line.quantity || 1;
            rate = line.rate || 0;
            amount = line.lineAmount || (qty * rate);
            break;
          case 'FUEL_SURCHARGE':
            // TASK-9440 — ONE fixed item (the words change every ticket, and an
            // item named after them would create a new QuickBooks item each time).
            item = getQBItemName('FUEL_SURCHARGE');
            description = line.description || 'Fuel Surcharge';
            qty = 1;
            amount = Number(line.lineAmount) || 0;
            rate = amount;
            break;
          default:
            item = line.description || line.itemCode || line.type || 'Other';
            description = (line.type === 'EMPLOYEE' ? getEmployeeDisplayName(line) : null) || line.assetName || line.description || '';
            qty = line.hours || 1;
            rate = line.rate || line.lineAmount || 0;
            amount = line.lineAmount || (qty * rate);
            break;
        }

        const row = [
          invoiceNo, customer, invoiceDate, dueDate, 'Net 30',
          item, description,
          parseFloat(qty.toFixed(2)), parseFloat(rate.toFixed(2)), parseFloat(amount.toFixed(2)),
          serviceDate, memo
        ];
        rows.push(row.map(csvEscape).join(','));
      }

      // Add Sales Tax line if ticket has tax
      if (ticket.taxEnabled && ticket.taxAmount > 0) {
        const taxRow = [
          invoiceNo, customer, invoiceDate, dueDate, 'Net 30',
          'Sales Tax', 'Sales Tax (' + (ticket.taxRate || 0).toFixed(2) + '%)',
          '1', parseFloat((ticket.taxAmount || 0).toFixed(2)), parseFloat((ticket.taxAmount || 0).toFixed(2)),
          serviceDate, memo
        ];
        rows.push(taxRow.map(csvEscape).join(','));
      }
    }
  }

  const today = (() => { const _d = new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })();
  // TASK-1346 (4th-COD) — the two callers produce the same column shape, so the
  // FILENAME is the other in-artifact cue that tells a bookkeeper which one they
  // are holding: the curated, externally-billed-free selection export keeps the
  // qb-export name; the visible-list dump is named for what it is.
  downloadCsv(
    (opts && opts.visibleListDump)
      ? `dispatchdeck-ticket-list-${today}.csv`
      : `dispatchdeck-qb-export-${today}.csv`,
    rows.join('\n')
  );
  showToast(`Exported ${selectedTickets.length} ticket${selectedTickets.length !== 1 ? 's' : ''} to CSV`, 'success');
}

// ============================================
// QUICKBOOKS IIF EXPORT
// ============================================

function iifEscape(val) {
  if (val == null) return '';
  return String(val).replace(/[\t\r\n]/g, ' ').trim();
}

function downloadIIF(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getQBCustomerName(ticket) {
  // Look up mapped QB name from customer doc, fall back to DD name
  const cust = BillingState.customers?.find(c => c.id === ticket.customerId);
  const customerName = cust?.qbCustomerName || cust?.name || ticket.customerName || 'Unknown';
  // QB Desktop uses "Customer:Job" format for sub-jobs/projects
  const jobName = ticket.jobTitle || ticket.jobName || '';
  return iifEscape(jobName ? customerName + ':' + jobName : customerName);
}

const QB_DEFAULT_ITEM_NAMES = {
  EMPLOYEE: 'Labor',
  EQUIPMENT: 'Equipment Rental',
  DOWNTIME: 'Downtime',
  STANDBY: 'Standby',
  POTHOLE: 'Pothole',
  PERMIT: 'Load Permits',
  FIELD_ITEM: 'Field Items',
  MISC: 'Miscellaneous'
};

// The QB Desktop item a line type exports under.
// TASK-9668 — with a mapping configured, an unmapped type returns null. It used
// to be skipped right there, so a FIELD_ITEM line (TITS maps only EQUIPMENT /
// MISC / PERMIT) silently fell out of the .iif while staying on the invoice.
// exportToIIF now asks before export and passes {fallback:true} once the biller
// agrees, so nothing on the invoice is dropped without them seeing it.
function getQBItemName(lineType, opts) {
  const settings = window.AppState?.settings || {};
  const qbMap = settings.qbItemMap || {};
  // TASK-9440 — the fuel surcharge is money the paper invoice shows, so it is
  // NEVER skipped as "unmapped" (that would export a lower total to QuickBooks
  // Desktop than the customer was billed). Mapped name, else "Fuel Surcharge".
  if (lineType === 'FUEL_SURCHARGE') return qbMap.FUEL_SURCHARGE || 'Fuel Surcharge';
  const hasQBSettings = Object.keys(qbMap).length > 0;
  if (hasQBSettings && qbMap[lineType]) return qbMap[lineType];
  if (hasQBSettings && !(opts && opts.fallback)) return null;
  // No QB settings configured (or the biller accepted the fallback) — defaults
  return QB_DEFAULT_ITEM_NAMES[lineType] || lineType || 'Service';
}

// TASK-9668 — the .iif carries exactly the lines the customer's invoice carries.
// Same shared predicate as every invoice render (isLineInvoiced + the customer's
// Active-contract hide list), so a line hidden on the invoice can't reach QB.
// Caller must have awaited loadReviewContractRates(customerId) first.
function iifInvoicedLines(ticket, lines) {
  const ex = getInvoiceExcludeLineTypes(ticket && ticket.customerId);
  return (lines || []).filter(l => isLineInvoiced(l, ex));
}

// One ticket line → the fields its SPL row carries. Moved verbatim out of
// exportToIIF's loop (TASK-9668) so the unmapped-type check prices a line the
// same way the export does.
function iifLineFields(line) {
  let desc = '', qty = 0, rate = 0, amount = 0;
  switch (line.type) {
    case 'EMPLOYEE':
      desc = getEmployeeDisplayName(line);
      qty = line.hours || 0;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
    case 'EQUIPMENT': {
      desc = (line.assetName || 'Equipment') + (line.fleetNumber ? ' #' + line.fleetNumber : '');
      qty = line.hours || 0;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
    }
    case 'PERMIT':
      desc = line.description || 'Permit';
      qty = 1;
      rate = line.lineAmount || line.baseCost || 0;
      amount = rate;
      break;
    case 'DOWNTIME':
    case 'STANDBY':
    case 'POTHOLE':
      desc = getEmployeeDisplayName(line);
      qty = line.hours || 0;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
    case 'FIELD_ITEM':
      desc = line.description || 'Field Item';
      qty = line.quantity || 1;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
    case 'MISC':
      desc = line.description || 'Miscellaneous';
      qty = line.quantity || 1;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
    default:
      desc = line.description || line.assetName || '';
      qty = line.hours || line.quantity || 1;
      rate = line.rate || 0;
      amount = line.lineAmount || (qty * rate);
      break;
  }
  return { desc, qty, rate, amount };
}

// What the INVOICE shows for a line (invoiceRenderedSubtotal semantics): the
// stored lineAmount, DOWNTIME as a deduction. Used for lines that only reach the
// .iif through the unmapped-type fallback (COD Code Reviewer): iifLineFields'
// older formulas add DOWNTIME positively and turn an explicit $0 lineAmount into
// qty × rate, which would put a different number in QB than on the invoice.
// Mapped lines keep iifLineFields exactly as before (existing output unchanged).
function iifInvoiceAmount(line) {
  return (line && line.type === 'DOWNTIME' ? -1 : 1) * (Number(line && line.lineAmount) || 0);
}

// Invoiced lines whose type has no QB item mapped, grouped by type, for the
// pre-export prompt. [] when nothing would be dropped.
function iifUnmappedSummary(ticketLineSets) {
  const byType = new Map();
  for (const lines of (ticketLineSets || [])) {
    for (const l of (lines || [])) {
      if (getQBItemName(l.type)) continue;
      const k = String(l.type || '');
      const row = byType.get(k) || { type: k, count: 0, amount: 0 };
      row.count++;
      row.amount += iifInvoiceAmount(l);
      byType.set(k, row);
    }
  }
  return [...byType.values()].map(r => ({ ...r, amount: Math.round(r.amount * 100) / 100 }));
}

function iifUnmappedMessage(summary) {
  const label = (t) => (EXCLUDABLE_LINE_TYPES.find(x => x.type === t)?.label) || t || 'Other';
  const parts = summary.map(r =>
    `${label(r.type)} — ${r.count} line${r.count !== 1 ? 's' : ''}, ${formatCurrency(r.amount)} (exports as "${getQBItemName(r.type, { fallback: true })}")`);
  return 'These lines are on the invoice but have no QuickBooks item mapped: ' + parts.join('; ') + '. ' +
    'Export them under the item names shown? Make sure those items exist in QuickBooks. ' +
    'To stop this question, map the type in Billing Settings → QuickBooks, or hide the type on that customer\'s invoice. ' +
    'Cancel exports nothing.';
}

// ══ TASK-9251 TELEMETRY — the funnel EVERY invoice email goes through ══
//
// The Send button lives in the invoice PREVIEW POPUP (a separate window.open'd
// document); it calls back into this function on `window.opener.BillingModule`.
// When the callable throws, the popup shows an alert() and then the popup is
// closed and gone — so "I never got the invoice" arrives at support with no way
// to tell whether the biller clicked Send at all. That is verbatim the question
// lib/telemetry.js was built to answer, on the surface where it costs money:
// an invoice that never left ages in A/R and nobody finds out until someone
// reconciles.
//
// Failure ONLY. A successful send already writes tenants/{tid}/emailLog plus
// `invoiceEmailSentAt/To/By` on the ticket server-side — instrumenting that
// here would be the happy path this sink must stay out of.
// PII: recipient COUNTS, never the addresses (they are in the toast and in the
// server-side emailLog; they do not belong in a free-form telemetry context).
function _logInvoiceEmailFailure(emailData, outcome, err) {
  try {
    window.Telemetry?.logUserAction('invoice_email_failed', {
      ticketId: emailData?.ticketId || null,
      invoiceNumber: emailData?.invoiceNumber || null,
      outcome,                                          // 'threw' | 'not_success'
      unconfirmed: !!err?.unconfirmed,                  // TASK-1412 — "can't tell" vs a definite fail
      recipientCount: (emailData?.recipientEmails || []).length,
      ccCount: (emailData?.ccEmails || []).length,
      hasAttachmentHtml: !!emailData?.invoiceHtml,
      errorCode: err?.code ? String(err.code).slice(0, 80) : null,
      errorMessage: window.Telemetry?.scrubPii?.(err?.message || err) || null,
    });
  } catch (_) { /* a diagnostic must never become a second failure */ }
}

// TASK-1412 follow-up (COD) — the server can only say "unconfirmed" when it is
// still around to answer. A client timeout, a dropped connection, or
// a gateway 502 all lose the response on the way BACK, after Gmail/SendGrid may
// already have taken the invoice. Those must read as "can't tell — check Sent",
// never as a plain failure with Send re-armed, or the biller emails the customer
// a duplicate invoice.
//
// Default is UNKNOWN. An error counts as a definite "not sent" only when the
// server said so: details.sent === false (every classified Gmail failure sets
// it) or a code that is rejected before any send is attempted. The server's
// outer-catch wrap ("internal: Failed to send invoice email: …") is NOT proof
// of non-delivery (COD round 2): it also wraps a SendGrid hang-up after
// SendGrid accepted the message, so it stays unknown.
const INVOICE_SEND_UNCONFIRMED_MSG = "We lost contact before the send finished, so we can't tell whether this invoice went out. " +
  'Check your Sent folder (or refresh and look for the Emailed badge on the ticket) before sending again — resending now could email the customer a duplicate.';
const _INVOICE_SEND_DEFINITE_CODES = new Set([
  'invalid-argument', 'permission-denied', 'unauthenticated', 'failed-precondition',
  'not-found', 'already-exists', 'resource-exhausted', 'out-of-range', 'unimplemented',
]);
function _isInvoiceSendOutcomeUnknown(err) {
  const d = (err && typeof err === 'object' && err.details) || {};
  if (d.reason === 'gmail_unconfirmed' || d.sent === null) return true;
  if (d.sent === false) return false;
  const code = String((err && err.code) || '').replace(/^functions\//, '');
  if (_INVOICE_SEND_DEFINITE_CODES.has(code)) return false;
  return true; // deadline-exceeded, unavailable, unknown, cancelled, internal, no code at all
}
// Tags the error the popup's .catch reads (err.unconfirmed / err.userMessage).
// Never replaces the original message — telemetry and the plain-failure alert
// still show what actually went wrong.
function _tagInvoiceSendOutcome(err) {
  let e = err;
  if (!e || typeof e !== 'object' || Object.isFrozen(e) || !Object.isExtensible(e)) {
    e = Object.assign(new Error(String((err && err.message) || err || 'Unknown error')),
      { code: err && err.code, details: err && err.details, cause: err });
  }
  e.unconfirmed = _isInvoiceSendOutcomeUnknown(err);
  if (e.unconfirmed) e.userMessage = INVOICE_SEND_UNCONFIRMED_MSG;
  return e;
}

// Per-ticket send state kept HERE, in the opener, because the popup's own flag
// dies when the popup closes — and a second popup for the same invoice starts
// clean (COD round 3). In-flight blocks any overlapping send outright;
// unconfirmed blocks a send until the popup passes opts.resendConfirmed (the
// biller got past the duplicate-risk confirm). Cleared on a confirmed send.
const _invoiceSendsInFlight = new Set();
const _invoiceSendsUnconfirmed = new Set();
function _invoiceSendBlockedError(message, userMessage) {
  const e = _tagInvoiceSendOutcome(Object.assign(new Error(message), { details: { reason: 'gmail_unconfirmed', sent: null } }));
  e.userMessage = userMessage;
  return e;
}

// Called with the settled outcome of one send. err === null means confirmed.
function _settleInvoiceSend(sendKey, err) {
  if (!sendKey) return;
  _invoiceSendsInFlight.delete(sendKey);
  if (!err) _invoiceSendsUnconfirmed.delete(sendKey);
  else if (err.unconfirmed) _invoiceSendsUnconfirmed.add(sendKey);
}

// TASK-1412 (2026-10-03) — the server writes billingGmailConnected:false /
// billingGmailNeedsReconnect:true BEFORE it answers gmail_reconnect_required,
// but the Company Info card and the Invoice Builder pointer paint from a status
// loaded once. Reload it so both stop saying "Invoices send from your Gmail"
// the moment a send has proved otherwise. Fire-and-forget; never throws.
function _refreshBillingGmailAfterDeadSend(err) {
  if (!err || !err.details || err.details.reason !== 'gmail_reconnect_required') return;
  try {
    if (typeof loadBillingGmailStatus !== 'function') return;
    Promise.resolve(loadBillingGmailStatus()).then(() => {
      try { if (typeof refreshSendingIdentityCard === 'function') refreshSendingIdentityCard(); } catch (_) {}
      try { if (typeof renderInvoiceBuilderBody === 'function') renderInvoiceBuilderBody(); } catch (_) {}
    }).catch(() => {});
  } catch (_) { /* status refresh is a nicety; the error itself already reached the biller */ }
}

async function sendInvoiceEmail(emailData, opts = {}) {
  const sendKey = (emailData && emailData.ticketId) || null;
  if (sendKey && _invoiceSendsInFlight.has(sendKey)) {
    throw _invoiceSendBlockedError('A send of this invoice is already in progress.',
      'This invoice is still sending from another window. Wait for that to finish, then check your Sent folder before sending again.');
  }
  if (sendKey && _invoiceSendsUnconfirmed.has(sendKey) && !opts.resendConfirmed) {
    throw _invoiceSendBlockedError('An earlier send of this invoice could not be confirmed.',
      "An earlier send of this invoice could not be confirmed, so it may already be in the customer's inbox. Check your Sent folder before sending again.");
  }
  // Reserve BEFORE the first await (COD round 4 ❌): two calls that both pass
  // the check above and then yield on the import would otherwise both send.
  if (sendKey) _invoiceSendsInFlight.add(sendKey);
  let functions, httpsCallable;
  try {
    ({ functions, httpsCallable } = await __ddChunkWrap(import('../firebase-config.js?v=1868')));
  } catch (loadErr) {
    if (sendKey) _invoiceSendsInFlight.delete(sendKey); // nothing was sent
    throw loadErr;
  }
  // 75s, deliberately LONGER than the server's 60s (v1 default — the callable
  // sets no timeoutSeconds). With the old 30s the client gave up while the
  // server was still working, so "check your Sent folder" could be checked
  // before the send even happened and a resend raced the first (COD round 2).
  // Now a client timeout means the server call is already over.
  const sendFn = httpsCallable(functions, 'sendInvoiceEmail', { timeout: 75000 });
  let result;
  try {
    result = await sendFn(emailData);
  } catch (err) {
    // Tagged, then rethrown — the popup's .catch reads err.unconfirmed to choose
    // between "Error sending" and "can't tell, check Sent". The log adds the
    // durable row that outlives the popup.
    err = _tagInvoiceSendOutcome(err);
    _settleInvoiceSend(sendKey, err);
    if (typeof _refreshBillingGmailAfterDeadSend === 'function') _refreshBillingGmailAfterDeadSend(err);
    _logInvoiceEmailFailure(emailData, 'threw', err);
    throw err;
  }
  if (result?.data?.success) {
    _settleInvoiceSend(sendKey, null);
    window.showToast?.(`Invoice emailed to ${emailData.recipientEmails.join(', ')}`, 'success');
  } else {
    // The popup would turn the button GREEN and print "Sent!" on any RESOLVED
    // promise, so a resolve-without-success renders as a send that never
    // happened — TASK-9219's exact shape ({ok:true} over 17 silent failures).
    // Today's server always throws instead, so this arm is a tripwire for a
    // future one that does not. It answered without saying "sent", so we can't
    // stand behind a send either way: reject as UNCONFIRMED, never resolve.
    const unconfirmedErr = _tagInvoiceSendOutcome(Object.assign(new Error('The server did not confirm this invoice email.'),
      { details: { reason: 'gmail_unconfirmed', sent: null } }));
    _settleInvoiceSend(sendKey, unconfirmedErr);
    _logInvoiceEmailFailure(emailData, 'not_success', unconfirmedErr);
    throw unconfirmedErr;
  }
  return result;
}

async function exportToIIF() {
  const ids = [...BillingState.selectedTicketIds];
  if (ids.length === 0) {
    showToast('Select tickets to export', 'error');
    return;
  }

  // TASK-1346 (COD Code Reviewer ❌) — the card's QB button and the view-ticket
  // modal both refuse externally-billed tickets, but this function is what the
  // always-visible "Export QB (.iif)" bar button calls, and it exported anything
  // selected. An .iif carrying EXT-<ref> imported into QB Desktop creates a SECOND
  // invoice for work QuickBooks already invoiced. Refuse them here, where every
  // caller passes through. (Uninvoiced tickets are still exportable on purpose —
  // cutting the invoice in QB from a DD ticket is a real workflow.)
  const selectedTickets = await _dropExternallyBilledForQb(
    BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id) && t.status !== 'deleted').map(_emitTicketMoney), // TASK-9646 S3b emitted money
    'QuickBooks (.iif)'
  );
  if (selectedTickets === null) return; // cancelled
  if (selectedTickets.length === 0) {
    showToast('No matching tickets found', 'error');
    return;
  }

  if (selectedTickets.length > 200) {
    showToast('QB Desktop may struggle with large imports. Consider exporting in batches of 200.', 'info');
  }

  showToast('Preparing IIF export...', 'info');

  const settings = window.AppState?.settings || {};
  const arAccount = settings.qbAccountsReceivable || 'Accounts Receivable';
  const acctMap = settings.qbAccountMap || {};
  const terms = settings.qbDefaultTerms || 'Net 30';
  const termsDays = parseInt(terms.replace(/\D/g, '')) || 30;

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const T = '\t';

  // TASK-9668 — load every ticket's lines FIRST, keep only what the customer's
  // invoice carries (the hide list needs each customer's Active contract loaded),
  // and ask about unmapped line types BEFORE anything is written.
  await Promise.all([...new Set(selectedTickets.map(t => t.customerId).filter(Boolean))]
    .map(cid => loadReviewContractRates(cid).catch(() => null)));

  const prepared = [];
  const loadFailed = [];
  const allHidden = [];
  for (const ticket of selectedTickets) {
    let rawLines;
    try {
      const linesSnap = await getDocs(
        collection(db, 'tenants', tenant, 'tickets', ticket.id, 'ticketLines')
      );
      rawLines = linesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      // Used to fall through to the grandTotal summary row — an unverified total
      // that also ignores the hide list. Skip it and say so instead.
      console.warn('[Billing] Error loading ticketLines for IIF export:', err);
      loadFailed.push(ticket);
      continue;
    }
    const ticketLines = iifInvoicedLines(ticket, rawLines);
    if (rawLines.length > 0 && ticketLines.length === 0) {
      // Every line is hidden on this customer's invoice — nothing to carry.
      allHidden.push(ticket);
      continue;
    }
    prepared.push({ ticket, ticketLines });
  }

  const unmapped = iifUnmappedSummary(prepared.map(p => p.ticketLines));
  if (unmapped.length > 0) {
    const go = await window.showConfirm?.(
      'Some invoice lines have no QuickBooks item',
      iifUnmappedMessage(unmapped),
      { confirmText: 'Export with default names', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
    );
    if (!go) return;
  }

  if (prepared.length === 0) {
    showToast(`Nothing to export — ${loadFailed.length ? loadFailed.length + ' ticket(s) could not be loaded' : 'every line on the selected tickets is hidden on the invoice'}`, 'error');
    return;
  }

  // IIF header rows
  const lines = [];
  lines.push(['!TRNS', 'TRNSID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO', 'TERMS', 'DUEDATE', 'PONUM'].join(T));
  lines.push(['!SPL', 'SPLID', 'TRNSTYPE', 'DATE', 'ACCNT', 'NAME', 'AMOUNT', 'DOCNUM', 'MEMO', 'QNTY', 'PRICE', 'INVITEM', 'EXTRA'].join(T));
  lines.push('!ENDTRNS');

  for (const { ticket, ticketLines } of prepared) {
    // QB Desktop DOCNUM max 11 chars — strip "INV-" prefix if too long
    const rawInvNo = ticket.invoiceNumber || ticket.ticketNo || ticket.id;
    const invoiceNo = iifEscape(String(rawInvNo).length > 11 ? String(rawInvNo).replace(/^INV-/, '') : rawInvNo);
    const customer = getQBCustomerName(ticket);
    const invoiceDate = formatDateMMDDYYYY(ticket.date);
    const dueDate = addDays(ticket.date, termsDays);
    const serviceDate = formatDateMMDDYYYY(ticket.workDate || ticket.date);

    const memoParts = [];
    if (ticket.poNumber) memoParts.push('PO: ' + ticket.poNumber);
    if (ticket.vendorNumber) memoParts.push('Vendor: ' + ticket.vendorNumber);
    if (ticket.wellName) memoParts.push('Well: ' + ticket.wellName + (ticket.wellNumber ? ' #' + ticket.wellNumber : ''));
    if (ticket.jobName || ticket.jobTitle) memoParts.push('Job: ' + (ticket.jobName || ticket.jobTitle));
    const memo = iifEscape(memoParts.join(' | '));
    const poNum = iifEscape(ticket.poNumber || '');

    // Calculate total from lines (for balance validation)
    let lineTotal = 0;
    const splRows = [];

    if (ticketLines.length === 0) {
      // Single summary line (ticket genuinely has no lines)
      const amt = parseFloat(ticket.grandTotal || 0);
      lineTotal = amt;
      splRows.push([
        'SPL', '', 'INVOICE', invoiceDate, '', customer,
        (-amt).toFixed(2), invoiceNo, iifEscape(ticket.jobName || 'Service'),
        '1', (-amt).toFixed(2), 'Service', ''
      ].join(T));
    } else {
      for (const line of ticketLines) {
        // The biller already agreed (above) to export unmapped types under the
        // default item name — nothing on the invoice is dropped here any more.
        const mapped = getQBItemName(line.type);
        const itemName = mapped || getQBItemName(line.type, { fallback: true });
        let { desc, qty, rate, amount } = iifLineFields(line);
        if (!mapped) {
          // Newly exported (was dropped): carry the invoice's own amount.
          amount = iifInvoiceAmount(line);
          if (line.type === 'DOWNTIME') rate = -rate;
        }

        lineTotal += amount;

        // SPL rows are negative (credit to income)
        const lineAcct = acctMap[line.type] || '';
        splRows.push([
          'SPL', '', 'INVOICE', invoiceDate, iifEscape(lineAcct), customer,
          (-amount).toFixed(2), invoiceNo, iifEscape(desc),
          qty ? (-qty).toFixed(2) : '', rate ? (-rate).toFixed(2) : '',
          iifEscape(itemName), ''
        ].join(T));
      }

      // Tax line
      if (ticket.taxEnabled && ticket.taxAmount > 0) {
        const taxAmt = parseFloat(ticket.taxAmount || 0);
        lineTotal += taxAmt;
        splRows.push([
          'SPL', '', 'INVOICE', invoiceDate, '', customer,
          (-taxAmt).toFixed(2), invoiceNo,
          iifEscape('Sales Tax (' + (ticket.taxRate || 0).toFixed(2) + '%)'),
          '', '', 'Sales Tax', ''
        ].join(T));
      }
    }

    // TRNS row (debit to AR — positive amount = total invoice)
    lines.push([
      'TRNS', '', 'INVOICE', invoiceDate, iifEscape(arAccount), customer,
      lineTotal.toFixed(2), invoiceNo, memo, iifEscape(terms), dueDate, poNum
    ].join(T));

    // Add all SPL rows
    for (const row of splRows) {
      lines.push(row);
    }

    lines.push('ENDTRNS');
  }

  const today = (() => { const _d = new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })();
  downloadIIF(`dispatchdeck-qb-${today}.iif`, lines.join('\n'));
  const _skipNote = [
    allHidden.length ? `${allHidden.length} skipped (every line hidden on the invoice)` : '',
    loadFailed.length ? `${loadFailed.length} skipped (lines could not be loaded — try again)` : '',
  ].filter(Boolean).join('; ');
  showToast(`Exported ${prepared.length} invoice${prepared.length !== 1 ? 's' : ''} to IIF${_skipNote ? ' — ' + _skipNote : ''}`, _skipNote ? 'warning' : 'success');
}

// ============================================
// OPENINVOICE (PIDX v1.62) XML EXPORT
// ============================================

function xmlEsc(val) {
  if (val == null) return '';
  return String(val).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function pidxDate(val) {
  if (!val) return '';
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildPIDXInvoiceXML(ticket, ticketLines, settings) {
  const companyName = settings?.companyName || 'Company';
  const invoiceNo = ticket.invoiceNumber || ticket.ticketNo || ticket.id;
  const invoiceDate = pidxDate(ticket.date || ticket.workDate);
  const customerName = ticket.customerName || 'Customer';

  // Build line items
  const lineItemsXML = [];
  let lineNum = 10;
  let invoiceTotal = 0;

  // Sort lines by sortOrder
  const sortedLines = [...ticketLines].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  for (const line of sortedLines) {
    // TASK-9436 (COD r1) — a PERMIT line's billed amount is its stored lineAmount
    // (marked up, whole cents); qty × a rounded unit price can miss it by a cent.
    const _isPermitAmt = line.type === 'PERMIT' && line.lineAmount != null;
    const qty = parseFloat(line.quantity || line.hours || (_isPermitAmt ? 1 : 0));
    const rate = parseFloat(line.rate || 0);
    const total = parseFloat(line.total || (_isPermitAmt ? line.lineAmount : (qty * rate)) || 0);
    invoiceTotal += total;

    let desc = '';
    switch (line.type) {
      case 'EMPLOYEE': desc = line.personName || line.description || 'Labor'; break;
      case 'EQUIPMENT': desc = line.vehicleName || line.description || 'Equipment'; break;
      case 'DOWNTIME': desc = 'Downtime' + (line.notes ? ' - ' + line.notes : ''); break;
      case 'STANDBY': desc = 'Standby' + (line.notes ? ' - ' + line.notes : ''); break;
      case 'FIELD_ITEM': desc = line.description || line.lineItemName || 'Field Item'; break;
      case 'MISC': desc = line.description || 'Miscellaneous'; break;
      case 'PERMIT': desc = line.description || 'Permit'; break;
      default: desc = line.description || line.type || 'Service';
    }

    const uom = line.rateUnit || (line.type === 'EMPLOYEE' ? 'HR' : 'EA');

    lineItemsXML.push(`    <pidx:InvoiceLineItem>
      <pidx:LineItemNumber>${lineNum}</pidx:LineItemNumber>
      <pidx:InvoiceQuantity UnitOfMeasurement="${xmlEsc(uom)}">${qty}</pidx:InvoiceQuantity>
      <pidx:LineItemInformation>
        <pidx:LineItemDescription>${xmlEsc(desc.substring(0, 80))}</pidx:LineItemDescription>
      </pidx:LineItemInformation>${ticket.poNumber ? `
      <pidx:PurchaseOrderInformation>
        <pidx:PurchaseOrderNumber>${xmlEsc(ticket.poNumber)}</pidx:PurchaseOrderNumber>
      </pidx:PurchaseOrderInformation>` : ''}${ticket.ticketNo ? `
      <pidx:FieldTicketInformation>
        <pidx:FieldTicketNumber>${xmlEsc(ticket.ticketNo)}</pidx:FieldTicketNumber>
      </pidx:FieldTicketInformation>` : ''}
      <pidx:Pricing>
        <pidx:UnitPrice>${rate.toFixed(2)}</pidx:UnitPrice>
      </pidx:Pricing>
      <pidx:LineItemTotal>${total.toFixed(2)}</pidx:LineItemTotal>${ticket.workDate ? `
      <pidx:ServiceDateTime serviceIndicator="ServicePeriod">
        <pidx:ServicePeriodEnd>${pidxDate(ticket.workDate)}</pidx:ServicePeriodEnd>
      </pidx:ServiceDateTime>` : ''}${ticket.afeNumber ? `
      <pidx:ReferenceInformation referenceInformationIndicator="AFENumber">
        <pidx:ReferenceNumber>${xmlEsc(ticket.afeNumber)}</pidx:ReferenceNumber>
      </pidx:ReferenceInformation>` : ''}
    </pidx:InvoiceLineItem>`);

    lineNum += 10;
  }

  // If no line items but has grand total, add a single summary line
  if (lineItemsXML.length === 0 && ticket.grandTotal) {
    const total = parseFloat(ticket.grandTotal);
    invoiceTotal = total;
    lineItemsXML.push(`    <pidx:InvoiceLineItem>
      <pidx:LineItemNumber>10</pidx:LineItemNumber>
      <pidx:InvoiceQuantity UnitOfMeasurement="EA">1</pidx:InvoiceQuantity>
      <pidx:LineItemInformation>
        <pidx:LineItemDescription>Service</pidx:LineItemDescription>
      </pidx:LineItemInformation>
      <pidx:Pricing>
        <pidx:UnitPrice>${total.toFixed(2)}</pidx:UnitPrice>
      </pidx:Pricing>
      <pidx:LineItemTotal>${total.toFixed(2)}</pidx:LineItemTotal>
    </pidx:InvoiceLineItem>`);
  }

  // Tax handling
  let taxXML = '';
  const taxAmt = parseFloat(ticket.taxAmount || 0);
  if (taxAmt > 0) {
    invoiceTotal += taxAmt;
    taxXML = `
    <pidx:Tax>
      <pidx:TaxAmount>${taxAmt.toFixed(2)}</pidx:TaxAmount>
      <pidx:TaxPercent>${parseFloat(ticket.taxRate || 0)}</pidx:TaxPercent>
      <pidx:TaxTypeCode>SalesTax</pidx:TaxTypeCode>
    </pidx:Tax>`;
  }

  // WHO / WHERE / WHAT description
  const commentParts = [];
  if (companyName) commentParts.push('WHO: ' + companyName);
  if (ticket.wellName) commentParts.push('WHERE: ' + ticket.wellName + (ticket.wellNumber ? ' #' + ticket.wellNumber : ''));
  else if (ticket.location) commentParts.push('WHERE: ' + ticket.location);
  if (ticket.jobName || ticket.jobTitle) commentParts.push('WHAT: ' + (ticket.jobName || ticket.jobTitle));
  const comment = commentParts.join(' | ');

  return `<?xml version="1.0" encoding="UTF-8"?>
<pidx:Invoice transactionPurposeIndicator="Original" version="1.62"
  xmlns:pidx="http://www.pidx.org/schemas/v1.62">
  <pidx:InvoiceProperties>
    <pidx:InvoiceNumber>${xmlEsc(invoiceNo)}</pidx:InvoiceNumber>
    <pidx:InvoiceDate>${invoiceDate}</pidx:InvoiceDate>
    <pidx:PartnerInformation partnerRoleIndicator="BuyerParty">
      <pidx:PartnerIdentifier partnerIdentifierIndicator="AssignedByBuyer">${xmlEsc(ticket.customerId || '')}</pidx:PartnerIdentifier>
      <pidx:PartnerName>${xmlEsc(customerName)}</pidx:PartnerName>
    </pidx:PartnerInformation>
    <pidx:PartnerInformation partnerRoleIndicator="SellerParty">
      <pidx:PartnerIdentifier partnerIdentifierIndicator="AssignedBySeller">${xmlEsc(settings?.tenantId || '')}</pidx:PartnerIdentifier>
      <pidx:PartnerName>${xmlEsc(companyName)}</pidx:PartnerName>
    </pidx:PartnerInformation>
    <pidx:InvoiceTypeCode>Original</pidx:InvoiceTypeCode>${ticket.poNumber ? `
    <pidx:PurchaseOrderInformation>
      <pidx:PurchaseOrderNumber>${xmlEsc(ticket.poNumber)}</pidx:PurchaseOrderNumber>
    </pidx:PurchaseOrderInformation>` : ''}${ticket.ticketNo ? `
    <pidx:FieldTicketInformation>
      <pidx:FieldTicketNumber>${xmlEsc(ticket.ticketNo)}</pidx:FieldTicketNumber>
    </pidx:FieldTicketInformation>` : ''}
    <pidx:PrimaryCurrency>
      <pidx:CurrencyCode>USD</pidx:CurrencyCode>
    </pidx:PrimaryCurrency>${(ticket.wellName || ticket.location) ? `
    <pidx:JobLocationInformation>
      <pidx:JobDescription>${xmlEsc(ticket.wellName || ticket.location || '')}</pidx:JobDescription>
    </pidx:JobLocationInformation>` : ''}${ticket.workDate ? `
    <pidx:ServiceDateTime serviceIndicator="ServicePeriod">
      <pidx:ServicePeriodEnd>${pidxDate(ticket.workDate)}</pidx:ServicePeriodEnd>
    </pidx:ServiceDateTime>` : ''}${comment ? `
    <pidx:Comment>${xmlEsc(comment)}</pidx:Comment>` : ''}
  </pidx:InvoiceProperties>
  <pidx:InvoiceDetails>
${lineItemsXML.join('\n')}
  </pidx:InvoiceDetails>
  <pidx:InvoiceSummary>
    <pidx:TotalLineItems>${sortedLines.length || 1}</pidx:TotalLineItems>
    <pidx:InvoiceTotal>${invoiceTotal.toFixed(2)}</pidx:InvoiceTotal>${taxXML}${ticket.subtotal ? `
    <pidx:SubTotalAmount>${parseFloat(ticket.subtotal).toFixed(2)}</pidx:SubTotalAmount>` : ''}
  </pidx:InvoiceSummary>
</pidx:Invoice>`;
}

async function exportOpenInvoice(ticketId) {
  const ticket = _emitTicketMoney(BillingState.tickets.find(t => t.id === ticketId)); // TASK-9646 S3b emitted money
  if (!ticket) {
    showToast('Ticket not found', 'error');
    return;
  }
  // TASK-1346 sweep — "is this billed?": the 110 invoice-marker-only tickets carry
  // real DD invoice numbers, so refusing to export them was wrong.
  if (!isTicketInvoiced(ticket)) {
    showToast('Generate an invoice first before exporting to OpenInvoice', 'error');
    return;
  }
  // TASK-1346 — the button is hidden for these, but the function is the real
  // boundary: an EDI file carrying EXT-<ref> would land in a shipper's AP
  // portal with a number that doesn't match what QuickBooks actually sent.
  if (isBilledExternally(ticket)) {
    showToast('This ticket was billed outside DispatchDeck — export the invoice from that system instead.', 'error');
    return;
  }

  showToast('Generating OpenInvoice XML...', 'info');

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

  // Fetch ticket lines
  let ticketLines = [];
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticket.id, 'ticketLines'));
    ticketLines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Billing] Error loading ticketLines for OpenInvoice export:', err);
  }

  const settings = window.AppState?.settings || {};
  settings.tenantId = tenant;

  const xml = buildPIDXInvoiceXML(ticket, ticketLines, settings);

  // Download as XML file
  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (ticket.invoiceNumber || 'invoice') + '-openinvoice.xml';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  showToast('OpenInvoice XML exported', 'success');
}

async function exportOpenInvoiceBatch() {
  const ids = [...BillingState.selectedTicketIds];
  if (ids.length === 0) {
    showToast('Select tickets to export', 'error');
    return;
  }

  // TASK-1346 — externally-billed tickets never get pushed as PIDX EDI.
  // TASK-1346 sweep — "is this billed?" (same widening as exportOpenInvoice).
  // 4th-COD Code Reviewer ⚠️ — this used to drop externally-billed tickets SILENTLY
  // while the .iif and CSV paths made the user acknowledge the skipped money. Same
  // helper, same blocking confirm, so the three batch exporters actually behave the
  // same way instead of only claiming to.
  const _oiInvoiced = BillingState.tickets.filter(t => BillingState.selectedTicketIds.has(t.id) && isTicketInvoiced(t)).map(_emitTicketMoney); // TASK-9646 S3b emitted money
  const selectedTickets = await _dropExternallyBilledForQb(_oiInvoiced, 'OpenInvoice (PIDX XML)');
  if (selectedTickets === null) return; // cancelled
  if (selectedTickets.length === 0) {
    showToast('No DispatchDeck-invoiced tickets selected. Generate invoices first.', 'error');
    return;
  }

  showToast('Generating OpenInvoice XML files...', 'info');

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  const settings = window.AppState?.settings || {};
  settings.tenantId = tenant;

  for (const ticket of selectedTickets) {
    let ticketLines = [];
    try {
      const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticket.id, 'ticketLines'));
      ticketLines = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('[Billing] Error loading ticketLines for OpenInvoice export:', err);
    }

    const xml = buildPIDXInvoiceXML(ticket, ticketLines, settings);
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (ticket.invoiceNumber || ticket.ticketNo || ticket.id) + '-openinvoice.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  showToast(`Exported ${selectedTickets.length} OpenInvoice XML file${selectedTickets.length !== 1 ? 's' : ''}`, 'success');
}

async function exportFilteredTickets() {
  // Get currently filtered/visible tickets (same filtering logic as renderTicketsList)
  // Exclude deleted tickets from export
  let tickets = BillingState.tickets.filter(t => t.status !== 'deleted');

  if (BillingState.ticketJobFilter) {
    tickets = tickets.filter(t => t.jobId === BillingState.ticketJobFilter);
  }

  // TASK-9414 — never export a project-filtered set computed before the jobs
  // (which carry the link) have landed: it would be "No project" for everything.
  if (BillingState.ticketProjectMode && !BillingState._jobsLoaded) {
    window.showToast?.('Projects are still loading — try Export again in a moment.', 'info');
    return;
  }
  // TASK-1618 trap 1 — the export MUST know about the project drill-down too.
  // Same shared predicate as renderTicketsList; widening one without the other is
  // the export-disagrees-with-the-screen bug TASK-857/1345 already paid for once.
  tickets = tickets.filter(_ticketInProjectFilter);

  if (BillingState.ticketCustomerFilter.size > 0) {
    tickets = tickets.filter(t => BillingState.ticketCustomerFilter.has(t.customerId));
  }

  // TASK-1345 — the SAME filter the list uses (this switch was a divergent
  // second copy: it keyed on invoiceNumber where the list keyed on `invoiced`,
  // and it had no 'all'/'paid'/'written_off' cases at all, so the accountant's
  // CSV could legitimately contain a different set than the screen).
  tickets = _applyStatusOrAgingScope(tickets); // TASK-9654 — same scope as the list (status, or the QB aging bucket)

  // Apply search filter to export too (TASK-1400 — same trimmed query as the
  // list, so a padded search can't hand the accountant a different set).
  // TASK-4662 — this WAS a third hand-copied field list, which is exactly the
  // divergence the TASK-1345 comment above describes; it now calls the same
  // _ticketSearchFields() the list does, so the two cannot drift again.
  const q = _ticketSearchQuery();
  if (q) {
    tickets = tickets.filter(t => _ticketSearchFields(t).some(f => f && String(f).toLowerCase().includes(q)));
  }

  // TASK-4662 (COD Code Reviewer) — and the DELIVERY chip has to narrow the
  // export too. Without this, exporting while "Unsent" is active hands the
  // accountant every ticket on screen AND every one the chip is hiding — the
  // same class of "the CSV disagrees with the screen" bug TASK-1345 fixed.
  if (BillingState.deliveryFilter) {
    const _df = TICKET_DELIVERY_FILTERS.find(f => f.key === BillingState.deliveryFilter);
    if (_df) tickets = tickets.filter(_df.test);
  }

  if (tickets.length === 0) {
    showToast('No tickets to export', 'info');
    return;
  }

  // Temporarily set selectedTicketIds to the filtered set, reuse exportToQuickBooks, then restore
  const savedSelection = new Set(BillingState.selectedTicketIds);
  BillingState.selectedTicketIds = new Set(tickets.map(t => t.id));
  // visibleListDump — TASK-1345 (i) requires this CSV to be exactly the on-screen
  // set; see the note on exportToQuickBooks for why the external-billed skip is
  // deliberately NOT applied on this path.
  await exportToQuickBooks({ visibleListDump: true });
  BillingState.selectedTicketIds = savedSelection;
}

// TASK-1414 — "does this work already have a ticket somewhere else?"
//
// Offering "Create Ticket" whenever nothing matches EXACTLY on (jobId, workDate)
// is how the same work gets billed twice. That exact match misses two ways:
//   • TWIN JOB RECORDS — a crew change spawns a new job doc, so one real job is
//     spread across many jobIds (TITS carries 110 duplicated titles across 401
//     job records; "Davidson atlas" alone has 12).
//   • DATE DRIFT — a ticket is cut on the day it was BUILT, not the day worked.
// Both fired at once on 2026-08-01: Ethan's blade haul looked unticketed by
// either key, but was billed on #100425 dated 8/4 under job "BW Chad Allen" and
// already invoiced as INV-202608-015.
//
// Returns tickets on SIBLING job docs (same customer + same normalized title),
// optionally within `windowDays` of a date. The caller keeps its own exact-match
// list untouched — this only feeds a warning, because a second ticket on one job
// IS sometimes right (split crews, a second load) and the human decides.
function findTwinJobTickets(job, excludeTickets, dateStr, windowDays = 3) {
  if (!job) return [];
  const _norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const title = _norm(job.title || job.name);
  // An untitled job would otherwise twin with every other untitled job for the
  // same customer (COD Code Reviewer). No title, no twin matching.
  if (!title) return [];
  const key = `${job.customerId || '?'}|${title}`;
  const twinIds = new Set((BillingState.jobs || [])
    .filter(j => `${j.customerId || '?'}|${_norm(j.title || j.name)}` === key)
    .map(j => j.id));
  const exclude = new Set((excludeTickets || []).map(t => t.id));
  const gap = (a, b) => (!a || !b) ? Infinity : Math.abs((new Date(a + 'T12:00:00') - new Date(b + 'T12:00:00')) / 86400000);
  return (BillingState.tickets || [])
    // originalJobId, not just jobId — a ticket reassigned to another job keeps
    // the immutable original, and that is the one pointing at the sibling
    // (matches the dedup aliases generateTicketFromJob already relies on).
    .filter(t => !exclude.has(t.id) && (twinIds.has(t.jobId) || twinIds.has(t.originalJobId)))
    .filter(t => !dateStr || gap(t.workDate, dateStr) <= windowDays)
    .sort((a, b) => String(a.workDate).localeCompare(String(b.workDate)));
}

// =============================================================================
// TASK-1428 — "was this work already billed?" — the WORDS check.
// =============================================================================
// findTwinJobTickets above is the weak version of this question: it needs an
// EXACT normalized job title on the same customer within 3 days. Measured
// against three real August cases it missed all three, because the titles drift:
//   dispatch "blade"                            -> billed under "BW Chad Allen"
//   dispatch "KSI: RS Piepkorn- Haul John Deer"  -> billed under "RS Piepkorn"
//   dispatch "PAL property bore 2"              -> billed under "PAL property bore 1"
//
// The signal is not in the title, it is in the PROSE. Whoever built the ticket
// copied the driver's note, so the field note and the billed note describe the
// same haul in nearly the same words. This is the check that has been running in
// scripts/ticket-prep-report.cjs (findBilledElsewhere) and it caught all three.
//
// Why it matters more than tidiness: on 2026-08-10 the "no ticket yet" list held
// three phantoms AND one real $1,445 miss, indistinguishable. A list that is
// mostly wrong teaches people to ignore it, and then it fails on the day it is
// finally right.
const _BE_STOP = new Set(['the','and','for','from','with','that','this','was','had','has','get','got','into','out','off','back','then','over','all','our','their','are','were','went','going','job','load','loads','loaded','truck','trucks','trailer','location','locations','today','day','morning','afternoon','hour','hours','need','needed','just','also','here','there','when','while','after','before','down','onto','one','two','three','pick','picked','drop','dropped','take','took','put','set','run','ran','have','been','they','them','his','her','not','but','yard']);
function _beTokens(s) {
  return new Set((String(s || '').toLowerCase().match(/[a-z0-9#]{3,}/g) || []).filter(w => !_BE_STOP.has(w)));
}
// The matcher's candidate window (days either side of the work date). Shared by
// findWorkBilledElsewhere and the clock-entry fetch that feeds it (TASK-9693).
const BE_WINDOW_DAYS = 10;
function _beDaysApart(a, b) {
  if (!a || !b) return Infinity;
  return Math.abs((new Date(a + 'T12:00:00') - new Date(b + 'T12:00:00')) / 86400000);
}

// The evidence a jobDay can offer about what was actually done. The driver's own
// clock note is the richest source and is NOT in client state (billing only
// subscribes to ACTIVE entries), so it is fetched on demand — one bounded query
// when a modal opens, never during list render.
async function gatherJobDayEvidence(jobId, dateStr) {
  const job = (BillingState.jobs || []).find(j => j.id === jobId) || {};
  let noteText = '';
  // null = NOT CHECKED. Never default to 0: the job-detail modal passes no date,
  // so a 0 default made every whole-job view claim "nobody clocked in" without
  // ever looking (COD Domain + Code Reviewer: a new deterministic false alarm,
  // the exact failure this task exists to remove).
  let entryCount = null;
  // TASK-9693 — ticketId -> the shift-note prose of the clock entries linked to
  // that ticket, for the CANDIDATE side of findWorkBilledElsewhere. That side
  // used to read `ticket.shiftNotes`, a crew-shared copy every driver-PIN session
  // could read; the copy is gone, so the same prose is read from its real home,
  // the per-operator clock entries (office-gated read: billing module).
  const notesByTicket = new Map();
  let windowEntries = null;
  if (dateStr && BillingState.tenantId) {
    try {
      const start = new Date(dateStr + 'T00:00:00');
      const end = new Date(start); end.setDate(end.getDate() + 1);
      // ONE bounded query covering the matcher's ±10-day ticket window (plus a
      // day of slack each side) — replaces the old single-day query, which is
      // re-derived below by filtering on clockIn.
      const winStart = new Date(start); winStart.setDate(winStart.getDate() - BE_WINDOW_DAYS - 1);
      const winEnd = new Date(end); winEnd.setDate(winEnd.getDate() + BE_WINDOW_DAYS + 1);
      const snap = await getDocs(query(
        collection(db, 'tenants', BillingState.tenantId, 'clockEntries'),
        where('clockIn', '>=', Timestamp.fromDate(winStart)),
        where('clockIn', '<', Timestamp.fromDate(winEnd))
      ));
      const all = snap.docs.map(d => d.data());
      const startMs = start.getTime(), endMs = end.getTime();
      const mine = all.filter(e => {
        const ms = e.clockIn?.toMillis?.() ?? (e.clockIn ? new Date(e.clockIn).getTime() : NaN);
        // TASK-1429 (COD r1) — a deleted punch is not time on this job.
        return e.jobId === jobId && e.status !== 'deleted' && ms >= startMs && ms < endMs;
      });
      entryCount = mine.length;
      // TASK-1429 — every job's entries in the window, so a planned day with no
      // time on this job can say where its crew's time went that day.
      windowEntries = all;
      noteText = mine.map(e => `${e.notes || ''} ${e.clockOutNotes || ''}`).join(' ');
      for (const e of all) {
        if (!e.ticketId) continue;
        const txt = `${e.clockOutNotes || ''} ${e.draftShiftNotes || ''}`.trim();
        if (!txt) continue;
        notesByTicket.set(e.ticketId, `${notesByTicket.get(e.ticketId) || ''} ${txt}`);
      }
    } catch (e) {
      // Fail OPEN: no evidence means we simply do not show the extra warning.
      // The existing twin check still runs, so this never makes things worse.
      console.warn('[Billing] billed-elsewhere evidence fetch failed', e?.message);
      return { text: '', entryCount: null, job, notesByTicket, windowEntries: null };
    }
  }
  return { text: `${noteText} ${job.title || ''} ${job.notes || ''}`.trim(), entryCount, job, notesByTicket, windowEntries };
}

// Does any EXISTING ticket already describe this work? Same customer, any job,
// any nearby date. Returns the strongest match or null.
function findWorkBilledElsewhere(evidenceText, customerId, dateStr, excludeIds, notesByTicket) {
  const mine = _beTokens(evidenceText);
  if (mine.size < 3) return null;            // too little text to claim anything
  const skip = new Set(excludeIds || []);
  let best = null;
  for (const t of (BillingState.tickets || [])) {
    if (skip.has(t.id)) continue;
    if (t.status === 'deleted') continue;
    if (customerId && t.customerId !== customerId) continue;
    if (dateStr && t.workDate && _beDaysApart(dateStr, t.workDate) > BE_WINDOW_DAYS) continue;
    const jobTitle = ((BillingState.jobs || []).find(j => j.id === (t.jobId || t.originalJobId)) || {}).title || '';
    // TASK-1644 follow-on — the shift-note prose is in the CANDIDATE TOKEN SOURCE
    // and nowhere else. TASK-9693: it is read from the ticket's linked clock
    // entries (`notesByTicket`, built in gatherJobDayEvidence), NOT from a
    // `ticket.shiftNotes` copy — that crew-readable copy no longer exists.
    // This check works because both sides are the same prose: the
    // evidence side (`mine`) is built from the day's clockEntry notes/clockOutNotes,
    // and until TASK-1644 the candidate side matched because submitClockOut copied
    // that same shift note into ticket.notes AND ticket.workPerformed. 1644 removed
    // that copy — correctly, it was printing a driver's first-person paragraph on the
    // customer invoice — which left a brand-new clock-out ticket contributing only
    // jobTitle tokens. That is precisely the ticket most likely to be double-billed,
    // so the warning went quiet exactly where it earns its keep. The prose is still
    // stored, untouched, on each operator's clock entry (clockOutNotes /
    // draftShiftNotes), so reading it here restores the pre-1644 recall without
    // restoring the leak: this is a token Set used only to COUNT overlaps.
    //
    // 🔴 Do NOT add shiftNotes to `desc` in renderBilledElsewhereWarning below. That
    // string is DISPLAYED, and displaying the shift note is the thing 1644 removed.
    // A fresh clock-out ticket now renders that card with a match count and no quote,
    // which is the honest shape — the count is the signal, the driver's voice is not
    // ours to republish.
    const entryNotes = (notesByTicket && notesByTicket.get(t.id)) || '';
    const other = _beTokens(`${t.notes || ''} ${t.workPerformed || ''} ${entryNotes} ${jobTitle}`);
    if (!other.size) continue;
    let hits = 0;
    for (const w of mine) if (other.has(w)) hits++;
    const score = hits / Math.min(mine.size, Math.max(other.size, 1));
    if (hits >= 3 && score >= 0.3 && (!best || hits > best.hits)) best = { t, hits, score };
  }
  return best;
}

function renderBilledElsewhereWarning(match, dateStr) {
  if (!match) return '';
  const t = match.t;
  const gap = _beDaysApart(dateStr, t.workDate);
  const strength = gap === 0 ? 'Almost certainly already billed'
    : gap <= 3 ? 'Probably already billed'
      : 'Similar wording — check this is not the same load';
  const inv = t.invoiceNumber ? ` · invoiced ${escapeHtml(t.invoiceNumber)}` : '';
  const desc = String(t.workPerformed || t.notes || '').replace(/\s+/g, ' ').trim().slice(0, 160);
  return `
    <div style="border:1px solid var(--status-warning,#F59E0B);background:#FFFBEB;border-radius:var(--radius-sm);padding:12px;margin-top:8px;">
      <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);margin-bottom:4px;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">report_problem</span>
        ${strength}
      </div>
      <div style="font-size:0.82rem;color:var(--text-secondary);line-height:1.5;">
        This day's work reads like ticket
        <strong>${t.ticketNo ? '#' + escapeHtml(String(t.ticketNo)) : 'a ticket'}</strong>
        (${escapeHtml(String(t.workDate || ''))}${inv}) — ${match.hits} matching words.
        ${desc ? `<div style="margin-top:6px;font-style:italic;">&ldquo;${escapeHtml(desc)}&rdquo;</div>` : ''}
        <div style="margin-top:6px;">Check that one before creating a new ticket, or you may bill it twice.</div>
      </div>
    </div>`;
}

// TASK-1429 — renderNoLinkedTimeWarning moved to billingJobDayReconcile.js
// (genericNoTimeCard) with its copy unchanged; it is only shown in that lazy
// path, so it no longer rides the app's cold load.

// Shared post-render pass for BOTH job modals. Runs after the synchronous render
// so the fetch never blocks paint, then rewrites the warning slot and the action
// button in place.
async function applyBilledElsewhereGuard(jobId, dateStr, existingTicketIds) {
  const slot = document.getElementById('billing-job-be-slot');
  const btn = document.getElementById('btn-generate-ticket');
  if (!slot && !btn) return;
  // A DATE IS REQUIRED (COD round 2). Without one there are no clock notes to
  // reason from and no date window, so the matcher would compare a bare job
  // title against every ticket this customer has ever had and demote the button
  // on that. Not checked means SILENT — the whole-job modal keeps the existing
  // findTwinJobTickets warning and nothing else changes.
  if (!dateStr) return;
  // STALE-MODAL GUARD (COD): the fetch is async, so the user can close this job
  // and open another before it resolves. Stamp the slot with the job+day we are
  // answering for and refuse to write if the modal has moved on.
  const stamp = `${jobId}|${dateStr || ''}`;
  if (slot) slot.dataset.beFor = stamp;

  const ev = await gatherJobDayEvidence(jobId, dateStr);

  const stillMine = () => {
    const cur = document.getElementById('billing-job-be-slot');
    return cur && cur.dataset.beFor === stamp ? cur : null;
  };
  const demote = (title) => {
    const b = document.getElementById('btn-generate-ticket');
    if (!b) return;
    b.classList.remove('btn-primary');
    b.classList.add('btn-secondary');
    b.title = title;
  };

  // entryCount === null means NOT CHECKED (no date supplied, or the read failed).
  // Say nothing and change nothing — silence is correct when we do not know.
  if (ev.entryCount === 0) {
    // TASK-1429 — a planned day nobody clocked into. Before the generic card,
    // say where THIS DAY'S CREW clocked in that day and what ticket / invoice
    // that time is already on (the 8/08 Core Oilfield near double-bill). No
    // planned crew -> the generic "no clock time" card; a failed module load ->
    // the button is still demoted with that sentence. Nothing is hidden and the
    // Create Ticket button stays. Lazy import (perf
    // budget); pure functions, billing.js passes its state + helpers in
    // (never a cross-import, Pitfall #1).
    let out = null;
    try {
      out = (await __ddChunkWrap(import('./billingJobDayReconcile.js?v=1429001'))).reconcileForModal(BillingState, jobId, dateStr, ev.windowEntries, isTicketInvoiced, escapeHtml, toDateStr);
    } catch (e) {
      console.warn('[Billing] job-day reconcile unavailable', e?.message);
    }
    const cur = stillMine();
    if (cur) { if (out) cur.innerHTML = out.html; demote(out ? out.title : 'No clock time is linked to this job for this day — check the crew time first'); }
    return;
  }
  const match = findWorkBilledElsewhere(ev.text, ev.job.customerId, dateStr, existingTicketIds, ev.notesByTicket);
  if (!match) return;
  const cur = stillMine();
  if (cur) {
    cur.innerHTML = renderBilledElsewhereWarning(match, dateStr);
    demote(`Looks like ticket #${match.t.ticketNo || ''} already covers this work — check it first`);
  }
}

// Shared warning card for both job modals (calendar day cell + job detail).
function renderTwinTicketWarning(nearby, scopeLabel) {
  if (!nearby || !nearby.length) return '';
  return `
    <div style="border:1px solid var(--status-warning,#F59E0B);background:#FFFBEB;border-radius:var(--radius-sm);padding:12px;margin-top:4px;">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
        <span class="material-icons" style="font-size:18px;color:#B45309;">warning</span>
        <strong style="font-size:0.85rem;color:#78350F;">This job already has ${nearby.length} ticket${nearby.length !== 1 ? 's' : ''}${scopeLabel ? ' ' + scopeLabel : ''}</strong>
      </div>
      <p style="font-size:0.78rem;color:#78350F;margin:0 0 10px;">
        Tickets are often cut on the day they were built, not the day of the work — and a crew change can put the same job under a second record. Check these before creating another.
      </p>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${nearby.map(t => `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;background:var(--bg-page);border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:8px 10px;">
            <span style="font-size:0.8rem;color:var(--text-primary);">
              <strong>${t.ticketNo ? '#' + escapeHtml(String(t.ticketNo)) : 'Ticket'}</strong>
              · ${t.workDate ? new Date(t.workDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
              · $${(t.grandTotal || 0).toFixed(2)}
              ${isTicketInvoiced(t) && t.invoiceNumber ? ` <span class="badge badge-invoice-num">${escapeHtml(t.invoiceNumber)}</span>` : ''}
              ${isTicketInvoiced(t)
                ? '<span class="badge badge-invoiced"><span class="material-icons">check_circle</span>Invoiced</span>'
                : `<span class="badge badge-${escapeHtml(String(t.status || ''))}">${escapeHtml(capitalizeFirst(t.status || ''))}</span>`}
            </span>
            <button class="btn btn-sm btn-secondary" onclick="window.BillingModule.closeModal('billing-job');window.BillingModule.openReviewTicket('${escapeHtml(String(t.id))}')">
              <span class="material-icons">edit_note</span> Open
            </button>
          </div>`).join('')}
      </div>
    </div>`;
}

// ============================================
// CALENDAR JOB → TICKET ACTIONS (date-aware)
// ============================================
function showCalendarJobTickets(jobId, dateStr) {
  const job = BillingState.jobs.find(j => j.id === jobId);
  if (!job) return;

  // Find tickets for this job on this date
  const _tmd = (t) => t.workDate === dateStr || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
  let tickets = (BillingState.tickets || []).filter(t => t.jobId === jobId && _tmd(t));

  const nearby = findTwinJobTickets(job, tickets, dateStr);

  // Single ticket → go directly to review/edit
  if (tickets.length === 1) {
    openReviewTicket(tickets[0].id);
    return;
  }

  // Multiple tickets or zero → show picker in the billing-job modal
  BillingState.selectedJob = job;
  const titleEl = document.getElementById('billing-job-title');
  if (titleEl) {
    const dateLabel = dateStr ? new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
    titleEl.innerHTML = escapeHtml(job.title || 'Job') + (dateLabel ? ` <span style="font-weight:400;font-size:0.85rem;color:var(--text-secondary);">— ${dateLabel}</span>` : '');
  }

  const content = document.getElementById('billing-job-content');
  if (!content) return;

  let html = '';

  if (tickets.length === 0) {
    html += `
      <div style="text-align:center;padding:24px 16px;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);margin-bottom:8px;">receipt_long</span>
        <p style="color:var(--text-secondary);margin-bottom:16px;">No tickets for this job on ${dateStr ? new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'this date'}.</p>
      </div>`;
    // TASK-1414 — but this job DOES have tickets a day or two either side, so
    // creating another here is how the same work gets billed twice.
    html += renderTwinTicketWarning(nearby, 'within a few days');
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:8px;">`;
    for (const t of tickets) {
      const empCount = t.employeeCount || t.employees?.length || 0;
      const hrs = getTicketLiveHours(t.id, t.totalHours || 0);
      const total = t.grandTotal || 0;
      const dateLabel = t.workDate ? new Date(t.workDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
      html += `
        <div style="border:1px solid ${t.status === 'draft' ? '#FECACA' : 'var(--border-light)'};border-radius:var(--radius-sm);padding:12px;background:${t.status === 'draft' ? '#FFFBFB' : 'var(--bg-page)'};">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
            <span style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">
              ${t.ticketNo ? '#' + t.ticketNo + ' — ' : ''}${dateLabel}
            </span>
            <span style="display:flex;gap:4px;align-items:center;flex-wrap:wrap;">
              ${/* TASK-1346 sweep — "is this billed?" (display) */ isTicketInvoiced(t) && t.invoiceNumber ? `<span class="badge badge-invoice-num">${escapeHtml(t.invoiceNumber)}</span>` : ''}
              ${isTicketInvoiced(t) ? '<span class="badge badge-invoiced"><span class="material-icons">check_circle</span>Invoiced</span>' : ''}
              <span class="badge badge-${t.status}">${capitalizeFirst(t.status)}</span>
              ${/* TASK-4738 — the third surface that builds its own ticket rows and
                   showed no delivery state. The dense PILL here, not the ribbon:
                   this is a picker list, and it reads the same
                   _ticketDeliveryState() as everywhere else. */ _ticketDeliveryBadgeHtml(t)}
            </span>
          </div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">
            ${empCount} employee${empCount !== 1 ? 's' : ''} · ${hrs.toFixed(1)} hrs · $${total.toFixed(2)}
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button class="btn btn-sm btn-primary" onclick="window.BillingModule.closeModal('billing-job');window.BillingModule.openReviewTicket('${t.id}')">
              <span class="material-icons">edit_note</span> Edit Ticket
            </button>
            ${/* TASK-1346 sweep — books-integrity gate (double-bill) */ !isTicketInvoiced(t)
              ? `<button class="btn btn-sm btn-primary" style="background:var(--status-success);" onclick="window.BillingModule.closeModal('billing-job');window.BillingModule.generateInvoice('${t.id}')">
                  <span class="material-icons">receipt</span> Invoice
                </button>`
              : `<button class="btn btn-sm btn-secondary" onclick="window.BillingModule.closeModal('billing-job');window.BillingModule.viewInvoice('${t.id}')">
                  <span class="material-icons">receipt</span> View Invoice
                </button>`
            }
          </div>
        </div>`;
    }
    html += `</div>`;
  }

  // TASK-1428 — slot the async words-check fills in after paint.
  if (tickets.length === 0) html += `<div id="billing-job-be-slot"></div>`;
  content.innerHTML = html;

  // Action buttons
  const actionsEl = document.getElementById('billing-job-actions');
  if (actionsEl) {
    let actionBtns = `<button class="btn btn-secondary" onclick="window.BillingModule.closeModal('billing-job')">Close</button>`;
    if (tickets.length === 0) {
      // TASK-1390 — thread the CLICKED day into the create, and say the target
      // day on the button BEFORE the click (UX Lead + Code Reviewer): the old
      // date-less call fell back to today's-else-LAST jobDay, so a ticket cut
      // from the Aug 3 cell landed on Aug 4 where this cell couldn't see it.
      const _ctLabel = dateStr ? `Create Ticket for ${new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Create Ticket';
      // TASK-1414 — when this job already has tickets a day or two either side,
      // creating another is more likely a duplicate than a gap. Demote the
      // button to secondary so it stops reading as the obvious next action.
      // Deliberately NOT removed: a second ticket on one job IS sometimes right
      // (split crews, a second load), and the person looking at the warning is
      // better placed to judge than this heuristic.
      actionBtns += ` <button class="btn ${nearby.length ? 'btn-secondary' : 'btn-primary'}" onclick="window.BillingModule.generateTicketFromJob('${jobId}', '${dateStr || ''}')" id="btn-generate-ticket"${nearby.length ? ' title="This job already has tickets within a few days — check those first"' : ''}>
        <span class="material-icons">add_circle</span> ${_ctLabel}
      </button>`;
    }
    actionsEl.innerHTML = actionBtns;
    // TASK-1428 — same words-check, scoped to the CLICKED day.
    if (tickets.length === 0) applyBilledElsewhereGuard(jobId, dateStr || null, tickets.map(t => t.id));
  }

  document.getElementById('modal-billing-job')?.classList.remove('hidden');
}

// ============================================
// GET NEXT TICKET NUMBER (shared with clock.js)
// ============================================
// TASK-1339 — THE canonical ticket-number allocator. clock.js calls this via
// window.BillingModule (never an ES import — ?v= busters would give it a second
// module instance, Pitfall #1). app.js's public ticket builder calls it the
// same way. TASK-1339 fixed two money bugs here: the catch used to swallow
// EVERY failure and return the 1001 seed (TITS is at ~100,400, so one transient
// blip would have restarted numbering into permanent collision), and the query
// had no limit(1) so it read the whole tickets collection per call.
//
// TASK-1354(b) — and now it is RACE-FREE.
//
// THE RACE IT REPLACES: allocation was `max(ticketNo) + 1`. A read-then-write
// max has no serialization, so two people saving at the same moment both read
// 100425 and both mint 100426. Nothing keys off ticketNo, so it corrupts no
// records — but two tickets that print the same "#100426" in front of a
// customer is a real problem, and it silently breaks every ticketNo join a
// report or a QuickBooks reconciliation makes (see TASK-1362 — the QB match is
// already held together with narrative shingles, it does not need duplicate
// numbers on top). TASK-1339 converged SIX client paths onto this function,
// which raised the odds rather than creating the bug.
//
// THE FIX: a per-tenant counter doc at `tenants/{tid}/_counters/ticketNo`,
// incremented inside runTransaction(). Firestore serializes transactions that
// touch the same document, so two simultaneous savers get 100426 and 100427,
// never 100426 twice. `_counters` is the existing slip-numbering precedent
// (utils/slipHelpers.js) and its firestore.rules block already exists;
// TASK-1354 adds the monotonic guard to it.
//
// 🔴 THIS COMMENT USED TO LIE, AND THE LIE IS THE BUG (TASK-4823).
// It said a losing transaction "is re-run against the newly committed value".
// That is what Firestore does on ABORTED — and it is NOT what happens here.
// The monotonic rule TASK-1354(b) added to this very counter
// (`request.resource.data.value > resource.data.value`) means the loser's
// commit is evaluated against the state the WINNER just wrote, so `next >
// current` is FALSE and Firestore answers **permission-denied**, which the Web
// SDK treats as FATAL and refuses to re-run. The transaction that was supposed
// to protect the sequence is the thing that killed the retry.
//
// MEASURED on `_e2e_ticketno`, real Firestore, real rules, the deployed bundle:
//   N=2  → 2 ok / 0 failed        N=5  → 1 ok / 4 permission-denied
//   N=10 → 4 ok / 6 denied        N=25 → 2 ok / 23 denied
//   N=50 → 2 ok / 48 denied
// Zero DUPLICATES in every run — the counter always did its job. What it never
// guaranteed is that a caller gets a number AT ALL. Two drivers clocking out in
// the same second, and one ticket has no number. The retry loop below
// (`_allocateCounterWithRetry`) closes that half. Same fix, same shape, same
// numbers as dispatch.js allocateJobNumber (TASK-4781).
//
// WHY THE SERVER TWIN (ticketGateway.js getNextTicketNumberServer) HAS NO SUCH
// LOOP, stated here because the two are deliberate mirrors and the next reader
// will ask: it runs on the Admin SDK, which bypasses firestore.rules entirely.
// There is no monotonic guard to deny it, so a lost race surfaces as ABORTED
// and the Admin SDK's own transaction retry re-runs it against the committed
// value — the behaviour THIS comment used to claim for the client. Adding a
// second retry layer there would stack on top of a working one.
//
// SEEDING — there is NO migration script and NO backfill to run. The counter is
// created lazily by the first allocation and seeded from the tenant's live max,
// so an existing tenant resumes exactly where its history left off (TITS: max
// 100425 → counter 100426 → the next ticket is #100426). A tenant with ZERO
// tickets seeds to TICKET_NO_SEED and mints #1001 — byte-identical to the seed
// the old allocator used for an empty sequence.
//
// WHY THE MAX-PROBE RUNS ON EVERY CALL, not just on seed: the counter is not
// the only thing minting numbers. (1) During a rollout, a browser still holding
// the PREVIOUS bundle runs the old read-max allocator and bumps no counter —
// trusting the counter alone would re-issue its numbers. (2) Admin-SDK scripts
// and backfills (TASK-1354(a) wrote #100409–#100425 this way) set ticketNo
// directly. (3) firestore.rules has never validated ticketNo on ticket create,
// so a raw-SDK write can plant any number. Raising the counter to
// `max(counter, observedMax)` every time means the sequence can only move
// forward and a stale, tampered, or rolled-back counter self-heals instead of
// handing out a number that is already on a ticket.
//
// BE PRECISE ABOUT WHAT THAT DOES AND DOESN'T GUARANTEE (COD Code Reviewer):
// the probe runs OUTSIDE the transaction — the Web SDK cannot run a query
// inside one, only document reads — so self-healing is EVENTUAL, not atomic. A
// number minted outside the counter that commits in the gap between our probe
// and our transaction is not seen by THIS allocation and can still collide
// once; the next allocation's probe sees it and the sequence corrects. So the
// honest guarantee is two-part:
//   • Between any two callers of THIS allocator — every client path and every
//     server path — collision is IMPOSSIBLE, not merely unlikely: both read
//     and write ONE document inside a transaction, and the counter is
//     monotonic, so no number can be issued twice. That is the race
//     TASK-1354(b) fixes. It is NOT a promise that every caller gets a number:
//     on the client the loser is DENIED, not re-run — see TASK-4823 above, and
//     `_allocateCounterWithRetry` is what makes the second promise true.
//   • Against a writer that bypasses the counter entirely (a browser still on
//     the previous bundle, an admin-SDK script) there is a one-allocation
//     window. Strictly better than the old allocator, where every concurrent
//     pair collided, but it is a rollout-window caveat, not immunity — which
//     is why the previous bundle should be considered drained before treating
//     duplicate ticket numbers as impossible.
const TICKET_NO_SEED = 1000; // → the first ticket on an empty tenant is #1001

// orderBy+limit(1) deliberately omits docs with no `ticketNo` — Firestore drops
// them from an ordered query, which is what we want: the unnumbered
// (ticketNoPending) tickets must never anchor the sequence.
async function _probeMaxTicketNo(tenant) {
  const snap = await getDocs(query(
    collection(db, 'tenants', tenant, 'tickets'),
    orderBy('ticketNo', 'desc'),
    limit(1)
  ));
  if (snap.docs.length === 0) return null;
  const maxNo = snap.docs[0].data().ticketNo;
  return Number.isFinite(maxNo) ? maxNo : null;
}

async function getNextTicketNumber(tenant) {
  // The probe is BEST-EFFORT once the counter exists (the counter is then the
  // authority) but REQUIRED to seed it — a blind seed would restart a tenant
  // sitting at ~100,400 back at #1001 and collide on the very first save. One
  // retry, since a numbering failure is nearly always a transient read and the
  // callers that can't safely fail (clock-in) shouldn't be traded for a blip.
  let observedMax = null;
  let probeErr = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      observedMax = await _probeMaxTicketNo(tenant);
      probeErr = null;
      break;
    } catch (e) {
      probeErr = e;
      if (attempt === 0) await new Promise(r => setTimeout(r, 400));
    }
  }

  // TASK-4823 — THE PROBE RUNS ONCE, OUTSIDE THE RETRY LOOP, deliberately. It is
  // a whole query round-trip and it exists to SEED and SELF-HEAL, not to resolve
  // contention: by the time we are retrying, the counter has already moved PAST
  // any max the probe could have seen. Keeping it out here is what makes each
  // retry cheap enough for the attempt budget to actually drain.
  return await _allocateCounterWithRetry('ticket number',
    () => _getNextTicketNumberTxn(tenant, observedMax, probeErr));
}

async function _getNextTicketNumberTxn(tenant, observedMax, probeErr) {
  const counterRef = doc(db, 'tenants', tenant, '_counters', 'ticketNo');
  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? snap.data().value : null;
    const haveCounter = Number.isFinite(current);

    if (!haveCounter && probeErr) {
      // No counter to read AND no max to seed from — there is nothing safe to
      // return. Re-thrown as a plain Error so the SDK treats it as fatal rather
      // than a retryable transaction error.
      throw new Error(
        'Could not allocate a ticket number: ' + (probeErr?.message || probeErr)
      );
    }

    let base;
    if (haveCounter || observedMax != null) {
      base = Math.max(
        haveCounter ? current : Number.NEGATIVE_INFINITY,
        observedMax != null ? observedMax : Number.NEGATIVE_INFINITY
      );
    } else {
      // Genuinely empty sequence — no counter, no numbered ticket.
      base = TICKET_NO_SEED;
    }

    const next = base + 1;
    tx.set(counterRef, {
      value: next,
      updatedAt: Timestamp.now(),
      // seededFrom is forensics: it answers "did this counter start from the
      // live max or from the empty-tenant seed" without re-deriving it later.
      ...(snap.exists() ? {} : { createdAt: Timestamp.now(), seededFrom: base })
    }, { merge: true });
    return next;
  });
}

// ============================================
// TASK-842-A3 — RENTAL TICKET ROUTING (pure)
// ============================================
// Decides how generateTicketFromJob routes a job's ticket generation. PURE —
// no Firestore/DOM/AppState — so a node harness can prove the branch logic
// (the money math itself lives server-side in functions/rentalBilling.js and
// the generateRentalTicketSlices transaction; the client only sends intent).
//
// Classification mirrors functions/rentalBilling.js classifyAsset EXACTLY:
// consumable FIRST (a consumable never enters the rental-period engine — it
// never returns, so a span engine would bill it "still out" forever), then
// rental (both live shapes: quantityType==='rental' OR isRental===true — the
// same TASK-730a matcher the legacy equipment seeding below uses), else standard.
function classifyTicketAsset(asset) {
  if (!asset) return 'standard';
  if (asset.consumable === true) return 'consumable';
  if (asset.quantityType === 'rental' || asset.isRental === true) return 'rental';
  return 'standard';
}

// jobDays: ALL of this job's jobDay docs — a rental deployed on day 1 must still
//   route a ticket cut on day 30, so every day's asset refs are scanned, not just
//   the target day's; deploy/return direction doesn't matter (a RETURN day needs
//   the engine to bill the closing slice).
// rosterAssets: roster `assets` docs. Trucks/trailers can never be rental-class —
//   the engine reads only the assets collection — so only 'asset'-type refs route.
// crewData/equipmentData: the target-day seeds the legacy path would use.
function planRentalTicketRouting(jobDays, rosterAssets, crewData, equipmentData) {
  const assets = Array.isArray(rosterAssets) ? rosterAssets : [];
  // Same dual lookup the legacy equipment resolver uses (GAS-imported name refs)
  const resolveAsset = (ref) => _resolveFleetRef(ref, assets); // TASK-4529 — id pass exhausted before name/partNumber
  const rentalAssetIds = new Set();
  for (const jd of (Array.isArray(jobDays) ? jobDays : [])) {
    for (const ref of ((jd && jd.assets) || [])) {
      const asset = resolveAsset(ref);
      if (classifyTicketAsset(asset) === 'rental') rentalAssetIds.add(asset.id);
    }
  }
  // Legacy equipment = the target day's seeds minus the engine-managed rentals
  // (leaving them in would double-bill: once as a TASK-730a qty line on the
  // legacy ticket, once as a rental slice). Consumables + plain assets stay.
  const legacyEquipment = (Array.isArray(equipmentData) ? equipmentData : []).filter(e => {
    if (!e || e.type !== 'asset') return true;
    const asset = resolveAsset(e.ref);
    return !(asset && rentalAssetIds.has(asset.id));
  });
  return {
    useRentalEngine: rentalAssetIds.size > 0,
    rentalAssetIds,
    legacyEquipment,
    // Mixed job → the legacy path still runs for the non-rental content. A job
    // with NOTHING legacy to bill (no crew, no non-rental equipment) is
    // rental-only: the engine's rental draft ticket IS the ticket.
    hasLegacyContent: ((crewData && crewData.length) || 0) > 0 || legacyEquipment.length > 0,
  };
}

// ============================================
// GENERATE TICKET FROM JOB (AI E-Ticket)
// ============================================
async function generateTicketFromJob(jobId, dateStr) {
  const job = BillingState.jobs.find(j => j.id === jobId);
  if (!job) {
    window.showToast?.('Job not found', 'error');
    return;
  }

  // TASK-1390 — dateStr ('YYYY-MM-DD') is the CLICKED calendar day. When
  // supplied, it is authoritative for the ticket's workDate/jobDay: the old
  // today's-else-LAST-jobDay fallback dated an Aug 3 click's ticket Aug 4
  // (invoiced $435 on the wrong day, invisible to the Aug 3 cell → a second
  // ticket 26s later). The fallback below survives ONLY for date-less callers
  // (the AR/AP job modal) — unchanged behavior there.
  dateStr = (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) ? dateStr : null;

  const btn = document.getElementById('btn-generate-ticket');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Loading...';
  }

  // Collect job data for the AI
  const relatedJobDays = BillingState.jobDays
    .filter(jd => jd.jobId === jobId)
    .sort((a, b) => { const da = typeof a.date === 'string' ? a.date : a.date?.toDate ? a.date.toDate().toISOString().slice(0,10) : ''; const db = typeof b.date === 'string' ? b.date : b.date?.toDate ? b.date.toDate().toISOString().slice(0,10) : ''; return da.localeCompare(db); });

  // Target jobDay: the clicked day exactly when we have one (TASK-1390 — no
  // fallback to another day's crew), else today's-else-last (legacy callers).
  const todayStr = formatDateISO(new Date());
  const _jdDateOf = (jd) => typeof jd.date === 'string' ? jd.date.slice(0, 10) : jd.date?.toDate ? formatDateISO(jd.date.toDate()) : formatDateISO(new Date(jd.date));
  const targetJobDay = dateStr
    ? relatedJobDays.find(jd => _jdDateOf(jd) === dateStr)
    : (relatedJobDays.find(jd => _jdDateOf(jd) === todayStr) || relatedJobDays[relatedJobDays.length - 1]);

  // Resolve crew names and roles
  let crewData = [];
  if (targetJobDay) {
    const allRefs = [
      ...(targetJobDay.drivers || []).map(ref => ({ ref, defaultRole: 'CDL' })),
      ...(targetJobDay.helpers || []).map(ref => ({ ref, defaultRole: 'NON_CDL' }))
    ];
    for (const { ref: personRef, defaultRole } of allRefs) {
      const person = BillingState.people.find(p => p.id === personRef || p.name === personRef);
      if (person) {
        const isCdl = person.cdlLicensed === true ||
          /cdl|driver/i.test(person.crewType || person.role || '') ||
          person.tags?.includes('CDL') || defaultRole === 'CDL';
        crewData.push({
          personId: person.id,
          personName: person.name,
          role: isCdl ? 'CDL' : 'NON_CDL'
        });
      }
    }
  }

  // Resolve equipment (let — the rental-engine branch below may filter it)
  let equipmentData = [];
  const seenEquipRefs = new Set();
  if (targetJobDay) {
    for (const ref of (targetJobDay.trucks || [])) {
      if (!seenEquipRefs.has(ref)) { seenEquipRefs.add(ref); equipmentData.push({ type: 'truck', ref, name: ref }); }
    }
    for (const ref of (targetJobDay.trailers || [])) {
      if (!seenEquipRefs.has(ref)) { seenEquipRefs.add(ref); equipmentData.push({ type: 'trailer', ref, name: ref }); }
    }
    for (const ref of (targetJobDay.assets || [])) {
      if (!seenEquipRefs.has(ref)) { seenEquipRefs.add(ref); equipmentData.push({ type: 'asset', ref, name: ref }); }
    }
  }

  const customer = job.customerId ? BillingState.customers.find(c => c.id === job.customerId) : null;

  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;

  // TASK-1390 — 🔴 COD-MANDATED double-bill mitigation (Adversary + Domain):
  // before seeding roster crew onto a new ticket, read this job+day's REAL
  // clockEntries and drop any person whose hours already sit on a ticket
  // (manual entries now auto-attach server-side; the jobDay roster knows
  // nothing about that). Without this, the roster-seeded crew line and the
  // entry's attached line bill the same person's day twice. Server half of the
  // guard: the TASK-1027 reconciler's personId-adopt dedup (it claims an
  // unclaimed same-person overlapping line instead of minting a second one).
  const _ticketDayStr = dateStr || (targetJobDay ? _jdDateOf(targetJobDay) : todayStr);
  if (crewData.length > 0) {
    try {
      const _ceSnap = await getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId)));
      const _billedPersonIds = new Set();
      for (const d of _ceSnap.docs) {
        const e = d.data();
        if (!e.ticketId || !e.personId || e.status === 'deleted') continue;
        const cin = e.clockIn?.toDate ? e.clockIn.toDate() : (e.clockIn ? new Date(e.clockIn) : null);
        if (cin && formatDateISO(cin) === _ticketDayStr) _billedPersonIds.add(e.personId);
      }
      const _excluded = crewData.filter(c => _billedPersonIds.has(c.personId));
      if (_excluded.length > 0) {
        crewData = crewData.filter(c => !_billedPersonIds.has(c.personId));
        window.showToast?.(`${_excluded.map(c => c.personName).join(', ')} already ${_excluded.length === 1 ? 'has' : 'have'} hours on a ticket for this day — not re-added.`, 'info');
      }
    } catch (e) {
      // Query failure → proceed without the exclusion (status-quo seeding);
      // the server-side adopt dedup still prevents a duplicate line.
      console.warn('[Billing] TASK-1390 billed-crew check failed (seeding without exclusion):', e);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // TASK-842-A3 — RENTAL-ENGINE ROUTING. Rental-class assets bill as a PERIOD
  // (deploy→return span swept into slices by the server-side
  // generateRentalTicketSlices transaction), NOT as per-day equipment lines.
  // This branch decides the route BEFORE the AI call and BEFORE the
  // workDate+jobId dedup below, and never mutates that legacy path: a job with
  // NO rental assets has useRentalEngine=false and skips straight through with
  // zero behavior change. A MIXED job (rental + consumable/plain/labor) takes
  // BOTH branches: the engine runs FIRST (so the dedup's open-existing
  // early-return can't silently skip it — COD Edge #1), then the legacy path
  // continues for the non-rental content with the engine-managed assets
  // excluded from equipment seeding. Consumable one-time lines stay on the
  // legacy path untouched (they are 842-A2b). Option A trust model: everything
  // lands on DRAFT tickets the biller reviews before invoicing — the client
  // sends intent only; all money math + writes happen in the server transaction.
  // ══════════════════════════════════════════════════════════════════════════
  await loadReviewEquipment(); // idempotent cache — the legacy path's own call below stays a no-op

  // TASK-4741 — the moment a biller first tries to turn this day into money is
  // the moment to say the day's equipment doesn't exist. All 31 unresolvable
  // refs in live data sit on jobDays (28× "BJ" on trippin-trucking, the
  // TASK-4724 case), and every one of those 31 days produced NO TICKET AT ALL —
  // which is exactly why nothing downstream ever caught them: the truck did not
  // arrive as a wrong line, it silently dropped off the invoice. The existing
  // TASK-1674 S4 stamp fires only for refs that DID become lines, so on the real
  // population its coverage is zero.
  //
  // REPORT, NEVER REPAIR (row 🔴, and the TASK-4724 lesson): fixing "BJ" needed
  // a human to say it meant Blue Jay, and the honest answer turned out to be
  // "don't bother, it's invoiced." So this asks, it does not guess — and the
  // biller can proceed, because a day whose truck ref is stale is still a day
  // that has to be billed.
  {
    const _fr4741 = await _reviewFullRoster();
    const _dayAudit = auditEquipmentRefs(
      _equipmentRefsFromJobDays(targetJobDay ? [targetJobDay] : []),
      _fr4741.roster, _fr4741.loaded
    );
    if (_dayAudit.status === 'ok' && _dayAudit.unresolved.length > 0) {
      const _n = _dayAudit.unresolved.length;
      // Quote the raw reference — never invent a unit name we could not resolve.
      const _list = _dayAudit.unresolved.map(u => `"${u.ref}"`).join(' · ');
      // 🔴 COD Code Reviewer caught this copy telling the biller the opposite of
      // what the code does. Generation writes the raw reference straight onto
      // the line (`assetId: asset?.id || equip.ref`), so the line IS created —
      // it just carries a name nobody can look up and a blank vehicleType, which
      // means the price book cannot match it by type and its rate is a fallback
      // guess. Say exactly that; a warning that misdescribes the outcome is
      // worse than no warning.
      const _msg = `This job day lists ${_n} piece${_n === 1 ? '' : 's'} of equipment that ${_n === 1 ? "isn't" : "aren't"} in your fleet: ${_list}. ` +
        `${_n === 1 ? 'It' : 'They'} will still go on the ticket, but under that same name — so the customer sees ${_n === 1 ? 'a unit' : 'units'} nobody can look up, and the price book can't match ${_n === 1 ? 'its' : 'their'} rate by vehicle type. ` +
        `Fix the job day first if that's a typo or a retired unit.`;
      if (typeof window.showConfirm === 'function') {
        const _go = await window.showConfirm(
          _n === 1 ? "A vehicle on this day isn't in your fleet" : "Vehicles on this day aren't in your fleet",
          _msg,
          { confirmText: 'Create ticket anyway', cancelText: 'Cancel', icon: 'warning', iconColor: '#F59E0B' }
        );
        if (!_go) return;
      } else {
        window.showToast?.(_msg, 'warning');
      }
    } else if (_dayAudit.status !== 'ok' && (targetJobDay?.trucks?.length || targetJobDay?.trailers?.length || targetJobDay?.assets?.length)) {
      // Could not check != clean. Say so; do not block the ticket over it.
      window.showToast?.("Couldn't load your fleet list, so this day's equipment wasn't checked against it.", 'warning');
    }
  }

  const rentalPlan = planRentalTicketRouting(relatedJobDays, ReviewState.assets || [], crewData, equipmentData);
  if (rentalPlan.useRentalEngine) {
    let engine = null;
    let engineErr = null;
    try {
      // workDateLocal = the same jobDay-derived local date the legacy ticket
      // uses (display-only label on the rental draft; validated server-side) —
      // NOT unconditionally "today", so a rental ticket cut for a past job day
      // lands on the same billing-calendar day the legacy path would use (COD
      // Code Reviewer). Same normalization as the TASK-1019 block below.
      const _reRaw = targetJobDay?.date || dateStr || todayStr; // TASK-1390 — clicked day wins over today
      const _reDateStr = typeof _reRaw === 'string' ? _reRaw.slice(0, 10)
        : _reRaw?.toDate ? formatDateISO(_reRaw.toDate())
        : _reRaw instanceof Date ? formatDateISO(_reRaw) : todayStr;
      const genSlices = httpsCallable(functions, 'generateRentalTicketSlices', { timeout: 60000 });
      engine = (await genSlices({ tenantId: tenant, jobId, workDateLocal: _reDateStr }))?.data || null;
      if (!engine) engineErr = new Error('empty rental engine response');
    } catch (err) {
      engineErr = err;
      console.warn('[Billing] Rental slice generation failed:', err);
    }
    if (engine?.note === 'no rental assets on this job') {
      // Server disagrees (it reads the authoritative assetTransactions deploy
      // ledger): the flagged assets were never deploy-processed, so the engine
      // will never bill them. Fall through to the FULL legacy path (no
      // exclusion) — exactly the pre-A3 behavior for these assets.
    } else {
      // The engine owns the rental assets from here (it swept them, has them
      // watermarked, or errored and will sweep them on a retry) — exclude them
      // from legacy equipment seeding so they can't double-bill.
      equipmentData = rentalPlan.legacyEquipment;
      if (engine && engine.slices > 0) {
        const unresolvedN = (engine.unresolvedRateAssets || []).length;
        window.showToast?.(
          `Rental billing: ${engine.slices} line(s) swept to the rental ticket` +
          (unresolvedN ? ` — ${unresolvedN} with no Price Book rate (billed $0, set the rate on the draft)` : ''),
          unresolvedN ? 'warning' : 'success');
      }
      if (!rentalPlan.hasLegacyContent) {
        // Rental-only job — no crew, no non-rental equipment. The rental draft
        // ticket IS the ticket; don't create an empty legacy shell beside it.
        closeModal('billing-job');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
        if (engineErr) {
          const busy = /already generating/i.test(engineErr?.message || '') || engineErr?.code === 'functions/aborted';
          window.showToast?.(busy
            ? 'Rental billing is already generating for this job — try again in a moment.'
            : 'Rental billing failed — no ticket was created. Try again.', 'error');
          return;
        }
        if (engine?.ticketId) {
          await openReviewTicket(engine.ticketId);
        } else {
          // 'nothing new to bill' — open the job's existing draft rental ticket when we have it.
          const existingRental = (BillingState.tickets || []).find(t =>
            // TASK-1346 sweep — "is this billed?": we want the OPEN rental draft,
            // never one that already carries an invoice number.
            t.jobId === jobId && t.rentalTicket === true && !isTicketInvoiced(t) && t.status !== 'deleted' && t.status !== 'archived');
          if (existingRental) await openReviewTicket(existingRental.id);
          else window.showToast?.('Rental billing is up to date — nothing new to bill', 'info');
        }
        return;
      }
      if (engineErr) {
        // Mixed job, engine failed: continue the legacy ticket WITHOUT the
        // rental assets (seeding them now would double-bill once the engine
        // succeeds later) and tell the biller the rental lines are missing.
        window.showToast?.('Rental billing failed — rental lines were NOT added. Generate again to retry.', 'warning');
      }
    }
  }

  // TASK-1019 — normalize the target date to a plain YYYY-MM-DD string so the
  // server can match it against real clockEntries (a Firestore Timestamp would
  // JSON-serialize to {seconds,nanoseconds} garbage in the AI payload).
  const _jdRawDate = targetJobDay?.date || dateStr || todayStr; // TASK-1390 — clicked day wins over today
  const _jdDateStr = typeof _jdRawDate === 'string' ? _jdRawDate.slice(0, 10)
    : _jdRawDate?.toDate ? formatDateISO(_jdRawDate.toDate())
    : _jdRawDate instanceof Date ? formatDateISO(_jdRawDate) : todayStr;

  const jobData = {
    jobTitle: job.title || '',
    jobDescription: job.description || '',
    customerName: customer?.name || job.customer || '',
    customerId: job.customerId || '',
    location: job.location || job.address || '',
    poNumber: job.poNumber || '',
    vendorNumber: job.vendorNumber || '',
    wellName: job.wellName || '',
    wellNumber: job.wellNumber || '',
    startTime: targetJobDay?.startTime || job.defaultStart || job.startTime || '',
    endTime: targetJobDay?.endTime || '',
    date: _jdDateStr,
    crew: crewData,
    equipment: equipmentData
  };

  let ticketData = null;

  // Try AI generation first
  try {
    let _gtClientTz = '';
    try { _gtClientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { /* server defaults */ }
    const idToken = await window.AppState?.user?.getIdToken?.();
    if (idToken) {
      const response = await fetch(
        'https://us-central1-dispatch-deck-be88c.cloudfunctions.net/generateTicket',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          // TASK-1019 — thread the jobId so the SERVER queries this job's real
          // clockEntries for time data (the AI must never invent billable
          // times; client jobData is never trusted for times). Timezone is
          // presentation-only so clock timestamps render as local HH:MM.
          body: JSON.stringify({ data: { jobData, jobId, clientTimeZone: _gtClientTz } })
        }
      );
      const result = await response.json();
      if (result.ok && result.ticket) {
        ticketData = result.ticket;
        // Never use AI-generated work notes — user enters their own
        ticketData.workPerformed = '';
        window.ddLog?.('[Billing] AI generated ticket data:', ticketData);
      } else {
        console.warn('[Billing] AI generation failed, falling back to direct mapping:', result.error || result);
      }
    }
  } catch (aiErr) {
    console.warn('[Billing] AI generation error, falling back to direct mapping:', aiErr);
  }

  // Fallback: direct field mapping (no AI)
  if (!ticketData) {
    ticketData = {
      customerName: jobData.customerName,
      poNumber: jobData.poNumber,
      vendorNumber: jobData.vendorNumber,
      location: jobData.location,
      wellName: jobData.wellName,
      wellNumber: jobData.wellNumber,
      workPerformed: '',  // User enters notes — never auto-generate
      employees: crewData,
      timeEntries: jobData.startTime ? [{
        timeFrom: jobData.startTime,
        timeTo: jobData.endTime || '',
        description: jobData.jobTitle
      }] : [],
      materials: []
    };
  }

  // Create a draft ticket in Firestore, then open the full Review Ticket modal
  closeModal('billing-job');

  // (tenant is resolved above, before the TASK-842-A3 rental-engine branch)
  const ticketCustomer = customer || BillingState.customers.find(c => c.id === job.customerId);
  const jobDate = targetJobDay?.date || dateStr || todayStr; // TASK-1390 — the ticket's date/workDate follow the clicked day
  // Normalize date: could be a string ('2026-02-28'), Firestore Timestamp, or Date object
  let parsedDate;
  if (typeof jobDate === 'string') {
    parsedDate = new Date(jobDate + 'T00:00:00');
  } else if (jobDate?.toDate) {
    parsedDate = jobDate.toDate();
  } else if (jobDate instanceof Date) {
    parsedDate = jobDate;
  } else {
    parsedDate = new Date();
  }
  const dateTs = Timestamp.fromDate(parsedDate);
  const workDate = `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`; // YYYY-MM-DD local for dedup matching with clock.js

  // TASK-1433 — the tiers below are UX pre-screening only. The AUTHORITATIVE
  // same-job-same-day check now runs inside the createTicketChecked Cloud
  // Function's Firestore transaction (atomically with the create), because this
  // client read-then-write was a TOCTOU race: two tabs could both pass the read
  // before either write landed. These two locals carry the operator's explicit
  // duplicate decision through to that server transaction.
  let _dupOverrideConfirmed = false;
  let _dupSeenIds = [];

  // Tiered dedup: check for existing ticket, excluding splits/invoiced/archived
  try {
    // TASK-1409 — the status filter used to be `in ['draft','ready']`, which meant
    // ARCHIVED and INVOICED tickets never entered the result set at all. Archiving a
    // ticket therefore made it invisible to the duplicate check, so pressing Generate
    // Ticket again silently produced a second ticket for a job-day that was already
    // billed (TITS #100398 → #100436 → #100437). The status filter is gone; every
    // ticket for the day is fetched and the tiers below decide what to do. A
    // single-field where() needs no composite index and a day's ticket count is small.
    const dedupQuery = query(
      collection(db, 'tenants', tenant, 'tickets'),
      where('workDate', '==', workDate)
    );
    const dedupSnap = await getDocs(dedupQuery);
    // TASK-1398 — the LAST structural blind spot in this check. Both halves of
    // the dedup (this query and createTicketChecked's transaction) key on
    // `workDate`, so a ticket that has no workDate field is invisible to both
    // and Generate Ticket mints a silent second for a day that is already
    // billed. Every current create path stamps workDate, but pre-workDate rows
    // are still live: 5 non-deleted on TITS (#100410 and #1069-#1072, all
    // archived — i.e. all already billed, the exact double-bill shape).
    //
    // Recovered by the only other key those rows carry, the `date` Timestamp.
    // Single-field range → no composite index, and it reads one day's tickets.
    //
    // Rows WITH a workDate are dropped from this second set on purpose. Folding
    // them in would key the dedup on `date` as well as `workDate`, and those two
    // legitimately differ — a ticket built on the 4th for work done on the 3rd
    // (TASK-1390) carries date=8/4, workDate=8/3. Generating for 8/4 would then
    // flag it as a duplicate of work it does not bill: a false alarm on correct
    // work, which is how a warning gets trained away. Restricting to workDate-less
    // rows adds exactly the invisible ones and cannot false-positive.
    let _genLegacyDocs = [];
    try {
      const _dayStart = new Date(parsedDate); _dayStart.setHours(0, 0, 0, 0);
      const _dayEnd = new Date(_dayStart); _dayEnd.setDate(_dayEnd.getDate() + 1);
      const _legacySnap = await getDocs(query(
        collection(db, 'tenants', tenant, 'tickets'),
        where('date', '>=', Timestamp.fromDate(_dayStart)),
        where('date', '<', Timestamp.fromDate(_dayEnd))
      ));
      _genLegacyDocs = _legacySnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => !t.workDate);
    } catch (e) {
      // Its own try: a failure here must not take down the primary workDate
      // dedup with it (the outer catch fails OPEN and creates the ticket).
      console.warn('[Billing] TASK-1398 legacy workDate-less dedup scan failed:', e?.message);
    }
    // TASK-842-A3 (COD Blast ❌): exclude rental-engine tickets from the legacy
    // dedup candidates. The engine's draft ticket shares jobId+customerId+workDate
    // with a same-day legacy ticket, so without this a MIXED job's dedup would
    // "open existing" on the rental ticket and silently drop the crew/consumable
    // ticket. Inert pre-A3: rentalTicket docs only exist once the engine has run.
    // TASK-1409 / TASK-1398 -- the bucketing, the tiers and every fact the
    // confirm quotes now live in classifyJobDayTickets() (PURE, see its header
    // near isTicketInvoiced). Nothing from here down DECIDES anything; it only
    // does I/O. tests/task1398-generate-ticket-dedup.unit.cjs asserts every
    // branch of that decision -- draft twin, INVOICED twin, ARCHIVED twin, no
    // twin, and a twin on a different job+day -- without Firestore.
    const _genDecision = classifyJobDayTickets(
      dedupSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      { jobId, customerId: job.customerId || null },
      _genLegacyDocs
    );
    if (_genDecision.action === 'open') {
      // Ticket already exists -- open it for review instead of creating a duplicate
      showToast('Ticket already exists for this job & date — opening it', 'info');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
      openReviewTicket(_genDecision.existing.id);
      return;
    }

    // TASK-1409 -- an already-BILLED ticket for this job-day does NOT block. Measured
    // on TITS: of the 15 job-days carrying more than one live ticket, 10 have real
    // money on BOTH with different amounts (separate loads/hitches on the same job
    // the same day is normal oilfield billing), so refusing outright would break
    // legitimate work. Instead: name the existing ticket, its invoice and its amount,
    // and let the operator decide with the facts in front of them. Warn and demote,
    // never hard-block. The override is recorded to jobAuditLog (server-side).
    const _dupBilled = _genDecision.action === 'confirm' ? _genDecision.dupBilled : null;
    if (_dupBilled) {
      const _twinNote = _genDecision.twinNotes;
      const _dupInv = _genDecision.invoiceNo;
      const _dupAmt = _genDecision.amount;
      const _dupIsBilled = _genDecision.isBilled;
      const _dupState = _genDecision.state;
      const _dupLabel = _genDecision.label;
      // COD Code-Reviewer + Independent ⚠️ — "the dialog helper is missing" and "the
      // operator pressed Cancel" are different answers and must not collapse into the
      // same falsy value. `window.showConfirm?.()` yields undefined when the helper
      // isn't loaded, which would have silently returned and stranded a ticket the
      // operator actually wanted, with nothing on screen explaining why. If we cannot
      // ASK, we must not block billing — warn loudly and let the ticket through.
      // Only a real, deliberate Cancel stops it.
      if (typeof window.showConfirm !== 'function') {
        // COD Skeptic 🔴 — fail CLOSED here, not open. A missing showConfirm means
        // app.js didn't load properly; creating a billable duplicate the operator
        // never consented to is worse than making them reload. This is NOT the
        // silent strand Catch 1 was about — the toast names the conflicting ticket
        // and says exactly what to do, and the button is re-enabled.
        console.warn('[Billing] showConfirm unavailable — refusing to create a duplicate unconfirmed');
        showToast(`${_dupLabel} already ${_dupIsBilled ? 'bills' : 'covers'} this job on ${workDate}. Couldn't open the confirmation — reload the page and try again.`, 'error');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
        return;
      } else {
        const proceed = await window.showConfirm(
          _dupIsBilled ? 'This job-day is already billed' : 'This job-day already has a ticket',
          `${_dupBilled.ticketNo ? `Ticket #${_dupBilled.ticketNo}` : 'An earlier ticket'} for this job on ${workDate} is ${_dupState} for ${formatCurrency(_dupAmt)}. ` +
          `Creating another ticket will bill this day twice unless it covers different work.` +
          (_twinNote.length ? ` Same customer, same day, other jobs: ${_twinNote.join(', ')}.` : ''),
          // Explicit labels (COD UX ⚠️): "Cancel" alone is ambiguous here — it reads
          // as "cancel the warning" as easily as "don't make the ticket".
          { confirmText: 'Create another ticket', cancelText: "Go back — don't create", icon: 'warning', iconColor: '#F59E0B' }
        );
        if (!proceed) {
          if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
          return;
        }
      }
      // TASK-1433 — the operator explicitly chose a second ticket; carry that
      // decision (and the ticket they were shown) to the server-side create so
      // the transaction creates it deliberately instead of re-prompting.
      // The old client-side TICKET_DUPLICATE_DAY_OVERRIDE jobAuditLog write that
      // lived here moved into createTicketChecked (COD Code Reviewer: two rows
      // with the same action for one decision double-counts overrides). The
      // server receipt is strictly richer — it carries the CREATED ticket's id
      // plus every duplicate live at create-time, and it can't fire for a
      // create that never landed.
      _dupOverrideConfirmed = true;
      _dupSeenIds = [_dupBilled.id];
    }
  } catch (e) {
    console.warn('[Billing] Dedup check failed, creating new ticket:', e);
  }

  const _genTaxSettings = window.AppState?.settings || {};
  // TASK-1339 — the allocator now THROWS rather than silently reseeding at 1001.
  // This is a dispatcher-initiated action with a button to re-press, so fail
  // visibly instead of minting a colliding number.
  let ticketNo;
  try {
    ticketNo = await getNextTicketNumber(tenant);
  } catch (err) {
    console.error('[Billing] Ticket number allocation failed:', err);
    window.logError?.(err, { module: 'billing', operation: 'TICKET_NUMBER_ALLOC', jobId });
    showToast('Could not assign a ticket number — check your connection and try again.', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
    return;
  }
  const ticketDoc = {
    ticketNo,
    date: dateTs,
    workDate,
    status: 'draft',
    customerId: job.customerId || null,
    customerName: ticketCustomer?.name || job.customer || '',
    jobId: jobId,
    jobName: job.title || '',
    poNumber: ticketData.poNumber || job.poNumber || '',
    vendorNumber: ticketData.vendorNumber || job.vendorNumber || '',
    wellName: ticketData.wellName || job.wellName || '',
    wellNumber: ticketData.wellNumber || job.wellNumber || '',
    jobType: _ticketJobTypeFromJob(job), // TASK-9390 — label snapshot (null for the default type)
    jobTypeCode: _ticketJobTypeCodeFromJob(job), // TASK-9390 — the slug, so a job move judges by code
    location: ticketData.location || job.location || job.address || '',
    // TASK-9646 S4a — no `jobNotes` copy: crew sessions read every ticket key;
    // the ticket views show the job's own note (_ticketDispatchNotes).
    workPerformed: ticketData.workPerformed || '',
    employees: crewData,
    employeeCount: crewData.length,
    timeEntries: ticketData.timeEntries || [],
    materials: ticketData.materials || [],
    grandTotal: 0,
    totalHours: 0,
    source: 'job_generate',
    originalJobId: jobId || null,
    originalCustomerId: job.customerId || null,
    originalWorkDate: workDate,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy: window.AppState?.user?.uid || 'unknown'
  };
  if (_genTaxSettings.taxEnabled) {
    ticketDoc.taxEnabled = true;
    ticketDoc.taxRate = _genTaxSettings.defaultTaxRate || 0;
    ticketDoc.taxCategories = _genTaxSettings.defaultTaxCategories || { labor: true, equipment: true, permits: true, fieldItems: true, misc: true, downtime: true, standby: true };
    ticketDoc.subtotal = 0;
    ticketDoc.taxableSubtotal = 0;
    ticketDoc.taxAmount = 0;
  }

  try {
    // TASK-1433 — the create is TRANSACTIONAL server-side now (createTicketChecked
    // runs the same-job-same-day check and the create in one Firestore
    // transaction), closing the TOCTOU race the client tiers above can't close.
    // Server owns createdAt/updatedAt (serverTimestamp) + createdBy (auth uid);
    // `date` travels as millis because a Timestamp doesn't survive callable JSON.
    const _ctcPayload = { ...ticketDoc };
    delete _ctcPayload.createdAt;
    delete _ctcPayload.updatedAt;
    delete _ctcPayload.createdBy;
    delete _ctcPayload.date;
    _ctcPayload.dateMs = dateTs.toMillis();
    const _ctcFn = httpsCallable(functions, 'createTicketChecked', { timeout: 30000 });
    let _ctcRes = (await _ctcFn({
      tenantId: tenant,
      ticket: _ctcPayload,
      allowDuplicate: _dupOverrideConfirmed,
      seenDuplicateIds: _dupSeenIds,
    }))?.data;
    if (_ctcRes && _ctcRes.ok === false && Array.isArray(_ctcRes.duplicates)) {
      // The race actually happened: a ticket for this job-day landed between
      // the client pre-check above and the server transaction. Same house
      // dialog as the pre-check; only a real, deliberate Cancel stops it.
      const _rd = _ctcRes.duplicates[0] || {};
      if (typeof window.showConfirm !== 'function') {
        // Same fail-CLOSED stance as the pre-check (COD Skeptic on TASK-1409):
        // never create an unconfirmed billable duplicate.
        showToast(`Ticket ${_rd.ticketNo ? '#' + _rd.ticketNo : ''} already exists for this job on ${workDate}. Couldn't open the confirmation — reload the page and try again.`, 'error');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
        return;
      }
      const _rdAmt = Number(_rd.grandTotal || 0);
      const _proceedRace = await window.showConfirm(
        'A ticket for this job-day just appeared',
        `Ticket ${_rd.ticketNo ? '#' + _rd.ticketNo : ''} for this job on ${workDate} was created moments ago` +
        `${_rd.invoiceNo ? ` and is invoiced (${_rd.invoiceNo})` : (_rd.status ? ` (${_rd.status})` : '')}` +
        `${_rdAmt ? ` for ${formatCurrency(_rdAmt)}` : ''}. ` +
        `Creating another ticket will bill this day twice unless it covers different work.`,
        { confirmText: 'Create another ticket', cancelText: "Go back — don't create", icon: 'warning', iconColor: '#F59E0B' }
      );
      if (!_proceedRace) {
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">receipt</span> Generate Ticket'; }
        return;
      }
      _ctcRes = (await _ctcFn({
        tenantId: tenant,
        ticket: _ctcPayload,
        allowDuplicate: true,
        seenDuplicateIds: _ctcRes.duplicates.map(d => d.id),
      }))?.data;
    }
    if (!_ctcRes?.ok || !_ctcRes.id) {
      throw new Error('createTicketChecked returned no ticket id');
    }
    const docRef = { id: _ctcRes.id };
    const newTicket = { id: docRef.id, ...ticketDoc };
    BillingState.tickets.push(newTicket);

    // Create EMPLOYEE ticketLines from crew data with rate matching
    if (crewData.length > 0) {
      // Pre-load contract rates for rate matching
      let contractLineItems = [];
      if (job.customerId) {
        try {
          const contractsSnap = await getDocs(
            query(collection(db, 'tenants', tenant, 'customers', job.customerId, 'contracts'), where('status', '==', 'Active'))
          );
          for (const cDoc of contractsSnap.docs) {
            const liSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', job.customerId, 'contracts', cDoc.id, 'lineItems'));
            liSnap.docs.forEach(d => contractLineItems.push({ id: d.id, ...d.data() }));
          }
        } catch (e) { console.warn('[Billing] Could not load contract rates for ticket gen:', e); }
      }

      // Cache for review rate lookup
      if (contractLineItems.length > 0 && job.customerId) {
        ReviewState.contractRateCache[job.customerId] = { lineItems: contractLineItems };
      }

      // Load lineItemSelections from the jobDay for dispatcher rate picks
      const lineItemSelections = targetJobDay?.lineItemSelections || {};

      // Parse job start/end times for auto-populating line fromTime/toTime
      const _genStartTime = jobData.startTime || '';
      const _genEndTime = jobData.endTime || '';
      let _genFromTs = null, _genToTs = null;
      // clock-dst-all — the job's HH:MM is COMPANY time on the ticket's day in the
      // company zone. Non-interactive: a time that happened twice, never
      // happened, or an unreadable zone leaves the line times EMPTY (same as a
      // job with no start time) — never a guessed instant.
      const _genKey = typeof jobDate === 'string' ? jobDate.slice(0, 10) : window.TenantLocalTime.dateKey(parsedDate);
      const _genRead = (hhmm) => {
        const mm = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
        if (!mm || !_genKey) return null;
        const r = window.TenantLocalTime.read(`${_genKey}T${mm[1].padStart(2, '0')}:${mm[2]}`, { noHints: true }); // COD: never inherit a pick
        if (!r.ok) { console.warn('[Billing] job time not auto-filled on the ticket:', r.code); return null; }
        return Timestamp.fromMillis(r.ms);
      };
      if (_genStartTime) _genFromTs = _genRead(_genStartTime);
      if (_genEndTime) _genToTs = _genRead(_genEndTime);

      // TASK-1615-B — this job's garbage-coordinate stops, computed ONCE for both
      // line loops below. `job` is already in hand, so this costs no read.
      const _rmBadStopsOnJob = _rmBadStops(job);
      let _rmSuspectBorn = 0;
      for (const emp of crewData) {
        const personSel = lineItemSelections[`person:${emp.personId}`];
        const _empR = (personSel?.rate != null)
          ? { rate: personSel.rate, rateUnit: personSel.rateUnit || 'Hourly', roundToHalfHour: personSel.roundToHalfHour || false, revenueCategory: personSel.revenueCategory || '', includeOnInvoice: true }
          : lookupReviewRate(job.customerId, 'EMPLOYEE', emp.role, emp.personName, '');
        // Auto-populate quantity for "Per Mile" employee lines from job route mileage.
        // TASK-1615-B — the NUMBER is unchanged (see the block comment at
        // _rmPlottableCoord: a stricter calcRouteMileage bills $0 silently). What is
        // new is the stamp below, and _rmIsPerMileUnit is the shared unit test so
        // the auto-fill and the gate can never disagree about what "Per Mile" is.
        let empAutoQty = 0;
        if (_rmIsPerMileUnit(_empR.rateUnit) && job.waypoints?.length >= 2) {
          const routeMiles = window.DispatchModule?.calcRouteMileage?.(job.waypoints);
          if (routeMiles) empAutoQty = Math.round(routeMiles);
        }
        const lineData = {
          type: 'EMPLOYEE',
          personId: emp.personId,
          personName: emp.personName,
          role: emp.role,
          hours: 0,
          quantity: empAutoQty || 0,
          rate: _empR.rate,
          rateUnit: _empR.rateUnit,
          roundToHalfHour: _empR.roundToHalfHour || false,
          revenueCategory: _empR.revenueCategory || '',
          includeOnInvoice: _empR.includeOnInvoice,
          lineAmount: empAutoQty > 0 ? empAutoQty * (_empR.rate || 0) : 0,
          createdAt: Timestamp.now()
        };
        if (_rmStampSuspect(lineData, _rmBadStopsOnJob)) _rmSuspectBorn++;
        if (_genFromTs) lineData.fromTime = _genFromTs;
        if (_genToTs) lineData.toTime = _genToTs;
        if (personSel?.lineItemId) { lineData.lineItemId = personSel.lineItemId; lineData.contractId = personSel.contractId; lineData.lineItemDescription = personSel.description || ''; }
        await addDoc(collection(db, 'tenants', tenant, 'tickets', docRef.id, 'ticketLines'), lineData);
      }

      // Load trucks/trailers/assets for resolving dispatch assignment refs
      await loadReviewEquipment();
      const rosterTrucks = ReviewState.trucks || [];
      const rosterTrailers = ReviewState.trailers || [];
      const rosterAssets = ReviewState.assets || [];

      // Create EQUIPMENT ticketLines from dispatch assignments
      // TASK-1548 — count lines born priced-with-no-hours-and-no-times (the birth
      // defect: nothing is wired to ever fill them; clock_out lines get the punch
      // window, these get nothing). Surfaced in the toast + the review banner, and
      // they BLOCK ready/invoice until a human enters hours or marks non-billable.
      let _zhBornNeedingHours = 0;
      for (const equip of equipmentData) {
        let asset = null;
        let assetName = '';
        let fleetNumber = '';
        let vehicleType = '';
        if (equip.type === 'truck') {
          asset = _resolveFleetRef(equip.ref, rosterTrucks);
        } else if (equip.type === 'trailer') {
          asset = _resolveFleetRef(equip.ref, rosterTrailers);
        } else {
          asset = _resolveFleetRef(equip.ref, rosterAssets);
        }
        if (asset) {
          assetName = window.getUnitDisplay ? window.getUnitDisplay(asset) : (asset.unit || asset.name || asset.partNumber || equip.ref);
          fleetNumber = asset.unit || asset.partNumber || '';
          vehicleType = asset.type || '';
        } else {
          assetName = equip.ref;
          fleetNumber = equip.ref;
          // TASK-1674 S4 — the jobDay reference resolved to NOTHING in the
          // roster, so `vehicleType` stays blank and the price book can never
          // match this vehicle by type. 14 billed TITS lines carry a blank
          // vehicleType for exactly this reason. Do NOT touch _resolveFleetRef
          // itself (byte-tested twin) — record the failure instead, so the
          // review banner can name the reference that didn't resolve.
          console.warn('[Billing] TASK-1674 — jobDay equipment ref did not resolve to a roster vehicle:',
            equip.type, equip.ref);
        }
        const _refUnresolved = !asset;
        const equipKey = `${equip.type}:${asset?.id || equip.ref}`;
        const equipSel = lineItemSelections[equipKey];
        const _eqR = (equipSel?.rate != null)
          ? { rate: equipSel.rate, rateUnit: equipSel.rateUnit || 'Hourly', roundToHalfHour: _billingSelectionRounds(equipSel, job.customerId), revenueCategory: equipSel.revenueCategory || '', includeOnInvoice: true } // TASK-9489
          : lookupReviewRate(job.customerId, 'EQUIPMENT', equip.type, assetName, vehicleType);
        // Auto-populate quantity for "Per Mile" lines from job route mileage.
        // TASK-1615-B — number unchanged, stamped below when the route is bad.
        let autoQty = 0;
        if (_rmIsPerMileUnit(_eqR.rateUnit) && job.waypoints?.length >= 2) {
          const routeMiles = window.DispatchModule?.calcRouteMileage?.(job.waypoints);
          if (routeMiles) autoQty = Math.round(routeMiles);
        }
        // TASK-730a: seed deployed rental / quantity-tracked assets onto the ticket with their
        // real deployed COUNT (from jobDay.assetQtys) instead of falling to quantity 0 → $0.
        // The dispatch deploy/return flow stores the count in assetQtys[id] (defaulting to 1 when
        // ===1, mirroring dispatch.js) and marks returns via assetDirections[id]==='return'.
        // Only DEPLOYED rentals bill — skip returns (mats coming back to the yard aren't a charge).
        // The rate UNIT is biller-editable per line in the review editor (see rate-unit selector),
        // so we seed a sensible default but never lock the biller into hourly.
        const _isRentalAsset = !!asset && (asset.quantityType === 'rental' || asset.isRental);
        const _assetDir = (targetJobDay?.assetDirections || {})[asset?.id] || 'deploy';
        const _isReturnedRental = _isRentalAsset && _assetDir === 'return';
        let _rentalQty = 0;
        let _rentalRateUnit = null;
        let _isDeployedRental = false;
        if (_isRentalAsset && !_isReturnedRental) {
          _isDeployedRental = true;
          const _seededQty = (targetJobDay?.assetQtys || {})[asset.id];
          _rentalQty = (_seededQty != null && _seededQty > 0) ? _seededQty : 1; // dispatch stores qty only when >1; default 1
          // Default the rate unit: asset's configured unit if it has one, else the looked-up rate's
          // unit, else 'Each' (per-mat). Biller can change it per line in the review editor.
          _rentalRateUnit = asset.rentalRateUnit || asset.rateUnit || _eqR.rateUnit || 'Each';
        }
        // A RETURNED rental must never bill — force qty/amount to 0 even if a Per-Mile rate
        // matched and set autoQty (mats coming back to the yard aren't a charge). (COD: Code Reviewer)
        // Also force a NON-time-based unit ('Each') for returns so openReviewTicket's self-heal
        // (which recomputes hours/lineAmount from fromTime/toTime when hours===0 on a time-based
        // line) can't silently re-bill the returned line back to a dollar amount.
        const _finalRateUnit = _isReturnedRental ? 'Each' : (_isDeployedRental ? _rentalRateUnit : _eqR.rateUnit);
        const _finalQty = _isReturnedRental ? 0 : (_isDeployedRental ? _rentalQty : (autoQty || 0));
        const _finalAmount = _isReturnedRental
          ? 0
          : (_isDeployedRental
            ? _rentalQty * (_eqR.rate || 0)
            : (autoQty > 0 ? autoQty * (_eqR.rate || 0) : 0));
        const lineData = {
          type: 'EQUIPMENT',
          assetId: asset?.id || equip.ref,
          assetName,
          assetType: equip.type,
          fleetNumber,
          vehicleType,
          hours: 0,
          quantity: _finalQty,
          rate: _eqR.rate,
          rateUnit: _finalRateUnit,
          roundToHalfHour: _eqR.roundToHalfHour || false,
          revenueCategory: _eqR.revenueCategory || '',
          includeOnInvoice: _eqR.includeOnInvoice,
          lineAmount: _finalAmount,
          createdAt: Timestamp.now()
        };
        // Don't stamp times on a returned-rental line — a time-based self-heal would re-bill it.
        if (_genFromTs && !_isReturnedRental) lineData.fromTime = _genFromTs;
        if (_genToTs && !_isReturnedRental) lineData.toTime = _genToTs;
        if (equipSel?.lineItemId) { lineData.lineItemId = equipSel.lineItemId; lineData.contractId = equipSel.contractId; lineData.lineItemDescription = equipSel.description || ''; }
        // TASK-1674 — a guessed rate is written, but never silently. The review
        // banner derives the flag live too (see _equipRateGuessLines), so this
        // stamp is the durable record rather than the only signal.
        if (_eqR?.rateGuessed === true) {
          lineData.rateResolvedBy = 'fallback:unmatched-vehicle-type';
          lineData._billingReviewFlag = {
            reason: 'equipment_rate_guessed',
            matchedVia: _eqR.matchedVia || '',
            matchedDescription: _eqR.matchedDescription || '',
            candidateCount: _eqR.candidateCount || 0,
            vehicleType: vehicleType || '',
            assetName: assetName || '',
            rate: _eqR.rate != null ? _eqR.rate : null,
            flaggedAt: Timestamp.now()
          };
        }
        if (_refUnresolved) {
          lineData._billingReviewFlag = {
            reason: 'equipment_ref_unresolved', ref: String(equip.ref || ''),
            assetType: equip.type || '', flaggedAt: Timestamp.now()
          };
        }
        if (_rmStampSuspect(lineData, _rmBadStopsOnJob)) _rmSuspectBorn++;
        if (equipmentLineNeedsHours(lineData) && !(lineData.fromTime && lineData.toTime)) _zhBornNeedingHours++;
        await addDoc(collection(db, 'tenants', tenant, 'tickets', docRef.id, 'ticketLines'), lineData);
      }
      if (_zhBornNeedingHours > 0) {
        window.showToast?.(`Draft ticket created — ${_zhBornNeedingHours} equipment line${_zhBornNeedingHours !== 1 ? 's need' : ' needs'} hours before it can be invoiced`, 'info');
      } else if (_rmSuspectBorn === 0) {
        window.showToast?.('Draft ticket created', 'success');
      }
      // TASK-1615-B — fired LAST so it is the toast left on screen. The biller is
      // standing right here when the number is born; telling them now is cheaper
      // than telling them at the invoice gate.
      if (_rmSuspectBorn > 0) {
        window.showToast?.(`${_rmSuspectBorn} Per Mile line${_rmSuspectBorn !== 1 ? 's' : ''} got mileage from a destination with a bad location — check the miles before invoicing`, 'error');
      }
    } else {
      window.showToast?.('Draft ticket created', 'success');
    }
    await openReviewTicket(docRef.id);
  } catch (err) {
    console.error('[Billing] Error creating ticket from job:', err);
    window.showToast?.('Failed to create ticket', 'error');
  }

  if (btn) {
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons">edit_note</span> Edit Ticket';
  }
}

function prefillTicket(ticketData, job) {
  // Reset the form
  document.getElementById('modal-ticket-title').textContent = 'New Ticket (from Job)';
  document.getElementById('form-ticket').reset();
  document.getElementById('ticket-id').value = '';

  // Set date
  const jobDate = job?.startDate || new Date();
  const dateStr = typeof jobDate === 'string' ? jobDate :
    jobDate?.toDate ? formatDateInput(jobDate.toDate()) : formatDateInput(new Date(jobDate));
  document.getElementById('ticket-date').value = dateStr || formatDateInput(new Date());

  // Set status to draft
  document.getElementById('ticket-status').value = 'draft';

  // Set customer
  updateCustomerDropdown();
  if (job?.customerId) {
    document.getElementById('ticket-customer').value = job.customerId;
  }

  // Set fields from AI/fallback data
  document.getElementById('ticket-po').value = ticketData.poNumber || '';
  document.getElementById('ticket-well-name').value = ticketData.wellName || '';
  document.getElementById('ticket-well-number').value = ticketData.wellNumber || '';
  document.getElementById('ticket-location').value = ticketData.location || '';
  document.getElementById('ticket-work').value = ticketData.workPerformed || '';

  // Populate employees
  const employeesContainer = document.getElementById('ticket-employees');
  employeesContainer.innerHTML = '';
  const employees = ticketData.employees || [];
  if (employees.length > 0) {
    employees.forEach(emp => {
      addEmployeeRow({
        personId: emp.personId || '',
        role: emp.role || 'NON_CDL'
      });
    });
  } else {
    addEmployeeRow();
  }

  // Populate time entries
  const timeContainer = document.getElementById('ticket-time-entries');
  timeContainer.innerHTML = '';
  const timeEntries = ticketData.timeEntries || [];
  if (timeEntries.length > 0) {
    timeEntries.forEach(entry => {
      addTimeEntry({
        timeFrom: entry.timeFrom || '',
        timeTo: entry.timeTo || '',
        description: entry.description || ''
      });
    });
  } else {
    addTimeEntry();
  }

  // Populate materials
  const materialsContainer = document.getElementById('ticket-materials');
  materialsContainer.innerHTML = '';
  if (ticketData.materials && ticketData.materials.length > 0) {
    ticketData.materials.forEach(mat => {
      addMaterialRow({
        name: mat.name || '',
        quantity: mat.quantity || 0,
        unit: mat.unit || ''
      });
    });
  }

  // Store job reference for linking
  const jobIdInput = document.getElementById('ticket-job-id');
  if (jobIdInput) jobIdInput.value = job?.id || '';

  // Show the modal
  document.getElementById('modal-ticket').classList.remove('hidden');
  window.showToast?.('Ticket pre-filled from job', 'success');
}

// ============================================
// COMPANY LOGO UPLOAD (LinkedIn-style)
// ============================================
function initBillingLogo() {
  const logoUrl = BillingState.companyLogoUrl;
  const img = document.getElementById('billing-logo-img');
  const placeholder = document.getElementById('billing-logo-placeholder');
  const container = document.getElementById('billing-logo-upload');
  if (!img || !placeholder || !container) return;

  if (logoUrl) {
    img.src = logoUrl;
    img.style.display = '';
    placeholder.style.display = 'none';
    container.classList.add('has-logo');
  } else {
    img.style.display = 'none';
    placeholder.style.display = '';
    container.classList.remove('has-logo');
  }
}

function updateBillingHeader() {
  const td = window.AppState?.tenantData || {};
  const nameEl = document.getElementById('billing-header-company-name');
  const detailEl = document.getElementById('billing-header-company-detail');
  if (!nameEl) return;

  const companyName = td.companyName || td.name || '';
  if (companyName) {
    nameEl.textContent = companyName;
  } else {
    nameEl.textContent = 'AR / AP';
  }

  if (!detailEl) return;
  const phones = td.companyPhones || (td.phone ? [{ phone: td.phone }] : []);
  const emails = td.companyEmails || (td.email ? [{ email: td.email }] : []);
  const addrs = td.companyAddresses || (td.address ? [{ address: td.address }] : []);

  const formatPhone = (p) => {
    const d = (p || '').replace(/\D/g, '');
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    return p;
  };

  const line1Parts = [];
  if (phones.length > 0 && phones[0].phone) line1Parts.push(formatPhone(phones[0].phone));
  if (emails.length > 0 && emails[0].email) line1Parts.push(emails[0].email);
  const line2 = addrs.length > 0 && addrs[0].address ? addrs[0].address : '';

  if (line1Parts.length > 0 || line2 || companyName) {
    let html = '';
    if (line1Parts.length > 0) html += escapeHtml(line1Parts.join(' · '));
    if (line2) html += (html ? '<br>' : '') + escapeHtml(line2);
    detailEl.innerHTML = html || '<span style="color:var(--text-muted);font-style:italic;">Click to add company info</span>';
  } else {
    detailEl.innerHTML = '<span style="color:var(--text-muted);font-style:italic;">Click to set up company info for invoices</span>';
  }
}

// ============================================
// COMPANY INFO MODAL (CRM-style)
// ============================================
function openCompanyInfoModal() {
  let modal = document.getElementById('modal-company-info');
  if (!modal) {
    modal = document.createElement('div');
    modal.className = 'modal hidden';
    modal.id = 'modal-company-info';
    modal.innerHTML = `
      <div class="modal-content" style="max-width:520px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
        <div class="modal-header" style="flex-shrink:0;">
          <h3 style="display:flex;align-items:center;gap:8px;">
            <span class="material-icons" style="color:var(--brand-blue);">business</span>
            Company Information
          </h3>
          <button class="btn-icon" onclick="window.BillingModule.closeCompanyInfoModal()" style="color:var(--text-secondary);">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="padding:var(--spacing-lg);overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">
          <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 16px;">This information appears on your invoices and field tickets.</p>

          <div style="display:flex;align-items:center;gap:16px;margin-bottom:20px;">
            <div class="ci-logo-area" id="ci-logo-area" onclick="document.getElementById('billing-logo-input').click()" title="Upload company logo" style="position:relative;width:80px;height:80px;min-width:80px;border-radius:10px;border:2px solid var(--border-light);background:var(--bg-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;overflow:hidden;transition:border-color 0.2s;">
              <img id="ci-logo-preview" src="" alt="" style="display:none;width:100%;height:100%;object-fit:contain;padding:4px;border-radius:8px;">
              <div id="ci-logo-empty" style="display:flex;flex-direction:column;align-items:center;gap:2px;">
                <span class="material-icons" style="font-size:28px;color:var(--text-muted);">add_a_photo</span>
                <span style="font-size:0.6rem;color:var(--text-muted);font-weight:600;">LOGO</span>
              </div>
              <div id="ci-logo-edit-badge" style="display:none;position:absolute;bottom:2px;right:2px;width:24px;height:24px;border-radius:50%;background:var(--brand-blue);color:#fff;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.2);">
                <span class="material-icons" style="font-size:14px;">edit</span>
              </div>
            </div>
            <div style="flex:1;">
              <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:4px;display:block;">Company Name</label>
              <input type="text" id="ci-company-name" class="form-control" placeholder="Your Company Name" style="font-size:14px;">
            </div>
          </div>

          <div class="crm-form-section" style="margin-bottom:20px;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
              <div class="crm-form-section-title" style="font-weight:700;font-size:0.9rem;color:var(--text-primary);margin:0;display:flex;align-items:center;gap:6px;">
                <span class="material-icons" style="font-size:18px;">phone</span> Phone Numbers
              </div>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addCIPhoneRow()" style="padding:3px 10px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;">add</span> Add
              </button>
            </div>
            <div id="ci-phones" style="display:flex;flex-direction:column;gap:6px;"></div>
          </div>

          <div class="crm-form-section" style="margin-bottom:20px;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
              <div class="crm-form-section-title" style="font-weight:700;font-size:0.9rem;color:var(--text-primary);margin:0;display:flex;align-items:center;gap:6px;">
                <span class="material-icons" style="font-size:18px;">email</span> Email Addresses
              </div>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addCIEmailRow()" style="padding:3px 10px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;">add</span> Add
              </button>
            </div>
            <div id="ci-emails" style="display:flex;flex-direction:column;gap:6px;"></div>
          </div>

          <!-- TASK-4771 — EMAIL SENDING IDENTITY. This is where the Connect
               Gmail control lives now. It used to sit inside the Invoice
               Template Designer (Templates -> Email tab), which is a designer
               for the MESSAGE, not a place anybody looks for an account-level
               Google connection. It belongs here, beside the company phones,
               emails and addresses, because this modal is already where the
               carrier declares who they are. -->
          <div class="crm-form-section" style="margin-bottom:20px;">
            <div class="crm-form-section-title" style="font-weight:700;font-size:0.9rem;color:var(--text-primary);margin:0 0 8px;display:flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">send</span> Email Sending Identity
            </div>
            <div id="ci-sending-identity"></div>
          </div>

          <div class="crm-form-section" style="margin-bottom:0;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
              <div class="crm-form-section-title" style="font-weight:700;font-size:0.9rem;color:var(--text-primary);margin:0;display:flex;align-items:center;gap:6px;">
                <span class="material-icons" style="font-size:18px;">location_on</span> Addresses
              </div>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.addCIAddressRow()" style="padding:3px 10px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;">add</span> Add
              </button>
            </div>
            <div id="ci-addresses" style="display:flex;flex-direction:column;gap:6px;"></div>
          </div>
        </div>
        <div class="form-actions" style="flex-shrink:0;padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
          <button class="btn btn-secondary" onclick="window.BillingModule.closeCompanyInfoModal()">Cancel</button>
          <button class="btn btn-primary" id="btn-save-company-info" onclick="window.BillingModule.saveCompanyInfo()">
            <span class="material-icons">save</span> Save
          </button>
        </div>
      </div>`;
    document.body.appendChild(modal);
  }

  // Populate from tenant data
  const td = window.AppState?.tenantData || {};
  const ch = window.AppState?.settings?.companyHeader || {};
  document.getElementById('ci-company-name').value = td.companyName || td.name || '';

  // Reset & populate phone rows
  _ciPhoneCounter = 0;
  document.getElementById('ci-phones').innerHTML = '';
  const phones = td.companyPhones && td.companyPhones.length > 0 ? td.companyPhones : (td.phone ? [{ label: 'Main', phone: td.phone }] : []);
  if (phones.length > 0) {
    phones.forEach((p, i) => addCIPhoneRow(p.label, p.phone, ch.phoneLabel ? p.label === ch.phoneLabel : i === 0));
  } else {
    addCIPhoneRow('Main', '', true);
  }

  // Reset & populate email rows
  _ciEmailCounter = 0;
  document.getElementById('ci-emails').innerHTML = '';
  const emails = td.companyEmails && td.companyEmails.length > 0 ? td.companyEmails : (td.email ? [{ label: 'General', email: td.email }] : []);
  if (emails.length > 0) {
    emails.forEach((e, i) => addCIEmailRow(e.label, e.email, ch.emailLabel ? e.label === ch.emailLabel : i === 0));
  } else {
    addCIEmailRow('General', '', true);
  }

  // Reset & populate address rows
  _ciAddrCounter = 0;
  document.getElementById('ci-addresses').innerHTML = '';
  const addrs = td.companyAddresses && td.companyAddresses.length > 0 ? td.companyAddresses : (td.address ? [{ label: 'Main', address: td.address }] : []);
  if (addrs.length > 0) {
    addrs.forEach((a, i) => addCIAddressRow(a.label, a.address, ch.addressLabel ? a.label === ch.addressLabel : i === 0));
  } else {
    addCIAddressRow('Main', '', true);
  }

  // Sync logo preview inside modal
  const logoUrl = BillingState.companyLogoUrl;
  const ciPreview = document.getElementById('ci-logo-preview');
  const ciEmpty = document.getElementById('ci-logo-empty');
  const ciEditBadge = document.getElementById('ci-logo-edit-badge');
  if (logoUrl && ciPreview) {
    ciPreview.src = logoUrl;
    ciPreview.style.display = '';
    if (ciEmpty) ciEmpty.style.display = 'none';
    if (ciEditBadge) ciEditBadge.style.display = 'flex';
  } else if (ciPreview) {
    ciPreview.style.display = 'none';
    if (ciEmpty) ciEmpty.style.display = '';
    if (ciEditBadge) ciEditBadge.style.display = 'none';
  }

  // TASK-4771 — paint the sending-identity card from whatever state we already
  // hold (so it never flashes empty), then reconcile against the tenant doc.
  // loadBillingGmailStatus is the SAME loader the Invoice Builder used; the
  // state object it fills (InvoiceBuilderState.gmailStatus) is shared, so both
  // surfaces can never disagree.
  _ensureSendingIdentityStyles();
  refreshSendingIdentityCard();
  loadBillingGmailStatus().then(refreshSendingIdentityCard).catch(() => {});

  // This modal is now reachable FROM another modal — the Invoice Builder's
  // Email tab points at it. Both are `.modal` (z-index 200/210), so without an
  // explicit bump the winner would be whichever happened to be appended to
  // <body> last, which depends on the order the user opened them in. 260 sits
  // above both and below toasts (1100) and the assistant FAB (2200).
  modal.style.zIndex = '260';

  _t9986ApplyReadOnly(modal, ['btn-save-company-info']); // TASK-9986 S1
  modal.classList.remove('hidden');
  setTimeout(() => document.getElementById('ci-company-name')?.focus(), 100);
}

function closeCompanyInfoModal() {
  document.getElementById('modal-company-info')?.classList.add('hidden');
}

// ============================================================================
// TASK-4771 — EMAIL SENDING IDENTITY CARD
// ============================================================================
// The Connect / Disconnect control for the tenant's own Gmail. It lived in the
// Invoice Template Designer (Templates -> Email tab); it now lives in Company
// Info, and the Email tab carries a POINTER at this one, not a second copy.
//
// 🔴 ONE CONTROL, ONE STATE OBJECT. Both surfaces read
// InvoiceBuilderState.gmailStatus and both refresh through
// refreshSendingIdentityCard()/renderInvoiceBuilderBody(), so a connect or a
// disconnect performed from either place is reflected in the other. A second
// half-wired copy of an OAuth connection is worse than either placement alone.
//
// Styles are injected from here rather than added to styles.css: this task's
// surface is billing.js, and every selector is scoped to classes this file
// owns. Same pattern as TASK-4740 and TASK-4745.
//
// 🔴 NO BACKTICKS ANYWHERE INSIDE THE TEMPLATE LITERAL BELOW, comments
// included. TASK-4745 hit exactly that: a backtick in a CSS comment closed the
// template early, node --check still passed, and the injected stylesheet
// silently became a fragment. It fails open and nothing throws.
//
// Touch targets are keyed on (pointer: coarse), NOT a viewport width — a cab
// tablet at 1024px needs 44px and a 1024px mouse window does not. The
// !important is deliberate: styles.css:9691 sets a 40px min-height on
// .btn-secondary/.btn-sm inside a WIDTH query, and although these buttons carry
// their own classes rather than those, the !important means a later tidy-up
// that reclasses them as .btn-secondary cannot silently shrink them back.
//
// Contrast: body copy is var(--text-secondary) (#475569 on #F8FAFC), never
// var(--text-muted) (#94A3B8), which fails AA on a light card.
function _ensureSendingIdentityStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ci-sending-identity-styles')) return;
  const el = document.createElement('style');
  el.id = 'ci-sending-identity-styles';
  el.textContent = `
.ci-si-card { padding: 12px; border: 1px solid var(--border-light); border-radius: 8px; background: #F8FAFC; }
.ci-si-card.ci-si-connected { background: #ECFDF5; border-color: #A7F3D0; }
.ci-si-top { display: flex; align-items: flex-start; gap: 10px; }
.ci-si-icon { font-size: 20px; color: var(--text-secondary); flex-shrink: 0; margin-top: 1px; }
.ci-si-card.ci-si-connected .ci-si-icon { color: #047857; }
.ci-si-copy { flex: 1; min-width: 0; }
.ci-si-head { font-size: 13px; font-weight: 600; color: var(--text-primary); line-height: 1.35; }
.ci-si-sub { font-size: 11.5px; color: var(--text-secondary); line-height: 1.5; margin-top: 4px; overflow-wrap: anywhere; }
.ci-si-addr { font-weight: 600; color: var(--text-primary); }
.ci-si-actions { display: flex; justify-content: flex-end; margin-top: 10px; }
.ci-si-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border-radius: 6px; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 8px 14px; min-height: 36px; }
.ci-si-btn-connect { background: #0F172A; color: #fff; border: 1px solid #0F172A; }
.ci-si-btn-connect:hover { background: #1E293B; }
.ci-si-btn-quiet { background: #fff; color: var(--text-secondary); border: 1px solid #CBD5E1; }
.ci-si-btn-quiet:hover { color: var(--text-primary); border-color: #94A3B8; }
.ci-si-btn[disabled] { opacity: 0.6; cursor: default; }
@media (pointer: coarse) {
  .ci-si-btn { min-height: 44px !important; padding: 10px 16px !important; }
}`;
  document.head.appendChild(el);
}

// The card itself. Renders three states: unknown (status not loaded yet),
// connected, and the default — which is where every live tenant actually is.
//
// 🔴 THE DEFAULT COPY SAYS WHAT ACTUALLY HAPPENS AND PROMISES NOTHING ELSE.
// On the SendGrid path (functions/src/index.js sendInvoiceEmail) the customer
// sees the company NAME as the sender but notifications@dispatchdeck.app as the
// address, and Reply-To is the invoice template's replyTo falling back to the
// company email. A display name is not authentication and we do not control
// the recipient's spam filter, so there is no deliverability claim here.
function renderSendingIdentityCard() {
  const g = InvoiceBuilderState.gmailStatus || { connected: false, loaded: false };
  const td = window.AppState?.tenantData || {};
  // TASK-4771 [2]: exactly ONE connect and ONE disconnect binding in this file.
  // TASK-1412 added a third state that needs both, so each is built once here.
  const connectBtn = (icon, label) => `<button type="button" class="ci-si-btn ci-si-btn-connect" onclick="window.BillingModule.connectBillingGmail(event)">
        <span class="material-icons" style="font-size:15px;">${icon}</span>${label}
      </button>`;
  const disconnectBtn = () => `<button type="button" class="ci-si-btn ci-si-btn-quiet" onclick="window.BillingModule.disconnectBillingGmail(event)">
          <span class="material-icons" style="font-size:15px;">link_off</span>Disconnect
        </button>`;

  // connected===true with loaded===false is the optimistic state the OAuth
  // popup hands back; showing the spinner there would hide a success.
  if (!g.loaded && !g.connected) {
    return `<div class="ci-si-card">
      <div class="ci-si-top">
        <span class="material-icons ci-si-icon">hourglass_empty</span>
        <div class="ci-si-copy"><div class="ci-si-head">Checking how your invoices send…</div></div>
      </div>
    </div>`;
  }

  if (g.connected) {
    return `<div class="ci-si-card ci-si-connected">
      <div class="ci-si-top">
        <span class="material-icons ci-si-icon">check_circle</span>
        <div class="ci-si-copy">
          <div class="ci-si-head">Invoices send from your Gmail</div>
          <div class="ci-si-sub"><span class="ci-si-addr">${escapeHtml(g.email || '')}</span> — that is the address your customer sees, and replies land in that inbox.</div>
        </div>
      </div>
      <div class="ci-si-actions">
        ${disconnectBtn()}
      </div>
    </div>`;
  }

  if (g.needsReconnect) {
    return `<div class="ci-si-card">
      <div class="ci-si-top">
        <span class="material-icons ci-si-icon" style="color:#B45309;">error</span>
        <div class="ci-si-copy">
          <div class="ci-si-head">Invoice emails are NOT going out — your Gmail stopped working</div>
          <div class="ci-si-sub">Google stopped accepting sends from <span class="ci-si-addr">${escapeHtml(g.email || 'your Gmail')}</span> (the access was revoked or expired). Reconnect it, or disconnect to send from notifications@dispatchdeck.app instead.</div>
        </div>
      </div>
      <div class="ci-si-actions">
        ${connectBtn('refresh', 'Reconnect Gmail')}
        ${disconnectBtn()}
      </div>
    </div>`;
  }

  const companyName = td.companyName || td.name || '';
  const replyTo = (window.AppState?.settings?.invoiceEmailTemplate?.replyTo || '').trim()
    || ((td.companyEmails && td.companyEmails[0] && td.companyEmails[0].email) || td.email || '').trim();
  const senderPhrase = companyName
    ? `<span class="ci-si-addr">${escapeHtml(companyName)}</span> as the sender name`
    : 'your company name as the sender name';
  const replyPhrase = replyTo
    ? `<span class="ci-si-addr">${escapeHtml(replyTo)}</span>`
    : 'the Reply-To address on your invoice email template';

  return `<div class="ci-si-card">
    <div class="ci-si-top">
      <span class="material-icons ci-si-icon">mail</span>
      <div class="ci-si-copy">
        <div class="ci-si-head">Currently sending via DispatchDeck on your behalf.</div>
        <div class="ci-si-sub">Your customer sees ${senderPhrase}, but the address on the message is <span class="ci-si-addr">notifications@dispatchdeck.app</span>. Replies go to ${replyPhrase}.</div>
        <div class="ci-si-sub">Connect Gmail to send invoices from your own address instead. You can disconnect any time and go back to this.</div>
      </div>
    </div>
    <div class="ci-si-actions">
      ${connectBtn('link', 'Connect Gmail')}
    </div>
  </div>`;
}

// Repaint the card wherever it is mounted. A no-op when the Company Info modal
// has never been opened, so it is safe to call from the OAuth result handlers.
function refreshSendingIdentityCard() {
  const host = typeof document !== 'undefined' && document.getElementById('ci-sending-identity');
  if (!host) return;
  _ensureSendingIdentityStyles();
  host.innerHTML = renderSendingIdentityCard();
}

// Row builders for company info modal
const CI_PHONE_LABELS = ['Main', 'Office', 'Cell', 'Dispatch', 'Billing', 'Fax'];
const CI_EMAIL_LABELS = ['General', 'Billing', 'Operations', 'Invoicing', 'Dispatch', 'Accounting'];
const CI_ADDRESS_LABELS = ['Main', 'Billing', 'Mailing', 'Yard', 'Site'];
let _ciPhoneCounter = 0, _ciEmailCounter = 0, _ciAddrCounter = 0;

function addCIPhoneRow(label, phone, invoiceSelected) {
  const container = document.getElementById('ci-phones');
  if (!container) return;
  const idx = _ciPhoneCounter++;
  const row = document.createElement('div');
  row.className = 'ci-row';
  row.id = `ci-phone-${idx}`;
  row.style.cssText = 'display:flex;align-items:center;gap:6px;';
  row.innerHTML = `
    <input type="radio" name="ci-phone-invoice" value="${idx}" ${invoiceSelected ? 'checked' : ''} title="Show on invoice" style="flex-shrink:0;">
    <select class="form-control ci-phone-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${CI_PHONE_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="tel" class="form-control ci-phone-value" placeholder="(701) 555-1234" value="${escapeHtml(phone || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="document.getElementById('ci-phone-${idx}')?.remove()" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>`;
  container.appendChild(row);
}

function addCIEmailRow(label, email, invoiceSelected) {
  const container = document.getElementById('ci-emails');
  if (!container) return;
  const idx = _ciEmailCounter++;
  const row = document.createElement('div');
  row.className = 'ci-row';
  row.id = `ci-email-${idx}`;
  row.style.cssText = 'display:flex;align-items:center;gap:6px;';
  row.innerHTML = `
    <input type="radio" name="ci-email-invoice" value="${idx}" ${invoiceSelected ? 'checked' : ''} title="Show on invoice" style="flex-shrink:0;">
    <select class="form-control ci-email-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${CI_EMAIL_LABELS.map(l => `<option value="${l}" ${l === (label || 'General') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="email" class="form-control ci-email-value" placeholder="email@company.com" value="${escapeHtml(email || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="document.getElementById('ci-email-${idx}')?.remove()" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>`;
  container.appendChild(row);
}

function addCIAddressRow(label, address, invoiceSelected) {
  const container = document.getElementById('ci-addresses');
  if (!container) return;
  const idx = _ciAddrCounter++;
  const row = document.createElement('div');
  row.className = 'ci-row';
  row.id = `ci-addr-${idx}`;
  row.style.cssText = 'display:flex;align-items:center;gap:6px;';
  row.innerHTML = `
    <input type="radio" name="ci-addr-invoice" value="${idx}" ${invoiceSelected ? 'checked' : ''} title="Show on invoice" style="flex-shrink:0;">
    <select class="form-control ci-addr-label" style="width:110px;font-size:13px;padding:6px 8px;">
      ${CI_ADDRESS_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <div style="flex:1;position:relative;">
      <input type="text" class="form-control ci-addr-value" placeholder="123 Main St, City, ST 12345" value="${escapeHtml(address || '')}" style="font-size:13px;padding:6px 8px;width:100%;">
    </div>
    <button type="button" onclick="document.getElementById('ci-addr-${idx}')?.remove()" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>`;
  container.appendChild(row);
  // Address autocomplete
  const addrInput = row.querySelector('.ci-addr-value');
  if (addrInput && window.attachAddressAutocomplete) window.attachAddressAutocomplete(addrInput);
}

async function saveCompanyInfo() {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return; } // TASK-9986 S1
  const btn = document.getElementById('btn-save-company-info');
  const origHTML = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const companyName = document.getElementById('ci-company-name')?.value.trim() || '';

    // Gather phones
    const companyPhones = [];
    let invoicePhoneLabel = null;
    document.querySelectorAll('#ci-phones .ci-row').forEach(row => {
      const phone = row.querySelector('.ci-phone-value')?.value.trim();
      if (!phone) return;
      const label = row.querySelector('.ci-phone-label')?.value || 'Main';
      companyPhones.push({ label, phone });
      const radio = row.querySelector('input[type="radio"]');
      if (radio?.checked) invoicePhoneLabel = label;
    });

    // Gather emails
    const companyEmails = [];
    let invoiceEmailLabel = null;
    document.querySelectorAll('#ci-emails .ci-row').forEach(row => {
      const email = row.querySelector('.ci-email-value')?.value.trim();
      if (!email) return;
      const label = row.querySelector('.ci-email-label')?.value || 'General';
      companyEmails.push({ label, email });
      const radio = row.querySelector('input[type="radio"]');
      if (radio?.checked) invoiceEmailLabel = label;
    });

    // Gather addresses
    const companyAddresses = [];
    let invoiceAddrLabel = null;
    document.querySelectorAll('#ci-addresses .ci-row').forEach(row => {
      const address = row.querySelector('.ci-addr-value')?.value.trim();
      if (!address) return;
      const label = row.querySelector('.ci-addr-label')?.value || 'Main';
      companyAddresses.push({ label, address });
      const radio = row.querySelector('input[type="radio"]');
      if (radio?.checked) invoiceAddrLabel = label;
    });

    const primaryPhone = companyPhones.length > 0 ? companyPhones[0].phone : '';
    const primaryEmail = companyEmails.length > 0 ? companyEmails[0].email : '';
    const primaryAddress = companyAddresses.length > 0 ? companyAddresses[0].address : '';

    const tenant = BillingState.tenantId;
    const tenantRef = doc(db, 'tenants', tenant);
    // 🔴 TASK-9280 — seven owned top-level keys and the seven companyHeader
    // leaves this modal edits. No `...existingSettings`, and no
    // `...(existingSettings.companyHeader || {})`; the getDoc that fed both is
    // gone, because reading the map is what created the stale snapshot that the
    // spread then wrote back over the SMS consent record
    // (settings.smsInbox.optedOutOfAi) and everything else under `settings`.
    // `merge: true` recurses into maps, so any OTHER companyHeader sub-key —
    // and every other settings key — survives by OMISSION. Verified against real
    // Firestore 2026-09-22, not assumed. See saveBillingSettings for the long note.
    //
    // 🔴 TASK-9280, second half — showName / showPhone / showEmail / showAddress
    // are GONE from this payload, and that is a fix, not an omission. This modal
    // has no control for any of them: the four "Show company name / phone / email
    // / address" checkboxes live in the Invoice Builder's Company Header panel
    // (renderIBCompanyHeaderSection, ids ib-ch-name/-phone/-email/-address) and
    // are persisted by saveInvoiceBuilder. Writing them as hardcoded `true` here
    // — which the pre-TASK-9280 code did, AFTER the spread, so the spread never
    // protected them either — meant that fixing a phone number in Company Info
    // silently turned every one of those four back on. Every reader defaults a
    // missing leaf to SHOWN (`ch.showName !== false`, resolveCompanyInfo ~:15651
    // and the panel itself), so dropping them is safe for a tenant that has never
    // opened the Invoice Builder. Found by COD's Edge Case Hunter on this diff;
    // verified against the readers before acting.
    //
    // What this modal DOES own is which labelled phone/email/address is the
    // invoice one — the radios gathered above.
    //
    // ⚠️ SEPARATE, PRE-EXISTING, NOT FIXED HERE — companyHeader is genuinely
    // co-owned: the Invoice Builder panel carries the same three label selects,
    // and saveInvoiceBuilder writes the WHOLE map from its open-time state. With
    // Templates open on the Email tab those selects are not rendered, so
    // readIBSettingsFromUI leaves the open-time label in place and saving
    // Templates puts back a label this modal changed in between. That is a stale
    // local-state overwrite, not a lost write, and the real fix is to split the
    // map's ownership (labels here, show* there) — its own task + COD.
    const companyHeader = {
      phoneLabel: invoicePhoneLabel,
      emailLabel: invoiceEmailLabel,
      addressLabel: invoiceAddrLabel
    };

    await setDoc(tenantRef, {
      companyName, companyPhones, companyEmails, companyAddresses,
      phone: primaryPhone, email: primaryEmail, address: primaryAddress,
      settings: { companyHeader }
    }, { merge: true });

    // Update local state
    if (window.AppState?.tenantData) {
      window.AppState.tenantData.companyName = companyName;
      window.AppState.tenantData.name = companyName || window.AppState.tenantData.name;
      window.AppState.tenantData.companyPhones = companyPhones;
      window.AppState.tenantData.companyEmails = companyEmails;
      window.AppState.tenantData.companyAddresses = companyAddresses;
      window.AppState.tenantData.phone = primaryPhone;
      window.AppState.tenantData.email = primaryEmail;
      window.AppState.tenantData.address = primaryAddress;
    }
    if (window.AppState?.settings) {
      // TASK-9280 — the WRITE is narrow, so the local mirror has to merge the
      // owned leaves over the live listener view rather than replace the map.
      // Assigning `companyHeader` straight across would drop any sub-key this
      // modal does not edit (saveInvoiceBuilder writes others) until the tenant
      // onSnapshot caught up, and updateBillingHeader() reads it synchronously
      // on the next line.
      window.AppState.settings.companyHeader = { ...(window.AppState.settings.companyHeader || {}), ...companyHeader };
    }

    window.showToast?.('Company info saved', 'success');
    updateBillingHeader();
    closeCompanyInfoModal();
  } catch (err) {
    console.error('[Billing] Error saving company info:', err);
    window.showToast?.('Error saving company info', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHTML; }
  }
}

async function uploadLogo(input) {
  const file = input.files?.[0];
  if (!file) return;

  // Auto-compress large logos instead of rejecting
  let uploadFile = file;
  if (file.size > 500 * 1024 && typeof window.compressImage === 'function') {
    const compressed = await window.compressImage(file, 500 * 1024, 800);
    if (compressed) uploadFile = compressed;
  }

  const img = document.getElementById('billing-logo-img');
  const placeholder = document.getElementById('billing-logo-placeholder');
  const container = document.getElementById('billing-logo-upload');

  // Show preview immediately — TASK-1383 guarded shared reader. Preview is
  // best-effort: a failed read just skips the preview (the Storage upload
  // below proceeds either way, exactly as before).
  window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
    if (img) { img.src = dataUrl; img.style.display = ''; }
    if (placeholder) placeholder.style.display = 'none';
    if (container) container.classList.add('has-logo');
    // Also update modal preview if open
    const ciPreview = document.getElementById('ci-logo-preview');
    const ciEmpty = document.getElementById('ci-logo-empty');
    if (ciPreview) { ciPreview.src = dataUrl; ciPreview.style.display = ''; }
    if (ciEmpty) ciEmpty.style.display = 'none';
    const ciEditBadge = document.getElementById('ci-logo-edit-badge');
    if (ciEditBadge) ciEditBadge.style.display = 'flex';
  }).catch(() => { /* preview only — upload below still runs */ });

  // Upload to Firebase Storage
  try {
    const tenant = BillingState.tenantId;
    const logoRef = ref(storage, `tenants/${tenant}/branding/company-logo`);
    await uploadBytes(logoRef, uploadFile, { contentType: uploadFile.type || 'image/jpeg' });
    const logoUrl = await getDownloadURL(logoRef);

    // Save URL to Firestore
    const tenantRef = doc(db, 'tenants', tenant);
    await setDoc(tenantRef, { companyLogoUrl: logoUrl }, { merge: true });

    // Update local state
    BillingState.companyLogoUrl = logoUrl;
    if (window.AppState?.tenantData) window.AppState.tenantData.companyLogoUrl = logoUrl;

    window.showToast?.('Logo uploaded', 'success');
  } catch (err) {
    console.error('[Billing] Logo upload error:', err);
    window.showToast?.('Error uploading logo', 'error');
    // Revert preview
    if (img) { img.src = ''; img.style.display = 'none'; }
    if (placeholder) placeholder.style.display = '';
    if (container) container.classList.remove('has-logo');
  }

  input.value = '';
}

// ============================================
// INVOICE BUILDER
// ============================================

function showInvoiceBuilder() {
  let modal = document.getElementById('modal-invoice-builder');
  if (!modal) {
    // Create modal DOM on first open
    modal = document.createElement('div');
    modal.className = 'modal hidden';
    modal.id = 'modal-invoice-builder';
    modal.innerHTML = `
      <div class="modal-content modal-fullscreen" style="display:flex;flex-direction:column;max-width:100%;width:100%;height:100vh;height:100dvh;margin:0;border-radius:0;">
        <div class="modal-header" style="flex-shrink:0;">
          <h3 style="display:flex;align-items:center;gap:8px;">
            <span class="material-icons" style="color:var(--brand-blue);">tune</span>
            Templates
          </h3>
          <button class="btn-icon" onclick="window.BillingModule.closeInvoiceBuilder()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="ib-body" style="flex:1;overflow:hidden;display:flex;flex-direction:column;"></div>
      </div>
    `;
    document.body.appendChild(modal);
  }
  resetInvoiceBuilderState();
  resetTicketBuilder();
  modal.classList.remove('hidden');
  renderInvoiceBuilderBody();
  // TASK-9986 S1 — templates are a Billing setting: read-only without the switch.
  // A tab re-render can re-enable inputs; saveInvoiceBuilder refuses regardless.
  _t9986ApplyReadOnly(modal, ['ib-save-btn']);
}

function closeInvoiceBuilder() {
  const modal = document.getElementById('modal-invoice-builder');
  if (modal) modal.classList.add('hidden');
  if (InvoiceBuilderState._debounceTimer) {
    clearTimeout(InvoiceBuilderState._debounceTimer);
    InvoiceBuilderState._debounceTimer = null;
  }
}

function resetInvoiceBuilderState() {
  const settings = window.AppState?.settings || {};
  InvoiceBuilderState.mode = 'mass';
  // TASK-4683 A3 — has the approval-signature dropdown been changed in THIS
  // session? The mass save only writes that key when it has (see saveInvoiceBuilder).
  InvoiceBuilderState._approvalSigTouched = false;
  InvoiceBuilderState.selectedCustomerId = null;
  InvoiceBuilderState.selectedContractId = null;
  InvoiceBuilderState.previewTab = 'invoice';
  InvoiceBuilderState.localSettings = {
    showClockTimesOnTickets: settings.showClockTimesOnTickets !== false,
    showEmployeeNamesOnTickets: settings.showEmployeeNamesOnTickets === true, // TASK-865
    showJobNotesOnTickets: settings.showJobNotesOnTickets === true, // TASK-10185 — default OFF
    // TASK-9390 / TASK-10203 — header fields (see _ibHeaderFields).
    headerFields: _ibHeaderFields(settings.headerFields),
    invoiceSectionOrder: [...(settings.invoiceSectionOrder || DEFAULT_SECTION_ORDER)],
    perAssetNotes: settings.perAssetNotes === true,
    perAssetNotesTypes: { ...({ truck: true, trailer: true, asset: true }), ...(settings.perAssetNotesTypes || {}) },
    companyHeader: { showName: true, showPhone: true, showEmail: true, showAddress: true, phoneLabel: null, emailLabel: null, addressLabel: null, ...(settings.companyHeader || {}) },
    slipTemplate: {
      showRunningTotal: true,
      showGpsCoords: true,
      showPhotos: true,
      showLineItems: true,
      customFooterText: '',
      ...(settings.slipTemplate || {})
    }
  };
  // Load column config: the tenant-level template (settings.invoiceColumns,
  // written by the mass save) first; else the first contract that has one
  // (pre-fix tenants, whose template only ever lived on contract docs); else
  // defaults. Same precedence the renderer uses in getInvoiceColumnConfig.
  const firstContract = _billableCustomers().reduce((found, cust) => {
    if (found) return found;
    const cached = ReviewState.contractRateCache[cust.id];
    return cached?.contractData?.invoiceColumns ? cached.contractData : null;
  }, null);
  const ic = settings.invoiceColumns || firstContract?.invoiceColumns || {};
  InvoiceBuilderState.localInvoiceColumns = resolveInvoiceColumnsGrid(ic);
  // Load field ticket settings from first contract or defaults
  const fts = firstContract?.fieldTicketSettings || {};
  InvoiceBuilderState.localFieldTicketSettings = {
    requireDriverSignature: fts.requireDriverSignature || false,
    requireCustomerSignature: fts.requireCustomerSignature || false,
    requireStamp: fts.requireStamp || false,
    requireEmailCopy: fts.requireEmailCopy || false,
    // TASK-4714 — DELIBERATELY EMPTY here, unlike every other key. `firstContract`
    // above is "the first contract ANY customer has that carries invoiceColumns"
    // — fine for booleans that are really tenant-wide defaults, wrong for an
    // address, which belongs to exactly one customer. Seeding it would put that
    // customer's AP address in the box while a different contract is selected.
    // The per-contract value is loaded in `onIBContractChange`, which is also the
    // only place it is ever written back from.
    defaultEmailRecipient: '',
    showCostsOnDriverTicket: fts.showCostsOnDriverTicket !== false,
    customerApprovalSignature: _approvalSigMode(fts.customerApprovalSignature)
  };
  // Load email template from settings
  const iet = settings.invoiceEmailTemplate || {};
  InvoiceBuilderState.localEmailTemplate = {
    replyTo: iet.replyTo || '',
    subject: iet.subject || 'Invoice {{invoiceNumber}} from {{companyName}}',
    body: iet.body || 'Please find the invoice details below.\n\nInvoice: {{invoiceNumber}}\nAmount Due: {{amount}}\nPO: {{poNumber}}\nPayment Terms: {{terms}}',
    paymentInstructions: iet.paymentInstructions || ''
  };
  InvoiceBuilderState._contractCache = {};
  InvoiceBuilderState._initialized = true;
}

function renderInvoiceBuilderBody() {
  const body = document.getElementById('ib-body');
  if (!body) return;

  const isBuilder = InvoiceBuilderState.previewTab === 'builder';
  const isEmail = InvoiceBuilderState.previewTab === 'email';
  // Lazy-load Gmail status the first time the Email tab opens, then re-render
  if (isEmail && !InvoiceBuilderState.gmailStatus.loaded) {
    loadBillingGmailStatus().then(() => renderInvoiceBuilderBody()).catch(() => {});
  }
  const settingsHTML = isBuilder ? renderTBSettingsPanel() : isEmail ? renderEmailTemplateSettingsPanel() : renderIBSettingsPanel();
  const previewHTML = renderIBPreviewPanel();

  body.innerHTML = `
    <div class="ib-layout" style="flex:1;overflow:hidden;">
      <div class="ib-settings-panel">${settingsHTML}</div>
      <div class="ib-preview-panel">${previewHTML}</div>
    </div>
    ${isBuilder ? '' : `<div class="ib-save-bar">${renderIBSaveBar()}</div>`}
  `;

  // Setup drag for section order
  if (isBuilder) {
    setupTBSectionDrag();
  } else {
    const sectionList = document.getElementById('ib-section-active-list');
    if (sectionList) setupIBSectionOrderDrag(sectionList);
  }

  // Initial preview
  schedulePreviewUpdate();
}

function renderIBSlipSettingsPanel() {
  const st = InvoiceBuilderState.localSettings.slipTemplate || {};
  return `
    <div class="ib-section">
      <div class="ib-section-title">Ship Slip Layout</div>
      <p style="font-size:0.78rem;color:var(--text-muted);margin:4px 0 8px;">
        Controls what shows on the printable slip. Changes apply to all future slip prints. Existing slips re-print with current settings.
      </p>
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.BillingModule.openPrinterSetup()" style="margin-bottom:14px;display:inline-flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;">help_outline</span> Printer Setup Guide
      </button>
      <div class="ib-toggle-row" style="margin-bottom:8px;">
        <input type="checkbox" id="ib-slip-running-total" ${st.showRunningTotal !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-slip-running-total">Show running ticket total</label>
      </div>
      <div class="ib-toggle-row" style="margin-bottom:8px;">
        <input type="checkbox" id="ib-slip-gps" ${st.showGpsCoords !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-slip-gps">Show GPS coordinates</label>
      </div>
      <div class="ib-toggle-row" style="margin-bottom:8px;">
        <input type="checkbox" id="ib-slip-photos" ${st.showPhotos !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-slip-photos">Show photos</label>
      </div>
      <div class="ib-toggle-row" style="margin-bottom:14px;">
        <input type="checkbox" id="ib-slip-line-items" ${st.showLineItems !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-slip-line-items">Show line items</label>
      </div>
      <div style="margin-top:8px;">
        <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Custom footer text</div>
        <textarea id="ib-slip-footer" rows="3" placeholder="e.g. Customer signature certifies bbls transferred. Disputes within 24 hrs." style="width:100%;font-size:12px;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;resize:vertical;" oninput="window.BillingModule.onIBSettingChange()">${escapeHtml(st.customFooterText || '')}</textarea>
      </div>
    </div>
  `;
}

function renderIBSettingsPanel() {
  // Slip tab gets a dedicated settings panel — slip layout is independent of
  // invoice appearance/columns/etc.
  if (InvoiceBuilderState.previewTab === 'slip') {
    return renderIBSlipSettingsPanel();
  }

  let html = '';

  // Mode toggle
  html += renderIBModeToggle();

  // Customer/contract picker (single mode only)
  if (InvoiceBuilderState.mode === 'single') {
    html += renderIBCustomerPicker();
  }

  // TASK-848 — per-custom-section "Show on Customer Invoice" toggle. Lives in the invoice
  // template builder (Chris: NOT the section editor). Persists showOnInvoice on the section
  // in settings.customTicketSections; missing flag = shown (back-compat). Only affects the
  // printed/PDF customer invoice — in-app detail + review screens always show everything.
  const _ibCustomSections = _bCsDefs();
  const _ibCustomSectionRows = _ibCustomSections.map(cs => `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);">
        <span class="material-icons" style="font-size:14px;color:var(--accent-teal);">${escapeHtml(cs.icon || 'tune')}</span>
        <span style="flex:1;font-size:0.8rem;font-weight:500;color:var(--text-primary);">${escapeHtml(cs.name || 'Custom Section')}</span>
        <label style="display:flex;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);cursor:pointer;margin:0;white-space:nowrap;" title="Show on Customer Invoice">
          <input type="checkbox" ${cs.showOnInvoice !== false ? 'checked' : ''} onchange="window.BillingModule.toggleCustomSectionInvoice('${window.escJsAttr(cs.id)}', this.checked)" style="accent-color:var(--brand-blue);">
          Show on Customer Invoice
        </label>
        ${_cmHasGps(cs) ? `<button type="button" class="btn btn-secondary btn-sm" style="padding:2px 8px;font-size:0.72rem;white-space:nowrap;" title="Choose what a customer may see on their portal map (TASK-4845)" onclick="window.BillingModule.openCustomerMapConfig('${window.escJsAttr(cs.id)}')">
          <span class="material-icons" style="font-size:13px;vertical-align:middle;">map</span> Customer map${cs.showOnCustomerPortal === true ? ': On' : ''}
        </button>` : ''}
      </div>`).join('');

  // Invoice Appearance section
  html += `<div class="ib-section">
    <div class="ib-section-title">Invoice Appearance</div>
    <div style="margin-top:4px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Section Order</div>
      <div id="ib-section-order-wrapper"></div>
    </div>
    ${_ibCustomSections.length > 0 ? `
    <div style="margin-top:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;display:flex;justify-content:space-between;gap:8px;">
        <span>Custom Sections</span>
        ${_ibCustomSections.some(_cmHasGps) ? `<a href="#" style="font-weight:500;" onclick="window.BillingModule.openPublishedMapLayers();return false;">Published customer maps</a>` : ''}
      </div>
      <div style="display:flex;flex-direction:column;gap:4px;">${_ibCustomSectionRows}</div>
    </div>` : ''}
    <div class="ib-toggle-row" style="margin-top:12px;">
      <input type="checkbox" id="ib-show-emp-names" ${InvoiceBuilderState.localSettings.showEmployeeNamesOnTickets ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-show-emp-names">Show employee names on tickets &amp; invoices (adds a Role column; off = role labels only)</label>
    </div>
    <div style="margin-top:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Job type (from Dispatch)</div>
      <div class="ib-toggle-row">
        <input type="checkbox" id="ib-hdr-jobtype-ticket" ${InvoiceBuilderState.localSettings.headerFields?.jobType?.ticket ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-hdr-jobtype-ticket">Show job type on tickets</label>
      </div>
      <div class="ib-toggle-row" style="margin-top:6px;">
        <input type="checkbox" id="ib-hdr-jobtype-invoice" ${InvoiceBuilderState.localSettings.headerFields?.jobType?.invoice ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-hdr-jobtype-invoice">Show job type on invoices</label>
      </div>
    </div>
    <div style="margin-top:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Project (from Dispatch)</div>
      <div class="ib-toggle-row">
        <input type="checkbox" id="ib-hdr-project-ticket" ${InvoiceBuilderState.localSettings.headerFields?.project?.ticket !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-hdr-project-ticket">Show project on tickets</label>
      </div>
      <div class="ib-toggle-row" style="margin-top:6px;">
        <input type="checkbox" id="ib-hdr-project-invoice" ${InvoiceBuilderState.localSettings.headerFields?.project?.invoice === true ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-hdr-project-invoice">Show project on invoices (only when every ticket is on the same project)</label>
      </div>
    </div>
    <div style="margin-top:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Dispatch job notes</div>
      <div class="ib-toggle-row">
        <input type="checkbox" id="ib-show-job-notes" ${InvoiceBuilderState.localSettings.showJobNotesOnTickets ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
        <label for="ib-show-job-notes">Print the job's dispatch notes on tickets. The customer sees them on the printed, emailed and PDF ticket (own section at the end; long notes continue on the next page)</label>
      </div>
    </div>
    <div class="ib-toggle-row" style="margin-top:12px;">
      <input type="checkbox" id="ib-per-asset-notes" ${InvoiceBuilderState.localSettings.perAssetNotes ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-per-asset-notes">Per-asset notes on invoices</label>
    </div>
    <div id="ib-pan-detail" style="display:${InvoiceBuilderState.localSettings.perAssetNotes ? 'flex' : 'none'};gap:12px;padding:8px 0 0 26px;">
      ${['truck', 'trailer', 'asset'].map(t => `
        <label style="display:flex;align-items:center;gap:4px;font-size:12px;cursor:pointer;">
          <input type="checkbox" id="ib-pan-${t}" ${InvoiceBuilderState.localSettings.perAssetNotesTypes[t] !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
          ${t === 'truck' ? 'Vehicles' : t === 'trailer' ? 'Trailers' : 'Assets'}
        </label>
      `).join('')}
    </div>
  </div>`;

  // Company Header section
  html += renderIBCompanyHeader();

  // Column Visibility section
  html += `<div class="ib-section">
    <div class="ib-section-title">Column Visibility</div>
    ${renderIBColumnConfig()}
  </div>`;

  // Field Ticket Settings section
  html += `<div class="ib-section">
    <div class="ib-section-title">Field Ticket Settings</div>
    ${renderIBFieldTicketSettings()}
  </div>`;

  return html;
}

function renderIBModeToggle() {
  const isMass = InvoiceBuilderState.mode === 'mass';
  return `<div class="ib-mode-toggle">
    <button class="ib-mode-btn ${isMass ? 'active' : ''}" onclick="window.BillingModule.setIBMode('mass')">All Contracts</button>
    <button class="ib-mode-btn ${!isMass ? 'active' : ''}" onclick="window.BillingModule.setIBMode('single')">Single Contract</button>
  </div>`;
}

function renderIBCustomerPicker() {
  const selectedCust = InvoiceBuilderState.selectedCustomerId;

  let html = `<div class="ib-picker">
    <select id="ib-customer-select" onchange="window.BillingModule.onIBCustomerChange(this.value)">
      <option value="">Select customer...</option>
      ${_billingSalesCustomerOptionsHtml(selectedCust || null) /* TASK-9548 — billing picker, no internal customers */}
    </select>`;

  if (selectedCust) {
    const contracts = InvoiceBuilderState._contractCache[selectedCust] || [];
    const selectedContract = InvoiceBuilderState.selectedContractId;
    html += `<select id="ib-contract-select" onchange="window.BillingModule.onIBContractChange(this.value)">
      <option value="">Select price book...</option>
      ${contracts.map(c => `<option value="${c.id}" ${c.id === selectedContract ? 'selected' : ''}>${invoiceEsc(c.title || c.name || 'Untitled')}${c.status === 'Active' ? '' : ' (' + (c.status || 'Draft') + ')'}</option>`).join('')}
    </select>`;
  }

  html += `</div>`;
  return html;
}

function renderIBColumnConfig() {
  const sections = [
    { key: 'labor', label: 'People', cols: ['showTimes', 'showRole', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'vehicles', label: 'Vehicles', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'trailers', label: 'Trailers', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'assets', label: 'Assets', cols: ['showTimes', 'showHours', 'showRate', 'showTotal', 'showLineItem'] },
    { key: 'downtime', label: 'Downtime', cols: ['showTimes', 'showHours', 'showRate', 'showTotal'] },
    { key: 'standby', label: 'Standby', cols: ['showTimes', 'showHours', 'showRate', 'showTotal'] }
  ];
  const allCols = ['showTimes', 'showRole', 'showHours', 'showRate', 'showTotal', 'showLineItem'];
  const colLabels = { showTimes: 'Times', showRole: 'Role', showHours: 'Hrs/Qty', showRate: 'Rate', showTotal: 'Total', showLineItem: 'Line Item' };

  const bodyRows = sections.map(sec => {
    const cfg = InvoiceBuilderState.localInvoiceColumns[sec.key] || {};
    return `<tr>
      <td>${sec.label}</td>
      ${allCols.map(col => {
        if (!sec.cols.includes(col)) return `<td><span style="color:var(--border-light);">&mdash;</span></td>`;
        return `<td><input type="checkbox" data-ib-ic-section="${sec.key}" data-ib-ic-col="${col}" ${cfg[col] !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()"></td>`;
      }).join('')}
    </tr>`;
  }).join('');

  return `<table class="ib-col-table">
    <thead><tr>
      <th style="text-align:left;">Section</th>
      ${allCols.map(col => `<th>${colLabels[col]}</th>`).join('')}
    </tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>`;
}

function renderIBFieldTicketSettings() {
  const fts = InvoiceBuilderState.localFieldTicketSettings;
  const toggles = [
    { id: 'ib-fts-driver-sig', key: 'requireDriverSignature', label: 'Request driver signature' },
    { id: 'ib-fts-customer-sig', key: 'requireCustomerSignature', label: 'Request customer signature' },
    { id: 'ib-fts-stamp', key: 'requireStamp', label: 'Request company stamp' },
    { id: 'ib-fts-email', key: 'requireEmailCopy', label: 'Request email copy' },
    { id: 'ib-fts-show-costs', key: 'showCostsOnDriverTicket', label: 'Show costs on driver ticket' }
  ];
  // TASK-4714 — the recipient input this panel never had. Arming "Request email
  // copy" here used to be a toggle with nowhere to put the address, so the only
  // possible outcome was a contract that requires a copy the server must refuse.
  //
  // 🔴 MASS MODE DELIBERATELY DOES NOT OFFER THE FIELD. One address applied to
  // every customer's contract at once would mail customer A's field ticket —
  // rates, hours, signatures — to customer B's AP desk. That is a worse bug than
  // the one being fixed, so mass mode warns and sends you to the per-contract
  // editor instead of quietly accepting a value it cannot apply safely.
  // Always in the DOM, shown/hidden by the toggle — the same trick the Price Book
  // contract editor uses. Re-rendering the panel on a checkbox change would drop
  // whatever else the biller has half-typed in it.
  const isSingle = InvoiceBuilderState.mode === 'single' && !!InvoiceBuilderState.selectedContractId;
  const missing = fts.requireEmailCopy && !_isPlausibleEmail(fts.defaultEmailRecipient);
  const emailBlock = `<div id="ib-fts-email-block" style="padding:2px 0 6px 24px;${fts.requireEmailCopy ? '' : 'display:none;'}">
    ${isSingle
    ? `<input type="email" id="ib-fts-email-to" class="form-control" placeholder="Email the field ticket to…"
          value="${window.escAttr(fts.defaultEmailRecipient || '')}"
          style="font-size:12px;padding:5px 8px;${missing ? 'border-color:#DC2626;' : ''}"
          onchange="window.BillingModule.onIBSettingChange()">
        <div style="font-size:11px;color:#B91C1C;margin-top:3px;${missing ? '' : 'display:none;'}">Without an address the driver's copy is refused and nobody is told.</div>`
    : `<div style="font-size:11px;color:#B91C1C;">Each customer needs its own address — set it on the contract (Price Book → Contract → Field Ticket Settings). Applying these settings to every contract will not change a recipient already set.</div>`}
  </div>`;

  return toggles.map(t => `
    <div class="ib-toggle-row">
      <input type="checkbox" id="${t.id}" ${fts[t.key] ? 'checked' : ''} onchange="window.BillingModule.${t.key === 'requireEmailCopy' ? 'onIBEmailCopyToggle(this)' : 'onIBSettingChange()'}">
      <label for="${t.id}">${t.label}</label>
    </div>
    ${t.key === 'requireEmailCopy' ? emailBlock : ''}
  `).join('') + _approvalSigSelectHtml('ib-fts-approval-sig', fts.customerApprovalSignature,
    'onchange="window.BillingModule.onIBApprovalSigChange()"', 'ib',
    // Single mode edits ONE contract — say so when that contract is not Active.
    // Mass mode spans every contract, so there is no one status to speak for.
    isSingle
      ? ((InvoiceBuilderState._contractCache[InvoiceBuilderState.selectedCustomerId] || [])
        .find(c => c.id === InvoiceBuilderState.selectedContractId)?.status || 'Draft')
      : null);
}

// TASK-4683 A3 — the dropdown was changed in this session, so "apply to all"
// may now write it. Until then the mass save leaves every contract's value alone.
function onIBApprovalSigChange() {
  InvoiceBuilderState._approvalSigTouched = true;
  onIBSettingChange();
}

// ── TASK-4683 A3 — "Signature on the approval link": Required / Optional / Off ──
// Locked decision 6. Lives on the contract's existing `fieldTicketSettings` as
// `customerApprovalSignature`, written by BOTH forms that own that map (this
// Templates panel — one contract or all of them — and the Price Book contract
// editor). The approval server resolves it from the customer's ACTIVE contracts
// and enforces it; this is only where the office sets it.
//
// 🔴 NOT the same thing as "Request customer signature" above, which is the pad
// the DRIVER hands the customer at close-out. This one is the customer's own
// signature on the emailed Accept / Reject page. Two different moments, two
// settings, and the label says which.
//
// Missing / unknown ⇒ 'optional' — what the approval page did before this
// setting existed, so no existing contract changes behaviour by being opened.
const _APPROVAL_SIG_MODES = ['optional', 'required', 'off'];
function _approvalSigMode(v) {
  const m = String(v == null ? '' : v).trim().toLowerCase();
  return _APPROVAL_SIG_MODES.includes(m) ? m : 'optional';
}
// `status`: the contract's status, or null when there is no single contract
// (mass mode). Any non-Active status gets the inline note — the server only
// reads Active contracts, so a value set on a Draft does nothing yet.
const _APPROVAL_SIG_INACTIVE_NOTE = 'Only applies while this contract is Active.';
function _approvalSigSelectHtml(id, value, attrs, variant, status) {
  const cur = _approvalSigMode(value);
  const inactive = status != null && status !== 'Active';
  const note = status == null ? ''
    : `<div id="${id}-inactive" style="${variant === 'ib' ? 'flex-basis:100%;' : ''}font-size:11px;color:#92400E;${inactive ? '' : 'display:none;'}">${_APPROVAL_SIG_INACTIVE_NOTE}</div>`;
  const opts = [
    ['optional', 'Optional — they can sign or not'],
    ['required', 'Required to accept'],
    ['off', 'Off — the Accept click is the record']
  ].map(([v, l]) => `<option value="${v}" ${cur === v ? 'selected' : ''}>${l}</option>`).join('');
  const help = 'The customer’s signature on the emailed Accept / Reject page. Rejecting never needs one.';
  if (variant === 'ib') {
    return `<div class="ib-toggle-row" style="flex-wrap:wrap;">
      <label for="${id}">Signature on the approval link</label>
      <select id="${id}" class="form-control" style="font-size:12px;padding:5px 8px;max-width:260px;" ${attrs}>${opts}</select>
      <div style="flex-basis:100%;font-size:11px;color:var(--text-secondary);">${help}</div>
      ${note}
    </div>`;
  }
  return `<div style="display:flex;flex-direction:column;gap:4px;margin-top:4px;">
      <label for="${id}" style="font-size:13px;color:var(--text-primary);margin:0;">Signature on the approval link</label>
      <select id="${id}" class="form-control" style="font-size:13px;padding:6px 10px;max-width:280px;" ${attrs}>${opts}</select>
      <div style="font-size:11px;color:var(--text-secondary);">${help}</div>
      ${note}
    </div>`;
}

// TASK-4714 — reveal the recipient input the moment the requirement is armed, so
// the office cannot arm it without being shown what it still needs.
function onIBEmailCopyToggle(cb) {
  const block = document.getElementById('ib-fts-email-block');
  if (block) block.style.display = cb && cb.checked ? '' : 'none';
  if (cb && cb.checked) setTimeout(() => document.getElementById('ib-fts-email-to')?.focus(), 0);
  onIBSettingChange();
}

function renderIBCompanyHeader() {
  const ch = InvoiceBuilderState.localSettings.companyHeader || {};
  const td = window.AppState?.tenantData || {};
  const phones = td.companyPhones || (td.phone ? [{ label: 'Main', phone: td.phone }] : []);
  const emails = td.companyEmails || (td.email ? [{ label: 'General', email: td.email }] : []);
  const addresses = td.companyAddresses || (td.address ? [{ label: 'Main', address: td.address }] : []);

  const phoneSelect = phones.length > 1 ? `
    <select id="ib-ch-phone-label" style="font-size:11px;padding:2px 4px;border:1px solid var(--border-light);border-radius:4px;margin-left:8px;" onchange="window.BillingModule.onIBSettingChange()">
      ${phones.map(p => `<option value="${window.escAttr(p.label || '')}" ${ch.phoneLabel === p.label ? 'selected' : ''}>${invoiceEsc(p.label || '')}: ${invoiceEsc(p.phone)}</option>`).join('')}
    </select>` : '';

  const emailSelect = emails.length > 1 ? `
    <select id="ib-ch-email-label" style="font-size:11px;padding:2px 4px;border:1px solid var(--border-light);border-radius:4px;margin-left:8px;" onchange="window.BillingModule.onIBSettingChange()">
      ${emails.map(e => `<option value="${window.escAttr(e.label || '')}" ${ch.emailLabel === e.label ? 'selected' : ''}>${invoiceEsc(e.label || '')}: ${invoiceEsc(e.email)}</option>`).join('')}
    </select>` : '';

  const addressSelect = addresses.length > 1 ? `
    <select id="ib-ch-addr-label" style="font-size:11px;padding:2px 4px;border:1px solid var(--border-light);border-radius:4px;margin-left:8px;" onchange="window.BillingModule.onIBSettingChange()">
      ${addresses.map(a => `<option value="${window.escAttr(a.label || '')}" ${ch.addressLabel === a.label ? 'selected' : ''}>${invoiceEsc(a.label || '')}: ${invoiceEsc(a.address).slice(0, 30)}${a.address.length > 30 ? '...' : ''}</option>`).join('')}
    </select>` : '';

  return `<div class="ib-section">
    <div class="ib-section-title" style="display:flex;align-items:center;justify-content:space-between;">
      Company Header
      <button class="btn-icon" onclick="window.BillingModule.openBillingSettings()" title="Edit company info" style="padding:2px;">
        <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">edit</span>
      </button>
    </div>
    <div class="ib-toggle-row">
      <input type="checkbox" id="ib-ch-name" ${ch.showName !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-ch-name">Show company name</label>
    </div>
    <div class="ib-toggle-row">
      <input type="checkbox" id="ib-ch-phone" ${ch.showPhone !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-ch-phone">Show phone</label>${phoneSelect}
    </div>
    <div class="ib-toggle-row">
      <input type="checkbox" id="ib-ch-email" ${ch.showEmail !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-ch-email">Show email</label>${emailSelect}
    </div>
    <div class="ib-toggle-row">
      <input type="checkbox" id="ib-ch-address" ${ch.showAddress !== false ? 'checked' : ''} onchange="window.BillingModule.onIBSettingChange()">
      <label for="ib-ch-address">Show address</label>${addressSelect}
    </div>
  </div>`;
}

function renderIBPreviewPanel() {
  const tab = InvoiceBuilderState.previewTab;
  const isBuilder = tab === 'builder';
  const isTicketMode = ['ticket','ticketonly'].includes(window.AppState?.settings?.clockOutMode || 'simple');
  const ticketFormLabel = (window.AppState?.settings?.clockOutMode === 'ticketonly') ? 'Ticket Form' : 'Clock-Out Form';
  return `
    <div class="ib-preview-tabs">
      <button class="ib-preview-tab ${tab === 'invoice' ? 'active' : ''}" onclick="window.BillingModule.setIBPreviewTab('invoice')">Invoice Preview</button>
      <button class="ib-preview-tab ${tab === 'ticket' ? 'active' : ''}" onclick="window.BillingModule.setIBPreviewTab('ticket')">Field Ticket</button>
      ${window.AppState?.settings?.enableShipSlips === true ? `<button class="ib-preview-tab ${tab === 'slip' ? 'active' : ''}" onclick="window.BillingModule.setIBPreviewTab('slip')">Ship Slip</button>` : ''}
      <button class="ib-preview-tab ${isBuilder ? 'active' : ''}${!isTicketMode ? ' disabled-hint' : ''}" ${!isTicketMode ? `onclick="window.BillingModule.promptEnableBuildTicket()"` : `onclick="window.BillingModule.setIBPreviewTab('builder')"`}>${ticketFormLabel}</button>
      <button class="ib-preview-tab ${tab === 'email' ? 'active' : ''}" onclick="window.BillingModule.setIBPreviewTab('email')">Invoice Email</button>
    </div>
    ${isBuilder ? `<div id="tb-editor" class="tb-editor">${renderTicketBuilderEditor()}</div>` : tab === 'email' ? `<div id="ib-email-preview" style="flex:1;overflow:auto;padding:16px;background:#f1f5f9;border-radius:8px;">${renderEmailPreviewPanel()}</div>` : `<iframe id="ib-preview-iframe" class="ib-preview-frame" sandbox="allow-same-origin"></iframe>`}
  `;
}

function resolveEmailMergeFields(text, data) {
  return (text || '')
    .replace(/\{\{customer\}\}/g, data.customer || '')
    .replace(/\{\{companyName\}\}/g, data.companyName || '')
    .replace(/\{\{invoiceNumber\}\}/g, data.invoiceNumber || '')
    .replace(/\{\{amount\}\}/g, data.amount || '')
    .replace(/\{\{poNumber\}\}/g, data.poNumber || '')
    .replace(/\{\{terms\}\}/g, data.terms || '')
    .replace(/\{\{date\}\}/g, data.date || '')
    .replace(/\{\{dueDate\}\}/g, data.dueDate || '');
}

function renderEmailTemplateSettingsPanel() {
  const et = InvoiceBuilderState.localEmailTemplate;
  const mergeFields = [
    { tag: '{{customer}}', label: 'Customer' },
    { tag: '{{companyName}}', label: 'Company' },
    { tag: '{{invoiceNumber}}', label: 'Invoice #' },
    { tag: '{{amount}}', label: 'Amount' },
    { tag: '{{poNumber}}', label: 'PO #' },
    { tag: '{{terms}}', label: 'Terms' },
    { tag: '{{date}}', label: 'Date' },
    { tag: '{{dueDate}}', label: 'Due Date' }
  ];
  const mergeFieldBadges = mergeFields.map(f =>
    `<button type="button" class="ib-merge-badge" onclick="window.BillingModule.insertMergeField(this.closest('.ib-section').querySelector('textarea,input[type=text]')?.id, '${f.tag}')"
      style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;border-radius:12px;font-size:11px;cursor:pointer;font-weight:500;white-space:nowrap;"
      title="Click to insert ${f.tag}">${f.label}</button>`
  ).join(' ');

  // TASK-4771 — THE CONNECT / DISCONNECT CONTROL MOVED TO COMPANY INFO.
  // What is left here is a POINTER, deliberately: anyone who learned the old
  // location has to land somewhere, and the honest thing for a TEMPLATE
  // designer to say about an account-level Google connection is where it lives.
  // It reads the same InvoiceBuilderState.gmailStatus the card does, and its
  // only action opens that one control — there is no second Connect button, no
  // second Disconnect, and no second copy of the wiring.
  _ensureSendingIdentityStyles();
  const g = InvoiceBuilderState.gmailStatus || { connected: false, loaded: false };
  const gmailHead = g.connected
    ? `Invoices send from <span class="ci-si-addr">${invoiceEsc(g.email || 'your Gmail')}</span>`
    : g.needsReconnect // TASK-1412
      ? `Invoice emails are NOT going out — <span class="ci-si-addr">${invoiceEsc(g.email || 'your Gmail')}</span> needs to be reconnected.`
      : (g.loaded ? 'Currently sending via DispatchDeck on your behalf.' : 'Checking how your invoices send…');
  const gmailRow = `<div class="ci-si-card${g.connected ? ' ci-si-connected' : ''}">
      <div class="ci-si-top">
        <span class="material-icons ci-si-icon">${g.connected ? 'check_circle' : 'mail'}</span>
        <div class="ci-si-copy">
          <div class="ci-si-head">${gmailHead}</div>
          <div class="ci-si-sub">Who invoices come FROM is a company setting, not part of this template. It lives in Company Info, with your phone numbers and addresses.</div>
        </div>
      </div>
      <div class="ci-si-actions">
        <button type="button" class="ci-si-btn ci-si-btn-quiet" onclick="window.BillingModule.openCompanyInfoModal()">
          <span class="material-icons" style="font-size:15px;">business</span>Open Company Info
        </button>
      </div>
    </div>`;

  return `
    <div class="ib-section">
      <div class="ib-section-title">Invoice Email Template</div>
      <p style="font-size:12px;color:var(--text-secondary);margin:0 0 12px;">Customize the default email sent with invoices. Use merge fields to auto-fill data.</p>

      <div style="margin-bottom:14px;">${gmailRow}</div>

      <div style="margin-bottom:14px;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Reply-To Email</label>
        <input type="text" id="ib-email-reply-to" value="${invoiceEsc(et.replyTo)}" placeholder="your@company.com"
          style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;font-size:13px;box-sizing:border-box;"
          onchange="window.BillingModule.onIBSettingChange()">
        <div style="font-size:11px;color:#64748b;margin-top:4px;">${g.connected ? 'Ignored when sending from your Gmail — replies go to your inbox.' : ''}</div>
      </div>

      <div style="margin-bottom:14px;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Subject Line</label>
        <input type="text" id="ib-email-subject" value="${invoiceEsc(et.subject)}"
          style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;font-size:13px;box-sizing:border-box;"
          onfocus="window._lastFocusedEmailField='ib-email-subject'"
          onchange="window.BillingModule.onIBSettingChange()">
        <div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">${mergeFields.map(f =>
          `<button type="button" style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;border-radius:12px;font-size:11px;cursor:pointer;font-weight:500;white-space:nowrap;"
            onclick="window.BillingModule.insertMergeField('ib-email-subject', '${f.tag}')">${f.label}</button>`
        ).join('')}</div>
      </div>

      <div style="margin-bottom:14px;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Message Body</label>
        <textarea id="ib-email-body" rows="6"
          style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;font-size:13px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;"
          onfocus="window._lastFocusedEmailField='ib-email-body'"
          onchange="window.BillingModule.onIBSettingChange()">${invoiceEsc(et.body)}</textarea>
        <div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">${mergeFields.map(f =>
          `<button type="button" style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#EEF2FF;color:#4338CA;border:1px solid #C7D2FE;border-radius:12px;font-size:11px;cursor:pointer;font-weight:500;white-space:nowrap;"
            onclick="window.BillingModule.insertMergeField('ib-email-body', '${f.tag}')">${f.label}</button>`
        ).join('')}</div>
      </div>

      <div style="margin-bottom:8px;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Payment Instructions <span style="font-weight:400;color:var(--text-secondary);">(optional)</span></label>
        <textarea id="ib-email-payment" rows="3"
          style="width:100%;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;font-size:13px;box-sizing:border-box;resize:vertical;font-family:inherit;line-height:1.5;"
          onfocus="window._lastFocusedEmailField='ib-email-payment'"
          onchange="window.BillingModule.onIBSettingChange()" placeholder="e.g. Bank wire info, Zelle, mailing address for checks...">${invoiceEsc(et.paymentInstructions)}</textarea>
      </div>
    </div>
  `;
}

function renderEmailPreviewPanel() {
  const et = InvoiceBuilderState.localEmailTemplate;
  const sample = buildIBSampleData();
  const { companyName, companyEmail, logoUrl } = sample;
  const sampleData = {
    customer: sample.customer?.name || 'Acme Energy LLC',
    companyName: companyName || 'Your Company',
    invoiceNumber: sample.ticket?.invoiceNumber || 'INV-1042',
    amount: invoiceFmtMoney(sample.ticket?.grandTotal || 6925.00),
    poNumber: sample.ticket?.poNumber || 'PO-2026-0847',
    terms: 'Net 30',
    date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    dueDate: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); })()
  };

  // Read latest values from DOM if available
  const subjectEl = document.getElementById('ib-email-subject');
  const bodyEl = document.getElementById('ib-email-body');
  const paymentEl = document.getElementById('ib-email-payment');
  const replyToEl = document.getElementById('ib-email-reply-to');
  const subject = subjectEl ? subjectEl.value : et.subject;
  const body = bodyEl ? bodyEl.value : et.body;
  const payment = paymentEl ? paymentEl.value : et.paymentInstructions;
  const replyTo = replyToEl ? replyToEl.value : et.replyTo;

  const resolvedSubject = resolveEmailMergeFields(subject, sampleData);
  const resolvedBody = resolveEmailMergeFields(body, sampleData).replace(/\n/g, '<br>');
  const resolvedPayment = resolveEmailMergeFields(payment, sampleData).replace(/\n/g, '<br>');

  const logo = logoUrl || 'https://dispatchdeck.app/logo-light-tight.png';

  return `
    <div style="max-width:560px;margin:0 auto;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
      <div style="background:#fff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.08);overflow:hidden;">
        <div style="background:#f8fafc;padding:6px 16px;border-bottom:1px solid #e2e8f0;">
          <div style="font-size:11px;color:#64748b;margin-bottom:2px;">Subject</div>
          <div style="font-size:13px;font-weight:600;color:#0f172a;">${invoiceEsc(resolvedSubject)}</div>
          ${replyTo ? `<div style="font-size:11px;color:#64748b;margin-top:4px;">Reply-To: ${invoiceEsc(replyTo)}</div>` : ''}
        </div>
        <div style="padding:24px;">
          <div style="text-align:center;margin-bottom:20px;">
            <img src="${invoiceEsc(logo)}" alt="Logo" style="max-height:50px;max-width:180px;">
          </div>
          <div style="font-size:14px;color:#1e293b;line-height:1.6;margin-bottom:20px;">${resolvedBody}</div>
          <div style="background:#f1f5f9;border-radius:8px;padding:16px;margin-bottom:20px;">
            <div style="font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Invoice Preview</div>
            <div style="font-size:13px;color:#475569;line-height:1.5;">
              <div><strong>Invoice:</strong> ${invoiceEsc(sampleData.invoiceNumber)}</div>
              <div><strong>Customer:</strong> ${invoiceEsc(sampleData.customer)}</div>
              <div><strong>Amount:</strong> ${invoiceEsc(sampleData.amount)}</div>
              <div><strong>Date:</strong> ${invoiceEsc(sampleData.date)}</div>
              <div style="font-style:italic;color:#94a3b8;margin-top:6px;">[Full invoice table will appear here]</div>
            </div>
          </div>
          ${resolvedPayment ? `
            <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:14px;margin-bottom:20px;">
              <div style="font-size:12px;font-weight:700;color:#92400E;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Payment Instructions</div>
              <div style="font-size:13px;color:#78350F;line-height:1.5;">${resolvedPayment}</div>
            </div>
          ` : ''}
          <div style="border-top:1px solid #e2e8f0;padding-top:16px;text-align:center;">
            <div style="font-size:11px;color:#94a3b8;">Sent via <a href="https://dispatchdeck.app" style="color:#3b82f6;text-decoration:none;font-weight:600;">DispatchDeck</a></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function insertMergeField(fieldId, tag) {
  const el = document.getElementById(fieldId);
  if (!el) return;
  const start = el.selectionStart || 0;
  const end = el.selectionEnd || 0;
  const val = el.value;
  el.value = val.substring(0, start) + tag + val.substring(end);
  el.selectionStart = el.selectionEnd = start + tag.length;
  el.focus();
  // Trigger preview update
  onIBSettingChange();
}

function renderIBSaveBar() {
  const isMass = InvoiceBuilderState.mode === 'mass';
  return `
    ${isMass ? `<div class="ib-save-warning">
      <span class="material-icons" style="font-size:16px;">warning</span>
      Mass mode will override invoice settings on all contracts.
    </div>` : ''}
    <div class="ib-save-actions">
      <button class="btn btn-secondary" onclick="window.BillingModule.closeInvoiceBuilder()">Cancel</button>
      <button class="btn btn-primary" id="ib-save-btn" onclick="window.BillingModule.saveInvoiceBuilder()">
        <span class="material-icons" style="font-size:16px;">save</span> Save
      </button>
    </div>
  `;
}

// Section order drag within Invoice Builder
function setupIBSectionOrderDrag(container) {
  let dragEl = null;
  container.addEventListener('dragstart', (e) => {
    dragEl = e.target.closest('[data-section-key]');
    if (!dragEl) return;
    dragEl.style.opacity = '0.4';
    e.dataTransfer.effectAllowed = 'move';
  });
  container.addEventListener('dragend', () => {
    if (dragEl) dragEl.style.opacity = '1';
    dragEl = null;
    container.querySelectorAll('[data-section-key]').forEach(el => { el.style.borderTop = ''; el.style.borderBottom = ''; });
  });
  container.addEventListener('dragover', (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const target = e.target.closest('[data-section-key]');
    if (!target || target === dragEl) return;
    container.querySelectorAll('[data-section-key]').forEach(el => { el.style.borderTop = ''; el.style.borderBottom = ''; });
    const rect = target.getBoundingClientRect();
    if (e.clientY < rect.top + rect.height / 2) {
      target.style.borderTop = '2px solid var(--brand-blue)';
    } else {
      target.style.borderBottom = '2px solid var(--brand-blue)';
    }
  });
  container.addEventListener('drop', (e) => {
    e.preventDefault();
    const target = e.target.closest('[data-section-key]');
    if (!target || !dragEl || target === dragEl) return;
    const rect = target.getBoundingClientRect();
    if (e.clientY < rect.top + rect.height / 2) {
      container.insertBefore(dragEl, target);
    } else {
      container.insertBefore(dragEl, target.nextSibling);
    }
    container.querySelectorAll('[data-section-key]').forEach(el => { el.style.borderTop = ''; el.style.borderBottom = ''; });
    // Trigger preview update after reorder
    onIBSettingChange();
  });
}

function renderIBSectionOrderInBuilder() {
  const wrapper = document.getElementById('ib-section-order-wrapper');
  if (!wrapper) return;
  const activeKeys = InvoiceBuilderState.localSettings.invoiceSectionOrder;
  const allKeys = Object.keys(INVOICE_SECTION_META);
  const hiddenKeys = allKeys.filter(k => !activeKeys.includes(k));

  let html = `<div id="ib-section-active-list" style="display:flex;flex-direction:column;gap:4px;">`;
  activeKeys.forEach(key => {
    const meta = INVOICE_SECTION_META[key];
    if (!meta) return;
    html += `<div draggable="true" data-section-key="${key}" style="display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);cursor:grab;user-select:none;">
      <span class="material-icons" style="font-size:14px;color:var(--text-secondary);cursor:grab;">drag_indicator</span>
      <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">${meta.icon}</span>
      <span style="flex:1;font-size:0.8rem;font-weight:500;color:var(--text-primary);">${meta.label}</span>
      <button type="button" onclick="window.BillingModule.removeIBSection('${key}')" style="background:none;border:none;cursor:pointer;padding:2px;display:flex;align-items:center;" title="Remove">
        <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">close</span>
      </button>
    </div>`;
  });
  html += `</div>`;

  if (hiddenKeys.length > 0) {
    html += `<div style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">`;
    hiddenKeys.forEach(key => {
      const meta = INVOICE_SECTION_META[key];
      if (!meta) return;
      html += `<button type="button" onclick="window.BillingModule.addIBSection('${key}')" style="display:inline-flex;align-items:center;gap:4px;padding:4px 8px;background:var(--bg-secondary);border:1px dashed var(--border-light);border-radius:var(--radius-sm);cursor:pointer;font-size:0.75rem;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:12px;color:var(--brand-blue);">add</span>
        ${meta.label}
      </button>`;
    });
    html += `</div>`;
  }

  wrapper.innerHTML = html;
  const list = document.getElementById('ib-section-active-list');
  if (list) setupIBSectionOrderDrag(list);
}

// ============================================
// CLOCK-OUT FORM — Template Builder
// ============================================
// TASK-9337 -- settings.customTicketSections is now writable by billing-module
// STAFF (tenant-root module arm), not just admins, and an admin opens every
// surface that renders it (Ticket Builder, invoice builder, review). Section and
// field ids land in HTML attributes and inline-handler JS strings, so drop any
// section/field whose id is not a plain token -- the Template Builder only mints
// 'cts_' / 'f_' + base36, and a live census (84 tenants, 4 sections, 13 fields)
// found none that fail. Text (name/icon/label) is escaped at each sink instead.
// Same filter as clock.js _csDefs / jobboard.js _jbCsDefs. WRITE paths keep
// reading the raw stored array so a save never silently deletes a section.
function _bCsDefs() {
  const raw = window.AppState?.settings?.customTicketSections;
  if (!Array.isArray(raw)) return [];
  const ok = (v) => /^[A-Za-z0-9_-]{1,64}$/.test(String(v ?? ''));
  return raw
    .filter(cs => cs && ok(cs.id))
    .map(cs => (Array.isArray(cs.fields) ? { ...cs, fields: cs.fields.filter(f => f && ok(f.id)) } : cs));
}

function resetTicketBuilder() {
  _tbLocalId = 0;

  // Load saved section config from settings, or use defaults
  const savedSections = window.AppState?.settings?.clockOutFormSections;
  if (savedSections && Array.isArray(savedSections)) {
    TicketBuilderState.sections = TB_SECTIONS.map(s => {
      const saved = savedSections.find(ss => ss.key === s.key);
      // TASK-1483 — the clock-out surfaces treat an UNSAVED key as "show"
      // (isCOFSectionEnabled/jbCOFEnabled), gated for Personal Vehicle by the
      // tenant's feature switch. Mirror that here so the designer never shows
      // the section OFF while drivers see it ON.
      if (!saved && s.key === 'personalVehicle') return { ...s, enabled: window.AppState?.settings?.personalVehicle?.enabled === true };
      return { ...s, enabled: saved ? saved.enabled : s.defaultOn };
    });
  } else {
    TicketBuilderState.sections = TB_SECTIONS.map(s => ({ ...s, enabled: s.defaultOn }));
  }

  // Include custom sections in the sections list
  const customSections = _bCsDefs();
  for (const cs of customSections) {
    const csKey = 'custom_' + cs.id;
    if (!TicketBuilderState.sections.find(s => s.key === csKey)) {
      const saved = savedSections?.find(ss => ss.key === csKey);
      TicketBuilderState.sections.push({
        key: csKey, label: cs.name, icon: cs.icon || 'tune',
        enabled: saved ? saved.enabled : true
      });
    }
  }

  TicketBuilderState.dirty = false;
}

// ---- Left panel: Clock-Out Form template config ----
function renderTBSettingsPanel() {
  const sections = TicketBuilderState.sections;
  const isTicketOnly = window.AppState?.settings?.clockOutMode === 'ticketonly';
  const ticketFormLabel = isTicketOnly ? 'Ticket Form' : 'Clock-Out Form';
  const sectionsDesc = isTicketOnly
    ? 'Configure which sections drivers see on the field ticket they fill out. The preview on the right shows what the form will look like.'
    : 'Configure which sections drivers see when clocking out. The preview on the right shows what the form will look like.';

  let html = `<div class="ib-section">
    <div class="ib-section-title">${ticketFormLabel} Template</div>
    <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">${sectionsDesc}</p>
  </div>`;

  // Sections toggle + drag
  html += `<div class="ib-section">
    <div class="ib-section-title">Form Sections</div>
    <div id="tb-section-list" style="display:flex;flex-direction:column;gap:4px;">
      ${sections.map(s => `
        <div draggable="true" data-tb-section="${escapeHtml(s.key)}" class="tb-section-row">
          <span class="material-icons" style="font-size:14px;color:var(--text-secondary);cursor:grab;">drag_indicator</span>
          <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">${escapeHtml(s.icon)}</span>
          <span style="flex:1;font-size:0.8rem;font-weight:500;color:var(--text-primary);">${escapeHtml(s.label)}</span>
          <label class="tb-toggle">
            <input type="checkbox" ${s.enabled ? 'checked' : ''} onchange="window.BillingModule.toggleTBSection('${window.escJsAttr(s.key)}', this.checked)">
            <span class="tb-toggle-slider"></span>
          </label>
        </div>
      `).join('')}
    </div>
  </div>`;

  // Custom Sections (TASK-9567: this is also how drivers record piece-rate
  // quantities — the old Piece Rate toggle rendered nothing at clock-out)
  const customSections = _bCsDefs();
  html += `<div class="ib-section">
    <div class="ib-section-title">Custom Sections</div>
    <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">Add custom data fields to the clock-out form. Optionally tie them to a line item for automatic billing.</p>
    <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">Bill per load, ton or yard? Add a number field here and tie it to that Price Book line. Drivers type the count at clock-out.</p>
    <div id="tb-custom-sections-list" style="display:flex;flex-direction:column;gap:4px;">
      ${customSections.map(cs => {
        const csKey = 'custom_' + cs.id;
        const csEnabled = TicketBuilderState.sections.find(s => s.key === csKey)?.enabled !== false;
        return `
          <div class="tb-section-row" style="align-items:center;">
            <span class="material-icons" style="font-size:14px;color:var(--accent-teal);">${escapeHtml(cs.icon || 'tune')}</span>
            <span style="flex:1;font-size:0.8rem;font-weight:500;color:var(--text-primary);">${escapeHtml(cs.name)}</span>
            <span style="font-size:0.65rem;color:var(--text-secondary);margin-right:4px;">${cs.fields?.length || 0} field${(cs.fields?.length || 0) !== 1 ? 's' : ''}</span>
            ${cs.fields?.some(f => f.lineItemBinding) ? '<span class="material-icons" style="font-size:12px;color:var(--brand-blue);" title="Has line item bindings">link</span>' : ''}
            <button class="btn-icon" onclick="window.BillingModule.editCustomSection('${window.escJsAttr(cs.id)}')" title="Edit" style="padding:2px;">
              <span class="material-icons" style="font-size:14px;">edit</span>
            </button>
            <button class="btn-icon" onclick="window.BillingModule.deleteCustomSection('${window.escJsAttr(cs.id)}')" title="Delete" style="padding:2px;color:#ef4444;">
              <span class="material-icons" style="font-size:14px;">delete_outline</span>
            </button>
            <label class="tb-toggle">
              <input type="checkbox" ${csEnabled ? 'checked' : ''} onchange="window.BillingModule.toggleTBSection('${window.escJsAttr(csKey)}', this.checked)">
              <span class="tb-toggle-slider"></span>
            </label>
          </div>`;
      }).join('')}
    </div>
    <button class="btn btn-secondary btn-sm" style="margin-top:8px;width:100%;" onclick="window.BillingModule.editCustomSection(null)">
      <span class="material-icons" style="font-size:14px;">add</span> Add Custom Section
    </button>
  </div>`;

  // Save button
  html += `<div style="padding-top:12px;">
    <button class="btn btn-primary" style="width:100%;" id="cof-save-btn" onclick="window.BillingModule.saveCOFTemplate()">
      <span class="material-icons" style="font-size:16px;">save</span> Save Template
    </button>
  </div>`;

  return html;
}

// ---- Custom Section Editor ----
const CS_ICONS = ['tune','construction','water_drop','local_gas_station','speed','straighten','plumbing','build','science','thermostat','oil_barrel','electric_bolt','compress','settings_input_component','precision_manufacturing','pin_drop','my_location','landscape','engineering','handyman','agriculture','inventory_2'];
const CS_FIELD_TYPES = [
  { value: 'number', label: 'Number', icon: 'pin' },
  { value: 'text', label: 'Text', icon: 'text_fields' },
  { value: 'checkbox', label: 'Checkbox', icon: 'check_box' },
  { value: 'waypoint', label: 'Address / Map Link', icon: 'place' },
  { value: 'gps', label: 'GPS Location', icon: 'my_location' },
  // TASK-4607 — the generic attachment. Deliberately NOT called "Permit": the
  // ask was for a configurable field so we are not hardcoding one customer's
  // workflow. A tenant that needs permits names it Permit; another names it
  // Soil Ticket. Never carries a lineItemBinding — billing quantities must be
  // numeric, and the editor already clears the binding on any non-number type.
  { value: 'file', label: 'File / Photo', icon: 'attach_file' }
];

let _csEditing = null; // { id, name, icon, fields, lineItemBinding } or null for new

function editCustomSection(sectionId) {
  const existing = _bCsDefs().find(s => s.id === sectionId);
  if (existing) {
    _csEditing = JSON.parse(JSON.stringify(existing)); // deep clone
    // Migrate old section-level binding to field-level if needed
    if (_csEditing.lineItemBinding && !_csEditing.fields.some(f => f.lineItemBinding)) {
      const targetField = _csEditing.fields.find(f => f.id === _csEditing.lineItemBinding.qtyFieldId);
      if (targetField) {
        targetField.lineItemBinding = {
          itemCode: _csEditing.lineItemBinding.itemCode,
          description: _csEditing.lineItemBinding.description
        };
      }
      delete _csEditing.lineItemBinding;
    }
  } else {
    _csEditing = {
      id: 'cts_' + Date.now().toString(36),
      name: '',
      icon: 'tune',
      repeatable: false,
      fields: [{ id: 'f_' + Date.now().toString(36), label: '', type: 'number', lineItemBinding: null }]
    };
  }
  renderCustomSectionEditor();
}

function renderCustomSectionEditor() {
  const cs = _csEditing;
  if (!cs) return;
  const isNew = !_bCsDefs().find(s => s.id === cs.id);

  // Remove any existing editor
  document.getElementById('custom-section-editor-modal')?.remove();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'custom-section-editor-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;max-height:85dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">tune</span>${isNew ? 'New' : 'Edit'} Custom Section</h3>
        <button class="btn-icon" onclick="document.getElementById('custom-section-editor-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <!-- Section Name -->
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Section Name <span style="color:#ef4444;">*</span></label>
          <input type="text" id="cs-name" class="form-control" value="${escapeHtml(cs.name)}" placeholder="e.g. Pump Hours, Pipe Measurements" maxlength="50" oninput="window.BillingModule._csUpdateName(this.value)">
        </div>

        <!-- Icon Picker -->
        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Icon</label>
          <div style="display:flex;flex-wrap:wrap;gap:6px;">
            ${CS_ICONS.map(ic => `
              <button type="button" class="cs-icon-btn${cs.icon === ic ? ' active' : ''}" onclick="window.BillingModule._csSetIcon('${ic}')" style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:8px;border:2px solid ${cs.icon === ic ? 'var(--brand-blue)' : 'var(--border-light)'};background:${cs.icon === ic ? '#EFF6FF' : 'var(--bg-secondary)'};cursor:pointer;transition:all 0.15s;">
                <span class="material-icons" style="font-size:18px;color:${cs.icon === ic ? 'var(--brand-blue)' : 'var(--text-secondary)'};">${ic}</span>
              </button>
            `).join('')}
          </div>
        </div>

        <!-- Fields -->
        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Fields</label>
          <p style="font-size:0.75rem;color:var(--text-secondary);margin:0 0 8px;">Number fields can optionally be tied to a line item — the value becomes the billing quantity.</p>
          <div id="cs-fields-list" style="display:flex;flex-direction:column;gap:10px;">
            ${cs.fields.map((f, i) => _csFieldRow(f, i)).join('')}
          </div>
          <button type="button" class="btn btn-secondary btn-sm" style="margin-top:8px;" onclick="window.BillingModule._csAddField()">
            <span class="material-icons" style="font-size:14px;">add</span> Add Field
          </button>
        </div>

        <!-- Repeatable toggle -->
        <div class="form-group" style="margin-bottom:8px;border-top:1px solid var(--border-light);padding-top:12px;">
          <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;">
            <input type="checkbox" id="cs-repeatable" ${cs.repeatable ? 'checked' : ''} onchange="window.BillingModule._csSetRepeatable(this.checked)" style="margin-top:2px;">
            <span>
              <span style="font-weight:600;color:var(--text-primary);">Allow multiple entries</span>
              <span style="display:block;font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">The crew can tap "+ Add another" to log this section more than once on a single ticket (e.g. several potholes or bores). Up to 25 entries.</span>
            </span>
          </label>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('custom-section-editor-modal')?.remove()">Cancel</button>
        <button class="btn btn-primary" id="cs-save-btn" onclick="window.BillingModule.saveCustomSection()">
          <span class="material-icons" style="font-size:16px;">save</span> ${isNew ? 'Create Section' : 'Save Changes'}
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function _csFieldRow(field, index) {
  const canBind = field.type === 'number';
  const hasBind = canBind && field.lineItemBinding;
  const bindHint = !canBind ? `<div style="font-size:0.65rem;color:var(--text-muted);margin-top:3px;font-style:italic;">${field.type === 'waypoint' ? 'Address / Map Link fields accept addresses, map links, or lat/long' : field.type === 'gps' ? 'GPS fields save the driver\'s current location' : 'Line items can only be tied to number fields'}</div>` : '';

  return `
    <div style="border:1px solid var(--border-light,#e2e8f0);border-radius:8px;padding:8px 10px;" data-cs-field-idx="${index}">
      <div style="display:flex;gap:6px;align-items:center;">
        <input type="text" class="form-control" value="${escapeHtml(field.label)}" placeholder="Field label" style="flex:1;font-size:0.85rem;padding:8px 10px;" oninput="window.BillingModule._csUpdateField(${index},'label',this.value)">
        <select class="form-control" style="width:110px;font-size:0.8rem;padding:8px;" onchange="window.BillingModule._csUpdateField(${index},'type',this.value)">
          ${CS_FIELD_TYPES.map(t => `<option value="${t.value}" ${field.type === t.value ? 'selected' : ''}>${t.label}</option>`).join('')}
        </select>
        <button class="btn-icon" onclick="window.BillingModule._csRemoveField(${index})" title="Remove" style="padding:2px;color:#ef4444;flex-shrink:0;">
          <span class="material-icons" style="font-size:16px;">close</span>
        </button>
      </div>
      ${hasBind ? `
        <div style="display:flex;align-items:center;gap:6px;margin-top:6px;padding:6px 8px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:6px;">
          <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">link</span>
          <span style="flex:1;font-size:0.75rem;font-weight:500;color:var(--brand-blue);">${escapeHtml(field.lineItemBinding.description || field.lineItemBinding.itemCode)}</span>
          <button class="btn-icon" onclick="window.BillingModule._csRemoveFieldBinding(${index})" title="Remove" style="padding:1px;color:#ef4444;">
            <span class="material-icons" style="font-size:14px;">close</span>
          </button>
        </div>
      ` : canBind ? `
        <button type="button" class="btn btn-secondary" style="margin-top:6px;padding:4px 10px;font-size:0.7rem;border-radius:6px;" onclick="window.BillingModule._csShowFieldBindingPicker(${index})">
          <span class="material-icons" style="font-size:12px;">link</span> Assign Line Item
        </button>
      ` : bindHint}
    </div>
  `;
}

function _csUpdateName(val) { if (_csEditing) _csEditing.name = val; }
function _csSetIcon(icon) { if (_csEditing) { _csEditing.icon = icon; renderCustomSectionEditor(); } }
function _csSetRepeatable(val) { if (_csEditing) _csEditing.repeatable = !!val; }
function _csAddField() {
  if (!_csEditing) return;
  _csEditing.fields.push({ id: 'f_' + Date.now().toString(36), label: '', type: 'number' });
  renderCustomSectionEditor();
  // Scroll to the new field
  setTimeout(() => {
    const list = document.getElementById('cs-fields-list');
    if (list?.lastElementChild) list.lastElementChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 50);
}
function _csRemoveField(idx) {
  if (!_csEditing || _csEditing.fields.length <= 1) { window.showToast?.('Need at least one field', 'error'); return; }
  const removed = _csEditing.fields.splice(idx, 1)[0];
  // Clear binding if it referenced this field
  if (_csEditing.lineItemBinding?.qtyFieldId === removed.id) _csEditing.lineItemBinding = null;
  renderCustomSectionEditor();
}
function _csUpdateField(idx, prop, val) {
  if (!_csEditing) return;
  _csEditing.fields[idx][prop] = val;
  // If type changed away from number and it was the bound qty field, clear binding
  if (prop === 'type' && val !== 'number' && _csEditing.lineItemBinding?.qtyFieldId === _csEditing.fields[idx].id) {
    _csEditing.lineItemBinding = null;
  }
}
function _csRemoveBinding() {
  // Legacy — no longer used at section level
}

function _csRemoveFieldBinding(fieldIdx) {
  if (!_csEditing) return;
  _csEditing.fields[fieldIdx].lineItemBinding = null;
  renderCustomSectionEditor();
}

let _csBindingFieldIdx = null;

// TASK-846-A — cache of the tenant's Price Book items for the binding picker.
let _csPriceBookItems = null;
async function _csLoadPriceBook() {
  if (_csPriceBookItems) return _csPriceBookItems;
  const tenant = BillingState.tenantId === 'demo-tenant' ? 'demo' : BillingState.tenantId;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'itemCodes'));
    _csPriceBookItems = snap.docs.map(d => {
      const x = d.data() || {};
      return { code: String(x.code || d.id).toUpperCase(), description: x.description || '', rate: Number(x.defaultRate || 0), rateUnit: x.defaultRateUnit || x.rateUnit || '' };
    }).sort((a, b) => a.code.localeCompare(b.code));
  } catch (e) { console.warn('[Billing] price book load for binding picker', e); _csPriceBookItems = []; }
  return _csPriceBookItems;
}

async function _csShowFieldBindingPicker(fieldIdx) {
  if (!_csEditing) return;
  const field = _csEditing.fields[fieldIdx];
  if (field.type !== 'number') { window.showToast?.('Only number fields can be tied to line items', 'error'); return; }
  _csBindingFieldIdx = fieldIdx;

  // TASK-846-A — bind to a REAL Price Book item (dropdown, not free text) so the
  // itemCode always matches and the reconciler resolves a rate (kills the
  // typo→$0 trap, e.g. field "Dumps" vs Price Book "Disposal").
  const items = await _csLoadPriceBook();
  const curCode = (field.lineItemBinding?.itemCode || '').toUpperCase();

  document.getElementById('cs-binding-picker')?.remove();
  const picker = document.createElement('div');
  picker.className = 'modal';
  picker.id = 'cs-binding-picker';
  const optionsHtml = items.length
    ? items.map(it => `<option value="${escapeHtml(it.code)}"${it.code === curCode ? ' selected' : ''}>${escapeHtml(it.code)}${it.description ? ' — ' + escapeHtml(it.description) : ''}${it.rate > 0 ? ` (default $${it.rate.toFixed(2)}${it.rateUnit ? '/' + escapeHtml(it.rateUnit) : ''})` : ' (no default rate)'}</option>`).join('')
    : '';
  picker.innerHTML = `
    <div class="modal-content modal-small" style="max-width:400px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">link</span>Assign Line Item</h3>
        <button class="btn-icon" onclick="document.getElementById('cs-binding-picker')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 12px;">The value of <strong>${escapeHtml(field.label || 'this field')}</strong> will become the quantity on an auto-calculated billing line.</p>
        ${items.length ? `
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Price Book Item <span style="color:#ef4444;">*</span></label>
          <select id="cs-bind-item-code" class="form-control" onchange="window.BillingModule._csBindOnItemChange()">
            <option value="">Select an item…</option>
            ${optionsHtml}
          </select>
          <p style="font-size:0.7rem;color:var(--text-secondary);margin:4px 0 0;">The line bills the captured value × the customer's Price Book price for this item. A customer with no price for it gets the default rate shown here.</p>
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Line Description</label>
          <input type="text" id="cs-bind-description" class="form-control" placeholder="Defaults to the item's description" maxlength="100" value="${escapeHtml(field.lineItemBinding?.description || '')}">
        </div>
        ` : `
        <div style="padding:12px;background:rgba(220,38,38,0.06);border-radius:8px;font-size:0.8rem;color:#DC2626;">
          Your Price Book has no items yet. Add items in <strong>Billing → Price Book</strong> first, then bind this field to one.
        </div>`}
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('cs-binding-picker')?.remove()">Cancel</button>
        ${items.length ? `<button class="btn btn-primary" onclick="window.BillingModule._csConfirmBinding()">
          <span class="material-icons" style="font-size:16px;">link</span> Assign
        </button>` : ''}
      </div>
    </div>
  `;
  document.body.appendChild(picker);
}

// Auto-fill the description from the selected Price Book item when it's blank.
function _csBindOnItemChange() {
  const sel = document.getElementById('cs-bind-item-code');
  const desc = document.getElementById('cs-bind-description');
  if (!sel || !desc) return;
  const it = (_csPriceBookItems || []).find(i => i.code === sel.value);
  if (it && !desc.value.trim()) desc.value = it.description || it.code;
}

function _csConfirmBinding() {
  const itemCode = document.getElementById('cs-bind-item-code')?.value?.trim();
  const description = document.getElementById('cs-bind-description')?.value?.trim();
  if (!itemCode) { window.showToast?.('Select a Price Book item', 'error'); return; }
  if (_csBindingFieldIdx == null || !_csEditing) return;
  _csEditing.fields[_csBindingFieldIdx].lineItemBinding = { itemCode: itemCode.toUpperCase(), description: description || itemCode.toUpperCase() };
  _csBindingFieldIdx = null;
  document.getElementById('cs-binding-picker')?.remove();
  renderCustomSectionEditor();
}

async function saveCustomSection() {
  if (!_csEditing) return;
  const cs = _csEditing;

  // Validate
  if (!cs.name.trim()) { window.showToast?.('Section name is required', 'error'); return; }
  const emptyFields = cs.fields.filter(f => !f.label.trim());
  if (emptyFields.length > 0) { window.showToast?.('All fields need a label', 'error'); return; }

  const btn = document.getElementById('cs-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenant = getTenant();
    if (!tenant) throw new Error('No tenant');

    const tenantRef = doc(db, 'tenants', tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingSettings = tenantDoc.exists() ? (tenantDoc.data().settings || {}) : {};
    const sections = existingSettings.customTicketSections || [];

    // Update or insert
    const idx = sections.findIndex(s => s.id === cs.id);
    if (idx >= 0) {
      sections[idx] = cs;
    } else {
      cs.sortOrder = sections.length;
      sections.push(cs);
    }

    // Also ensure clockOutFormSections has an entry for this custom section
    const cofSections = existingSettings.clockOutFormSections || [];
    const csKey = 'custom_' + cs.id;
    if (!cofSections.find(s => s.key === csKey)) {
      cofSections.push({ key: csKey, enabled: true });
    }

    // 🔴 TASK-9280 — two owned arrays, no `...existingSettings`. The getDoc a few
    // lines up is still needed (it is the base the two arrays are edited from),
    // but its snapshot must NOT be written back: `merge: true` REPLACES arrays
    // wholesale and rewrites every scalar leaf it is handed, so the spread
    // reverted anything another writer changed in the window — including
    // settings.smsInbox.optedOutOfAi, the SMS consent record. Omitting those keys
    // is what preserves them. Verified against real Firestore 2026-09-22. See
    // saveBillingSettings for the long note.
    //
    // ⚠️ Both keys ARE arrays, so replacement is the intended semantic here and
    // removing a section works. The read→modify→write on the array is still a
    // TOCTOU between two admins editing sections at the same time — pre-existing,
    // unchanged by this narrowing, and a different fix (arrayUnion/transaction).
    await setDoc(tenantRef, {
      settings: {
        customTicketSections: sections,
        clockOutFormSections: cofSections
      }
    }, { merge: true });

    // Update local state
    window.AppState.settings.customTicketSections = sections;
    window.AppState.settings.clockOutFormSections = cofSections;

    // Also add to TicketBuilderState.sections if not present
    if (!TicketBuilderState.sections.find(s => s.key === csKey)) {
      TicketBuilderState.sections.push({ key: csKey, label: cs.name, icon: cs.icon || 'tune', enabled: true });
    } else {
      const tbSec = TicketBuilderState.sections.find(s => s.key === csKey);
      tbSec.label = cs.name;
      tbSec.icon = cs.icon || 'tune';
    }

    window.showToast?.(`Custom section "${cs.name}" saved`, 'success');
    document.getElementById('custom-section-editor-modal')?.remove();
    _csEditing = null;

    // Re-render settings panel + preview
    const panel = document.querySelector('.ib-settings-panel');
    if (panel) panel.innerHTML = renderTBSettingsPanel();
    rerenderTBEditor();
  } catch (err) {
    console.error('[Billing] Save custom section error:', err);
    window.showToast?.('Error saving custom section', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save'; }
  }
}

// TASK-848 — persist the per-section "Show on Customer Invoice" flag from the invoice
// template builder. Same tenant-root settings write path as saveCustomSection (admin-gated
// by firestore.rules). Missing flag = shown, so we only ever write an explicit boolean.
async function toggleCustomSectionInvoice(sectionId, show) {
  try {
    const tenant = getTenant();
    if (!tenant) throw new Error('No tenant');

    const tenantRef = doc(db, 'tenants', tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingSettings = tenantDoc.exists() ? (tenantDoc.data().settings || {}) : {};
    const sections = existingSettings.customTicketSections || [];
    const cs = sections.find(s => s.id === sectionId);
    if (!cs) { window.showToast?.('Custom section not found', 'error'); return; }

    cs.showOnInvoice = show !== false;

    // 🔴 TASK-9280 — one owned array, no `...existingSettings` (the getDoc stays;
    // it is the base `sections` was read from, but its snapshot must not be
    // written back — see saveCustomSection / saveBillingSettings).
    await setDoc(tenantRef, {
      settings: { customTicketSections: sections }
    }, { merge: true });
    window.AppState.settings.customTicketSections = sections;

    if (!cs.showOnInvoice && (cs.fields || []).some(f => f?.type === 'number')) {
      // COD Domain fold — hiding a section with NUMBER fields is the dangerous case:
      // oilfield customers often require those quantities (TOL, pipe dims) to approve
      // the invoice. Soft warning, not a block.
      window.showToast?.(`"${cs.name}" is off the customer invoice — heads up, it has number fields some customers require to approve an invoice`, 'warning');
    } else {
      window.showToast?.(`"${cs.name}" will ${cs.showOnInvoice ? 'show on' : 'stay off'} the customer invoice`, 'success');
    }
  } catch (err) {
    console.error('[Billing] Toggle custom section invoice visibility error:', err);
    window.showToast?.('Error saving invoice visibility', 'error');
    renderInvoiceBuilderBody(); // revert the checkbox to the persisted state
  }
}

// ============================================================================
// TASK-4845 — CUSTOMER MAP (Line Information on the customer portal map).
//
// Two carrier-side surfaces; the server (publishTicketMapLayer /
// unpublishTicketMapLayer in functions/src/index.js) is what enforces all of it.
//   1. SETUP, per tenant, opt-in, default NOTHING: openCustomerMapConfig() —
//      section `showOnCustomerPortal`, per-field `showOnCustomerPortal`, and an
//      optional `portalLabel` (the label the customer reads). Only a section with
//      a GPS Location field can go on a map; only number / text / checkbox values
//      can go with it. "Show on Customer Invoice" is a DIFFERENT flag and never
//      implies this one.
//   2. PUBLISH, per ticket: previewCustomerMapLayer() shows the EXACT rows and
//      free text that will go out, the target project and whether its portal Map
//      is on; Publish is a sign-off bound to that preview by a content hash (the
//      server refuses if anything changed). Unpublish removes the layer.
// A published layer is a FROZEN snapshot: editing, renaming or deleting the
// section here never changes what a customer already sees — the setup modal says
// how many layers are out, so the carrier is never silently diverging from it.
// ============================================================================
const CM_PUBLISHABLE_TYPES = ['number', 'text', 'checkbox'];
const _cmHasGps = (cs) => Array.isArray(cs?.fields) && cs.fields.some(f => f && f.type === 'gps');
let _cmConfigSectionId = null;
let _cmPreview = null;

async function _cmCountPublishedFromSection(sectionId) {
  try {
    const snap = await getDocs(collection(db, 'tenants', getTenant(), 'projects'));
    let n = 0;
    snap.forEach(d => {
      const ls = d.data().publishedMapLayers;
      if (Array.isArray(ls)) n += ls.filter(l => l && l.sourceSectionId === sectionId).length;
    });
    return n;
  } catch (e) {
    return null; // unknown, not zero
  }
}

async function openCustomerMapConfig(sectionId) {
  const cs = _bCsDefs().find(s => s.id === sectionId);
  if (!cs) { window.showToast?.('Custom section not found', 'error'); return; }
  if (!_cmHasGps(cs)) {
    window.showToast?.(`"${cs.name}" has no GPS Location field, so it can't go on a customer map`, 'warning');
    return;
  }
  _cmConfigSectionId = sectionId;
  const gps = cs.fields.find(f => f && f.type === 'gps');
  const publishedCount = await _cmCountPublishedFromSection(sectionId);
  const fieldRows = cs.fields.filter(f => f && f !== gps).map(f => {
    if (!CM_PUBLISHABLE_TYPES.includes(f.type)) {
      return `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;color:var(--text-secondary);font-size:0.8rem;">
          <span class="material-icons" style="font-size:16px;">block</span>
          <span>${escapeHtml(f.label || '')} — never shown to customers (${escapeHtml(f.type === 'waypoint' ? 'map links stay internal' : f.type === 'file' ? 'files stay internal' : 'only one location per row')})</span>
        </div>`;
    }
    return `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;flex-wrap:wrap;">
        <label style="display:flex;align-items:center;gap:6px;margin:0;min-width:150px;flex:1;cursor:pointer;font-size:0.85rem;color:var(--text-primary);">
          <input type="checkbox" data-cm-field="${window.escAttr(f.id)}" ${f.showOnCustomerPortal === true ? 'checked' : ''} style="accent-color:var(--brand-blue);">
          ${escapeHtml(f.label || '')}
        </label>
        <input type="text" class="form-control" data-cm-label="${window.escAttr(f.id)}" maxlength="60"
          value="${window.escAttr(typeof f.portalLabel === 'string' ? f.portalLabel : '')}"
          placeholder="Customer sees: ${window.escAttr(f.label || '')}" style="flex:1;min-width:150px;font-size:0.8rem;padding:6px 8px;">
      </div>`;
  }).join('');

  document.getElementById('customer-map-config-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'customer-map-config-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;max-height:85vh;max-height:85dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">map</span>Customer map — ${escapeHtml(cs.name || 'Custom Section')}</h3>
        <button class="btn-icon" onclick="document.getElementById('customer-map-config-modal')?.remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;margin-bottom:12px;">
          <input type="checkbox" id="cm-section-on" ${cs.showOnCustomerPortal === true ? 'checked' : ''} style="margin-top:3px;accent-color:var(--brand-blue);">
          <span>
            <span style="font-weight:600;color:var(--text-primary);">Allow this section on a customer's portal map</span>
            <span style="display:block;font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">Pins come from "${escapeHtml(gps.label || 'GPS Location')}". Nothing goes to a customer until someone opens a ticket, previews the customer view and presses Publish — and the project's portal Map must be on.</span>
          </span>
        </label>
        <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin:4px 0;">Fields the customer may see (off by default)</div>
        ${fieldRows || '<div style="font-size:0.8rem;color:var(--text-secondary);">No other fields — customers will see the pin only.</div>'}
        ${publishedCount ? `<div style="margin-top:12px;padding:8px 10px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;font-size:0.8rem;color:#92400E;">
            ${publishedCount} map layer${publishedCount === 1 ? ' is' : 's are'} already published from this section. They stay exactly as published — changes here only affect the next publish.
            <a href="#" onclick="window.BillingModule.openPublishedMapLayers();return false;">Manage published maps</a>
          </div>` : ''}
      </div>
      <div class="form-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('customer-map-config-modal')?.remove()">Cancel</button>
        <button class="btn btn-primary" id="cm-save-btn" onclick="window.BillingModule.saveCustomerMapConfig()">
          <span class="material-icons" style="font-size:16px;">save</span> Save
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
}

async function saveCustomerMapConfig() {
  const sectionId = _cmConfigSectionId;
  const modal = document.getElementById('customer-map-config-modal');
  if (!sectionId || !modal) return;
  const btn = document.getElementById('cm-save-btn');
  if (btn) btn.disabled = true;
  try {
    const tenant = getTenant();
    if (!tenant) throw new Error('No tenant');
    const tenantRef = doc(db, 'tenants', tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingSettings = tenantDoc.exists() ? (tenantDoc.data().settings || {}) : {};
    const sections = existingSettings.customTicketSections || [];
    const cs = sections.find(s => s && s.id === sectionId);
    if (!cs) { window.showToast?.('Custom section not found', 'error'); return; }
    // Explicit booleans only — the server publishes on `=== true`, nothing else.
    cs.showOnCustomerPortal = modal.querySelector('#cm-section-on')?.checked === true;
    for (const f of (Array.isArray(cs.fields) ? cs.fields : [])) {
      if (!f || !CM_PUBLISHABLE_TYPES.includes(f.type)) continue;
      const box = [...modal.querySelectorAll('[data-cm-field]')].find(el => el.getAttribute('data-cm-field') === f.id);
      const lbl = [...modal.querySelectorAll('[data-cm-label]')].find(el => el.getAttribute('data-cm-label') === f.id);
      if (!box) continue;
      f.showOnCustomerPortal = box.checked === true;
      const pl = String(lbl?.value || '').trim().slice(0, 60);
      if (pl) f.portalLabel = pl; else delete f.portalLabel;
    }
    // 🔴 TASK-9280 — one owned array, no `...existingSettings` (same as toggleCustomSectionInvoice).
    await setDoc(tenantRef, { settings: { customTicketSections: sections } }, { merge: true });
    window.AppState.settings.customTicketSections = sections;
    modal.remove();
    _cmConfigSectionId = null;
    window.showToast?.(cs.showOnCustomerPortal
      ? `"${cs.name}" can now be published to a customer map from its tickets`
      : `"${cs.name}" is off the customer map`, 'success');
  } catch (err) {
    console.error('[Billing] Save customer map config error:', err);
    window.showToast?.('Error saving customer map setup', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

// Every published customer-map layer in the tenant, each with Unpublish. This is
// the path that works when the ticket-side preview cannot (the section was
// switched off or deleted, the ticket deleted, the job moved to another
// project) — without it a layer could outlive every way to take it down.
async function openPublishedMapLayers() {
  _cmModalShell(`<div class="modal-body" style="padding:24px;text-align:center;color:var(--text-secondary);"><span class="btn-spinner"></span> Loading…</div>`, 'Published customer maps');
  let rows = [];
  try {
    const snap = await getDocs(collection(db, 'tenants', getTenant(), 'projects'));
    snap.forEach(d => {
      const p = d.data();
      for (const l of (Array.isArray(p.publishedMapLayers) ? p.publishedMapLayers : [])) {
        if (!l || typeof l.layerId !== 'string') continue;
        const when = l.publishedAt?.toDate ? l.publishedAt.toDate() : null;
        rows.push({ projectId: d.id, projectName: p.name || 'Project', layerId: l.layerId, sectionName: l.sectionName || 'Line Information', workDate: l.workDate || '', when, n: Array.isArray(l.points) ? l.points.length : 0 });
      }
    });
  } catch (err) {
    _cmModalShell(`<div class="modal-body" style="padding:16px;color:#991B1B;">Couldn't load published maps. ${escapeHtml(_cmCallableMessage(err))}</div>`, 'Published customer maps');
    return;
  }
  rows.sort((a, b) => (b.when ? b.when.getTime() : 0) - (a.when ? a.when.getTime() : 0));
  const list = rows.map(r => `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;">
        <div style="flex:1;min-width:180px;">
          <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">${escapeHtml(r.projectName)}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(r.sectionName)}${r.workDate ? ` · ${escapeHtml(r.workDate)}` : ''} · ${r.n} location${r.n === 1 ? '' : 's'}${r.when ? ` · published ${escapeHtml(r.when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}` : ''}</div>
        </div>
        <button class="btn btn-secondary btn-sm" style="color:#B91C1C;" onclick="window.BillingModule.unpublishCustomerMapLayerById('${window.escJsAttr(r.projectId)}','${window.escJsAttr(r.layerId)}')">Unpublish</button>
      </div>`).join('');
  _cmModalShell(`
      <div class="modal-body" style="padding:16px;">
        <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">What each customer sees on their portal map. These are frozen snapshots — editing a ticket or a section does not change them.</div>
        ${list || '<div style="font-size:0.85rem;color:var(--text-secondary);">Nothing is published to a customer map.</div>'}
      </div>
      <div class="form-actions"><button class="btn btn-secondary" onclick="document.getElementById('customer-map-preview-modal')?.remove()">Close</button></div>`, 'Published customer maps');
}

async function unpublishCustomerMapLayerById(projectId, layerId) {
  if (!confirm('Take this line information off the customer map?')) return;
  try {
    const fn = httpsCallable(functions, 'unpublishTicketMapLayer');
    await fn({ projectId, layerId });
    window.showToast?.('Removed from the customer map', 'success');
  } catch (err) {
    window.showToast?.(`Not removed — ${_cmCallableMessage(err)}`, 'error');
  }
  openPublishedMapLayers();
}

// The per-ticket entry point, shown on the ticket detail under its custom sections.
function _cmTicketStripHtml(ticket) {
  if (!ticket || !ticket.id || !ticket.jobId || !ticket.customSectionData || typeof ticket.customSectionData !== 'object') return '';
  const eligible = _bCsDefs().filter(cs => cs.showOnCustomerPortal === true && _cmHasGps(cs) && ticket.customSectionData[cs.id] != null);
  if (!eligible.length) return '';
  return `<div class="detail-section" id="cm-ticket-strip">
      <div class="detail-section-title"><span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">map</span>Customer map</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;">
        ${eligible.map(cs => `<button class="btn btn-sm btn-secondary" onclick="window.BillingModule.previewCustomerMapLayer('${window.escJsAttr(ticket.id)}','${window.escJsAttr(cs.id)}')">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">visibility</span> Share ${escapeHtml(cs.name || 'section')} on the customer map (preview first)
          </button>`).join('')}
      </div>
    </div>`;
}

function _cmModalShell(inner, title) {
  document.getElementById('customer-map-preview-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'customer-map-preview-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:600px;max-height:85vh;max-height:85dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">map</span>${escapeHtml(title || 'Preview customer view')}</h3>
        <button class="btn-icon" onclick="document.getElementById('customer-map-preview-modal')?.remove()"><span class="material-icons">close</span></button>
      </div>
      ${inner}
    </div>`;
  document.body.appendChild(modal);
  return modal;
}

function _cmCallableMessage(err) {
  return (err && err.message ? String(err.message) : 'Something went wrong').replace(/^FirebaseError:\s*/, '');
}

async function previewCustomerMapLayer(ticketId, sectionId) {
  _cmPreview = null;
  _cmModalShell(`<div class="modal-body" style="padding:24px;text-align:center;color:var(--text-secondary);"><span class="btn-spinner"></span> Building the customer view…</div>`);
  try {
    const fn = httpsCallable(functions, 'publishTicketMapLayer');
    const res = (await fn({ ticketId, sectionId, mode: 'preview' })).data;
    _cmPreview = { ticketId, sectionId, res };
    _renderCmPreview();
  } catch (err) {
    console.warn('[Billing] customer map preview failed:', err);
    _cmModalShell(`<div class="modal-body" style="padding:16px;"><div id="cm-preview-error" style="padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;color:#991B1B;font-size:0.85rem;">${escapeHtml(_cmCallableMessage(err))}</div></div>
      <div class="form-actions"><button class="btn btn-secondary" onclick="window.BillingModule.openPublishedMapLayers()">Published customer maps</button><button class="btn btn-secondary" onclick="document.getElementById('customer-map-preview-modal')?.remove()">Close</button></div>`);
  }
}

function _renderCmPreview(errorMsg) {
  if (!_cmPreview) return;
  const r = _cmPreview.res || {};
  const pts = Array.isArray(r.points) ? r.points : [];
  const proj = r.project || {};
  const warn = (txt) => `<div style="margin-top:8px;padding:8px 10px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;font-size:0.8rem;color:#92400E;">${txt}</div>`;
  const rows = pts.map((p, i) => `
      <div style="padding:8px 10px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:6px;background:var(--bg-secondary);">
        <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);">#${i + 1} <span style="font-weight:400;color:var(--text-secondary);">${escapeHtml(p.lat.toFixed(6))}, ${escapeHtml(p.lng.toFixed(6))}</span></div>
        ${(p.fields || []).map(t => `<div style="font-size:0.85rem;color:var(--text-primary);overflow-wrap:anywhere;"><span style="color:var(--text-secondary);">${escapeHtml(t.label)}:</span> ${escapeHtml(t.value)}</div>`).join('') || '<div style="font-size:0.8rem;color:var(--text-secondary);">Pin only — no fields selected</div>'}
      </div>`).join('');
  const publishedWhen = r.existing?.publishedAt ? new Date(r.existing.publishedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  _cmModalShell(`
      <div class="modal-body" style="padding:16px;">
        <div style="font-size:0.85rem;color:var(--text-primary);">Goes to the customer portal for <strong>${escapeHtml(proj.name || 'this project')}</strong>, as <strong>${escapeHtml(r.sectionName || '')}</strong>${r.workDate ? ` (${escapeHtml(r.workDate)})` : ''}.</div>
        ${proj.portalEnabled ? '' : warn("This project's customer portal is off. Customers won't see this until the portal is turned on.")}
        ${proj.mapOn ? '' : warn("The Map is off on this project's portal. Turn on Map in the project's portal settings or customers won't see this.")}
        ${r.omitted ? warn(`${r.omitted} of ${r.total} row${r.total === 1 ? '' : 's'} ${r.omitted === 1 ? 'has' : 'have'} no usable GPS location and will NOT be on the map.${r.truncated ? ` (${r.truncated} over the ${pts.length}-pin limit.)` : ''}`) : ''}
        ${r.existing ? `<div id="cm-existing" style="margin-top:8px;padding:8px 10px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;font-size:0.8rem;color:#1E40AF;">Already published${publishedWhen ? ` ${escapeHtml(publishedWhen)}` : ''} (${r.existing.pointCount} location${r.existing.pointCount === 1 ? '' : 's'}). Publishing again replaces it.</div>` : ''}
        ${r.existing?.stale ? warn('<strong id="cm-stale">The ticket has changed since this was published.</strong> The customer still sees the old version until you republish.') : ''}
        ${(r.elsewhere || []).length ? warn(`This ticket is also published on ${r.elsewhere.map(e => `<strong>${escapeHtml(e.projectName)}</strong>`).join(', ')} — its job has moved. Publishing here takes it off there. <a href="#" onclick="window.BillingModule.openPublishedMapLayers();return false;">Manage published maps</a>`) : ''}
        <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin:14px 0 6px;">Exactly what the customer will read — ${pts.length} location${pts.length === 1 ? '' : 's'}. Check the notes.</div>
        <div id="cm-preview-rows">${rows || '<div style="font-size:0.85rem;color:var(--text-secondary);">No row on this ticket has a usable GPS location. Nothing can be published.</div>'}</div>
        ${errorMsg ? `<div id="cm-preview-error" style="margin-top:8px;padding:8px 10px;background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;color:#991B1B;font-size:0.85rem;">${escapeHtml(errorMsg)}</div>` : ''}
      </div>
      <div class="form-actions" style="flex-wrap:wrap;">
        <button class="btn btn-secondary" onclick="document.getElementById('customer-map-preview-modal')?.remove()">Cancel</button>
        ${r.existing ? `<button class="btn btn-secondary" id="cm-unpublish-btn" style="color:#B91C1C;" onclick="window.BillingModule.unpublishCustomerMapLayer()">Unpublish</button>` : ''}
        <button class="btn btn-primary" id="cm-publish-btn" ${pts.length ? '' : 'disabled'} onclick="window.BillingModule.publishCustomerMapLayer()">
          <span class="material-icons" style="font-size:16px;">publish</span> ${r.existing ? 'Republish' : 'Publish'} to customer map
        </button>
      </div>`);
}

async function publishCustomerMapLayer() {
  if (!_cmPreview) return;
  const btn = document.getElementById('cm-publish-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Publishing…'; }
  try {
    const fn = httpsCallable(functions, 'publishTicketMapLayer');
    const out = (await fn({ ticketId: _cmPreview.ticketId, sectionId: _cmPreview.sectionId, mode: 'publish', confirmHash: _cmPreview.res.contentHash })).data;
    document.getElementById('customer-map-preview-modal')?.remove();
    const name = _cmPreview.res?.project?.name || 'the project';
    _cmPreview = null;
    window.showToast?.(`Published ${out.pointCount} location${out.pointCount === 1 ? '' : 's'} to ${name}'s customer map${out.omitted ? ` (${out.omitted} without GPS left off)` : ''}${out.removedElsewhere ? ` and took the old copy off ${out.removedElsewhere} other project${out.removedElsewhere === 1 ? '' : 's'}` : ''}`, 'success');
  } catch (err) {
    console.warn('[Billing] customer map publish failed:', err);
    _renderCmPreview(_cmCallableMessage(err));
  }
}

async function unpublishCustomerMapLayer() {
  const ex = _cmPreview?.res?.existing;
  const projectId = _cmPreview?.res?.project?.id;
  if (!ex || !ex.layerId || !projectId) return;
  if (!confirm('Take this line information off the customer map?')) return;
  const btn = document.getElementById('cm-unpublish-btn');
  if (btn) btn.disabled = true;
  try {
    const fn = httpsCallable(functions, 'unpublishTicketMapLayer');
    await fn({ projectId, layerId: ex.layerId });
    document.getElementById('customer-map-preview-modal')?.remove();
    _cmPreview = null;
    window.showToast?.('Removed from the customer map', 'success');
  } catch (err) {
    console.warn('[Billing] customer map unpublish failed:', err);
    _renderCmPreview(_cmCallableMessage(err));
  }
}

async function deleteCustomSection(sectionId) {
  const cs = _bCsDefs().find(s => s.id === sectionId);
  if (!cs) return;
  // TASK-4845 — published customer-map layers are frozen snapshots; deleting the
  // section does NOT take them down. Say so rather than silently diverging.
  // Counted from the published layers themselves, not the section's current
  // flag — a section switched off can still have layers out (COD Edge lane).
  const _cmOut = _cmHasGps(cs) ? await _cmCountPublishedFromSection(sectionId) : 0;
  const _cmNote = _cmOut
    ? `\n\n${_cmOut} customer map layer${_cmOut === 1 ? ' was' : 's were'} published from this section. ${_cmOut === 1 ? 'It stays' : 'They stay'} on the customer's portal until you unpublish (Invoice Builder → Custom Sections → Published customer maps).`
    : (_cmOut === null && cs.showOnCustomerPortal === true ? '\n\nAnything already published to a customer map from this section stays published until you unpublish it (Invoice Builder → Custom Sections → Published customer maps).' : '');
  if (!confirm(`Delete custom section "${cs.name}"? This cannot be undone.${_cmNote}`)) return;

  try {
    const tenant = getTenant();
    const tenantRef = doc(db, 'tenants', tenant);
    const tenantDoc = await getDoc(tenantRef);
    const existingSettings = tenantDoc.exists() ? (tenantDoc.data().settings || {}) : {};
    const sections = (existingSettings.customTicketSections || []).filter(s => s.id !== sectionId);
    const cofSections = (existingSettings.clockOutFormSections || []).filter(s => s.key !== 'custom_' + sectionId);

    // 🔴 TASK-9280 — two owned arrays, no `...existingSettings` (the getDoc stays;
    // both arrays are filtered from it, but its snapshot must not be written
    // back — see saveCustomSection / saveBillingSettings). Arrays replace
    // wholesale under merge:true, so the section really is removed.
    await setDoc(tenantRef, {
      settings: { customTicketSections: sections, clockOutFormSections: cofSections }
    }, { merge: true });

    window.AppState.settings.customTicketSections = sections;
    window.AppState.settings.clockOutFormSections = cofSections;
    TicketBuilderState.sections = TicketBuilderState.sections.filter(s => s.key !== 'custom_' + sectionId);

    window.showToast?.(`Deleted "${cs.name}"`, 'success');
    const panel = document.querySelector('.ib-settings-panel');
    if (panel) panel.innerHTML = renderTBSettingsPanel();
    rerenderTBEditor();
  } catch (err) {
    console.error('[Billing] Delete custom section error:', err);
    window.showToast?.('Error deleting custom section', 'error');
  }
}

// ---- Right panel: Clock-Out Form preview (mockup data) ----
function renderTicketBuilderPreview() {
  const sections = TicketBuilderState.sections.filter(s => s.enabled);

  let html = `<div class="tb-preview-scroll">`;

  // Dark header card with sample data
  html += `<div class="tb-header-card">
    <div class="tb-header-times">
      <div class="tb-header-time">
        <span class="tb-header-time-label">IN</span>
        <span class="tb-header-time-value tb-time-green">6:00 AM</span>
      </div>
      <div class="tb-header-hours">
        <strong>10.50</strong>
        <span>hours</span>
      </div>
      <div class="tb-header-time">
        <span class="tb-header-time-label">OUT</span>
        <span class="tb-header-time-value tb-time-red">4:30 PM</span>
      </div>
    </div>
    <div class="tb-header-badges">
      <div class="tb-header-badge"><span class="tb-badge-label">EMPLOYEE</span><span class="tb-badge-value">John Smith</span></div>
      <div class="tb-header-badge"><span class="tb-badge-label">CUSTOMER</span><span class="tb-badge-value">Acme Energy</span></div>
    </div>
  </div>`;

  if (sections.length === 0) {
    html += `<div style="text-align:center;padding:32px 16px;color:var(--text-secondary);font-size:0.85rem;">
      <span class="material-icons" style="font-size:36px;display:block;margin-bottom:8px;opacity:0.4;">toggle_off</span>
      Enable sections on the left to see the clock-out form preview.
    </div>`;
  }

  // Render each enabled section as a collapsible card with mockup content
  sections.forEach(s => {
    const content = renderCOFPreviewSection(s.key);
    html += `<div class="review-details-card collapsed">
      <div class="review-details-header" onclick="this.parentElement.classList.toggle('collapsed')">
        <span><span class="material-icons">${escapeHtml(s.icon)}</span> ${escapeHtml(s.label)}</span>
        <span class="material-icons review-details-chevron">expand_less</span>
      </div>
      <div class="review-details-body">${content}</div>
    </div>`;
  });

  html += `</div>`;
  return html;
}

// Mockup preview sections for Clock-Out Form template
function renderCOFPreviewSection(key) {
  if (key === 'timeclock') {
    return `<div class="tb-preview-clocks">
      <div class="tb-field">
        <label>Clock In</label>
        <input type="datetime-local" disabled value="2026-03-13T06:00" style="opacity:0.6;">
      </div>
      <div class="tb-field">
        <label>Clock Out</label>
        <input type="datetime-local" disabled value="2026-03-13T16:30" style="opacity:0.6;">
      </div>
    </div>`;
  }

  if (key === 'startStopTimes') {
    return `<div class="tb-preview-clocks">
      <div class="tb-field">
        <label>Work Start</label>
        <input type="datetime-local" disabled value="2026-03-13T06:15" style="opacity:0.6;">
      </div>
      <div class="tb-field">
        <label>Work Stop</label>
        <input type="datetime-local" disabled value="2026-03-13T16:00" style="opacity:0.6;">
      </div>
      <p style="color:var(--text-secondary);font-size:12px;margin:4px 0 0;">Separate from clock times. Use when billable hours differ from clock in/out.</p>
    </div>`;
  }

  if (key === 'notes') {
    return `<div style="display:flex;flex-direction:column;gap:12px;">
      <div class="tb-field">
        <label>Shift Notes <span style="font-weight:400;color:var(--text-secondary);">(internal)</span></label>
        <textarea rows="2" disabled placeholder="What did you do today?" style="opacity:0.6;">Ran pipe from location A to B. Weather was good.</textarea>
      </div>
      <div class="tb-field">
        <label>Ticket Notes <span style="font-weight:400;color:var(--text-secondary);">(appears on invoice)</span></label>
        <textarea rows="2" disabled placeholder="Notes for customer..." style="opacity:0.6;">10.5 hrs on location, no issues.</textarea>
      </div>
    </div>`;
  }

  if (key === 'equipment') {
    return `<div style="display:flex;flex-direction:column;gap:6px;">
      <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);opacity:0.6;">
        <span style="flex:1;font-size:13px;color:var(--text-primary);">101 — Kenworth T880</span>
        <span style="font-size:13px;color:var(--text-secondary);">10.5 hrs</span>
      </div>
      <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);opacity:0.6;">
        <span style="flex:1;font-size:13px;color:var(--text-primary);">T-201 — Flatbed Trailer</span>
        <span style="font-size:13px;color:var(--text-secondary);">10.5 hrs</span>
      </div>
      <p style="color:var(--text-secondary);font-size:12px;margin:4px 0 0;">Driver selects equipment from fleet roster at clock-out.</p>
    </div>`;
  }

  if (key === 'downtime') {
    return `<div style="display:flex;flex-direction:column;gap:6px;">
      <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);opacity:0.6;">
        <span style="flex:1;font-size:13px;color:var(--text-primary);">John Smith</span>
        <span style="font-size:13px;color:var(--text-secondary);">2.0 hrs</span>
        <span style="font-size:12px;color:var(--text-secondary);">Waiting on parts</span>
      </div>
      <p style="color:var(--text-secondary);font-size:12px;margin:4px 0 0;">Driver logs downtime hours and reason.</p>
    </div>`;
  }

  if (key === 'standby') {
    return `<div style="display:flex;flex-direction:column;gap:6px;">
      <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);opacity:0.6;">
        <span style="flex:1;font-size:13px;color:var(--text-primary);">John Smith</span>
        <span style="font-size:13px;color:var(--text-secondary);">1.5 hrs</span>
        <span style="font-size:12px;color:var(--text-secondary);">Waiting on customer</span>
      </div>
      <p style="color:var(--text-secondary);font-size:12px;margin:4px 0 0;">Driver logs standby hours at clock-out.</p>
    </div>`;
  }

  if (key === 'personalVehicle') {
    const _pvOn = window.AppState?.settings?.personalVehicle?.enabled === true;
    return `<div style="display:flex;flex-direction:column;gap:6px;border:2px solid #0F766E;border-radius:8px;padding:8px;">
      <div style="font-size:13px;font-weight:700;color:#0F766E;">Personal Vehicle — paid back to the driver</div>
      <label style="display:flex;align-items:center;gap:8px;opacity:0.6;"><input type="checkbox" disabled checked> <span style="font-size:0.85rem;">I used my own vehicle</span></label>
      <div class="tb-field"><label>Miles driven (required)</label><input type="number" disabled value="42" style="opacity:0.6;"></div>
      <p style="color:var(--text-secondary);font-size:12px;margin:4px 0 0;">Paid to the employee on payroll, never billed to the customer (that is a Per Mile Price Book line). Only people enrolled on HR → Employment info see it.${_pvOn ? '' : ' <strong>Off for this company</strong> — turn it on and set your rate in Workforce → Claims.'}</p>
    </div>`;
  }

  if (key === 'photos') {
    return `<div style="text-align:center;padding:12px;opacity:0.6;">
      <span class="material-icons" style="font-size:28px;color:var(--text-secondary);">add_a_photo</span>
      <p style="color:var(--text-secondary);font-size:12px;margin:6px 0 0;">Driver can attach photos at clock-out.</p>
    </div>`;
  }

  // Custom sections
  if (key.startsWith('custom_')) {
    const csId = key.replace('custom_', '');
    const cs = _bCsDefs().find(s => s.id === csId);
    if (cs) {
      return `<div style="display:flex;flex-direction:column;gap:10px;">
        ${cs.fields.map(f => {
          if (f.type === 'checkbox') {
            return `<label style="display:flex;align-items:center;gap:8px;opacity:0.6;cursor:default;">
              <input type="checkbox" disabled> <span style="font-size:0.85rem;">${escapeHtml(f.label)}</span>
            </label>`;
          }
          if (f.type === 'waypoint') {
            return `<div class="tb-field">
              <label><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">place</span>${escapeHtml(f.label)}</label>
              <input type="text" disabled placeholder="Address, map link, or lat/long..." style="opacity:0.6;">
            </div>`;
          }
          if (f.type === 'gps') {
            return `<div class="tb-field">
              <label><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">my_location</span>${escapeHtml(f.label)}</label>
              <div style="display:flex;gap:6px;opacity:0.6;">
                <button disabled style="padding:8px 14px;border:1px solid #e2e8f0;border-radius:8px;background:#f8fafc;font-size:0.8rem;display:flex;align-items:center;gap:4px;">
                  <span class="material-icons" style="font-size:16px;">my_location</span> Grab Location
                </button>
                <input type="text" disabled placeholder="46.8772, -103.9849" style="flex:1;">
              </div>
            </div>`;
          }
          return `<div class="tb-field">
            <label>${escapeHtml(f.label)}</label>
            <input type="${f.type === 'number' ? 'number' : 'text'}" disabled placeholder="${f.type === 'number' ? '0' : '...'}" style="opacity:0.6;">
          </div>`;
        }).join('')}
        ${cs.fields.filter(f => f.lineItemBinding).map(f => `<div style="display:flex;align-items:center;gap:6px;padding:6px 8px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:6px;font-size:0.7rem;color:var(--brand-blue);margin-top:4px;">
          <span class="material-icons" style="font-size:12px;">link</span>
          ${escapeHtml(f.label)}: ${escapeHtml(f.lineItemBinding.description || f.lineItemBinding.itemCode)}
        </div>`).join('')}
      </div>`;
    }
  }

  return '';
}

// ---- Right panel render wrapper ----
function renderTicketBuilderEditor() {
  return renderTicketBuilderPreview();
}

// ---- Ticket Builder event handlers ----
function toggleTBSection(key, enabled) {
  const s = TicketBuilderState.sections.find(sec => sec.key === key);
  if (s) s.enabled = enabled;
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
  rerenderTBSettingsPanel();
}

function onTBHeaderChange(field, value) {
  const h = TicketBuilderState.header;
  const people = window.AppState?.people || [];
  const customers = BillingState.customers || [];
  const jobs = BillingState.jobs || [];

  if (field === 'employee') {
    const person = people.find(p => p.id === value);
    h.employeeId = value;
    h.employeeName = person?.name || '';
  } else if (field === 'customer') {
    const cust = customers.find(c => c.id === value);
    h.customerId = value;
    h.customerName = cust?.name || '';
  } else if (field === 'job') {
    const job = jobs.find(j => j.id === value);
    h.jobId = value;
    h.jobName = job?.title || job?.name || '';
    if (job?.location && !h.location) h.location = job.location;
    if (job?.customerId && !h.customerId) {
      const cust = customers.find(c => c.id === job.customerId);
      if (cust) { h.customerId = cust.id; h.customerName = cust.name || ''; }
    }
  } else {
    h[field] = value;
  }
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function onTBFieldChange(field, value) {
  TicketBuilderState[field] = value;
  TicketBuilderState.dirty = true;
}

async function copyTicketBuilderLink() {
  const tenant = BillingState.tenantId;
  if (!tenant) { window.showToast?.('Not logged in', 'error'); return; }
  try {
    // Check if a ticket_builder token already exists for this tenant
    const existingSnap = await getDocs(query(
      collection(db, 'accessTokens'),
      where('tenantId', '==', tenant),
      where('type', '==', 'ticket_builder')
    ));

    let tokenId;
    if (existingSnap.empty) {
      // Create a new token
      const tokenRef = await addDoc(collection(db, 'accessTokens'), {
        tenantId: tenant,
        type: 'ticket_builder',
        createdAt: Timestamp.now(),
        createdBy: window.AppState?.user?.uid || 'unknown'
      });
      tokenId = tokenRef.id;
    } else {
      tokenId = existingSnap.docs[0].id;
    }

    const url = `${window.location.origin}?ticket=${tokenId}`;
    await navigator.clipboard.writeText(url);
    window.showToast?.('Ticket Builder link copied!', 'success');
  } catch (err) {
    console.error('[Billing] Error creating ticket builder link:', err);
    window.showToast?.('Error creating link', 'error');
  }
}

function toggleTBSmartAssist(enabled) {
  TicketBuilderState.smartAssistEnabled = enabled;
  if (enabled && window.SmartAssistant) {
    window.SmartAssistant.register('tb-shift-notes', () => [], 'Ticket Builder', { formatOnly: true });
    window.SmartAssistant.register('tb-ticket-notes', () => [], 'Ticket Builder', { formatOnly: true });
  } else if (window.SmartAssistant) {
    window.SmartAssistant.unregister('tb-shift-notes');
    window.SmartAssistant.unregister('tb-ticket-notes');
  }
}

function addTBEquip() {
  TicketBuilderState.equipment.push({ _id: tbNextId(), assetId: '', assetName: '', assetType: '', fleetNumber: '', hours: 0, rate: 0 });
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function removeTBEquip(idx) {
  TicketBuilderState.equipment.splice(idx, 1);
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function updateTBEquip(idx, assetId) {
  const trucks = window.AppState?.trucks || [];
  const trailers = window.AppState?.trailers || [];
  const assets = window.AppState?.assets || [];
  const eq = TicketBuilderState.equipment[idx];
  if (!eq) return;
  const truck = trucks.find(t => t.id === assetId);
  const trailer = !truck && trailers.find(t => t.id === assetId);
  const asset = !truck && !trailer && assets.find(a => a.id === assetId);
  if (truck) { eq.assetId = truck.id; eq.assetName = `${truck.unit || ''} ${truck.nickname || truck.make || ''}`.trim(); eq.assetType = 'truck'; eq.fleetNumber = truck.unit || ''; }
  else if (trailer) { eq.assetId = trailer.id; eq.assetName = `${trailer.unit || ''} ${trailer.nickname || trailer.make || ''}`.trim(); eq.assetType = 'trailer'; eq.fleetNumber = trailer.unit || ''; }
  else if (asset) { eq.assetId = asset.id; eq.assetName = asset.name || asset.id; eq.assetType = 'asset'; eq.fleetNumber = ''; }
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function updateTBEquipField(idx, field, value) {
  const eq = TicketBuilderState.equipment[idx];
  if (eq) eq[field] = value;
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function addTBTimeEntry(type) {
  const list = type === 'downtime' ? TicketBuilderState.downtime : TicketBuilderState.standby;
  list.push({ _id: tbNextId(), personId: '', personName: '', hours: 0, rate: 0, notes: '' });
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function removeTBTimeEntry(type, idx) {
  const list = type === 'downtime' ? TicketBuilderState.downtime : TicketBuilderState.standby;
  list.splice(idx, 1);
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function updateTBTimeEntry(type, idx, field, value) {
  const list = type === 'downtime' ? TicketBuilderState.downtime : TicketBuilderState.standby;
  const entry = list[idx];
  if (!entry) return;
  if (field === 'person') {
    const people = window.AppState?.people || [];
    const p = people.find(pp => pp.id === value);
    entry.personId = value;
    entry.personName = p?.name || '';
  } else {
    entry[field] = value;
  }
  TicketBuilderState.dirty = true;
  rerenderTBEditor();
}

function focusTBTimes() {
  const s = TicketBuilderState.sections.find(sec => sec.key === 'timeclock');
  if (s && !s.enabled) { s.enabled = true; rerenderTBSettingsPanel(); }
  rerenderTBEditor();
  setTimeout(() => {
    const el = document.getElementById('tb-clock-in');
    if (el) el.focus();
  }, 100);
}

function rerenderTBEditor() {
  const editor = document.getElementById('tb-editor');
  if (!editor) return;
  editor.innerHTML = renderTicketBuilderPreview();
}

function rerenderTBSettingsPanel() {
  if (InvoiceBuilderState.previewTab !== 'builder') return;
  const panel = document.querySelector('.ib-settings-panel');
  if (panel) {
    panel.innerHTML = renderTBSettingsPanel();
    setupTBSectionDrag();
  }
}

function setupTBSectionDrag() {
  const container = document.getElementById('tb-section-list');
  if (!container) return;
  let dragEl = null;
  container.addEventListener('dragstart', e => {
    dragEl = e.target.closest('[data-tb-section]');
    if (!dragEl) return;
    dragEl.style.opacity = '0.4';
    e.dataTransfer.effectAllowed = 'move';
  });
  container.addEventListener('dragend', () => {
    if (dragEl) dragEl.style.opacity = '1';
    dragEl = null;
    container.querySelectorAll('[data-tb-section]').forEach(el => { el.style.borderTop = ''; el.style.borderBottom = ''; });
  });
  container.addEventListener('dragover', e => {
    e.preventDefault();
    const target = e.target.closest('[data-tb-section]');
    if (!target || target === dragEl) return;
    container.querySelectorAll('[data-tb-section]').forEach(el => { el.style.borderTop = ''; el.style.borderBottom = ''; });
    const rect = target.getBoundingClientRect();
    if (e.clientY < rect.top + rect.height / 2) target.style.borderTop = '2px solid var(--brand-blue)';
    else target.style.borderBottom = '2px solid var(--brand-blue)';
  });
  container.addEventListener('drop', e => {
    e.preventDefault();
    const target = e.target.closest('[data-tb-section]');
    if (!target || !dragEl || target === dragEl) return;
    const fromKey = dragEl.dataset.tbSection;
    const toKey = target.dataset.tbSection;
    const arr = TicketBuilderState.sections;
    const fromIdx = arr.findIndex(s => s.key === fromKey);
    const toIdx = arr.findIndex(s => s.key === toKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = arr.splice(fromIdx, 1);
    const rect = target.getBoundingClientRect();
    if (e.clientY < rect.top + rect.height / 2) arr.splice(toIdx, 0, moved);
    else arr.splice(toIdx + (fromIdx < toIdx ? 0 : 1), 0, moved);
    rerenderTBSettingsPanel();
    rerenderTBEditor();
  });
}

// Save Clock-Out Form template to tenant settings
async function saveCOFTemplate() {
  const btn = document.getElementById('cof-save-btn');
  const origHTML = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenant = getTenant();
    if (!tenant) { showToast('No tenant', 'error'); return; }

    const sectionConfig = TicketBuilderState.sections.map(s => ({
      key: s.key,
      enabled: s.enabled
    }));

    const tenantRef = doc(db, 'tenants', tenant);

    // 🔴 TASK-9280 — one owned array, and the getDoc that fed the old
    // `...existingSettings` spread is gone: this function builds sectionConfig
    // entirely from TicketBuilderState, so it never needed the prior state.
    // Reading it only created the stale snapshot that the spread then wrote back
    // over the SMS consent record (settings.smsInbox.optedOutOfAi) and every
    // other key this template does not own. `merge: true` preserves omitted keys
    // — that is what protects them, not the spread. Verified against real
    // Firestore 2026-09-22. See saveBillingSettings for the long note.
    await setDoc(tenantRef, {
      settings: {
        clockOutFormSections: sectionConfig
      }
    }, { merge: true });

    // Update local AppState
    if (window.AppState?.settings) {
      window.AppState.settings.clockOutFormSections = sectionConfig;
    }

    showToast('Clock-out form template saved', 'success');
    TicketBuilderState.dirty = false;
  } catch (err) {
    console.error('[COF] Save error:', err);
    showToast('Failed to save template', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHTML; }
  }
}

// Legacy alias
async function saveTBTicket() { return saveCOFTemplate(); }

function removeIBSection(key) {
  const order = InvoiceBuilderState.localSettings.invoiceSectionOrder;
  const updated = order.filter(k => k !== key);
  if (updated.length === 0) { window.showToast?.('Must keep at least one section', 'error'); return; }
  InvoiceBuilderState.localSettings.invoiceSectionOrder = updated;
  renderIBSectionOrderInBuilder();
  schedulePreviewUpdate();
}

function addIBSection(key) {
  const order = InvoiceBuilderState.localSettings.invoiceSectionOrder;
  if (order.includes(key)) return;
  order.push(key);
  renderIBSectionOrderInBuilder();
  schedulePreviewUpdate();
}

// Event handlers
function onIBSettingChange() {
  readIBSettingsFromUI();
  schedulePreviewUpdate();
}

function readEmailTemplateFromUI() {
  const replyTo = document.getElementById('ib-email-reply-to');
  const subject = document.getElementById('ib-email-subject');
  const body = document.getElementById('ib-email-body');
  const payment = document.getElementById('ib-email-payment');
  if (replyTo) InvoiceBuilderState.localEmailTemplate.replyTo = replyTo.value.trim();
  if (subject) InvoiceBuilderState.localEmailTemplate.subject = subject.value || 'Invoice {{invoiceNumber}} from {{companyName}}';
  if (body) InvoiceBuilderState.localEmailTemplate.body = body.value || '';
  if (payment) InvoiceBuilderState.localEmailTemplate.paymentInstructions = payment.value || '';
}

function readIBSettingsFromUI() {
  // Email template (if on email tab)
  readEmailTemplateFromUI();

  // TASK-865 — employee names on tickets/invoices
  const empNamesEl = document.getElementById('ib-show-emp-names');
  if (empNamesEl) InvoiceBuilderState.localSettings.showEmployeeNamesOnTickets = empNamesEl.checked;
  // TASK-10185 — dispatch job notes on tickets
  const jobNotesEl = document.getElementById('ib-show-job-notes');
  if (jobNotesEl) InvoiceBuilderState.localSettings.showJobNotesOnTickets = jobNotesEl.checked;

  // TASK-9390 / TASK-10203 — header switches (job type, project; ticket / invoice
  // independent). A missing checkbox keeps the stored leaf, never resets it.
  const hf = _ibHeaderFields(InvoiceBuilderState.localSettings.headerFields);
  for (const [f, k] of [['jobType', 'jobtype'], ['project', 'project']]) {
    for (const s of ['ticket', 'invoice']) {
      const el = document.getElementById(`ib-hdr-${k}-${s}`);
      if (el) hf[f][s] = el.checked;
    }
  }
  InvoiceBuilderState.localSettings.headerFields = hf;

  // Per-asset notes
  const panEl = document.getElementById('ib-per-asset-notes');
  if (panEl) {
    InvoiceBuilderState.localSettings.perAssetNotes = panEl.checked;
    const detail = document.getElementById('ib-pan-detail');
    if (detail) detail.style.display = panEl.checked ? 'flex' : 'none';
  }
  ['truck', 'trailer', 'asset'].forEach(t => {
    const el = document.getElementById('ib-pan-' + t);
    if (el) InvoiceBuilderState.localSettings.perAssetNotesTypes[t] = el.checked;
  });

  // Section order from DOM
  const activeList = document.getElementById('ib-section-active-list');
  if (activeList) {
    InvoiceBuilderState.localSettings.invoiceSectionOrder = [...activeList.querySelectorAll('[data-section-key]')].map(el => el.dataset.sectionKey);
  }

  // Column config
  buildIBColumnConfigFromUI();

  // Field ticket settings
  const ftsMap = {
    'ib-fts-driver-sig': 'requireDriverSignature',
    'ib-fts-customer-sig': 'requireCustomerSignature',
    'ib-fts-stamp': 'requireStamp',
    'ib-fts-email': 'requireEmailCopy',
    'ib-fts-show-costs': 'showCostsOnDriverTicket'
  };
  Object.entries(ftsMap).forEach(([elId, key]) => {
    const el = document.getElementById(elId);
    if (el) InvoiceBuilderState.localFieldTicketSettings[key] = el.checked;
  });
  // TASK-4714 — single-contract mode only; the input does not exist in mass mode
  // on purpose (one address across every customer would cross-send tickets).
  const _ftEmailEl = document.getElementById('ib-fts-email-to');
  if (_ftEmailEl) InvoiceBuilderState.localFieldTicketSettings.defaultEmailRecipient = _ftEmailEl.value.trim();
  // TASK-4683 A3 — the approval-page signature setting.
  const _ftApprovalSigEl = document.getElementById('ib-fts-approval-sig');
  if (_ftApprovalSigEl) InvoiceBuilderState.localFieldTicketSettings.customerApprovalSignature = _approvalSigMode(_ftApprovalSigEl.value);

  // Company header
  const ch = InvoiceBuilderState.localSettings.companyHeader || {};
  const chName = document.getElementById('ib-ch-name');
  if (chName) ch.showName = chName.checked;
  const chPhone = document.getElementById('ib-ch-phone');
  if (chPhone) ch.showPhone = chPhone.checked;
  const chEmail = document.getElementById('ib-ch-email');
  if (chEmail) ch.showEmail = chEmail.checked;
  const chAddr = document.getElementById('ib-ch-address');
  if (chAddr) ch.showAddress = chAddr.checked;
  const chPhoneLabel = document.getElementById('ib-ch-phone-label');
  if (chPhoneLabel) ch.phoneLabel = chPhoneLabel.value;
  const chEmailLabel = document.getElementById('ib-ch-email-label');
  if (chEmailLabel) ch.emailLabel = chEmailLabel.value;
  const chAddrLabel = document.getElementById('ib-ch-addr-label');
  if (chAddrLabel) ch.addressLabel = chAddrLabel.value;
  InvoiceBuilderState.localSettings.companyHeader = ch;

  // Slip template (only present when slip tab is active)
  const stRT = document.getElementById('ib-slip-running-total');
  const stGps = document.getElementById('ib-slip-gps');
  const stPh = document.getElementById('ib-slip-photos');
  const stLI = document.getElementById('ib-slip-line-items');
  const stFooter = document.getElementById('ib-slip-footer');
  if (stRT || stGps || stPh || stLI || stFooter) {
    const st = InvoiceBuilderState.localSettings.slipTemplate || {};
    if (stRT) st.showRunningTotal = stRT.checked;
    if (stGps) st.showGpsCoords = stGps.checked;
    if (stPh) st.showPhotos = stPh.checked;
    if (stLI) st.showLineItems = stLI.checked;
    if (stFooter) st.customFooterText = stFooter.value;
    InvoiceBuilderState.localSettings.slipTemplate = st;
  }
}

function buildIBColumnConfigFromUI() {
  const checkboxes = document.querySelectorAll('[data-ib-ic-section]');
  const ic = {};
  checkboxes.forEach(cb => {
    const sec = cb.dataset.ibIcSection;
    const col = cb.dataset.ibIcCol;
    if (!ic[sec]) ic[sec] = {};
    ic[sec][col] = cb.checked;
  });
  if (Object.keys(ic).length > 0) {
    InvoiceBuilderState.localInvoiceColumns = { ...InvoiceBuilderState.localInvoiceColumns, ...ic };
  }
  // Sync tenant-level showClockTimesOnTickets from column checkboxes
  // If ANY section has showTimes checked, the tenant setting should be true
  //
  // ⚠️ PRE-EXISTING, NOT FIXED BY TASK-9280, FLAGGED HERE SO IT IS NOT LOST —
  // this assignment is NOT guarded by the `Object.keys(ic).length > 0` check
  // three lines above, and it should be. When the Templates panel is on the
  // Email or Slip tab no `[data-ib-ic-section]` checkbox is rendered, so `ic` is
  // empty, `anyTimesChecked` is false, and saveInvoiceBuilder then PERSISTS
  // `settings.showClockTimesOnTickets: false` — turning clock times off on every
  // ticket because someone saved from a tab that never showed the control.
  //   It is one half of a pair, which is why it is not fixed on a lost-write
  // ticket: readIBSettingsFromUI has the same shape for `companyHeader`'s label
  // selects (they are only read `if (el)`, so an unrendered tab keeps the
  // OPEN-TIME label and saveInvoiceBuilder writes it back over a label Company
  // Info changed in between). Both are stale-local-state overwrites from an
  // unrendered panel, both were found by COD on the TASK-9280 diff, and they
  // want one task + COD together — fixing only this one leaves the other.
  const anyTimesChecked = Object.values(ic).some(sec => sec.showTimes === true);
  InvoiceBuilderState.localSettings.showClockTimesOnTickets = anyTimesChecked;
}

function setIBMode(mode) {
  InvoiceBuilderState.mode = mode;
  InvoiceBuilderState.selectedCustomerId = null;
  InvoiceBuilderState.selectedContractId = null;
  renderInvoiceBuilderBody();
}

async function onIBCustomerChange(customerId) {
  InvoiceBuilderState.selectedCustomerId = customerId || null;
  InvoiceBuilderState.selectedContractId = null;

  if (customerId && !InvoiceBuilderState._contractCache[customerId]) {
    const tenant = getTenant();
    try {
      const snap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'));
      InvoiceBuilderState._contractCache[customerId] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error('[IB] Error loading contracts:', err);
      InvoiceBuilderState._contractCache[customerId] = [];
    }
  }
  renderInvoiceBuilderBody();
}

function onIBContractChange(contractId) {
  InvoiceBuilderState.selectedContractId = contractId || null;
  // TASK-4714 — 🔴 CLEAR THE RECIPIENT ON EVERY CONTRACT CHANGE, BEFORE the
  // reload, because the reload below is guarded by `if (contract)`. On a
  // `_contractCache` miss (or a contract id not in it) that guard falls through
  // and `localFieldTicketSettings` keeps whatever the PREVIOUS selection left in
  // it — and the single-contract save writes that map straight onto the newly
  // selected contract. With a recipient now living in this state, that is one
  // customer's AP address landing on another customer's contract, which is the
  // driver allowlist for their field tickets. Every other key in this map is a
  // harmless boolean; this one addresses mail, so it is cleared unconditionally
  // and only ever re-set from the contract actually being edited.
  if (InvoiceBuilderState.localFieldTicketSettings) {
    InvoiceBuilderState.localFieldTicketSettings.defaultEmailRecipient = '';
  }
  if (contractId) {
    const contracts = InvoiceBuilderState._contractCache[InvoiceBuilderState.selectedCustomerId] || [];
    const contract = contracts.find(c => c.id === contractId);
    if (contract) {
      // Load contract-specific settings into local state — this contract's grid
      // → legacy equipment → tenant template → defaults, via the ONE helper.
      // (COD round 2: this used to skip the tenant template, so a customer who
      // relied on it was shown — and on Save re-baked with — hardcoded defaults.)
      InvoiceBuilderState.localInvoiceColumns = resolveInvoiceColumnsGrid(contract.invoiceColumns);
      const fts = contract.fieldTicketSettings || {};
      InvoiceBuilderState.localFieldTicketSettings = {
        requireDriverSignature: fts.requireDriverSignature || false,
        requireCustomerSignature: fts.requireCustomerSignature || false,
        requireStamp: fts.requireStamp || false,
        requireEmailCopy: fts.requireEmailCopy || false,
        // TASK-4714 — carried through local state so the SAVE can write it back.
        // It was absent here, and the save writes `fieldTicketSettings` as a WHOLE
        // MAP — a nested object in updateDoc replaces the map rather than merging
        // — so every save from this panel silently DELETED whatever recipient the
        // Price Book contract editor had set. Mass save did it to every contract
        // of every customer at once. That is how the fleet ended up with 165
        // contracts, zero recipients and two `requireEmailCopy` flags on.
        defaultEmailRecipient: fts.defaultEmailRecipient || '',
        showCostsOnDriverTicket: fts.showCostsOnDriverTicket !== false,
        // TASK-4683 A3 — same trap as the recipient above: the single-contract
        // save writes this map WHOLE, so a key not loaded here is a key deleted.
        customerApprovalSignature: _approvalSigMode(fts.customerApprovalSignature)
      };
    }
  }
  renderInvoiceBuilderBody();
}

function promptEnableBuildTicket() {
  // Show a helpful prompt when user clicks grayed-out Clock-Out Form tab
  const modal = document.getElementById('modal-billing-settings');
  if (modal) {
    // Open billing settings and highlight the clock-out mode section
    openBillingSettings();
    setTimeout(() => {
      const ticketLabel = document.getElementById('clockout-mode-ticket-label');
      if (ticketLabel) {
        ticketLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ticketLabel.style.boxShadow = '0 0 0 3px var(--brand-blue)';
        setTimeout(() => { ticketLabel.style.boxShadow = ''; }, 2500);
      }
    }, 300);
    window.showToast?.('Select "Build Ticket" mode to customize the Clock-Out Form', 'info');
  }
}

function setIBPreviewTab(tab) {
  const wasBuilder = InvoiceBuilderState.previewTab === 'builder';
  const wasEmail = InvoiceBuilderState.previewTab === 'email';
  const isBuilder = tab === 'builder';
  const isEmail = tab === 'email';

  // Read email template from UI before switching away
  if (wasEmail) {
    readEmailTemplateFromUI();
  }

  InvoiceBuilderState.previewTab = tab;

  // Auto-enable TB smart assist for founder on first switch to builder
  if (isBuilder && !wasBuilder && !TicketBuilderState._saInitialized) {
    TicketBuilderState._saInitialized = true;
    if (isFounder()) {
      TicketBuilderState.smartAssistEnabled = true;
      toggleTBSmartAssist(true);
    }
  }

  // If switching to/from builder or email, re-render entire body (left panel changes)
  if (wasBuilder !== isBuilder || wasEmail !== isEmail) {
    renderInvoiceBuilderBody();
  } else {
    // Just swap the right panel
    const panel = document.querySelector('.ib-preview-panel');
    if (panel) {
      panel.innerHTML = renderIBPreviewPanel();
      if (tab !== 'builder' && tab !== 'email') updateIBPreview();
    }
  }
}

function schedulePreviewUpdate() {
  if (InvoiceBuilderState._debounceTimer) clearTimeout(InvoiceBuilderState._debounceTimer);
  InvoiceBuilderState._debounceTimer = setTimeout(() => {
    updateIBPreview();
    // Also render section order UI (needs to happen after body render)
    renderIBSectionOrderInBuilder();
  }, 300);
}

function updateIBPreview() {
  if (InvoiceBuilderState.previewTab === 'builder') return;
  if (InvoiceBuilderState.previewTab === 'email') {
    const previewEl = document.getElementById('ib-email-preview');
    if (previewEl) previewEl.innerHTML = renderEmailPreviewPanel();
    return;
  }
  const iframe = document.getElementById('ib-preview-iframe');
  if (!iframe) return;

  const sample = buildIBSampleData();
  let previewHTML;
  if (InvoiceBuilderState.previewTab === 'invoice') {
    previewHTML = buildIBInvoicePreviewHTML(sample);
  } else if (InvoiceBuilderState.previewTab === 'slip') {
    previewHTML = buildIBSlipPreviewHTML(sample);
  } else {
    previewHTML = buildIBTicketPreviewHTML(sample);
  }
  iframe.srcdoc = previewHTML;
}

// Builds a sample Ship Slip preview using the slipHelpers buildSlipHTML helper
// with mock data. Used by the Templates page Ship Slip tab.
function buildIBSlipPreviewHTML(sample) {
  const sampleSlip = {
    slipNumber: 'SS-1024',
    leaseName: 'Smith Federal',
    wellName: '#4H',
    wellApiNumber: '33-053-12345',
    materialType: 'Hot Oil',
    tankInUnit: 'bbls',
    tankInValue: 145,
    tankOutUnit: 'bbls',
    tankOutValue: 95,
    bblsTransferred: 50,
    customerRepName: 'J. Stevens',
    notes: 'Tank top filled to gauge mark; customer rep on site at 2:14 PM.',
    photoUrls: [],
    lineItemRefs: [
      { description: 'Hot Oil Service — Smith #4H', qty: '3.50', unit: 'hrs' },
      { description: 'Mileage', qty: '42', unit: 'mi' }
    ],
    driverName: 'Mike Andersen',
    truckName: 'Truck 47',
    gpsCoords: { lat: 47.78342, lng: -103.62541, accuracy: 8 },
    status: 'open',
    createdAt: new Date()
  };
  return slipBuildHTML({
    slip: sampleSlip,
    ticket: { invoiceNumber: sample?.ticket?.invoiceNumber || 'INV-202604-001' },
    customer: { name: sample?.companyName ? 'Acme Energy LLC' : 'Acme Energy LLC' },
    company: {
      name: sample?.companyName || '',
      address: sample?.companyAddress || '',
      phone: sample?.companyPhone || '',
      email: sample?.companyEmail || ''
    },
    runningTotal: 230,
    includeRunningTotal: true,
    template: InvoiceBuilderState.localSettings.slipTemplate || null,
  });
}

function buildIBSampleData() {
  const data = JSON.parse(JSON.stringify(INVOICE_BUILDER_SAMPLE_DATA));
  // Re-hydrate dates (JSON stringify kills them)
  data.ticket.date = new Date();
  data.ticket.workDate = (() => { const _d = new Date(); return `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`; })();
  data.lines.forEach(l => {
    if (l.fromTime) l.fromTime = new Date(l.fromTime);
    if (l.toTime) l.toTime = new Date(l.toTime);
  });
  // Use real tenant info resolved through IB company header prefs
  const ibHeader = InvoiceBuilderState.localSettings.companyHeader;
  const resolved = resolveCompanyInfo(ibHeader);
  if (window.AppState?.tenantData) {
    data.companyName = resolved.companyName || resolved.rawCompanyName;
    data.companyAddress = resolved.companyAddress;
    data.companyPhone = resolved.companyPhone;
    data.companyEmail = resolved.companyEmail;
    data.logoUrl = resolved.logoUrl || '';
  } else {
    data.companyName = 'Your Company Name';
    data.companyAddress = '';
    data.companyPhone = '';
    data.companyEmail = '';
    data.logoUrl = '';
  }
  return data;
}

function buildIBInvoicePreviewHTML(sample) {
  const { ticket, lines, customer, job, companyName, companyAddress, companyPhone, companyEmail, logoUrl } = sample;
  const ls = InvoiceBuilderState.localSettings;
  const ic = InvoiceBuilderState.localInvoiceColumns;

  // TASK-865 — names+Role-column display flag (was isFounder). Read the IB
  // panel's PENDING local value (like the other IB toggles) so the preview
  // reflects the checkbox before Save; falls back to the loaded setting.
  const _founder = ls.showEmployeeNamesOnTickets === true;

  const empLines = lines.filter(l => l.type === 'EMPLOYEE');
  const equipLines = lines.filter(l => l.type === 'EQUIPMENT' && l.includeOnInvoice !== false);
  const vehicleLines = equipLines.filter(l => l.assetType === 'truck');
  const trailerLines = equipLines.filter(l => l.assetType === 'trailer');
  const assetLines = equipLines.filter(l => !l.assetType || l.assetType === 'asset');
  const downLines = lines.filter(l => l.type === 'DOWNTIME');
  const standbyLines = lines.filter(l => l.type === 'STANDBY');
  const permitLines = lines.filter(l => l.type === 'PERMIT');
  const fieldItemLines = lines.filter(l => l.type === 'FIELD_ITEM');
  const miscLines = lines.filter(l => l.type === 'MISC');
  const grandTotal = lines.reduce((s, l) => s + (l.type === 'DOWNTIME' ? -1 : 1) * (l.lineAmount || 0), 0);

  const workDate = new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // Build section HTML map
  const sectionMap = {};

  // Notes section
  if (ls.perAssetNotes) {
    const eqWithNotes = equipLines.filter(l => l.notes?.trim() && ls.perAssetNotesTypes[l.assetType] !== false);
    let notesHtml = eqWithNotes.map(l => `<div style="margin-bottom:8px;padding-left:10px;border-left:3px solid #3B82F6;">
      <div style="font-weight:600;font-size:13px;color:#0F172A;">${invoiceEsc(l.assetName || 'Equipment')}</div>
      <div style="font-size:12px;color:#334155;white-space:pre-wrap;">${invoiceEsc(l.notes)}</div>
    </div>`).join('');
    if (ticket.workPerformed) {
      if (notesHtml) notesHtml += '<div style="margin-top:10px;font-size:11px;font-weight:600;color:#64748B;text-transform:uppercase;">General Notes</div>';
      notesHtml += `<div style="color:#334155;font-size:13px;white-space:pre-wrap;">${invoiceEsc(ticket.workPerformed)}</div>`;
    }
    if (notesHtml) sectionMap.notes = `<div style="margin-bottom:24px;"><div class="section-title">Work Performed</div>${notesHtml}</div>`;
  } else if (ticket.workPerformed) {
    sectionMap.notes = `<div style="margin-bottom:24px;"><div class="section-title">Work Performed</div><div style="color:#334155;font-size:13px;white-space:pre-wrap;">${invoiceEsc(ticket.workPerformed)}</div></div>`;
  }

  // Labor
  if (empLines.length > 0) {
    const cc = ic.labor || {};
    const _sR = cc.showRate !== false;
    const _sT = cc.showTotal !== false;
    const _sRole = cc.showRole !== false;
    const _sLI = cc.showLineItem === true;
    const _sTimes = cc.showTimes !== false;
    const _sHrs = cc.showHours !== false;
    if (_sR || _sT || _sRole || _sLI || _sTimes || _sHrs) {
      const sub = empLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
      let colCount = 1; // name
      if (_founder && _sRole) colCount++;
      if (_sTimes) colCount += 2;
      if (_sLI) colCount++;
      if (_sHrs) colCount++;
      if (_sR) colCount++;

      let h = `<div class="section-title">Labor</div><table><thead><tr>
        <th>${_founder ? 'Name' : 'Role'}</th>${_founder && _sRole ? '<th>Role</th>' : ''}${_sTimes ? '<th>Clock In</th><th>Clock Out</th>' : ''}${_sLI ? '<th>Line Item</th>' : ''}${_sHrs ? '<th class="right">Hours</th>' : ''}${_sR ? '<th class="right">Rate</th>' : ''}${_sT ? '<th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
      empLines.forEach(l => {
        const name = _founder ? (l.personName || '-') : (l.role || 'Employee');
        h += `<tr><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(name)}</td>
          ${_founder && _sRole ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.role || '-')}</td>` : ''}
          ${_sTimes ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineFromHtml(l)}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineToHtml(l)}</td>` : ''}
          ${_sLI ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.lineItemDescription || '')}</td>` : ''}
          ${_sHrs ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${(l.hours || 0).toFixed(2)}</td>` : ''}
          ${_sR ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
          ${_sT ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
        </tr>`;
      });
      if (_sT) h += `<tr class="subtotal-row"><td colspan="${colCount}" style="text-align:right;">Labor Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(sub)}</td></tr>`;
      h += `</tbody></table>`;
      sectionMap.labor = h;
    }
  }

  // Equipment sub-sections (vehicles, trailers, assets)
  const buildEquipSub = (subLines, title, sectionKey) => {
    if (subLines.length === 0) return '';
    const cc = ic[sectionKey] || {};
    const _sR = cc.showRate !== false;
    const _sT = cc.showTotal !== false;
    const _sLI = cc.showLineItem === true;
    const _sTimes = cc.showTimes !== false;
    const _sHrs = cc.showHours !== false;
    if (!_sR && !_sT && !_sLI && !_sTimes && !_sHrs) return '';
    const sub = subLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let cols = 2;
    if (_sLI) cols++;
    if (_sTimes) cols += 2;
    if (_sHrs) cols++;
    if (_sR) cols++;

    let h = `<div class="section-title">${invoiceEsc(title)}</div><table><thead><tr>
      <th>Asset</th><th>Fleet #</th>${_sLI ? '<th>Line Item</th>' : ''}${_sTimes ? '<th>From</th><th>To</th>' : ''}${_sHrs ? '<th class="right">QTY</th>' : ''}${_sR ? '<th class="right">Rate</th>' : ''}${_sT ? '<th class="right">Amount</th>' : ''}
    </tr></thead><tbody>`;
    subLines.forEach(l => {
      const _pu = !isTimeBasedUnit(l.rateUnit);
      h += `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.assetName || '-')}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.fleetNumber || '-')}</td>
        ${_sLI ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.lineItemDescription || '')}</td>` : ''}
        ${_sTimes ? (_pu ? '<td style="color:#94A3B8;">—</td><td style="color:#94A3B8;">—</td>' : `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineFromHtml(l)}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineToHtml(l)}</td>`) : ''}
        ${_sHrs ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${_pu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
        ${_sR ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
        ${_sT ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      </tr>`;
    });
    if (_sT) h += `<tr class="subtotal-row"><td colspan="${cols}" style="text-align:right;">${invoiceEsc(title)} Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(sub)}</td></tr>`;
    h += `</tbody></table>`;
    return h;
  };

  const equipHtml = buildEquipSub(vehicleLines, 'Vehicles', 'vehicles') + buildEquipSub(trailerLines, 'Trailers', 'trailers') + buildEquipSub(assetLines, 'Assets', 'assets');
  if (equipHtml) sectionMap.equipment = equipHtml;

  // Downtime
  if (downLines.length > 0) {
    const cc = ic.downtime || {};
    const _sR = cc.showRate !== false;
    const _sT = cc.showTotal !== false;
    const _sTimes = cc.showTimes !== false;
    const _sHrs = cc.showHours !== false;
    const sub = downLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let cols = 1;
    if (_sTimes) cols += 2;
    if (_sHrs) cols++;
    if (_sR) cols++;

    let h = `<div class="section-title">Downtime Deduction</div><table><thead><tr>
      <th>Person</th>${_sTimes ? '<th>From</th><th>To</th>' : ''}${_sHrs ? '<th class="right">Hours</th>' : ''}${_sR ? '<th class="right">Rate</th>' : ''}${_sT ? '<th class="right">Deduction</th>' : ''}<th>Notes</th>
    </tr></thead><tbody>`;
    downLines.forEach(l => {
      h += `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.personName || '-')}</td>
        ${_sTimes ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineFromHtml(l)}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineToHtml(l)}</td>` : ''}
        ${_sHrs ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${(l.hours || 0).toFixed(2)}</td>` : ''}
        ${_sR ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
        ${_sT ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;color:#DC2626;">-${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${invoiceEsc(l.notes || '')}</td>
      </tr>`;
    });
    if (_sT) h += `<tr class="subtotal-row"><td colspan="${cols}" style="text-align:right;">Downtime Deduction</td><td style="text-align:right;color:#DC2626;">-${invoiceFmtMoney(sub)}</td><td></td></tr>`;
    h += `</tbody></table>`;
    sectionMap.downtime = h;
  }

  // Standby
  if (standbyLines.length > 0) {
    const cc = ic.standby || {};
    const _sR = cc.showRate !== false;
    const _sT = cc.showTotal !== false;
    const _sTimes = cc.showTimes !== false;
    const _sHrs = cc.showHours !== false;
    const sub = standbyLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let cols = 1;
    if (_sTimes) cols += 2;
    if (_sHrs) cols++;
    if (_sR) cols++;

    let h = `<div class="section-title">Standby</div><table><thead><tr>
      <th>Person</th>${_sTimes ? '<th>From</th><th>To</th>' : ''}${_sHrs ? '<th class="right">Hours</th>' : ''}${_sR ? '<th class="right">Rate</th>' : ''}${_sT ? '<th class="right">Amount</th>' : ''}<th>Notes</th>
    </tr></thead><tbody>`;
    standbyLines.forEach(l => {
      h += `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.personName || '-')}</td>
        ${_sTimes ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineFromHtml(l)}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${lineToHtml(l)}</td>` : ''}
        ${_sHrs ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${(l.hours || 0).toFixed(2)}</td>` : ''}
        ${_sR ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;">${invoiceFmtMoney(l.rate)}</td>` : ''}
        ${_sT ? `<td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
        <td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:12px;color:#475569;">${invoiceEsc(l.notes || '')}</td>
      </tr>`;
    });
    if (_sT) h += `<tr class="subtotal-row"><td colspan="${cols}" style="text-align:right;">Standby Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(sub)}</td><td></td></tr>`;
    h += `</tbody></table>`;
    sectionMap.standby = h;
  }

  // Permits
  if (permitLines.length > 0) {
    const sub = permitLines.reduce((s, l) => s + (l.lineAmount || 0), 0);
    let h = `<div class="section-title">Load Permits</div><table><thead><tr><th>Description</th><th class="right">Amount</th></tr></thead><tbody>`;
    permitLines.forEach(l => {
      h += `<tr><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;">${invoiceEsc(l.description || '-')}</td><td style="padding:6px 8px;border-bottom:1px solid #E2E8F0;text-align:right;font-weight:600;">${invoiceFmtMoney(l.lineAmount)}</td></tr>`;
    });
    h += `<tr class="subtotal-row"><td style="text-align:right;">Load Permit Subtotal</td><td style="text-align:right;">${invoiceFmtMoney(sub)}</td></tr></tbody></table>`;
    sectionMap.permit = h;
  }

  // Field Items
  if (fieldItemLines.length > 0) {
    sectionMap.fieldItems = buildFieldItemsTableHTML(fieldItemLines);
  }

  // Miscellaneous
  if (miscLines.length > 0) {
    sectionMap.misc = buildMiscTableHTML(miscLines);
  }

  // Ordered sections
  const orderedHTML = ls.invoiceSectionOrder.map(key => sectionMap[key] || '').join('');

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${invoiceStylesHTML()}</style></head><body>
  <div class="invoice-page">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;">
      <div>
        ${logoUrl ? `<img src="${invoiceSafeUrl(logoUrl)}" style="max-height:90px;max-width:300px;margin-bottom:8px;display:block;" alt="Logo">` : ''}
        <div style="font-size:24px;font-weight:800;color:#0F172A;">${invoiceEsc(companyName)}</div>
        ${companyAddress ? `<div style="color:#475569;margin-top:4px;">${invoiceEsc(companyAddress)}</div>` : ''}
        ${companyPhone ? `<div style="color:#475569;">${invoiceEsc(companyPhone)}</div>` : ''}
        ${companyEmail ? `<div style="color:#475569;">${invoiceEsc(companyEmail)}</div>` : ''}
      </div>
      <div style="text-align:right;">
        <div style="font-size:28px;font-weight:800;color:#3B82F6;letter-spacing:1px;">INVOICE</div>
        <div style="font-size:16px;font-weight:600;color:#0F172A;margin-top:4px;">INV-202603-001</div>
        <div style="color:#475569;margin-top:4px;">Date: ${workDate}</div>
        <div style="color:#475569;">Ticket #${ticket.ticketNo}</div>
      </div>
    </div>
    <div style="margin-bottom:24px;padding:16px;background:#F8FAFC;border-radius:6px;">
      <div style="font-size:11px;font-weight:700;color:#64748B;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">Bill To</div>
      <div style="font-size:16px;font-weight:700;color:#0F172A;">${invoiceEsc(customer.name)}</div>
      <div style="color:#475569;margin-top:2px;">${invoiceEsc(customer.address)}</div>
      <div style="color:#475569;">${invoiceEsc(customer.phone)}</div>
    </div>
    <div style="margin-bottom:24px;display:grid;grid-template-columns:1fr 1fr;gap:8px 24px;font-size:13px;">
      <div><span style="color:#64748B;">Job:</span> <strong>${invoiceEsc(job.title)}</strong></div>
      ${(() => { const t = _ticketHeaderJobType(ticket, 'invoice', { headerFields: ls.headerFields }); return t ? `<div><span style="color:#64748B;">Type:</span> ${invoiceEsc(t)}</div>` : ''; })()}
      ${ls.headerFields?.project?.invoice === true ? '<div><span style="color:#64748B;">Project:</span> Sample Project</div>' : ''}
      <div><span style="color:#64748B;">Location:</span> ${invoiceEsc(ticket.location)}</div>
      <div><span style="color:#64748B;">Work Date:</span> ${workDate}</div>
      <div><span style="color:#64748B;">PO #:</span> ${invoiceEsc(ticket.poNumber)}</div>
    </div>
    ${orderedHTML}
    <div class="invoice-footer">
      <div style="margin-top:32px;padding:16px;background:#0F172A;border-radius:6px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:18px;font-weight:700;color:#fff;">Grand Total</span>
        <span style="font-size:24px;font-weight:800;color:#fff;">${invoiceFmtMoney(grandTotal)}</span>
      </div>
      <div style="margin-top:8px;text-align:center;color:#64748B;font-size:11px;">
        Thank you for your business — <strong style="color:#475569;">${invoiceEsc(companyName)}</strong>
      </div>
    </div>
  </div>
  </body></html>`;
}

function buildIBTicketPreviewHTML(sample) {
  const { ticket, lines, customer, job, companyName, logoUrl } = sample;
  const ic = InvoiceBuilderState.localInvoiceColumns;
  const fts = InvoiceBuilderState.localFieldTicketSettings;
  const _showCosts = fts.showCostsOnDriverTicket;
  const _laborTimes = (ic.labor || {}).showTimes !== false;
  const _eqTimes = (ic.vehicles || {}).showTimes !== false || (ic.trailers || {}).showTimes !== false || (ic.assets || {}).showTimes !== false;

  const empLines = lines.filter(l => l.type === 'EMPLOYEE');
  const equipLines = lines.filter(l => l.type === 'EQUIPMENT');
  const downLines = lines.filter(l => l.type === 'DOWNTIME');
  const standbyLines = lines.filter(l => l.type === 'STANDBY');
  const permitLines = lines.filter(l => l.type === 'PERMIT');
  const grandTotal = lines.reduce((s, l) => s + (l.type === 'DOWNTIME' ? -1 : 1) * (l.lineAmount || 0), 0);
  const workDate = new Date(ticket.workDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${invoiceStylesHTML()}</style></head><body>
  <div class="ticket-detail-page" style="page-break-before:auto;">
    <div class="detail-header" style="display:flex;justify-content:space-between;align-items:flex-start;">
      <div>
        ${logoUrl ? `<img src="${invoiceSafeUrl(logoUrl)}" style="max-height:60px;max-width:240px;margin-bottom:6px;display:block;" alt="Logo">` : ''}
        ${companyName ? `<div style="font-size:14px;font-weight:700;color:#0F172A;">${invoiceEsc(companyName)}</div>` : ''}
      </div>
      <div style="text-align:right;">
        <div style="font-size:16px;font-weight:800;color:#3B82F6;">FIELD TICKET</div>
        <div style="font-size:13px;font-weight:600;">Ticket #${ticket.ticketNo}</div>
        <div style="font-size:12px;color:#475569;">${workDate}</div>
      </div>
    </div>
    <div class="detail-info-grid">
      <div><span class="label">Customer:</span> <strong>${invoiceEsc(customer.name)}</strong></div>
      <div><span class="label">Job:</span> ${invoiceEsc(job.title)}</div>
      ${(() => { const t = _ticketHeaderJobType(ticket, 'ticket', { headerFields: InvoiceBuilderState.localSettings.headerFields }); return t ? `<div><span class="label">Type:</span> ${invoiceEsc(t)}</div>` : ''; })()}
      ${InvoiceBuilderState.localSettings.headerFields?.project?.ticket !== false ? '<div><span class="label">Project:</span> Sample Project</div>' : ''}
      <div><span class="label">Location:</span> ${invoiceEsc(ticket.location)}</div>
      <div><span class="label">PO #:</span> ${invoiceEsc(ticket.poNumber)}</div>
    </div>`;

  // Labor
  // Hours are ALWAYS on the field ticket. buildTicketDetailHTML — the renderer
  // behind Print Ticket and the batch print, both of which pass isFieldTicket:
  // true — never consults invoiceColumns (that grid is the INVOICE template).
  // These four flags used to read it, so unticking Hrs/Qty in the invoice grid
  // previewed a ticket that lost its hours while the printed ticket kept them
  // (Lacey, 2026-09-24). The preview now mirrors the renderer.
  const _ftLaborHrs = true;
  if (empLines.length > 0) {
    html += `<div class="detail-section"><div class="detail-section-title">Labor</div>
      <table class="detail-table"><thead><tr>
        <th>Name</th>${_laborTimes ? '<th>Clock In</th><th>Clock Out</th>' : ''}${_ftLaborHrs ? '<th class="right">Hours</th>' : ''}${_showCosts ? '<th class="right">Rate</th><th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
    empLines.forEach(l => {
      html += `<tr><td>${invoiceEsc(l.personName || '-')}</td>
        ${_laborTimes ? `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>` : ''}
        ${_ftLaborHrs ? `<td class="right">${(l.hours || 0).toFixed(2)}</td>` : ''}
        ${_showCosts ? `<td class="right">${invoiceFmtMoney(l.rate)}</td><td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      </tr>`;
    });
    html += `</tbody></table></div>`;
  }

  // Equipment
  const _ftEqHrs = true;
  if (equipLines.length > 0) {
    html += `<div class="detail-section"><div class="detail-section-title">Equipment</div>
      <table class="detail-table"><thead><tr>
        <th>Asset</th><th>Fleet #</th>${_eqTimes ? '<th>From</th><th>To</th>' : ''}${_ftEqHrs ? '<th class="right">QTY</th>' : ''}${_showCosts ? '<th class="right">Rate</th><th class="right">Amount</th>' : ''}
      </tr></thead><tbody>`;
    equipLines.forEach(l => {
      const _pu = !isTimeBasedUnit(l.rateUnit);
      html += `<tr><td>${invoiceEsc(l.assetName || '-')}</td><td>${invoiceEsc(l.fleetNumber || '-')}</td>
        ${_eqTimes ? (_pu ? '<td>—</td><td>—</td>' : `<td>${lineFromHtml(l)}</td><td>${lineToHtml(l)}</td>`) : ''}
        ${_ftEqHrs ? `<td class="right">${_pu ? ((l.quantity || 0) + ' ' + invoiceEsc(l.rateUnit || '')) : (l.hours || 0).toFixed(2)}</td>` : ''}
        ${_showCosts ? `<td class="right">${invoiceFmtMoney(l.rate)}</td><td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}
      </tr>`;
    });
    html += `</tbody></table></div>`;
  }

  // Downtime
  const _ftDownHrs = true;
  if (downLines.length > 0) {
    html += `<div class="detail-section"><div class="detail-section-title">Downtime</div>
      <table class="detail-table"><thead><tr><th>Person</th>${_ftDownHrs ? '<th class="right">Hours</th>' : ''}<th>Notes</th></tr></thead><tbody>`;
    downLines.forEach(l => {
      html += `<tr><td>${invoiceEsc(l.personName || '-')}</td>${_ftDownHrs ? `<td class="right">${(l.hours || 0).toFixed(2)}</td>` : ''}<td>${invoiceEsc(l.notes || '')}</td></tr>`;
    });
    html += `</tbody></table></div>`;
  }

  // Standby
  const _ftStandbyHrs = true;
  if (standbyLines.length > 0) {
    html += `<div class="detail-section"><div class="detail-section-title">Standby</div>
      <table class="detail-table"><thead><tr><th>Person</th>${_ftStandbyHrs ? '<th class="right">Hours</th>' : ''}<th>Notes</th></tr></thead><tbody>`;
    standbyLines.forEach(l => {
      html += `<tr><td>${invoiceEsc(l.personName || '-')}</td>${_ftStandbyHrs ? `<td class="right">${(l.hours || 0).toFixed(2)}</td>` : ''}<td>${invoiceEsc(l.notes || '')}</td></tr>`;
    });
    html += `</tbody></table></div>`;
  }

  // Permits
  if (permitLines.length > 0) {
    html += `<div class="detail-section"><div class="detail-section-title">Load Permits</div>
      <table class="detail-table"><thead><tr><th>Description</th>${_showCosts ? '<th class="right">Amount</th>' : ''}</tr></thead><tbody>`;
    permitLines.forEach(l => {
      html += `<tr><td>${invoiceEsc(l.description || '-')}</td>${_showCosts ? `<td class="right bold">${invoiceFmtMoney(l.lineAmount)}</td>` : ''}</tr>`;
    });
    html += `</tbody></table></div>`;
  }

  // Notes
  if (ticket.workPerformed) {
    html += `<div class="detail-section"><div class="detail-section-title">Work Performed</div>
      <div class="detail-notes">${invoiceEsc(ticket.workPerformed)}</div></div>`;
  }

  // Total
  if (_showCosts) {
    html += `<div class="detail-total"><span>Total</span><span>${invoiceFmtMoney(grandTotal)}</span></div>`;
  }

  // Signature lines (based on field ticket settings)
  if (fts.requireDriverSignature || fts.requireCustomerSignature || fts.requireStamp) {
    html += `<div style="margin-top:32px;display:flex;gap:24px;flex-wrap:wrap;">`;
    if (fts.requireDriverSignature) {
      html += `<div style="flex:1;min-width:200px;">
        <div style="border-bottom:1px solid #0F172A;height:40px;margin-bottom:4px;"></div>
        <div style="font-size:11px;color:#64748B;font-weight:600;">Driver Signature</div>
        <div style="font-size:10px;color:#94A3B8;">Date: _______________</div>
      </div>`;
    }
    if (fts.requireCustomerSignature) {
      html += `<div style="flex:1;min-width:200px;">
        <div style="border-bottom:1px solid #0F172A;height:40px;margin-bottom:4px;"></div>
        <div style="font-size:11px;color:#64748B;font-weight:600;">Customer Signature</div>
        <div style="font-size:10px;color:#94A3B8;">Date: _______________</div>
      </div>`;
    }
    if (fts.requireStamp) {
      html += `<div style="flex:1;min-width:140px;text-align:center;">
        <div style="width:100px;height:100px;border:2px dashed #CBD5E1;border-radius:8px;margin:0 auto 4px;display:flex;align-items:center;justify-content:center;color:#94A3B8;font-size:11px;font-weight:600;">STAMP</div>
        <div style="font-size:11px;color:#64748B;font-weight:600;">Company Stamp</div>
      </div>`;
    }
    html += `</div>`;
  }

  // TASK-10185 — where the dispatch job notes land when the switch is on.
  if (InvoiceBuilderState.localSettings.showJobNotesOnTickets) {
    html += `<div class="detail-section" style="margin-top:24px;">
      <div class="detail-section-title">Dispatch Notes</div>
      <div class="detail-notes" style="white-space:pre-wrap;">Haul from the north yard to the Johnson 2-14 pad. Call the site lead before arriving. (The job's own notes print here; long notes continue on the next page.)</div>
    </div>`;
  }

  html += `</div></body></html>`;
  return html;
}

// Save Invoice Builder settings
async function saveInvoiceBuilder() {
  if (_t9986BillingReadOnly()) { _t9986Refuse(); return; } // TASK-9986 S1
  const btn = document.getElementById('ib-save-btn');
  const origHTML = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    // Read final state from UI
    readIBSettingsFromUI();

    // TASK-4714 — validate BEFORE the first write, not between two of them. The
    // tenant-level settings go out a few lines below; refusing after that would
    // leave half this save applied and the other half not. Same gate as the Price
    // Book contract editor — two forms write this one setting, and a rule enforced
    // in only one of them is not a rule.
    // Mass mode is exempt because it deliberately never writes the recipient at
    // all (one address across every customer would cross-send tickets); it warns
    // by name after the batch instead.
    if (InvoiceBuilderState.mode === 'single' && InvoiceBuilderState.selectedContractId) {
      const _fts = InvoiceBuilderState.localFieldTicketSettings || {};
      if (_fts.requireEmailCopy && !_isPlausibleEmail(_fts.defaultEmailRecipient)) {
        const el = document.getElementById('ib-fts-email-to');
        if (el) { el.style.borderColor = '#DC2626'; el.focus(); }
        window.showToast?.('Add the email address the field ticket should go to, or turn off "Request email copy". Without it the driver\'s copy is refused and nobody is told.', 'error');
        return;
      }
    }

    const tenant = getTenant();
    const tenantRef = doc(db, 'tenants', tenant);

    // Save tenant-level settings (including email template)
    //
    // 🔴 TASK-9280 — eight owned keys, no `...existingSettings`, and the getDoc
    // that fed it is gone: every value below comes from InvoiceBuilderState, so
    // this function never needed the prior state. Reading it only created the
    // stale snapshot the spread then wrote back — `merge: true` REPLACES arrays
    // wholesale and rewrites every scalar leaf it is handed, so a Save here
    // reverted anything another writer changed in the window, including
    // settings.smsInbox.optedOutOfAi (the SMS consent record, written Admin-SDK
    // side by optOutOfAi on an inbound "NO AI" text). Omitting those keys is what
    // preserves them, not the spread — verified against real Firestore
    // 2026-09-22. See saveBillingSettings for the long note.
    //
    // ⚠️ invoiceSectionOrder is an ARRAY, so removeIBSection still removes.
    // companyHeader / slipTemplate / perAssetNotesTypes are maps built in
    // resetInvoiceBuilderState from the live settings plus a full default set, so
    // every leaf is present on every save and merge:true's never-remove rule
    // costs nothing here. companyHeader has a second writer (saveCompanyInfo).
    const invoiceEmailTemplate = { ...InvoiceBuilderState.localEmailTemplate };
    // Column grid — built here because the MASS save also persists it as the
    // tenant-level template (see getInvoiceColumnConfig: a customer with no
    // Active contract has no contract doc to carry it, so the invoice used to
    // fall back to hardcoded defaults and show every column). Every section
    // carries every key on every save, so merge:true's never-remove rule costs
    // nothing here. Single-contract mode edits ONE contract and deliberately
    // does not touch the tenant template.
    const invoiceColumns = { ...InvoiceBuilderState.localInvoiceColumns };
    delete invoiceColumns.equipment; // don't save legacy key
    const _isMassSave = !(InvoiceBuilderState.mode === 'single' && InvoiceBuilderState.selectedContractId);
    const newSettings = {
      ...(_isMassSave ? { invoiceColumns } : {}),
      showClockTimesOnTickets: InvoiceBuilderState.localSettings.showClockTimesOnTickets,
      showEmployeeNamesOnTickets: InvoiceBuilderState.localSettings.showEmployeeNamesOnTickets === true, // TASK-865
      showJobNotesOnTickets: InvoiceBuilderState.localSettings.showJobNotesOnTickets === true, // TASK-10185 (firestore.rules t9337BillingKeys)
      // TASK-9390 — whole map, every leaf always present (merge:true never
      // removes a leaf, so a partial map could strand an old value). Tenant-
      // level on BOTH save modes: it is a template setting, not a contract one.
      // TASK-10203 — project rides in the same map (firestore.rules t9390ShapeOk).
      headerFields: _ibHeaderFields(InvoiceBuilderState.localSettings.headerFields),
      invoiceSectionOrder: InvoiceBuilderState.localSettings.invoiceSectionOrder,
      perAssetNotes: InvoiceBuilderState.localSettings.perAssetNotes,
      perAssetNotesTypes: InvoiceBuilderState.localSettings.perAssetNotesTypes,
      companyHeader: InvoiceBuilderState.localSettings.companyHeader,
      slipTemplate: InvoiceBuilderState.localSettings.slipTemplate,
      invoiceEmailTemplate
    };
    await setDoc(tenantRef, { settings: newSettings }, { merge: true });

    // Update local AppState
    if (window.AppState?.settings) {
      window.AppState.settings.showClockTimesOnTickets = newSettings.showClockTimesOnTickets;
      window.AppState.settings.showEmployeeNamesOnTickets = newSettings.showEmployeeNamesOnTickets; // TASK-865
      window.AppState.settings.showJobNotesOnTickets = newSettings.showJobNotesOnTickets; // TASK-10185
      window.AppState.settings.headerFields = newSettings.headerFields; // TASK-9390 (+ project, TASK-10203)
      window.AppState.settings.invoiceSectionOrder = newSettings.invoiceSectionOrder;
      window.AppState.settings.perAssetNotes = newSettings.perAssetNotes;
      window.AppState.settings.perAssetNotesTypes = newSettings.perAssetNotesTypes;
      window.AppState.settings.companyHeader = newSettings.companyHeader;
      window.AppState.settings.slipTemplate = newSettings.slipTemplate;
      window.AppState.settings.invoiceEmailTemplate = invoiceEmailTemplate;
      if (_isMassSave) window.AppState.settings.invoiceColumns = invoiceColumns;
    }

    // Field ticket settings for contracts (invoiceColumns built above)
    const fieldTicketSettings = { ...InvoiceBuilderState.localFieldTicketSettings };
    fieldTicketSettings.defaultEmailRecipient = _repairEmail(fieldTicketSettings.defaultEmailRecipient);

    if (InvoiceBuilderState.mode === 'single' && InvoiceBuilderState.selectedContractId) {
      // Single contract save
      const custId = InvoiceBuilderState.selectedCustomerId;
      const contractId = InvoiceBuilderState.selectedContractId;
      await updateDoc(doc(db, 'tenants', tenant, 'customers', custId, 'contracts', contractId), {
        invoiceColumns,
        fieldTicketSettings
      });
      // Invalidate this customer's cached contractData (10-min TTL in
      // loadReviewContractRates) so the next invoice render reads the new grid —
      // the mass branch clears the whole cache; this branch cleared nothing.
      delete ReviewState.contractRateCache[custId];
      window.showToast?.('Invoice settings saved for this contract', 'success');
    } else {
      // Mass save — batch update all contracts (customers only — P0 2026-10-06)
      const customers = _billableCustomers();
      let contractCount = 0;
      const batches = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;

      // TASK-4714 — 🔴 THE MASS SAVE USED TO WRITE `fieldTicketSettings` AS A
      // WHOLE MAP, and a nested object in updateDoc REPLACES the map instead of
      // merging into it. Since this panel never carried `defaultEmailRecipient`,
      // one "apply to all contracts" wiped the recipient off every contract of
      // every customer in the tenant — silently, with a green success toast. That
      // is the mechanism behind the fleet-wide zero this task was filed on.
      //
      // Field PATHS now, one per key: each contract keeps its own recipient, and
      // nothing this panel does not show can be destroyed by saving it.
      const _ftsPaths = {};
      Object.entries(fieldTicketSettings).forEach(([k, v]) => {
        if (k === 'defaultEmailRecipient') return; // per-customer, never mass-written
        // TASK-4683 A3 — the approval-signature gate is only mass-written when the
        // dropdown was actually changed in this session. Its displayed value is
        // seeded from an arbitrary "first contract", so writing it on every save
        // would silently reset a contract set to Required back to Optional (or
        // push one contract's Off onto the whole fleet) on an unrelated toggle.
        if (k === 'customerApprovalSignature' && !InvoiceBuilderState._approvalSigTouched) return;
        _ftsPaths[`fieldTicketSettings.${k}`] = v;
      });
      const _missingRecipient = [];

      for (const cust of customers) {
        const contractsSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', cust.id, 'contracts'));
        for (const contractDoc of contractsSnap.docs) {
          currentBatch.update(contractDoc.ref, { invoiceColumns, ..._ftsPaths });
          // Arming the requirement across the fleet arms it on contracts that
          // have no address of their own. Say WHICH ones, by name, instead of a
          // green toast over a broken close-out flow.
          if (fieldTicketSettings.requireEmailCopy &&
              !_isPlausibleEmail((contractDoc.data().fieldTicketSettings || {}).defaultEmailRecipient)) {
            _missingRecipient.push(`${cust.name || 'Customer'} — ${contractDoc.data().title || 'Untitled'}`);
          }
          contractCount++;
          opCount++;
          if (opCount >= 450) {
            batches.push(currentBatch);
            currentBatch = writeBatch(db);
            opCount = 0;
          }
        }
      }
      if (opCount > 0) batches.push(currentBatch);
      for (const b of batches) { await b.commit(); }

      // Invalidate rate caches
      ReviewState.contractRateCache = {};

      window.showToast?.(`Invoice settings applied to ${contractCount} contracts`, 'success');
      if (_missingRecipient.length) {
        const _shown = _missingRecipient.slice(0, 4).join('; ');
        window.showToast?.(`${_missingRecipient.length} contract${_missingRecipient.length === 1 ? '' : 's'} now ask for an emailed copy with no recipient set — the driver's copy will be refused. ${_shown}${_missingRecipient.length > 4 ? `, +${_missingRecipient.length - 4} more` : ''}`, 'error', null, 12000);
        console.warn('[Billing] TASK-4714 — contracts requiring an email copy with no recipient:', _missingRecipient);
      }
    }

    closeInvoiceBuilder();
  } catch (err) {
    console.error('[IB] Error saving:', err);
    window.showToast?.('Error saving invoice settings: ' + (err.message || 'Unknown'), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = origHTML; }
  }
}

// ============================================
// OVERFLOW MENU (mobile)
// ============================================
function toggleBillingOverflow(e) {
  e.stopPropagation();
  const wrap = e.currentTarget.closest('.billing-overflow-wrap');
  const dd = wrap ? wrap.querySelector('.billing-overflow-dropdown') : document.getElementById('billing-overflow-dropdown');
  if (!dd) return;
  const wasHidden = dd.classList.contains('hidden');
  dd.classList.toggle('hidden');
  if (wasHidden) {
    const close = (ev) => { if (!dd.contains(ev.target)) { dd.classList.add('hidden'); document.removeEventListener('click', close); } };
    setTimeout(() => document.addEventListener('click', close), 0);
  }
}

// ============================================
// FINANCIAL SETUP WIZARD
// ============================================
// Guided flow for consultants setting up a new client's financial integrations
let _wizardStep = 0;

async function openFinancialWizard() {
  _wizardStep = 0;

  // Determine current state
  const td = window.AppState?.tenantData || {};
  // TASK-9338: check-printing bank details moved off the tenant root (driver-
  // readable) into admin-only financeSecure/checkPrinting, so AppState.settings
  // no longer carries them. Ask the AP module (apCheckPrint 'status', no bank
  // numbers returned); unavailable reads as "not set up" — it's only a checkmark.
  const hasBankSetup = await (window.APModule?.hasCheckPrintingSetup?.() ?? false);
  const hasPlaid = td.plaidConnected;
  const hasQBO = (await ensureQBOLiveStatus()) === true; // TASK-9589 live, not the cached flag

  const existing = document.getElementById('modal-financial-wizard');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-financial-wizard';
  modal.style.zIndex = '1200';

  function renderWizardStep() {
    const steps = [
      { icon: 'account_balance', title: 'Connect Your Bank', desc: 'Link your bank account via Plaid for automatic transaction pulls and real-time balance.', done: hasPlaid, skip: true },
      { icon: 'print', title: 'Set Up Check Printing', desc: 'Enter your bank account details for printing checks on standard 3-per-page check stock.', done: hasBankSetup, skip: true },
      { icon: 'sync', title: 'Connect QuickBooks', desc: 'Sync invoices, bills, and payments with QuickBooks Online. Or use QB Desktop IIF export.', done: hasQBO, skip: true },
    ];

    const step = steps[_wizardStep] || null;
    const progress = Math.round(((_wizardStep) / steps.length) * 100);

    if (!step) {
      // Done!
      modal.innerHTML = `
        <div class="modal-content" style="max-width:500px;text-align:center;padding:0;">
          <div style="background:linear-gradient(135deg, var(--brand-blue), var(--accent-teal));padding:40px;color:#fff;">
            <span class="material-icons" style="font-size:64px;margin-bottom:12px;">check_circle</span>
            <h2 style="margin:0 0 8px;font-size:1.5rem;">Financial Suite Ready</h2>
            <p style="margin:0;opacity:0.9;">Your financial integrations are configured.</p>
          </div>
          <div style="padding:24px;">
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:20px;">
              ${steps.map(s => `
                <div style="text-align:center;padding:12px;background:${s.done ? '#F0FDF4' : '#F8FAFC'};border-radius:8px;">
                  <span class="material-icons" style="font-size:24px;color:${s.done ? '#10B981' : '#94A3B8'};">${s.done ? 'check_circle' : 'radio_button_unchecked'}</span>
                  <div style="font-size:12px;margin-top:4px;color:${s.done ? '#065F46' : '#64748B'};">${s.title.replace('Connect ', '').replace('Set Up ', '')}</div>
                </div>
              `).join('')}
            </div>
            <button class="btn btn-primary" onclick="document.getElementById('modal-financial-wizard').remove()" style="width:100%;">
              Done
            </button>
          </div>
        </div>`;
      return;
    }

    modal.innerHTML = `
      <div class="modal-content" style="max-width:500px;padding:0;">
        <div style="background:linear-gradient(135deg, var(--brand-blue), var(--accent-teal));padding:24px;color:#fff;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
            <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
              <span class="material-icons">tune</span> Financial Setup
            </h3>
            <button class="btn-icon" onclick="document.getElementById('modal-financial-wizard').remove()" style="color:#fff;">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div style="background:rgba(255,255,255,0.2);border-radius:8px;height:6px;overflow:hidden;">
            <div style="background:#fff;height:100%;width:${progress}%;border-radius:8px;transition:width 0.3s;"></div>
          </div>
          <div style="font-size:12px;margin-top:6px;opacity:0.8;">Step ${_wizardStep + 1} of ${steps.length}</div>
        </div>
        <div style="padding:24px;">
          <div style="text-align:center;margin-bottom:20px;">
            <span class="material-icons" style="font-size:48px;color:${step.done ? '#10B981' : 'var(--brand-blue)'};">${step.done ? 'check_circle' : step.icon}</span>
            <h3 style="margin:12px 0 4px;font-size:1.1rem;">${step.title}</h3>
            <p style="font-size:0.85rem;color:var(--text-secondary);margin:0;">${step.desc}</p>
            ${step.done ? '<div style="margin-top:8px;font-size:0.85rem;color:#10B981;font-weight:600;">Already configured</div>' : ''}
          </div>

          ${_wizardStep === 0 && !hasPlaid ? `
            <button class="btn btn-primary" style="width:100%;background:#10B981;border-color:#10B981;margin-bottom:8px;" onclick="window.BillingModule.startPlaidLink()">
              <span class="material-icons" style="font-size:16px;">account_balance</span> Connect Bank via Plaid
            </button>
          ` : ''}

          ${_wizardStep === 1 && !hasBankSetup ? `
            <button class="btn btn-primary" style="width:100%;margin-bottom:8px;" onclick="window.APModule?.showBankSetup?.()">
              <span class="material-icons" style="font-size:16px;">print</span> Set Up Bank Account
            </button>
          ` : ''}

          ${_wizardStep === 2 && !hasQBO ? `
            <button class="btn btn-primary" style="width:100%;background:#2CA01C;border-color:#2CA01C;margin-bottom:8px;" onclick="window.BillingModule.startQBOConnect(event)">
              <span class="material-icons" style="font-size:16px;">sync</span> Connect QuickBooks Online
            </button>
            <div style="text-align:center;font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">— or —</div>
            <button class="btn btn-secondary" style="width:100%;margin-bottom:8px;" onclick="window.BillingModule.openQBSetup()">
              <span class="material-icons" style="font-size:16px;">download</span> Set Up QB Desktop Export
            </button>
          ` : ''}

          <div style="display:flex;gap:8px;margin-top:12px;">
            ${_wizardStep > 0 ? `<button class="btn btn-secondary" style="flex:1;" onclick="window.BillingModule._wizardPrev()">
              <span class="material-icons" style="font-size:16px;">arrow_back</span> Back
            </button>` : ''}
            <button class="btn ${step.done ? 'btn-primary' : 'btn-secondary'}" style="flex:1;" onclick="window.BillingModule._wizardNext()">
              ${step.done ? 'Next' : 'Skip'} <span class="material-icons" style="font-size:16px;">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>`;
  }

  document.body.appendChild(modal);
  renderWizardStep();

  // Store render fn for navigation
  window._renderWizardStep = renderWizardStep;
}

function _wizardNext() {
  _wizardStep++;
  window._renderWizardStep?.();
}

function _wizardPrev() {
  _wizardStep = Math.max(0, _wizardStep - 1);
  window._renderWizardStep?.();
}

// ============================================
// SETTLEMENT STATEMENTS
// ============================================

async function loadSettlementData(personId, startDate, endDate) {
  const tenant = BillingState.tenantId;
  if (!tenant) throw new Error('No tenant');

  // Query clockEntries for person, then filter by date in code
  // (avoids needing a composite index for personId + clockIn ASC range)
  const startMs = new Date(startDate + 'T00:00:00').getTime();
  const endMs = new Date(endDate + 'T23:59:59').getTime();

  const entriesSnap = await getDocs(
    query(collection(db, 'tenants', tenant, 'clockEntries'),
      where('personId', '==', personId),
      orderBy('clockIn', 'desc')
    )
  );

  // Filter to date range in code
  const filteredEntries = entriesSnap.docs.filter(d => {
    const ci = d.data().clockIn;
    const ms = ci?.toDate?.()?.getTime?.() || 0;
    return ms >= startMs && ms <= endMs;
  });

  const ticketIds = [...new Set(filteredEntries.map(d => d.data().ticketId).filter(Boolean))];
  if (ticketIds.length === 0) return [];

  // Batch-fetch ticketLines in groups of 20
  const allLines = [];
  for (let i = 0; i < ticketIds.length; i += 20) {
    const batch = ticketIds.slice(i, i + 20);
    for (const tid of batch) {
      const linesSnap = await getDocs(collection(db, 'tenants', tenant, 'tickets', tid, 'ticketLines'));
      for (const ld of linesSnap.docs) {
        const line = { id: ld.id, ticketId: tid, ...ld.data() };
        // Only piece-rate EMPLOYEE lines for this person
        if (line.personId === personId && line.lineType === 'employee' && !isTimeBasedUnit(line.rateUnit)) {
          allLines.push(line);
        }
      }
    }
  }

  // Enrich with ticket info (date, customer, job)
  const ticketMap = {};
  for (const tid of ticketIds) {
    const t = BillingState.tickets?.find(t => t.id === tid);
    if (t) ticketMap[tid] = t;
    else {
      const tSnap = await getDoc(doc(db, 'tenants', tenant, 'tickets', tid));
      if (tSnap.exists()) ticketMap[tid] = { id: tSnap.id, ...tSnap.data() };
    }
  }

  return allLines.map(line => ({
    ...line,
    ticketDate: ticketMap[line.ticketId]?.workDate || ticketMap[line.ticketId]?.date || null,
    customerName: ticketMap[line.ticketId]?.customerName || '',
    jobName: ticketMap[line.ticketId]?.jobName || ''
  }));
}

// TASK-PENDING-SETTLEMENT — businessName is a peopleTax field (hr.js SECURE_TAX_FIELDS) since
// the April 2026 tax migration; the broadcast people doc no longer carries it,
// so reading person.businessName here printed a blank for every contractor.
// `taxInfo` = { tax, status } from _billingLoadSettlementTax. status 'denied'
// or 'error' adds an on-screen (never printed) note so a missing business name
// reads as "hidden from you" / "could not load", not "none on file".
function buildSettlementHTML(person, lines, periodStart, periodEnd, companyInfo, taxInfo) {
  const ci = companyInfo || resolveCompanyInfo();
  const personName = person.name || 'Unknown';
  const taxStatus = (taxInfo && taxInfo.status) || 'ok';
  const businessName = (taxStatus === 'ok' && taxInfo && taxInfo.tax && taxInfo.tax.businessName) || '';
  const taxNote = taxStatus === 'denied'
    ? 'Business name restricted - HR Directory permission required to show it on this statement.'
    : taxStatus === 'error' ? 'Business name unavailable - it could not be loaded. Close this window and try again.' : '';
  const isContractor = !!window.WorkerClass?.resolve(person).is1099; // TASK-9178 — the ONE W-2 / 1099 rule
  const payRate = parseFloat(person.payRate) || 0;
  const payRateUnit = person.payRateUnit || 'Each';

  // Group lines by date
  const sorted = [...lines].sort((a, b) => {
    const da = a.ticketDate?.toDate?.() || (a.ticketDate ? new Date(a.ticketDate) : new Date(0));
    const db2 = b.ticketDate?.toDate?.() || (b.ticketDate ? new Date(b.ticketDate) : new Date(0));
    return da - db2;
  });

  let grandTotal = 0;
  const qtyByUnit = {};

  let rowsHTML = '';
  for (const line of sorted) {
    const dt = line.ticketDate?.toDate?.() || (line.ticketDate ? new Date(line.ticketDate) : null);
    const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const qty = parseFloat(line.quantity) || 0;
    const amount = qty * payRate;
    grandTotal += amount;
    const unit = line.rateUnit || payRateUnit;
    qtyByUnit[unit] = (qtyByUnit[unit] || 0) + qty;

    rowsHTML += `<tr>
      <td>${invoiceEsc(dateStr)}</td>
      <td>${invoiceEsc(line.jobName || line.customerName || '—')}</td>
      <td>${invoiceEsc(line.customerName || '—')}</td>
      <td class="right">${qty}</td>
      <td>${invoiceEsc(unit)}</td>
      <td class="right">${invoiceFmtMoney(payRate)}</td>
      <td class="right bold">${invoiceFmtMoney(amount)}</td>
    </tr>`;
  }

  // Subtotals by unit
  let subtotalsHTML = '';
  const units = Object.keys(qtyByUnit);
  if (units.length > 1) {
    subtotalsHTML = '<div style="margin:16px 0 8px;font-size:13px;font-weight:600;color:#475569;">Subtotals by Unit</div>';
    for (const u of units) {
      subtotalsHTML += `<div style="font-size:13px;padding:2px 0;">${qtyByUnit[u]} ${invoiceEsc(u)} @ ${invoiceFmtMoney(payRate)} = ${invoiceFmtMoney(qtyByUnit[u] * payRate)}</div>`;
    }
  }

  const fmtPeriod = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return `<!DOCTYPE html><html><head><title>Settlement - ${invoiceEsc(personName)}</title>
<style>${invoiceStylesHTML()}
  .settlement-header { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #0F172A; }
  .settlement-total { font-size:24px;font-weight:800;color:#0F172A;margin-top:16px;padding:12px 16px;background:#F1F5F9;border-radius:6px;text-align:right; }
  .settlement-disclaimer { margin-top:24px;padding:12px;background:#FEF3C7;border-radius:6px;font-size:11px;color:#92400E;line-height:1.5; }
  td { padding:6px 8px;border-bottom:1px solid #E2E8F0;font-size:13px; }
  td.right { text-align:right; }
  td.bold { font-weight:600; }
</style></head><body>
<div class="invoice-page">
  <div class="no-print" style="text-align:right;margin-bottom:16px;">
    <button onclick="window.print()" style="padding:8px 20px;background:#3B82F6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:14px;">Print / Save PDF</button>
  </div>
  ${taxNote ? `<div class="no-print settlement-tax-note" style="margin-bottom:16px;padding:10px 14px;background:#FFFBEB;border:1px solid #FCD34D;border-radius:6px;color:#0F172A;font-size:13px;">${invoiceEsc(taxNote)}</div>` : ''}
  <div class="settlement-header">
    <div>
      ${ci.logoUrl ? `<img src="${invoiceSafeUrl(ci.logoUrl)}" style="max-height:48px;margin-bottom:8px;">` : ''}
      <div style="font-size:16px;font-weight:700;">${invoiceEsc(ci.companyName)}</div>
      ${ci.address ? `<div style="font-size:12px;color:#64748B;">${invoiceEsc(ci.address)}</div>` : ''}
      ${ci.phone ? `<div style="font-size:12px;color:#64748B;">${invoiceEsc(ci.phone)}</div>` : ''}
    </div>
    <div style="text-align:right;">
      <div style="font-size:20px;font-weight:700;color:#0F172A;">${isContractor ? 'Pay Summary' : 'Settlement Statement'}</div>
      <div style="font-size:13px;color:#64748B;margin-top:4px;">Period: ${fmtPeriod(periodStart)} — ${fmtPeriod(periodEnd)}</div>
    </div>
  </div>

  <div style="margin-bottom:20px;padding:12px;background:#F8FAFC;border-radius:6px;border:1px solid #E2E8F0;">
    <div style="font-size:14px;font-weight:700;">${invoiceEsc(personName)}</div>
    ${businessName ? `<div style="font-size:13px;color:#64748B;">${invoiceEsc(businessName)}</div>` : ''}
    <div style="font-size:12px;color:#64748B;margin-top:2px;">${isContractor ? 'Contractor (1099)' : 'Employee (W-2)'} · Pay Rate: ${invoiceFmtMoney(payRate)}${invoiceEsc(payRateUnit ? ' ' + payRateUnit : '')}</div>
  </div>

  <table style="width:100%;border-collapse:collapse;">
    <thead><tr>
      <th>Date</th><th>Job</th><th>Customer</th><th class="right">Qty</th><th>Unit</th><th class="right">Rate</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>${rowsHTML}</tbody>
    <tfoot><tr style="border-top:2px solid #0F172A;">
      <td colspan="6" style="text-align:right;font-weight:700;padding:10px 8px;">Total</td>
      <td class="right bold" style="padding:10px 8px;font-size:15px;">${invoiceFmtMoney(grandTotal)}</td>
    </tr></tfoot>
  </table>

  ${subtotalsHTML}

  <div class="settlement-total">Total Due: ${invoiceFmtMoney(grandTotal)}</div>

  <div class="settlement-disclaimer">
    ${isContractor ? '<strong>Note:</strong> This is a pay summary, not an invoice. Payment is subject to contract terms.' : '<strong>FLSA Notice:</strong> Does not include overtime adjustments. Verify FLSA/FMCSA exemption status with your payroll provider.'}
  </div>

  <div style="margin-top:24px;text-align:center;font-size:11px;color:#94A3B8;">
    Generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${invoiceEsc(ci.companyName)} · Powered by DispatchDeck
  </div>
</div></body></html>`;
}

// TASK-PENDING-SETTLEMENT — the settlement's business name lives in peopleTax. ONE getDoc for
// the one person being settled, under the unchanged peopleTax rule
// (isPartnerElevated || hasModule('hr-directory')) — that rule is the boundary,
// this adds no read path. DENIED is not ABSENT (TASK-9354 1099 pattern):
//   { status:'ok', tax }        readable (tax = {} when no peopleTax doc exists)
//   { status:'denied', tax:null } the rules refused this viewer
//   { status:'error',  tax:null } transient failure (offline, etc.)
async function _billingLoadSettlementTax(tenant, personId) {
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'peopleTax', personId));
    return { status: 'ok', tax: snap.exists() ? (snap.data() || {}) : {} };
  } catch (e) {
    if (e && e.code === 'permission-denied') return { status: 'denied', tax: null };
    console.warn('[Billing] settlement peopleTax read failed', personId, e?.message || e);
    return { status: 'error', tax: null };
  }
}

async function generateSettlement(personId, startDate, endDate) {
  const tenant = BillingState.tenantId;
  if (!tenant) { window.showToast?.('No tenant', 'error'); return; }

  // TASK-1481 — comp-first resolution (payRate/payRateUnit move to peopleComp).
  // 🔴 DENIED ≠ ABSENT: if the viewer was denied the comp collection and the
  // person doc carries no legacy pay fields, every settlement line would price
  // at $0.00 — refuse visibly instead.
  const rawPerson = BillingState.people?.find(p => p.id === personId);
  if (!rawPerson) { window.showToast?.('Person not found', 'error'); return; }
  if (window.PersonComp?.compUnknown?.(rawPerson)) {
    window.showToast?.('You do not have access to pay rates (HR Directory access is required), so this settlement would show $0. Ask an admin to generate it.', 'error');
    return;
  }
  const person = window.PersonComp ? window.PersonComp.effective(rawPerson) : rawPerson;
  // TASK-PENDING-SETTLEMENT — never a silent $0. compUnknown() above keys on
  // personHasOwnComp(), which is true for anyone whose broadcast doc still
  // carries flsaStatus/hrOvertimeExempt (deliberately kept there), so it cannot
  // tell "rate hidden/not loaded yet" from "no rate". Decide on the RESOLVED
  // piece rate instead: none → say WHY (no access / still loading / not set).
  // Before the first peopleComp snapshot (status idle/loading — billing
  // subscribes in its deferred phase 2) effective() falls back to the stripped
  // person doc, which is exactly where the $0 came from.
  const _settleRate = Number(parseFloat(person && person.payRate));
  if (!(Number.isFinite(_settleRate) && _settleRate > 0)) {
    const _compStatus = window.PersonComp?.status?.();
    const msg = window.PersonComp?.cannotKnow?.()
      ? 'You do not have access to pay rates (HR Directory access is required), so this settlement would show $0. Ask an admin to generate it.'
      : (window.PersonComp && _compStatus !== 'ready')
        ? 'Pay rates are still loading. Try again in a moment.'
        : `No piece rate is set for ${person.name || 'this person'}, so this settlement would show $0. Set it in HR → Employment first.`;
    window.showToast?.(msg, 'error');
    return;
  }

  window.showToast?.('Generating settlement...', 'info');

  try {
    const lines = await loadSettlementData(personId, startDate, endDate);
    if (lines.length === 0) {
      window.showToast?.('No piece-rate lines found for this period', 'error');
      return;
    }

    const payRate = parseFloat(person.payRate) || 0;
    const grandTotal = lines.reduce((sum, l) => sum + ((parseFloat(l.quantity) || 0) * payRate), 0);

    const taxInfo = await _billingLoadSettlementTax(tenant, personId);
    if (taxInfo.status === 'denied') window.showToast?.('Business name hidden on this statement - your account needs HR Directory permission to see it.', 'warning');
    else if (taxInfo.status === 'error') window.showToast?.('Business name could not be loaded - the statement was generated without it.', 'warning');
    const html = buildSettlementHTML(person, lines, startDate, endDate, undefined, taxInfo);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }

    // Log to settlementLog
    await addDoc(collection(db, 'tenants', tenant, 'settlementLog'), {
      personId,
      personName: person.name || '',
      periodStart: startDate,
      periodEnd: endDate,
      total: grandTotal,
      lineCount: lines.length,
      generatedAt: Timestamp.now(),
      generatedBy: window.AppState?.user?.email || ''
    });

    window.showToast?.('Settlement generated', 'success');
    window.logAudit?.('billing', 'SETTLEMENT_GENERATED', { personId, periodStart: startDate, periodEnd: endDate, total: grandTotal });
  } catch (err) {
    console.error('[Billing] Settlement error:', err);
    window.showToast?.('Error generating settlement: ' + err.message, 'error');
  }
}

function showSettlementModal(personId) {
  // TASK-1481 — comp-first (the modal subtitle shows payType/payRate/payRateUnit).
  const _rawPerson = BillingState.people?.find(p => p.id === personId);
  const person = (window.PersonComp && _rawPerson) ? window.PersonComp.effective(_rawPerson) : _rawPerson;
  if (!person) return;

  // Default to current pay period (last 2 weeks)
  const today = new Date();
  const twoWeeksAgo = new Date(today);
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  const fmtDate = (d) => d.toISOString().split('T')[0];

  const modalHTML = `
    <div id="settlement-modal-overlay" style="position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;" onclick="if(event.target===this)this.remove()">
      <div style="background:#fff;border-radius:12px;padding:24px;max-width:420px;width:90%;box-shadow:0 8px 32px rgba(0,0,0,0.2);">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
          <h3 style="margin:0;font-size:1.1rem;">Generate Settlement</h3>
          <button onclick="document.getElementById('settlement-modal-overlay').remove()" style="background:none;border:none;cursor:pointer;font-size:20px;color:#64748B;">✕</button>
        </div>
        <div style="margin-bottom:12px;">
          <div style="font-weight:600;font-size:0.9rem;">${escapeHtml(person.name || 'Unknown')}</div>
          <div style="font-size:0.8rem;color:#64748B;">${window.PersonComp?.compUnknown?.(_rawPerson) ? 'Pay rate hidden - HR Directory access required' : person.payType === 'pieceRate' ? `Piece Rate: $${parseFloat(person.payRate || 0).toFixed(2)} ${escapeHtml(person.payRateUnit || 'Each')}` : 'Hourly'}</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
          <div>
            <label style="font-size:0.78rem;font-weight:600;color:#475569;">Start Date</label>
            <input type="date" id="settlement-start" value="${fmtDate(twoWeeksAgo)}" style="width:100%;padding:8px;border:1px solid #CBD5E1;border-radius:6px;font-size:0.85rem;">
          </div>
          <div>
            <label style="font-size:0.78rem;font-weight:600;color:#475569;">End Date</label>
            <input type="date" id="settlement-end" value="${fmtDate(today)}" style="width:100%;padding:8px;border:1px solid #CBD5E1;border-radius:6px;font-size:0.85rem;">
          </div>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button onclick="document.getElementById('settlement-modal-overlay').remove()" class="btn btn-secondary" style="padding:8px 16px;">Cancel</button>
          <button id="btn-gen-settlement" onclick="window.BillingModule.generateSettlement('${personId}', document.getElementById('settlement-start').value, document.getElementById('settlement-end').value).then(()=>document.getElementById('settlement-modal-overlay')?.remove())" class="btn btn-primary" style="padding:8px 16px;">
            <span class="material-icons" style="font-size:16px;">receipt_long</span> Generate
          </button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', modalHTML);
}

// ============================================
// BILLING GMAIL — Connect / Disconnect / Status
// ============================================

async function loadBillingGmailStatus() {
  const tenantId = BillingState.tenantId;
  if (!tenantId) return;
  try {
    const tDoc = await getDoc(doc(db, 'tenants', tenantId));
    const t = tDoc.exists() ? tDoc.data() : {};
    InvoiceBuilderState.gmailStatus = {
      connected: !!t.billingGmailConnected,
      // TASK-1412 — the server keeps sending through the stored (dead) grant
      // until a reconnect or disconnect, so "not connected" must not read as
      // "sending via DispatchDeck" here.
      needsReconnect: !t.billingGmailConnected && !!t.billingGmailNeedsReconnect,
      email: t.billingGmailEmail || null,
      name: t.billingGmailName || null,
      loaded: true,
    };
  } catch (err) {
    console.warn('[Billing Gmail] status load failed:', err);
    InvoiceBuilderState.gmailStatus.loaded = true;
  }
}

async function connectBillingGmail(event) {
  const btn = event?.currentTarget;
  const original = btn ? btn.innerHTML : null;
  if (btn) { btn.disabled = true; btn.innerHTML = 'Opening…'; }
  try {
    if (window.showToast) window.showToast('Opening Google sign-in…', 'info');
    const getUrl = httpsCallable(functions, 'billingGetGmailAuthUrl');
    const res = await getUrl({ tenantId: BillingState.tenantId });
    const authUrl = res.data?.url;
    if (!authUrl) throw new Error('No auth URL returned');
    window.open(authUrl, '_blank', 'width=520,height=700');
  } catch (err) {
    console.error('[Billing Gmail] connect error:', err);
    if (window.showToast) window.showToast(`Error: ${err.message || err}`, 'error');
  } finally {
    if (btn) { btn.disabled = false; if (original != null) btn.innerHTML = original; }
  }
}

async function disconnectBillingGmail(event) {
  if (!confirm('Disconnect Gmail? Invoices will go back to sending from notifications@dispatchdeck.app.')) return;
  const btn = event?.currentTarget;
  const original = btn ? btn.innerHTML : null;
  if (btn) { btn.disabled = true; btn.innerHTML = 'Disconnecting…'; }
  try {
    const fn = httpsCallable(functions, 'billingDisconnectGmail');
    await fn({ tenantId: BillingState.tenantId });
    InvoiceBuilderState.gmailStatus = { connected: false, email: null, name: null, loaded: true };
    if (window.showToast) window.showToast('Gmail disconnected', 'success');
    // TASK-4771 — repaint BOTH surfaces. Disconnect is now clicked from the
    // Company Info card, but the Invoice Builder's pointer echoes the same
    // state, and either one can be open behind the other.
    try { refreshSendingIdentityCard(); } catch (_) {}
    if (typeof renderInvoiceBuilderBody === 'function') renderInvoiceBuilderBody();
  } catch (err) {
    console.error('[Billing Gmail] disconnect error:', err);
    if (window.showToast) window.showToast(`Error: ${err.message || err}`, 'error');
    if (btn) { btn.disabled = false; if (original != null) btn.innerHTML = original; }
  }
}

// TASK-387 — apply an OAuth result delivered by the self-closing popup.
function applyBillingGmailOAuthResult(result) {
  if (!result) return;
  if (result.error) {
    if (window.showToast) window.showToast(`Gmail connect failed: ${result.error}`, 'error');
    return;
  }
  if (result.connected) {
    const email = result.email || '';
    // Optimistically reflect; force a status reload next panel open so the
    // authoritative tenant fields reconcile.
    InvoiceBuilderState.gmailStatus = { connected: true, email, name: null, loaded: false };
    // TASK-4771 — the popup can now be launched from the Company Info card, so
    // that is the surface most likely to be open when the result lands. Repaint
    // it as well as the builder; both are no-ops when not mounted.
    try { refreshSendingIdentityCard(); } catch (_) {}
    try { if (typeof renderInvoiceBuilderBody === 'function') renderInvoiceBuilderBody(); } catch (_) {}
    if (window.showToast) window.showToast(`Gmail connected: ${email}`, 'success');
  }
}

// Primary path: the OAuth popup postMessages the result here, then self-closes.
// Trusted senders: the app's own origin (no-opener URL-param fallback) AND the
// Cloud Functions origin that actually serves the callback popup page — the
// callback is NOT same-origin (served from cloudfunctions.net), so a
// same-origin-only check would drop every valid message (TASK-374 re-COD lesson).
// The data.type check is the second gate.
const _BILLING_OAUTH_ORIGINS = [window.location.origin, 'https://us-central1-dispatch-deck-be88c.cloudfunctions.net'];
window.addEventListener('message', (ev) => {
  if (!_BILLING_OAUTH_ORIGINS.includes(ev.origin)) return;
  const data = ev.data;
  if (!data || data.type !== 'billing-gmail-oauth') return;
  applyBillingGmailOAuthResult(data);
});

// Fallback path: popup had no opener (popup-blocked / TWA) and redirected the
// top-level window back with query params. Handle them on load.
(function handleBillingGmailCallbackParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('billing-gmail-connected')) {
      const email = params.get('email') || '';
      setTimeout(() => {
        if (window.showToast) window.showToast(`Gmail connected: ${email}`, 'success');
      }, 1000);
      // Force reload of status next time the panel opens
      InvoiceBuilderState.gmailStatus = { connected: true, email, name: null, loaded: false };
      params.delete('billing-gmail-connected');
      params.delete('email');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    } else if (params.has('billing-gmail-error')) {
      const err = params.get('billing-gmail-error');
      setTimeout(() => {
        if (window.showToast) window.showToast(`Gmail connect failed: ${err}`, 'error');
      }, 1000);
      params.delete('billing-gmail-error');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    }
  } catch (e) { /* ignore */ }
})();

// ============================================
// EXPOSE TO WINDOW
// ============================================
window.BillingState = BillingState;
// TASK-4564 — the rounding decision, exported PURE so any surface (and
// tests/task4564-line-rounding.unit.cjs) can ask it without ReviewState or
// Firestore. `window.*`, never a cross-import — a `?v=`-busted import would mint
// a second module instance (Pitfall #1).
window.BillingRounding = { decideLineRounding, roundHalfHour };
// TASK-1398 — the job-day duplicate decision, exported for the same reason the
// rounding decision is: window.*, NEVER a ?v= cross-import (Pitfall #1).
window.BillingDedup = { classifyJobDayTickets, isTicketInvoiced, isTicketArchivedStatus, classifyCalendarTicketStatus, classifyDayBillingState, isBilledExternally };
// TASK-10043 S4 — the ONE entry point to the lazy "Billing-only jobs" list
// (billingOnlyJobsUi.js; its writes go through the billingOnlyJobs callable).
function openBillingOnlyJobs() {
  document.querySelectorAll('.billing-overflow-dropdown').forEach((d) => d.classList.add('hidden'));
  __ddChunkWrap(import('./billingOnlyJobsUi.js?v=10134001')).then((m) => m.open({ BillingState, escapeHtml, httpsCallable, functions }))
    .catch(() => window.showToast?.('Could not open the billing-only jobs list — reload the page and try again.', 'error'));
}
window.BillingModule = {
  openBillingOnlyJobs, // TASK-10043 S4
  // TASK-9440 — fuel surcharge settings (onclick handlers in the settings modal)
  saveFuelSurchargeCompany,
  saveFuelSurchargeOverride,
  removeFuelSurchargeOverride,
  loadFuelOverridePriceBooks,
  connectBillingGmail,
  disconnectBillingGmail,
  loadBillingGmailStatus,
  // TASK-9589 — the shared LIVE QuickBooks answer (true / false / null = not checked yet).
  getQBOConnectionTruth,
  isQBOConnectedLive,
  refreshQBOStatus,
  // TASK-9756/9757 — the setup window repaints the panel after the read-only switch;
  // BI → QuickBooks check opens the setup window on its account-map step.
  renderQBOStatusPanel,
  openQBOSetup,
  // TASK-4771 — exported so a test (and any future surface) can render the
  // sending-identity card without reaching into module scope.
  renderSendingIdentityCard,
  refreshSendingIdentityCard,
  showAddTicket,
  editTicket,
  viewTicket,
  togglePpeBillExceptions, refreshPpeBillExceptions, // TASK-9725 S2 UI — billed supplies "needs a look" card (Tickets view)
  openPpeIssueDrilldown, // TASK-9725 drill-down — the signed issues behind a billed PPE line
  showTicketPhoto,   // TASK-1220 — gallery thumbnail → lightbox
  deleteTicketPhoto, // TASK-1214 — biller photo-delete recourse
  saveTicket,
  deleteTicket,
  undoDeleteTicket,
  archiveTicket,
  // TASK-4745 — the ticket-detail modal's click. Awaits archiveTicket and closes
  // the modal only if the archive actually happened, so a refusal is not raised
  // over a screen the user has already lost.
  archiveTicketFromView,
  // TASK-4645 — the bulk-capable form. archiveTicket delegates to it, so the
  // partition-and-name-the-skipped behaviour is the ONLY archive behaviour; any
  // future multi-select archive wires straight to this and inherits the gate
  // rather than reinventing it (which is how the unbilled sweep happened).
  archiveTickets,
  unarchiveTicket,
  unarchiveFromReview,
  // TASK-1345 — one-action recovery from the archive trap.
  unarchiveAndInvoice,
  // TASK-1346 — record a ticket billed in QuickBooks/elsewhere.
  markInvoicedExternally,
  markInvoicedExternallyBulk,
  switchToBilledElsewhere, unarchiveAndMarkBilledElsewhere, // hotfix/billed-elsewhere-1020
  reviewSavedInvoice, // TASK-9735 follow-up — saved-screen Invoice
  reviewSavedBilledElsewhere, // TASK-9735 follow-up — saved-screen Billed elsewhere
  markSelectedTicketsPaid, // TASK-9735 follow-up — bulk Mark paid
  unarchiveFromView,
  addEmployeeRow,
  addTimeEntry,
  addMaterialRow,
  closeModal,
  openWorkforce,
  // Calendar navigation
  navigateMonth,
  goToToday,
  filterByCustomer,
  toggleCustomerDropdown,
  filterCustomerDropdown,
  selectCustomerFilter,
  toggleTimeOff,
  toggleBoardOverlay, // TASK-10043 S3
  setCalendarScope,   // TASK-10043 S3 — Month / Week
  // Job detail + drill-down
  showBillingJobDetail,
  showBillingDayDetails,
  showCalendarJobTickets,
  closeDayThenShowJob,
  showJobTickets,
  showAllTickets,
  backToCalendar,
  switchFilter,
  loadOlderTickets,
  navigateDailyDate,
  dailyGoToToday,
  dailyPickDate,
  toggleTicketCustomer,
  toggleTcfDropdown,
  filterTcfDropdown,
  toggleShowArchived,
  onTicketSearch,
  // Generate ticket from job
  generateTicketFromJob,
  classifyTicketAsset,      // TASK-842-A3 — pure routing helpers (also used by tests)
  planRentalTicketRouting,  // TASK-842-A3
  // Permit attachment + AR/AP-initiated upload (writes back to job)
  attachPermitToTicket,
  // TASK-4649 — missing-permit flag (non-blocking)
  toggleMissingPermitFilter,
  dismissPermitFlag,
  undoDismissPermitFlag,
  uploadPermitFromBilling,
  // Invoice & review actions
  reviewTicket,
  generateInvoice,
  viewInvoice,
  uninvoiceTicket,
  dismissPayChange, // TASK-9983
  toggleReviewFilter, reviewDecide, reviewCreateTicket, openReviewWeekly, loadReviewRange, // TASK-10041
  toggleNoClockFilter, // TASK-10186
  fixTicketDateToJobDay, // TASK-10043 S1 — ticket dated off its dispatch day
  markAsInvoiced,
  unmarkImpact, // TASK-10164-B — the invoice page's un-mark confirm
  printTicket,
  printSelectedTickets,      // TASK-9182 — bulk print selected tickets
  printReviewTicket,
  showTicketEmailDialog,  // TASK-4662 — email the FIELD TICKET to the customer
  emailReviewTicket,      // TASK-4662
  recordTicketSend,       // TASK-4662 — log a send made outside DispatchDeck
  loadTicketSendLog,      // TASK-4662 — read one ticket's append-only send log
  switchQboAgingFilter,   // TASK-9654 — QuickBooks aging buckets on the receivables list
  openInvoicesPanel,      // TASK-4767 S2 — Invoices: who owes you, record payments (lazy invoicePayments.js)
  switchDeliveryFilter,   // TASK-4662 — the orthogonal Unsent/Awaiting/Edited chips
  toggleUnsentIncludeNoEmail, // TASK-9588 — "N hidden · no-email customers" beside Unsent
  showCustomerTicketEmailPref, // TASK-9588 — per-customer "No ticket emails" quick setting
  saveCustomerTicketEmailPref,
  toggleReviewTimeEdit,
  updateReviewTime,
  // Ticket line edit/delete
  deleteTicketLine,
  editTicketLine,
  saveTicketLineEdit,
  // Review ticket modal
  openReviewTicket,
  closeReviewModal,
  closeViewTicketModal, // TASK-9411
  saveReviewTicket: _saveReviewTicketGuarded, // TASK-9735 — one at a time, stays on the ticket
  toggleDriverEditLock, // TASK-566 — per-ticket driver lock 🔒
  computeTicketTotalsFromLines, // TASK-405 — pure ticket-total helper (also used by tests)
  updateReviewEmployeeLine,
  updateReviewEquipmentLine,
  resolveUnpricedEquipment, // TASK-1165 S2 — one-click unpriced-equipment fix
  acknowledgeGuessedRate, // TASK-1674 S2 — biller confirms a guessed equipment rate
  acknowledgeTemplateQty, // TASK-9258 — biller confirms a template-placed quantity
  markEquipmentZeroHours, // TASK-1548 — mark/un-mark a priced 0-hour line deliberately non-billable
                          // (TASK-1405 — the SAME affordance releases an unpriced line that ran)
  markRouteMileageOk, // TASK-1615-B — record that a bad-route Per Mile quantity was checked
  auditEquipmentRefs, // TASK-4741 — pure unresolvable-equipment-ref audit (3 states: ok / could_not_check)
  _equipmentRefsFromLines, // TASK-4741 — ticket EQUIPMENT lines -> audit input
  _equipmentRefsFromJobDays, // TASK-4741 — jobDay truck/trailer/asset refs -> audit input
  equipmentLineNeedsHours, // TASK-1548 — the ready/invoice gate predicate (twin in clock.js TCX)
  equipmentLineUnpriced, // TASK-1405 — its mirror: hours with no rate (twin in clock.js TCX)
  updateReviewEquipmentUnit,
  updateReviewEquipmentNotes,
  updateReviewEquipmentInternalNotes,
  moveReviewEquipmentLine,
  addReviewPermitRow,
  updateReviewPermitLine,
  updateReviewHeaderField,
  // TASK-4828 — PO entry hygiene on the ticket review modal (the live ticket PO
  // field). Warns at QuickBooks' 15-char cap and offers this customer's own
  // spelling. Never blocks, never rewrites.
  onReviewPoInput,
  useExistingReviewPoSpelling,
  updateReviewWorkTime,
  toggleReviewDateEdit,
  updateReviewDate,
  updateReviewCustomer,
  addDayFromReview,
  deleteReviewTicket,
  onReviewEmployeeSelect,
  onReviewEquipmentSelect,
  onReviewEquipmentRateSelect,
  onReviewItemCodeChange,
  addReviewEmployeeRow,
  addReviewEquipmentRow,
  addReviewMiscRow,
  removeReviewLine,
  restoreMissingCrew, // TASK-9755
  keepCrewOffTicket, // TASK-9755
  moveSisterHoursHere, // TASK-9866 — the crew's same-day hours on a sister job → this job
  updateReviewDowntimeNotes,
  updateReviewStandbyNotes,
  updateReviewMiscLine,
  // Ship Slips
  openShipSlipForm,
  closeShipSlipForm,
  saveShipSlip,
  printShipSlip,
  voidShipSlip,
  toggleShipSlipTankUnit,
  recomputeShipSlipBbls,
  fireSitePing,
  handleShipSlipPhotoPick,
  removeShipSlipPhoto,
  openPrinterSetup,
  runTestPrint,
  // Ticket consolidation
  consolidateTickets,
  // Ticket notes
  saveTicketNotes,
  // Item codes management
  showItemCodesModal,
  renderBillingItemCodesBody,
  showBillingItemCodeForm,
  saveBillingItemCode,
  deleteBillingItemCode,
  toggleBillable,
  // Customer rates popover
  showCustomerRates,
  navigateToCustomerContract,
  // Accordion hierarchy
  toggleCustomerExpand,
  toggleJobExpand,
  // Ticket selection
  toggleTicketSelect,
  clearTicketSelection,
  consolidateSelected,
  combineSelectedTickets,
  moveSelectedTicketsToJob, // TASK-1581 slice 1
  groupSelectedTicketsIntoProject, // TASK-1618 part 2
  createGroupedInvoice,
  // Downtime deduction
  // QuickBooks export
  exportToQuickBooks,
  exportToIIF,
  exportPaymentIIF,
  exportFilteredTickets,
  // OpenInvoice (PIDX) export
  exportOpenInvoice,
  exportOpenInvoiceBatch,
  // Line Items management
  showLineItemsModal,
  closeLineItemsModal,
  onLiCustomerChange,
  onLiContractChange,
  toggleLiMassUpdate,
  showLiLineItemForm,
  saveLiLineItem,
  onAppliesToKindChange,
  onUomChange,
  toggleLiLineItem,
  deleteLiLineItem,
  cancelLiForm,
  onMassUpdateRate,
  onMassZeroOut,
  onMassDelete,
  undoMassDelete,
  confirmMassChanges,
  saveAllMassChanges,
  discardMassChanges,
  toggleMassEditForm,
  onMassEditKindChange,
  saveMassEdit,
  showMassAddForm,
  addMassLineItem,
  cancelMassAddForm,
  undoMassAdd,
  onMassAddAppliesToKindChange,
  showLiContractForm,
  saveLiContract,
  showLiQuickAddCustomer,
  saveLiQuickCustomer,
  // Billing settings
  openBillingSettings,
  promptEnableBuildTicket,
  closeBillingSettings,
  saveBillingSettings,
  onEquipmentRoundingToggle, // TASK-9489
  onLaborBillingToggle, // TASK-4875
  onTicketClockSyncToggle, // TASK-9982
  onClockOutModeChange,
  // TASK-9553 — per-person clock mode (sub-modal of Billing Settings)
  openPersonClockModes,
  closePersonClockModes,
  setPersonClockMode,
  // TASK-9373 — Driver App settings (sub-modal of Billing Settings)
  openDriverAppSettings,
  closeDriverAppSettings,
  applyDriverAppPreset,
  toggleDriverAppItem,
  moveDriverAppItem,
  setDriverAppGate,
  // Invoice email
  sendInvoiceEmail,
  insertMergeField,
  // QB setup wizard
  openQBSetup,
  closeQBSetup,
  toggleQBSection,
  uploadQBFile,
  saveQBSetup,
  // QB customer + item mapping
  uploadQBCustomerList,
  saveQBCustomerMappings,
  applyQBMatches,
  uploadQBItemList,
  applyQBItemMatches,
  // Company info contact rows
  addBillingPhoneRow,
  removeBillingPhoneRow,
  addBillingEmailRow,
  removeBillingEmailRow,
  addBillingAddressRow,
  removeBillingAddressRow,
  // Invoice section toggle
  // Invoice customization
  cleanupInvoiceNotes,
  handleReviewCleanupNotes,
  saveInvoiceColumns,
  saveInvoiceColumnsMass,
  // Logo upload + company info
  uploadLogo,
  openCompanyInfoModal,
  closeCompanyInfoModal,
  saveCompanyInfo,
  addCIPhoneRow,
  addCIEmailRow,
  addCIAddressRow,
  // View switching
  switchBillingView,
  navigateToAP,
  navigateToReconcile, // TASK-4867 — Billing strip → BI Reconciliation
  // TASK-1618 — AR/AP Projects view
  showProjectTickets,
  setProjectsFilter,
  setProjectsCustomer,
  setProjectsSearch,
  toggleProjectsGroup,
  // TASK-9414 — Project filter on Tickets + add jobs to a project
  setTicketProjectFilter,
  addSelectedTicketsToProject,
  addJobToProject,
  // TASK-9414 C — Projects tab New / Edit / Add jobs (billing callables)
  newBillingProject,
  editBillingProject,
  addJobsToBillingProject,
  openProjectInDispatch,
  loadBillingProjects,
  markTicketPaid,
  unmarkTicketPaid,
  writeOffTicket,
  unwriteOffTicket,
  // Billing onboarding checklist
  dismissBillingChecklist,
  showNewTicketHelper,
  // Role-based display toggle
  toggleShowNames,
  // Job management (rename, delete, drag & drop)
  getFilteredJobsForCustomer,
  // TASK-4591 — job-picker relevance window escape hatches (inline onclick).
  showOlderReviewJobs,
  expandReviewHeader, // TASK-9608
  showOlderSplitJobs,
  updateReviewJob,
  changeReviewTicketProject, // TASK-9810
  startJobRename,
  saveJobRename,
  cancelJobRename,
  deleteJob,
  onTicketDragStart,
  onTicketDragEnd,
  onJobDragOver,
  onJobDragLeave,
  onTicketDrop,
  onTicketDropNewJob,
  onDropMarkPaid,
  // TASK-9610 — project folders + move a job between projects
  onJobDragStart,
  onJobDragEnd,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
  onFolderBodyDragOver,
  onFolderBodyDrop,
  onJobZoneDragOver,
  onJobZoneDrop,
  openMoveJobToProject,
  toggleProjectFolder,
  filterToProjectFolder,
  // Document attachments
  handleReviewDocUpload,
  removeReviewDoc,
  toggleDocPrintWithInvoice,
  setReviewDocType, // TASK-9868
  // Tax
  toggleReviewTax,
  updateReviewTaxRate,
  toggleReviewTaxCategory,
  // Invoice Builder
  showInvoiceBuilder,
  closeInvoiceBuilder,
  onIBSettingChange,
  onIBEmailCopyToggle, // TASK-4714
  onIBApprovalSigChange, // TASK-4683 A3
  setIBMode,
  onIBCustomerChange,
  onIBContractChange,
  setIBPreviewTab,
  saveInvoiceBuilder,
  removeIBSection,
  addIBSection,
  // Clock-Out Form template
  toggleTBSection,
  saveCOFTemplate,
  // Custom Sections
  editCustomSection,
  deleteCustomSection,
  saveCustomSection,
  // TASK-848 — shared read-only renderer (clock.js calls it via window.*, never an ES
  // import: cross-importing ?v=-busted modules creates duplicate instances) + the
  // invoice-visibility toggle from the Templates builder.
  buildCustomSectionDataHtml,
  // TASK-9116 — the ONE visibility decision for a custom section. Exported so a new
  // customer-facing surface reuses it instead of re-deriving a fail-open test.
  isCustomSectionVisibleTo,
  // TASK-1339 — THE ticket-number allocator. One owner, three callers
  // (AR/AP New Ticket, generateTicketFromJob, clock.js ticket bootstrap).
  getNextTicketNumber,
  // TASK-10107 — the view-ticket "Create return ticket" item (lazy chunk).
  createReturnTicket: (id) => __ddChunkWrap(import('./returnTicketMirror.js?v=10107001')).then(m => m.createReturnTicket(id)),
  // TASK-4823 — NOT a supported surface. `allocateInvoiceNumber` is internal to
  // this module (its two real callers are right here in this file); the alias
  // exists so tests/ticket-number-allocator.spec.js can fire N of them in
  // parallel from a real browser under real rules and PROVE the shared retry on
  // the invoice counter too, rather than assuming it because the ticket counter
  // passed. Named `__...ForTest` so nothing mistakes it for API. It grants no
  // capability the already-exported generateInvoice does not — the invoice
  // counter's audience is enforced by firestore.rules, not by this export.
  __allocateInvoiceNumberForTest: allocateInvoiceNumber,
  // TASK-846-B — editable custom-section data in the Review Ticket modal
  updateReviewCustomField,
  addReviewCustomInstance,
  removeReviewCustomInstance,
  toggleCustomSectionInvoice,
  openCustomerMapConfig, // TASK-4845
  saveCustomerMapConfig, // TASK-4845
  previewCustomerMapLayer, // TASK-4845
  publishCustomerMapLayer, // TASK-4845
  unpublishCustomerMapLayer, // TASK-4845
  openPublishedMapLayers, // TASK-4845
  unpublishCustomerMapLayerById, // TASK-4845
  _csUpdateName,
  _csSetIcon,
  _csSetRepeatable,
  _csAddField,
  _csRemoveField,
  _csUpdateField,
  _csRemoveBinding,
  _csRemoveFieldBinding,
  _csShowFieldBindingPicker,
  _csConfirmBinding,
  _csBindOnItemChange,
  onTBHeaderChange,
  onTBFieldChange,
  addTBEquip,
  removeTBEquip,
  updateTBEquip,
  updateTBEquipField,
  addTBTimeEntry,
  removeTBTimeEntry,
  updateTBTimeEntry,
  focusTBTimes,
  saveTBTicket,
  toggleTBSmartAssist,
  copyTicketBuilderLink,
  // Mobile overflow menu
  toggleBillingOverflow,
  // Legacy alias (removed, but cached HTML may still call it)
  showTicketsByFilter: switchFilter,
  // Split ticket
  showSplitTicketModal,
  onSplitHoursChange,
  addJobFromSplit,
  executeSplitTicket,
  addJobFromReview,
  renameJobFromReview,
  // Plaid + QBO integration
  startPlaidLink,
  disconnectPlaid,
  startQBOConnect,
  disconnectQBO,
  pullQBOAccounts,
  openQBOAccountMapping,
  syncTicketToQBO,
  syncAPToQBO,
  syncAPPaymentToQBO,
  unlockVoidedQboPush, // TASK-9537 slice 3
  // Financial Setup Wizard
  openFinancialWizard,
  _wizardNext,
  _wizardPrev,
  // Settlement statements
  generateSettlement,
  showSettlementModal,
  loadSettlementData,
  buildSettlementHTML,
  invoiceStylesHTML,
};

export default { initBilling };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
